import React, { useState, useEffect, useRef } from 'react';
import { supabase } from './lib/supabase';
import Auth from './Auth';
import TecladoNumerico from './TecladoNumerico';
import { AreaChart, Area, XAxis, YAxis,
         CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

// O transporte e o design system mudaram de casa em 07/10/2026: moram em
// `src/comum/`, compartilhados com a interface da Rotina Contábil.
import { API, focarEmpresa, guardarToken } from './comum/transporte';
import { G, TIPO, MarcaAIdvisor } from './comum/design';
import {
  IconeInfo, BotaoSaibaMais, IconeLixeira, IconeLapis, BotaoRemover,
  IconeCadeado, IconeAlerta, IconeFechar, IconeReverter,
  BP60M_TOKENS, thPlan, tdPlan, COLUNA_LARGA,
  RodapeNavFonte, CardFonteEntrada,
  MarcaDaguaTitulo, QuadrosIntro,
} from './comum/componentes';
export { focarEmpresa, guardarToken };


// Helper: bloqueia operacoes de edicao quando o usuario eh admin (read-only).
// Retorna true se a operacao deve ser bloqueada (e mostra alerta).
// DEV (28/05/2026): admin pode editar durante o desenvolvimento.
// REATIVAR (=true) na Fase 1.5, junto com a autorizacao temporaria.
const ADMIN_SOMENTE_LEITURA = false;
function bloquearSeAdmin() {
  if (!ADMIN_SOMENTE_LEITURA) return false;
  if (typeof window !== 'undefined' && window.__ehAdmin) {
    alert(
      'Modo Administrador (somente leitura).\n\n' +
      'Voce nao pode editar dados sem autorizacao do empresario.\n' +
      'Para editar, solicite a liberacao temporaria de suporte.'
    );
    return true;
  }
  return false;
}



// ─────────────────────────────────────────────────────────────────────────
// <CarimboAIdvisor /> — selo "Powered by RUNGOAL AIDVISOR" estilo carimbo
// de tinta. Marca presença da IA na tela. Aplicar SOMENTE em telas onde a
// IA real ou heurística está embarcada. Container precisa de `position:
// relative` pro carimbo ficar no canto superior direito.
// Decidido 12/06/2026 (Fabio): 1a "Powered by" · 2(ii) carimbo de tinta ·
// 3 mais nítido · 4b 120×120 · 5 convive com marca d'água do título ·
// 6 só páginas IA · 7 vermelho/laranja de carimbo.
// ─────────────────────────────────────────────────────────────────────────
// O CARIMBO NASCE NÍTIDO desde 08/10/2026. O padrão era o desfoque de 0,3
// pixel, que imita tinta de carimbo — e em 120 pixels come o desenho: a
// marca saía borrada em 22 das 24 telas que o usam. Em 02/10 a landing
// passou à variante nítida uma a uma; agora a regra se inverte, e quem
// quiser o efeito de tinta pede `desfocado`.
const CarimboAIdvisor = ({ top = -9, right = 200, tamanho = 120, variant = 'multicolor', desfocado = false }) => {
  // multicolor (default 13/06 · validado em T7 e adotado em todas as telas com IA):
  //   moldura laranja · "Powered by" verde · RUNGOAL AIDVISOR em cores oficiais da
  //   marca (RUNGOAL verde + AI laranja + DVISOR verde).
  // monocromatico (legado · pré-13/06): tudo em laranja-vermelho de tinta #D2461F
  const cores = variant === 'multicolor'
    ? { mold: G.laranja, powered: G.verde, base: G.verde, ai: G.laranja }
    : { mold: '#D2461F', powered: '#D2461F', base: '#D2461F', ai: '#D2461F' };
  return (
    <div style={{
      position: 'absolute', top, right,
      width: tamanho, height: tamanho,
      pointerEvents: 'none', zIndex: 5,
      transform: 'rotate(30deg)',
      transformOrigin: 'center',
      opacity: 0.85,
      filter: desfocado ? 'blur(0.3px) contrast(1.05)' : 'none',
    }}>
      <div style={{
        width: '100%', height: '100%',
        border: `3px solid ${cores.mold}`, borderRadius: '50%',
        position: 'relative', boxSizing: 'border-box',
        display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center',
        padding: 10,
      }}>
        {/* anel interno · reforça aspecto de carimbo oficial */}
        <div style={{
          position: 'absolute', top: 6, left: 6, right: 6, bottom: 6,
          border: `1px solid ${cores.mold}`, borderRadius: '50%',
          pointerEvents: 'none',
        }}/>
        <div style={{
          fontSize: tamanho * 0.075, fontWeight: 700, color: cores.powered,
          textTransform: 'uppercase', letterSpacing: '0.16em',
          textAlign: 'center', lineHeight: 1.1,
          fontFamily: "'Outfit', sans-serif",
        }}>
          Powered by
        </div>
        <div style={{
          fontSize: tamanho * 0.105, fontWeight: 900,
          textTransform: 'uppercase', letterSpacing: '0.04em',
          textAlign: 'center', marginTop: 4, lineHeight: 1.05,
          fontFamily: "'Outfit', sans-serif",
        }}>
          <span style={{ color: cores.base }}>RUNGOAL</span><br/>
          <span style={{ color: cores.ai }}>AI</span><span style={{ color: cores.base }}>DVISOR</span>
        </div>
      </div>
    </div>
  );
};

// Pacotes Pro e AiPro APOSENTADOS em 28/09/2026. Eram a nomenclatura de
// 09/06, revogada pelas modalidades Controller · Consultor · Consultor M&A ·
// Contábil, que não são degraus de um mesmo produto: são quatro públicos, e
// entre elas não existe "subir de plano".
//
// Saíram daqui: `isAiPro`, `<MarcaTier />` e o portão `premium` do menu. Quem
// decide o que cada um enxerga hoje é `ABAS_POR_MODALIDADE` — aba não
// contratada simplesmente não aparece, sem convite para comprá-la.
//
// A coluna `empresas.tier` fica no banco, sem uso, como histórico.


// ═══════════════════════════════════════════════════════════════════════
// BP60M_TOKENS — Design System da jornada Business Plan 60M
// Single source of truth: TODA tarefa do BP60M usa esses tokens via <TarefaBP60M />.
// Mudou um token aqui → reflete em todas as 20 tarefas automaticamente.
// Doc: memory/design-system-bp60m.md
// ═══════════════════════════════════════════════════════════════════════

// ═══════════════════════════════════════════════════════════════════════
// <TarefaBP60M /> — Shell reutilizável que envolve TODA tarefa do BP60M.
// Padroniza header (eyebrow + título + marca-d'água + selo de status),
// barra de progresso (20 bolinhas), conteúdo central (children) e footer
// (6 botões). Cada tarefa específica passa apenas conteúdo + callbacks.
//
// Props:
//   numero (1-20)            · número da tarefa atual
//   total (default 20)       · total de tarefas
//   eyebrow                  · string acima do título (ex: "Tarefa 6 de 18 · Análise Estratégica")
//   titulo                   · string grande
//   subtitulo                · string descritiva curta
//   iconeMarca               · SVG element (marca-d'água atrás do título)
//   status                   · 'nao-iniciado' | 'em-edicao' | 'em-revisao' | 'aprovado'
//   tarefasFeitas            · array de números (ex: [1,2,3,4,5,6])
//   onVoltar / onAvancar     · callbacks de navegação
//   onSalvarRascunho/onRevisar/onEditar/onAprovar · callbacks de ação (opcionais)
//   podeAvancar (default false) · habilita botão Avançar
//   children                 · conteúdo específico da tarefa
//   acoesVisiveis (default ['rascunho','editar','revisar','aprovar']) · controla quais aparecem
// ═══════════════════════════════════════════════════════════════════════
const TarefaBP60M = ({
  numero, total = 20, eyebrow, titulo, subtitulo, iconeMarca,
  status = 'em-edicao', tarefasFeitas = [],
  onVoltar, onAvancar, onSalvarRascunho, onRevisar, onEditar, onAprovar,
  podeAvancar = false, podeVoltar = true,
  children,
  acoesVisiveis = ['rascunho','editar','revisar','aprovar'],
  // Preview do BP compilado (botão abaixo do selo · só renderiza se onPreview existe)
  previewCount = 0, onPreview,
  // Quando true, oculta o footer com botões (são renderizados externamente · ex: rodapé sticky)
  ocultarBotoes = false,
}) => {
  const T = BP60M_TOKENS;
  const selo = T.selos[status] || T.selos['em-edicao'];
  const percent = Math.round((numero / total) * 100);

  // Botão helper — aplica estilo base + variante
  const Btn = ({ variante, onClick, disabled, children: cont }) => {
    const estilo = { ...T.botoes.base, ...T.botoes[variante] };
    if (disabled) { estilo.cursor = 'not-allowed'; estilo.opacity = 0.45; }
    return <button onClick={onClick} disabled={disabled} style={estilo}>{cont}</button>;
  };

  // Número da barra de progresso (substitui bolinha · 11/06/2026).
  // Marca d'água laranja: todas em G.laranja com opacity variável.
  //   atual:    opacity 1.0  · fontWeight 800
  //   feita:    opacity 0.65 · fontWeight 700
  //   bloq:     opacity 0.30 · fontWeight 600
  const NumeroProgresso = ({ n }) => {
    const atual = n === numero;
    let opacity, fontWeight;
    if (atual) { opacity = 1.0; fontWeight = 800; }
    else if (tarefasFeitas.includes(n)) { opacity = 0.65; fontWeight = 700; }
    else { opacity = 0.30; fontWeight = 600; }
    return (
      <span title={`Tarefa ${n}`} style={{
        fontSize: atual ? 14 : 11,
        fontWeight, color: G.laranja, opacity,
        fontVariantNumeric: 'tabular-nums',
        fontFamily: "'Outfit', sans-serif",
        minWidth: atual ? 18 : 14, textAlign: 'center',
        letterSpacing: '0.02em',
        lineHeight: 1,
        display: 'inline-block',
      }}>{n}</span>
    );
  };

  return (
    <div style={{
      background: T.cores.bg, borderRadius: T.layout.radiusTela,
      padding: T.layout.paddingTela, position: 'relative',
      boxShadow: '0 2px 12px rgba(0,0,0,0.04)',
      overflow: 'hidden',
    }}>
      <CarimboAIdvisor />
      {/* HEADER */}
      <div style={{ display: 'flex', justifyContent: 'space-between',
        alignItems: 'flex-start', marginBottom: 14, gap: 14 }}>
        <div style={{ position: 'relative' }}>
          <div style={{ position: 'relative', display: 'inline-block' }}>
            <h1 style={{ ...T.tipo.tituloTarefa, margin: 0,
              position: 'relative', zIndex: 1 }}>{titulo}</h1>
            {iconeMarca && (
              <div style={{ position: 'absolute', right: -8, bottom: -34,
                color: G.verde, opacity: 0.13, transform: 'rotate(30deg)',
                transformOrigin: 'center', pointerEvents: 'none', zIndex: 0 }}>
                {iconeMarca}
              </div>
            )}
          </div>
          {subtitulo && (
            <p style={{ ...T.tipo.subtitulo, margin: '5px 0 0' }}>{subtitulo}</p>
          )}
        </div>
        {/* Selo de status + botão "Preview BP" (opcional) empilhados no canto */}
        <div style={{ display: 'flex', flexDirection: 'column',
          alignItems: 'flex-end', gap: 8 }}>
          <div style={{
            padding: '6px 12px', borderRadius: 0, fontSize: 10,
            fontWeight: 800, letterSpacing: '0.10em', textTransform: 'uppercase',
            background: selo.bg, color: selo.cor, border: selo.border,
            whiteSpace: 'nowrap',
          }}>{selo.label}</div>
          {onPreview && (
            <button onClick={onPreview}
              disabled={previewCount === 0}
              title={previewCount === 0 ? 'Aprove ao menos uma tarefa para ver o preview' : 'Ver Business Plan compilado'}
              style={{
                background: previewCount > 0 ? G.verdeLight : '#F5F5F1',
                border: `1px solid ${previewCount > 0 ? G.verde : G.borda}`,
                color: previewCount > 0 ? G.verde : G.txt2,
                padding: '6px 10px', fontSize: 10, fontWeight: 700,
                letterSpacing: '0.08em', textTransform: 'uppercase',
                borderRadius: 0,
                cursor: previewCount > 0 ? 'pointer' : 'not-allowed',
                fontFamily: "'Outfit', sans-serif", whiteSpace: 'nowrap',
                display: 'inline-flex', alignItems: 'center', gap: 6,
              }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"
                style={{ flexShrink: 0 }} aria-hidden="true">
                <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7z" />
                <circle cx="12" cy="12" r="3" />
              </svg>
              Preview · {previewCount}/{total}
            </button>
          )}
        </div>
      </div>

      {/* BARRA DE PROGRESSO — números (marca d'água laranja) substituindo bolinhas.
          Fundo verde leve (mesmo dos cards selecionados) destaca a faixa. */}
      <div style={{ display: 'flex', justifyContent: 'space-between',
        alignItems: 'center', margin: '10px 0 14px', padding: '8px 14px',
        background: G.verdeLight, borderRadius: 0 }}>
        <span style={{ ...T.tipo.eyebrow }}>
          {eyebrow}
        </span>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          {Array.from({ length: total }, (_, i) => i + 1).map(n => <NumeroProgresso key={n} n={n}/>)}
        </div>
        <span style={{ fontSize: 11, fontWeight: 700, color: G.laranja,
          letterSpacing: '0.08em', textTransform: 'uppercase' }}>
          {percent}%
        </span>
      </div>

      {/* CONTEÚDO CENTRAL — cada tarefa renderiza aqui */}
      <div style={{ marginBottom: 28, minHeight: T.layout.minHeightConteudo }}>
        {children || (
          <div style={{
            background: T.cores.bege, border: `2px dashed ${T.cores.borda}`,
            borderRadius: 0, padding: '40px 24px', textAlign: 'center',
            color: T.cores.txtSub, fontSize: 13, fontStyle: 'italic',
          }}>
            <strong style={{ display: 'block', fontStyle: 'normal',
              color: T.cores.txt, fontSize: 15, fontWeight: 700, marginBottom: 6 }}>
              Área de conteúdo desta Tarefa
            </strong>
            Cada tarefa renderiza dentro deste shell o conteúdo específico —
            checklist, cards de IA ou formulário livre.
          </div>
        )}
      </div>

      {/* FOOTER (oculto quando ocultarBotoes=true · botões vão pra rodapé externo) */}
      {!ocultarBotoes && (
        <div style={{ display: 'flex', justifyContent: 'space-between',
          alignItems: 'center', paddingTop: 18,
          borderTop: `1px solid ${T.cores.borda}`, gap: 14 }}>
          <Btn variante="secundario" onClick={onVoltar} disabled={!podeVoltar}>← Anterior</Btn>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {acoesVisiveis.includes('rascunho') &&
              <Btn variante="rascunho" onClick={onSalvarRascunho}>Salvar Rascunho</Btn>}
            {acoesVisiveis.includes('editar') &&
              <Btn variante="editar" onClick={onEditar}>Editar</Btn>}
            {acoesVisiveis.includes('revisar') &&
              <Btn variante="revisar" onClick={onRevisar}>Revisar</Btn>}
            {acoesVisiveis.includes('aprovar') &&
              <Btn variante="aprovar" onClick={onAprovar}>✓ Aprovar</Btn>}
          </div>
          <Btn variante="primario" onClick={onAvancar} disabled={!podeAvancar}>Avançar →</Btn>
        </div>
      )}
    </div>
  );
};

// Array estático das empresas demo · tier define gate Pro vs AiPro nas features
// (Adicionais · Balanço Patrimonial · BP60M completo · lista dinâmica de dívidas).
// TODO: quando login real (Lacuna #2 / Fase 4), tier vem do backend por usuário.
//
// `demo: true` (30/07/2026) — dado SINTÉTICO, criado para exercitar a
// ferramenta. Nenhuma dessas empresas existe. A marcação impede a confusão
// com cliente real: até aqui ShopMax e MORED apareciam idênticas no seletor,
// e a MORED tem documentação contábil de verdade.
const EMPRESAS = [
  { id: 'shopmax',     nome: 'ShopMax',      setor: 'E-commerce',        tier: 'aipro', demo: true },
  { id: 'sabor_arte',  nome: 'Sabor & Arte', setor: 'Bar/Restaurante',   tier: 'pro',   demo: true },
  { id: 'clinicavida', nome: 'ClínicaVida',  setor: 'Saúde',             tier: 'pro',   demo: true },
  { id: 'construmax',  nome: 'Construmax',   setor: 'Construção Civil',  tier: 'pro',   demo: true },
  // Bancada de testes (30/07/2026). Existe para que nenhum teste precise ser
  // feito na MORED, que tem balancete e razão reais de um cliente do escritório.
  // Duas vezes usei a MORED como laboratório (produto fantasma em 28/07, quatro
  // produtos "TESTE" em 29/07) — ambos removidos, mas o padrão era ruim.
  { id: 'sandbox',     nome: 'SANDBOX · Bancada de testes',
                                             setor: 'geral',             tier: 'aipro', demo: true },
];


// ═══════════════════════════════════════════════════════════════════════
// EXEMPLO DIDÁTICO — a fábrica de bolos (30/07/2026)
// ═══════════════════════════════════════════════════════════════════════
// Toda empresa nova nasce com a receita do bolo em Custos Diretos, para o
// consultor mostrar a mecânica ao cliente em vez de explicá-la sobre uma tela
// vazia. Sai num clique quando o cadastro real começa.
//
// Não fere "base zerada, nada nativo": aquele princípio proíbe dado que se
// PASSA POR MEDIÇÃO (o custo fixo de R$ 20.300 que surgiu na MORED como se
// viesse do balancete). Aqui o exemplo se declara exemplo — rótulo na linha,
// faixa no topo, botão de remoção. O que engana é o default disfarçado.
const SeloExemplo = () => (
  <span style={{
    display: 'inline-block', fontSize: 8.5, fontWeight: 800,
    letterSpacing: '0.1em', textTransform: 'uppercase',
    color: '#B45309', background: '#FFFBEB', border: '1px solid #FCD34D',
    borderRadius: 0, padding: '1px 5px', lineHeight: 1.4, whiteSpace: 'nowrap',
  }}>exemplo</span>
);

// Faixa de topo · aparece só enquanto o exemplo existir na empresa.
const FaixaExemplo = ({ quantos, onRemover, removendo }) => {
  if (!quantos) return null;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 14,
      flexWrap: 'wrap', padding: '11px 16px', marginBottom: 14,
      background: '#FFFBEB', border: '1px solid #FCD34D', borderRadius: 0 }}>
      <SeloExemplo/>
      <div style={{ fontSize: 12, lineHeight: 1.45, color: G.txt1, flex: 1,
        minWidth: 260 }}>
        <strong>Exemplo de demonstração — uma fábrica de bolos</strong>, para
        você ver a mecânica funcionando: insumo com índice de consumo e preço,
        composição do produto, custo direto e margem. Remova quando começar o
        cadastro real da empresa.
      </div>
      <button onClick={onRemover} disabled={removendo}
        style={{ padding: '7px 14px', fontSize: 11, fontWeight: 800,
          letterSpacing: '0.06em', textTransform: 'uppercase',
          background: '#FFF', color: '#B45309', border: '1px solid #B45309',
          borderRadius: 0, cursor: removendo ? 'wait' : 'pointer',
          fontFamily: "'Outfit', sans-serif", opacity: removendo ? 0.6 : 1,
          whiteSpace: 'nowrap' }}>
        {removendo ? 'Removendo…' : 'Remover o exemplo'}
      </button>
    </div>
  );
};

// Deriva do id, e não do campo `demo` do objeto: a empresa restaurada do
// localStorage guarda apenas {id, nome, setor}, então `empresa.demo` chega
// undefined depois de um F5 e o selo sumia da tela (visto em 30/07).
const ehEmpresaDemo = (id) => EMPRESAS.some(e => e.id === id && e.demo);

// Selo de demonstração — some para empresa real, aparece para dado sintético.
// Usa o laranja da marca: chama atenção sem soar erro (o vermelho é do aviso
// de contaminação, que é falha de verdade).
const SeloDemonstracao = ({ compacto = false }) => (
  <span style={{
    display: 'inline-block', fontSize: compacto ? 9 : 10, fontWeight: 800,
    letterSpacing: '0.1em', textTransform: 'uppercase',
    color: '#F47C3C', background: 'rgba(244,124,60,0.14)',
    border: '1px solid rgba(244,124,60,0.45)', borderRadius: 0,
    padding: compacto ? '1px 5px' : '2px 7px', lineHeight: 1.4,
    whiteSpace: 'nowrap',
  }}>demonstração</span>
);

// PERIODOS sao gerados dinamicamente do range real de dados (gerarPeriodos)
// — do primeiro mes alimentado ate o mes vigente. Substitui a lista hardcoded.
const MESES_ABREV = ['Jan','Fev','Mar','Abr','Mai','Jun','Jul','Ago','Set','Out','Nov','Dez'];
function gerarPeriodos(dataInicio, dataFim) {
  if (!dataInicio || !dataFim) return [];
  const ini = new Date(dataInicio + 'T00:00:00');
  const fim = new Date(dataFim + 'T00:00:00');
  const meses = [];
  const cur = new Date(ini.getFullYear(), ini.getMonth(), 1);
  while (cur <= fim) {
    const y = cur.getFullYear();
    const m = cur.getMonth();
    const lastDay = new Date(y, m + 1, 0).getDate();
    meses.push({
      label: `${MESES_ABREV[m]}/${y}`,
      inicio: `${y}-${String(m+1).padStart(2,'0')}-01`,
      fim: `${y}-${String(m+1).padStart(2,'0')}-${String(lastDay).padStart(2,'0')}`,
    });
    cur.setMonth(cur.getMonth() + 1);
  }
  return meses;
}

// Janela padrão de exibição (regra de Fabio · P678, 10/09/2026): os ÚLTIMOS 12
// MESES COMPLETOS. O mês corrente não entra — está pela metade e faria a
// comparação mensal mentir. Quando o último mês COM DADO é anterior ao último
// mês completo, a janela ancora nele: numa empresa cuja contabilidade vai até
// dez/2025, o certo é mostrar jan–dez/2025, não doze meses em branco.
//
// `periodos` é a saída de gerarPeriodos (do 1º mês alimentado ao último).
// Devolve {inicio, fim} tirados dessa mesma lista — nunca meses inventados.
const MESES_JANELA = 12;
function janelaPadrao(periodos, hoje = new Date()) {
  if (!periodos || periodos.length === 0) return null;

  // Último mês COMPLETO pelo relógio: o anterior ao corrente. O construtor do
  // Date normaliza mês -1, então janeiro vira dezembro do ano passado sozinho.
  const ref = new Date(hoje.getFullYear(), hoje.getMonth() - 1, 1);
  const chaveCompleto = `${ref.getFullYear()}-${String(ref.getMonth() + 1).padStart(2, '0')}`;

  const chave = p => p.inicio.slice(0, 7);
  // Fim = último mês com dado que já esteja fechado. Se a empresa só tem o mês
  // corrente (ainda aberto), não há mês completo: mostra o que existe, em vez
  // de devolver uma janela vazia.
  const fechados = periodos.filter(p => chave(p) <= chaveCompleto);
  if (fechados.length === 0) {
    return { inicio: periodos[0], fim: periodos[periodos.length - 1] };
  }

  const fim = fechados[fechados.length - 1];
  const iFim = periodos.indexOf(fim);
  // Menos de 12 meses disponíveis: começa no primeiro que existe.
  const iIni = Math.max(0, iFim - (MESES_JANELA - 1));
  return { inicio: periodos[iIni], fim };
}

const CATEGORIAS_TIPO = [
  'Receita Operacional', 'Receita Financeira',
  'Despesa Operacional', 'Despesa Comercial', 'Despesa Administrativa',
  'Despesa Financeira', 'Tributos', 'CAPEX', 'Transferência'
];

const fmtBRL = v => v == null ? 'N/D' :
  'R$ ' + Math.abs(v).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const fmtPct = v => v == null ? 'N/D' : (v * 100).toFixed(1).replace('.', ',') + '%';

// 'AAAA-MM' → 'jan/26'. Nasceu dentro da TelaPlano (base de análise) e subiu
// ao escopo global em 01/09 para o Previsto x Real usar a MESMA grafia na
// janela do plano — um rótulo, uma implementação.
const rotuloMesCurto = (mes) => {
  const ABREV = ['jan','fev','mar','abr','mai','jun','jul','ago','set','out','nov','dez'];
  const [ano, m] = String(mes || '').split('-');
  return (ABREV[parseInt(m, 10) - 1] || m) + '/' + String(ano || '').slice(2);
};

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{ background: G.card, borderRadius: 0, padding: '10px 16px',
      }}>
      <p style={{ fontWeight: 700, color: G.txt1, marginBottom: 6, fontSize: 13 }}>{label}</p>
      {payload.map((p, i) => (
        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12 }}>
          <span style={{ width: 8, height: 8, borderRadius: '50%',
            background: p.color, display: 'inline-block' }}/>
          <span style={{ color: G.txt2 }}>{p.name}:</span>
          <span style={{ color: G.txt1, fontWeight: 600 }}>{fmtBRL(p.value)}</span>
        </div>
      ))}
    </div>
  );
};

// ── Configurações ─────────────────────────────────────────────────
const SETORES_VALOR = [
  'Tecnologia/SaaS', 'E-commerce', 'Saude', 'Construcao Civil',
  'Bar/Restaurante', 'Agronegocio', 'Educacao', 'Logistica/Transporte',
  'Industria/Manufatura', 'Servicos Profissionais', 'Varejo Fisico',
  'Imobiliario', 'Academia/Fitness', 'Beleza/Estetica', 'geral'
];

// Teclado numérico on-screen reutilizável (Configurações, Cadastro, etc.).
// Recebe o valor exibido e callbacks — quem chama decide como aplicar (mascara,
// raw, etc.). `permitirDecimal` controla se mostra a tecla "." (false em CEP/CNPJ).
// <TecladoNumerico /> — extraído para ./TecladoNumerico.js (importado no topo)
// para ser reusado também pelo Auth.js sem dependência circular.

// ═══════════════════════════════════════════════════════════════════════
// <CampoNumericoBP60M /> — auxiliar reutilizável que aplica o
// <TecladoNumerico /> padrão a qualquer campo numérico do app. Renderiza
// um display clicável formatado e abre o teclado virtual como modal
// flutuante. Substitui inputs `type="number"` espalhados (sem inputMode,
// sem TecladoNumerico) por uma experiência única e consistente.
//
// Props:
//   valor           — valor atual (número ou null)
//   onChange(num)   — chamado no OK · recebe número (ou null se vazio)
//   label           — rótulo mostrado no header do TecladoNumerico
//   permitirDecimal — false em campos inteiros (default true)
//   min · max       — limites · respeitados no OK (capa o valor)
//   prefixo · sufixo — rótulos opcionais ao lado do display (R$ · % · ×)
//   formato         — 'int' · 'brl' · 'decimal' (default) — formatação ptBR
//   textAlign       — 'right' (default) ou 'left'
//   largura         — largura CSS do display (default '100%')
//   disabled        — bloqueia clique
// ═══════════════════════════════════════════════════════════════════════
const CampoNumericoBP60M = ({
  valor, onChange, label,
  permitirDecimal = true,
  min, max,
  prefixo, sufixo,
  formato = 'decimal',
  textAlign = 'right',
  largura,
  disabled = false,
  alerta = false,   // realça o número em vermelho e aumenta um pouco quando true
}) => {
  const [aberto, setAberto] = useState(false);
  const [buffer, setBuffer] = useState('');

  const abrir = () => {
    if (disabled) return;
    setBuffer(valor !== undefined && valor !== null && valor !== ''
      ? String(valor).replace('.', '.') : '');
    setAberto(true);
  };
  const formatar = (v) => {
    if (v === undefined || v === null || v === '') return '—';
    const num = Number(String(v).replace(',', '.'));
    if (isNaN(num)) return String(v);
    // Item 7.8.3: `brl` mostra R$ 30.000.000,00; `decimal` mostra
    // 30.000.000,00, sem unidade; `int` nao tem casas decimais.
    if (formato === 'brl') return num.toLocaleString('pt-BR',
      { style: 'currency', currency: 'BRL',
        minimumFractionDigits: 2, maximumFractionDigits: 2 });
    if (formato === 'int') return num.toLocaleString('pt-BR',
      { maximumFractionDigits: 0 });
    return num.toLocaleString('pt-BR',
      { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  //: Os formatos que levam parte decimal. `int` fica de fora: contador com
  //: virgula nao e numero, e erro a vista.
  const COM_CASAS = formato === 'brl' || formato === 'decimal';

  // O que aparece no visor ENQUANTO ele digita, pelo item 7.8.1: os digitos
  // entram na parte inteira, e os pontos de milhar e a virgula aparecem
  // sozinhos. Digitar 3-0-0-0-0-0-0-0 mostra "30.000.000,00" na hora, nao um
  // paredao de zeros que ninguem conta.
  const visorDoBuffer = () => {
    if (buffer === '') return '';
    // `int` não tem casas decimais, mas tem milhar: 30.000.000 (acréscimo do
    // Fabio ao item 7.8.3, em 12/10/2026). Devolver o buffer cru mostraria
    // "30000000" — o paredão de zeros que a regra veio justamente desfazer.
    if (!COM_CASAS) return Number(buffer || 0).toLocaleString('pt-BR',
      { maximumFractionDigits: 0 });
    const [inteira, decimal] = buffer.split('.');
    const esquerda = Number(inteira || 0).toLocaleString('pt-BR',
      { maximumFractionDigits: 0 });
    return `${esquerda},${(decimal ?? '').padEnd(2, '0').slice(0, 2)}`;
  };

  const digito = (dTecla) => {
    // O teclado manda vírgula; o buffer guarda ponto, que é o que o JavaScript
    // entende como número. A tradução é aqui, num lugar só.
    const d = dTecla === ',' ? '.' : dTecla;
    setBuffer(v => {
      if (d === '.' && v.includes('.')) return v;
      // Item 7.8.2: os centavos so comecam depois da tecla decimal e vao ate
      // duas casas; a terceira e recusada. Sem esta trava o visor mostraria
      // "30.000.000,50" com o usuario tendo digitado 505, e o numero gravado
      // seria outro.
      if (COM_CASAS && v.includes('.')
          && v.split('.')[1].length >= 2) return v;
      const novo = (v || '') + d;
      if (max != null && Number(novo.replace(',', '.')) > max) return v;
      return novo;
    });
  };
  const apagar  = () => setBuffer(v => v.slice(0, -1));
  const limpar  = () => setBuffer('');
  const confirmar = () => {
    const num = Number(String(buffer).replace(',', '.'));
    if (buffer === '' || isNaN(num)) {
      onChange(null);
    } else {
      let final = num;
      if (min != null && final < min) final = min;
      if (max != null && final > max) final = max;
      onChange(final);
    }
    setAberto(false);
    setBuffer('');
  };

  return (
    <>
      <div onClick={abrir}
        style={{ display: 'flex', alignItems: 'stretch',
          border: `1px solid ${G.borda}`, borderRadius: 0,
          overflow: 'hidden',
          cursor: disabled ? 'not-allowed' : 'pointer',
          background: disabled ? G.fundo : '#FFF',
          height: 36, opacity: disabled ? 0.6 : 1,
          width: largura || '100%',
          boxSizing: 'border-box' }}>
        {prefixo && (
          <span style={{ padding: '0 10px', display: 'flex',
            alignItems: 'center', fontSize: 11, color: G.txt2,
            background: G.fundo, borderRight: `1px solid ${G.borda}` }}>
            {prefixo}
          </span>
        )}
        <span style={{ flex: 1, padding: '0 12px', display: 'flex',
          alignItems: 'center',
          justifyContent: textAlign === 'right' ? 'flex-end' : 'flex-start',
          fontSize: alerta ? 15 : 13,
          fontWeight: alerta ? 900 : 700,
          color: alerta ? G.vermelho : G.txt1,
          fontFamily: "'IBM Plex Mono', monospace" }}>
          {formatar(valor)}
        </span>
        {sufixo && (
          <span style={{ padding: '0 10px', display: 'flex',
            alignItems: 'center', fontSize: 11, color: G.txt2,
            background: G.fundo, borderLeft: `1px solid ${G.borda}` }}>
            {sufixo}
          </span>
        )}
      </div>

      {aberto && (
        <div onClick={confirmar}
          style={{ position: 'fixed', inset: 0,
            background: 'rgba(0,0,0,0.40)', zIndex: 9999,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: 20 }}>
          <div onClick={e => e.stopPropagation()}
            style={{ maxWidth: 320, width: '100%' }}>
            <TecladoNumerico
              valor={buffer}
              visor={visorDoBuffer()}
              label={label}
              onDigito={digito}
              onApagar={apagar}
              onLimpar={limpar}
              onFechar={confirmar}
              // `int` não tem centavos: a tecla nem aparece (acréscimo do
              // Fabio ao item 7.8.3, em 12/10/2026). `COM_CASAS` é a mesma
              // régua que o visor e a trava de duas casas já usam.
              permitirDecimal={permitirDecimal && COM_CASAS}
            />
          </div>
        </div>
      )}
    </>
  );
};


// ═══════════════════════════════════════════════════════════════════════
// Tipos de dívida por classificação · alimenta dropdown da <ListaDividas />
// ═══════════════════════════════════════════════════════════════════════
const TIPOS_DIVIDA = {
  cp: [
    'Conta Garantida (cheque especial PJ)',
    'Capital de Giro CP',
    'Antecipação de Recebíveis',
    'Desconto de Duplicatas',
    'Outros CP',
  ],
  lp: [
    'Financiamento BNDES',
    'Financiamento Bancário (CG LP)',
    'Financiamento de Veículo',
    'Financiamento de Imóvel',
    'Financiamento de Equipamento',
    'Mútuo com Sócios',
    'Outros LP',
  ],
};

// ═══════════════════════════════════════════════════════════════════════
// <ListaDividas /> — gerencia lista dinâmica de dívidas (CP ou LP) de uma
// empresa. Cards expansíveis: collapsed mostra resumo (banco · tipo · R$),
// expanded mostra form completo. Botão "+ Adicionar" no topo. Lixeira remove.
// AiPro-only (renderizado em Configurações Adicionais).
// ═══════════════════════════════════════════════════════════════════════
const ListaDividas = ({ empresa, classificacao, titulo, subtitulo, corBorda = G.verde }) => {
  const [dividas, setDividas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expandido, setExpandido] = useState(null);  // id do card aberto
  // TecladoNumerico interno · campoAtivo = { id, campo } para saber qual input editar
  const [campoTeclado, setCampoTeclado] = useState(null);
  const [valorTeclado, setValorTeclado] = useState('');

  const carregar = async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/empresas/${empresa.id}/dividas`);
      if (r.ok) {
        const todas = await r.json();
        setDividas(todas.filter(d => d.classificacao === classificacao));
      }
    } catch (_) {}
    setLoading(false);
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { carregar(); }, [empresa.id, classificacao]);

  const adicionar = async () => {
    const tipoPadrao = TIPOS_DIVIDA[classificacao][0];
    try {
      const r = await fetch(`${API}/empresas/${empresa.id}/dividas`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          classificacao, tipo: tipoPadrao, banco: '', saldo: 0,
          custo_am: null, vencimento_meses: classificacao === 'lp' ? 12 : null,
        }),
      });
      if (r.ok) {
        const { id } = await r.json();
        await carregar();
        setExpandido(id);   // já abre a nova para editar
      }
    } catch (_) {}
  };

  const atualizar = async (id, dados) => {
    // Optimistic UI: atualiza localmente primeiro
    setDividas(prev => prev.map(d => d.id === id ? { ...d, ...dados } : d));
    try {
      await fetch(`${API}/empresas/${empresa.id}/dividas/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(dados),
      });
    } catch (_) {}
  };

  const remover = async (id) => {
    if (!window.confirm('Remover esta linha de dívida?')) return;
    setDividas(prev => prev.filter(d => d.id !== id));
    try {
      await fetch(`${API}/empresas/${empresa.id}/dividas/${id}`, { method: 'DELETE' });
    } catch (_) {}
  };

  // ── Teclado numérico interno ── (mesma UX dos campos de Configurações)
  const abrirTeclado = (id, campo, valorAtual) => {
    setCampoTeclado({ id, campo });
    setValorTeclado(valorAtual != null ? String(valorAtual) : '');
  };
  const tecladoDigito = d => {
    setValorTeclado(prev => {
      const atual = prev === '0' ? '' : prev;
      return atual + d;
    });
  };
  const tecladoApagar = () => setValorTeclado(prev => prev.slice(0, -1));
  const tecladoLimpar = () => setValorTeclado('');
  const tecladoFechar = () => {
    if (campoTeclado) {
      const num = valorTeclado === '' ? null : parseFloat(valorTeclado);
      atualizar(campoTeclado.id, { [campoTeclado.campo]: num });
    }
    setCampoTeclado(null);
    setValorTeclado('');
  };
  const rotuloCampo = c => ({
    saldo: 'Saldo Devedor (R$)',
    custo_am: 'Custo (% a.m.)',
    vencimento_meses: 'Vencimento (meses)',
  }[c] || c);

  const subtotal = dividas.reduce((acc, d) => acc + (Number(d.saldo) || 0), 0);
  const fmtBR = v => Number(v || 0).toLocaleString('pt-BR',
    { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  return (
    <div style={{ background: G.card, padding: '16px 18px',
      borderLeft: `4px solid ${corBorda}` }}>
      <div style={{ display: 'flex', justifyContent: 'space-between',
        alignItems: 'flex-start', marginBottom: 4 }}>
        <div>
          <h3 style={{ margin: 0, fontSize: 13, fontWeight: 800, color: G.txt1 }}>
            {titulo}
          </h3>
          <p style={{ margin: '2px 0 0', fontSize: 11, color: G.txt2,
            fontStyle: 'italic' }}>{subtitulo}</p>
        </div>
        <div style={{ textAlign: 'right' }}>
          <div style={{ fontSize: 10, color: G.txt2, letterSpacing: '0.06em',
            textTransform: 'uppercase', fontWeight: 700 }}>
            Subtotal · {dividas.length} {dividas.length === 1 ? 'linha' : 'linhas'}
          </div>
          <div style={{ fontSize: 16, fontWeight: 800, color: G.txt1,
            fontFamily: "'Outfit', sans-serif" }}>
            R$ {fmtBR(subtotal)}
          </div>
        </div>
      </div>

      {/* Lista de cards (cada dívida) */}
      <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 8 }}>
        {loading && dividas.length === 0 && (
          <div style={{ fontSize: 12, color: G.txt2, fontStyle: 'italic',
            padding: 8 }}>Carregando…</div>
        )}
        {!loading && dividas.length === 0 && (
          <div style={{ fontSize: 12, color: G.txt2, fontStyle: 'italic',
            padding: 12, background: G.fundo, borderRadius: 0,
            textAlign: 'center' }}>
            Nenhuma dívida {classificacao === 'cp' ? 'de curto prazo' : 'de longo prazo'} cadastrada.
          </div>
        )}
        {dividas.map(d => {
          const aberto = expandido === d.id;
          return (
            <div key={d.id} style={{
              background: aberto ? G.fundo : '#FFF',
              border: `1px solid ${aberto ? corBorda : G.borda}`,
              borderRadius: 0, transition: 'background 0.15s',
            }}>
              {/* Header (resumo · sempre visível). O expansor usa o botão de
                  ação inline do design (padroes-edicao-bp60m item 1): par
                  canônico "▾ Clique para abrir" / "▴ Clique para fechar",
                  outline laranja, minWidth 170. Esta lista abria com um "+"
                  discreto desde 09/06 — fora do padrão, e ninguém descobria
                  que a linha abria (26/07). */}
              <div onClick={() => setExpandido(aberto ? null : d.id)}
                style={{ display: 'flex', justifyContent: 'space-between',
                alignItems: 'center', padding: '10px 14px', cursor: 'pointer',
                gap: 12 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10,
                  flex: 1, minWidth: 0 }}>
                  <span style={{ fontSize: 12, fontWeight: 700, color: G.txt1,
                    overflow: 'hidden', textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap' }}>
                    {d.banco || '(sem banco)'} · {d.tipo}
                  </span>
                </div>
                <span style={{ fontSize: 13, fontWeight: 800, color: G.txt1,
                  fontFamily: "'Outfit', sans-serif", whiteSpace: 'nowrap' }}>
                  R$ {fmtBR(d.saldo)}
                </span>
                <button onClick={e => { e.stopPropagation(); setExpandido(aberto ? null : d.id); }}
                  style={{ background: '#FFF', color: G.laranja,
                    border: `1px solid ${G.laranja}`, padding: '5px 12px',
                    fontSize: 11, fontWeight: 700, letterSpacing: '0.06em',
                    textTransform: 'uppercase', borderRadius: 0,
                    cursor: 'pointer', whiteSpace: 'nowrap',
                    minWidth: 170, textAlign: 'center' }}>
                  {aberto ? '▴ Clique para fechar' : '▾ Clique para abrir'}
                </button>
                <button onClick={e => { e.stopPropagation(); remover(d.id); }}
                  title="Remover esta linha"
                  style={{ background: 'transparent', border: 'none',
                    cursor: 'pointer', color: G.vermelho, fontSize: 16,
                    padding: '0 4px' }}>
                  <IconeLixeira />
                </button>
              </div>
              {/* Form expandido */}
              {aberto && (
                <div style={{ padding: '4px 14px 14px',
                  display: 'grid',
                  gridTemplateColumns: classificacao === 'lp' ? 'repeat(5, 1fr)' : 'repeat(4, 1fr)',
                  gap: 10 }}>
                  <Field label="Banco">
                    <input type="text" value={d.banco || ''}
                      onChange={e => atualizar(d.id, { banco: e.target.value })}
                      placeholder="Ex: Itaú, BNDES..."
                      style={inputStyle}/>
                  </Field>
                  <Field label="Tipo">
                    <select value={d.tipo}
                      onChange={e => atualizar(d.id, { tipo: e.target.value })}
                      style={inputStyle}>
                      {TIPOS_DIVIDA[classificacao].map(t =>
                        <option key={t} value={t}>{t}</option>
                      )}
                    </select>
                  </Field>
                  {/* Saldo da dívida que veio do balancete não se digita: é o
                      número do documento. Mesma regra de Estoque e Imobilizado
                      (26/07). O servidor também recusa a alteração — bloqueio só
                      na tela seria aparência de trava. */}
                  <Field label={d.codigo_conta
                    ? <>Saldo devedor (R$) <span style={{ color: G.verde, fontWeight: 700,
                        textTransform: 'none', letterSpacing: 0 }}>· da contabilidade</span></>
                    : 'Saldo devedor (R$)'}>
                    {d.codigo_conta ? (
                      <div style={{ ...inputStyle, background: G.verdeLight,
                        border: `1px solid ${G.verde}`, display: 'flex',
                        alignItems: 'center', justifyContent: 'space-between' }}>
                        <span style={{ color: G.txt2, fontSize: 12 }}>R$</span>
                        <span style={{ fontWeight: 700 }}>{fmtBR(d.saldo)}</span>
                      </div>
                    ) : (
                      <ValorDisplay valor={d.saldo} prefix="R$"
                        onClick={() => abrirTeclado(d.id, 'saldo', d.saldo)}/>
                    )}
                  </Field>
                  <Field label="Custo (% a.m.)">
                    <ValorDisplay valor={d.custo_am} suffix="%"
                      onClick={() => abrirTeclado(d.id, 'custo_am', d.custo_am)}/>
                  </Field>
                  {classificacao === 'lp' && (
                    <Field label="Vencimento (meses)">
                      <ValorDisplay valor={d.vencimento_meses} suffix="m" inteiro
                        onClick={() => abrirTeclado(d.id, 'vencimento_meses', d.vencimento_meses)}/>
                    </Field>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Botão Adicionar */}
      <button onClick={adicionar}
        style={{ marginTop: 12, background: '#FFF', color: corBorda,
          border: `2px dashed ${corBorda}`, borderRadius: 0,
          padding: '8px 16px', fontSize: 11, fontWeight: 700,
          letterSpacing: '0.06em', textTransform: 'uppercase',
          cursor: 'pointer', width: '100%' }}>
        + Adicionar linha de dívida
      </button>

      {/* TecladoNumerico modal · aparece sobreposto quando algum valor numérico
          é clicado. Padrão UX do app (decisão 09/06): todo input de valor abre teclado. */}
      {campoTeclado && (
        <div onClick={tecladoFechar}
          style={{ position: 'fixed', inset: 0,
            background: 'rgba(0,0,0,0.40)', zIndex: 9999,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: 20 }}>
          <div onClick={e => e.stopPropagation()}
            style={{ maxWidth: 320, width: '100%' }}>
            <TecladoNumerico
              valor={valorTeclado}
              label={rotuloCampo(campoTeclado.campo)}
              onDigito={tecladoDigito}
              onApagar={tecladoApagar}
              onLimpar={tecladoLimpar}
              onFechar={tecladoFechar}
              permitirDecimal={campoTeclado.campo !== 'vencimento_meses'}
            />
          </div>
        </div>
      )}
    </div>
  );
};

// Helpers visuais inline pros inputs da ListaDividas
const inputStyle = {
  width: '100%', boxSizing: 'border-box',
  padding: '7px 10px', fontSize: 12, color: G.txt1,
  border: `1px solid ${G.borda}`, borderRadius: 0,
  background: '#fff', fontFamily: 'inherit',
};
const Field = ({ label, children }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
    <label style={{ fontSize: 10, fontWeight: 700, color: G.txt2,
      textTransform: 'uppercase', letterSpacing: '0.05em' }}>
      {label}
    </label>
    {children}
  </div>
);

// Display de valor numérico read-only (clicável) — abre TecladoNumerico no parent.
// Padrão: prefix/suffix em fundo bege, valor centralizado.
const ValorDisplay = ({ valor, prefix, suffix, onClick, inteiro = false }) => {
  const fmt = v => {
    if (v == null || v === '') return '—';
    return inteiro
      ? Number(v).toLocaleString('pt-BR')
      : Number(v).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };
  return (
    <div onClick={onClick}
      style={{ display: 'flex', border: `1px solid ${G.borda}`,
        borderRadius: 0, overflow: 'hidden', cursor: 'pointer',
        background: '#fff', transition: 'border-color 0.15s' }}
      onMouseOver={e => e.currentTarget.style.borderColor = G.verde}
      onMouseOut={e => e.currentTarget.style.borderColor = G.borda}>
      {prefix && (
        <span style={{ padding: '7px 10px', background: G.fundo,
          fontSize: 11, color: G.txt2, borderRight: `1px solid ${G.borda}` }}>
          {prefix}
        </span>
      )}
      <span style={{ flex: 1, padding: '7px 10px', fontSize: 12,
        color: G.txt1, fontWeight: 600, textAlign: 'right' }}>
        {fmt(valor)}
      </span>
      {suffix && (
        <span style={{ padding: '7px 10px', background: G.fundo,
          fontSize: 11, color: G.txt2, borderLeft: `1px solid ${G.borda}` }}>
          {suffix}
        </span>
      )}
    </div>
  );
};

// Campos de Configurações Financeiras que o balancete/razão sabe preencher.
// O `ir` fica de fora de propósito: ele é derivado do Regime Tributário do
// Cadastro mesmo sem documento contábil, então não distingue as duas situações.
const CAMPOS_DA_CONTABILIDADE = ['saldo_cc', 'saldo_cdb', 'wacc_e', 'wacc_d',
                                 'divida_perc_cp', 'depreciacao_mensal'];
// Os da Página 2 (Dados para o Balanço Patrimonial): saem das contas 1.1.3 e
// 1.2.3 do balancete.
const CAMPOS_DA_CONTABILIDADE_BALANCO = ['estoque', 'imobilizado'];

const TelaConfiguracoes = ({ empresa, onSalvo, subTelaInicial = 'principal' }) => {
  const [form, setForm] = useState({
    saldo_cc: '0', saldo_cdb: '0',
    wacc_e: '500000', wacc_d: '200000',
    wacc_ke: '28', wacc_kd: '14',
    ir: '15', depreciacao_mensal: '0',
    perpetuidade_g: '3',
    // Detalhamento da Dívida (vivem em empresas, alimentam Balanço Patrimonial)
    divida_perc_cp: '40',     // % que vence em ≤12 meses
    divida_venc_medio: '3',   // vencimento médio em anos
    // Configurações Adicionais — dados pro Balanço Patrimonial (vivem em empresas)
    estoque: '0',
    imobilizado: '0',
    capital_social: '0',
    objeto_social: '',
  });
  const [setor, setSetor] = useState(empresa.setor || 'geral');
  const [campoAtivo, setCampoAtivo] = useState(null);
  const [loading, setLoading]       = useState(false);
  const [salvando, setSalvando]     = useState(false);
  const [sucesso, setSucesso]       = useState(false);
  const [erro, setErro]             = useState(null);
  // Sub-tela: 'principal' (config padrão) | 'adicionais' (Balanço Patrimonial).
  // Definida pelo hub de Configuração (card 2 = principal · card 3 = adicionais);
  // a navegação entre elas passou para o rodapé do hub (sem botão interno).
  const subTela = subTelaInicial;
  // Data-base patrimonial (último extrato CC consolidado) — mostrar no header
  const [dataRefPatrimoniais, setDataRefPatrimoniais] = useState(null);
  // Campos preenchidos pelo balancete/razão — ficam em leitura, sem digitação.
  // Lista vazia = empresa só com extrato: aqui é a única fonte, tudo editável.
  const [camposContabeis, setCamposContabeis] = useState([]);
  const [dataBaseContabil, setDataBaseContabil] = useState(null);
  // Dívidas que o balancete registra e ainda não estão na lista do consultor.

  // O que o balancete informa para esta tela, numa chamada só. Fica fora do
  // GET /empresas/{id} de propósito: aquele é usado por várias telas e não pode
  // pagar o custo de ler o balancete.
  const carregarPatrimonioContabil = async () => {
    try {
      const r = await fetch(`${API}/empresas/${empresa.id}/patrimonio-contabil`);
      if (!r.ok) return {};
      return await r.json();
    } catch (_) { return {}; }
  };

  useEffect(() => {
    const buscar = async () => {
      setLoading(true);
      try {
        // Carrega parametros (saldo, WACC, impostos, perpetuidade)
        const res  = await fetch(`${API}/parametros/${empresa.id}`);
        const data = await res.json();
        // Carrega dados patrimoniais da empresa (divida CP/venc, data ref)
        const resE = await fetch(`${API}/empresas/${empresa.id}`);
        const dataE = resE.ok ? await resE.json() : {};
        // Estoque, Imobilizado e as dívidas que o balancete registra.
        const pat = await carregarPatrimonioContabil();
        setForm({
          saldo_cc:           String(data.saldo_cc || 0),
          saldo_cdb:          String(data.saldo_cdb || 0),
          wacc_e:             String(data.wacc_e || 500000),
          wacc_d:             String(data.wacc_d || 200000),
          wacc_ke:            String(Math.round((data.wacc_ke || 0.28) * 100)),
          wacc_kd:            String(Math.round((data.wacc_kd || 0.14) * 100)),
          ir:                 String(Math.round((data.ir || 0.15) * 100)),
          depreciacao_mensal: String(data.depreciacao_mensal || 0),
          perpetuidade_g:     String(Math.round((data.perpetuidade_g ?? 0.03) * 100)),
          // O balancete manda quando existe; senão vale o que está gravado.
          divida_perc_cp:     String(data.divida_perc_cp ?? dataE.divida_perc_cp ?? 40),
          divida_venc_medio:  String(dataE.divida_venc_medio ?? 3),
          // O balancete manda quando existe; senão vale o que está gravado.
          estoque:            String(pat.estoque ?? dataE.estoque ?? 0),
          imobilizado:        String(pat.imobilizado ?? dataE.imobilizado ?? 0),
          capital_social:     String(dataE.capital_social ?? 0),
          objeto_social:      dataE.objeto_social || '',
        });
        setSetor(empresa.setor || 'geral');
        setDataRefPatrimoniais(dataE.data_referencia_patrimoniais || null);
        // Quais campos vieram da contabilidade — a tela bloqueia esses.
        // Duas origens: `parametros` (saldos, PL, dívida, IR) e o balancete
        // (estoque, imobilizado, que alimentam o Balanço Patrimonial).
        setCamposContabeis([...(data.campos_contabeis || []),
                            ...(pat.campos_contabeis || [])]);
        setDataBaseContabil(data.data_base_contabil || null);
      } catch(e) { setErro('Erro ao carregar parâmetros.'); }
      setLoading(false);
    };
    buscar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [empresa]);


  const handleSalvar = async () => {
    if (bloquearSeAdmin()) return;    setSalvando(true); setErro(null); setSucesso(false);
    try {
      const payload = {
        saldo_cc:           parseFloat(form.saldo_cc) || 0,
        saldo_cdb:          parseFloat(form.saldo_cdb) || 0,
        wacc_e:             parseFloat(form.wacc_e) || 500000,
        wacc_d:             parseFloat(form.wacc_d) || 200000,
        wacc_ke:            (parseFloat(form.wacc_ke) || 28) / 100,
        wacc_kd:            (parseFloat(form.wacc_kd) || 14) / 100,
        ir:                 (parseFloat(form.ir) || 15) / 100,
        depreciacao_mensal: parseFloat(form.depreciacao_mensal) || 0,
        perpetuidade_g:     (parseFloat(form.perpetuidade_g) || 3) / 100,
      };
      const res = await fetch(`${API}/parametros/${empresa.id}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error('Erro ao salvar');
      // Setor + dados patrimoniais (divida) vivem em empresas (nao em parametros)
      const r2 = await fetch(`${API}/empresas/${empresa.id}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          setor,
          divida_perc_cp:    parseFloat(form.divida_perc_cp) || 0,
          divida_venc_medio: parseFloat(form.divida_venc_medio) || 0,
          estoque:           parseFloat(form.estoque) || 0,
          imobilizado:       parseFloat(form.imobilizado) || 0,
          capital_social:    parseFloat(form.capital_social) || 0,
          objeto_social:     form.objeto_social || null,
        }),
      });
      if (!r2.ok) throw new Error('Erro ao salvar setor/dívida');
      setSucesso(true);
      setTimeout(() => { setSucesso(false); onSalvo(); }, 1500);
    } catch(e) { setErro(e.message); }
    setSalvando(false);
  };

  const teclarDigito = (d) => {
    if (!campoAtivo) return;
    setForm(prev => {
      const atual = prev[campoAtivo] === '0' ? '' : prev[campoAtivo];
      return { ...prev, [campoAtivo]: atual + d };
    });
  };

  const teclarApagar = () => {
    if (!campoAtivo) return;
    setForm(prev => {
      const novo = prev[campoAtivo].slice(0, -1) || '0';
      return { ...prev, [campoAtivo]: novo };
    });
  };

  const teclarLimpar = () => {
    if (!campoAtivo) return;
    setForm(prev => ({ ...prev, [campoAtivo]: '0' }));
  };

  const Campo = ({ label, campo, prefix = '', suffix = '', dica = '' }) => {
    const ativo = campoAtivo === campo;
    // Campo alimentado pelo balancete/razão: sem digitação, para a contabilidade
    // não competir com o que foi digitado (25/07).
    const daContabilidade = camposContabeis.includes(campo);
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        <label style={{ fontSize: 12, fontWeight: 700, color: G.txt2,
          textTransform: 'uppercase', letterSpacing: '0.06em' }}>
          {label}
          {daContabilidade && (
            <span style={{ marginLeft: 8, color: G.verde, fontWeight: 700,
              textTransform: 'none', letterSpacing: 0, fontSize: 11 }}>
              · da contabilidade
            </span>
          )}
        </label>
        <div onClick={() => { if (!daContabilidade) setCampoAtivo(campo); }}
          style={{ display: 'flex', alignItems: 'center',
            border: `2px solid ${daContabilidade ? G.verde : (ativo ? G.verde : G.borda)}`,
            borderRadius: 0,
            background: daContabilidade ? G.verdeLight : (ativo ? G.verdeLight : '#fff'),
            overflow: 'hidden', cursor: daContabilidade ? 'default' : 'pointer' }}>
          {prefix && (
            <span style={{ padding: '10px 12px', background: G.fundo,
              fontSize: 13, color: G.txt2, borderRight: `1px solid ${G.borda}` }}>
              {prefix}
            </span>
          )}
          <span style={{ flex: 1, padding: '10px 12px',
            fontSize: 14, color: G.txt1,
            fontWeight: (ativo && !daContabilidade) ? 700 : 400 }}>
            {/* Campo bloqueado não passa pelo teclado numérico, então pode ser
                exibido já formatado em pt-BR. Editável continua cru, senão o
                separador de milhar entraria na digitação. */}
            {daContabilidade && !isNaN(Number(form[campo]))
              ? Number(form[campo]).toLocaleString('pt-BR',
                  { minimumFractionDigits: 2, maximumFractionDigits: 2 })
              : form[campo]}
          </span>
          {suffix && (
            <span style={{ padding: '10px 12px', background: G.fundo,
              fontSize: 13, color: G.txt2, borderLeft: `1px solid ${G.borda}` }}>
              {suffix}
            </span>
          )}
        </div>
        {dica && <span style={{ fontSize: 11, color: G.txt2 }}>{dica}</span>}
      </div>
    );
  };

  const Teclado = campoAtivo ? (
    <TecladoNumerico
      valor={form[campoAtivo]}
      label={campoAtivo.replace(/_/g, ' ').toUpperCase()}
      onDigito={teclarDigito}
      onApagar={teclarApagar}
      onLimpar={teclarLimpar}
      onFechar={() => setCampoAtivo(null)}
      permitirDecimal={true}
    />
  ) : null;

  if (loading) return (
    <div style={{ padding: '24px 28px', color: G.txt2 }}>Carregando...</div>
  );

  // Sub-tela "Configurações Adicionais" — Balanço Patrimonial
  // 3 seções: Patrimônio Operacional · Capital Social · Objeto Social
  if (subTela === 'adicionais') {
    const Teclado = campoAtivo ? (
      <TecladoNumerico
        valor={form[campoAtivo]}
        label={(campoAtivo || '').replace(/_/g, ' ').toUpperCase()}
        onDigito={teclarDigito}
        onApagar={teclarApagar}
        onLimpar={teclarLimpar}
        onFechar={() => setCampoAtivo(null)}
        permitirDecimal={true}
      />
    ) : null;
    return (
      <div style={{ padding: '24px 28px' }}>
        <div style={{ marginBottom: 24, display: 'flex',
          alignItems: 'flex-start', justifyContent: 'space-between', gap: 16 }}>
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, color: G.verde,
              letterSpacing: '0.14em', textTransform: 'uppercase' }}>
              Configurações · Adicionais
            </div>
            <div style={{ position: 'relative', display: 'inline-block' }}>
              <h2 style={{ margin: '4px 0 0', fontSize: 20, fontWeight: 800, color: G.txt1,
                position: 'relative', zIndex: 1 }}>
                Dados para o Balanço Patrimonial
              </h2>
              {/* Marca d'água — ícone da balança (mesmo da página Balanço Patrimonial) */}
              <MarcaDaguaTitulo>
                <line x1="12" y1="3" x2="12" y2="21"/>
                <line x1="3" y1="7" x2="21" y2="7"/>
                <circle cx="6" cy="7" r="1"/>
                <circle cx="18" cy="7" r="1"/>
                <path d="M3 12l3-5 3 5a3 3 0 11-6 0z"/>
                <path d="M15 12l3-5 3 5a3 3 0 11-6 0z"/>
              </MarcaDaguaTitulo>
            </div>
            <p style={{ ...TIPO.subtitulo, margin: '4px 0 0' }}>
              Dados estruturais que alimentam o Balanço Patrimonial e a projeção de Valor.
            </p>
            {/* Sem data, como em Configurações Financeiras: a posição acompanha
                o último balancete enviado e muda sozinha a cada documento. */}
            <p style={{ margin: '4px 0 0', fontSize: 11, color: G.txt2, fontStyle: 'italic' }}>
              {/* Testar contra os campos DESTA tela, não contra a lista inteira:
                  `ir` é derivado do regime do Cadastro mesmo sem balancete, e a
                  ShopMax (só extrato) dizia "na posição do último balancete". */}
              {camposContabeis.some(c => CAMPOS_DA_CONTABILIDADE_BALANCO.includes(c))
                ? 'Valores patrimoniais na posição do último balancete enviado.'
                : 'Valores patrimoniais conforme informados abaixo.'}
            </p>
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            <button onClick={handleSalvar} disabled={salvando}
              style={{ background: G.verde, color: '#fff', border: 'none',
                borderRadius: 0, padding: '10px 24px', fontSize: 13,
                fontWeight: 700, cursor: 'pointer',
                boxShadow: `0 2px 8px ${G.verde}44` }}>
              {salvando ? 'Salvando...' : 'Salvar'}
            </button>
          </div>
        </div>

        {erro && (
          <div style={{ background: G.vermelhoL, color: G.vermelho,
            padding: 14, marginBottom: 16, fontSize: 13, borderRadius: 0 }}>{erro}</div>
        )}
        {sucesso && (
          <div style={{ background: G.verdeLight, color: G.verde,
            padding: 14, marginBottom: 16, fontSize: 13, fontWeight: 700, borderRadius: 0 }}>
            ✓ Dados salvos com sucesso!
          </div>
        )}

        {/* Mesmo par de avisos de Configurações Financeiras: quem tem documento
            contábil não digita; quem não tem precisa saber o que compromete. */}
        {camposContabeis.some(c => CAMPOS_DA_CONTABILIDADE_BALANCO.includes(c)) ? (
          <div style={{ background: G.verdeLight, borderLeft: `4px solid ${G.verde}`,
            padding: '12px 16px', marginBottom: 16, fontSize: 12.5, color: G.txt1,
            lineHeight: 1.5 }}>
            <strong style={{ color: G.verde }}>Não é preciso digitar.</strong>{' '}
            Estoque e Imobilizado vêm do balancete. Os campos marcados{' '}
            <strong style={{ color: G.verde }}>· da contabilidade</strong> alimentam o
            Ativo do Balanço Patrimonial e a projeção de Valor.
          </div>
        ) : (
          <div style={{ background: G.laranjaLight, borderLeft: `4px solid ${G.laranja}`,
            padding: '12px 16px', marginBottom: 16, fontSize: 12.5, color: G.txt1,
            lineHeight: 1.5 }}>
            <strong style={{ color: G.laranja }}>Esta empresa não enviou balancete nem razão.</strong>{' '}
            Aqui é a única fonte destes dados — preencha-os, ou ficam comprometidos:
            o Ativo do Balanço Patrimonial (Estoque e Imobilizado) e a projeção de Valor.
          </div>
        )}

        <div style={{ display: 'grid',
          gridTemplateColumns: campoAtivo ? '1fr 280px' : '1fr', gap: 16 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr',
            gap: 16, alignItems: 'stretch' }}>

            {/* Seção 1: Patrimônio Operacional · full width compacto (sem buraco)
                Estoque e Imobilizado lado a lado · header inline à esquerda */}
            <div style={{ background: G.card, padding: '14px 18px',
              gridColumn: '1 / -1', display: 'grid',
              gridTemplateColumns: '1fr 2fr 2fr', gap: 16,
              alignItems: 'center' }}>
              <div>
                <h3 style={{ margin: 0, fontSize: 13,
                  fontWeight: 800, color: G.txt1 }}>Patrimônio Operacional</h3>
                <p style={{ margin: '2px 0 0', fontSize: 11, color: G.txt2,
                  fontStyle: 'italic', lineHeight: 1.35 }}>
                  Alimenta Ativo Circulante (Estoque) e Não-Circulante (Imobilizado).
                </p>
              </div>
              <Campo label="Estoque atual" campo="estoque"
                prefix="R$" dica="Valor total do estoque no inventário"/>
              <Campo label="Imobilizado total" campo="imobilizado"
                prefix="R$" dica="Valor contábil de máquinas, veículos, imóveis"/>
            </div>

            {/* Seção 2: Endividamento Curto Prazo · lista dinâmica · full width
                Capital Social e Objeto Social MOVIDOS para Minha Empresa em 09/06
                (dados cadastrais, não pertencem ao ciclo do Balanço). */}
            <div style={{ gridColumn: '1 / -1' }}>
              <ListaDividas empresa={empresa} classificacao="cp"
                titulo="Endividamento de Curto Prazo (≤12 meses)"
                subtitulo="Passivo Circulante · Conta Garantida, Capital de Giro CP, Antecipação de Recebíveis e outras dívidas de curto prazo."
                corBorda={G.laranja}/>
            </div>

            {/* Seção 3: Endividamento Longo Prazo · lista dinâmica · full width */}
            <div style={{ gridColumn: '1 / -1' }}>
              <ListaDividas empresa={empresa} classificacao="lp"
                titulo="Endividamento de Longo Prazo (>12 meses)"
                subtitulo="Passivo Não-Circulante · BNDES, Financiamento Bancário, Veículos/Imóveis, Mútuo com Sócios."
                corBorda={G.verde}/>
            </div>

          </div>
          {Teclado}
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: '24px 28px' }}>
      <div style={{ marginBottom: 24, display: 'flex',
        alignItems: 'flex-start', justifyContent: 'space-between' }}>
        <div>
          <div style={{ position: 'relative', display: 'inline-block' }}>
            <h2 style={{ margin: 0, fontSize: 20, fontWeight: 800, color: G.txt1,
              position: 'relative', zIndex: 1 }}>
              Configurações Financeiras
            </h2>
            {/* Marca d'água — ícone do menu (configurações · engrenagem), 5× (20→100),
                right -10 do final do título, bottom -30, rotação 30° horário */}
            <MarcaDaguaTitulo>
              <circle cx="12" cy="12" r="3"/>
              <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"/>
            </MarcaDaguaTitulo>
          </div>
          <p style={{ ...TIPO.subtitulo, margin: '4px 0 0' }}>
            clique em um campo para editar
          </p>
          {/* Sem data: a posição acompanha o último balancete enviado e muda
              sozinha a cada novo documento — mostrar a data só criava a dúvida
              de estar ou não ligada ao seletor de período do topo (25/07). */}
          <p style={{ margin: '2px 0 0', fontSize: 11, color: G.txt2, fontStyle: 'italic' }}>
            {dataBaseContabil
              ? 'Valores patrimoniais na posição do último balancete enviado.'
              : 'Valores patrimoniais conforme informados abaixo.'}
          </p>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <button onClick={handleSalvar} disabled={salvando}
            style={{ background: G.verde, color: '#fff', border: 'none',
              borderRadius: 0, padding: '10px 24px', fontSize: 13,
              fontWeight: 700, cursor: 'pointer',
              boxShadow: `0 2px 8px ${G.verde}44` }}>
            {salvando ? 'Salvando...' : 'Salvar Configurações'}
          </button>
        </div>
      </div>

      {erro && (
        <div style={{ background: G.vermelhoL, color: G.vermelho,
          padding: 14, borderRadius: 0, marginBottom: 16, fontSize: 13 }}>
          {erro}
        </div>
      )}
      {sucesso && (
        <div style={{ background: G.verdeLight, color: G.verde,
          padding: 14, borderRadius: 0, marginBottom: 16,
          fontSize: 13, fontWeight: 700 }}>
          ✓ Parâmetros salvos com sucesso!
        </div>
      )}

      {/* Dois avisos, um para cada situação: quem tem documento contábil não
          precisa digitar; quem só tem extrato precisa — e merece saber o que
          fica comprometido se deixar em branco. */}
      {camposContabeis.some(c => CAMPOS_DA_CONTABILIDADE.includes(c)) ? (
        <div style={{ background: G.verdeLight, borderLeft: `4px solid ${G.verde}`,
          padding: '12px 16px', marginBottom: 16, fontSize: 12.5, color: G.txt1,
          lineHeight: 1.5 }}>
          <strong style={{ color: G.verde }}>Não é preciso digitar.</strong>{' '}
          Saldos, patrimônio, dívida, depreciação e imposto vêm do balancete e do
          razão. Os campos marcados <strong style={{ color: G.verde }}>· da contabilidade</strong>{' '}
          seguem alimentando Dashboard, DRE, Valor e Balanço.
        </div>
      ) : (
        <div style={{ background: G.laranjaLight, borderLeft: `4px solid ${G.laranja}`,
          padding: '12px 16px', marginBottom: 16, fontSize: 12.5, color: G.txt1,
          lineHeight: 1.5 }}>
          <strong style={{ color: G.laranja }}>Esta empresa não enviou balancete nem razão.</strong>{' '}
          Aqui é a única fonte destes dados — preencha-os, ou ficam comprometidos:
          meses de caixa e o cartão Caixa, ROIC, WACC, Valor da Empresa,
          EBITDA e o Balanço Patrimonial.
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: campoAtivo ? '1fr 280px' : '1fr', gap: 16 }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr',
          gap: 12, alignItems: 'stretch' }}>

          {/* Posição de Caixa · antes mostrava o consolidado dos extratos
              (R$ 0,00 para quem usa balancete) enquanto o Dashboard calculava
              com outro valor. Agora é um campo só, com a fonte declarada. */}
          <div style={{ background: G.card, borderRadius: 0,
            padding: '16px 18px' }}>
            <h3 style={{ margin: '0 0 12px', fontSize: 13,
              fontWeight: 800, color: G.txt1 }}>Posição de Caixa</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <Campo label="Saldo Conta Corrente" campo="saldo_cc"
                prefix="R$" dica="Caixa e equivalentes"/>
              <Campo label="Saldo Aplicações" campo="saldo_cdb"
                prefix="R$" dica="Aplicações financeiras"/>
            </div>
          </div>

          <div style={{ background: G.card, borderRadius: 0,
            padding: '16px 18px' }}>
            <h3 style={{ margin: '0 0 12px', fontSize: 13,
              fontWeight: 800, color: G.txt1 }}>Patrimônio e Dívida</h3>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <Campo label="Capital Próprio (E)" campo="wacc_e"
                prefix="R$" dica="Patrimônio líquido"/>
              <Campo label="Capital Terceiros (D)" campo="wacc_d"
                prefix="R$" dica="Dívidas financeiras"/>
              <Campo label="% Curto Prazo (≤12m)" campo="divida_perc_cp"
                suffix="%" dica="Divide CP vs LP no Balanço"/>
              <Campo label="Depreciação Mensal" campo="depreciacao_mensal"
                prefix="R$" dica="Depreciação de ativos fixos"/>
            </div>
          </div>

          <div style={{ background: G.card, borderRadius: 0,
            padding: '16px 18px' }}>
            <h3 style={{ margin: '0 0 12px', fontSize: 13,
              fontWeight: 800, color: G.txt1 }}>Estratégia & Valor</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <Campo label="Taxa de Crescimento (g)" campo="perpetuidade_g"
                suffix="%" dica="Crescimento perpétuo (Gordon)"/>
            </div>
          </div>

          {/* Custo de Capital — só as premissas. Capital Próprio (E), Capital
              Terceiros (D), saldos, IR, depreciação, setor e % curto prazo saíram
              em 25/07: vinham do balancete e do Cadastro, e a digitação competia
              com o dado contábil. O card deixou de ser "Estrutura de Capital"
              porque E e D não são mais informados aqui. */}
          <div style={{ background: G.card, borderRadius: 0,
            padding: '14px 18px' }}>
            <h3 style={{ margin: '0 0 12px', fontSize: 13,
              fontWeight: 800, color: G.txt1 }}>Custo de Capital</h3>
            <div style={{ display: 'grid',
              gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <Campo label="Custo Capital Próprio (Ke)" campo="wacc_ke"
                suffix="%" dica="Retorno exigido pelos sócios"/>
              <Campo label="Custo da Dívida (Kd)" campo="wacc_kd"
                suffix="%" dica="Taxa média dos empréstimos"/>
              <Campo label="Vencimento Médio" campo="divida_venc_medio"
                suffix="anos" dica="Prazo médio até quitar"/>
            </div>
          </div>
        </div>

        {Teclado}
      </div>
    </div>
  );
};

// ── Resumo Mensal ────────────────────────────────────────────────
const TelaResumoMensal = ({ empresa, periodoInicio, periodoFim }) => {
  const [resumo, setResumo] = useState([]);
  const [loading, setLoading] = useState(false);
  const [erro, setErro] = useState(null);
  // Eixo de leitura: 'competencia' (quando o negócio aconteceu · o de sempre) ·
  // 'caixa' (quando o dinheiro andou) · 'ambas' (os dois lado a lado).
  // Só tem efeito onde existe `razao_caixa`; sem isso o seletor nem aparece.
  const [eixo, setEixo] = useState('competencia');
  // Cenário da Reforma (Etapa 5 · aba 2): lente sobre tributos/resultado por
  // mês, só no eixo competência. null = 'Hoje'; rota dedicada, oficial intacto.
  const [anoCenario, setAnoCenario] = useState(null);
  const [resumoCenario, setResumoCenario] = useState(null);
  const [erroCenario, setErroCenario] = useState(null);
  const [carregandoCenario, setCarregandoCenario] = useState(false);

  useEffect(() => {
    if (anoCenario == null || !empresa?.id) return;
    let vivo = true;
    setCarregandoCenario(true); setErroCenario(null);
    (async () => {
      try {
        const r = await fetch(`${API}/reforma/resumo/${empresa.id}?ano=${anoCenario}`);
        const j = await r.json();
        if (!r.ok) throw new Error(j.detail || 'Cenário indisponível.');
        if (vivo) setResumoCenario(j);
      } catch (e) { if (vivo) { setErroCenario(e.message); setResumoCenario(null); } }
      if (vivo) setCarregandoCenario(false);
    })();
    return () => { vivo = false; };
  }, [anoCenario, empresa?.id]);

  useEffect(() => {
    // Limpa ANTES de buscar: sem isso a tabela da empresa anterior continuava
    // à vista durante a carga, e o seletor de eixo aparecia numa empresa que
    // não tem eixo caixa ([[blindagem-contaminacao-empresas]]).
    setResumo([]);
    setEixo('competencia');
    let vivo = true;
    const buscar = async () => {
      setLoading(true); setErro(null);
      try {
        const res = await fetch(`${API}/indicadores/resumo-mensal/${empresa.id}`);
        const data = await res.json();
        if (!vivo) return;                 // troca rápida: resposta velha não pinta
        if (!res.ok) throw new Error(data.detail || 'Erro');
        setResumo(data);
      } catch(e) { if (vivo) setErro('Erro ao carregar resumo.'); }
      if (vivo) setLoading(false);
    };
    buscar();
    return () => { vivo = false; };
  }, [empresa]);

  const fmtK = v => v == null ? '-' :
    'R$ ' + (Math.abs(v)/1000).toFixed(1) + 'k';
  // Saldo pode ser dos dois sinais, e `fmtK` engole o menos (faz sentido para
  // despesa, que já vai entre parênteses; para saldo, o número passa a mentir —
  // −R$ 94,3 mil aparecia como "R$ 94.3k", e só a cor denunciava. Cor não é
  // número: quem imprime a tela em preto e branco lê o contrário do que é).
  // Vírgula decimal, não ponto: em português "94.3" se lê como noventa e quatro
  // mil e trezentos. O `fmtK` acima carrega o mesmo defeito e é usado em toda a
  // tabela — trocar lá é mudança de escopo maior, anotada para depois.
  const fmtKS = v => v == null ? '-' :
    (v < 0 ? '−' : '') + 'R$ ' + (Math.abs(v)/1000).toFixed(1).replace('.', ',') + 'k';
  const fmtPctLocal = v => v == null ? '-' :
    (v * 100).toFixed(1) + '%';
  const corValor = v => v >= 0 ? G.verde : G.vermelho;
  // 'AAAA-MM' -> 'mar/25'. Antes mostrava só o número do mês, então com mais de
  // um ano a lista ia de 1 a 12 e recomeçava, sem dizer de que ano (24/07).
  const MESES_ABREV = ['jan','fev','mar','abr','mai','jun',
                       'jul','ago','set','out','nov','dez'];
  const rotuloMes = (mes) => {
    const [ano, m] = String(mes || '').split('-');
    const i = parseInt(m, 10) - 1;
    return (MESES_ABREV[i] || m) + '/' + String(ano || '').slice(2);
  };

  // Respeita o período escolhido no seletor do topo — antes a tela mostrava
  // sempre todos os meses, ignorando a seleção (24/07). Mesmo padrão do Dashboard.
  const resumoFiltrado = (periodoInicio && periodoFim)
    ? resumo.filter(m => m.mes >= periodoInicio.inicio.slice(0, 7)
                      && m.mes <= periodoFim.fim.slice(0, 7))
    : resumo;

  // A empresa tem o eixo caixa? Depende de ter razão contábil processado a
  // partir de 25/08/2026 — quem só tem extrato, ou razão lido antes disso, não
  // tem. Sem dado, o seletor não existe e a tela é a de sempre.
  const temCaixa = resumoFiltrado.some(m => m.tem_caixa);
  // Guarda contra o estado órfão: trocar para uma empresa sem caixa deixaria o
  // seletor num modo que ela não tem, e a tabela sairia vazia.
  const eixoAtivo = temCaixa ? eixo : 'competencia';

  // Algum mês tem o saldo em banco vindo do balancete? É o que autoriza o selo
  // `· da contabilidade` — que fica no RODAPÉ da tabela de caixa, junto da
  // explicação do número, nunca no cabeçalho: cabeçalho é fixo e apareceria
  // sobre coluna vazia em quem não tem documento contábil
  // ([[selo-so-onde-ha-fonte]]).
  const temSaldoContabil = resumoFiltrado.some(m => m.saldo_banco != null);
  // No rodapé vale a posição do ÚLTIMO mês com saldo, nunca a soma: saldo é
  // fotografia, não fluxo.
  const saldoBancoFinal = [...resumoFiltrado].reverse()
    .find(m => m.saldo_banco != null)?.saldo_banco ?? null;

  // Em cenário (só no eixo competência): tributos e resultado do ano escolhido
  // entram no lugar dos oficiais — lente de exibição, dado original intacto.
  const emCenario = anoCenario != null && resumoCenario
    && resumoCenario.ano === anoCenario && eixoAtivo === 'competencia';
  const cenarioPorMes = emCenario
    ? Object.fromEntries(resumoCenario.meses.map((m) => [m.mes, m]))
    : {};
  const resumoExibido = emCenario
    ? resumoFiltrado.map((m) => cenarioPorMes[m.mes]
        ? { ...m, tributos: cenarioPorMes[m.mes].tributos,
            resultado: cenarioPorMes[m.mes].resultado }
        : m)
    : resumoFiltrado;

  // Calcula totais
  const totais = resumoExibido.reduce((acc, m) => ({
    // `receita_operacional` passou a ser a BRUTA (01/08/2026). Aqui a base é
    // líquida das devoluções, que é a que sustenta a margem.
    receita:    acc.receita    + m.receita_operacional - (m.deducoes_receita || 0) + m.receita_financeira,
    cpv:        acc.cpv        + m.despesa_operacional,
    desp_comerc:acc.desp_comerc+ (m.despesa_comercial || 0),
    desp_adm:   acc.desp_adm   + m.despesa_administrativa,
    desp_fin:   acc.desp_fin   + m.despesa_financeira,
    tributos:   acc.tributos   + m.tributos,
    resultado:  acc.resultado  + m.resultado,
    entradas:   acc.entradas   + (m.entradas_caixa || 0),
    saidas:     acc.saidas     + (m.saidas_caixa || 0),
    saldo:      acc.saldo      + (m.saldo_caixa || 0),
  }), { receita: 0, cpv: 0, desp_comerc: 0, desp_adm: 0, desp_fin: 0, tributos: 0,
        resultado: 0, entradas: 0, saidas: 0, saldo: 0 });

  return (
    <div style={{ padding: '24px 28px', position: 'relative', overflow: 'hidden' }}>
      <CarimboAIdvisor />
      <div style={{ marginBottom: 24 }}>
        <div style={{ position: 'relative', display: 'inline-block' }}>
          <h2 style={{ margin: 0, fontSize: 20, fontWeight: 800, color: G.txt1,
            position: 'relative', zIndex: 1 }}>
            Resumo Mensal
          </h2>
          {/* Marca d'água — ícone do menu (calendário), 5× (20→100),
              right -10 mantém, desceu 3px (bottom -18 → -21), 30° horário */}
          <MarcaDaguaTitulo>
              <rect x="3" y="4" width="18" height="18" rx="2"/>
            <line x1="16" y1="2" x2="16" y2="6"/>
            <line x1="8" y1="2" x2="8" y2="6"/>
            <line x1="3" y1="10" x2="21" y2="10"/>
            </MarcaDaguaTitulo>
        </div>
        <p style={{ ...TIPO.subtitulo, margin: '4px 0 0' }}>
          evolução mês a mês
        </p>

      </div>

      <QuadrosIntro
        titulo="o Resumo Mensal"
        oQueE={temCaixa
          ? <>A evolução <strong>mês a mês</strong>, em dois eixos. <strong>Competência</strong> conta
            quando o negócio aconteceu — é a leitura da DRE. <strong>Caixa</strong> conta quando o
            dinheiro entrou e saiu do banco. <strong>Ambas</strong> põe os dois lado a lado.</>
          : <>A evolução <strong>mês a mês</strong> da receita e da despesa, na
            mesma classificação da DRE. Mostra a tendência que o total do período
            esconde.</>}
        // NÃO afirmar que o gap é capital de giro preso: o lucro também sai
        // como distribuição aos sócios, investimento e pagamento de dívida.
        // Na MORED a distribuição responde por quase todo o gap, e a frase
        // antiga mandava o dono procurar dinheiro que ele mesmo tirou.
        oQueFaz={temCaixa
          ? <>Lucro e dinheiro não são a mesma coisa. Procure os meses em que o
            resultado é bom e o <strong>caixa é negativo</strong> — e descubra para onde o
            lucro foi: distribuição aos sócios, investimento, pagamento de dívida
            ou dinheiro parado em recebimento e estoque.</>
          : <>Percorra a linha do tempo procurando <strong>meses fora do
            padrão</strong>. Um mês destoante costuma ser lançamento mal
            classificado — ou um fato que merece nota no laudo.</>}
      />

      {/* Seletor de eixo — depois dos quadros, que explicam o que cada um é.
          Trocar de eixo TROCA as colunas, não acrescenta: a tabela já tem 9 e
          somar as de caixa daria 12, ilegíveis. */}
      {!loading && temCaixa && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 12,
          margin: '0 0 16px', flexWrap: 'wrap' }}>
          <span style={{ fontSize: 10.5, fontWeight: 700, color: G.txt2,
            textTransform: 'uppercase', letterSpacing: '0.07em' }}>
            Ver por
          </span>
          <div style={{ display: 'flex', gap: 8 }}>
            {[
              ['competencia', 'Competência', 'Quando o negócio aconteceu — a leitura da DRE'],
              ['caixa',       'Caixa',       'Quando o dinheiro entrou e saiu do banco'],
              ['ambas',       'Ambas',       'Lucro e caixa lado a lado, e a distância entre eles'],
            ].map(([id, rotulo, ajuda]) => {
              const on = eixoAtivo === id;
              return (
                <button key={id} onClick={() => setEixo(id)} title={ajuda}
                  style={{
                    // Largura fixa: os três do mesmo tamanho, senão "Caixa"
                    // fica um botão pequeno ao lado de "Competência".
                    width: 132, padding: '9px 0', border: 'none', borderRadius: 0,
                    cursor: 'pointer', fontFamily: 'inherit', fontSize: 12.5,
                    fontWeight: 700, letterSpacing: '0.01em',
                    background: on ? G.laranja : G.laranjaLight,
                    color: on ? '#FFF' : G.laranja,
                    transition: 'background .15s, color .15s',
                  }}>
                  {rotulo}
                </button>
              );
            })}
          </div>
          {/* Regime — mesma linha, empurrado à direita (pedido do Fabio, 31/08) */}
          {eixoAtivo === 'competencia' && temSaldoContabil && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 12,
              marginLeft: 'auto' }}>
              <span style={{ fontSize: 10.5, fontWeight: 700, color: G.txt2,
                textTransform: 'uppercase', letterSpacing: '0.07em' }}>
                Regime
              </span>
              <div style={{ display: 'flex', gap: 8 }}>
                {[[null, 'Hoje'], [2027, '2027'], [2030, '2030'], [2033, '2033']]
                  .map(([ano, rot]) => {
                    const on = anoCenario === ano;
                    return (
                      <button key={rot} onClick={() => setAnoCenario(ano)}
                        style={{ width: 96, padding: '9px 0', border: 'none',
                          borderRadius: 0, cursor: 'pointer', fontFamily: 'inherit',
                          fontSize: 12.5, fontWeight: 700, letterSpacing: '0.01em',
                          background: on ? G.laranja : G.laranjaLight,
                          color: on ? '#FFF' : G.laranja,
                          transition: 'background .15s, color .15s' }}>
                        {rot}
                      </button>
                    );
                  })}
              </div>
              {carregandoCenario && (
                <span style={{ fontSize: 12, color: G.txt2, fontStyle: 'italic' }}>
                  montando…
                </span>
              )}
              {emCenario && (
                <span style={{ padding: '4px 10px', background: '#FFFBEB',
                  border: '1px solid #B45309', borderRadius: 0, fontSize: 11,
                  fontWeight: 700, color: '#B45309', letterSpacing: '0.06em',
                  textTransform: 'uppercase' }}>
                  · {anoCenario}{resumoCenario.tem_estimativa ? ' · ESTIMATIVA' : ''}
                </span>
              )}
            </div>
          )}
        </div>
      )}
      {erroCenario && anoCenario != null && (
        <div style={{ margin: '0 0 12px', padding: '10px 14px',
          background: '#FFFBEB', borderLeft: '3px solid #B45309',
          borderRadius: 0, fontSize: 12, color: '#B45309' }}>
          {erroCenario}
        </div>
      )}

      {erro && (
        <div style={{ background: G.vermelhoL, color: G.vermelho,
          padding: 14, borderRadius: 0, marginBottom: 16 }}>{erro}</div>
      )}

      {/* O "Carregando" era um `return` antes do cabeçalho — os 2 quadros
          sumiam enquanto os dados vinham. Agora só o conteúdo troca, e a tela
          se apresenta desde o primeiro instante (padrão da aba Alertas). */}
      {loading && (
        <div style={{ padding: '40px 0', textAlign: 'center', color: G.txt2 }}>
          Carregando...
        </div>
      )}

      {!loading && eixoAtivo === 'competencia' && <div style={{ background: G.card, borderRadius: 0,
        overflow: 'auto' }}>

        {/* Header */}
        <div style={{ display: 'grid',
          gridTemplateColumns: '70px 1fr 1fr 1fr 1fr 1fr 1fr 1fr 80px',
          padding: '12px 16px', background: G.verde, minWidth: 800 }}>
          {['Mês','Receita','CPV','Comercial','Desp Adm','Desp Fin','Tributos','Resultado','Margem'].map(h => (
            <span key={h} style={{ fontSize: 10, fontWeight: 700,
              color: 'rgba(255,255,255,0.8)', textTransform: 'uppercase',
              textAlign: h !== 'Mês' ? 'right' : 'left' }}>{h}</span>
          ))}
        </div>

        {/* Linhas */}
        <div style={{ minWidth: 800 }}>
          {resumoExibido.map((m, i) => {
            const receita = m.receita_operacional - (m.deducoes_receita || 0) + m.receita_financeira;
            const margem  = receita > 0 ? m.resultado / receita : 0;
            const positivo = m.resultado >= 0;
            return (
              <div key={m.mes} style={{
                display: 'grid',
                gridTemplateColumns: '70px 1fr 1fr 1fr 1fr 1fr 1fr 1fr 80px',
                padding: '11px 16px', alignItems: 'center',
                borderBottom: `1px solid ${G.borda}`,
                background: i % 2 === 0 ? '#fff' : G.fundo,
              }}>
                <span style={{ fontSize: 13, fontWeight: 700, color: G.txt1 }}>
                  {rotuloMes(m.mes)}
                </span>
                <span style={{ fontSize: 13, color: G.verde,
                  fontWeight: 600, textAlign: 'right' }}>
                  {fmtK(receita)}
                </span>
                <span style={{ fontSize: 13, color: G.vermelho, textAlign: 'right' }}>
                  ({fmtK(m.despesa_operacional)})
                </span>
                <span style={{ fontSize: 13, color: G.vermelho, textAlign: 'right' }}>
                  ({fmtK(m.despesa_comercial || 0)})
                </span>
                <span style={{ fontSize: 13, color: G.vermelho, textAlign: 'right' }}>
                  ({fmtK(m.despesa_administrativa)})
                </span>
                <span style={{ fontSize: 13, color: G.vermelho, textAlign: 'right' }}>
                  ({fmtK(m.despesa_financeira)})
                </span>
                <span style={{ fontSize: 13, color: G.vermelho, textAlign: 'right' }}>
                  ({fmtK(m.tributos)})
                </span>
                {/* `fmtKS`, não `fmtK`: resultado pode ser prejuízo, e o menos
                    tem de aparecer. A Construmax mostrava 11 dos 12 meses de
                    prejuízo como se fossem lucro (25/08). */}
                <span style={{ fontSize: 13, fontWeight: 700,
                  color: corValor(m.resultado), textAlign: 'right' }}>
                  {fmtKS(m.resultado)}
                </span>
                <span style={{ fontSize: 12, fontWeight: 700, textAlign: 'right',
                  color: positivo ? G.verde : G.vermelho,
                  background: positivo ? G.verdeLight : G.vermelhoL,
                  borderRadius: 0, padding: '2px 6px' }}>
                  {fmtPctLocal(margem)}
                </span>
              </div>
            );
          })}
        </div>

        {/* Totais */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: '70px 1fr 1fr 1fr 1fr 1fr 1fr 1fr 80px',
          padding: '12px 16px', background: G.fundo,
          borderTop: `2px solid ${G.borda}`, minWidth: 800,
        }}>
          <span style={{ fontSize: 13, fontWeight: 800, color: G.txt1 }}>TOTAL</span>
          <span style={{ fontSize: 13, fontWeight: 800,
            color: G.verde, textAlign: 'right' }}>{fmtK(totais.receita)}</span>
          <span style={{ fontSize: 13, fontWeight: 800,
            color: G.vermelho, textAlign: 'right' }}>({fmtK(totais.cpv)})</span>
          <span style={{ fontSize: 13, fontWeight: 800,
            color: G.vermelho, textAlign: 'right' }}>({fmtK(totais.desp_comerc)})</span>
          <span style={{ fontSize: 13, fontWeight: 800,
            color: G.vermelho, textAlign: 'right' }}>({fmtK(totais.desp_adm)})</span>
          <span style={{ fontSize: 13, fontWeight: 800,
            color: G.vermelho, textAlign: 'right' }}>({fmtK(totais.desp_fin)})</span>
          <span style={{ fontSize: 13, fontWeight: 800,
            color: G.vermelho, textAlign: 'right' }}>({fmtK(totais.tributos)})</span>
          <span style={{ fontSize: 13, fontWeight: 800,
            color: corValor(totais.resultado), textAlign: 'right' }}>
            {fmtKS(totais.resultado)}
          </span>
          <span style={{ fontSize: 12, fontWeight: 800, textAlign: 'right',
            color: totais.resultado >= 0 ? G.verde : G.vermelho }}>
            {fmtPctLocal(totais.receita > 0 ? totais.resultado/totais.receita : 0)}
          </span>
        </div>
      </div>}

      {/* ── EIXO CAIXA · quando o dinheiro andou ────────────────────── */}
      {!loading && eixoAtivo === 'caixa' && (
        <div style={{ background: G.card, borderRadius: 0, overflow: 'auto' }}>
          <div style={{ display: 'grid',
            gridTemplateColumns: '90px 1fr 1fr 1fr 1fr', padding: '12px 16px',
            background: G.verde, minWidth: 640 }}>
            {['Mês','Entradas','Saídas','Saldo do mês','Saldo em banco'].map(h => (
              <span key={h} style={{ fontSize: 10, fontWeight: 700,
                color: 'rgba(255,255,255,0.8)', textTransform: 'uppercase',
                textAlign: h !== 'Mês' ? 'right' : 'left' }}>{h}</span>
            ))}
          </div>
          <div style={{ minWidth: 640 }}>
            {resumoFiltrado.map((m, i) => (
              <div key={m.mes} style={{ display: 'grid',
                gridTemplateColumns: '90px 1fr 1fr 1fr 1fr', padding: '11px 16px',
                alignItems: 'center', borderBottom: `1px solid ${G.borda}`,
                background: i % 2 === 0 ? '#fff' : G.fundo }}>
                <span style={{ fontSize: 13, fontWeight: 700, color: G.txt1 }}>
                  {rotuloMes(m.mes)}
                </span>
                <span style={{ fontSize: 13, color: G.verde, fontWeight: 600,
                  textAlign: 'right' }}>{fmtK(m.entradas_caixa || 0)}</span>
                <span style={{ fontSize: 13, color: G.vermelho,
                  textAlign: 'right' }}>({fmtK(m.saidas_caixa || 0)})</span>
                <span style={{ fontSize: 13, fontWeight: 700,
                  color: corValor(m.saldo_caixa || 0), textAlign: 'right' }}>
                  {fmtKS(m.saldo_caixa || 0)}
                </span>
                {/* Dinheiro que a empresa TINHA — copiado do balancete, não
                    somado por nós. Sem balancete no mês, célula vazia: zero
                    diria que a empresa está sem dinheiro, o que é outra coisa. */}
                <span style={{ fontSize: 13, fontWeight: 700,
                  color: m.saldo_banco == null ? G.txt2 : corValor(m.saldo_banco),
                  textAlign: 'right' }}>
                  {m.saldo_banco == null ? '—' : fmtKS(m.saldo_banco)}
                </span>
              </div>
            ))}
          </div>
          <div style={{ display: 'grid',
            gridTemplateColumns: '90px 1fr 1fr 1fr 1fr', padding: '12px 16px',
            background: G.fundo, borderTop: `2px solid ${G.borda}`, minWidth: 640 }}>
            <span style={{ fontSize: 13, fontWeight: 800, color: G.txt1 }}>TOTAL</span>
            <span style={{ fontSize: 13, fontWeight: 800, color: G.verde,
              textAlign: 'right' }}>{fmtK(totais.entradas)}</span>
            <span style={{ fontSize: 13, fontWeight: 800, color: G.vermelho,
              textAlign: 'right' }}>({fmtK(totais.saidas)})</span>
            <span style={{ fontSize: 13, fontWeight: 800,
              color: corValor(totais.saldo), textAlign: 'right' }}>{fmtKS(totais.saldo)}</span>
            {/* Saldo em banco é POSIÇÃO, não fluxo: somar os 24 meses daria um
                número sem significado. O que vale é o do último mês. */}
            <span style={{ fontSize: 13, fontWeight: 800,
              color: corValor(saldoBancoFinal ?? 0), textAlign: 'right' }}>
              {saldoBancoFinal == null ? '—' : fmtKS(saldoBancoFinal)}
            </span>
          </div>
          <div style={{ padding: '12px 16px', fontSize: 11, color: G.txt2,
            lineHeight: 1.5, borderTop: `1px solid ${G.borda}` }}>
            <strong>Entradas e saídas</strong> vêm do razão: o movimento das contas de caixa,
            banco e aplicação. Dinheiro que a empresa passa de uma conta própria
            para outra não entra aqui — trocar de bolso não é entrada nem saída.
            {temSaldoContabil && (
              <> <strong>Saldo em banco</strong> é a posição no fim de cada mês, somando
                conta corrente e aplicações{' '}
                <span style={{ color: G.verde, fontWeight: 700 }}>· da contabilidade</span>.
                No total, vale o saldo do último mês, não a soma dos meses.</>
            )}
          </div>
        </div>
      )}

      {/* ── AMBAS · o lucro contra o dinheiro ───────────────────────── */}
      {!loading && eixoAtivo === 'ambas' && (
        <div style={{ background: G.card, borderRadius: 0, overflow: 'auto' }}>
          <div style={{ display: 'grid',
            gridTemplateColumns: '90px 1fr 1fr 1fr 190px', padding: '12px 16px',
            background: G.verde, minWidth: 720 }}>
            {['Mês','Resultado','Caixa do mês','Diferença','Leitura'].map(h => (
              <span key={h} style={{ fontSize: 10, fontWeight: 700,
                color: 'rgba(255,255,255,0.8)', textTransform: 'uppercase',
                // "Leitura" alinha à esquerda como a célula dela, mas com recuo:
                // encostada na "Diferença" (à direita) saía "DIFERENÇALEITURA".
                textAlign: h === 'Mês' ? 'left' : (h === 'Leitura' ? 'left' : 'right'),
                paddingLeft: h === 'Leitura' ? 16 : 0 }}>{h}</span>
            ))}
          </div>
          <div style={{ minWidth: 720 }}>
            {resumoFiltrado.map((m, i) => {
              const res = m.resultado;
              // Mês SEM eixo caixa (razão parcial) não tem leitura: sem esta
              // guarda ele entrava como zero e ganhava o rótulo verde "lucro
              // virou caixa" — dizendo que o dinheiro chegou quando não se
              // sabe. A coluna Saldo em banco já mostra "—" nesse caso.
              const semCaixa = !m.tem_caixa;
              const cx = m.saldo_caixa || 0;
              // Quatro combinações, cada uma uma conversa diferente com o dono.
              const leitura = semCaixa
                ? { txt: 'sem dado de caixa',  cor: G.txt2,     bg: G.fundo }
                : res >= 0 && cx >= 0
                ? { txt: 'lucro virou caixa',  cor: G.verde,    bg: G.verdeLight }
                : res >= 0 && cx < 0
                ? { txt: 'lucro no papel',     cor: '#B45309',  bg: '#FFFBEB' }
                : res < 0 && cx >= 0
                ? { txt: 'caixa sem lucro',    cor: '#B45309',  bg: '#FFFBEB' }
                : { txt: 'perda e queima',     cor: G.vermelho, bg: G.vermelhoL };
              return (
                <div key={m.mes} style={{ display: 'grid',
                  gridTemplateColumns: '90px 1fr 1fr 1fr 190px', padding: '11px 16px',
                  alignItems: 'center', borderBottom: `1px solid ${G.borda}`,
                  background: i % 2 === 0 ? '#fff' : G.fundo }}>
                  <span style={{ fontSize: 13, fontWeight: 700, color: G.txt1 }}>
                    {rotuloMes(m.mes)}
                  </span>
                  <span style={{ fontSize: 13, fontWeight: 700,
                    color: corValor(res), textAlign: 'right' }}>{fmtKS(res)}</span>
                  <span style={{ fontSize: 13, fontWeight: 700,
                    color: semCaixa ? G.txt2 : corValor(cx), textAlign: 'right' }}>
                    {semCaixa ? '—' : fmtKS(cx)}
                  </span>
                  <span style={{ fontSize: 13, color: G.txt2, textAlign: 'right' }}>
                    {semCaixa ? '—' : fmtKS(cx - res)}
                  </span>
                  <span style={{ fontSize: 11.5, fontWeight: 700, color: leitura.cor,
                    background: leitura.bg, borderRadius: 0, padding: '3px 9px',
                    justifySelf: 'start', marginLeft: 16 }}>
                    {leitura.txt}
                  </span>
                </div>
              );
            })}
          </div>
          <div style={{ display: 'grid',
            gridTemplateColumns: '90px 1fr 1fr 1fr 190px', padding: '12px 16px',
            background: G.fundo, borderTop: `2px solid ${G.borda}`, minWidth: 720 }}>
            <span style={{ fontSize: 13, fontWeight: 800, color: G.txt1 }}>TOTAL</span>
            <span style={{ fontSize: 13, fontWeight: 800,
              color: corValor(totais.resultado), textAlign: 'right' }}>{fmtKS(totais.resultado)}</span>
            <span style={{ fontSize: 13, fontWeight: 800,
              color: corValor(totais.saldo), textAlign: 'right' }}>{fmtKS(totais.saldo)}</span>
            <span style={{ fontSize: 13, fontWeight: 800, color: G.txt2,
              textAlign: 'right' }}>{fmtKS(totais.saldo - totais.resultado)}</span>
            <span/>
          </div>
          <div style={{ padding: '12px 16px', fontSize: 11, color: G.txt2,
            lineHeight: 1.5, borderTop: `1px solid ${G.borda}` }}>
            <strong>Resultado</strong> é o lucro do mês, contado quando a venda
            aconteceu. <strong>Caixa</strong> é o que de fato entrou menos o que
            saiu do banco. A distância entre os dois tem quatro caminhos:
            distribuição aos sócios, investimento, pagamento de dívida ou
            dinheiro parado em recebimento e estoque.
          </div>
        </div>
      )}

      {/* Card do cenário — largura total (a tabela do Resumo é larga demais
          para dividir a linha, ao contrário da DRE). Mesmo conteúdo: narrativa
          + valor a compensar líquido + as 4 medidas numeradas. */}
      {emCenario && resumoCenario.comentario && (
        <div style={{ background: '#FFF', border: `1px solid ${G.laranja}`,
          borderRadius: 0, padding: '12px 14px', marginTop: 14 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
            <MarcaAIdvisor variant="default" />
          </div>
          <div style={{ fontSize: 12.5, fontWeight: 800, color: G.txt1, marginBottom: 6 }}>
            {resumoCenario.comentario.titulo}
          </div>
          {resumoCenario.comentario.paragrafos.map((par, i) => (
            <p key={i} style={{ fontSize: 11.5, color: G.txt1, lineHeight: 1.5,
              margin: '0 0 6px' }}>
              {par}
            </p>
          ))}
          {resumoCenario.comentario.compensacao && (
            resumoCenario.comentario.compensacao.sobra ? (
              <div style={{ padding: '8px 12px', background: G.verdeLight,
                borderLeft: `3px solid ${G.verde}`, borderRadius: 0,
                fontSize: 11.5, lineHeight: 1.5, marginTop: 8 }}>
                <strong>Nada a compensar:</strong> no líquido de créditos, o cenário fica{' '}
                <strong style={{ color: G.verde }}>
                  {fmtBRL(resumoCenario.comentario.compensacao.valor_periodo)} a favor
                </strong>{' '}
                {resumoCenario.comentario.compensacao.rotulo}.
              </div>
            ) : (
              <>
                <div style={{ padding: '8px 12px', background: G.laranjaLight,
                  borderLeft: `3px solid ${G.laranja}`, borderRadius: 0,
                  fontSize: 11.5, lineHeight: 1.5, margin: '8px 0' }}>
                  <strong>Valor a compensar (líquido de créditos):</strong>{' '}
                  <strong style={{ color: G.laranja }}>
                    {fmtBRL(resumoCenario.comentario.compensacao.valor_periodo)}
                  </strong>{' '}
                  {resumoCenario.comentario.compensacao.rotulo}
                  {resumoCenario.comentario.compensacao.valor_ano
                    ? <> · {fmtBRL(resumoCenario.comentario.compensacao.valor_ano)} por ano</>
                    : null}.
                </div>
                <div style={{ fontSize: 11, fontWeight: 800, color: G.txt2,
                  letterSpacing: '0.08em', textTransform: 'uppercase',
                  margin: '0 0 6px' }}>
                  Como compensar
                </div>
                <ol style={{ margin: 0, paddingLeft: 18 }}>
                  {resumoCenario.comentario.medidas.map((m, i) => (
                    <li key={i} style={{ fontSize: 11.5, color: G.txt1,
                      lineHeight: 1.45, marginBottom: 4 }}>
                      {m}
                    </li>
                  ))}
                </ol>
              </>
            )
          )}
          {resumoCenario.comentario.tem_estimativa && (
            <div style={{ fontSize: 10.5, color: '#B45309', marginTop: 4 }}>
              Alíquota de referência ainda em estimativa oficial.
            </div>
          )}
        </div>
      )}

      <ComentarioAssessoria empresa={empresa} tela="resumo"
        periodoInicio={periodoInicio} periodoFim={periodoFim}/>
    </div>
  );
};

const TelaDRE = ({ indicadores, periodo, empresa }) => {
  // Cenário da Reforma (Etapa 5): estados ANTES do early-return — regra dos
  // hooks. null = 'Hoje' (DRE oficial, caminho intocado).
  const [anoCenario, setAnoCenario] = useState(null);
  const [dreCenario, setDreCenario] = useState(null);
  const [erroCenario, setErroCenario] = useState(null);
  const [carregandoCenario, setCarregandoCenario] = useState(false);

  // Troca de empresa ou de período: volta para 'Hoje' — cenário de uma empresa
  // jamais pode vestir o nome de outra (blindagem anti-contaminação).
  useEffect(() => {
    setAnoCenario(null); setDreCenario(null); setErroCenario(null);
  }, [empresa?.id, periodo?.inicio, periodo?.fim]);

  useEffect(() => {
    if (anoCenario == null || !empresa?.id) return;
    let vivo = true;
    setCarregandoCenario(true); setErroCenario(null);
    (async () => {
      try {
        const url = new URL(`${API}/reforma/dre/${empresa.id}`);
        url.searchParams.set('ano', anoCenario);
        if (periodo?.inicio && periodo?.fim) {
          url.searchParams.set('data_inicio', periodo.inicio);
          url.searchParams.set('data_fim', periodo.fim);
        }
        const r = await fetch(url);
        const j = await r.json();
        if (!r.ok) throw new Error(j.detail || 'Cenário indisponível.');
        if (vivo) setDreCenario(j);
      } catch (e) { if (vivo) { setErroCenario(e.message); setDreCenario(null); } }
      if (vivo) setCarregandoCenario(false);
    })();
    return () => { vivo = false; };
  }, [anoCenario, empresa?.id, periodo?.inicio, periodo?.fim]);

  if (!indicadores) return (
    <div style={{ padding: '24px 28px', color: G.txt2 }}>Carregando DRE...</div>
  );

  const rec_bruta     = indicadores.receita_bruta;
  // Devoluções e abatimentos — conta 3.1.2 do balancete. Vinham somados dentro
  // da receita, e o topo da DRE divergia da contabilidade em R$ 395.862,88 na
  // MORED (24 meses), embora a Receita Líquida fechasse exata.
  const deducoes      = indicadores.deducoes_receita || 0;
  const tributos      = indicadores.tributos || 0;
  // Ler o campo, não refazer a conta: a cascata tem TRÊS degraus
  // (bruta − deduções − tributos). Regra duplicada é regra que se descola.
  const rec_liquida   = indicadores.receita_liquida || 0;
  const cpv           = indicadores.cpv || 0;
  const lucro_bruto   = indicadores.lucro_bruto;
  const desp_comerc   = indicadores.despesa_comercial || 0;
  const desp_adm      = indicadores.despesa_administrativa || 0;
  const ebit          = indicadores.ebit;
  const res_fin       = indicadores.resultado_financeiro || 0;
  const lair          = indicadores.lair || 0;
  const lucro_liquido = indicadores.lucro_liquido || 0;
  const ir_csll       = lair - lucro_liquido;
  const regime        = indicadores.regime || '';
  const ehSimples     = regime === 'Simples Nacional';
  const fonteDre      = indicadores.fonte_dre || 'razao';
  const naoClassificado = indicadores.dre_nao_classificado || 0;

  const LinhaDestaque = ({ label, valor, pct, cor, tamanho=14, bold=false, separador=false }) => (
    <>
      {separador && <div style={{ borderTop: `2px solid ${G.borda}`, margin: '4px 0' }}/>}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 150px 130px',
        padding: '10px 20px', borderRadius: 0,
        background: cor || 'transparent', alignItems: 'center' }}>
        <span style={{ fontSize: tamanho, fontWeight: bold ? 800 : 400, color: G.txt1 }}>
          {label}
        </span>
        <span style={{ fontSize: tamanho, fontWeight: bold ? 800 : 600,
          color: valor >= 0 ? G.txt1 : G.vermelho, textAlign: 'right' }}>
          {/* Com SINAL (08/10/2026). O fmtBRL aplica Math.abs, e o prejuízo
              saía com o número do lucro — só a cor os separava. Cor não é
              número: impressa em preto e branco, ou para quem não distingue
              vermelho, a linha mentia. */}
          {dinheiroComSinal(valor)}
        </span>
        <span style={{ fontSize: 12, color: G.txt2, textAlign: 'right' }}>
          {pct != null ? fmtPct(pct) : ''}
        </span>
      </div>
    </>
  );

  // `base` diz sobre QUAL receita o percentual é calculado. Os tributos deduzem a
  // receita BRUTA; do CPV para baixo tudo é sobre a LÍQUIDA — a mesma base das
  // margens. Sem isso a coluna não fechava: 100% − 55% aparecia como 40,2%
  // (laudo 2 · 25/07).
  const LinhaDeducao = ({ label, valor, base }) => {
    const den = base === 'bruta' ? rec_bruta : rec_liquida;
    return (
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 150px 130px',
        padding: '8px 20px 8px 36px', alignItems: 'center' }}>
        <span style={{ fontSize: 13, color: G.txt2 }}>
          {label}
          {/* A base do percentual é dita NA LINHA que foge do padrão da coluna.
              O cabeçalho anuncia a receita líquida; só o tributo mede sobre a
              bruta, e é justamente o número que o consultor cita como alíquota
              efetiva — 22,0% da venda. Sem o aviso, quem soma a coluna erra
              ([[rotulo-tem-que-descrever-o-numero]]). */}
          {base === 'bruta' && (
            <span style={{ fontSize: 11.5, color: G.txt2, opacity: 0.75 }}>
              {' '}· sobre a receita bruta
            </span>
          )}
        </span>
        <span style={{ fontSize: 13, color: G.vermelho, textAlign: 'right' }}>
          ({fmtBRL(valor)})
        </span>
        <span style={{ fontSize: 12, color: G.txt2, textAlign: 'right' }}>
          {den > 0 ? fmtPct(valor / den) : ''}
        </span>
      </div>
    );
  };

  // ── DRE na LINGUAGEM DO CLIENTE ────────────────────────────────────
  // Quando a empresa tem parametrização própria (tabela `depara_linha_cliente`)
  // o backend popula `indicadores.dre_cliente` com as linhas literais do
  // contador — ordem, nomes e agrupamentos originais, sem tradução.
  // Ver memory/dre-linguagem-do-cliente.md
  const dreCliente = indicadores.dre_cliente;
  if (dreCliente && Array.isArray(dreCliente.linhas) && dreCliente.linhas.length) {
    // Cenário da Reforma Tributária (Etapa 5 · 31/08): 'Hoje' mostra a DRE
    // oficial pelo caminho de sempre — o cenário vem de rota DEDICADA
    // (/reforma/dre) e NUNCA sobrescreve a original; é lente de exibição.
    // 2026 fica fora do toggle de propósito: a fase de teste não muda o caixa.
    const emCenario = anoCenario != null && dreCenario
      && dreCenario.cenario_reforma?.ano === anoCenario;
    const dreAtiva = emCenario ? dreCenario : dreCliente;
    return (
      <div style={{ padding: '24px 28px', position: 'relative', overflow: 'hidden' }}>
        <CarimboAIdvisor />
        <div style={{ marginBottom: 20 }}>
          <div style={{ position: 'relative', display: 'inline-block' }}>
            <h2 style={{ margin: 0, fontSize: 20, fontWeight: 800, color: G.txt1,
              position: 'relative', zIndex: 1 }}>
              DRE — Demonstrativo de Resultado
            </h2>
            <MarcaDaguaTitulo>
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
              <polyline points="14 2 14 8 20 8"/>
              <line x1="8" y1="13" x2="16" y2="13"/>
              <line x1="8" y1="17" x2="16" y2="17"/>
              <line x1="8" y1="9" x2="10" y2="9"/>
            </MarcaDaguaTitulo>
          </div>
          {/* O selo verde só sai quando a leitura foi COMPLETA — a mesma regra
              que o layout M&A segue desde 02/08, e que este layout não herdou
              quando foi escrito. Sobrando conta que o de-para não soube ler, a
              DRE fica incompleta com cara de conferida, e o aviso âmbar mais
              abaixo perde a briga: o olho pega o verde primeiro. Aqui o próprio
              selo muda de cor e diz o que falta. */}
          {emCenario ? (
            <div style={{ marginTop: 8, display: 'inline-block', padding: '4px 10px',
              background: '#FFFBEB', border: '1px solid #B45309', borderRadius: 0,
              fontSize: 11, fontWeight: 700, color: '#B45309',
              letterSpacing: '0.06em', textTransform: 'uppercase' }}>
              · cenário da reforma · {anoCenario}
              {dreCenario.cenario_reforma?.tem_estimativa ? ' · estimativa' : ''}
            </div>
          ) : (
            <div style={{ marginTop: 8, display: 'inline-block',
              padding: '4px 10px',
              background: dreCliente.nao_classificado > 0 ? '#FFFBEB' : G.verdeLight,
              border: `1px solid ${dreCliente.nao_classificado > 0 ? '#B45309' : G.verde}`,
              borderRadius: 0, fontSize: 11, fontWeight: 700,
              color: dreCliente.nao_classificado > 0 ? '#B45309' : G.verde,
              letterSpacing: '0.06em', textTransform: 'uppercase' }}>
              {dreCliente.nao_classificado > 0
                ? '· da contabilidade · leitura incompleta'
                : '· da contabilidade'}
            </div>
          )}
        </div>

        <QuadrosIntro
          titulo="a DRE"
          oQueE={<>O <strong>Demonstrativo de Resultado do Exercício</strong> —
            receita, custos e despesas organizados até o lucro, aqui na
            <strong> linguagem da contabilidade do cliente</strong>: estrutura,
            ordem e nomenclatura idênticas às do balancete.</>}
          oQueFaz={<>Confira cada linha contra o balancete — o selo diz a fonte
            e se a leitura foi completa. No seletor <strong>Regime</strong>,
            veja a mesma DRE sob o cenário da reforma tributária, com o
            comentário do <MarcaAIdvisor variant="default" /> ao lado.</>}
        />

        {/* Tabela à esquerda com o toggle acima; em cenário, o card do RGAI
            ocupa a direita com o TOPO ALINHADO à linha dos botões (pedido do
            Fabio, 31/08). */}
        <div style={{ display: 'grid',
          gridTemplateColumns: emCenario ? 'minmax(0,1fr) 480px' : '1fr',
          gap: 16, alignItems: 'stretch' }}>
        <div>
        {/* Toggle do cenário — padrão dos botões do Resumo Mensal. 'Hoje' é a
            DRE oficial; os anos chamam a rota dedicada. */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14 }}>
          <span style={{ fontSize: 11, fontWeight: 800, color: G.txt2,
            letterSpacing: '0.08em', textTransform: 'uppercase' }}>
            Regime
          </span>
          <div style={{ display: 'flex', gap: 8 }}>
            {[[null, 'Hoje', 'A DRE oficial, como está na contabilidade'],
              [2027, '2027', 'CBS plena entra; PIS/Cofins e IPI saem'],
              [2030, '2030', 'Meio da troca: ICMS/ISS a 80%'],
              [2033, '2033', 'Regime cheio: só CBS + IBS']].map(([ano, rot, ajuda]) => {
              const on = anoCenario === ano;
              return (
                <button key={rot} onClick={() => setAnoCenario(ano)} title={ajuda}
                  style={{ width: 132, padding: '9px 0', border: 'none', borderRadius: 0,
                    cursor: 'pointer', fontFamily: 'inherit', fontSize: 12.5,
                    fontWeight: 700, letterSpacing: '0.01em',
                    background: on ? G.laranja : G.laranjaLight,
                    color: on ? '#FFF' : G.laranja,
                    transition: 'background .15s, color .15s' }}>
                  {rot}
                </button>
              );
            })}
          </div>
          {carregandoCenario && (
            <span style={{ fontSize: 12, color: G.txt2, fontStyle: 'italic' }}>
              montando o cenário…
            </span>
          )}
        </div>
        {erroCenario && anoCenario != null && (
          <div style={{ marginBottom: 12, padding: '10px 14px', background: '#FFFBEB',
            borderLeft: '3px solid #B45309', borderRadius: 0, fontSize: 12,
            color: '#B45309' }}>
            {erroCenario}
          </div>
        )}

        <div style={{ background: '#FFF', borderRadius: 0, padding: 8 }}>
          {dreAtiva.linhas.map((l) => {
            const v = l.valor || 0;
            const neg = v < 0;
            return (
              <div key={l.ordem} style={{
                display: 'grid',
                gridTemplateColumns: emCenario ? '34px 1fr 150px' : '40px 1fr 180px',
                alignItems: 'center', padding: '10px 14px',
                borderBottom: `1px solid ${G.borda}`,
              }}>
                <span style={{ fontSize: 11, fontWeight: 700, color: G.txt2,
                  letterSpacing: '0.06em' }}>{l.ordem}.</span>
                <span style={{ fontSize: 14, fontWeight: 600, color: G.txt1 }}>
                  {l.nome}
                  {l.codigo_conta && (
                    <span style={{ fontSize: 11, color: G.txt2,
                      marginLeft: 10, fontWeight: 400 }}>
                      · conta {l.codigo_conta}
                    </span>
                  )}
                </span>
                <span style={{ fontSize: 14, fontWeight: 700,
                  color: neg ? G.vermelho : G.txt1, textAlign: 'right' }}>
                  {/* Com SINAL (08/10/2026), pelo mesmo motivo da linha da DRE
                      acima: a cor sozinha não distingue os dois números. */}
                  {dinheiroComSinal(v)}
                </span>
              </div>
            );
          })}
          <div style={{
            display: 'grid',
            gridTemplateColumns: emCenario ? '34px 1fr 150px' : '40px 1fr 180px',
            alignItems: 'center', padding: '14px 14px',
            background: G.verdeLight, borderTop: `2px solid ${G.verde}`,
            marginTop: 4, borderRadius: 0,
          }}>
            <span/>
            <span style={{ fontSize: 15, fontWeight: 800, color: G.verde,
              letterSpacing: '0.04em', textTransform: 'uppercase' }}>
              Resultado Líquido
            </span>
            <span style={{ fontSize: 16, fontWeight: 800,
              color: dreAtiva.resultado_liquido < 0 ? G.vermelho : G.verde,
              textAlign: 'right' }}>
              {/* Com SINAL (08/10/2026): o Resultado Líquido é a linha onde
                  prejuízo virava lucro. O dinheiroComSinal já existia na casa,
                  e o comentário da declaração dele descreve este defeito. */}
              {dinheiroComSinal(dreAtiva.resultado_liquido)}
              {emCenario && (() => {
                // Variação contra o "hoje" (pedido do Fabio, 31/08). Cor pelo
                // EFEITO real: resultado maior = verde. Inversão de sinal
                // entre hoje e cenário não tem percentual honesto: "—".
                const hoje = dreCenario.cenario_reforma?.resultado_original;
                const cen = dreCenario.resultado_liquido;
                if (!hoje) return null;
                const inverteu = (hoje > 0) !== (cen > 0);
                const d = (cen - hoje) / Math.abs(hoje);
                return (
                  <span style={{ display: 'block', fontSize: 12, fontWeight: 800,
                    color: inverteu ? G.vermelho : (d >= 0 ? G.verde : G.vermelho) }}>
                    {inverteu ? '— inverteu o sinal'
                      : (d >= 0 ? '+' : '−') + (Math.abs(d) * 100).toFixed(1).replace('.', ',')
                        + '% vs hoje'}
                  </span>
                );
              })()}
            </span>
          </div>
        </div>
        </div>
        {emCenario && dreCenario.comentario && (
          <div style={{ background: '#FFF', border: `1px solid ${G.laranja}`,
            borderRadius: 0, padding: '12px 14px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
              <MarcaAIdvisor variant="default" />
            </div>
            <div style={{ fontSize: 12.5, fontWeight: 800, color: G.txt1,
              marginBottom: 6 }}>
              {dreCenario.comentario.titulo}
            </div>
            {dreCenario.comentario.paragrafos.map((par, i) => (
              <p key={i} style={{ fontSize: 11.5, color: G.txt1, lineHeight: 1.5,
                margin: '0 0 6px' }}>
                {par}
              </p>
            ))}
            {/* Compensação (forma do Fabio, 31/08): o valor LÍQUIDO a compensar
                e as 4 medidas numeradas — ou a sobra a favor, quando os
                créditos cobrem. Capital de giro é tratado à parte, na aba
                Reforma Tributária. */}
            {dreCenario.comentario.compensacao && (
              dreCenario.comentario.compensacao.sobra ? (
                <div style={{ padding: '10px 12px', background: G.verdeLight,
                  borderLeft: `3px solid ${G.verde}`, borderRadius: 0,
                  fontSize: 12, lineHeight: 1.5, marginTop: 10 }}>
                  <strong>Nada a compensar:</strong> no líquido de créditos, o
                  cenário fica{' '}
                  <strong style={{ color: G.verde }}>
                    {fmtBRL(dreCenario.comentario.compensacao.valor_periodo)} a favor
                  </strong>{' '}
                  {dreCenario.comentario.compensacao.rotulo}.
                </div>
              ) : (
                <>
                  <div style={{ padding: '8px 12px', background: G.laranjaLight,
                    borderLeft: `3px solid ${G.laranja}`, borderRadius: 0,
                    fontSize: 11.5, lineHeight: 1.5, margin: '8px 0' }}>
                    <strong>Valor a compensar (líquido de créditos):</strong>{' '}
                    <strong style={{ color: G.laranja }}>
                      {fmtBRL(dreCenario.comentario.compensacao.valor_periodo)}
                    </strong>{' '}
                    {dreCenario.comentario.compensacao.rotulo}
                    {dreCenario.comentario.compensacao.valor_ano
                      ? <> · {fmtBRL(dreCenario.comentario.compensacao.valor_ano)} por ano</>
                      : null}.
                  </div>
                  <div style={{ fontSize: 11, fontWeight: 800, color: G.txt2,
                    letterSpacing: '0.08em', textTransform: 'uppercase',
                    margin: '0 0 6px' }}>
                    Como compensar
                  </div>
                  <ol style={{ margin: 0, paddingLeft: 18 }}>
                    {dreCenario.comentario.medidas.map((m, i) => (
                      <li key={i} style={{ fontSize: 11.5, color: G.txt1,
                        lineHeight: 1.45, marginBottom: 4 }}>
                        {m}
                      </li>
                    ))}
                  </ol>
                </>
              )
            )}
            {dreCenario.comentario.tem_estimativa && (
              <div style={{ fontSize: 10.5, color: '#B45309', marginTop: 4 }}>
                Alíquota de referência ainda em estimativa oficial.
              </div>
            )}
          </div>
        )}
        </div>

        {dreCliente.nao_classificado > 0 && (
          <div style={{ marginTop: 12, padding: '10px 14px',
            background: '#FFFBEB', borderLeft: `3px solid #B45309`,
            fontSize: 12, color: '#B45309', borderRadius: 0 }}>
            {/* Sem "R$" no texto (08/10/2026): o fmtBRL já traz o símbolo, e
                a tela escrevia "R$ R$ 1.234,50". */}
            ⚠ {fmtBRL(dreCliente.nao_classificado)} em contas de resultado
            não puderam ser atribuídas a uma linha da parametrização.
          </div>
        )}

        <div style={{ marginTop: 14, fontSize: 11, color: G.txt2, fontStyle: 'italic' }}>
          {emCenario
            ? <>Cenário simulado sobre a estrutura atual da DRE — no regime cheio a
                própria forma de contabilizar muda (CBS/IBS por fora). Alíquota de
                referência ainda é estimativa oficial. Resultado oficial do período:{' '}
                {fmtBRL(dreCenario.cenario_reforma?.resultado_original)}.</>
            : <>Estrutura, ordem e nomenclatura idênticas à parametrização enviada pela
                contabilidade do cliente. Fonte: {dreCliente.fonte}
                {dreCliente.exercicios ? ` · exercícios ${dreCliente.exercicios.join(', ')}` : ''}.</>}
        </div>

        <ComentarioAssessoria empresa={empresa} tela="dre"
          periodoInicio={{ inicio: periodo?.inicio }} periodoFim={{ fim: periodo?.fim }}/>
      </div>
    );
  }
  // ── fim da DRE-linguagem-cliente · abaixo segue o layout M&A padrão ─

  return (
    <div style={{ padding: '24px 28px', position: 'relative', overflow: 'hidden' }}>
      <CarimboAIdvisor />
      <div style={{ marginBottom: 24 }}>
        <div style={{ position: 'relative', display: 'inline-block' }}>
          <h2 style={{ margin: 0, fontSize: 20, fontWeight: 800, color: G.txt1,
            position: 'relative', zIndex: 1 }}>
            DRE — Demonstrativo de Resultado
          </h2>
          {/* Marca d'água — ícone do menu (dre · documento), 5× (20→100),
              right -10 do final do título, bottom -30, rotação 30° horário */}
          <MarcaDaguaTitulo>
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
            <polyline points="14 2 14 8 20 8"/>
            <line x1="8" y1="13" x2="16" y2="13"/>
            <line x1="8" y1="17" x2="16" y2="17"/>
            <line x1="8" y1="9" x2="10" y2="9"/>
            </MarcaDaguaTitulo>
        </div>
        <p style={{ ...TIPO.subtitulo, margin: '4px 0 0' }}>
          {regime ? `Regime: ${regime}` : ''}
          {/* De onde vieram os números. O balancete ANUAL traz os lançamentos de
              encerramento e fecha com o que o contador entregou; o MENSAL não os
              tem, e o consultor precisa saber disso antes de levar o número ao
              cliente ([[fonte-contabil-de-tras-para-frente]]). */}
          {/* O selo verde só sai quando a leitura foi COMPLETA. Conta de
              resultado que o de-para não soube ler deixaria a DRE incompleta
              com cara de conferida — número faltando sob carimbo de certo
              (auditoria de 02/08). */}
          {fonteDre === 'balancete_anual' && naoClassificado === 0 && (
            <span style={{ marginLeft: 8, color: G.verde, fontWeight: 700 }}>
              · da contabilidade
            </span>
          )}
          {fonteDre === 'balancete_anual' && naoClassificado !== 0 && (
            <span style={{ marginLeft: 8, color: G.vermelho, fontWeight: 700 }}>
              · leitura incompleta — {fmtBRL(Math.abs(naoClassificado))} do balancete
              não foi reconhecido
            </span>
          )}
          {fonteDre === 'balancete_mensal' && (
            <span style={{ marginLeft: 8, color: G.laranja, fontWeight: 700 }}>
              · dos balancetes mensais — não inclui os lançamentos de encerramento
            </span>
          )}
        </p>
      </div>

      <QuadrosIntro
        titulo="a DRE"
        oQueE={<>O <strong>Demonstrativo de Resultado do Exercício</strong> —
          receita, custos e despesas organizados até o lucro, em três níveis:
          EBIT, LAIR e Lucro Líquido.</>}
        oQueFaz={<>Confira cada linha contra o balancete. A coluna de percentual
          mostra o <strong>peso de cada item na receita líquida</strong> e revela
          onde a margem se perde — só o tributo mede sobre a receita bruta, como
          a própria linha avisa.</>}
      />

      <div style={{ background: G.card, borderRadius: 0,
        overflow: 'hidden' }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 150px 130px',
          padding: '12px 20px', background: G.verde }}>
          {['Linha', 'Valor', '% da Receita Líquida'].map(h => (
            <span key={h} style={{ fontSize: 11, fontWeight: 700,
              color: 'rgba(255,255,255,0.7)', textTransform: 'uppercase',
              textAlign: h !== 'Linha' ? 'right' : 'left' }}>{h}</span>
          ))}
        </div>

        {/* Sem percentual na Receita Bruta: ela está ACIMA da base da coluna.
            Exibia 100,0% e a Receita Líquida também — dois valores diferentes,
            R$ 17,2 mi e R$ 13,4 mi, marcados como se fossem o mesmo inteiro. */}
        <LinhaDestaque label="Receita Bruta" valor={rec_bruta} pct={null} bold tamanho={15}/>
        {/* Só aparece quando existe: empresa sem devolução não vê linha zerada
            ([[base-zerada-nada-nativo]]). */}
        {deducoes > 0 && (
          <LinhaDeducao label="(-) Deduções da receita bruta · devoluções e abatimentos"
            valor={deducoes} base="bruta"/>
        )}
        <LinhaDeducao label={ehSimples ? "(-) DAS — Simples Nacional" : "(-) Tributos sobre vendas"} valor={tributos} base="bruta"/>
        {/* As margens são sobre a RECEITA LÍQUIDA (praxe de mercado e padrão M&A).
            Antes esta coluna usava a bruta e brigava com os cartões logo abaixo,
            que já usavam a líquida — 40,2% aqui contra 51,4% ali (laudo 24/07). */}
        <LinhaDestaque label="= Receita Líquida" valor={rec_liquida}
          pct={1} separador/>
        <LinhaDeducao label="(-) CPV — Custo do Produto/Serviço Vendido" valor={cpv}/>
        <LinhaDestaque label="= Lucro Bruto" valor={lucro_bruto}
          pct={rec_liquida > 0 ? lucro_bruto/rec_liquida : 0}
          cor={G.verdeLight} bold separador/>
        <LinhaDeducao label="(-) Despesas Comerciais" valor={desp_comerc}/>
        <LinhaDeducao label="(-) Despesas Administrativas" valor={desp_adm}/>
        <LinhaDestaque label="= EBIT (Resultado Operacional)" valor={ebit}
          pct={rec_liquida > 0 ? ebit/rec_liquida : 0}
          cor={ebit >= 0 ? G.verdeLight : G.vermelhoL} bold separador/>
        <LinhaDestaque label="(+/-) Resultado Financeiro" valor={res_fin}
          pct={rec_liquida > 0 ? res_fin/rec_liquida : 0}/>
        <LinhaDestaque label="= LAIR (Lucro Antes do IR)" valor={lair}
          pct={rec_liquida > 0 ? lair/rec_liquida : 0} bold separador/>
        {/* Simples nao tem linha de IR/CSLL (ja embutido no DAS) */}
        {!ehSimples && <LinhaDeducao label="(-) Provisão IR / CSLL" valor={ir_csll}/>}
        <LinhaDestaque label="= Lucro Líquido" valor={lucro_liquido}
          pct={rec_liquida > 0 ? lucro_liquido/rec_liquida : 0}
          cor={lucro_liquido >= 0 ? G.verdeLight : G.vermelhoL}
          bold tamanho={16} separador/>
      </div>

      <div style={{ display: 'grid',
        gridTemplateColumns: 'repeat(4, 1fr)', gap: 14, marginTop: 20 }}>
        {[
          { label: 'Margem Bruta',   valor: fmtPct(indicadores.margem_bruta),  cor: G.verde   },
          { label: 'Margem EBITDA',  valor: fmtPct(indicadores.margem_ebitda), cor: G.verde   },
          { label: 'Margem EBIT',    valor: fmtPct(indicadores.margem_ebit),   cor: G.verdeMid},
          { label: 'Margem Líquida', valor: fmtPct(indicadores.margem_liquida),
            cor: lucro_liquido >= 0 ? G.verde : G.vermelho },
        ].map(c => (
          <div key={c.label} style={{ background: G.card, borderRadius: 0,
            overflow: 'hidden' }}>
            <div style={{ height: 3, background: c.cor }}/>
            <div style={{ padding: '16px 18px' }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: G.txt2,
                textTransform: 'uppercase', marginBottom: 6 }}>{c.label}</div>
              <div style={{ fontSize: 24, fontWeight: 800, color: c.cor }}>{c.valor}</div>
            </div>
          </div>
        ))}
      </div>

      <ComentarioAssessoria empresa={empresa} tela="dre"
        periodoInicio={{ inicio: periodo?.inicio }} periodoFim={{ fim: periodo?.fim }}/>
    </div>
  );
};

// ── Upload ────────────────────────────────────────────────────────
// ── Tela Extrato ─────────────────────────────────────────────────
// Tela Cadastro Empresa
const TelaCadastro = ({ empresa, onSalvo }) => {
  const SETORES = [
    'Tecnologia/SaaS', 'E-commerce', 'Saude', 'Construcao Civil',
    'Bar/Restaurante', 'Agronegocio', 'Educacao', 'Logistica/Transporte',
    'Industria/Manufatura', 'Servicos Profissionais', 'Varejo Fisico',
    'Imobiliario', 'Academia/Fitness', 'Beleza/Estetica', 'geral'
  ];
  // Nomes OFICIAIS dos regimes — têm de bater com `app/categorias.py`, senão o
  // cálculo do imposto não reconhece a empresa e ela cai no padrão (Simples).
  // A Mored ficou gravada como "Presumido" e o IR saía zerado (25/07).
  const REGIMES = ['Simples Nacional', 'Lucro Presumido', 'Lucro Real'];

  // Campos existentes (cadastro inicial gratuito) + novos campos opcionais
  // que viram obrigatorios na contratacao/assinatura (logica Lacuna #2).
  const [form, setForm] = useState({
    razao_social: '', setor: 'geral', contato: '', email: '', celular: '',
    cnpj: '', cep: '', logradouro: '', numero: '', complemento: '',
    bairro: '', cidade: '', uf: '',
    // Presença digital — opcionais hoje, viram obrigatórios no tier pago.
    // Insumo para RUNGOAL AIDVISOR construir perfil de atuação/comportamento.
    site: '', instagram: '', linkedin: '',
    // Dados do CNPJ usados em Configurações Adicionais (Balanço Patrimonial) —
    // capturados pelo autofill BrasilAPI; ficam invisíveis nesta tela.
    capital_social: '', objeto_social: '',
  });
  const [regime, setRegime] = useState('');
  const [salvando, setSalvando] = useState(false);
  const [sucesso, setSucesso]   = useState(false);
  const [erro, setErro]         = useState(null);
  const [buscandoCep, setBuscandoCep]   = useState(false);
  const [buscandoCnpj, setBuscandoCnpj] = useState(false);
  const [cnpjErro, setCnpjErro]         = useState(null);
  // Teclado numerico on-screen: 'cnpj' | 'cep' | 'celular' | 'numero' | null
  const [campoAtivo, setCampoAtivo] = useState(null);
  // Labels exibidos no Teclado por campo
  const LABEL_CAMPO = {
    cnpj: 'CNPJ', cep: 'CEP', celular: 'CELULAR', numero: 'NÚMERO',
  };
  // Acoes do Teclado — adicionam digito ao raw e re-aplicam mascara via handleChange
  const tecladoDigito = (d) => {
    if (!campoAtivo) return;
    const atual = form[campoAtivo] || '';
    handleChange(campoAtivo, atual + d);
  };
  const tecladoApagar = () => {
    if (!campoAtivo) return;
    const raw = (form[campoAtivo] || '').replace(/\D/g, '');
    handleChange(campoAtivo, raw.slice(0, -1));
  };
  const tecladoLimpar = () => {
    if (!campoAtivo) return;
    handleChange(campoAtivo, '');
  };
  const tecladoFechar = () => {
    // Ao fechar o teclado, dispara busca para os campos que tem auto-fill
    if (campoAtivo === 'cep')  buscarCep(form.cep);
    if (campoAtivo === 'cnpj') buscarCnpj(form.cnpj);
    setCampoAtivo(null);
  };

  useEffect(() => {
    const buscar = async () => {
      try {
        const [resE, resR] = await Promise.all([
          fetch(`${API}/empresas/${empresa.id}`),
          fetch(`${API}/empresas/${empresa.id}/regime`).catch(() => null),
        ]);
        const dataE = await resE.json();
        if (resE.ok && dataE) {
          setForm({
            razao_social: dataE.razao_social || '',
            setor:        dataE.setor        || 'geral',
            contato:      dataE.contato      || '',
            email:        dataE.email        || '',
            celular:      dataE.celular      || '',
            cnpj:         dataE.cnpj         || '',
            cep:          dataE.cep          || '',
            logradouro:   dataE.logradouro   || '',
            numero:       dataE.numero       || '',
            complemento:  dataE.complemento  || '',
            bairro:       dataE.bairro       || '',
            cidade:       dataE.cidade       || '',
            uf:           dataE.uf           || '',
            site:         dataE.site         || '',
            instagram:    dataE.instagram    || '',
            linkedin:     dataE.linkedin     || '',
            capital_social: reaisParaMoeda(dataE.capital_social),
            objeto_social:  dataE.objeto_social  || '',
          });
        }
        if (resR && resR.ok) {
          const dataR = await resR.json();
          setRegime(dataR.regime || '');
        }
      } catch(e) {}
    };
    buscar();
  }, [empresa]);

  const mascaraCelular = (v) => {
    v = v.replace(/\D/g, '').slice(0, 11);
    if (v.length <= 2)  return `(${v}`;
    if (v.length <= 7)  return `(${v.slice(0,2)}) ${v.slice(2)}`;
    if (v.length <= 11) return `(${v.slice(0,2)}) ${v.slice(2,7)}-${v.slice(7)}`;
    return v;
  };
  const mascaraCnpj = (v) => {
    v = v.replace(/\D/g, '').slice(0, 14);
    if (v.length <= 2)  return v;
    if (v.length <= 5)  return `${v.slice(0,2)}.${v.slice(2)}`;
    if (v.length <= 8)  return `${v.slice(0,2)}.${v.slice(2,5)}.${v.slice(5)}`;
    if (v.length <= 12) return `${v.slice(0,2)}.${v.slice(2,5)}.${v.slice(5,8)}/${v.slice(8)}`;
    return `${v.slice(0,2)}.${v.slice(2,5)}.${v.slice(5,8)}/${v.slice(8,12)}-${v.slice(12)}`;
  };
  const mascaraCep = (v) => {
    v = v.replace(/\D/g, '').slice(0, 8);
    if (v.length <= 5) return v;
    return `${v.slice(0,5)}-${v.slice(5)}`;
  };
  // Capital Social: máscara de moeda contábil — os dígitos preenchem da direita,
  // os 2 últimos são os centavos. Exibe "4.015.600,00" (padrão fmtBRL do app).
  // O valor no banco é número em reais; o desmascaramento acontece no handleSalvar.
  const mascaraMoeda = (v) => {
    const d = String(v).replace(/\D/g, '');
    if (!d) return '';
    return (Number(d) / 100).toLocaleString('pt-BR',
      { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };
  // Valor em reais (banco/BrasilAPI, ex.: 4015600 ou 4015600.5) -> string "4.015.600,00".
  const reaisParaMoeda = (reais) => {
    const n = Number(reais);
    return (!n || isNaN(n)) ? '' : mascaraMoeda(String(Math.round(n * 100)));
  };

  const handleChange = (campo, valor) => {
    if (campo === 'celular')     valor = mascaraCelular(valor);
    if (campo === 'cnpj')        valor = mascaraCnpj(valor);
    if (campo === 'cep')         valor = mascaraCep(valor);
    if (campo === 'capital_social') valor = mascaraMoeda(valor);
    if (campo === 'uf')          valor = valor.toUpperCase().slice(0, 2);
    setForm(prev => ({ ...prev, [campo]: valor }));
    // Limpa mensagem de erro do CNPJ ao editar (e antes de re-buscar)
    if (campo === 'cnpj') setCnpjErro(null);
    // Auto-busca ao completar dígitos suficientes
    if (campo === 'cnpj' && valor.replace(/\D/g, '').length === 14) buscarCnpj(valor);
    if (campo === 'cep'  && valor.replace(/\D/g, '').length === 8)  buscarCep(valor);
  };

  // ViaCEP: ao completar 8 digitos, preenche logradouro/bairro/cidade/uf
  const buscarCep = async (cepRaw) => {
    const cepLimpo = cepRaw.replace(/\D/g, '');
    if (cepLimpo.length !== 8) return;
    setBuscandoCep(true);
    try {
      const res = await fetch(`https://viacep.com.br/ws/${cepLimpo}/json/`);
      const d = await res.json();
      if (!d.erro) {
        setForm(prev => ({
          ...prev,
          logradouro: d.logradouro || prev.logradouro,
          bairro:     d.bairro     || prev.bairro,
          cidade:     d.localidade || prev.cidade,
          uf:         d.uf         || prev.uf,
        }));
      }
    } catch(e) {}
    setBuscandoCep(false);
  };

  // Mapeia CNAE (Receita Federal) → setor do RunGoal. Heuristica por divisao
  // (2 primeiros digitos) com casos especiais por classe/subclasse.
  // Tabela de divisoes: https://cnae.ibge.gov.br
  const mapearSetorPorCnae = (cnaeCode) => {
    if (!cnaeCode) return null;
    const cod = String(cnaeCode).replace(/\D/g, '').padStart(7, '0');
    const div = parseInt(cod.slice(0, 2), 10);
    const cls = parseInt(cod.slice(0, 4), 10);
    // Casos especiais (mais granular que a divisao)
    if (cls === 4791) return 'E-commerce';
    if (cls === 9602) return 'Beleza/Estetica';
    if (cls === 9313) return 'Academia/Fitness';
    // Divisoes amplas
    if (div >= 1  && div <= 3)  return 'Agronegocio';
    if (div >= 10 && div <= 33) return 'Industria/Manufatura';
    if (div >= 41 && div <= 43) return 'Construcao Civil';
    if (div >= 45 && div <= 47) return 'Varejo Fisico';
    if (div >= 49 && div <= 53) return 'Logistica/Transporte';
    if (div === 56)             return 'Bar/Restaurante';
    if (div === 62 || div === 63) return 'Tecnologia/SaaS';
    if (div === 68)             return 'Imobiliario';
    if (div >= 69 && div <= 75) return 'Servicos Profissionais';
    if (div === 85)             return 'Educacao';
    if (div >= 86 && div <= 88) return 'Saude';
    return null;  // sem mapeamento — mantem 'geral'
  };

  // BrasilAPI: ao completar 14 digitos do CNPJ, busca dados publicos da Receita.
  // Override (nao preserve): o lookup É EXATAMENTE para trazer esses dados,
  // entao a chamada explicita sobrescreve. Usuario pode editar manualmente depois.
  const buscarCnpj = async (cnpjRaw) => {
    const limpo = cnpjRaw.replace(/\D/g, '');
    if (limpo.length !== 14) return;
    setBuscandoCnpj(true);
    setCnpjErro(null);
    try {
      const res = await fetch(`https://brasilapi.com.br/api/cnpj/v1/${limpo}`);
      if (!res.ok) {
        // 404 = CNPJ nao encontrado na Receita; outros 5xx = erro temporario
        setCnpjErro(res.status === 404
          ? 'CNPJ não encontrado na Receita Federal. Verifique e digite novamente.'
          : 'Não foi possível consultar o CNPJ agora. Tente novamente em alguns instantes.');
        setBuscandoCnpj(false);
        return;
      }
      const d = await res.json();
      const logradouroCompleto = [d.descricao_tipo_de_logradouro, d.logradouro]
                                  .filter(Boolean).join(' ');
      const setorMapeado = mapearSetorPorCnae(d.cnae_fiscal);
      setForm(prev => ({
        ...prev,
        ...(d.razao_social ? { razao_social: d.razao_social } : {}),
        ...(d.cep          ? { cep: mascaraCep(d.cep) }       : {}),
        ...(logradouroCompleto ? { logradouro: logradouroCompleto } : {}),
        ...(d.numero       ? { numero: String(d.numero) }     : {}),
        ...(d.complemento  ? { complemento: d.complemento }   : {}),
        ...(d.bairro       ? { bairro: d.bairro }             : {}),
        ...(d.municipio    ? { cidade: d.municipio }          : {}),
        ...(d.uf           ? { uf: d.uf }                     : {}),
        ...(d.email        ? { email: d.email }               : {}),
        // Setor: sobrescreve com o mapeado do CNAE (mesma logica dos demais campos).
        ...(setorMapeado ? { setor: setorMapeado } : {}),
        // Capital Social e Objeto Social vêm do payload BrasilAPI direto.
        // Não aparecem no Cadastro — ficam invisíveis aqui e visíveis/editáveis
        // em Configurações Adicionais (sub-tela própria para Balanço Patrimonial).
        ...(d.capital_social !== undefined && d.capital_social !== null
            ? { capital_social: reaisParaMoeda(d.capital_social) } : {}),
        ...((d.descricao_atividade_principal || d.cnae_fiscal_descricao)
            ? { objeto_social: d.descricao_atividade_principal || d.cnae_fiscal_descricao } : {}),
      }));
    } catch(e) {
      setCnpjErro('Erro de conexão ao consultar CNPJ. Verifique sua internet.');
    }
    setBuscandoCnpj(false);
  };

  // Cadastro Completo: tudo obrigatório, menos Complemento e a Presença Digital
  // (Site · Instagram · LinkedIn). Antes NADA era validado — o rótulo "obrigatório"
  // era só aparência e o salvar gravava cadastro pela metade (25/07).
  const validarObrigatorios = () => {
    const faltando = [];
    const exigir = (valor, rotulo) => {
      if (!String(valor ?? '').trim()) faltando.push(rotulo);
    };
    exigir(form.razao_social,   'Razão Social');
    exigir(form.cnpj,           'CNPJ');
    exigir(form.setor,          'Setor');
    exigir(regime,              'Regime Tributário');
    exigir(form.capital_social, 'Capital Social');
    exigir(form.objeto_social,  'Objeto Social');
    exigir(form.cep,            'CEP');
    exigir(form.logradouro,     'Logradouro');
    exigir(form.numero,         'Número');
    exigir(form.bairro,         'Bairro');
    exigir(form.cidade,         'Cidade');
    exigir(form.uf,             'UF');
    exigir(form.contato,        'Nome do Responsável');
    exigir(form.email,          'E-mail');
    exigir(form.celular,        'Celular');
    if (faltando.length) return `Preencha os campos obrigatórios: ${faltando.join(' · ')}.`;

    // Formato — impede gravar dado quebrado que depois contamina documentos e cálculos.
    const so = (v) => String(v).replace(/\D/g, '');
    if (so(form.cnpj).length !== 14) return 'CNPJ incompleto — informe os 14 dígitos.';
    if (so(form.cep).length !== 8) return 'CEP incompleto — informe os 8 dígitos.';
    if (![10, 11].includes(so(form.celular).length)) return 'Celular incompleto — informe DDD e número.';
    if (!/^\S+@\S+\.\S+$/.test(String(form.email).trim())) return 'E-mail inválido.';
    if (!/^[A-Za-z]{2}$/.test(String(form.uf).trim())) return 'UF inválida — use a sigla de 2 letras.';
    const capital = parseFloat(String(form.capital_social).replace(/\./g, '').replace(',', '.'));
    if (!isFinite(capital) || capital < 0) return 'Capital Social inválido.';
    return null;
  };

  const handleSalvar = async () => {
    if (bloquearSeAdmin()) return;
    const problema = validarObrigatorios();
    if (problema) { setSucesso(false); setErro(problema); return; }
    setSalvando(true); setErro(null);
    try {
      const res = await fetch(`${API}/empresas/${empresa.id}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        // capital_social sai desmascarado (remove pontos de milhar, vírgula decimal -> ponto) -> número puro em reais no banco
        body: JSON.stringify({ ...form, capital_social: parseFloat(String(form.capital_social).replace(/\./g, '').replace(',', '.')) || 0 })
      });
      if (!res.ok) throw new Error('Erro ao salvar dados da empresa');
      // Regime: chamada separada (tabela empresas_regimes com historico)
      if (regime) {
        const r2 = await fetch(`${API}/empresas/${empresa.id}/regime`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ regime })
        });
        if (!r2.ok) throw new Error('Erro ao salvar regime tributário');
      }
      setSucesso(true);
      setTimeout(() => { setSucesso(false); if (onSalvo) onSalvo(form); }, 1500);
    } catch(e) { setErro(e.message); }
    setSalvando(false);
  };

  // ATENÇÃO: NÃO declarar componente Campo dentro deste componente — o React recria
  // a função a cada render, desmonta/remonta o <input> e a digitação perde foco/caracteres.
  // JSX inline abaixo (verboso mas estável). Estilos compartilhados em constantes.
  const LABEL_STYLE = { fontSize: 11, fontWeight: 700, color: G.txt2,
    textTransform: 'uppercase', letterSpacing: '0.06em' };
  const INPUT_STYLE = { padding: '9px 12px', border: `1px solid ${G.borda}`,
    borderRadius: 0, fontSize: 14, color: G.txt1, background: '#fff',
    fontFamily: 'inherit' };
  // Inputs opcionais: fundo bege claro neutro, distinguivel do branco dos obrigatorios
  const INPUT_OPC_STYLE = { ...INPUT_STYLE, background: '#F5F5F1' };
  const OPC_STYLE = { marginLeft: 8, color: '#8A7B4F', fontWeight: 600,
    textTransform: 'none', fontSize: 12, fontStyle: 'normal' };

  return (
    <div style={{ padding: '24px 28px', position: 'relative', overflow: 'hidden' }}>
      <CarimboAIdvisor />
      <div style={{ marginBottom: 24, display: 'flex',
        justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <div style={{ position: 'relative', display: 'inline-block' }}>
            <h2 style={{ margin: 0, fontSize: 20, fontWeight: 800, color: G.txt1,
              position: 'relative', zIndex: 1 }}>
              Cadastro da Empresa
            </h2>
            {/* Marca d'água — ícone do menu (cadastro · documento ID), 5× (20→100),
                right -10 do final do título, bottom -30, rotação 30° horário */}
            <MarcaDaguaTitulo>
              <rect x="4" y="2" width="16" height="20" rx="2"/>
              <line x1="9" y1="6" x2="11" y2="6"/>
              <line x1="13" y1="6" x2="15" y2="6"/>
              <line x1="9" y1="10" x2="11" y2="10"/>
              <line x1="13" y1="10" x2="15" y2="10"/>
              <line x1="9" y1="14" x2="11" y2="14"/>
              <line x1="13" y1="14" x2="15" y2="14"/>
              <path d="M10 22v-4h4v4"/>
            </MarcaDaguaTitulo>
          </div>
          {/* Frase em linha própria (padrão de Documentos Contábeis): sem o nome
              da empresa à frente ela termina antes do CarimboAIdvisor. */}
          <p style={{ ...TIPO.subtitulo, margin: '2px 0 0' }}>
            A partir do Pré-Cadastro, a <MarcaAIdvisor variant="default" /> preenche o Cadastro Completo, deixando-o editável.
          </p>
        </div>
        <button onClick={handleSalvar} disabled={salvando}
          style={{ background: G.verde, color: '#fff', border: 'none',
            borderRadius: 0, padding: '10px 24px', fontSize: 13,
            fontWeight: 700, cursor: 'pointer',
            boxShadow: `0 2px 8px ${G.verde}44` }}>
          {salvando ? 'Salvando...' : 'Salvar Cadastro'}
        </button>
      </div>


      <QuadrosIntro
        titulo="o Cadastro"
        oQueE={<>O registro da <strong>identidade da empresa</strong> — razão
          social, CNPJ, endereço, capital social e objeto social. É a base de
          que todas as demais telas dependem.</>}
        oQueFaz={<>Confira e complete os dados. A <MarcaAIdvisor variant="default"/>{' '}
          preenche o que consegue a partir do Pré-Cadastro e de fontes públicas;
          você <strong>revisa e corrige</strong> o que estiver diferente.</>}
      />

      {sucesso && (
        <div style={{ background: G.verdeLight, color: G.verde, padding: 10,
          marginBottom: 10, fontSize: 13, fontWeight: 700 }}>
          ✓ Cadastro salvo com sucesso!
        </div>
      )}
      {erro && (
        <div style={{ background: G.vermelhoL, color: G.vermelho, padding: 10,
          marginBottom: 10, fontSize: 13 }}>{erro}</div>
      )}

    <div style={{ display: 'grid',
      gridTemplateColumns: campoAtivo ? '1fr 280px' : '1fr',
      gap: 16, alignItems: 'start' }}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr',
        gap: 16, alignItems: 'stretch' }}>

        {/* IDENTIFICAÇÃO */}
        {/* IDENTIFICAÇÃO — full width · 4 campos lado a lado (Razão larga + CNPJ + Setor + Regime).
            Layout horizontal igual ao Endereço (decisão Fabio 06/06). */}
        <div style={{ background: G.card, padding: '16px 18px', gridColumn: '1 / -1' }}>
          <h3 style={{ margin: '0 0 12px', fontSize: 13,
            fontWeight: 800, color: G.txt1 }}>Identificação</h3>
          <div style={{ display: 'grid',
            gridTemplateColumns: '2fr 1fr 1fr 1fr', gap: 12 }}>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <label style={LABEL_STYLE}>Razão Social</label>
              <input value={form.razao_social}
                onChange={e => handleChange('razao_social', e.target.value)}
                placeholder="Ex: RunGoal Consultoria Ltda"
                style={INPUT_STYLE}/>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <label style={LABEL_STYLE}>
                {buscandoCnpj ? 'CNPJ (buscando...)' : 'CNPJ'}
              </label>
              <input value={form.cnpj}
                onChange={e => handleChange('cnpj', e.target.value)}
                onFocus={() => setCampoAtivo('cnpj')}
                placeholder="00.000.000/0000-00"
                inputMode="numeric"
                style={cnpjErro ? { ...INPUT_STYLE,
                  border: `1.5px solid ${G.vermelho}` } : INPUT_STYLE}/>
              {cnpjErro ? (
                <span style={{ fontSize: 11, color: G.vermelho, fontWeight: 600 }}>
                  {cnpjErro}
                </span>
              ) : (
                <span style={{ fontSize: 10, color: G.txt2 }}>
                  Preenche Razão Social + Endereço
                </span>
              )}
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <label style={LABEL_STYLE}>Setor</label>
              <select value={form.setor}
                onChange={e => handleChange('setor', e.target.value)}
                style={INPUT_STYLE}>
                {SETORES.map(s => <option key={s}>{s}</option>)}
              </select>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <label style={LABEL_STYLE}>
                Regime Tributário
              </label>
              <select value={regime}
                onChange={e => setRegime(e.target.value)}
                style={INPUT_STYLE}>
                <option value="">Selecione...</option>
                {REGIMES.map(r => <option key={r} value={r}>{r}</option>)}
              </select>
            </div>

          </div>
        </div>


        {/* DADOS INSTITUCIONAIS — full width · alimenta Balanço Patrimonial
            Capital Social + Objeto Social vêm do BrasilAPI (autofill no CNPJ).
            Movidos para cá em 09/06 (antes ficavam em Configurações Adicionais). */}
        <div style={{ background: G.card, padding: '16px 18px', gridColumn: '1 / -1' }}>
          <h3 style={{ margin: '0 0 4px', fontSize: 13,
            fontWeight: 800, color: G.txt1 }}>
            Dados Institucionais
            <span style={{ marginLeft: 8, fontSize: 10, color: G.txt2,
              fontWeight: 400, fontStyle: 'italic' }}>
              (preenchidos automaticamente do CNPJ · BrasilAPI/Receita Federal · editáveis)
            </span>
          </h3>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr',
            gap: 12, alignItems: 'stretch' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <label style={LABEL_STYLE}>Capital Social <span style={OPC_STYLE}>(R$)</span></label>
              <input value={form.capital_social} type="text" inputMode="decimal"
                onChange={e => handleChange('capital_social', e.target.value)}
                placeholder="0"
                style={INPUT_STYLE}/>
              <span style={{ fontSize: 10, color: G.txt2 }}>
                Capital integralizado no contrato social
              </span>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <label style={LABEL_STYLE}>Objeto Social <span style={OPC_STYLE}>(descrição da atividade)</span></label>
              <textarea
                value={form.objeto_social}
                onChange={e => handleChange('objeto_social', e.target.value)}
                placeholder="Ex: Comércio varejista de artigos diversos · vendas presencial e online..."
                rows={2}
                style={{
                  ...INPUT_STYLE,
                  fontFamily: 'inherit', lineHeight: 1.45,
                  resize: 'vertical', minHeight: 56,
                }}/>
              <span style={{ fontSize: 10, color: G.txt2 }}>
                Insumo para a RUNGOAL AIDVISOR entender o ramo real de atuação
              </span>
            </div>
          </div>
        </div>


        {/* ENDEREÇO — full width */}
        <div style={{ background: G.card, padding: '16px 18px', gridColumn: '1 / -1' }}>
          <h3 style={{ margin: '0 0 12px', fontSize: 13,
            fontWeight: 800, color: G.txt1 }}>
            Endereço
            <span style={{ marginLeft: 8, fontSize: 10, color: G.txt2,
              fontWeight: 400, fontStyle: 'italic' }}>
              (CEP preenche automaticamente Logradouro, Bairro, Cidade e UF)
            </span>
          </h3>

          <div style={{ display: 'grid',
            gridTemplateColumns: '180px 1fr 120px', gap: 12, marginBottom: 12 }}>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <label style={LABEL_STYLE}>
                {buscandoCep ? 'CEP (buscando...)' : 'CEP'}
              </label>
              <input value={form.cep}
                onChange={e => handleChange('cep', e.target.value)}
                onFocus={() => setCampoAtivo('cep')}
                placeholder="00000-000"
                inputMode="numeric"
                style={INPUT_STYLE}/>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <label style={LABEL_STYLE}>Logradouro</label>
              <input value={form.logradouro}
                onChange={e => handleChange('logradouro', e.target.value)}
                placeholder="Rua, Avenida, Travessa"
                style={INPUT_STYLE}/>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <label style={LABEL_STYLE}>Número</label>
              <input value={form.numero}
                onChange={e => handleChange('numero', e.target.value)}
                onFocus={() => setCampoAtivo('numero')}
                placeholder="123"
                inputMode="numeric"
                style={INPUT_STYLE}/>
            </div>

          </div>

          <div style={{ display: 'grid',
            gridTemplateColumns: '1fr 1fr 1fr 80px', gap: 12 }}>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <label style={LABEL_STYLE}>Complemento <span style={OPC_STYLE}>(opcional)</span></label>
              <input value={form.complemento}
                onChange={e => handleChange('complemento', e.target.value)}
                placeholder="Sala, andar, bloco"
                style={INPUT_OPC_STYLE}/>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <label style={LABEL_STYLE}>Bairro</label>
              <input value={form.bairro}
                onChange={e => handleChange('bairro', e.target.value)}
                style={INPUT_STYLE}/>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <label style={LABEL_STYLE}>Cidade</label>
              <input value={form.cidade}
                onChange={e => handleChange('cidade', e.target.value)}
                style={INPUT_STYLE}/>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <label style={LABEL_STYLE}>UF</label>
              <input value={form.uf}
                onChange={e => handleChange('uf', e.target.value)}
                placeholder="SP"
                style={INPUT_STYLE}/>
            </div>

          </div>
        </div>

        {/* CONTATO */}
        <div style={{ background: G.card, padding: '16px 18px' }}>
          <h3 style={{ margin: '0 0 12px', fontSize: 13,
            fontWeight: 800, color: G.txt1 }}>Contato</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <label style={LABEL_STYLE}>Nome do Responsável</label>
              <input value={form.contato}
                onChange={e => handleChange('contato', e.target.value)}
                placeholder="Ex: João Silva"
                style={INPUT_STYLE}/>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <label style={LABEL_STYLE}>E-mail</label>
              <input value={form.email} type="email"
                onChange={e => handleChange('email', e.target.value)}
                placeholder="Ex: joao@empresa.com.br"
                style={INPUT_STYLE}/>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <label style={LABEL_STYLE}>Celular</label>
              <input value={form.celular}
                onChange={e => handleChange('celular', e.target.value)}
                onFocus={() => setCampoAtivo('celular')}
                placeholder="(11) 99999-9999"
                inputMode="numeric"
                style={INPUT_STYLE}/>
              <span style={{ fontSize: 10, color: G.txt2 }}>
                DDD + número com 9 dígitos
              </span>
            </div>

          </div>
        </div>

        {/* PRESENÇA DIGITAL — flui auto na col 2 ao lado do Contato (Identificação e Endereço
            estão full-width acima, então Contato ocupa col 1 e esta seção vai para col 2). */}
        <div style={{ background: G.card, padding: '16px 18px' }}>
          <h3 style={{ margin: '0 0 12px', fontSize: 13,
            fontWeight: 800, color: G.txt1 }}>
            Presença Digital
            <span style={{ marginLeft: 8, fontSize: 10, color: G.txt2,
              fontWeight: 400, fontStyle: 'italic' }}>
              (opcional · ajuda o RUNGOAL AIDVISOR a entender o perfil da empresa)
            </span>
          </h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <label style={LABEL_STYLE}>Site <span style={OPC_STYLE}>(opcional)</span></label>
              <input value={form.site} type="url"
                onChange={e => handleChange('site', e.target.value)}
                placeholder="https://empresa.com.br"
                style={INPUT_OPC_STYLE}/>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <label style={LABEL_STYLE}>Instagram <span style={OPC_STYLE}>(opcional)</span></label>
              <input value={form.instagram}
                onChange={e => handleChange('instagram', e.target.value)}
                placeholder="@empresa"
                style={INPUT_OPC_STYLE}/>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <label style={LABEL_STYLE}>LinkedIn <span style={OPC_STYLE}>(opcional)</span></label>
              <input value={form.linkedin} type="url"
                onChange={e => handleChange('linkedin', e.target.value)}
                placeholder="linkedin.com/company/empresa"
                style={INPUT_OPC_STYLE}/>
            </div>
          </div>
        </div>
      </div>

      {campoAtivo && (
        <TecladoNumerico
          valor={form[campoAtivo]}
          label={LABEL_CAMPO[campoAtivo]}
          onDigito={tecladoDigito}
          onApagar={tecladoApagar}
          onLimpar={tecladoLimpar}
          onFechar={tecladoFechar}
          permitirDecimal={false}
        />
      )}
    </div>
    </div>
  );
};


// Tela Comparativo
const TelaComparativo = ({ empresa, parametros }) => {
  const [ano, setAno]           = useState(2025);
  const [comparativo, setComp]  = useState([]);
  const [loading, setLoading]   = useState(true);   // nasce carregando: a janela do plano ainda não chegou
  const [erro, setErro]         = useState(null);
  const [grupoAtivo, setGrupoAtivo] = useState('RECEITAS');
  // ── Janela do plano validado (01/09): o Planejamento 12M começa no mês
  // seguinte ao último dado e pode atravessar anos civis — o seletor passa a
  // oferecer essa janela além dos anos (decisão do Fabio, P432).
  const [planoJanela, setPlanoJanela] = useState(null);   // {de, ate} em 'AAAA-MM'
  const [usarJanela, setUsarJanela] = useState(false);
  // Só busca o comparativo DEPOIS de saber se há janela — senão duas chamadas
  // correm (com e sem janela) e a resposta obsoleta zera a tela.
  const [janelaResolvida, setJanelaResolvida] = useState(false);
  const reqComparativo = useRef(0);

  const ANOS = [2025, 2026, 2027, 2028, 2029];
  const MESES = ['Jan','Fev','Mar','Abr','Mai','Jun','Jul','Ago','Set','Out','Nov','Dez'];

  // Colunas na ORDEM da janela (jun→mai não pode sair como jan→dez) e com o
  // ANO na chave: janela que atravessa anos tem dois exercícios (01/09).
  const colunas = (() => {
    if (usarJanela && planoJanela) {
      const t0 = parseInt(planoJanela.de.slice(0, 4), 10) * 12 + parseInt(planoJanela.de.slice(5, 7), 10) - 1;
      const t1 = parseInt(planoJanela.ate.slice(0, 4), 10) * 12 + parseInt(planoJanela.ate.slice(5, 7), 10) - 1;
      return Array.from({ length: Math.max(0, t1 - t0 + 1) }, (_, i) => {
        const t = t0 + i;
        const a = Math.floor(t / 12), m = (t % 12) + 1;
        return { ano: a, mes: m, label: MESES[m - 1] + (i === 0 || m === 1 ? `/${String(a).slice(2)}` : '') };
      });
    }
    return Array.from({ length: 12 }, (_, i) => ({ ano, mes: i + 1, label: MESES[i] }));
  })();
  const achar = (col, cat) => comparativo.find(
    c => c.mes === col.mes && (c.ano == null || c.ano === col.ano) && c.categoria === cat);

  const GRUPOS = [
    { label: 'RECEITAS', cats: ['Receita Operacional', 'Receita Financeira'], tipo: 'receita' },
    { label: 'APORTES E CAPTACOES', cats: ['Aporte de Socio', 'Captacao / Financiamento'], tipo: 'aporte' },
    { label: 'DESPESAS', cats: ['Despesa Operacional', 'Despesa Comercial', 'Despesa Administrativa', 'Despesa Financeira', 'Tributos'], tipo: 'despesa' },
    { label: 'SAIDAS FINANCEIRAS', cats: ['Amortizacao de Divida', 'Retirada de Socio', 'Distribuicao de Lucros'], tipo: 'saida' },
  ];

  const CORES_GRUPO = {
    receita: G.verde, aporte: '#7C3AED', despesa: G.vermelho, saida: '#B45309',
  };

  // Janela do plano validado — se existe, a tela abre nela (é o que o
  // empresário acabou de congelar), com os anos civis ainda disponíveis.
  useEffect(() => {
    if (!empresa?.id) return;
    let vivo = true;
    (async () => {
      try {
        const r = await fetch(`${API}/planos/${empresa.id}/vigente?tipo=12m`);
        const p = r.ok ? await r.json() : null;
        if (!vivo) return;
        if (!p || !p.periodo_inicio || !p.periodo_fim) {
          setPlanoJanela(null); setUsarJanela(false);
        } else {
          setPlanoJanela({ de: String(p.periodo_inicio).slice(0, 7),
                           ate: String(p.periodo_fim).slice(0, 7) });
          setUsarJanela(true);
        }
      } catch (_) { if (vivo) { setPlanoJanela(null); setUsarJanela(false); } }
      if (vivo) setJanelaResolvida(true);
    })();
    return () => { vivo = false; setJanelaResolvida(false); };
  }, [empresa?.id]);

  useEffect(() => {
    if (!janelaResolvida) return;      // evita a chamada obsoleta da montagem
    carregarComparativo();
  }, [empresa, ano, usarJanela, planoJanela, janelaResolvida]);

  const carregarComparativo = async () => {
    // Token de requisição: só a resposta da ÚLTIMA chamada pinta a tela. Sem
    // isto, a resposta de um ano sem orçamento chegava depois e zerava o que
    // a janela do plano já tinha mostrado (defeito visto pelo Fabio, 01/09).
    const meuToken = ++reqComparativo.current;
    setLoading(true); setErro(null);
    try {
      const emJanela = usarJanela && planoJanela;
      const url = new URL(`${API}/orcamento/${empresa.id}/${ano}/comparativo`);
      if (emJanela) {
        url.searchParams.set('de', planoJanela.de);
        url.searchParams.set('ate', planoJanela.ate);
      }
      const res  = await fetch(url);
      const data = await res.json().catch(() => null);
      if (meuToken !== reqComparativo.current) return;   // resposta obsoleta
      // Erro do backend não pode virar tabela de "---" sem aviso: parece
      // "sem orçamento" quando foi falha (auditoria 02/09).
      if (!res.ok) throw new Error(data?.detail || `Erro ${res.status} ao comparar.`);
      setComp(Array.isArray(data) ? data : []);
    } catch(e) {
      if (meuToken === reqComparativo.current) {
        setComp([]);
        setErro(e.message || 'Erro ao carregar comparativo.');
      }
    }
    if (meuToken === reqComparativo.current) setLoading(false);
  };

  const fmtBRLComp = v => v == null ? '---' : 'R$ ' + Math.abs(v).toLocaleString('pt-BR', {minimumFractionDigits:2,maximumFractionDigits:2});
  const fmtVar = v => v == null ? '---' : (v > 0 ? '+' : '') + v.toFixed(1) + '%';

  const corStatus = (status, tipo, variacao) => {
    if (status === 'nd' || variacao == null) return G.txt2;
    if (tipo === 'receita') {
      if (variacao >= 0) return G.verde;
      if (status === 'alerta') return G.laranja;
      return G.vermelho;
    }
    if (variacao > 0) return status === 'critico' ? G.vermelho : G.laranja;
    if (variacao < 0) return '#B45309';
    return G.verde;
  };

  const tooltipStatus = (tipo, variacao) => {
    if (variacao == null) return null;
    if ((tipo === 'despesa' || tipo === 'saida' || tipo === 'aporte') && variacao < 0)
      return 'Verifique se a despesa foi postergada';
    return null;
  };

  return (
    <div style={{ padding: '24px 28px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between',
        alignItems: 'flex-start', marginBottom: 24 }}>
        <div>
          <div style={{ position: 'relative', display: 'inline-block' }}>
            <h2 style={{ margin: 0, fontSize: 20, fontWeight: 800, color: G.txt1,
              position: 'relative', zIndex: 1 }}>
              Previsto x Realizado
            </h2>
            {/* Marca d'água — ícone do menu (comparativo · polyline), 5× (20→100),
                right -10 do final do título, bottom -30, rotação 30° horário */}
            <MarcaDaguaTitulo>
              <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/>
            </MarcaDaguaTitulo>
          </div>
          <p style={{ ...TIPO.subtitulo, margin: '4px 0 0' }}>
            acompanhamento do plano vigente
          </p>
        </div>
        <select value={usarJanela && planoJanela ? 'plano' : String(ano)}
          onChange={e => {
            if (e.target.value === 'plano') { setUsarJanela(true); return; }
            setUsarJanela(false); setAno(parseInt(e.target.value));
          }}
          style={{ padding: '7px 12px', borderRadius: 0, fontSize: 13, fontWeight: 600, color: G.verde, background: G.verdeLight }}>
          {planoJanela && (
            <option value="plano">
              Plano 12M · {rotuloMesCurto(planoJanela.de)} → {rotuloMesCurto(planoJanela.ate)}
            </option>
          )}
          {ANOS.map(a => <option key={a} value={String(a)}>{a}</option>)}
        </select>
      </div>

      <QuadrosIntro
        titulo="o Previsto x Realizado"
        oQueE={<>A comparação entre o <strong>plano validado</strong> e o que de
          fato aconteceu, mês a mês, na mesma classificação do
          Planejamento 12M.</>}
        oQueFaz={<>Acompanhe o desvio de cada mês. Diferença grande e repetida
          indica <strong>premissa errada no plano</strong>, não só falha de
          execução — vale rever o Planejamento 12M.</>}
      />

      {erro && <div style={{ background: G.vermelhoL, color: G.vermelho, padding: 10,
        borderRadius: 0, marginBottom: 12 }}>{erro}</div>}

      {/* Grafico compacto */}
      {comparativo.length > 0 && (
        <div style={{ background: G.card, borderRadius: 0, padding: '16px 20px',
          marginBottom: 18, }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: G.txt2,
            textTransform: 'uppercase', marginBottom: 10 }}>
            Receita — Previsto x Realizado
          </div>
          <ResponsiveContainer width="100%" height={160}>
            <AreaChart data={colunas.map((col) => {
              const prev = achar(col, 'Receita Operacional');
              const desp = achar(col, 'Despesa Operacional');
              return {
                mes: col.label,
                recPrev: prev?.valor_previsto || 0,
                recReal: prev?.valor_realizado || null,
                despPrev: desp?.valor_previsto || 0,
                despReal: desp?.valor_realizado || null,
              };
            })} margin={{ top: 2, right: 4, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={G.borda} vertical={false}/>
              <XAxis dataKey="mes" tick={{ fontSize: 10, fill: G.txt2 }} axisLine={false} tickLine={false}/>
              <YAxis tick={{ fontSize: 9, fill: G.txt2 }} axisLine={false} tickLine={false}
                tickFormatter={v => 'R$'+(v/1000).toFixed(0)+'k'}/>
              <Tooltip content={<CustomTooltip />}/>
              <Area type="monotone" dataKey="recPrev" name="Rec Prev"
                stroke={G.verde} strokeWidth={1.5} strokeDasharray="5 3" fill="none" dot={false}/>
              <Area type="monotone" dataKey="recReal" name="Rec Real"
                stroke={G.verde} strokeWidth={2} fill="none"
                dot={{ fill: G.verde, r: 2 }} connectNulls={false}/>
              <Area type="monotone" dataKey="despPrev" name="Desp Prev"
                stroke={G.laranja} strokeWidth={1.5} strokeDasharray="5 3" fill="none" dot={false}/>
              <Area type="monotone" dataKey="despReal" name="Desp Real"
                stroke={G.laranja} strokeWidth={2} fill="none"
                dot={{ fill: G.laranja, r: 2 }} connectNulls={false}/>
            </AreaChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Filtro por grupo */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 16, flexWrap: 'wrap' }}>
        {GRUPOS.map(g => (
          <button key={g.label} onClick={() => setGrupoAtivo(g.label)}
            style={{ padding: '6px 14px', borderRadius: 0, border: 'none',
              cursor: 'pointer', fontSize: 11, fontWeight: 700,
              background: grupoAtivo === g.label ? CORES_GRUPO[g.tipo] : G.fundo,
              color: grupoAtivo === g.label ? '#fff' : G.txt2 }}>
            {g.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div style={{ padding: 40, textAlign: 'center', color: G.txt2 }}>Carregando...</div>
      ) : (
        <div style={{ background: G.card, borderRadius: 0, overflow: 'auto',
          maxHeight: 'calc(100vh - 490px)' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 900 }}>
            <thead style={{ position: 'sticky', top: 0, zIndex: 2 }}>
              <tr style={{ background: G.verde }}>
                <th style={{ padding: '9px 12px', textAlign: 'left', fontSize: 11,
                  fontWeight: 700, color: 'rgba(255,255,255,0.8)',
                  textTransform: 'uppercase', width: 160 }}>Categoria</th>
                <th style={{ padding: '9px 8px', fontSize: 10,
                  color: 'rgba(255,255,255,0.8)', textTransform: 'uppercase' }}>Info</th>
                {colunas.map(col => (
                  <th key={`${col.ano}-${col.mes}`} style={{ padding: '9px 3px', textAlign: 'center', fontSize: 10,
                    fontWeight: 700, color: 'rgba(255,255,255,0.8)',
                    textTransform: 'uppercase' }}>{col.label}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {GRUPOS.filter(g => g.label === grupoAtivo).map(grupo => (
                <>
                  <tr key={grupo.label} style={{ background: G.fundo }}>
                    <td colSpan={colunas.length + 2} style={{ padding: '6px 12px', fontSize: 10,
                      fontWeight: 800, color: CORES_GRUPO[grupo.tipo],
                      textTransform: 'uppercase' }}>{grupo.label}</td>
                  </tr>
                  {grupo.cats.map(cat => (
                    <>
                      <tr key={`${cat}-prev`}>
                        <td rowSpan={3} style={{ padding: '5px 12px', fontSize: 11,
                          color: G.txt1, fontWeight: 600,
                          borderRight: `1px solid ${G.borda}`,
                          borderBottom: `2px solid ${G.borda}` }}>{cat}</td>
                        <td style={{ padding: '3px 8px', fontSize: 10, color: G.txt2, fontWeight: 600 }}>Prev</td>
                        {colunas.map(col => {
                          const item = achar(col, cat);
                          return (
                            <td key={`${col.ano}-${col.mes}`} style={{ padding: '3px', textAlign: 'center', fontSize: 10, color: G.txt2 }}>
                              {item ? fmtBRLComp(item.valor_previsto) : '---'}
                            </td>
                          );
                        })}
                      </tr>
                      <tr key={`${cat}-real`}>
                        <td style={{ padding: '3px 8px', fontSize: 10, color: G.verde, fontWeight: 600 }}>Real</td>
                        {colunas.map(col => {
                          const item = achar(col, cat);
                          return (
                            <td key={`${col.ano}-${col.mes}`} style={{ padding: '3px', textAlign: 'center',
                              fontSize: 10, color: G.verde, fontWeight: 600 }}>
                              {item ? fmtBRLComp(item.valor_realizado) : '---'}
                            </td>
                          );
                        })}
                      </tr>
                      <tr key={`${cat}-var`} style={{ borderBottom: `2px solid ${G.borda}` }}>
                        <td style={{ padding: '3px 8px', fontSize: 10, color: G.txt2, fontWeight: 600 }}>Var%</td>
                        {colunas.map(col => {
                          const item = achar(col, cat);
                          const cor = item ? corStatus(item.status, grupo.tipo, item.variacao_pct) : G.txt2;
                          const tip = item ? tooltipStatus(grupo.tipo, item.variacao_pct) : null;
                          return (
                            <td key={`${col.ano}-${col.mes}`} style={{ padding: '3px', textAlign: 'center',
                              fontSize: 10, fontWeight: 700, color: cor,
                              cursor: tip ? 'help' : 'default' }}
                              title={tip || ''}>
                              {item ? fmtVar(item.variacao_pct) : '---'}
                              {tip && ''}
                            </td>
                          );
                        })}
                      </tr>
                    </>
                  ))}
                </>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

// Tela Previsto x Realizado
// Tela Plano — narrativa estrategica da previsao (vigente), gerada pela IA (em
// producao via Lacuna #1; para teste, planos seedados em `planos`). Mostra
// estrategia + alavancas + resultado projetado + alerta/freio de realidade.
// ════════════════════════════════════════════════════════════════════════
// PLANEJAMENTO (12m) — espírito "sobreviver / aprendizado". Patamares
// intermediários de controle, baseados em metas operacionais de curto prazo.
//
// Wizard 2 passos:
//   Passo 1: empresário escolhe até 2 metas (de 4 modalidades + "Outra")
//   Passo 2: plano apresentado com tabela trimestral, alavancas e freios
//
// Estados: rascunho ↔ validado (binário).
// Edição: só receita/despesa editáveis · botão Recalcular dispara Advisor
// (seed/placeholder até Lacuna #1 entrar com IA real) para regenerar narrativa.
// ════════════════════════════════════════════════════════════════════════

// Modalidades de meta de curto prazo (cobre dores recorrentes de PME
// brasileira que o RunGoal consegue medir com os dados que ele já tem).
// METAS DE RESULTADO × COMPLEMENTARES (02/09/2026 · decisão do Fabio).
// O valor da empresa é EBITDA × múltiplo; só quatro alavancas mexem no
// EBITDA — volume · preço · custo por unidade · estrutura fixa. Três delas
// são OBRIGATÓRIAS: todo plano leva ao menos uma, porque plano que não move
// resultado não é plano. As complementares valem por outro caminho, e cada
// uma DECLARA qual (múltiplo · financiamento) — antes o selo medido mostrava
// "+0,0% vs base" e parecia plano vazio.
const METAS_PLANO = [
  { id: 'receita', grupo: 'resultado',
    nome: 'Aumentar Receita',
    desc: 'Vender mais na mesma estrutura — volume, novos clientes, frequência.' },
  { id: 'custo_producao', grupo: 'resultado',
    nome: 'Reduzir Custo de Produção',
    desc: 'Quanto sai da empresa por real vendido — fornecedor, mix, processo.' },
  { id: 'despesa_fixa', grupo: 'resultado',
    nome: 'Reduzir Despesa Fixa',
    desc: 'O que a empresa paga para existir, venda ou não venda.' },
  { id: 'ticket_medio', grupo: 'complementar', efeito: 'compõe com o volume na receita',
    nome: 'Aumentar Ticket Médio',
    desc: 'Preço realizado — combos, mix e desconto concedido.' },
  { id: 'concentracao_clientes', grupo: 'complementar', efeito: 'reduz risco — vale no múltiplo, não no lucro',
    nome: 'Reduzir Concentração de Clientes',
    desc: 'Diluir a dependência do maior cliente sem abrir mão da receita dele.' },
  { id: 'caixa_operacional', grupo: 'complementar', efeito: 'financia o crescimento — não muda o lucro',
    nome: 'Aumentar Caixa Operacional',
    desc: 'Prazos, cobrança e giro: o dinheiro que a operação devolve.' },
  { id: 'retencao_lucro', grupo: 'complementar', efeito: 'financia o crescimento e reduz o imposto de dividendos',
    nome: 'Reter Lucro',
    desc: 'Segurar distribuição para fortalecer o caixa da empresa.' },
];
const META_EH_RESULTADO = (id) =>
  (METAS_PLANO.find(m => m.id === id) || {}).grupo === 'resultado';

// Mock estático — placeholder até o Advisor real entrar (Lacuna #1).
// Substitui o seed do backend para acelerar iteração de UI.
// Valores em milhares (R$ k). Estrutura mensal: 12 valores por linha,
// um por mês do Plano (a partir do mês corrente).
// PLANO_MOCK removido em 29/07/2026 junto com `gerarPlanoMock`: plano inteiro
// inventado (estratégia, projeção, alavancas) que nenhuma empresa gerou.
// O Planejamento 12M nasce vazio e é preenchido por /planos/{id}/gerar, que
// lê as transações da própria empresa. Princípio `base-zerada-nada-nativo`.

const TelaPlano = ({ empresa, onValidado, onAbrirConfig }) => {
  // Persistência em localStorage (mock — até a Lacuna #1 entrar com backend real).
  // Sobrevive a mudança de aba (a tela é desmontada/remontada e perderia o state).
  // Chave versionada (v2 = estrutura mensal · valores em proj.X[i].m[0..11]).
  // Schema antigo (v1 trimestral, l.q[0..3]) é incompatível — descartado ao montar.
  // v6 = + estrategia_fragmentos (3 fragmentos da Tese, antes hardcoded no JSX)
  // v7 (31/08): meta quantificada + laudo + capex + selos. O bump descarta o
  // plano da era do mock (baseline dobrado, sem laudo) — lixo por definição.
  const STORAGE_KEY = `rungoal:plano12m-v7:${empresa?.id || 'default'}`;
  const loadSaved = () => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return {};
      const s = JSON.parse(raw);
      // Sanity check do schema: planoAtual.proj.receitas[0].m precisa existir e ter 12 itens
      if (s.planoAtual && s.planoAtual.proj) {
        const r0 = s.planoAtual.proj?.receitas?.[0];
        if (!r0 || !Array.isArray(r0.m) || r0.m.length !== 12) {
          return { passo: 'meta' };
        }
      }
      // Sanity check v3 — alavancas tem que ser array de strings (não objetos)
      if (s.planoAtual?.alavancas && s.planoAtual.alavancas.length > 0
          && typeof s.planoAtual.alavancas[0] !== 'string') {
        return { passo: 'meta' };
      }
      // Sanity check v6 — planoAtual precisa ter estrategia_fragmentos.
      // Se está faltando (gerado antes do P1), descartar para regenerar do mock.
      if (s.planoAtual && !Array.isArray(s.planoAtual.estrategia_fragmentos)) {
        return { passo: 'meta' };
      }
      return s;
    } catch { return {}; }
  };
  const saved = loadSaved();

  // Wizard: 'meta' (passo 1) | 'plano' (passo 2)
  const [passo, setPasso] = useState(saved.passo || 'meta');

  // Passo 1 — seleção de metas
  const [metasSel, setMetasSel] = useState(saved.metasSel || []);
  const [outraMeta, setOutraMeta] = useState(saved.outraMeta || '');

  // Passo 2 — estado do plano apresentado
  const [planoAtual, setPlanoAtual] = useState(saved.planoAtual || null);
  const [editando, setEditando]     = useState(false);   // edição não persiste
  const [planoBackup, setPlanoBackup] = useState(null);  // snapshot p/ "Cancelar"
  const [validado, setValidado]     = useState(saved.validado || false);

  // Salva no localStorage sempre que algo persistível muda.
  // Editando + backup ficam fora — são estado transitório de UI.
  React.useEffect(() => {
    if (!empresa?.id) return;
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({
        passo, metasSel, outraMeta, planoAtual, validado,
      }));
    } catch {}
  }, [STORAGE_KEY, empresa?.id, passo, metasSel, outraMeta, planoAtual, validado]);

  // ── Helpers de formatação ──────────────────────────────────────────
  // valores em milhares (R$ k) — só números no grid para reduzir ruído visual
  const fmtK = (v) => v == null ? '—' : Number(v).toLocaleString('pt-BR', { maximumFractionDigits: 0 });
  // valores em reais cheios pros KPIs (formato bonito)
  const fmtBR = (v) => v == null ? '—'
    : 'R$ ' + Number(v).toLocaleString('pt-BR',
        { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  // ── Seleção de metas no Passo 1 ────────────────────────────────────
  const toggleMeta = (id) => {
    setMetasSel(prev => {
      if (prev.includes(id)) return prev.filter(x => x !== id);
      if (prev.length >= 2) return prev;   // cap 2
      return [...prev, id];
    });
  };
  const totalMetasSelecionadas = metasSel.length + (outraMeta.trim() ? 1 : 0);
  const metasOk = totalMetasSelecionadas > 0 && totalMetasSelecionadas <= 2;

  // Loading + erro do RUNGOAL AIDVISOR (chamada IA real)
  const [gerando, setGerando] = useState(false);
  const [erroAdvisor, setErroAdvisor] = useState(null);

  // ── BASE DE ANÁLISE sugerida pela RGAI (31/07) ──────────────────────
  // Horizonte da projeção (12 meses à frente) e janela da base (o passado)
  // são eixos INDEPENDENTES. Fabio: *"Os dados do passado não precisam ter
  // obrigatoriamente 12 meses também... não deve ter limite de prazo máximo.
  // Quanto mais dados, melhor será a análise."* E: *"RGAI deve sugerir um
  // período adequado para a tese escolhida."*
  //
  // O campo nasce PREENCHIDO com a sugestão e o motivo — a IA é o consultor,
  // nenhum campo nasce vazio. O consultor encurta se quiser.
  const [base, setBase] = useState(null);          // resposta do backend
  const [mesesBase, setMesesBase] = useState(null); // escolha do consultor
  const chaveMetas = metasSel.map(id => METAS_PLANO.find(m => m.id === id)?.nome)
                             .filter(Boolean).join('|');
  useEffect(() => {
    if (!empresa?.id) return;
    let vivo = true;
    (async () => {
      try {
        const url = new URL(`${API}/planos/${empresa.id}/base-sugerida`);
        url.searchParams.set('metas', chaveMetas);
        const r = await fetch(url);
        if (!r.ok) { if (vivo) setBase(null); return; }
        const d = await r.json();
        if (!vivo) return;
        setBase(d);
        setMesesBase(d.sugerido);   // a sugestão vira o valor do campo
      } catch (_) { if (vivo) setBase(null); }
    })();
    return () => { vivo = false; };
  }, [empresa?.id, chaveMetas]);

  // Prazos oferecidos: os múltiplos de ciclo que cabem no histórico, mais o
  // histórico inteiro e a própria sugestão. Chips em vez de campo numérico —
  // o valor vira janela de leitura, e chip não admite valor fora da faixa.
  const opcoesBase = (() => {
    if (!base?.suficiente) return [];
    const disp = base.disponiveis;
    const brutos = [12, 18, 24, 36, 48, 60].filter(n => n <= disp);
    return [...new Set([...brutos, disp, base.sugerido])]
      .filter(n => n >= base.minimo && n <= disp).sort((a, b) => a - b);
  })();

  // 'AAAA-MM' -> 'jan/24' (mesmo formato usado no seletor de período)
  const rotuloMesBase = rotuloMesCurto;   // SSOT no escopo global (01/09)

  // Janela que o consultor escolheu: conta para trás a partir do último mês.
  const janelaBase = (() => {
    if (!base?.periodo_todo) return { de: '', ate: '' };
    const ate = base.periodo_todo.ate;
    const [a, m] = String(ate).split('-').map(Number);
    const recuo = (mesesBase || base.sugerido || base.disponiveis) - 1;
    const total = (a * 12 + (m - 1)) - recuo;
    const de = `${Math.floor(total / 12)}-${String((total % 12) + 1).padStart(2, '0')}`;
    return { de, ate };
  })();

  // ── Meta QUANTIFICADA + banda medida (31/08): o empresário diz o QUANTO;
  // a banda (piso demonstrado · teto dos picos) diz o caminho; acima do teto
  // a fonte do capex é escolha obrigatória dele.
  const [bandaOrg, setBandaOrg] = useState(null);
  const [metaReceita, setMetaReceita] = useState('');   // % digitado ("15")
  const [metaDespesa, setMetaDespesa] = useState('');   // % de redução, digitado positivo
  const [metaCusto, setMetaCusto] = useState('');       // % de redução da fração custo/receita
  const [metaCaixa, setMetaCaixa] = useState('');       // % de aumento da geração de caixa
  const [metaTicket, setMetaTicket] = useState('');     // % de aumento do ticket médio
  const [metaConc, setMetaConc] = useState('');         // % de redução da participação do maior cliente
  const [metaRet, setMetaRet] = useState('');           // % ALVO de retenção do lucro
  const [fonteCapex, setFonteCapex] = useState(null);
  useEffect(() => {
    if (!empresa?.id) { setBandaOrg(null); return; }
    let vivo = true;
    (async () => {
      try {
        const r = await fetch(`${API}/planos/${empresa.id}/limite-sem-capex`);
        const d = r.ok ? await r.json() : null;
        if (vivo) setBandaOrg(d);
      } catch (_) { if (vivo) setBandaOrg(null); }
    })();
    return () => { vivo = false; };
  }, [empresa?.id]);
  const temMetaReceita = metasSel.includes('receita');
  const temMetaDespesa = metasSel.includes('despesa_fixa');
  // Teto POR META, alinhado ao backend (re-auditoria 01/09): despesa e
  // custo 90 · concentração 95 · retenção 100 · receita e ticket 300 ·
  // caixa 500. Fora da faixa o campo fica laranja e o Gerar trava.
  const pctDigitado = (s, teto = 300) => {
    const v = parseFloat(String(s).replace(',', '.'));
    if (!Number.isFinite(v) || v <= 0 || v > teto) return null;
    return v / 100;
  };
  const metaRecFrac = pctDigitado(metaReceita, 300);
  // Crescimento EFETIVO da receita: volume e preço COMPÕEM — +12% de volume
  // com +10% de preço entrega +23,2%, não +22%. Serve para MOSTRAR ao
  // empresário e para o backend cobrar a curva da RGAI; NÃO classifica regime.
  const crescEfetivo = (() => {
    const v = temMetaReceita ? metaRecFrac : null;
    const p = metasSel.includes('ticket_medio') ? pctDigitado(metaTicket, 300) : null;
    if (v != null && p != null) return (1 + v) * (1 + p) - 1;
    return v != null ? v : p;
  })();
  // CAPEX é capacidade física, e quem a consome é o VOLUME. Preço não pede
  // máquina: o ticket tem banda própria (regimeTicket, que no topo dá
  // reestruturação). Espelha `planos.py` (auditoria 02/09).
  const regimeMeta = (temMetaReceita && metaRecFrac != null)
    ? (bandaOrg?.disponivel
        ? (metaRecFrac <= bandaOrg.piso_pct ? 'ritmo'
          : metaRecFrac <= bandaOrg.teto_pct ? 'eficiencia' : 'capex')
        : 'sem_banda')
    : null;
  // Banda da DESPESA FIXA (31/08): mesma escada, trocando capex por
  // reestruturação — cortar além do enxugamento provado é mudar a estrutura.
  const bandaFixa = bandaOrg?.despesa_fixa;
  const metaDespFrac = pctDigitado(metaDespesa, 90);
  const regimeDespesa = (temMetaDespesa && metaDespFrac != null)
    ? (bandaFixa?.disponivel
        ? (metaDespFrac <= bandaFixa.piso_reducao_pct ? 'ritmo'
          : metaDespFrac <= bandaFixa.teto_reducao_pct ? 'eficiencia' : 'reestruturacao')
        : 'sem_banda')
    : null;
  // As outras duas metas, com a mesma escada (31/08): custo = fração custo/
  // receita · caixa = geração anual, teto pelo melhor semestre consecutivo.
  const temMetaCusto = metasSel.includes('custo_producao');
  const temMetaCaixa = metasSel.includes('caixa_operacional');
  const bandaCusto = bandaOrg?.custo_producao;
  const bandaCaixa = bandaOrg?.caixa_operacional;
  const metaCustoFrac = pctDigitado(metaCusto, 90);
  const metaCaixaFrac = pctDigitado(metaCaixa, 500);
  const regimeCusto = (temMetaCusto && metaCustoFrac != null)
    ? (bandaCusto?.disponivel
        ? (metaCustoFrac <= bandaCusto.piso_reducao_pct ? 'ritmo'
          : metaCustoFrac <= bandaCusto.teto_reducao_pct ? 'eficiencia' : 'reestruturacao')
        : 'sem_banda')
    : null;
  const regimeCaixa = (temMetaCaixa && metaCaixaFrac != null)
    ? (bandaCaixa?.disponivel
        ? (metaCaixaFrac <= bandaCaixa.piso_pct ? 'ritmo'
          : metaCaixaFrac <= bandaCaixa.teto_pct ? 'eficiencia' : 'reestruturacao')
        : 'sem_banda')
    : null;
  // As três aprovadas em 31/08: ticket (aumento) · concentração (redução
  // relativa) · retenção (alvo absoluto — acima do atual é decisão societária).
  const temMetaTicket = metasSel.includes('ticket_medio');
  const temMetaConc = metasSel.includes('concentracao_clientes');
  const temMetaRet = metasSel.includes('retencao_lucro');
  const bandaTicket = bandaOrg?.ticket_medio;
  const bandaConc = bandaOrg?.concentracao_clientes;
  const bandaRet = bandaOrg?.retencao_lucro;
  const metaTicketFrac = pctDigitado(metaTicket, 300);
  const metaConcFrac = pctDigitado(metaConc, 95);
  const metaRetFrac = pctDigitado(metaRet, 100);
  const regimeTicket = (temMetaTicket && metaTicketFrac != null)
    ? (bandaTicket?.disponivel
        ? (metaTicketFrac <= bandaTicket.piso_pct ? 'ritmo'
          : metaTicketFrac <= bandaTicket.teto_pct ? 'eficiencia' : 'reestruturacao')
        : 'sem_banda')
    : null;
  const regimeConc = (temMetaConc && metaConcFrac != null)
    ? (bandaConc?.disponivel
        ? (metaConcFrac <= bandaConc.piso_reducao_pct ? 'ritmo'
          : metaConcFrac <= bandaConc.teto_reducao_pct ? 'eficiencia' : 'reestruturacao')
        : 'sem_banda')
    : null;
  const regimeRet = (temMetaRet && metaRetFrac != null)
    ? (bandaRet?.disponivel
        ? (metaRetFrac <= bandaRet.retencao_atual_pct ? 'ritmo' : 'decisao')
        : 'sem_banda')
    : null;

  // Sem base suficiente o backend recusa com 422 — barra aqui, com o motivo
  // já na tela, em vez de deixar o consultor clicar para levar erro.
  // Meta selecionada sem o "quanto" não gera; acima do teto, sem fonte não gera.
  // Regra de combinação (02/09): ao menos uma meta de resultado.
  const temResultado = metasSel.some(META_EH_RESULTADO);
  const soComplementar = metasSel.length > 0 && !temResultado;
  // Separado do capex para o aviso do rodapé cobrar só o que falta.
  const quantoSemCapexOk = (!temMetaReceita || metaRecFrac != null)
    && (!temMetaDespesa || metaDespFrac != null)
    && (!temMetaCusto || metaCustoFrac != null)
    && (!temMetaCaixa || metaCaixaFrac != null)
    && (!temMetaTicket || metaTicketFrac != null)
    && (!temMetaConc || metaConcFrac != null)
    && (!temMetaRet || metaRetFrac != null);
  const quantoOk = quantoSemCapexOk && (regimeMeta !== 'capex' || !!fonteCapex);
  const podeGerar = metasOk && base?.suficiente !== false && quantoOk && !soComplementar;

  const gerarPlano = async () => {
    const nomesMetas = [
      ...metasSel.map(id => METAS_PLANO.find(m => m.id === id)?.nome).filter(Boolean),
      ...(outraMeta.trim() ? [outraMeta.trim()] : []),
    ];

    setGerando(true);
    setErroAdvisor(null);
    try {
      // Chama o RUNGOAL AIDVISOR (Claude API via backend) — Lacuna #1 ativada
      const res = await fetch(`${API}/planos/${empresa.id}/gerar`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          metas: nomesMetas.filter(m => METAS_PLANO.some(p => p.nome === m)),
          metas_outra: outraMeta.trim() || null,
          meses_base: mesesBase || null,   // omitido = o backend sugere
          // Meta quantificada (31/08): redução digitada positiva vira fração
          // negativa; fonte só viaja quando o regime exige capex.
          meta_receita_pct: temMetaReceita ? metaRecFrac : null,
          meta_despesa_pct: (temMetaDespesa && metaDespFrac != null)
            ? -metaDespFrac : null,
          meta_custo_pct: (temMetaCusto && metaCustoFrac != null)
            ? -metaCustoFrac : null,
          meta_caixa_pct: temMetaCaixa ? metaCaixaFrac : null,
          meta_ticket_pct: temMetaTicket ? metaTicketFrac : null,
          meta_concentracao_pct: (temMetaConc && metaConcFrac != null)
            ? -metaConcFrac : null,
          meta_retencao_pct: temMetaRet ? metaRetFrac : null,
          fonte_capex: regimeMeta === 'capex' ? fonteCapex : null,
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({ detail: 'Erro desconhecido' }));
        throw new Error(err.detail || `Erro ${res.status} ao gerar plano`);
      }
      const plano = await res.json();
      // Advisor já devolve metasNomes — mas garantimos como fallback
      if (!plano.metasNomes) plano.metasNomes = nomesMetas;
      setPlanoAtual(plano);
      setValidado(false);
      setPasso('plano');
    } catch (e) {
      console.error('[gerarPlano]', e);
      setErroAdvisor(e.message || 'Erro ao gerar plano. Tente novamente.');
    } finally {
      setGerando(false);
    }
  };

  // `gerarPlanoMock` removido em 29/07/2026: era código morto (nunca chamado)
  // que injetava um plano inventado na tela. Código morto de dado nativo é
  // armadilha esperando ser religada. O plano vem de /planos/{id}/gerar, que
  // monta o contexto a partir das transações da própria empresa.

  // ── Edição / Cancelar / Recalcular / Validar (Passo 2) ─────────────
  const entrarEdicao = () => {
    setPlanoBackup(JSON.parse(JSON.stringify(planoAtual)));   // snapshot
    setEditando(true);
  };
  const cancelarEdicao = () => {
    setPlanoAtual(planoBackup);
    setPlanoBackup(null);
    setEditando(false);
  };
  const [recalculadoFlag, setRecalculadoFlag] = useState(false);
  const [mostrarPassado, setMostrarPassado] = useState(false);  // passado consolidado oculto por padrão
  const [modalTese, setModalTese] = useState(false);  // popup "Saiba mais" da estratégia
  const [modalAlavancas, setModalAlavancas] = useState(false);  // popup das alavancas
  const [modalCuidados, setModalCuidados]   = useState(false);  // popup dos cuidados
  const recalcular = () => {
    // Mock: regenera narrativa baseada nos novos números editados.
    // Em prod, chama backend → Advisor → recalcula estratégia/alavancas/freios.
    setPlanoAtual(prev => ({
      ...prev,
      estrategia: prev.estrategia + ' [recalculado · placeholder até Lacuna #1]',
    }));
    setRecalculadoFlag(true);
    // Auto-some após 5s — apenas feedback visual transitório
    setTimeout(() => setRecalculadoFlag(false), 5000);
  };
  const editarValor = (grupo, linhaIdx, mIdx, valor) => {
    const n = parseFloat(String(valor).replace(',', '.')) || 0;
    setPlanoAtual(prev => {
      const novo = { ...prev, proj: { ...prev.proj,
        [grupo]: prev.proj[grupo].map((l, i) =>
          i === linhaIdx ? { ...l, m: l.m.map((v, j) => j === mIdx ? n : v) } : l),
      }};
      return novo;
    });
  };
  // Validar CONGELA o plano como orçamento (01/09): o plano vive no navegador
  // e é o aceite que o manda ao banco — daí ele passa a alimentar o Previsto x
  // Real, na janela real de 12 meses (pode atravessar anos civis).
  const [validando, setValidando] = useState(false);
  const [erroValidar, setErroValidar] = useState(null);
  // A janela CONFIRMADA pelo backend — a faixa mostra o que está gravado, não
  // o que a tela recalcularia (auditoria 02/09). Nasce nula a cada sessão: um
  // `validado` salvo de antes não afirma mais que existe orçamento.
  const [janelaGravada, setJanelaGravada] = useState(null);
  const validar = async () => {
    setValidando(true); setErroValidar(null);
    const mesInicio = `${HOJE_REF.getFullYear()}-${String(HOJE_REF.getMonth() + 1).padStart(2, '0')}`;
    try {
      const res = await fetch(`${API}/planos/${empresa.id}/validar?tipo=12m`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ plano: planoAtual, mes_inicio: mesInicio }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || 'Falha ao congelar o plano.');
      setJanelaGravada({ de: String(data?.janela?.de || '').slice(0, 7),
                         ate: String(data?.janela?.ate || '').slice(0, 7),
                         linhas: data?.orcamento_linhas ?? null });
      setValidado(true);
      setEditando(false);
      setPlanoBackup(null);
      if (onValidado) onValidado(planoAtual);
    } catch (e) {
      // Não marca validado: o empresário precisa saber que o orçamento NÃO foi
      // congelado ([[a-tela-nao-pode-mentir-sobre-os-dados]]).
      setErroValidar(e.message || 'Falha ao congelar o plano.');
    } finally {
      setValidando(false);
    }
  };
  const reabrirEdicaoPosValidado = () => {
    setValidado(false);
    entrarEdicao();
  };

  // ── Derivados (calculados a partir da projeção mensal) ─────────────
  // Lucro Líquido (estimado) = Receita Total − Despesa Total (já inclui
  // tributos e despesa financeira no mock). Pra PME no Simples, aproximação
  // aceitável — depreciação tipicamente desprezível.
  const derivados = (proj) => {
    if (!proj) return null;
    // Defensivo: linha sem .m (estrutura antiga ou inválida) é tratada como zeros
    const somaM = (arr) => Array.from({length: 12}, (_, m) =>
      arr.reduce((s, l) => s + (Array.isArray(l.m) ? (l.m[m] || 0) : 0), 0));
    const recM  = somaM(proj.receitas || []);
    const desM  = somaM(proj.despesas || []);
    const lucroM = recM.map((r, i) => r - desM[i]);
    const totalAno = (a) => a.reduce((x, y) => x + y, 0);
    return {
      receitas_m: recM,
      despesas_m: desM,
      lucro_m:    lucroM,
      receitas_ano: totalAno(recM),
      despesas_ano: totalAno(desM),
      lucro_ano:    totalAno(lucroM),
    };
  };

  // ── Passado REAL + calendário do Plano (31/08) ─────────────────────
  // Regra do Fabio: o Plano começa LOGO APÓS o último mês com registro de
  // dados da empresa (MORED: dez/2025 → o Plano abre em jan/2026). O
  // histórico consolidado vem do MESMO resumo mensal das outras telas; o
  // mock determinístico (HOJE_REF fixo em jun/2026 + hash) saiu.
  const [resumoReal, setResumoReal] = useState(null);
  useEffect(() => {
    if (!empresa?.id) { setResumoReal(null); return; }
    let vivo = true;
    (async () => {
      try {
        const r = await fetch(`${API}/indicadores/resumo-mensal/${empresa.id}`);
        const d = r.ok ? await r.json() : [];
        if (vivo) setResumoReal(Array.isArray(d) ? d : []);
      } catch (_) { if (vivo) setResumoReal([]); }
    })();
    return () => { vivo = false; };
  }, [empresa?.id]);
  const mesesReais = (resumoReal || []).map((r) => r.mes).filter(Boolean).sort();
  // "AAAA-MM" do último mês + slice(5,7) SEM o −1 = o mês seguinte, direto
  // (o Date estoura dez→jan sozinho). Sem movimento algum, parte do mês
  // corrente — base zerada.
  const HOJE_REF = mesesReais.length
    ? new Date(parseInt(mesesReais[mesesReais.length - 1].slice(0, 4), 10),
               parseInt(mesesReais[mesesReais.length - 1].slice(5, 7), 10), 1)
    : new Date(new Date().getFullYear(), new Date().getMonth(), 1);
  const mesesPassado = mesesReais.map((m) =>
    new Date(parseInt(m.slice(0, 4), 10), parseInt(m.slice(5, 7), 10) - 1, 1));
  const mesesPlano = Array.from({length: 12}, (_, i) =>
    new Date(HOJE_REF.getFullYear(), HOJE_REF.getMonth() + i, 1));
  // Consolidado real da linha no mês, em R$ mil (a grade fala em k). Linha do
  // plano sem coluna correspondente no resumo mostra 0 no passado.
  const CAMPO_RESUMO = {
    'Receita Operacional':      'receita_operacional',
    'Receita Financeira':       'receita_financeira',
    'Despesa Operacional':      'despesa_operacional',
    'Despesa Comercial':        'despesa_comercial',
    'Despesa Administrativa':   'despesa_administrativa',
    'Despesa Financeira':       'despesa_financeira',
    'Tributos':                 'tributos',
  };
  const valorPassado = (cat, data) => {
    const chave = `${data.getFullYear()}-${String(data.getMonth() + 1).padStart(2, '0')}`;
    const row = (resumoReal || []).find((r) => r.mes === chave);
    const campo = CAMPO_RESUMO[cat];
    if (!row || !campo) return 0;
    return Math.abs(Number(row[campo]) || 0) / 1000;
  };

  // ── Componentes visuais comuns ─────────────────────────────────────
  const HeaderTela = ({ subtitulo }) => (
    <div style={{ marginBottom: 24, position: 'relative' }}>
      <span style={{
        position: 'absolute', right: 0, top: 0,
        fontFamily: "Impact, 'Arial Narrow Bold', 'Helvetica Neue', sans-serif",
        fontSize: 40, fontWeight: 900, textTransform: 'uppercase',
        letterSpacing: '0.02em', color: G.verde, opacity: 0.13,
        lineHeight: 1, pointerEvents: 'none', userSelect: 'none',
      }}>12M</span>
      <div style={{ position: 'relative', display: 'inline-block' }}>
        <h2 style={{ margin: 0, fontSize: 20, fontWeight: 800, color: G.txt1,
          position: 'relative', zIndex: 1 }}>Planejamento 12M</h2>
        <MarcaDaguaTitulo>
          <circle cx="12" cy="12" r="10"/>
          <circle cx="12" cy="12" r="6"/>
          <circle cx="12" cy="12" r="2"/>
        </MarcaDaguaTitulo>
      </div>
      <p style={{ ...TIPO.subtitulo, margin: '4px 0 0' }}>
        {subtitulo}
      </p>
    </div>
  );

  // ════════════════════════════════════════════════════════════════════
  // PASSO 1 — escolher metas
  // ════════════════════════════════════════════════════════════════════
  if (passo === 'meta') {
    return (
      <div style={{ padding: '24px 28px', position: 'relative', overflow: 'hidden' }}>
        <CarimboAIdvisor />
        <HeaderTela subtitulo={<>escolha até 2 metas de curto prazo · o <MarcaAIdvisor variant="default" /> vai gerar o plano</>} />

        <div style={{ maxWidth: 980 }}>

          {/* O bloco "Como funciona" que existia aqui virou os 2 quadros do
              padrão (30/07). Correção junto: "pros próximos" → "para os
              próximos" (memória `linguagem-formal-rungoal`). */}
          <QuadrosIntro
            titulo="o Planejamento 12M"
            oQueE={<>O plano de <strong>doze meses</strong> da empresa: metas,
              projeção trimestral, alavancas de crescimento e pontos de atenção
              — construído a partir do histórico já lançado.</>}
            oQueFaz={<>Selecione <strong>até 2 metas</strong> que melhor
              representam suas prioridades para os próximos 12 meses. A{' '}
              <MarcaAIdvisor variant="default"/> gera o plano; você edita os
              números e revalida quantas vezes precisar.</>}
          />

          {/* ── Parecer do conselheiro (31/08): determinístico, das bandas
              medidas — aponta onde a meta mais pede atenção; a escolha é do
              empresário. */}
          {bandaOrg?.conselheiro?.parecer && (
            <div style={{ background: G.laranjaLight,
              borderLeft: `4px solid ${G.laranja}`, borderRadius: 0,
              padding: '14px 18px', marginBottom: 14 }}>
              <div style={{ fontSize: 12, fontWeight: 800, color: G.laranja,
                letterSpacing: '0.08em', textTransform: 'uppercase',
                marginBottom: 6, display: 'flex', alignItems: 'center',
                gap: 10, flexWrap: 'wrap' }}>
                O conselheiro leu os seus números
                <MarcaAIdvisor variant="default" />
              </div>
              <div style={{ fontSize: 13, lineHeight: 1.55, color: G.txt1 }}>
                {bandaOrg.conselheiro.parecer}{' '}
                <strong>A escolha é sua</strong> — as recomendadas estão marcadas
                em laranja abaixo.
              </div>
            </div>
          )}

          {/* Grid de metas em DOIS grupos (02/09): resultado × complementar */}
          <div style={{ fontSize: 11, fontWeight: 800, color: G.verde,
            letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 8 }}>
            Metas de resultado
            <span style={{ fontWeight: 500, textTransform: 'none', letterSpacing: 0,
              color: G.txt2, marginLeft: 8 }}>
              · movem o lucro e o valor da empresa — escolha ao menos uma
            </span>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
            {METAS_PLANO.filter(m => m.grupo === 'resultado').map(m => {
              const sel = metasSel.includes(m.id);
              const desabilitado = !sel && totalMetasSelecionadas >= 2;
              const rec = bandaOrg?.conselheiro?.recomendadas?.find(r => r.id === m.id);
              const na = bandaOrg?.conselheiro?.nao_aplicaveis?.find(r => r.id === m.id);
              return (
                <div key={m.id}
                  onClick={() => !desabilitado && toggleMeta(m.id)}
                  style={{
                    background: sel ? G.verdeLight : G.card,
                    border: sel ? `2px solid ${G.verde}`
                      : rec ? `2px solid ${G.laranja}` : `1px solid ${G.borda}`,
                    boxShadow: rec && !sel ? `0 2px 10px ${G.laranja}33` : 'none',
                    borderRadius: 0, padding: '16px 18px',
                    cursor: desabilitado ? 'not-allowed' : 'pointer',
                    opacity: desabilitado ? 0.5 : (na && !sel ? 0.65 : 1),
                    transition: 'all 0.15s ease',
                    position: 'relative',
                  }}>
                  {/* Círculo de seleção SEMPRE visível: vazio = "clique aqui",
                      preenchido = escolhida (o card branco puro não dizia que
                      era clicável — Fabio, 31/08). */}
                  <span style={{ position: 'absolute', top: 12, right: 12,
                    width: 22, height: 22, borderRadius: '50%',
                    background: sel ? G.verde : '#FFF',
                    border: sel ? 'none' : `2px solid ${G.borda}`,
                    color: '#fff', display: 'flex', alignItems: 'center',
                    justifyContent: 'center', fontSize: 13, fontWeight: 700,
                    boxSizing: 'border-box' }}>{sel ? '✓' : ''}</span>
                  {rec && !sel && (
                    <span style={{ position: 'absolute', top: -10, left: 14,
                      background: G.laranja, color: '#FFF', fontSize: 10,
                      fontWeight: 800, letterSpacing: '0.08em',
                      padding: '3px 10px', borderRadius: 0 }}>
                      RGAI RECOMENDA
                    </span>
                  )}
                  <div style={{ fontSize: 14, fontWeight: 800, color: sel ? G.verde : G.txt1,
                    marginBottom: 4, paddingRight: 24 }}>
                    {m.nome}
                  </div>
                  <div style={{ fontSize: 12, color: G.txt2, lineHeight: 1.45 }}>
                    {m.desc}
                  </div>
                  {rec && (
                    <div style={{ fontSize: 11, color: '#B45309', marginTop: 8,
                      lineHeight: 1.4, fontWeight: 600 }}>
                      {rec.motivo}
                    </div>
                  )}
                  {na && (
                    <div title={na.motivo} style={{ marginTop: 8,
                      display: 'inline-block', padding: '3px 8px', borderRadius: 0,
                      fontSize: 10, fontWeight: 700, background: G.fundo,
                      color: G.txt2, border: `1px solid ${G.borda}` }}>
                      SEM BASE NO SEU PERFIL
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Grupo 2 · complementares: valem por outro caminho, e cada card
              declara qual. Nunca sozinhas (a trava vive em `podeGerar`). */}
          <div style={{ fontSize: 11, fontWeight: 800, color: G.laranja,
            letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 8 }}>
            Metas complementares
            <span style={{ fontWeight: 500, textTransform: 'none', letterSpacing: 0,
              color: G.txt2, marginLeft: 8 }}>
              · acompanham uma meta de resultado, nunca sozinhas
            </span>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
            {METAS_PLANO.filter(m => m.grupo === 'complementar').map(m => {
              const sel = metasSel.includes(m.id);
              const desabilitado = !sel && totalMetasSelecionadas >= 2;
              const rec = bandaOrg?.conselheiro?.recomendadas?.find(r => r.id === m.id);
              const na = bandaOrg?.conselheiro?.nao_aplicaveis?.find(r => r.id === m.id);
              return (
                <div key={m.id}
                  onClick={() => !desabilitado && toggleMeta(m.id)}
                  style={{
                    // Selecionada é VERDE nos DOIS grids; laranja fica só para
                    // "recomendada" — antes os dois estados eram laranja aqui e
                    // não dava para distinguir (auditoria 02/09).
                    background: sel ? G.verdeLight : G.card,
                    border: sel ? `2px solid ${G.verde}`
                      : rec ? `2px solid ${G.laranja}` : `1px solid ${G.borda}`,
                    boxShadow: rec && !sel ? `0 2px 10px ${G.laranja}33` : 'none',
                    borderRadius: 0, padding: '16px 18px',
                    cursor: desabilitado ? 'not-allowed' : 'pointer',
                    opacity: desabilitado ? 0.5 : (na && !sel ? 0.65 : 1),
                    transition: 'all 0.15s ease', position: 'relative',
                  }}>
                  <span style={{ position: 'absolute', top: 12, right: 12,
                    width: 22, height: 22, borderRadius: '50%',
                    background: sel ? G.verde : '#FFF',
                    border: sel ? 'none' : `2px solid ${G.borda}`,
                    color: '#fff', display: 'flex', alignItems: 'center',
                    justifyContent: 'center', fontSize: 13, fontWeight: 700,
                    boxSizing: 'border-box' }}>{sel ? '✓' : ''}</span>
                  {rec && !sel && (
                    <span style={{ position: 'absolute', top: -10, left: 14,
                      background: G.laranja, color: '#FFF', fontSize: 10,
                      fontWeight: 800, letterSpacing: '0.08em',
                      padding: '3px 10px', borderRadius: 0 }}>
                      RGAI RECOMENDA
                    </span>
                  )}
                  <div style={{ fontSize: 14, fontWeight: 800,
                    color: sel ? G.verde : G.txt1, marginBottom: 4, paddingRight: 24 }}>
                    {m.nome}
                  </div>
                  <div style={{ fontSize: 12, color: G.txt2, lineHeight: 1.45 }}>
                    {m.desc}
                  </div>
                  {/* O efeito DECLARADO: onde essa meta aparece de verdade */}
                  <div style={{ fontSize: 11, color: G.txt2, marginTop: 6,
                    fontStyle: 'italic', lineHeight: 1.4 }}>
                    {m.efeito}
                  </div>
                  {rec && (
                    <div style={{ fontSize: 11, color: '#B45309', marginTop: 6,
                      lineHeight: 1.4, fontWeight: 600 }}>
                      {rec.motivo}
                    </div>
                  )}
                  {na && (
                    <div title={na.motivo} style={{ marginTop: 8,
                      display: 'inline-block', padding: '3px 8px', borderRadius: 0,
                      fontSize: 10, fontWeight: 700, background: G.fundo,
                      color: G.txt2, border: `1px solid ${G.borda}` }}>
                      SEM BASE NO SEU PERFIL
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {soComplementar && (
            <div style={{ padding: '12px 18px', background: '#FFFBEB',
              borderLeft: '3px solid #B45309', borderRadius: 0, marginBottom: 12,
              fontSize: 12.5, color: '#B45309', lineHeight: 1.5 }}>
              <strong>Falta uma meta de resultado.</strong> Metas complementares
              melhoram risco ou caixa, mas não movem o lucro sozinhas — escolha
              também vender mais, reduzir o custo por unidade ou reduzir a
              estrutura fixa.
            </div>
          )}

          {/* ── Quanto? — a meta vira número sobre a banda MEDIDA (31/08).
              Até o piso = ritmo · piso→teto = eficiência sem capex · acima
              do teto = capex, e a fonte é escolha do empresário. */}
          {(temMetaReceita || temMetaDespesa || temMetaCusto || temMetaCaixa
            || temMetaTicket || temMetaConc || temMetaRet) && (
            <div style={{ background: G.card, border: `1px solid ${G.borda}`,
              borderRadius: 0, padding: '14px 18px', marginBottom: 12 }}>
              <div style={{ fontSize: 11, color: G.txt2, fontWeight: 700,
                textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 10 }}>
                Quanto? <span style={{ fontWeight: 500, textTransform: 'none',
                  letterSpacing: 0 }}>· banda medida do seu histórico pela{' '}
                  <MarcaAIdvisor variant="default"/></span>
              </div>

              {temMetaReceita && (
                <>
                  {bandaOrg?.disponivel ? (
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 10 }}>
                      <span title={bandaOrg.metodo_piso}
                        style={{ padding: '4px 10px', borderRadius: 0, fontSize: 11,
                          fontWeight: 700, background: G.verdeLight, color: G.verde }}>
                        PISO +{(bandaOrg.piso_pct * 100).toFixed(1).replace('.', ',')}% · ritmo demonstrado
                      </span>
                      <span title={bandaOrg.metodo_teto}
                        style={{ padding: '4px 10px', borderRadius: 0, fontSize: 11,
                          fontWeight: 700, background: G.laranjaLight, color: G.laranja }}>
                        TETO +{(bandaOrg.teto_pct * 100).toFixed(1).replace('.', ',')}% · capacidade provada
                      </span>
                    </div>
                  ) : (
                    <div style={{ fontSize: 12, color: '#B45309', marginBottom: 10 }}>
                      {bandaOrg?.motivo || 'Banda indisponível — a meta segue livre e o laudo declara a limitação.'}
                    </div>
                  )}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8,
                    fontSize: 13, color: G.txt1, marginBottom: 8, flexWrap: 'wrap' }}>
                    Crescer a receita em
                    <input inputMode="decimal" value={metaReceita} placeholder="informe"
                      onChange={e => setMetaReceita(e.target.value.replace(/[^0-9.,]/g, ''))}
                      aria-label="Meta de crescimento da receita em 12 meses, em porcentagem"
                      style={{ width: 86, padding: '8px 10px', borderRadius: 0,
                        border: `1px solid ${metaRecFrac != null ? G.borda : G.laranja}`,
                        fontSize: 13, fontFamily: 'inherit', textAlign: 'right' }}/>
                    <strong>%</strong> em 12 meses
                  </div>
                  {regimeMeta === 'ritmo' && (
                    <div style={{ fontSize: 12, color: G.verde, fontWeight: 600 }}>
                      Dentro do ritmo demonstrado — plano de manutenção do ritmo, sem capex.
                    </div>
                  )}
                  {regimeMeta === 'eficiencia' && (
                    <div style={{ fontSize: 12, color: G.laranja, fontWeight: 600 }}>
                      Entre o piso e o teto — plano de eficiência e produtividade sobre a
                      estrutura atual, sem capex.
                    </div>
                  )}
                  {regimeMeta === 'sem_banda' && (
                    <div style={{ fontSize: 12, color: G.txt2 }}>
                      Sem banda medida — o plano sai e o laudo declara a limitação.
                    </div>
                  )}
                  {regimeMeta === 'capex' && (
                    <div style={{ padding: '10px 12px', background: '#FFFBEB',
                      borderLeft: '3px solid #B45309', borderRadius: 0 }}>
                      <div style={{ fontSize: 12, color: '#B45309', fontWeight: 700,
                        marginBottom: 8 }}>
                        Acima do teto orgânico — o plano exige capex. De onde ele vem?
                      </div>
                      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                        {[['capital_proprio', 'Capital próprio'],
                          ['reinvestimento_dividendos', 'Reinvestir dividendos'],
                          ['emprestimo', 'Empréstimo'],
                          ['equity', 'Equity']].map(([id, rot]) => {
                          const on = fonteCapex === id;
                          return (
                            <button key={id} type="button" onClick={() => setFonteCapex(id)}
                              style={{ padding: '8px 14px', fontSize: 12, fontWeight: 700,
                                background: on ? G.verde : '#FFF',
                                color: on ? '#FFF' : G.txt1,
                                border: `1px solid ${on ? G.verde : G.borda}`,
                                borderRadius: 0, cursor: 'pointer',
                                fontFamily: "'Outfit', sans-serif" }}>
                              {on ? '✓ ' : ''}{rot}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </>
              )}

              {temMetaDespesa && (
                <div style={{ marginTop: temMetaReceita ? 12 : 0 }}>
                  {bandaFixa?.disponivel ? (
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 10 }}>
                      <span title={bandaFixa.metodo_piso}
                        style={{ padding: '4px 10px', borderRadius: 0, fontSize: 11,
                          fontWeight: 700, background: G.verdeLight, color: G.verde }}>
                        PISO −{(bandaFixa.piso_reducao_pct * 100).toFixed(1).replace('.', ',')}% · redução demonstrada
                      </span>
                      <span title={bandaFixa.metodo_teto}
                        style={{ padding: '4px 10px', borderRadius: 0, fontSize: 11,
                          fontWeight: 700, background: G.laranjaLight, color: G.laranja }}>
                        TETO −{(bandaFixa.teto_reducao_pct * 100).toFixed(1).replace('.', ',')}% · enxugamento provado
                      </span>
                    </div>
                  ) : (
                    <div style={{ fontSize: 12, color: '#B45309', marginBottom: 10 }}>
                      {bandaFixa?.motivo || 'Banda da despesa fixa indisponível — meta livre, o laudo declara a limitação.'}
                    </div>
                  )}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8,
                    fontSize: 13, color: G.txt1, flexWrap: 'wrap' }}>
                    Reduzir a despesa fixa em
                    <input inputMode="decimal" value={metaDespesa} placeholder="informe"
                      onChange={e => setMetaDespesa(e.target.value.replace(/[^0-9.,]/g, ''))}
                      aria-label="Meta de redução da despesa fixa em 12 meses, em porcentagem"
                      style={{ width: 86, padding: '8px 10px', borderRadius: 0,
                        border: `1px solid ${metaDespFrac != null ? G.borda : G.laranja}`,
                        fontSize: 13, fontFamily: 'inherit', textAlign: 'right' }}/>
                    <strong>%</strong> em 12 meses
                  </div>
                  {regimeDespesa === 'ritmo' && (
                    <div style={{ fontSize: 12, color: G.verde, fontWeight: 600, marginTop: 6 }}>
                      Dentro da redução já demonstrada — manter o ritmo.
                    </div>
                  )}
                  {regimeDespesa === 'eficiencia' && (
                    <div style={{ fontSize: 12, color: G.laranja, fontWeight: 600, marginTop: 6 }}>
                      Dentro do enxugamento provado — eficiência sobre a estrutura atual.
                    </div>
                  )}
                  {regimeDespesa === 'reestruturacao' && (
                    <div style={{ fontSize: 12, color: '#B45309', fontWeight: 600, marginTop: 6 }}>
                      Acima do enxugamento provado — exige reestruturação (contratos,
                      quadro, estrutura); o laudo vai detalhar.
                    </div>
                  )}
                  {regimeDespesa === 'sem_banda' && (
                    <div style={{ fontSize: 12, color: G.txt2, marginTop: 6 }}>
                      Sem banda medida — o plano sai e o laudo declara a limitação.
                    </div>
                  )}
                </div>
              )}

              {temMetaCusto && (
                <div style={{ marginTop: (temMetaReceita || temMetaDespesa) ? 12 : 0 }}>
                  {bandaCusto?.disponivel ? (
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 10 }}>
                      <span title={bandaCusto.metodo_piso}
                        style={{ padding: '4px 10px', borderRadius: 0, fontSize: 11,
                          fontWeight: 700, background: G.verdeLight, color: G.verde }}>
                        PISO −{(bandaCusto.piso_reducao_pct * 100).toFixed(1).replace('.', ',')}% · melhora demonstrada
                      </span>
                      <span title={bandaCusto.metodo_teto}
                        style={{ padding: '4px 10px', borderRadius: 0, fontSize: 11,
                          fontWeight: 700, background: G.laranjaLight, color: G.laranja }}>
                        TETO −{(bandaCusto.teto_reducao_pct * 100).toFixed(1).replace('.', ',')}% · margem provada
                      </span>
                    </div>
                  ) : (
                    <div style={{ fontSize: 12, color: '#B45309', marginBottom: 10 }}>
                      {bandaCusto?.motivo || 'Banda do custo indisponível — meta livre, o laudo declara a limitação.'}
                    </div>
                  )}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8,
                    fontSize: 13, color: G.txt1, flexWrap: 'wrap' }}>
                    Reduzir o custo de produção em
                    <input inputMode="decimal" value={metaCusto} placeholder="informe"
                      onChange={e => setMetaCusto(e.target.value.replace(/[^0-9.,]/g, ''))}
                      aria-label="Meta de redução da fração custo sobre receita em 12 meses, em porcentagem"
                      style={{ width: 86, padding: '8px 10px', borderRadius: 0,
                        border: `1px solid ${metaCustoFrac != null ? G.borda : G.laranja}`,
                        fontSize: 13, fontFamily: 'inherit', textAlign: 'right' }}/>
                    <strong>%</strong> por real vendido, em 12 meses
                  </div>
                  {regimeCusto === 'ritmo' && (
                    <div style={{ fontSize: 12, color: G.verde, fontWeight: 600, marginTop: 6 }}>
                      Dentro da melhora já demonstrada — manter o ritmo.
                    </div>
                  )}
                  {regimeCusto === 'eficiencia' && (
                    <div style={{ fontSize: 12, color: G.laranja, fontWeight: 600, marginTop: 6 }}>
                      Dentro da margem provada — eficiência produtiva, sem capex.
                    </div>
                  )}
                  {regimeCusto === 'reestruturacao' && (
                    <div style={{ fontSize: 12, color: '#B45309', fontWeight: 600, marginTop: 6 }}>
                      Acima da margem provada — exige reestruturação produtiva
                      (fornecedor, mix, processo); o laudo vai detalhar.
                    </div>
                  )}
                  {regimeCusto === 'sem_banda' && (
                    <div style={{ fontSize: 12, color: G.txt2, marginTop: 6 }}>
                      Sem banda medida — o plano sai e o laudo declara a limitação.
                    </div>
                  )}
                </div>
              )}

              {temMetaCaixa && (
                <div style={{ marginTop: (temMetaReceita || temMetaDespesa || temMetaCusto) ? 12 : 0 }}>
                  {bandaCaixa?.disponivel ? (
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 10 }}>
                      <span title={bandaCaixa.metodo_piso}
                        style={{ padding: '4px 10px', borderRadius: 0, fontSize: 11,
                          fontWeight: 700, background: G.verdeLight, color: G.verde }}>
                        PISO +{(bandaCaixa.piso_pct * 100).toFixed(1).replace('.', ',')}% · crescimento demonstrado
                      </span>
                      <span title={bandaCaixa.metodo_teto}
                        style={{ padding: '4px 10px', borderRadius: 0, fontSize: 11,
                          fontWeight: 700, background: G.laranjaLight, color: G.laranja }}>
                        TETO +{(bandaCaixa.teto_pct * 100).toFixed(1).replace('.', ',')}% · geração provada
                      </span>
                    </div>
                  ) : (
                    <div style={{ fontSize: 12, color: '#B45309', marginBottom: 10 }}>
                      {bandaCaixa?.motivo || 'Banda do caixa indisponível — meta livre, o laudo declara a limitação.'}
                    </div>
                  )}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8,
                    fontSize: 13, color: G.txt1, flexWrap: 'wrap' }}>
                    Aumentar o caixa operacional em
                    <input inputMode="decimal" value={metaCaixa} placeholder="informe"
                      onChange={e => setMetaCaixa(e.target.value.replace(/[^0-9.,]/g, ''))}
                      aria-label="Meta de aumento da geração de caixa em 12 meses, em porcentagem"
                      style={{ width: 86, padding: '8px 10px', borderRadius: 0,
                        border: `1px solid ${metaCaixaFrac != null ? G.borda : G.laranja}`,
                        fontSize: 13, fontFamily: 'inherit', textAlign: 'right' }}/>
                    <strong>%</strong> em 12 meses
                  </div>
                  {regimeCaixa === 'ritmo' && (
                    <div style={{ fontSize: 12, color: G.verde, fontWeight: 600, marginTop: 6 }}>
                      Dentro do crescimento demonstrado — manter o ritmo.
                    </div>
                  )}
                  {regimeCaixa === 'eficiencia' && (
                    <div style={{ fontSize: 12, color: G.laranja, fontWeight: 600, marginTop: 6 }}>
                      Dentro da geração provada — prazos, cobrança e giro, sem capex.
                    </div>
                  )}
                  {regimeCaixa === 'reestruturacao' && (
                    <div style={{ fontSize: 12, color: '#B45309', fontWeight: 600, marginTop: 6 }}>
                      Acima da geração provada — exige reestruturar prazos, capital de
                      giro e política de distribuição; o laudo vai detalhar.
                    </div>
                  )}
                  {regimeCaixa === 'sem_banda' && (
                    <div style={{ fontSize: 12, color: G.txt2, marginTop: 6 }}>
                      Sem banda medida — o plano sai e o laudo declara a limitação.
                    </div>
                  )}
                </div>
              )}

              {temMetaTicket && (
                <div style={{ marginTop: (temMetaReceita || temMetaDespesa || temMetaCusto
                  || temMetaCaixa) ? 12 : 0 }}>
                  {bandaTicket?.disponivel ? (
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 10 }}>
                      <span title={bandaTicket.metodo_piso}
                        style={{ padding: '4px 10px', borderRadius: 0, fontSize: 11,
                          fontWeight: 700, background: G.verdeLight, color: G.verde }}>
                        PISO +{(bandaTicket.piso_pct * 100).toFixed(1).replace('.', ',')}% · crescimento demonstrado
                      </span>
                      <span title={bandaTicket.metodo_teto}
                        style={{ padding: '4px 10px', borderRadius: 0, fontSize: 11,
                          fontWeight: 700, background: G.laranjaLight, color: G.laranja }}>
                        TETO +{(bandaTicket.teto_pct * 100).toFixed(1).replace('.', ',')}% · ticket provado
                      </span>
                    </div>
                  ) : (
                    <div style={{ fontSize: 12, color: '#B45309', marginBottom: 10 }}>
                      {bandaTicket?.motivo || 'Banda do ticket indisponível — meta livre, o laudo declara a limitação.'}
                    </div>
                  )}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8,
                    fontSize: 13, color: G.txt1, flexWrap: 'wrap' }}>
                    Aumentar o ticket médio em
                    <input inputMode="decimal" value={metaTicket} placeholder="informe"
                      onChange={e => setMetaTicket(e.target.value.replace(/[^0-9.,]/g, ''))}
                      aria-label="Meta de aumento do ticket médio em 12 meses, em porcentagem"
                      style={{ width: 86, padding: '8px 10px', borderRadius: 0,
                        border: `1px solid ${metaTicketFrac != null ? G.borda : G.laranja}`,
                        fontSize: 13, fontFamily: 'inherit', textAlign: 'right' }}/>
                    <strong>%</strong> em 12 meses
                  </div>
                  {regimeTicket === 'ritmo' && (
                    <div style={{ fontSize: 12, color: G.verde, fontWeight: 600, marginTop: 6 }}>
                      Dentro do crescimento demonstrado — manter o ritmo.
                    </div>
                  )}
                  {regimeTicket === 'eficiencia' && (
                    <div style={{ fontSize: 12, color: G.laranja, fontWeight: 600, marginTop: 6 }}>
                      Dentro do ticket provado — mix, combos e preço, sem capex.
                    </div>
                  )}
                  {regimeTicket === 'reestruturacao' && (
                    <div style={{ fontSize: 12, color: '#B45309', fontWeight: 600, marginTop: 6 }}>
                      Acima do ticket provado — exige reposicionar portfólio e preço;
                      o laudo vai detalhar.
                    </div>
                  )}
                  {regimeTicket === 'sem_banda' && (
                    <div style={{ fontSize: 12, color: G.txt2, marginTop: 6 }}>
                      Sem banda medida — o plano sai e o laudo declara a limitação.
                    </div>
                  )}
                </div>
              )}

              {temMetaConc && (
                <div style={{ marginTop: (temMetaReceita || temMetaDespesa || temMetaCusto
                  || temMetaCaixa || temMetaTicket) ? 12 : 0 }}>
                  {bandaConc?.disponivel ? (
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 10 }}>
                      <span style={{ padding: '4px 10px', borderRadius: 0, fontSize: 11,
                        fontWeight: 700, background: G.fundo, color: G.txt1,
                        border: `1px solid ${G.borda}` }}>
                        MAIOR CLIENTE HOJE: {(bandaConc.participacao_atual * 100).toFixed(1).replace('.', ',')}% da receita
                      </span>
                      <span title={bandaConc.metodo_piso}
                        style={{ padding: '4px 10px', borderRadius: 0, fontSize: 11,
                          fontWeight: 700, background: G.verdeLight, color: G.verde }}>
                        PISO −{(bandaConc.piso_reducao_pct * 100).toFixed(1).replace('.', ',')}% · diluição demonstrada
                      </span>
                      <span title={bandaConc.metodo_teto}
                        style={{ padding: '4px 10px', borderRadius: 0, fontSize: 11,
                          fontWeight: 700, background: G.laranjaLight, color: G.laranja }}>
                        TETO −{(bandaConc.teto_reducao_pct * 100).toFixed(1).replace('.', ',')}% · diluição já operada
                      </span>
                    </div>
                  ) : (
                    <div style={{ fontSize: 12, color: '#B45309', marginBottom: 10 }}>
                      {bandaConc?.motivo || 'Banda indisponível — meta livre, o laudo declara a limitação.'}
                    </div>
                  )}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8,
                    fontSize: 13, color: G.txt1, flexWrap: 'wrap' }}>
                    Reduzir a participação do maior cliente em
                    <input inputMode="decimal" value={metaConc} placeholder="informe"
                      onChange={e => setMetaConc(e.target.value.replace(/[^0-9.,]/g, ''))}
                      aria-label="Meta de redução da participação do maior cliente em 12 meses, em porcentagem"
                      style={{ width: 86, padding: '8px 10px', borderRadius: 0,
                        border: `1px solid ${metaConcFrac != null ? G.borda : G.laranja}`,
                        fontSize: 13, fontFamily: 'inherit', textAlign: 'right' }}/>
                    <strong>%</strong> em 12 meses
                  </div>
                  {regimeConc === 'ritmo' && (
                    <div style={{ fontSize: 12, color: G.verde, fontWeight: 600, marginTop: 6 }}>
                      Dentro da diluição demonstrada — manter o ritmo.
                    </div>
                  )}
                  {regimeConc === 'eficiencia' && (
                    <div style={{ fontSize: 12, color: G.laranja, fontWeight: 600, marginTop: 6 }}>
                      Dentro da diluição já operada — ampliar a base sem perder o grande.
                    </div>
                  )}
                  {regimeConc === 'reestruturacao' && (
                    <div style={{ fontSize: 12, color: '#B45309', fontWeight: 600, marginTop: 6 }}>
                      Acima da diluição já operada — exige reestruturar o comercial
                      (novos canais e segmentos); o laudo vai detalhar.
                    </div>
                  )}
                  {regimeConc === 'sem_banda' && (
                    <div style={{ fontSize: 12, color: G.txt2, marginTop: 6 }}>
                      Sem banda medida — o plano sai e o laudo declara a limitação.
                    </div>
                  )}
                </div>
              )}

              {temMetaRet && (
                <div style={{ marginTop: (temMetaReceita || temMetaDespesa || temMetaCusto
                  || temMetaCaixa || temMetaTicket || temMetaConc) ? 12 : 0 }}>
                  {bandaRet?.disponivel ? (
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 10 }}>
                      <span title={bandaRet.metodo}
                        style={{ padding: '4px 10px', borderRadius: 0, fontSize: 11,
                          fontWeight: 700, background: G.verdeLight, color: G.verde }}>
                        RETENÇÃO ATUAL: {(bandaRet.retencao_atual_pct * 100).toFixed(1).replace('.', ',')}% do lucro
                      </span>
                    </div>
                  ) : (
                    <div style={{ fontSize: 12, color: '#B45309', marginBottom: 10 }}>
                      {bandaRet?.motivo || 'Retenção não medida — meta livre, o laudo declara a limitação.'}
                    </div>
                  )}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8,
                    fontSize: 13, color: G.txt1, flexWrap: 'wrap' }}>
                    Reter
                    <input inputMode="decimal" value={metaRet} placeholder="informe"
                      onChange={e => setMetaRet(e.target.value.replace(/[^0-9.,]/g, ''))}
                      aria-label="Meta de retenção do lucro em 12 meses, em porcentagem do lucro"
                      style={{ width: 86, padding: '8px 10px', borderRadius: 0,
                        border: `1px solid ${metaRetFrac != null ? G.borda : G.laranja}`,
                        fontSize: 13, fontFamily: 'inherit', textAlign: 'right' }}/>
                    <strong>%</strong> do lucro dos próximos 12 meses
                  </div>
                  {regimeRet === 'ritmo' && (
                    <div style={{ fontSize: 12, color: G.verde, fontWeight: 600, marginTop: 6 }}>
                      Já pratica esta retenção — manter.
                    </div>
                  )}
                  {regimeRet === 'decisao' && (
                    <div style={{ fontSize: 12, color: '#B45309', fontWeight: 600, marginTop: 6 }}>
                      Acima da retenção atual — entregável por decisão societária; o
                      laudo mostra o efeito no caixa e no imposto dos dividendos.
                    </div>
                  )}
                  {regimeRet === 'sem_banda' && (
                    <div style={{ fontSize: 12, color: G.txt2, marginTop: 6 }}>
                      Sem lucro positivo medido — o plano sai e o laudo declara a limitação.
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Outra meta (texto livre) */}
          <div style={{ background: G.card, border: `1px solid ${G.borda}`,
            borderRadius: 0, padding: '14px 18px', marginBottom: 16 }}>
            <div style={{ fontSize: 11, color: G.txt2, fontWeight: 700,
              textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 8 }}>
              Outra meta (opcional)
            </div>
            <input
              value={outraMeta}
              onChange={e => setOutraMeta(e.target.value)}
              placeholder="Descreva uma meta específica do seu negócio…"
              disabled={metasSel.length >= 2 && !outraMeta.trim()}
              style={{ width: '100%', padding: '10px 12px',
                border: `1px solid ${G.borda}`, borderRadius: 0,
                fontSize: 13, fontFamily: 'inherit', color: G.txt1,
                background: '#fff', boxSizing: 'border-box' }}/>
            <div style={{ fontSize: 11, color: G.txt2, marginTop: 6 }}>
              Caso queira propor algo fora da lista — o <MarcaAIdvisor variant="default" /> tenta interpretar e gerar o plano.
            </div>
          </div>

          {/* ── Base de análise ────────────────────────────────────────
              Passado e futuro são eixos independentes: a projeção é sempre
              de 12 meses à frente; a base é o histórico que a sustenta.
              Trava de 12 meses no mínimo (um ciclo completo), sem teto —
              quanto mais dados, melhor a análise. A RGAI já sugere o prazo
              adequado à tese; o consultor encurta se quiser. */}
          {base && (
            <div style={{ background: G.card, border: `1px solid ${G.borda}`,
              borderRadius: 0, padding: '14px 18px', marginBottom: 16 }}>
              <div style={{ fontSize: 11, color: G.txt2, fontWeight: 700,
                textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 8 }}>
                Base de análise <span style={{ fontWeight: 500, textTransform: 'none',
                  letterSpacing: 0 }}>· sugerida pela <MarcaAIdvisor variant="default"/></span>
              </div>

              {!base.suficiente ? (
                <div style={{ fontSize: 12.5, color: G.vermelho, lineHeight: 1.5 }}>
                  A empresa tem <strong>{base.disponiveis} {base.disponiveis === 1 ? 'mês' : 'meses'}</strong> de
                  movimento apurado. O plano exige no mínimo <strong>{base.minimo} meses</strong> —
                  um ciclo completo — para separar tendência de sazonalidade.
                </div>
              ) : (
                <>
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    {opcoesBase.map(n => {
                      const sel = mesesBase === n;
                      return (
                        <button key={n} type="button" onClick={() => setMesesBase(n)}
                          style={{ padding: '8px 14px', fontSize: 12, fontWeight: 700,
                            background: sel ? G.verde : '#FFF', color: sel ? '#FFF' : G.txt1,
                            border: `1px solid ${sel ? G.verde : G.borda}`, borderRadius: 0,
                            cursor: 'pointer', fontFamily: "'Outfit', sans-serif" }}>
                          {sel ? '✓ ' : ''}{n} meses{n === base.disponiveis ? ' · tudo' : ''}
                        </button>
                      );
                    })}
                  </div>
                  <div style={{ fontSize: 12.5, color: G.txt1, marginTop: 10, lineHeight: 1.5 }}>
                    <strong>{rotuloMesBase(janelaBase.de)} → {rotuloMesBase(janelaBase.ate)}</strong>
                    {mesesBase !== base.sugerido && (
                      <span style={{ color: G.txt2 }}> · você ajustou; a sugestão era {base.sugerido} meses</span>
                    )}
                  </div>
                  <div style={{ fontSize: 11.5, color: G.txt2, marginTop: 4, lineHeight: 1.45 }}>
                    {base.razao}
                  </div>
                </>
              )}
            </div>
          )}

          {/* Footer com contador + botão */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            background: G.card, border: `1px solid ${G.borda}`, borderRadius: 0,
            padding: '14px 20px' }}>
            <div style={{ fontSize: 13, color: G.txt2 }}>
              <strong style={{ color: totalMetasSelecionadas > 0 ? G.verde : G.txt2,
                fontWeight: 700 }}>
                {totalMetasSelecionadas}
              </strong> de 2 metas selecionadas
              {/* O motivo do botão travado tem de estar AO LADO dele — o aviso
                  do topo fica fora da vista aqui embaixo (auditoria 02/09). */}
              {soComplementar && (
                <span style={{ color: '#B45309', fontWeight: 600, marginLeft: 10 }}>
                  · falta uma meta de resultado
                </span>
              )}
              {!soComplementar && !quantoOk && metasSel.length > 0 && (() => {
                // Cobra só o que de fato falta: o "quanto" já informado não se
                // pede de novo (auditoria 02/09).
                const faltaCapex = regimeMeta === 'capex' && !fonteCapex;
                const faltaQuanto = !quantoSemCapexOk;
                if (!faltaQuanto && !faltaCapex) return null;
                return (
                  <span style={{ color: '#B45309', fontWeight: 600, marginLeft: 10 }}>
                    · {faltaQuanto ? 'informe o quanto de cada meta' : 'escolha a fonte do capex'}
                    {faltaQuanto && faltaCapex ? ' e a fonte do capex' : ''}
                  </span>
                );
              })()}
              {/* Volume e preço COMPÕEM: o empresário digita 12% e 10% em
                  campos diferentes e a receita cresce 23,2%. Sem mostrar a
                  conta, o card de regime parecia decidir por conta própria.
                  Só aparece com o formulário completo — ao lado de um aviso de
                  pendência viraria ruído (visto no harness). */}
              {quantoOk && !soComplementar && crescEfetivo != null && temMetaReceita
                && metasSel.includes('ticket_medio') && metaRecFrac != null && (
                <span style={{ color: G.txt2, marginLeft: 10 }}>
                  · receita efetiva <strong style={{ color: G.laranja }}>
                    +{(crescEfetivo * 100).toFixed(1).replace('.', ',')}%
                  </strong> (volume × preço)
                </span>
              )}
            </div>
            <button onClick={gerarPlano} disabled={!podeGerar || gerando}
              style={{ background: (podeGerar && !gerando) ? G.verde : G.borda,
                color: (podeGerar && !gerando) ? '#fff' : G.txt2,
                border: 'none', borderRadius: 0, padding: '12px 28px',
                fontSize: 12, fontWeight: 700, letterSpacing: '0.08em',
                textTransform: 'uppercase',
                cursor: (podeGerar && !gerando) ? 'pointer' : 'not-allowed',
                boxShadow: (podeGerar && !gerando) ? `0 2px 8px ${G.verde}44` : 'none',
                transition: 'all 0.15s ease', minWidth: 200 }}>
              {gerando ? <><MarcaAIdvisor variant="default" /> gerando plano…</> : 'Gerar Plano →'}
            </button>
          </div>

          {/* Erro — banner inline */}
          {erroAdvisor && (
            <div style={{ marginTop: 12, padding: '12px 18px',
              background: G.vermelhoL, borderLeft: `3px solid ${G.vermelho}`,
              fontSize: 12, color: G.vermelho, lineHeight: 1.5, borderRadius: 0 }}>
              <strong>Erro:</strong> {erroAdvisor}
            </div>
          )}

        </div>

        {/* ═══════════════════════════════════════════════════════════════
            AVISO DE ESPERA (02/09) — sem esfumaçado e sem logo girando
            (pedido do Fabio: "o logo Rungoal girando fica ruim"). Cartão
            discreto, centrado, com a marca RUNGOAL AIDVISOR oscilando no padrão que
            já existia (`rungoalAdvisorPulse`) e os três pontos pulsantes.
            ═══════════════════════════════════════════════════════════════ */}
        {gerando && (
          <div style={{
            position: 'fixed', inset: 0, zIndex: 9999,
            background: 'rgba(26, 26, 26, 0.30)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <style>{`
              @keyframes rungoalAdvisorPulse {
                0%, 100% { opacity: 0.60; }
                50%      { opacity: 1.00; }
              }
              @keyframes rungoalBarraVaiVem {
                0%   { transform: translateX(-100%); }
                100% { transform: translateX(365%); }
              }
            `}</style>

            <div style={{
              background: '#FFF', borderRadius: 0,
              border: `1px solid ${G.borda}`,
              boxShadow: '0 8px 28px rgba(26,26,26,0.18)',
              padding: '22px 30px', maxWidth: 420, textAlign: 'center',
            }}>
              <div style={{ animation: 'rungoalAdvisorPulse 1.8s ease-in-out infinite' }}>
                <MarcaAIdvisor variant="default" />
              </div>
              <div style={{
                marginTop: 10, fontSize: 14, fontWeight: 800, color: G.txt1,
                letterSpacing: '0.04em', textTransform: 'uppercase',
              }}>
                Plano em estruturação — aguarde
              </div>
              {/* Barra em movimento: prova visual de que a ferramenta está
                  trabalhando, não travada (Fabio, 02/09). */}
              <div style={{ marginTop: 12, height: 4, borderRadius: 0,
                background: G.borda, overflow: 'hidden' }}>
                <div style={{ width: '38%', height: '100%', borderRadius: 0,
                  background: `linear-gradient(90deg, ${G.verde}, ${G.laranja})`,
                  animation: 'rungoalBarraVaiVem 1.6s ease-in-out infinite' }}/>
              </div>
              <div style={{ marginTop: 12, fontSize: 12, color: G.txt2, lineHeight: 1.45 }}>
                Leitura do histórico, desenho da estratégia e projeção dos 12
                meses. Leva cerca de um minuto.
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // ════════════════════════════════════════════════════════════════════
  // PASSO 2 — plano apresentado
  // ════════════════════════════════════════════════════════════════════
  const der = derivados(planoAtual.proj);
  const MES_ABR = ['Jan','Fev','Mar','Abr','Mai','Jun','Jul','Ago','Set','Out','Nov','Dez'];

  return (
    <div style={{ padding: '24px 28px', position: 'relative', overflow: 'hidden' }}>
      <CarimboAIdvisor />
      <HeaderTela subtitulo={
        <>período <strong>12 meses</strong> · status <strong>{
          validado ? 'editado e validado' : (editando ? 'em edição' : 'rascunho')
        }</strong></>
      } />

      <div>

        {/* ── Banner contextual de estado ─────────────────────────── */}
        {/* Banner "Editado e Validado" movido pro rodapé (ao lado dos botões) */}

        {editando && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 14,
            background: G.laranjaLight, borderLeft: `4px solid ${G.laranja}`,
            padding: '14px 22px', marginBottom: 18, borderRadius: 0 }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: G.laranja,
                letterSpacing: '0.14em', textTransform: 'uppercase', marginBottom: 2 }}>
                Modo Edição
              </div>
              <div style={{ fontSize: 13, color: G.txt1, fontWeight: 500, lineHeight: 1.5 }}>
                Ajuste os valores de <strong>Receita</strong> e <strong>Despesa</strong> na tabela
                trimestral (campos em laranja). Depois clique em <strong>“Recalcular Plano →”</strong>
                para o <span style={{ display: 'inline-block', background: '#FFF', padding: '2px 9px', borderRadius: 0, verticalAlign: 'baseline', lineHeight: 1.1 }}><MarcaAIdvisor variant="default" /></span> regerar Estratégia, Alavancas e Pontos de Atenção. Por fim, clique
                em <strong>“Validar e Seguir”</strong>.
              </div>
              {recalculadoFlag && (
                <div style={{ marginTop: 10, padding: '8px 12px',
                  background: G.verdeLight, borderLeft: `3px solid ${G.verde}`,
                  fontSize: 12, color: G.verde, fontWeight: 600 }}>
                  ✓ Narrativa atualizada pelo <span style={{ display: 'inline-block', background: '#FFF', padding: '2px 9px', borderRadius: 0, verticalAlign: 'baseline', lineHeight: 1.1 }}><MarcaAIdvisor variant="default" /></span> — confira e valide.
                </div>
              )}
            </div>
          </div>
        )}

        {!validado && !editando && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 14,
            background: G.card, border: `1px solid ${G.borda}`,
            borderLeft: `4px solid ${G.verde}`,
            padding: '14px 22px', marginBottom: 18, borderRadius: 0 }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: G.verde,
                letterSpacing: '0.14em', textTransform: 'uppercase', marginBottom: 2 }}>
                Plano Gerado · Rascunho
              </div>
              <div style={{ fontSize: 13, color: G.txt1, fontWeight: 500, lineHeight: 1.5 }}>
                O <MarcaAIdvisor /> propôs este plano a partir das metas escolhidas
                e do histórico da empresa. <strong>Revise</strong> a estratégia, projeção, alavancas
                e pontos de atenção. Se algo precisa ajustar, clique em <strong>“Editar Plano”</strong>.
                Quando estiver de acordo, clique em <strong>“Validar e Seguir”</strong> para congelar
                como orçamento dos próximos 12 meses.
              </div>
            </div>
          </div>
        )}

        {/* Banner verde escuro — Metas do plano */}
        <div style={{ background: '#1B6B2F', color: '#FFF', borderRadius: 0,
          padding: '16px 22px', marginBottom: 18 }}>
          <div style={{ fontSize: 10, fontWeight: 700, opacity: 0.8,
            textTransform: 'uppercase', letterSpacing: '0.14em', marginBottom: 10 }}>
            Metas deste Plano
          </div>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            {planoAtual.metasNomes.map((nome, i) => (
              <span key={i} style={{
                background: 'rgba(255,255,255,0.12)',
                border: '1px solid rgba(255,255,255,0.28)',
                padding: '6px 12px', borderRadius: 0,
                fontSize: 13, fontWeight: 600,
                letterSpacing: '0.01em' }}>
                {nome}
              </span>
            ))}
          </div>
        </div>

        {/* KPIs do ano — 4 cards */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)',
          gap: 12, marginBottom: 18 }}>
          {[
            { label: 'Receita Bruta', val: fmtBR(der.receitas_ano * 1000),
              sub: '12 meses · projeção' },
            { label: 'Despesas Totais', val: fmtBR(der.despesas_ano * 1000),
              sub: '12 meses · projeção' },
            { label: 'Lucro Líquido', val: fmtBR(der.lucro_ano * 1000),
              sub: `margem ${der.receitas_ano > 0 ? ((der.lucro_ano/der.receitas_ano)*100).toFixed(1).replace('.', ',') : '—'}%` },
            { label: 'Lucro Acumulado (mês 12)',
              val: fmtBR(der.lucro_m[11] * 1000),
              sub: 'último mês da janela rolante' },
          ].map(k => (
            <div key={k.label} style={{ background: G.card,
              border: `1px solid ${G.borda}`, borderRadius: 0,
              padding: '14px 16px' }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: G.txt2,
                textTransform: 'uppercase', letterSpacing: '0.10em', marginBottom: 8 }}>
                {k.label}
              </div>
              <div style={{ fontSize: 20, fontWeight: 800, color: G.txt1,
                fontVariantNumeric: 'tabular-nums', marginBottom: 4 }}>
                {k.val}
              </div>
              <div style={{ fontSize: 11, color: G.txt2 }}>{k.sub}</div>
            </div>
          ))}
        </div>

        {/* ── Laudo de viabilidade da meta (31/08): o QUANTO era do dono; o
            veredito, as ações e o capex dimensionado são do RUNGOAL AIDVISOR — tudo
            ancorado na banda e nos fatos medidos dos picos. */}
        {planoAtual?.laudo && (() => {
          const ld = planoAtual.laudo;
          const REG = {
            ritmo:      { rot: 'DENTRO DO RITMO · SEM CAPEX', bg: G.verdeLight, cor: G.verde },
            eficiencia: { rot: 'EFICIÊNCIA E PRODUTIVIDADE · SEM CAPEX', bg: G.laranjaLight, cor: G.laranja },
            capex:      { rot: 'EXIGE CAPEX', bg: '#FFFBEB', cor: '#B45309' },
            reestruturacao: { rot: 'EXIGE REESTRUTURAÇÃO', bg: '#FFFBEB', cor: '#B45309' },
            decisao:    { rot: 'DECISÃO SOCIETÁRIA', bg: G.laranjaLight, cor: G.laranja },
            sem_banda:  { rot: 'SEM BANDA MEDIDA', bg: G.fundo, cor: G.txt2 },
          }[ld.regime] || { rot: ld.regime, bg: G.fundo, cor: G.txt2 };
          return (
            <div style={{ background: G.card, border: `1px solid ${G.borda}`,
              borderRadius: 0, padding: '16px 20px', marginBottom: 18 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10,
                marginBottom: 8, flexWrap: 'wrap' }}>
                <MarcaAIdvisor variant="default" />
                <span style={{ fontSize: 12, fontWeight: 800,
                  letterSpacing: '0.08em', textTransform: 'uppercase', color: G.txt1 }}>
                  Laudo de viabilidade da meta
                </span>
                <span style={{ padding: '3px 10px', borderRadius: 0, fontSize: 10.5,
                  fontWeight: 800, letterSpacing: '0.05em',
                  background: REG.bg, color: REG.cor }}>
                  {REG.rot}
                </span>
              </div>
              <div style={{ fontSize: 12.5, lineHeight: 1.55, color: G.txt1,
                marginBottom: 10 }}>
                {ld.veredito}
              </div>
              <ol style={{ margin: '0 0 0 18px', fontSize: 12, lineHeight: 1.55,
                color: G.txt1 }}>
                {(ld.acoes || []).map((a, i) => <li key={i}>{a}</li>)}
              </ol>
              {ld.capex && (
                <div style={{ marginTop: 12, paddingTop: 10,
                  borderTop: `1px solid ${G.borda}`, fontSize: 12, lineHeight: 1.55 }}>
                  <strong>Capex dimensionado:</strong>{' '}
                  R$ {Number(ld.capex.valor_total || 0).toLocaleString('pt-BR',
                    { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  {' '}· em quê: {(ld.capex.em_que || []).join(' · ')}.
                  <div style={{ color: G.txt2, marginTop: 4 }}>{ld.capex.efeito_fonte}</div>
                </div>
              )}
            </div>
          );
        })()}

        {/* ─── Planilha Mensal (passado consolidado + 12m Plano) ─── */}
        {(() => {
          // Linhas categorizadas (receitas + despesas).
          // Pra o passado, valor vem do mock determinístico (valorPassado).
          // Pra o Plano (próximos 12), valor vem de planoAtual.proj (editável).
          const linhasRec = planoAtual.proj.receitas.map((l, idx) => ({
            grupo: 'receitas', linha: l.linha, idx, futuro: l.m, cor: G.verde,
          }));
          const linhasDes = planoAtual.proj.despesas.map((l, idx) => ({
            grupo: 'despesas', linha: l.linha, idx, futuro: l.m, cor: G.vermelho,
          }));
          // CAPEX do laudo vira linha própria, FORA do lucro (investimento
          // não é despesa de resultado); azul da casa, sem edição.
          const cronogramaCapex = planoAtual?.laudo?.capex?.cronograma_m;
          const linhasCapex = (Array.isArray(cronogramaCapex) && cronogramaCapex.length === 12)
            ? [{ grupo: 'capex', linha: 'CAPEX (investimento)', idx: 0,
                 futuro: cronogramaCapex, cor: G.azul }]
            : [];
          const grupos = [
            { label: 'Receitas', cor: G.verde, linhas: linhasRec },
            { label: 'Despesas', cor: G.vermelho, linhas: linhasDes },
            ...(linhasCapex.length
              ? [{ label: 'Investimento', cor: G.azul, linhas: linhasCapex }]
              : []),
          ];

          // ── Janela visível: por padrão, só os 12 meses do Plano. Quando
          //    `mostrarPassado` for true, inclui também o histórico consolidado.
          const passadoVisivel = mostrarPassado ? mesesPassado : [];
          const mesesVisiveis = [...passadoVisivel, ...mesesPlano];
          const offsetPassado = passadoVisivel.length;  // 0 quando oculto, N quando visível

          // Pra cada mês visível, valor por linha
          const valorMes = (linha, mesIdx) => {
            // mesIdx < offsetPassado → passado (consolidado, do mock)
            // mesIdx >= offsetPassado → futuro (do plano, editável)
            if (mesIdx < offsetPassado) {
              return valorPassado(linha.linha, passadoVisivel[mesIdx]);
            }
            return linha.futuro[mesIdx - offsetPassado];
          };

          // Anos no horizonte VISÍVEL (para colunas Total Ano + separador entre anos)
          const anos = [...new Set(mesesVisiveis.map(m => m.getFullYear()))];
          const mesesPorAno = ano => mesesVisiveis.filter(m => m.getFullYear() === ano);
          const indexDeMes = (m) => mesesVisiveis.findIndex(x =>
            x.getFullYear() === m.getFullYear() && x.getMonth() === m.getMonth());

          // Total por linha em um ano específico
          const totalLinhaAno = (linha, ano) => mesesPorAno(ano)
            .reduce((s, m) => s + valorMes(linha, indexDeMes(m)), 0);

          // Lucro Líquido por mês = receitas − despesas
          const lucroMesIdx = (mesIdx) => {
            const rec = linhasRec.reduce((s, l) => s + valorMes(l, mesIdx), 0);
            const des = linhasDes.reduce((s, l) => s + valorMes(l, mesIdx), 0);
            return rec - des;
          };
          const lucroAno = (ano) => mesesPorAno(ano)
            .reduce((s, m) => s + lucroMesIdx(indexDeMes(m)), 0);

          // Status por mês: 'realizado' (passado) ou 'plano' (futuro 12m)
          const ehFuturo = (mesIdx) => mesIdx >= offsetPassado;

          // Index do mês HOJE (primeiro do Plano)
          const idxHoje = offsetPassado;

          // Posiciona cell na primeira coluna do Plano: borda esquerda laranja
          // (marcador "agora").
          const borderHoje = (mesIdx) => mesIdx === idxHoje
            ? `2px solid ${G.laranja}` : undefined;
          // Separador entre anos (borda esquerda no primeiro mês de cada ano,
          // exceto o primeiro mês absoluto)
          const borderAno = (mesIdx) => {
            if (mesIdx === 0) return undefined;
            if (mesesVisiveis[mesIdx].getFullYear() !== mesesVisiveis[mesIdx-1].getFullYear())
              return `1px solid ${G.borda}`;
            return undefined;
          };
          // Combina borda HOJE + ano (HOJE vence)
          const bordaEsq = (mesIdx) => borderHoje(mesIdx) || borderAno(mesIdx);

          return (
            <div style={{ background: G.card,
              border: `${editando ? 2 : 1}px solid ${editando ? G.laranja : G.borda}`,
              borderRadius: 0, padding: '16px 0 4px', marginBottom: 18,
              transition: 'border 0.18s ease' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between',
                alignItems: 'center', padding: '0 22px 12px',
                borderBottom: `1px solid ${G.borda}`, gap: 14, flexWrap: 'wrap' }}>
                <div style={{ fontSize: 11, color: editando ? G.laranja : G.txt2,
                  fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.10em' }}>
                  Projeção Mensal — {mostrarPassado ? 'passado consolidado + ' : ''}12 meses do Plano
                  {editando && <span style={{ marginLeft: 10, fontSize: 10,
                    background: G.laranja, color: '#FFF', padding: '3px 10px',
                    borderRadius: 0, letterSpacing: '0.10em' }}>EM EDIÇÃO</span>}
                </div>
                {/* Selos à DIREITA, num bloco só: o space-between do pai jogava
                    o selo da afinação para o meio, longe dos medidos (harness). */}
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap',
                  alignItems: 'center' }}>
                {/* Ajuste aritmético da curva (02/09) — não é selo medido. */}
                {planoAtual?.afinacao && (
                  <span title={planoAtual.afinacao.nota}
                    style={{ padding: '3px 9px', borderRadius: 0, fontSize: 10.5,
                      fontWeight: 800, background: '#FFFBEB', color: '#B45309',
                      border: '1px solid #B45309' }}>
                    CURVA AJUSTADA PARA FECHAR A META
                  </span>
                )}
                {/* Selos MEDIDOS (31/08): o fator que a curva entrega contra a
                    base anualizada — conferido no backend, não declarado. */}
                {planoAtual?.selos && (planoAtual.selos.fator_receita != null
                  || planoAtual.selos.fator_despesa != null) && (
                  <div title={planoAtual.selos.metodo}
                    style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    {planoAtual.selos.fator_receita != null && (
                      <span style={{ padding: '3px 9px', borderRadius: 0,
                        fontSize: 10.5, fontWeight: 800,
                        background: G.verdeLight, color: G.verde }}>
                        RECEITA {planoAtual.selos.fator_receita >= 0 ? '+' : ''}
                        {(planoAtual.selos.fator_receita * 100).toFixed(1).replace('.', ',')}% vs base · medido
                      </span>
                    )}
                    {planoAtual.selos.fator_despesa != null && (
                      <span style={{ padding: '3px 9px', borderRadius: 0,
                        fontSize: 10.5, fontWeight: 800,
                        background: G.vermelhoL, color: G.vermelho }}>
                        DESPESAS TOTAIS {planoAtual.selos.fator_despesa >= 0 ? '+' : ''}
                        {(planoAtual.selos.fator_despesa * 100).toFixed(1).replace('.', ',')}% vs base · medido
                      </span>
                    )}
                  </div>
                )}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12,
                  fontSize: 10, color: G.txt2 }}>
                  {mostrarPassado && (
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                      <span style={{ width: 10, height: 10, background: G.txt1,
                        borderRadius: '50%' }}/>
                      Realizado
                    </span>
                  )}
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                    <span style={{ width: 10, height: 10, background: G.verde,
                      borderRadius: '50%' }}/>
                    Plano 12m
                  </span>
                  <span style={{ paddingLeft: 6, paddingRight: 4,
                    borderLeft: `1px solid ${G.borda}` }}>
                    em milhares (R$ k)
                  </span>
                  <button onClick={() => setMostrarPassado(v => !v)}
                    style={{ padding: '6px 12px', border: 'none',
                      background: G.verde, color: '#FFF',
                      fontSize: 10, fontWeight: 700, cursor: 'pointer',
                      letterSpacing: '0.08em', textTransform: 'uppercase',
                      borderRadius: 0, fontFamily: 'inherit',
                      boxShadow: `0 1px 4px ${G.verde}44`,
                      transition: 'all 0.15s ease' }}>
                    {mostrarPassado
                      ? `Ocultar Histórico (${mesesPassado.length}m)`
                      : `Mostrar Histórico (${mesesPassado.length}m)`}
                  </button>
                </div>
              </div>
              {editando && (
                <div style={{ background: G.laranjaLight, color: G.laranja,
                  padding: '10px 22px', fontSize: 12, fontWeight: 600,
                  borderBottom: `1px solid ${G.borda}` }}>
                  Edite os campos do <strong>Plano</strong> (verde) abaixo · o passado
                  é consolidado e não é editável · Lucro Líquido recalcula
                  automaticamente · clique em <strong>"Recalcular Plano →"</strong>
                  pro <span style={{ display: 'inline-block', background: '#FFF', padding: '2px 9px', borderRadius: 0, verticalAlign: 'baseline', lineHeight: 1.1 }}><MarcaAIdvisor variant="default" /></span> atualizar a narrativa.
                </div>
              )}
              <div style={{ overflowX: 'auto' }}>
                <table style={{ borderCollapse: 'collapse',
                  fontVariantNumeric: 'tabular-nums' }}>
                  <thead style={{ position: 'sticky', top: 0, zIndex: 3 }}>
                    {/* Linha de anos */}
                    <tr style={{ background: '#FAFAF6' }}>
                      <th style={{ padding: '6px 14px', borderBottom: `1px solid ${G.borda}`,
                        borderRight: `1px solid ${G.borda}`,
                        position: 'sticky', left: 0, background: '#FAFAF6', zIndex: 2,
                        minWidth: 190 }}/>
                      {anos.map(ano => {
                        const cols = mesesPorAno(ano).length;
                        return (
                          <React.Fragment key={ano}>
                            <th colSpan={cols} style={{ padding: '6px 6px',
                              textAlign: 'center', fontSize: 11, fontWeight: 700,
                              color: G.txt1, letterSpacing: '0.04em',
                              borderBottom: `1px solid ${G.borda}`,
                              borderLeft: `1px solid ${G.borda}` }}>
                              {ano}
                            </th>
                            <th style={{ padding: '6px 6px', textAlign: 'center',
                              fontSize: 9, fontWeight: 700, color: G.txt2,
                              letterSpacing: '0.10em', textTransform: 'uppercase',
                              borderBottom: `1px solid ${G.borda}`,
                              borderLeft: `1px solid ${G.borda}`,
                              background: '#F0F0EC' }}>
                              Total
                            </th>
                          </React.Fragment>
                        );
                      })}
                    </tr>
                    {/* Linha de meses */}
                    <tr style={{ background: '#FAFAF6' }}>
                      <th style={{ padding: '8px 14px', textAlign: 'left',
                        fontSize: 10, fontWeight: 700, color: G.txt2,
                        letterSpacing: '0.08em', textTransform: 'uppercase',
                        borderBottom: `1px solid ${G.borda}`,
                        borderRight: `1px solid ${G.borda}`,
                        position: 'sticky', left: 0, background: '#FAFAF6', zIndex: 2,
                        minWidth: 190 }}>
                        Categoria
                      </th>
                      {anos.map(ano => {
                        const mesesA = mesesPorAno(ano);
                        return (
                          <React.Fragment key={ano}>
                            {mesesA.map(m => {
                              const i = indexDeMes(m);
                              const futuro = ehFuturo(i);
                              const isHoje = i === idxHoje;
                              return (
                                <th key={`m-${i}`}
                                  style={{ padding: '6px 4px', textAlign: 'center',
                                    fontSize: 10, fontWeight: 700,
                                    color: isHoje ? G.laranja : (futuro ? G.verde : G.txt2),
                                    letterSpacing: '0.04em',
                                    borderBottom: `1px solid ${G.borda}`,
                                    borderLeft: bordaEsq(i),
                                    minWidth: 46, whiteSpace: 'nowrap' }}>
                                  {MES_ABR[m.getMonth()]}
                                </th>
                              );
                            })}
                            <th style={{ padding: '6px 6px', textAlign: 'center',
                              fontSize: 9, fontWeight: 700, color: G.txt2,
                              letterSpacing: '0.08em', textTransform: 'uppercase',
                              borderBottom: `1px solid ${G.borda}`,
                              borderLeft: `1px solid ${G.borda}`,
                              background: '#F0F0EC', minWidth: 60 }}>
                              Ano
                            </th>
                          </React.Fragment>
                        );
                      })}
                    </tr>
                  </thead>
                  <tbody>
                    {grupos.map(grupo => (
                      <React.Fragment key={grupo.label}>
                        {/* Header do grupo */}
                        <tr>
                          <td colSpan={1 + mesesVisiveis.length + anos.length}
                            style={{ padding: '6px 14px', background: '#FAFAF6',
                              borderTop: `1px solid ${G.borda}`,
                              borderBottom: `1px solid ${G.borda}`,
                              position: 'sticky', left: 0, zIndex: 1 }}>
                            <span style={{ fontSize: 10, fontWeight: 700,
                              color: grupo.cor, letterSpacing: '0.12em',
                              textTransform: 'uppercase' }}>
                              {grupo.label}
                            </span>
                          </td>
                        </tr>
                        {/* Linhas */}
                        {grupo.linhas.map(linha => (
                          <tr key={linha.linha} style={{ borderBottom: '1px solid #F0F0EC' }}>
                            <td style={{ padding: '6px 14px', fontSize: 12.5,
                              color: G.txt1, fontWeight: 500,
                              position: 'sticky', left: 0, background: G.card, zIndex: 1,
                              borderRight: `1px solid ${G.borda}` }}>
                              {linha.linha}
                            </td>
                            {anos.map(ano => {
                              const mesesA = mesesPorAno(ano);
                              return (
                                <React.Fragment key={`${linha.linha}-${ano}`}>
                                  {mesesA.map(m => {
                                    const i = indexDeMes(m);
                                    const futuro = ehFuturo(i);
                                    const v = valorMes(linha, i);
                                    const idxNoPlano = futuro ? i - offsetPassado : null;
                                    return (
                                      <td key={`v-${linha.linha}-${i}`}
                                        style={{ padding: '4px 3px', textAlign: 'right',
                                          borderLeft: bordaEsq(i) }}>
                                        {editando && futuro && linha.grupo !== 'capex' ? (
                                          <input
                                            value={linha.futuro[idxNoPlano]}
                                            type="text" inputMode="decimal"
                                            onChange={e => editarValor(linha.grupo, linha.idx, idxNoPlano, e.target.value)}
                                            style={{ width: 44, textAlign: 'right',
                                              padding: '3px 4px',
                                              border: `1px solid ${G.laranja}`,
                                              background: G.laranjaLight,
                                              borderRadius: 0, fontSize: 11,
                                              fontFamily: 'inherit', fontWeight: 600,
                                              color: G.txt1,
                                              fontVariantNumeric: 'tabular-nums',
                                              outline: 'none', boxSizing: 'border-box' }}/>
                                        ) : (
                                          <span style={{ fontSize: 11,
                                            color: v === 0 ? '#A5ADB3'
                                              : (futuro ? linha.cor : G.txt1),
                                            fontWeight: futuro ? 600 : 500 }}>
                                            {v === 0 ? '—' : fmtK(v)}
                                          </span>
                                        )}
                                      </td>
                                    );
                                  })}
                                  <td style={{ padding: '6px 8px', textAlign: 'right',
                                    fontSize: 12, fontWeight: 700, color: linha.cor,
                                    borderLeft: `1px solid ${G.borda}`,
                                    background: '#F8F8F4' }}>
                                    {fmtK(totalLinhaAno(linha, ano))}
                                  </td>
                                </React.Fragment>
                              );
                            })}
                          </tr>
                        ))}
                      </React.Fragment>
                    ))}
                    {/* Lucro Líquido — spotlight */}
                    <tr style={{ background: 'rgba(27,107,47,0.04)',
                      borderTop: `1.5px solid ${G.verde}` }}>
                      <td style={{ padding: '10px 14px', fontSize: 11, fontWeight: 700,
                        color: G.verde, letterSpacing: '0.10em',
                        textTransform: 'uppercase',
                        position: 'sticky', left: 0,
                        background: 'rgba(232,242,235,1)', zIndex: 1,
                        borderRight: `1px solid ${G.borda}` }}>
                        Lucro Líquido
                      </td>
                      {anos.map(ano => {
                        const mesesA = mesesPorAno(ano);
                        return (
                          <React.Fragment key={`ll-${ano}`}>
                            {mesesA.map(m => {
                              const i = indexDeMes(m);
                              const futuro = ehFuturo(i);
                              const v = lucroMesIdx(i);
                              return (
                                <td key={`ll-${i}`}
                                  style={{ padding: '8px 3px', textAlign: 'right',
                                    fontSize: 11.5, fontWeight: 800,
                                    color: v >= 0 ? (futuro ? G.verde : G.txt1) : G.vermelho,
                                    borderLeft: bordaEsq(i) }}>
                                  {fmtK(v)}
                                </td>
                              );
                            })}
                            <td style={{ padding: '8px 8px', textAlign: 'right',
                              fontSize: 12.5, fontWeight: 800,
                              color: lucroAno(ano) >= 0 ? G.verde : G.vermelho,
                              borderLeft: `1px solid ${G.borda}`,
                              background: 'rgba(0,0,0,0.03)' }}>
                              {fmtK(lucroAno(ano))}
                            </td>
                          </React.Fragment>
                        );
                      })}
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          );
        })()}

        {/* ─── Grid 3 colunas · Tese · Alavancas · Cuidados ─────────────────
            Padrão "escadinha amena" uniforme nos 3 painéis:
              - chip à esquerda (numeração / timing) + frase em italic ao lado
              - bloco CENTRALIZADO horizontalmente · escadinha amena (step 12px)
              - frases com maxWidth + ellipsis (proteção contra IA verbosa)
              - mesma estrutura visual · paleta varia por painel:
                  Tese      → fundo verde escuro · texto/chip/marca-d'água BRANCA
                  Alavancas → fundo branco       · texto/chip/marca-d'água LARANJA
                  Cuidados  → fundo laranja      · texto/chip/marca-d'água BRANCA
                              (marca-d'água em laranja escuro p/ contraste)
              - ícones decorativos no canto INFERIOR ESQUERDO
            ─────────────────────────────────────────────────────────────── */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)',
          gap: 14, alignItems: 'stretch', marginBottom: 18 }}>

        {/* ─── 1. TESE DO PLANO · fundo verde escuro ─── */}
        <div style={{
          background: '#134721',
          padding: '14px 16px 16px',
          borderRadius: 0,
          position: 'relative',
          overflow: 'hidden',
          minHeight: 200,
        }}>
          {/* Marca d'água "T" — branca translúcida */}
          <span style={{
            position: 'absolute', right: -10, top: -28,
            fontFamily: "Impact, 'Arial Narrow Bold', sans-serif",
            fontSize: 100, fontWeight: 900, lineHeight: 1,
            color: 'rgba(255,255,255,0.05)',
            pointerEvents: 'none', userSelect: 'none',
          }}>T</span>

          {/* Ícone marca d'água — compass branco · CANTO INFERIOR ESQUERDO */}
          <svg width="44" height="44" viewBox="0 0 24 24" fill="none"
            stroke="currentColor" strokeWidth="1.5"
            strokeLinecap="round" strokeLinejoin="round"
            style={{
              position: 'absolute', left: 12, bottom: 12,
              color: 'rgba(255,255,255,0.20)',
              pointerEvents: 'none', zIndex: 0,
            }}>
            <circle cx="12" cy="12" r="10"/>
            <polygon points="16.24 7.76 14.12 14.12 7.76 16.24 9.88 9.88 16.24 7.76"/>
          </svg>

          {/* Header */}
          <div style={{ display: 'flex', justifyContent: 'space-between',
            alignItems: 'center', position: 'relative', zIndex: 2 }}>
            <div style={{
              fontSize: 9, color: 'rgba(255,255,255,0.55)',
              letterSpacing: '0.18em', textTransform: 'uppercase',
              fontWeight: 700,
            }}>
              Tese do Plano
            </div>
            <BotaoSaibaMais onClick={() => setModalTese(true)} claro contorno tamanho="sm" />
          </div>

          {/* Bloco escadinha · CENTRALIZADO VERTICALMENTE no painel
              · primeira frase encostada na margem esquerda
              · cada item desloca +12px para direita (escadinha amena) */}
          <div style={{
            position: 'absolute',
            top: '50%', left: 16, right: 16,
            transform: 'translateY(-50%)',
            display: 'flex', flexDirection: 'column',
            alignItems: 'flex-start', gap: 6,
            zIndex: 1,
          }}>
            {(planoAtual.estrategia_fragmentos || []).map((frag, i) => {
              const offset = i * 12;
              const renderTexto = () => {
                if (!frag.accent_word) return frag.texto;
                const idx = frag.texto.indexOf(frag.accent_word);
                if (idx === -1) return frag.texto;
                return (
                  <>
                    {frag.texto.slice(0, idx)}
                    <span style={{ color: '#F8B27A',
                      borderBottom: '2px solid #F47C3C', paddingBottom: 1 }}>
                      {frag.accent_word}
                    </span>
                    {frag.texto.slice(idx + frag.accent_word.length)}
                  </>
                );
              };
              return (
                <div key={i} style={{
                  display: 'flex', alignItems: 'center', gap: 10,
                  marginLeft: offset,
                  width: `calc(100% - ${offset}px)`,
                }}>
                  {/* Chip — numeração */}
                  <span style={{
                    flexShrink: 0,
                    fontFamily: "Impact, 'Arial Narrow Bold', sans-serif",
                    fontSize: 14, fontWeight: 900, lineHeight: 1,
                    color: '#FFF',
                    background: 'rgba(255,255,255,0.10)',
                    border: '2px solid #FFF',
                    padding: '3px 6px',
                    letterSpacing: '0.05em',
                    borderRadius: 0, textAlign: 'center', minWidth: 22,
                  }}>
                    {String(i + 1).padStart(2, '0')}
                  </span>
                  {/* Frase — Outfit italic 800 · maxWidth + ellipsis */}
                  <span style={{
                    flex: '1 1 auto', minWidth: 0,
                    fontFamily: "'Outfit', sans-serif",
                    fontSize: 13, fontWeight: 800,
                    fontStyle: 'italic', lineHeight: 1.15,
                    color: '#FFF', letterSpacing: '-0.01em',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  }}>
                    {renderTexto()}
                  </span>
                </div>
              );
            })}
          </div>
        </div>

        {/* ─── 2. ALAVANCAS · fundo branco · escadinha LARANJA ─── */}
        <div style={{
          background: G.card, border: `1px solid ${G.borda}`,
          borderRadius: 0, padding: '14px 16px 16px',
          position: 'relative', overflow: 'hidden',
          minHeight: 200,
        }}>
          {/* Marca d'água "A" — laranja escurecido (escala 30%) */}
          <span style={{
            position: 'absolute', right: -10, top: -28,
            fontFamily: "Impact, 'Arial Narrow Bold', sans-serif",
            fontSize: 100, fontWeight: 900, lineHeight: 1,
            color: 'rgba(196,88,38,0.18)',
            pointerEvents: 'none', userSelect: 'none',
          }}>A</span>

          {/* Ícone marca d'água — wrench laranja queimado · canto inferior ESQUERDO */}
          <svg width="44" height="44" viewBox="0 0 24 24" fill="none"
            stroke="currentColor" strokeWidth="1.5"
            strokeLinecap="round" strokeLinejoin="round"
            style={{
              position: 'absolute', left: 12, bottom: 12,
              color: 'rgba(196,88,38,0.50)',
              pointerEvents: 'none', zIndex: 0,
            }}>
            <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"/>
          </svg>

          {/* Header */}
          <div style={{ display: 'flex', justifyContent: 'space-between',
            alignItems: 'center', position: 'relative', zIndex: 2 }}>
            <div style={{
              fontSize: 9, color: G.txt2, letterSpacing: '0.18em',
              textTransform: 'uppercase', fontWeight: 700,
            }}>
              Como vou chegar lá
            </div>
            <BotaoSaibaMais onClick={() => setModalAlavancas(true)} claro contorno tamanho="sm" />
          </div>

          {/* Bloco escadinha · CENTRALIZADO VERTICALMENTE · até 4 alavancas */}
          <div style={{
            position: 'absolute',
            top: '50%', left: 16, right: 16,
            transform: 'translateY(-50%)',
            display: 'flex', flexDirection: 'column',
            alignItems: 'flex-start', gap: 6,
            zIndex: 1,
          }}>
            {(planoAtual.alavancas || []).slice(0, 4).map((a, i) => {
              const texto = typeof a === 'string' ? a : (a?.titulo || '');
              const offset = i * 12;
              return (
                <div key={i} style={{
                  display: 'flex', alignItems: 'center', gap: 10,
                  marginLeft: offset,
                  width: `calc(100% - ${offset}px)`,
                }}>
                  {/* Chip — numeração com borda laranja */}
                  <span style={{
                    flexShrink: 0,
                    fontFamily: "Impact, 'Arial Narrow Bold', sans-serif",
                    fontSize: 14, fontWeight: 900, lineHeight: 1,
                    color: G.laranja, background: '#FFF',
                    border: `2px solid ${G.laranja}`,
                    padding: '3px 6px',
                    letterSpacing: '0.05em',
                    borderRadius: 0, textAlign: 'center', minWidth: 22,
                  }}>
                    {String(i + 1).padStart(2, '0')}
                  </span>
                  {/* Frase — Outfit italic · LARANJA · ocupa todo espaço disponível */}
                  <span style={{
                    flex: '1 1 auto', minWidth: 0,
                    fontFamily: "'Outfit', sans-serif",
                    fontSize: 13, fontWeight: 800,
                    fontStyle: 'italic', lineHeight: 1.15,
                    color: G.laranja, letterSpacing: '-0.01em',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  }}>
                    {texto}
                  </span>
                </div>
              );
            })}
          </div>
        </div>

        {/* ─── 3. CUIDADOS / ARMADILHAS · fundo laranja vibrante ─── */}
        <div style={{
          background: G.laranja, color: '#FFF',
          borderRadius: 0, padding: '14px 16px 16px',
          position: 'relative', overflow: 'hidden',
          minHeight: 200,
        }}>
          {/* Marca d'água "!" — laranja escuro translúcido */}
          <span style={{
            position: 'absolute', right: -4, top: -38,
            fontFamily: "Impact, 'Arial Narrow Bold', sans-serif",
            fontSize: 130, fontWeight: 900, lineHeight: 1,
            color: 'rgba(124,45,14,0.22)',
            pointerEvents: 'none', userSelect: 'none',
          }}>!</span>

          {/* Ícone marca d'água — alert-triangle laranja escuro · INFERIOR ESQUERDA */}
          <svg width="44" height="44" viewBox="0 0 24 24" fill="none"
            stroke="currentColor" strokeWidth="1.5"
            strokeLinecap="round" strokeLinejoin="round"
            style={{
              position: 'absolute', left: 12, bottom: 12,
              color: 'rgba(124,45,14,0.32)',
              pointerEvents: 'none', zIndex: 0,
            }}>
            <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
            <line x1="12" y1="9" x2="12" y2="13"/>
            <line x1="12" y1="17" x2="12.01" y2="17"/>
          </svg>

          {/* Header */}
          <div style={{ display: 'flex', justifyContent: 'space-between',
            alignItems: 'center', position: 'relative', zIndex: 2 }}>
            <div style={{
              fontSize: 9, color: 'rgba(255,255,255,0.85)',
              letterSpacing: '0.18em', textTransform: 'uppercase',
              fontWeight: 700,
            }}>
              Cuidados · não esqueça
            </div>
            <BotaoSaibaMais onClick={() => setModalCuidados(true)} claro contorno tamanho="sm" />
          </div>

          {/* Bloco escadinha · CENTRALIZADO VERTICALMENTE · 3 cuidados */}
          <div style={{
            position: 'absolute',
            top: '50%', left: 16, right: 16,
            transform: 'translateY(-50%)',
            display: 'flex', flexDirection: 'column',
            alignItems: 'flex-start', gap: 6,
            zIndex: 1,
          }}>
            {(planoAtual.freios || []).slice(0, 3).map((f, i) => {
              const obj = typeof f === 'string' ? { timing: '!', titulo: f } : f;
              const offset = i * 12;
              return (
                <div key={i} style={{
                  display: 'flex', alignItems: 'center', gap: 10,
                  marginLeft: offset,
                  width: `calc(100% - ${offset}px)`,
                }}>
                  {/* Chip timing — borda branca */}
                  <span style={{
                    flexShrink: 0,
                    fontFamily: "Impact, 'Arial Narrow Bold', sans-serif",
                    fontSize: 14, fontWeight: 900, lineHeight: 1,
                    color: '#FFF', background: 'rgba(255,255,255,0.15)',
                    border: '2px solid #FFF',
                    padding: '3px 6px',
                    letterSpacing: '0.05em',
                    borderRadius: 0, textAlign: 'center', minWidth: 22,
                  }}>
                    {obj.timing}
                  </span>
                  {/* Frase — Outfit italic, branca · maxWidth + ellipsis */}
                  <span style={{
                    flex: '1 1 auto', minWidth: 0,
                    fontFamily: "'Outfit', sans-serif",
                    fontSize: 13, fontWeight: 800,
                    fontStyle: 'italic', lineHeight: 1.15,
                    color: '#FFF', letterSpacing: '-0.01em',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  }}>
                    {obj.titulo}
                  </span>
                </div>
              );
            })}
          </div>
        </div>

        </div>{/* fim grid 3 colunas */}

        {/* ═══════════ MODAIS (position:fixed · fora do fluxo do grid) ═══════════ */}

        {/* Modal "Saiba mais" Tese — design editorial coerente com o card */}
        {modalTese && (
          <div onClick={() => setModalTese(false)}
            style={{ position: 'fixed', inset: 0,
              background: 'rgba(0,0,0,0.65)', zIndex: 1000,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              padding: 20, backdropFilter: 'blur(4px)' }}>
            <div onClick={e => e.stopPropagation()}
              style={{ background: G.card, borderRadius: 0,
                maxWidth: 680, width: '100%', maxHeight: '90vh', overflow: 'hidden',
                boxShadow: '0 20px 60px rgba(0,0,0,0.35)',
                display: 'flex', flexDirection: 'column' }}>
              <div style={{ background: '#134721', color: '#FFF',
                padding: '22px 28px 20px', position: 'relative', overflow: 'hidden',
                flexShrink: 0 }}>
                <span style={{ position: 'absolute', right: -10, top: -50,
                  fontFamily: "Impact, 'Arial Narrow Bold', sans-serif",
                  fontSize: 170, fontWeight: 900, lineHeight: 1,
                  color: 'rgba(255,255,255,0.07)', pointerEvents: 'none', userSelect: 'none',
                }}>T</span>
                <div style={{ display: 'flex', justifyContent: 'space-between',
                  alignItems: 'flex-start', position: 'relative', zIndex: 1 }}>
                  <div>
                    <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.55)',
                      letterSpacing: '0.20em', textTransform: 'uppercase',
                      fontWeight: 700, marginBottom: 6 }}>
                      Tese do Plano · Detalhada
                    </div>
                    <div style={{ fontFamily: "Impact, 'Arial Narrow Bold', sans-serif",
                      fontSize: 28, fontWeight: 900, lineHeight: 1.05,
                      color: '#FFF', letterSpacing: '0.005em' }}>
                      Melhorar o <span style={{ color: '#F47C3C',
                        borderBottom: '4px solid #F47C3C', paddingBottom: 2 }}>CAIXA</span> da operação.
                    </div>
                  </div>
                  <button onClick={() => setModalTese(false)} title="Fechar" aria-label="Fechar"
                    style={{ background: 'rgba(255,255,255,0.10)',
                      border: '1px solid rgba(255,255,255,0.25)', color: '#FFF',
                      width: 30, height: 30, borderRadius: 0, cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center', justifyContent: 'center',
                      padding: 0, fontFamily: 'inherit' }}><IconeFechar /></button>
                </div>
              </div>
              <div style={{ padding: '26px 32px 24px', overflowY: 'auto', flex: 1 }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: G.laranja,
                  letterSpacing: '0.18em', textTransform: 'uppercase', marginBottom: 12 }}>
                  Como vai funcionar
                </div>
                <p style={{ margin: 0, fontSize: 14.5, color: G.txt1,
                  lineHeight: 1.65, fontWeight: 500 }}>
                  <span style={{ float: 'left',
                    fontFamily: "Impact, 'Arial Narrow Bold', sans-serif",
                    fontSize: 56, fontWeight: 900, lineHeight: 0.85,
                    color: G.verde, marginRight: 10, marginTop: 4,
                  }}>{planoAtual.estrategia.charAt(0)}</span>
                  {planoAtual.estrategia.slice(1)}
                </p>
              </div>
              <div style={{ padding: '12px 32px', background: '#FAFAF6',
                borderTop: `1px solid ${G.borda}`, fontSize: 11, color: G.txt2,
                fontStyle: 'italic', flexShrink: 0 }}>
                Conteúdo gerado pelo <MarcaAIdvisor />{' '}
                a partir das metas escolhidas. Edite o plano se quiser ajustar a tese.
              </div>
            </div>
          </div>
        )}

        {/* Modal das Alavancas */}
        {modalAlavancas && (
          <div onClick={() => setModalAlavancas(false)}
            style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.65)',
              zIndex: 1000, display: 'flex', alignItems: 'center',
              justifyContent: 'center', padding: 20, backdropFilter: 'blur(4px)' }}>
            <div onClick={e => e.stopPropagation()}
              style={{ background: G.card, borderRadius: 0,
                maxWidth: 680, width: '100%', maxHeight: '90vh', overflow: 'hidden',
                boxShadow: '0 20px 60px rgba(0,0,0,0.35)',
                display: 'flex', flexDirection: 'column' }}>
              <div style={{ background: '#134721', color: '#FFF',
                padding: '22px 28px 20px', position: 'relative', overflow: 'hidden',
                flexShrink: 0 }}>
                <span style={{ position: 'absolute', right: -10, top: -50,
                  fontFamily: "Impact, 'Arial Narrow Bold', sans-serif",
                  fontSize: 170, fontWeight: 900, lineHeight: 1,
                  color: 'rgba(255,255,255,0.07)', pointerEvents: 'none',
                  userSelect: 'none' }}>A</span>
                <div style={{ display: 'flex', justifyContent: 'space-between',
                  alignItems: 'flex-start', position: 'relative', zIndex: 1 }}>
                  <div>
                    <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.55)',
                      letterSpacing: '0.20em', textTransform: 'uppercase',
                      fontWeight: 700, marginBottom: 6 }}>
                      Alavancas · detalhamento
                    </div>
                    <div style={{ fontFamily: "Impact, 'Arial Narrow Bold', sans-serif",
                      fontSize: 28, fontWeight: 900, lineHeight: 1.05, color: '#FFF' }}>
                      Como vou <span style={{ color: '#F47C3C',
                        borderBottom: '4px solid #F47C3C',
                        paddingBottom: 2 }}>chegar lá</span>.
                    </div>
                  </div>
                  <button onClick={() => setModalAlavancas(false)} title="Fechar" aria-label="Fechar"
                    style={{ background: 'rgba(255,255,255,0.10)',
                      border: '1px solid rgba(255,255,255,0.25)', color: '#FFF',
                      width: 30, height: 30, borderRadius: 0, cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center', justifyContent: 'center',
                      padding: 0, fontFamily: 'inherit' }}><IconeFechar /></button>
                </div>
              </div>
              <div style={{ padding: '20px 32px 24px', overflowY: 'auto', flex: 1 }}>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8,
                  marginBottom: 20, paddingBottom: 18,
                  borderBottom: `1px solid ${G.borda}` }}>
                  {(planoAtual.alavancas || []).map((a, i) => {
                    const texto = typeof a === 'string' ? a : (a?.titulo || '');
                    return (
                      <span key={i} style={{
                        fontFamily: "Impact, 'Arial Narrow Bold', sans-serif",
                        fontSize: 13, fontWeight: 900,
                        color: i % 2 === 0 ? G.verde : G.laranja,
                        letterSpacing: '0.02em', padding: '4px 10px',
                        border: `1px solid ${i % 2 === 0 ? G.verde : G.laranja}33`,
                        borderRadius: 0,
                        background: i % 2 === 0 ? G.verdeLight : G.laranjaLight }}>
                        {texto}
                      </span>
                    );
                  })}
                </div>
                <div style={{ fontSize: 10, fontWeight: 700, color: G.laranja,
                  letterSpacing: '0.18em', textTransform: 'uppercase', marginBottom: 12 }}>
                  Por que essas alavancas
                </div>
                <div style={{ fontSize: 14, color: G.txt1, lineHeight: 1.65,
                  fontWeight: 500 }}>
                  {(planoAtual.alavancas_explicacao || '').split('\n\n').map((par, i) => {
                    const partes = par.split('**');
                    const renderTexto = (pulaPrimeiroChar) => partes.map((p, k) => {
                      let conteudo = (pulaPrimeiroChar && k === 0) ? p.slice(1) : p;
                      return k % 2 === 1
                        ? <strong key={k} style={{ color: G.verde, fontWeight: 700 }}>{p}</strong>
                        : <React.Fragment key={k}>{conteudo}</React.Fragment>;
                    });
                    return (
                      <p key={i} style={{ margin: '0 0 12px' }}>
                        {i === 0 ? (
                          <>
                            <span style={{ float: 'left',
                              fontFamily: "Impact, 'Arial Narrow Bold', sans-serif",
                              fontSize: 50, fontWeight: 900, lineHeight: 0.85,
                              color: G.verde, marginRight: 10, marginTop: 4,
                            }}>{par.charAt(0)}</span>
                            {renderTexto(true)}
                          </>
                        ) : renderTexto(false)}
                      </p>
                    );
                  })}
                </div>
              </div>
              <div style={{ padding: '12px 32px', background: '#FAFAF6',
                borderTop: `1px solid ${G.borda}`, fontSize: 11, color: G.txt2,
                fontStyle: 'italic', flexShrink: 0 }}>
                Sugerido pelo <MarcaAIdvisor />.
                As quatro alavancas sustentam a tese do plano.
              </div>
            </div>
          </div>
        )}

        {/* Modal dos Cuidados */}
        {modalCuidados && (
          <div onClick={() => setModalCuidados(false)}
            style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.65)',
              zIndex: 1000, display: 'flex', alignItems: 'center',
              justifyContent: 'center', padding: 20, backdropFilter: 'blur(4px)' }}>
            <div onClick={e => e.stopPropagation()}
              style={{ background: G.card, borderRadius: 0,
                maxWidth: 680, width: '100%', maxHeight: '90vh', overflow: 'hidden',
                boxShadow: '0 20px 60px rgba(0,0,0,0.35)',
                display: 'flex', flexDirection: 'column' }}>
              <div style={{ background: G.laranja, color: '#FFF',
                padding: '22px 28px 20px', position: 'relative', overflow: 'hidden',
                flexShrink: 0 }}>
                <span style={{ position: 'absolute', right: 0, top: -50,
                  fontFamily: "Impact, 'Arial Narrow Bold', sans-serif",
                  fontSize: 180, fontWeight: 900, lineHeight: 1,
                  color: 'rgba(255,255,255,0.12)', pointerEvents: 'none',
                  userSelect: 'none' }}>!</span>
                <div style={{ display: 'flex', justifyContent: 'space-between',
                  alignItems: 'flex-start', position: 'relative', zIndex: 1 }}>
                  <div>
                    <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.85)',
                      letterSpacing: '0.20em', textTransform: 'uppercase',
                      fontWeight: 700, marginBottom: 6 }}>
                      Cuidados · detalhamento
                    </div>
                    <div style={{ fontFamily: "'Outfit', sans-serif",
                      fontSize: 26, fontWeight: 800, fontStyle: 'italic',
                      lineHeight: 1.05, color: '#FFF', letterSpacing: '-0.01em' }}>
                      Não deixe para resolver no <span style={{
                        color: '#FFFBEB', borderBottom: '4px solid #FFFBEB',
                        paddingBottom: 2, fontStyle: 'normal' }}>SUSTO</span>.
                    </div>
                  </div>
                  <button onClick={() => setModalCuidados(false)} title="Fechar" aria-label="Fechar"
                    style={{ background: 'rgba(255,255,255,0.18)',
                      border: '1px solid rgba(255,255,255,0.40)', color: '#FFF',
                      width: 30, height: 30, borderRadius: 0, cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center', justifyContent: 'center',
                      padding: 0, fontFamily: 'inherit' }}><IconeFechar /></button>
                </div>
              </div>
              <div style={{ padding: '20px 32px 24px', overflowY: 'auto', flex: 1 }}>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8,
                  marginBottom: 20, paddingBottom: 18,
                  borderBottom: `1px solid ${G.borda}` }}>
                  {(planoAtual.freios || []).map((f, i) => {
                    const obj = typeof f === 'string' ? { timing: '!', titulo: f } : f;
                    return (
                      <span key={i} style={{
                        display: 'inline-flex', alignItems: 'center', gap: 8,
                        padding: '4px 10px',
                        border: `1px solid ${G.laranja}33`, borderRadius: 0,
                        background: G.laranjaLight }}>
                        <span style={{
                          fontFamily: "Impact, 'Arial Narrow Bold', sans-serif",
                          fontSize: 11, fontWeight: 900, color: '#FFF',
                          background: G.laranja, padding: '2px 6px',
                          borderRadius: 0, letterSpacing: '0.04em',
                        }}>{obj.timing}</span>
                        <span style={{
                          fontFamily: "'Outfit', sans-serif",
                          fontSize: 12, fontWeight: 700, fontStyle: 'italic',
                          color: G.laranja, letterSpacing: '-0.005em',
                        }}>{obj.titulo}</span>
                      </span>
                    );
                  })}
                </div>
                <div style={{ fontSize: 10, fontWeight: 700, color: G.laranja,
                  letterSpacing: '0.18em', textTransform: 'uppercase', marginBottom: 12 }}>
                  Por que esses cuidados
                </div>
                <div style={{ fontSize: 14, color: G.txt1, lineHeight: 1.65, fontWeight: 500 }}>
                  {(planoAtual.cuidados_explicacao || '').split('\n\n').map((par, i) => {
                    const partes = par.split('**');
                    const renderTexto = (pulaPrimeiroChar) => partes.map((p, k) => {
                      let conteudo = (pulaPrimeiroChar && k === 0) ? p.slice(1) : p;
                      return k % 2 === 1
                        ? <strong key={k} style={{ color: G.laranja, fontWeight: 700 }}>{p}</strong>
                        : <React.Fragment key={k}>{conteudo}</React.Fragment>;
                    });
                    return (
                      <p key={i} style={{ margin: '0 0 12px' }}>
                        {i === 0 ? (
                          <>
                            <span style={{ float: 'left',
                              fontFamily: "Impact, 'Arial Narrow Bold', sans-serif",
                              fontSize: 50, fontWeight: 900, lineHeight: 0.85,
                              color: G.laranja, marginRight: 10, marginTop: 4,
                            }}>{par.charAt(0)}</span>
                            {renderTexto(true)}
                          </>
                        ) : renderTexto(false)}
                      </p>
                    );
                  })}
                </div>
              </div>
              <div style={{ padding: '12px 32px', background: '#FAFAF6',
                borderTop: `1px solid ${G.borda}`, fontSize: 11, color: G.txt2,
                fontStyle: 'italic', flexShrink: 0 }}>
                Mapeado pelo <span style={{ display: 'inline-block', background: '#FFF', padding: '2px 9px', borderRadius: 0, verticalAlign: 'baseline', lineHeight: 1.1 }}><MarcaAIdvisor variant="default" /></span>{' '}
                a partir de eventos sazonais e contratuais do calendário.
              </div>
            </div>
          </div>
        )}

        {/* Footer com ações — varia por estado.
            Texto "Editado e validado" (verde, sem card) à esquerda quando aplicável. */}
        <div style={{ display: 'flex',
          justifyContent: validado && !editando ? 'space-between' : 'flex-end',
          alignItems: 'center', gap: 10, marginTop: 18, flexWrap: 'wrap' }}>

          {/* Texto verde de validado — só aparece pós-validado */}
          {validado && !editando && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ color: G.verde, fontWeight: 800, fontSize: 14,
                lineHeight: 1 }}>✓</span>
              <span style={{ color: G.verde, fontSize: 13, fontWeight: 600,
                lineHeight: 1.4 }}>
                <strong style={{ fontWeight: 800 }}>Editado e validado.</strong>{' '}
                <span style={{ fontWeight: 500 }}>Plano virou orçamento congelado dos próximos 12 meses.</span>
              </span>
            </div>
          )}

          <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
          <button onClick={() => setPasso('meta')}
            style={{ padding: '10px 18px', borderRadius: 0,
              border: `1px solid ${G.borda}`, background: '#fff', color: G.txt1,
              cursor: 'pointer', fontSize: 12, fontWeight: 600,
              letterSpacing: '0.06em', textTransform: 'uppercase' }}>
            ⟲ Revisar Metas
          </button>

          {validado && !editando && (
            <button onClick={reabrirEdicaoPosValidado}
              style={{ padding: '10px 18px', borderRadius: 0,
                border: `1px solid ${G.verde}`, background: '#fff', color: G.verde,
                cursor: 'pointer', fontSize: 12, fontWeight: 700,
                letterSpacing: '0.06em', textTransform: 'uppercase' }}>
              Editar Plano
            </button>
          )}

          {!editando && !validado && (
            <>
              <button onClick={onAbrirConfig}
                style={{ padding: '10px 18px', borderRadius: 0,
                  border: `1px solid ${G.borda}`, background: '#fff', color: G.txt1,
                  cursor: 'pointer', fontSize: 12, fontWeight: 600,
                  letterSpacing: '0.06em', textTransform: 'uppercase' }}>
                Parâmetros
              </button>
              <button onClick={entrarEdicao}
                style={{ padding: '10px 18px', borderRadius: 0,
                  border: `1px solid ${G.verde}`, background: '#fff', color: G.verde,
                  cursor: 'pointer', fontSize: 12, fontWeight: 700,
                  letterSpacing: '0.06em', textTransform: 'uppercase' }}>
                Editar Plano
              </button>
              <button onClick={validar} disabled={validando}
                style={{ padding: '10px 22px', borderRadius: 0, border: 'none',
                  minWidth: 190, textAlign: 'center',
                  background: validando ? G.borda : G.verde,
                  color: validando ? G.txt2 : '#fff',
                  cursor: validando ? 'wait' : 'pointer', fontSize: 12, fontWeight: 700,
                  letterSpacing: '0.06em', textTransform: 'uppercase',
                  boxShadow: validando ? 'none' : `0 2px 8px ${G.verde}44` }}>
                {validando ? 'Congelando…' : 'Validar e Seguir'}
              </button>
            </>
          )}

          {editando && (
            <>
              <button onClick={cancelarEdicao}
                style={{ padding: '10px 18px', borderRadius: 0,
                  border: `1px solid ${G.borda}`, background: '#fff', color: G.txt2,
                  cursor: 'pointer', fontSize: 12, fontWeight: 600,
                  letterSpacing: '0.06em', textTransform: 'uppercase' }}>
                Cancelar
              </button>
              <button onClick={recalcular}
                style={{ padding: '10px 18px', borderRadius: 0,
                  border: `1px solid ${G.laranja}`, background: G.laranjaLight, color: G.laranja,
                  cursor: 'pointer', fontSize: 12, fontWeight: 700,
                  letterSpacing: '0.06em', textTransform: 'uppercase' }}>
                Recalcular Plano →
              </button>
              <button onClick={validar} disabled={validando}
                style={{ padding: '10px 22px', borderRadius: 0, border: 'none',
                  minWidth: 190, textAlign: 'center',
                  background: validando ? G.borda : G.verde,
                  color: validando ? G.txt2 : '#fff',
                  cursor: validando ? 'wait' : 'pointer', fontSize: 12, fontWeight: 700,
                  letterSpacing: '0.06em', textTransform: 'uppercase',
                  boxShadow: validando ? 'none' : `0 2px 8px ${G.verde}44` }}>
                {validando ? 'Congelando…' : 'Validar e Seguir'}
              </button>
            </>
          )}
          </div>{/* fim agrupador botões */}
        </div>

        {erroValidar && (
          <div style={{ marginTop: 14, padding: '12px 18px', background: G.vermelhoL,
            borderLeft: `3px solid ${G.vermelho}`, borderRadius: 0,
            fontSize: 12.5, color: G.vermelho, lineHeight: 1.5 }}>
            <strong>O plano NÃO foi congelado.</strong> {erroValidar} O Previsto x
            Real continua sem este plano — tente novamente.
          </div>
        )}
        {validado && !erroValidar && janelaGravada && (
          <div style={{ marginTop: 14, padding: '12px 18px', background: G.verdeLight,
            borderLeft: `3px solid ${G.verde}`, borderRadius: 0,
            fontSize: 12.5, color: G.txt1, lineHeight: 1.5 }}>
            <strong>Orçamento congelado.</strong> O plano virou o orçamento de{' '}
            <strong>{rotuloMesBase(janelaGravada.de)}</strong> a{' '}
            <strong>{rotuloMesBase(janelaGravada.ate)}</strong>
            {janelaGravada.linhas != null && ` · ${janelaGravada.linhas} linhas gravadas`}
            {' '}e já aparece na aba <strong>Previsto x Real</strong>.
          </div>
        )}

        <p style={{ fontSize: 11, color: G.txt2, marginTop: 16, fontStyle: 'italic' }}>
          Ao validar, os 12 meses do plano viram o <strong>orçamento congelado</strong> —
          o orçamento contra o qual o Previsto x Real compara o realizado, mês a mês.
        </p>

      </div>
    </div>
  );
};

// Tela Business Plan 5A — visão estratégica de 5 anos goal-driven (EBITDA 5x etc).
// Compartilha tabela `planos` com TelaPlano via campo `tipo`. Conteúdo seedado
// para MVP (depende da Lacuna #1 — Integração IA Claude — para gerar dinâmicamente).
//
// Wizard de 2 passos (MVP):
//   Passo 1: Form de Meta (tipo + valor) — empresário escolhe condições de contorno
//   Passo 2: Plano gerado (apresentação)
// Passos 3+4 (negociação iterativa de alavancas/freios) ficam para próxima iteração.

// Opções de meta por setor — chips contextualizados pelo perfil de crescimento típico
// do segmento. Sempre tem "Outro" para o empresário escapar das opções (campo livre).
const META_OPCOES_POR_SETOR = {
  'Tecnologia/SaaS':         { ebitda: [3, 5, 7, 10], receita: [3, 5, 7, 10], margem: [20, 25, 30, 40], valor: [5, 7, 10, 15] },
  'E-commerce':              { ebitda: [3, 5, 7, 10], receita: [3, 5, 7], margem: [10, 15, 20, 25], valor: [3, 5, 7, 10] },
  'Saude':                   { ebitda: [2, 3, 5],    receita: [2, 3, 5],    margem: [15, 20, 25, 30], valor: [2, 3, 5, 7] },
  'Construcao Civil':        { ebitda: [1.5, 2, 3],  receita: [1.5, 2, 3],  margem: [10, 15, 20],     valor: [1.5, 2, 3] },
  'Bar/Restaurante':         { ebitda: [1.5, 2, 3],  receita: [1.5, 2, 3],  margem: [10, 15, 20],     valor: [1.5, 2, 3] },
  'Industria/Manufatura':    { ebitda: [2, 3, 5],    receita: [2, 3, 5],    margem: [10, 15, 20, 25], valor: [2, 3, 5] },
  'Servicos Profissionais':  { ebitda: [2, 3, 5],    receita: [2, 3, 5],    margem: [15, 20, 30, 40], valor: [2, 3, 5] },
  'Varejo Fisico':           { ebitda: [2, 3, 5],    receita: [2, 3, 5],    margem: [8, 12, 15, 20],  valor: [2, 3, 5] },
  // Demais setores (Agro, Educacao, Logistica, etc) caem no default
  default:                   { ebitda: [2, 3, 5, 7], receita: [2, 3, 5, 7], margem: [10, 15, 20, 25], valor: [2, 3, 5, 7] },
};

const TIPOS_META = [
  { id: 'ebitda',  label: 'Multiplicar EBITDA',    unidade: '×',   ajuda: 'Quanto vezes o EBITDA atual no horizonte de 5 anos' },
  { id: 'receita', label: 'Multiplicar Receita',   unidade: '×',   ajuda: 'Quanto vezes o faturamento atual em 5 anos' },
  { id: 'margem',  label: 'Margem EBITDA alvo',    unidade: '%',   ajuda: 'Chegar a uma margem EBITDA específica em 5 anos' },
  { id: 'valor',   label: 'Multiplicar Valor da Empresa', unidade: '×', ajuda: 'Quanto vezes o valuation atual em 5 anos' },
];

// ═══════════════════════════════════════════════════════════════════════
// <TelaBP60M /> — wrapper da jornada Business Plan 60M
// Estado inicial: 'preview-template' (demonstra Tarefa 7 SWOT com placeholder
// no shell <TarefaBP60M />). Próximas tarefas (landing, Tarefa 1, Tarefa 2...)
// virão substituindo este state aos poucos. A TelaBusinessPlan antiga continua
// existindo (preservada) e será reaproveitada como conteúdo de uma das tarefas.
// ═══════════════════════════════════════════════════════════════════════
// ═══════════════════════════════════════════════════════════════════════
// ICONES_BP60M — 1 ícone POR TAREFA (20 ícones), com equivalência semântica.
// Indexado por ID da tarefa (1-20). Lucide-style stroke 1.5.
// ═══════════════════════════════════════════════════════════════════════
const _svg = (paths) => (
  <svg width="100" height="100" viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    {paths}
  </svg>
);
const ICONES_BP60M = {
  // Reestruturação 12/06/2026: BP60M passa de 20 → 18 tarefas com fusões.
  // 01 — Meta & Equity (target — 3 círculos concêntricos)
  1: _svg(<><circle cx="12" cy="12" r="10"/><circle cx="12" cy="12" r="6"/><circle cx="12" cy="12" r="2"/></>),
  // 02 — Descrição da Empresa (edifício)
  2: _svg(<><rect x="4" y="2" width="16" height="20" rx="1"/><line x1="9" y1="6" x2="11" y2="6"/><line x1="13" y1="6" x2="15" y2="6"/><line x1="9" y1="10" x2="11" y2="10"/><line x1="13" y1="10" x2="15" y2="10"/><line x1="9" y1="14" x2="11" y2="14"/><line x1="13" y1="14" x2="15" y2="14"/><path d="M10 22v-4h4v4"/></>),
  // 03 — Visão · Missão · Valores (olho)
  3: _svg(<><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></>),
  // 04 — Produtos & Serviços (caixa 3D / package)
  4: _svg(<><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/><polyline points="3.27 6.96 12 12.01 20.73 6.96"/><line x1="12" y1="22.08" x2="12" y2="12"/></>),
  // 05 — Mercado & Concorrência (fusão · pie-chart)
  5: _svg(<><path d="M21.21 15.89A10 10 0 1 1 8 2.83"/><path d="M22 12A10 10 0 0 0 12 2v10z"/></>),
  // 06 — SWOT (4 quadrados)
  6: _svg(<><rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/><rect x="3" y="14" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/></>),
  // 07 — Estratégia & Modelo de Negócio (xadrez / king piece simplificado)
  7: _svg(<><path d="M5 17h14v3H5z"/><path d="M9 17v-2l-2-7h10l-2 7v2"/><circle cx="12" cy="6" r="2"/><line x1="10" y1="3" x2="14" y2="3"/></>),
  // 08 — Marketing & Vendas (megafone)
  8: _svg(<><polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/><path d="M19.07 4.93a10 10 0 0 1 0 14.14M15.54 8.46a5 5 0 0 1 0 7.07"/></>),
  // 09 — Plano Operacional (engrenagem)
  9: _svg(<><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"/></>),
  // 10 — Estrutura Org & RH (pessoas)
  10: _svg(<><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 00-3-3.87"/><path d="M16 3.13a4 4 0 010 7.75"/></>),
  // 11 — Análise de Riscos (triângulo de aviso)
  11: _svg(<><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></>),
  // 12 — ESG & Sustentabilidade (folha · NOVA tarefa)
  12: _svg(<><path d="M11 20A7 7 0 0 1 9.8 6.1C15.5 5 17 4.48 19 2c1 2 2 4.18 2 8 0 5.5-4.78 10-10 10z"/><path d="M2 21c0-3 1.85-5.36 5.08-6"/></>),
  // 13 — Projeções 5 anos (gráfico de linha ascendente)
  13: _svg(<><polyline points="3 17 9 11 13 15 21 7"/><polyline points="14 7 21 7 21 14"/></>),
  // 14 — Valuation DCF (cifrão)
  14: _svg(<><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></>),
  // 15 — Estrutura de Capital · Funding & Exit (cofre · fusão)
  15: _svg(<><path d="M20 12V8H6a2 2 0 0 1 0-4h12v4"/><path d="M4 6v12c0 1.1.9 2 2 2h14v-4"/><path d="M18 12a2 2 0 0 0 0 4h4v-4z"/></>),
  // 16 — Apêndices (paperclip)
  16: _svg(<><path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"/></>),
  // 17 — Materiais de Captação (Teaser + Pitch Deck/Apresentação)
  17: _svg(<><rect x="2" y="3" width="20" height="14" rx="2"/><line x1="8" y1="21" x2="16" y2="21"/><line x1="12" y1="17" x2="12" y2="21"/><polyline points="6 9 10 13 14 9 18 13"/></>),
  // 18 — Entrega & Próximos Passos (troféu · inclui Sumário Executivo absorvido)
  18: _svg(<><circle cx="12" cy="8" r="7"/><polyline points="8.21 13.89 7 23 12 20 17 23 15.79 13.88"/></>),
};

// ═══════════════════════════════════════════════════════════════════════
// TAREFAS_BP60M — Single source of truth pras 20 tarefas da jornada BP60M.
// Mudou texto/grupo/ícone de uma tarefa? Edite 1 linha aqui → reflete no
// tabuleiro da landing, na navegação, no header de cada tarefa.
// ═══════════════════════════════════════════════════════════════════════
const TAREFAS_BP60M = [
  // Reestruturação 12/06: 20 → 18 tarefas com 3 fusões + 1 nova ESG.
  { id: 1,  titulo: 'Meta & Equity',           grupo: 'inicio',       grupoLabel: 'Definição inicial',
    subtitulo: 'Meta de transformação em 5 anos + tese de Equity.' },
  { id: 2,  titulo: 'Descrição da Empresa',    grupo: 'apresentacao', grupoLabel: 'Apresentação',
    subtitulo: 'Histórico, estrutura, instalações, equipe-chave.' },
  { id: 3,  titulo: 'Visão · Missão · Valores', grupo: 'apresentacao', grupoLabel: 'Apresentação',
    subtitulo: 'Aspiração de longo prazo + razão de existir + princípios.' },
  { id: 4,  titulo: 'Produtos & Serviços',     grupo: 'apresentacao', grupoLabel: 'Apresentação',
    subtitulo: 'Portfólio, diferenciais e roadmap de inovação.' },
  { id: 5,  titulo: 'Mercado & Concorrência',  grupo: 'estrategica',  grupoLabel: 'Análise Estratégica',
    subtitulo: 'TAM · SAM · SOM + players principais e posicionamento competitivo.' },
  { id: 6,  titulo: 'SWOT',                    grupo: 'estrategica',  grupoLabel: 'Análise Estratégica',
    subtitulo: 'Forças, Fraquezas, Oportunidades, Ameaças consolidadas.' },
  { id: 7,  titulo: 'Estratégia & Modelo de Negócio', grupo: 'execucao', grupoLabel: 'Modelo de Execução',
    subtitulo: 'Unit economics, pricing, go-to-market.' },
  { id: 8,  titulo: 'Marketing & Vendas',      grupo: 'execucao',     grupoLabel: 'Modelo de Execução',
    subtitulo: 'Posicionamento, canais de aquisição e funil de vendas.' },
  { id: 9,  titulo: 'Plano Operacional',       grupo: 'execucao',     grupoLabel: 'Modelo de Execução',
    subtitulo: 'Cadeia de valor, fornecedores, tecnologia, infraestrutura.' },
  { id: 10, titulo: 'Estrutura Org & RH',      grupo: 'execucao',     grupoLabel: 'Modelo de Execução',
    subtitulo: 'Organograma, hires-chave, plano de pessoas 5 anos.' },
  { id: 11, titulo: 'Análise de Riscos',       grupo: 'risco',        grupoLabel: 'Risco',
    subtitulo: 'Riscos de mercado, operacional, regulatório + mitigação.' },
  { id: 12, titulo: 'ESG',                     grupo: 'risco',        grupoLabel: 'Risco',
    subtitulo: 'Pilares ambiental, social e governança · riscos ESG · compromissos públicos.' },
  { id: 13, titulo: 'Projeções 5 anos',        grupo: 'financeiro',   grupoLabel: 'Financeiro',
    subtitulo: 'DRE, FCF, Balanço, CAPEX, sensibilidade e cenários.' },
  { id: 14, titulo: 'Valuation',         grupo: 'financeiro',   grupoLabel: 'Financeiro',
    subtitulo: 'DCF, múltiplos comparáveis, sensibilidade.' },
  { id: 15, titulo: 'Conclusão', grupo: 'financeiro', grupoLabel: 'Financeiro',
    subtitulo: 'Confirmação das decisões de T1 (Funding · Exit) à luz das projeções e valuation · Tese final RUNGOAL AIDVISOR para o empresário sair a campo.' },
  { id: 16, titulo: 'Entrega',                 grupo: 'entrega',      grupoLabel: 'Finalização',
    subtitulo: 'Sumário Executivo + 4 materiais (Teaser · Pitch Deck · IM · Plano de Ação Interno) com download em PDF + próximos passos.' },
];

// ═══════════════════════════════════════════════════════════════════════
// TAREFAS_BP60M_SU · 17 tarefas para a modalidade Startup. T13 nova
// "Estruturação Financeira Inicial" (insere ANTES de Projeções) ·
// T13..T16 do EE viram T14..T17 no SU. Numeração e títulos podem variar
// conforme personalizações sejam aplicadas tarefa por tarefa.
// ═══════════════════════════════════════════════════════════════════════
const TAREFAS_BP60M_SU = [
  ...TAREFAS_BP60M.slice(0, 12),
  { id: 13, titulo: 'Estruturação Financeira Inicial', grupo: 'financeiro', grupoLabel: 'Financeiro',
    subtitulo: 'Receitas · Custos Diretos · OPEX · Impostos · CAPEX · Capital de Giro · Runway · construídos do zero como hipótese sólida do futuro.' },
  ...TAREFAS_BP60M.slice(12).map(t => ({ ...t, id: t.id + 1, ...(t.id === 14 ? { titulo: 'Valuation', subtitulo: 'Múltiplos · DCF · Capital de Risco.' } : {}) })),
];

// ═══════════════════════════════════════════════════════════════════════
// <LandingBP60M /> — porta de entrada do BP60M (boas-vindas premium)
// Renderiza:
//   1. Hero verde escuro + título "Articule a tese de investimento"
//      + parágrafo num quadro branco contendo <MarcaAIdvisor />
//   2. 5 finalidades (captar equity, captar dívida, vender M&A, profissionalizar, sucessão)
//   3. Tabuleiro de 18 tarefas
//   4. Como funciona (4 cards)
//   5. Garantias (Confidencialidade · Credibilidade)
//   6. FAQ (5 perguntas em grid 2 colunas, <details> nativo)
//   7. CTA Final
//   8. Selo Premium
// ═══════════════════════════════════════════════════════════════════════
const LandingBP60M = ({ onComecar, onContinuar, onVerExemplo, temProgresso = false, tarefasFeitas = [], onIrParaTarefa, modo = 'EE' }) => {
  // Modo da modalidade · 'EE' = Empresa Estabelecida · 'SU' = Startup.
  // Marca d'água no canto superior direito do Hero identifica visualmente
  // qual jornada está rodando. EE quebra em 2 linhas pra caber bem no canto.
  const marcaDaguaLinhas = modo === 'SU'
    ? ['STARTUP']
    : [];
  // Quem está clicando: admin tem acesso livre a qualquer tarefa; usuário só nas feitas + próxima não-feita.
  const ehAdmin = typeof window !== 'undefined' && !!window.__ehAdmin;
  // Próxima tarefa não-feita = "atual" sob a ótica do usuário. Se todas feitas, null.
  const proxNaoFeitaId = (() => {
    const total = modo === 'SU' ? 17 : 16;
    for (let i = 1; i <= total; i++) if (!tarefasFeitas.includes(i)) return i;
    return null;
  })();
  // Hover state pra animação de scale dos quadrinhos clicáveis
  const [hoveredId, setHoveredId] = useState(null);
  const isClicavel = (n) => ehAdmin || tarefasFeitas.includes(n) || n === proxNaoFeitaId;
  // Estilos compartilhados (referenciam G para coerência)
  const VERDE_DEEP = '#134721';
  const BEGE = '#F5F5F1';
  // Modal "Saiba mais" — concentra 5 Finalidades + Como Funciona + Garantias + FAQ
  // Versão compacta scroll-free 12/06: Landing tem só Hero + Tabuleiro + Rodapé.
  const [saibaMais, setSaibaMais] = useState(false);

  // Tabuleiro · 16 tarefas (Empresa Estabelecida) ou 17 tarefas (Startup ·
  // com T13 Estruturação Financeira Inicial inserida antes das Projeções).
  // Fonte canônica: TAREFAS_BP60M e TAREFAS_BP60M_SU (objetos completos).
  const TAREFAS_OBJ = modo === 'SU' ? TAREFAS_BP60M_SU : TAREFAS_BP60M;
  const TAREFAS = TAREFAS_OBJ.map(t => t.titulo);

  // 5 finalidades (Por quê & Quando)
  const FINALIDADES = [
    { titulo: 'Captar Equity',     desc: 'Fundo · VC · anjo',
      icon: <><path d="M3 3v18h18"/><path d="M7 17l4-4 4 4 4-6"/></> },
    { titulo: 'Captar Dívida',     desc: 'Banco · debênture',
      icon: <><rect x="3" y="7" width="18" height="13" rx="2"/><path d="M8 7V4a1 1 0 011-1h6a1 1 0 011 1v3"/></> },
    { titulo: 'Vender (M&A)',      desc: 'Exit · secondary',
      icon: <><path d="M7 17l5-5 4 4 5-7"/><path d="M7 7h10v10"/></> },
    { titulo: 'Profissionalizar', desc: 'Ativo investível',
      icon: <><circle cx="12" cy="12" r="9"/><path d="M12 7v5l3 2"/></> },
    { titulo: 'Sucessão · JV',     desc: 'Alinhar sócios',
      icon: <><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 00-3-3.87"/><path d="M16 3.13a4 4 0 010 7.75"/></> },
  ];

  // FAQ
  const FAQ = [
    { q: 'Posso fazer junto com meu CFO ou sócio?',
      a: 'Sim. Você convida usuários adicionais com permissão de leitura ou edição.' },
    { q: 'Tem histórico de quem editou o quê?',
      a: 'Sim. Cada tarefa registra autor, data e versão. Reverte versão a qualquer momento.' },
    { q: 'Como funciona o pagamento? Tem demo gratuita?',
      a: 'BP60M faz parte do plano sênior. Antes, exemplo navegável da ShopMax + 3 primeiras tarefas em modo demo.' },
    { q: 'Posso usar para mais de uma empresa minha?',
      a: 'Sim. Plano sênior cobre até 3 empresas (holdings, spin-offs, JVs). Adicionais via add-on.' },
    { q: 'Em que formatos eu exporto o BP final?',
      a: 'PDF (40-80 páginas) · Pitch Deck em PPTX/PDF · planilhas DCF em XLSX · CSVs granulares.' },
  ];

  // Botões helpers
  const ctaPrimario = {
    background: G.laranja, color: '#FFF', padding: '14px 32px',
    fontSize: 13, fontWeight: 800, letterSpacing: '0.10em',
    textTransform: 'uppercase', borderRadius: 0, border: 'none',
    cursor: 'pointer', fontFamily: 'inherit',
    boxShadow: '0 4px 16px rgba(244,124,60,0.40)',
    display: 'inline-flex', alignItems: 'center', gap: 8,
  };
  const ctaSecundario = {
    background: 'transparent', color: 'rgba(255,255,255,0.85)',
    padding: '13px 22px', fontSize: 12, fontWeight: 700,
    letterSpacing: '0.08em', textTransform: 'uppercase',
    border: '1px solid rgba(255,255,255,0.35)',
    borderRadius: 0, cursor: 'pointer', fontFamily: 'inherit',
    marginLeft: 10,
  };

  // Estilos compactos para rodapé
  return (
    <div style={{ maxWidth: 1100, margin: '0 auto', padding: '20px 28px 24px' }}>

      {/* ═══════════ HERO COMPACTO ═══════════ */}
      <section style={{ background: VERDE_DEEP, color: '#FFF',
        borderRadius: 0, padding: '28px 36px', position: 'relative',
        overflow: 'hidden', marginBottom: 14 }}>
        {/* Marca d'água "60M" */}
        <div style={{ position: 'absolute', right: -30, bottom: -60,
          fontFamily: "'Outfit', sans-serif", fontSize: 200, fontWeight: 900,
          lineHeight: 0.85, color: 'rgba(255,255,255,0.05)',
          pointerEvents: 'none', userSelect: 'none', letterSpacing: '-0.04em' }}>
          60M
        </div>
        {/* Marca d'água da modalidade no canto superior direito (EE ou SU) */}
        <div style={{ position: 'absolute', top: 18, right: 24,
          fontFamily: "'Outfit', sans-serif", fontSize: 28, fontWeight: 900,
          lineHeight: 1.05, color: 'rgba(255,255,255,0.10)',
          pointerEvents: 'none', userSelect: 'none',
          letterSpacing: '0.16em', textTransform: 'uppercase',
          textAlign: 'right' }}>
          {marcaDaguaLinhas.map((linha, i) => (
            <div key={i}>{linha}</div>
          ))}
        </div>
        <div style={{ fontSize: 10, fontWeight: 700,
          color: 'rgba(255,255,255,0.65)', letterSpacing: '0.20em',
          textTransform: 'uppercase', marginBottom: 10 }}>
          Business Plan · 60 meses · padrão M&amp;A
        </div>
        <h1 style={{ fontSize: 32, fontWeight: 900, lineHeight: 1.1,
          letterSpacing: '-0.02em', marginBottom: 12, maxWidth: 720,
          fontFamily: "'Outfit', sans-serif" }}>
          Articule a <span style={{ color: G.laranja }}>tese de investimento</span> da sua empresa.
        </h1>
        <p style={{ fontSize: 14, lineHeight: 1.5,
          color: 'rgba(255,255,255,0.92)', maxWidth: 720, marginBottom: 18 }}>
          Em 16 tarefas guiadas, o <span style={{ display: 'inline-block', background: '#FFF', padding: '2px 9px', borderRadius: 0, verticalAlign: 'baseline', lineHeight: 1.1 }}><MarcaAIdvisor variant="default" /></span> ajuda você a construir
          um Business Plan de 5 anos no padrão Boutique de M&amp;A — narrativa, DCF,
          valuation, cap table · pronto para fundos, bancos, compradores ou sócios.
        </p>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
          <button onClick={onComecar} style={ctaPrimario}>Começar Jornada →</button>
          {temProgresso && (
            <button onClick={onContinuar} style={ctaSecundario}>Continuar de onde parei</button>
          )}
        </div>
      </section>

      {/* ═══════════ TABULEIRO DE 16 TAREFAS ═══════════ */}
      <section style={{ background: '#FFF', borderRadius: 0,
        padding: '20px 28px', marginBottom: 14 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between',
          alignItems: 'baseline', marginBottom: 14 }}>
          <h2 style={{ fontSize: 16, fontWeight: 800, color: G.txt1,
            margin: 0, letterSpacing: '-0.01em',
            fontFamily: "'Outfit', sans-serif" }}>
            16 tarefas modulares — você dirige o ritmo
          </h2>
          <span style={{ fontSize: 11, color: G.txt2, fontStyle: 'italic' }}>
            ~2-6h de dedicação · em quantas sessões quiser
          </span>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(9, 1fr)',
          gap: 6 }}>
          {TAREFAS.map((nome, i) => {
            const n = i + 1;
            const final = n === TAREFAS.length;
            const feita = tarefasFeitas.includes(n);
            const verde = final || feita;
            const clicavel = isClicavel(n);
            const ativo = hoveredId === n && clicavel;
            return (
              <div key={n}
                onClick={clicavel && onIrParaTarefa ? () => onIrParaTarefa(n) : undefined}
                onMouseEnter={() => clicavel && setHoveredId(n)}
                onMouseLeave={() => setHoveredId(null)}
                style={{
                  position: 'relative', overflow: 'hidden',
                  aspectRatio: '1', background: verde ? G.verde : G.laranja,
                  border: `1.5px solid ${verde ? G.verde : G.laranja}`,
                  borderRadius: 0, display: 'flex',
                  alignItems: 'center', justifyContent: 'center', padding: 6,
                  opacity: clicavel ? 1 : 0.55,
                  cursor: clicavel ? 'pointer' : 'not-allowed',
                  transform: ativo ? 'scale(1.04)' : 'scale(1)',
                  transition: 'transform 0.15s, opacity 0.15s',
                }}>
                {/* Marca d'água · número grande translúcido no canto superior direito */}
                <div style={{ position: 'absolute', top: -6, right: 4,
                  fontSize: 78, fontWeight: 900,
                  color: '#FFF',
                  opacity: verde ? 0.18 : 0.22,
                  lineHeight: 1, pointerEvents: 'none', userSelect: 'none',
                  letterSpacing: '-0.04em',
                  fontFamily: "'Outfit', sans-serif" }}>
                  {n}
                </div>
                {/* Nome da tarefa centralizado · acima da marca d'água */}
                <div style={{ position: 'relative', zIndex: 1,
                  fontSize: 15, fontWeight: 800,
                  color: '#FFF', textAlign: 'center',
                  lineHeight: 1.15, letterSpacing: '-0.01em',
                  fontFamily: "'Outfit', sans-serif",
                  overflowWrap: 'break-word' }}>
                  {nome}
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* ═══════════ RODAPÉ COMPACTO · CTA + Saiba mais ═══════════ */}
      <section style={{ display: 'flex', justifyContent: 'space-between',
        alignItems: 'center', flexWrap: 'wrap', gap: 14,
        padding: '4px 6px' }}>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginLeft: 'auto' }}>
          <BotaoSaibaMais onClick={() => setSaibaMais(true)} contorno tamanho="lg" seta={false} />
          <button onClick={onVerExemplo} style={{
            ...ctaSecundario, color: G.txt2,
            borderColor: G.borda, marginLeft: 0,
          }}>
            Ver exemplo (ShopMax)
          </button>
          <button onClick={onComecar} style={ctaPrimario}>Começar →</button>
        </div>
      </section>

      {/* ═══════════ MODAL · SAIBA MAIS ═══════════ */}
      {saibaMais && (
        <div onClick={() => setSaibaMais(false)}
          style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            background: 'rgba(0,0,0,0.5)', zIndex: 100,
            display: 'flex', justifyContent: 'center', alignItems: 'center',
            padding: 24,
          }}>
          <div onClick={e => e.stopPropagation()}
            style={{
              background: '#FFF', maxWidth: 860, width: '100%',
              maxHeight: '90vh', overflow: 'auto',
              borderRadius: 0, padding: '28px 36px',
              boxShadow: '0 10px 40px rgba(0,0,0,0.25)',
              position: 'relative',
            }}>
            {/* Header do modal */}
            <div style={{ display: 'flex', justifyContent: 'space-between',
              alignItems: 'flex-start', marginBottom: 18, gap: 14,
              borderBottom: `1px solid ${G.borda}`, paddingBottom: 14 }}>
              <div>
                <div style={{ fontSize: 11, fontWeight: 700,
                  letterSpacing: '0.10em', textTransform: 'uppercase',
                  color: G.verde, marginBottom: 4 }}>
                  BP60M · saiba mais
                </div>
                <div style={{ fontSize: 20, fontWeight: 800, color: G.txt1 }}>
                  Como funciona, garantias e perguntas
                </div>
              </div>
              <button onClick={() => setSaibaMais(false)}
                style={{ background: 'transparent', border: 'none',
                  fontSize: 24, cursor: 'pointer', color: G.txt2,
                  padding: 0, lineHeight: 1 }} title="Fechar" aria-label="Fechar"><IconeFechar /></button>
            </div>

            {/* 5 Finalidades */}
            <div style={{ marginBottom: 22 }}>
              <div style={{ fontSize: 11, fontWeight: 800, color: G.verde,
                letterSpacing: '0.16em', textTransform: 'uppercase',
                marginBottom: 8 }}>
                Por quê &amp; Quando fazer
              </div>
              <p style={{ ...TIPO.subtitulo, margin: '0 0 14px' }}>
                Um BP de 60 meses cristaliza um argumento defensável de
                {' '}<strong style={{ color: G.txt1 }}>transformação de valor</strong>{' '}
                da sua empresa. Cinco situações típicas:
              </p>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)',
                gap: 10 }}>
                {FINALIDADES.map((f, i) => (
                  <div key={i} style={{ background: BEGE, borderRadius: 0,
                    padding: '12px 10px', textAlign: 'center' }}>
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none"
                      stroke={G.verde} strokeWidth="2" strokeLinecap="round"
                      strokeLinejoin="round" style={{ marginBottom: 6 }}>
                      {f.icon}
                    </svg>
                    <div style={{ fontSize: 12, fontWeight: 800,
                      color: G.txt1, marginBottom: 2 }}>{f.titulo}</div>
                    <div style={{ fontSize: 10, color: G.txt2, lineHeight: 1.35 }}>
                      {f.desc}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Como funciona */}
            <div style={{ marginBottom: 22 }}>
              <div style={{ fontSize: 11, fontWeight: 800, color: G.verde,
                letterSpacing: '0.16em', textTransform: 'uppercase',
                marginBottom: 8 }}>
                Como funciona · você dirige · <MarcaAIdvisor /> acelera
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)',
                gap: 10 }}>
                {[
                  { t: 'Modular', d: 'Cada tarefa é independente. Avança quando completar a missão atual.' },
                  { t: 'IA especialista', d: <><MarcaAIdvisor variant="default" /> aplica conhecimento M&A · setor · indicadores reais.</> },
                  { t: 'Empresário decide', d: 'IA propõe, você seleciona e valida. Edição livre em qualquer ponto.' },
                  { t: 'Entrega profissional', d: 'PDF completo · Pitch Deck slides · planilhas DCF · pronto para apresentar.' },
                ].map((c, i) => (
                  <div key={i} style={{ background: BEGE, borderRadius: 0,
                    padding: '11px 14px', borderLeft: `3px solid ${G.laranja}` }}>
                    <div style={{ fontSize: 12, fontWeight: 800, color: G.txt1,
                      marginBottom: 4, textTransform: 'uppercase',
                      letterSpacing: '0.05em' }}>{c.t}</div>
                    <div style={{ fontSize: 12, color: G.txt2, lineHeight: 1.45 }}>
                      {c.d}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Garantias */}
            <div style={{ marginBottom: 22, display: 'grid',
              gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <div style={{ padding: '12px 16px',
                borderLeft: `4px solid ${G.verde}`,
                background: G.verdeLight, borderRadius: 0 }}>
                <div style={{ fontSize: 12, fontWeight: 800, color: G.verde,
                  letterSpacing: '0.08em', textTransform: 'uppercase',
                  marginBottom: 4 }}>Confidencialidade</div>
                <div style={{ fontSize: 12, color: G.txt1, lineHeight: 1.45 }}>
                  Seus dados não treinam modelos públicos · LGPD operacional ·
                  export/delete a qualquer momento.
                </div>
              </div>
              <div style={{ padding: '12px 16px',
                borderLeft: `4px solid ${G.laranja}`,
                background: G.laranjaLight, borderRadius: 0 }}>
                <div style={{ fontSize: 12, fontWeight: 800, color: G.laranja,
                  letterSpacing: '0.08em', textTransform: 'uppercase',
                  marginBottom: 4 }}>Credibilidade do output</div>
                <div style={{ fontSize: 12, color: G.txt1, lineHeight: 1.45 }}>
                  Formato Boutique de M&amp;A · DCF e múltiplos defensáveis ·
                  pronto para VC, banco ou comprador.
                </div>
              </div>
            </div>

            {/* FAQ */}
            <div>
              <div style={{ fontSize: 11, fontWeight: 800, color: G.verde,
                letterSpacing: '0.16em', textTransform: 'uppercase',
                marginBottom: 8 }}>
                Perguntas frequentes
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr',
                columnGap: 22, rowGap: 0 }}>
                {FAQ.map((f, i) => (
                  <details key={i} style={{ borderTop: `1px solid ${G.borda}`,
                    padding: '8px 0', cursor: 'pointer',
                    borderBottom: i >= FAQ.length - 2 ? `1px solid ${G.borda}` : 'none' }}>
                    <summary style={{ fontSize: 12, fontWeight: 700, color: G.txt1,
                      listStyle: 'none', display: 'flex',
                      justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
                      {f.q}
                      <span style={{ fontSize: 16, fontWeight: 600,
                        color: G.verde, flexShrink: 0 }}>+</span>
                    </summary>
                    <div style={{ marginTop: 6, fontSize: 12, color: G.txt2,
                      lineHeight: 1.5, paddingRight: 20 }}>
                      {f.a}
                    </div>
                  </details>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════
// <TelaBalancoPatrimonial /> — aba premium (gate AiPro).
// Snapshot patrimonial montado a partir de GET /empresas/{id}/balanco.
// Ativo (Caixa+Aplicações+Estoque+Imobilizado) = Passivo (CP+LP) + PL
// (Capital Social + Resultado Acumulado plug). Sempre fecha por construção.
// Pensado em 10/06/2026.
// ═══════════════════════════════════════════════════════════════════════

// ═══════════════════════════════════════════════════════════════════════
// MEDIDORES + <MiniMedidor/> — escopo de MÓDULO (30/07/2026)
// ═══════════════════════════════════════════════════════════════════════
// Nasceram dentro da TelaBalancoPatrimonial. Subiram para cá quando o
// COMENTÁRIO DA ASSESSORIA passou a existir em cinco telas (Resumo Mensal ·
// Alertas · DRE · Balanço · Valor): copiar a barra de medidor cinco vezes
// seria criar cinco versões que divergem na primeira mudança de faixa.
//
// Cada medidor declara as FAIXAS com a cor e o rótulo — é delas que sai o
// veredito visual. Mudar uma faixa aqui muda em todas as telas.
const MEDIDORES = {
  liquidez: {
    label: 'Liquidez Imediata',
    descricao: 'Caixa + Aplicações ÷ Passivo Circulante',
    // Teto ampliado de 1,5× para 6,0× em 30/07. A MORED tem 7,07× — quase
    // cinco vezes o teto antigo — e a tela exibia "≥ 1,5×". Não era erro de
    // conta: era a ESCALA APAGANDO O NÚMERO. Quem lia "≥ 1,5×" entendia
    // "no limite superior da faixa confortável", quando o caso real é caixa
    // ocioso (R$ 1,74 mi para R$ 246 mil de passivo curto) — conversa
    // completamente diferente, sobre aplicar ou distribuir.
    //
    // Acima de 6× a barra continua saturando, mas o VALOR mostrado passa a
    // ser o real (ver `fmt`): a régua tem fim, o número não.
    min: 0, max: 6.0,
    faixas: [
      { ate: 0.2, cor: G.vermelho, rotulo: 'crítica' },
      { ate: 0.5, cor: '#B45309',  rotulo: 'baixa' },
      { ate: 1.0, cor: '#FCD34D',  rotulo: 'razoável' },
      { ate: 2.5, cor: G.verde,    rotulo: 'confortável' },
      { ate: 6.0, cor: '#84CC16',  rotulo: 'caixa ocioso' },
    ],
    // Mostra SEMPRE o valor real, mesmo quando passa do teto da régua. O
    // formato antigo (`≥ 1,5×`) escondia a diferença entre 1,6× e 7,1× —
    // situações opostas exibidas com o mesmo texto.
    fmt: v => v.toFixed(1).replace('.', ',') + '×',
    fmtTick: v => v.toFixed(1).replace('.', ',') + '×',
  },
  endividamento: {
    // "Endividamento Total" dizia uma coisa e continha outra (30/07). Na MORED,
    // dos R$ 2,12 mi de passivo, R$ 1,82 mi é DIVIDENDO A PAGAR aos sócios e a
    // dívida onerosa registrada é ZERO — mas o rótulo sugeria alavancagem
    // bancária de 33%. Para banco, comprador e sócio, dever ao próprio dono e
    // dever a terceiro são situações opostas.
    //
    // O indicador continua existindo, com o nome do que ele mede. Quem quer
    // ver dívida vê `endividamento_oneroso`, logo abaixo.
    label: 'Passivo sobre Ativo',
    descricao: 'Passivo Total ÷ Ativo Total — inclui obrigações com sócios',
    min: 0, max: 100,
    faixas: [
      { ate: 30,  cor: G.verde,    rotulo: 'conservador' },
      { ate: 50,  cor: '#84CC16',  rotulo: 'equilibrado' },
      { ate: 70,  cor: '#B45309',  rotulo: 'elevado' },
      { ate: 100, cor: G.vermelho, rotulo: 'alto' },
    ],
    fmt:     v => v.toFixed(0) + '%',
    fmtTick: v => v.toFixed(0) + '%',
  },
  concentracao_cp: {
    label: 'Concentração no Curto Prazo',
    descricao: 'Passivo Circulante ÷ Passivo Total',
    min: 0, max: 100,
    faixas: [
      { ate: 40,  cor: G.verde,    rotulo: 'baixa' },
      { ate: 70,  cor: '#FCD34D',  rotulo: 'média' },
      { ate: 100, cor: '#B45309',  rotulo: 'alta' },
    ],
    fmt:     v => v.toFixed(0) + '%',
    fmtTick: v => v.toFixed(0) + '%',
  },
};

// ── Mini medidor gráfico ───────────────────────────────────────────────
// Barra horizontal segmentada com cores das faixas + marcador da posição
// atual + ticks nos limites + rótulo da faixa em que o valor cai.
// Compact (padrão Dashboard: cards de DRE/Despesas — barra slim + marker 3×10)
const MiniMedidor = ({ tipo, valor }) => {
  const m = MEDIDORES[tipo];
  if (!m) return null;
  const vClamp = Math.max(m.min, Math.min(valor, m.max));
  const posPct = ((vClamp - m.min) / (m.max - m.min)) * 100;
  const faixaAtual = m.faixas.find(f => vClamp <= f.ate) || m.faixas[m.faixas.length - 1];

  return (
    <div style={{ width: '100%' }}>
      {/* Barra segmentada — marker estilo padrão Dashboard (3×10 com sombra branca) */}
      <div style={{ position: 'relative', height: 6,
        borderRadius: 0, overflow: 'visible', display: 'flex' }}>
        {m.faixas.map((f, i) => {
          const inicio = i === 0 ? m.min : m.faixas[i - 1].ate;
          const largura = ((f.ate - inicio) / (m.max - m.min)) * 100;
          return (
            <div key={i} style={{
              width: `${largura}%`, background: f.cor, opacity: 0.42,
              borderTopLeftRadius:    i === 0 ? 3 : 0,
              borderBottomLeftRadius: i === 0 ? 3 : 0,
              borderTopRightRadius:    i === m.faixas.length - 1 ? 3 : 0,
              borderBottomRightRadius: i === m.faixas.length - 1 ? 3 : 0,
            }}/>
          );
        })}
        {/* Marker padrão dashboard: 3px largura, 10 alt, sombra branca */}
        <div style={{ position: 'absolute', left: `${posPct}%`,
          top: -2, width: 3, height: 10,
          background: faixaAtual.cor, borderRadius: 0,
          transform: 'translateX(-1.5px)',
          boxShadow: '0 0 0 1.5px #FFF', zIndex: 2 }}/>
      </div>
      {/* Ticks dos limites — bem compactos */}
      <div style={{ position: 'relative', height: 11, marginTop: 4 }}>
        {m.faixas.slice(0, -1).map((f, i) => {
          const posLabelPct = ((f.ate - m.min) / (m.max - m.min)) * 100;
          return (
            <div key={i} style={{
              position: 'absolute', left: `${posLabelPct}%`,
              transform: 'translateX(-50%)',
              fontSize: 9.5, color: G.txt2,
              fontVariantNumeric: 'tabular-nums',
            }}>
              {m.fmtTick(f.ate)}
            </div>
          );
        })}
      </div>
    </div>
  );
};


// ── Medidores das demais telas (30/07) ──────────────────────────────────
// Faixas de margem: referência de PME brasileira, não de grande empresa.
// Endividamento ONEROSO — só empréstimo, financiamento, debênture e
// arrendamento. É o que gera juros e o que banco e comprador olham.
// Separado do "Passivo sobre Ativo" em 30/07: na MORED o passivo é 33,5% do
// ativo mas a dívida onerosa é praticamente zero — o dinheiro é devido aos
// sócios, não ao mercado.
MEDIDORES.divida_onerosa = {
  label: 'Endividamento Oneroso',
  descricao: 'Empréstimos e financiamentos ÷ Ativo Total',
  min: 0, max: 60,
  faixas: [
    { ate: 10, cor: G.verde,    rotulo: 'baixo' },
    { ate: 25, cor: '#84CC16',  rotulo: 'moderado' },
    { ate: 40, cor: '#B45309',  rotulo: 'elevado' },
    { ate: 60, cor: G.vermelho, rotulo: 'alto' },
  ],
  fmt:     v => v.toFixed(1).replace('.', ',') + '%',
  fmtTick: v => v.toFixed(0) + '%',
};

// `passivo_sobre_ativo` é o mesmo medidor que a chave histórica `endividamento`
// — o nome de exibição já foi corrigido lá em cima. O apelido existe para o
// parecer referenciar pelo nome do que o número mede, sem quebrar chamada
// antiga que ainda use a chave original.
MEDIDORES.passivo_sobre_ativo = MEDIDORES.endividamento;

MEDIDORES.margem_operacional = {
  label: 'Margem Operacional',
  descricao: 'EBIT ÷ Receita Líquida',
  min: 0, max: 40,
  faixas: [
    { ate: 5,  cor: G.vermelho, rotulo: 'apertada' },
    { ate: 12, cor: '#B45309',  rotulo: 'estreita' },
    { ate: 20, cor: '#FCD34D',  rotulo: 'saudável' },
    { ate: 40, cor: G.verde,    rotulo: 'forte' },
  ],
  fmt:     v => v.toFixed(1).replace('.', ',') + '%',
  fmtTick: v => v.toFixed(0) + '%',
};
MEDIDORES.margem_bruta = {
  label: 'Margem Bruta',
  descricao: 'Lucro Bruto ÷ Receita Líquida',
  min: 0, max: 80,
  faixas: [
    { ate: 20, cor: G.vermelho, rotulo: 'apertada' },
    { ate: 35, cor: '#B45309',  rotulo: 'estreita' },
    { ate: 55, cor: '#FCD34D',  rotulo: 'saudável' },
    { ate: 80, cor: G.verde,    rotulo: 'forte' },
  ],
  fmt:     v => v.toFixed(1).replace('.', ',') + '%',
  fmtTick: v => v.toFixed(0) + '%',
};
MEDIDORES.margem_ebitda = {
  label: 'Margem EBITDA',
  descricao: 'EBITDA ÷ Receita Líquida',
  min: 0, max: 40,
  faixas: [
    { ate: 8,  cor: G.vermelho, rotulo: 'apertada' },
    { ate: 15, cor: '#B45309',  rotulo: 'estreita' },
    { ate: 25, cor: '#FCD34D',  rotulo: 'saudável' },
    { ate: 40, cor: G.verde,    rotulo: 'forte' },
  ],
  fmt:     v => v.toFixed(1).replace('.', ',') + '%',
  fmtTick: v => v.toFixed(0) + '%',
};
MEDIDORES.peso_tributario = {
  label: 'Peso Tributário',
  descricao: 'Tributos sobre venda ÷ Receita Bruta',
  min: 0, max: 40,
  faixas: [
    { ate: 8,  cor: G.verde,    rotulo: 'leve' },
    { ate: 18, cor: '#84CC16',  rotulo: 'moderado' },
    { ate: 28, cor: '#B45309',  rotulo: 'pesado' },
    { ate: 40, cor: G.vermelho, rotulo: 'muito pesado' },
  ],
  fmt:     v => v.toFixed(1).replace('.', ',') + '%',
  fmtTick: v => v.toFixed(0) + '%',
};
// Meses de caixa: quanto a empresa sobrevive sem vender nada.
MEDIDORES.cobertura_caixa = {
  label: 'Meses de Caixa',
  descricao: 'Caixa + Aplicações ÷ Despesa fixa mensal',
  min: 0, max: 12,
  faixas: [
    { ate: 1,  cor: G.vermelho, rotulo: 'crítica' },
    { ate: 3,  cor: '#B45309',  rotulo: 'curta' },
    { ate: 6,  cor: '#FCD34D',  rotulo: 'razoável' },
    { ate: 12, cor: G.verde,    rotulo: 'confortável' },
  ],
  fmt:     v => v >= 12 ? '≥ 12 meses' : v.toFixed(1).replace('.', ',') + ' meses',
  fmtTick: v => v.toFixed(0) + 'm',
};
// Folga sobre o ponto de equilíbrio — quanto a receita pode cair sem dar prejuízo.
MEDIDORES.folga_breakeven = {
  label: 'Folga sobre o Break-even',
  descricao: '(Receita − Break-even) ÷ Receita',
  min: 0, max: 60,
  faixas: [
    { ate: 5,  cor: G.vermelho, rotulo: 'no limite' },
    { ate: 15, cor: '#B45309',  rotulo: 'estreita' },
    { ate: 30, cor: '#FCD34D',  rotulo: 'razoável' },
    { ate: 60, cor: G.verde,    rotulo: 'confortável' },
  ],
  fmt:     v => v.toFixed(1).replace('.', ',') + '%',
  fmtTick: v => v.toFixed(0) + '%',
};

// ═══════════════════════════════════════════════════════════════════════
// <ComentarioAssessoria/> — o quadro do RGAI ao final de cada aba
// ═══════════════════════════════════════════════════════════════════════
// Vive em Resumo Mensal · Alertas · DRE · Balanço Patrimonial · Valor.
// Mesmo molde do parecer que já existia no Balanço: cabeçalho discreto com a
// marca, grade de 3 cards, medidor gráfico e rótulo da faixa.
//
// A VOZ é a de ASSESSOR QUE PREPAROU O DOCUMENTO, nunca a de avaliador dando
// nota ao próprio cliente (memória `voz-rgai-consultor-senior`, 05/07): ele
// apresenta, contextualiza o número e encaminha a verificação — não elogia.
// Daí o rótulo "COMENTÁRIO DA ASSESSORIA": "leitura" implicaria julgar.
//
// O TEXTO vem do BACKEND, que é onde os números são medidos — e é o único
// lugar em que a IA real pode entrar na Fase 3 sem reescrever a tela.
const ComentarioAssessoria = ({ empresa, tela, periodoInicio, periodoFim }) => {
  const [cards, setCards] = useState(null);
  const [carregando, setCarregando] = useState(true);
  // A falha PRECISA aparecer. A primeira versão deste componente devolvia
  // `null` em qualquer erro — e quando não apareceu em nenhuma das cinco
  // telas, não havia como saber se era rota, período, permissão ou render.
  // Diagnóstico que depende do console é diagnóstico que o consultor não faz.
  const [falha, setFalha] = useState(null);

  const dataInicio = periodoInicio?.inicio || null;
  const dataFim = periodoFim?.fim || null;

  useEffect(() => {
    if (!empresa?.id) { setFalha('sem empresa selecionada'); setCarregando(false); return; }
    let vivo = true;
    (async () => {
      setCarregando(true); setFalha(null);
      try {
        const url = new URL(`${API}/indicadores/comentario/${empresa.id}`);
        url.searchParams.set('tela', tela);
        if (dataInicio && dataFim) {
          url.searchParams.set('data_inicio', dataInicio);
          url.searchParams.set('data_fim', dataFim);
        }
        const r = await fetch(url);
        if (!r.ok) {
          const corpo = await r.text().catch(() => '');
          if (vivo) { setFalha(`HTTP ${r.status} · ${corpo.slice(0, 180)}`); setCarregando(false); }
          return;
        }
        const d = await r.json();
        if (vivo) {
          setCards(d.cards || []);
          if (!d.cards || d.cards.length === 0) setFalha('resposta sem cards');
          setCarregando(false);
        }
      } catch (e) {
        if (vivo) { setFalha(`${e?.name || 'erro'}: ${e?.message || e}`); setCarregando(false); }
      }
    })();
    return () => { vivo = false; };
  }, [empresa?.id, tela, dataInicio, dataFim]);

  if (carregando) return (
    <div style={{ marginTop: 28, fontSize: 11.5, color: G.txt2, fontStyle: 'italic' }}>
      Comentário do <MarcaAIdvisor variant="default" /> — preparando…
    </div>
  );

  if (falha) return (
    <div style={{ marginTop: 28, padding: '12px 16px', background: '#FFFBEB',
      border: '1px solid #FCD34D', borderRadius: 0, fontSize: 12,
      lineHeight: 1.45, color: G.txt1 }}>
      <strong>Comentário do </strong><span style={{ display: 'inline-block', background: '#FFF', padding: '2px 9px', borderRadius: 0, verticalAlign: 'baseline', lineHeight: 1.1 }}><MarcaAIdvisor variant="default" /></span><strong> indisponível.</strong>{' '}
      <span style={{ color: G.txt2 }}>({tela} · {falha})</span>
    </div>
  );

  if (!cards || cards.length === 0) return null;

  const corPonto = { ok: G.verde, atencao: '#B45309', critico: G.vermelho };

  return (
    <div style={{ marginTop: 28 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10,
        marginBottom: 12, flexWrap: 'wrap' }}>
        <MarcaAIdvisor />
        <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.10em',
          textTransform: 'uppercase', color: G.txt2 }}>
          · comentário
        </span>
      </div>

      <div style={{ display: 'grid',
        gridTemplateColumns: `repeat(${Math.min(cards.length, 3)}, 1fr)`, gap: 16 }}>
        {cards.map((c, i) => (
          <div key={i} style={{ background: '#FFF', padding: '14px 18px',
            display: 'flex', flexDirection: 'column', gap: 8,
            // O card que sobra da grade de 3 (o 4º — na prática o Lucro ×
            // Caixa, o mais narrativo) ocupa a linha inteira: sozinho numa
            // coluna ficava "perdido", e os quatro lado a lado fariam o mais
            // longo ditar uma fileira cheia de vazio (Fabio, 31/08).
            gridColumn: (cards.length > 3 && i === cards.length - 1
              && cards.length % 3 === 1) ? '1 / -1' : 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between',
              alignItems: 'baseline', gap: 8 }}>
              <span style={{ fontSize: 11.5, fontWeight: 600,
                letterSpacing: '0.14em', color: G.verde,
                textTransform: 'uppercase' }}>
                {c.medidor && MEDIDORES[c.medidor.tipo]
                  ? MEDIDORES[c.medidor.tipo].label : (c.titulo || '')}
              </span>
              {c.medidor && MEDIDORES[c.medidor.tipo] && (
                <span style={{ fontSize: 16, fontWeight: 700,
                  color: corPonto[c.t] || G.txt1, letterSpacing: '-0.01em',
                  fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
                  {MEDIDORES[c.medidor.tipo].fmt(c.medidor.valor)}
                </span>
              )}
            </div>

            <div style={{ fontSize: 12, lineHeight: 1.45, color: G.txt1 }}>
              {c.texto}
            </div>

            {c.medidor && MEDIDORES[c.medidor.tipo] && (
              <div style={{ marginTop: 'auto', paddingTop: 6 }}>
                <MiniMedidor tipo={c.medidor.tipo} valor={c.medidor.valor}/>
              </div>
            )}

            <div style={{ marginTop: 4, fontSize: 10.5, fontWeight: 700,
              letterSpacing: '0.06em', textTransform: 'uppercase',
              color: corPonto[c.t] || G.txt2, textAlign: 'center' }}>
              {c.medidor && MEDIDORES[c.medidor.tipo]
                ? (MEDIDORES[c.medidor.tipo].faixas.find(f =>
                    Math.min(c.medidor.valor, MEDIDORES[c.medidor.tipo].max) <= f.ate)?.rotulo)
                : (c.t === 'critico' ? 'crítico' : c.t === 'atencao' ? 'atenção' : 'ok')}
            </div>
          </div>
        ))}
      </div>

      <div style={{ fontSize: 10.5, color: G.txt2, marginTop: 10,
        fontStyle: 'italic' }}>
        Comentário elaborado sobre os números desta empresa no período selecionado.
        Não substitui parecer contábil ou de auditoria.
      </div>
    </div>
  );
};

const TelaBalancoPatrimonial = ({ empresa, periodoFim }) => {
  const [balanco, setBalanco] = useState(null);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState(null);

  useEffect(() => {
    let cancelado = false;
    setLoading(true);
    setErro(null);
    // Balanço é RETRATO numa data: o `ate` recorta no fim do período escolhido.
    // Sem ele a tela mostrava sempre o mês mais recente do acervo, mesmo com o
    // seletor num período que termina antes ([[verificar-nos-dois-lados-da-virada]]).
    fetch(`${API}/empresas/${empresa.id}/balanco`
      + (periodoFim?.fim ? `?ate=${periodoFim.fim}` : ''))
      .then(r => r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`)))
      .then(d => { if (!cancelado) { setBalanco(d); setLoading(false); } })
      .catch(e => { if (!cancelado) { setErro(e.message); setLoading(false); } });
    return () => { cancelado = true; };
  }, [empresa.id, periodoFim?.fim]);

  const fmt = v => 'R$ ' + Number(v || 0).toLocaleString('pt-BR',
    { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  // Linha de item dentro de uma seção (label esquerda · valor direita)
  const Linha = ({ label, valor, indent = 0, sub = false }) => (
    <div style={{
      display: 'flex', justifyContent: 'space-between', alignItems: 'baseline',
      padding: '6px 0', paddingLeft: indent,
      fontSize: 13, color: sub ? G.txt2 : G.txt1,
      fontStyle: sub ? 'italic' : 'normal',
    }}>
      <span>{label}</span>
      <span style={{ fontVariantNumeric: 'tabular-nums' }}>{fmt(valor)}</span>
    </div>
  );

  // Header de seção (uppercase + linha tênue embaixo)
  const SecHeader = ({ children }) => (
    <div style={{
      fontSize: 10, fontWeight: 700, letterSpacing: '0.10em',
      textTransform: 'uppercase', color: G.txt2,
      borderBottom: `1px solid ${G.borda}`, paddingBottom: 6,
      marginTop: 14, marginBottom: 8,
    }}>
      {children}
    </div>
  );

  // Subtotal de seção (bold, borda em cima)
  const Subtotal = ({ label, valor }) => (
    <div style={{
      display: 'flex', justifyContent: 'space-between', alignItems: 'baseline',
      borderTop: `1px solid ${G.borda}`,
      marginTop: 4, paddingTop: 6, paddingBottom: 2,
      fontSize: 13, fontWeight: 700, color: G.txt1,
    }}>
      <span>{label}</span>
      <span style={{ fontVariantNumeric: 'tabular-nums' }}>{fmt(valor)}</span>
    </div>
  );

  // Formata data ISO para DD/MM/AAAA
  const fmtData = iso => {
    if (!iso) return null;
    const [a, m, d] = iso.split('-');
    return `${d}/${m}/${a}`;
  };

  // ── Definição dos medidores (faixas e referências dos indicadores) ────
  // Cada medidor: escala numérica + faixas coloridas + formatador do valor.
  // As faixas seguem padrões da literatura de análise de balanços para PMEs.
  // ── Parecer heurístico do RUNGOAL AIDVISOR ──────────────────────────
  // Texto interpretativo + medidor numérico associado (quando aplicável).
  // Heurístico — contrato de IA real vira lote para Auditoria Fase 3 (Apêndice E).
  const gerarParecer = (b) => {
    if (!b) return null;
    const caixa = b.ativo.circulante.caixa_cc + b.ativo.circulante.aplicacoes;
    const passCP = b.passivo.circulante.subtotal;
    const passTotal = b.passivo.total;
    const ativoTotal = b.ativo.total;
    const pl = b.patrimonio_liquido.total;

    const liquidez       = passCP > 0      ? caixa / passCP      : null;
    const endivPct       = ativoTotal > 0  ? (passTotal / ativoTotal) * 100 : 0;
    const concentracaoCP = passTotal > 0   ? (passCP / passTotal) * 100     : 0;

    // DÍVIDA ONEROSA — só o que gera juros (empréstimo, financiamento,
    // debênture, arrendamento). O backend já a separa em `passivo.*.dividas`;
    // o parecer usava o passivo INTEIRO e chamava de "endividamento" (30/07).
    // Na MORED isso dava 33,5% de "endividamento" com dívida onerosa ZERO:
    // R$ 1,82 mi do passivo é dividendo a pagar aos sócios. Para banco e
    // comprador, dever ao dono e dever ao mercado são coisas opostas.
    const somaDividas = (grupo) => ((grupo && grupo.dividas) || [])
      .reduce((s, d) => s + (Number(d.valor ?? d.saldo ?? 0) || 0), 0);
    const dividaOnerosa = somaDividas(b.passivo.circulante)
                        + somaDividas(b.passivo.nao_circulante);
    const onerosoPct = ativoTotal > 0 ? (dividaOnerosa / ativoTotal) * 100 : 0;
    // Passivo que NÃO é dívida de mercado: fornecedor, tributo, salário e,
    // no caso da MORED, dividendo a pagar.
    const naoOneroso = Math.max(0, passTotal - dividaOnerosa);

    const pontos = [];

    // PL negativo — sinal mais grave, entra primeiro (sem medidor numérico)
    if (pl < 0) {
      pontos.push({ t: 'critico',
        texto: `Patrimônio Líquido negativo (R$ ${Math.abs(pl).toLocaleString('pt-BR', {minimumFractionDigits:2, maximumFractionDigits:2})}) — o passivo já supera o ativo. Descapitalização: pede aporte de sócios ou reestruturação imediata.` });
    }

    // Liquidez Imediata
    if (liquidez === null) {
      pontos.push({ t: 'ok',
        texto: `Sem empréstimos de curto prazo — empresa sem pressão de caixa imediata.` });
    } else {
      let tipo, frase;
      if (liquidez >= 1) {
        tipo = 'ok';
        frase = `Caixa + aplicações cobrem mais que o passivo de curto prazo. Folga para honrar compromissos.`;
      } else if (liquidez >= 0.5) {
        tipo = 'atencao';
        frase = `Caixa cobre boa parte mas não a totalidade do passivo de curto prazo. Vale acompanhar mês a mês.`;
      } else if (liquidez >= 0.2) {
        tipo = 'atencao';
        frase = `Caixa cobre apenas uma fração do passivo de curto prazo. Risco de descasamento nos próximos meses.`;
      } else {
        tipo = 'critico';
        frase = `Caixa muito aquém do passivo de curto prazo. Tensão real de pagamentos — priorizar geração de caixa ou alongamento de dívida.`;
      }
      pontos.push({ t: tipo, texto: frase,
        medidor: { tipo: 'liquidez', valor: liquidez } });
    }

    // Endividamento Total
    if (passTotal === 0) {
      pontos.push({ t: 'ok',
        texto: `Empresa sem endividamento financeiro — operação totalmente financiada por capital próprio.` });
    } else {
      // O veredito sai da DÍVIDA ONEROSA, não do passivo inteiro (30/07).
      // Chamar de "endividamento" o que inclui fornecedor, tributo e dividendo
      // a pagar sugere alavancagem que pode não existir — na MORED dava 33,5%
      // "equilibrado" com dívida bancária zero.
      const fmtR = (v) => 'R$ ' + Number(v || 0).toLocaleString('pt-BR',
        { minimumFractionDigits: 2, maximumFractionDigits: 2 });
      const parte = naoOneroso > 0 && passTotal > 0
        ? ` Do passivo de ${fmtR(passTotal)}, ${fmtR(naoOneroso)} não é dívida de mercado — são obrigações de operação (fornecedor, tributo, salário) ou com os próprios sócios.`
        : '';

      let tipo, frase;
      if (dividaOnerosa <= 0) {
        tipo = 'ok';
        frase = `Sem dívida onerosa: a empresa não tem empréstimo nem financiamento no balanço.${parte}`;
      } else if (onerosoPct < 15) {
        tipo = 'ok';
        frase = `Dívida onerosa de ${fmtR(dividaOnerosa)} — baixa perante o ativo. Há espaço para captar caso surja oportunidade de crescimento.${parte}`;
      } else if (onerosoPct < 35) {
        tipo = 'ok';
        frase = `Dívida onerosa de ${fmtR(dividaOnerosa)} — patamar usual para PME brasileira, com capacidade de pagamento a confirmar no fluxo de caixa.${parte}`;
      } else if (onerosoPct < 55) {
        tipo = 'atencao';
        frase = `Dívida onerosa de ${fmtR(dividaOnerosa)} — elevada perante o ativo. Pouca margem para novas captações sem aumentar o risco.${parte}`;
      } else {
        tipo = 'critico';
        frase = `Dívida onerosa de ${fmtR(dividaOnerosa)} — considere reestruturação de prazos ou aporte de capital.${parte}`;
      }
      pontos.push({ t: tipo, texto: frase,
        medidor: { tipo: 'divida_onerosa', valor: onerosoPct } });

      // O passivo total sobre o ativo continua exibido — com o NOME CERTO.
      // É informação de estrutura, não de alavancagem.
      pontos.push({
        t: endivPct < 50 ? 'ok' : endivPct < 70 ? 'atencao' : 'critico',
        texto: `O passivo total representa ${endivPct.toLocaleString('pt-BR', { maximumFractionDigits: 1 })}% do ativo — é quanto do que a empresa tem está comprometido com terceiros, a qualquer título. O restante pertence aos sócios.`,
        medidor: { tipo: 'passivo_sobre_ativo', valor: endivPct } });
    }

    // Concentração CP — só destaca se passivo total > 0 E concentração > 70%
    if (passTotal > 0 && concentracaoCP > 70 && pl >= 0) {
      pontos.push({ t: 'atencao',
        texto: `Endividamento concentrado no curto prazo — boa parte vence em até 12 meses. Avalie alongar prazos para aliviar pressão mensal.`,
        medidor: { tipo: 'concentracao_cp', valor: concentracaoCP } });
    }

    return pontos.slice(0, 3);   // máximo 3 pontos para não sobrecarregar
  };

  const parecer = gerarParecer(balanco);
  const corPonto = { ok: G.verde, atencao: '#B45309', critico: G.vermelho };
  const bgPonto  = { ok: G.verdeLight, atencao: '#FFFBEB', critico: '#FEF2F2' };

  return (
    <div style={{ padding: '24px 28px', position: 'relative', overflow: 'hidden' }}>
      <CarimboAIdvisor />
      {/* Header */}
      <div style={{ marginBottom: 20, display: 'flex',
        alignItems: 'flex-start', justifyContent: 'space-between' }}>
        <div>
          <div style={{ position: 'relative', display: 'inline-block' }}>
            <h2 style={{ margin: 0, fontSize: 20, fontWeight: 800, color: G.txt1,
              position: 'relative', zIndex: 1 }}>
              Balanço Patrimonial
            </h2>
            <MarcaDaguaTitulo>
              <line x1="12" y1="3" x2="12" y2="21"/>
              <line x1="3" y1="7" x2="21" y2="7"/>
              <circle cx="6" cy="7" r="1"/>
              <circle cx="18" cy="7" r="1"/>
              <path d="M3 12l3-5 3 5a3 3 0 11-6 0z"/>
              <path d="M15 12l3-5 3 5a3 3 0 11-6 0z"/>
            </MarcaDaguaTitulo>
          </div>
          <p style={{ ...TIPO.subtitulo, margin: '4px 0 0' }}>
            {balanco?.data_referencia && <>posição em <strong style={{ color: G.txt1 }}>{fmtData(balanco.data_referencia)}</strong></>}
          </p>
        </div>
        {/* Selo AiPro removido em 25/07 junto com a trava de tier — o Balanço
            deixou de ser exclusivo do pacote premium até a rediscussão. */}
      </div>

      <QuadrosIntro
        titulo="o Balanço Patrimonial"
        oQueE={<>A <strong>fotografia do patrimônio</strong> numa data: o que a
          empresa tem (ativo) e o que ela deve (passivo). A diferença entre os
          dois é o que pertence aos sócios.</>}
        oQueFaz={<>Confira a posição na data de referência. Conta sem lastro em
          documento aparece <strong>estimada</strong> — complete-a nas
          Configurações Complementares para o Balanço fechar com a
          contabilidade.</>}
      />

      {/* Loading / erro / conteúdo */}
      {loading && (
        <div style={{ padding: 40, textAlign: 'center', color: G.txt2, fontSize: 13 }}>
          Montando o Balanço…
        </div>
      )}

      {!loading && erro && (
        <div style={{ padding: 20, background: '#FEF2F2', border: `1px solid ${G.vermelho}`,
          borderRadius: 0, color: G.vermelho, fontSize: 13 }}>
          Erro ao carregar balanço: {erro}
        </div>
      )}

      {!loading && !erro && balanco && (
        <>
          {/* Aviso quando ainda não há data_referencia (empresa sem upload de CC) */}
          {!balanco.data_referencia && (
            <div style={{ padding: '10px 14px', background: '#FFFBEB',
              border: `1px solid #FCD34D`, borderRadius: 0, color: '#92400E',
              fontSize: 12, marginBottom: 16 }}>
              <IconeAlerta /> Sem extrato de Conta Corrente carregado — o Balanço considera saldo de caixa zero.
              Faça upload de extrato em Extrato para ter a foto correta.
            </div>
          )}

          {/* Grid 2 colunas: Ativo · Passivo + PL */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24 }}>

            {/* ─── ATIVO ─────────────────────────────────────────── */}
            <div style={{ background: G.card, border: `1px solid ${G.borda}`,
              padding: '16px 20px', borderRadius: 0 }}>
              <div style={{ fontSize: 13, fontWeight: 800, color: G.verde,
                letterSpacing: '0.06em', textTransform: 'uppercase',
                paddingBottom: 8, borderBottom: `2px solid ${G.verde}` }}>
                Ativo
              </div>

              <SecHeader>Ativo Circulante</SecHeader>
              <Linha label="Caixa e Equivalentes de Caixa" valor={balanco.ativo.circulante.caixa_cc} />
              <Linha label="Aplicações Financeiras" valor={balanco.ativo.circulante.aplicacoes} />
              <Linha label="Estoques" valor={balanco.ativo.circulante.estoque} />
              <Subtotal label="Total do Ativo Circulante" valor={balanco.ativo.circulante.subtotal} />

              <SecHeader>Ativo Não Circulante</SecHeader>
              <Linha label="Imobilizado" valor={balanco.ativo.nao_circulante.imobilizado} />
              <Subtotal label="Total do Ativo Não Circulante" valor={balanco.ativo.nao_circulante.subtotal} />

              {/* Total Ativo */}
              <div style={{ marginTop: 16, padding: '10px 12px',
                background: G.verdeLight, borderRadius: 0,
                display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                <span style={{ fontSize: 12, fontWeight: 800, color: G.verde,
                  letterSpacing: '0.06em', textTransform: 'uppercase' }}>
                  Total do Ativo
                </span>
                <span style={{ fontSize: 17, fontWeight: 800, color: G.verde,
                  fontVariantNumeric: 'tabular-nums' }}>
                  {fmt(balanco.ativo.total)}
                </span>
              </div>
            </div>

            {/* ─── PASSIVO + PL ──────────────────────────────────── */}
            <div style={{ background: G.card, border: `1px solid ${G.borda}`,
              padding: '16px 20px', borderRadius: 0 }}>
              <div style={{ fontSize: 13, fontWeight: 800, color: G.laranja,
                letterSpacing: '0.06em', textTransform: 'uppercase',
                paddingBottom: 8, borderBottom: `2px solid ${G.laranja}` }}>
                Passivo e Patrimônio Líquido
              </div>

              <SecHeader>Passivo Circulante</SecHeader>
              {balanco.passivo.circulante.dividas.length === 0 ? (
                <div style={{ padding: '6px 0', fontSize: 12, color: G.txt2,
                  fontStyle: 'italic' }}>
                  Sem empréstimos e financiamentos de curto prazo.
                </div>
              ) : (
                balanco.passivo.circulante.dividas.map(d => (
                  <Linha key={d.id}
                    label={`${d.banco || '—'} · ${d.tipo}`}
                    valor={d.saldo} sub />
                ))
              )}
              <Subtotal label="Total do Passivo Circulante" valor={balanco.passivo.circulante.subtotal} />

              <SecHeader>Passivo Não Circulante</SecHeader>
              {balanco.passivo.nao_circulante.dividas.length === 0 ? (
                <div style={{ padding: '6px 0', fontSize: 12, color: G.txt2,
                  fontStyle: 'italic' }}>
                  Sem empréstimos e financiamentos de longo prazo.
                </div>
              ) : (
                balanco.passivo.nao_circulante.dividas.map(d => (
                  <Linha key={d.id}
                    label={`${d.banco || '—'} · ${d.tipo}`}
                    valor={d.saldo} sub />
                ))
              )}
              <Subtotal label="Total do Passivo Não Circulante" valor={balanco.passivo.nao_circulante.subtotal} />

              <SecHeader>Patrimônio Líquido</SecHeader>
              <Linha label="Capital Social" valor={balanco.patrimonio_liquido.capital_social} />
              <Linha
                label="Lucros/Prejuízos Acumulados"
                valor={balanco.patrimonio_liquido.resultado_acumulado}
              />
              <Subtotal label="Total do Patrimônio Líquido" valor={balanco.patrimonio_liquido.total} />

              {/* Total Passivo + PL */}
              <div style={{ marginTop: 16, padding: '10px 12px',
                background: G.laranjaLight, borderRadius: 0,
                display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                <span style={{ fontSize: 12, fontWeight: 800, color: G.laranja,
                  letterSpacing: '0.06em', textTransform: 'uppercase' }}>
                  Total Passivo + PL
                </span>
                <span style={{ fontSize: 17, fontWeight: 800, color: G.laranja,
                  fontVariantNumeric: 'tabular-nums' }}>
                  {fmt(balanco.total_passivo_pl)}
                </span>
              </div>
            </div>
          </div>

          {/* Parecer RUNGOAL AIDVISOR — mini-dashboard de dicas (padrão dos cards
              do Dashboard: fundo branco, padding compacto, header verde uppercase,
              barra de medidor slim 6px com marker 3×10). 3 cards lado a lado. */}
          {parecer && parecer.length > 0 && (
            <div style={{ marginTop: 28 }}>
              {/* Cabeçalho discreto da seção */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 10,
                marginBottom: 12 }}>
                <MarcaAIdvisor />
                <span style={{ fontSize: 11, fontWeight: 700,
                  letterSpacing: '0.10em', textTransform: 'uppercase',
                  color: G.txt2 }}>
                  · interpreta este Balanço
                </span>
              </div>

              {/* Grid de 3 cards compactos lado a lado (padrão do Dashboard) */}
              <div style={{ display: 'grid',
                gridTemplateColumns: 'repeat(3, 1fr)', gap: 16 }}>
                {parecer.map((p, i) => (
                  <div key={i} style={{
                    background: '#FFF', padding: '14px 18px',
                    display: 'flex', flexDirection: 'column', gap: 8,
                  }}>
                    {/* Header verde uppercase + valor à direita (estilo DRE/Receita) */}
                    <div style={{ display: 'flex', justifyContent: 'space-between',
                      alignItems: 'baseline' }}>
                      <span style={{ fontSize: 11.5, fontWeight: 600,
                        letterSpacing: '0.14em', color: G.verde,
                        textTransform: 'uppercase' }}>
                        {p.medidor ? MEDIDORES[p.medidor.tipo].label : 'PATRIMÔNIO'}
                      </span>
                      {p.medidor && (
                        <span style={{ fontSize: 16, fontWeight: 700,
                          color: corPonto[p.t], letterSpacing: '-0.01em',
                          fontVariantNumeric: 'tabular-nums' }}>
                          {MEDIDORES[p.medidor.tipo].fmt(p.medidor.valor)}
                        </span>
                      )}
                    </div>

                    {/* Texto interpretativo compacto */}
                    <div style={{ fontSize: 12, lineHeight: 1.45,
                      color: G.txt1 }}>
                      {p.texto}
                    </div>

                    {/* Medidor compacto (apenas se houver) — fica empurrado pro fim */}
                    {p.medidor && (
                      <div style={{ marginTop: 'auto', paddingTop: 6 }}>
                        <MiniMedidor tipo={p.medidor.tipo} valor={p.medidor.valor}/>
                      </div>
                    )}

                    {/* Rótulo da faixa colorido */}
                    <div style={{ marginTop: 4,
                      fontSize: 10.5, fontWeight: 700,
                      letterSpacing: '0.06em', textTransform: 'uppercase',
                      color: corPonto[p.t],
                      textAlign: 'center' }}>
                      {p.medidor
                        ? (MEDIDORES[p.medidor.tipo].faixas.find(f => Math.min(p.medidor.valor, MEDIDORES[p.medidor.tipo].max) <= f.ate)?.rotulo)
                        : (p.t === 'critico' ? 'crítico' : p.t === 'atencao' ? 'atenção' : 'ok')}
                    </div>
                  </div>
                ))}
              </div>

              {/* Disclaimer minimalista */}
              <p style={{ marginTop: 10, fontSize: 11, color: G.txt2,
                fontStyle: 'italic', lineHeight: 1.5 }}>
                Parecer gerado automaticamente a partir dos indicadores de liquidez,
                endividamento e estrutura patrimonial. Não substitui análise contábil
                formal.
              </p>
            </div>
          )}

          {/* Nota de rodapé */}
          <p style={{ marginTop: 16, fontSize: 11, color: G.txt2,
            fontStyle: 'italic', lineHeight: 1.55 }}>
            <strong>Como o Balanço é montado:</strong> Caixa e Equivalentes + Aplicações vêm dos extratos
            consolidados · Estoques, Imobilizado e Capital Social vêm de Configurações · Empréstimos
            e Financiamentos vêm da lista dinâmica em Configurações Adicionais · <strong>Lucros/Prejuízos
            Acumulados</strong> é o resultado retido na empresa ao longo do tempo (calculado para fechar
            a equação Ativo = Passivo + Patrimônio Líquido).
          </p>

          {/* Nota aprovada pelo Fabio (31/08): efeito PATRIMONIAL da reforma —
              dado da lei, sem simulação; o Balanço não entra no seletor de
              cenário porque é fotografia, não fluxo. */}
          <p style={{ marginTop: 6, fontSize: 11, color: G.txt2,
            fontStyle: 'italic', lineHeight: 1.55 }}>
            <strong>Reforma Tributária:</strong> créditos de ICMS não usados até 31/12/2032
            continuam valendo — homologados, compensam-se com o IBS em 240 parcelas mensais
            (EC 132/2023, ADCT art. 134 · LC 227/2026); crédito de ativo imobilizado mantém o
            prazo de 1/48. Detalhe na aba Reforma Tributária.
          </p>

          {/* O Balanço já tem parecer próprio (liquidez · endividamento ·
              concentração). Este comentário olha o que aquele não cobre: a
              ligação entre o patrimônio e o resultado que ele produziu. */}
          <ComentarioAssessoria empresa={empresa} tela="balanco"/>
        </>
      )}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════
// Rascunho heurístico da Tarefa 2 por empresa (simula geração futura da IA).
// Quando IA real plugar (pós-Auditoria Fase 3), troca a fonte do rascunho
// sem mexer na UX. Spec: memory/bp60m-coleta-dados.md (princípio "IA propõe ·
// empresário valida").
// ═══════════════════════════════════════════════════════════════════════
const RASCUNHO_TAREFA_2 = {
  shopmax: {
    historia: [
      { id_catalogo: 'shopmax_h1', ano: '2019', marco: 'Fundação da ShopMax como loja online de móveis e decoração com foco em curadoria de design' },
      { id_catalogo: 'shopmax_h2', ano: '2020', marco: 'Expansão acelerada do catálogo durante a pandemia · ticket médio sobe 35%' },
      { id_catalogo: 'shopmax_h3', ano: '2022', marco: 'Lançamento da linha private label · margem bruta sobe 8 p.p.' },
      { id_catalogo: 'shopmax_h4', ano: '2024', marco: 'Inauguração do Centro de Distribuição próprio em São Paulo · prazos caem 40%' },
    ],
    equipe: [
      { id_catalogo: 'shopmax_e1', nome: 'Fabio Souza',    funcao: 'Founder · CEO', bio: 'Engenheiro com 15 anos de varejo digital · liderou e-commerce em duas grandes redes antes de fundar a ShopMax' },
      { id_catalogo: 'shopmax_e2', nome: 'Camila Pereira', funcao: 'COO',           bio: 'Background em supply chain (Magalu, Mercado Livre) · responsável pela operação e logística' },
      { id_catalogo: 'shopmax_e3', nome: 'Rafael Almeida', funcao: 'CMO',           bio: 'Especialista em marketing digital e brand · construiu marca própria private label' },
    ],
    instalacoes: [
      { id_catalogo: 'shopmax_i1', tipo: 'Sede administrativa',     local: 'São Paulo · SP · Bairro Pinheiros · 250 m²' },
      { id_catalogo: 'shopmax_i2', tipo: 'Centro de Distribuição',  local: 'Cajamar · SP · 1.200 m² · operação 24/7' },
    ],
  },
  sabor_arte: {
    historia: [
      { id_catalogo: 'sabor_h1', ano: '2017', marco: 'Inauguração do bar/restaurante com chef premiado e proposta de cozinha brasileira contemporânea' },
      { id_catalogo: 'sabor_h2', ano: '2019', marco: 'Cardápio recebe destaque em guias gastronômicos da cidade' },
      { id_catalogo: 'sabor_h3', ano: '2021', marco: 'Sobrevive à pandemia com operação 100% delivery e voucher de futuras refeições' },
      { id_catalogo: 'sabor_h4', ano: '2023', marco: 'Reabertura completa pós-Covid · faturamento volta ao patamar pré-pandemia' },
    ],
    equipe: [
      { id_catalogo: 'sabor_e1', nome: 'Marcos Pereira', funcao: 'Chef Executivo · Sócio', bio: 'Formado pelo Le Cordon Bleu · 12 anos em restaurantes de São Paulo' },
      { id_catalogo: 'sabor_e2', nome: 'Beatriz Lima',   funcao: 'Sócia · Gestão',         bio: 'Administradora com MBA em Hospitalidade · responsável pelo financeiro e parcerias' },
      { id_catalogo: 'sabor_e3', nome: 'Diego Souza',    funcao: 'Maître · Sommelier',     bio: 'Certificado WSET nível 2 · curadoria de carta de vinhos e atendimento' },
    ],
    instalacoes: [
      { id_catalogo: 'sabor_i1', tipo: 'Restaurante principal', local: 'São Paulo · SP · Jardins · 180 m² · 60 lugares' },
    ],
  },
  clinicavida: {
    historia: [
      { id_catalogo: 'clinica_h1', ano: '2015', marco: 'Fundação como clínica multidisciplinar com 3 especialidades médicas' },
      { id_catalogo: 'clinica_h2', ano: '2018', marco: 'Credenciamento dos principais convênios de saúde · base de pacientes triplica' },
      { id_catalogo: 'clinica_h3', ano: '2021', marco: 'Telemedicina implantada durante a pandemia · 30% das consultas migram para remoto' },
      { id_catalogo: 'clinica_h4', ano: '2023', marco: 'Expansão para 7 especialidades · contratação de equipe sênior' },
    ],
    equipe: [
      { id_catalogo: 'clinica_e1', nome: 'Dr. Ricardo Mendes',   funcao: 'Diretor Técnico · Sócio',   bio: 'Médico cardiologista · responsável técnico da clínica · 20 anos de prática' },
      { id_catalogo: 'clinica_e2', nome: 'Dra. Patricia Castro', funcao: 'Diretora Clínica · Sócia',  bio: 'Ginecologista · coordena equipe médica e protocolos' },
      { id_catalogo: 'clinica_e3', nome: 'Sandra Oliveira',      funcao: 'Administradora',            bio: 'Gestora hospitalar com 10 anos em redes de saúde' },
    ],
    instalacoes: [
      { id_catalogo: 'clinica_i1', tipo: 'Unidade principal', local: 'São Paulo · SP · Bairro Moema · 400 m² · 8 consultórios' },
    ],
  },
  construmax: {
    historia: [
      { id_catalogo: 'construmax_h1', ano: '2010', marco: 'Fundação como prestadora de serviços de reforma e pequenas obras' },
      { id_catalogo: 'construmax_h2', ano: '2015', marco: 'Primeira obra de grande porte (edifício comercial · 4.000 m²)' },
      { id_catalogo: 'construmax_h3', ano: '2019', marco: 'Abertura da loja de materiais de construção · verticalização da operação' },
      { id_catalogo: 'construmax_h4', ano: '2024', marco: 'Carteira de obras supera R$ 8 mi/ano · estrutura técnica e administrativa consolidada' },
    ],
    equipe: [
      { id_catalogo: 'construmax_e1', nome: 'Eng. Carlos Ribeiro', funcao: 'Sócio Fundador · Engenheiro Responsável', bio: 'Engenheiro civil · CREA ativo · 25 anos de mercado · responsável técnico das obras' },
      { id_catalogo: 'construmax_e2', nome: 'Mariana Costa',       funcao: 'Sócia · Diretora Comercial',               bio: 'Background em vendas B2B · responsável pela carteira de clientes corporativos' },
      { id_catalogo: 'construmax_e3', nome: 'Eng. Lucas Tavares',  funcao: 'Gerente de Obras',                         bio: 'Engenheiro civil · gestão de até 5 obras simultâneas' },
    ],
    instalacoes: [
      { id_catalogo: 'construmax_i1', tipo: 'Escritório central', local: 'São Paulo · SP · 180 m² · administração e engenharia' },
      { id_catalogo: 'construmax_i2', tipo: 'Loja de materiais',  local: 'São Paulo · SP · 600 m² · varejo e atacado' },
    ],
  },
};

// ═══════════════════════════════════════════════════════════════════════
// Tarefa 3 — Visão · Missão · Valores
// Questionário guiado (4 Visão + 4 Missão + 3 Valores = 11 perguntas com
// 3 alternativas cada). Cada alternativa contribui para geração heurística
// do texto da definição. Empresário pode editar livremente o resultado.
// ═══════════════════════════════════════════════════════════════════════
const QUESTIONARIO_VMV = {
  visao: {
    label: 'Visão',
    descricao: 'Aspiração de longo prazo · onde a empresa quer chegar',
    caracteristicas: [
      { id: 'horizonte', pergunta: 'Horizonte temporal',
        opcoes: [
          { id: 'a', label: '3 anos', detalhe: 'objetivo tangível e palpável' },
          { id: 'b', label: '5-10 anos', detalhe: 'transformação relevante do negócio' },
          { id: 'c', label: '15-20 anos', detalhe: 'legado e perpetuidade geracional' },
        ] },
      { id: 'escopo', pergunta: 'Escopo de impacto',
        opcoes: [
          { id: 'a', label: 'Local / Regional', detalhe: 'liderança na paraça' },
          { id: 'b', label: 'Nacional', detalhe: 'referência no setor BR' },
          { id: 'c', label: 'Internacional', detalhe: 'atuação ou referência global' },
        ] },
      { id: 'tipo', pergunta: 'Tipo de liderança',
        opcoes: [
          { id: 'a', label: 'Escala / tamanho', detalhe: 'maior do segmento' },
          { id: 'b', label: 'Qualidade / excelência', detalhe: 'padrão técnico' },
          { id: 'c', label: 'Inovação / disrupção', detalhe: 'mudar o setor' },
        ] },
      { id: 'vetor', pergunta: 'Vetor de crescimento',
        opcoes: [
          { id: 'a', label: 'Orgânico', detalhe: 'crescer com o mercado existente' },
          { id: 'b', label: 'M&A / franchising', detalhe: 'comprar ou licenciar' },
          { id: 'c', label: 'IPO / venda futura', detalhe: 'evento de liquidez' },
        ] },
    ],
  },
  missao: {
    label: 'Missão',
    descricao: 'Razão de existir hoje · o que fazemos e para quem',
    caracteristicas: [
      { id: 'cliente', pergunta: 'Cliente principal',
        opcoes: [
          { id: 'a', label: 'Empresas (B2B)', detalhe: 'outras empresas como clientes' },
          { id: 'b', label: 'Pessoas / famílias (B2C)', detalhe: 'consumidor final' },
          { id: 'c', label: 'Setor público / instituições', detalhe: 'órgãos, clínicas, escolas' },
        ] },
      { id: 'valor', pergunta: 'Valor entregue',
        opcoes: [
          { id: 'a', label: 'Solução prática', detalhe: 'resolve um problema funcional' },
          { id: 'b', label: 'Experiência / emoção', detalhe: 'gera sentimento positivo' },
          { id: 'c', label: 'Transformação', detalhe: 'muda o cliente ou a vida dele' },
        ] },
      { id: 'diferencial', pergunta: 'Diferencial central',
        opcoes: [
          { id: 'a', label: 'Acessibilidade', detalhe: 'preço / conveniência' },
          { id: 'b', label: 'Excelência técnica', detalhe: 'qualidade superior' },
          { id: 'c', label: 'Relacionamento próximo', detalhe: 'atendimento humano' },
        ] },
      { id: 'modelo', pergunta: 'Modelo de receita',
        opcoes: [
          { id: 'a', label: 'Transação única', detalhe: 'venda direta · pontual' },
          { id: 'b', label: 'Recorrência / assinatura', detalhe: 'receita previsível' },
          { id: 'c', label: 'Projetos / contratos', detalhe: 'serviço continuado de prazo' },
        ] },
    ],
  },
  valores: {
    label: 'Valores',
    descricao: 'Princípios que guiam decisões internas',
    caracteristicas: [
      { id: 'foco', pergunta: 'Foco predominante',
        opcoes: [
          { id: 'a', label: 'Pessoas', detalhe: 'equipe + cliente · cultura humana' },
          { id: 'b', label: 'Resultado', detalhe: 'performance · meritocracia' },
          { id: 'c', label: 'Processo', detalhe: 'excelência operacional · disciplina' },
        ] },
      { id: 'etica', pergunta: 'Postura ética inegociável',
        opcoes: [
          { id: 'a', label: 'Honestidade', detalhe: 'transparência absoluta' },
          { id: 'b', label: 'Sustentabilidade', detalhe: 'responsabilidade socioambiental' },
          { id: 'c', label: 'Diversidade', detalhe: 'respeito · inclusão' },
        ] },
      { id: 'estilo', pergunta: 'Estilo de operação',
        opcoes: [
          { id: 'a', label: 'Inovação', detalhe: 'experimentação · risco controlado' },
          { id: 'b', label: 'Tradição', detalhe: 'consistência · confiabilidade' },
          { id: 'c', label: 'Velocidade', detalhe: 'agilidade · ciclos curtos' },
        ] },
    ],
  },
};

// Rascunho VMV pré-preenchido por empresa (simula IA que encontrou na pesquisa)
const RASCUNHO_TAREFA_3 = {
  shopmax: {
    visao:   { texto: 'Ser a marca brasileira de referência em decoração acessível e curadoria de design até 2030.',
               escolhas: { horizonte: 'b', escopo: 'b', tipo: 'b', vetor: 'a' } },
    missao:  { texto: 'Levar móveis e decoração com curadoria de design e logística confiável para famílias brasileiras que querem morar bem com bom-senso financeiro.',
               escolhas: { cliente: 'b', valor: 'b', diferencial: 'a', modelo: 'a' } },
    valores: { texto: '**Curadoria** — todo item passa por nosso filtro de design.\n**Confiabilidade** — prazo e qualidade são compromisso, não promessa.\n**Acessibilidade** — bom design não precisa ser caro.\n**Inovação** — testar novos formatos com método.',
               escolhas: { foco: 'c', etica: 'a', estilo: 'a' } },
  },
  sabor_arte: {
    visao:   { texto: 'Ser referência em gastronomia brasileira contemporânea em São Paulo nos próximos 5 anos.',
               escolhas: { horizonte: 'b', escopo: 'a', tipo: 'b', vetor: 'a' } },
    missao:  { texto: 'Oferecer experiência gastronômica autêntica que celebra ingredientes nacionais com técnica refinada e hospitalidade calorosa.',
               escolhas: { cliente: 'b', valor: 'b', diferencial: 'b', modelo: 'a' } },
    valores: { texto: '**Autenticidade** — sabores honestos · sem atalhos.\n**Hospitalidade** — cada cliente é convidado de casa.\n**Excelência** — ingrediente, preparo, salão.\n**Sustentabilidade** — produtores locais · respeito ao desperdício.',
               escolhas: { foco: 'a', etica: 'b', estilo: 'b' } },
  },
  clinicavida: {
    visao:   { texto: 'Ser referência regional em saúde preventiva e multidisciplinar até 2030.',
               escolhas: { horizonte: 'b', escopo: 'a', tipo: 'b', vetor: 'a' } },
    missao:  { texto: 'Oferecer cuidado médico integral, humanizado e acessível, prevenindo doenças e promovendo qualidade de vida.',
               escolhas: { cliente: 'b', valor: 'c', diferencial: 'c', modelo: 'a' } },
    valores: { texto: '**Cuidado humano** — paciente é pessoa, não diagnóstico.\n**Ética** — conduta médica acima de qualquer pressão.\n**Excelência clínica** — atualização constante · base científica.\n**Acessibilidade** — atendimento de qualidade para base ampla.',
               escolhas: { foco: 'a', etica: 'a', estilo: 'b' } },
  },
  construmax: {
    visao:   { texto: 'Ser a construtora preferida do mercado corporativo de médio porte em São Paulo até 2030.',
               escolhas: { horizonte: 'b', escopo: 'a', tipo: 'b', vetor: 'a' } },
    missao:  { texto: 'Entregar obras com prazo, qualidade e segurança, criando valor real para clientes corporativos com engenharia confiável.',
               escolhas: { cliente: 'a', valor: 'a', diferencial: 'b', modelo: 'c' } },
    valores: { texto: '**Segurança** — zero acidente é a meta inegociável.\n**Cumprimento de prazo** — contrato é compromisso pessoal.\n**Qualidade técnica** — engenharia bem feita, sem atalhos.\n**Transparência** — orçamento aberto, comunicação direta.',
               escolhas: { foco: 'c', etica: 'a', estilo: 'b' } },
  },
};

// Gerador heurístico do texto da definição a partir das escolhas do questionário
const gerarTextoVMV = (definicao, escolhas, empresa) => {
  const nome = empresa?.nome || 'A empresa';
  const setor = (empresa?.setor || 'seu setor').toLowerCase();
  const get = (k) => {
    const c = QUESTIONARIO_VMV[definicao]?.caracteristicas.find(c => c.id === k);
    const opt = c?.opcoes.find(o => o.id === escolhas[k]);
    return opt?.label?.toLowerCase() || null;
  };
  if (definicao === 'visao') {
    const h = get('horizonte');
    const e = get('escopo');
    const t = get('tipo');
    const v = get('vetor');
    const horizonteTxt = h?.includes('3') ? 'em 3 anos' : h?.includes('5') ? 'até 2030' : h?.includes('15') ? 'até 2040' : 'no longo prazo';
    const escopoTxt = e === 'local / regional' ? 'em nossa paraça' : e === 'nacional' ? 'no Brasil' : 'no cenário internacional';
    const tipoTxt = t === 'escala / tamanho' ? 'a maior empresa do setor' : t === 'qualidade / excelência' ? 'a referência em qualidade do setor' : 'a empresa que redefiniu o setor';
    const vetorTxt = v === 'orgânico' ? 'crescendo organicamente' : v?.includes('m&a') ? 'através de aquisições estratégicas' : v?.includes('ipo') ? 'preparada para acessar mercado de capitais' : '';
    return `Ser ${tipoTxt} ${escopoTxt}, ${horizonteTxt}${vetorTxt ? `, ${vetorTxt}` : ''}.`;
  }
  if (definicao === 'missao') {
    const c = get('cliente');
    const v = get('valor');
    const d = get('diferencial');
    const m = get('modelo');
    const clienteTxt = c?.includes('b2b') ? 'empresas que' : c?.includes('b2c') ? 'famílias e pessoas que' : 'instituições que';
    const valorTxt = v === 'solução prática' ? 'precisam resolver um problema real' : v?.includes('experiência') ? 'buscam uma experiência diferenciada' : 'querem transformar sua realidade';
    const difTxt = d === 'acessibilidade' ? 'com acesso justo e conveniente' : d?.includes('excelência') ? 'com excelência técnica reconhecida' : 'com relacionamento próximo e humano';
    const modeloTxt = m?.includes('transação') ? '' : m?.includes('recorrência') ? ' através de relacionamento contínuo' : ' por meio de projetos sob medida';
    return `Atender ${clienteTxt} ${valorTxt} no setor de ${setor}, ${difTxt}${modeloTxt}.`;
  }
  if (definicao === 'valores') {
    const f = get('foco');
    const e = get('etica');
    const s = get('estilo');
    const fTxt = f === 'pessoas' ? '**Pessoas em primeiro lugar** — equipe e cliente são o centro de toda decisão.' :
                 f === 'resultado' ? '**Resultado importa** — entregamos, mensuramos, evoluímos.' :
                 '**Processo bem feito** — disciplina e método como base do crescimento.';
    const eTxt = e === 'honestidade' ? '**Honestidade absoluta** — transparência em todas as relações.' :
                 e === 'sustentabilidade' ? '**Sustentabilidade** — responsabilidade socioambiental em cada decisão.' :
                 '**Diversidade e respeito** — inclusão como princípio, não discurso.';
    const sTxt = s === 'inovação' ? '**Inovação com método** — experimentar com risco controlado.' :
                 s === 'tradição' ? '**Tradição e consistência** — confiabilidade construída no tempo.' :
                 '**Velocidade e agilidade** — ciclos curtos, decisões rápidas.';
    return `${fTxt}\n${eTxt}\n${sTxt}`;
  }
  return '';
};

// ═══════════════════════════════════════════════════════════════════════
// Versão SU (Startup) do questionário VMV. Mesma estrutura e mesmos IDs
// (visao/missao/valores · a/b/c) do catálogo EE — muda só o vocabulário
// das perguntas e alternativas para o universo de startups. Selecionado em
// ConteudoTarefa3 quando modo === 'SU'.
// ═══════════════════════════════════════════════════════════════════════
const QUESTIONARIO_VMV_SU = {
  visao: {
    label: 'Visão',
    descricao: 'Aspiração de longo prazo · onde a startup quer chegar',
    caracteristicas: [
      { id: 'horizonte', pergunta: 'Horizonte temporal',
        opcoes: [
          { id: 'a', label: '3–5 anos', detalhe: 'próxima rodada ou etapa de captação' },
          { id: 'b', label: '5–7 anos', detalhe: 'escala e liderança de categoria' },
          { id: 'c', label: '10+ anos', detalhe: 'redefinir o mercado' },
        ] },
      { id: 'escopo', pergunta: 'Escopo de impacto',
        opcoes: [
          { id: 'a', label: 'Nicho dominado primeiro', detalhe: 'vencer um segmento antes de expandir' },
          { id: 'b', label: 'Nacional', detalhe: 'referência no setor BR' },
          { id: 'c', label: 'Internacional', detalhe: 'global desde o início' },
        ] },
      { id: 'tipo', pergunta: 'Tipo de liderança',
        opcoes: [
          { id: 'a', label: 'Escala / tamanho', detalhe: 'crescimento acelerado' },
          { id: 'b', label: 'Tecnologia / produto', detalhe: 'melhor produto do segmento' },
          { id: 'c', label: 'Inovação / disrupção', detalhe: 'criar categoria nova' },
        ] },
      { id: 'vetor', pergunta: 'Vetor de crescimento',
        opcoes: [
          { id: 'a', label: 'Orgânico', detalhe: 'puxado pelo próprio produto' },
          { id: 'b', label: 'Captação + expansão agressiva', detalhe: 'crescer com capital de investidores' },
          { id: 'c', label: 'IPO / venda futura', detalhe: 'evento de liquidez' },
        ] },
    ],
  },
  missao: {
    label: 'Missão',
    descricao: 'Razão de existir hoje · o que fazemos e para quem',
    caracteristicas: [
      { id: 'cliente', pergunta: 'Cliente principal',
        opcoes: [
          { id: 'a', label: 'Empresas (B2B)', detalhe: 'outras empresas como clientes' },
          { id: 'b', label: 'Pessoas / famílias (B2C)', detalhe: 'consumidor final' },
          { id: 'c', label: 'Desenvolvedores / outras startups', detalhe: 'plataforma para quem constrói' },
        ] },
      { id: 'valor', pergunta: 'Valor entregue',
        opcoes: [
          { id: 'a', label: 'Solução prática', detalhe: 'resolve uma dor aguda e urgente' },
          { id: 'b', label: 'Experiência / emoção', detalhe: 'nova experiência · 10× melhor' },
          { id: 'c', label: 'Transformação', detalhe: 'democratiza o que era caro ou exclusivo' },
        ] },
      { id: 'diferencial', pergunta: 'Diferencial central',
        opcoes: [
          { id: 'a', label: 'Tecnologia proprietária', detalhe: 'ativo técnico difícil de copiar' },
          { id: 'b', label: 'Velocidade / produto', detalhe: 'experiência e ritmo do produto' },
          { id: 'c', label: 'Comunidade / rede', detalhe: 'efeito de rede entre usuários' },
        ] },
      { id: 'modelo', pergunta: 'Modelo de receita',
        opcoes: [
          { id: 'a', label: 'Assinatura recorrente', detalhe: 'receita previsível' },
          { id: 'b', label: 'Transação / marketplace', detalhe: 'comissão por transação' },
          { id: 'c', label: 'Uso / consumo', detalhe: 'cobrança sob demanda' },
        ] },
    ],
  },
  valores: {
    label: 'Valores',
    descricao: 'Princípios que guiam decisões internas',
    caracteristicas: [
      { id: 'foco', pergunta: 'Foco predominante',
        opcoes: [
          { id: 'a', label: 'Obsessão pelo usuário', detalhe: 'usuário no centro de toda decisão' },
          { id: 'b', label: 'Tração / resultado', detalhe: 'métricas · crescimento' },
          { id: 'c', label: 'Time e cultura', detalhe: 'pessoas certas · cultura forte' },
        ] },
      { id: 'etica', pergunta: 'Postura ética inegociável',
        opcoes: [
          { id: 'a', label: 'Honestidade', detalhe: 'transparência absoluta' },
          { id: 'b', label: 'Privacidade e uso responsável de dados', detalhe: 'o dado do usuário é respeitado' },
          { id: 'c', label: 'Diversidade', detalhe: 'respeito · inclusão' },
        ] },
      { id: 'estilo', pergunta: 'Estilo de operação',
        opcoes: [
          { id: 'a', label: 'Experimentação', detalhe: 'testar rápido · aprender' },
          { id: 'b', label: 'Foco e disciplina', detalhe: 'fazer poucas coisas muito bem' },
          { id: 'c', label: 'Velocidade', detalhe: 'agilidade · ciclos curtos' },
        ] },
    ],
  },
};

// Gerador heurístico SU · keyed nos IDs das escolhas (a/b/c), independente
// dos rótulos do catálogo. Mantém o EE intacto (zero regressão).
const gerarTextoVMV_SU = (definicao, escolhas, empresa) => {
  const setor = (empresa?.setor || 'seu setor').toLowerCase();
  if (definicao === 'visao') {
    const horizonteTxt = { a: 'nos próximos 3 a 5 anos', b: 'em 5 a 7 anos', c: 'na próxima década' }[escolhas?.horizonte] || 'no longo prazo';
    const escopoTxt = { a: 'dominando primeiro nosso nicho', b: 'no Brasil', c: 'no cenário internacional' }[escolhas?.escopo] || 'no nosso mercado';
    const tipoTxt = { a: 'a empresa que mais cresce no setor', b: 'a referência em tecnologia e produto do setor', c: 'a empresa que criou uma nova categoria' }[escolhas?.tipo] || 'uma referência no setor';
    const vetorTxt = { a: 'com crescimento puxado pelo próprio produto', b: 'com captação e expansão agressiva', c: 'preparada para um evento de liquidez' }[escolhas?.vetor] || '';
    return `Ser ${tipoTxt} ${escopoTxt}, ${horizonteTxt}${vetorTxt ? `, ${vetorTxt}` : ''}.`;
  }
  if (definicao === 'missao') {
    const clienteTxt = { a: 'empresas que', b: 'pessoas que', c: 'desenvolvedores e startups que' }[escolhas?.cliente] || 'clientes que';
    const valorTxt = { a: 'enfrentam uma dor aguda e urgente', b: 'buscam uma experiência muito melhor', c: 'querem acesso ao que antes era caro ou exclusivo' }[escolhas?.valor] || 'precisam de uma solução melhor';
    const difTxt = { a: 'com tecnologia proprietária', b: 'com um produto rápido e superior', c: 'com o poder da nossa comunidade e efeito de rede' }[escolhas?.diferencial] || 'com um diferencial claro';
    const modeloTxt = { a: ' em um modelo de assinatura recorrente', b: ' em um modelo de marketplace', c: ' com cobrança por uso' }[escolhas?.modelo] || '';
    return `Atender ${clienteTxt} ${valorTxt} no setor de ${setor}, ${difTxt}${modeloTxt}.`;
  }
  if (definicao === 'valores') {
    const fTxt = { a: '**Obsessão pelo usuário** — o usuário no centro de toda decisão.',
                   b: '**Tração acima de tudo** — métricas e crescimento guiam o dia a dia.',
                   c: '**Time e cultura** — as pessoas certas antes de qualquer processo.' }[escolhas?.foco] || '';
    const eTxt = { a: '**Honestidade absoluta** — transparência em todas as relações.',
                   b: '**Privacidade e uso responsável de dados** — o dado do usuário é respeitado.',
                   c: '**Diversidade e respeito** — inclusão como princípio, não discurso.' }[escolhas?.etica] || '';
    const sTxt = { a: '**Experimentação** — testar rápido, medir e aprender.',
                   b: '**Foco e disciplina** — fazer poucas coisas muito bem.',
                   c: '**Velocidade** — ciclos curtos e decisões rápidas.' }[escolhas?.estilo] || '';
    return [fTxt, eTxt, sTxt].filter(Boolean).join('\n');
  }
  return '';
};

// Rascunho VMV-SU pré-preenchido por empresa (IA já preencheu · campo nunca vazio)
const RASCUNHO_TAREFA_3_SU = {
  shopmax: {
    visao:   { texto: 'Ser a referência em tecnologia e produto de decoração para o lar no Brasil em 5 a 7 anos, dominando primeiro nosso nicho.',
               escolhas: { horizonte: 'b', escopo: 'a', tipo: 'b', vetor: 'b' } },
    missao:  { texto: 'Atender pessoas que querem acesso ao que antes era caro ou exclusivo em decoração, com um produto rápido e superior, em um modelo de marketplace.',
               escolhas: { cliente: 'b', valor: 'c', diferencial: 'b', modelo: 'b' } },
    valores: { texto: '**Obsessão pelo usuário** — o usuário no centro de toda decisão.\n**Privacidade e uso responsável de dados** — o dado do usuário é respeitado.\n**Experimentação** — testar rápido, medir e aprender.',
               escolhas: { foco: 'a', etica: 'b', estilo: 'a' } },
  },
  sabor_arte: {
    visao:   { texto: 'Ser a empresa que criou uma nova categoria em gastronomia por assinatura, dominando primeiro nosso nicho, nos próximos 3 a 5 anos.',
               escolhas: { horizonte: 'a', escopo: 'a', tipo: 'c', vetor: 'a' } },
    missao:  { texto: 'Atender pessoas que buscam uma experiência muito melhor no setor de alimentação, com um produto rápido e superior, em um modelo de assinatura recorrente.',
               escolhas: { cliente: 'b', valor: 'b', diferencial: 'b', modelo: 'a' } },
    valores: { texto: '**Obsessão pelo usuário** — o usuário no centro de toda decisão.\n**Honestidade absoluta** — transparência em todas as relações.\n**Experimentação** — testar rápido, medir e aprender.',
               escolhas: { foco: 'a', etica: 'a', estilo: 'a' } },
  },
  clinicavida: {
    visao:   { texto: 'Ser a referência em tecnologia e produto de saúde preventiva no Brasil em 5 a 7 anos, dominando primeiro nosso nicho.',
               escolhas: { horizonte: 'b', escopo: 'a', tipo: 'b', vetor: 'b' } },
    missao:  { texto: 'Atender pessoas que querem acesso ao que antes era caro ou exclusivo em saúde, com tecnologia proprietária, em um modelo de assinatura recorrente.',
               escolhas: { cliente: 'b', valor: 'c', diferencial: 'a', modelo: 'a' } },
    valores: { texto: '**Obsessão pelo usuário** — o usuário no centro de toda decisão.\n**Privacidade e uso responsável de dados** — o dado do usuário é respeitado.\n**Foco e disciplina** — fazer poucas coisas muito bem.',
               escolhas: { foco: 'a', etica: 'b', estilo: 'b' } },
  },
  construmax: {
    visao:   { texto: 'Ser a referência em tecnologia e produto para gestão de obras no Brasil em 5 a 7 anos, dominando primeiro nosso nicho.',
               escolhas: { horizonte: 'b', escopo: 'a', tipo: 'b', vetor: 'b' } },
    missao:  { texto: 'Atender empresas que enfrentam uma dor aguda e urgente no setor de construção, com tecnologia proprietária, em um modelo de assinatura recorrente.',
               escolhas: { cliente: 'a', valor: 'a', diferencial: 'a', modelo: 'a' } },
    valores: { texto: '**Tração acima de tudo** — métricas e crescimento guiam o dia a dia.\n**Honestidade absoluta** — transparência em todas as relações.\n**Foco e disciplina** — fazer poucas coisas muito bem.',
               escolhas: { foco: 'b', etica: 'a', estilo: 'b' } },
  },
};

// ═══════════════════════════════════════════════════════════════════════
// <ConteudoTarefa3 /> — Visão · Missão · Valores (3ª tarefa do BP60M).
// Mostra rascunho heurístico pré-preenchido + assistente collapsável por
// definição. Empresário edita o texto livremente.
// ═══════════════════════════════════════════════════════════════════════
const ConteudoTarefa3 = ({ vmv, setVmv, empresa, modo = 'EE' }) => {
  const catalogoVMV = modo === 'SU' ? QUESTIONARIO_VMV_SU : QUESTIONARIO_VMV;
  const rascunhosVMV = modo === 'SU' ? RASCUNHO_TAREFA_3_SU : RASCUNHO_TAREFA_3;
  const gerarTexto = modo === 'SU' ? gerarTextoVMV_SU : gerarTextoVMV;
  const rascunho = rascunhosVMV[empresa?.id] || {
    visao: { texto: '', escolhas: {} },
    missao: { texto: '', escolhas: {} },
    valores: { texto: '', escolhas: {} },
  };
  const [assistenteAberto, setAssistenteAberto] = useState({ visao: false, missao: false, valores: false });

  const obter = (def) => vmv?.[def] ?? rascunho[def];

  const setarTexto = (def, texto) => {
    setVmv({
      visao: obter('visao'), missao: obter('missao'), valores: obter('valores'),
      ...vmv,
      [def]: { ...(obter(def)), texto },
    });
  };
  const setarEscolha = (def, caracId, opcaoId) => {
    const atual = obter(def);
    setVmv({
      visao: obter('visao'), missao: obter('missao'), valores: obter('valores'),
      ...vmv,
      [def]: { ...atual, escolhas: { ...atual.escolhas, [caracId]: opcaoId } },
    });
  };
  const gerarDoAssistente = (def) => {
    const atual = obter(def);
    const novoTexto = gerarTexto(def, atual.escolhas, empresa);
    setarTexto(def, novoTexto);
  };

  const HEADER_BLOCO = {
    fontSize: 16, fontWeight: 700, letterSpacing: '0.10em',
    textTransform: 'uppercase', color: G.laranja, marginBottom: 8,
  };
  const HEADER_AUX = {
    fontSize: 12, fontWeight: 400, color: G.txt2,
    textTransform: 'none', letterSpacing: 0, marginLeft: 10,
  };

  const BlocoDefinicao = ({ def }) => {
    const q = catalogoVMV[def];
    const atual = obter(def);
    const aberto = assistenteAberto[def];
    return (
      <div>
        {/* Header: label em laranja 16px + descrição auxiliar */}
        <div style={HEADER_BLOCO}>
          {q.label}
          <span style={HEADER_AUX}>· {q.descricao}</span>
        </div>
        {/* Textarea com o texto editável */}
        <textarea value={atual.texto} onChange={e => setarTexto(def, e.target.value)}
          rows={def === 'valores' ? 6 : 3}
          placeholder={`Texto de ${q.label} · edite livremente ou use o assistente abaixo`}
          style={{
            width: '100%', padding: '10px 14px', fontSize: 13,
            border: `1px solid ${G.borda}`, borderRadius: 0,
            fontFamily: "'Outfit', sans-serif", color: G.txt1,
            background: '#FFF', lineHeight: 1.5, resize: 'vertical',
            boxSizing: 'border-box',
          }}/>
        {/* Toggle do assistente · padrão "Clique para abrir" laranja */}
        <div style={{ marginTop: 10, padding: '8px 14px',
          background: G.verdeLight, borderRadius: 0,
          display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{ flex: 1, fontSize: 12, color: G.txt1, lineHeight: 1.4 }}>
            <strong style={{ color: G.verde }}>Assistente</strong> · não tem essa definição? Responda algumas perguntas para te ajudar a escrever.
          </div>
          <button onClick={() => setAssistenteAberto({ ...assistenteAberto, [def]: !aberto })}
            style={{
              flexShrink: 0,
              background: aberto ? '#FFF' : G.laranjaLight,
              border: `1px solid ${G.laranja}`,
              color: G.laranja,
              padding: '4px 9px', fontSize: 9, fontWeight: 800,
              letterSpacing: '0.08em', textTransform: 'uppercase',
              borderRadius: 0, cursor: 'pointer',
              fontFamily: "'Outfit', sans-serif",
              whiteSpace: 'nowrap',
            }}>
            {aberto ? '▴ Clique para fechar' : '▾ Clique para abrir'}
          </button>
        </div>
        {aberto && (
          <div style={{ marginTop: 10, padding: '14px 16px',
            background: G.verdeLight, borderRadius: 0 }}>
            {/* Grid 4 colunas com perguntas numeradas + alternativas verticalizadas */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)',
              gap: 0 }}>
              {q.caracteristicas.map((c, idx) => (
                <div key={c.id} style={{
                  padding: idx === 0 ? '0 14px 0 0' : '0 14px',
                  borderLeft: idx > 0 ? `1px solid ${G.borda}` : 'none',
                }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: G.txt1,
                    marginBottom: 12 }}>
                    {idx + 1}. {c.pergunta}
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    {c.opcoes.map(o => {
                      const ativo = atual.escolhas?.[c.id] === o.id;
                      return (
                        <label key={o.id}
                          style={{ display: 'flex', alignItems: 'flex-start',
                            gap: 8, cursor: 'pointer',
                            fontFamily: "'Outfit', sans-serif" }}>
                          <input type="radio"
                            name={`${def}_${c.id}`}
                            checked={ativo}
                            onChange={() => setarEscolha(def, c.id, o.id)}
                            style={{
                              appearance: 'none', WebkitAppearance: 'none',
                              width: 16, height: 16,
                              border: `2px solid ${ativo ? G.verde : G.borda}`,
                              borderRadius: 0,
                              background: ativo ? G.verde : '#FFF',
                              cursor: 'pointer', flexShrink: 0, marginTop: 2,
                            }}/>
                          <div>
                            <div style={{ fontSize: 12, fontWeight: 800,
                              color: ativo ? G.verde : G.txt1, marginBottom: 1 }}>
                              {o.label}
                            </div>
                            <div style={{ fontSize: 11, color: G.txt2,
                              lineHeight: 1.35 }}>
                              {o.detalhe}
                            </div>
                          </div>
                        </label>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
            <button onClick={() => gerarDoAssistente(def)}
              style={{
                marginTop: 14, background: G.verde, color: '#FFF',
                border: 'none', padding: '9px 18px',
                fontSize: 11, fontWeight: 700, letterSpacing: '0.08em',
                textTransform: 'uppercase', borderRadius: 0, cursor: 'pointer',
                fontFamily: "'Outfit', sans-serif",
              }}>
              Gerar texto a partir das escolhas →
            </button>
          </div>
        )}
      </div>
    );
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
      {/* 2 quadros lado a lado: Definição + Instruções */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <div style={{ padding: '12px 16px', background: G.verdeLight,
          borderLeft: `4px solid ${G.verde}`, borderRadius: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 800, color: G.verde,
            letterSpacing: '0.06em', textTransform: 'uppercase',
            marginBottom: 6 }}>
            O que é Visão · Missão · Valores
          </div>
          <div style={{ fontSize: 12, lineHeight: 1.45, color: G.txt1 }}>
            Definem a <strong>identidade estratégica</strong> da empresa.
            <strong> Visão</strong> = onde quer chegar · <strong>Missão</strong> =
            por que existe hoje · <strong>Valores</strong> = princípios inegociáveis.
          </div>
        </div>
        <div style={{ padding: '12px 16px', background: G.laranjaLight,
          borderLeft: `4px solid ${G.laranja}`, borderRadius: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 800, color: G.laranja,
            letterSpacing: '0.06em', textTransform: 'uppercase',
            marginBottom: 6 }}>
            O que você faz aqui
          </div>
          <div style={{ fontSize: 12, lineHeight: 1.45, color: G.txt1 }}>
            Edite cada texto diretamente OU clique em <strong>Assistente</strong> para
            responder perguntas guiadas que ajudam a escrever · clique Aprovar para
            enviar ao BP.
          </div>
        </div>
      </div>

      <BlocoDefinicao def="visao"/>
      <BlocoDefinicao def="missao"/>
      <BlocoDefinicao def="valores"/>
    </div>
  );
};


// ═══════════════════════════════════════════════════════════════════════
// Tarefa 4 — Produtos & Serviços
// 2 blocos editáveis: Portfólio (Nome + Descrição + Mix% opcional) +
// Diferenciais Competitivos (3-5 itens). Padrão visual da Tarefa 2.
// ═══════════════════════════════════════════════════════════════════════
// Intervalos para o input "% Investimento em P&D" — empresário marca a faixa.
// IA usa o ponto médio pra cálculos derivados.
const INTERVALOS_PD = [
  { id: 'a', label: '0 a 1%',    meio: 0.5,  desc: 'baixo · setores tradicionais (construção, varejo simples)' },
  { id: 'b', label: '1 a 3%',    meio: 2,    desc: 'médio · varejo, serviços, alimentação' },
  { id: 'c', label: '3 a 5%',    meio: 4,    desc: 'alto · indústria, saúde' },
  { id: 'd', label: '5 a 10%',   meio: 7.5,  desc: 'muito alto · tecnologia, pesquisa aplicada' },
  { id: 'e', label: 'Mais de 10%', meio: 15, desc: 'extremo · biotech, farmacêutica, deep tech' },
];

// Portfólio de produtos por empresa — ESVAZIADO em 29/07/2026.
// Era semeadura nativa: a Composição carregava produtos que ninguém cadastrou
// (a Mored abria com "Produto/serviço sem nome" a R$ 118,00 / custo R$ 59,00).
// Princípio `base-zerada-nada-nativo`: empresa nova encontra tudo vazio; só o
// algoritmo é nativo. A constante fica (7 pontos a referenciam, todos com
// fallback vazio) — o que sai é o DADO.
const RASCUNHO_TAREFA_4 = {};

// Insumos do custo direto unitário · Formação de Preço (T4 · só SU).
// Lista muda conforme o tipo do item (Mercadoria ou Serviço).
const INSUMOS_PRECO_SU = {
  mercadoria: [
    { id: 'compra',        label: 'Custo de compra ou produção da unidade' },
    { id: 'materia',       label: 'Matéria-prima e componentes' },
    { id: 'embalagem',     label: 'Embalagem' },
    { id: 'frete_entrada', label: 'Frete de compra (entrada)' },
    { id: 'perdas',        label: 'Perdas e quebras' },
  ],
  servico: [
    { id: 'mao_obra',      label: 'Mão de obra direta (horas × custo/hora)' },
    { id: 'materiais',     label: 'Materiais aplicados no serviço' },
    { id: 'deslocamento',  label: 'Deslocamento e diárias' },
    { id: 'software',      label: 'Software e licenças por atendimento' },
    { id: 'subcontratacao',label: 'Subcontratação e terceiros' },
  ],
};

// Helpers de Formação de Preço no NÍVEL DO ARQUIVO · usados pela T4 (onde o
// custo é montado) e pela T13 (que consolida em leitura). Fonte única.
// Referência inicial de insumos da RGAI (R$) · stand-in da IA real (Fase 3).
const IA_INSUMO_REF_SU = {
  mercadoria: { compra: 50, materia: 0, embalagem: 3, frete_entrada: 4, perdas: 2 },
  servico:    { mao_obra: 40, materiais: 5, deslocamento: 5, software: 3, subcontratacao: 0 },
};
const tipoProdutoSU = (item, setor) => {
  if (item.tipo) return item.tipo;
  const s = (setor || '').toLowerCase();
  if (s.includes('serv') || s.includes('saude') || s.includes('saúde') ||
      s.includes('tech') || s.includes('software') || s.includes('clinic')) return 'servico';
  return 'mercadoria';
};
const insumoValorSU = (item, insId, setor) => {
  const t = tipoProdutoSU(item, setor);
  return item.insumos?.[insId] ?? IA_INSUMO_REF_SU[t][insId] ?? 0;
};
const custoDiretoUnitSU = (item, setor) => INSUMOS_PRECO_SU[tipoProdutoSU(item, setor)].reduce(
  (s, it) => s + (Number(insumoValorSU(item, it.id, setor)) || 0), 0);
const precoVendaSU = (item, setor) => {
  if (item.precoVenda !== undefined && item.precoVenda !== null) return Number(item.precoVenda) || 0;
  return Math.round(custoDiretoUnitSU(item, setor) * 2);
};

const ConteudoTarefa4 = ({ produtosServicos, setProdutosServicos, empresa, modo = 'EE' }) => {
  const [graficosT4Aberto, setGraficosT4Aberto] = useState(false);
  const rascunho = RASCUNHO_TAREFA_4[empresa?.id] || {
    portfolio: [], diferenciais: [],
    pipeline: [], investimentoPD_intervalo: null, parcerias: [],
  };
  const portfolio                = produtosServicos?.portfolio                ?? rascunho.portfolio;
  const diferenciais             = produtosServicos?.diferenciais             ?? rascunho.diferenciais;
  const pipeline                 = produtosServicos?.pipeline                 ?? rascunho.pipeline                 ?? [];
  const investimentoPD_intervalo = produtosServicos?.investimentoPD_intervalo ?? rascunho.investimentoPD_intervalo ?? null;
  const parcerias                = produtosServicos?.parcerias                ?? rascunho.parcerias                ?? [];
  const [pdAberto, setPdAberto] = useState(false);

  const atualizar = (campo, valor) => {
    setProdutosServicos(prev => ({
      portfolio, diferenciais, pipeline, investimentoPD_intervalo, parcerias,
      ...prev,
      [campo]: valor,
    }));
  };

  // ── Formação de Preço de Venda (só SU) · ESPELHA o Portfólio ──────────
  // A RUNGOAL AIDVISOR já conhece os produtos/serviços (são os do Portfólio)
  // e sugere, para cada um: tipo · composição de insumos · custos em R$ ·
  // e o Preço de Venda competitivo de mercado. Custo Direto = soma dos
  // insumos. Margem de Contribuição = Preço − Custo Direto (impostos e
  // despesas variáveis entram na T13). A margem NÃO é digitada · é resultado.
  // Composição vive dentro de cada item do portfólio (campos tipo/insumos/precoVenda).
  // Helpers locais delegam para as funções no nível do arquivo (fonte única
  // compartilhada com a T13). setorEmp é o setor da empresa.
  const tipoDoProduto = (p) => tipoProdutoSU(p, empresa?.setor);
  const insumoValor = (p, insId) => insumoValorSU(p, insId, empresa?.setor);
  const custoUnit = (p) => custoDiretoUnitSU(p, empresa?.setor);
  const precoVenda = (p) => precoVendaSU(p, empresa?.setor);
  const margemContrib = (p) => precoVenda(p) - custoUnit(p);
  // Edição grava DENTRO do item do portfólio (espelho · fonte única).
  const editarComposicao = (idx, campo, valor) => {
    const nova = portfolio.map((p, i) => i === idx ? { ...p, [campo]: valor } : p);
    atualizar('portfolio', nova);
  };
  const editarInsumoComposicao = (idx, insId, valor) => {
    const nova = portfolio.map((p, i) => i === idx
      ? { ...p, insumos: { ...(p.insumos || {}), [insId]: valor } } : p);
    atualizar('portfolio', nova);
  };
  const [precoAberto, setPrecoAberto] = useState(null);

  // Validação do mix 100% (regra Fabio 13/06): empresário precisa fechar a soma
  const somaMix = portfolio.reduce((s, p) => s + (Number(p.mix) || 0), 0);
  const mixOK = somaMix === 100;
  const editarPortfolio = (i, k, v) => {
    const nova = portfolio.map((m, idx) => idx === i ? { ...m, [k]: v } : m);
    atualizar('portfolio', nova);
  };
  const removerPortfolio = (i) => atualizar('portfolio', portfolio.filter((_, idx) => idx !== i));
  const adicionarPortfolio = () => atualizar('portfolio', [...portfolio, { id_catalogo: null, nome: '', descricao: '', mix: 0 }]);
  const setorEmp = empresa?.setor || 'geral';
  const catalogoPortfolio = PORTFOLIO_CATALOGO_POR_SETOR[setorEmp] || PORTFOLIO_CATALOGO_POR_SETOR['geral'];
  const chipMarcado = (catId) => portfolio.some(p => p.id_catalogo === catId);
  const toggleChipCatalogo = (catItem) => {
    if (chipMarcado(catItem.id)) {
      atualizar('portfolio', portfolio.filter(p => p.id_catalogo !== catItem.id));
    } else {
      atualizar('portfolio', [...portfolio, {
        id_catalogo: catItem.id,
        nome: catItem.nome,
        descricao: catItem.descricao,
        mix: catItem.mix,
      }]);
    }
  };

  const editarDif = (i, k, v) => {
    const nova = diferenciais.map((m, idx) => idx === i ? { ...m, [k]: v } : m);
    atualizar('diferenciais', nova);
  };
  const removerDif = (i) => atualizar('diferenciais', diferenciais.filter((_, idx) => idx !== i));
  const adicionarDif = () => atualizar('diferenciais', [...diferenciais, { id_catalogo: null, titulo: '', detalhe: '' }]);
  const catalogoDif = DIFERENCIAIS_CATALOGO_POR_SETOR[setorEmp] || DIFERENCIAIS_CATALOGO_POR_SETOR['geral'];
  const chipDifMarcado = (catId) => diferenciais.some(d => d.id_catalogo === catId);
  const toggleChipDif = (catItem) => {
    if (chipDifMarcado(catItem.id)) {
      atualizar('diferenciais', diferenciais.filter(d => d.id_catalogo !== catItem.id));
    } else {
      atualizar('diferenciais', [...diferenciais, {
        id_catalogo: catItem.id,
        titulo: catItem.titulo,
        detalhe: catItem.detalhe,
      }]);
    }
  };

  // Pipeline
  const editarPipe = (i, k, v) => {
    const nova = pipeline.map((m, idx) => idx === i ? { ...m, [k]: v } : m);
    atualizar('pipeline', nova);
  };
  const removerPipe = (i) => atualizar('pipeline', pipeline.filter((_, idx) => idx !== i));
  const adicionarPipe = () => atualizar('pipeline', [...pipeline, { id_catalogo: null, descricao: '', prazo: '' }]);
  const catalogoPipe = PIPELINE_CATALOGO_POR_SETOR[setorEmp] || PIPELINE_CATALOGO_POR_SETOR['geral'];
  const chipPipeMarcado = (catId) => pipeline.some(p => p.id_catalogo === catId);
  const toggleChipPipe = (catItem) => {
    if (chipPipeMarcado(catItem.id)) {
      atualizar('pipeline', pipeline.filter(p => p.id_catalogo !== catItem.id));
    } else {
      atualizar('pipeline', [...pipeline, {
        id_catalogo: catItem.id,
        descricao: catItem.descricao,
        prazo: catItem.prazo,
      }]);
    }
  };

  // Parcerias
  const editarPar = (i, k, v) => {
    const nova = parcerias.map((m, idx) => idx === i ? { ...m, [k]: v } : m);
    atualizar('parcerias', nova);
  };
  const removerPar = (i) => atualizar('parcerias', parcerias.filter((_, idx) => idx !== i));
  const adicionarPar = () => atualizar('parcerias', [...parcerias, { id_catalogo: null, nome: '', tipo: '' }]);
  const catalogoPar = PARCERIAS_CATALOGO_POR_SETOR[setorEmp] || PARCERIAS_CATALOGO_POR_SETOR['geral'];
  const chipParMarcado = (catId) => parcerias.some(p => p.id_catalogo === catId);
  const toggleChipPar = (catItem) => {
    if (chipParMarcado(catItem.id)) {
      atualizar('parcerias', parcerias.filter(p => p.id_catalogo !== catItem.id));
    } else {
      atualizar('parcerias', [...parcerias, {
        id_catalogo: catItem.id,
        nome: catItem.nome,
        tipo: catItem.tipo,
      }]);
    }
  };

  const inputBase = {
    width: '100%', padding: '8px 12px', fontSize: 13,
    border: `1px solid ${G.borda}`, borderRadius: 0,
    fontFamily: "'Outfit', sans-serif", color: G.txt1,
    background: '#FFF', boxSizing: 'border-box',
  };
  const btnAdd = {
    background: '#FFF', border: `1px dashed ${G.verde}`,
    color: G.verde, padding: '8px 14px',
    fontSize: 11, fontWeight: 700, letterSpacing: '0.06em',
    textTransform: 'uppercase', borderRadius: 0, cursor: 'pointer',
    fontFamily: "'Outfit', sans-serif", marginTop: 8,
  };
  const btnRemover = {
    background: 'transparent', border: 'none', color: G.txt2,
    fontSize: 16, cursor: 'pointer', padding: '2px 6px',
    fontFamily: "'Outfit', sans-serif",
  };
  const HEADER_BLOCO = {
    fontSize: 16, fontWeight: 700, letterSpacing: '0.10em',
    textTransform: 'uppercase', color: G.laranja, marginBottom: 10,
  };
  const HEADER_AUX = {
    fontSize: 12, fontWeight: 400, color: G.txt2,
    textTransform: 'none', letterSpacing: 0, marginLeft: 10,
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
      {/* 2 quadros lado a lado: Definição + Instruções */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <div style={{ padding: '12px 16px', background: G.verdeLight,
          borderLeft: `4px solid ${G.verde}`, borderRadius: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 800, color: G.verde,
            letterSpacing: '0.06em', textTransform: 'uppercase',
            marginBottom: 6 }}>
            O que é Produtos & Serviços
          </div>
          <div style={{ fontSize: 12, lineHeight: 1.45, color: G.txt1 }}>
            Descreve <strong>o que a empresa oferece</strong> e por que o cliente
            escolhe. Cobre <strong>portfólio</strong> (mix), <strong>diferenciais</strong>
            {' '}competitivos e <strong>roadmap de inovação</strong> (pipeline, P&D, parcerias).
          </div>
        </div>
        <div style={{ padding: '12px 16px', background: G.laranjaLight,
          borderLeft: `4px solid ${G.laranja}`, borderRadius: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 800, color: G.laranja,
            letterSpacing: '0.06em', textTransform: 'uppercase',
            marginBottom: 6 }}>
            O que você faz aqui
          </div>
          <div style={{ fontSize: 12, lineHeight: 1.45, color: G.txt1 }}>
            <strong>Revise o portfólio</strong> sugerido (edite ou adicione) ·
            confirme <strong>diferenciais</strong> · informe <strong>pipeline</strong>
            e investimento em P&D · clique Aprovar para enviar ao BP.
          </div>
        </div>
      </div>

      {/* BLOCO 1 — Portfólio (chips IA · linhas marcadas · extras escritos · exceção controlada) */}
      <div>
        <div style={HEADER_BLOCO}>
          Bloco 1 · {modo === 'SU' ? 'Produto' : 'Portfólio'}
          <span style={HEADER_AUX}>· <MarcaAIdvisor /> propôs o portfólio típico do seu setor · marque o que oferece</span>
        </div>

        {/* Painel de chips · sugestões IA do setor */}
        <div style={{ background: G.fundo, border: `1px solid ${G.borda}`,
          borderRadius: 0, padding: '10px 14px', marginBottom: 12 }}>
          <div style={{ fontSize: 10, fontWeight: 800, color: G.txt2,
            letterSpacing: '0.10em', textTransform: 'uppercase', marginBottom: 8 }}>
            Sugestões da IA · marque o que você oferece
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {catalogoPortfolio.map(c => {
              const ativo = chipMarcado(c.id);
              return (
                <button key={c.id} type="button"
                  onClick={() => toggleChipCatalogo(c)}
                  title={c.descricao}
                  style={{
                    padding: '5px 11px', fontSize: 12, fontWeight: 600,
                    background: ativo ? G.verde : '#FFF',
                    color: ativo ? '#FFF' : G.txt1,
                    border: `1px solid ${ativo ? G.verde : G.borda}`,
                    borderRadius: 999, cursor: 'pointer',
                    fontFamily: "'Outfit', sans-serif",
                  }}>
                  {ativo ? '✓ ' : ''}{c.nome}
                </button>
              );
            })}
          </div>
        </div>

        {/* Lista do portfólio · cada linha vinda do catálogo é read-only · extras são editáveis */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {portfolio.map((p, i) => {
            const doCatalogo = !!p.id_catalogo;
            return (
              <div key={i} style={{
                padding: '12px 14px', background: '#FFF',
                border: `1px solid ${G.borda}`, borderRadius: 0,
                display: 'grid', gridTemplateColumns: '1.4fr 2fr 90px auto',
                gap: 10, alignItems: 'start',
              }}>
                {doCatalogo ? (
                  <>
                    <div style={{ padding: '7px 10px', fontSize: 13, fontWeight: 700,
                      color: G.txt1, lineHeight: 1.3 }}>
                      {p.nome}
                    </div>
                    <div style={{ padding: '7px 10px', fontSize: 12,
                      color: G.txt2, fontStyle: 'italic', lineHeight: 1.4 }}>
                      {p.descricao}
                    </div>
                  </>
                ) : (
                  <>
                    <input value={p.nome} onChange={e => editarPortfolio(i, 'nome', e.target.value)}
                      placeholder="Nome do produto/serviço próprio" style={inputBase}/>
                    <input value={p.descricao} onChange={e => editarPortfolio(i, 'descricao', e.target.value)}
                      placeholder="Descrição curta" style={inputBase}/>
                  </>
                )}
                <div style={{ minWidth: 110 }}>
                  <CampoNumericoBP60M
                    valor={p.mix}
                    onChange={(v) => editarPortfolio(i, 'mix', v)}
                    label="Mix do produto"
                    permitirDecimal={false}
                    min={0} max={100}
                    sufixo="%"
                    formato="int"
                  />
                </div>
                <button onClick={() => removerPortfolio(i)} title="Remover" style={btnRemover}><IconeLixeira /></button>
              </div>
            );
          })}
          {portfolio.length === 0 && (
            <div style={{ fontSize: 12, color: G.txt2, fontStyle: 'italic',
              padding: '12px 14px', background: G.fundo, borderRadius: 0 }}>
              Marque sugestões da IA acima ou adicione um produto/serviço próprio abaixo.
            </div>
          )}
        </div>
        <button onClick={adicionarPortfolio} style={btnAdd}>+ Adicionar produto/serviço próprio</button>

        {/* Resumo calculado read-only · padrão 10 */}
        <div style={{ background: '#FFF', border: `1px solid ${G.borda}`,
          borderRadius: 0, padding: '12px 14px', marginTop: 12 }}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 14,
            marginBottom: portfolio.length > 0 ? 10 : 0 }}>
            <div style={{ fontSize: 10, fontWeight: 800, color: G.txt2,
              letterSpacing: '0.10em', textTransform: 'uppercase' }}>
              Mix de Receita
            </div>
            <div style={{ fontSize: 22, fontWeight: 900,
              color: mixOK ? G.verde : G.vermelho }}>
              {somaMix}%
            </div>
          </div>
          {portfolio.filter(p => Number(p.mix) > 0).length > 0 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {portfolio.filter(p => Number(p.mix) > 0).map((p, i) => (
                <span key={i} style={{
                  padding: '4px 10px', fontSize: 11, fontWeight: 700,
                  background: G.verdeLight, color: G.verde,
                  border: `1px solid ${G.verde}`, borderRadius: 999,
                }}>
                  {(p.nome || 'Item sem nome').substring(0, 32)}: {p.mix}%
                </span>
              ))}
            </div>
          )}
          {portfolio.length === 0 && (
            <div style={{ fontSize: 12, color: G.txt2, fontStyle: 'italic' }}>
              Adicione produtos/serviços acima e informe o % de cada para o mix aparecer aqui.
            </div>
          )}
        </div>

        {/* Painel limitador-alerta · Padrão 8 (mesmo de T8 Bloco 2 e T9 Bloco 2) */}
        {portfolio.length > 0 && (
          <div style={{ marginTop: 10, padding: '8px 12px',
            background: mixOK ? G.verdeLight : '#FEF2F2',
            border: `1px solid ${mixOK ? G.verde : G.vermelho}`,
            borderRadius: 0, fontSize: 12,
            color: mixOK ? G.verde : G.vermelho, fontWeight: 700,
            display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10 }}>
            <span>
              {mixOK
                ? `✓ Soma 100% · válido`
                : <><IconeAlerta /> {somaMix < 100
                  ? `Soma ${somaMix}% · faltam ${100 - somaMix}% para fechar`
                  : `Soma ${somaMix}% · excede ${somaMix - 100}% acima de 100%`}</>}
            </span>
            {!mixOK && (
              <span style={{ fontSize: 11, fontWeight: 400, fontStyle: 'italic', whiteSpace: 'nowrap' }}>
                Aprovação bloqueada até soma fechar 100%
              </span>
            )}
          </div>
        )}

        {/* Botão de visualização do mix de receita */}
        <BotaoVisualizarGraficos
          aberto={graficosT4Aberto}
          onToggle={() => setGraficosT4Aberto(v => !v)}
          rotulo="Visualizar mix de receita">
          <div>
            <div style={{ fontSize: 10, fontWeight: 800, color: G.txt2,
              letterSpacing: '0.10em', textTransform: 'uppercase', marginBottom: 8 }}>
              Ilustração · {TITULO_EXHIBIT_POR_TIPO['t4_mix_receita']}
            </div>
            <GraficoPizzaMix items={portfolio}/>
          </div>
        </BotaoVisualizarGraficos>
      </div>

      {/* ═══ BLOCO 2 · Formação de Preço de Venda (SÓ STARTUP) ═══════════
          Espelha o Portfólio · a RGAI sugere composição + preço de mercado ·
          margem de contribuição como consequência (sem margem digitada). */}
      {modo === 'SU' && (
        <div>
          <div style={{ fontSize: 16, fontWeight: 700, letterSpacing: '0.10em',
            textTransform: 'uppercase', color: G.laranja, marginBottom: 8 }}>
            Bloco 2 · Formação de Preço de Venda
            <span style={{ fontSize: 12, fontWeight: 400, color: G.txt2,
              textTransform: 'none', letterSpacing: 0, marginLeft: 10 }}>
              · composição de custos e preço de cada produto/serviço
            </span>
          </div>
          <div style={{ padding: '10px 14px', background: G.verdeLight,
            borderLeft: `3px solid ${G.verde}`, borderRadius: 0, marginBottom: 10,
            fontSize: 12, lineHeight: 1.45, color: G.txt1 }}>
            A <MarcaAIdvisor /> conhece os produtos do Portfólio e já sugeriu, para
            cada um, a <strong>composição de insumos</strong> e o <strong>Preço de
            Venda de mercado</strong>. Confirme ou ajuste. A <strong>Margem de
            Contribuição</strong> é o resultado (Preço − Custo Direto) · impostos e
            despesas variáveis entram na Estruturação Financeira.
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {portfolio.map((item, idx) => {
              const aberto = precoAberto === idx;
              const tipo = tipoDoProduto(item);
              const cu = custoUnit(item);
              const pv = precoVenda(item);
              const mc = margemContrib(item);
              const mcPct = pv > 0 ? (mc / pv) * 100 : 0;
              const insumos = INSUMOS_PRECO_SU[tipo];
              const fmt = (n) => (Number(n) || 0).toLocaleString('pt-BR',
                { style: 'currency', currency: 'BRL', maximumFractionDigits: 2 });
              return (
                <div key={idx} style={{ background: '#FFF',
                  border: `1px solid ${aberto ? G.verde : G.borda}`, borderRadius: 0 }}>
                  {/* Header resumo */}
                  <div onClick={() => setPrecoAberto(aberto ? null : idx)}
                    style={{ padding: '10px 14px', cursor: 'pointer',
                      display: 'flex', alignItems: 'center', gap: 12 }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13, fontWeight: 800, color: G.txt1 }}>
                        {item.nome || 'Produto/serviço sem nome'}
                      </div>
                      <div style={{ fontSize: 11.5, color: G.txt2, marginTop: 2 }}>
                        {tipo === 'servico' ? 'Serviço' : 'Mercadoria'}
                        {' · '}custo {fmt(cu)} · preço <strong>{fmt(pv)}</strong>
                        {' · '}contribuição {fmt(mc)}
                      </div>
                    </div>
                    <button type="button"
                      onClick={(e) => { e.stopPropagation(); setPrecoAberto(aberto ? null : idx); }}
                      style={{ flexShrink: 0,
                        background: aberto ? '#FFF' : G.laranjaLight,
                        border: `1px solid ${G.laranja}`, color: G.laranja,
                        padding: '5px 12px', minWidth: 170, textAlign: 'center',
                        fontSize: 11, fontWeight: 700, letterSpacing: '0.06em',
                        textTransform: 'uppercase', borderRadius: 0, cursor: 'pointer',
                        fontFamily: "'Outfit', sans-serif", whiteSpace: 'nowrap' }}>
                      {aberto ? '▴ Clique para fechar' : '▾ Clique para abrir'}
                    </button>
                  </div>

                  {aberto && (
                    <div style={{ padding: '12px 14px 14px',
                      borderTop: `1px dashed ${G.borda}`,
                      display: 'flex', flexDirection: 'column', gap: 12 }}>
                      {/* Tipo (sugerido pela RGAI · confirma/ajusta) */}
                      <div>
                        <div style={{ fontSize: 10.5, fontWeight: 800, color: G.txt2,
                          letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 4 }}>
                          Tipo <span style={{ fontWeight: 500, textTransform: 'none',
                            letterSpacing: 0, color: G.txt2 }}>· sugerido pela RUNGOAL AIDVISOR</span>
                        </div>
                        <div style={{ display: 'flex', gap: 6, maxWidth: 320 }}>
                          {[{ id: 'mercadoria', label: 'Mercadoria' }, { id: 'servico', label: 'Serviço' }].map(t => {
                            const sel = tipo === t.id;
                            return (
                              <button key={t.id} type="button"
                                onClick={() => editarComposicao(idx, 'tipo', t.id)}
                                style={{ flex: 1, padding: '8px 10px', fontSize: 12, fontWeight: 700,
                                  background: sel ? G.verde : '#FFF', color: sel ? '#FFF' : G.txt1,
                                  border: `1px solid ${sel ? G.verde : G.borda}`, borderRadius: 0,
                                  cursor: 'pointer', fontFamily: "'Outfit', sans-serif" }}>
                                {sel ? '✓ ' : ''}{t.label}
                              </button>
                            );
                          })}
                        </div>
                      </div>

                      {/* Insumos · custo direto unitário em R$ (RGAI sugeriu) */}
                      <div>
                        <div style={{ fontSize: 10.5, fontWeight: 800, color: G.txt2,
                          letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 6 }}>
                          Insumos · custo direto unitário (R$)
                          <span style={{ fontWeight: 500, textTransform: 'none',
                            letterSpacing: 0, color: G.txt2 }}> · estimado pela RUNGOAL AIDVISOR · ajuste</span>
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                          {insumos.map(it => (
                            <div key={it.id} style={{ display: 'grid',
                              gridTemplateColumns: '1fr 150px', gap: 12, alignItems: 'center' }}>
                              <div style={{ fontSize: 12.5, color: G.txt1, lineHeight: 1.35 }}>
                                {it.label}
                              </div>
                              <CampoNumericoBP60M
                                valor={insumoValor(item, it.id)}
                                onChange={(v) => editarInsumoComposicao(idx, it.id, v)}
                                label={it.label}
                                permitirDecimal={true}
                                min={0}
                                formato="brl"
                              />
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Preço de venda (mercado · editável) + saídas calculadas */}
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
                        <div>
                          <div style={{ fontSize: 10.5, fontWeight: 800, color: G.txt2,
                            letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 4 }}>
                            Preço de Venda <span style={{ fontWeight: 500, textTransform: 'none',
                              letterSpacing: 0, color: G.txt2 }}>· mercado</span>
                          </div>
                          <CampoNumericoBP60M
                            valor={pv}
                            onChange={(v) => editarComposicao(idx, 'precoVenda', v)}
                            label="Preço de Venda competitivo de mercado (R$)"
                            permitirDecimal={true}
                            min={0}
                            formato="brl"
                          />
                        </div>
                        <div>
                          <div style={{ fontSize: 10.5, fontWeight: 800, color: G.txt2,
                            letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 4 }}>
                            Custo Direto Unitário
                          </div>
                          <div style={{ height: 36, display: 'flex', alignItems: 'center',
                            justifyContent: 'flex-end', padding: '0 12px',
                            border: `1px solid ${G.borda}`, borderRadius: 0, background: G.fundo,
                            fontSize: 14, fontWeight: 700, color: G.txt1,
                            fontFamily: "'IBM Plex Mono', monospace" }}>
                            {fmt(cu)}
                          </div>
                        </div>
                        <div>
                          <div style={{ fontSize: 10.5, fontWeight: 800, color: G.txt2,
                            letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 4 }}>
                            Margem de Contribuição
                          </div>
                          <div style={{ height: 36, display: 'flex', alignItems: 'center',
                            justifyContent: 'flex-end', gap: 8, padding: '0 12px',
                            border: `1px solid ${mc >= 0 ? G.verde : G.vermelho}`, borderRadius: 0,
                            background: mc >= 0 ? G.verdeLight : '#FEF2F2',
                            fontSize: 14, fontWeight: 900, color: mc >= 0 ? G.verde : G.vermelho,
                            fontFamily: "'IBM Plex Mono', monospace" }}>
                            <span>{fmt(mc)}</span>
                            <span style={{ fontSize: 11, fontWeight: 700 }}>
                              {mcPct.toLocaleString('pt-BR', { maximumFractionDigits: 0 })}%
                            </span>
                          </div>
                        </div>
                      </div>
                      <div style={{ fontSize: 11, color: G.txt2, fontStyle: 'italic' }}>
                        Margem de contribuição antes de impostos e despesas variáveis ·
                        o equilíbrio com os custos fixos é calculado na Estruturação Financeira.
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
            {portfolio.length === 0 && (
              <div style={{ fontSize: 12, color: G.txt2, fontStyle: 'italic',
                padding: '12px 14px', background: G.fundo, borderRadius: 0 }}>
                Marque os produtos/serviços no Portfólio (Bloco 1) · eles aparecem
                aqui com a composição de custos sugerida pela RUNGOAL AIDVISOR.
              </div>
            )}
          </div>
          <button onClick={adicionarPortfolio} style={btnAdd}>+ Adicionar produto/serviço</button>
        </div>
      )}

      {/* BLOCO 2 — Diferenciais Competitivos (chips IA · linhas marcadas · extras escritos) */}
      <div>
        <div style={HEADER_BLOCO}>
          Bloco {modo === 'SU' ? 3 : 2} · Diferenciais Competitivos
          <span style={HEADER_AUX}>· <MarcaAIdvisor /> propôs diferenciais típicos do seu setor · marque o que se aplica</span>
        </div>

        {/* Painel de chips · sugestões IA do setor */}
        <div style={{ background: G.fundo, border: `1px solid ${G.borda}`,
          borderRadius: 0, padding: '10px 14px', marginBottom: 12 }}>
          <div style={{ fontSize: 10, fontWeight: 800, color: G.txt2,
            letterSpacing: '0.10em', textTransform: 'uppercase', marginBottom: 8 }}>
            Sugestões da IA · marque o que se aplica
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {catalogoDif.map(c => {
              const ativo = chipDifMarcado(c.id);
              return (
                <button key={c.id} type="button"
                  onClick={() => toggleChipDif(c)}
                  title={c.detalhe}
                  style={{
                    padding: '5px 11px', fontSize: 12, fontWeight: 600,
                    background: ativo ? G.verde : '#FFF',
                    color: ativo ? '#FFF' : G.txt1,
                    border: `1px solid ${ativo ? G.verde : G.borda}`,
                    borderRadius: 999, cursor: 'pointer',
                    fontFamily: "'Outfit', sans-serif",
                  }}>
                  {ativo ? '✓ ' : ''}{c.titulo}
                </button>
              );
            })}
          </div>
        </div>

        {/* Lista de diferenciais · catálogo read-only · extras editáveis */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {diferenciais.map((d, i) => {
            const doCatalogo = !!d.id_catalogo;
            return (
              <div key={i} style={{
                padding: '12px 14px', background: '#FFF',
                border: `1px solid ${G.borda}`, borderRadius: 0,
                display: 'grid', gridTemplateColumns: '1fr 2fr auto',
                gap: 10, alignItems: 'start',
              }}>
                {doCatalogo ? (
                  <>
                    <div style={{ padding: '7px 10px', fontSize: 13, fontWeight: 700,
                      color: G.txt1, lineHeight: 1.3 }}>
                      {d.titulo}
                    </div>
                    <div style={{ padding: '7px 10px', fontSize: 12,
                      color: G.txt2, fontStyle: 'italic', lineHeight: 1.4 }}>
                      {d.detalhe}
                    </div>
                  </>
                ) : (
                  <>
                    <input value={d.titulo} onChange={e => editarDif(i, 'titulo', e.target.value)}
                      placeholder="Diferencial próprio" style={{ ...inputBase, fontWeight: 700 }}/>
                    <input value={d.detalhe} onChange={e => editarDif(i, 'detalhe', e.target.value)}
                      placeholder="Detalhe · por que isso é vantagem competitiva" style={inputBase}/>
                  </>
                )}
                <button onClick={() => removerDif(i)} title="Remover" style={btnRemover}><IconeLixeira /></button>
              </div>
            );
          })}
          {diferenciais.length === 0 && (
            <div style={{ fontSize: 12, color: G.txt2, fontStyle: 'italic',
              padding: '12px 14px', background: G.fundo, borderRadius: 0 }}>
              Marque sugestões da IA acima ou adicione um diferencial próprio abaixo.
            </div>
          )}
        </div>
        <button onClick={adicionarDif} style={btnAdd}>+ Adicionar diferencial próprio</button>
      </div>

      {/* BLOCO 3 — Inovação & P&D · pipeline + investimento + parcerias */}
      <div>
        <div style={HEADER_BLOCO}>
          Bloco {modo === 'SU' ? 4 : 3} · Inovação & P&D
          <span style={HEADER_AUX}>· pipeline futuro · investimento · parcerias estratégicas</span>
        </div>

        {/* Sub 3a — Pipeline de Inovação (chips IA · lista marcada · extras) */}
        <div style={{ marginBottom: 14 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: G.txt2,
            textTransform: 'uppercase', letterSpacing: '0.06em',
            marginBottom: 6 }}>
            Pipeline de Inovação
          </div>
          <div style={{ background: G.fundo, border: `1px solid ${G.borda}`,
            borderRadius: 0, padding: '10px 14px', marginBottom: 10 }}>
            <div style={{ fontSize: 10, fontWeight: 800, color: G.txt2,
              letterSpacing: '0.10em', textTransform: 'uppercase', marginBottom: 8 }}>
              Sugestões da IA · marque o que faz sentido
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {catalogoPipe.map(c => {
                const ativo = chipPipeMarcado(c.id);
                return (
                  <button key={c.id} type="button"
                    onClick={() => toggleChipPipe(c)}
                    title={`${c.descricao} · ${c.prazo}`}
                    style={{
                      padding: '5px 11px', fontSize: 12, fontWeight: 600,
                      background: ativo ? G.verde : '#FFF',
                      color: ativo ? '#FFF' : G.txt1,
                      border: `1px solid ${ativo ? G.verde : G.borda}`,
                      borderRadius: 999, cursor: 'pointer',
                      fontFamily: "'Outfit', sans-serif",
                    }}>
                    {ativo ? '✓ ' : ''}{c.descricao.substring(0, 50)}
                  </button>
                );
              })}
            </div>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {pipeline.map((p, i) => {
              const doCatalogo = !!p.id_catalogo;
              return (
                <div key={i} style={{
                  padding: '10px 14px', background: '#FFF',
                  border: `1px solid ${G.borda}`, borderRadius: 0,
                  display: 'grid', gridTemplateColumns: '2fr 140px auto',
                  gap: 10, alignItems: 'start',
                }}>
                  {doCatalogo ? (
                    <>
                      <div style={{ padding: '7px 10px', fontSize: 13, fontWeight: 700,
                        color: G.txt1, lineHeight: 1.3 }}>
                        {p.descricao}
                      </div>
                      <div style={{ padding: '7px 10px', fontSize: 12, color: G.txt2,
                        fontStyle: 'italic', textAlign: 'center' }}>
                        {p.prazo || '—'}
                      </div>
                    </>
                  ) : (
                    <>
                      <input value={p.descricao} onChange={e => editarPipe(i, 'descricao', e.target.value)}
                        placeholder="Iniciativa de inovação · novo produto/serviço/método"
                        style={inputBase}/>
                      <input value={p.prazo || ''} onChange={e => editarPipe(i, 'prazo', e.target.value)}
                        placeholder="Prazo (opcional)" style={{ ...inputBase, textAlign: 'center' }}/>
                    </>
                  )}
                  <button onClick={() => removerPipe(i)} title="Remover" style={btnRemover}><IconeLixeira /></button>
                </div>
              );
            })}
          </div>
          <button onClick={adicionarPipe} style={btnAdd}>+ Adicionar iniciativa própria</button>
        </div>

        {/* Sub 3b — Investimento em P&D (dropdown intervalo) */}
        <div style={{ marginBottom: 14,
          background: '#FFF',
          border: `1px solid ${pdAberto ? G.verde : G.borda}`,
          borderRadius: 0, overflow: 'hidden' }}>
          <div style={{
            background: pdAberto ? G.verdeLight : 'transparent',
            padding: '8px 14px',
            display: 'flex', alignItems: 'center', gap: 12,
          }}>
            <div style={{
              flexShrink: 0, width: 22, height: 22, borderRadius: '50%',
              background: investimentoPD_intervalo ? G.verde : G.borda,
              color: '#FFF', fontSize: 11, fontWeight: 800,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              {investimentoPD_intervalo ? '✓' : '%'}
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: G.txt1,
                lineHeight: 1.3 }}>
                Qual o % da receita destinado a Pesquisa e Desenvolvimento?
              </div>
              {investimentoPD_intervalo && !pdAberto && (
                <div style={{ fontSize: 12, color: G.verde, fontWeight: 600,
                  marginTop: 2 }}>
                  {INTERVALOS_PD.find(o => o.id === investimentoPD_intervalo)?.label}
                </div>
              )}
            </div>
            <button onClick={() => setPdAberto(!pdAberto)}
              style={{
                flexShrink: 0,
                background: pdAberto ? '#FFF' : G.laranjaLight,
                border: `1px solid ${G.laranja}`,
                color: G.laranja,
                padding: '4px 9px', fontSize: 9, fontWeight: 800,
                letterSpacing: '0.08em', textTransform: 'uppercase',
                borderRadius: 0, cursor: 'pointer',
                fontFamily: "'Outfit', sans-serif",
                whiteSpace: 'nowrap',
              }}>
              {pdAberto ? '▴ Clique para fechar' : '▾ Clique para abrir'}
            </button>
          </div>
          {pdAberto && (
            <div style={{ padding: '4px 14px 12px',
              display: 'flex', flexDirection: 'column', gap: 6 }}>
              {INTERVALOS_PD.map(o => {
                const ativo = investimentoPD_intervalo === o.id;
                return (
                  <label key={o.id}
                    style={{ display: 'flex', alignItems: 'flex-start', gap: 10,
                      padding: '8px 12px', cursor: 'pointer',
                      background: ativo ? G.verdeLight : '#FFF',
                      border: ativo ? `2px solid ${G.verde}` : `1px solid ${G.borda}`,
                      borderRadius: 0,
                      fontFamily: "'Outfit', sans-serif",
                    }}>
                    <input type="radio"
                      name="investimentoPD"
                      checked={ativo}
                      onChange={() => { atualizar('investimentoPD_intervalo', o.id); setPdAberto(false); }}
                      style={{
                        appearance: 'none', WebkitAppearance: 'none',
                        width: 14, height: 14, marginTop: 3, flexShrink: 0,
                        border: `2px solid ${ativo ? G.verde : G.borda}`,
                        borderRadius: 0,
                        background: ativo ? G.verde : '#FFF',
                        cursor: 'pointer',
                      }}/>
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 800,
                        color: ativo ? G.verde : G.txt1, marginBottom: 2 }}>
                        {o.label}
                      </div>
                      <div style={{ fontSize: 11, color: G.txt2, lineHeight: 1.4 }}>
                        {o.desc}
                      </div>
                    </div>
                  </label>
                );
              })}
            </div>
          )}
        </div>

        {/* Sub 3c — Parcerias relevantes (chips IA · lista marcada · extras) */}
        <div>
          <div style={{ fontSize: 12, fontWeight: 700, color: G.txt2,
            textTransform: 'uppercase', letterSpacing: '0.06em',
            marginBottom: 6 }}>
            Parcerias Relevantes
          </div>
          <div style={{ background: G.fundo, border: `1px solid ${G.borda}`,
            borderRadius: 0, padding: '10px 14px', marginBottom: 10 }}>
            <div style={{ fontSize: 10, fontWeight: 800, color: G.txt2,
              letterSpacing: '0.10em', textTransform: 'uppercase', marginBottom: 8 }}>
              Sugestões da IA · marque quais você tem ou pretende ter
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {catalogoPar.map(c => {
                const ativo = chipParMarcado(c.id);
                return (
                  <button key={c.id} type="button"
                    onClick={() => toggleChipPar(c)}
                    title={c.tipo}
                    style={{
                      padding: '5px 11px', fontSize: 12, fontWeight: 600,
                      background: ativo ? G.verde : '#FFF',
                      color: ativo ? '#FFF' : G.txt1,
                      border: `1px solid ${ativo ? G.verde : G.borda}`,
                      borderRadius: 999, cursor: 'pointer',
                      fontFamily: "'Outfit', sans-serif",
                    }}>
                    {ativo ? '✓ ' : ''}{c.nome}
                  </button>
                );
              })}
            </div>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {parcerias.map((p, i) => {
              const doCatalogo = !!p.id_catalogo;
              return (
                <div key={i} style={{
                  padding: '10px 14px', background: '#FFF',
                  border: `1px solid ${G.borda}`, borderRadius: 0,
                  display: 'grid', gridTemplateColumns: '1.4fr 1.4fr auto',
                  gap: 10, alignItems: 'start',
                }}>
                  {doCatalogo ? (
                    <>
                      <div style={{ padding: '7px 10px', fontSize: 13, fontWeight: 700,
                        color: G.txt1, lineHeight: 1.3 }}>
                        {p.nome}
                      </div>
                      <div style={{ padding: '7px 10px', fontSize: 12, color: G.txt2,
                        fontStyle: 'italic' }}>
                        {p.tipo}
                      </div>
                    </>
                  ) : (
                    <>
                      <input value={p.nome} onChange={e => editarPar(i, 'nome', e.target.value)}
                        placeholder="Nome do parceiro próprio" style={{ ...inputBase, fontWeight: 700 }}/>
                      <input value={p.tipo} onChange={e => editarPar(i, 'tipo', e.target.value)}
                        placeholder="Tipo · natureza da parceria" style={inputBase}/>
                    </>
                  )}
                  <button onClick={() => removerPar(i)} title="Remover" style={btnRemover}><IconeLixeira /></button>
                </div>
              );
            })}
          </div>
          <button onClick={adicionarPar} style={btnAdd}>+ Adicionar parceria própria</button>
        </div>
      </div>

    </div>
  );
};


// ═══════════════════════════════════════════════════════════════════════
// Tarefa 5 — Mercado & Concorrência (fusão T5+T6 antigas · decidido 12/06)
// ═══════════════════════════════════════════════════════════════════════
// Catálogo de Tendências de Mercado típicas por setor
const TENDENCIAS_CATALOGO_POR_SETOR = {
  'E-commerce': [
    { id: 'social_commerce',   titulo: 'Social commerce em alta',         detalhe: 'Venda via redes sociais · live commerce ganha espaço' },
    { id: 'ia_recomenda',      titulo: 'IA aplicada à recomendação',      detalhe: 'Personalização da experiência aumenta conversão e ticket' },
    { id: 'sustentavel_cons',  titulo: 'Sustentabilidade no consumo',     detalhe: 'Consumidor valoriza embalagem reciclável e origem ética' },
    { id: 'logistica_rapida',  titulo: 'Logística rápida (same-day)',     detalhe: 'Expectativa de entrega em poucas horas em grandes capitais' },
    { id: 'mp_especializado',  titulo: 'Marketplaces especializados',     detalhe: 'Verticais especializados (móveis, decor, beleza) ganham peso' },
    { id: 'd2c_marcas',        titulo: 'Marcas D2C em expansão',          detalhe: 'Linhas próprias · margem superior · relação direta com cliente' },
  ],
  'Bar/Restaurante': [
    { id: 'cozinha_contemp',   titulo: 'Cozinha brasileira contemporânea',detalhe: 'Releituras autorais ganham guias e prêmios' },
    { id: 'mixologia',         titulo: 'Mixologia autoral em ascensão',   detalhe: 'Drinks assinados, ingredientes locais · bar premiado' },
    { id: 'plant_based',       titulo: 'Plant-based e vegetarianos',      detalhe: 'Cardápios vegetarianos e veganos viram exigência' },
    { id: 'delivery_premium',  titulo: 'Delivery premium',                detalhe: 'Casas premium criam linhas próprias de delivery sem comprometer marca' },
    { id: 'eventos_colab',     titulo: 'Eventos gastronômicos colaborativos', detalhe: 'Chef convidado · 4-hands · pop-ups · jantares temáticos' },
    { id: 'sustent_origem',    titulo: 'Sustentabilidade na origem',      detalhe: 'Ingredientes locais · circular · zero desperdício' },
  ],
  'Saúde': [
    { id: 'telemed_consol',    titulo: 'Telemedicina consolidada',        detalhe: 'Pós-pandemia · paciente espera modalidade híbrida' },
    { id: 'preventiva',        titulo: 'Medicina preventiva ganha espaço',detalhe: 'Pacotes anuais de check-up · cuidado continuado' },
    { id: 'ia_diag',           titulo: 'IA diagnóstica em ascensão',      detalhe: 'Triagem e suporte ao diagnóstico via IA' },
    { id: 'saude_mental',      titulo: 'Saúde mental ampliada',           detalhe: 'Demanda crescente · psicoterapia, psiquiatria, bem-estar' },
    { id: 'saude_corp',        titulo: 'Saúde corporativa B2B em expansão', detalhe: 'Empresas contratam pacotes corporativos preventivos' },
    { id: 'coordenado',        titulo: 'Cuidado integrado e coordenado',  detalhe: 'Coordenação entre especialidades · prontuário único' },
  ],
  'Construção Civil': [
    { id: 'bim_padrao',        titulo: 'BIM (Building Information Modeling)', detalhe: 'BIM virou padrão para obras corporativas e públicas' },
    { id: 'construcao_sustent',titulo: 'Construção sustentável certificada', detalhe: 'LEED, AQUA · clientes corporativos exigem certificação' },
    { id: 'pre_fabricados',    titulo: 'Pré-fabricados / industrialização', detalhe: 'Steel frame, drywall, módulos pré-fabricados ganham tração' },
    { id: 'automacao_canteiro',titulo: 'Automação de canteiro',           detalhe: 'Dashboards de campo, IoT em obras, gestão remota' },
    { id: 'energia_solar',     titulo: 'Energia solar embarcada',         detalhe: 'Projetos integram geração solar desde a fundação' },
    { id: 'mao_obra_esc',      titulo: 'Escassez de mão de obra qualificada',detalhe: 'Pressão de custo · imperativo de produtividade' },
  ],
  'geral': [
    { id: 'digital_op',        titulo: 'Digitalização da operação',       detalhe: 'Sistemas integrados · dados em tempo real' },
    { id: 'sustent_req',       titulo: 'Sustentabilidade como requisito', detalhe: 'Clientes corporativos exigem ESG · regulação avançando' },
    { id: 'personalizacao',    titulo: 'Expectativa de personalização',   detalhe: 'Consumidor B2C e B2B esperam experiência customizada' },
    { id: 'perfil_consumidor', titulo: 'Mudança no perfil do consumidor', detalhe: 'Gerações mais novas · novos hábitos de consumo' },
    { id: 'ia_operacao',       titulo: 'IA aplicada à operação',          detalhe: 'Atendimento, suporte e processos com IA' },
    { id: 'plataformas_mp',    titulo: 'Plataformas de marketplace',      detalhe: 'Verticais especializadas ganham peso no setor' },
  ],
};

// Catálogo de Tipos de Concorrente por setor (IA propõe arquétipos · empresário põe nome real)
const CONCORRENTES_TIPOS_POR_SETOR = {
  'E-commerce': [
    { id: 'mp_lider',       nome: 'Marketplace líder',                 share: '40%', posicionamento: 'Variedade enorme · preço competitivo · marca consolidada' },
    { id: 'ecom_especial',  nome: 'E-commerce especializado',          share: '15%', posicionamento: 'Curadoria editorial · ticket médio elevado' },
    { id: 'mp_vertical',    nome: 'Marketplace vertical de móveis',    share: '12%', posicionamento: 'Foco em mobília · catálogo profundo' },
    { id: 'big_retail',     nome: 'Big retailer com canal online',     share: '10%', posicionamento: 'Marca consolidada · loja física + online' },
    { id: 'd2c_marca',      nome: 'Marca direta D2C (private label)',  share: '8%',  posicionamento: 'Linha própria · margem superior · marketing direto' },
  ],
  'Bar/Restaurante': [
    { id: 'autoral_consag', nome: 'Restaurante autoral consagrado',    share: 'top 5', posicionamento: 'Chef-proprietário consolidado · guias premiados' },
    { id: 'premiado_michel',nome: 'Casa premiada (Michelin / 50 Best)',share: 'top 3', posicionamento: 'Reconhecimento internacional · clientela exclusiva' },
    { id: 'rede_premium',   nome: 'Restaurante de redes premium',      share: 'médio', posicionamento: 'Padrão consistente · presença regional' },
    { id: 'almoco_bairro',  nome: 'Casa de almoço executivo de bairro',share: 'local', posicionamento: 'Preço acessível · volume · clientela corporativa' },
    { id: 'bistro',         nome: 'Bistrô / bar gastronômico',         share: 'local', posicionamento: 'Ambiente intimista · cardápio enxuto' },
    { id: 'nova_ascensao',  nome: 'Casa nova em ascensão',             share: 'novo',  posicionamento: 'Buzz recente · chef em emergência' },
  ],
  'Saúde': [
    { id: 'hospital_ref',   nome: 'Hospital de referência',            share: 'top regional', posicionamento: 'Marca consolidada · alta complexidade' },
    { id: 'clinica_bairro', nome: 'Clínica de bairro tradicional',     share: 'local',  posicionamento: 'Médico de família · vínculo histórico' },
    { id: 'rede_multissetor', nome: 'Rede de clínicas multissetorial', share: '20%',  posicionamento: 'Escala · cobertura regional · convênios' },
    { id: 'telemed_plat',   nome: 'Plataforma de telemedicina',        share: '10%',  posicionamento: 'Custo baixo · disponibilidade · tech-first' },
    { id: 'clinica_espec',  nome: 'Clínica de especialidade única',    share: 'nicho', posicionamento: 'Profundidade em uma área · ticket alto' },
    { id: 'plano_rede_prop',nome: 'Plano de saúde com rede própria',   share: 'top',   posicionamento: 'Integração vertical · clientela cativa' },
  ],
  'Construção Civil': [
    { id: 'reg_consolidada',nome: 'Construtora regional consolidada',  share: 'top regional', posicionamento: 'Carteira recorrente · obras médias' },
    { id: 'empreiteira_corp',nome: 'Empreiteira de obras corporativas', share: '15%',  posicionamento: 'Especialização B2B · grandes clientes' },
    { id: 'nacional_grande',nome: 'Construtora nacional de grande porte', share: '25%', posicionamento: 'Escala · obras de grande complexidade' },
    { id: 'retrofit',       nome: 'Empresa de retrofit / reforma',     share: 'nicho', posicionamento: 'Especialização em reforma · alto padrão' },
    { id: 'loja_corporat',  nome: 'Loja de materiais corporativa',     share: '10%',  posicionamento: 'Suprimento integrado · canal B2B' },
    { id: 'eng_consult',    nome: 'Engenharia consultiva',             share: 'nicho', posicionamento: 'Projetos técnicos · sem execução' },
  ],
  'geral': [
    { id: 'lider_mercado',  nome: 'Líder de mercado',                  share: '30%',  posicionamento: 'Marca consolidada · escala · poder de barganha' },
    { id: 'regional_consol',nome: 'Player regional consolidado',       share: '15%',  posicionamento: 'Conhecimento profundo da região · vínculo local' },
    { id: 'nicho_espec',    nome: 'Especializado em nicho',            share: 'nicho', posicionamento: 'Profundidade · ticket alto · clientela seletiva' },
    { id: 'marca_premium',  nome: 'Marca premium',                     share: 'top',   posicionamento: 'Preço elevado · imagem aspiracional' },
    { id: 'baixo_custo',    nome: 'Player de baixo custo',             share: 'volume',posicionamento: 'Preço competitivo · escala · volume' },
    { id: 'entrante_rec',   nome: 'Entrante recente',                  share: 'novo',  posicionamento: 'Modelo disruptivo · busca crescimento' },
  ],
};

// Catálogo de Vantagens Competitivas (Moats) típicas por setor
const MOATS_CATALOGO_POR_SETOR = {
  'E-commerce': [
    { id: 'curadoria_moat',    tipo: 'Curadoria editorial diferenciada', detalhe: 'Filtro de design e qualidade único · difícil de replicar' },
    { id: 'marca_moat_ec',     tipo: 'Marca conhecida e respeitada',     detalhe: 'Top of mind em design · confiança do consumidor' },
    { id: 'cd_moat',           tipo: 'CD próprio (vantagem logística)',  detalhe: 'Prazo de entrega menor que concorrência' },
    { id: 'private_moat',      tipo: 'Private label exclusiva',          detalhe: 'Margem superior · não revendida por concorrentes' },
    { id: 'crm_moat',          tipo: 'Base ativa de clientes / CRM',     detalhe: 'CAC reduzido · cross-sell e upsell de baixo custo' },
    { id: 'acordos_fornec',    tipo: 'Acordos exclusivos com fornecedores', detalhe: 'Linhas exclusivas que concorrência não consegue listar' },
  ],
  'Bar/Restaurante': [
    { id: 'chef_moat',         tipo: 'Chef-proprietário com reputação',  detalhe: 'Marca pessoal · clientela vinculada ao chef' },
    { id: 'ponto_moat',        tipo: 'Ponto / localização premium',      detalhe: 'Endereço estabelecido · fluxo natural difícil de copiar' },
    { id: 'cardapio_autoral',  tipo: 'Carta única e autoral',            detalhe: 'Receitas exclusivas · técnica diferenciada' },
    { id: 'lista_espera',      tipo: 'Reservas com lista de espera',     detalhe: 'Exclusividade · clientela fiel · ticket premium' },
    { id: 'guias_moat',        tipo: 'Reputação em guias gastronômicos', detalhe: 'Prêmios Michelin / 50 Best / Gastrô · visibilidade nacional' },
    { id: 'equipe_estavel',    tipo: 'Equipe formada e estável',         detalhe: 'Baixa rotatividade · padrão preservado · treinamento interno' },
  ],
  'Saúde': [
    { id: 'corpo_clinico',     tipo: 'Corpo clínico multidisciplinar',   detalhe: 'Coordenação entre especialidades · prontuário único' },
    { id: 'credenciamento',    tipo: 'Credenciamento com convênios principais', detalhe: 'Atendimento direto · base ampla de pacientes elegíveis' },
    { id: 'equipamentos',      tipo: 'Equipamentos de ponta proprietários', detalhe: 'Diagnósticos precisos · ticket premium' },
    { id: 'tecnologia_saude',  tipo: 'Tecnologia (prontuário + telemed)',detalhe: 'Integração presencial + remoto · experiência diferenciada' },
    { id: 'pacientes_recor',   tipo: 'Base de pacientes recorrentes',    detalhe: 'CAC reduzido · relacionamento de longo prazo' },
    { id: 'reputacao_local',   tipo: 'Reputação local consolidada',      detalhe: 'Indicação boca a boca · top of mind regional' },
  ],
  'Construção Civil': [
    { id: 'crea_responsavel',  tipo: 'Responsável técnico CREA ativo',   detalhe: 'Engenheiro presente em todas as obras · diferencial reconhecido' },
    { id: 'carteira_corp',     tipo: 'Carteira de clientes corporativos',detalhe: 'Recorrência B2B · contratos continuados' },
    { id: 'loja_propria',      tipo: 'Loja própria de materiais',        detalhe: 'Custo de insumos reduzido · vantagem competitiva direta' },
    { id: 'equipe_tecnica',    tipo: 'Equipe técnica estável',           detalhe: 'Padrão preservado · know-how interno · curva de aprendizado' },
    { id: 'certificacoes_obra',tipo: 'Certificações ambientais',         detalhe: 'LEED, AQUA, ISO · acesso a obras corporativas restritas' },
    { id: 'carteira_referencia', tipo: 'Carteira de obras de referência', detalhe: 'Portfólio de marca · credibilidade para novos contratos' },
  ],
  'geral': [
    { id: 'marca_geral',       tipo: 'Marca consolidada',                detalhe: 'Top of mind no segmento · confiança do cliente' },
    { id: 'base_rec',          tipo: 'Base de clientes recorrente',      detalhe: 'CAC reduzido · receita previsível · relacionamento de longo prazo' },
    { id: 'tec_propria',       tipo: 'Tecnologia proprietária',          detalhe: 'Sistema interno único · difícil de replicar' },
    { id: 'escala_moat',       tipo: 'Escala / poder de barganha',       detalhe: 'Volume permite negociação melhor com fornecedores e canais' },
    { id: 'nicho_moat',        tipo: 'Conhecimento profundo do nicho',   detalhe: 'Anos de experiência · profundidade · ticket premium' },
    { id: 'local_moat',        tipo: 'Localização privilegiada',         detalhe: 'Ponto físico ou regional difícil de igualar' },
  ],
};

// Intervalos para TAM / SAM / SOM (R$ em milhões) · empresário marca a faixa
const INTERVALOS_TAM = [
  { id: 'a', label: 'Até R$ 100 mi',           meio: 50,     desc: 'mercado nicho · região delimitada' },
  { id: 'b', label: 'R$ 100 mi a R$ 1 bilhão', meio: 500,    desc: 'mercado regional / setor específico' },
  { id: 'c', label: 'R$ 1 bi a R$ 10 bi',      meio: 5000,   desc: 'mercado nacional · setor de médio porte' },
  { id: 'd', label: 'R$ 10 bi a R$ 100 bi',    meio: 50000,  desc: 'mercado nacional grande · varejo, saúde, construção' },
  { id: 'e', label: 'Mais de R$ 100 bi',       meio: 200000, desc: 'mercado massivo · alimentação, financeiro, energia' },
];
const INTERVALOS_SAM = [
  { id: 'a', label: 'Até R$ 10 mi',           meio: 5,      desc: 'segmento estreito · nicho de alto valor' },
  { id: 'b', label: 'R$ 10 mi a R$ 100 mi',   meio: 50,     desc: 'segmento atendível com modelo atual' },
  { id: 'c', label: 'R$ 100 mi a R$ 1 bi',    meio: 500,    desc: 'parcela acessível por geografia + canal' },
  { id: 'd', label: 'R$ 1 bi a R$ 10 bi',     meio: 5000,   desc: 'segmento atendível com expansão' },
  { id: 'e', label: 'Mais de R$ 10 bi',       meio: 30000,  desc: 'segmento muito amplo · raro para PME' },
];
const INTERVALOS_SOM = [
  { id: 'a', label: 'Até R$ 1 mi',            meio: 0.5,    desc: 'empresa nascente · 0-1 ano' },
  { id: 'b', label: 'R$ 1 mi a R$ 10 mi',     meio: 5,      desc: 'empresa pequena · em crescimento' },
  { id: 'c', label: 'R$ 10 mi a R$ 100 mi',   meio: 50,     desc: 'empresa de médio porte · player relevante' },
  { id: 'd', label: 'R$ 100 mi a R$ 1 bi',    meio: 500,    desc: 'líder regional · expansão nacional' },
  { id: 'e', label: 'Mais de R$ 1 bi',        meio: 3000,   desc: 'líder de mercado · escala consolidada' },
];

const RASCUNHO_TAREFA_5 = {
  shopmax: {
    tam_intervalo: 'c', tam_desc: 'E-commerce de móveis e decoração no Brasil',
    sam_intervalo: 'c', sam_desc: 'Segmento curadoria + ticket médio + alcance logístico',
    som_intervalo: 'c', som_desc: 'Parcela conquistável em 5 anos',
    tendencias: [
      { id_catalogo: 'd2c_marcas',       titulo: 'Marcas D2C em expansão',          detalhe: 'Linhas próprias · margem superior · relação direta com cliente' },
      { id_catalogo: 'logistica_rapida', titulo: 'Logística rápida (same-day)',     detalhe: 'Expectativa de entrega em poucas horas em grandes capitais' },
      { id_catalogo: 'sustentavel_cons', titulo: 'Sustentabilidade no consumo',     detalhe: 'Consumidor valoriza embalagem reciclável e origem ética' },
    ],
    players: [
      { id_catalogo: 'mp_lider',       nome: 'Marketplace líder',                share: '40%', posicionamento: 'Variedade enorme · preço competitivo · marca consolidada' },
      { id_catalogo: 'mp_vertical',    nome: 'Marketplace vertical de móveis',   share: '12%', posicionamento: 'Foco em mobília · catálogo profundo' },
      { id_catalogo: 'big_retail',     nome: 'Big retailer com canal online',    share: '10%', posicionamento: 'Marca consolidada · loja física + online' },
      { id_catalogo: 'ecom_especial',  nome: 'E-commerce especializado',         share: '15%', posicionamento: 'Curadoria editorial · ticket médio elevado' },
    ],
    moats: [
      { id_catalogo: 'curadoria_moat', tipo: 'Curadoria editorial diferenciada', detalhe: 'Filtro de design e qualidade único · difícil de replicar' },
      { id_catalogo: 'cd_moat',        tipo: 'CD próprio (vantagem logística)',  detalhe: 'Prazo de entrega menor que concorrência' },
      { id_catalogo: 'private_moat',   tipo: 'Private label exclusiva',          detalhe: 'Margem superior · não revendida por concorrentes' },
    ],
  },
  sabor_arte: {
    tam_intervalo: 'd', tam_desc: 'Restaurantes em SP capital',
    sam_intervalo: 'c', sam_desc: 'Alta gastronomia · pontos premium · ticket médio R$ 150+',
    som_intervalo: 'c', som_desc: 'Pontos região Jardins/Itaim · alvo 5 anos',
    tendencias: [
      { id_catalogo: 'cozinha_contemp',   titulo: 'Cozinha brasileira contemporânea',   detalhe: 'Releituras autorais ganham guias e prêmios' },
      { id_catalogo: 'sustent_origem',    titulo: 'Sustentabilidade na origem',         detalhe: 'Ingredientes locais · circular · zero desperdício' },
      { id_catalogo: 'delivery_premium',  titulo: 'Delivery premium',                   detalhe: 'Casas premium criam linhas próprias de delivery sem comprometer marca' },
    ],
    players: [
      { id_catalogo: 'autoral_consag',  nome: 'Restaurante autoral consagrado',    share: 'top 5',  posicionamento: 'Chef-proprietário consolidado · guias premiados' },
      { id_catalogo: 'premiado_michel', nome: 'Casa premiada (Michelin / 50 Best)',share: 'top 3',  posicionamento: 'Reconhecimento internacional · clientela exclusiva' },
      { id_catalogo: 'bistro',          nome: 'Bistrô / bar gastronômico',         share: 'local', posicionamento: 'Ambiente intimista · cardápio enxuto' },
      { id_catalogo: 'nova_ascensao',   nome: 'Casa nova em ascensão',             share: 'novo',  posicionamento: 'Buzz recente · chef em emergência' },
    ],
    moats: [
      { id_catalogo: 'chef_moat',        tipo: 'Chef-proprietário com reputação', detalhe: 'Marca pessoal · clientela vinculada ao chef' },
      { id_catalogo: 'cardapio_autoral', tipo: 'Carta única e autoral',           detalhe: 'Receitas exclusivas · técnica diferenciada' },
      { id_catalogo: 'ponto_moat',       tipo: 'Ponto / localização premium',     detalhe: 'Endereço estabelecido · fluxo natural difícil de copiar' },
    ],
  },
  clinicavida: {
    tam_intervalo: 'e', tam_desc: 'Saúde privada Brasil (consultas + exames + procedimentos)',
    sam_intervalo: 'd', sam_desc: 'Clínicas multidisciplinares em SP capital',
    som_intervalo: 'c', som_desc: 'Bairro Moema e adjacências · alvo 5 anos',
    tendencias: [
      { id_catalogo: 'preventiva',      titulo: 'Medicina preventiva ganha espaço',  detalhe: 'Pacotes anuais de check-up · cuidado continuado' },
      { id_catalogo: 'telemed_consol',  titulo: 'Telemedicina consolidada',          detalhe: 'Pós-pandemia · paciente espera modalidade híbrida' },
      { id_catalogo: 'saude_mental',    titulo: 'Saúde mental ampliada',             detalhe: 'Demanda crescente · psicoterapia, psiquiatria, bem-estar' },
    ],
    players: [
      { id_catalogo: 'hospital_ref',    nome: 'Hospital de referência',            share: 'top regional', posicionamento: 'Marca consolidada · alta complexidade' },
      { id_catalogo: 'rede_multissetor',nome: 'Rede de clínicas multissetorial',   share: '20%',          posicionamento: 'Escala · cobertura regional · convênios' },
      { id_catalogo: 'telemed_plat',    nome: 'Plataforma de telemedicina',        share: '10%',          posicionamento: 'Custo baixo · disponibilidade · tech-first' },
      { id_catalogo: 'clinica_bairro',  nome: 'Clínica de bairro tradicional',     share: 'local',        posicionamento: 'Médico de família · vínculo histórico' },
    ],
    moats: [
      { id_catalogo: 'corpo_clinico',    tipo: 'Corpo clínico multidisciplinar',           detalhe: 'Coordenação entre especialidades · prontuário único' },
      { id_catalogo: 'tecnologia_saude', tipo: 'Tecnologia (prontuário + telemed)',        detalhe: 'Integração presencial + remoto · experiência diferenciada' },
      { id_catalogo: 'credenciamento',   tipo: 'Credenciamento com convênios principais',  detalhe: 'Atendimento direto · base ampla de pacientes elegíveis' },
    ],
  },
  construmax: {
    tam_intervalo: 'd', tam_desc: 'Construção corporativa Brasil (obras comerciais e adaptações)',
    sam_intervalo: 'c', sam_desc: 'SP/RMC · médio porte · clientes corporativos',
    som_intervalo: 'c', som_desc: 'Carteira projetada · 5 anos',
    tendencias: [
      { id_catalogo: 'bim_padrao',         titulo: 'BIM (Building Information Modeling)',  detalhe: 'BIM virou padrão para obras corporativas e públicas' },
      { id_catalogo: 'pre_fabricados',     titulo: 'Pré-fabricados / industrialização',    detalhe: 'Steel frame, drywall, módulos pré-fabricados ganham tração' },
      { id_catalogo: 'construcao_sustent', titulo: 'Construção sustentável certificada',   detalhe: 'LEED, AQUA · clientes corporativos exigem certificação' },
    ],
    players: [
      { id_catalogo: 'reg_consolidada',  nome: 'Construtora regional consolidada',  share: 'top regional', posicionamento: 'Carteira recorrente · obras médias' },
      { id_catalogo: 'empreiteira_corp', nome: 'Empreiteira de obras corporativas', share: '15%',          posicionamento: 'Especialização B2B · grandes clientes' },
      { id_catalogo: 'nacional_grande',  nome: 'Construtora nacional de grande porte', share: '25%',       posicionamento: 'Escala · obras de grande complexidade' },
    ],
    moats: [
      { id_catalogo: 'crea_responsavel', tipo: 'Responsável técnico CREA ativo', detalhe: 'Engenheiro presente em todas as obras · diferencial reconhecido' },
      { id_catalogo: 'loja_propria',     tipo: 'Loja própria de materiais',      detalhe: 'Custo de insumos reduzido · vantagem competitiva direta' },
      { id_catalogo: 'carteira_corp',    tipo: 'Carteira de clientes corporativos',detalhe: 'Recorrência B2B · contratos continuados' },
    ],
  },
};

// ═══════════════════════════════════════════════════════════════════════
// Versão SU (Startup) dos catálogos da T5. Mesmas chaves de setor do EE.
// Tendências reescritas para startup · Concorrentes ampliados com campo
// "categoria" (Incumbente · Startup · Global). Moats reusam o catálogo EE.
// Selecionados em ConteudoTarefa5 quando modo === 'SU'.
// ═══════════════════════════════════════════════════════════════════════
const TENDENCIAS_CATALOGO_POR_SETOR_SU = {
  'E-commerce': [
    { id: 'social_commerce_su',      titulo: 'Social e live commerce',            detalhe: 'Venda por redes e transmissões ao vivo' },
    { id: 'ia_recomenda_su',         titulo: 'IA na recomendação e personalização', detalhe: 'Mais conversão e ticket médio' },
    { id: 'same_day_su',             titulo: 'Logística same-day e fulfillment',  detalhe: 'Entrega em horas · operação terceirizada' },
    { id: 'mp_vertical_su',          titulo: 'Marketplaces verticais e nichados', detalhe: 'Plataformas especializadas ganham peso' },
    { id: 'd2c_nativa_su',           titulo: 'Marcas D2C nativas digitais',       detalhe: 'Relação direta · margem superior' },
    { id: 'pagamentos_embarcados_su',titulo: 'Pagamentos embarcados',             detalhe: 'Pix · checkout transparente · BNPL · menos atrito' },
  ],
  'Bar/Restaurante': [
    { id: 'assinatura_gastro_su',    titulo: 'Assinatura e clubes gastronômicos', detalhe: 'Receita recorrente · fidelização' },
    { id: 'dark_kitchen_su',         titulo: 'Dark kitchens e cozinhas compartilhadas', detalhe: 'Operação enxuta · baixo CAPEX inicial' },
    { id: 'ia_cardapio_su',          titulo: 'IA na precificação e no cardápio',  detalhe: 'Engenharia de menu orientada a dados' },
    { id: 'delivery_proprio_su',     titulo: 'Canal de delivery próprio',         detalhe: 'Menos dependência de marketplace · margem' },
    { id: 'plant_based_su',          titulo: 'Plant-based e novos hábitos',       detalhe: 'Cardápios alternativos viram exigência' },
    { id: 'dados_fidelidade_su',     titulo: 'Dados e fidelidade first-party',    detalhe: 'CRM próprio · recompra' },
  ],
  'Saúde': [
    { id: 'telemed_su',              titulo: 'Telemedicina e cuidado híbrido',    detalhe: 'Atendimento remoto + presencial' },
    { id: 'preventiva_assinatura_su',titulo: 'Saúde preventiva por assinatura',   detalhe: 'Pacotes anuais · receita recorrente' },
    { id: 'ia_diag_su',              titulo: 'IA no diagnóstico e triagem',       detalhe: 'Suporte à decisão clínica' },
    { id: 'saude_mental_su',         titulo: 'Saúde mental digital',              detalhe: 'Demanda crescente · escalável' },
    { id: 'wearables_su',            titulo: 'Wearables e monitoramento contínuo',detalhe: 'Dados do paciente em tempo real' },
    { id: 'saude_corp_su',           titulo: 'Saúde corporativa B2B',             detalhe: 'Empresas contratam pacotes preventivos' },
  ],
  'Construção Civil': [
    { id: 'bim_su',                  titulo: 'BIM e gêmeo digital da obra',       detalhe: 'Projeto e execução orientados a dados' },
    { id: 'pre_fabricado_su',        titulo: 'Construção industrializada e modular', detalhe: 'Steel frame · pré-fabricados · velocidade' },
    { id: 'saas_gestao_obra_su',     titulo: 'Plataformas SaaS de gestão de obra',detalhe: 'Canteiro conectado · gestão remota' },
    { id: 'marketplace_insumos_su',  titulo: 'Marketplace de insumos e serviços', detalhe: 'Compra digital · comparação de preço' },
    { id: 'sustent_cert_su',         titulo: 'Construção sustentável certificada',detalhe: 'LEED · AQUA · exigência corporativa' },
    { id: 'iot_canteiro_su',         titulo: 'IoT e automação de canteiro',       detalhe: 'Sensores · produtividade · segurança' },
  ],
  'geral': [
    { id: 'digitalizacao_su',        titulo: 'Digitalização da operação',         detalhe: 'Processos e dados em tempo real' },
    { id: 'ia_operacao_su',          titulo: 'Automação por IA',                  detalhe: 'Atendimento, suporte e processos com IA' },
    { id: 'open_finance_su',         titulo: 'Open finance e pagamentos embarcados', detalhe: 'Novos fluxos de dados e receita' },
    { id: 'economia_compart_su',     titulo: 'Economia compartilhada e assinatura', detalhe: 'Acesso no lugar de posse · recorrência' },
    { id: 'esg_su',                  titulo: 'ESG como requisito',                detalhe: 'Clientes e investidores exigem' },
    { id: 'plataformas_su',          titulo: 'Plataformas e efeito de rede',      detalhe: 'Marketplaces e ecossistemas ganham peso' },
  ],
};

const CONCORRENTES_TIPOS_POR_SETOR_SU = {
  'E-commerce': [
    { id: 'mp_lider_su',        categoria: 'Incumbente', nome: 'Marketplace líder',                 share: '40%',      posicionamento: 'Variedade + marca consolidada' },
    { id: 'big_retail_su',      categoria: 'Incumbente', nome: 'Big retailer com canal online',      share: '10%',      posicionamento: 'Físico + online' },
    { id: 'startup_vertical_su',categoria: 'Startup',    nome: 'Startup vertical de decor/móveis',   share: 'nicho',    posicionamento: 'Curadoria + experiência digital' },
    { id: 'd2c_startup_su',     categoria: 'Startup',    nome: 'Marca D2C nativa digital',           share: 'crescente',posicionamento: 'Linha própria · margem superior' },
    { id: 'global_ecom_su',     categoria: 'Global',     nome: 'Gigante global entrando no BR',      share: 'crescente',posicionamento: 'Preço agressivo + escala' },
  ],
  'Bar/Restaurante': [
    { id: 'rede_consolidada_su',   categoria: 'Incumbente', nome: 'Rede consolidada',                share: 'top regional', posicionamento: 'Padrão consistente · escala' },
    { id: 'autoral_tradicional_su',categoria: 'Incumbente', nome: 'Casa autoral tradicional',        share: 'local',        posicionamento: 'Reputação · clientela fiel' },
    { id: 'foodtech_delivery_su',  categoria: 'Startup',    nome: 'Foodtech / dark kitchen',         share: 'crescente',    posicionamento: 'Operação digital · delivery-first' },
    { id: 'assinatura_startup_su', categoria: 'Startup',    nome: 'Startup de assinatura gastronômica', share: 'nicho',     posicionamento: 'Recorrência · fidelização' },
    { id: 'app_global_su',         categoria: 'Global',     nome: 'Plataforma global de delivery',   share: 'top',          posicionamento: 'Escala · efeito de rede' },
  ],
  'Saúde': [
    { id: 'hospital_ref_su',            categoria: 'Incumbente', nome: 'Hospital / rede de referência',   share: 'top regional', posicionamento: 'Marca · alta complexidade' },
    { id: 'clinica_tradicional_su',     categoria: 'Incumbente', nome: 'Clínica tradicional de bairro',   share: 'local',        posicionamento: 'Vínculo histórico' },
    { id: 'healthtech_telemed_su',      categoria: 'Startup',    nome: 'Healthtech de telemedicina',      share: 'crescente',    posicionamento: 'Tech-first · custo baixo' },
    { id: 'startup_preventiva_su',      categoria: 'Startup',    nome: 'Startup de saúde preventiva',      share: 'nicho',        posicionamento: 'Assinatura · cuidado continuado' },
    { id: 'plataforma_global_saude_su', categoria: 'Global',     nome: 'Plataforma global de saúde',      share: 'crescente',    posicionamento: 'Escala · capital abundante' },
  ],
  'Construção Civil': [
    { id: 'construtora_consolidada_su',      categoria: 'Incumbente', nome: 'Construtora regional consolidada',  share: 'top regional', posicionamento: 'Carteira recorrente' },
    { id: 'nacional_grande_su',              categoria: 'Incumbente', nome: 'Construtora nacional de grande porte', share: '25%',       posicionamento: 'Escala · alta complexidade' },
    { id: 'contech_gestao_su',               categoria: 'Startup',    nome: 'Contech de gestão de obra',         share: 'crescente',    posicionamento: 'SaaS · canteiro conectado' },
    { id: 'marketplace_insumos_startup_su',  categoria: 'Startup',    nome: 'Startup de marketplace de insumos', share: 'nicho',        posicionamento: 'Compra digital · preço' },
    { id: 'player_global_construcao_su',     categoria: 'Global',     nome: 'Player global de construção modular', share: 'crescente',  posicionamento: 'Industrializado · escala' },
  ],
  'geral': [
    { id: 'lider_incumbente_su',        categoria: 'Incumbente', nome: 'Líder incumbente do setor',    share: '30%',      posicionamento: 'Marca · escala · barganha' },
    { id: 'regional_consol_su',         categoria: 'Incumbente', nome: 'Player regional consolidado',  share: '15%',      posicionamento: 'Vínculo local · conhecimento' },
    { id: 'startup_disruptiva_su',      categoria: 'Startup',    nome: 'Startup disruptiva do nicho',  share: 'crescente',posicionamento: 'Modelo novo · tech-first' },
    { id: 'startup_bem_capitalizada_su',categoria: 'Startup',    nome: 'Startup bem capitalizada',     share: 'nicho',    posicionamento: 'Caixa · velocidade de expansão' },
    { id: 'player_global_su',           categoria: 'Global',     nome: 'Player global entrando no BR', share: 'crescente',posicionamento: 'Escala · capital · preço' },
  ],
};

// Rascunho T5-SU pré-preenchido por empresa (IA já preencheu · campo nunca vazio).
// metodo = 'bottom_up' (o que convence investidor) · som_desc enfatiza captura em 5 anos.
const RASCUNHO_TAREFA_5_SU = {
  shopmax: {
    tam_intervalo: 'c', tam_desc: 'E-commerce de móveis e decoração no Brasil',
    sam_intervalo: 'c', sam_desc: 'Curadoria + ticket médio + alcance logístico',
    som_intervalo: 'c', som_desc: 'Meta de captura em 5 anos · alinhada à Meta da T1',
    metodo: 'bottom_up',
    tendencias: [
      { id_catalogo: 'd2c_nativa_su',    titulo: 'Marcas D2C nativas digitais',       detalhe: 'Relação direta · margem superior' },
      { id_catalogo: 'same_day_su',      titulo: 'Logística same-day e fulfillment',  detalhe: 'Entrega em horas · operação terceirizada' },
      { id_catalogo: 'ia_recomenda_su',  titulo: 'IA na recomendação e personalização', detalhe: 'Mais conversão e ticket médio' },
    ],
    players: [
      { id_catalogo: 'mp_lider_su',        categoria: 'Incumbente', nome: 'Marketplace líder',               share: '40%',      posicionamento: 'Variedade + marca consolidada' },
      { id_catalogo: 'startup_vertical_su',categoria: 'Startup',    nome: 'Startup vertical de decor/móveis', share: 'nicho',    posicionamento: 'Curadoria + experiência digital' },
      { id_catalogo: 'global_ecom_su',     categoria: 'Global',     nome: 'Gigante global entrando no BR',    share: 'crescente',posicionamento: 'Preço agressivo + escala' },
    ],
    moats: [
      { id_catalogo: 'curadoria_moat', tipo: 'Curadoria editorial diferenciada', detalhe: 'Filtro de design e qualidade único · difícil de replicar' },
      { id_catalogo: 'cd_moat',        tipo: 'CD próprio (vantagem logística)',  detalhe: 'Prazo de entrega menor que concorrência' },
      { id_catalogo: 'private_moat',   tipo: 'Private label exclusiva',          detalhe: 'Margem superior · não revendida por concorrentes' },
    ],
  },
  sabor_arte: {
    tam_intervalo: 'd', tam_desc: 'Restaurantes em SP capital',
    sam_intervalo: 'c', sam_desc: 'Alta gastronomia · pontos premium · ticket médio R$ 150+',
    som_intervalo: 'c', som_desc: 'Meta de captura em 5 anos · alinhada à Meta da T1',
    metodo: 'bottom_up',
    tendencias: [
      { id_catalogo: 'assinatura_gastro_su', titulo: 'Assinatura e clubes gastronômicos', detalhe: 'Receita recorrente · fidelização' },
      { id_catalogo: 'delivery_proprio_su',  titulo: 'Canal de delivery próprio',         detalhe: 'Menos dependência de marketplace · margem' },
      { id_catalogo: 'dados_fidelidade_su',  titulo: 'Dados e fidelidade first-party',    detalhe: 'CRM próprio · recompra' },
    ],
    players: [
      { id_catalogo: 'rede_consolidada_su',  categoria: 'Incumbente', nome: 'Rede consolidada',        share: 'top regional', posicionamento: 'Padrão consistente · escala' },
      { id_catalogo: 'foodtech_delivery_su', categoria: 'Startup',    nome: 'Foodtech / dark kitchen', share: 'crescente',    posicionamento: 'Operação digital · delivery-first' },
      { id_catalogo: 'app_global_su',        categoria: 'Global',     nome: 'Plataforma global de delivery', share: 'top',    posicionamento: 'Escala · efeito de rede' },
    ],
    moats: [
      { id_catalogo: 'chef_moat',        tipo: 'Chef-proprietário com reputação', detalhe: 'Marca pessoal · clientela vinculada ao chef' },
      { id_catalogo: 'cardapio_autoral', tipo: 'Carta única e autoral',           detalhe: 'Receitas exclusivas · técnica diferenciada' },
      { id_catalogo: 'ponto_moat',       tipo: 'Ponto / localização premium',     detalhe: 'Endereço estabelecido · fluxo natural difícil de copiar' },
    ],
  },
  clinicavida: {
    tam_intervalo: 'e', tam_desc: 'Saúde privada Brasil (consultas + exames + procedimentos)',
    sam_intervalo: 'd', sam_desc: 'Clínicas multidisciplinares em SP capital',
    som_intervalo: 'c', som_desc: 'Meta de captura em 5 anos · alinhada à Meta da T1',
    metodo: 'bottom_up',
    tendencias: [
      { id_catalogo: 'preventiva_assinatura_su', titulo: 'Saúde preventiva por assinatura', detalhe: 'Pacotes anuais · receita recorrente' },
      { id_catalogo: 'telemed_su',               titulo: 'Telemedicina e cuidado híbrido',  detalhe: 'Atendimento remoto + presencial' },
      { id_catalogo: 'saude_mental_su',          titulo: 'Saúde mental digital',            detalhe: 'Demanda crescente · escalável' },
    ],
    players: [
      { id_catalogo: 'hospital_ref_su',       categoria: 'Incumbente', nome: 'Hospital / rede de referência', share: 'top regional', posicionamento: 'Marca · alta complexidade' },
      { id_catalogo: 'healthtech_telemed_su', categoria: 'Startup',    nome: 'Healthtech de telemedicina',    share: 'crescente',    posicionamento: 'Tech-first · custo baixo' },
      { id_catalogo: 'startup_preventiva_su', categoria: 'Startup',    nome: 'Startup de saúde preventiva',    share: 'nicho',        posicionamento: 'Assinatura · cuidado continuado' },
    ],
    moats: [
      { id_catalogo: 'corpo_clinico',    tipo: 'Corpo clínico multidisciplinar',           detalhe: 'Coordenação entre especialidades · prontuário único' },
      { id_catalogo: 'tecnologia_saude', tipo: 'Tecnologia (prontuário + telemed)',        detalhe: 'Integração presencial + remoto · experiência diferenciada' },
      { id_catalogo: 'credenciamento',   tipo: 'Credenciamento com convênios principais',  detalhe: 'Atendimento direto · base ampla de pacientes elegíveis' },
    ],
  },
  construmax: {
    tam_intervalo: 'd', tam_desc: 'Construção corporativa Brasil (obras comerciais e adaptações)',
    sam_intervalo: 'c', sam_desc: 'SP/RMC · médio porte · clientes corporativos',
    som_intervalo: 'c', som_desc: 'Meta de captura em 5 anos · alinhada à Meta da T1',
    metodo: 'bottom_up',
    tendencias: [
      { id_catalogo: 'bim_su',              titulo: 'BIM e gêmeo digital da obra',       detalhe: 'Projeto e execução orientados a dados' },
      { id_catalogo: 'pre_fabricado_su',    titulo: 'Construção industrializada e modular', detalhe: 'Steel frame · pré-fabricados · velocidade' },
      { id_catalogo: 'saas_gestao_obra_su', titulo: 'Plataformas SaaS de gestão de obra',detalhe: 'Canteiro conectado · gestão remota' },
    ],
    players: [
      { id_catalogo: 'construtora_consolidada_su', categoria: 'Incumbente', nome: 'Construtora regional consolidada',  share: 'top regional', posicionamento: 'Carteira recorrente' },
      { id_catalogo: 'contech_gestao_su',          categoria: 'Startup',    nome: 'Contech de gestão de obra',         share: 'crescente',    posicionamento: 'SaaS · canteiro conectado' },
      { id_catalogo: 'nacional_grande_su',         categoria: 'Incumbente', nome: 'Construtora nacional de grande porte', share: '25%',      posicionamento: 'Escala · alta complexidade' },
    ],
    moats: [
      { id_catalogo: 'crea_responsavel', tipo: 'Responsável técnico CREA ativo', detalhe: 'Engenheiro presente em todas as obras · diferencial reconhecido' },
      { id_catalogo: 'loja_propria',     tipo: 'Loja própria de materiais',      detalhe: 'Custo de insumos reduzido · vantagem competitiva direta' },
      { id_catalogo: 'carteira_corp',    tipo: 'Carteira de clientes corporativos',detalhe: 'Recorrência B2B · contratos continuados' },
    ],
  },
};

// ═══════════════════════════════════════════════════════════════════════
// <ConteudoTarefa5 /> — Mercado & Concorrência (fusão T5+T6).
// 2 blocos: Mercado (TAM/SAM/SOM + Tendências) · Concorrência (Players + Moats).
// ═══════════════════════════════════════════════════════════════════════
const ConteudoTarefa5 = ({ mercadoConcorrencia, setMercadoConcorrencia, empresa, modo = 'EE' }) => {
  const rascunho = (modo === 'SU' ? RASCUNHO_TAREFA_5_SU : RASCUNHO_TAREFA_5)[empresa?.id] || {
    tam_intervalo: null, tam_desc: '', sam_intervalo: null, sam_desc: '',
    som_intervalo: null, som_desc: '', tendencias: [], players: [], moats: [], metodo: 'bottom_up',
  };
  const metodo = mercadoConcorrencia?.metodo ?? rascunho.metodo ?? 'bottom_up';
  const tam_intervalo = mercadoConcorrencia?.tam_intervalo ?? rascunho.tam_intervalo;
  const tam_desc      = mercadoConcorrencia?.tam_desc      ?? rascunho.tam_desc;
  const sam_intervalo = mercadoConcorrencia?.sam_intervalo ?? rascunho.sam_intervalo;
  const sam_desc      = mercadoConcorrencia?.sam_desc      ?? rascunho.sam_desc;
  const som_intervalo = mercadoConcorrencia?.som_intervalo ?? rascunho.som_intervalo;
  const som_desc      = mercadoConcorrencia?.som_desc      ?? rascunho.som_desc;
  const tendencias  = mercadoConcorrencia?.tendencias  ?? rascunho.tendencias;
  const players     = mercadoConcorrencia?.players     ?? rascunho.players;
  const moats       = mercadoConcorrencia?.moats       ?? rascunho.moats;
  const [tamSamSomAberto, setTamSamSomAberto] = useState(null);  // 'tam'|'sam'|'som'|null
  const [graficosT5Aberto, setGraficosT5Aberto] = useState(false);

  const atualizar = (campo, valor) => {
    setMercadoConcorrencia(prev => ({
      tam_intervalo, tam_desc, sam_intervalo, sam_desc, som_intervalo, som_desc,
      tendencias, players, moats, metodo,
      ...prev,
      [campo]: valor,
    }));
  };
  const editarLista = (campo, lista, i, k, v) => {
    const nova = lista.map((m, idx) => idx === i ? { ...m, [k]: v } : m);
    atualizar(campo, nova);
  };
  const removerLista = (campo, lista, i) => atualizar(campo, lista.filter((_, idx) => idx !== i));
  // Catálogos por setor e helpers de toggle de chips (Tendências · Concorrentes · Moats)
  const setorEmp = empresa?.setor || 'geral';
  const fonteTend = modo === 'SU' ? TENDENCIAS_CATALOGO_POR_SETOR_SU : TENDENCIAS_CATALOGO_POR_SETOR;
  const fonteConc = modo === 'SU' ? CONCORRENTES_TIPOS_POR_SETOR_SU : CONCORRENTES_TIPOS_POR_SETOR;
  const catalogoTend = fonteTend[setorEmp] || fonteTend['geral'];
  const catalogoConc = fonteConc[setorEmp] || fonteConc['geral'];
  const catalogoMoat = MOATS_CATALOGO_POR_SETOR[setorEmp]      || MOATS_CATALOGO_POR_SETOR['geral'];
  const chipTendMarcado = (catId) => tendencias.some(t => t.id_catalogo === catId);
  const chipConcMarcado = (catId) => players.some(p => p.id_catalogo === catId);
  const chipMoatMarcado = (catId) => moats.some(m => m.id_catalogo === catId);
  const toggleChipTend = (catItem) => {
    if (chipTendMarcado(catItem.id)) atualizar('tendencias', tendencias.filter(t => t.id_catalogo !== catItem.id));
    else atualizar('tendencias', [...tendencias, { id_catalogo: catItem.id, titulo: catItem.titulo, detalhe: catItem.detalhe }]);
  };
  const toggleChipConc = (catItem) => {
    if (chipConcMarcado(catItem.id)) atualizar('players', players.filter(p => p.id_catalogo !== catItem.id));
    else atualizar('players', [...players, { id_catalogo: catItem.id, categoria: catItem.categoria, nome: catItem.nome, share: catItem.share, posicionamento: catItem.posicionamento }]);
  };
  const toggleChipMoat = (catItem) => {
    if (chipMoatMarcado(catItem.id)) atualizar('moats', moats.filter(m => m.id_catalogo !== catItem.id));
    else atualizar('moats', [...moats, { id_catalogo: catItem.id, tipo: catItem.tipo, detalhe: catItem.detalhe }]);
  };

  const inputBase = {
    width: '100%', padding: '8px 12px', fontSize: 13,
    border: `1px solid ${G.borda}`, borderRadius: 0,
    fontFamily: "'Outfit', sans-serif", color: G.txt1,
    background: '#FFF', boxSizing: 'border-box',
  };
  const btnAdd = {
    background: '#FFF', border: `1px dashed ${G.verde}`,
    color: G.verde, padding: '8px 14px',
    fontSize: 11, fontWeight: 700, letterSpacing: '0.06em',
    textTransform: 'uppercase', borderRadius: 0, cursor: 'pointer',
    fontFamily: "'Outfit', sans-serif", marginTop: 8,
  };
  const btnRemover = {
    background: 'transparent', border: 'none', color: G.txt2,
    fontSize: 16, cursor: 'pointer', padding: '2px 6px',
    fontFamily: "'Outfit', sans-serif",
  };
  const HEADER_BLOCO = {
    fontSize: 16, fontWeight: 700, letterSpacing: '0.10em',
    textTransform: 'uppercase', color: G.laranja, marginBottom: 10,
  };
  const HEADER_AUX = {
    fontSize: 12, fontWeight: 400, color: G.txt2,
    textTransform: 'none', letterSpacing: 0, marginLeft: 10,
  };
  const SUB_HEADER = {
    fontSize: 12, fontWeight: 700, color: G.txt2,
    textTransform: 'uppercase', letterSpacing: '0.06em',
    marginBottom: 6,
  };
  const fmtR$ = v => v == null || v === ''
    ? '—'
    : (v >= 1000
        ? `R$ ${(v / 1000).toLocaleString('pt-BR', { maximumFractionDigits: 1 })} bi`
        : `R$ ${Number(v).toLocaleString('pt-BR')} mi`);

  // Categoria do concorrente (SU) · cor por tipo
  const CATEGORIAS_SU = ['Incumbente', 'Startup', 'Global'];
  const corCategoria = (cat) => cat === 'Startup' ? G.laranja : cat === 'Global' ? '#2563EB' : '#6B7280';
  const BadgeCategoria = ({ cat }) => cat ? (
    <span style={{
      display: 'inline-block', padding: '1px 7px', fontSize: 9, fontWeight: 800,
      letterSpacing: '0.04em', textTransform: 'uppercase', borderRadius: 999,
      color: '#FFF', background: corCategoria(cat), whiteSpace: 'nowrap',
      fontFamily: "'Outfit', sans-serif",
    }}>{cat}</span>
  ) : null;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
      {/* 2 quadros lado a lado: Definição + Instruções */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <div style={{ padding: '12px 16px', background: G.verdeLight,
          borderLeft: `4px solid ${G.verde}`, borderRadius: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 800, color: G.verde,
            letterSpacing: '0.06em', textTransform: 'uppercase',
            marginBottom: 6 }}>
            O que é Mercado & Concorrência
          </div>
          <div style={{ fontSize: 12, lineHeight: 1.45, color: G.txt1 }}>
            Dimensiona a <strong>oportunidade</strong> (TAM/SAM/SOM + tendências) e
            mapeia os <strong>concorrentes</strong> com vantagens defensáveis (moats).
            Posiciona a empresa no contexto setorial.
          </div>
        </div>
        <div style={{ padding: '12px 16px', background: G.laranjaLight,
          borderLeft: `4px solid ${G.laranja}`, borderRadius: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 800, color: G.laranja,
            letterSpacing: '0.06em', textTransform: 'uppercase',
            marginBottom: 6 }}>
            O que você faz aqui
          </div>
          <div style={{ fontSize: 12, lineHeight: 1.45, color: G.txt1 }}>
            <strong>Confirme os valores</strong> TAM/SAM/SOM · revise tendências,
            concorrentes e vantagens competitivas sugeridas · adicione próprios ·
            clique Aprovar para enviar ao BP.
          </div>
        </div>
      </div>

      {/* BLOCO 1 — Mercado */}
      <div>
        <div style={HEADER_BLOCO}>
          Bloco 1 · Mercado
          <span style={HEADER_AUX}>· TAM · SAM · SOM + tendências do setor</span>
        </div>

        {/* Método de Estimativa (SU) · Top-down × Bottom-up */}
        {modo === 'SU' && (
          <div style={{ background: '#FFF', border: `1px solid ${G.borda}`,
            borderRadius: 0, padding: '10px 14px', marginBottom: 12 }}>
            <div style={SUB_HEADER}>Método de Estimativa</div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {[
                { id: 'bottom_up', label: 'Bottom-up', detalhe: 'parte do cliente e do ticket · o que convence investidor' },
                { id: 'top_down',  label: 'Top-down',  detalhe: 'parte do mercado total e recorta a fatia' },
              ].map(m => {
                const ativo = metodo === m.id;
                return (
                  <label key={m.id} style={{ flex: 1, minWidth: 220, display: 'flex', alignItems: 'flex-start',
                    gap: 8, padding: '8px 12px', cursor: 'pointer',
                    background: ativo ? G.verdeLight : '#FFF',
                    border: ativo ? `2px solid ${G.verde}` : `1px solid ${G.borda}`,
                    borderRadius: 0, fontFamily: "'Outfit', sans-serif" }}>
                    <input type="radio" name="metodo_estimativa" checked={ativo}
                      onChange={() => atualizar('metodo', m.id)}
                      style={{ appearance: 'none', WebkitAppearance: 'none', width: 14, height: 14, marginTop: 2,
                        flexShrink: 0, border: `2px solid ${ativo ? G.verde : G.borda}`, borderRadius: 0,
                        background: ativo ? G.verde : '#FFF', cursor: 'pointer' }}/>
                    <div>
                      <div style={{ fontSize: 12, fontWeight: 800, color: ativo ? G.verde : G.txt1 }}>
                        {m.label}{m.id === 'bottom_up' ? ' ★' : ''}
                      </div>
                      <div style={{ fontSize: 11, color: G.txt2, lineHeight: 1.35 }}>{m.detalhe}</div>
                    </div>
                  </label>
                );
              })}
            </div>
          </div>
        )}

        {/* Quadro didático compacto TAM/SAM/SOM */}
        <div style={{ background: G.verdeLight, padding: '10px 14px',
          borderRadius: 0, marginBottom: 12,
          display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
          {[
            { sigla: 'TAM', nome: 'Total Addressable Market', detalhe: 'mercado total do setor' },
            { sigla: 'SAM', nome: 'Serviceable Available Market', detalhe: 'parcela acessível para empresa' },
            { sigla: 'SOM', nome: 'Serviceable Obtainable Market', detalhe: modo === 'SU' ? 'meta de captura em 5 anos · alinhada à Meta da T1' : 'parcela conquistável em 5 anos' },
          ].map(d => (
            <div key={d.sigla}>
              <div style={{ fontSize: 14, fontWeight: 900, color: G.verde,
                letterSpacing: '0.04em' }}>
                {d.sigla}
              </div>
              <div style={{ fontSize: 10, fontWeight: 700, color: G.txt2,
                letterSpacing: '0.06em', textTransform: 'uppercase' }}>
                {d.nome}
              </div>
              <div style={{ fontSize: 11, color: G.txt2, lineHeight: 1.4 }}>
                {d.detalhe}
              </div>
            </div>
          ))}
        </div>

        {/* 3 cards de valores TAM/SAM/SOM */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10,
          marginBottom: 14 }}>
          {[
            { sigla: 'TAM', intervalo: tam_intervalo, desc: tam_desc, opcoes: INTERVALOS_TAM, campoI: 'tam_intervalo', campoD: 'tam_desc' },
            { sigla: 'SAM', intervalo: sam_intervalo, desc: sam_desc, opcoes: INTERVALOS_SAM, campoI: 'sam_intervalo', campoD: 'sam_desc' },
            { sigla: 'SOM', intervalo: som_intervalo, desc: som_desc, opcoes: INTERVALOS_SOM, campoI: 'som_intervalo', campoD: 'som_desc' },
          ].map(item => {
            const opt = item.opcoes.find(o => o.id === item.intervalo);
            const aberto = tamSamSomAberto === item.sigla;
            return (
              <div key={item.sigla} style={{
                background: '#FFF',
                border: `1px solid ${aberto ? G.verde : G.borda}`,
                borderRadius: 0, overflow: 'hidden',
              }}>
                <div style={{
                  background: aberto ? G.verdeLight : 'transparent',
                  padding: '10px 12px',
                  display: 'flex', alignItems: 'center', gap: 10,
                }}>
                  <div style={{
                    fontSize: 13, fontWeight: 800, color: G.verde,
                    letterSpacing: '0.08em', textTransform: 'uppercase',
                  }}>
                    {item.sigla}
                  </div>
                  <div style={{ flex: 1, fontSize: 12, color: G.txt1,
                    lineHeight: 1.3 }}>
                    {opt
                      ? <><strong>{opt.label}</strong> <span style={{ color: G.txt2, fontSize: 11 }}>· sugerido pelo <MarcaAIdvisor /></span></>
                      : <span style={{ color: G.txt2 }}>marque o intervalo</span>}
                  </div>
                  <button onClick={() => setTamSamSomAberto(aberto ? null : item.sigla)}
                    style={{
                      flexShrink: 0,
                      background: aberto ? '#FFF' : G.laranjaLight,
                      border: `1px solid ${G.laranja}`,
                      color: G.laranja,
                      padding: '3px 8px', fontSize: 9, fontWeight: 800,
                      letterSpacing: '0.08em', textTransform: 'uppercase',
                      borderRadius: 0, cursor: 'pointer',
                      fontFamily: "'Outfit', sans-serif",
                      whiteSpace: 'nowrap',
                    }}>
                    {aberto ? '▴ Clique para fechar' : (opt ? <><IconeAlerta /> Discordo · ajustar</> : '▾ Escolher')}
                  </button>
                </div>
                {aberto && (
                  <div style={{ padding: '4px 12px 10px',
                    display: 'flex', flexDirection: 'column', gap: 4 }}>
                    {item.opcoes.map(o => {
                      const ativo = item.intervalo === o.id;
                      return (
                        <label key={o.id}
                          style={{ display: 'flex', alignItems: 'flex-start', gap: 8,
                            padding: '6px 10px', cursor: 'pointer',
                            background: ativo ? G.verdeLight : '#FFF',
                            border: ativo ? `2px solid ${G.verde}` : `1px solid ${G.borda}`,
                            borderRadius: 0,
                            fontFamily: "'Outfit', sans-serif",
                          }}>
                          <input type="radio" name={`${item.sigla}_intervalo`}
                            checked={ativo}
                            onChange={() => { atualizar(item.campoI, o.id); setTamSamSomAberto(null); }}
                            style={{
                              appearance: 'none', WebkitAppearance: 'none',
                              width: 13, height: 13, marginTop: 3, flexShrink: 0,
                              border: `2px solid ${ativo ? G.verde : G.borda}`,
                              borderRadius: 0,
                              background: ativo ? G.verde : '#FFF',
                              cursor: 'pointer',
                            }}/>
                          <div>
                            <div style={{ fontSize: 12, fontWeight: 700,
                              color: ativo ? G.verde : G.txt1 }}>
                              {o.label}
                            </div>
                            <div style={{ fontSize: 10, color: G.txt2, lineHeight: 1.35 }}>
                              {o.desc}
                            </div>
                          </div>
                        </label>
                      );
                    })}
                  </div>
                )}
                <div style={{ padding: '0 12px 10px' }}>
                  <input value={item.desc} onChange={e => atualizar(item.campoD, e.target.value)}
                    placeholder="Descrição curta"
                    style={{ ...inputBase, fontSize: 11, padding: '6px 10px' }}/>
                </div>
              </div>
            );
          })}
        </div>

        {/* Tendências (chips IA · lista marcada · extras) */}
        <div>
          <div style={SUB_HEADER}>Tendências de Mercado</div>
          <div style={{ background: G.fundo, border: `1px solid ${G.borda}`,
            borderRadius: 0, padding: '10px 14px', marginBottom: 10 }}>
            <div style={{ fontSize: 10, fontWeight: 800, color: G.txt2,
              letterSpacing: '0.10em', textTransform: 'uppercase', marginBottom: 8 }}>
              Sugestões da IA · marque as que se aplicam ao seu mercado
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {catalogoTend.map(c => {
                const ativo = chipTendMarcado(c.id);
                return (
                  <button key={c.id} type="button"
                    onClick={() => toggleChipTend(c)}
                    title={c.detalhe}
                    style={{
                      padding: '5px 11px', fontSize: 12, fontWeight: 600,
                      background: ativo ? G.verde : '#FFF',
                      color: ativo ? '#FFF' : G.txt1,
                      border: `1px solid ${ativo ? G.verde : G.borda}`,
                      borderRadius: 999, cursor: 'pointer',
                      fontFamily: "'Outfit', sans-serif",
                    }}>
                    {ativo ? '✓ ' : ''}{c.titulo}
                  </button>
                );
              })}
            </div>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {tendencias.map((t, i) => {
              const doCatalogo = !!t.id_catalogo;
              return (
                <div key={i} style={{
                  padding: '10px 14px', background: '#FFF',
                  border: `1px solid ${G.borda}`, borderRadius: 0,
                  display: 'grid', gridTemplateColumns: '1fr 2fr auto', gap: 10, alignItems: 'start',
                }}>
                  {doCatalogo ? (
                    <>
                      <div style={{ padding: '7px 10px', fontSize: 13, fontWeight: 700,
                        color: G.txt1, lineHeight: 1.3 }}>
                        {t.titulo}
                      </div>
                      <div style={{ padding: '7px 10px', fontSize: 12, color: G.txt2,
                        fontStyle: 'italic', lineHeight: 1.4 }}>
                        {t.detalhe}
                      </div>
                    </>
                  ) : (
                    <>
                      <input value={t.titulo} onChange={e => editarLista('tendencias', tendencias, i, 'titulo', e.target.value)}
                        placeholder="Tendência própria" style={{ ...inputBase, fontWeight: 700 }}/>
                      <input value={t.detalhe || ''} onChange={e => editarLista('tendencias', tendencias, i, 'detalhe', e.target.value)}
                        placeholder="Detalhe / impacto (opcional)" style={inputBase}/>
                    </>
                  )}
                  <button onClick={() => removerLista('tendencias', tendencias, i)} title="Remover" style={btnRemover}><IconeLixeira /></button>
                </div>
              );
            })}
          </div>
          <button onClick={() => atualizar('tendencias', [...tendencias, { id_catalogo: null, titulo: '', detalhe: '' }])} style={btnAdd}>
            + Adicionar tendência própria
          </button>
        </div>

        {/* Botão de visualização TAM/SAM/SOM */}
        <BotaoVisualizarGraficos
          aberto={graficosT5Aberto}
          onToggle={() => setGraficosT5Aberto(v => !v)}
          rotulo="Visualizar mercado endereçável">
          <div>
            <div style={{ fontSize: 10, fontWeight: 800, color: G.txt2,
              letterSpacing: '0.10em', textTransform: 'uppercase', marginBottom: 8 }}>
              Ilustração · {TITULO_EXHIBIT_POR_TIPO['t5_tam_sam_som']}
            </div>
            <GraficoTAMSAMSOM
              tamId={tam_intervalo}
              samId={sam_intervalo}
              somId={som_intervalo}/>
          </div>
        </BotaoVisualizarGraficos>
      </div>

      {/* BLOCO 2 — Concorrência */}
      <div>
        <div style={HEADER_BLOCO}>
          Bloco 2 · Concorrência
          <span style={HEADER_AUX}>· concorrentes principais + vantagens competitivas defensáveis</span>
        </div>

        {/* Concorrentes (chips IA = arquétipos · linhas editáveis para nome real) */}
        <div style={{ marginBottom: 14 }}>
          <div style={SUB_HEADER}>Concorrentes Principais</div>
          <div style={{ background: G.fundo, border: `1px solid ${G.borda}`,
            borderRadius: 0, padding: '10px 14px', marginBottom: 10 }}>
            <div style={{ fontSize: 10, fontWeight: 800, color: G.txt2,
              letterSpacing: '0.10em', textTransform: 'uppercase', marginBottom: 8 }}>
              Arquétipos de concorrente · marque os que existem no seu mercado
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {catalogoConc.map(c => {
                const ativo = chipConcMarcado(c.id);
                return (
                  <button key={c.id} type="button"
                    onClick={() => toggleChipConc(c)}
                    title={c.posicionamento}
                    style={{
                      padding: '5px 11px', fontSize: 12, fontWeight: 600,
                      background: ativo ? G.verde : '#FFF',
                      color: ativo ? '#FFF' : G.txt1,
                      border: `1px solid ${ativo ? G.verde : G.borda}`,
                      borderRadius: 999, cursor: 'pointer',
                      fontFamily: "'Outfit', sans-serif",
                    }}>
                    {ativo ? '✓ ' : ''}{c.nome}
                  </button>
                );
              })}
            </div>
            <div style={{ marginTop: 6, fontSize: 10, color: G.txt2,
              fontStyle: 'italic', lineHeight: 1.3 }}>
              Sugestões da IA são arquétipos · ajuste o nome real do concorrente na linha abaixo.
            </div>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {players.map((p, i) => {
              const doCatalogo = !!p.id_catalogo;
              return (
                <div key={i} style={{
                  padding: '10px 14px', background: '#FFF',
                  border: `1px solid ${G.borda}`, borderRadius: 0,
                  display: 'grid',
                  gridTemplateColumns: modo === 'SU' ? '1.2fr 110px 100px 1.9fr auto' : '1.2fr 120px 2fr auto',
                  gap: 10, alignItems: 'start',
                }}>
                  {doCatalogo ? (
                    <>
                      <div style={{ padding: '7px 10px', fontSize: 13, fontWeight: 700,
                        color: G.txt1, lineHeight: 1.3 }}>
                        {p.nome}
                      </div>
                      {modo === 'SU' && (
                        <div style={{ padding: '8px 4px' }}>
                          <BadgeCategoria cat={p.categoria} />
                        </div>
                      )}
                      <div style={{ padding: '7px 10px', fontSize: 12, color: G.txt2,
                        textAlign: 'center', fontStyle: 'italic' }}>
                        {p.share || '—'}
                      </div>
                      <div style={{ padding: '7px 10px', fontSize: 12, color: G.txt2,
                        fontStyle: 'italic', lineHeight: 1.4 }}>
                        {p.posicionamento}
                      </div>
                    </>
                  ) : (
                    <>
                      <input value={p.nome} onChange={e => editarLista('players', players, i, 'nome', e.target.value)}
                        placeholder="Nome do concorrente próprio" style={{ ...inputBase, fontWeight: 700 }}/>
                      {modo === 'SU' && (
                        <select value={p.categoria || 'Startup'}
                          onChange={e => editarLista('players', players, i, 'categoria', e.target.value)}
                          style={{ ...inputBase, padding: '8px 6px' }}>
                          {CATEGORIAS_SU.map(c => <option key={c} value={c}>{c}</option>)}
                        </select>
                      )}
                      <input value={p.share || ''} onChange={e => editarLista('players', players, i, 'share', e.target.value)}
                        placeholder="Share / porte" style={{ ...inputBase, textAlign: 'center' }}/>
                      <input value={p.posicionamento || ''} onChange={e => editarLista('players', players, i, 'posicionamento', e.target.value)}
                        placeholder="Posicionamento competitivo" style={inputBase}/>
                    </>
                  )}
                  <button onClick={() => removerLista('players', players, i)} title="Remover" style={btnRemover}><IconeLixeira /></button>
                </div>
              );
            })}
          </div>
          <button onClick={() => atualizar('players', [...players, { id_catalogo: null, categoria: modo === 'SU' ? 'Startup' : undefined, nome: '', share: '', posicionamento: '' }])} style={btnAdd}>
            + Adicionar concorrente próprio
          </button>
        </div>

        {/* Moats (chips IA · lista marcada · extras) */}
        <div>
          <div style={SUB_HEADER}>Vantagens Competitivas Defensáveis</div>
          <div style={{ background: G.fundo, border: `1px solid ${G.borda}`,
            borderRadius: 0, padding: '10px 14px', marginBottom: 10 }}>
            <div style={{ fontSize: 10, fontWeight: 800, color: G.txt2,
              letterSpacing: '0.10em', textTransform: 'uppercase', marginBottom: 8 }}>
              Sugestões da IA · marque as vantagens que a empresa tem
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {catalogoMoat.map(c => {
                const ativo = chipMoatMarcado(c.id);
                return (
                  <button key={c.id} type="button"
                    onClick={() => toggleChipMoat(c)}
                    title={c.detalhe}
                    style={{
                      padding: '5px 11px', fontSize: 12, fontWeight: 600,
                      background: ativo ? G.verde : '#FFF',
                      color: ativo ? '#FFF' : G.txt1,
                      border: `1px solid ${ativo ? G.verde : G.borda}`,
                      borderRadius: 999, cursor: 'pointer',
                      fontFamily: "'Outfit', sans-serif",
                    }}>
                    {ativo ? '✓ ' : ''}{c.tipo}
                  </button>
                );
              })}
            </div>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {moats.map((m, i) => {
              const doCatalogo = !!m.id_catalogo;
              return (
                <div key={i} style={{
                  padding: '10px 14px', background: '#FFF',
                  border: `1px solid ${G.borda}`, borderRadius: 0,
                  display: 'grid', gridTemplateColumns: '1fr 2fr auto', gap: 10, alignItems: 'start',
                }}>
                  {doCatalogo ? (
                    <>
                      <div style={{ padding: '7px 10px', fontSize: 13, fontWeight: 700,
                        color: G.txt1, lineHeight: 1.3 }}>
                        {m.tipo}
                      </div>
                      <div style={{ padding: '7px 10px', fontSize: 12, color: G.txt2,
                        fontStyle: 'italic', lineHeight: 1.4 }}>
                        {m.detalhe}
                      </div>
                    </>
                  ) : (
                    <>
                      <input value={m.tipo} onChange={e => editarLista('moats', moats, i, 'tipo', e.target.value)}
                        placeholder="Tipo de vantagem própria" style={{ ...inputBase, fontWeight: 700 }}/>
                      <input value={m.detalhe || ''} onChange={e => editarLista('moats', moats, i, 'detalhe', e.target.value)}
                        placeholder="Detalhe · por que é defensável" style={inputBase}/>
                    </>
                  )}
                  <button onClick={() => removerLista('moats', moats, i)} title="Remover" style={btnRemover}><IconeLixeira /></button>
                </div>
              );
            })}
          </div>
          <button onClick={() => atualizar('moats', [...moats, { id_catalogo: null, tipo: '', detalhe: '' }])} style={btnAdd}>
            + Adicionar vantagem própria
          </button>
        </div>
      </div>
    </div>
  );
};


// ═══════════════════════════════════════════════════════════════════════
// Tarefa 6 — SWOT (matriz 2×2 · checkbox múltiplo · IA propõe · empresário valida)
// Cada item: { texto, marcado, fonte }. State inicial pega rascunho heurístico
// com todos marcados; empresário desmarca o que não se aplica e adiciona próprios.
// ═══════════════════════════════════════════════════════════════════════
const RASCUNHO_TAREFA_6 = {
  shopmax: {
    forcas: [
      'Curadoria editorial · cada item filtrado por equipe de design',
      'Centro de Distribuição próprio reduz prazo de entrega em 40%',
      'Linha private label com margem 8 p.p. acima da média do setor',
      'Atendimento humanizado · NPS 25 p.p. acima da média',
      'Base de clientes recorrente · 35% das vendas vêm de retorno',
      'Operação enxuta · equipe pequena com alta produtividade',
    ],
    fraquezas: [
      'Dependência de marketplaces como canal de aquisição',
      'Escala menor que líderes (Magalu, MadeiraMadeira)',
      'Marca ainda concentrada no Sudeste',
      'Capital limitado para investir em mídia paga em larga escala',
      'Ticket médio em produtos commodity pressionado por marketplaces',
    ],
    oportunidades: [
      'Modelo D2C ganha tração · margens 8-15 p.p. maiores',
      'Home office consolidado · demanda recorrente por mobiliário',
      'Novas categorias adjacentes (iluminação técnica, têxteis premium)',
      'Brand collabs com designers locais · marca editorial',
      'BNDES e fintechs financiando logística reversa e CDs',
    ],
    ameacas: [
      'Amazon Brasil expandindo categoria casa e decoração',
      'Magalu pressionando preço com escala e crédito próprio',
      'Frete e última milha cada vez mais caros',
      'ICMS estadual e regras de e-commerce mudando',
      'Inflação de matéria-prima (madeira, metais) comprime margens',
    ],
  },
  sabor_arte: {
    forcas: [
      'Chef premiado · formação Le Cordon Bleu · marca pessoal forte',
      'Cardápio sazonal atualizado 4×/ano com ingredientes locais',
      'Carta de vinhos curada por sommelier WSET certificado',
      'Localização premium nos Jardins · alto fluxo do público-alvo',
      'Ambiente premiado por design · referência em guias',
      'Equipe estável · baixa rotatividade na cozinha e salão',
    ],
    fraquezas: [
      'Alto custo fixo (ponto premium, equipe especializada)',
      'Dependência do fluxo presencial em horário de pico',
      'Margem operacional apertada · típica de alta gastronomia',
      'Escala limitada · não dá para crescer sem novo ponto',
      'Marca muito ligada à figura do chef (risco de pessoa-chave)',
    ],
    oportunidades: [
      'Turismo gastronômico em São Paulo crescendo',
      'Delivery premium consolidado · receita complementar sem canibalizar salão',
      'Eventos privados crescem · margem alta e previsibilidade',
      'Parcerias com hotéis e roteiros gastronômicos',
      'Linha de produtos gourmet para retail seletivo',
    ],
    ameacas: [
      'Aluguel pode subir significativamente na renovação',
      'Concorrência nova abre todo mês na região',
      'Inflação de ingredientes (especialmente importados)',
      'Mudança de hábito · cliente troca salão por delivery',
      'Regulamentação alimentar mais rígida · custos de compliance',
    ],
  },
  clinicavida: {
    forcas: [
      'Equipe multidisciplinar coordenada sob mesmo prontuário',
      'Telemedicina integrada · mesmo médico presencial e remoto',
      'Convênios ativos com principais operadoras',
      '7 especialidades médicas no mesmo espaço',
      'Programas preventivos próprios com renovação anual de 80%',
      'Localização em Moema · público-alvo concentrado',
    ],
    fraquezas: [
      'Marca conhecida apenas no bairro e adjacências',
      'Dependência alta de convênios (~70% da receita)',
      'Espaço físico limitado · 8 consultórios em uso pleno',
      'Base de pacientes concentrada num único bairro',
      'Custo médico crescente pressiona margem',
    ],
    oportunidades: [
      'Medicina preventiva ganha tração · pacientes pagam por prevenção',
      'Saúde mental sai do estigma · demanda explosiva',
      'Telemedicina amplia base sem novo espaço físico',
      'Pacotes corporativos de bem-estar para empresas próximas',
      'Parcerias com farmácias e academias para programas integrados',
    ],
    ameacas: [
      'Dasa, Fleury e Einstein expandindo no segmento médio',
      'Convênios pressionando preço por consulta e procedimento',
      'Novas redes franqueadas (Amparo, Care+) chegando',
      'Regulamentação CFM mudando regras de telemedicina',
      'Custo de equipamento médico em alta',
    ],
  },
  construmax: {
    forcas: [
      'Engenheiro responsável presente em todas as obras (CREA ativo)',
      'Loja própria de materiais reduz custo de insumos em 15%',
      'Capacidade simultânea de até 5 obras paralelas',
      'Histórico de 95% de entregas no prazo contratual',
      'Equipe técnica fixa e qualificada',
      'Carteira de referências corporativas sólida',
    ],
    fraquezas: [
      'Capacidade limitada · obras acima de R$ 50 mi ficam fora do escopo',
      'Dependência de 3-4 clientes corporativos top da carteira',
      'Baixa adoção interna de BIM e ferramentas digitais',
      'Marca regional · sem presença fora de SP/Campinas',
      'Investimento em P&D historicamente baixo (0,5%)',
    ],
    oportunidades: [
      'BIM e automação de obras viram exigência de grandes clientes',
      'ESG vira critério de seleção de construtoras',
      'Contratos turn-key crescem · cliente quer um único responsável',
      'Retrofit corporativo em alta com volta aos escritórios',
      'Materiais sustentáveis e certificações verdes valorizados',
    ],
    ameacas: [
      'Grandes integradoras descendo para o segmento médio',
      'Pequenas empreiteiras canibalizando preço sem CREA ativo',
      'Inflação de materiais comprimindo margens',
      'Mudanças trabalhistas elevando custo de pessoal',
      'Escassez de mão-de-obra qualificada nas obras',
    ],
  },
};

// Rascunho SWOT-SU · mesma forma do EE (arrays de string) · vocabulário de
// startup (runway · early adopters · product-market fit · incumbente · downround).
// Selecionado em ConteudoTarefa6 quando modo === 'SU'.
const RASCUNHO_TAREFA_6_SU = {
  shopmax: {
    forcas: [
      'Founders com domínio do problema · vividos no varejo de decoração',
      'Curadoria + tecnologia proprietária de recomendação',
      'Velocidade de iteração · ciclos curtos de produto',
      'Base inicial de early adopters engajada (35% de recompra)',
      'Cultura enxuta e orientada a dados',
    ],
    fraquezas: [
      'Caixa limitado · runway curto para mídia paga em escala',
      'Brand recognition baixo fora do Sudeste',
      'Operação logística ainda sendo construída',
      'Dependência de poucos founders (pessoa-chave)',
      'Sem track record de escala nacional',
    ],
    oportunidades: [
      'Janela do modelo D2C nativo digital em abertura',
      'Pagamentos embarcados (Pix · BNPL) reduzem atrito',
      'Categorias adjacentes prontas (iluminação · têxteis premium)',
      'Big techs e marketplaces comprando players verticais',
      'Home office consolidado · demanda recorrente',
    ],
    ameacas: [
      'Incumbente (marketplace líder) reage e copia a curadoria',
      'Entrada de gigante global com preço agressivo',
      'Runway acabar antes do product-market fit em novas categorias',
      'Talento de tecnologia disputado por big techs',
      'Queda no apetite de investidor (downround) trava captação',
    ],
  },
  sabor_arte: {
    forcas: [
      'Chef-founder com marca pessoal e domínio do produto',
      'Cardápio autoral proprietário · difícil de copiar',
      'Velocidade de experimentação no menu (dados de recompra)',
      'Base de early adopters fiéis · alto NPS',
      'Localização premium com fluxo do público-alvo',
    ],
    fraquezas: [
      'Caixa limitado · alto custo fixo aperta o runway',
      'Operação ainda dependente do salão presencial',
      'Marca ligada à pessoa do chef (pessoa-chave)',
      'Sem operação digital / delivery próprio consolidada',
      'Escala limitada · difícil crescer sem novo ponto',
    ],
    oportunidades: [
      'Janela da assinatura gastronômica (receita recorrente) em abertura',
      'Delivery premium próprio · nova receita sem canibalizar salão',
      'Dark kitchen para expandir com baixo CAPEX',
      'Dados first-party e CRM para recompra',
      'Turismo gastronômico em São Paulo crescendo',
    ],
    ameacas: [
      'Rede consolidada reage e replica o conceito',
      'Plataforma global de delivery captura a margem',
      'Runway acabar antes de validar o modelo de recorrência',
      'Inflação de ingredientes comprime margem apertada',
      'Concorrência nova abrindo todo mês na região',
    ],
  },
  clinicavida: {
    forcas: [
      'Equipe multidisciplinar com domínio clínico do problema',
      'Telemedicina + prontuário integrados (tecnologia própria)',
      'Programa preventivo proprietário · renovação anual de 80%',
      'Base de pacientes recorrentes (early adopters)',
      'Cultura de cuidado orientada a dados',
    ],
    fraquezas: [
      'Caixa limitado · runway curto para expandir tecnologia',
      'Brand recognition restrito ao bairro',
      'Dependência alta de convênios (~70% da receita)',
      'Operação de escala ainda sendo construída',
      'Espaço físico limitado (8 consultórios em uso pleno)',
    ],
    oportunidades: [
      'Janela da saúde preventiva por assinatura em abertura',
      'Telemedicina amplia base sem novo espaço físico',
      'Saúde mental digital · demanda explosiva e escalável',
      'Pacotes corporativos B2B para empresas próximas',
      'Big players de saúde comprando clínicas tech-first',
    ],
    ameacas: [
      'Incumbente (Dasa · Fleury · Einstein) reage no segmento médio',
      'Entrada de plataforma global de saúde bem capitalizada',
      'Runway acabar antes do product-market fit da assinatura',
      'Convênios pressionando preço por consulta',
      'Regulação CFM mudando regras de telemedicina',
    ],
  },
  construmax: {
    forcas: [
      'Founders engenheiros com domínio técnico do problema (CREA ativo)',
      'Loja própria de materiais · vantagem de custo estrutural',
      'Histórico de 95% de entregas no prazo (track record inicial)',
      'Equipe técnica fixa e qualificada',
      'Carteira de referências corporativas como early adopters',
    ],
    fraquezas: [
      'Caixa limitado · runway curto para investir em tecnologia',
      'Baixa adoção interna de BIM e ferramentas digitais',
      'Dependência de 3-4 clientes corporativos (concentração)',
      'Marca regional · sem presença fora de SP/Campinas',
      'Operação de escala ainda sendo construída',
    ],
    oportunidades: [
      'Janela do SaaS de gestão de obra (canteiro conectado) em abertura',
      'BIM e gêmeo digital viram exigência de grandes clientes',
      'Construção modular reduz prazo e CAPEX',
      'Marketplace de insumos como nova frente digital',
      'ESG e certificações verdes viram critério de seleção',
    ],
    ameacas: [
      'Incumbente (integradora nacional) desce ao segmento médio',
      'Entrada de player global de construção industrializada',
      'Runway acabar antes de validar o produto digital',
      'Talento de tecnologia disputado por big techs',
      'Inflação de materiais comprime margens',
    ],
  },
};

const ConteudoTarefa6 = ({ swot, setSwot, empresa, modo = 'EE' }) => {
  const rascunho = (modo === 'SU' ? RASCUNHO_TAREFA_6_SU : RASCUNHO_TAREFA_6)[empresa?.id] || {
    forcas: [], fraquezas: [], oportunidades: [], ameacas: [],
  };
  const obter = (quad) => {
    if (swot?.[quad]) return swot[quad];
    // IA pré-marca os candidatos · empresário desmarca o que não se aplica
    return rascunho[quad].map(texto => ({ texto, marcado: true, fonte: 'ia' }));
  };
  const forcas        = obter('forcas');
  const fraquezas     = obter('fraquezas');
  const oportunidades = obter('oportunidades');
  const ameacas       = obter('ameacas');
  const [graficosT6Aberto, setGraficosT6Aberto] = useState(false);

  const atualizar = (campo, valor) => {
    setSwot(prev => ({
      forcas, fraquezas, oportunidades, ameacas,
      ...prev,
      [campo]: valor,
    }));
  };
  const toggleItem = (quad, lista, i) => {
    const nova = lista.map((m, idx) => idx === i ? { ...m, marcado: !m.marcado } : m);
    atualizar(quad, nova);
  };
  const removerItem = (quad, lista, i) => atualizar(quad, lista.filter((_, idx) => idx !== i));
  const editarTexto = (quad, lista, i, v) => {
    const nova = lista.map((m, idx) => idx === i ? { ...m, texto: v } : m);
    atualizar(quad, nova);
  };
  const adicionarItem = (quad, lista) => {
    atualizar(quad, [...lista, { texto: '', marcado: true, fonte: 'usuario' }]);
  };

  const inputBase = {
    width: '100%', padding: '6px 10px', fontSize: 13,
    border: `1px solid ${G.borda}`, borderRadius: 0,
    fontFamily: "'Outfit', sans-serif", color: G.txt1,
    background: '#FFF', boxSizing: 'border-box',
  };
  const btnRemover = {
    background: 'transparent', border: 'none', color: G.txt2,
    fontSize: 14, cursor: 'pointer', padding: '2px 6px',
  };

  const cores = {
    forcas:        { bg: G.verdeLight,    fg: G.verde,       nome: 'Forças',         sub: 'Internas · positivas' },
    fraquezas:     { bg: '#FEF2F2',       fg: G.vermelho,    nome: 'Fraquezas',      sub: 'Internas · negativas' },
    oportunidades: { bg: G.laranjaLight,  fg: G.laranja,     nome: 'Oportunidades',  sub: 'Externas · positivas' },
    ameacas:       { bg: '#FFFBEB',       fg: '#B45309',     nome: 'Ameaças',        sub: 'Externas · negativas' },
  };

  const Quadrante = ({ quad, lista, sigla }) => {
    const c = cores[quad];
    return (
      <div style={{
        background: c.bg, borderRadius: 0, padding: '10px 12px',
        border: `1px solid ${c.fg}33`,
      }}>
        <div style={{ marginBottom: 8, display: 'flex',
          alignItems: 'baseline', gap: 8 }}>
          <span style={{ fontSize: 18, fontWeight: 900, color: c.fg,
            letterSpacing: '-0.02em', lineHeight: 1 }}>
            {sigla}
          </span>
          <span style={{ fontSize: 13, fontWeight: 800, color: c.fg,
            letterSpacing: '-0.01em', lineHeight: 1 }}>
            {c.nome}
          </span>
          <span style={{ fontSize: 10, color: G.txt2, lineHeight: 1,
            marginLeft: 'auto' }}>
            {c.sub}
          </span>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {lista.map((item, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'flex-start',
              gap: 7, padding: '2px 0' }}>
              <input type="checkbox"
                checked={item.marcado}
                onChange={() => toggleItem(quad, lista, i)}
                style={{
                  appearance: 'none', WebkitAppearance: 'none',
                  width: 13, height: 13, marginTop: 3, flexShrink: 0,
                  border: `2px solid ${item.marcado ? c.fg : G.borda}`,
                  borderRadius: 0,
                  background: item.marcado ? c.fg : '#FFF',
                  cursor: 'pointer',
                }}/>
              {item.fonte === 'usuario' ? (
                <input value={item.texto}
                  onChange={e => editarTexto(quad, lista, i, e.target.value)}
                  placeholder="Descreva o item"
                  style={inputBase}/>
              ) : (
                <div style={{ flex: 1, fontSize: 12, lineHeight: 1.4,
                  color: item.marcado ? G.txt1 : G.txt2,
                  fontWeight: item.marcado ? 500 : 400,
                  cursor: 'pointer',
                }}
                  onClick={() => toggleItem(quad, lista, i)}>
                  {item.texto}
                </div>
              )}
              {item.fonte === 'usuario' && (
                <button onClick={() => removerItem(quad, lista, i)} title="Remover" style={btnRemover}><IconeLixeira /></button>
              )}
            </div>
          ))}
        </div>

        <button onClick={() => adicionarItem(quad, lista)}
          style={{ background: 'transparent', border: `1px dashed ${c.fg}`,
            color: c.fg, padding: '4px 10px', fontSize: 9, fontWeight: 700,
            letterSpacing: '0.06em', textTransform: 'uppercase',
            borderRadius: 0, cursor: 'pointer', marginTop: 6,
            fontFamily: "'Outfit', sans-serif" }}>
          + Adicionar item próprio
        </button>
      </div>
    );
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {/* 2 quadros lado a lado: Definição + Instruções */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        {/* Quadro esquerda · Definição */}
        <div style={{ padding: '12px 16px', background: G.verdeLight,
          borderLeft: `4px solid ${G.verde}`, borderRadius: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 800, color: G.verde,
            letterSpacing: '0.06em', textTransform: 'uppercase',
            marginBottom: 6 }}>
            O que é SWOT
          </div>
          <div style={{ fontSize: 12, lineHeight: 1.45, color: G.txt1 }}>
            Matriz clássica que cruza visão <strong>interna</strong> (forças e fraquezas)
            com <strong>externa</strong> (oportunidades e ameaças). Síntese estratégica
            do posicionamento da empresa.
          </div>
        </div>
        {/* Quadro direita · Instruções */}
        <div style={{ padding: '12px 16px', background: G.laranjaLight,
          borderLeft: `4px solid ${G.laranja}`, borderRadius: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 800, color: G.laranja,
            letterSpacing: '0.06em', textTransform: 'uppercase',
            marginBottom: 6 }}>
            O que você faz aqui
          </div>
          <div style={{ fontSize: 12, lineHeight: 1.45, color: G.txt1 }}>
            O <MarcaAIdvisor /> propôs candidatos para cada quadrante.
            <strong> Marque os itens que se aplicam</strong> à sua empresa · use
            {' '}<strong>+ Adicionar item próprio</strong> para incluir seus · clique
            Aprovar para enviar ao Business Plan.
          </div>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
        <Quadrante quad="forcas"        lista={forcas}        sigla="S"/>
        <Quadrante quad="fraquezas"     lista={fraquezas}     sigla="W"/>
        <Quadrante quad="oportunidades" lista={oportunidades} sigla="O"/>
        <Quadrante quad="ameacas"       lista={ameacas}       sigla="T"/>
      </div>

      {/* Botão de visualização da matriz SWOT pronta */}
      <BotaoVisualizarGraficos
        aberto={graficosT6Aberto}
        onToggle={() => setGraficosT6Aberto(v => !v)}
        rotulo="Visualizar matriz SWOT">
        <div>
          <div style={{ fontSize: 10, fontWeight: 800, color: G.txt2,
            letterSpacing: '0.10em', textTransform: 'uppercase', marginBottom: 8 }}>
            Ilustração · {TITULO_EXHIBIT_POR_TIPO['t6_swot']}
          </div>
          <GraficoSWOT
            forcas={forcas}
            fraquezas={fraquezas}
            oportunidades={oportunidades}
            ameacas={ameacas}/>
        </div>
      </BotaoVisualizarGraficos>
    </div>
  );
};


// ═══════════════════════════════════════════════════════════════════════
// Tarefa 7 — Estratégia & Modelo de Negócio
// 3 blocos: Estratégia (checkbox múltiplo) · Modelo de Negócio (3 textos)
// · Unit Economics (CAC, LTV, Margem).
// ═══════════════════════════════════════════════════════════════════════
const CANDIDATOS_ESTRATEGIA = [
  { id: 'lideranca_custo',  label: 'Liderança em Custo',       detalhe: 'preço mais baixo do segmento · escala e eficiência operacional' },
  { id: 'diferenciacao',    label: 'Diferenciação',            detalhe: 'produto/serviço único · qualidade superior · marca premium' },
  { id: 'foco_nicho',       label: 'Foco em Nicho',            detalhe: 'segmento específico de mercado · atendimento especializado' },
  { id: 'escala',           label: 'Escala',                   detalhe: 'crescer rápido em volume · ganho de mercado' },
  { id: 'inovacao',         label: 'Velocidade de Inovação',   detalhe: 'lançar produtos novos antes da concorrência · ciclo curto' },
  { id: 'relacionamento',   label: 'Relacionamento Próximo',   detalhe: 'atendimento humano · fidelização · NPS alto' },
];

// Catálogos de alternativas para o Bloco 2 da T7 (Modelo de Negócio)
// 3 campos · cada um com 4 opções por setor + opções gerais
// IA pré-marca a alternativa mais coerente com o perfil
const PROPOSTAS_VALOR_POR_SETOR = {
  'E-commerce': [
    { id: 'curadoria',       label: 'Curadoria editorial com design diferenciado e entrega rápida' },
    { id: 'variedade',       label: 'Ampla variedade de produtos com preço competitivo' },
    { id: 'private_label',   label: 'Linha exclusiva (private label) com margem superior' },
    { id: 'experiencia',     label: 'Experiência de compra premium e personalizada' },
  ],
  'Bar/Restaurante': [
    { id: 'autoral',         label: 'Cozinha autoral e ambiente premiado' },
    { id: 'volume',          label: 'Casa de alto volume com preço acessível' },
    { id: 'origem',          label: 'Ingredientes locais e narrativa de origem' },
    { id: 'eventos',         label: 'Casa especializada em eventos corporativos e privativos' },
  ],
  'Saúde': [
    { id: 'multidisciplinar',label: 'Atendimento multidisciplinar integrado com tempo de resposta rápido' },
    { id: 'tecnologia',      label: 'Tecnologia clínica de ponta combinada a telemedicina' },
    { id: 'rede_convenios',  label: 'Rede credenciada com convênios principais e ampla cobertura' },
    { id: 'prevencao',       label: 'Foco em prevenção e cuidado continuado da saúde' },
  ],
  'Construção Civil': [
    { id: 'engenharia',      label: 'Engenharia própria com responsabilidade técnica e prazo garantido' },
    { id: 'loja_materiais',  label: 'Suprimento próprio via loja de materiais integrada' },
    { id: 'corporativas',    label: 'Especialização em obras corporativas e institucionais' },
    { id: 'sustentavel',     label: 'Construção sustentável com certificações de mercado' },
  ],
  'geral': [
    { id: 'qualidade',       label: 'Qualidade superior reconhecida no segmento' },
    { id: 'preco',           label: 'Preço competitivo com boa relação custo-benefício' },
    { id: 'atendimento',     label: 'Atendimento premium e personalizado' },
    { id: 'nicho',           label: 'Especialização em nicho de mercado' },
  ],
};

const MODELOS_RECEITA_POR_SETOR = {
  'E-commerce': [
    { id: 'transacao',       label: 'Receita por transação direta no e-commerce · ticket médio em foco' },
    { id: 'marketplace',     label: 'Comissão sobre vendas de terceiros (modelo marketplace)' },
    { id: 'assinatura',      label: 'Receita recorrente (assinatura mensal/anual)' },
    { id: 'private_label_r', label: 'Mix de revenda + linha própria (private label · margem superior)' },
  ],
  'Bar/Restaurante': [
    { id: 'consumo_salao',   label: 'Consumo no salão (almoço/jantar) · ticket médio por pessoa' },
    { id: 'delivery',        label: 'Salão + delivery próprio ou plataforma' },
    { id: 'eventos_buffet',  label: 'Eventos corporativos e privativos (buffet fechado)' },
    { id: 'misto',           label: 'Mix de salão + delivery + eventos' },
  ],
  'Saúde': [
    { id: 'consultas',       label: 'Consultas e procedimentos avulsos (particular + convênio)' },
    { id: 'planos_assinatura',label: 'Plano de assinatura preventiva mensal/anual' },
    { id: 'pacotes_corp',    label: 'Pacotes corporativos para empresas' },
    { id: 'telemedicina_r',  label: 'Telemedicina como linha de receita adicional escalável' },
  ],
  'Construção Civil': [
    { id: 'obras_unitarias', label: 'Obras unitárias por contrato (custo + margem)' },
    { id: 'incorporacao',    label: 'Incorporação imobiliária (venda de unidades)' },
    { id: 'loja_varejo',     label: 'Varejo de materiais (loja própria)' },
    { id: 'mix_obra_loja',   label: 'Mix integrado de obras + loja de materiais' },
  ],
  'geral': [
    { id: 'transacao_g',     label: 'Transação direta · pagamento à vista por produto/serviço' },
    { id: 'recorrente',      label: 'Receita recorrente · assinatura ou contrato continuado' },
    { id: 'projeto',         label: 'Receita por projeto · contratos de execução' },
    { id: 'misto_g',         label: 'Modelo misto · combinação de fontes' },
  ],
};

const SEGMENTOS_POR_SETOR = {
  'E-commerce': [
    { id: 'familias_b_c',    label: 'Famílias de 30-50 anos · classes B e C+ · região Sudeste' },
    { id: 'jovens_a_b',      label: 'Jovens de 20-35 anos · classes A e B · grandes capitais' },
    { id: 'empresas_b2b',    label: 'Pequenas e médias empresas (B2B)' },
    { id: 'amplo_nacional',  label: 'Público amplo · cobertura nacional · classes B/C/D' },
  ],
  'Bar/Restaurante': [
    { id: 'gourmet_local',   label: 'Público gourmet local · classes A e B+ do bairro/cidade' },
    { id: 'corporativo',     label: 'Público corporativo · executivos e empresas para eventos' },
    { id: 'familias',        label: 'Famílias e grupos de amigos · faixa etária mista' },
    { id: 'turistico',       label: 'Público turístico e turismo gastronômico' },
  ],
  'Saúde': [
    { id: 'particulares',    label: 'Pacientes particulares · classes A e B+ região metropolitana' },
    { id: 'convenios',       label: 'Pacientes via convênio · cobertura ampla por planos' },
    { id: 'corporativo_s',   label: 'Empresas (saúde corporativa e ocupacional)' },
    { id: 'idosos',          label: 'Pacientes idosos · cuidado continuado e geriatria' },
  ],
  'Construção Civil': [
    { id: 'corporativo_c',   label: 'Clientes corporativos · empresas e instituições' },
    { id: 'incorporadoras',  label: 'Incorporadoras e construtoras parceiras' },
    { id: 'reformas',        label: 'Pessoas físicas · reformas residenciais e comerciais' },
    { id: 'misto_c',         label: 'Público misto · obras corporativas + varejo de materiais' },
  ],
  'geral': [
    { id: 'consumidor_b2c',  label: 'Consumidor final (B2C) · pessoas físicas' },
    { id: 'empresas_b2b_g',  label: 'Empresas (B2B) · pessoas jurídicas' },
    { id: 'governo',         label: 'Setor público · órgãos governamentais' },
    { id: 'misto_b2x',       label: 'Misto · atende B2C e B2B' },
  ],
};

// Perguntas de Unit Economics em forma de intervalos (chips multiple-choice)
// · empresário escolhe a faixa que mais se aproxima · IA usa o ponto médio
// pra calcular CAC, LTV e LTV/CAC automaticamente.
const PERGUNTAS_UNIT_ECON = [
  { id: 'novos_clientes_mes',
    label: 'Quantos novos clientes você ganha por mês?',
    opcoes: [
      { id: 'a', label: 'Até 10',          meio: 5 },
      { id: 'b', label: '10 a 50',         meio: 30 },
      { id: 'c', label: '50 a 200',        meio: 125 },
      { id: 'd', label: '200 a 1.000',     meio: 600 },
      { id: 'e', label: 'Mais de 1.000',   meio: 2000 },
    ],
  },
  { id: 'gasto_marketing_mes',
    label: 'Quanto gasta por mês com marketing e vendas?',
    opcoes: [
      { id: 'a', label: 'Até R$ 500',         meio: 250 },
      { id: 'b', label: 'R$ 500 a 2.000',     meio: 1250 },
      { id: 'c', label: 'R$ 2.000 a 10.000',  meio: 6000 },
      { id: 'd', label: 'R$ 10.000 a 50.000', meio: 30000 },
      { id: 'e', label: 'Mais de R$ 50.000',  meio: 100000 },
    ],
  },
  { id: 'ticket_medio',
    label: 'Qual o ticket médio de cada venda?',
    opcoes: [
      { id: 'a', label: 'Até R$ 50',          meio: 25 },
      { id: 'b', label: 'R$ 50 a 300',        meio: 175 },
      { id: 'c', label: 'R$ 300 a 1.000',     meio: 650 },
      { id: 'd', label: 'R$ 1.000 a 10.000',  meio: 5500 },
      { id: 'e', label: 'Mais de R$ 10.000',  meio: 30000 },
    ],
  },
  { id: 'vida_util_meses',
    label: 'Por quanto tempo um cliente típico continua comprando?',
    opcoes: [
      { id: 'a', label: '1 a 3 meses',     meio: 2 },
      { id: 'b', label: '3 a 12 meses',    meio: 7 },
      { id: 'c', label: '1 a 3 anos',      meio: 24 },
      { id: 'd', label: '3 a 5 anos',      meio: 48 },
      { id: 'e', label: 'Mais de 5 anos',  meio: 84 },
    ],
  },
  { id: 'margem_media',
    label: 'Qual a margem média por venda? (% de lucro sobre o preço)',
    opcoes: [
      { id: 'a', label: '0 a 10%',     meio: 5 },
      { id: 'b', label: '10 a 25%',    meio: 17.5 },
      { id: 'c', label: '25 a 50%',    meio: 37.5 },
      { id: 'd', label: '50 a 75%',    meio: 62.5 },
      { id: 'e', label: 'Mais de 75%', meio: 85 },
    ],
  },
];

const RASCUNHO_TAREFA_7 = {
  shopmax: {
    estrategias: ['diferenciacao', 'foco_nicho'],
    proposta_valor_id: 'curadoria',
    modelo_receita_id: 'private_label_r',
    segmentos_id:     'familias_b_c',
    unit_econ_respostas: {
      novos_clientes_mes: 'c', gasto_marketing_mes: 'c',
      ticket_medio: 'b', vida_util_meses: 'b', margem_media: 'b',
    },
  },
  sabor_arte: {
    estrategias: ['diferenciacao'],
    proposta_valor_id: 'autoral',
    modelo_receita_id: 'misto',
    segmentos_id:     'corporativo',
    unit_econ_respostas: {
      novos_clientes_mes: 'b', gasto_marketing_mes: 'b',
      ticket_medio: 'b', vida_util_meses: 'c', margem_media: 'c',
    },
  },
  clinicavida: {
    estrategias: ['relacionamento', 'diferenciacao'],
    proposta_valor_id: 'multidisciplinar',
    modelo_receita_id: 'consultas',
    segmentos_id:     'convenios',
    unit_econ_respostas: {
      novos_clientes_mes: 'b', gasto_marketing_mes: 'b',
      ticket_medio: 'a', vida_util_meses: 'c', margem_media: 'c',
    },
  },
  construmax: {
    estrategias: ['relacionamento', 'escala'],
    proposta_valor_id: 'corporativas',
    modelo_receita_id: 'mix_obra_loja',
    segmentos_id:     'corporativo_c',
    unit_econ_respostas: {
      novos_clientes_mes: 'a', gasto_marketing_mes: 'b',
      ticket_medio: 'e', vida_util_meses: 'b', margem_media: 'b',
    },
  },
};

// ═══════════════════════════════════════════════════════════════════════
// Versão SU (Startup) dos catálogos da T7. Selecionados em ConteudoTarefa7
// quando modo === 'SU'. Estrutura dos 3 blocos mantida.
// Bloco 1: estratégias expandidas (6 EE + 6 SU). Bloco 2: 3 catálogos por
// setor reescritos. Bloco 3: 2 perguntas extras (Churn · Payback) + benchmark.
// ═══════════════════════════════════════════════════════════════════════
const CANDIDATOS_ESTRATEGIA_SU = [
  ...CANDIDATOS_ESTRATEGIA,
  { id: 'disrupcao',   label: 'Disrupção do incumbente',      detalhe: 'atacar o líder por baixo com modelo novo' },
  { id: 'plataforma',  label: 'Plataforma / Marketplace',     detalhe: 'conectar dois lados · efeito de rede' },
  { id: 'oceano_azul', label: 'Estratégia oceano azul',       detalhe: 'criar espaço de mercado sem concorrência direta' },
  { id: 'land_expand', label: 'Land & expand',                detalhe: 'entrar pequeno e crescer dentro do cliente' },
  { id: 'plg',         label: 'Crescimento via produto (PLG)',detalhe: 'o próprio produto puxa a aquisição' },
  { id: 'b2b2c',       label: 'B2B2C',                        detalhe: 'vender via empresas que levam ao consumidor final' },
];

const PROPOSTAS_VALOR_POR_SETOR_SU = {
  'E-commerce': [
    { id: 'curadoria_ia',     label: 'Curadoria com recomendação por IA e entrega rápida' },
    { id: 'd2c_nativa',       label: 'Marca D2C nativa digital · relação direta' },
    { id: 'self_serve_ec',    label: 'Checkout self-serve sem atrito' },
    { id: 'personalizacao_ec',label: 'Personalização da vitrine em escala' },
  ],
  'Bar/Restaurante': [
    { id: 'tech_gastro',       label: 'Experiência gastronômica habilitada por tecnologia' },
    { id: 'self_serve_bar',    label: 'Pedido e fidelidade self-serve pelo app' },
    { id: 'sob_demanda_bar',   label: 'Sob demanda · delivery e assinatura' },
    { id: 'personalizacao_bar',label: 'Cardápio personalizado por dados de consumo' },
  ],
  'Saúde': [
    { id: 'tech_saude',        label: 'Cuidado habilitado por tecnologia (telemedicina)' },
    { id: 'self_serve_saude',  label: 'Agendamento e acompanhamento self-serve' },
    { id: 'sob_demanda_saude', label: 'Atendimento sob demanda · quando o paciente precisa' },
    { id: 'preventiva_escala', label: 'Prevenção personalizada em escala' },
  ],
  'Construção Civil': [
    { id: 'tech_obra',       label: 'Gestão de obra habilitada por tecnologia (SaaS)' },
    { id: 'self_serve_obra', label: 'Orçamento e acompanhamento self-serve' },
    { id: 'sob_demanda_obra',label: 'Serviços e insumos sob demanda' },
    { id: 'modular_escala',  label: 'Construção modular padronizada em escala' },
  ],
  'geral': [
    { id: 'tech_habilitado', label: 'Serviço habilitado por tecnologia' },
    { id: 'self_serve',      label: 'Self-serve · o cliente se atende sozinho' },
    { id: 'sob_demanda',     label: 'Sob demanda · acesso quando precisa' },
    { id: 'personalizacao',  label: 'Personalização em escala' },
  ],
};

const MODELOS_RECEITA_POR_SETOR_SU = {
  'E-commerce': [
    { id: 'marketplace_take', label: 'Take-rate de marketplace (comissão)' },
    { id: 'assinatura_ec',    label: 'Assinatura / clube recorrente' },
    { id: 'd2c_venda',        label: 'Venda direta D2C · margem própria' },
    { id: 'freemium_ec',      label: 'Freemium + upsell' },
  ],
  'Bar/Restaurante': [
    { id: 'assinatura_gastro',label: 'Assinatura / clube gastronômico recorrente' },
    { id: 'take_delivery',    label: 'Take-rate de delivery (comissão)' },
    { id: 'venda_direta_bar', label: 'Venda direta no salão e delivery próprio' },
    { id: 'eventos_bar',      label: 'Eventos e experiências sob demanda' },
  ],
  'Saúde': [
    { id: 'assinatura_saude', label: 'Assinatura preventiva recorrente' },
    { id: 'pay_per_use_saude',label: 'Pagamento por uso (consultas e exames)' },
    { id: 'b2b_corp_saude',   label: 'Contratos corporativos B2B' },
    { id: 'telemed_escala',   label: 'Telemedicina como receita escalável' },
  ],
  'Construção Civil': [
    { id: 'saas_obra',     label: 'Assinatura de SaaS de gestão de obra' },
    { id: 'take_insumos',  label: 'Take-rate de marketplace de insumos' },
    { id: 'projeto_obra',  label: 'Receita por projeto (custo + margem)' },
    { id: 'modular_venda', label: 'Venda de solução modular padronizada' },
  ],
  'geral': [
    { id: 'assinatura_saas', label: 'Assinatura recorrente (SaaS)' },
    { id: 'take_rate',       label: 'Take-rate de marketplace (comissão)' },
    { id: 'freemium',        label: 'Freemium · gratuito + plano pago' },
    { id: 'pay_per_use',     label: 'Pagamento por uso' },
  ],
};

const SEGMENTOS_POR_SETOR_SU = {
  'E-commerce': [
    { id: 'early_decor',           label: 'Early adopters de decoração · engajados' },
    { id: 'recorrentes_ec',        label: 'Compradores recorrentes (power users)' },
    { id: 'pmes_b2b_ec',           label: 'PMEs (B2B) · revenda e escritórios' },
    { id: 'consumidor_digital_ec', label: 'Consumidor digital · Sudeste' },
  ],
  'Bar/Restaurante': [
    { id: 'early_gastro',           label: 'Early adopters gastronômicos · formadores de opinião' },
    { id: 'clientes_fieis_bar',     label: 'Clientes fiéis (power users) · recompra' },
    { id: 'corporativo_bar',        label: 'Empresas (B2B) · eventos e benefícios' },
    { id: 'consumidor_digital_bar', label: 'Consumidor digital · delivery e assinatura' },
  ],
  'Saúde': [
    { id: 'early_saude',              label: 'Early adopters de saúde digital' },
    { id: 'pacientes_recorrentes',    label: 'Pacientes recorrentes (power users)' },
    { id: 'corporativo_saude',        label: 'Empresas (B2B) · saúde corporativa' },
    { id: 'consumidor_digital_saude', label: 'Consumidor digital · telemedicina' },
  ],
  'Construção Civil': [
    { id: 'early_contech',      label: 'Early adopters de tecnologia de obra' },
    { id: 'construtoras_power', label: 'Construtoras que usam intensamente (power users)' },
    { id: 'corporativo_obra',   label: 'Clientes corporativos (B2B)' },
    { id: 'profissionais_obra', label: 'Profissionais e pequenas obras · digital' },
  ],
  'geral': [
    { id: 'early_adopters', label: 'Early adopters · primeiros usuários engajados' },
    { id: 'power_users',    label: 'Power users · uso intenso · referência' },
    { id: 'b2b_startup',    label: 'Empresas (B2B) · times pequenos e médios' },
    { id: 'b2c_digital',    label: 'Consumidor digital (B2C)' },
  ],
};

// Perguntas extras SU no Bloco 3 (não entram no cálculo CAC/LTV · trazem
// benchmark de retenção inline).
const PERGUNTAS_SU_EXTRA = [
  { id: 'churn_mensal',
    label: 'Qual o churn mensal esperado? (% de clientes que saem por mês)',
    referencia: 'Referência: SaaS B2B saudável ≤ 2%/mês · B2C ≤ 5%/mês',
    bomAte: 5,
    opcoes: [
      { id: 'a', label: 'Até 2%',        meio: 1.5 },
      { id: 'b', label: '2% a 5%',       meio: 3.5 },
      { id: 'c', label: '5% a 10%',      meio: 7.5 },
      { id: 'd', label: 'Mais de 10%',   meio: 15 },
    ] },
  { id: 'payback_meses',
    label: 'Em quantos meses o cliente paga o custo de aquisição (CAC)?',
    referencia: 'Referência: abaixo de 12 meses é saudável',
    bomAte: 12,
    opcoes: [
      { id: 'a', label: 'Até 6 meses',     meio: 4 },
      { id: 'b', label: '6 a 12 meses',    meio: 9 },
      { id: 'c', label: '12 a 24 meses',   meio: 18 },
      { id: 'd', label: 'Mais de 24 meses',meio: 30 },
    ] },
];

const RASCUNHO_TAREFA_7_SU = {
  shopmax: {
    estrategias: ['diferenciacao', 'plg'],
    proposta_valor_id: 'curadoria_ia',
    modelo_receita_id: 'd2c_venda',
    segmentos_id:     'early_decor',
    unit_econ_respostas: {
      novos_clientes_mes: 'c', gasto_marketing_mes: 'c',
      ticket_medio: 'b', vida_util_meses: 'b', margem_media: 'b',
      churn_mensal: 'b', payback_meses: 'b',
    },
  },
  sabor_arte: {
    estrategias: ['diferenciacao', 'oceano_azul'],
    proposta_valor_id: 'tech_gastro',
    modelo_receita_id: 'assinatura_gastro',
    segmentos_id:     'early_gastro',
    unit_econ_respostas: {
      novos_clientes_mes: 'b', gasto_marketing_mes: 'b',
      ticket_medio: 'b', vida_util_meses: 'c', margem_media: 'c',
      churn_mensal: 'b', payback_meses: 'b',
    },
  },
  clinicavida: {
    estrategias: ['diferenciacao', 'land_expand'],
    proposta_valor_id: 'tech_saude',
    modelo_receita_id: 'assinatura_saude',
    segmentos_id:     'early_saude',
    unit_econ_respostas: {
      novos_clientes_mes: 'b', gasto_marketing_mes: 'b',
      ticket_medio: 'a', vida_util_meses: 'c', margem_media: 'c',
      churn_mensal: 'a', payback_meses: 'b',
    },
  },
  construmax: {
    estrategias: ['relacionamento', 'land_expand'],
    proposta_valor_id: 'tech_obra',
    modelo_receita_id: 'saas_obra',
    segmentos_id:     'early_contech',
    unit_econ_respostas: {
      novos_clientes_mes: 'a', gasto_marketing_mes: 'b',
      ticket_medio: 'e', vida_util_meses: 'b', margem_media: 'b',
      churn_mensal: 'a', payback_meses: 'c',
    },
  },
};

const ConteudoTarefa7 = ({ estrategiaModelo, setEstrategiaModelo, empresa, modo = 'EE' }) => {
  const rascunho = (modo === 'SU' ? RASCUNHO_TAREFA_7_SU : RASCUNHO_TAREFA_7)[empresa?.id] || {
    estrategias: [], proposta_valor_id: null, modelo_receita_id: null, segmentos_id: null,
    unit_econ_respostas: {},
  };
  const obter = (campo) => estrategiaModelo?.[campo] ?? rascunho[campo];
  const estrategias          = obter('estrategias');
  const proposta_valor_id    = obter('proposta_valor_id');
  const modelo_receita_id    = obter('modelo_receita_id');
  const segmentos_id         = obter('segmentos_id');
  const unit_econ_respostas  = obter('unit_econ_respostas') || {};
  const setorEmp             = empresa?.setor || 'geral';
  const fonteEstrategia      = modo === 'SU' ? CANDIDATOS_ESTRATEGIA_SU : CANDIDATOS_ESTRATEGIA;
  const fontePropostas       = modo === 'SU' ? PROPOSTAS_VALOR_POR_SETOR_SU : PROPOSTAS_VALOR_POR_SETOR;
  const fonteReceita         = modo === 'SU' ? MODELOS_RECEITA_POR_SETOR_SU : MODELOS_RECEITA_POR_SETOR;
  const fonteSegmentos       = modo === 'SU' ? SEGMENTOS_POR_SETOR_SU : SEGMENTOS_POR_SETOR;
  const propostasOpcoes      = fontePropostas[setorEmp] || fontePropostas['geral'];
  const receitaOpcoes        = fonteReceita[setorEmp] || fonteReceita['geral'];
  const segmentosOpcoes      = fonteSegmentos[setorEmp] || fonteSegmentos['geral'];

  // Accordion exclusivo · apenas 1 pergunta aberta por vez · ao responder, fecha
  // a atual e abre a próxima não-respondida.
  const [perguntaAberta, setPerguntaAberta] = useState(null);
  const [graficosT7B3Aberto, setGraficosT7B3Aberto] = useState(false);

  const atualizar = (campo, valor) => {
    setEstrategiaModelo(prev => ({
      estrategias, proposta_valor_id, modelo_receita_id, segmentos_id, unit_econ_respostas,
      ...prev,
      [campo]: valor,
    }));
  };
  const setarResposta = (perguntaId, opcaoId) => {
    const novasRespostas = { ...unit_econ_respostas, [perguntaId]: opcaoId };
    atualizar('unit_econ_respostas', novasRespostas);
    // Fecha a atual e abre a próxima não-respondida
    const idx = PERGUNTAS_UNIT_ECON.findIndex(p => p.id === perguntaId);
    const proxNaoResp = PERGUNTAS_UNIT_ECON.slice(idx + 1).find(p => !novasRespostas[p.id]);
    setPerguntaAberta(proxNaoResp?.id || null);
  };
  const togglePergunta = (perguntaId) => {
    setPerguntaAberta(perguntaAberta === perguntaId ? null : perguntaId);
  };
  // Perguntas extras SU (Churn · Payback) · só setam a resposta e fecham (não
  // entram no cálculo CAC/LTV).
  const setarRespostaExtra = (perguntaId, opcaoId) => {
    atualizar('unit_econ_respostas', { ...unit_econ_respostas, [perguntaId]: opcaoId });
    setPerguntaAberta(null);
  };
  // Cálculos automáticos a partir das escolhas (ponto médio do intervalo)
  const meio = (perguntaId) => {
    const p = PERGUNTAS_UNIT_ECON.find(x => x.id === perguntaId);
    const o = p?.opcoes.find(x => x.id === unit_econ_respostas[perguntaId]);
    return o?.meio;
  };
  const novosClientes  = meio('novos_clientes_mes');
  const gastoMkt       = meio('gasto_marketing_mes');
  const ticketMedio    = meio('ticket_medio');
  const vidaUtil       = meio('vida_util_meses');
  const margemPct      = meio('margem_media');
  const cac = (novosClientes && gastoMkt) ? Math.round(gastoMkt / novosClientes) : null;
  const ltv = (ticketMedio && vidaUtil && margemPct != null)
    ? Math.round(ticketMedio * vidaUtil * (margemPct / 100))
    : null;
  const razao = (ltv && cac) ? (ltv / cac) : null;
  const todasRespondidas = PERGUNTAS_UNIT_ECON.every(p => unit_econ_respostas[p.id]);
  const toggleEstrategia = (id) => {
    const novo = estrategias.includes(id)
      ? estrategias.filter(e => e !== id)
      : [...estrategias, id];
    atualizar('estrategias', novo);
  };

  const inputBase = {
    width: '100%', padding: '8px 12px', fontSize: 13,
    border: `1px solid ${G.borda}`, borderRadius: 0,
    fontFamily: "'Outfit', sans-serif", color: G.txt1,
    background: '#FFF', boxSizing: 'border-box',
  };
  const HEADER_BLOCO = {
    fontSize: 16, fontWeight: 700, letterSpacing: '0.10em',
    textTransform: 'uppercase', color: G.laranja, marginBottom: 10,
  };
  const HEADER_AUX = {
    fontSize: 12, fontWeight: 400, color: G.txt2,
    textTransform: 'none', letterSpacing: 0, marginLeft: 10,
  };
  const SUB_HEADER = {
    fontSize: 12, fontWeight: 700, color: G.txt2,
    textTransform: 'uppercase', letterSpacing: '0.06em',
    marginBottom: 6,
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
      {/* 2 quadros lado a lado: Definição + Instruções */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <div style={{ padding: '12px 16px', background: G.verdeLight,
          borderLeft: `4px solid ${G.verde}`, borderRadius: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 800, color: G.verde,
            letterSpacing: '0.06em', textTransform: 'uppercase',
            marginBottom: 6 }}>
            O que é Estratégia & Modelo de Negócio
          </div>
          <div style={{ fontSize: 12, lineHeight: 1.45, color: G.txt1 }}>
            Define <strong>como a empresa ganha</strong>: estratégia competitiva
            escolhida, proposta de valor única, modelo de receita e o
            <strong> custo e valor do cliente</strong> (CAC, LTV, margem). Junto,
            é o coração financeiro do BP.
          </div>
        </div>
        <div style={{ padding: '12px 16px', background: G.laranjaLight,
          borderLeft: `4px solid ${G.laranja}`, borderRadius: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 800, color: G.laranja,
            letterSpacing: '0.06em', textTransform: 'uppercase',
            marginBottom: 6 }}>
            O que você faz aqui
          </div>
          <div style={{ fontSize: 12, lineHeight: 1.45, color: G.txt1 }}>
            <strong>Marque</strong> as estratégias competitivas que se aplicam ·
            revise os 3 textos do modelo · <strong>informe os 3 números</strong>
            de custo e valor do cliente (referência setorial inline) · clique Aprovar.
          </div>
        </div>
      </div>

      {/* BLOCO 1 — Estratégia Competitiva (accordion único · múltipla escolha) */}
      <div>
        <div style={HEADER_BLOCO}>
          Bloco 1 · Estratégia Competitiva
          <span style={HEADER_AUX}>· clique para abrir · pode marcar mais de uma resposta</span>
        </div>
        <div style={{
          background: '#FFF',
          border: `1px solid ${perguntaAberta === 'estrategias' ? G.verde : G.borda}`,
          borderRadius: 0, overflow: 'hidden',
        }}>
          {/* Linha de chamada · trigger é apenas o botão à direita */}
          <div style={{
            background: perguntaAberta === 'estrategias' ? G.verdeLight : 'transparent',
            padding: '8px 14px',
            display: 'flex', alignItems: 'center', gap: 12,
          }}>
            <div style={{
              flexShrink: 0, width: 22, height: 22, borderRadius: '50%',
              background: estrategias.length > 0 ? G.verde : G.borda,
              color: '#FFF', fontSize: 11, fontWeight: 800,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              {estrategias.length > 0 ? '✓' : '★'}
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: G.txt1,
                lineHeight: 1.3 }}>
                Quais estratégias competitivas se aplicam ao seu negócio? <span style={{ color: G.txt2, fontWeight: 400 }}>(pode marcar mais de uma · geralmente 2-3)</span>
              </div>
              {estrategias.length > 0 && perguntaAberta !== 'estrategias' && (
                <div style={{ fontSize: 12, color: G.verde, fontWeight: 600,
                  marginTop: 2 }}>
                  {estrategias.map(id => fonteEstrategia.find(c => c.id === id)?.label).filter(Boolean).join(' · ')}
                </div>
              )}
            </div>
            <button onClick={() => togglePergunta('estrategias')}
              style={{
                flexShrink: 0,
                background: perguntaAberta === 'estrategias' ? '#FFF' : G.laranjaLight,
                border: `1px solid ${G.laranja}`,
                color: G.laranja,
                padding: '4px 9px', fontSize: 9, fontWeight: 800,
                letterSpacing: '0.08em', textTransform: 'uppercase',
                borderRadius: 0, cursor: 'pointer',
                fontFamily: "'Outfit', sans-serif",
                whiteSpace: 'nowrap',
              }}>
              {perguntaAberta === 'estrategias' ? '▴ Clique para fechar' : '▾ Clique para abrir'}
            </button>
          </div>
          {/* Lista verticalizada · só visível quando aberta */}
          {perguntaAberta === 'estrategias' && (
            <div style={{ padding: '4px 14px 12px',
              display: 'flex', flexDirection: 'column', gap: 6 }}>
              {fonteEstrategia.map(c => {
                const marcado = estrategias.includes(c.id);
                return (
                  <label key={c.id}
                    style={{ display: 'flex', alignItems: 'flex-start', gap: 10,
                      padding: '8px 12px', cursor: 'pointer',
                      background: marcado ? G.verdeLight : '#FFF',
                      border: marcado ? `2px solid ${G.verde}` : `1px solid ${G.borda}`,
                      borderRadius: 0,
                      fontFamily: "'Outfit', sans-serif",
                    }}>
                    <input type="checkbox" checked={marcado}
                      onChange={() => toggleEstrategia(c.id)}
                      style={{
                        appearance: 'none', WebkitAppearance: 'none',
                        width: 14, height: 14, marginTop: 3, flexShrink: 0,
                        border: `2px solid ${marcado ? G.verde : G.borda}`,
                        borderRadius: 0,
                        background: marcado ? G.verde : '#FFF',
                        cursor: 'pointer',
                      }}/>
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 800,
                        color: marcado ? G.verde : G.txt1, marginBottom: 2 }}>
                        {c.label}
                      </div>
                      <div style={{ fontSize: 11, color: G.txt2, lineHeight: 1.4 }}>
                        {c.detalhe}
                      </div>
                    </div>
                  </label>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* BLOCO 2 — Modelo de Negócio (3 accordions · seleção única · IA pré-marca) */}
      <div>
        <div style={HEADER_BLOCO}>
          Bloco 2 · Modelo de Negócio
          <span style={HEADER_AUX}>· <MarcaAIdvisor /> propõe alternativas · clique para abrir cada bloco</span>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {[
            { campo: 'proposta_valor_id', accordionId: 'proposta_valor',
              pergunta: 'Qual proposta de valor o seu negócio entrega?',
              valor: proposta_valor_id, opcoes: propostasOpcoes },
            { campo: 'modelo_receita_id', accordionId: 'modelo_receita',
              pergunta: 'Como o seu negócio gera receita?',
              valor: modelo_receita_id, opcoes: receitaOpcoes },
            { campo: 'segmentos_id', accordionId: 'segmentos',
              pergunta: 'Quais segmentos de cliente você atende?',
              valor: segmentos_id, opcoes: segmentosOpcoes },
          ].map(b => {
            const aberta = perguntaAberta === b.accordionId;
            const respondida = !!b.valor;
            const respostaTxt = respondida
              ? b.opcoes.find(o => o.id === b.valor)?.label
              : null;
            return (
              <div key={b.accordionId} style={{
                background: '#FFF',
                border: `1px solid ${aberta ? G.verde : G.borda}`,
                borderRadius: 0, overflow: 'hidden',
              }}>
                {/* Cabeçalho · trigger é o botão à direita */}
                <div style={{
                  background: aberta ? G.verdeLight : 'transparent',
                  padding: '8px 14px',
                  display: 'flex', alignItems: 'center', gap: 12,
                }}>
                  <div style={{
                    flexShrink: 0, width: 22, height: 22, borderRadius: '50%',
                    background: respondida ? G.verde : G.borda,
                    color: '#FFF', fontSize: 11, fontWeight: 800,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    {respondida ? '✓' : '★'}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: G.txt1,
                      lineHeight: 1.3 }}>
                      {b.pergunta}
                    </div>
                    {respondida && !aberta && (
                      <div style={{ fontSize: 12, color: G.verde, fontWeight: 600,
                        marginTop: 2 }}>
                        {respostaTxt}
                      </div>
                    )}
                  </div>
                  <button onClick={() => togglePergunta(b.accordionId)}
                    style={{
                      flexShrink: 0,
                      background: aberta ? '#FFF' : G.laranjaLight,
                      border: `1px solid ${G.laranja}`,
                      color: G.laranja,
                      padding: '4px 9px', fontSize: 9, fontWeight: 800,
                      letterSpacing: '0.08em', textTransform: 'uppercase',
                      borderRadius: 0, cursor: 'pointer',
                      fontFamily: "'Outfit', sans-serif",
                      whiteSpace: 'nowrap',
                    }}>
                    {aberta ? '▴ Clique para fechar' : '▾ Clique para abrir'}
                  </button>
                </div>
                {/* Opções verticalizadas (visível apenas quando aberta) */}
                {aberta && (
                  <div style={{ padding: '4px 14px 12px',
                    display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {b.opcoes.map(o => {
                      const ativo = b.valor === o.id;
                      return (
                        <label key={o.id}
                          style={{ display: 'flex', alignItems: 'center', gap: 10,
                            padding: '8px 12px', cursor: 'pointer',
                            background: ativo ? G.verdeLight : '#FFF',
                            border: ativo ? `2px solid ${G.verde}` : `1px solid ${G.borda}`,
                            borderRadius: 0,
                            fontFamily: "'Outfit', sans-serif",
                          }}>
                          <input type="radio"
                            name={b.campo}
                            checked={ativo}
                            onChange={() => atualizar(b.campo, o.id)}
                            style={{
                              appearance: 'none', WebkitAppearance: 'none',
                              width: 14, height: 14, flexShrink: 0,
                              border: `2px solid ${ativo ? G.verde : G.borda}`,
                              borderRadius: 0,
                              background: ativo ? G.verde : '#FFF',
                              cursor: 'pointer',
                            }}/>
                          <span style={{ fontSize: 13,
                            color: ativo ? G.verde : G.txt1,
                            fontWeight: ativo ? 700 : 500,
                            lineHeight: 1.4 }}>
                            {o.label}
                          </span>
                        </label>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* BLOCO 3 — Custo e Valor do Cliente (chips de seleção única · IA pré-marca) */}
      <div>
        <div style={HEADER_BLOCO}>
          Bloco 3 · Custo e Valor do Cliente
          <span style={HEADER_AUX}>· <MarcaAIdvisor /> pré-marcou a faixa típica do seu perfil · ajuste se necessário</span>
        </div>
        {/* Lista de perguntas · cada uma com chips em linha · radio (1 marcado por pergunta) */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {PERGUNTAS_UNIT_ECON.map(p => {
            const aberta = perguntaAberta === p.id;
            const respondida = !!unit_econ_respostas[p.id];
            const respostaTxt = respondida
              ? p.opcoes.find(o => o.id === unit_econ_respostas[p.id])?.label
              : null;
            return (
              <div key={p.id} style={{
                background: '#FFF',
                border: `1px solid ${aberta ? G.verde : G.borda}`,
                borderRadius: 0, overflow: 'hidden',
              }}>
                <div style={{
                  background: aberta ? G.verdeLight : 'transparent',
                  padding: '8px 14px',
                  display: 'flex', alignItems: 'center', gap: 12,
                }}>
                  <div style={{
                    flexShrink: 0, width: 22, height: 22, borderRadius: '50%',
                    background: respondida ? G.verde : G.borda,
                    color: '#FFF', fontSize: 11, fontWeight: 800,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    {respondida ? '✓' : '★'}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: G.txt1,
                      lineHeight: 1.3 }}>
                      {p.label}
                    </div>
                    {respondida && !aberta && (
                      <div style={{ fontSize: 12, color: G.verde, fontWeight: 600,
                        marginTop: 2 }}>
                        {respostaTxt}
                      </div>
                    )}
                  </div>
                  <button onClick={() => togglePergunta(p.id)}
                    style={{
                      flexShrink: 0,
                      background: aberta ? '#FFF' : G.laranjaLight,
                      border: `1px solid ${G.laranja}`,
                      color: G.laranja,
                      padding: '4px 9px', fontSize: 9, fontWeight: 800,
                      letterSpacing: '0.08em', textTransform: 'uppercase',
                      borderRadius: 0, cursor: 'pointer',
                      fontFamily: "'Outfit', sans-serif",
                      whiteSpace: 'nowrap',
                    }}>
                    {aberta ? '▴ Clique para fechar' : '▾ Clique para abrir'}
                  </button>
                </div>
                {aberta && (
                  <div style={{ padding: '4px 14px 12px',
                    display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {p.opcoes.map(o => {
                      const ativo = unit_econ_respostas[p.id] === o.id;
                      return (
                        <label key={o.id}
                          style={{ display: 'flex', alignItems: 'center', gap: 10,
                            padding: '8px 12px', cursor: 'pointer',
                            background: ativo ? G.verdeLight : '#FFF',
                            border: ativo ? `2px solid ${G.verde}` : `1px solid ${G.borda}`,
                            borderRadius: 0,
                            fontFamily: "'Outfit', sans-serif",
                          }}>
                          <input type="radio"
                            name={`pergunta_${p.id}`}
                            checked={ativo}
                            onChange={() => setarResposta(p.id, o.id)}
                            style={{
                              appearance: 'none', WebkitAppearance: 'none',
                              width: 14, height: 14, flexShrink: 0,
                              border: `2px solid ${ativo ? G.verde : G.borda}`,
                              borderRadius: 0,
                              background: ativo ? G.verde : '#FFF',
                              cursor: 'pointer',
                            }}/>
                          <span style={{ fontSize: 13,
                            color: ativo ? G.verde : G.txt1,
                            fontWeight: ativo ? 700 : 500,
                            lineHeight: 1.4 }}>
                            {o.label}
                          </span>
                        </label>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* 3 cards de resultado calculado pela IA */}
        <div style={{ marginTop: 14, display: 'grid',
          gridTemplateColumns: '1fr 1fr 1fr', gap: 10 }}>
          {[
            { label: 'CAC', titulo: 'Custo de Aquisição',
              valor: cac, sufixo: 'R$',
              explicacao: 'Quanto custa para você conquistar 1 cliente novo.' },
            { label: 'LTV', titulo: 'Valor ao Longo do Tempo',
              valor: ltv, sufixo: 'R$',
              explicacao: 'Quanto cada cliente gera de lucro durante todo o relacionamento.' },
            { label: 'LTV / CAC', titulo: 'Saúde do modelo',
              valor: razao, sufixo: '×',
              explicacao: 'Saudável quando > 3× · para cada R$ 1 em aquisição, retorna R$ X.' },
          ].map(item => {
            // Cor por faixa (só pra LTV/CAC)
            let cor = G.txt2;
            if (item.label.includes('/')) {
              if (item.valor != null) {
                cor = item.valor >= 3 ? G.verde : item.valor >= 1 ? '#B45309' : G.vermelho;
              }
            } else {
              cor = item.valor != null ? G.txt1 : G.txt2;
            }
            const exibir = item.valor == null ? '—'
              : item.sufixo === 'R$' ? `R$ ${Math.round(item.valor).toLocaleString('pt-BR')}`
              : item.sufixo === '×'  ? `${item.valor.toFixed(1).replace('.', ',')}×`
              : item.valor;
            return (
              <div key={item.label} style={{
                padding: '12px 14px',
                background: todasRespondidas ? G.verdeLight : '#F5F5F1',
                border: `1px solid ${todasRespondidas ? G.verde : G.borda}`,
                borderRadius: 0,
              }}>
                <div style={{ fontSize: 11, fontWeight: 800,
                  color: todasRespondidas ? G.verde : G.txt2,
                  letterSpacing: '0.08em', textTransform: 'uppercase',
                  marginBottom: 4 }}>
                  {item.label}
                </div>
                <div style={{ fontSize: 20, fontWeight: 800, color: cor,
                  marginBottom: 4, fontVariantNumeric: 'tabular-nums' }}>
                  {exibir}
                </div>
                <div style={{ fontSize: 11, color: G.txt2, lineHeight: 1.4,
                  fontStyle: 'italic' }}>
                  {item.explicacao}
                </div>
              </div>
            );
          })}
        </div>
        {!todasRespondidas && (
          <div style={{ marginTop: 10, fontSize: 11, color: G.txt2,
            fontStyle: 'italic', textAlign: 'center' }}>
            Responda as 5 perguntas acima para o <MarcaAIdvisor /> calcular os indicadores.
          </div>
        )}

        {/* Métricas de retenção (SU) · Churn e Payback com benchmark inline */}
        {modo === 'SU' && (
          <div style={{ marginTop: 16 }}>
            <div style={SUB_HEADER}>Métricas de retenção (startup)</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {PERGUNTAS_SU_EXTRA.map(p => {
                const aberta = perguntaAberta === p.id;
                const respondida = !!unit_econ_respostas[p.id];
                const respostaTxt = respondida
                  ? p.opcoes.find(o => o.id === unit_econ_respostas[p.id])?.label
                  : null;
                return (
                  <div key={p.id} style={{
                    background: '#FFF',
                    border: `1px solid ${aberta ? G.verde : G.borda}`,
                    borderRadius: 0, overflow: 'hidden',
                  }}>
                    <div style={{
                      background: aberta ? G.verdeLight : 'transparent',
                      padding: '8px 14px',
                      display: 'flex', alignItems: 'center', gap: 12,
                    }}>
                      <div style={{
                        flexShrink: 0, width: 22, height: 22, borderRadius: '50%',
                        background: respondida ? G.verde : G.borda,
                        color: '#FFF', fontSize: 11, fontWeight: 800,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                      }}>
                        {respondida ? '✓' : '★'}
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 13, fontWeight: 700, color: G.txt1,
                          lineHeight: 1.3 }}>
                          {p.label}
                        </div>
                        {respondida && !aberta && (
                          <div style={{ fontSize: 12, color: G.verde, fontWeight: 600,
                            marginTop: 2 }}>
                            {respostaTxt}
                          </div>
                        )}
                      </div>
                      <button onClick={() => togglePergunta(p.id)}
                        style={{
                          flexShrink: 0,
                          background: aberta ? '#FFF' : G.laranjaLight,
                          border: `1px solid ${G.laranja}`,
                          color: G.laranja,
                          padding: '4px 9px', fontSize: 9, fontWeight: 800,
                          letterSpacing: '0.08em', textTransform: 'uppercase',
                          borderRadius: 0, cursor: 'pointer',
                          fontFamily: "'Outfit', sans-serif",
                          whiteSpace: 'nowrap',
                        }}>
                        {aberta ? '▴ Clique para fechar' : '▾ Clique para abrir'}
                      </button>
                    </div>
                    {aberta && (
                      <div style={{ padding: '4px 14px 12px',
                        display: 'flex', flexDirection: 'column', gap: 6 }}>
                        {p.opcoes.map(o => {
                          const ativo = unit_econ_respostas[p.id] === o.id;
                          return (
                            <label key={o.id}
                              style={{ display: 'flex', alignItems: 'center', gap: 10,
                                padding: '8px 12px', cursor: 'pointer',
                                background: ativo ? G.verdeLight : '#FFF',
                                border: ativo ? `2px solid ${G.verde}` : `1px solid ${G.borda}`,
                                borderRadius: 0,
                                fontFamily: "'Outfit', sans-serif",
                              }}>
                              <input type="radio" name={`pergunta_${p.id}`}
                                checked={ativo}
                                onChange={() => setarRespostaExtra(p.id, o.id)}
                                style={{
                                  appearance: 'none', WebkitAppearance: 'none',
                                  width: 14, height: 14, flexShrink: 0,
                                  border: `2px solid ${ativo ? G.verde : G.borda}`,
                                  borderRadius: 0,
                                  background: ativo ? G.verde : '#FFF',
                                  cursor: 'pointer',
                                }}/>
                              <span style={{ fontSize: 13,
                                color: ativo ? G.verde : G.txt1,
                                fontWeight: ativo ? 700 : 500,
                                lineHeight: 1.4 }}>
                                {o.label}
                              </span>
                            </label>
                          );
                        })}
                        <div style={{ fontSize: 11, color: G.txt2, fontStyle: 'italic',
                          marginTop: 2 }}>
                          {p.referencia}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Botão de visualização da economia unitária */}
        <BotaoVisualizarGraficos
          aberto={graficosT7B3Aberto}
          onToggle={() => setGraficosT7B3Aberto(v => !v)}
          rotulo="Visualizar economia unitária do cliente">
          <div>
            <div style={{ fontSize: 10, fontWeight: 800, color: G.txt2,
              letterSpacing: '0.10em', textTransform: 'uppercase', marginBottom: 8 }}>
              Ilustração · {TITULO_EXHIBIT_POR_TIPO['t7_unit_econ']}
            </div>
            <GraficoUnitEconomics respostas={unit_econ_respostas}/>
          </div>
        </BotaoVisualizarGraficos>
      </div>
    </div>
  );
};


// ═══════════════════════════════════════════════════════════════════════
// Compiladores heurísticos — geram Markdown narrativo de cada tarefa
// quando o empresário aprova. Salvam em `documentoBP.secao_N`. Tarefa 18
// consolida tudo na entrega final. Quando IA real plugar (Auditoria Fase 3),
// substitui esses compiladores por chamadas LLM que enriquecem a narrativa.
// Pensado em 11/06/2026.
// ═══════════════════════════════════════════════════════════════════════
const compilarTarefa1 = (meta, equityFlag, empresa) => {
  const tipo = TIPOS_META_BP60M.find(t => t.id === meta?.tipo) || TIPOS_META_BP60M[0];
  const valor = Number(meta?.valor || 0).toLocaleString('pt-BR');
  const fraseMeta = `${tipo.fraseAntes}**${valor}**${tipo.fraseDepois}`;
  let tese;
  if (meta?.tipo === 'saida') {
    tese = `A meta de **Saída via Venda** direciona os Materiais de Captação (Teaser + Apresentação Comercial) para **candidatos a comprador** — estratégicos ou financeiros — em horizonte de 60 meses.`;
  } else if (equityFlag) {
    tese = `A empresa contempla **captação via Equity** para acelerar a meta. O Business Plan estima o valor necessário e os Materiais de Captação são direcionados para **investidor externo**.`;
  } else {
    tese = `A empresa optou por **crescimento via geração própria** (caixa operacional + dívida bancária). Os Materiais de Captação são produzidos em **tom institucional** — apoio a relacionamento com bancos, posicionamento e abertura futura.`;
  }
  return `## 1. Meta & Equity

### Meta de Transformação

${fraseMeta} A trajetória abaixo ilustra a evolução prevista do indicador-meta ao longo dos 5 anos do Business Plan, partindo do patamar atual e progredindo conforme o crescimento esperado.

<!--GRAFICO:t1_trajetoria-->

### Tese de Equity

${tese}`;
};

const compilarTarefa2 = (descricaoEmpresa, empresa) => {
  const rascunho = RASCUNHO_TAREFA_2[empresa?.id] || { historia: [], equipe: [], instalacoes: [] };
  const historia    = descricaoEmpresa?.historia    ?? rascunho.historia;
  const equipe      = descricaoEmpresa?.equipe      ?? rascunho.equipe;
  const instalacoes = descricaoEmpresa?.instalacoes ?? rascunho.instalacoes;

  const cnpj    = empresa?.cnpj || '—';
  const setor   = empresa?.setor || '—';
  const sede    = empresa?.endereco_cidade || empresa?.endereco || 'São Paulo · SP';
  const capital = empresa?.capital_social
    ? `R$ ${Number(empresa.capital_social).toLocaleString('pt-BR')}`
    : 'não informado';
  const regime  = empresa?.regime || '—';
  const objeto  = empresa?.objeto_social || empresa?.descricao_atividade || '—';

  const identidade = `A **${empresa?.nome || 'empresa'}** atua no setor de **${setor}**, sediada em ${sede}. ` +
    `Inscrita no CNPJ ${cnpj}, regime tributário ${regime}, com capital social de ${capital}. ` +
    `Objeto social: ${objeto}.`;

  const historiaMd = historia.length
    ? historia.map(m => `- **${m.ano}** — ${m.marco}`).join('\n')
    : '_Histórico ainda não cadastrado._';

  const equipeMd = equipe.length
    ? equipe.map(p => `**${p.nome}** · ${p.funcao}${p.bio ? `  \n${p.bio}` : ''}`).join('\n\n')
    : '_Equipe-chave ainda não cadastrada._';

  const instMd = instalacoes.length
    ? instalacoes.map(u => `- **${u.tipo}** — ${u.local}`).join('\n')
    : '_Instalações adicionais não informadas._';

  return `## 2. Descrição da Empresa

### Identidade

${identidade}

### Histórico & Marcos

${historiaMd}

### Equipe-chave

${equipeMd}

### Instalações

${instMd}`;
};

const compilarTarefa3 = (vmv, empresa) => {
  const rascunho = RASCUNHO_TAREFA_3[empresa?.id] || {
    visao: { texto: '', escolhas: {} },
    missao: { texto: '', escolhas: {} },
    valores: { texto: '', escolhas: {} },
  };
  const visao   = vmv?.visao?.texto   || rascunho.visao.texto   || '_Visão ainda não definida._';
  const missao  = vmv?.missao?.texto  || rascunho.missao.texto  || '_Missão ainda não definida._';
  const valores = vmv?.valores?.texto || rascunho.valores.texto || '_Valores ainda não definidos._';
  return `## 3. Visão · Missão · Valores

### Visão

${visao}

### Missão

${missao}

### Valores

${valores}`;
};

const compilarTarefa4 = (produtosServicos, empresa) => {
  const rascunho = RASCUNHO_TAREFA_4[empresa?.id] || {
    portfolio: [], diferenciais: [],
    pipeline: [], investimentoPD_intervalo: null, parcerias: [],
  };
  const portfolio                = produtosServicos?.portfolio                ?? rascunho.portfolio;
  const diferenciais             = produtosServicos?.diferenciais             ?? rascunho.diferenciais;
  const pipeline                 = produtosServicos?.pipeline                 ?? rascunho.pipeline                 ?? [];
  const investimentoPD_intervalo = produtosServicos?.investimentoPD_intervalo ?? rascunho.investimentoPD_intervalo ?? null;
  const parcerias                = produtosServicos?.parcerias                ?? rascunho.parcerias                ?? [];

  const portMd = portfolio.length
    ? portfolio.map(p => `- **${p.nome}**${p.mix != null && p.mix !== '' ? ` (${p.mix}% da receita)` : ''} — ${p.descricao}`).join('\n')
    : '_Portfólio ainda não cadastrado._';
  const difMd = diferenciais.length
    ? diferenciais.map(d => `- **${d.titulo}** — ${d.detalhe}`).join('\n')
    : '_Diferenciais competitivos não informados._';
  const pipeMd = pipeline.length
    ? pipeline.map(p => `- ${p.descricao}${p.prazo ? ` _(${p.prazo})_` : ''}`).join('\n')
    : '_Pipeline de inovação não informado._';
  const pdOpcao = INTERVALOS_PD.find(o => o.id === investimentoPD_intervalo);
  const pdMd = pdOpcao
    ? `Investimento estimado em P&D: **${pdOpcao.label}** da receita (${pdOpcao.desc}).`
    : '_Investimento em P&D não informado._';
  const parMd = parcerias.length
    ? parcerias.map(p => `- **${p.nome}** — ${p.tipo}`).join('\n')
    : '_Parcerias relevantes não informadas._';

  return `## 4. Produtos & Serviços

### Portfólio

${portMd}

A ilustração a seguir consolida o **mix de receita** por produto/serviço, evidenciando a contribuição relativa de cada linha do portfólio para o resultado total.

<!--GRAFICO:t4_mix_receita-->

### Diferenciais Competitivos

${difMd}

### Inovação & P&D

**Pipeline de Inovação**

${pipeMd}

**Investimento em P&D**

${pdMd}

**Parcerias Relevantes**

${parMd}`;
};

const compilarTarefa5 = (mercadoConcorrencia, empresa) => {
  const rascunho = RASCUNHO_TAREFA_5[empresa?.id] || {
    tam_intervalo: null, tam_desc: '', sam_intervalo: null, sam_desc: '',
    som_intervalo: null, som_desc: '', tendencias: [], players: [], moats: [],
  };
  const get = (campo) => mercadoConcorrencia?.[campo] ?? rascunho[campo];
  const labelInterval = (intervaloId, opcoes) => {
    const o = opcoes.find(x => x.id === intervaloId);
    return o ? o.label : '_não informado_';
  };
  const tendencias = get('tendencias');
  const players    = get('players');
  const moats      = get('moats');
  const tendMd = tendencias.length
    ? tendencias.map(t => `- **${t.titulo}**${t.detalhe ? ` — ${t.detalhe}` : ''}`).join('\n')
    : '_Tendências não informadas._';
  const plMd = players.length
    ? players.map(p => `- **${p.nome}** _(${p.share || '—'})_ — ${p.posicionamento || ''}`).join('\n')
    : '_Players principais não informados._';
  const moatsMd = moats.length
    ? moats.map(m => `- **${m.tipo}** — ${m.detalhe || ''}`).join('\n')
    : '_Vantagens defensáveis não informadas._';
  return `## 5. Mercado & Concorrência

### Mercado

**TAM** · ${labelInterval(get('tam_intervalo'), INTERVALOS_TAM)} — ${get('tam_desc') || '_não descrito_'}
**SAM** · ${labelInterval(get('sam_intervalo'), INTERVALOS_SAM)} — ${get('sam_desc') || '_não descrito_'}
**SOM** · ${labelInterval(get('som_intervalo'), INTERVALOS_SOM)} — ${get('som_desc') || '_não descrito_'}

A ilustração abaixo posiciona o **mercado total**, a **parcela endereçável** e o **território conquistável** em escala proporcional, evidenciando o espaço de crescimento disponível.

<!--GRAFICO:t5_tam_sam_som-->

**Tendências do setor**

${tendMd}

### Concorrência

**Players principais**

${plMd}

**Vantagens defensáveis (Moats)**

${moatsMd}`;
};

const compilarTarefa6 = (swot, empresa) => {
  const rascunho = RASCUNHO_TAREFA_6[empresa?.id] || {
    forcas: [], fraquezas: [], oportunidades: [], ameacas: [],
  };
  const obter = (quad) => {
    if (swot?.[quad]) return swot[quad];
    // IA pré-marca os candidatos · empresário desmarca o que não se aplica
    return rascunho[quad].map(texto => ({ texto, marcado: true, fonte: 'ia' }));
  };
  const renderLista = (lista) => {
    const marcados = lista.filter(i => i.marcado && i.texto);
    return marcados.length
      ? marcados.map(i => `- ${i.texto}`).join('\n')
      : '_Sem itens selecionados._';
  };
  return `## 6. SWOT

A matriz a seguir consolida o diagnóstico estratégico em 4 quadrantes (Forças, Fraquezas, Oportunidades e Ameaças), permitindo visualizar o posicionamento competitivo num único quadro.

<!--GRAFICO:t6_swot-->

### Forças (S)

${renderLista(obter('forcas'))}

### Fraquezas (W)

${renderLista(obter('fraquezas'))}

### Oportunidades (O)

${renderLista(obter('oportunidades'))}

### Ameaças (T)

${renderLista(obter('ameacas'))}`;
};

const compilarTarefa7 = (estrategiaModelo, empresa) => {
  const rascunho = RASCUNHO_TAREFA_7[empresa?.id] || {
    estrategias: [], proposta_valor: '', modelo_receita: '', segmentos: '',
    unit_econ_respostas: {},
  };
  const obter = (c) => estrategiaModelo?.[c] ?? rascunho[c];
  const estrategias = obter('estrategias');
  const estrategiasMd = estrategias.length
    ? estrategias.map(id => {
        const c = CANDIDATOS_ESTRATEGIA.find(x => x.id === id);
        return c ? `- **${c.label}** — ${c.detalhe}` : `- ${id}`;
      }).join('\n')
    : '_Estratégia competitiva não definida._';
  // Cálculo CAC/LTV/Razão a partir dos intervalos
  const respostas = obter('unit_econ_respostas') || {};
  const meio = (pid) => {
    const p = PERGUNTAS_UNIT_ECON.find(x => x.id === pid);
    const o = p?.opcoes.find(x => x.id === respostas[pid]);
    return o?.meio;
  };
  const nc = meio('novos_clientes_mes');
  const gm = meio('gasto_marketing_mes');
  const tm = meio('ticket_medio');
  const vu = meio('vida_util_meses');
  const mm = meio('margem_media');
  const cac = (nc && gm) ? Math.round(gm / nc) : null;
  const ltv = (tm && vu && mm != null) ? Math.round(tm * vu * (mm / 100)) : null;
  const razao = (ltv && cac) ? (ltv / cac) : null;
  const fmtR$ = v => v == null ? '—' : `R$ ${Number(v).toLocaleString('pt-BR')}`;
  const fmtRz = v => v == null ? '—' : `${v.toFixed(1).replace('.', ',')}×`;
  // Resolve as 3 alternativas escolhidas no Bloco 2
  const setorT7 = empresa?.setor || 'geral';
  const propostas = PROPOSTAS_VALOR_POR_SETOR[setorT7] || PROPOSTAS_VALOR_POR_SETOR['geral'];
  const receitas  = MODELOS_RECEITA_POR_SETOR[setorT7] || MODELOS_RECEITA_POR_SETOR['geral'];
  const segOpcoes = SEGMENTOS_POR_SETOR[setorT7] || SEGMENTOS_POR_SETOR['geral'];
  const propostaTxt = propostas.find(o => o.id === obter('proposta_valor_id'))?.label
                      || '_Proposta de valor ainda não escolhida._';
  const receitaTxt  = receitas.find(o => o.id === obter('modelo_receita_id'))?.label
                      || '_Modelo de receita ainda não escolhido._';
  const segmentoTxt = segOpcoes.find(o => o.id === obter('segmentos_id'))?.label
                      || '_Segmento ainda não escolhido._';
  return `## 7. Estratégia & Modelo de Negócio

### Estratégia Competitiva

${estrategiasMd}

### Modelo de Negócio

**Proposta de Valor**

${propostaTxt}

**Modelo de Receita**

${receitaTxt}

**Segmentos Atendidos**

${segmentoTxt}

### Custo e Valor do Cliente (calculado pelo RUNGOAL AIDVISOR)

A ilustração a seguir consolida a economia unitária do negócio em três indicadores fundamentais — **CAC** (custo de aquisição), **LTV** (valor por cliente ao longo da vida) e **LTV/CAC** (saúde da economia unitária).

<!--GRAFICO:t7_unit_econ-->

- **CAC** (Custo de Aquisição por Cliente): ${fmtR$(cac)}
- **LTV** (Valor do Cliente ao Longo do Tempo): ${fmtR$(ltv)}
- **LTV / CAC**: ${fmtRz(razao)} _(saudável > 3×)_`;
};

// ════════════════════════════════════════════════════════════════════════
// TAREFA 8 — Marketing & Vendas
// 4 blocos: Posicionamento de Marca · Canais & Mix (validação soma = 100%) ·
// Funil & Conversão · Investimento em Marketing (% receita · classes estratégicas)
// ════════════════════════════════════════════════════════════════════════

const CANAIS_AQUISICAO = [
  { id: 'social_paid',    label: 'Mídia paga em redes sociais',   tipo: 'Digital' },
  { id: 'google_ads',     label: 'Google Ads · busca paga',       tipo: 'Digital' },
  { id: 'social_org',     label: 'Conteúdo orgânico em redes',    tipo: 'Digital' },
  { id: 'seo',            label: 'SEO · busca orgânica',          tipo: 'Digital' },
  { id: 'email_crm',      label: 'E-mail · CRM · WhatsApp',       tipo: 'Relacionamento' },
  { id: 'parcerias',      label: 'Parcerias e indicações',        tipo: 'Relacionamento' },
  { id: 'vendas_diretas', label: 'Vendas diretas (consultivas)',  tipo: 'Direto' },
  { id: 'ponto_fisico',   label: 'Ponto físico · vitrine',        tipo: 'Físico' },
  { id: 'eventos',        label: 'Eventos e feiras',              tipo: 'Físico' },
  { id: 'marketplace',    label: 'Marketplaces de terceiros',     tipo: 'Digital' },
];

// Versão SU (Startup): saem os canais físicos (ponto físico · eventos e feiras),
// entram 5 canais típicos de startup. Selecionado em ConteudoTarefa8 (modo SU).
const CANAIS_AQUISICAO_SU = [
  { id: 'social_paid',    label: 'Mídia paga em redes sociais',   tipo: 'Digital' },
  { id: 'google_ads',     label: 'Google Ads · busca paga',       tipo: 'Digital' },
  { id: 'social_org',     label: 'Conteúdo orgânico em redes',    tipo: 'Digital' },
  { id: 'seo',            label: 'SEO · busca orgânica',          tipo: 'Digital' },
  { id: 'email_crm',      label: 'E-mail · CRM · WhatsApp',       tipo: 'Relacionamento' },
  { id: 'parcerias',      label: 'Parcerias e indicações',        tipo: 'Relacionamento' },
  { id: 'vendas_diretas', label: 'Vendas diretas (consultivas)',  tipo: 'Direto' },
  { id: 'marketplace',    label: 'Marketplaces / app stores',     tipo: 'Digital' },
  { id: 'plg',            label: 'Crescimento via produto (PLG)', tipo: 'Produto' },
  { id: 'comunidade',     label: 'Comunidade',                    tipo: 'Relacionamento' },
  { id: 'referral',       label: 'Programa de referência',        tipo: 'Relacionamento' },
  { id: 'influencers',    label: 'Influenciadores e creators',    tipo: 'Digital' },
  { id: 'eventos_tech',   label: 'Eventos técnicos e hackathons', tipo: 'Presencial' },
];

const ETAPAS_FUNIL = [
  { id: 'topo',       label: 'Topo · Descoberta',   descricao: 'visitantes e impressões' },
  { id: 'meio',       label: 'Meio · Consideração', descricao: 'leads engajados' },
  { id: 'fundo',      label: 'Fundo · Decisão',     descricao: 'oportunidades qualificadas' },
  { id: 'fechamento', label: 'Fechamento',          descricao: 'vendas concretizadas' },
];

const PERGUNTAS_INVEST_MKT = [
  { id: 'pct_receita',
    label: 'Quanto da receita você investe em marketing e vendas hoje?',
    opcoes: [
      { id: 'a', label: 'Até 3% da receita',        meio: 1.5 },
      { id: 'b', label: '3% a 7% da receita',       meio: 5 },
      { id: 'c', label: '7% a 12% da receita',      meio: 9.5 },
      { id: 'd', label: '12% a 20% da receita',     meio: 16 },
      { id: 'e', label: 'Acima de 20% da receita',  meio: 25 },
    ],
  },
];

// ─── Catálogos por setor — Bloco 1 (Posicionamento de Marca) ────────────
// Cada chip tem { id, chip (Title Case · exibido), frase (lowercase · entra na frase) }
const CARACTERISTICAS_POSICIONAMENTO_POR_SETOR = {
  'E-commerce': [
    { id: 'preco_acessivel',     chip: 'Preço acessível',          frase: 'preço acessível' },
    { id: 'curadoria',           chip: 'Curadoria editorial',      frase: 'curadoria editorial' },
    { id: 'entrega_rapida',      chip: 'Entrega rápida',           frase: 'entrega rápida' },
    { id: 'private_label',       chip: 'Linha private label',      frase: 'linha private label' },
    { id: 'marketplace_3p',      chip: 'Marketplace de terceiros', frase: 'venda em marketplaces de terceiros' },
    { id: 'lifestyle_aspir',     chip: 'Lifestyle aspiracional',   frase: 'apelo lifestyle aspiracional' },
    { id: 'sustentavel',         chip: 'Sustentável',              frase: 'cadeia sustentável' },
    { id: 'personalizacao',      chip: 'Personalização',           frase: 'personalização do catálogo' },
    { id: 'devolucao_facil',     chip: 'Devolução facilitada',     frase: 'devolução facilitada' },
    { id: 'programa_fidelidade', chip: 'Programa de fidelidade',   frase: 'programa de fidelidade' },
  ],
  'Bar/Restaurante': [
    { id: 'cozinha_autoral',    chip: 'Cozinha autoral',          frase: 'cozinha autoral' },
    { id: 'ambiente_premiado',  chip: 'Ambiente premiado',        frase: 'ambiente premiado' },
    { id: 'ingredientes_locais',chip: 'Ingredientes locais',      frase: 'ingredientes locais' },
    { id: 'eventos_privados',   chip: 'Eventos privados',         frase: 'eventos privados' },
    { id: 'delivery_premium',   chip: 'Delivery premium',         frase: 'delivery premium' },
    { id: 'executivo_corp',     chip: 'Atendimento corporativo',  frase: 'atendimento corporativo' },
    { id: 'narrativa_origem',   chip: 'Narrativa de origem',      frase: 'narrativa de origem' },
    { id: 'carta_drinks',       chip: 'Carta de drinks autoral',  frase: 'carta de drinks autoral' },
    { id: 'gastronomia_reg',    chip: 'Gastronomia regional',     frase: 'gastronomia regional' },
    { id: 'experiencia_imers',  chip: 'Experiência imersiva',     frase: 'experiência imersiva' },
  ],
  'Saúde': [
    { id: 'multidisciplinar',   chip: 'Atendimento multidisciplinar',     frase: 'atendimento multidisciplinar' },
    { id: 'telemed_integrada',  chip: 'Telemedicina integrada ao presencial', frase: 'telemedicina integrada ao presencial' },
    { id: 'prevencao_anual',    chip: 'Programas preventivos anuais',     frase: 'programas preventivos anuais' },
    { id: 'acolhimento',        chip: 'Acolhimento humanizado',           frase: 'acolhimento humanizado' },
    { id: 'rede_convenios',     chip: 'Rede de convênios ampla',          frase: 'rede de convênios ampla' },
    { id: 'agendamento_rapido', chip: 'Agendamento em até 24h',           frase: 'agendamento em até 24h' },
    { id: 'coord_cuidado',      chip: 'Coordenação de cuidado',           frase: 'coordenação de cuidado entre especialidades' },
    { id: 'tecnologia_clinica', chip: 'Tecnologia clínica própria',       frase: 'tecnologia clínica própria' },
    { id: 'programas_corp',     chip: 'Programas corporativos',           frase: 'programas corporativos' },
    { id: 'localizacao_estr',   chip: 'Localização estratégica',          frase: 'localização estratégica' },
  ],
  'Construção Civil': [
    { id: 'engenheiro_presente',chip: 'Engenheiro responsável presente', frase: 'engenheiro responsável presente em obra' },
    { id: 'prazo_cumprido',     chip: 'Cumprimento de prazo',            frase: 'cumprimento rigoroso de prazo' },
    { id: 'loja_propria',       chip: 'Loja própria de insumos',         frase: 'loja própria de insumos' },
    { id: 'turn_key',           chip: 'Projeto turn-key',                frase: 'entrega turn-key' },
    { id: 'foco_corporativo',   chip: 'Foco corporativo',                frase: 'foco em clientes corporativos' },
    { id: 'sla_garantido',      chip: 'SLA contratual garantido',        frase: 'SLA contratual garantido' },
    { id: 'gestao_risco',       chip: 'Gestão de risco da obra',         frase: 'gestão de risco da obra' },
    { id: 'portfolio_tecnico',  chip: 'Portfólio técnico documentado',   frase: 'portfólio técnico documentado' },
    { id: 'equipe_propria',     chip: 'Equipe própria estável',          frase: 'equipe própria estável' },
    { id: 'seguranca_canteiro', chip: 'Segurança no canteiro',           frase: 'segurança rigorosa no canteiro' },
  ],
  'geral': [
    { id: 'qualidade',       chip: 'Qualidade',           frase: 'qualidade reconhecida' },
    { id: 'preco_justo',     chip: 'Preço justo',         frase: 'preço justo' },
    { id: 'atendimento',     chip: 'Atendimento próximo', frase: 'atendimento próximo' },
    { id: 'experiencia',     chip: 'Experiência única',   frase: 'experiência única' },
    { id: 'inovacao',        chip: 'Inovação',            frase: 'inovação' },
    { id: 'tradicao',        chip: 'Tradição',            frase: 'tradição' },
    { id: 'confiabilidade',  chip: 'Confiabilidade',      frase: 'confiabilidade' },
    { id: 'flexibilidade',   chip: 'Flexibilidade',       frase: 'flexibilidade' },
    { id: 'velocidade',      chip: 'Velocidade',          frase: 'velocidade de entrega' },
    { id: 'personalizacao_g',chip: 'Personalização',      frase: 'personalização' },
  ],
};

const CARACTERISTICAS_TOM_POR_SETOR = {
  'E-commerce': [
    { id: 'aspiracional',    chip: 'Aspiracional',      frase: 'aspiracional' },
    { id: 'acessivel',       chip: 'Acessível',         frase: 'acessível' },
    { id: 'lifestyle',       chip: 'Lifestyle',         frase: 'lifestyle' },
    { id: 'bem_humorado',    chip: 'Bem-humorado',      frase: 'bem-humorado' },
    { id: 'proximo',         chip: 'Próximo',           frase: 'próximo do cliente' },
    { id: 'direto',          chip: 'Direto',            frase: 'direto' },
    { id: 'educativo',       chip: 'Educativo',         frase: 'educativo' },
    { id: 'inspirador',      chip: 'Inspirador',        frase: 'inspirador' },
    { id: 'descontraido',    chip: 'Descontraído',      frase: 'descontraído' },
    { id: 'bom_senso_fin',   chip: 'Bom-senso financeiro', frase: 'com bom-senso financeiro' },
  ],
  'Bar/Restaurante': [
    { id: 'sofisticado',     chip: 'Sofisticado',       frase: 'sofisticado' },
    { id: 'acolhedor',       chip: 'Acolhedor',         frase: 'acolhedor' },
    { id: 'afetuoso',        chip: 'Afetuoso',          frase: 'afetuoso' },
    { id: 'autoral',         chip: 'Autoral',           frase: 'com identidade autoral' },
    { id: 'narrativo',       chip: 'Narrativo',         frase: 'narrativo' },
    { id: 'gastronomico',    chip: 'Gastronômico',      frase: 'gastronômico' },
    { id: 'descontraido',    chip: 'Descontraído',      frase: 'descontraído' },
    { id: 'convidativo',     chip: 'Convidativo',       frase: 'convidativo' },
    { id: 'informal_premium',chip: 'Informal premium',  frase: 'informal-premium' },
    { id: 'sem_rebusque',    chip: 'Sem rebuscamento',  frase: 'sem rebuscamento' },
  ],
  'Saúde': [
    { id: 'acolhedor',       chip: 'Acolhedor',          frase: 'acolhedor' },
    { id: 'confianca',       chip: 'De confiança',       frase: 'de confiança' },
    { id: 'claro',           chip: 'Claro',              frase: 'claro' },
    { id: 'humano',          chip: 'Humano',             frase: 'humano' },
    { id: 'tranquilizador',  chip: 'Tranquilizador',     frase: 'tranquilizador' },
    { id: 'educativo',       chip: 'Educativo',          frase: 'educativo' },
    { id: 'respeitoso',      chip: 'Respeitoso',         frase: 'respeitoso' },
    { id: 'direto_clinico',  chip: 'Direto sem medicalizar', frase: 'direto sem medicalizar em excesso' },
    { id: 'proximo',         chip: 'Próximo',            frase: 'próximo do paciente' },
    { id: 'tecnico_acess',   chip: 'Técnico acessível',  frase: 'técnico em linguagem acessível' },
  ],
  'Construção Civil': [
    { id: 'tecnico',         chip: 'Técnico',            frase: 'técnico' },
    { id: 'direto',          chip: 'Direto',             frase: 'direto' },
    { id: 'confiavel',       chip: 'Confiável',          frase: 'confiável' },
    { id: 'assertivo',       chip: 'Assertivo',          frase: 'assertivo' },
    { id: 'objetivo',        chip: 'Objetivo',           frase: 'objetivo' },
    { id: 'foco_retorno',    chip: 'Foco no retorno',    frase: 'com foco no retorno do contratante' },
    { id: 'seguranca_arg',   chip: 'Segurança argumentativa', frase: 'com argumentação de segurança' },
    { id: 'transparente',    chip: 'Transparente',       frase: 'transparente' },
    { id: 'consultivo',      chip: 'Consultivo',         frase: 'consultivo' },
    { id: 'metodico',        chip: 'Metódico',           frase: 'metódico' },
  ],
  'geral': [
    { id: 'proximo',         chip: 'Próximo',            frase: 'próximo' },
    { id: 'direto',          chip: 'Direto',             frase: 'direto' },
    { id: 'confiavel',       chip: 'Confiável',          frase: 'confiável' },
    { id: 'acolhedor',       chip: 'Acolhedor',          frase: 'acolhedor' },
    { id: 'tecnico',         chip: 'Técnico',            frase: 'técnico' },
    { id: 'descontraido',    chip: 'Descontraído',       frase: 'descontraído' },
    { id: 'consultivo',      chip: 'Consultivo',         frase: 'consultivo' },
    { id: 'educativo',       chip: 'Educativo',          frase: 'educativo' },
    { id: 'inspirador',      chip: 'Inspirador',         frase: 'inspirador' },
    { id: 'objetivo',        chip: 'Objetivo',           frase: 'objetivo' },
  ],
};

// Templates de frase por setor — slot {LISTA} recebe as características em prosa.
const TEMPLATE_FRASE_POSICIONAMENTO = {
  'E-commerce':       'Negócio digital marcado por {LISTA}.',
  'Bar/Restaurante':  'Experiência gastronômica marcada por {LISTA}.',
  'Saúde':            'Atendimento de saúde marcado por {LISTA}.',
  'Construção Civil': 'Obras marcadas por {LISTA}.',
  'geral':            'Negócio marcado por {LISTA}.',
};
const TEMPLATE_FRASE_TOM = {
  'E-commerce':       'Tom {LISTA}.',
  'Bar/Restaurante':  'Tom {LISTA}.',
  'Saúde':            'Tom {LISTA}, transmitindo cuidado ao paciente.',
  'Construção Civil': 'Tom {LISTA}, com foco no decisor B2B.',
  'geral':            'Tom {LISTA}.',
};

// Junta uma lista de strings em prosa: "a, b, c e d"
const juntarEmProsa = (lista) => {
  const arr = lista.filter(Boolean);
  if (arr.length === 0) return '';
  if (arr.length === 1) return arr[0];
  if (arr.length === 2) return `${arr[0]} e ${arr[1]}`;
  return `${arr.slice(0, -1).join(', ')} e ${arr[arr.length - 1]}`;
};

// Resolve a frase de Posicionamento a partir das características selecionadas
const montarFrasePosicionamento = (setor, ids, extras, modo = 'EE') => {
  const fonte = modo === 'SU' ? CARACTERISTICAS_POSICIONAMENTO_POR_SETOR_SU : CARACTERISTICAS_POSICIONAMENTO_POR_SETOR;
  const catalogo = fonte[setor] || fonte['geral'];
  const partes = (ids || [])
    .map(id => catalogo.find(c => c.id === id)?.frase)
    .filter(Boolean)
    .concat((extras || []).map(e => (e || '').trim().toLowerCase()).filter(Boolean));
  if (partes.length === 0) return '';
  const tmpl = TEMPLATE_FRASE_POSICIONAMENTO[setor] || TEMPLATE_FRASE_POSICIONAMENTO['geral'];
  return tmpl.replace('{LISTA}', juntarEmProsa(partes));
};

const montarFraseTom = (setor, ids, extras, modo = 'EE') => {
  const fonte = modo === 'SU' ? CARACTERISTICAS_TOM_POR_SETOR_SU : CARACTERISTICAS_TOM_POR_SETOR;
  const catalogo = fonte[setor] || fonte['geral'];
  const partes = (ids || [])
    .map(id => catalogo.find(c => c.id === id)?.frase)
    .filter(Boolean)
    .concat((extras || []).map(e => (e || '').trim().toLowerCase()).filter(Boolean));
  if (partes.length === 0) return '';
  const tmpl = TEMPLATE_FRASE_TOM[setor] || TEMPLATE_FRASE_TOM['geral'];
  return tmpl.replace('{LISTA}', juntarEmProsa(partes));
};

// ─── Catálogos por setor × etapa — Bloco 3 (Funil & Conversão) ──────────
// Estrutura: { setor: { topo:[chips], meio:[...], fundo:[...], fechamento:[...], gargalo:[...] } }
const CARACTERISTICAS_FUNIL_POR_SETOR_E_ETAPA = {
  'E-commerce': {
    topo: [
      { id: 'midia_paga_redes',  chip: 'Mídia paga em redes',    frase: 'mídia paga em redes sociais' },
      { id: 'ads_busca',         chip: 'Anúncios de busca',      frase: 'anúncios de busca paga' },
      { id: 'influenciadores',   chip: 'Influenciadores',        frase: 'influenciadores' },
      { id: 'conteudo_organico', chip: 'Conteúdo orgânico',      frase: 'conteúdo orgânico em redes' },
      { id: 'seo_categoria',     chip: 'SEO de categoria',       frase: 'SEO em páginas de categoria' },
      { id: 'marketplaces',      chip: 'Listagem em marketplaces', frase: 'listagem em marketplaces' },
    ],
    meio: [
      { id: 'retargeting',       chip: 'Retargeting',            frase: 'retargeting de visitantes' },
      { id: 'email_whats',       chip: 'E-mail e WhatsApp',      frase: 'sequência de e-mail e WhatsApp' },
      { id: 'curadoria_ambiente',chip: 'Curadoria por ambiente', frase: 'curadoria por ambiente' },
      { id: 'prova_social',      chip: 'Prova social',           frase: 'prova social' },
      { id: 'video_unboxing',    chip: 'Vídeos de unboxing',     frase: 'vídeos de unboxing' },
      { id: 'catalogo_pdp',      chip: 'Catálogo indexado',      frase: 'catálogo bem indexado' },
    ],
    fundo: [
      { id: 'pdp_otimizada',     chip: 'Página de produto otimizada', frase: 'página de produto com prova social' },
      { id: 'frete_gratis',      chip: 'Frete grátis acima de X',     frase: 'frete grátis acima de um valor' },
      { id: 'parcelamento',      chip: 'Parcelamento facilitado',     frase: 'parcelamento facilitado' },
      { id: 'garantia_clara',    chip: 'Garantia clara',              frase: 'política de garantia clara' },
      { id: 'chat_atendimento',  chip: 'Chat de atendimento',         frase: 'chat de atendimento' },
      { id: 'cross_sell',        chip: 'Cross-sell',                  frase: 'recomendação de produtos complementares' },
    ],
    fechamento: [
      { id: 'checkout_otim',     chip: 'Checkout otimizado',     frase: 'checkout otimizado' },
      { id: 'lembrete_carrinho', chip: 'Lembrete de carrinho',   frase: 'lembrete de carrinho abandonado' },
      { id: 'indicacao_pos',     chip: 'Indicação no pós-venda', frase: 'programa de indicação pós-venda' },
      { id: 'entrega_rapida',    chip: 'Entrega rápida',         frase: 'entrega rápida confirmada' },
      { id: 'atendimento_pos',   chip: 'Atendimento pós-compra', frase: 'atendimento pós-compra' },
      { id: 'pesquisa_satisf',   chip: 'Pesquisa de satisfação', frase: 'pesquisa de satisfação' },
    ],
    gargalo: [
      { id: 'carrinho_abandon',  chip: 'Carrinho abandonado alto',     frase: 'taxa elevada de carrinho abandonado' },
      { id: 'conversao_pdp',     chip: 'Conversão na PDP baixa',       frase: 'baixa conversão na página de produto' },
      { id: 'cac_alto',          chip: 'CAC alto',                     frase: 'custo de aquisição acima da meta' },
      { id: 'recompra_baixa',    chip: 'Recompra baixa',               frase: 'recompra abaixo do esperado' },
      { id: 'ticket_baixo',      chip: 'Ticket médio baixo',           frase: 'ticket médio baixo' },
      { id: 'frete_perde',       chip: 'Frete caro perde venda',       frase: 'frete encarece e derruba conversão' },
    ],
  },
  'Bar/Restaurante': {
    topo: [
      { id: 'conteudo_insta',    chip: 'Conteúdo no Instagram',    frase: 'conteúdo orgânico no Instagram' },
      { id: 'imprensa_gastr',    chip: 'Mídia gastronômica',       frase: 'reportagens em mídia gastronômica' },
      { id: 'influenciadores_f', chip: 'Influenciadores de food',  frase: 'influenciadores de gastronomia' },
      { id: 'vitrine_rua',       chip: 'Vitrine e fachada',        frase: 'vitrine e fachada chamativas' },
      { id: 'google_maps',       chip: 'Google Maps',              frase: 'presença otimizada no Google Maps' },
      { id: 'guias_locais',      chip: 'Guias e listas locais',    frase: 'aparição em guias e listas locais' },
    ],
    meio: [
      { id: 'reviews_guias',     chip: 'Reviews em guias',         frase: 'reviews em guias (TripAdvisor, TimeOut)' },
      { id: 'bastidor',          chip: 'Vídeos de bastidor',       frase: 'vídeos de bastidor' },
      { id: 'curadoria_chef',    chip: 'Curadoria do chef',        frase: 'curadoria do chef' },
      { id: 'depoimentos',       chip: 'Depoimentos de clientes',  frase: 'depoimentos de clientes' },
      { id: 'fotos_pratos',      chip: 'Fotos profissionais dos pratos', frase: 'fotos profissionais dos pratos' },
      { id: 'stories_eventos',   chip: 'Stories de eventos',       frase: 'stories de eventos no salão' },
    ],
    fundo: [
      { id: 'reserva_direta',    chip: 'Reserva direta no site',   frase: 'reserva direta no site' },
      { id: 'menu_degust',       chip: 'Menu degustação',          frase: 'sugestão de menu degustação' },
      { id: 'eventos_privados',  chip: 'Eventos privados',         frase: 'opção de eventos privados' },
      { id: 'whats_reservas',    chip: 'WhatsApp de reservas',     frase: 'WhatsApp dedicado a reservas' },
      { id: 'menu_executivo',    chip: 'Menu executivo',           frase: 'menu executivo destacado' },
      { id: 'ofertas_grupos',    chip: 'Ofertas para grupos',      frase: 'ofertas para grupos' },
    ],
    fechamento: [
      { id: 'recepcao_personal', chip: 'Recepção personalizada',   frase: 'recepção personalizada' },
      { id: 'experiencia_pres',  chip: 'Experiência presencial',   frase: 'experiência presencial premiada' },
      { id: 'pos_visita_email',  chip: 'Pós-visita por e-mail',    frase: 'pós-visita por e-mail' },
      { id: 'fidelidade',        chip: 'Programa de fidelidade',   frase: 'programa de fidelidade' },
      { id: 'presente_dia',      chip: 'Mimo do dia',              frase: 'mimo especial do dia' },
      { id: 'sugestao_reserva',  chip: 'Sugestão de próxima reserva', frase: 'sugestão de próxima reserva' },
    ],
    gargalo: [
      { id: 'fora_pico',         chip: 'Conversão fora do pico',   frase: 'baixa conversão fora do horário de pico' },
      { id: 'ocupacao_almoco',   chip: 'Ocupação no almoço',       frase: 'ocupação baixa no almoço de dias úteis' },
      { id: 'ticket_almoco',     chip: 'Ticket médio do almoço',   frase: 'ticket médio reduzido no almoço' },
      { id: 'delivery_marg',     chip: 'Margem do delivery',       frase: 'margem reduzida no delivery' },
      { id: 'reviews_neg',       chip: 'Reviews negativos pontuais', frase: 'reviews negativos pontuais' },
      { id: 'sazonal',           chip: 'Sazonalidade',             frase: 'baixa em meses sazonais' },
    ],
  },
  'Saúde': {
    topo: [
      { id: 'conteudo_educ',     chip: 'Conteúdo educativo',       frase: 'conteúdo educativo em saúde' },
      { id: 'parcerias_conv',    chip: 'Parcerias com convênios',  frase: 'parcerias com convênios' },
      { id: 'rhs_corp',          chip: 'Áreas de RH corporativo',  frase: 'parcerias com áreas de RH corporativo' },
      { id: 'seo_sintomas',      chip: 'SEO para sintomas',        frase: 'SEO para sintomas e especialidades' },
      { id: 'maps_saude',        chip: 'Google Maps',              frase: 'presença no Google Maps' },
      { id: 'indicacao_medica',  chip: 'Indicação médica',         frase: 'indicação médica entre especialidades' },
    ],
    meio: [
      { id: 'conteudo_redes',    chip: 'Conteúdo em redes',        frase: 'conteúdo orgânico em redes' },
      { id: 'otimiz_busca',      chip: 'Otimização de busca',      frase: 'otimização de busca' },
      { id: 'linkedin_corp',     chip: 'LinkedIn institucional',   frase: 'LinkedIn institucional' },
      { id: 'newsletter',        chip: 'Newsletter de saúde',      frase: 'newsletter de saúde' },
      { id: 'depoimentos_pac',   chip: 'Depoimentos de pacientes', frase: 'depoimentos de pacientes' },
      { id: 'materiais_educ',    chip: 'Materiais educativos',     frase: 'materiais educativos próprios' },
    ],
    fundo: [
      { id: 'agend_online',      chip: 'Agendamento online',       frase: 'agendamento online simples' },
      { id: 'retorno_24h',       chip: 'Retorno em até 24h',       frase: 'retorno em até 24 horas' },
      { id: 'telemed',           chip: 'Telemedicina disponível',  frase: 'telemedicina disponível' },
      { id: 'whats_clinica',     chip: 'WhatsApp da clínica',      frase: 'WhatsApp da clínica' },
      { id: 'pacotes_familia',   chip: 'Pacotes família',          frase: 'pacotes família' },
      { id: 'cobertura_conv',    chip: 'Cobertura de convênios',   frase: 'cobertura ampla de convênios' },
    ],
    fechamento: [
      { id: 'consulta_efet',     chip: 'Consulta presencial ou tele', frase: 'consulta presencial ou por telemedicina' },
      { id: 'followup_auto',     chip: 'Follow-up automatizado',   frase: 'follow-up automatizado' },
      { id: 'prevencao_ret',     chip: 'Plano de prevenção',       frase: 'plano de retorno e prevenção' },
      { id: 'tele_apoio',        chip: 'Apoio por telemedicina',   frase: 'apoio contínuo por telemedicina' },
      { id: 'programa_anual',    chip: 'Programa anual',           frase: 'programa preventivo anual' },
      { id: 'ficha_completa',    chip: 'Ficha clínica completa',   frase: 'ficha clínica completa e integrada' },
    ],
    gargalo: [
      { id: 'reativ_pac',        chip: 'Reativação de inativos',   frase: 'reativação de pacientes inativos' },
      { id: 'agenda_lacuna',     chip: 'Lacunas na agenda',        frase: 'lacunas na agenda' },
      { id: 'tele_conversao',    chip: 'Conversão para tele',      frase: 'baixa conversão para telemedicina' },
      { id: 'noshow',            chip: 'No-show de consultas',     frase: 'faltas em consultas (no-show)' },
      { id: 'ticket_proced',     chip: 'Ticket por procedimento',  frase: 'ticket médio baixo por procedimento' },
      { id: 'retencao_anual',    chip: 'Retenção pós-1º ano',      frase: 'retenção fraca após o primeiro ano' },
    ],
  },
  'Construção Civil': {
    topo: [
      { id: 'prospec_ativa',     chip: 'Prospecção ativa',         frase: 'prospecção ativa' },
      { id: 'parc_arq',          chip: 'Parcerias com arquitetos', frase: 'parcerias com arquitetos' },
      { id: 'parc_gestores',     chip: 'Gestores corporativos',    frase: 'parcerias com gestores corporativos' },
      { id: 'feiras_setor',      chip: 'Feiras setoriais',         frase: 'presença em feiras setoriais' },
      { id: 'portfolio_seo',     chip: 'Portfólio online',         frase: 'portfólio online indexado' },
      { id: 'press_setor',       chip: 'Press releases setoriais', frase: 'press releases setoriais' },
    ],
    meio: [
      { id: 'portfolio_tec',     chip: 'Portfólio técnico',        frase: 'apresentação de portfólio técnico' },
      { id: 'visita_canteiro',   chip: 'Visita ao canteiro',       frase: 'visita ao canteiro de obra semelhante' },
      { id: 'cases_doc',         chip: 'Cases documentados',       frase: 'cases documentados' },
      { id: 'depoimentos_b2b',   chip: 'Depoimentos B2B',          frase: 'depoimentos de clientes B2B' },
      { id: 'white_papers',      chip: 'White papers técnicos',    frase: 'white papers técnicos' },
      { id: 'webinars',          chip: 'Webinars setoriais',       frase: 'webinars setoriais' },
    ],
    fundo: [
      { id: 'cotacao_det',       chip: 'Cotação detalhada',        frase: 'cotação detalhada com prazo' },
      { id: 'prazo_garant',      chip: 'Prazo garantido',          frase: 'garantia contratual de prazo' },
      { id: 'garantia_tec',      chip: 'Garantia técnica',         frase: 'garantia técnica da obra' },
      { id: 'financ_loja',       chip: 'Financiamento via loja',   frase: 'financiamento via loja de insumos' },
      { id: 'visita_obra_atual', chip: 'Visita guiada à obra',     frase: 'visita guiada a uma obra em andamento' },
      { id: 'ref_anteriores',    chip: 'Referências anteriores',   frase: 'referências de obras anteriores' },
    ],
    fechamento: [
      { id: 'contrato_sla',      chip: 'Contrato com SLA',         frase: 'contrato com SLA de prazo' },
      { id: 'canteiro_30d',      chip: 'Canteiro em até 30 dias',  frase: 'canteiro montado em até 30 dias' },
      { id: 'entrega_prazo',     chip: 'Entrega no prazo',         frase: 'entrega no prazo prometido' },
      { id: 'pos_obra_garantia', chip: 'Pós-obra com garantia',    frase: 'pós-obra com garantia técnica' },
      { id: 'reunioes_prog',     chip: 'Reuniões de progresso',    frase: 'reuniões periódicas de progresso' },
      { id: 'relatorio_obra',    chip: 'Relatório de obra',        frase: 'relatório semanal de obra' },
    ],
    gargalo: [
      { id: 'ciclo_b2b_longo',   chip: 'Ciclo B2B longo',          frase: 'ciclo de venda B2B muito longo' },
      { id: 'competicao_preco',  chip: 'Competição por preço',     frase: 'pressão de competição por preço' },
      { id: 'decisao_comp',      chip: 'Decisão compartilhada',    frase: 'decisão compartilhada entre stakeholders' },
      { id: 'agenda_b2b',        chip: 'Dificuldade de agenda',    frase: 'dificuldade de marcar reunião com decisor' },
      { id: 'volume_propostas',  chip: 'Volume de propostas',      frase: 'baixo volume de propostas em pipeline' },
      { id: 'cotacao_contrato',  chip: 'Cotação → contrato',       frase: 'conversão de cotação para contrato' },
    ],
  },
  'geral': {
    topo: [
      { id: 'redes_sociais',     chip: 'Redes sociais',            frase: 'redes sociais' },
      { id: 'google_busca',      chip: 'Google e busca paga',      frase: 'Google e busca paga' },
      { id: 'indicacoes',        chip: 'Indicações',               frase: 'indicações de clientes' },
      { id: 'conteudo_org',      chip: 'Conteúdo orgânico',        frase: 'conteúdo orgânico' },
      { id: 'parcerias',         chip: 'Parcerias',                frase: 'parcerias estratégicas' },
      { id: 'eventos',           chip: 'Eventos',                  frase: 'eventos' },
    ],
    meio: [
      { id: 'conteudo_org_m',    chip: 'Conteúdo orgânico',        frase: 'conteúdo orgânico' },
      { id: 'otimiz_busca_m',    chip: 'Otimização de busca',      frase: 'otimização de busca' },
      { id: 'reviews_g',         chip: 'Reviews e depoimentos',    frase: 'reviews e depoimentos' },
      { id: 'depoimentos_g',     chip: 'Depoimentos de clientes',  frase: 'depoimentos de clientes' },
      { id: 'materiais_g',       chip: 'Materiais informativos',   frase: 'materiais informativos' },
      { id: 'prova_social_g',    chip: 'Prova social',             frase: 'prova social' },
    ],
    fundo: [
      { id: 'proposta_clara',    chip: 'Proposta clara',           frase: 'proposta clara' },
      { id: 'garantia_g',        chip: 'Garantia',                 frase: 'garantia' },
      { id: 'atend_personal',    chip: 'Atendimento personalizado', frase: 'atendimento personalizado' },
      { id: 'prova_social_g2',   chip: 'Prova social',             frase: 'prova social' },
      { id: 'pacotes_g',         chip: 'Pacotes e combos',         frase: 'pacotes e combos' },
      { id: 'pagamento_facil',   chip: 'Facilidade de pagamento',  frase: 'facilidade de pagamento' },
    ],
    fechamento: [
      { id: 'pos_venda_g',       chip: 'Atendimento pós-venda',    frase: 'atendimento pós-venda' },
      { id: 'fidelidade_g',      chip: 'Programa de fidelidade',   frase: 'programa de fidelidade' },
      { id: 'followup_g',        chip: 'Follow-up',                frase: 'follow-up' },
      { id: 'relacionamento_g',  chip: 'Relacionamento contínuo',  frase: 'relacionamento contínuo' },
      { id: 'garantia_ext_g',    chip: 'Garantia estendida',       frase: 'garantia estendida' },
      { id: 'satisfacao_g',      chip: 'Pesquisa de satisfação',   frase: 'pesquisa de satisfação' },
    ],
    gargalo: [
      { id: 'conversao_g',       chip: 'Conversão baixa',          frase: 'conversão abaixo da meta' },
      { id: 'ciclo_longo_g',     chip: 'Ciclo de venda longo',     frase: 'ciclo de venda longo' },
      { id: 'cac_g',             chip: 'CAC alto',                 frase: 'custo de aquisição alto' },
      { id: 'recompra_g',        chip: 'Recompra baixa',           frase: 'recompra baixa' },
      { id: 'ticket_g',          chip: 'Ticket médio baixo',       frase: 'ticket médio baixo' },
      { id: 'retencao_g',        chip: 'Retenção baixa',           frase: 'retenção baixa' },
    ],
  },
};

// Templates por etapa — slot {LISTA} recebe as características em prosa.
const TEMPLATE_FRASE_ETAPA = {
  topo:       'Atrai clientes por meio de {LISTA}.',
  meio:       'Engaja leads com {LISTA}.',
  fundo:      'Converte oportunidades com {LISTA}.',
  fechamento: 'Fecha vendas com {LISTA}.',
  gargalo:    'Principal gargalo: {LISTA}.',
};

// Resolve a frase de uma etapa do funil a partir das características selecionadas
const montarFraseEtapa = (setor, etapa, ids, extras, modo = 'EE') => {
  const fonte = modo === 'SU' ? CARACTERISTICAS_FUNIL_POR_SETOR_E_ETAPA_SU : CARACTERISTICAS_FUNIL_POR_SETOR_E_ETAPA;
  const porSetor = fonte[setor] || fonte['geral'];
  const catalogo = porSetor[etapa] || [];
  const partes = (ids || [])
    .map(id => catalogo.find(c => c.id === id)?.frase)
    .filter(Boolean)
    .concat((extras || []).map(e => (e || '').trim().toLowerCase()).filter(Boolean));
  if (partes.length === 0) return '';
  const tmpl = TEMPLATE_FRASE_ETAPA[etapa] || '{LISTA}.';
  return tmpl.replace('{LISTA}', juntarEmProsa(partes));
};

const RASCUNHO_TAREFA_8 = {
  shopmax: {
    pos_chars: ['preco_acessivel', 'curadoria', 'entrega_rapida', 'private_label'],
    pos_extras: [],
    tom_chars: ['aspiracional', 'acessivel', 'lifestyle', 'bom_senso_fin'],
    tom_extras: [],
    canais_mix: [
      { id: 'social_paid', peso: 35 },
      { id: 'google_ads',  peso: 25 },
      { id: 'seo',         peso: 15 },
      { id: 'email_crm',   peso: 15 },
      { id: 'marketplace', peso: 10 },
    ],
    funil_chars: {
      topo:       ['midia_paga_redes', 'ads_busca', 'conteudo_organico'],
      meio:       ['retargeting', 'email_whats', 'prova_social'],
      fundo:      ['pdp_otimizada', 'frete_gratis', 'parcelamento'],
      fechamento: ['checkout_otim', 'lembrete_carrinho', 'indicacao_pos'],
      gargalo:    ['carrinho_abandon', 'conversao_pdp'],
    },
    funil_extras: { topo: [], meio: [], fundo: [], fechamento: [], gargalo: [] },
    invest_mkt_respostas: { pct_receita: 'c' },
  },
  sabor_arte: {
    pos_chars: ['cozinha_autoral', 'ambiente_premiado', 'ingredientes_locais', 'narrativa_origem'],
    pos_extras: [],
    tom_chars: ['sofisticado', 'autoral', 'afetuoso', 'sem_rebusque'],
    tom_extras: [],
    canais_mix: [
      { id: 'social_org',   peso: 35 },
      { id: 'parcerias',    peso: 20 },
      { id: 'ponto_fisico', peso: 20 },
      { id: 'eventos',      peso: 15 },
      { id: 'email_crm',    peso: 10 },
    ],
    funil_chars: {
      topo:       ['conteudo_insta', 'imprensa_gastr'],
      meio:       ['reviews_guias', 'bastidor', 'curadoria_chef'],
      fundo:      ['reserva_direta', 'menu_degust', 'eventos_privados'],
      fechamento: ['recepcao_personal', 'experiencia_pres', 'pos_visita_email'],
      gargalo:    ['fora_pico', 'ocupacao_almoco'],
    },
    funil_extras: { topo: [], meio: [], fundo: [], fechamento: [], gargalo: [] },
    invest_mkt_respostas: { pct_receita: 'a' },
  },
  clinicavida: {
    pos_chars: ['multidisciplinar', 'telemed_integrada', 'prevencao_anual', 'coord_cuidado'],
    pos_extras: [],
    tom_chars: ['acolhedor', 'confianca', 'claro', 'direto_clinico'],
    tom_extras: [],
    canais_mix: [
      { id: 'email_crm',      peso: 30 },
      { id: 'parcerias',      peso: 25 },
      { id: 'social_org',     peso: 20 },
      { id: 'seo',            peso: 15 },
      { id: 'vendas_diretas', peso: 10 },
    ],
    funil_chars: {
      topo:       ['conteudo_educ', 'parcerias_conv', 'rhs_corp'],
      meio:       ['conteudo_redes', 'otimiz_busca', 'depoimentos_pac'],
      fundo:      ['agend_online', 'retorno_24h', 'telemed'],
      fechamento: ['consulta_efet', 'followup_auto', 'prevencao_ret'],
      gargalo:    ['reativ_pac'],
    },
    funil_extras: { topo: [], meio: [], fundo: [], fechamento: [], gargalo: [] },
    invest_mkt_respostas: { pct_receita: 'b' },
  },
  construmax: {
    pos_chars: ['engenheiro_presente', 'prazo_cumprido', 'foco_corporativo', 'loja_propria'],
    pos_extras: [],
    tom_chars: ['tecnico', 'direto', 'confiavel', 'foco_retorno'],
    tom_extras: [],
    canais_mix: [
      { id: 'vendas_diretas', peso: 50 },
      { id: 'parcerias',      peso: 25 },
      { id: 'eventos',        peso: 15 },
      { id: 'seo',            peso: 10 },
    ],
    funil_chars: {
      topo:       ['prospec_ativa', 'parc_arq', 'parc_gestores'],
      meio:       ['portfolio_tec', 'visita_canteiro'],
      fundo:      ['cotacao_det', 'prazo_garant', 'garantia_tec', 'financ_loja'],
      fechamento: ['contrato_sla', 'canteiro_30d'],
      gargalo:    ['ciclo_b2b_longo', 'decisao_comp'],
    },
    funil_extras: { topo: [], meio: [], fundo: [], fechamento: [], gargalo: [] },
    invest_mkt_respostas: { pct_receita: 'a' },
  },
};

// ═══════════════════════════════════════════════════════════════════════
// Catálogos SU (Startup) da T8. Estrutura idêntica ao EE (mesmos campos e
// chaves de setor). Selecionados em ConteudoTarefa8 quando modo === 'SU'.
// ═══════════════════════════════════════════════════════════════════════
const CARACTERISTICAS_POSICIONAMENTO_POR_SETOR_SU = {
  'E-commerce': [
    { id: 'curadoria_ia',       chip: 'Curadoria por IA',        frase: 'curadoria de produtos por inteligência artificial' },
    { id: 'modelo_d2c',         chip: 'Modelo D2C',              frase: 'venda direta ao consumidor (D2C)' },
    { id: 'checkout_selfserve', chip: 'Checkout self-serve',     frase: 'checkout self-serve em poucos cliques' },
    { id: 'logistica_sameday',  chip: 'Logística same-day',      frase: 'logística com entrega no mesmo dia' },
    { id: 'preco_dinamico',     chip: 'Preço dinâmico',          frase: 'precificação dinâmica orientada por dados' },
    { id: 'app_mobile_first',   chip: 'App mobile-first',        frase: 'experiência mobile-first no aplicativo' },
    { id: 'recomendacao_dados', chip: 'Recomendação por dados',  frase: 'recomendação personalizada por dados de navegação' },
    { id: 'assinatura_recorr',  chip: 'Assinatura recorrente',   frase: 'modelo de assinatura recorrente' },
    { id: 'marketplace_aberto', chip: 'Marketplace aberto',      frase: 'marketplace aberto a vendedores parceiros' },
    { id: 'sustentavel_tech',   chip: 'Sustentável e tech',      frase: 'operação sustentável apoiada por tecnologia' },
  ],
  'Bar/Restaurante': [
    { id: 'pedido_selfserve',   chip: 'Pedido self-serve',       frase: 'pedido self-serve pelo aplicativo' },
    { id: 'delivery_proprio',   chip: 'Delivery próprio',        frase: 'plataforma de delivery próprio' },
    { id: 'cozinha_dados',      chip: 'Cozinha orientada a dados', frase: 'cardápio otimizado por dados de vendas' },
    { id: 'clube_fidelidade',   chip: 'Clube de fidelidade',     frase: 'clube de fidelidade digital com pontos' },
    { id: 'ghost_kitchen',      chip: 'Ghost kitchen',           frase: 'operação em cozinha-fantasma escalável' },
    { id: 'comunidade_local',   chip: 'Comunidade local',        frase: 'comunidade local engajada nas redes' },
    { id: 'menu_qr',            chip: 'Menu por QR code',        frase: 'menu digital acessado por QR code' },
    { id: 'assinatura_refei',   chip: 'Assinatura de refeições', frase: 'assinatura recorrente de refeições' },
    { id: 'experiencia_insta',  chip: 'Experiência instagramável', frase: 'experiência instagramável e compartilhável' },
    { id: 'insumos_rastread',   chip: 'Insumos rastreáveis',     frase: 'insumos rastreáveis com origem transparente' },
  ],
  'Saúde': [
    { id: 'telemedicina',       chip: 'Telemedicina',            frase: 'atendimento por telemedicina' },
    { id: 'agenda_selfserve',   chip: 'Agenda self-serve',       frase: 'agendamento self-serve pelo aplicativo' },
    { id: 'dados_paciente',     chip: 'Dados do paciente',       frase: 'prontuário e dados do paciente centralizados' },
    { id: 'prevencao_ativa',    chip: 'Prevenção ativa',         frase: 'foco em prevenção ativa e acompanhamento' },
    { id: 'assinatura_saude',   chip: 'Assinatura de saúde',     frase: 'plano de assinatura de saúde recorrente' },
    { id: 'ia_triagem',         chip: 'IA de triagem',           frase: 'triagem inicial assistida por inteligência artificial' },
    { id: 'jornada_digital',    chip: 'Jornada digital',         frase: 'jornada de cuidado totalmente digital' },
    { id: 'rede_credenciada',   chip: 'Rede credenciada',        frase: 'rede credenciada integrada por API' },
    { id: 'wearables_conect',   chip: 'Wearables conectados',    frase: 'integração com wearables e dispositivos conectados' },
    { id: 'privacidade_lgpd',   chip: 'Privacidade e LGPD',      frase: 'privacidade e conformidade com a LGPD' },
  ],
  'Construção Civil': [
    { id: 'plataforma_obra',    chip: 'Plataforma de obra',      frase: 'plataforma digital de gestão de obra' },
    { id: 'orcamento_online',   chip: 'Orçamento online',        frase: 'orçamento online self-serve' },
    { id: 'bim_integrado',      chip: 'BIM integrado',           frase: 'modelagem BIM integrada ao fluxo' },
    { id: 'marketplace_forn',   chip: 'Marketplace de fornecedores', frase: 'marketplace de fornecedores e prestadores' },
    { id: 'dados_produtiv',     chip: 'Dados de produtividade',  frase: 'gestão orientada por dados de produtividade' },
    { id: 'construtech_modular', chip: 'Construtech modular',    frase: 'solução construtech de construção modular' },
    { id: 'transparencia_custo', chip: 'Transparência de custo', frase: 'transparência total de custo ao cliente' },
    { id: 'acompanha_realtime', chip: 'Acompanhamento em tempo real', frase: 'acompanhamento da obra em tempo real' },
    { id: 'sustentavel_verde',  chip: 'Sustentável e verde',     frase: 'construção sustentável com foco verde' },
    { id: 'suporte_tecnico',    chip: 'Suporte técnico próximo', frase: 'suporte técnico próximo ao construtor' },
  ],
  'geral': [
    { id: 'tecnologia_propria', chip: 'Tecnologia própria',      frase: 'tecnologia própria como diferencial' },
    { id: 'self_serve',         chip: 'Self-serve',              frase: 'experiência self-serve do início ao fim' },
    { id: 'escalavel',          chip: 'Escalável',               frase: 'modelo escalável de baixo custo marginal' },
    { id: 'comunidade',         chip: 'Comunidade',              frase: 'comunidade engajada em torno do produto' },
    { id: 'dados_personaliz',   chip: 'Dados e personalização',  frase: 'dados e personalização da experiência' },
    { id: 'velocidade',         chip: 'Velocidade',              frase: 'velocidade de entrega e iteração' },
    { id: 'freemium_acessivel', chip: 'Freemium/acessível',      frase: 'modelo freemium e acessível' },
    { id: 'foco_early_adopter', chip: 'Foco no early adopter',   frase: 'foco no early adopter' },
    { id: 'integracoes',        chip: 'Integrações',             frase: 'integrações com outras ferramentas' },
    { id: 'suporte_proximo',    chip: 'Suporte próximo',         frase: 'suporte próximo e humano ao cliente' },
  ],
};

const CARACTERISTICAS_TOM_POR_SETOR_SU = {
  'E-commerce': [
    { id: 'direto_compra',      chip: 'Direto',                  frase: 'direto ao ponto na jornada de compra' },
    { id: 'aspiracional',       chip: 'Aspiracional',            frase: 'aspiracional e desejável' },
    { id: 'proximo_cliente',    chip: 'Próximo',                 frase: 'próximo do cliente nas redes' },
    { id: 'urgencia_saudavel',  chip: 'Urgência saudável',       frase: 'com senso de urgência saudável' },
    { id: 'transparente_preco', chip: 'Transparente',            frase: 'transparente sobre preço e prazo' },
    { id: 'data_driven',        chip: 'Data-driven',             frase: 'objetivo e orientado por dados' },
    { id: 'divertido',          chip: 'Divertido',               frase: 'leve e divertido' },
    { id: 'inspirador_lifestyle', chip: 'Inspirador',            frase: 'inspirador em torno de um lifestyle' },
    { id: 'educativo_produto',  chip: 'Educativo',               frase: 'educativo sobre o uso do produto' },
    { id: 'confiavel_pos_venda', chip: 'Confiável',              frase: 'confiável no pós-venda' },
  ],
  'Bar/Restaurante': [
    { id: 'caloroso',           chip: 'Caloroso',                frase: 'caloroso e acolhedor' },
    { id: 'apetite',            chip: 'Apetitoso',               frase: 'que desperta o apetite' },
    { id: 'proximo_comunidade', chip: 'Próximo',                 frase: 'próximo da comunidade local' },
    { id: 'descontraido',       chip: 'Descontraído',            frase: 'descontraído e bem-humorado' },
    { id: 'autentico',          chip: 'Autêntico',               frase: 'autêntico sobre a origem dos pratos' },
    { id: 'transparente_insumo', chip: 'Transparente',           frase: 'transparente sobre insumos e preparo' },
    { id: 'convidativo',        chip: 'Convidativo',             frase: 'convidativo à experiência presencial' },
    { id: 'jovem',              chip: 'Jovem',                   frase: 'jovem e conectado às redes' },
    { id: 'educativo_gastro',   chip: 'Educativo',               frase: 'educativo sobre gastronomia' },
    { id: 'confiavel_higiene',  chip: 'Confiável',               frase: 'confiável em higiene e qualidade' },
  ],
  'Saúde': [
    { id: 'acolhedor',          chip: 'Acolhedor',               frase: 'acolhedor e humano' },
    { id: 'claro_acessivel',    chip: 'Técnico acessível',       frase: 'técnico, porém acessível ao paciente' },
    { id: 'proximo_cuidado',    chip: 'Próximo',                 frase: 'próximo em cada etapa do cuidado' },
    { id: 'confiavel_clinico',  chip: 'Confiável',               frase: 'confiável e clinicamente embasado' },
    { id: 'transparente_conduta', chip: 'Transparente',          frase: 'transparente sobre conduta e custos' },
    { id: 'empatico',           chip: 'Empático',                frase: 'empático com o momento do paciente' },
    { id: 'objetivo_dados',     chip: 'Objetivo',                frase: 'objetivo e apoiado em dados de saúde' },
    { id: 'inspirador_bemestar', chip: 'Inspirador',             frase: 'inspirador em torno do bem-estar' },
    { id: 'educativo_saude',    chip: 'Educativo',               frase: 'educativo sobre prevenção e saúde' },
    { id: 'seguro_privado',     chip: 'Seguro',                  frase: 'seguro e cuidadoso com dados sensíveis' },
  ],
  'Construção Civil': [
    { id: 'direto_tecnico',     chip: 'Direto',                  frase: 'direto e técnico' },
    { id: 'confiavel_prazo',    chip: 'Confiável',               frase: 'confiável em prazo e execução' },
    { id: 'proximo_obra',       chip: 'Próximo',                 frase: 'próximo do canteiro de obras' },
    { id: 'transparente_custo', chip: 'Transparente',            frase: 'transparente sobre custo e cronograma' },
    { id: 'tecnico_acessivel',  chip: 'Técnico acessível',       frase: 'técnico acessível a quem não é engenheiro' },
    { id: 'objetivo_produtiv',  chip: 'Objetivo',                frase: 'objetivo e orientado por produtividade' },
    { id: 'inovador',           chip: 'Inovador',                frase: 'inovador para um setor tradicional' },
    { id: 'solido',             chip: 'Sólido',                  frase: 'sólido e seguro' },
    { id: 'educativo_construir', chip: 'Educativo',              frase: 'educativo sobre o processo de construir' },
    { id: 'parceiro',           chip: 'Parceiro',                frase: 'parceiro do construtor de ponta a ponta' },
  ],
  'geral': [
    { id: 'direto',             chip: 'Direto',                  frase: 'direto ao ponto' },
    { id: 'proximo',            chip: 'Próximo',                 frase: 'próximo e humano' },
    { id: 'tecnico_acessivel',  chip: 'Técnico acessível',       frase: 'técnico, porém acessível' },
    { id: 'ousado',             chip: 'Ousado',                  frase: 'ousado e disruptivo' },
    { id: 'transparente',       chip: 'Transparente',            frase: 'transparente com o cliente' },
    { id: 'objetivo_data',      chip: 'Objetivo (data-driven)',  frase: 'objetivo e orientado por dados' },
    { id: 'inspirador',         chip: 'Inspirador',              frase: 'inspirador em torno da missão' },
    { id: 'bem_humorado',       chip: 'Bem-humorado',            frase: 'bem-humorado e leve' },
    { id: 'educativo',          chip: 'Educativo',               frase: 'educativo sobre o problema resolvido' },
    { id: 'confiavel',          chip: 'Confiável',               frase: 'confiável e consistente' },
  ],
};

const CARACTERISTICAS_FUNIL_POR_SETOR_E_ETAPA_SU = {
  'E-commerce': {
    topo: [
      { id: 'ecom_topo_social_paid',  chip: 'Mídia paga social',      frase: 'aquisição via mídia paga em redes sociais' },
      { id: 'ecom_topo_conteudo',     chip: 'Conteúdo e SEO',         frase: 'conteúdo e SEO de topo de funil' },
      { id: 'ecom_topo_influencers',  chip: 'Influenciadores',        frase: 'influenciadores de nicho' },
      { id: 'ecom_topo_marketplace',  chip: 'Marketplaces',           frase: 'presença em marketplaces' },
      { id: 'ecom_topo_referral',     chip: 'Indique e ganhe',        frase: 'programa de indique e ganhe' },
    ],
    meio: [
      { id: 'ecom_meio_onboarding',   chip: 'Onboarding',             frase: 'onboarding do primeiro pedido' },
      { id: 'ecom_meio_email_crm',    chip: 'E-mail e CRM',           frase: 'fluxos de e-mail e CRM' },
      { id: 'ecom_meio_retargeting',  chip: 'Retargeting',            frase: 'retargeting de carrinho abandonado' },
      { id: 'ecom_meio_prova_social', chip: 'Prova social',           frase: 'prova social por avaliações' },
      { id: 'ecom_meio_wishlist',     chip: 'Lista de desejos',       frase: 'engajamento por lista de desejos' },
    ],
    fundo: [
      { id: 'ecom_fundo_cupom',       chip: 'Cupom de primeira',      frase: 'cupom de primeira compra' },
      { id: 'ecom_fundo_frete_gratis', chip: 'Frete grátis',          frase: 'gatilho de frete grátis' },
      { id: 'ecom_fundo_review',      chip: 'Avaliações',             frase: 'avaliações e casos de uso' },
      { id: 'ecom_fundo_comparador',  chip: 'Comparador',             frase: 'comparador de produtos' },
      { id: 'ecom_fundo_garantia',    chip: 'Garantia',               frase: 'garantia e política de troca' },
    ],
    fechamento: [
      { id: 'ecom_fech_checkout',     chip: 'Checkout rápido',        frase: 'conversão por checkout rápido' },
      { id: 'ecom_fech_pix',          chip: 'Pix e parcelamento',     frase: 'Pix e parcelamento no fechamento' },
      { id: 'ecom_fech_upsell',       chip: 'Upsell e cross-sell',    frase: 'upsell e cross-sell no carrinho' },
      { id: 'ecom_fech_assinatura',   chip: 'Assinatura',             frase: 'oferta de assinatura recorrente' },
      { id: 'ecom_fech_recorrencia',  chip: 'Recompra',               frase: 'gatilho de recompra' },
    ],
    gargalo: [
      { id: 'ecom_garg_cac_alto',     chip: 'CAC alto',               frase: 'custo de aquisição elevado' },
      { id: 'ecom_garg_carrinho',     chip: 'Abandono de carrinho',   frase: 'abandono de carrinho' },
      { id: 'ecom_garg_recompra',     chip: 'Baixa recompra',         frase: 'baixa taxa de recompra' },
      { id: 'ecom_garg_frete',        chip: 'Custo de frete',         frase: 'custo de frete como barreira' },
      { id: 'ecom_garg_conversao',    chip: 'Conversão baixa',        frase: 'conversão baixa no checkout' },
    ],
  },
  'Bar/Restaurante': {
    topo: [
      { id: 'bar_topo_social_org',    chip: 'Redes orgânicas',        frase: 'aquisição por redes sociais orgânicas' },
      { id: 'bar_topo_influencers',   chip: 'Influenciadores food',   frase: 'influenciadores de gastronomia' },
      { id: 'bar_topo_comunidade',    chip: 'Comunidade local',       frase: 'comunidade local nas redes' },
      { id: 'bar_topo_geo_ads',       chip: 'Anúncios geolocalizados', frase: 'anúncios geolocalizados no entorno' },
      { id: 'bar_topo_maps',          chip: 'Google Maps',            frase: 'presença no Google Maps' },
    ],
    meio: [
      { id: 'bar_meio_fidelidade',    chip: 'Clube de fidelidade',    frase: 'clube de fidelidade digital' },
      { id: 'bar_meio_email',         chip: 'E-mail e WhatsApp',      frase: 'fluxos de e-mail e WhatsApp' },
      { id: 'bar_meio_retargeting',   chip: 'Retargeting',            frase: 'retargeting de quem visitou o cardápio' },
      { id: 'bar_meio_prova_social',  chip: 'Prova social',           frase: 'prova social por reviews' },
      { id: 'bar_meio_menu_qr',       chip: 'Menu por QR',            frase: 'engajamento pelo menu em QR code' },
    ],
    fundo: [
      { id: 'bar_fundo_cupom',        chip: 'Cupom de estreia',       frase: 'cupom de primeira visita' },
      { id: 'bar_fundo_combo',        chip: 'Combo promocional',      frase: 'combo promocional de experimentação' },
      { id: 'bar_fundo_review',       chip: 'Avaliações',             frase: 'avaliações e fotos de clientes' },
      { id: 'bar_fundo_degustacao',   chip: 'Degustação',             frase: 'degustação e evento de estreia' },
      { id: 'bar_fundo_reserva',      chip: 'Reserva online',         frase: 'reserva online self-serve' },
    ],
    fechamento: [
      { id: 'bar_fech_pedido_app',    chip: 'Pedido no app',          frase: 'conversão por pedido no aplicativo' },
      { id: 'bar_fech_delivery',      chip: 'Delivery próprio',       frase: 'fechamento por delivery próprio' },
      { id: 'bar_fech_assinatura',    chip: 'Assinatura de refeições', frase: 'assinatura recorrente de refeições' },
      { id: 'bar_fech_pix',           chip: 'Pix na mesa',            frase: 'pagamento por Pix na mesa' },
      { id: 'bar_fech_upsell',        chip: 'Upsell de combo',        frase: 'upsell de combos e adicionais' },
    ],
    gargalo: [
      { id: 'bar_garg_cac_alto',      chip: 'CAC alto',               frase: 'custo de aquisição elevado' },
      { id: 'bar_garg_recorrencia',   chip: 'Baixa recorrência',      frase: 'baixa recorrência de clientes' },
      { id: 'bar_garg_ticket',        chip: 'Ticket médio baixo',     frase: 'ticket médio baixo' },
      { id: 'bar_garg_sazonalidade',  chip: 'Sazonalidade',           frase: 'sazonalidade de demanda' },
      { id: 'bar_garg_comissao_app',  chip: 'Comissão de app',        frase: 'comissão alta de apps de delivery' },
    ],
  },
  'Saúde': {
    topo: [
      { id: 'sau_topo_seo',           chip: 'SEO e conteúdo',         frase: 'aquisição por SEO e conteúdo de saúde' },
      { id: 'sau_topo_social_paid',   chip: 'Mídia paga',             frase: 'mídia paga segmentada' },
      { id: 'sau_topo_parcerias',     chip: 'Parcerias',              frase: 'parcerias com empresas e clínicas' },
      { id: 'sau_topo_indicacao',     chip: 'Indicação médica',       frase: 'indicação de profissionais de saúde' },
      { id: 'sau_topo_comunidade',    chip: 'Comunidade',             frase: 'comunidade de pacientes engajada' },
    ],
    meio: [
      { id: 'sau_meio_onboarding',    chip: 'Onboarding digital',     frase: 'onboarding digital do paciente' },
      { id: 'sau_meio_email',         chip: 'E-mail e lembretes',     frase: 'e-mails e lembretes de cuidado' },
      { id: 'sau_meio_conteudo',      chip: 'Conteúdo educativo',     frase: 'conteúdo educativo de relacionamento' },
      { id: 'sau_meio_prova_social',  chip: 'Prova social',           frase: 'prova social de pacientes' },
      { id: 'sau_meio_retargeting',   chip: 'Retargeting',            frase: 'retargeting de quem iniciou agendamento' },
    ],
    fundo: [
      { id: 'sau_fundo_primeira',     chip: 'Primeira consulta',      frase: 'oferta de primeira consulta' },
      { id: 'sau_fundo_teleconsulta', chip: 'Teleconsulta',           frase: 'teleconsulta de baixo atrito' },
      { id: 'sau_fundo_casos',        chip: 'Casos de sucesso',       frase: 'casos de sucesso de pacientes' },
      { id: 'sau_fundo_plano',        chip: 'Plano de saúde',         frase: 'plano de assinatura de saúde' },
      { id: 'sau_fundo_avaliacao',    chip: 'Avaliação inicial',      frase: 'avaliação inicial gratuita' },
    ],
    fechamento: [
      { id: 'sau_fech_agenda',        chip: 'Agendamento self-serve', frase: 'conversão por agendamento self-serve' },
      { id: 'sau_fech_assinatura',    chip: 'Assinatura',             frase: 'fechamento por plano de assinatura' },
      { id: 'sau_fech_pix',           chip: 'Pix e parcelamento',     frase: 'Pix e parcelamento no fechamento' },
      { id: 'sau_fech_onboard_pago',  chip: 'Onboarding pago',        frase: 'onboarding pago guiado' },
      { id: 'sau_fech_recorrencia',   chip: 'Retorno programado',     frase: 'retorno programado recorrente' },
    ],
    gargalo: [
      { id: 'sau_garg_cac_alto',      chip: 'CAC alto',               frase: 'custo de aquisição elevado' },
      { id: 'sau_garg_ativacao',      chip: 'Ativação baixa',         frase: 'baixa ativação após o cadastro' },
      { id: 'sau_garg_churn',         chip: 'Churn',                  frase: 'churn de assinantes' },
      { id: 'sau_garg_confianca',     chip: 'Barreira de confiança',  frase: 'barreira de confiança no digital' },
      { id: 'sau_garg_regulacao',     chip: 'Regulação',              frase: 'restrições de regulação e publicidade' },
    ],
  },
  'Construção Civil': {
    topo: [
      { id: 'con_topo_vendas_dir',    chip: 'Vendas diretas',         frase: 'aquisição por vendas diretas' },
      { id: 'con_topo_seo',           chip: 'SEO e conteúdo',         frase: 'conteúdo técnico e SEO' },
      { id: 'con_topo_eventos_tech',  chip: 'Eventos do setor',       frase: 'eventos e feiras de construtech' },
      { id: 'con_topo_parcerias',     chip: 'Parcerias',              frase: 'parcerias com incorporadoras' },
      { id: 'con_topo_linkedin',      chip: 'LinkedIn ads',           frase: 'mídia paga no LinkedIn' },
    ],
    meio: [
      { id: 'con_meio_onboarding',    chip: 'Onboarding',             frase: 'onboarding da plataforma de obra' },
      { id: 'con_meio_email',         chip: 'E-mail e CRM',           frase: 'fluxos de e-mail e CRM' },
      { id: 'con_meio_webinar',       chip: 'Webinars',               frase: 'webinars técnicos de relacionamento' },
      { id: 'con_meio_prova_social',  chip: 'Prova social',           frase: 'prova social de obras entregues' },
      { id: 'con_meio_retargeting',   chip: 'Retargeting',            frase: 'retargeting de quem pediu orçamento' },
    ],
    fundo: [
      { id: 'con_fundo_orcamento',    chip: 'Orçamento online',       frase: 'orçamento online self-serve' },
      { id: 'con_fundo_demo',         chip: 'Demonstração',           frase: 'demonstração guiada da plataforma' },
      { id: 'con_fundo_piloto',       chip: 'Obra piloto',            frase: 'projeto piloto em uma obra' },
      { id: 'con_fundo_casos',        chip: 'Casos de obra',          frase: 'casos de obra entregue' },
      { id: 'con_fundo_roi',          chip: 'Cálculo de ROI',         frase: 'cálculo de retorno do investimento' },
    ],
    fechamento: [
      { id: 'con_fech_proposta',      chip: 'Proposta comercial',     frase: 'conversão por proposta comercial' },
      { id: 'con_fech_contrato',      chip: 'Contrato digital',       frase: 'fechamento por contrato digital' },
      { id: 'con_fech_onboard_pago',  chip: 'Onboarding pago',        frase: 'onboarding pago da plataforma' },
      { id: 'con_fech_upsell',        chip: 'Upsell de módulos',      frase: 'upsell de módulos adicionais' },
      { id: 'con_fech_assinatura',    chip: 'Assinatura',             frase: 'assinatura recorrente da plataforma' },
    ],
    gargalo: [
      { id: 'con_garg_ciclo_longo',   chip: 'Ciclo longo',            frase: 'ciclo de venda longo' },
      { id: 'con_garg_cac_alto',      chip: 'CAC alto',               frase: 'custo de aquisição elevado' },
      { id: 'con_garg_ativacao',      chip: 'Ativação baixa',         frase: 'baixa ativação na plataforma' },
      { id: 'con_garg_resistencia',   chip: 'Resistência a mudança',  frase: 'resistência à mudança do setor' },
      { id: 'con_garg_trial_pago',    chip: 'Trial → pago',           frase: 'conversão de trial para pago' },
    ],
  },
  'geral': {
    topo: [
      { id: 'ger_topo_social_paid',   chip: 'Mídia paga',             frase: 'aquisição por mídia paga' },
      { id: 'ger_topo_conteudo',      chip: 'Conteúdo',               frase: 'marketing de conteúdo' },
      { id: 'ger_topo_plg',           chip: 'Product-led',            frase: 'crescimento product-led (PLG)' },
      { id: 'ger_topo_comunidade',    chip: 'Comunidade',             frase: 'construção de comunidade' },
      { id: 'ger_topo_influencers',   chip: 'Influenciadores',        frase: 'parcerias com influenciadores' },
    ],
    meio: [
      { id: 'ger_meio_onboarding',    chip: 'Onboarding',             frase: 'ativação por onboarding' },
      { id: 'ger_meio_email_crm',     chip: 'E-mail e CRM',           frase: 'fluxos de e-mail e CRM' },
      { id: 'ger_meio_retargeting',   chip: 'Retargeting',            frase: 'retargeting de visitantes' },
      { id: 'ger_meio_prova_social',  chip: 'Prova social',           frase: 'prova social e depoimentos' },
      { id: 'ger_meio_conteudo',      chip: 'Conteúdo de relacionamento', frase: 'conteúdo de relacionamento' },
    ],
    fundo: [
      { id: 'ger_fundo_trial',        chip: 'Trial',                  frase: 'trial gratuito' },
      { id: 'ger_fundo_demo',         chip: 'Demonstração',           frase: 'demonstração do produto' },
      { id: 'ger_fundo_freemium',     chip: 'Freemium',               frase: 'plano freemium de entrada' },
      { id: 'ger_fundo_casos',        chip: 'Casos de uso',           frase: 'casos de uso e cases' },
      { id: 'ger_fundo_roi',          chip: 'Cálculo de ROI',         frase: 'cálculo de retorno do investimento' },
    ],
    fechamento: [
      { id: 'ger_fech_checkout',      chip: 'Checkout',               frase: 'conversão por checkout self-serve' },
      { id: 'ger_fech_upgrade',       chip: 'Upgrade',                frase: 'upgrade de plano gratuito para pago' },
      { id: 'ger_fech_onboard_pago',  chip: 'Onboarding pago',        frase: 'onboarding pago guiado' },
      { id: 'ger_fech_assinatura',    chip: 'Assinatura',             frase: 'fechamento por assinatura recorrente' },
      { id: 'ger_fech_upsell',        chip: 'Upsell',                 frase: 'upsell e expansão de conta' },
    ],
    gargalo: [
      { id: 'ger_garg_cac_alto',      chip: 'CAC alto',               frase: 'custo de aquisição elevado' },
      { id: 'ger_garg_churn',         chip: 'Churn',                  frase: 'churn de clientes' },
      { id: 'ger_garg_ativacao',      chip: 'Ativação baixa',         frase: 'baixa ativação de novos usuários' },
      { id: 'ger_garg_trial_pago',    chip: 'Trial → pago',           frase: 'conversão de trial para pago' },
      { id: 'ger_garg_conversao',     chip: 'Conversão baixa',        frase: 'conversão baixa ao longo do funil' },
    ],
  },
};

const METRICAS_FUNIL_SU = {
  topo:       { campo: 'm_visitantes', label: 'Visitantes únicos/mês' },
  meio:       { campo: 'm_cadastros',  label: 'Cadastros/mês' },
  fundo:      { campo: 'm_demos',      label: 'Demonstrações marcadas/mês' },
  fechamento: { campo: 'm_pagantes',   label: 'Clientes pagantes novos/mês' },
};

const PERGUNTAS_INVEST_MKT_SU = [
  {
    id: 'orcamento_mkt',
    label: 'Como você define o orçamento de marketing?',
    opcoes: [
      { id: 'a', label: 'Percentual fixo do capital captado' },
      { id: 'b', label: 'Valor mensal fixo em reais' },
      { id: 'c', label: 'Custo de aquisição alvo guiando o gasto' },
    ],
  },
  {
    id: 'time_crescimento',
    label: 'Como é composto o time de crescimento?',
    opcoes: [
      { id: 'a', label: 'Time interno dedicado' },
      { id: 'b', label: 'Agência externa' },
      { id: 'c', label: 'Modelo híbrido' },
    ],
  },
];

const RASCUNHO_TAREFA_8_SU = {
  shopmax: {
    pos_chars: ['curadoria_ia', 'modelo_d2c', 'checkout_selfserve', 'recomendacao_dados'],
    pos_extras: [],
    tom_chars: ['aspiracional', 'data_driven', 'inspirador_lifestyle', 'proximo_cliente'],
    tom_extras: [],
    canais_mix: [
      { id: 'social_paid', peso: 35 },
      { id: 'plg', peso: 20 },
      { id: 'influencers', peso: 20 },
      { id: 'google_ads', peso: 15 },
      { id: 'email_crm', peso: 10 },
    ],
    funil_chars: {
      topo: ['ecom_topo_social_paid', 'ecom_topo_influencers', 'ecom_topo_referral'],
      meio: ['ecom_meio_retargeting', 'ecom_meio_email_crm'],
      fundo: ['ecom_fundo_cupom', 'ecom_fundo_frete_gratis'],
      fechamento: ['ecom_fech_checkout', 'ecom_fech_upsell'],
      gargalo: ['ecom_garg_cac_alto', 'ecom_garg_carrinho'],
    },
    funil_extras: { topo: [], meio: [], fundo: [], fechamento: [], gargalo: [] },
    funil_metricas: { m_visitantes: '8000', m_cadastros: '2200', m_demos: '', m_pagantes: '340' },
    invest_mkt_respostas: { orcamento_mkt: 'c', time_crescimento: 'c' },
  },
  sabor_arte: {
    pos_chars: ['pedido_selfserve', 'comunidade_local', 'clube_fidelidade', 'experiencia_insta'],
    pos_extras: [],
    tom_chars: ['caloroso', 'jovem', 'descontraido', 'proximo_comunidade'],
    tom_extras: [],
    canais_mix: [
      { id: 'social_org', peso: 35 },
      { id: 'comunidade', peso: 25 },
      { id: 'referral', peso: 20 },
      { id: 'influencers', peso: 12 },
      { id: 'email_crm', peso: 8 },
    ],
    funil_chars: {
      topo: ['bar_topo_social_org', 'bar_topo_comunidade', 'bar_topo_influencers'],
      meio: ['bar_meio_fidelidade', 'bar_meio_prova_social'],
      fundo: ['bar_fundo_cupom', 'bar_fundo_combo'],
      fechamento: ['bar_fech_pedido_app', 'bar_fech_delivery'],
      gargalo: ['bar_garg_recorrencia', 'bar_garg_ticket'],
    },
    funil_extras: { topo: [], meio: [], fundo: [], fechamento: [], gargalo: [] },
    funil_metricas: { m_visitantes: '3000', m_cadastros: '900', m_demos: '', m_pagantes: '180' },
    invest_mkt_respostas: { orcamento_mkt: 'b', time_crescimento: 'a' },
  },
  clinicavida: {
    pos_chars: ['telemedicina', 'agenda_selfserve', 'dados_paciente', 'privacidade_lgpd'],
    pos_extras: [],
    tom_chars: ['acolhedor', 'claro_acessivel', 'confiavel_clinico', 'seguro_privado'],
    tom_extras: [],
    canais_mix: [
      { id: 'seo', peso: 35 },
      { id: 'parcerias', peso: 25 },
      { id: 'plg', peso: 18 },
      { id: 'social_paid', peso: 14 },
      { id: 'email_crm', peso: 8 },
    ],
    funil_chars: {
      topo: ['sau_topo_seo', 'sau_topo_parcerias', 'sau_topo_indicacao'],
      meio: ['sau_meio_onboarding', 'sau_meio_email'],
      fundo: ['sau_fundo_primeira', 'sau_fundo_teleconsulta'],
      fechamento: ['sau_fech_agenda', 'sau_fech_assinatura'],
      gargalo: ['sau_garg_ativacao', 'sau_garg_churn'],
    },
    funil_extras: { topo: [], meio: [], fundo: [], fechamento: [], gargalo: [] },
    funil_metricas: { m_visitantes: '2500', m_cadastros: '600', m_demos: '', m_pagantes: '120' },
    invest_mkt_respostas: { orcamento_mkt: 'c', time_crescimento: 'c' },
  },
  construmax: {
    pos_chars: ['plataforma_obra', 'orcamento_online', 'acompanha_realtime', 'transparencia_custo'],
    pos_extras: [],
    tom_chars: ['direto_tecnico', 'confiavel_prazo', 'objetivo_produtiv', 'parceiro'],
    tom_extras: [],
    canais_mix: [
      { id: 'vendas_diretas', peso: 35 },
      { id: 'parcerias', peso: 25 },
      { id: 'eventos_tech', peso: 20 },
      { id: 'seo', peso: 12 },
      { id: 'email_crm', peso: 8 },
    ],
    funil_chars: {
      topo: ['con_topo_vendas_dir', 'con_topo_parcerias', 'con_topo_eventos_tech'],
      meio: ['con_meio_onboarding', 'con_meio_prova_social'],
      fundo: ['con_fundo_orcamento', 'con_fundo_demo'],
      fechamento: ['con_fech_proposta', 'con_fech_contrato'],
      gargalo: ['con_garg_ciclo_longo', 'con_garg_trial_pago'],
    },
    funil_extras: { topo: [], meio: [], fundo: [], fechamento: [], gargalo: [] },
    funil_metricas: { m_visitantes: '1200', m_cadastros: '300', m_demos: '45', m_pagantes: '20' },
    invest_mkt_respostas: { orcamento_mkt: 'b', time_crescimento: 'b' },
  },
};

const ConteudoTarefa8 = ({ marketingVendas, setMarketingVendas, empresa, modo = 'EE' }) => {
  const rascunho = (modo === 'SU' ? RASCUNHO_TAREFA_8_SU : RASCUNHO_TAREFA_8)[empresa?.id] || {
    pos_chars: [], pos_extras: [],
    tom_chars: [], tom_extras: [],
    canais_mix: [],
    funil_chars: { topo: [], meio: [], fundo: [], fechamento: [], gargalo: [] },
    funil_extras: { topo: [], meio: [], fundo: [], fechamento: [], gargalo: [] },
    funil_metricas: {},
    invest_mkt_respostas: {},
  };
  const obter = (campo) => marketingVendas?.[campo] ?? rascunho[campo];
  const pos_chars            = obter('pos_chars') || [];
  const pos_extras           = obter('pos_extras') || [];
  const pos_livre            = obter('pos_livre');
  const pos_editou_livre     = obter('pos_editou_livre') || false;
  const tom_chars            = obter('tom_chars') || [];
  const tom_extras           = obter('tom_extras') || [];
  const tom_livre            = obter('tom_livre');
  const tom_editou_livre     = obter('tom_editou_livre') || false;
  const canais_mix           = obter('canais_mix') || [];
  const funil_chars          = obter('funil_chars') || {};
  const funil_extras         = obter('funil_extras') || {};
  const funil_livre          = obter('funil_livre') || {};
  const funil_editou_livre   = obter('funil_editou_livre') || {};
  const invest_mkt_respostas = obter('invest_mkt_respostas') || {};
  const funil_metricas       = obter('funil_metricas') || {};

  // Setor da empresa (cai em "geral" se não houver mapeamento)
  const setor = empresa?.setor || 'geral';
  const fonteCanais = modo === 'SU' ? CANAIS_AQUISICAO_SU : CANAIS_AQUISICAO;
  const fonteInvest = modo === 'SU' ? PERGUNTAS_INVEST_MKT_SU : PERGUNTAS_INVEST_MKT;
  const fontePos    = modo === 'SU' ? CARACTERISTICAS_POSICIONAMENTO_POR_SETOR_SU : CARACTERISTICAS_POSICIONAMENTO_POR_SETOR;
  const fonteTom    = modo === 'SU' ? CARACTERISTICAS_TOM_POR_SETOR_SU : CARACTERISTICAS_TOM_POR_SETOR;
  const fonteFunil  = modo === 'SU' ? CARACTERISTICAS_FUNIL_POR_SETOR_E_ETAPA_SU : CARACTERISTICAS_FUNIL_POR_SETOR_E_ETAPA;
  const catalogoPos = fontePos[setor] || fontePos['geral'];
  const catalogoTom = fonteTom[setor] || fonteTom['geral'];
  const catalogoFunilSetor = fonteFunil[setor] || fonteFunil['geral'];

  // Frase efetivamente exibida e compilada:
  // - se o empresário editou livremente, usa a frase livre
  // - caso contrário, recompõe a partir dos chips selecionados (+ extras)
  const frasePosicionamento = pos_editou_livre && pos_livre
    ? pos_livre
    : montarFrasePosicionamento(setor, pos_chars, pos_extras, modo);
  const fraseTom = tom_editou_livre && tom_livre
    ? tom_livre
    : montarFraseTom(setor, tom_chars, tom_extras, modo);
  const fraseEtapa = (etapaId) => (funil_editou_livre?.[etapaId] && funil_livre?.[etapaId])
    ? funil_livre[etapaId]
    : montarFraseEtapa(setor, etapaId, funil_chars[etapaId] || [], funil_extras[etapaId] || [], modo);

  const [perguntaAberta, setPerguntaAberta] = useState(null);
  const [novoChipPos, setNovoChipPos] = useState('');
  const [novoChipTom, setNovoChipTom] = useState('');
  const [novoChipFunil, setNovoChipFunil] = useState({ topo: '', meio: '', fundo: '', fechamento: '', gargalo: '' });
  const [graficosT8B2Aberto, setGraficosT8B2Aberto] = useState(false);
  const [graficosT8B3Aberto, setGraficosT8B3Aberto] = useState(false);

  const atualizar = (campo, valor) => {
    setMarketingVendas(prev => ({
      pos_chars, pos_extras, pos_livre, pos_editou_livre,
      tom_chars, tom_extras, tom_livre, tom_editou_livre,
      canais_mix,
      funil_chars, funil_extras, funil_livre, funil_editou_livre,
      funil_metricas,
      invest_mkt_respostas,
      ...prev,
      [campo]: valor,
    }));
  };
  // Métrica inline por etapa do funil (SU) · guarda em funil_metricas
  const atualizarMetricaFunil = (campo, valor) => {
    atualizar('funil_metricas', { ...funil_metricas, [campo]: valor });
  };
  // Toggle de chip em uma lista (pos_chars ou tom_chars)
  const toggleChip = (campo, id) => {
    const lista = obter(campo) || [];
    const novo = lista.includes(id) ? lista.filter(x => x !== id) : [...lista, id];
    atualizar(campo, novo);
  };
  const adicionarExtra = (campo, texto) => {
    const t = (texto || '').trim();
    if (!t) return;
    const lista = obter(campo) || [];
    if (lista.map(x => x.toLowerCase()).includes(t.toLowerCase())) return;
    atualizar(campo, [...lista, t]);
  };
  const removerExtra = (campo, idx) => {
    const lista = obter(campo) || [];
    atualizar(campo, lista.filter((_, i) => i !== idx));
  };
  const setarRespostaMkt = (perguntaId, opcaoId) => {
    const novasRespostas = { ...invest_mkt_respostas, [perguntaId]: opcaoId };
    atualizar('invest_mkt_respostas', novasRespostas);
    setPerguntaAberta(null);
  };
  const togglePerguntaMkt = (perguntaId) => {
    setPerguntaAberta(perguntaAberta === perguntaId ? null : perguntaId);
  };
  const atualizarPesoCanal = (canalId, novoPeso) => {
    const lim = Math.max(0, Math.min(100, Number(novoPeso) || 0));
    const existe = canais_mix.find(c => c.id === canalId);
    let novo;
    if (existe) {
      novo = lim === 0
        ? canais_mix.filter(c => c.id !== canalId)
        : canais_mix.map(c => c.id === canalId ? { ...c, peso: lim } : c);
    } else {
      novo = lim === 0 ? canais_mix : [...canais_mix, { id: canalId, peso: lim }];
    }
    atualizar('canais_mix', novo);
  };
  // ── Handlers das etapas do funil (5 etapas: topo, meio, fundo, fechamento, gargalo) ──
  const toggleChipFunil = (etapaId, chipId) => {
    const lista = funil_chars[etapaId] || [];
    const novo = lista.includes(chipId) ? lista.filter(x => x !== chipId) : [...lista, chipId];
    atualizar('funil_chars', { ...funil_chars, [etapaId]: novo });
  };
  const adicionarExtraFunil = (etapaId, texto) => {
    const t = (texto || '').trim();
    if (!t) return;
    const lista = funil_extras[etapaId] || [];
    if (lista.map(x => x.toLowerCase()).includes(t.toLowerCase())) return;
    atualizar('funil_extras', { ...funil_extras, [etapaId]: [...lista, t] });
  };
  const removerExtraFunil = (etapaId, idx) => {
    const lista = funil_extras[etapaId] || [];
    atualizar('funil_extras', { ...funil_extras, [etapaId]: lista.filter((_, i) => i !== idx) });
  };
  const setFraseLivreEtapa = (etapaId, texto) => {
    atualizar('funil_livre', { ...funil_livre, [etapaId]: texto });
  };
  const ligarEditaLivreEtapa = (etapaId) => {
    atualizar('funil_livre', { ...funil_livre, [etapaId]: fraseEtapa(etapaId) });
    atualizar('funil_editou_livre', { ...funil_editou_livre, [etapaId]: true });
  };
  const desligarEditaLivreEtapa = (etapaId) => {
    atualizar('funil_editou_livre', { ...funil_editou_livre, [etapaId]: false });
    atualizar('funil_livre', { ...funil_livre, [etapaId]: '' });
  };
  const setarTexto = (campo, valor) => atualizar(campo, valor);

  const somaMix = canais_mix.reduce((s, c) => s + (Number(c.peso) || 0), 0);

  const inputBase = {
    width: '100%', padding: '8px 12px', fontSize: 13,
    border: `1px solid ${G.borda}`, borderRadius: 0,
    fontFamily: "'Outfit', sans-serif", color: G.txt1,
    background: '#FFF', boxSizing: 'border-box',
  };
  const HEADER_BLOCO = {
    fontSize: 16, fontWeight: 700, letterSpacing: '0.10em',
    textTransform: 'uppercase', color: G.laranja, marginBottom: 10,
  };
  const HEADER_AUX = {
    fontSize: 12, fontWeight: 400, color: G.txt2,
    textTransform: 'none', letterSpacing: 0, marginLeft: 10,
  };
  const SUB_HEADER = {
    fontSize: 12, fontWeight: 700, color: G.txt2,
    textTransform: 'uppercase', letterSpacing: '0.06em',
    marginBottom: 6,
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
      {/* 2 quadros lado a lado: Definição + Instruções */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <div style={{ padding: '12px 16px', background: G.verdeLight,
          borderLeft: `4px solid ${G.verde}`, borderRadius: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 800, color: G.verde,
            letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 6 }}>
            O que é Marketing &amp; Vendas
          </div>
          <div style={{ fontSize: 12, lineHeight: 1.45, color: G.txt1 }}>
            Define <strong>como a empresa chega ao cliente</strong>: mensagem de marca,
            mix de canais de aquisição, etapas do funil de venda e quanto da receita é
            investida em marketing.
          </div>
        </div>
        <div style={{ padding: '12px 16px', background: G.laranjaLight,
          borderLeft: `4px solid ${G.laranja}`, borderRadius: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 800, color: G.laranja,
            letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 6 }}>
            O que você faz aqui
          </div>
          <div style={{ fontSize: 12, lineHeight: 1.45, color: G.txt1 }}>
            <strong>Revise</strong> a mensagem-chave e o tom · <strong>ajuste o mix de
            canais</strong> até somar 100% · descreva cada etapa do funil e o gargalo
            principal · escolha a <strong>faixa de investimento</strong> em marketing.
          </div>
        </div>
      </div>

      {/* BLOCO 1 — Posicionamento de Marca */}
      <div>
        <div style={HEADER_BLOCO}>
          Bloco 1 · Posicionamento de Marca
          <span style={HEADER_AUX}>· mensagem-chave e tom de comunicação propostos pela <MarcaAIdvisor /></span>
        </div>
        {/* Texto explicativo curto da jornada do bloco */}
        <div style={{ padding: '10px 14px', background: G.fundo,
          border: `1px solid ${G.borda}`, borderRadius: 0, marginBottom: 12,
          fontSize: 12, lineHeight: 1.45, color: G.txt1 }}>
          <MarcaAIdvisor /> selecionou características típicas do seu setor
          (<strong>{setor}</strong>) e montou a frase abaixo.
          <strong> Marque ou desmarque</strong> os itens conforme o que mais
          combina com o projeto · <strong>adicione</strong> características próprias
          se faltar alguma · a frase é <strong>recomposta automaticamente</strong>.
          Se preferir, edite o texto livremente.
        </div>

        {/* ── Sub-bloco: Mensagem-chave ──────────────────────────── */}
        <div style={{ marginBottom: 16 }}>
          {/* Linha topo: [Título] · [Frase composta] · [Botão Editar] */}
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 8 }}>
            <div style={{ ...SUB_HEADER, marginBottom: 0, paddingTop: 8, flexShrink: 0 }}>
              Mensagem-chave
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              {pos_editou_livre ? (
                <textarea value={pos_livre || ''}
                  onChange={e => atualizar('pos_livre', e.target.value)}
                  rows={3} style={{ ...inputBase, lineHeight: 1.45, resize: 'vertical',
                    background: G.laranjaLight, borderColor: G.laranja }}/>
              ) : (
                <div style={{ padding: '10px 14px', background: G.verdeLight,
                  borderLeft: `3px solid ${G.verde}`, borderRadius: 0,
                  fontSize: 13, lineHeight: 1.5, color: G.txt1, fontStyle: 'italic' }}>
                  {frasePosicionamento || '_Selecione pelo menos uma característica para compor a frase._'}
                </div>
              )}
            </div>
            <div style={{ flexShrink: 0 }}>
              {pos_editou_livre ? (
                <button onClick={() => { atualizar('pos_editou_livre', false); atualizar('pos_livre', ''); }}
                  style={{ background: '#FFF', color: G.verde, border: `1px solid ${G.verde}`,
                    padding: '5px 12px', minWidth: 170, textAlign: 'center', fontSize: 11, fontWeight: 700,
                    letterSpacing: '0.06em', textTransform: 'uppercase',
                    borderRadius: 0, cursor: 'pointer', whiteSpace: 'nowrap' }}>
                  <IconeReverter /> Voltar à <MarcaAIdvisor variant="default" />
                </button>
              ) : (
                <button onClick={() => { atualizar('pos_livre', frasePosicionamento); atualizar('pos_editou_livre', true); }}
                  style={{ background: '#FFF', color: G.laranja, border: `1px solid ${G.laranja}`,
                    padding: '5px 12px', minWidth: 170, textAlign: 'center', fontSize: 11, fontWeight: 700,
                    letterSpacing: '0.06em', textTransform: 'uppercase',
                    borderRadius: 0, cursor: 'pointer', whiteSpace: 'nowrap' }}>
                  <IconeLapis /> Editar livremente
                </button>
              )}
            </div>
          </div>
          {/* Chips de características */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 8 }}>
            {catalogoPos.map(c => {
              const sel = pos_chars.includes(c.id);
              return (
                <button key={c.id} onClick={() => toggleChip('pos_chars', c.id)}
                  style={{
                    padding: '5px 11px', fontSize: 12, fontWeight: 600,
                    background: sel ? G.verde : '#FFF',
                    color: sel ? '#FFF' : G.txt1,
                    border: `1px solid ${sel ? G.verde : G.borda}`,
                    borderRadius: 999, cursor: 'pointer',
                    fontFamily: "'Outfit', sans-serif",
                  }}>
                  {sel ? '✓ ' : ''}{c.chip}
                </button>
              );
            })}
            {pos_extras.map((extra, idx) => (
              <button key={`extra-${idx}`} onClick={() => removerExtra('pos_extras', idx)}
                style={{
                  padding: '5px 11px', fontSize: 12, fontWeight: 600,
                  background: G.laranja, color: '#FFF',
                  border: `1px solid ${G.laranja}`,
                  borderRadius: 999, cursor: 'pointer',
                  fontFamily: "'Outfit', sans-serif",
                }}>
                ✓ {extra} ✕
              </button>
            ))}
          </div>
          {/* Adicionar característica extra */}
          <div style={{ display: 'flex', gap: 6 }}>
            <input type="text" value={novoChipPos}
              onChange={e => setNovoChipPos(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter') {
                  adicionarExtra('pos_extras', novoChipPos);
                  setNovoChipPos('');
                }
              }}
              placeholder="Adicionar característica própria…"
              style={{ ...inputBase, fontSize: 12, padding: '6px 10px' }}/>
            <button onClick={() => { adicionarExtra('pos_extras', novoChipPos); setNovoChipPos(''); }}
              style={{ background: G.laranja, color: '#FFF', border: 'none',
                padding: '6px 14px', fontSize: 11, fontWeight: 700,
                letterSpacing: '0.06em', textTransform: 'uppercase',
                borderRadius: 0, cursor: 'pointer', whiteSpace: 'nowrap' }}>
              + Adicionar
            </button>
          </div>
        </div>

        {/* ── Sub-bloco: Tom de comunicação ──────────────────────── */}
        <div>
          {/* Linha topo: [Título] · [Frase composta] · [Botão Editar] */}
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 8 }}>
            <div style={{ ...SUB_HEADER, marginBottom: 0, paddingTop: 8, flexShrink: 0 }}>
              Tom de comunicação
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              {tom_editou_livre ? (
                <textarea value={tom_livre || ''}
                  onChange={e => atualizar('tom_livre', e.target.value)}
                  rows={2} style={{ ...inputBase, lineHeight: 1.45, resize: 'vertical',
                    background: G.laranjaLight, borderColor: G.laranja }}/>
              ) : (
                <div style={{ padding: '10px 14px', background: G.verdeLight,
                  borderLeft: `3px solid ${G.verde}`, borderRadius: 0,
                  fontSize: 13, lineHeight: 1.5, color: G.txt1, fontStyle: 'italic' }}>
                  {fraseTom || '_Selecione pelo menos uma característica para compor o tom._'}
                </div>
              )}
            </div>
            <div style={{ flexShrink: 0 }}>
              {tom_editou_livre ? (
                <button onClick={() => { atualizar('tom_editou_livre', false); atualizar('tom_livre', ''); }}
                  style={{ background: '#FFF', color: G.verde, border: `1px solid ${G.verde}`,
                    padding: '5px 12px', minWidth: 170, textAlign: 'center', fontSize: 11, fontWeight: 700,
                    letterSpacing: '0.06em', textTransform: 'uppercase',
                    borderRadius: 0, cursor: 'pointer', whiteSpace: 'nowrap' }}>
                  <IconeReverter /> Voltar à <MarcaAIdvisor variant="default" />
                </button>
              ) : (
                <button onClick={() => { atualizar('tom_livre', fraseTom); atualizar('tom_editou_livre', true); }}
                  style={{ background: '#FFF', color: G.laranja, border: `1px solid ${G.laranja}`,
                    padding: '5px 12px', minWidth: 170, textAlign: 'center', fontSize: 11, fontWeight: 700,
                    letterSpacing: '0.06em', textTransform: 'uppercase',
                    borderRadius: 0, cursor: 'pointer', whiteSpace: 'nowrap' }}>
                  <IconeLapis /> Editar livremente
                </button>
              )}
            </div>
          </div>
          {/* Chips */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 8 }}>
            {catalogoTom.map(c => {
              const sel = tom_chars.includes(c.id);
              return (
                <button key={c.id} onClick={() => toggleChip('tom_chars', c.id)}
                  style={{
                    padding: '5px 11px', fontSize: 12, fontWeight: 600,
                    background: sel ? G.verde : '#FFF',
                    color: sel ? '#FFF' : G.txt1,
                    border: `1px solid ${sel ? G.verde : G.borda}`,
                    borderRadius: 999, cursor: 'pointer',
                    fontFamily: "'Outfit', sans-serif",
                  }}>
                  {sel ? '✓ ' : ''}{c.chip}
                </button>
              );
            })}
            {tom_extras.map((extra, idx) => (
              <button key={`extra-${idx}`} onClick={() => removerExtra('tom_extras', idx)}
                style={{
                  padding: '5px 11px', fontSize: 12, fontWeight: 600,
                  background: G.laranja, color: '#FFF',
                  border: `1px solid ${G.laranja}`,
                  borderRadius: 999, cursor: 'pointer',
                  fontFamily: "'Outfit', sans-serif",
                }}>
                ✓ {extra} ✕
              </button>
            ))}
          </div>
          {/* Adicionar */}
          <div style={{ display: 'flex', gap: 6 }}>
            <input type="text" value={novoChipTom}
              onChange={e => setNovoChipTom(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter') {
                  adicionarExtra('tom_extras', novoChipTom);
                  setNovoChipTom('');
                }
              }}
              placeholder="Adicionar característica própria…"
              style={{ ...inputBase, fontSize: 12, padding: '6px 10px' }}/>
            <button onClick={() => { adicionarExtra('tom_extras', novoChipTom); setNovoChipTom(''); }}
              style={{ background: G.laranja, color: '#FFF', border: 'none',
                padding: '6px 14px', fontSize: 11, fontWeight: 700,
                letterSpacing: '0.06em', textTransform: 'uppercase',
                borderRadius: 0, cursor: 'pointer', whiteSpace: 'nowrap' }}>
              + Adicionar
            </button>
          </div>
        </div>
      </div>

      {/* BLOCO 2 — Canais & Mix */}
      <div>
        <div style={HEADER_BLOCO}>
          Bloco 2 · Canais &amp; Mix
          <span style={HEADER_AUX}>· ajuste o peso de cada canal · a soma precisa fechar em 100%</span>
        </div>
        <div style={{ background: '#FFF', border: `1px solid ${G.borda}`, borderRadius: 0 }}>
          {fonteCanais.map((canal, idx) => {
            const item = canais_mix.find(c => c.id === canal.id);
            const peso = item?.peso ?? 0;
            return (
              <div key={canal.id} style={{
                display: 'grid', gridTemplateColumns: '1fr 110px 80px', alignItems: 'center', gap: 12,
                padding: '8px 14px',
                borderTop: idx === 0 ? 'none' : `1px solid ${G.borda}`,
                background: peso > 0 ? G.verdeLight : 'transparent',
              }}>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 600, color: G.txt1 }}>{canal.label}</div>
                  <div style={{ fontSize: 10, color: G.txt2, letterSpacing: '0.06em',
                    textTransform: 'uppercase' }}>{canal.tipo}</div>
                </div>
                <CampoNumericoBP60M
                  valor={peso}
                  onChange={(v) => atualizarPesoCanal(canal.id, v ?? 0)}
                  label={`Peso · ${canal.label}`}
                  permitirDecimal={false}
                  min={0} max={100}
                  sufixo="%"
                  formato="int"
                />
                <div style={{ fontSize: 13, fontWeight: 700, color: peso > 0 ? G.verde : G.txt2,
                  textAlign: 'right' }}>{peso}%</div>
              </div>
            );
          })}
        </div>
        {/* Painel limitador-alerta (mesma solução do Bloco 1 da T4) */}
        <div style={{ marginTop: 10, padding: '8px 12px',
          background: somaMix === 100 ? G.verdeLight : '#FEF2F2',
          border: `1px solid ${somaMix === 100 ? G.verde : G.vermelho}`,
          borderRadius: 0, fontSize: 12,
          color: somaMix === 100 ? G.verde : G.vermelho, fontWeight: 700,
          display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10 }}>
          <span>
            {somaMix === 100
              ? `✓ Soma 100% · válido`
              : <><IconeAlerta /> {somaMix < 100
                ? `Soma ${somaMix}% · faltam ${100 - somaMix}% para fechar`
                : `Soma ${somaMix}% · excede ${somaMix - 100}% acima de 100%`}</>}
          </span>
          {somaMix !== 100 && (
            <span style={{ fontSize: 11, fontWeight: 400, fontStyle: 'italic', whiteSpace: 'nowrap' }}>
              Aprovação bloqueada até soma fechar 100%
            </span>
          )}
        </div>

        {/* Botão de visualização do mix de canais */}
        <BotaoVisualizarGraficos
          aberto={graficosT8B2Aberto}
          onToggle={() => setGraficosT8B2Aberto(v => !v)}
          rotulo="Visualizar mix de canais">
          <div>
            <div style={{ fontSize: 10, fontWeight: 800, color: G.txt2,
              letterSpacing: '0.10em', textTransform: 'uppercase', marginBottom: 8 }}>
              Ilustração · {TITULO_EXHIBIT_POR_TIPO['t8_canais']}
            </div>
            <GraficoCanaisMix canaisMix={canais_mix}/>
          </div>
        </BotaoVisualizarGraficos>
      </div>

      {/* BLOCO 3 — Funil & Conversão (chips + frase composta por etapa) */}
      <div>
        <div style={HEADER_BLOCO}>
          Bloco 3 · Funil &amp; Conversão
          <span style={HEADER_AUX}>· características de cada etapa propostas pela <MarcaAIdvisor /></span>
        </div>
        {/* Texto explicativo curto da jornada do bloco (mesmo conceito do Bloco 1) */}
        <div style={{ padding: '10px 14px', background: G.fundo,
          border: `1px solid ${G.borda}`, borderRadius: 0, marginBottom: 12,
          fontSize: 12, lineHeight: 1.45, color: G.txt1 }}>
          <MarcaAIdvisor /> selecionou características típicas do seu setor
          (<strong>{setor}</strong>) para cada etapa do funil de venda e montou as
          frases abaixo. <strong>Marque ou desmarque</strong> os itens conforme o que
          mais combina com o projeto · <strong>adicione</strong> características próprias
          se faltar alguma · cada frase é <strong>recomposta automaticamente</strong>.
          Se preferir, edite o texto de qualquer etapa livremente.
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
          {[...ETAPAS_FUNIL, { id: 'gargalo', label: 'Gargalo principal', descricao: 'principal entrave de conversão · próximo passo' }].map(etapa => {
            const catalogoEtapa = catalogoFunilSetor[etapa.id] || [];
            const chipsEtapa = funil_chars[etapa.id] || [];
            const extrasEtapa = funil_extras[etapa.id] || [];
            const editouLivre = funil_editou_livre?.[etapa.id] || false;
            const livreEtapa = funil_livre?.[etapa.id] || '';
            const fraseAtual = fraseEtapa(etapa.id);
            return (
              <div key={etapa.id} style={ etapa.id === 'gargalo'
                ? { paddingTop: 12, borderTop: `1px dashed ${G.borda}` }
                : null }>
                {/* Linha topo: [Título + descrição] · [Frase composta] · [Botão Editar] */}
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 8 }}>
                  <div style={{ ...SUB_HEADER, marginBottom: 0, paddingTop: 8, flexShrink: 0, width: 180 }}>
                    {etapa.label}
                    <div style={{ fontSize: 10, fontWeight: 400, color: G.txt2,
                      textTransform: 'none', letterSpacing: 0, marginTop: 2 }}>
                      {etapa.descricao}
                    </div>
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    {editouLivre ? (
                      <textarea value={livreEtapa}
                        onChange={e => setFraseLivreEtapa(etapa.id, e.target.value)}
                        rows={2} style={{ ...inputBase, lineHeight: 1.45, resize: 'vertical',
                          background: G.laranjaLight, borderColor: G.laranja }}/>
                    ) : (
                      <div style={{ padding: '10px 14px', background: G.verdeLight,
                        borderLeft: `3px solid ${G.verde}`, borderRadius: 0,
                        fontSize: 13, lineHeight: 1.5, color: G.txt1, fontStyle: 'italic' }}>
                        {fraseAtual || '_Selecione pelo menos uma característica para compor a frase._'}
                      </div>
                    )}
                  </div>
                  <div style={{ flexShrink: 0 }}>
                    {editouLivre ? (
                      <button onClick={() => desligarEditaLivreEtapa(etapa.id)}
                        style={{ background: '#FFF', color: G.verde, border: `1px solid ${G.verde}`,
                          padding: '5px 12px', minWidth: 170, textAlign: 'center', fontSize: 11, fontWeight: 700,
                          letterSpacing: '0.06em', textTransform: 'uppercase',
                          borderRadius: 0, cursor: 'pointer', whiteSpace: 'nowrap' }}>
                        <IconeReverter /> Voltar à <MarcaAIdvisor variant="default" />
                      </button>
                    ) : (
                      <button onClick={() => ligarEditaLivreEtapa(etapa.id)}
                        style={{ background: '#FFF', color: G.laranja, border: `1px solid ${G.laranja}`,
                          padding: '5px 12px', minWidth: 170, textAlign: 'center', fontSize: 11, fontWeight: 700,
                          letterSpacing: '0.06em', textTransform: 'uppercase',
                          borderRadius: 0, cursor: 'pointer', whiteSpace: 'nowrap' }}>
                        <IconeLapis /> Editar livremente
                      </button>
                    )}
                  </div>
                </div>
                {/* Chips marcáveis + extras laranja */}
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 8 }}>
                  {catalogoEtapa.map(c => {
                    const sel = chipsEtapa.includes(c.id);
                    return (
                      <button key={c.id} onClick={() => toggleChipFunil(etapa.id, c.id)}
                        style={{
                          padding: '5px 11px', fontSize: 12, fontWeight: 600,
                          background: sel ? G.verde : '#FFF',
                          color: sel ? '#FFF' : G.txt1,
                          border: `1px solid ${sel ? G.verde : G.borda}`,
                          borderRadius: 999, cursor: 'pointer',
                          fontFamily: "'Outfit', sans-serif",
                        }}>
                        {sel ? '✓ ' : ''}{c.chip}
                      </button>
                    );
                  })}
                  {extrasEtapa.map((extra, idx) => (
                    <button key={`extra-${idx}`} onClick={() => removerExtraFunil(etapa.id, idx)}
                      style={{
                        padding: '5px 11px', fontSize: 12, fontWeight: 600,
                        background: G.laranja, color: '#FFF',
                        border: `1px solid ${G.laranja}`,
                        borderRadius: 999, cursor: 'pointer',
                        fontFamily: "'Outfit', sans-serif",
                      }}>
                      ✓ {extra} ✕
                    </button>
                  ))}
                </div>
                {/* Métrica inline da etapa (SU) · benchmark quantitativo */}
                {modo === 'SU' && METRICAS_FUNIL_SU[etapa.id] && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                    <span style={{ fontSize: 11, fontWeight: 700, color: G.txt2,
                      textTransform: 'uppercase', letterSpacing: '0.04em', whiteSpace: 'nowrap' }}>
                      {METRICAS_FUNIL_SU[etapa.id].label}
                    </span>
                    <div style={{ width: 150 }}>
                      <CampoNumericoBP60M
                        valor={funil_metricas[METRICAS_FUNIL_SU[etapa.id].campo] ?? ''}
                        onChange={(v) => atualizarMetricaFunil(METRICAS_FUNIL_SU[etapa.id].campo, v ?? '')}
                        label={METRICAS_FUNIL_SU[etapa.id].label}
                        permitirDecimal={false}
                        formato="int"
                      />
                    </div>
                  </div>
                )}
                {/* Adicionar característica extra */}
                <div style={{ display: 'flex', gap: 6 }}>
                  <input type="text" value={novoChipFunil[etapa.id] || ''}
                    onChange={e => setNovoChipFunil({ ...novoChipFunil, [etapa.id]: e.target.value })}
                    onKeyDown={e => {
                      if (e.key === 'Enter') {
                        adicionarExtraFunil(etapa.id, novoChipFunil[etapa.id]);
                        setNovoChipFunil({ ...novoChipFunil, [etapa.id]: '' });
                      }
                    }}
                    placeholder="Adicionar característica própria…"
                    style={{ ...inputBase, fontSize: 12, padding: '6px 10px' }}/>
                  <button onClick={() => {
                      adicionarExtraFunil(etapa.id, novoChipFunil[etapa.id]);
                      setNovoChipFunil({ ...novoChipFunil, [etapa.id]: '' });
                    }}
                    style={{ background: G.laranja, color: '#FFF', border: 'none',
                      padding: '6px 14px', fontSize: 11, fontWeight: 700,
                      letterSpacing: '0.06em', textTransform: 'uppercase',
                      borderRadius: 0, cursor: 'pointer', whiteSpace: 'nowrap' }}>
                    + Adicionar
                  </button>
                </div>
              </div>
            );
          })}
        </div>

        {/* Botão de visualização do funil */}
        <BotaoVisualizarGraficos
          aberto={graficosT8B3Aberto}
          onToggle={() => setGraficosT8B3Aberto(v => !v)}
          rotulo="Visualizar funil de marketing e vendas">
          <div>
            <div style={{ fontSize: 10, fontWeight: 800, color: G.txt2,
              letterSpacing: '0.10em', textTransform: 'uppercase', marginBottom: 8 }}>
              Ilustração · {TITULO_EXHIBIT_POR_TIPO['t8_funil']}
            </div>
            <GraficoFunilVendas funilChars={funil_chars}/>
          </div>
        </BotaoVisualizarGraficos>
      </div>

      {/* BLOCO 4 — Investimento em Marketing */}
      <div>
        <div style={HEADER_BLOCO}>
          Bloco 4 · Investimento em Marketing
          <span style={HEADER_AUX}>{modo === 'SU'
            ? '· estratégia de orçamento e composição do time de crescimento'
            : '· faixa de % da receita aplicada em marketing e vendas'}</span>
        </div>
        {/* Texto explicativo curto · como e por quem a faixa foi estimada (padrão 2 do catálogo) */}
        <div style={{ padding: '10px 14px', background: G.fundo,
          border: `1px solid ${G.borda}`, borderRadius: 0, marginBottom: 12,
          fontSize: 12, lineHeight: 1.45, color: G.txt1 }}>
          {modo === 'SU' ? (
            <>
              <MarcaAIdvisor /> propõe, para o perfil de <strong>startup</strong> no setor
              (<strong>{setor}</strong>), como definir o <strong>orçamento de marketing</strong>
              e como compor o <strong>time de crescimento</strong>. <strong>Revise</strong>
              cada resposta · as escolhas viram insumo da projeção financeira do BP.
            </>
          ) : (
            <>
              <MarcaAIdvisor /> estimou a faixa de investimento em marketing com base no
              setor da empresa (<strong>{setor}</strong>) e nas características já registradas
              no projeto (canais escolhidos, modelo de receita, perfil de cliente).
              <strong> Revise</strong> a faixa marcada · se discordar, clique em
              <strong> Revisar</strong> e escolha outra. A faixa selecionada vira insumo da
              projeção financeira do BP.
            </>
          )}
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {fonteInvest.map(p => {
            const aberta = perguntaAberta === p.id;
            const resp = invest_mkt_respostas[p.id];
            const opcaoSel = p.opcoes.find(o => o.id === resp);
            return (
              <div key={p.id}>
                {/* Linha topo do sub-bloco · botão FORA do card, alinhado com botões dos Blocos 1 e 3 */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: aberta ? 8 : 0 }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: G.txt1 }}>
                      {p.label}
                    </div>
                    {resp && !aberta && (
                      <div style={{ fontSize: 12, color: G.verde, fontWeight: 700, marginTop: 2 }}>
                        ✓ {opcaoSel?.label}
                      </div>
                    )}
                  </div>
                  <button onClick={() => togglePerguntaMkt(p.id)}
                    style={{ background: '#FFF', color: G.laranja,
                      border: `1px solid ${G.laranja}`,
                      padding: '5px 12px', minWidth: 170, textAlign: 'center', fontSize: 11, fontWeight: 700,
                      letterSpacing: '0.06em', textTransform: 'uppercase',
                      borderRadius: 0, cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0 }}>
                    {aberta ? '▴ Clique para fechar' : (resp ? <><IconeLapis /> Revisar</> : '▾ Clique para abrir')}
                  </button>
                </div>
                {/* Card com as opções · só renderiza quando o accordion está aberto */}
                {aberta && (
                  <div style={{
                    background: '#FFF',
                    border: `1px solid ${G.verde}`,
                    borderRadius: 0, overflow: 'hidden',
                    padding: '8px 14px 12px', display: 'flex',
                    flexDirection: 'column', gap: 4,
                  }}>
                    {p.opcoes.map(o => {
                      const sel = resp === o.id;
                      return (
                        <button key={o.id} onClick={() => setarRespostaMkt(p.id, o.id)}
                          style={{
                            display: 'block', textAlign: 'left',
                            padding: '8px 12px',
                            background: sel ? G.verdeLight : '#FFF',
                            color: G.txt1, fontSize: 13,
                            border: `1px solid ${sel ? G.verde : G.borda}`,
                            borderRadius: 0, cursor: 'pointer',
                            fontFamily: "'Outfit', sans-serif",
                          }}>
                          {sel ? '● ' : '○ '}{o.label}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

const compilarTarefa8 = (marketingVendas, empresa) => {
  const rascunho = RASCUNHO_TAREFA_8[empresa?.id] || {
    pos_chars: [], pos_extras: [],
    tom_chars: [], tom_extras: [],
    canais_mix: [],
    funil_chars: { topo: [], meio: [], fundo: [], fechamento: [], gargalo: [] },
    funil_extras: { topo: [], meio: [], fundo: [], fechamento: [], gargalo: [] },
    invest_mkt_respostas: {},
  };
  const obter = (c) => marketingVendas?.[c] ?? rascunho[c];
  const setor = empresa?.setor || 'geral';
  const posChars       = obter('pos_chars') || [];
  const posExtras      = obter('pos_extras') || [];
  const posLivre       = obter('pos_livre');
  const posEditouLivre = obter('pos_editou_livre') || false;
  const tomChars       = obter('tom_chars') || [];
  const tomExtras      = obter('tom_extras') || [];
  const tomLivre       = obter('tom_livre');
  const tomEditouLivre = obter('tom_editou_livre') || false;
  const posicionamento = (posEditouLivre && posLivre)
    ? posLivre
    : (montarFrasePosicionamento(setor, posChars, posExtras) || '_Posicionamento ainda não descrito._');
  const tom = (tomEditouLivre && tomLivre)
    ? tomLivre
    : (montarFraseTom(setor, tomChars, tomExtras) || '_Tom de comunicação ainda não descrito._');
  const mix             = obter('canais_mix') || [];
  const funilChars      = obter('funil_chars') || {};
  const funilExtras     = obter('funil_extras') || {};
  const funilLivre      = obter('funil_livre') || {};
  const funilEditouLivre = obter('funil_editou_livre') || {};
  const respMkt         = obter('invest_mkt_respostas') || {};

  const linhasMix = mix
    .filter(c => (c.peso || 0) > 0)
    .sort((a, b) => (b.peso || 0) - (a.peso || 0))
    .map(c => {
      const canal = CANAIS_AQUISICAO.find(x => x.id === c.id);
      return `- ${canal?.label || c.id} (${canal?.tipo || '—'}) · **${c.peso}%**`;
    })
    .join('\n') || '_Mix de canais ainda não definido._';

  const resolverFraseEtapa = (etapaId) => {
    if (funilEditouLivre?.[etapaId] && funilLivre?.[etapaId]) return funilLivre[etapaId];
    return montarFraseEtapa(setor, etapaId, funilChars[etapaId] || [], funilExtras[etapaId] || []) || '_Etapa ainda não descrita._';
  };
  const linhasFunil = ETAPAS_FUNIL
    .map(etapa => `**${etapa.label}** _(${etapa.descricao})_\n\n${resolverFraseEtapa(etapa.id)}`)
    .join('\n\n');
  const gargalo = resolverFraseEtapa('gargalo');

  const p = PERGUNTAS_INVEST_MKT.find(x => x.id === 'pct_receita');
  const opt = p?.opcoes.find(x => x.id === respMkt.pct_receita);
  const linhaInvest = opt
    ? `Faixa escolhida: **${opt.label}** (~${opt.meio}% sobre a receita).`
    : '_Faixa de investimento ainda não escolhida._';

  return `## 8. Marketing & Vendas

### Posicionamento de Marca

**Mensagem-chave**

${posicionamento}

**Tom de comunicação**

${tom}

### Canais de Aquisição (Mix)

${linhasMix}

A ilustração abaixo evidencia a participação relativa de cada canal no mix de aquisição.

<!--GRAFICO:t8_canais-->

### Funil & Conversão

${linhasFunil}

O funil a seguir representa a jornada do cliente — do primeiro contato à compra — com o número de elementos definidos em cada etapa do plano comercial.

<!--GRAFICO:t8_funil-->

**Gargalo principal e próximo passo**

${gargalo}

### Investimento em Marketing

${linhaInvest}`;
};

// ════════════════════════════════════════════════════════════════════════
// TAREFA 9 — Plano Operacional
// 4 blocos: Como o negócio funciona (chips por setor) · Fornecedores e
// Parceiros Críticos (lista editável com % concentração) · Sistemas e
// Tecnologia (chips por setor) · Estrutura Física e Capacidade (2 textos
// + 1 pergunta de capacidade qualitativa).
// Linguagem ajustada pra PME "pré-corporate" (já cresceu, sem consultor M&A).
// ════════════════════════════════════════════════════════════════════════

const CARACTERISTICAS_ATIVIDADES_POR_SETOR = {
  'E-commerce': [
    { id: 'catalogo_curado',    chip: 'Curadoria de catálogo',          frase: 'curadoria de catálogo' },
    { id: 'compras_estoque',    chip: 'Compras e estoque',              frase: 'compras e gestão de estoque' },
    { id: 'logistica_propria',  chip: 'Logística própria',              frase: 'logística própria' },
    { id: 'marketing_digital',  chip: 'Marketing digital',              frase: 'marketing digital' },
    { id: 'atendimento_chat',   chip: 'Atendimento por chat/WhatsApp',  frase: 'atendimento por chat e WhatsApp' },
    { id: 'pos_venda',          chip: 'Pós-venda e suporte',            frase: 'pós-venda e suporte' },
    { id: 'ti_plataforma',      chip: 'Plataforma de e-commerce',       frase: 'operação da plataforma de e-commerce' },
    { id: 'gestao_marketplace', chip: 'Gestão de marketplaces',         frase: 'gestão de marketplaces de terceiros' },
  ],
  'Bar/Restaurante': [
    { id: 'cozinha_operacao',  chip: 'Cozinha e preparo',          frase: 'operação da cozinha e preparo' },
    { id: 'salao_atendimento', chip: 'Atendimento no salão',       frase: 'atendimento no salão' },
    { id: 'gestao_reservas',   chip: 'Gestão de reservas',         frase: 'gestão de reservas' },
    { id: 'compras_fornec',    chip: 'Compras de ingredientes',    frase: 'compras de ingredientes' },
    { id: 'delivery_op',       chip: 'Operação de delivery',       frase: 'operação de delivery' },
    { id: 'eventos_priv',      chip: 'Eventos privados',           frase: 'operação de eventos privados' },
    { id: 'marketing_redes',   chip: 'Marketing e redes sociais',  frase: 'marketing e redes sociais' },
    { id: 'treinamento',       chip: 'Treinamento da equipe',      frase: 'treinamento contínuo da equipe' },
  ],
  'Saúde': [
    { id: 'atendimento_clinico',chip: 'Atendimento clínico presencial', frase: 'atendimento clínico presencial' },
    { id: 'telemedicina',       chip: 'Telemedicina',                   frase: 'telemedicina' },
    { id: 'agenda_recepcao',    chip: 'Agenda e recepção',              frase: 'agenda e recepção' },
    { id: 'exames_proced',      chip: 'Exames e procedimentos',         frase: 'exames e procedimentos' },
    { id: 'gestao_convenios',   chip: 'Gestão de convênios',            frase: 'gestão de convênios' },
    { id: 'prontuario',         chip: 'Prontuário eletrônico',          frase: 'manutenção do prontuário eletrônico' },
    { id: 'followup_pac',       chip: 'Follow-up de pacientes',         frase: 'follow-up de pacientes' },
    { id: 'educacao_saude',     chip: 'Educação em saúde',              frase: 'conteúdo de educação em saúde' },
  ],
  'Construção Civil': [
    { id: 'engenharia_proj',   chip: 'Engenharia de projeto',          frase: 'engenharia de projeto' },
    { id: 'canteiro_exec',     chip: 'Execução de canteiro',           frase: 'execução de canteiro de obra' },
    { id: 'compras_insumos',   chip: 'Compras de insumos',             frase: 'compras de insumos' },
    { id: 'gestao_obra',       chip: 'Gestão de obra',                 frase: 'gestão de obra (prazo, custo, qualidade)' },
    { id: 'seguranca_trab',    chip: 'Segurança do trabalho',          frase: 'segurança do trabalho' },
    { id: 'relacionamento_cli',chip: 'Relacionamento com cliente',     frase: 'relacionamento com o cliente da obra' },
    { id: 'entrega_obra',      chip: 'Entrega e pós-obra',             frase: 'entrega e pós-obra' },
    { id: 'loja_materiais',    chip: 'Loja de materiais',              frase: 'operação da loja de materiais' },
  ],
  'geral': [
    { id: 'producao_servico',  chip: 'Produção/Prestação de serviço',  frase: 'produção/prestação do serviço principal' },
    { id: 'vendas',            chip: 'Vendas',                         frase: 'vendas' },
    { id: 'atendimento',       chip: 'Atendimento ao cliente',         frase: 'atendimento ao cliente' },
    { id: 'compras',           chip: 'Compras',                        frase: 'compras' },
    { id: 'estoque_logistica', chip: 'Estoque e logística',            frase: 'estoque e logística' },
    { id: 'gestao_admin',      chip: 'Gestão administrativa',          frase: 'gestão administrativa' },
    { id: 'marketing',         chip: 'Marketing',                      frase: 'marketing' },
    { id: 'pos_venda_g',       chip: 'Pós-venda',                      frase: 'pós-venda' },
  ],
};

const CARACTERISTICAS_SISTEMAS_POR_SETOR = {
  'E-commerce': [
    { id: 'erp',               chip: 'ERP integrado',              frase: 'ERP integrado' },
    { id: 'plataforma_ecom',   chip: 'Plataforma de e-commerce',   frase: 'plataforma de e-commerce robusta' },
    { id: 'crm',               chip: 'CRM',                        frase: 'CRM de clientes' },
    { id: 'gestao_estoque',    chip: 'Gestão de estoque (WMS)',    frase: 'sistema de gestão de estoque (WMS)' },
    { id: 'pagamento',         chip: 'Plataforma de pagamentos',   frase: 'plataforma de pagamentos integrada' },
    { id: 'automacao_mkt',     chip: 'Automação de marketing',     frase: 'automação de marketing' },
    { id: 'analytics',         chip: 'Analytics e BI (Business Intelligence)',             frase: 'analytics e BI dedicados' },
    { id: 'ia_recomendacao',   chip: 'IA de recomendação',         frase: 'IA de recomendação no catálogo' },
  ],
  'Bar/Restaurante': [
    { id: 'pdv',                chip: 'PDV (frente de caixa)',     frase: 'PDV de frente de caixa' },
    { id: 'gestao_estoque_r',   chip: 'Controle de estoque',       frase: 'controle de estoque integrado ao PDV' },
    { id: 'reservas_online',    chip: 'Reservas online',           frase: 'sistema de reservas online' },
    { id: 'delivery_apps',      chip: 'Integração com delivery',   frase: 'integração com apps de delivery' },
    { id: 'gestao_financeira',  chip: 'Gestão financeira',         frase: 'gestão financeira dedicada' },
    { id: 'erp_restaurante',    chip: 'ERP de restaurante',        frase: 'ERP de restaurante' },
    { id: 'crm_clientes',       chip: 'CRM de clientes',           frase: 'CRM de clientes' },
    { id: 'automacao_compras',  chip: 'Automação de compras',      frase: 'automação de compras com sugestão de pedido' },
  ],
  'Saúde': [
    { id: 'prontuario_eletro', chip: 'Prontuário eletrônico',      frase: 'prontuário eletrônico' },
    { id: 'agendamento_online',chip: 'Agendamento online',         frase: 'agendamento online' },
    { id: 'gestao_convenios_s',chip: 'Gestão de convênios',        frase: 'gestão de convênios' },
    { id: 'telemed_plat',      chip: 'Plataforma de telemedicina', frase: 'plataforma de telemedicina' },
    { id: 'crm_pacientes',     chip: 'CRM de pacientes',           frase: 'CRM de pacientes' },
    { id: 'analytics_saude',   chip: 'Analytics clínico',          frase: 'analytics clínico' },
    { id: 'auto_followup',     chip: 'Automação de follow-up',     frase: 'automação de follow-up' },
    { id: 'ia_apoio_diag',     chip: 'IA de apoio diagnóstico',    frase: 'IA de apoio diagnóstico' },
  ],
  'Construção Civil': [
    { id: 'erp_construcao',   chip: 'ERP de construção',           frase: 'ERP de construção' },
    { id: 'bim_3d',           chip: 'Modelagem BIM/3D',            frase: 'modelagem BIM/3D' },
    { id: 'gestao_canteiro',  chip: 'Gestão de canteiro',          frase: 'sistema de gestão de canteiro' },
    { id: 'gestao_compras_c', chip: 'Sistema de compras',          frase: 'sistema de compras' },
    { id: 'crm_clientes_c',   chip: 'CRM de clientes',             frase: 'CRM de clientes' },
    { id: 'auto_documentos',  chip: 'Automação de documentos',     frase: 'automação de documentos contratuais' },
    { id: 'planejamento_obra',chip: 'Planejamento de obra',        frase: 'planejamento de obra com cronograma' },
    { id: 'analytics_kpis',   chip: 'Relatórios de indicadores operacionais (BI)',     frase: 'BI de KPIs operacionais' },
  ],
  'geral': [
    { id: 'erp_g',            chip: 'ERP',                          frase: 'ERP' },
    { id: 'crm_g',            chip: 'CRM',                          frase: 'CRM' },
    { id: 'automacao_g',      chip: 'Automação de processos',       frase: 'automação de processos' },
    { id: 'analytics_g',      chip: 'Analytics e BI (Business Intelligence)',               frase: 'analytics e BI' },
    { id: 'pagamento_g',      chip: 'Plataforma de pagamentos',     frase: 'plataforma de pagamentos' },
    { id: 'financeira_g',     chip: 'Gestão financeira',            frase: 'gestão financeira dedicada' },
    { id: 'ia_g',             chip: 'IA embarcada',                 frase: 'IA embarcada em processos-chave' },
    { id: 'comunicacao_g',    chip: 'Plataforma de comunicação',    frase: 'plataforma de comunicação interna' },
  ],
};

const TEMPLATE_FRASE_ATIVIDADES = {
  'E-commerce':       'Opera com {LISTA}.',
  'Bar/Restaurante':  'Opera com {LISTA}.',
  'Saúde':            'Opera com {LISTA}.',
  'Construção Civil': 'Opera com {LISTA}.',
  'geral':            'Opera com {LISTA}.',
};
const TEMPLATE_FRASE_SISTEMAS = {
  'E-commerce':       'Sustenta a operação com {LISTA}.',
  'Bar/Restaurante':  'Sustenta a operação com {LISTA}.',
  'Saúde':            'Sustenta a operação com {LISTA}.',
  'Construção Civil': 'Sustenta a operação com {LISTA}.',
  'geral':            'Sustenta a operação com {LISTA}.',
};

const montarFraseAtividades = (setor, ids, extras, modo = 'EE') => {
  const fonte = modo === 'SU' ? CARACTERISTICAS_ATIVIDADES_POR_SETOR_SU : CARACTERISTICAS_ATIVIDADES_POR_SETOR;
  const catalogo = fonte[setor] || fonte['geral'];
  const partes = (ids || [])
    .map(id => catalogo.find(c => c.id === id)?.frase)
    .filter(Boolean)
    .concat((extras || []).map(e => (e || '').trim().toLowerCase()).filter(Boolean));
  if (partes.length === 0) return '';
  const tmpl = TEMPLATE_FRASE_ATIVIDADES[setor] || TEMPLATE_FRASE_ATIVIDADES['geral'];
  return tmpl.replace('{LISTA}', juntarEmProsa(partes));
};
const montarFraseSistemas = (setor, ids, extras, modo = 'EE') => {
  const fonte = modo === 'SU' ? CARACTERISTICAS_SISTEMAS_POR_SETOR_SU : CARACTERISTICAS_SISTEMAS_POR_SETOR;
  const catalogo = fonte[setor] || fonte['geral'];
  const partes = (ids || [])
    .map(id => catalogo.find(c => c.id === id)?.frase)
    .filter(Boolean)
    .concat((extras || []).map(e => (e || '').trim().toLowerCase()).filter(Boolean));
  if (partes.length === 0) return '';
  const tmpl = TEMPLATE_FRASE_SISTEMAS[setor] || TEMPLATE_FRASE_SISTEMAS['geral'];
  return tmpl.replace('{LISTA}', juntarEmProsa(partes));
};

// Tipos de fornecedor por setor — sugestões pra dropdown na lista editável
const TIPOS_FORNECEDOR_POR_SETOR = {
  'E-commerce': [
    { id: 'fabricantes',       label: 'Fabricantes/marcas' },
    { id: 'distribuidores',    label: 'Distribuidores' },
    { id: 'logistica',         label: 'Transportadora/logística' },
    { id: 'plataforma_ecom',   label: 'Plataforma de e-commerce' },
    { id: 'meio_pagamento',    label: 'Adquirente/Gateway de pagamento' },
    { id: 'midias_ads',        label: 'Plataformas de mídia (Meta/Google)' },
    { id: 'outros',            label: 'Outros' },
  ],
  'Bar/Restaurante': [
    { id: 'produtores_locais', label: 'Produtores locais' },
    { id: 'distrib_atacado',   label: 'Distribuidores atacadistas' },
    { id: 'bebidas',           label: 'Fornecedores de bebidas' },
    { id: 'equipamentos',      label: 'Fornecedores de equipamentos' },
    { id: 'delivery_apps',     label: 'Apps de delivery (iFood, Rappi)' },
    { id: 'terceirizados',     label: 'Serviços terceirizados' },
    { id: 'outros',            label: 'Outros' },
  ],
  'Saúde': [
    { id: 'laboratorios',      label: 'Laboratórios' },
    { id: 'planos_convenios',  label: 'Convênios e planos' },
    { id: 'equipamentos_med',  label: 'Equipamentos médicos' },
    { id: 'farmaceuticas',     label: 'Farmacêuticas/medicamentos' },
    { id: 'imagem',            label: 'Centro de imagem terceirizado' },
    { id: 'ti_prontuario',     label: 'Fornecedor de prontuário eletrônico' },
    { id: 'outros',            label: 'Outros' },
  ],
  'Construção Civil': [
    { id: 'fornec_materiais',  label: 'Fornecedores de materiais' },
    { id: 'empreiteiros',      label: 'Empreiteiros/mão de obra' },
    { id: 'equipamentos_obra', label: 'Aluguel de equipamentos' },
    { id: 'engenharia_terc',   label: 'Projetos terceirizados (arq/cálculo)' },
    { id: 'transporte_obra',   label: 'Transporte de materiais' },
    { id: 'seguros',           label: 'Seguradoras' },
    { id: 'outros',            label: 'Outros' },
  ],
  'geral': [
    { id: 'materias_primas',   label: 'Matérias-primas' },
    { id: 'servicos_essenc',   label: 'Serviços essenciais' },
    { id: 'tecnologia',        label: 'Tecnologia' },
    { id: 'pagamentos',        label: 'Pagamentos' },
    { id: 'logistica',         label: 'Logística' },
    { id: 'marketing',         label: 'Marketing/Publicidade' },
    { id: 'outros',            label: 'Outros' },
  ],
};

// Opções de capacidade — escala qualitativa estratégica (Bloco 4)
const OPCOES_CAPACIDADE = [
  { id: 'a', label: 'Tranquila — sobra fôlego para crescer 50%+ sem investir' },
  { id: 'b', label: 'Confortável — dá para crescer 20-50% no atual' },
  { id: 'c', label: 'No limite — cresceria pouco sem investir' },
  { id: 'd', label: 'Apertada — já com gargalo em algumas frentes' },
  { id: 'e', label: 'Saturada — precisa investir agora' },
];

const RASCUNHO_TAREFA_9 = {
  shopmax: {
    atividades_chars: ['catalogo_curado', 'compras_estoque', 'logistica_propria', 'marketing_digital', 'ti_plataforma'],
    atividades_extras: [],
    fornecedores: [
      { id_catalogo: 'shopmax_f1', nome: 'Marcas de design (private label)', tipo: 'fabricantes',     concentracao: 25 },
      { id_catalogo: 'shopmax_f2', nome: 'Transportadora (CD próprio)',      tipo: 'logistica',       concentracao: 15 },
      { id_catalogo: 'shopmax_f3', nome: 'Plataforma de e-commerce (Vtex)',  tipo: 'plataforma_ecom', concentracao: 12 },
      { id_catalogo: 'shopmax_f4', nome: 'Gateway de pagamento',             tipo: 'meio_pagamento',  concentracao: 5 },
      { id_catalogo: 'shopmax_f5', nome: 'Meta + Google Ads',                tipo: 'midias_ads',      concentracao: 30 },
    ],
    sistemas_chars: ['erp', 'plataforma_ecom', 'crm', 'automacao_mkt', 'analytics'],
    sistemas_extras: [],
    instalacoes: 'CD próprio em Cajamar (SP) com 4.500 m² · escritório central na zona oeste.',
    expansao: 'Próximo passo: 2º CD em MG ou PR para reduzir frete e ampliar SLA para Sudeste e Sul.',
    capacidade_resposta: 'b',
  },
  sabor_arte: {
    atividades_chars: ['cozinha_operacao', 'salao_atendimento', 'gestao_reservas', 'compras_fornec', 'eventos_priv'],
    atividades_extras: [],
    fornecedores: [
      { id_catalogo: 'sabor_f1', nome: 'Produtores rurais (SP/MG)',      tipo: 'produtores_locais', concentracao: 35 },
      { id_catalogo: 'sabor_f2', nome: 'Distribuidor de carnes premium', tipo: 'distrib_atacado',   concentracao: 25 },
      { id_catalogo: 'sabor_f3', nome: 'Importadora de vinhos',          tipo: 'bebidas',           concentracao: 15 },
      { id_catalogo: 'sabor_f4', nome: 'Limpeza terceirizada',           tipo: 'terceirizados',     concentracao: 5 },
    ],
    sistemas_chars: ['pdv', 'gestao_estoque_r', 'reservas_online', 'crm_clientes'],
    sistemas_extras: [],
    instalacoes: 'Restaurante nos Jardins (SP) com salão de 80 lugares + 2 salas privadas para eventos · cozinha equipada para 150 cobertos por noite.',
    expansao: 'Próximo passo: filial em Itaim ou Pinheiros (mesmo padrão) prevista para os anos 3-4.',
    capacidade_resposta: 'c',
  },
  clinicavida: {
    atividades_chars: ['atendimento_clinico', 'telemedicina', 'agenda_recepcao', 'gestao_convenios', 'followup_pac'],
    atividades_extras: [],
    fornecedores: [
      { id_catalogo: 'clinica_f1', nome: 'Bradesco Saúde',                tipo: 'planos_convenios', concentracao: 28 },
      { id_catalogo: 'clinica_f2', nome: 'SulAmérica',                    tipo: 'planos_convenios', concentracao: 22 },
      { id_catalogo: 'clinica_f3', nome: 'Amil',                          tipo: 'planos_convenios', concentracao: 18 },
      { id_catalogo: 'clinica_f4', nome: 'Laboratório DASA (terceiro)',   tipo: 'laboratorios',     concentracao: 10 },
      { id_catalogo: 'clinica_f5', nome: 'Centro de imagem terceirizado', tipo: 'imagem',           concentracao: 8 },
    ],
    sistemas_chars: ['prontuario_eletro', 'agendamento_online', 'gestao_convenios_s', 'telemed_plat', 'crm_pacientes'],
    sistemas_extras: [],
    instalacoes: 'Clínica em Moema (SP) com 12 consultórios + 2 salas de procedimento + recepção dimensionada para 80 pacientes por dia.',
    expansao: 'Próximo passo: unidade em Vila Mariana ou Brooklin nos próximos 2 a 3 anos · expandir capacidade da telemedicina.',
    capacidade_resposta: 'b',
  },
  construmax: {
    atividades_chars: ['engenharia_proj', 'canteiro_exec', 'compras_insumos', 'gestao_obra', 'seguranca_trab', 'loja_materiais'],
    atividades_extras: [],
    fornecedores: [
      { id_catalogo: 'construmax_f1', nome: 'Cimento Votorantim',   tipo: 'fornec_materiais',  concentracao: 20 },
      { id_catalogo: 'construmax_f2', nome: 'Aço Gerdau',           tipo: 'fornec_materiais',  concentracao: 15 },
      { id_catalogo: 'construmax_f3', nome: 'Empreiteiro estrutura',tipo: 'empreiteiros',      concentracao: 25 },
      { id_catalogo: 'construmax_f4', nome: 'Empreiteiro acabamento',tipo: 'empreiteiros',     concentracao: 18 },
      { id_catalogo: 'construmax_f5', nome: 'Locação de equipamentos',tipo: 'equipamentos_obra',concentracao: 10 },
    ],
    sistemas_chars: ['erp_construcao', 'gestao_canteiro', 'gestao_compras_c', 'planejamento_obra'],
    sistemas_extras: [],
    instalacoes: 'Sede administrativa + canteiro modular (kit completo de barracos, central de armadura, refeitório) que se monta em até 30 dias em qualquer obra.',
    expansao: 'Próximo passo: 2º kit de canteiro para atender 2 obras simultâneas + ampliar loja própria de insumos para atender atacado.',
    capacidade_resposta: 'c',
  },
};

// ═══════════════════════════════════════════════════════════════════════
// Catálogos SU (Startup) da T9. Atividades seguem por setor (a operação
// muda por ramo). Sistemas e Fornecedores são STACK técnico — iguais em
// qualquer setor → lista única ('geral'). Selecionados quando modo === 'SU'.
// ═══════════════════════════════════════════════════════════════════════
const CARACTERISTICAS_ATIVIDADES_POR_SETOR_SU = {
  'E-commerce': [
    { id: 'curadoria_ia',        chip: 'Curadoria por IA',              frase: 'curadoria de catálogo por IA' },
    { id: 'plataforma_propria',  chip: 'Operação da plataforma própria', frase: 'operação da plataforma própria' },
    { id: 'pipeline_dados',      chip: 'Pipeline de dados',             frase: 'pipeline de dados e recomendação' },
    { id: 'growth',              chip: 'Marketing de crescimento',      frase: 'marketing de crescimento (growth)' },
    { id: 'logistica_3pl',       chip: 'Logística terceirizada (3PL)',  frase: 'logística terceirizada (3PL)' },
    { id: 'atendimento_selfserve', chip: 'Atendimento self-serve',      frase: 'atendimento self-serve e chat' },
    { id: 'gestao_marketplace',  chip: 'Gestão de vendedores',          frase: 'gestão de vendedores no marketplace' },
    { id: 'ciclos_produto',      chip: 'Ciclos curtos de produto',      frase: 'ciclos curtos de produto' },
  ],
  'Bar/Restaurante': [
    { id: 'cozinha_op',          chip: 'Operação de cozinha',           frase: 'operação de cozinha' },
    { id: 'app_pedidos',         chip: 'App de pedidos self-serve',     frase: 'app de pedidos self-serve' },
    { id: 'dados_cardapio',      chip: 'Dados de cardápio',             frase: 'dados de cardápio e recompra' },
    { id: 'growth_local',        chip: 'Growth local e comunidade',     frase: 'growth local e comunidade' },
    { id: 'delivery_op',         chip: 'Delivery próprio',              frase: 'operação de delivery próprio' },
    { id: 'fidelidade_op',       chip: 'Fidelidade digital',            frase: 'programa de fidelidade digital' },
    { id: 'ciclos_menu',         chip: 'Ciclos curtos de menu',         frase: 'ciclos curtos de cardápio' },
    { id: 'suporte_cliente',     chip: 'Suporte ao cliente',            frase: 'suporte ao cliente' },
  ],
  'Saúde': [
    { id: 'telemed_op',          chip: 'Operação de telemedicina',      frase: 'operação de telemedicina' },
    { id: 'agenda_selfserve',    chip: 'Agendamento self-serve',        frase: 'agendamento self-serve' },
    { id: 'pipeline_dados_saude',chip: 'Dados e prontuário',            frase: 'pipeline de dados e prontuário' },
    { id: 'growth_saude',        chip: 'Growth e parcerias',            frase: 'growth e parcerias' },
    { id: 'jornada_paciente',    chip: 'Jornada do paciente',           frase: 'gestão da jornada do paciente' },
    { id: 'followup_auto',       chip: 'Follow-up automatizado',        frase: 'follow-up automatizado' },
    { id: 'conteudo_saude',      chip: 'Conteúdo educativo',            frase: 'conteúdo educativo em saúde' },
    { id: 'suporte_paciente',    chip: 'Suporte ao paciente',           frase: 'suporte ao paciente' },
  ],
  'Construção Civil': [
    { id: 'plataforma_obra_op',  chip: 'Operação da plataforma de obra', frase: 'operação da plataforma de obra' },
    { id: 'onboarding_cliente',  chip: 'Onboarding do cliente',         frase: 'onboarding do cliente' },
    { id: 'pipeline_dados_obra', chip: 'Dados de produtividade',        frase: 'pipeline de dados de produtividade' },
    { id: 'growth_b2b',          chip: 'Growth e vendas B2B',           frase: 'growth e vendas B2B' },
    { id: 'suporte_tecnico_op',  chip: 'Suporte técnico',               frase: 'suporte técnico ao construtor' },
    { id: 'marketplace_insumos_op', chip: 'Marketplace de insumos',     frase: 'operação do marketplace de insumos' },
    { id: 'ciclos_produto_obra', chip: 'Ciclos curtos de produto',      frase: 'ciclos curtos de produto' },
    { id: 'sucesso_cliente',     chip: 'Sucesso do cliente',            frase: 'sucesso do cliente' },
  ],
  'geral': [
    { id: 'produto_op',          chip: 'Operação do produto',           frase: 'operação do produto principal' },
    { id: 'atendimento_selfserve_g', chip: 'Atendimento self-serve',    frase: 'atendimento self-serve' },
    { id: 'pipeline_dados_g',    chip: 'Pipeline de dados',             frase: 'pipeline de dados e métricas' },
    { id: 'growth_g',            chip: 'Marketing de crescimento',      frase: 'marketing de crescimento (growth)' },
    { id: 'vendas_g',            chip: 'Vendas',                        frase: 'vendas' },
    { id: 'sucesso_cliente_g',   chip: 'Sucesso do cliente',            frase: 'sucesso do cliente' },
    { id: 'ciclos_produto_g',    chip: 'Ciclos curtos de produto',      frase: 'ciclos curtos de produto' },
    { id: 'operacao_dados_g',    chip: 'Operação orientada a dados',    frase: 'operação orientada a dados' },
  ],
};

// Sistemas SU = stack técnico · lista única (fallback 'geral' cobre todos os setores)
const CARACTERISTICAS_SISTEMAS_POR_SETOR_SU = {
  'geral': [
    { id: 'linguagem',           chip: 'Linguagem e framework',         frase: 'linguagem e framework principal' },
    { id: 'arquitetura',         chip: 'Arquitetura (serviços)',        frase: 'arquitetura em serviços ou monolito' },
    { id: 'nuvem',               chip: 'Provedor de nuvem',             frase: 'provedor de nuvem' },
    { id: 'cicd',                chip: 'CI/CD (entrega contínua)',      frase: 'automação de entrega (CI/CD)' },
    { id: 'banco_dados',         chip: 'Banco de dados gerenciado',     frase: 'banco de dados gerenciado' },
    { id: 'painel_dados',        chip: 'Painel de dados e métricas',    frase: 'painel de dados e métricas' },
    { id: 'atendimento_sistema', chip: 'Sistema de atendimento',        frase: 'sistema de atendimento ao cliente' },
    { id: 'ferramentas_ia',      chip: 'Ferramentas de IA',             frase: 'ferramentas de IA' },
  ],
};

// Fornecedores SU = parceiros de tecnologia · lista única (fallback 'geral')
const TIPOS_FORNECEDOR_POR_SETOR_SU = {
  'geral': [
    { id: 'nuvem',       label: 'Provedores de nuvem (AWS · Google Cloud · Azure)' },
    { id: 'pagamento',   label: 'Plataformas de pagamento (Stripe · Adyen · Mercado Pago)' },
    { id: 'identidade',  label: 'Identidade e autenticação' },
    { id: 'comunicacao', label: 'Comunicação (e-mail · SMS · push)' },
    { id: 'dados_ia',    label: 'Dados / Inteligência Artificial' },
    { id: 'canal',       label: 'Parceiros de canal e revenda' },
    { id: 'outros',      label: 'Outros' },
  ],
};

const RASCUNHO_TAREFA_9_SU = {
  shopmax: {
    atividades_chars: ['curadoria_ia', 'plataforma_propria', 'pipeline_dados', 'growth', 'atendimento_selfserve'],
    atividades_extras: [],
    fornecedores: [
      { id_catalogo: 'sm_f1', nome: 'Amazon Web Services',        tipo: 'nuvem',       concentracao: 20 },
      { id_catalogo: 'sm_f2', nome: 'Stripe',                     tipo: 'pagamento',   concentracao: 12 },
      { id_catalogo: 'sm_f3', nome: 'Meta + Google Ads',          tipo: 'canal',       concentracao: 30 },
      { id_catalogo: 'sm_f4', nome: 'Provedor de identidade (Auth)', tipo: 'identidade', concentracao: 5 },
      { id_catalogo: 'sm_f5', nome: 'Ferramentas de IA (recomendação)', tipo: 'dados_ia', concentracao: 10 },
    ],
    sistemas_chars: ['linguagem', 'arquitetura', 'nuvem', 'painel_dados', 'ferramentas_ia'],
    sistemas_extras: [],
    instalacoes: 'Operação remota-first · sem estrutura física própria (logística via 3PL).',
    expansao: 'Próximo passo: ampliar capacidade da plataforma e o time de produto.',
    capacidade_resposta: 'b',
    disponibilidade: 99.9,
    capacidade_escala: 50000,
  },
  sabor_arte: {
    atividades_chars: ['app_pedidos', 'dados_cardapio', 'growth_local', 'delivery_op', 'fidelidade_op'],
    atividades_extras: [],
    fornecedores: [
      { id_catalogo: 'sa_f1', nome: 'Google Cloud',               tipo: 'nuvem',       concentracao: 18 },
      { id_catalogo: 'sa_f2', nome: 'Mercado Pago',               tipo: 'pagamento',   concentracao: 15 },
      { id_catalogo: 'sa_f3', nome: 'Comunicação (WhatsApp · e-mail)', tipo: 'comunicacao', concentracao: 12 },
      { id_catalogo: 'sa_f4', nome: 'Apps de delivery (parceiros)', tipo: 'canal',     concentracao: 25 },
    ],
    sistemas_chars: ['linguagem', 'nuvem', 'painel_dados', 'atendimento_sistema'],
    sistemas_extras: [],
    instalacoes: 'Cozinha compartilhada (dark kitchen) · operação enxuta.',
    expansao: 'Próximo passo: 2ª cozinha e ampliar a base de assinantes.',
    capacidade_resposta: 'c',
    disponibilidade: 99.5,
    capacidade_escala: 10000,
  },
  clinicavida: {
    atividades_chars: ['telemed_op', 'agenda_selfserve', 'pipeline_dados_saude', 'growth_saude', 'followup_auto'],
    atividades_extras: [],
    fornecedores: [
      { id_catalogo: 'cv_f1', nome: 'Microsoft Azure',            tipo: 'nuvem',       concentracao: 20 },
      { id_catalogo: 'cv_f2', nome: 'Stripe',                     tipo: 'pagamento',   concentracao: 10 },
      { id_catalogo: 'cv_f3', nome: 'Identidade (conformidade LGPD)', tipo: 'identidade', concentracao: 12 },
      { id_catalogo: 'cv_f4', nome: 'Ferramentas de IA de triagem', tipo: 'dados_ia',  concentracao: 15 },
      { id_catalogo: 'cv_f5', nome: 'Plataforma de comunicação',  tipo: 'comunicacao', concentracao: 8 },
    ],
    sistemas_chars: ['linguagem', 'nuvem', 'banco_dados', 'painel_dados', 'ferramentas_ia'],
    sistemas_extras: [],
    instalacoes: 'Atendimento majoritariamente digital · consultório parceiro sob demanda.',
    expansao: 'Próximo passo: ampliar telemedicina e a base de assinantes preventivos.',
    capacidade_resposta: 'b',
    disponibilidade: 99.9,
    capacidade_escala: 20000,
  },
  construmax: {
    atividades_chars: ['plataforma_obra_op', 'onboarding_cliente', 'pipeline_dados_obra', 'growth_b2b', 'suporte_tecnico_op'],
    atividades_extras: [],
    fornecedores: [
      { id_catalogo: 'cx_f1', nome: 'Amazon Web Services',        tipo: 'nuvem',       concentracao: 18 },
      { id_catalogo: 'cx_f2', nome: 'Adyen',                      tipo: 'pagamento',   concentracao: 8 },
      { id_catalogo: 'cx_f3', nome: 'Parceiros de canal (revenda)', tipo: 'canal',     concentracao: 25 },
      { id_catalogo: 'cx_f4', nome: 'Ferramentas de dados / BI',  tipo: 'dados_ia',    concentracao: 12 },
    ],
    sistemas_chars: ['linguagem', 'arquitetura', 'nuvem', 'painel_dados'],
    sistemas_extras: [],
    instalacoes: 'Time remoto-first · sem estrutura física própria.',
    expansao: 'Próximo passo: escalar o produto para novas construtoras e regiões.',
    capacidade_resposta: 'c',
    disponibilidade: 99.5,
    capacidade_escala: 5000,
  },
};

const ConteudoTarefa9 = ({ planoOperacional, setPlanoOperacional, empresa, modo = 'EE' }) => {
  const rascunho = (modo === 'SU' ? RASCUNHO_TAREFA_9_SU : RASCUNHO_TAREFA_9)[empresa?.id] || {
    atividades_chars: [], atividades_extras: [],
    fornecedores: [],
    sistemas_chars: [], sistemas_extras: [],
    instalacoes: '', expansao: '', capacidade_resposta: '',
    disponibilidade: '', capacidade_escala: '',
  };
  const obter = (campo) => planoOperacional?.[campo] ?? rascunho[campo];
  const atividades_chars        = obter('atividades_chars') || [];
  const atividades_extras       = obter('atividades_extras') || [];
  const atividades_livre        = obter('atividades_livre');
  const atividades_editou_livre = obter('atividades_editou_livre') || false;
  const fornecedores            = obter('fornecedores') || [];
  const sistemas_chars          = obter('sistemas_chars') || [];
  const sistemas_extras         = obter('sistemas_extras') || [];
  const sistemas_livre          = obter('sistemas_livre');
  const sistemas_editou_livre   = obter('sistemas_editou_livre') || false;
  const instalacoes             = obter('instalacoes') || '';
  const expansao                = obter('expansao') || '';
  const instalacoes_editou_livre = obter('instalacoes_editou_livre') || false;
  const expansao_editou_livre    = obter('expansao_editou_livre') || false;
  const capacidade_resposta     = obter('capacidade_resposta') || '';
  const disponibilidade         = obter('disponibilidade') || '';
  const capacidade_escala       = obter('capacidade_escala') || '';

  const setor = empresa?.setor || 'geral';
  const fonteAtividades = modo === 'SU' ? CARACTERISTICAS_ATIVIDADES_POR_SETOR_SU : CARACTERISTICAS_ATIVIDADES_POR_SETOR;
  const fonteSistemas   = modo === 'SU' ? CARACTERISTICAS_SISTEMAS_POR_SETOR_SU : CARACTERISTICAS_SISTEMAS_POR_SETOR;
  const fonteFornecedor = modo === 'SU' ? TIPOS_FORNECEDOR_POR_SETOR_SU : TIPOS_FORNECEDOR_POR_SETOR;
  const catalogoAtividades = fonteAtividades[setor] || fonteAtividades['geral'];
  const catalogoSistemas   = fonteSistemas[setor]   || fonteSistemas['geral'];
  const tiposFornecedor    = fonteFornecedor[setor] || fonteFornecedor['geral'];

  const fraseAtividades = atividades_editou_livre && atividades_livre
    ? atividades_livre
    : montarFraseAtividades(setor, atividades_chars, atividades_extras, modo);
  const fraseSistemas = sistemas_editou_livre && sistemas_livre
    ? sistemas_livre
    : montarFraseSistemas(setor, sistemas_chars, sistemas_extras, modo);

  const [novoChipAtividade, setNovoChipAtividade] = useState('');
  const [novoChipSistema,   setNovoChipSistema]   = useState('');
  const [perguntaAberta, setPerguntaAberta] = useState(null);

  const atualizar = (campo, valor) => {
    setPlanoOperacional(prev => ({
      atividades_chars, atividades_extras, atividades_livre, atividades_editou_livre,
      fornecedores,
      sistemas_chars, sistemas_extras, sistemas_livre, sistemas_editou_livre,
      instalacoes, expansao, capacidade_resposta,
      disponibilidade, capacidade_escala,
      ...prev,
      [campo]: valor,
    }));
  };
  const toggleChip = (campo, id) => {
    const lista = obter(campo) || [];
    const novo = lista.includes(id) ? lista.filter(x => x !== id) : [...lista, id];
    atualizar(campo, novo);
  };
  const adicionarExtra = (campo, texto) => {
    const t = (texto || '').trim();
    if (!t) return;
    const lista = obter(campo) || [];
    if (lista.map(x => x.toLowerCase()).includes(t.toLowerCase())) return;
    atualizar(campo, [...lista, t]);
  };
  const removerExtra = (campo, idx) => {
    const lista = obter(campo) || [];
    atualizar(campo, lista.filter((_, i) => i !== idx));
  };

  // Fornecedores (lista editável)
  const adicionarFornecedor = () => {
    atualizar('fornecedores', [...fornecedores, { id_catalogo: null, nome: '', tipo: tiposFornecedor[0]?.id || '', concentracao: 0 }]);
  };
  const catalogoForn = (rascunho.fornecedores || []);
  const chipFornMarcado = (catId) => fornecedores.some(f => f.id_catalogo === catId);
  const toggleChipForn = (catItem) => {
    if (chipFornMarcado(catItem.id_catalogo)) atualizar('fornecedores', fornecedores.filter(f => f.id_catalogo !== catItem.id_catalogo));
    else atualizar('fornecedores', [...fornecedores, catItem]);
  };
  const editarFornecedor = (idx, campo, valor) => {
    const novo = fornecedores.map((f, i) => i === idx ? { ...f, [campo]: valor } : f);
    atualizar('fornecedores', novo);
  };
  const removerFornecedor = (idx) => {
    atualizar('fornecedores', fornecedores.filter((_, i) => i !== idx));
  };

  const setarRespostaCapacidade = (opcaoId) => {
    atualizar('capacidade_resposta', opcaoId);
    setPerguntaAberta(null);
  };
  const togglePerguntaCapacidade = () => {
    setPerguntaAberta(perguntaAberta === 'capacidade' ? null : 'capacidade');
  };

  const inputBase = {
    width: '100%', padding: '8px 12px', fontSize: 13,
    border: `1px solid ${G.borda}`, borderRadius: 0,
    fontFamily: "'Outfit', sans-serif", color: G.txt1,
    background: '#FFF', boxSizing: 'border-box',
  };
  const HEADER_BLOCO = {
    fontSize: 16, fontWeight: 700, letterSpacing: '0.10em',
    textTransform: 'uppercase', color: G.laranja, marginBottom: 10,
  };
  const HEADER_AUX = {
    fontSize: 12, fontWeight: 400, color: G.txt2,
    textTransform: 'none', letterSpacing: 0, marginLeft: 10,
  };
  const SUB_HEADER = {
    fontSize: 12, fontWeight: 700, color: G.txt2,
    textTransform: 'uppercase', letterSpacing: '0.06em',
    marginBottom: 6,
  };

  // Estilo unificado dos botões outline laranja (padrão 1 do catálogo)
  const botaoEditar = (cor) => ({
    background: '#FFF', color: cor,
    border: `1px solid ${cor}`,
    padding: '5px 12px', minWidth: 170, textAlign: 'center', fontSize: 11, fontWeight: 700,
    letterSpacing: '0.06em', textTransform: 'uppercase',
    borderRadius: 0, cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0,
  });

  const opcaoCapacidadeSel = OPCOES_CAPACIDADE.find(o => o.id === capacidade_resposta);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
      {/* 2 quadros lado a lado: Definição + Instruções */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <div style={{ padding: '12px 16px', background: G.verdeLight,
          borderLeft: `4px solid ${G.verde}`, borderRadius: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 800, color: G.verde,
            letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 6 }}>
            O que é Plano Operacional
          </div>
          <div style={{ fontSize: 12, lineHeight: 1.45, color: G.txt1 }}>
            Descreve <strong>como o negócio funciona no dia a dia</strong>: atividades
            que entregam valor ao cliente, fornecedores que sustentam a operação,
            sistemas que dão suporte e a estrutura física que suporta o crescimento.
          </div>
        </div>
        <div style={{ padding: '12px 16px', background: G.laranjaLight,
          borderLeft: `4px solid ${G.laranja}`, borderRadius: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 800, color: G.laranja,
            letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 6 }}>
            O que você faz aqui
          </div>
          <div style={{ fontSize: 12, lineHeight: 1.45, color: G.txt1 }}>
            Revise as <strong>atividades</strong> e os <strong>sistemas</strong>
            propostos · ajuste a <strong>lista de fornecedores</strong> e a
            <strong> concentração</strong> · descreva a <strong>estrutura física</strong>
            e escolha a <strong>folga de capacidade</strong> atual.
          </div>
        </div>
      </div>

      {/* BLOCO 1 — Como o negócio funciona */}
      <div>
        <div style={HEADER_BLOCO}>
          Bloco 1 · Como o negócio funciona
          <span style={HEADER_AUX}>· atividades principais propostas pela <MarcaAIdvisor /></span>
        </div>
        {/* Texto explicativo curto */}
        <div style={{ padding: '10px 14px', background: G.fundo,
          border: `1px solid ${G.borda}`, borderRadius: 0, marginBottom: 12,
          fontSize: 12, lineHeight: 1.45, color: G.txt1 }}>
          <MarcaAIdvisor /> selecionou atividades típicas do seu setor
          (<strong>{setor}</strong>) e pré-marcou as que costumam fazer parte
          da operação. <strong>Marque ou desmarque</strong> conforme o que se
          aplica · <strong>adicione</strong> atividades próprias se faltar
          alguma · a frase abaixo é <strong>recomposta automaticamente</strong>.
          Se preferir, edite o texto livremente.
        </div>
        {/* Linha topo: título + frase composta + botão editar */}
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 8 }}>
          <div style={{ ...SUB_HEADER, marginBottom: 0, paddingTop: 8, flexShrink: 0, width: 180 }}>
            Atividades principais
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            {atividades_editou_livre ? (
              <textarea value={atividades_livre || ''}
                onChange={e => atualizar('atividades_livre', e.target.value)}
                rows={3} style={{ ...inputBase, lineHeight: 1.45, resize: 'vertical',
                  background: G.laranjaLight, borderColor: G.laranja }}/>
            ) : (
              <div style={{ padding: '10px 14px', background: G.verdeLight,
                borderLeft: `3px solid ${G.verde}`, borderRadius: 0,
                fontSize: 13, lineHeight: 1.5, color: G.txt1, fontStyle: 'italic' }}>
                {fraseAtividades || '_Selecione pelo menos uma atividade para compor a frase._'}
              </div>
            )}
          </div>
          <div>
            {atividades_editou_livre ? (
              <button onClick={() => { atualizar('atividades_editou_livre', false); atualizar('atividades_livre', ''); }}
                style={botaoEditar(G.verde)}>
                <IconeReverter /> Voltar à <MarcaAIdvisor variant="default" />
              </button>
            ) : (
              <button onClick={() => { atualizar('atividades_livre', fraseAtividades); atualizar('atividades_editou_livre', true); }}
                style={botaoEditar(G.laranja)}>
                <IconeLapis /> Editar livremente
              </button>
            )}
          </div>
        </div>
        {/* Chips */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 8 }}>
          {catalogoAtividades.map(c => {
            const sel = atividades_chars.includes(c.id);
            return (
              <button key={c.id} onClick={() => toggleChip('atividades_chars', c.id)}
                style={{
                  padding: '5px 11px', fontSize: 12, fontWeight: 600,
                  background: sel ? G.verde : '#FFF',
                  color: sel ? '#FFF' : G.txt1,
                  border: `1px solid ${sel ? G.verde : G.borda}`,
                  borderRadius: 999, cursor: 'pointer',
                  fontFamily: "'Outfit', sans-serif",
                }}>
                {sel ? '✓ ' : ''}{c.chip}
              </button>
            );
          })}
          {atividades_extras.map((extra, idx) => (
            <button key={`extra-${idx}`} onClick={() => removerExtra('atividades_extras', idx)}
              style={{
                padding: '5px 11px', fontSize: 12, fontWeight: 600,
                background: G.laranja, color: '#FFF',
                border: `1px solid ${G.laranja}`,
                borderRadius: 999, cursor: 'pointer',
                fontFamily: "'Outfit', sans-serif",
              }}>
              ✓ {extra} ✕
            </button>
          ))}
        </div>
        <div style={{ display: 'flex', gap: 6 }}>
          <input type="text" value={novoChipAtividade}
            onChange={e => setNovoChipAtividade(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter') {
                adicionarExtra('atividades_extras', novoChipAtividade);
                setNovoChipAtividade('');
              }
            }}
            placeholder="Adicionar atividade própria…"
            style={{ ...inputBase, fontSize: 12, padding: '6px 10px' }}/>
          <button onClick={() => { adicionarExtra('atividades_extras', novoChipAtividade); setNovoChipAtividade(''); }}
            style={{ background: G.laranja, color: '#FFF', border: 'none',
              padding: '6px 14px', fontSize: 11, fontWeight: 700,
              letterSpacing: '0.06em', textTransform: 'uppercase',
              borderRadius: 0, cursor: 'pointer', whiteSpace: 'nowrap' }}>
            + Adicionar
          </button>
        </div>
      </div>

      {/* BLOCO 2 — Fornecedores e Parceiros Críticos */}
      <div>
        <div style={HEADER_BLOCO}>
          Bloco 2 · Fornecedores e Parceiros Críticos
          <span style={HEADER_AUX}>· quem sustenta a operação · % indica peso na operação</span>
        </div>
        <div style={{ padding: '10px 14px', background: G.fundo,
          border: `1px solid ${G.borda}`, borderRadius: 0, marginBottom: 12,
          fontSize: 12, lineHeight: 1.45, color: G.txt1 }}>
          <MarcaAIdvisor /> pesquisou nas suas transações bancárias e cadastros
          os fornecedores mais relevantes. Marque os que se confirmam ·
          <strong> fornecedor acima de 30%</strong> é sinal de dependência alta que pesa no BP.
        </div>
        {catalogoForn.length > 0 && (
          <div style={{ background: G.fundo, border: `1px solid ${G.borda}`,
            borderRadius: 0, padding: '10px 14px', marginBottom: 10 }}>
            <div style={{ fontSize: 10, fontWeight: 800, color: G.txt2,
              letterSpacing: '0.10em', textTransform: 'uppercase', marginBottom: 8 }}>
              Encontrado pela IA · marque os que se confirmam
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {catalogoForn.map(c => {
                const ativo = chipFornMarcado(c.id_catalogo);
                return (
                  <button key={c.id_catalogo} type="button"
                    onClick={() => toggleChipForn(c)}
                    title={`${c.nome} · ${c.concentracao}% da operação`}
                    style={{
                      padding: '5px 11px', fontSize: 12, fontWeight: 600,
                      background: ativo ? G.verde : '#FFF',
                      color: ativo ? '#FFF' : G.txt1,
                      border: `1px solid ${ativo ? G.verde : G.borda}`,
                      borderRadius: 999, cursor: 'pointer',
                      fontFamily: "'Outfit', sans-serif",
                    }}>
                    {ativo ? '✓ ' : ''}{c.nome}
                  </button>
                );
              })}
            </div>
          </div>
        )}
        <div style={{ background: '#FFF', border: `1px solid ${G.borda}`, borderRadius: 0 }}>
          {fornecedores.map((f, idx) => {
            const alerta = (Number(f.concentracao) || 0) > 30;
            const doCatalogo = !!f.id_catalogo;
            return (
              <div key={idx} style={{
                display: 'grid', gridTemplateColumns: '1.6fr 1.2fr 100px 36px',
                alignItems: 'center', gap: 10, padding: '8px 14px',
                borderTop: idx === 0 ? 'none' : `1px solid ${G.borda}`,
                background: alerta ? '#FFFBEB' : 'transparent',
              }}>
                {doCatalogo ? (
                  <>
                    <div style={{ padding: '6px 10px', fontSize: 13, fontWeight: 700, color: G.txt1 }}>
                      {f.nome}
                    </div>
                    <div style={{ padding: '6px 10px', fontSize: 12, color: G.txt2, fontStyle: 'italic' }}>
                      {(tiposFornecedor.find(t => t.id === f.tipo) || {}).label || f.tipo}
                    </div>
                    <div style={{ minWidth: 110 }}>
                      <CampoNumericoBP60M
                        valor={f.concentracao ?? 0}
                        onChange={(v) => editarFornecedor(idx, 'concentracao', v ?? 0)}
                        label={`Concentração · ${f.nome || 'fornecedor'}`}
                        permitirDecimal={false}
                        min={0} max={100}
                        sufixo="%"
                        formato="int"
                        alerta={(Number(f.concentracao) || 0) > 30}
                      />
                      {(Number(f.concentracao) || 0) > 30 && (
                        <div style={{ fontSize: 10, color: G.vermelho,
                          fontWeight: 700, marginTop: 4, textAlign: 'right',
                          fontStyle: 'italic' }}>
                          <IconeAlerta /> acima de 30%
                        </div>
                      )}
                    </div>
                  </>
                ) : (
                  <>
                    <input value={f.nome}
                      onChange={e => editarFornecedor(idx, 'nome', e.target.value)}
                      placeholder="Nome do fornecedor próprio"
                      style={{ ...inputBase, padding: '6px 10px' }}/>
                    <select value={f.tipo}
                      onChange={e => editarFornecedor(idx, 'tipo', e.target.value)}
                      style={{ ...inputBase, padding: '6px 10px' }}>
                      {tiposFornecedor.map(t => (
                        <option key={t.id} value={t.id}>{t.label}</option>
                      ))}
                    </select>
                    <div style={{ minWidth: 110 }}>
                      <CampoNumericoBP60M
                        valor={f.concentracao ?? 0}
                        onChange={(v) => editarFornecedor(idx, 'concentracao', v ?? 0)}
                        label={`Concentração · ${f.nome || 'fornecedor'}`}
                        permitirDecimal={false}
                        min={0} max={100}
                        sufixo="%"
                        formato="int"
                        alerta={(Number(f.concentracao) || 0) > 30}
                      />
                      {(Number(f.concentracao) || 0) > 30 && (
                        <div style={{ fontSize: 10, color: G.vermelho,
                          fontWeight: 700, marginTop: 4, textAlign: 'right',
                          fontStyle: 'italic' }}>
                          <IconeAlerta /> acima de 30%
                        </div>
                      )}
                    </div>
                  </>
                )}
                <button onClick={() => removerFornecedor(idx)}
                  title="Remover"
                  style={{ background: 'transparent', border: 'none', color: G.vermelho,
                    cursor: 'pointer', fontSize: 16, padding: 0 }}>
                  ✕
                </button>
              </div>
            );
          })}
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          marginTop: 8, gap: 10 }}>
          <button onClick={adicionarFornecedor}
            style={{ background: G.laranja, color: '#FFF', border: 'none',
              padding: '6px 14px', fontSize: 11, fontWeight: 700,
              letterSpacing: '0.06em', textTransform: 'uppercase',
              borderRadius: 0, cursor: 'pointer' }}>
            + Adicionar fornecedor próprio
          </button>
        </div>
      </div>

      {/* BLOCO 3 — Sistemas e Tecnologia */}
      <div>
        <div style={HEADER_BLOCO}>
          Bloco 3 · Sistemas e Tecnologia
          <span style={HEADER_AUX}>· sistemas que sustentam a operação propostos pela <MarcaAIdvisor /></span>
        </div>
        <div style={{ padding: '10px 14px', background: G.fundo,
          border: `1px solid ${G.borda}`, borderRadius: 0, marginBottom: 12,
          fontSize: 12, lineHeight: 1.45, color: G.txt1 }}>
          <MarcaAIdvisor /> selecionou sistemas típicos do seu setor
          (<strong>{setor}</strong>) e pré-marcou os que costumam estar presentes.
          <strong> Marque ou desmarque</strong> conforme o que a empresa já tem ou
          pretende ter · <strong>adicione</strong> sistemas próprios se faltar
          algum · a frase abaixo é <strong>recomposta automaticamente</strong>.
        </div>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 8 }}>
          <div style={{ ...SUB_HEADER, marginBottom: 0, paddingTop: 8, flexShrink: 0, width: 180 }}>
            Sistemas e tecnologia
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            {sistemas_editou_livre ? (
              <textarea value={sistemas_livre || ''}
                onChange={e => atualizar('sistemas_livre', e.target.value)}
                rows={3} style={{ ...inputBase, lineHeight: 1.45, resize: 'vertical',
                  background: G.laranjaLight, borderColor: G.laranja }}/>
            ) : (
              <div style={{ padding: '10px 14px', background: G.verdeLight,
                borderLeft: `3px solid ${G.verde}`, borderRadius: 0,
                fontSize: 13, lineHeight: 1.5, color: G.txt1, fontStyle: 'italic' }}>
                {fraseSistemas || '_Selecione pelo menos um sistema para compor a frase._'}
              </div>
            )}
          </div>
          <div>
            {sistemas_editou_livre ? (
              <button onClick={() => { atualizar('sistemas_editou_livre', false); atualizar('sistemas_livre', ''); }}
                style={botaoEditar(G.verde)}>
                <IconeReverter /> Voltar à <MarcaAIdvisor variant="default" />
              </button>
            ) : (
              <button onClick={() => { atualizar('sistemas_livre', fraseSistemas); atualizar('sistemas_editou_livre', true); }}
                style={botaoEditar(G.laranja)}>
                <IconeLapis /> Editar livremente
              </button>
            )}
          </div>
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 8 }}>
          {catalogoSistemas.map(c => {
            const sel = sistemas_chars.includes(c.id);
            return (
              <button key={c.id} onClick={() => toggleChip('sistemas_chars', c.id)}
                style={{
                  padding: '5px 11px', fontSize: 12, fontWeight: 600,
                  background: sel ? G.verde : '#FFF',
                  color: sel ? '#FFF' : G.txt1,
                  border: `1px solid ${sel ? G.verde : G.borda}`,
                  borderRadius: 999, cursor: 'pointer',
                  fontFamily: "'Outfit', sans-serif",
                }}>
                {sel ? '✓ ' : ''}{c.chip}
              </button>
            );
          })}
          {sistemas_extras.map((extra, idx) => (
            <button key={`extra-${idx}`} onClick={() => removerExtra('sistemas_extras', idx)}
              style={{
                padding: '5px 11px', fontSize: 12, fontWeight: 600,
                background: G.laranja, color: '#FFF',
                border: `1px solid ${G.laranja}`,
                borderRadius: 999, cursor: 'pointer',
                fontFamily: "'Outfit', sans-serif",
              }}>
              ✓ {extra} ✕
            </button>
          ))}
        </div>
        <div style={{ display: 'flex', gap: 6 }}>
          <input type="text" value={novoChipSistema}
            onChange={e => setNovoChipSistema(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter') {
                adicionarExtra('sistemas_extras', novoChipSistema);
                setNovoChipSistema('');
              }
            }}
            placeholder="Adicionar sistema próprio…"
            style={{ ...inputBase, fontSize: 12, padding: '6px 10px' }}/>
          <button onClick={() => { adicionarExtra('sistemas_extras', novoChipSistema); setNovoChipSistema(''); }}
            style={{ background: G.laranja, color: '#FFF', border: 'none',
              padding: '6px 14px', fontSize: 11, fontWeight: 700,
              letterSpacing: '0.06em', textTransform: 'uppercase',
              borderRadius: 0, cursor: 'pointer', whiteSpace: 'nowrap' }}>
            + Adicionar
          </button>
        </div>
      </div>

      {/* BLOCO 4 — Estrutura Física e Capacidade */}
      <div>
        <div style={HEADER_BLOCO}>
          Bloco 4 · {modo === 'SU' ? 'Capacidade Operacional' : 'Estrutura Física e Capacidade'}
          <span style={HEADER_AUX}>{modo === 'SU'
            ? '· disponibilidade · capacidade de escala · folga de capacidade'
            : '· instalações · plano de expansão · folga de capacidade'}</span>
        </div>
        <div style={{ padding: '10px 14px', background: G.fundo,
          border: `1px solid ${G.borda}`, borderRadius: 0, marginBottom: 12,
          fontSize: 12, lineHeight: 1.45, color: G.txt1 }}>
          Descreva a <strong>estrutura física atual</strong>, o <strong>plano de
          expansão</strong> previsto e a <strong>folga de capacidade</strong> hoje.
          A folga é o quanto a operação pode crescer no atual antes de precisar
          de investimento — informação crítica pro Business Plan dimensionar CAPEX.
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 4 }}>
              <div style={{ ...SUB_HEADER, marginBottom: 0, paddingTop: 8, flexShrink: 0, width: 180 }}>
                Instalações atuais{modo === 'SU' ? ' (opcional)' : ''}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                {instalacoes_editou_livre ? (
                  <textarea value={instalacoes || ''}
                    onChange={e => atualizar('instalacoes', e.target.value)}
                    rows={2} style={{ ...inputBase, lineHeight: 1.45, resize: 'vertical',
                      background: G.laranjaLight, borderColor: G.laranja }}/>
                ) : (
                  <div style={{ padding: '10px 14px', background: G.verdeLight,
                    borderLeft: `3px solid ${G.verde}`, borderRadius: 0,
                    fontSize: 13, lineHeight: 1.5, color: G.txt1, fontStyle: 'italic' }}>
                    {instalacoes || '_Pesquisa da IA não retornou informações sobre as instalações._'}
                  </div>
                )}
              </div>
              <div style={{ flexShrink: 0 }}>
                {instalacoes_editou_livre ? (
                  <button onClick={() => { atualizar('instalacoes_editou_livre', false); atualizar('instalacoes', rascunho.instalacoes || ''); }}
                    style={{ background: '#FFF', color: G.verde, border: `1px solid ${G.verde}`,
                      padding: '5px 12px', minWidth: 170, textAlign: 'center', fontSize: 11, fontWeight: 700,
                      letterSpacing: '0.06em', textTransform: 'uppercase',
                      borderRadius: 0, cursor: 'pointer', whiteSpace: 'nowrap' }}>
                    <IconeReverter /> Voltar à <MarcaAIdvisor variant="default" />
                  </button>
                ) : (
                  <button onClick={() => atualizar('instalacoes_editou_livre', true)}
                    style={{ background: '#FFF', color: G.laranja, border: `1px solid ${G.laranja}`,
                      padding: '5px 12px', minWidth: 170, textAlign: 'center', fontSize: 11, fontWeight: 700,
                      letterSpacing: '0.06em', textTransform: 'uppercase',
                      borderRadius: 0, cursor: 'pointer', whiteSpace: 'nowrap' }}>
                    <IconeLapis /> Editar livremente
                  </button>
                )}
              </div>
            </div>
          </div>
          <div>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 4 }}>
              <div style={{ ...SUB_HEADER, marginBottom: 0, paddingTop: 8, flexShrink: 0, width: 180 }}>
                Plano de expansão
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                {expansao_editou_livre ? (
                  <textarea value={expansao || ''}
                    onChange={e => atualizar('expansao', e.target.value)}
                    rows={2} style={{ ...inputBase, lineHeight: 1.45, resize: 'vertical',
                      background: G.laranjaLight, borderColor: G.laranja }}/>
                ) : (
                  <div style={{ padding: '10px 14px', background: G.verdeLight,
                    borderLeft: `3px solid ${G.verde}`, borderRadius: 0,
                    fontSize: 13, lineHeight: 1.5, color: G.txt1, fontStyle: 'italic' }}>
                    {expansao || '_Pesquisa da IA não retornou informações sobre expansão._'}
                  </div>
                )}
              </div>
              <div style={{ flexShrink: 0 }}>
                {expansao_editou_livre ? (
                  <button onClick={() => { atualizar('expansao_editou_livre', false); atualizar('expansao', rascunho.expansao || ''); }}
                    style={{ background: '#FFF', color: G.verde, border: `1px solid ${G.verde}`,
                      padding: '5px 12px', minWidth: 170, textAlign: 'center', fontSize: 11, fontWeight: 700,
                      letterSpacing: '0.06em', textTransform: 'uppercase',
                      borderRadius: 0, cursor: 'pointer', whiteSpace: 'nowrap' }}>
                    <IconeReverter /> Voltar à <MarcaAIdvisor variant="default" />
                  </button>
                ) : (
                  <button onClick={() => atualizar('expansao_editou_livre', true)}
                    style={{ background: '#FFF', color: G.laranja, border: `1px solid ${G.laranja}`,
                      padding: '5px 12px', minWidth: 170, textAlign: 'center', fontSize: 11, fontWeight: 700,
                      letterSpacing: '0.06em', textTransform: 'uppercase',
                      borderRadius: 0, cursor: 'pointer', whiteSpace: 'nowrap' }}>
                    <IconeLapis /> Editar livremente
                  </button>
                )}
              </div>
            </div>
          </div>
          {modo === 'SU' && (
            <>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <div style={{ ...SUB_HEADER, marginBottom: 0, flexShrink: 0, width: 180 }}>
                  Disponibilidade do serviço
                </div>
                <div style={{ width: 160 }}>
                  <CampoNumericoBP60M
                    valor={disponibilidade}
                    onChange={(v) => atualizar('disponibilidade', v ?? '')}
                    label="Disponibilidade do serviço"
                    permitirDecimal={true}
                    min={0} max={100}
                    sufixo="%"
                  />
                </div>
                <span style={{ fontSize: 11, color: G.txt2, fontStyle: 'italic' }}>
                  meta de uptime · ex.: 99,5% · 99,9%
                </span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <div style={{ ...SUB_HEADER, marginBottom: 0, flexShrink: 0, width: 180 }}>
                  Capacidade de escala
                </div>
                <div style={{ width: 160 }}>
                  <CampoNumericoBP60M
                    valor={capacidade_escala}
                    onChange={(v) => atualizar('capacidade_escala', v ?? '')}
                    label="Capacidade de escala"
                    permitirDecimal={false}
                    formato="int"
                  />
                </div>
                <span style={{ fontSize: 11, color: G.txt2, fontStyle: 'italic' }}>
                  usuários simultâneos suportados
                </span>
              </div>
            </>
          )}
          <div>
            <div style={SUB_HEADER}>Folga de capacidade hoje</div>
            <div style={{
              background: '#FFF',
              border: `1px solid ${perguntaAberta === 'capacidade' ? G.verde : G.borda}`,
              borderRadius: 0, overflow: 'hidden',
            }}>
              <div style={{
                background: perguntaAberta === 'capacidade' ? G.verdeLight : 'transparent',
                padding: '10px 14px', display: 'grid',
                gridTemplateColumns: '1fr auto', alignItems: 'center', gap: 10,
              }}>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: G.txt1 }}>
                    Como está a capacidade da operação atual?
                  </div>
                  {capacidade_resposta && perguntaAberta !== 'capacidade' && (
                    <div style={{ fontSize: 12, color: G.verde, fontWeight: 700, marginTop: 2 }}>
                      ✓ {opcaoCapacidadeSel?.label}
                    </div>
                  )}
                </div>
                <button onClick={togglePerguntaCapacidade}
                  style={{ background: '#FFF', color: G.laranja,
                    border: `1px solid ${G.laranja}`,
                    padding: '5px 12px', minWidth: 170, textAlign: 'center', fontSize: 11, fontWeight: 700,
                    letterSpacing: '0.06em', textTransform: 'uppercase',
                    borderRadius: 0, cursor: 'pointer', whiteSpace: 'nowrap' }}>
                  {perguntaAberta === 'capacidade' ? '▴ Clique para fechar' : (capacidade_resposta ? <><IconeLapis /> Revisar</> : '▾ Clique para abrir')}
                </button>
              </div>
              {perguntaAberta === 'capacidade' && (
                <div style={{ padding: '8px 14px 12px', display: 'flex',
                  flexDirection: 'column', gap: 4, borderTop: `1px solid ${G.borda}` }}>
                  {OPCOES_CAPACIDADE.map(o => {
                    const sel = capacidade_resposta === o.id;
                    return (
                      <button key={o.id} onClick={() => setarRespostaCapacidade(o.id)}
                        style={{
                          display: 'block', textAlign: 'left',
                          padding: '8px 12px',
                          background: sel ? G.verdeLight : '#FFF',
                          color: G.txt1, fontSize: 13,
                          border: `1px solid ${sel ? G.verde : G.borda}`,
                          borderRadius: 0, cursor: 'pointer',
                          fontFamily: "'Outfit', sans-serif",
                        }}>
                        {sel ? '● ' : '○ '}{o.label}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const compilarTarefa9 = (planoOperacional, empresa) => {
  const rascunho = RASCUNHO_TAREFA_9[empresa?.id] || {
    atividades_chars: [], atividades_extras: [],
    fornecedores: [],
    sistemas_chars: [], sistemas_extras: [],
    instalacoes: '', expansao: '', capacidade_resposta: '',
  };
  const obter = (c) => planoOperacional?.[c] ?? rascunho[c];
  const setor = empresa?.setor || 'geral';
  const atChars        = obter('atividades_chars') || [];
  const atExtras       = obter('atividades_extras') || [];
  const atLivre        = obter('atividades_livre');
  const atEditouLivre  = obter('atividades_editou_livre') || false;
  const sisChars       = obter('sistemas_chars') || [];
  const sisExtras      = obter('sistemas_extras') || [];
  const sisLivre       = obter('sistemas_livre');
  const sisEditouLivre = obter('sistemas_editou_livre') || false;
  const fornec         = obter('fornecedores') || [];
  const instalacoes    = obter('instalacoes') || '_Instalações ainda não descritas._';
  const expansao       = obter('expansao') || '_Plano de expansão ainda não descrito._';
  const capRespId      = obter('capacidade_resposta');

  const fraseAt = (atEditouLivre && atLivre)
    ? atLivre
    : (montarFraseAtividades(setor, atChars, atExtras) || '_Atividades principais ainda não descritas._');
  const fraseSis = (sisEditouLivre && sisLivre)
    ? sisLivre
    : (montarFraseSistemas(setor, sisChars, sisExtras) || '_Sistemas ainda não descritos._');

  const linhasFornec = fornec
    .filter(f => (f.nome || '').trim())
    .sort((a, b) => (b.concentracao || 0) - (a.concentracao || 0))
    .map(f => {
      const tipoLabel = (TIPOS_FORNECEDOR_POR_SETOR[setor] || TIPOS_FORNECEDOR_POR_SETOR['geral'])
        .find(t => t.id === f.tipo)?.label || f.tipo || '—';
      const alerta = (Number(f.concentracao) || 0) > 30 ? ' ⚠' : '';
      return `- **${f.nome}** (${tipoLabel}) · **${f.concentracao || 0}%**${alerta}`;
    })
    .join('\n') || '_Fornecedores ainda não descritos._';

  const opcaoCap = OPCOES_CAPACIDADE.find(o => o.id === capRespId);
  const linhaCap = opcaoCap
    ? `Folga de capacidade: **${opcaoCap.label}**`
    : '_Folga de capacidade ainda não informada._';

  return `## 9. Plano Operacional

### Como o negócio funciona

${fraseAt}

### Fornecedores e Parceiros Críticos

${linhasFornec}

### Sistemas e Tecnologia

${fraseSis}

### Estrutura Física e Capacidade

**Instalações atuais**

${instalacoes}

**Plano de expansão**

${expansao}

**Capacidade**

${linhaCap}`;
};

// ════════════════════════════════════════════════════════════════════════
// TAREFA 10 — Estrutura Org & RH
// 4 blocos: Estrutura do Time (organograma + chips de áreas funcionais) ·
// Pessoas-Chave (lista editável com % de dependência · alerta âmbar > 50%) ·
// Plano de Pessoas 5 anos (headcount hoje + hires por ano) ·
// Cultura e Retenção (chips de práticas + política de remuneração).
// Linguagem pré-corporate · padrão T8/T9.
// ════════════════════════════════════════════════════════════════════════

const CARACTERISTICAS_AREAS_POR_SETOR = {
  'E-commerce': [
    { id: 'comercial_vendas',   chip: 'Comercial e vendas',       frase: 'comercial e vendas' },
    { id: 'compras_estoque',    chip: 'Compras e estoque',        frase: 'compras e estoque' },
    { id: 'logistica',          chip: 'Logística e CD',           frase: 'logística e operação do CD' },
    { id: 'marketing',          chip: 'Marketing',                frase: 'marketing' },
    { id: 'atendimento_pos',    chip: 'Atendimento e pós-venda',  frase: 'atendimento e pós-venda' },
    { id: 'ti',                 chip: 'Tecnologia da Informação', frase: 'tecnologia da informação' },
    { id: 'financeiro',         chip: 'Financeiro e contábil',    frase: 'financeiro e contábil' },
    { id: 'juridico_compliance',chip: 'Jurídico e compliance',    frase: 'jurídico e compliance' },
    { id: 'gente_cultura',      chip: 'Gente e cultura (RH)',     frase: 'gente e cultura (RH)' },
  ],
  'Bar/Restaurante': [
    { id: 'cozinha',          chip: 'Cozinha',                  frase: 'cozinha' },
    { id: 'salao_atend',      chip: 'Salão e atendimento',      frase: 'salão e atendimento' },
    { id: 'bar_drinks',       chip: 'Bar e drinks',             frase: 'bar e drinks' },
    { id: 'compras_ingred',   chip: 'Compras de ingredientes',  frase: 'compras de ingredientes' },
    { id: 'delivery_op',      chip: 'Delivery',                 frase: 'delivery' },
    { id: 'eventos',          chip: 'Eventos privados',         frase: 'eventos privados' },
    { id: 'marketing_redes',  chip: 'Marketing e redes',        frase: 'marketing e redes' },
    { id: 'financeiro_b',     chip: 'Financeiro',               frase: 'financeiro' },
    { id: 'gente_cultura_b',  chip: 'Gente e cultura (RH)',     frase: 'gente e cultura (RH)' },
  ],
  'Saúde': [
    { id: 'atendimento_clinico', chip: 'Atendimento clínico',     frase: 'atendimento clínico' },
    { id: 'recepcao',            chip: 'Recepção e agenda',       frase: 'recepção e agenda' },
    { id: 'enfermagem',          chip: 'Enfermagem',              frase: 'enfermagem' },
    { id: 'gestao_convenios_s',  chip: 'Gestão de convênios',     frase: 'gestão de convênios' },
    { id: 'telemed',             chip: 'Telemedicina',            frase: 'telemedicina' },
    { id: 'marketing_s',         chip: 'Marketing',               frase: 'marketing' },
    { id: 'financeiro_s',        chip: 'Financeiro',              frase: 'financeiro' },
    { id: 'gente_cultura_s',     chip: 'Gente e cultura (RH)',    frase: 'gente e cultura (RH)' },
  ],
  'Construção Civil': [
    { id: 'engenharia',       chip: 'Engenharia de projeto',    frase: 'engenharia de projeto' },
    { id: 'obras_exec',       chip: 'Execução de obras',        frase: 'execução de obras' },
    { id: 'compras_obra',     chip: 'Compras de obra',          frase: 'compras de obra' },
    { id: 'seguranca',        chip: 'Segurança do trabalho',    frase: 'segurança do trabalho' },
    { id: 'comercial_c',      chip: 'Comercial',                frase: 'comercial' },
    { id: 'loja_materiais',   chip: 'Loja de materiais',        frase: 'loja de materiais' },
    { id: 'financeiro_c',     chip: 'Financeiro',               frase: 'financeiro' },
    { id: 'gente_cultura_c',  chip: 'Gente e cultura (RH)',     frase: 'gente e cultura (RH)' },
    { id: 'juridico_c',       chip: 'Jurídico',                 frase: 'jurídico' },
  ],
  'geral': [
    { id: 'comercial_g',      chip: 'Comercial',                frase: 'comercial' },
    { id: 'producao_g',       chip: 'Produção/Serviço',         frase: 'produção do serviço principal' },
    { id: 'compras_g',        chip: 'Compras',                  frase: 'compras' },
    { id: 'atendimento_g',    chip: 'Atendimento',              frase: 'atendimento' },
    { id: 'marketing_g',      chip: 'Marketing',                frase: 'marketing' },
    { id: 'financeiro_g',     chip: 'Financeiro',               frase: 'financeiro' },
    { id: 'gente_cultura_g',  chip: 'Gente e cultura (RH)',     frase: 'gente e cultura (RH)' },
    { id: 'juridico_g',       chip: 'Jurídico',                 frase: 'jurídico' },
    { id: 'ti_g',             chip: 'Tecnologia da Informação', frase: 'tecnologia da informação' },
  ],
};

const CARACTERISTICAS_CULTURA_POR_SETOR = {
  'E-commerce': [
    { id: 'meritocracia',     chip: 'Meritocracia',                 frase: 'meritocracia' },
    { id: 'bonus_resultado',  chip: 'Bônus por resultado',          frase: 'bônus por resultado' },
    { id: 'flexivel_remoto',  chip: 'Trabalho flexível/remoto',     frase: 'trabalho flexível e remoto' },
    { id: 'dev_continuo',     chip: 'Desenvolvimento contínuo',     frase: 'desenvolvimento contínuo' },
    { id: 'equity_eq',        chip: 'Participação nos lucros',      frase: 'participação nos lucros' },
    { id: 'diversidade',      chip: 'Diversidade e inclusão',       frase: 'diversidade e inclusão' },
    { id: 'saude_mental',     chip: 'Bem-estar e saúde mental',     frase: 'bem-estar e saúde mental' },
    { id: 'esg_consciente',   chip: 'Consciência ESG',              frase: 'consciência ESG' },
  ],
  'Bar/Restaurante': [
    { id: 'chef_protagonista',chip: 'Chef como protagonista',        frase: 'chef como protagonista' },
    { id: 'treinamento_cont', chip: 'Treinamento contínuo da equipe',frase: 'treinamento contínuo da equipe' },
    { id: 'bonus_atend',      chip: 'Bônus por atendimento',         frase: 'bônus por avaliações de atendimento' },
    { id: 'carreira_clara',   chip: 'Plano de carreira claro',       frase: 'plano de carreira claro' },
    { id: 'ambiente_familiar',chip: 'Ambiente familiar',             frase: 'ambiente familiar entre a equipe' },
    { id: 'valorizar_origem', chip: 'Valorização dos produtores',    frase: 'valorização da origem dos ingredientes' },
    { id: 'saude_mental_r',   chip: 'Saúde mental e descanso',       frase: 'saúde mental e descanso da equipe' },
    { id: 'diversidade_r',    chip: 'Diversidade',                   frase: 'diversidade' },
  ],
  'Saúde': [
    { id: 'cuidado_humano',   chip: 'Cuidado humanizado',           frase: 'cuidado humanizado' },
    { id: 'educacao_cont',    chip: 'Educação continuada',          frase: 'educação continuada' },
    { id: 'multidisciplinar', chip: 'Trabalho multidisciplinar',    frase: 'trabalho multidisciplinar' },
    { id: 'bonus_qualidade',  chip: 'Bônus por qualidade clínica',  frase: 'bônus por qualidade clínica' },
    { id: 'mentoria',         chip: 'Mentoria entre especialistas', frase: 'mentoria entre especialistas' },
    { id: 'saude_mental_s',   chip: 'Saúde mental do time',         frase: 'cuidado com a saúde mental do time' },
    { id: 'diversidade_s',    chip: 'Diversidade',                  frase: 'diversidade' },
    { id: 'esg_consciente_s', chip: 'Consciência ESG',              frase: 'consciência ESG' },
  ],
  'Construção Civil': [
    { id: 'seg_em_primeiro',  chip: 'Segurança em primeiro lugar',  frase: 'segurança em primeiro lugar' },
    { id: 'meritocracia_obra',chip: 'Meritocracia por obra',        frase: 'meritocracia por obra entregue' },
    { id: 'treinamento_tec',  chip: 'Treinamento técnico contínuo', frase: 'treinamento técnico contínuo' },
    { id: 'carreira_cant',    chip: 'Plano de carreira do canteiro',frase: 'plano de carreira do canteiro' },
    { id: 'valor_artesao',    chip: 'Valorização do mestre de obras',frase: 'valorização do mestre de obras' },
    { id: 'bonus_prazo',      chip: 'Bônus por cumprimento de prazo',frase: 'bônus por cumprimento de prazo' },
    { id: 'saude_seg_oc',     chip: 'Saúde e segurança ocupacional',frase: 'saúde e segurança ocupacional' },
    { id: 'diversidade_c',    chip: 'Diversidade',                  frase: 'diversidade' },
  ],
  'geral': [
    { id: 'meritocracia_g',   chip: 'Meritocracia',                 frase: 'meritocracia' },
    { id: 'bonus_resultado_g',chip: 'Bônus por resultado',          frase: 'bônus por resultado' },
    { id: 'treinamento_g',    chip: 'Treinamento contínuo',         frase: 'treinamento contínuo' },
    { id: 'carreira_g',       chip: 'Plano de carreira claro',      frase: 'plano de carreira claro' },
    { id: 'flexibilidade_g',  chip: 'Flexibilidade',                frase: 'flexibilidade no trabalho' },
    { id: 'diversidade_g',    chip: 'Diversidade',                  frase: 'diversidade' },
    { id: 'saude_mental_g',   chip: 'Bem-estar do time',            frase: 'bem-estar do time' },
    { id: 'esg_consciente_g', chip: 'Consciência ESG',              frase: 'consciência ESG' },
  ],
};

// ═══════════════════════════════════════════════════════════════════════
// Catálogos SU (Startup) da T10. Áreas · Cultura · Cargos de startup são
// FUNCIONAIS, não setoriais → lista única ('geral' cobre todos os setores).
// Selecionados em ConteudoTarefa10 quando modo === 'SU'.
// ═══════════════════════════════════════════════════════════════════════
const CARACTERISTICAS_AREAS_POR_SETOR_SU = {
  'geral': [
    { id: 'engenharia',      chip: 'Engenharia',            frase: 'engenharia' },
    { id: 'produto',         chip: 'Produto',               frase: 'produto' },
    { id: 'design',          chip: 'Design',                frase: 'design' },
    { id: 'vendas',          chip: 'Vendas',                frase: 'vendas' },
    { id: 'marketing',       chip: 'Marketing',             frase: 'marketing' },
    { id: 'operacoes',       chip: 'Operações',             frase: 'operações' },
    { id: 'sucesso_cliente', chip: 'Sucesso do cliente',    frase: 'sucesso do cliente' },
    { id: 'gente_cultura',   chip: 'Gente e cultura',       frase: 'gente e cultura' },
  ],
};

const CARACTERISTICAS_CULTURA_POR_SETOR_SU = {
  'geral': [
    { id: 'transparencia',    chip: 'Transparência',         frase: 'transparência' },
    { id: 'velocidade',       chip: 'Velocidade',            frase: 'velocidade' },
    { id: 'senso_dono',       chip: 'Senso de dono',         frase: 'senso de dono' },
    { id: 'obsessao_cliente', chip: 'Obsessão pelo cliente', frase: 'obsessão pelo cliente' },
    { id: 'vies_acao',        chip: 'Viés para a ação',      frase: 'viés para a ação' },
    { id: 'frugalidade',      chip: 'Frugalidade',           frase: 'frugalidade' },
    { id: 'diversidade',      chip: 'Diversidade e inclusão',frase: 'diversidade e inclusão' },
    { id: 'aprendizado',      chip: 'Aprendizado contínuo',  frase: 'aprendizado contínuo' },
  ],
};

const TEMPLATE_FRASE_AREAS = {
  'E-commerce':       'O time está organizado em {LISTA}.',
  'Bar/Restaurante':  'O time está organizado em {LISTA}.',
  'Saúde':            'O time está organizado em {LISTA}.',
  'Construção Civil': 'O time está organizado em {LISTA}.',
  'geral':            'O time está organizado em {LISTA}.',
};
const TEMPLATE_FRASE_CULTURA = {
  'E-commerce':       'Cultura voltada a {LISTA}.',
  'Bar/Restaurante':  'Cultura voltada a {LISTA}.',
  'Saúde':            'Cultura voltada a {LISTA}.',
  'Construção Civil': 'Cultura voltada a {LISTA}.',
  'geral':            'Cultura voltada a {LISTA}.',
};

const montarFraseAreas = (setor, ids, extras, modo = 'EE') => {
  const fonte = modo === 'SU' ? CARACTERISTICAS_AREAS_POR_SETOR_SU : CARACTERISTICAS_AREAS_POR_SETOR;
  const catalogo = fonte[setor] || fonte['geral'];
  const partes = (ids || [])
    .map(id => catalogo.find(c => c.id === id)?.frase)
    .filter(Boolean)
    .concat((extras || []).map(e => (e || '').trim().toLowerCase()).filter(Boolean));
  if (partes.length === 0) return '';
  const tmpl = TEMPLATE_FRASE_AREAS[setor] || TEMPLATE_FRASE_AREAS['geral'];
  return tmpl.replace('{LISTA}', juntarEmProsa(partes));
};
const montarFraseCultura = (setor, ids, extras, modo = 'EE') => {
  const fonte = modo === 'SU' ? CARACTERISTICAS_CULTURA_POR_SETOR_SU : CARACTERISTICAS_CULTURA_POR_SETOR;
  const catalogo = fonte[setor] || fonte['geral'];
  const partes = (ids || [])
    .map(id => catalogo.find(c => c.id === id)?.frase)
    .filter(Boolean)
    .concat((extras || []).map(e => (e || '').trim().toLowerCase()).filter(Boolean));
  if (partes.length === 0) return '';
  const tmpl = TEMPLATE_FRASE_CULTURA[setor] || TEMPLATE_FRASE_CULTURA['geral'];
  return tmpl.replace('{LISTA}', juntarEmProsa(partes));
};

// Catálogo de portfólio típico por setor · IA propõe · empresário marca o que oferece
// Cada item tem nome, descrição e mix_sugerido (% típico no setor)
const PORTFOLIO_CATALOGO_POR_SETOR = {
  'E-commerce': [
    { id: 'moveis_curadoria',  nome: 'Móveis sob curadoria',            descricao: 'Design contemporâneo com peças de marcas selecionadas',                     mix: 40 },
    { id: 'decoracao_objetos', nome: 'Decoração & objetos',             descricao: 'Iluminação, têxteis, arte parietal e acessórios decorativos',               mix: 30 },
    { id: 'private_label',     nome: 'Linha própria / private label',   descricao: 'Linha exclusiva com margem superior · curadoria editorial',                 mix: 22 },
    { id: 'catalogo_geral',    nome: 'Catálogo principal (revenda 3P)', descricao: 'Linhas de produto representativas, mix amplo · revenda de marcas',         mix: 40 },
    { id: 'acessorios_organizadores', nome: 'Acessórios e organizadores', descricao: 'Soluções de armazenamento e organização funcional',                      mix: 10 },
    { id: 'marketplace_3p',    nome: 'Marketplace de 3os',              descricao: 'Venda em marketplaces externos · alcance ampliado · margem reduzida',       mix: 15 },
    { id: 'premium',           nome: 'Linha premium curada',            descricao: 'Itens selecionados de alto valor · ticket médio elevado',                   mix: 10 },
    { id: 'sazonais',          nome: 'Cesta de fim de ano / sazonais',  descricao: 'Produtos sazonais (Natal, Páscoa, Dia das Mães)',                           mix: 3 },
    { id: 'pet_jardim',        nome: 'Pet & jardim',                    descricao: 'Linha de pet shop e jardinagem',                                            mix: 3 },
    { id: 'eletronicos',       nome: 'Eletrônicos',                     descricao: 'Linha de eletrônicos e gadgets',                                            mix: 2 },
  ],
  'Bar/Restaurante': [
    { id: 'a_la_carte',        nome: 'Jantar à la carte',               descricao: 'Cardápio sazonal contemporâneo + carta de vinhos curada',                   mix: 45 },
    { id: 'almoco_executivo',  nome: 'Almoço executivo',                descricao: 'Menu curto de seg a sex · entrada+prato+sobremesa+bebida',                  mix: 35 },
    { id: 'eventos_privados',  nome: 'Eventos privados (buffet)',       descricao: 'Casamentos, aniversários, jantares corporativos',                          mix: 15 },
    { id: 'delivery_takeaway', nome: 'Delivery & take-away',            descricao: 'Pratos selecionados via apps + retirada · sem comprometer experiência',    mix: 5 },
    { id: 'delivery_plat',     nome: 'Delivery via plataforma',         descricao: 'Pedidos via iFood / Rappi / 99Food',                                        mix: 10 },
    { id: 'delivery_proprio',  nome: 'Delivery próprio',                descricao: 'Delivery por canal próprio · margem maior',                                 mix: 5 },
    { id: 'drinks_autorais',   nome: 'Carta de drinks autorais',        descricao: 'Drinks assinados pelo bartender-chefe',                                     mix: 3 },
    { id: 'carta_vinhos',      nome: 'Carta de vinhos',                 descricao: 'Vinhos selecionados pelo sommelier',                                        mix: 2 },
  ],
  'Saúde': [
    { id: 'consultas_clinicas',nome: 'Consultas clínicas',              descricao: '7 especialidades médicas · agendamento simples',                            mix: 45 },
    { id: 'exames_diag',       nome: 'Exames diagnósticos',             descricao: 'Laboratoriais e de imagem básicos no próprio espaço',                       mix: 25 },
    { id: 'procedimentos',     nome: 'Procedimentos ambulatoriais',     descricao: 'Pequenos procedimentos realizados em consultório',                          mix: 15 },
    { id: 'telemedicina',      nome: 'Telemedicina',                    descricao: 'Consultas remotas integradas ao prontuário',                                mix: 10 },
    { id: 'preventivos',       nome: 'Programas preventivos',           descricao: 'Check-ups anuais e acompanhamento de pacientes crônicos',                   mix: 5 },
    { id: 'consultas_conv',    nome: 'Consultas via convênio',          descricao: 'Atendimentos cobertos por planos de saúde',                                 mix: 50 },
    { id: 'consultas_part',    nome: 'Consultas particulares',          descricao: 'Atendimentos pagos diretamente pelo paciente',                              mix: 25 },
  ],
  'Construção Civil': [
    { id: 'obras_corp',        nome: 'Obras corporativas',              descricao: 'Edifícios comerciais e adaptações para escritórios',                        mix: 60 },
    { id: 'reformas_alto',     nome: 'Reformas residenciais alto padrão',descricao: 'Reformas completas com gestão integrada',                                   mix: 20 },
    { id: 'loja_materiais',    nome: 'Loja de materiais',               descricao: 'Venda direta de insumos de construção · varejo e atacado',                  mix: 15 },
    { id: 'engenharia_consult',nome: 'Serviços de engenharia',          descricao: 'Projetos, laudos técnicos e consultoria estrutural',                        mix: 5 },
    { id: 'obras_publicas',    nome: 'Obras públicas (licitações)',     descricao: 'Contratos com órgãos públicos via licitação',                               mix: 8 },
    { id: 'manutencao',        nome: 'Manutenção predial',              descricao: 'Contratos continuados de manutenção',                                       mix: 2 },
  ],
  'geral': [
    { id: 'linha_principal',   nome: 'Linha principal',                 descricao: 'Produto/serviço carro-chefe do negócio',                                    mix: 50 },
    { id: 'linha_secundaria',  nome: 'Linha secundária',                descricao: 'Produto/serviço complementar',                                              mix: 25 },
    { id: 'servicos_assoc',    nome: 'Serviços associados',             descricao: 'Pós-venda · suporte · manutenção · garantia estendida',                     mix: 15 },
    { id: 'novos_lancamentos', nome: 'Novos produtos / lançamentos',    descricao: 'Lançamentos do ano · linhas em ramp-up',                                    mix: 10 },
  ],
};

// Catálogo de diferenciais competitivos típicos por setor · IA propõe · empresário marca
const DIFERENCIAIS_CATALOGO_POR_SETOR = {
  'E-commerce': [
    { id: 'curadoria_editorial',  titulo: 'Curadoria editorial',           detalhe: 'Cada item passa por filtro de design antes de entrar no catálogo' },
    { id: 'cd_proprio',           titulo: 'CD próprio',                    detalhe: 'Centro de distribuição reduz prazo de entrega em até 40%' },
    { id: 'private_label_dif',    titulo: 'Private label exclusiva',       detalhe: 'Linha desenvolvida internamente · margem superior à média' },
    { id: 'atendimento_humano',   titulo: 'Atendimento humanizado',        detalhe: 'Pós-venda real com pessoas, não apenas chatbot' },
    { id: 'logistica_rapida',     titulo: 'Logística expressa',            detalhe: 'Entregas em até 24h nas principais capitais' },
    { id: 'app_proprio',          titulo: 'App / Plataforma própria',      detalhe: 'Experiência de compra integrada · programa de fidelidade' },
    { id: 'multicanal',           titulo: 'Multicanal integrado',          detalhe: 'Loja online + física + marketplaces · estoque unificado' },
  ],
  'Bar/Restaurante': [
    { id: 'chef_premiado',        titulo: 'Chef premiado',                 detalhe: 'Formação internacional + experiência em casas reconhecidas' },
    { id: 'cardapio_sazonal',     titulo: 'Cardápio sazonal',              detalhe: 'Atualizado periodicamente com ingredientes locais e produtores parceiros' },
    { id: 'carta_vinhos_curada',  titulo: 'Carta de vinhos curada',        detalhe: 'Sommelier certificado · seleção em faixas de preço variadas' },
    { id: 'espaco_premiado',      titulo: 'Espaço premiado',               detalhe: 'Ambiente referência em guias gastronômicos' },
    { id: 'sustentabilidade_bar', titulo: 'Sustentabilidade na cozinha',   detalhe: 'Zero desperdício · ingredientes orgânicos · embalagens biodegradáveis' },
    { id: 'experiencia_unica',    titulo: 'Experiência gastronômica única',detalhe: 'Menu degustação · interação com chef · jantar harmonizado' },
  ],
  'Saúde': [
    { id: 'multidisciplinar_dif', titulo: 'Equipe multidisciplinar',       detalhe: 'Coordenação entre especialidades sob um único prontuário' },
    { id: 'telemed_integrada',    titulo: 'Telemedicina integrada',        detalhe: 'Consulta remota com o mesmo médico do presencial' },
    { id: 'preventivos_proprios', titulo: 'Programas preventivos próprios',detalhe: 'Pacotes anuais com check-up + nutrição + atividade física' },
    { id: 'convenios_principais', titulo: 'Convênio com principais operadoras', detalhe: 'Atendimento direto · sem reembolso ao paciente' },
    { id: 'tempo_resposta',       titulo: 'Tempo de resposta rápido',      detalhe: 'Agenda flexível · espera reduzida · atendimento de urgência' },
    { id: 'tecnologia_clinica',   titulo: 'Tecnologia clínica de ponta',   detalhe: 'Equipamentos de última geração · diagnósticos precisos' },
  ],
  'Construção Civil': [
    { id: 'eng_responsavel',      titulo: 'Engenheiro responsável presente', detalhe: 'CREA ativo · acompanhamento direto em todas as obras' },
    { id: 'capacidade_simul',     titulo: 'Capacidade simultânea',         detalhe: 'Estrutura para múltiplas obras paralelas sem perda de qualidade' },
    { id: 'compras_integradas',   titulo: 'Compras integradas',            detalhe: 'Loja própria de materiais reduz custo de insumos da obra' },
    { id: 'cumprimento_prazo',    titulo: 'Cumprimento de prazo',          detalhe: 'Carteira de referências com 95%+ de entregas no prazo contratual' },
    { id: 'metodo_construtivo',   titulo: 'Método construtivo moderno',    detalhe: 'Steel frame · drywall · pré-fabricados · agilidade na execução' },
    { id: 'sustentabilidade_obra',titulo: 'Construção sustentável',        detalhe: 'Certificações LEED/AQUA · materiais reciclados · eficiência energética' },
  ],
  'geral': [
    { id: 'qualidade_super',      titulo: 'Qualidade superior',            detalhe: 'Reconhecimento de mercado · padrão acima da concorrência' },
    { id: 'preco_competitivo',    titulo: 'Preço competitivo',             detalhe: 'Melhor relação custo-benefício do segmento' },
    { id: 'atendimento_premium',  titulo: 'Atendimento premium',           detalhe: 'Suporte personalizado · pós-venda atencioso' },
    { id: 'tecnologia_dif',       titulo: 'Tecnologia diferenciada',       detalhe: 'Soluções inovadoras na entrega do produto/serviço' },
    { id: 'nicho_especializado',  titulo: 'Especialização em nicho',       detalhe: 'Expertise focada num segmento específico de mercado' },
  ],
};

// Catálogo de iniciativas de Pipeline de Inovação típicas por setor · IA propõe (catalisador)
const PIPELINE_CATALOGO_POR_SETOR = {
  'E-commerce': [
    { id: 'private_label_expan', descricao: 'Expansão da linha private label · novas categorias',                 prazo: '2025-2026' },
    { id: 'configurador',        descricao: 'Plataforma de personalização online (configurador de produto)',      prazo: '2026' },
    { id: 'app_fidelidade',      descricao: 'App próprio · integração com programa de fidelidade',                 prazo: '2026-2027' },
    { id: 'ia_recomendacao',     descricao: 'IA de recomendação personalizada · curadoria automática',            prazo: '2026' },
    { id: 'multi_marketplace',   descricao: 'Marketplace próprio com vendedores curados',                          prazo: '2027' },
    { id: 'flagship_fisica',     descricao: 'Loja física flagship / pop-up · experiência da marca',                prazo: '2027' },
  ],
  'Bar/Restaurante': [
    { id: 'congelados_premium',  descricao: 'Linha de comida congelada premium para retail seletivo',              prazo: '2025' },
    { id: 'eventos_chefs',       descricao: 'Eventos gastronômicos colaborativos com chefs convidados',            prazo: 'recorrente' },
    { id: 'carta_cervejas',      descricao: 'Carta de cervejas artesanais exclusivas',                             prazo: '2026' },
    { id: 'assinatura_mensal',   descricao: 'Programa de assinatura mensal · degustação e vinhos',                 prazo: '2026' },
    { id: 'filial_regiao',       descricao: 'Filial em nova região · expansão geográfica',                         prazo: '2026-2027' },
    { id: 'cozinha_escola',      descricao: 'Cozinha-escola / cursos abertos ao público',                          prazo: '2027' },
  ],
  'Saúde': [
    { id: 'novas_especial',      descricao: 'Expansão para novas especialidades (geriatria e fisioterapia)',       prazo: '2025' },
    { id: 'protocolos_preven',   descricao: 'Protocolos preventivos por dimensão (cardiometabólico, mental, oncológico)', prazo: '2025-2026' },
    { id: 'ia_triagem',          descricao: 'IA de triagem clínica + recomendação de encaminhamento',              prazo: '2026-2027' },
    { id: 'telemed_24h',         descricao: 'Telemedicina 24h · cobertura completa',                               prazo: '2026' },
    { id: 'pacotes_corp_pipe',   descricao: 'Pacotes corporativos B2B · saúde ocupacional',                        prazo: '2026' },
    { id: 'nova_unidade',        descricao: 'Nova unidade física · expansão regional',                             prazo: '2027' },
  ],
  'Construção Civil': [
    { id: 'steel_frame',         descricao: 'Adoção de steel frame e drywall em obras corporativas',               prazo: '2025-2026' },
    { id: 'bim_dashboards',      descricao: 'Automação de gestão de obras (BIM + dashboards de campo)',            prazo: '2026' },
    { id: 'linha_sustentavel',   descricao: 'Linha sustentável (materiais reciclados e energia solar embarcada)',  prazo: '2027' },
    { id: 'pre_fabricados',      descricao: 'Pré-fabricados modulares · agilidade na execução',                    prazo: '2026' },
    { id: 'plataforma_digital',  descricao: 'Plataforma digital de acompanhamento de obra pelo cliente',           prazo: '2026' },
    { id: 'expansao_geografica', descricao: 'Expansão geográfica · novas regiões',                                 prazo: '2027' },
  ],
  'geral': [
    { id: 'novo_produto',        descricao: 'Novo produto/serviço · linha em desenvolvimento',                     prazo: '2025-2026' },
    { id: 'expansao_geo',        descricao: 'Expansão geográfica · novas regiões/cidades',                         prazo: '2026' },
    { id: 'digitalizacao',       descricao: 'Digitalização da operação · sistemas integrados',                     prazo: '2026' },
    { id: 'fidelizacao',         descricao: 'Programa de fidelização e relacionamento',                            prazo: '2026' },
    { id: 'sustentabilidade',    descricao: 'Linha sustentável / certificações ambientais',                        prazo: '2027' },
    { id: 'aquisicao_estrat',    descricao: 'Aquisição estratégica · M&A complementar',                            prazo: '2027' },
  ],
};

// Catálogo de Parcerias estratégicas típicas por setor
const PARCERIAS_CATALOGO_POR_SETOR = {
  'E-commerce': [
    { id: 'design_parceiros',  nome: 'Estúdios de design parceiros',           tipo: 'Design / Curadoria' },
    { id: 'private_label_for', nome: 'Fornecedores private label',             tipo: 'Produção / Manufatura' },
    { id: 'marketplace_tech',  nome: 'Plataforma de marketplace tech',         tipo: 'Tecnologia / Distribuição' },
    { id: 'operador_log',      nome: 'Operador logístico',                     tipo: 'Logística / Frete' },
    { id: 'agencia_perf',      nome: 'Agência de marketing de performance',    tipo: 'Marketing / Aquisição' },
    { id: 'influenciadores',   nome: 'Influenciadores e criadores de conteúdo',tipo: 'Marketing / Conteúdo' },
  ],
  'Bar/Restaurante': [
    { id: 'produtores_locais', nome: 'Produtores locais (hortifruti orgânico)',tipo: 'Cadeia de suprimento' },
    { id: 'sommelier_cons',    nome: 'Sommelier consultor',                    tipo: 'Curadoria / Wine list' },
    { id: 'escola_gastronomia',nome: 'Escola de gastronomia parceira',         tipo: 'Talento / Estágio' },
    { id: 'cervejaria_dest',   nome: 'Cervejaria/destilaria parceira',         tipo: 'Curadoria de bebidas' },
    { id: 'plataformas_res',   nome: 'Plataformas de reserva/delivery',        tipo: 'Distribuição digital' },
    { id: 'parceiro_eventos',  nome: 'Empresa de eventos corporativos',        tipo: 'Canal B2B' },
  ],
  'Saúde': [
    { id: 'hospitais_ref',     nome: 'Hospitais de referência (encaminhamento)', tipo: 'Rede clínica' },
    { id: 'labs_diag',         nome: 'Laboratórios diagnósticos (Fleury, DASA)', tipo: 'Exames complementares' },
    { id: 'empresa_telemed',   nome: 'Empresa de telemedicina (Conexa Saúde)', tipo: 'Tecnologia / Remoto' },
    { id: 'operadora_plano',   nome: 'Operadora de plano de saúde',            tipo: 'Convênios' },
    { id: 'universidade_saude',nome: 'Universidade · pesquisa clínica',        tipo: 'Pesquisa aplicada' },
    { id: 'saude_corporativa', nome: 'Empresa de saúde corporativa',           tipo: 'Canal B2B' },
  ],
  'Construção Civil': [
    { id: 'univ_engenharia',   nome: 'Universidade · engenharia civil',        tipo: 'Pesquisa aplicada' },
    { id: 'fornec_tec',        nome: 'Fornecedores de tecnologia construtiva', tipo: 'Inovação de método' },
    { id: 'software_bim',      nome: 'Empresa de software de gestão (BIM)',    tipo: 'Tecnologia operacional' },
    { id: 'log_canteiro',      nome: 'Empresa de logística de canteiro',       tipo: 'Logística operacional' },
    { id: 'sst_parceiro',      nome: 'Empresa de segurança do trabalho',       tipo: 'Compliance / SST' },
    { id: 'certificacao_amb',  nome: 'Empresa de certificação ambiental',      tipo: 'Sustentabilidade' },
  ],
  'geral': [
    { id: 'fornec_estrat',     nome: 'Fornecedor estratégico',                 tipo: 'Cadeia de suprimento' },
    { id: 'distribuidor_reg',  nome: 'Distribuidor regional',                  tipo: 'Distribuição' },
    { id: 'marketplace_geral', nome: 'Plataforma de marketplace',              tipo: 'Canal digital' },
    { id: 'tech_partner',      nome: 'Empresa de tecnologia',                  tipo: 'Tecnologia / Inovação' },
    { id: 'universidade',      nome: 'Universidade / instituição de pesquisa', tipo: 'Pesquisa aplicada' },
    { id: 'parc_logistico',    nome: 'Parceiro logístico',                     tipo: 'Logística' },
  ],
};

const ANOS_PLANO_HIRES = ['Ano 1', 'Ano 2', 'Ano 3', 'Ano 4', 'Ano 5'];

// Frases de contratação pré-pensadas por setor × ano · IA propõe · empresário escolhe
const CONTRATACOES_SUGERIDAS_POR_SETOR_E_ANO = {
  'E-commerce': {
    'Ano 1': [
      'CTO / Head of Engineering · time de TI passa de 15 para 20',
      'Head of Marketing sênior · time de marketing dobra',
      'Gerente de Compras adicional · acelera curadoria de catálogo',
      'Coordenador de Logística · prepara expansão de CD',
    ],
    'Ano 2': [
      'Head of Marketing sênior · time de marketing dobra para 15 pessoas',
      'Gerente de CD · estrutura segundo centro de distribuição',
      'Líder de Produto Digital · roadmap acelerado',
      'Diretor Comercial · expansão de canal direto',
    ],
    'Ano 3': [
      'Diretor de Expansão (2º CD) · +20 colaboradores em operações',
      'Head de Private Label · linha exclusiva escalada',
      'Gerente de Atendimento sênior · time SAC dobra',
      'Head of People · estrutura RH corporativo',
    ],
    'Ano 4': [
      'CFO mais sênior · time comercial de canal D2C ampliado',
      'Diretor de Atacado · novo canal B2B',
      'Head of Data · estrutura BI e analytics',
      'Diretor Internacional · primeiras operações em LATAM',
    ],
    'Ano 5': [
      'Liderança M&A · estrutura para eventual aquisição · ~180 colaboradores no total',
      'CMO sênior · marca consolidada nacional',
      'Conselho institucional · governança corporativa madura',
      'Diretor de M&A · busca ativa de aquisições estratégicas',
    ],
  },
  'Bar/Restaurante': {
    'Ano 1': [
      'Gerente de Marketing dedicado (parte-time hoje) · 2º Sous-chef',
      'Maître sênior · profissionalização do salão',
      'Responsável de Compras · negociação direta com fornecedores',
      'Gerente de Eventos · diversificação de receita',
    ],
    'Ano 2': [
      'Maître sênior + estruturação de plano de carreira de salão',
      'Sommelier · ampliar carta de vinhos',
      'Sous-chef adicional · prepara segunda casa',
      'Gerente de Mídias Sociais · presença digital expandida',
    ],
    'Ano 3': [
      'Time de filial 2 (Itaim ou Pinheiros) · ~30 novas pessoas',
      'Chef Executivo · supervisiona 2 casas',
      'Gerente Geral de filial · estrutura operacional duplicada',
      'Head of People · cultura corporativa entre casas',
    ],
    'Ano 4': [
      'Diretor Operacional · gerencia múltiplas casas',
      'CFO terceirizado vira interno',
      'Diretor de Expansão · prospecção de novos pontos',
      'Head of Eventos Corporativos · linha B2B consolidada',
    ],
    'Ano 5': [
      'Casa nova consolidada · ~70 colaboradores no total · expansão para eventos corporativos',
      'CEO institucional · liberar chef-proprietário para o produto',
      'Diretor de Franquias · modelo de expansão escalável',
      'Conselho consultivo · governança madura',
    ],
  },
  'Saúde': {
    'Ano 1': [
      'Mais 3 médicos especialistas (cardiologia, pediatria, endocrinologia) · Líder de Telemedicina',
      'Coordenador de Convênios · ampliar credenciamento',
      'Gerente de Recepção sênior · profissionalizar agenda',
      'Enfermeiro coordenador · qualidade clínica',
    ],
    'Ano 2': [
      'Coordenador de Pacientes Corporativos · 4 médicos · estruturação do programa preventivo anual',
      'Marketing médico dedicado · captação direta de pacientes',
      'Head of Telemedicina · escala da modalidade',
      'Coordenador de Qualidade · acreditação ONA/JCI',
    ],
    'Ano 3': [
      'Equipe inicial da 2ª unidade (Vila Mariana/Brooklin) · ~10 novas pessoas',
      'Diretor Clínico assistente · supervisão clínica entre unidades',
      'Coordenador de Operações · estrutura entre unidades',
      'Head of Faturamento · gestão financeira de convênios',
    ],
    'Ano 4': [
      'Diretor de Expansão · CFO sênior · estruturação para receber investimento',
      'Head de Programas Corporativos · linha B2B madura',
      'Diretor Médico institucional · governança clínica',
      'Head of Tecnologia Clínica · prontuário e analytics',
    ],
    'Ano 5': [
      '2ª unidade consolidada · ~95 colaboradores no total · plano preventivo escalado',
      'Conselho de Administração · governança corporativa',
      'Diretor de M&A · busca de aquisições no setor',
      'Head of Estratégia · expansão multi-cidade',
    ],
  },
  'Construção Civil': {
    'Ano 1': [
      '2º Engenheiro Residente · Comprador adicional para suportar 2 obras simultâneas',
      'Coordenador de Segurança do Trabalho · profissionalização SST',
      'Gerente de Loja de Materiais · expansão de varejo',
      'Engenheiro de Planejamento · controle de cronogramas',
    ],
    'Ano 2': [
      'Gerente Comercial corporativo · reforço de Segurança do Trabalho',
      'Diretor de Projetos · supervisão de múltiplas obras',
      'Coordenador de Compras Corporativas · centralização de suprimentos',
      'Head of Engenharia · padronização técnica',
    ],
    'Ano 3': [
      'Time inicial do 2º canteiro modular · ~25 novas pessoas em obras',
      'Gerente Geral de Canteiro · operação duplicada',
      'Coordenador de Materiais · gestão integrada de estoques',
      'Head of Qualidade · padronização de entregas',
    ],
    'Ano 4': [
      'Diretor de Expansão da loja de materiais (canal atacado) · CFO mais sênior',
      'Diretor de Engenharia institucional · responsabilidade técnica corporativa',
      'Head of Vendas Corporativas · captação ativa B2B',
      'Head of People · estrutura RH multi-canteiro',
    ],
    'Ano 5': [
      '2ª frente de obras consolidada · ~190 colaboradores totais · captação corporativa madura',
      'CEO institucional · separação entre operação e estratégia',
      'Conselho de Administração · governança corporativa',
      'Head of Sustentabilidade · certificações de mercado',
    ],
  },
  'geral': {
    'Ano 1': [
      'Reforço de time comercial · ampliar capacidade de geração de receita',
      'Profissionalização da gestão financeira · CFO ou Controller sênior',
      'Marketing dedicado · estrutura mínima de aquisição',
      'Operações reforçadas · 2-3 novas posições estruturantes',
    ],
    'Ano 2': [
      'Liderança de área-chave (Marketing · TI · Operações) · 2-3 reforços operacionais',
      'Estrutura comercial ampliada · gerente + executivos',
      'Profissionalização de RH · primeiro gerente de Gente e Cultura',
      'Diretor de área crítica · oxigenar a gestão',
    ],
    'Ano 3': [
      'Time da segunda unidade · ~15-25 novas posições',
      'Liderança institucional · COO ou Diretor Geral',
      'Equipe de expansão · estrutura comercial dedicada',
      'Reforço de operações para sustentar crescimento',
    ],
    'Ano 4': [
      'Diretor de Expansão · CFO sênior · estruturação para captação',
      'Head of People · cultura corporativa madura',
      'Liderança institucional · oxigenar fundador',
      'Equipe de M&A ou Business Development · busca ativa de novos negócios',
    ],
    'Ano 5': [
      'Estrutura corporativa consolidada · governança madura · prep para nova rodada',
      'Conselho consultivo / Administração · governança institucional',
      'Diretor de M&A · execução de aquisições',
      'Head of Estratégia · plano de longo prazo',
    ],
  },
};

// Patamares qualitativos de dependência de pessoa-chave
const OPCOES_DEPENDENCIA = [
  { id: 'a', label: 'Baixa (até 30%)' },
  { id: 'b', label: 'Média (30–50%)' },
  { id: 'c', label: 'Alta (50–70%)' },
  { id: 'd', label: 'Crítica (acima de 70%)' },
];

// Chips de Papel por área — IA usa pra compor a frase do papel sem o empresário escrever
const CHIPS_PAPEL_POR_AREA = {
  estrategia: {
    label: 'Estratégia',
    chips: [
      { id: 'visao',         label: 'Visão estratégica de longo prazo' },
      { id: 'investidores',  label: 'Relação com investidores e conselho' },
      { id: 'metas',         label: 'Definição de metas anuais' },
      { id: 'cultura',       label: 'Liderança de cultura e propósito' },
      { id: 'ma',            label: 'Decisões de M&A e parcerias' },
      { id: 'capital',       label: 'Captação de capital' },
    ],
  },
  comercial: {
    label: 'Comercial',
    chips: [
      { id: 'captacao',      label: 'Captação de novos clientes' },
      { id: 'contratos',     label: 'Negociação de contratos grandes' },
      { id: 'equipe',        label: 'Estruturação da equipe de vendas' },
      { id: 'pipeline',      label: 'Gestão de pipeline e forecast' },
      { id: 'key_accounts',  label: 'Relacionamento com clientes-chave' },
      { id: 'politica',      label: 'Definição de política comercial' },
    ],
  },
  marketing: {
    label: 'Marketing',
    chips: [
      { id: 'posicionamento',label: 'Posicionamento e proposta de valor' },
      { id: 'demanda',       label: 'Geração de demanda (campanhas)' },
      { id: 'branding',      label: 'Branding e identidade visual' },
      { id: 'conteudo',      label: 'Conteúdo e relacionamento com público' },
      { id: 'digital',       label: 'Performance digital (mídia paga, SEO)' },
      { id: 'pesquisa',      label: 'Pesquisa de mercado e cliente' },
    ],
  },
  operacoes: {
    label: 'Operações',
    chips: [
      { id: 'diaria',        label: 'Gestão da operação diária' },
      { id: 'qualidade',     label: 'Controle de qualidade e processos' },
      { id: 'fornecedores',  label: 'Negociação com fornecedores' },
      { id: 'estoque_cd',    label: 'Gestão de estoque e CD' },
      { id: 'capacidade',    label: 'Planejamento de capacidade' },
      { id: 'melhoria',      label: 'Melhoria contínua' },
    ],
  },
  financeiro: {
    label: 'Financeiro',
    chips: [
      { id: 'fechamento',    label: 'Fechamento contábil mensal' },
      { id: 'orcamento',     label: 'Planejamento orçamentário' },
      { id: 'fluxo_caixa',   label: 'Gestão de fluxo de caixa' },
      { id: 'bancos',        label: 'Relação com bancos e captação' },
      { id: 'impostos',      label: 'Apuração de impostos' },
      { id: 'margens',       label: 'Controle de margens e custos' },
    ],
  },
  rh: {
    label: 'RH',
    chips: [
      { id: 'recrutamento',  label: 'Recrutamento e seleção' },
      { id: 'treinamento',   label: 'Treinamento e desenvolvimento' },
      { id: 'avaliacao',     label: 'Avaliação de performance' },
      { id: 'cultura_org',   label: 'Cultura organizacional' },
      { id: 'folha',         label: 'Folha e benefícios' },
      { id: 'sucessao',      label: 'Sucessão e plano de carreira' },
    ],
  },
  ti: {
    label: 'TI',
    chips: [
      { id: 'infra',         label: 'Infraestrutura e sistemas core' },
      { id: 'seguranca',     label: 'Segurança da informação' },
      { id: 'dados',         label: 'Gestão de dados e BI' },
      { id: 'produto',       label: 'Desenvolvimento de produto digital' },
      { id: 'suporte',       label: 'Suporte interno' },
      { id: 'roadmap',       label: 'Roadmap de tecnologia' },
    ],
  },
};

// Heurística: descobre a área de um cargo a partir do nome (palavras-chave)
const inferirAreaDeCargo = (cargo) => {
  const c = (cargo || '').toLowerCase();
  if (/ceo|sócio|conselho|fundador|chef-proprietário|diretor clínico|diretor de engenharia|presidente/i.test(c)) return 'estrategia';
  if (/comercial|vendas|key account|maître|salão|atendimento/i.test(c)) return 'comercial';
  if (/marketing|branding|cmo/i.test(c)) return 'marketing';
  if (/operaç|coo|compras|estoque|logística|engenheir|canteiro|cozinha|bar|sous-chef|sommelier|eventos|enfermagem|médic|recepção|loja de materiais|execuç/i.test(c)) return 'operacoes';
  if (/financeir|cfo|controlador|tesourar|convênio|administrativo/i.test(c)) return 'financeiro';
  if (/rh|gente|cultura|pessoas|recrut/i.test(c)) return 'rh';
  if (/cto|ti|tecnologia|sistemas|dados|produto digital/i.test(c)) return 'ti';
  return 'estrategia';
};

// Monta a frase do papel a partir dos chips marcados
const montarFrasePapel = (areaId, chipIds) => {
  const area = CHIPS_PAPEL_POR_AREA[areaId];
  if (!area) return '';
  const labels = (chipIds || [])
    .map(id => area.chips.find(c => c.id === id)?.label)
    .filter(Boolean);
  if (labels.length === 0) return '';
  if (labels.length === 1) return labels[0];
  if (labels.length === 2) return `${labels[0]} + ${labels[1]}`;
  return labels.slice(0, -1).join(' · ') + ' + ' + labels[labels.length - 1];
};

// Funções essenciais por setor — IA define o mínimo que toda empresa do setor precisa cobrir.
// Todas devem estar mapeadas a pelo menos 1 cargo (regra de aprovação).
const FUNCOES_POR_SETOR = {
  'E-commerce': [
    { id: 'estrategia',    label: 'Estratégia' },
    { id: 'governanca',    label: 'Governança/Diretoria' },
    { id: 'comercial',     label: 'Comercial' },
    { id: 'marketing',     label: 'Marketing' },
    { id: 'compras',       label: 'Compras' },
    { id: 'estoque',       label: 'Estoque' },
    { id: 'logistica',     label: 'Logística' },
    { id: 'tecnologia',    label: 'Tecnologia' },
    { id: 'atendimento',   label: 'Atendimento ao cliente' },
    { id: 'financeiro',    label: 'Financeiro' },
    { id: 'juridico',      label: 'Jurídico/Compliance' },
    { id: 'gente_cultura', label: 'Gente e Cultura (RH)' },
  ],
  'Bar/Restaurante': [
    { id: 'estrategia',        label: 'Estratégia' },
    { id: 'governanca',        label: 'Governança/Diretoria' },
    { id: 'gastronomia',       label: 'Gastronomia (criação de cardápio)' },
    { id: 'operacao_cozinha',  label: 'Operação de cozinha' },
    { id: 'atendimento_salao', label: 'Atendimento de salão' },
    { id: 'bar',               label: 'Bar e drinks' },
    { id: 'compras',           label: 'Compras de ingredientes' },
    { id: 'eventos',           label: 'Eventos' },
    { id: 'marketing',         label: 'Marketing' },
    { id: 'financeiro',        label: 'Financeiro' },
    { id: 'gente_cultura',     label: 'Gente e Cultura (RH)' },
  ],
  'Saúde': [
    { id: 'estrategia',         label: 'Estratégia' },
    { id: 'governanca',         label: 'Governança/Diretoria' },
    { id: 'direcao_clinica',    label: 'Direção clínica' },
    { id: 'atendimento_medico', label: 'Atendimento médico' },
    { id: 'enfermagem',         label: 'Enfermagem' },
    { id: 'recepcao_agenda',    label: 'Recepção e agenda' },
    { id: 'gestao_convenios',   label: 'Gestão de convênios' },
    { id: 'telemedicina',       label: 'Telemedicina' },
    { id: 'marketing',          label: 'Marketing' },
    { id: 'financeiro',         label: 'Financeiro' },
    { id: 'juridico',           label: 'Jurídico/Compliance' },
    { id: 'gente_cultura',      label: 'Gente e Cultura (RH)' },
  ],
  'Construção Civil': [
    { id: 'estrategia',          label: 'Estratégia' },
    { id: 'governanca',          label: 'Governança/Diretoria' },
    { id: 'direcao_engenharia',  label: 'Direção de engenharia' },
    { id: 'execucao_obras',      label: 'Execução de obras' },
    { id: 'compras_obra',        label: 'Compras de obra' },
    { id: 'seguranca',           label: 'Segurança do trabalho' },
    { id: 'comercial',           label: 'Comercial' },
    { id: 'loja_materiais',      label: 'Loja de materiais' },
    { id: 'financeiro',          label: 'Financeiro' },
    { id: 'juridico',            label: 'Jurídico/Compliance' },
    { id: 'gente_cultura',       label: 'Gente e Cultura (RH)' },
  ],
  'geral': [
    { id: 'estrategia',    label: 'Estratégia' },
    { id: 'governanca',    label: 'Governança/Diretoria' },
    { id: 'comercial',     label: 'Comercial' },
    { id: 'producao',      label: 'Produção/Serviço principal' },
    { id: 'compras',       label: 'Compras' },
    { id: 'atendimento',   label: 'Atendimento' },
    { id: 'marketing',     label: 'Marketing' },
    { id: 'tecnologia',    label: 'Tecnologia' },
    { id: 'financeiro',    label: 'Financeiro' },
    { id: 'juridico',      label: 'Jurídico' },
    { id: 'gente_cultura', label: 'Gente e Cultura (RH)' },
  ],
};

// Cargos típicos por setor — dropdown sugestivo + headcount sugerido. Empresário pode escolher "Outro…"
const CARGOS_POR_SETOR = {
  'E-commerce': [
    { id: 'ceo',              label: 'CEO',                          headcount: 1 },
    { id: 'coo',              label: 'COO/Diretor de Operações',     headcount: 1 },
    { id: 'cto',              label: 'CTO/Diretor de TI',            headcount: 1 },
    { id: 'cfo',              label: 'CFO/Diretor Financeiro',       headcount: 1 },
    { id: 'cmo',              label: 'CMO/Diretor de Marketing',     headcount: 1 },
    { id: 'dir_comercial',    label: 'Diretor Comercial',            headcount: 1 },
    { id: 'ger_compras',      label: 'Gerente de Compras',           headcount: 1 },
    { id: 'ger_estoque',      label: 'Gerente de Estoque',           headcount: 1 },
    { id: 'ger_logistica',    label: 'Gerente de Logística',         headcount: 1 },
    { id: 'ger_atend',        label: 'Gerente de Atendimento',       headcount: 1 },
    { id: 'ger_juridico',     label: 'Gerente Jurídico',             headcount: 1 },
    { id: 'ger_rh',           label: 'Gerente de Gente e Cultura',   headcount: 1 },
    { id: 'socio_fundador',   label: 'Sócio/Fundador',               headcount: 1 },
    { id: 'conselho',         label: 'Conselho/Advisor',             headcount: 3 },
  ],
  'Bar/Restaurante': [
    { id: 'chef_prop',        label: 'Chef-proprietário',            headcount: 1 },
    { id: 'gerente_geral',    label: 'Gerente Geral',                headcount: 1 },
    { id: 'sous_chef',        label: 'Sous-chef',                    headcount: 1 },
    { id: 'chef_partida',     label: 'Chef de partida',              headcount: 1 },
    { id: 'maitre',           label: 'Maître',                       headcount: 1 },
    { id: 'ger_salao',        label: 'Gerente de Salão',             headcount: 1 },
    { id: 'bartender',        label: 'Bartender-chefe',              headcount: 1 },
    { id: 'sommelier',        label: 'Sommelier',                    headcount: 1 },
    { id: 'ger_eventos',      label: 'Gerente de Eventos',           headcount: 1 },
    { id: 'resp_compras',     label: 'Responsável de Compras',       headcount: 1 },
    { id: 'ger_marketing',    label: 'Gerente de Marketing',         headcount: 1 },
    { id: 'ger_financeiro',   label: 'Gerente Financeiro',           headcount: 1 },
    { id: 'socio',            label: 'Sócio',                        headcount: 1 },
  ],
  'Saúde': [
    { id: 'dir_clinico',      label: 'Diretor Clínico',              headcount: 1 },
    { id: 'dir_admin',        label: 'Diretor Administrativo',       headcount: 1 },
    { id: 'coord_med',        label: 'Coord. Médico',                headcount: 1 },
    { id: 'coord_enf',        label: 'Coord. de Enfermagem',         headcount: 1 },
    { id: 'medico_esp',       label: 'Médico Especialista',          headcount: 3 },
    { id: 'ger_convenios',    label: 'Gerente de Convênios',         headcount: 1 },
    { id: 'resp_telemed',     label: 'Responsável de Telemedicina',  headcount: 1 },
    { id: 'ger_recepcao',     label: 'Gerente de Recepção',          headcount: 1 },
    { id: 'ger_marketing',    label: 'Gerente de Marketing',         headcount: 1 },
    { id: 'ger_financeiro',   label: 'Gerente Financeiro',           headcount: 1 },
    { id: 'ger_juridico',     label: 'Gerente Jurídico',             headcount: 1 },
    { id: 'ger_rh',           label: 'Gerente de Gente e Cultura',   headcount: 1 },
    { id: 'socio',            label: 'Sócio',                        headcount: 1 },
    { id: 'conselho',         label: 'Conselho/Advisor',             headcount: 3 },
  ],
  'Construção Civil': [
    { id: 'dir_eng',          label: 'Diretor de Engenharia',        headcount: 1 },
    { id: 'dir_com_fin',      label: 'Diretor Comercial/Financeiro', headcount: 1 },
    { id: 'eng_residente',    label: 'Engenheiro Residente',         headcount: 2 },
    { id: 'mestre_obras',     label: 'Mestre de Obras',              headcount: 3 },
    { id: 'ger_compras',      label: 'Gerente de Compras',           headcount: 1 },
    { id: 'ger_seguranca',    label: 'Gerente de Segurança',         headcount: 1 },
    { id: 'ger_comercial',    label: 'Gerente Comercial',            headcount: 1 },
    { id: 'ger_loja',         label: 'Gerente da Loja de Materiais', headcount: 1 },
    { id: 'ger_financeiro',   label: 'Gerente Financeiro',           headcount: 1 },
    { id: 'ger_juridico',     label: 'Gerente Jurídico',             headcount: 1 },
    { id: 'ger_rh',           label: 'Gerente de Gente e Cultura',   headcount: 1 },
    { id: 'socio',            label: 'Sócio',                        headcount: 1 },
  ],
  'geral': [
    { id: 'ceo',              label: 'CEO',                          headcount: 1 },
    { id: 'coo',              label: 'COO',                          headcount: 1 },
    { id: 'cfo',              label: 'CFO',                          headcount: 1 },
    { id: 'cmo',              label: 'CMO',                          headcount: 1 },
    { id: 'cto',              label: 'CTO',                          headcount: 1 },
    { id: 'dir_comercial',    label: 'Diretor Comercial',            headcount: 1 },
    { id: 'dir_operacoes',    label: 'Diretor de Operações',         headcount: 1 },
    { id: 'gerente',          label: 'Gerente',                      headcount: 1 },
    { id: 'socio',            label: 'Sócio',                        headcount: 1 },
    { id: 'conselho',         label: 'Conselho/Advisor',             headcount: 3 },
  ],
};

// Cargos sugeridos pela IA por setor × função essencial. Cada item é nome literal do cargo.
// Empresário pode aceitar (clica no chip), apagar (X na linha do cargo) ou acrescentar (chip "+ Outro…").
const CARGOS_SUGERIDOS_POR_AREA = {
  'E-commerce': {
    estrategia:    ['CEO', 'Sócio/Fundador'],
    governanca:    ['Conselho/Advisor', 'Diretor Geral'],
    comercial:     ['Diretor Comercial', 'Gerente Comercial', 'Executivo de Vendas'],
    marketing:     ['CMO/Diretor de Marketing', 'Gerente de Marketing', 'Coordenador Digital'],
    compras:       ['Gerente de Compras', 'Comprador'],
    estoque:       ['Gerente de Estoque', 'Coordenador de CD'],
    logistica:     ['Gerente de Logística', 'Coordenador de Expedição'],
    tecnologia:    ['CTO/Diretor de TI', 'Tech Lead', 'Desenvolvedor'],
    atendimento:   ['Gerente de Atendimento', 'Supervisor SAC'],
    financeiro:    ['CFO/Diretor Financeiro', 'Gerente Financeiro', 'Controller'],
    juridico:      ['Gerente Jurídico'],
    gente_cultura: ['Gerente de Gente e Cultura', 'Recrutador'],
  },
  'Bar/Restaurante': {
    estrategia:        ['Sócio', 'Chef-proprietário'],
    governanca:        ['Diretor Geral', 'Sócio'],
    gastronomia:       ['Chef Executivo', 'Sous-chef'],
    operacao_cozinha:  ['Sous-chef', 'Chef de partida', 'Cozinheiro'],
    atendimento_salao: ['Maître', 'Gerente de Salão', 'Garçom'],
    bar:               ['Bartender-chefe', 'Bartender'],
    compras:           ['Responsável de Compras', 'Stewart'],
    eventos:           ['Gerente de Eventos', 'Coordenador de Eventos'],
    marketing:         ['Gerente de Marketing', 'Mídias Sociais'],
    financeiro:        ['Gerente Financeiro', 'Controller'],
    gente_cultura:     ['Gerente de Gente'],
  },
  'Saúde': {
    estrategia:         ['Diretor Clínico', 'Sócio Fundador'],
    governanca:         ['Diretor Administrativo', 'Conselho'],
    direcao_clinica:    ['Diretor Clínico', 'Coord. Médico'],
    atendimento_medico: ['Médico Especialista'],
    enfermagem:         ['Coord. de Enfermagem', 'Enfermeiro'],
    recepcao_agenda:    ['Gerente de Recepção', 'Recepcionista'],
    gestao_convenios:   ['Gerente de Convênios', 'Faturista'],
    telemedicina:       ['Responsável de Telemedicina'],
    marketing:          ['Gerente de Marketing'],
    financeiro:         ['CFO', 'Controller'],
    juridico:           ['Gerente Jurídico'],
    gente_cultura:      ['Gerente de Gente'],
  },
  'Construção Civil': {
    estrategia:         ['Sócio', 'Diretor de Engenharia'],
    governanca:         ['Diretor Geral', 'Conselho'],
    direcao_engenharia: ['Diretor de Engenharia', 'Engenheiro Sênior'],
    execucao_obras:     ['Engenheiro Residente', 'Mestre de Obras'],
    compras_obra:       ['Gerente de Compras', 'Comprador'],
    seguranca:          ['Gerente de Segurança', 'Técnico de SST'],
    comercial:          ['Gerente Comercial', 'Executivo de Vendas'],
    loja_materiais:     ['Gerente da Loja', 'Vendedor'],
    financeiro:         ['CFO', 'Controller'],
    juridico:           ['Gerente Jurídico'],
    gente_cultura:      ['Gerente de Gente'],
  },
  'geral': {
    estrategia:    ['CEO', 'Sócio'],
    governanca:    ['Diretor Geral', 'Conselho'],
    comercial:     ['Diretor Comercial', 'Gerente', 'Executivo'],
    producao:      ['Gerente de Produção', 'Coordenador'],
    compras:       ['Gerente de Compras', 'Comprador'],
    atendimento:   ['Gerente de Atendimento'],
    marketing:     ['CMO', 'Gerente de Marketing'],
    tecnologia:    ['CTO', 'Gerente de TI'],
    financeiro:    ['CFO', 'Gerente Financeiro'],
    juridico:      ['Gerente Jurídico'],
    gente_cultura: ['Gerente de Gente'],
  },
};

// Cargos SU · lista única (funcional · fallback 'geral' cobre todos os setores)
const CARGOS_POR_SETOR_SU = {
  'geral': [
    { id: 'ceo',            label: 'CEO / Fundador',                 headcount: 1 },
    { id: 'cto',            label: 'CTO / Diretor de Tecnologia',    headcount: 1 },
    { id: 'cpo',            label: 'CPO / Diretor de Produto',       headcount: 1 },
    { id: 'cro',            label: 'CRO / Diretor de Receita',       headcount: 1 },
    { id: 'coo',            label: 'COO / Diretor de Operações',     headcount: 1 },
    { id: 'lider_eng',      label: 'Líder de Engenharia',            headcount: 1 },
    { id: 'lider_produto',  label: 'Líder de Produto',               headcount: 1 },
    { id: 'lider_growth',   label: 'Líder de Crescimento',           headcount: 1 },
    { id: 'lider_design',   label: 'Líder de Design',                headcount: 1 },
    { id: 'lider_cs',       label: 'Líder de Sucesso do Cliente',    headcount: 1 },
    { id: 'gente_cultura',  label: 'Gente e Cultura',                headcount: 1 },
    { id: 'socio_fundador', label: 'Sócio-fundador',                 headcount: 1 },
    { id: 'conselho',       label: 'Conselho / Advisor',             headcount: 3 },
  ],
};

const CARGOS_SUGERIDOS_POR_AREA_SU = {
  'geral': {
    estrategia:      ['CEO / Fundador', 'Sócio-fundador'],
    governanca:      ['Conselho / Advisor'],
    engenharia:      ['CTO / Diretor de Tecnologia', 'Líder de Engenharia', 'Desenvolvedor'],
    produto:         ['CPO / Diretor de Produto', 'Líder de Produto', 'Product Manager'],
    design:          ['Líder de Design', 'Designer de Produto'],
    vendas:          ['CRO / Diretor de Receita', 'Executivo de Vendas'],
    marketing:       ['Líder de Crescimento', 'Analista de Marketing'],
    operacoes:       ['COO / Diretor de Operações', 'Analista de Operações'],
    sucesso_cliente: ['Líder de Sucesso do Cliente', 'Analista de CS'],
    gente_cultura:   ['Gente e Cultura', 'Recrutador'],
  },
};

// Funções essenciais SU · lista única (funcional · fallback 'geral')
const FUNCOES_POR_SETOR_SU = {
  'geral': [
    { id: 'estrategia',      label: 'Estratégia' },
    { id: 'governanca',      label: 'Governança/Conselho' },
    { id: 'engenharia',      label: 'Engenharia' },
    { id: 'produto',         label: 'Produto' },
    { id: 'design',          label: 'Design' },
    { id: 'vendas',          label: 'Vendas' },
    { id: 'marketing',       label: 'Marketing' },
    { id: 'operacoes',       label: 'Operações' },
    { id: 'sucesso_cliente', label: 'Sucesso do cliente' },
    { id: 'gente_cultura',   label: 'Gente e Cultura' },
  ],
};

// Opções dos campos novos SU (Bloco 2 dedicação · Bloco 3 rodada)
const DEDICACAO_OPCOES_SU = [
  { id: 'integral',    label: 'Tempo integral' },
  { id: 'parcial',     label: 'Tempo parcial' },
  { id: 'conselheiro', label: 'Conselheiro' },
];
const RODADAS_HIRE_SU = [
  { id: 'preseed', label: 'Pre-Seed' },
  { id: 'semente', label: 'Semente' },
  { id: 'serieA',  label: 'Série A' },
  { id: 'serieB',  label: 'Série B' },
  { id: 'proprio', label: 'Caixa próprio' },
];

const RASCUNHO_TAREFA_10 = {
  shopmax: {
    organograma_nos: [
      { id: 'ceo',         cargo: 'CEO',                       headcount: 1,  reporta_a: null,    funcoes: ['estrategia', 'governanca'] },
      { id: 'dir_com',     cargo: 'Diretor Comercial',         headcount: 2,  reporta_a: 'ceo',   funcoes: ['comercial'] },
      { id: 'comercial',   cargo: 'Comercial e vendas',        headcount: 18, reporta_a: 'dir_com', funcoes: ['comercial'] },
      { id: 'marketing',   cargo: 'Marketing',                 headcount: 10, reporta_a: 'dir_com', funcoes: ['marketing'] },
      { id: 'dir_ops',     cargo: 'COO/Diretor de Operações',  headcount: 2,  reporta_a: 'ceo',   funcoes: [] },
      { id: 'compras',     cargo: 'Compras e estoque',         headcount: 8,  reporta_a: 'dir_ops', funcoes: ['compras', 'estoque'] },
      { id: 'logistica',   cargo: 'Logística',                 headcount: 12, reporta_a: 'dir_ops', funcoes: ['logistica'] },
      { id: 'atendimento', cargo: 'Atendimento e pós-venda',   headcount: 5,  reporta_a: 'dir_ops', funcoes: ['atendimento'] },
      { id: 'dir_ti',      cargo: 'CTO/Diretor de TI',         headcount: 15, reporta_a: 'ceo',   funcoes: ['tecnologia'] },
      { id: 'dir_fin',     cargo: 'CFO/Diretor Financeiro',    headcount: 8,  reporta_a: 'ceo',   funcoes: ['financeiro', 'juridico', 'gente_cultura'] },
    ],
    organograma: 'Estrutura matricial enxuta · 3 níveis hierárquicos · ~85 pessoas no total. CEO dirige diretamente as 4 diretorias (Comercial, Operações, TI/Produto, Financeira), com Comercial e Operações desdobradas em sub-áreas operacionais.',
    areas_chars: ['comercial_vendas', 'compras_estoque', 'logistica', 'marketing', 'atendimento_pos', 'ti', 'financeiro'],
    areas_extras: [],
    pessoas_chave: [
      { nome: 'CEO/Sócio fundador',     area: 'estrategia', papel_chips: ['visao', 'investidores'],          dependencia: 'd' },
      { nome: 'Diretor de Operações',   area: 'operacoes',  papel_chips: ['estoque_cd', 'diaria', 'capacidade'], dependencia: 'b' },
      { nome: 'Líder de Produto/TI',    area: 'ti',         papel_chips: ['produto', 'roadmap', 'infra'],    dependencia: 'b' },
      { nome: 'Comprador-chefe',        area: 'operacoes',  papel_chips: ['fornecedores', 'qualidade'],      dependencia: 'a' },
    ],
    headcount_hoje: 'Hoje: ~85 pessoas distribuídas em 4 diretorias (Comercial 30 · Operações 25 · TI/Produto 15 · Financeiro 8 · sede 7).',
    hires_plano: [
      { ano: 'Ano 1', descricao: 'CTO/Head of Engineering · time de TI passa de 15 para 20' },
      { ano: 'Ano 2', descricao: 'Head of Marketing sênior · time de marketing dobra para 15 pessoas' },
      { ano: 'Ano 3', descricao: 'Diretor de Expansão (2º CD) · +20 colaboradores em operações' },
      { ano: 'Ano 4', descricao: 'CFO mais sênior · time comercial de canal D2C ampliado' },
      { ano: 'Ano 5', descricao: 'Liderança M&A para eventual aquisição · ~180 colaboradores totais' },
    ],
    cultura_chars: ['meritocracia', 'bonus_resultado', 'flexivel_remoto', 'dev_continuo', 'diversidade'],
    cultura_extras: [],
    remuneracao: 'Salário competitivo + bônus por resultado (até 25% do anual) + benefícios padrão (saúde, vale-refeição, gympass). Plano de participação nos lucros em discussão para o nível C.',
  },
  sabor_arte: {
    organograma_nos: [
      { id: 'chef',     cargo: 'Chef-proprietário',  headcount: 1,  reporta_a: null,   funcoes: ['estrategia', 'governanca', 'gastronomia'] },
      { id: 'gg',       cargo: 'Gerente Geral',      headcount: 1,  reporta_a: 'chef', funcoes: ['compras', 'marketing', 'financeiro', 'gente_cultura'] },
      { id: 'sous',     cargo: 'Sous-chef',          headcount: 1,  reporta_a: 'chef', funcoes: ['operacao_cozinha'] },
      { id: 'cozinha',  cargo: 'Cozinha',            headcount: 11, reporta_a: 'sous', funcoes: ['operacao_cozinha'] },
      { id: 'salao',    cargo: 'Salão e atendimento',headcount: 9,  reporta_a: 'gg',   funcoes: ['atendimento_salao'] },
      { id: 'bar',      cargo: 'Bar e drinks',       headcount: 4,  reporta_a: 'gg',   funcoes: ['bar'] },
      { id: 'eventos',  cargo: 'Eventos privados',   headcount: 2,  reporta_a: 'gg',   funcoes: ['eventos'] },
    ],
    organograma: 'Estrutura enxuta de 2-3 níveis · ~28 pessoas. Chef-proprietário no topo, Gerente Geral conduz operação de salão/bar/eventos · Sous-chef conduz a brigada de cozinha.',
    areas_chars: ['cozinha', 'salao_atend', 'bar_drinks', 'compras_ingred', 'eventos', 'marketing_redes', 'financeiro_b'],
    areas_extras: [],
    pessoas_chave: [
      { nome: 'Chef-proprietário',      area: 'estrategia', papel_chips: ['visao', 'cultura'],          dependencia: 'd' },
      { nome: 'Gerente Geral',          area: 'operacoes',  papel_chips: ['diaria', 'fornecedores'],    dependencia: 'c' },
      { nome: 'Sous-chef',              area: 'operacoes',  papel_chips: ['qualidade', 'melhoria'],     dependencia: 'b' },
      { nome: 'Sommelier',              area: 'operacoes',  papel_chips: ['qualidade'],                 dependencia: 'a' },
    ],
    headcount_hoje: 'Hoje: ~28 pessoas (Cozinha 12 · Salão 9 · Bar 4 · Eventos 2 · administrativo 1).',
    hires_plano: [
      { ano: 'Ano 1', descricao: 'Gerente de Marketing dedicado (parte-time hoje) · 2º Sous-chef' },
      { ano: 'Ano 2', descricao: 'Maître sênior + estruturação de plano de carreira de salão' },
      { ano: 'Ano 3', descricao: 'Time de filial 2 (Itaim ou Pinheiros) · ~30 novas pessoas' },
      { ano: 'Ano 4', descricao: 'Diretor Operacional para dar conta de 2 casas · CFO terceirizado vira interno' },
      { ano: 'Ano 5', descricao: 'Casa nova consolidada · ~70 colaboradores no total · expansão para eventos corporativos' },
    ],
    cultura_chars: ['chef_protagonista', 'treinamento_cont', 'carreira_clara', 'ambiente_familiar', 'valorizar_origem'],
    cultura_extras: [],
    remuneracao: 'Fixo por nível + gorjetas compartilhadas + bônus de avaliações em guias gastronômicos. Programa de educação técnica financiado para chefs em desenvolvimento.',
  },
  clinicavida: {
    organograma_nos: [
      { id: 'dir_clin',       cargo: 'Diretor Clínico',         headcount: 1,  reporta_a: null,       funcoes: ['estrategia', 'governanca', 'direcao_clinica'] },
      { id: 'dir_adm',        cargo: 'Diretor Administrativo',  headcount: 1,  reporta_a: 'dir_clin', funcoes: ['financeiro', 'juridico', 'gente_cultura'] },
      { id: 'coord_enf',      cargo: 'Coord. de Enfermagem',    headcount: 1,  reporta_a: 'dir_clin', funcoes: ['enfermagem'] },
      { id: 'medicos',        cargo: 'Médicos especialistas',   headcount: 18, reporta_a: 'dir_clin', funcoes: ['atendimento_medico', 'telemedicina'] },
      { id: 'enfermagem',     cargo: 'Enfermagem',              headcount: 10, reporta_a: 'coord_enf',funcoes: ['enfermagem'] },
      { id: 'ger_conv',       cargo: 'Gerente de Convênios',    headcount: 1,  reporta_a: 'dir_adm',  funcoes: ['gestao_convenios'] },
      { id: 'recepcao',       cargo: 'Recepção/Agenda',         headcount: 7,  reporta_a: 'dir_adm',  funcoes: ['recepcao_agenda'] },
      { id: 'administrativo', cargo: 'Administrativo',          headcount: 3,  reporta_a: 'dir_adm',  funcoes: ['marketing'] },
    ],
    organograma: 'Estrutura multidisciplinar · 3 níveis hierárquicos · ~42 pessoas. Diretor Clínico responde por médicos e qualidade · Diretor Administrativo conduz convênios, recepção e administrativo.',
    areas_chars: ['atendimento_clinico', 'recepcao', 'enfermagem', 'gestao_convenios_s', 'telemed', 'financeiro_s'],
    areas_extras: [],
    pessoas_chave: [
      { nome: 'Diretor Clínico',            area: 'estrategia', papel_chips: ['visao', 'cultura'],            dependencia: 'c' },
      { nome: 'Diretor Administrativo',     area: 'financeiro', papel_chips: ['bancos', 'fluxo_caixa', 'margens'], dependencia: 'c' },
      { nome: 'Coordenadora de Enfermagem', area: 'operacoes',  papel_chips: ['qualidade', 'melhoria'],       dependencia: 'b' },
      { nome: 'Gerente de Convênios',       area: 'financeiro', papel_chips: ['bancos', 'fechamento'],        dependencia: 'b' },
    ],
    headcount_hoje: 'Hoje: ~42 pessoas (Médicos 18 · Enfermagem 10 · Recepção/Agenda 7 · Administrativo 5 · Diretoria 2).',
    hires_plano: [
      { ano: 'Ano 1', descricao: 'Mais 3 médicos especialistas (cardiologia, pediatria, endocrinologia) + Líder de Telemedicina' },
      { ano: 'Ano 2', descricao: 'Coordenador de Pacientes Corporativos + 4 médicos · estruturação do programa preventivo anual' },
      { ano: 'Ano 3', descricao: 'Equipe inicial da 2ª unidade (Vila Mariana/Brooklin) · ~10 novas pessoas' },
      { ano: 'Ano 4', descricao: 'Diretor de Expansão · CFO sênior + estruturação para receber investimento' },
      { ano: 'Ano 5', descricao: '2ª unidade consolidada · ~95 colaboradores no total · plano preventivo escalado' },
    ],
    cultura_chars: ['cuidado_humano', 'educacao_cont', 'multidisciplinar', 'mentoria', 'saude_mental_s'],
    cultura_extras: [],
    remuneracao: 'Salário fixo competitivo + bônus por qualidade clínica + bolsas de educação continuada. Política de tempo protegido para descanso e prevenção de burnout.',
  },
  construmax: {
    organograma_nos: [
      { id: 'dir_eng',        cargo: 'Diretor de Engenharia',        headcount: 1,  reporta_a: null,        funcoes: ['estrategia', 'governanca', 'direcao_engenharia', 'seguranca'] },
      { id: 'dir_com',        cargo: 'Diretor Comercial/Financeiro', headcount: 1,  reporta_a: 'dir_eng',   funcoes: ['comercial', 'financeiro', 'juridico', 'gente_cultura'] },
      { id: 'eng_resid',      cargo: 'Engenheiro Residente',         headcount: 4,  reporta_a: 'dir_eng',   funcoes: ['direcao_engenharia'] },
      { id: 'compras',        cargo: 'Gerente de Compras',           headcount: 3,  reporta_a: 'dir_eng',   funcoes: ['compras_obra'] },
      { id: 'canteiro',       cargo: 'Canteiro/Execução',            headcount: 75, reporta_a: 'eng_resid', funcoes: ['execucao_obras'] },
      { id: 'comercial',      cargo: 'Gerente Comercial',            headcount: 5,  reporta_a: 'dir_com',   funcoes: ['comercial'] },
      { id: 'loja',           cargo: 'Gerente da Loja de Materiais', headcount: 18, reporta_a: 'dir_com',   funcoes: ['loja_materiais'] },
      { id: 'administrativo', cargo: 'Administrativo',               headcount: 10, reporta_a: 'dir_com',   funcoes: ['financeiro'] },
    ],
    organograma: 'Estrutura por obra · 3 níveis · ~120 pessoas (CLT + empreiteiros fixos). Engenharia divide-se em Engenheiros Residentes (que dirigem o canteiro) e Compras · Diretor Comercial/Financeiro dirige Comercial, Loja de materiais e Administrativo.',
    areas_chars: ['engenharia', 'obras_exec', 'compras_obra', 'seguranca', 'comercial_c', 'loja_materiais', 'financeiro_c'],
    areas_extras: [],
    pessoas_chave: [
      { nome: 'Diretor de Engenharia',  area: 'estrategia', papel_chips: ['visao', 'cultura', 'metas'],         dependencia: 'd' },
      { nome: 'Diretor Comercial',      area: 'comercial',  papel_chips: ['captacao', 'contratos', 'key_accounts'], dependencia: 'c' },
      { nome: 'Engenheiro Residente',   area: 'operacoes',  papel_chips: ['diaria', 'qualidade'],               dependencia: 'b' },
      { nome: 'Comprador-chefe',        area: 'operacoes',  papel_chips: ['fornecedores', 'estoque_cd'],        dependencia: 'b' },
    ],
    headcount_hoje: 'Hoje: ~120 pessoas (Canteiro 75 · Engenharia 12 · Comercial 5 · Loja de materiais 18 · Administrativo 10).',
    hires_plano: [
      { ano: 'Ano 1', descricao: '2º Engenheiro Residente + Comprador adicional para suportar 2 obras simultâneas' },
      { ano: 'Ano 2', descricao: 'Gerente Comercial corporativo + reforço de Segurança do Trabalho' },
      { ano: 'Ano 3', descricao: 'Time inicial do 2º canteiro modular · ~25 novas pessoas em obras' },
      { ano: 'Ano 4', descricao: 'Diretor de Expansão da loja de materiais (canal atacado) + CFO mais sênior' },
      { ano: 'Ano 5', descricao: '2ª frente de obras consolidada · ~190 colaboradores totais · captação corporativa madura' },
    ],
    cultura_chars: ['seg_em_primeiro', 'meritocracia_obra', 'treinamento_tec', 'valor_artesao', 'bonus_prazo'],
    cultura_extras: [],
    remuneracao: 'Fixo por cargo + bônus por cumprimento de prazo (até 15% do anual no canteiro). Plano de saúde estendido à família · educação técnica financiada para mestres de obras.',
  },
};

// Rascunho T10-SU · times enxutos de startup (funcoes → FUNCOES_SU · areas → AREAS_SU
// · cultura → CULTURA_SU · pessoas.area → CHIPS_PAPEL_POR_AREA · + campos SU).
const RASCUNHO_TAREFA_10_SU = {
  shopmax: {
    organograma_nos: [
      { id: 'ceo', cargo: 'CEO / Fundador',              headcount: 1, reporta_a: null,  funcoes: ['estrategia', 'governanca'] },
      { id: 'cto', cargo: 'CTO / Diretor de Tecnologia', headcount: 1, reporta_a: 'ceo', funcoes: ['engenharia'] },
      { id: 'cpo', cargo: 'CPO / Diretor de Produto',    headcount: 1, reporta_a: 'ceo', funcoes: ['produto', 'design'] },
      { id: 'cro', cargo: 'CRO / Diretor de Receita',    headcount: 1, reporta_a: 'ceo', funcoes: ['vendas', 'marketing'] },
      { id: 'eng', cargo: 'Engenharia',                  headcount: 5, reporta_a: 'cto', funcoes: ['engenharia'] },
      { id: 'cs',  cargo: 'Sucesso do cliente',          headcount: 2, reporta_a: 'cro', funcoes: ['sucesso_cliente', 'operacoes'] },
    ],
    organograma: 'Time enxuto de ~11 pessoas em 2 níveis. Founders acumulam funções · em fase inicial, poucas áreas estão preenchidas.',
    areas_chars: ['engenharia', 'produto', 'vendas', 'marketing', 'sucesso_cliente'],
    areas_extras: [],
    pessoas_chave: [
      { nome: 'CEO / Fundador',              area: 'estrategia', papel_chips: ['visao', 'capital'],           dependencia: 'd', participacao_societaria: 55, dedicacao: 'integral' },
      { nome: 'CTO / Diretor de Tecnologia', area: 'ti',         papel_chips: ['produto', 'roadmap', 'infra'], dependencia: 'c', participacao_societaria: 20, dedicacao: 'integral' },
      { nome: 'CRO / Diretor de Receita',    area: 'comercial',  papel_chips: ['captacao', 'pipeline'],        dependencia: 'b', participacao_societaria: 10, dedicacao: 'integral' },
      { nome: 'Advisor de Growth',           area: 'marketing',  papel_chips: ['demanda'],                     dependencia: 'a', participacao_societaria: 2,  dedicacao: 'conselheiro' },
    ],
    headcount_hoje: 'Hoje: ~11 pessoas (Engenharia 5 · Produto 2 · Receita 2 · Founders 2).',
    hires_plano: [
      { ano: 'Ano 1', descricao: 'Líder de Crescimento + 2 engenheiros', rodada: 'preseed' },
      { ano: 'Ano 2', descricao: 'Head de Produto + primeiro time de CS', rodada: 'semente' },
      { ano: 'Ano 3', descricao: 'COO + expansão de engenharia',          rodada: 'serieA' },
      { ano: 'Ano 4', descricao: 'CFO + liderança de dados',              rodada: 'serieA' },
      { ano: 'Ano 5', descricao: 'Estrutura para expansão internacional', rodada: 'serieB' },
    ],
    cultura_chars: ['senso_dono', 'velocidade', 'obsessao_cliente', 'transparencia', 'aprendizado'],
    cultura_extras: [],
    remuneracao: 'Salário de mercado ajustado à fase + participação societária (stock options) com vesting. Bônus por metas de tração.',
    retencao_equity: 'sim', retencao_vesting: '4 anos · cliff de 1 ano', retencao_bonus: 'sim', retencao_bemestar: 'sim',
  },
  sabor_arte: {
    organograma_nos: [
      { id: 'ceo',    cargo: 'CEO / Fundador',              headcount: 1, reporta_a: null,   funcoes: ['estrategia', 'governanca'] },
      { id: 'coo',    cargo: 'COO / Diretor de Operações',  headcount: 1, reporta_a: 'ceo',  funcoes: ['operacoes'] },
      { id: 'cpo',    cargo: 'CPO / Diretor de Produto',    headcount: 1, reporta_a: 'ceo',  funcoes: ['produto'] },
      { id: 'ops',    cargo: 'Operação (cozinha + delivery)', headcount: 6, reporta_a: 'coo', funcoes: ['operacoes'] },
      { id: 'growth', cargo: 'Líder de Crescimento',        headcount: 1, reporta_a: 'ceo',  funcoes: ['marketing', 'vendas'] },
    ],
    organograma: 'Time enxuto de ~9 pessoas em 2 níveis. Fundador acumula estratégia e cultura · poucas áreas preenchidas na fase inicial.',
    areas_chars: ['produto', 'operacoes', 'marketing', 'vendas'],
    areas_extras: [],
    pessoas_chave: [
      { nome: 'CEO / Fundador',             area: 'estrategia', papel_chips: ['visao', 'cultura'],   dependencia: 'd', participacao_societaria: 60, dedicacao: 'integral' },
      { nome: 'COO / Diretor de Operações', area: 'operacoes',  papel_chips: ['diaria', 'qualidade'], dependencia: 'c', participacao_societaria: 18, dedicacao: 'integral' },
      { nome: 'Líder de Crescimento',       area: 'marketing',  papel_chips: ['demanda', 'conteudo'], dependencia: 'b', participacao_societaria: 8,  dedicacao: 'integral' },
      { nome: 'Advisor gastronômico',       area: 'estrategia', papel_chips: ['cultura'],            dependencia: 'a', participacao_societaria: 1,  dedicacao: 'conselheiro' },
    ],
    headcount_hoje: 'Hoje: ~9 pessoas (Operação 6 · Produto 1 · Growth 1 · Fundador 1).',
    hires_plano: [
      { ano: 'Ano 1', descricao: 'Engenheiro de software + analista de dados', rodada: 'preseed' },
      { ano: 'Ano 2', descricao: 'Head de Operações da 2ª cozinha',            rodada: 'semente' },
      { ano: 'Ano 3', descricao: 'CRO + primeiro time de vendas B2B',          rodada: 'serieA' },
      { ano: 'Ano 4', descricao: 'CFO + expansão de CS',                       rodada: 'serieA' },
      { ano: 'Ano 5', descricao: 'Estrutura para novas praças',               rodada: 'serieB' },
    ],
    cultura_chars: ['obsessao_cliente', 'velocidade', 'frugalidade', 'senso_dono', 'aprendizado'],
    cultura_extras: [],
    remuneracao: 'Salário ajustado à fase + participação societária com vesting para o núcleo. Bônus por metas de recorrência.',
    retencao_equity: 'sim', retencao_vesting: '4 anos · cliff de 1 ano', retencao_bonus: 'sim', retencao_bemestar: 'nao',
  },
  clinicavida: {
    organograma_nos: [
      { id: 'ceo',     cargo: 'CEO / Fundador',              headcount: 1, reporta_a: null,  funcoes: ['estrategia', 'governanca'] },
      { id: 'cto',     cargo: 'CTO / Diretor de Tecnologia', headcount: 1, reporta_a: 'ceo', funcoes: ['engenharia'] },
      { id: 'dir_med', cargo: 'Diretor Médico',              headcount: 1, reporta_a: 'ceo', funcoes: ['operacoes', 'produto'] },
      { id: 'eng',     cargo: 'Engenharia',                  headcount: 4, reporta_a: 'cto', funcoes: ['engenharia'] },
      { id: 'cs',      cargo: 'Sucesso do cliente',          headcount: 3, reporta_a: 'ceo', funcoes: ['sucesso_cliente'] },
    ],
    organograma: 'Time enxuto de ~10 pessoas em 2 níveis. Fundador e Diretor Médico acumulam funções · poucas áreas preenchidas na fase inicial.',
    areas_chars: ['engenharia', 'produto', 'operacoes', 'sucesso_cliente'],
    areas_extras: [],
    pessoas_chave: [
      { nome: 'CEO / Fundador',              area: 'estrategia', papel_chips: ['visao', 'capital'],          dependencia: 'c', participacao_societaria: 50, dedicacao: 'integral' },
      { nome: 'CTO / Diretor de Tecnologia', area: 'ti',         papel_chips: ['infra', 'seguranca', 'dados'], dependencia: 'c', participacao_societaria: 22, dedicacao: 'integral' },
      { nome: 'Diretor Médico',              area: 'operacoes',  papel_chips: ['qualidade', 'melhoria'],      dependencia: 'c', participacao_societaria: 15, dedicacao: 'integral' },
      { nome: 'Advisor clínico',             area: 'estrategia', papel_chips: ['cultura'],                    dependencia: 'a', participacao_societaria: 2,  dedicacao: 'conselheiro' },
    ],
    headcount_hoje: 'Hoje: ~10 pessoas (Engenharia 4 · Sucesso do cliente 3 · Direção 2 · Fundador 1).',
    hires_plano: [
      { ano: 'Ano 1', descricao: 'Líder de Crescimento + engenheiro',       rodada: 'preseed' },
      { ano: 'Ano 2', descricao: 'Head de Produto clínico + CS',            rodada: 'semente' },
      { ano: 'Ano 3', descricao: 'COO + expansão de engenharia',            rodada: 'serieA' },
      { ano: 'Ano 4', descricao: 'CFO + liderança de dados de saúde',       rodada: 'serieA' },
      { ano: 'Ano 5', descricao: 'Estrutura para novos estados',            rodada: 'serieB' },
    ],
    cultura_chars: ['obsessao_cliente', 'transparencia', 'aprendizado', 'senso_dono', 'diversidade'],
    cultura_extras: [],
    remuneracao: 'Salário competitivo + participação societária com vesting. Cuidado com privacidade dos dados e bem-estar do time.',
    retencao_equity: 'sim', retencao_vesting: '4 anos · cliff de 1 ano', retencao_bonus: 'sim', retencao_bemestar: 'sim',
  },
  construmax: {
    organograma_nos: [
      { id: 'ceo', cargo: 'CEO / Fundador',              headcount: 1, reporta_a: null,  funcoes: ['estrategia', 'governanca'] },
      { id: 'cto', cargo: 'CTO / Diretor de Tecnologia', headcount: 1, reporta_a: 'ceo', funcoes: ['engenharia'] },
      { id: 'cro', cargo: 'CRO / Diretor de Receita',    headcount: 1, reporta_a: 'ceo', funcoes: ['vendas'] },
      { id: 'eng', cargo: 'Engenharia',                  headcount: 4, reporta_a: 'cto', funcoes: ['engenharia', 'produto'] },
      { id: 'cs',  cargo: 'Sucesso do cliente',          headcount: 2, reporta_a: 'cro', funcoes: ['sucesso_cliente', 'operacoes'] },
    ],
    organograma: 'Time enxuto de ~9 pessoas em 2 níveis. Founders acumulam funções · poucas áreas preenchidas na fase inicial.',
    areas_chars: ['engenharia', 'produto', 'vendas', 'sucesso_cliente'],
    areas_extras: [],
    pessoas_chave: [
      { nome: 'CEO / Fundador',              area: 'estrategia', papel_chips: ['visao', 'metas'],                  dependencia: 'd', participacao_societaria: 55, dedicacao: 'integral' },
      { nome: 'CTO / Diretor de Tecnologia', area: 'ti',         papel_chips: ['infra', 'roadmap'],               dependencia: 'c', participacao_societaria: 20, dedicacao: 'integral' },
      { nome: 'CRO / Diretor de Receita',    area: 'comercial',  papel_chips: ['captacao', 'contratos', 'key_accounts'], dependencia: 'c', participacao_societaria: 12, dedicacao: 'integral' },
      { nome: 'Advisor de construção',       area: 'estrategia', papel_chips: ['cultura'],                        dependencia: 'a', participacao_societaria: 2,  dedicacao: 'conselheiro' },
    ],
    headcount_hoje: 'Hoje: ~9 pessoas (Engenharia 4 · Sucesso do cliente 2 · Direção 2 · Fundador 1).',
    hires_plano: [
      { ano: 'Ano 1', descricao: 'Líder de Engenharia + vendedor B2B', rodada: 'preseed' },
      { ano: 'Ano 2', descricao: 'Head de Produto + time de CS',       rodada: 'semente' },
      { ano: 'Ano 3', descricao: 'COO + expansão de engenharia',       rodada: 'serieA' },
      { ano: 'Ano 4', descricao: 'CFO + liderança de dados',           rodada: 'serieA' },
      { ano: 'Ano 5', descricao: 'Estrutura para novas regiões',       rodada: 'serieB' },
    ],
    cultura_chars: ['senso_dono', 'velocidade', 'vies_acao', 'transparencia', 'aprendizado'],
    cultura_extras: [],
    remuneracao: 'Salário ajustado à fase + participação societária com vesting. Bônus por metas de adoção.',
    retencao_equity: 'sim', retencao_vesting: '4 anos · cliff de 1 ano', retencao_bonus: 'sim', retencao_bemestar: 'nao',
  },
};

// Layout do organograma (algoritmo tipo "tidy tree" simples · 2 passes recursivos)
// Recebe array de nós { id, cargo, headcount, reporta_a } e devolve { nodes, edges, width, height }
const layoutOrganograma = (nos) => {
  if (!nos || nos.length === 0) return { nodes: [], edges: [], width: 0, height: 0 };
  const root = nos.find(n => !n.reporta_a);
  if (!root) return { nodes: [], edges: [], width: 0, height: 0 };

  const W = 150;   // largura da caixa
  const H = 52;    // altura da caixa
  const GAP_X = 14;
  const GAP_Y = 32;

  // pass 1: calcula a largura horizontal de cada subárvore
  const widthOf = (id) => {
    const filhos = nos.filter(n => n.reporta_a === id);
    if (filhos.length === 0) return W;
    const sw = filhos.map(f => widthOf(f.id));
    return sw.reduce((a, b) => a + b, 0) + GAP_X * (filhos.length - 1);
  };

  const widthTotal = widthOf(root.id);
  const nodes = [];
  const edges = [];

  // pass 2: posiciona os nós e desenha as arestas
  const place = (id, x0, level) => {
    const w = widthOf(id);
    const node = nos.find(n => n.id === id);
    const nodeX = x0 + w / 2 - W / 2;
    const nodeY = level * (H + GAP_Y);
    nodes.push({ ...node, x: nodeX, y: nodeY });

    const filhos = nos.filter(n => n.reporta_a === id);
    let cursor = x0;
    for (const f of filhos) {
      const subW = widthOf(f.id);
      const childX = cursor + subW / 2 - W / 2;
      const childY = (level + 1) * (H + GAP_Y);
      // aresta L-shape: bottom-center do pai → ponto intermediário → top-center do filho
      edges.push({
        x1: nodeX + W / 2, y1: nodeY + H,
        x2: childX + W / 2, y2: childY,
        midY: nodeY + H + GAP_Y / 2,
      });
      place(f.id, cursor, level + 1);
      cursor += subW + GAP_X;
    }
  };

  place(root.id, 0, 0);
  const maxY = Math.max(...nodes.map(n => n.y)) + H;
  return { nodes, edges, width: widthTotal, height: maxY };
};

const ConteudoTarefa10 = ({ estruturaRh, setEstruturaRh, empresa, modo = 'EE' }) => {
  const rascunho = (modo === 'SU' ? RASCUNHO_TAREFA_10_SU : RASCUNHO_TAREFA_10)[empresa?.id] || {
    organograma_nos: [],
    organograma: '',
    areas_chars: [], areas_extras: [],
    pessoas_chave: [],
    headcount_hoje: '',
    hires_plano: [],
    cultura_chars: [], cultura_extras: [],
    remuneracao: '',
    retencao_equity: '', retencao_vesting: '', retencao_bonus: '', retencao_bemestar: '',
  };
  const obter = (campo) => estruturaRh?.[campo] ?? rascunho[campo];
  const organograma_nos      = (obter('organograma_nos') || []).map(n => ({ ...n, funcoes: n.funcoes || [] }));
  const organograma                = obter('organograma') || '';
  const organograma_editou_livre   = obter('organograma_editou_livre') || false;
  const areas_chars          = obter('areas_chars') || [];
  const areas_extras         = obter('areas_extras') || [];
  const areas_livre          = obter('areas_livre');
  const areas_editou_livre   = obter('areas_editou_livre') || false;
  const pessoas_chave        = obter('pessoas_chave') || [];
  const headcount_hoje       = obter('headcount_hoje') || '';
  const hires_plano          = obter('hires_plano') || [];
  const cultura_chars        = obter('cultura_chars') || [];
  const cultura_extras       = obter('cultura_extras') || [];
  const cultura_livre        = obter('cultura_livre');
  const cultura_editou_livre = obter('cultura_editou_livre') || false;
  const remuneracao                = obter('remuneracao') || '';
  const remuneracao_editou_livre   = obter('remuneracao_editou_livre') || false;
  const retencao_equity            = obter('retencao_equity') || '';
  const retencao_vesting           = obter('retencao_vesting') || '';
  const retencao_bonus             = obter('retencao_bonus') || '';
  const retencao_bemestar          = obter('retencao_bemestar') || '';

  const setor = empresa?.setor || 'geral';
  const fonteAreas    = modo === 'SU' ? CARACTERISTICAS_AREAS_POR_SETOR_SU : CARACTERISTICAS_AREAS_POR_SETOR;
  const fonteCultura  = modo === 'SU' ? CARACTERISTICAS_CULTURA_POR_SETOR_SU : CARACTERISTICAS_CULTURA_POR_SETOR;
  const fonteFuncoes  = modo === 'SU' ? FUNCOES_POR_SETOR_SU : FUNCOES_POR_SETOR;
  const fonteCargos   = modo === 'SU' ? CARGOS_POR_SETOR_SU : CARGOS_POR_SETOR;
  const fonteSugeridos = modo === 'SU' ? CARGOS_SUGERIDOS_POR_AREA_SU : CARGOS_SUGERIDOS_POR_AREA;
  const catalogoAreas   = fonteAreas[setor]   || fonteAreas['geral'];
  const catalogoCultura = fonteCultura[setor] || fonteCultura['geral'];
  const catalogoFuncoes = fonteFuncoes[setor] || fonteFuncoes['geral'];
  const catalogoCargos  = fonteCargos[setor]  || fonteCargos['geral'];

  // Funções cobertas por algum cargo · função sem cobertura é lacuna (⚠ vermelho)
  const funcoesCobertas = new Set();
  organograma_nos.forEach(n => (n.funcoes || []).forEach(f => funcoesCobertas.add(f)));

  const fraseAreas = areas_editou_livre && areas_livre
    ? areas_livre
    : montarFraseAreas(setor, areas_chars, areas_extras, modo);
  const fraseCultura = cultura_editou_livre && cultura_livre
    ? cultura_livre
    : montarFraseCultura(setor, cultura_chars, cultura_extras, modo);

  const [novoChipArea,    setNovoChipArea]    = useState('');
  const [novoChipCultura, setNovoChipCultura] = useState('');
  const [papelAbertoIdx,  setPapelAbertoIdx]  = useState(null);
  const [areasFechadas,   setAreasFechadas]   = useState({}); // { funcaoId: true } = fechada
  const [graficosBloco1Aberto, setGraficosBloco1Aberto] = useState(false);
  const [graficosBloco3Aberto, setGraficosBloco3Aberto] = useState(false);
  const [anoHireAberto,        setAnoHireAberto]        = useState(null);
  const papelDropdownRef = useRef(null);
  const papelBotaoRefs   = useRef({});

  const toggleAreaAberta = (funcaoId) => {
    setAreasFechadas(prev => ({ ...prev, [funcaoId]: !prev[funcaoId] }));
  };

  // Fecha o dropdown de chips quando clica fora dele · exceto se clicar no próprio botão (toggle)
  useEffect(() => {
    if (papelAbertoIdx === null) return;
    const handler = (e) => {
      const dentroDoPainel = papelDropdownRef.current && papelDropdownRef.current.contains(e.target);
      const botaoDaLinha = papelBotaoRefs.current[papelAbertoIdx];
      const noBotao = botaoDaLinha && botaoDaLinha.contains(e.target);
      if (!dentroDoPainel && !noBotao) {
        setPapelAbertoIdx(null);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [papelAbertoIdx]);

  const atualizar = (campo, valor) => {
    setEstruturaRh(prev => ({
      organograma_nos, organograma, areas_chars, areas_extras, areas_livre, areas_editou_livre,
      pessoas_chave, headcount_hoje, hires_plano,
      cultura_chars, cultura_extras, cultura_livre, cultura_editou_livre, remuneracao,
      retencao_equity, retencao_vesting, retencao_bonus, retencao_bemestar,
      ...prev,
      [campo]: valor,
    }));
  };

  // Organograma · handlers de edição da árvore
  const editarNo = (id, campo, valor) => {
    const novo = organograma_nos.map(n => n.id === id ? { ...n, [campo]: valor } : n);
    atualizar('organograma_nos', novo);
  };
  const adicionarNo = () => {
    const novoId = 'no_' + Date.now();
    const raiz = organograma_nos.find(n => !n.reporta_a);
    atualizar('organograma_nos', [...organograma_nos, {
      id: novoId, cargo: '', cargo_livre: true, headcount: 0,
      reporta_a: raiz?.id || null, funcoes: [],
    }]);
  };
  // Adiciona cargo já vinculado a uma área (função essencial do setor) — usado pelas sugestões da IA
  const adicionarCargoNaArea = (funcaoId, nomeCargo) => {
    const novoId = 'no_' + Date.now() + '_' + Math.floor(Math.random() * 1000);
    const raiz = organograma_nos.find(n => !n.reporta_a);
    const cargoMatch = catalogoCargos.find(c => c.label === nomeCargo);
    atualizar('organograma_nos', [...organograma_nos, {
      id: novoId,
      cargo: nomeCargo,
      cargo_livre: !cargoMatch,
      headcount: cargoMatch?.headcount || 1,
      reporta_a: raiz?.id && raiz.id !== novoId ? raiz.id : null,
      funcoes: [funcaoId],
    }]);
  };
  // Toggle de função no cargo
  const toggleFuncaoNoCargo = (idCargo, idFuncao) => {
    const novo = organograma_nos.map(n => {
      if (n.id !== idCargo) return n;
      const atual = n.funcoes || [];
      const has = atual.includes(idFuncao);
      return { ...n, funcoes: has ? atual.filter(f => f !== idFuncao) : [...atual, idFuncao] };
    });
    atualizar('organograma_nos', novo);
  };
  // Quando seleciona cargo do dropdown, pré-preenche label + headcount sugerido
  const selecionarCargoDoCatalogo = (idNo, cargoId) => {
    if (cargoId === 'outro') {
      const novo = organograma_nos.map(n => n.id === idNo ? { ...n, cargo: '', cargo_livre: true } : n);
      atualizar('organograma_nos', novo);
      return;
    }
    const cargo = catalogoCargos.find(c => c.id === cargoId);
    if (!cargo) return;
    const novo = organograma_nos.map(n => n.id === idNo ? {
      ...n, cargo: cargo.label, cargo_livre: false,
      headcount: (n.headcount && n.headcount > 0) ? n.headcount : cargo.headcount,
    } : n);
    atualizar('organograma_nos', novo);
  };
  const removerNo = (id) => {
    // remove o nó + reaponta filhos pro avô (pai do nó removido)
    const noRemovido = organograma_nos.find(n => n.id === id);
    const avoId = noRemovido?.reporta_a || null;
    const filtrado = organograma_nos
      .filter(n => n.id !== id)
      .map(n => n.reporta_a === id ? { ...n, reporta_a: avoId } : n);
    atualizar('organograma_nos', filtrado);
  };
  const toggleChip = (campo, id) => {
    const lista = obter(campo) || [];
    const novo = lista.includes(id) ? lista.filter(x => x !== id) : [...lista, id];
    atualizar(campo, novo);
  };
  const adicionarExtra = (campo, texto) => {
    const t = (texto || '').trim();
    if (!t) return;
    const lista = obter(campo) || [];
    if (lista.map(x => x.toLowerCase()).includes(t.toLowerCase())) return;
    atualizar(campo, [...lista, t]);
  };
  const removerExtra = (campo, idx) => {
    const lista = obter(campo) || [];
    atualizar(campo, lista.filter((_, i) => i !== idx));
  };

  // Pessoas-chave (lista editável) — schema novo: nome (cargo) · area · papel_chips · dependencia
  const adicionarPessoa = () => {
    atualizar('pessoas_chave', [...pessoas_chave, { nome: '', area: 'estrategia', papel_chips: [], dependencia: 'b', ...(modo === 'SU' ? { participacao_societaria: 0, dedicacao: 'integral' } : {}) }]);
  };
  const editarPessoa = (idx, campo, valor) => {
    const novo = pessoas_chave.map((p, i) => {
      if (i !== idx) return p;
      const atualizado = { ...p, [campo]: valor };
      // Quando muda o cargo (nome), recalcula a área via heurística e limpa chips
      if (campo === 'nome') {
        atualizado.area = inferirAreaDeCargo(valor);
        atualizado.papel_chips = [];
      }
      return atualizado;
    });
    atualizar('pessoas_chave', novo);
  };
  const togglePapelChip = (idx, chipId) => {
    const novo = pessoas_chave.map((p, i) => {
      if (i !== idx) return p;
      const chips = p.papel_chips || [];
      const ja = chips.includes(chipId);
      return { ...p, papel_chips: ja ? chips.filter(c => c !== chipId) : [...chips, chipId] };
    });
    atualizar('pessoas_chave', novo);
  };
  const removerPessoa = (idx) => {
    atualizar('pessoas_chave', pessoas_chave.filter((_, i) => i !== idx));
  };

  // Hires plano (lista por ano)
  const escolherContratacao = (ano, frase) => {
    // Mantém uma única linha por ano em hires_plano · sobrescreve a descrição · preserva rodada (SU)
    const existente = hires_plano.find(h => h.ano === ano);
    const semAno = hires_plano.filter(h => h.ano !== ano);
    atualizar('hires_plano', [...semAno, { ano, descricao: frase, ...(existente?.rodada ? { rodada: existente.rodada } : {}) }]);
  };
  // Rodada que financia a contratação (SU) · preserva a descrição existente
  const setRodadaHire = (ano, rodadaId) => {
    const existente = hires_plano.find(h => h.ano === ano);
    const semAno = hires_plano.filter(h => h.ano !== ano);
    atualizar('hires_plano', [...semAno, { ano, descricao: existente?.descricao || '', rodada: rodadaId }]);
  };
  const editarHire = (idx, campo, valor) => {
    const novo = hires_plano.map((h, i) => i === idx ? { ...h, [campo]: valor } : h);
    atualizar('hires_plano', novo);
  };
  const adicionarHire = () => {
    const proxAno = ANOS_PLANO_HIRES.find(a => !hires_plano.some(h => h.ano === a)) || 'Ano 1';
    atualizar('hires_plano', [...hires_plano, { ano: proxAno, descricao: '' }]);
  };
  const removerHire = (idx) => {
    atualizar('hires_plano', hires_plano.filter((_, i) => i !== idx));
  };

  const inputBase = {
    width: '100%', padding: '8px 12px', fontSize: 13,
    border: `1px solid ${G.borda}`, borderRadius: 0,
    fontFamily: "'Outfit', sans-serif", color: G.txt1,
    background: '#FFF', boxSizing: 'border-box',
  };
  const HEADER_BLOCO = {
    fontSize: 16, fontWeight: 700, letterSpacing: '0.10em',
    textTransform: 'uppercase', color: G.laranja, marginBottom: 10,
  };
  const HEADER_AUX = {
    fontSize: 12, fontWeight: 400, color: G.txt2,
    textTransform: 'none', letterSpacing: 0, marginLeft: 10,
  };
  const SUB_HEADER = {
    fontSize: 12, fontWeight: 700, color: G.txt2,
    textTransform: 'uppercase', letterSpacing: '0.06em',
    marginBottom: 6,
  };
  const botaoEditar = (cor) => ({
    background: '#FFF', color: cor,
    border: `1px solid ${cor}`,
    padding: '5px 12px', minWidth: 170, textAlign: 'center', fontSize: 11, fontWeight: 700,
    letterSpacing: '0.06em', textTransform: 'uppercase',
    borderRadius: 0, cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0,
  });

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
      {/* 2 quadros: Definição + Instruções */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <div style={{ padding: '12px 16px', background: G.verdeLight,
          borderLeft: `4px solid ${G.verde}`, borderRadius: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 800, color: G.verde,
            letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 6 }}>
            O que é Estrutura Org &amp; RH
          </div>
          <div style={{ fontSize: 12, lineHeight: 1.45, color: G.txt1 }}>
            Mostra <strong>como o time está organizado hoje</strong>: estrutura,
            pessoas-chave, tamanho do time e plano de contratações nos próximos
            5 anos · além da cultura e da política de remuneração.
          </div>
        </div>
        <div style={{ padding: '12px 16px', background: G.laranjaLight,
          borderLeft: `4px solid ${G.laranja}`, borderRadius: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 800, color: G.laranja,
            letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 6 }}>
            O que você faz aqui
          </div>
          <div style={{ fontSize: 12, lineHeight: 1.45, color: G.txt1 }}>
            Descreva o <strong>organograma</strong>, ajuste a <strong>lista de
            pessoas-chave</strong> com grau de dependência · revise o
            <strong> plano de pessoas dos próximos 5 anos</strong> e marque os
            elementos de <strong>cultura</strong>.
          </div>
        </div>
      </div>


      {/* BLOCO 1 — Estrutura do Time (organograma visual + matriz Cargo × Função) */}
      <div>
        <div style={HEADER_BLOCO}>
          Bloco 1 · Estrutura do Time
          <span style={HEADER_AUX}>· funções essenciais + cargos + organograma</span>
        </div>
        <div style={{ padding: '10px 14px', background: G.fundo,
          border: `1px solid ${G.borda}`, borderRadius: 0, marginBottom: 12,
          fontSize: 12, lineHeight: 1.45, color: G.txt1 }}>
          {modo === 'SU' ? (
            <>
              <MarcaAIdvisor /> sugeriu as <strong>funções essenciais</strong> de
              uma startup. Em <strong>fase inicial</strong>, é normal que
              <strong> poucas áreas estejam preenchidas</strong> e que os founders
              <strong> acumulem funções</strong> — cada cargo pode cobrir uma ou
              várias. O <strong>diagrama é recomposto</strong> automaticamente · em
              seguida escreva um comentário explicando a estrutura.
              <br/><strong>Toda função essencial precisa estar coberta por alguém</strong> (mesmo que um founder cubra várias).
            </>
          ) : (
            <>
              <MarcaAIdvisor /> cravou as <strong>funções essenciais</strong> que
              uma empresa do seu setor (<strong>{setor}</strong>) precisa cobrir —
              é padrão de mercado, não muda. Você ajusta a <strong>lista de
              cargos</strong> da empresa (cada cargo pode cobrir uma ou várias
              funções) · o <strong>diagrama é recomposto</strong> automaticamente ·
              e em seguida escreve um comentário explicando a estrutura.
              <br/><strong>Função descoberta = lacuna que impede aprovar.</strong>
            </>
          )}
        </div>

        {/* ── Cards por área (Header de Área · Cargo · Reporta a) ── */}
        <div style={{ ...SUB_HEADER, marginBottom: 8 }}>Áreas da empresa</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {catalogoFuncoes.map(funcao => {
            const cargosDaArea = organograma_nos.filter(n => (n.funcoes || []).includes(funcao.id));
            const sugestoes = fonteSugeridos[setor]?.[funcao.id]
              || fonteSugeridos['geral']?.[funcao.id] || [];
            const sugestoesNaoUsadas = sugestoes.filter(s =>
              !organograma_nos.some(n => n.cargo === s));
            const coberta = cargosDaArea.length > 0;
            const fechada = !!areasFechadas[funcao.id];
            return (
              <div key={funcao.id} style={{
                background: '#FFF',
                border: `1px solid ${coberta ? G.borda : G.vermelho}`,
                borderRadius: 0, overflow: 'hidden',
              }}>
                {/* Header do card · nome da área + botão abrir/fechar + contador */}
                <div style={{
                  padding: '8px 14px',
                  background: coberta ? G.verdeLight : '#FEF2F2',
                  color: coberta ? G.verde : G.vermelho,
                  borderBottom: fechada ? 'none' : `1px solid ${coberta ? G.verde : G.vermelho}`,
                  fontSize: 12, fontWeight: 800,
                  letterSpacing: '0.06em', textTransform: 'uppercase',
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10,
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <button type="button"
                      onClick={() => toggleAreaAberta(funcao.id)}
                      title={fechada ? 'Abrir' : 'Fechar'}
                      style={{
                        padding: '2px 8px', fontSize: 10, fontWeight: 700,
                        background: '#FFF', color: G.laranja,
                        border: `1px solid ${G.laranja}`, borderRadius: 0,
                        cursor: 'pointer', fontFamily: "'Outfit', sans-serif",
                        letterSpacing: '0.06em', textTransform: 'uppercase',
                        minWidth: 78, textAlign: 'center', flexShrink: 0,
                      }}>
                      {fechada ? '▾ abrir' : '▴ fechar'}
                    </button>
                    <span>{coberta ? '✓' : <IconeAlerta />} {funcao.label}</span>
                  </div>
                  {cargosDaArea.length > 0 && (
                    <span style={{ fontSize: 10, fontWeight: 600, letterSpacing: '0.04em' }}>
                      {cargosDaArea.length} {cargosDaArea.length === 1 ? 'cargo' : 'cargos'}
                    </span>
                  )}
                </div>
                {/* Body · cargos da área */}
                {!fechada && cargosDaArea.length > 0 && (
                  <div>
                    {/* Cabeçalho interno · 2 colunas */}
                    <div style={{
                      display: 'grid', gridTemplateColumns: '1.4fr 1.4fr 36px',
                      gap: 10, padding: '6px 14px',
                      background: G.fundo, fontSize: 10, fontWeight: 700, color: G.txt2,
                      letterSpacing: '0.06em', textTransform: 'uppercase',
                      borderBottom: `1px solid ${G.borda}`,
                    }}>
                      <div>Cargo</div>
                      <div>Reporta a</div>
                      <div/>
                    </div>
                    {cargosDaArea.map((n, idx) => {
                      const cargoMatch = catalogoCargos.find(c => c.label === n.cargo);
                      const valorSelect = n.cargo_livre ? 'outro' : (cargoMatch ? cargoMatch.id : 'outro');
                      return (
                        <div key={n.id} style={{
                          display: 'grid', gridTemplateColumns: '1.4fr 1.4fr 36px',
                          gap: 10, padding: '8px 14px', alignItems: 'center',
                          borderTop: idx === 0 ? 'none' : `1px solid ${G.borda}`,
                        }}>
                          {/* Cargo + headcount inline */}
                          <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                            {valorSelect === 'outro' ? (
                              <input value={n.cargo}
                                onChange={e => editarNo(n.id, 'cargo', e.target.value)}
                                placeholder="Nome do cargo"
                                style={{ ...inputBase, padding: '6px 10px', flex: 1 }}/>
                            ) : (
                              <select value={valorSelect}
                                onChange={e => selecionarCargoDoCatalogo(n.id, e.target.value)}
                                style={{ ...inputBase, padding: '6px 10px', flex: 1 }}>
                                {catalogoCargos.map(c => (
                                  <option key={c.id} value={c.id}>{c.label}</option>
                                ))}
                                <option value="outro">Outro…</option>
                              </select>
                            )}
                            <div style={{ width: 84, flexShrink: 0 }}>
                              <CampoNumericoBP60M
                                valor={n.headcount ?? 0}
                                onChange={(v) => editarNo(n.id, 'headcount', v ?? 0)}
                                label="Headcount · quantas pessoas"
                                permitirDecimal={false}
                                min={0}
                                formato="int"
                              />
                            </div>
                          </div>
                          {/* Reporta a */}
                          <select value={n.reporta_a || ''}
                            onChange={e => editarNo(n.id, 'reporta_a', e.target.value || null)}
                            style={{ ...inputBase, padding: '6px 10px' }}>
                            <option value="">— (topo)</option>
                            {organograma_nos
                              .filter(o => o.id !== n.id)
                              .map(o => (
                                <option key={o.id} value={o.id}>{o.cargo || '(sem nome)'}</option>
                              ))}
                          </select>
                          <button onClick={() => removerNo(n.id)}
                            title="Remover cargo"
                            style={{ background: 'transparent', border: 'none', color: G.vermelho,
                              cursor: 'pointer', fontSize: 16, padding: 0 }}>
                            ✕
                          </button>
                        </div>
                      );
                    })}
                  </div>
                )}
                {/* Chips de sugestão da IA · sempre na base do card */}
                {!fechada && sugestoesNaoUsadas.length > 0 && (
                  <div style={{ padding: '8px 14px', borderTop: cargosDaArea.length > 0 ? `1px solid ${G.borda}` : 'none',
                    display: 'flex', flexWrap: 'wrap', gap: 4, alignItems: 'center' }}>
                    <span style={{ fontSize: 10, fontWeight: 700, color: G.txt2,
                      letterSpacing: '0.06em', textTransform: 'uppercase', marginRight: 4 }}>
                      Sugestões IA:
                    </span>
                    {sugestoesNaoUsadas.map(nome => (
                      <button key={nome} type="button"
                        onClick={() => adicionarCargoNaArea(funcao.id, nome)}
                        title={`Adicionar ${nome} nessa área`}
                        style={{
                          padding: '4px 9px', fontSize: 11, fontWeight: 600,
                          background: '#FFF', color: G.laranja,
                          border: `1px dashed ${G.laranja}`, borderRadius: 0,
                          cursor: 'pointer', fontFamily: "'Outfit', sans-serif",
                        }}>
                        + {nome}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
        {/* Alerta de cobertura · função sem cargo bloqueia aprovar */}
        {catalogoFuncoes.some(f => !funcoesCobertas.has(f.id)) && (
          <div style={{ marginTop: 8, marginBottom: 14, fontSize: 11, color: G.vermelho,
            fontStyle: 'italic', fontWeight: 700 }}>
            <IconeAlerta /> Existem áreas sem cargo cobrindo — aprovação bloqueada até cada área
            ter pelo menos 1 cargo responsável.
          </div>
        )}

        {/* ── Comentário sobre o organograma · frase composta + Editar livremente ─── */}
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginTop: 8 }}>
          <div style={{ ...SUB_HEADER, marginBottom: 0, paddingTop: 8, flexShrink: 0, width: 180 }}>
            Comentário sobre o organograma
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            {organograma_editou_livre ? (
              <textarea value={organograma || ''}
                onChange={e => atualizar('organograma', e.target.value)}
                rows={3} style={{ ...inputBase, lineHeight: 1.45, resize: 'vertical',
                  background: G.laranjaLight, borderColor: G.laranja }}/>
            ) : (
              <div style={{ padding: '10px 14px', background: G.verdeLight,
                borderLeft: `3px solid ${G.verde}`, borderRadius: 0,
                fontSize: 13, lineHeight: 1.5, color: G.txt1, fontStyle: 'italic' }}>
                {organograma || '_Pesquisa da IA não retornou comentário sobre o organograma._'}
              </div>
            )}
          </div>
          <div style={{ flexShrink: 0 }}>
            {organograma_editou_livre ? (
              <button onClick={() => { atualizar('organograma_editou_livre', false); atualizar('organograma', rascunho.organograma || ''); }}
                style={{ background: '#FFF', color: G.verde, border: `1px solid ${G.verde}`,
                  padding: '5px 12px', minWidth: 170, textAlign: 'center', fontSize: 11, fontWeight: 700,
                  letterSpacing: '0.06em', textTransform: 'uppercase',
                  borderRadius: 0, cursor: 'pointer', whiteSpace: 'nowrap' }}>
                <IconeReverter /> Voltar à <MarcaAIdvisor variant="default" />
              </button>
            ) : (
              <button onClick={() => atualizar('organograma_editou_livre', true)}
                style={{ background: '#FFF', color: G.laranja, border: `1px solid ${G.laranja}`,
                  padding: '5px 12px', minWidth: 170, textAlign: 'center', fontSize: 11, fontWeight: 700,
                  letterSpacing: '0.06em', textTransform: 'uppercase',
                  borderRadius: 0, cursor: 'pointer', whiteSpace: 'nowrap' }}>
                <IconeLapis /> Editar livremente
              </button>
            )}
          </div>
        </div>

        {/* Botão de visualização do organograma (default fechado) */}
        <BotaoVisualizarGraficos
          aberto={graficosBloco1Aberto}
          onToggle={() => setGraficosBloco1Aberto(v => !v)}
          rotulo="Visualizar organograma">
          <div>
            <div style={{ fontSize: 10, fontWeight: 800, color: G.txt2,
              letterSpacing: '0.10em', textTransform: 'uppercase', marginBottom: 8 }}>
              Ilustração · {TITULO_EXHIBIT_POR_TIPO['t10_organograma']}
            </div>
            <GraficoOrganograma nos={organograma_nos}/>
          </div>
        </BotaoVisualizarGraficos>
      </div>

      {/* BLOCO 2 — Pessoas-Chave */}
      <div>
        <div style={HEADER_BLOCO}>
          Bloco 2 · Pessoas-Chave
          <span style={HEADER_AUX}>· grau de dependência mede o impacto da saída</span>
        </div>
        <div style={{ padding: '10px 14px', background: G.fundo,
          border: `1px solid ${G.borda}`, borderRadius: 0, marginBottom: 12,
          fontSize: 12, lineHeight: 1.45, color: G.txt1 }}>
          <MarcaAIdvisor /> pré-listou as pessoas-chave típicas do seu perfil.
          Ajuste a lista: <strong>edite, adicione ou remova</strong>. O
          <strong> grau de dependência</strong> mede o impacto na operação caso
          essa pessoa saísse — vai de <strong>Baixa</strong> a <strong>Crítica</strong>.
          <strong> Dependência Alta ou Crítica é sinal de risco-chave</strong> que pesa no BP.
        </div>
        <table style={{
          width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed',
          background: '#FFF', border: `1px solid ${G.borda}`, borderRadius: 0,
          fontFamily: "'Outfit', sans-serif",
        }}>
          <colgroup>
            <col style={{ width: '26%' }}/>
            <col style={{ width: '42%' }}/>
            <col style={{ width: '200px' }}/>
            <col style={{ width: '40px' }}/>
          </colgroup>
          <thead>
            <tr style={{ background: G.fundo, borderBottom: `1px solid ${G.borda}` }}>
              <th style={{ padding: '8px 14px', textAlign: 'left',
                fontSize: 10, fontWeight: 700, color: G.txt2,
                letterSpacing: '0.06em', textTransform: 'uppercase',
                borderRight: `1px solid ${G.borda}` }}>Cargo</th>
              <th style={{ padding: '8px 14px', textAlign: 'left',
                fontSize: 10, fontWeight: 700, color: G.txt2,
                letterSpacing: '0.06em', textTransform: 'uppercase',
                borderRight: `1px solid ${G.borda}` }}>Papel</th>
              <th style={{ padding: '8px 14px', textAlign: 'left',
                fontSize: 10, fontWeight: 700, color: G.txt2,
                letterSpacing: '0.06em', textTransform: 'uppercase',
                borderRight: `1px solid ${G.borda}` }}>Dependência</th>
              <th style={{ padding: '8px 14px' }}/>
            </tr>
          </thead>
          <tbody>
            {pessoas_chave.map((p, idx) => {
              const alerta = p.dependencia === 'c' || p.dependencia === 'd';
              const aberto = papelAbertoIdx === idx;
              const areaAtual = CHIPS_PAPEL_POR_AREA[p.area] || CHIPS_PAPEL_POR_AREA['estrategia'];
              const fraseMontada = montarFrasePapel(p.area, p.papel_chips);
              const bg = alerta ? '#FFFBEB' : '#FFF';
              const tdBase = {
                padding: '8px 14px', verticalAlign: 'top',
                borderTop: `1px solid ${G.borda}`,
                borderRight: `1px solid ${G.borda}`,
                background: bg,
              };
              return (
                <React.Fragment key={idx}>
                  <tr>
                    {/* Coluna 1 · Cargo */}
                    <td style={tdBase}>
                      <select value={p.nome}
                        onChange={e => editarPessoa(idx, 'nome', e.target.value)}
                        style={{ ...inputBase, padding: '6px 10px' }}>
                        {!organograma_nos.some(c => c.cargo === p.nome) && p.nome && (
                          <option value={p.nome}>{p.nome}</option>
                        )}
                        {organograma_nos.map(c => (
                          <option key={c.id} value={c.cargo}>{c.cargo}</option>
                        ))}
                      </select>
                    </td>
                    {/* Coluna 2 · Papel — botão-dropdown · clicar de novo fecha */}
                    <td style={tdBase}>
                      <button type="button"
                        ref={(el) => { papelBotaoRefs.current[idx] = el; }}
                        onClick={() => setPapelAbertoIdx(aberto ? null : idx)}
                        style={{
                          ...inputBase, padding: '6px 10px',
                          textAlign: 'left', cursor: 'pointer',
                          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                          gap: 8,
                          borderColor: aberto ? G.laranja : G.borda,
                          color: fraseMontada ? G.txt1 : '#9CA3AF',
                        }}>
                        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {fraseMontada || `Marcar elementos do papel (${areaAtual.label})`}
                        </span>
                        <span style={{ fontSize: 13, color: G.txt2, flexShrink: 0 }}>⌄</span>
                      </button>
                    </td>
                    {/* Coluna 3 · Dependência */}
                    <td style={tdBase}>
                      <select value={p.dependencia || 'b'}
                        onChange={e => editarPessoa(idx, 'dependencia', e.target.value)}
                        style={{ ...inputBase, padding: '6px 10px',
                          color: alerta ? '#B45309' : G.txt1,
                          fontWeight: alerta ? 700 : 400 }}>
                        {OPCOES_DEPENDENCIA.map(o => (
                          <option key={o.id} value={o.id}>{o.label}</option>
                        ))}
                      </select>
                    </td>
                    {/* Coluna 4 · Remover */}
                    <td style={{ padding: '8px 6px', verticalAlign: 'top',
                      borderTop: `1px solid ${G.borda}`, textAlign: 'center', background: bg }}>
                      <button onClick={() => removerPessoa(idx)}
                        title="Remover"
                        style={{ background: 'transparent', border: 'none', color: G.vermelho,
                          cursor: 'pointer', fontSize: 16, padding: 0, lineHeight: '30px' }}>
                        ✕
                      </button>
                    </td>
                  </tr>
                  {/* Linha SU · Participação societária + Dedicação (colSpan não mexe nas colunas) */}
                  {modo === 'SU' && (
                    <tr>
                      <td colSpan={4} style={{ padding: '2px 14px 10px', background: bg,
                        borderRight: `1px solid ${G.borda}` }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 18, flexWrap: 'wrap' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <span style={{ fontSize: 10, fontWeight: 700, color: G.txt2,
                              letterSpacing: '0.06em', textTransform: 'uppercase' }}>Participação societária</span>
                            <div style={{ width: 110 }}>
                              <CampoNumericoBP60M
                                valor={p.participacao_societaria ?? 0}
                                onChange={(v) => editarPessoa(idx, 'participacao_societaria', v ?? 0)}
                                label="Participação societária"
                                permitirDecimal={true} min={0} max={100} sufixo="%" />
                            </div>
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <span style={{ fontSize: 10, fontWeight: 700, color: G.txt2,
                              letterSpacing: '0.06em', textTransform: 'uppercase' }}>Dedicação</span>
                            <select value={p.dedicacao || 'integral'}
                              onChange={e => editarPessoa(idx, 'dedicacao', e.target.value)}
                              style={{ ...inputBase, padding: '6px 10px', width: 160 }}>
                              {DEDICACAO_OPCOES_SU.map(o => <option key={o.id} value={o.id}>{o.label}</option>)}
                            </select>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                  {/* Linha expansível · painel SÓ na coluna 2 */}
                  {aberto && (
                    <tr>
                      <td style={{ borderRight: `1px solid ${G.borda}`, background: bg }}/>
                      <td ref={papelDropdownRef} style={{
                        padding: '8px 14px 12px',
                        borderRight: `1px solid ${G.borda}`,
                        background: bg, verticalAlign: 'top',
                      }}>
                        <div style={{ fontSize: 10, fontWeight: 700, color: G.txt2,
                          letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 6 }}>
                          Elementos do papel · {areaAtual.label}
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                          {areaAtual.chips.map(chip => {
                            const marcado = (p.papel_chips || []).includes(chip.id);
                            return (
                              <button key={chip.id}
                                type="button"
                                onClick={() => togglePapelChip(idx, chip.id)}
                                style={{
                                  display: 'flex', alignItems: 'center', gap: 8,
                                  width: '100%', textAlign: 'left',
                                  padding: '6px 10px', fontSize: 13,
                                  border: `1px solid ${marcado ? G.laranja : G.borda}`,
                                  background: marcado ? G.laranjaLight : '#FFF',
                                  color: marcado ? '#B45309' : G.txt1,
                                  fontWeight: marcado ? 700 : 400,
                                  borderRadius: 0, cursor: 'pointer',
                                  fontFamily: "'Outfit', sans-serif",
                                }}>
                                <span style={{
                                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                                  width: 16, height: 16, flexShrink: 0,
                                  border: `1px solid ${marcado ? G.laranja : G.borda}`,
                                  background: marcado ? G.laranja : '#FFF',
                                  color: '#FFF', borderRadius: 0, fontSize: 11, fontWeight: 800,
                                }}>
                                  {marcado ? '✓' : ''}
                                </span>
                                {chip.label}
                              </button>
                            );
                          })}
                        </div>
                      </td>
                      <td style={{ borderRight: `1px solid ${G.borda}`, background: bg }}/>
                      <td style={{ background: bg }}/>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          marginTop: 8, gap: 10 }}>
          <button onClick={adicionarPessoa}
            style={{ background: G.laranja, color: '#FFF', border: 'none',
              padding: '6px 14px', fontSize: 11, fontWeight: 700,
              letterSpacing: '0.06em', textTransform: 'uppercase',
              borderRadius: 0, cursor: 'pointer' }}>
            + Adicionar pessoa-chave
          </button>
          {pessoas_chave.some(p => p.dependencia === 'c' || p.dependencia === 'd') && (
            <span style={{ fontSize: 11, color: '#B45309', fontStyle: 'italic' }}>
              <IconeAlerta /> Existem pessoas-chave em patamar Alta ou Crítica — risco-chave a mitigar.
            </span>
          )}
        </div>
      </div>

      {/* BLOCO 3 — Plano de Pessoas 5 anos (read-only do time hoje · accordions por ano) */}
      <div>
        <div style={HEADER_BLOCO}>
          Bloco 3 · Plano de Pessoas 5 anos
          <span style={HEADER_AUX}>· tamanho do time hoje (calculado) + contratações estratégicas por ano</span>
        </div>
        <div style={{ padding: '10px 14px', background: G.fundo,
          border: `1px solid ${G.borda}`, borderRadius: 0, marginBottom: 12,
          fontSize: 12, lineHeight: 1.45, color: G.txt1 }}>
          O <strong>tamanho do time hoje</strong> é calculado automaticamente a
          partir do organograma (Bloco 1). Para cada ano do plano, a <MarcaAIdvisor />
          {' '}propõe alternativas de contratação contextuais ao seu setor — clique
          em cada ano para escolher.
        </div>

        {/* Card read-only do tamanho do time hoje (calculado do organograma) */}
        {(() => {
          const totalHc = organograma_nos.reduce((s, n) => s + (n.headcount || 0), 0);
          const porArea = {};
          organograma_nos.forEach(n => {
            const a = inferirAreaDeCargo(n.cargo);
            porArea[a] = (porArea[a] || 0) + (n.headcount || 0);
          });
          const ordemAreas = ['estrategia', 'comercial', 'marketing', 'operacoes', 'financeiro', 'rh', 'ti'];
          return (
            <div style={{ background: '#FFF', border: `1px solid ${G.borda}`,
              borderRadius: 0, padding: '12px 14px', marginBottom: 14 }}>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 14,
                marginBottom: totalHc > 0 ? 10 : 0 }}>
                <div style={{ fontSize: 10, fontWeight: 800, color: G.txt2,
                  letterSpacing: '0.10em', textTransform: 'uppercase' }}>
                  Tamanho do time hoje
                </div>
                <div style={{ fontSize: 22, fontWeight: 900, color: G.verde }}>
                  {totalHc} {totalHc === 1 ? 'pessoa' : 'pessoas'}
                </div>
              </div>
              {totalHc > 0 && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {ordemAreas.filter(a => porArea[a] > 0).map(a => (
                    <span key={a} style={{
                      padding: '4px 10px', fontSize: 11, fontWeight: 700,
                      background: G.verdeLight, color: G.verde,
                      border: `1px solid ${G.verde}`, borderRadius: 999,
                    }}>
                      {CHIPS_PAPEL_POR_AREA[a]?.label || a}: {porArea[a]}
                    </span>
                  ))}
                </div>
              )}
              {totalHc === 0 && (
                <div style={{ fontSize: 12, color: G.txt2, fontStyle: 'italic' }}>
                  Preencha o organograma no Bloco 1 para o tamanho do time aparecer aqui.
                </div>
              )}
            </div>
          );
        })()}

        <div style={SUB_HEADER}>Plano de contratações estratégicas</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {ANOS_PLANO_HIRES.map((ano, idx) => {
            const hireDoAno = hires_plano.find(h => h.ano === ano);
            const respondida = !!hireDoAno?.descricao;
            const aberta = anoHireAberto === ano;
            const setorEmp = empresa?.setor || 'geral';
            const opcoes = (CONTRATACOES_SUGERIDAS_POR_SETOR_E_ANO[setorEmp]
              || CONTRATACOES_SUGERIDAS_POR_SETOR_E_ANO['geral'])[ano] || [];
            return (
              <div key={ano} style={{
                background: '#FFF',
                border: `1px solid ${aberta ? G.verde : G.borda}`,
                borderRadius: 0, overflow: 'hidden',
              }}>
                <div style={{
                  background: aberta ? G.verdeLight : 'transparent',
                  padding: '8px 14px',
                  display: 'flex', alignItems: 'center', gap: 12,
                }}>
                  <div style={{
                    flexShrink: 0, width: 22, height: 22, borderRadius: '50%',
                    background: respondida ? G.verde : G.borda,
                    color: '#FFF', fontSize: 11, fontWeight: 800,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    {respondida ? '✓' : (idx + 1)}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: G.txt1,
                      lineHeight: 1.3 }}>
                      {ano} · qual contratação estratégica?
                    </div>
                    {respondida && !aberta && (
                      <div style={{ fontSize: 12, color: G.verde, fontWeight: 600,
                        marginTop: 2 }}>
                        {hireDoAno.descricao}
                      </div>
                    )}
                  </div>
                  <button onClick={() => setAnoHireAberto(aberta ? null : ano)}
                    style={{
                      flexShrink: 0,
                      background: aberta ? '#FFF' : G.laranjaLight,
                      border: `1px solid ${G.laranja}`,
                      color: G.laranja,
                      padding: '4px 9px', fontSize: 9, fontWeight: 800,
                      letterSpacing: '0.08em', textTransform: 'uppercase',
                      borderRadius: 0, cursor: 'pointer',
                      fontFamily: "'Outfit', sans-serif",
                      whiteSpace: 'nowrap',
                    }}>
                    {aberta ? '▴ Clique para fechar' : '▾ Clique para abrir'}
                  </button>
                </div>
                {aberta && (
                  <div style={{ padding: '4px 14px 12px',
                    display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {opcoes.map((frase, fi) => {
                      const ativo = hireDoAno?.descricao === frase;
                      return (
                        <label key={fi}
                          style={{ display: 'flex', alignItems: 'flex-start', gap: 10,
                            padding: '8px 12px', cursor: 'pointer',
                            background: ativo ? G.verdeLight : '#FFF',
                            border: ativo ? `2px solid ${G.verde}` : `1px solid ${G.borda}`,
                            borderRadius: 0,
                            fontFamily: "'Outfit', sans-serif",
                          }}>
                          <input type="radio"
                            name={`contratacao_${ano}`}
                            checked={ativo}
                            onChange={() => escolherContratacao(ano, frase)}
                            style={{
                              appearance: 'none', WebkitAppearance: 'none',
                              width: 14, height: 14, marginTop: 3, flexShrink: 0,
                              border: `2px solid ${ativo ? G.verde : G.borda}`,
                              borderRadius: 0,
                              background: ativo ? G.verde : '#FFF',
                              cursor: 'pointer',
                            }}/>
                          <span style={{ fontSize: 13,
                            color: ativo ? G.verde : G.txt1,
                            fontWeight: ativo ? 700 : 500,
                            lineHeight: 1.4 }}>
                            {frase}
                          </span>
                        </label>
                      );
                    })}
                    {modo === 'SU' && (
                      <div style={{ marginTop: 4, display: 'flex', alignItems: 'center', gap: 8,
                        paddingTop: 8, borderTop: `1px dashed ${G.borda}` }}>
                        <span style={{ fontSize: 10, fontWeight: 700, color: G.txt2,
                          letterSpacing: '0.06em', textTransform: 'uppercase' }}>Rodada que financia</span>
                        <select value={hireDoAno?.rodada || ''}
                          onChange={e => setRodadaHire(ano, e.target.value)}
                          style={{ ...inputBase, padding: '6px 10px', width: 180 }}>
                          <option value="">—</option>
                          {RODADAS_HIRE_SU.map(o => <option key={o.id} value={o.id}>{o.label}</option>)}
                        </select>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Botão de visualização da linha do tempo (default fechado) */}
        <BotaoVisualizarGraficos
          aberto={graficosBloco3Aberto}
          onToggle={() => setGraficosBloco3Aberto(v => !v)}
          rotulo="Visualizar linha do tempo de contratações">
          <div>
            <div style={{ fontSize: 10, fontWeight: 800, color: G.txt2,
              letterSpacing: '0.10em', textTransform: 'uppercase', marginBottom: 8 }}>
              Ilustração · {TITULO_EXHIBIT_POR_TIPO['t10_timeline_rh']}
            </div>
            <GraficoTimelineRH hires={hires_plano}/>
          </div>
        </BotaoVisualizarGraficos>
      </div>

      {/* BLOCO 4 — Cultura e Retenção */}
      <div>
        <div style={HEADER_BLOCO}>
          Bloco 4 · Cultura e Retenção
          <span style={HEADER_AUX}>· práticas de cultura + política de remuneração</span>
        </div>
        <div style={{ padding: '10px 14px', background: G.fundo,
          border: `1px solid ${G.borda}`, borderRadius: 0, marginBottom: 12,
          fontSize: 12, lineHeight: 1.45, color: G.txt1 }}>
          <MarcaAIdvisor /> selecionou práticas de cultura típicas do seu
          setor (<strong>{setor}</strong>) e pré-marcou as mais coerentes
          com o perfil do projeto. Marque/desmarque · adicione próprias ·
          descreva a política de remuneração no campo livre.
        </div>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 8 }}>
          <div style={{ ...SUB_HEADER, marginBottom: 0, paddingTop: 8, flexShrink: 0, width: 180 }}>
            Cultura organizacional
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            {cultura_editou_livre ? (
              <textarea value={cultura_livre || ''}
                onChange={e => atualizar('cultura_livre', e.target.value)}
                rows={3} style={{ ...inputBase, lineHeight: 1.45, resize: 'vertical',
                  background: G.laranjaLight, borderColor: G.laranja }}/>
            ) : (
              <div style={{ padding: '10px 14px', background: G.verdeLight,
                borderLeft: `3px solid ${G.verde}`, borderRadius: 0,
                fontSize: 13, lineHeight: 1.5, color: G.txt1, fontStyle: 'italic' }}>
                {fraseCultura || '_Selecione pelo menos uma prática para compor a frase._'}
              </div>
            )}
          </div>
          <div>
            {cultura_editou_livre ? (
              <button onClick={() => { atualizar('cultura_editou_livre', false); atualizar('cultura_livre', ''); }}
                style={botaoEditar(G.verde)}>
                <IconeReverter /> Voltar à <MarcaAIdvisor variant="default" />
              </button>
            ) : (
              <button onClick={() => { atualizar('cultura_livre', fraseCultura); atualizar('cultura_editou_livre', true); }}
                style={botaoEditar(G.laranja)}>
                <IconeLapis /> Editar livremente
              </button>
            )}
          </div>
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 8 }}>
          {catalogoCultura.map(c => {
            const sel = cultura_chars.includes(c.id);
            return (
              <button key={c.id} onClick={() => toggleChip('cultura_chars', c.id)}
                style={{
                  padding: '5px 11px', fontSize: 12, fontWeight: 600,
                  background: sel ? G.verde : '#FFF',
                  color: sel ? '#FFF' : G.txt1,
                  border: `1px solid ${sel ? G.verde : G.borda}`,
                  borderRadius: 999, cursor: 'pointer',
                  fontFamily: "'Outfit', sans-serif",
                }}>
                {sel ? '✓ ' : ''}{c.chip}
              </button>
            );
          })}
          {cultura_extras.map((extra, idx) => (
            <button key={`extra-${idx}`} onClick={() => removerExtra('cultura_extras', idx)}
              style={{
                padding: '5px 11px', fontSize: 12, fontWeight: 600,
                background: G.laranja, color: '#FFF',
                border: `1px solid ${G.laranja}`,
                borderRadius: 999, cursor: 'pointer',
                fontFamily: "'Outfit', sans-serif",
              }}>
              ✓ {extra} ✕
            </button>
          ))}
        </div>
        <div style={{ display: 'flex', gap: 6, marginBottom: 14 }}>
          <input type="text" value={novoChipCultura}
            onChange={e => setNovoChipCultura(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter') {
                adicionarExtra('cultura_extras', novoChipCultura);
                setNovoChipCultura('');
              }
            }}
            placeholder="Adicionar prática própria…"
            style={{ ...inputBase, fontSize: 12, padding: '6px 10px' }}/>
          <button onClick={() => { adicionarExtra('cultura_extras', novoChipCultura); setNovoChipCultura(''); }}
            style={{ background: G.laranja, color: '#FFF', border: 'none',
              padding: '6px 14px', fontSize: 11, fontWeight: 700,
              letterSpacing: '0.06em', textTransform: 'uppercase',
              borderRadius: 0, cursor: 'pointer', whiteSpace: 'nowrap' }}>
            + Adicionar
          </button>
        </div>
        <div>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
            <div style={{ ...SUB_HEADER, marginBottom: 0, paddingTop: 8, flexShrink: 0, width: 180 }}>
              Política de remuneração
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              {remuneracao_editou_livre ? (
                <textarea value={remuneracao || ''}
                  onChange={e => atualizar('remuneracao', e.target.value)}
                  rows={3} style={{ ...inputBase, lineHeight: 1.45, resize: 'vertical',
                    background: G.laranjaLight, borderColor: G.laranja }}/>
              ) : (
                <div style={{ padding: '10px 14px', background: G.verdeLight,
                  borderLeft: `3px solid ${G.verde}`, borderRadius: 0,
                  fontSize: 13, lineHeight: 1.5, color: G.txt1, fontStyle: 'italic' }}>
                  {remuneracao || '_Pesquisa da IA não retornou política de remuneração._'}
                </div>
              )}
            </div>
            <div style={{ flexShrink: 0 }}>
              {remuneracao_editou_livre ? (
                <button onClick={() => { atualizar('remuneracao_editou_livre', false); atualizar('remuneracao', rascunho.remuneracao || ''); }}
                  style={{ background: '#FFF', color: G.verde, border: `1px solid ${G.verde}`,
                    padding: '5px 12px', minWidth: 170, textAlign: 'center', fontSize: 11, fontWeight: 700,
                    letterSpacing: '0.06em', textTransform: 'uppercase',
                    borderRadius: 0, cursor: 'pointer', whiteSpace: 'nowrap' }}>
                  <IconeReverter /> Voltar à <MarcaAIdvisor variant="default" />
                </button>
              ) : (
                <button onClick={() => atualizar('remuneracao_editou_livre', true)}
                  style={{ background: '#FFF', color: G.laranja, border: `1px solid ${G.laranja}`,
                    padding: '5px 12px', minWidth: 170, textAlign: 'center', fontSize: 11, fontWeight: 700,
                    letterSpacing: '0.06em', textTransform: 'uppercase',
                    borderRadius: 0, cursor: 'pointer', whiteSpace: 'nowrap' }}>
                  <IconeLapis /> Editar livremente
                </button>
              )}
            </div>
          </div>
        </div>
        {/* Sub-bloco Retenção (SU) */}
        {modo === 'SU' && (
          <div style={{ marginTop: 14 }}>
            <div style={SUB_HEADER}>Retenção</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 18, alignItems: 'flex-start' }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <span style={{ fontSize: 10, fontWeight: 700, color: G.txt2,
                  letterSpacing: '0.06em', textTransform: 'uppercase' }}>Participação societária</span>
                <select value={retencao_equity || 'nao'} onChange={e => atualizar('retencao_equity', e.target.value)}
                  style={{ ...inputBase, padding: '6px 10px', width: 120 }}>
                  <option value="sim">Sim</option>
                  <option value="nao">Não</option>
                </select>
              </div>
              {retencao_equity === 'sim' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4, flex: 1, minWidth: 200 }}>
                  <span style={{ fontSize: 10, fontWeight: 700, color: G.txt2,
                    letterSpacing: '0.06em', textTransform: 'uppercase' }}>Vesting padrão</span>
                  <input type="text" value={retencao_vesting || ''}
                    onChange={e => atualizar('retencao_vesting', e.target.value)}
                    placeholder="ex.: 4 anos · cliff de 1 ano"
                    style={{ ...inputBase, padding: '6px 10px' }}/>
                </div>
              )}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <span style={{ fontSize: 10, fontWeight: 700, color: G.txt2,
                  letterSpacing: '0.06em', textTransform: 'uppercase' }}>Bonificação por desempenho</span>
                <select value={retencao_bonus || 'nao'} onChange={e => atualizar('retencao_bonus', e.target.value)}
                  style={{ ...inputBase, padding: '6px 10px', width: 120 }}>
                  <option value="sim">Sim</option>
                  <option value="nao">Não</option>
                </select>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <span style={{ fontSize: 10, fontWeight: 700, color: G.txt2,
                  letterSpacing: '0.06em', textTransform: 'uppercase' }}>Pacote de bem-estar</span>
                <select value={retencao_bemestar || 'nao'} onChange={e => atualizar('retencao_bemestar', e.target.value)}
                  style={{ ...inputBase, padding: '6px 10px', width: 120 }}>
                  <option value="sim">Sim</option>
                  <option value="nao">Não</option>
                </select>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const ConteudoTarefa11 = ({ riscos, setRiscos, empresa, modo = 'EE' }) => {
  const setorEmp = empresa?.setor || 'geral';
  const fonteRiscos = modo === 'SU' ? RISCOS_CATALOGO_POR_SETOR_SU : RISCOS_CATALOGO_POR_SETOR;
  const catalogoRiscos = fonteRiscos[setorEmp] || fonteRiscos['geral'];
  const rascunho = (modo === 'SU' ? RASCUNHO_TAREFA_11_SU : RASCUNHO_TAREFA_11)[empresa?.id] || { riscos: [], mitigacao_extra: '' };
  const listaRiscos = riscos?.riscos ?? rascunho.riscos;
  const mitigacaoExtra = riscos?.mitigacao_extra ?? rascunho.mitigacao_extra ?? '';

  const atualizar = (campo, valor) => {
    setRiscos(prev => ({
      riscos: listaRiscos, mitigacao_extra: mitigacaoExtra,
      ...prev,
      [campo]: valor,
    }));
  };
  const chipRiscoMarcado = (catId) => listaRiscos.some(r => r.id_catalogo === catId);
  const toggleChipRisco = (categoria, catItem) => {
    if (chipRiscoMarcado(catItem.id)) {
      atualizar('riscos', listaRiscos.filter(r => r.id_catalogo !== catItem.id));
    } else {
      atualizar('riscos', [...listaRiscos, {
        id_catalogo: catItem.id, categoria,
        probabilidade: 'b', impacto: 'b',
      }]);
    }
  };
  const editarRisco = (catId, campo, valor) => {
    atualizar('riscos', listaRiscos.map(r => r.id_catalogo === catId ? { ...r, [campo]: valor } : r));
  };
  const editarRiscoIdx = (idx, campo, valor) => {
    atualizar('riscos', listaRiscos.map((r, i) => i === idx ? { ...r, [campo]: valor } : r));
  };
  const removerRiscoIdx = (idx) => {
    atualizar('riscos', listaRiscos.filter((_, i) => i !== idx));
  };
  const adicionarRiscoProprio = (categoria) => {
    atualizar('riscos', [...listaRiscos, {
      id_catalogo: null, categoria,
      titulo: '', descricao: '', mitigacao: '',
      probabilidade: 'b', impacto: 'b',
    }]);
  };

  const inputBase = {
    width: '100%', padding: '8px 12px', fontSize: 13,
    border: `1px solid ${G.borda}`, borderRadius: 0,
    fontFamily: "'Outfit', sans-serif", color: G.txt1,
    background: '#FFF', boxSizing: 'border-box',
  };
  const HEADER_BLOCO = {
    fontSize: 16, fontWeight: 700, letterSpacing: '0.10em',
    textTransform: 'uppercase', color: G.laranja, marginBottom: 8,
  };
  const HEADER_AUX = {
    fontSize: 12, fontWeight: 400, color: G.txt2,
    textTransform: 'none', letterSpacing: 0, marginLeft: 10,
  };

  const BlocoCategoria = ({ categoria, titulo, sub }) => {
    const opcoesCat = catalogoRiscos[categoria] || [];
    const riscosCat = listaRiscos.filter(r => r.categoria === categoria);
    return (
      <div>
        <div style={HEADER_BLOCO}>
          {titulo}
          <span style={HEADER_AUX}>· {sub}</span>
        </div>
        <div style={{ background: G.fundo, border: `1px solid ${G.borda}`,
          borderRadius: 0, padding: '10px 14px', marginBottom: 10 }}>
          <div style={{ fontSize: 10, fontWeight: 800, color: G.txt2,
            letterSpacing: '0.10em', textTransform: 'uppercase', marginBottom: 8 }}>
            Sugestões da IA · marque os riscos que se aplicam
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {opcoesCat.map(c => {
              const ativo = chipRiscoMarcado(c.id);
              return (
                <button key={c.id} type="button"
                  onClick={() => toggleChipRisco(categoria, c)}
                  title={c.descricao}
                  style={{
                    padding: '5px 11px', fontSize: 12, fontWeight: 600,
                    background: ativo ? G.verde : '#FFF',
                    color: ativo ? '#FFF' : G.txt1,
                    border: `1px solid ${ativo ? G.verde : G.borda}`,
                    borderRadius: 999, cursor: 'pointer',
                    fontFamily: "'Outfit', sans-serif",
                  }}>
                  {ativo ? '✓ ' : ''}{c.titulo}
                </button>
              );
            })}
          </div>
        </div>
        {riscosCat.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {riscosCat.map((r, idxCat) => {
              const idxGlobal = listaRiscos.indexOf(r);
              const doCatalogo = !!r.id_catalogo;
              const cat = doCatalogo ? (opcoesCat.find(c => c.id === r.id_catalogo) || {}) : null;
              const tituloMostrado = doCatalogo ? cat.titulo : r.titulo;
              const descMostrada = doCatalogo ? cat.descricao : r.descricao;
              const mitMostrada = doCatalogo ? cat.mitigacao : r.mitigacao;
              const probOpcao = ESCALA_PROB_IMPACTO.find(o => o.id === r.probabilidade);
              const impOpcao  = ESCALA_PROB_IMPACTO.find(o => o.id === r.impacto);
              return (
                <div key={`${r.id_catalogo || 'proprio'}_${idxCat}`} style={{
                  background: '#FFF', border: `1px solid ${doCatalogo ? G.borda : G.laranja}`,
                  borderRadius: 0, padding: '10px 14px',
                }}>
                  {doCatalogo ? (
                    <>
                      <div style={{ fontSize: 13, fontWeight: 700, color: G.txt1, marginBottom: 4 }}>
                        {tituloMostrado}
                      </div>
                      <div style={{ fontSize: 12, color: G.txt2, fontStyle: 'italic',
                        lineHeight: 1.4, marginBottom: 8 }}>
                        {descMostrada}
                      </div>
                    </>
                  ) : (
                    <div style={{ marginBottom: 8 }}>
                      <div style={{ fontSize: 10, fontWeight: 700, color: G.laranja,
                        letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 6 }}>
                        Risco próprio
                      </div>
                      <input value={tituloMostrado || ''}
                        onChange={e => editarRiscoIdx(idxGlobal, 'titulo', e.target.value)}
                        placeholder="Título do risco"
                        style={{ ...inputBase, fontWeight: 700, marginBottom: 6 }}/>
                      <textarea value={descMostrada || ''}
                        onChange={e => editarRiscoIdx(idxGlobal, 'descricao', e.target.value)}
                        rows={2} placeholder="Descrição · por que esse risco existe"
                        style={{ ...inputBase, lineHeight: 1.4, resize: 'vertical' }}/>
                    </div>
                  )}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto',
                    gap: 10, marginBottom: 8, alignItems: 'end' }}>
                    <div>
                      <div style={{ fontSize: 10, fontWeight: 700, color: G.txt2,
                        letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 4 }}>
                        Probabilidade
                      </div>
                      <select value={r.probabilidade}
                        onChange={e => editarRiscoIdx(idxGlobal, 'probabilidade', e.target.value)}
                        style={{ ...inputBase, padding: '6px 10px',
                          color: probOpcao?.cor || G.txt1, fontWeight: 700 }}>
                        {ESCALA_PROB_IMPACTO.map(o => (
                          <option key={o.id} value={o.id}>{o.label}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <div style={{ fontSize: 10, fontWeight: 700, color: G.txt2,
                        letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 4 }}>
                        Impacto
                      </div>
                      <select value={r.impacto}
                        onChange={e => editarRiscoIdx(idxGlobal, 'impacto', e.target.value)}
                        style={{ ...inputBase, padding: '6px 10px',
                          color: impOpcao?.cor || G.txt1, fontWeight: 700 }}>
                        {ESCALA_PROB_IMPACTO.map(o => (
                          <option key={o.id} value={o.id}>{o.label}</option>
                        ))}
                      </select>
                    </div>
                    {!doCatalogo && (
                      <button onClick={() => removerRiscoIdx(idxGlobal)} title="Remover"
                        style={{ background: 'transparent', border: 'none', color: G.vermelho,
                          cursor: 'pointer', fontSize: 16, padding: '6px 4px', height: 32 }}>
                        ✕
                      </button>
                    )}
                  </div>
                  {doCatalogo ? (
                    <div style={{ fontSize: 12, color: G.txt1, lineHeight: 1.4 }}>
                      <strong>Mitigação proposta pela IA:</strong> <span style={{ fontStyle: 'italic' }}>{mitMostrada}</span>
                    </div>
                  ) : (
                    <div>
                      <div style={{ fontSize: 10, fontWeight: 700, color: G.txt2,
                        letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 4 }}>
                        Mitigação
                      </div>
                      <textarea value={mitMostrada || ''}
                        onChange={e => editarRiscoIdx(idxGlobal, 'mitigacao', e.target.value)}
                        rows={2} placeholder="Como esse risco será mitigado?"
                        style={{ ...inputBase, lineHeight: 1.4, resize: 'vertical' }}/>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
        <button onClick={() => adicionarRiscoProprio(categoria)}
          style={{ background: '#FFF', border: `1px dashed ${G.laranja}`,
            color: G.laranja, padding: '6px 14px', fontSize: 11, fontWeight: 700,
            letterSpacing: '0.06em', textTransform: 'uppercase',
            borderRadius: 0, cursor: 'pointer', marginTop: 10,
            fontFamily: "'Outfit', sans-serif" }}>
          + Adicionar risco próprio
        </button>
      </div>
    );
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
      {/* 2 quadros: Definição + Instruções */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <div style={{ padding: '12px 16px', background: G.verdeLight,
          borderLeft: `4px solid ${G.verde}`, borderRadius: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 800, color: G.verde,
            letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 6 }}>
            O que é Análise de Riscos
          </div>
          <div style={{ fontSize: 12, lineHeight: 1.45, color: G.txt1 }}>
            Mapa de <strong>riscos críticos</strong> que podem afetar o Business Plan:
            <strong> mercado, operacional, financeiro, regulatório</strong>. Cada risco
            recebe uma <strong>probabilidade + impacto</strong> e uma <strong>mitigação</strong>
            proposta pela IA.
          </div>
        </div>
        <div style={{ padding: '12px 16px', background: G.laranjaLight,
          borderLeft: `4px solid ${G.laranja}`, borderRadius: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 800, color: G.laranja,
            letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 6 }}>
            O que você faz aqui
          </div>
          <div style={{ fontSize: 12, lineHeight: 1.45, color: G.txt1 }}>
            <strong>Marque</strong> os riscos que se aplicam · ajuste a
            <strong> probabilidade</strong> e o <strong>impacto</strong> · revise a
            <strong> mitigação</strong>. Riscos Alta/Crítica entram automaticamente no
            <strong> plano de mitigação consolidado</strong>.
          </div>
        </div>
      </div>

      <BlocoCategoria categoria="mercado"     titulo="Bloco 1 · Riscos de Mercado"     sub="concorrência · demanda · ticket"/>
      <BlocoCategoria categoria="operacional" titulo="Bloco 2 · Riscos Operacionais"   sub="pessoas-chave · fornecedores · tecnologia"/>
      <BlocoCategoria categoria="financeiro"  titulo="Bloco 3 · Riscos Financeiros"    sub="capital de giro · câmbio · inadimplência"/>
      <BlocoCategoria categoria="regulatorio" titulo="Bloco 4 · Riscos Regulatórios"   sub="LGPD · tributário · ambiental · trabalhista"/>

      <div>
        <div style={HEADER_BLOCO}>
          Bloco 5 · Plano de Mitigação Consolidado
          <span style={HEADER_AUX}>· riscos críticos (Alta/Crítica) entram aqui automaticamente</span>
        </div>
        <div style={{ background: '#FFF', border: `1px solid ${G.borda}`,
          borderRadius: 0, padding: '12px 14px' }}>
          {(() => {
            const criticos = listaRiscos.filter(r => r.probabilidade === 'c' || r.probabilidade === 'd' || r.impacto === 'c' || r.impacto === 'd');
            if (criticos.length === 0) {
              return (
                <div style={{ fontSize: 12, color: G.txt2, fontStyle: 'italic' }}>
                  Nenhum risco classificado como Alta ou Crítica até o momento.
                </div>
              );
            }
            return (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {criticos.map(r => {
                  const opcoesCat = catalogoRiscos[r.categoria] || [];
                  const cat = opcoesCat.find(c => c.id === r.id_catalogo) || {};
                  return (
                    <div key={r.id_catalogo} style={{ fontSize: 12, lineHeight: 1.5,
                      color: G.txt1, padding: '8px 12px',
                      borderLeft: `3px solid ${G.vermelho}`, background: '#FFFBEB' }}>
                      <strong>{cat.titulo}</strong> — {cat.mitigacao}
                    </div>
                  );
                })}
              </div>
            );
          })()}
        </div>
      </div>
    </div>
  );
};

const ConteudoTarefa12 = ({ esg, setEsg, empresa, modo = 'EE' }) => {
  const setorEmp = empresa?.setor || 'geral';
  const fonteESG          = modo === 'SU' ? ESG_CATALOGO_POR_SETOR_SU : ESG_CATALOGO_POR_SETOR;
  const fonteCompromissos = modo === 'SU' ? COMPROMISSOS_PUBLICOS_POR_SETOR_SU : COMPROMISSOS_PUBLICOS_POR_SETOR;
  const catalogoESG = fonteESG[setorEmp] || fonteESG['geral'];
  const catalogoCompromissos = fonteCompromissos[setorEmp] || fonteCompromissos['geral'];
  const rascunho = (modo === 'SU' ? RASCUNHO_TAREFA_12_SU : RASCUNHO_TAREFA_12)[empresa?.id] || { praticas: [], compromissos: [] };
  const praticas     = esg?.praticas     ?? rascunho.praticas;
  const compromissos = esg?.compromissos ?? rascunho.compromissos;

  const atualizar = (campo, valor) => {
    setEsg(prev => ({ praticas, compromissos, ...prev, [campo]: valor }));
  };
  const chipPratMarcada = (catId) => praticas.some(p => p.id_catalogo === catId);
  const toggleChipPrat = (dimensao, catItem) => {
    if (chipPratMarcada(catItem.id)) {
      atualizar('praticas', praticas.filter(p => p.id_catalogo !== catItem.id));
    } else {
      atualizar('praticas', [...praticas, {
        id_catalogo: catItem.id, dimensao, maturidade: 'b',
      }]);
    }
  };
  const editarPratIdx = (idx, campo, valor) => {
    atualizar('praticas', praticas.map((p, i) => i === idx ? { ...p, [campo]: valor } : p));
  };
  const removerPratIdx = (idx) => {
    atualizar('praticas', praticas.filter((_, i) => i !== idx));
  };
  const adicionarPratPropria = (dimensao) => {
    atualizar('praticas', [...praticas, {
      id_catalogo: null, dimensao,
      titulo: '', descricao: '', acao: '',
      maturidade: 'a',
    }]);
  };
  const chipCompMarcado = (catId) => compromissos.some(c => c.id_catalogo === catId);
  const toggleChipComp = (catItem) => {
    if (chipCompMarcado(catItem.id)) {
      atualizar('compromissos', compromissos.filter(c => c.id_catalogo !== catItem.id));
    } else {
      atualizar('compromissos', [...compromissos, { id_catalogo: catItem.id }]);
    }
  };
  const editarCompIdx = (idx, campo, valor) => {
    atualizar('compromissos', compromissos.map((c, i) => i === idx ? { ...c, [campo]: valor } : c));
  };
  const removerCompIdx = (idx) => {
    atualizar('compromissos', compromissos.filter((_, i) => i !== idx));
  };
  const adicionarCompProprio = () => {
    atualizar('compromissos', [...compromissos, {
      id_catalogo: null, titulo: '', descricao: '',
    }]);
  };

  const inputBase = {
    width: '100%', padding: '8px 12px', fontSize: 13,
    border: `1px solid ${G.borda}`, borderRadius: 0,
    fontFamily: "'Outfit', sans-serif", color: G.txt1,
    background: '#FFF', boxSizing: 'border-box',
  };
  const HEADER_BLOCO = {
    fontSize: 16, fontWeight: 700, letterSpacing: '0.10em',
    textTransform: 'uppercase', color: G.laranja, marginBottom: 8,
  };
  const HEADER_AUX = {
    fontSize: 12, fontWeight: 400, color: G.txt2,
    textTransform: 'none', letterSpacing: 0, marginLeft: 10,
  };

  const BlocoDimensao = ({ dimensao, titulo, sub }) => {
    const opcoesDim = catalogoESG[dimensao] || [];
    const praticasDim = praticas.filter(p => p.dimensao === dimensao);
    return (
      <div>
        <div style={HEADER_BLOCO}>
          {titulo}
          <span style={HEADER_AUX}>· {sub}</span>
        </div>
        <div style={{ background: G.fundo, border: `1px solid ${G.borda}`,
          borderRadius: 0, padding: '10px 14px', marginBottom: 10 }}>
          <div style={{ fontSize: 10, fontWeight: 800, color: G.txt2,
            letterSpacing: '0.10em', textTransform: 'uppercase', marginBottom: 8 }}>
            Sugestões da IA · marque as práticas que se aplicam
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {opcoesDim.map(c => {
              const ativo = chipPratMarcada(c.id);
              return (
                <button key={c.id} type="button"
                  onClick={() => toggleChipPrat(dimensao, c)}
                  title={c.descricao}
                  style={{
                    padding: '5px 11px', fontSize: 12, fontWeight: 600,
                    background: ativo ? G.verde : '#FFF',
                    color: ativo ? '#FFF' : G.txt1,
                    border: `1px solid ${ativo ? G.verde : G.borda}`,
                    borderRadius: 999, cursor: 'pointer',
                    fontFamily: "'Outfit', sans-serif",
                  }}>
                  {ativo ? '✓ ' : ''}{c.titulo}
                </button>
              );
            })}
          </div>
        </div>
        {praticasDim.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {praticasDim.map((p) => {
              const idxGlobal = praticas.indexOf(p);
              const doCatalogo = !!p.id_catalogo;
              const cat = doCatalogo ? (opcoesDim.find(c => c.id === p.id_catalogo) || {}) : null;
              const tituloMostrado = doCatalogo ? cat.titulo : p.titulo;
              const descMostrada = doCatalogo ? cat.descricao : p.descricao;
              const acaoMostrada = doCatalogo ? cat.acao : p.acao;
              const matOpcao = MATURIDADE_ESG.find(o => o.id === p.maturidade);
              return (
                <div key={`${p.id_catalogo || 'proprio'}_${idxGlobal}`} style={{
                  background: '#FFF', border: `1px solid ${doCatalogo ? G.borda : G.laranja}`,
                  borderRadius: 0, padding: '10px 14px',
                }}>
                  {doCatalogo ? (
                    <>
                      <div style={{ fontSize: 13, fontWeight: 700, color: G.txt1, marginBottom: 4 }}>
                        {tituloMostrado}
                      </div>
                      <div style={{ fontSize: 12, color: G.txt2, fontStyle: 'italic',
                        lineHeight: 1.4, marginBottom: 8 }}>
                        {descMostrada}
                      </div>
                    </>
                  ) : (
                    <div style={{ marginBottom: 8 }}>
                      <div style={{ fontSize: 10, fontWeight: 700, color: G.laranja,
                        letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 6 }}>
                        Prática própria
                      </div>
                      <input value={tituloMostrado || ''}
                        onChange={e => editarPratIdx(idxGlobal, 'titulo', e.target.value)}
                        placeholder="Título da prática"
                        style={{ ...inputBase, fontWeight: 700, marginBottom: 6 }}/>
                      <textarea value={descMostrada || ''}
                        onChange={e => editarPratIdx(idxGlobal, 'descricao', e.target.value)}
                        rows={2} placeholder="Descrição · o que essa prática é"
                        style={{ ...inputBase, lineHeight: 1.4, resize: 'vertical' }}/>
                    </div>
                  )}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr auto',
                    gap: 10, marginBottom: 8, alignItems: 'end' }}>
                    <div>
                      <div style={{ fontSize: 10, fontWeight: 700, color: G.txt2,
                        letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 4 }}>
                        Maturidade atual
                      </div>
                      <select value={p.maturidade}
                        onChange={e => editarPratIdx(idxGlobal, 'maturidade', e.target.value)}
                        style={{ ...inputBase, padding: '6px 10px',
                          color: matOpcao?.cor || G.txt1, fontWeight: 700 }}>
                        {MATURIDADE_ESG.map(o => (
                          <option key={o.id} value={o.id}>{o.label}</option>
                        ))}
                      </select>
                    </div>
                    {!doCatalogo && (
                      <button onClick={() => removerPratIdx(idxGlobal)} title="Remover"
                        style={{ background: 'transparent', border: 'none', color: G.vermelho,
                          cursor: 'pointer', fontSize: 16, padding: '6px 4px', height: 32 }}>
                        ✕
                      </button>
                    )}
                  </div>
                  {doCatalogo ? (
                    <div style={{ fontSize: 12, color: G.txt1, lineHeight: 1.4 }}>
                      <strong>Ação proposta pela IA:</strong> <span style={{ fontStyle: 'italic' }}>{acaoMostrada}</span>
                    </div>
                  ) : (
                    <div>
                      <div style={{ fontSize: 10, fontWeight: 700, color: G.txt2,
                        letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 4 }}>
                        Ação prevista
                      </div>
                      <textarea value={acaoMostrada || ''}
                        onChange={e => editarPratIdx(idxGlobal, 'acao', e.target.value)}
                        rows={2} placeholder="Como essa prática será implementada?"
                        style={{ ...inputBase, lineHeight: 1.4, resize: 'vertical' }}/>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
        <button onClick={() => adicionarPratPropria(dimensao)}
          style={{ background: '#FFF', border: `1px dashed ${G.laranja}`,
            color: G.laranja, padding: '6px 14px', fontSize: 11, fontWeight: 700,
            letterSpacing: '0.06em', textTransform: 'uppercase',
            borderRadius: 0, cursor: 'pointer', marginTop: 10,
            fontFamily: "'Outfit', sans-serif" }}>
          + Adicionar prática própria
        </button>
      </div>
    );
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
      {/* 2 quadros: Definição + Instruções */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <div style={{ padding: '12px 16px', background: G.verdeLight,
          borderLeft: `4px solid ${G.verde}`, borderRadius: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 800, color: G.verde,
            letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 6 }}>
            O que é ESG &amp; Sustentabilidade
          </div>
          <div style={{ fontSize: 12, lineHeight: 1.45, color: G.txt1 }}>
            Mapa das <strong>práticas ambientais, sociais e de governança</strong>
            que a empresa adota hoje + <strong>compromissos públicos</strong> que se
            propõe a atingir. Cada vez mais clientes corporativos, bancos e
            investidores exigem ESG estruturado.
          </div>
        </div>
        <div style={{ padding: '12px 16px', background: G.laranjaLight,
          borderLeft: `4px solid ${G.laranja}`, borderRadius: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 800, color: G.laranja,
            letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 6 }}>
            O que você faz aqui
          </div>
          <div style={{ fontSize: 12, lineHeight: 1.45, color: G.txt1 }}>
            <strong>Marque</strong> as práticas que a empresa já adota nas 3 dimensões
            (E, S, G) e ajuste a <strong>maturidade</strong>. Selecione os
            <strong> compromissos públicos</strong> que pretende formalizar no horizonte
            do BP.
          </div>
        </div>
      </div>

      <BlocoDimensao dimensao="ambiental"  titulo="Bloco 1 · Ambiental (E)"  sub="emissões · energia · água · resíduos"/>
      <BlocoDimensao dimensao="social"     titulo="Bloco 2 · Social (S)"     sub="cultura · diversidade · comunidade · fornecedores"/>
      <BlocoDimensao dimensao="governanca" titulo="Bloco 3 · Governança (G)" sub="conselho · ética · transparência · compliance"/>

      <div>
        <div style={HEADER_BLOCO}>
          Bloco 4 · Compromissos Públicos
          <span style={HEADER_AUX}>· metas e certificações com prazo</span>
        </div>
        <div style={{ background: G.fundo, border: `1px solid ${G.borda}`,
          borderRadius: 0, padding: '10px 14px', marginBottom: 10 }}>
          <div style={{ fontSize: 10, fontWeight: 800, color: G.txt2,
            letterSpacing: '0.10em', textTransform: 'uppercase', marginBottom: 8 }}>
            Sugestões da IA · marque os compromissos que se aplicam
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {catalogoCompromissos.map(c => {
              const ativo = chipCompMarcado(c.id);
              return (
                <button key={c.id} type="button"
                  onClick={() => toggleChipComp(c)}
                  title={c.descricao}
                  style={{
                    padding: '5px 11px', fontSize: 12, fontWeight: 600,
                    background: ativo ? G.verde : '#FFF',
                    color: ativo ? '#FFF' : G.txt1,
                    border: `1px solid ${ativo ? G.verde : G.borda}`,
                    borderRadius: 999, cursor: 'pointer',
                    fontFamily: "'Outfit', sans-serif",
                  }}>
                  {ativo ? '✓ ' : ''}{c.titulo}
                </button>
              );
            })}
          </div>
        </div>
        {compromissos.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {compromissos.map((c, idx) => {
              const doCatalogo = !!c.id_catalogo;
              const cat = doCatalogo ? (catalogoCompromissos.find(o => o.id === c.id_catalogo) || {}) : null;
              const tituloMostrado = doCatalogo ? cat.titulo : c.titulo;
              const descMostrada   = doCatalogo ? cat.descricao : c.descricao;
              return (
                <div key={`${c.id_catalogo || 'proprio'}_${idx}`} style={{
                  background: '#FFF', border: `1px solid ${doCatalogo ? G.borda : G.laranja}`,
                  borderRadius: 0, padding: '10px 14px',
                }}>
                  {doCatalogo ? (
                    <>
                      <div style={{ fontSize: 13, fontWeight: 700, color: G.txt1, marginBottom: 4 }}>
                        {tituloMostrado}
                      </div>
                      <div style={{ fontSize: 12, color: G.txt2, fontStyle: 'italic',
                        lineHeight: 1.4 }}>
                        {descMostrada}
                      </div>
                    </>
                  ) : (
                    <div>
                      <div style={{ fontSize: 10, fontWeight: 700, color: G.laranja,
                        letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 6 }}>
                        Compromisso próprio
                      </div>
                      <input value={tituloMostrado || ''}
                        onChange={e => editarCompIdx(idx, 'titulo', e.target.value)}
                        placeholder="Título do compromisso (ex: Net-zero em 2030)"
                        style={{ ...inputBase, fontWeight: 700, marginBottom: 6 }}/>
                      <textarea value={descMostrada || ''}
                        onChange={e => editarCompIdx(idx, 'descricao', e.target.value)}
                        rows={2} placeholder="Detalhe do compromisso e prazo"
                        style={{ ...inputBase, lineHeight: 1.4, resize: 'vertical' }}/>
                      <button onClick={() => removerCompIdx(idx)} title="Remover"
                        style={{ background: 'transparent', border: 'none', color: G.vermelho,
                          cursor: 'pointer', fontSize: 14, padding: '4px 0', marginTop: 4 }}>
                        ✕ Remover
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
        <button onClick={adicionarCompProprio}
          style={{ background: '#FFF', border: `1px dashed ${G.laranja}`,
            color: G.laranja, padding: '6px 14px', fontSize: 11, fontWeight: 700,
            letterSpacing: '0.06em', textTransform: 'uppercase',
            borderRadius: 0, cursor: 'pointer', marginTop: 10,
            fontFamily: "'Outfit', sans-serif" }}>
          + Adicionar compromisso próprio
        </button>
      </div>
    </div>
  );
};

const ConteudoTarefa13 = ({ projecoes, setProjecoes, empresa, modo, estruturaFin, produtosServicos, meta }) => {
  const [graficosT13Aberto, setGraficosT13Aberto] = useState(false);
  const [anoComposicaoSU, setAnoComposicaoSU] = useState(0);   // T14 SU · ano exibido na memória de cálculo do FCF
  const rascunho = RASCUNHO_TAREFA_13[empresa?.id] || {
    receita_inicial: 0, crescimento_id: 'b', cpv_id: 'b',
    despesas: { pessoal: 'b', estrutura: 'b', marketing: 'b', outras: 'b' },
    capex_id: 'b',
  };
  const receita_inicial = projecoes?.receita_inicial ?? rascunho.receita_inicial;
  const crescimento_id  = projecoes?.crescimento_id  ?? rascunho.crescimento_id;
  const cpv_id          = projecoes?.cpv_id          ?? rascunho.cpv_id;
  const despesas        = projecoes?.despesas        ?? rascunho.despesas;
  const capex_id        = projecoes?.capex_id        ?? rascunho.capex_id;

  const atualizar = (campo, valor) => {
    setProjecoes(prev => ({
      receita_inicial, crescimento_id, cpv_id, despesas, capex_id,
      ...prev,
      [campo]: valor,
    }));
  };
  const atualizarDespesa = (bucketId, opcaoId) => {
    setProjecoes(prev => ({
      receita_inicial, crescimento_id, cpv_id, despesas, capex_id,
      ...prev,
      despesas: { ...(prev?.despesas ?? despesas), [bucketId]: opcaoId },
    }));
  };

  const inputBase = {
    width: '100%', padding: '8px 12px', fontSize: 13,
    border: `1px solid ${G.borda}`, borderRadius: 0,
    fontFamily: "'Outfit', sans-serif", color: G.txt1,
    background: '#FFF', boxSizing: 'border-box',
  };
  const HEADER_BLOCO = {
    fontSize: 16, fontWeight: 700, letterSpacing: '0.10em',
    textTransform: 'uppercase', color: G.laranja, marginBottom: 8,
  };
  const HEADER_AUX = {
    fontSize: 12, fontWeight: 400, color: G.txt2,
    textTransform: 'none', letterSpacing: 0, marginLeft: 10,
  };

  const anos = calcularProjecao(receita_inicial, crescimento_id, cpv_id, despesas, capex_id);
  const fmtMi = (v) => v >= 1000000 ? `R$ ${(v/1000000).toFixed(2)} mi` : `R$ ${(v/1000).toFixed(0)} mil`;
  const fmtPct = (v) => `${(v*100).toFixed(1)}%`;
  const despesasPct = DESPESAS_BUCKETS.reduce((acc, b) => {
    const sel = b.opcoes.find(o => o.id === despesas?.[b.id])?.meio || 0;
    return acc + sel;
  }, 0);

  const BlocoChipWizard = ({ titulo, sub, campo, valorAtual, opcoes }) => (
    <div>
      <div style={HEADER_BLOCO}>
        {titulo}
        <span style={HEADER_AUX}>· {sub}</span>
      </div>
      <div style={{ background: G.fundo, border: `1px solid ${G.borda}`,
        borderRadius: 0, padding: '10px 14px' }}>
        <div style={{ fontSize: 10, fontWeight: 800, color: G.txt2,
          letterSpacing: '0.10em', textTransform: 'uppercase', marginBottom: 8 }}>
          Sugestões da IA · selecione uma classe estratégica
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          {opcoes.map(o => {
            const ativo = valorAtual === o.id;
            return (
              <button key={o.id} type="button"
                onClick={() => atualizar(campo, o.id)}
                title={o.desc}
                style={{
                  padding: '8px 12px', fontSize: 12, fontWeight: 600,
                  background: ativo ? G.verde : '#FFF',
                  color: ativo ? '#FFF' : G.txt1,
                  border: `1px solid ${ativo ? G.verde : G.borda}`,
                  borderRadius: 0, cursor: 'pointer',
                  fontFamily: "'Outfit', sans-serif", textAlign: 'left',
                }}>
                {ativo ? '✓ ' : ''}<strong>{o.label}</strong> · {o.faixa}
              </button>
            );
          })}
        </div>
        {valorAtual && (
          <div style={{ marginTop: 8, fontSize: 12, color: G.txt2, fontStyle: 'italic',
            lineHeight: 1.4 }}>
            {opcoes.find(o => o.id === valorAtual)?.desc}
          </div>
        )}
      </div>
    </div>
  );

  // ═══════════════════════════════════════════════════════════════════════
  // MODO STARTUP · Projeções derivam da Estruturação Financeira (T13 SU) +
  // Meta (T1). Nada se digita aqui: premissas read-only · DRE/FCF calculado.
  // ═══════════════════════════════════════════════════════════════════════
  if (modo === 'SU') {
    const base = calcularBaseFinSU(estruturaFin, produtosServicos, empresa, meta);
    // Curva de crescimento: sugestão da IA por tese OU override editado pelo usuário.
    const curvaIA = curvaCrescimentoSU(base, meta);                          // frações
    const editadoCresc = Array.isArray(projecoes?.crescimentoSU) && projecoes.crescimentoSU.length === 4;
    const curvaPct = editadoCresc
      ? projecoes.crescimentoSU
      : curvaIA.map(f => Math.round(f * 1000) / 10);                         // percentuais (1 casa)
    const anosSU = calcularProjecaoSU(base, meta, curvaPct.map(p => (Number(p) || 0) / 100));
    const ano5SU = anosSU[anosSU.length - 1];
    const teseSU = (TIPOS_META_BP60M_SU.find(t => t.id === meta?.tipo) || {}).titulo || 'sua tese';
    const ancoradaReceita = meta?.tipo === 'receita_alvo'
      && base.cagrNecessario != null && Number.isFinite(base.cagrNecessario);
    const setCrescAno = (idx, val) => {
      const atual = editadoCresc ? [...projecoes.crescimentoSU] : curvaIA.map(f => Math.round(f * 1000) / 10);
      atual[idx] = (val == null ? 0 : Number(val));
      setProjecoes(prev => ({ ...(prev || {}), crescimentoSU: atual }));
    };
    const resetCresc = () => setProjecoes(prev => {
      const n = { ...(prev || {}) }; delete n.crescimentoSU; return n;
    });
    const premissasSU = [
      { rot: 'Receita · Ano 1', val: fmtMi(base.receitaAno1), cor: G.verde },
      { rot: 'Custo direto', val: `${base.custoDirPctPond.toFixed(1)}% da receita`,
        sub: `margem de contribuição ${base.margemContribPct.toFixed(1)}%` },
      { rot: 'OPEX · ano', val: fmtMi(base.opexTotalAno) },
      { rot: `Impostos · ano (${base.regime})`, val: fmtMi(base.impTotal) },
      { rot: 'CAPEX inicial', val: fmtMi(base.capexTotal), cor: G.laranja },
      { rot: 'Capital de giro', val: fmtMi(base.cgTotal) },
    ];
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
        {/* 2 quadros: Definição + Instruções */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <div style={{ padding: '12px 16px', background: G.verdeLight,
            borderLeft: `4px solid ${G.verde}`, borderRadius: 0 }}>
            <div style={{ fontSize: 14, fontWeight: 800, color: G.verde,
              letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 6 }}>
              O que são as Projeções 5 anos
            </div>
            <div style={{ fontSize: 12, lineHeight: 1.45, color: G.txt1 }}>
              Tradução dos números da <strong>Estruturação Financeira</strong> em um
              {' '}<strong>DRE</strong> e um <strong>fluxo de caixa</strong> de 5 anos:
              o caminho da receita, do EBITDA e da geração/queima de caixa até a Meta.
            </div>
          </div>
          <div style={{ padding: '12px 16px', background: G.laranjaLight,
            borderLeft: `4px solid ${G.laranja}`, borderRadius: 0 }}>
            <div style={{ fontSize: 14, fontWeight: 800, color: G.laranja,
              letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 6 }}>
              O que você faz aqui
            </div>
            <div style={{ fontSize: 12, lineHeight: 1.45, color: G.txt1 }}>
              <strong>Confira as projeções.</strong> As premissas vêm da
              {' '}<strong>Tarefa 13 (Estruturação Financeira)</strong> e da sua
              {' '}<strong>Meta (Tarefa 1)</strong> — se algo estiver fora, ajuste lá.
              Aqui nada se digita.
            </div>
          </div>
        </div>

        {/* Premissas · read-only, vindas da T13 + Meta */}
        <div>
          <div style={HEADER_BLOCO}>
            Bloco 1 · Premissas
            <span style={HEADER_AUX}>· vêm da Estruturação Financeira (Tarefa 13) e da Meta (Tarefa 1)</span>
          </div>
          <div style={{ background: '#FFF', border: `1px solid ${G.borda}`,
            borderRadius: 0, padding: '14px 16px' }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12 }}>
              {premissasSU.map((p, i) => (
                <div key={i} style={{ background: G.fundo, border: `1px solid ${G.borda}`,
                  borderRadius: 0, padding: '10px 12px' }}>
                  <div style={{ fontSize: 10, fontWeight: 800, color: G.txt2,
                    letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 4 }}>
                    {p.rot}
                  </div>
                  <div style={{ fontSize: 17, fontWeight: 900, color: p.cor || G.txt1,
                    fontFamily: "'IBM Plex Mono', monospace" }}>{p.val}</div>
                  {p.sub && (
                    <div style={{ fontSize: 11, color: G.txt2, marginTop: 2 }}>{p.sub}</div>
                  )}
                </div>
              ))}
            </div>
            {/* Crescimento da receita · sugerido pela IA · editável (rotina de revisão) */}
            <div style={{ marginTop: 12, padding: '12px 14px', background: G.verdeLight,
              borderLeft: `3px solid ${G.verde}`, borderRadius: 0 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between',
                alignItems: 'baseline', gap: 10, marginBottom: 8, flexWrap: 'wrap' }}>
                <div style={{ fontSize: 12, lineHeight: 1.45, color: G.txt1, flex: 1, minWidth: 260 }}>
                  <strong>Crescimento da receita</strong> — a <MarcaAIdvisor /> sugeriu a curva
                  {' '}para a tese <strong>«{teseSU}»</strong>:{' '}
                  {ancoradaReceita
                    ? 'ancorada no CAGR necessário para atingir a Meta de receita.'
                    : 'perfil decrescente (agressivo no início · desacelera conforme escala).'}
                  {' '}O <strong>OPEX acompanha a ~metade</strong> desse ritmo (alavancagem operacional).
                </div>
                {editadoCresc && (
                  <button type="button" onClick={resetCresc}
                    style={{ background: 'transparent', color: G.verde,
                      border: `1px solid ${G.verde}`, borderRadius: 0, padding: '4px 10px',
                      fontSize: 11, fontWeight: 700, cursor: 'pointer',
                      fontFamily: 'inherit', whiteSpace: 'nowrap' }}>
                    <IconeReverter /> Voltar à sugestão da IA
                  </button>
                )}
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10 }}>
                {[0,1,2,3].map(idx => (
                  <div key={idx}>
                    <div style={{ fontSize: 10, fontWeight: 800, color: G.txt2,
                      letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 4 }}>
                      Ano {idx + 2}
                    </div>
                    <CampoNumericoBP60M
                      valor={curvaPct[idx]}
                      onChange={(v) => setCrescAno(idx, v)}
                      sufixo="%" formato="decimal" min={0} max={999} textAlign="right" />
                  </div>
                ))}
              </div>
              <div style={{ fontSize: 11, color: G.txt2, marginTop: 6, fontStyle: 'italic' }}>
                % de crescimento da receita sobre o ano anterior ·
                {' '}{editadoCresc ? 'ajustado por você' : 'sugestão da RUNGOAL AIDVISOR'}.
              </div>
            </div>
          </div>
        </div>

        {/* DRE / FCF projetado · 5 anos */}
        <div>
          <div style={HEADER_BLOCO}>
            Bloco 2 · Como o FCF de cada ano é formado
            <span style={HEADER_AUX}>· o passo a passo, do faturamento ao caixa</span>
          </div>
          {/* Seletor de ano */}
          <div style={{ display: 'flex', gap: 6, marginBottom: 10, flexWrap: 'wrap' }}>
            {anosSU.map((a, i) => {
              const ativo = anoComposicaoSU === i;
              return (
                <button key={i} type="button" onClick={() => setAnoComposicaoSU(i)}
                  style={{ padding: '6px 14px', fontSize: 12, fontWeight: 700,
                    background: ativo ? G.verde : '#FFF', color: ativo ? '#FFF' : G.txt1,
                    border: `1px solid ${ativo ? G.verde : G.borda}`, borderRadius: 0,
                    cursor: 'pointer', fontFamily: 'inherit' }}>
                  {a.ano}
                </button>
              );
            })}
          </div>
          {/* Passo a passo · Receita → FCF do ano selecionado */}
          {(() => {
            const aC = anosSU[anoComposicaoSU] || anosSU[0] || {};
            const ehAno1 = anoComposicaoSU === 0;
            const investC = (Number(aC.capex) || 0) + (Number(aC.deltaCG) || 0);
            const linhas = [
              { rot: 'Receita', val: Number(aC.receita) || 0, origem: ehAno1 ? 'ticket × volume · Tarefa 13' : 'cresce sobre o ano anterior' },
              { rot: '− Custo direto', val: -(Number(aC.custoDir) || 0), origem: '% da receita · Tarefa 4' },
              { rot: '− Custos fixos (OPEX)', val: -(Number(aC.opex) || 0), origem: ehAno1 ? 'Tarefa 13' : 'cresce à metade do ritmo' },
              { rot: '= Resultado operacional (EBITDA)', val: Number(aC.ebitda) || 0, sub: true },
              { rot: '− Impostos', val: -(Number(aC.impostos) || 0), origem: 'regime tributário · Tarefa 13' },
              { rot: '= Lucro do ano', val: Number(aC.lucroLiquido) || 0, sub: true },
              { rot: '− Investimento (CAPEX + capital de giro)', val: -investC, origem: ehAno1 ? 'Tarefa 13' : 'CAPEX só no Ano 1' },
              { rot: '= FCF · o caixa do ano', val: Number(aC.fcf) || 0, total: true },
            ];
            return (
              <div style={{ background: '#FFF', border: `1px solid ${G.borda}`, borderRadius: 0, padding: '4px 16px' }}>
                {linhas.map((l, i) => (
                  <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline',
                    gap: 12, padding: '9px 0', borderTop: i === 0 ? 'none' : `1px solid ${l.total ? G.verde : G.borda}` }}>
                    <div>
                      <span style={{ fontSize: 13, fontWeight: (l.sub || l.total) ? 800 : 500, color: l.total ? G.verde : G.txt1 }}>{l.rot}</span>
                      {l.origem && <span style={{ fontSize: 11, color: G.txt2, marginLeft: 8 }}>· {l.origem}</span>}
                    </div>
                    <span style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: l.total ? 18 : 13,
                      fontWeight: (l.sub || l.total) ? 800 : 600, color: l.val < 0 ? G.vermelho : (l.total ? G.verde : G.txt1) }}>{fmtMi(l.val)}</span>
                  </div>
                ))}
              </div>
            );
          })()}
          <div style={{ fontSize: 11, color: G.txt2, marginTop: 8, fontStyle: 'italic' }}>
            O <strong>Ano 1</strong> vem da Tarefa 13 (você confirmou cada número lá). Os <strong>Anos 2–5</strong> crescem sobre o Ano 1 pela curva de crescimento.
          </div>

          {/* Cenários · Ano 5 */}
          <div style={{ marginTop: 14, background: '#FFF', border: `1px solid ${G.borda}`,
            borderRadius: 0, padding: '12px 14px' }}>
            <div style={{ fontSize: 10, fontWeight: 800, color: G.txt2,
              letterSpacing: '0.10em', textTransform: 'uppercase', marginBottom: 10 }}>
              Cenários · receita / EBITDA / FCF no Ano 5
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10 }}>
              {CENARIOS_PROJ.map(c => {
                const fator = 1 + c.ajuste;
                return (
                  <div key={c.id} style={{
                    padding: '14px 16px', background: G.fundo,
                    border: `1px solid ${G.borda}`,
                    borderLeft: `4px solid ${c.cor}`, borderRadius: 0,
                  }}>
                    <div style={{ fontSize: 13, fontWeight: 800, color: c.cor,
                      letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 10 }}>
                      {c.label} {c.ajuste !== 0 && `(${c.ajuste > 0 ? '+' : ''}${Math.round(c.ajuste*100)}%)`}
                    </div>
                    <div style={{ fontSize: 13, color: G.txt2, lineHeight: 1.8 }}>
                      <div>Receita: <strong style={{ color: G.txt1, fontSize: 15 }}>{fmtMi(ano5SU.receita * fator)}</strong></div>
                      <div>EBITDA: <strong style={{ color: G.verde, fontSize: 15 }}>{fmtMi(ano5SU.ebitda * fator)}</strong></div>
                      <div>FCF: <strong style={{ color: ano5SU.fcf * fator >= 0 ? G.txt1 : G.vermelho, fontSize: 15 }}>{fmtMi(ano5SU.fcf * fator)}</strong></div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
      {/* 2 quadros: Definição + Instruções */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <div style={{ padding: '12px 16px', background: G.verdeLight,
          borderLeft: `4px solid ${G.verde}`, borderRadius: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 800, color: G.verde,
            letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 6 }}>
            O que são Projeções 5 anos
          </div>
          <div style={{ fontSize: 12, lineHeight: 1.45, color: G.txt1 }}>
            Estimativa quantitativa de <strong>receita</strong>, <strong>margem</strong>
            {' '}e <strong>investimento</strong> ano a ano. Gera o DRE, o FCF e os
            cenários (Base/Otimista/Pessimista) que sustentam o valuation.
          </div>
        </div>
        <div style={{ padding: '12px 16px', background: G.laranjaLight,
          borderLeft: `4px solid ${G.laranja}`, borderRadius: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 800, color: G.laranja,
            letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 6 }}>
            O que você faz aqui
          </div>
          <div style={{ fontSize: 12, lineHeight: 1.45, color: G.txt1 }}>
            <strong>Confirme a receita inicial</strong> e <strong>selecione uma classe
            estratégica</strong> para cada premissa (crescimento, margem, CAPEX).
            O DRE/FCF e os cenários são <strong>calculados automaticamente</strong>.
          </div>
        </div>
      </div>

      {/* B1 Receita inicial · número */}
      <div>
        <div style={HEADER_BLOCO}>
          Bloco 1 · Receita atual (ponto de partida)
          <span style={HEADER_AUX}>· receita anual estimada pela IA a partir do extrato</span>
        </div>
        <div style={{ background: '#FFF', border: `1px solid ${G.borda}`,
          borderRadius: 0, padding: '12px 14px' }}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 14 }}>
            <div style={{ fontSize: 10, fontWeight: 800, color: G.txt2,
              letterSpacing: '0.10em', textTransform: 'uppercase' }}>
              Receita atual estimada
            </div>
            <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 6 }}>
              <div style={{ width: 260 }}>
                <CampoNumericoBP60M
                  valor={receita_inicial}
                  onChange={(v) => atualizar('receita_inicial', v ?? 0)}
                  label="Receita atual estimada (R$/ano)"
                  permitirDecimal={true}
                  min={0}
                  formato="brl"
                />
              </div>
              <span style={{ fontSize: 12, color: G.txt2, fontWeight: 700 }}>/ano</span>
            </div>
          </div>
        </div>
      </div>

      <BlocoChipWizard
        titulo="Bloco 2 · Crescimento de Receita"
        sub="ritmo médio anual nos próximos 5 anos"
        campo="crescimento_id"
        valorAtual={crescimento_id}
        opcoes={CRESCIMENTO_RECEITA_INTERVALOS}/>

      <BlocoChipWizard
        titulo="Bloco 3 · Custos diretos (CPV)"
        sub="custo direto do produto/serviço vendido · % da receita"
        campo="cpv_id"
        valorAtual={cpv_id}
        opcoes={CPV_INTERVALOS}/>

      {/* B4 · Despesas operacionais · 4 sub-buckets */}
      <div>
        <div style={HEADER_BLOCO}>
          Bloco 4 · Despesas operacionais
          <span style={HEADER_AUX}>· composição em 4 buckets · % da receita</span>
        </div>
        <div style={{ background: G.fundo, border: `1px solid ${G.borda}`,
          borderRadius: 0, padding: '12px 14px',
          display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div style={{ fontSize: 10, fontWeight: 800, color: G.txt2,
            letterSpacing: '0.10em', textTransform: 'uppercase' }}>
            Sugestões da IA por bucket · selecione uma classe para cada
          </div>
          {DESPESAS_BUCKETS.map(b => {
            const selId = despesas?.[b.id];
            return (
              <div key={b.id} style={{ background: '#FFF',
                border: `1px solid ${G.borda}`, borderRadius: 0,
                padding: '10px 12px' }}>
                <div style={{ fontSize: 13, fontWeight: 800, color: G.txt1,
                  marginBottom: 2 }}>
                  {b.label}
                </div>
                <div style={{ fontSize: 11, color: G.txt2, marginBottom: 8 }}>
                  {b.sub}
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {b.opcoes.map(o => {
                    const ativo = selId === o.id;
                    return (
                      <button key={o.id} type="button"
                        onClick={() => atualizarDespesa(b.id, o.id)}
                        title={o.desc}
                        style={{
                          padding: '6px 10px', fontSize: 12, fontWeight: 600,
                          background: ativo ? G.verde : '#FFF',
                          color: ativo ? '#FFF' : G.txt1,
                          border: `1px solid ${ativo ? G.verde : G.borda}`,
                          borderRadius: 0, cursor: 'pointer',
                          fontFamily: "'Outfit', sans-serif",
                        }}>
                        {ativo ? '✓ ' : ''}<strong>{o.label}</strong> · {o.faixa}
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}
          <div style={{ display: 'flex', justifyContent: 'flex-end',
            alignItems: 'baseline', gap: 8, paddingTop: 4,
            borderTop: `1px solid ${G.borda}` }}>
            <div style={{ fontSize: 10, fontWeight: 800, color: G.txt2,
              letterSpacing: '0.10em', textTransform: 'uppercase' }}>
              Despesas totais
            </div>
            <div style={{ fontSize: 16, fontWeight: 800, color: G.laranja,
              fontVariantNumeric: 'tabular-nums' }}>
              {fmtPct(despesasPct)} da receita
            </div>
          </div>
        </div>
      </div>

      <BlocoChipWizard
        titulo="Bloco 5 · CAPEX médio"
        sub="investimento anual em ativos físicos e tecnologia · % da receita"
        campo="capex_id"
        valorAtual={capex_id}
        opcoes={CAPEX_INTERVALOS}/>

      {/* B6 · DRE/FCF projetado + cenários · calculado */}
      <div>
        <div style={HEADER_BLOCO}>
          Bloco 6 · DRE / FCF projetado &amp; Cenários
          <span style={HEADER_AUX}>· EBITDA = Receita − Custos − Despesas · margem é consequência</span>
        </div>
        <div style={{ background: '#FFF', border: `1px solid ${G.borda}`,
          borderRadius: 0, overflow: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse',
            fontFamily: "'Outfit', sans-serif", minWidth: 760 }}>
            <thead>
              <tr style={{ background: G.fundo, borderBottom: `1px solid ${G.borda}` }}>
                {['Período','Receita','Custos','Despesas','EBITDA','Margem','CAPEX','FCF'].map((h, i) => (
                  <th key={i} style={{ padding: '8px 12px',
                    textAlign: i === 0 ? 'left' : 'right',
                    fontSize: 10, fontWeight: 700, color: G.txt2,
                    letterSpacing: '0.06em', textTransform: 'uppercase' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {anos.map((a, i) => (
                <tr key={i} style={{
                  borderTop: i === 0 ? 'none' : `1px solid ${G.borda}`,
                  background: i === 0 ? G.verdeLight : i === 5 ? '#FFFBEB' : 'transparent',
                }}>
                  <td style={{ padding: '8px 12px', fontSize: 13, fontWeight: 700,
                    color: G.txt1 }}>{a.ano}</td>
                  <td style={{ padding: '8px 12px', fontSize: 13, color: G.txt1,
                    textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{fmtMi(a.receita)}</td>
                  <td style={{ padding: '8px 12px', fontSize: 13, color: G.txt2,
                    textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{fmtMi(a.custos)}</td>
                  <td style={{ padding: '8px 12px', fontSize: 13, color: '#2563EB',
                    textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{fmtMi(a.despesas)}</td>
                  <td style={{ padding: '8px 12px', fontSize: 13, color: G.verde,
                    textAlign: 'right', fontVariantNumeric: 'tabular-nums', fontWeight: 700 }}>{fmtMi(a.ebitda)}</td>
                  <td style={{ padding: '8px 12px', fontSize: 13, color: G.verde,
                    textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{fmtPct(a.margemPct)}</td>
                  <td style={{ padding: '8px 12px', fontSize: 13, color: G.laranja,
                    textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{fmtMi(a.capex)}</td>
                  <td style={{ padding: '8px 12px', fontSize: 13, color: a.fcf >= 0 ? G.txt1 : G.vermelho,
                    textAlign: 'right', fontVariantNumeric: 'tabular-nums', fontWeight: 700 }}>{fmtMi(a.fcf)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Cenários */}
        <div style={{ marginTop: 14, background: '#FFF', border: `1px solid ${G.borda}`,
          borderRadius: 0, padding: '12px 14px' }}>
          <div style={{ fontSize: 10, fontWeight: 800, color: G.txt2,
            letterSpacing: '0.10em', textTransform: 'uppercase', marginBottom: 10 }}>
            Cenários · receita / EBITDA / FCF no Ano 5
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10 }}>
            {CENARIOS_PROJ.map(c => {
              const ano5 = anos[5];
              const fator = 1 + c.ajuste;
              return (
                <div key={c.id} style={{
                  padding: '14px 16px', background: G.fundo,
                  border: `1px solid ${G.borda}`,
                  borderLeft: `4px solid ${c.cor}`, borderRadius: 0,
                }}>
                  <div style={{ fontSize: 13, fontWeight: 800, color: c.cor,
                    letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 10 }}>
                    {c.label} {c.ajuste !== 0 && `(${c.ajuste > 0 ? '+' : ''}${Math.round(c.ajuste*100)}%)`}
                  </div>
                  <div style={{ fontSize: 13, color: G.txt2, lineHeight: 1.8 }}>
                    <div>Receita: <strong style={{ color: G.txt1, fontSize: 15 }}>{fmtMi(ano5.receita * fator)}</strong></div>
                    <div>EBITDA: <strong style={{ color: G.verde, fontSize: 15 }}>{fmtMi(ano5.ebitda * fator)}</strong></div>
                    <div>FCF: <strong style={{ color: ano5.fcf * fator >= 0 ? G.txt1 : G.vermelho, fontSize: 15 }}>{fmtMi(ano5.fcf * fator)}</strong></div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Botão de visualização dos gráficos */}
        <BotaoVisualizarGraficos
          aberto={graficosT13Aberto}
          onToggle={() => setGraficosT13Aberto(v => !v)}
          rotulo="Visualizar gráficos e tabelas">
          <div>
            <div style={{ fontSize: 10, fontWeight: 800, color: G.txt2,
              letterSpacing: '0.10em', textTransform: 'uppercase', marginBottom: 8 }}>
              Ilustração · {TITULO_EXHIBIT_POR_TIPO['t13_evolucao']}
            </div>
            <GraficoProjecao5Anos anos={anos}/>
          </div>
          <div>
            <div style={{ fontSize: 10, fontWeight: 800, color: G.txt2,
              letterSpacing: '0.10em', textTransform: 'uppercase', marginBottom: 8 }}>
              Ilustração · {TITULO_EXHIBIT_POR_TIPO['t13_cenarios']}
            </div>
            <GraficoCenarios5 anoFinal={anos[5]}/>
          </div>
        </BotaoVisualizarGraficos>
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════
// TAREFA 13 — Projeções 5 anos
// 6 blocos: Receita · Crescimento · CPV · Despesas Operacionais (4 buckets) ·
//           CAPEX · DRE/FCF (calculado) + Cenários
// Padrão: chip wizard de classes estratégicas qualitativas · empresário valida
// EBITDA = consequência (Receita − Custos − Despesas), nunca premissa
// ═══════════════════════════════════════════════════════════════════════

const CRESCIMENTO_RECEITA_INTERVALOS = [
  { id: 'a', label: 'Conservador',  faixa: '5-10% a.a.',  meio: 0.075, desc: 'Mercado maduro · ganho marginal de share' },
  { id: 'b', label: 'Moderado',     faixa: '15-25% a.a.', meio: 0.20,  desc: 'Crescimento orgânico saudável · sustentável' },
  { id: 'c', label: 'Acelerado',    faixa: '30-50% a.a.', meio: 0.40,  desc: 'Aposta de scaleup · exige investimento + execução forte' },
  { id: 'd', label: 'Disruptivo',   faixa: '60%+ a.a.',   meio: 0.80,  desc: 'Crescimento extraordinário · raro · alto risco' },
];

// CPV (Custo do Produto Vendido) — diretamente ligado à venda
const CPV_INTERVALOS = [
  { id: 'a', label: 'Leve',        faixa: '5-15% receita',  meio: 0.10, desc: 'Serviço puro · pouco insumo direto' },
  { id: 'b', label: 'Médio',       faixa: '20-40% receita', meio: 0.30, desc: 'Restaurante · indústria leve · varejo com margem' },
  { id: 'c', label: 'Alto',        faixa: '45-65% receita', meio: 0.55, desc: 'Comércio típico · revenda · construção' },
  { id: 'd', label: 'Muito alto',  faixa: '70%+ receita',   meio: 0.75, desc: 'Commodity · distribuição · pouco valor agregado' },
];

// Despesas operacionais — 4 buckets · cada um com escala qualitativa em % receita
const DESPESAS_BUCKETS = [
  {
    id: 'pessoal',
    label: 'Pessoal',
    sub: 'folha de pagamento + encargos + benefícios',
    opcoes: [
      { id: 'a', label: 'Enxuto',      faixa: '5-10% receita',  meio: 0.075, desc: 'Time pequeno · alta produtividade' },
      { id: 'b', label: 'Padrão',      faixa: '12-22% receita', meio: 0.17,  desc: 'Patamar saudável para a operação' },
      { id: 'c', label: 'Alto',        faixa: '25-35% receita', meio: 0.30,  desc: 'Mão de obra intensiva · serviço' },
      { id: 'd', label: 'Muito alto',  faixa: '40%+ receita',   meio: 0.45,  desc: 'Especialistas caros · médico · jurídico sênior' },
    ],
  },
  {
    id: 'estrutura',
    label: 'Estrutura',
    sub: 'aluguel · contas · TI · seguros · manutenção',
    opcoes: [
      { id: 'a', label: 'Enxuto',      faixa: '1-3% receita',   meio: 0.02, desc: 'Modelo digital · home office · sublocação' },
      { id: 'b', label: 'Padrão',      faixa: '3-7% receita',   meio: 0.05, desc: 'Sede e operação típica' },
      { id: 'c', label: 'Alto',        faixa: '8-12% receita',  meio: 0.10, desc: 'Ponto comercial premium · múltiplas unidades' },
      { id: 'd', label: 'Muito alto',  faixa: '15%+ receita',   meio: 0.18, desc: 'Rede física pesada · imóveis caros' },
    ],
  },
  {
    id: 'marketing',
    label: 'Marketing & Comercial',
    sub: 'mídia · agências · comissões · eventos',
    opcoes: [
      { id: 'a', label: 'Enxuto',      faixa: '0-2% receita',   meio: 0.01, desc: 'Boca-a-boca · indicação · orgânico' },
      { id: 'b', label: 'Padrão',      faixa: '3-7% receita',   meio: 0.05, desc: 'Mídia paga moderada + equipe comercial' },
      { id: 'c', label: 'Alto',        faixa: '8-15% receita',  meio: 0.115, desc: 'Aquisição agressiva · escala de marca' },
      { id: 'd', label: 'Muito alto',  faixa: '18%+ receita',   meio: 0.22, desc: 'Marketing intensivo · LTV alto compensa CAC' },
    ],
  },
  {
    id: 'outras',
    label: 'Outras (administrativas)',
    sub: 'jurídico · contábil · seguros · viagens · diversos',
    opcoes: [
      { id: 'a', label: 'Enxuto',      faixa: '1-3% receita',   meio: 0.02, desc: 'Estrutura administrativa leve' },
      { id: 'b', label: 'Padrão',      faixa: '3-6% receita',   meio: 0.045, desc: 'Backoffice típico' },
      { id: 'c', label: 'Alto',        faixa: '7-12% receita',  meio: 0.095, desc: 'Compliance pesado · empresa regulada' },
      { id: 'd', label: 'Muito alto',  faixa: '15%+ receita',   meio: 0.18, desc: 'Estrutura corporativa robusta' },
    ],
  },
];

const CAPEX_INTERVALOS = [
  { id: 'a', label: 'Capital leve',         faixa: 'até 2% receita',  meio: 0.01, desc: 'Modelo asset-light · pouca infraestrutura' },
  { id: 'b', label: 'CAPEX padrão',         faixa: '2-5% receita',    meio: 0.035, desc: 'Manutenção + crescimento moderado' },
  { id: 'c', label: 'CAPEX intensivo',      faixa: '5-12% receita',   meio: 0.085, desc: 'Expansão física · CD novo · tecnologia' },
  { id: 'd', label: 'CAPEX muito intensivo',faixa: '12%+ receita',    meio: 0.18, desc: 'Indústria pesada · obras · infraestrutura' },
];

const CENARIOS_PROJ = [
  { id: 'pessimista', label: 'Pessimista', cor: '#7C3AED', ajuste: -0.20 },
  { id: 'base',       label: 'Base',       cor: G.laranja, ajuste:  0.00 },
  { id: 'otimista',   label: 'Otimista',   cor: G.verde,   ajuste:  0.20 },
];

const RASCUNHO_TAREFA_13 = {
  shopmax: {
    receita_inicial: 1500000,
    crescimento_id: 'c',
    cpv_id: 'c',
    despesas: { pessoal: 'a', estrutura: 'b', marketing: 'c', outras: 'a' },
    capex_id: 'b',
  },
  sabor_arte: {
    receita_inicial: 4800000,
    crescimento_id: 'b',
    cpv_id: 'b',
    despesas: { pessoal: 'c', estrutura: 'c', marketing: 'b', outras: 'b' },
    capex_id: 'a',
  },
  clinicavida: {
    receita_inicial: 12000000,
    crescimento_id: 'b',
    cpv_id: 'a',
    despesas: { pessoal: 'd', estrutura: 'b', marketing: 'a', outras: 'b' },
    capex_id: 'b',
  },
  construmax: {
    receita_inicial: 18000000,
    crescimento_id: 'b',
    cpv_id: 'c',
    despesas: { pessoal: 'b', estrutura: 'b', marketing: 'a', outras: 'a' },
    capex_id: 'c',
  },
};

// Calcula projeção 5 anos · EBITDA é consequência (Receita − CPV − Despesas)
const calcularProjecao = (receita0, crescId, cpvId, despesasIds, capexId) => {
  const cresc  = CRESCIMENTO_RECEITA_INTERVALOS.find(o => o.id === crescId)?.meio || 0;
  const cpv    = CPV_INTERVALOS.find(o => o.id === cpvId)?.meio || 0;
  const capex  = CAPEX_INTERVALOS.find(o => o.id === capexId)?.meio || 0;
  const despesasTotal = DESPESAS_BUCKETS.reduce((acc, b) => {
    const sel = b.opcoes.find(o => o.id === despesasIds?.[b.id])?.meio || 0;
    return acc + sel;
  }, 0);
  const anos = [];
  let receita = receita0 || 0;
  for (let i = 0; i <= 5; i++) {
    if (i > 0) receita = receita * (1 + cresc);
    const custos     = receita * cpv;
    const despesas   = receita * despesasTotal;
    const ebitda     = receita - custos - despesas;
    const margemPct  = receita > 0 ? ebitda / receita : 0;
    const capexV     = receita * capex;
    const fcf        = ebitda - capexV;
    anos.push({
      ano: i === 0 ? 'Hoje' : `Ano ${i}`,
      receita, custos, despesas, ebitda, margemPct, capex: capexV, fcf,
    });
  }
  return anos;
};

// ═══════════════════════════════════════════════════════════════════════
// Componente da Tarefa 14 — Valuation DCF
// 5 blocos: WACC · g · Método VT · Múltiplo · Resultado calculado
// ═══════════════════════════════════════════════════════════════════════

const ConteudoTarefa14 = ({ valuation, setValuation, projecoes, empresa, modo, estruturaFin, produtosServicos, meta, parametros }) => {
  const [dcfTabelaAberta, setDcfTabelaAberta] = useState(false);   // T15 SU · Bloco 2 · mostrar a conta do DCF
  const [vcContaAberta, setVcContaAberta] = useState(false);       // T15 SU · Bloco 3 · mostrar a conta do VC Method
  const rascT14 = RASCUNHO_TAREFA_14[empresa?.id] || {
    wacc_id: 'b', g_id: 'b', metodo_id: 'hibrido', multiplo: 7,
  };
  const rascT13 = RASCUNHO_TAREFA_13[empresa?.id] || {};
  // Indexa por SETOR, não por id de empresa (30/07). Empresa sem setor
  // mapeado cai em 'geral' — antes caía em `undefined` e a tarefa ficava
  // sem referência de múltiplo.
  const refSetor = MULTIPLO_POR_SETOR[empresa?.setor] || MULTIPLO_POR_SETOR['geral'];

  const wacc_id   = valuation?.wacc_id   ?? rascT14.wacc_id;
  const g_id      = valuation?.g_id      ?? rascT14.g_id;
  const metodo_id = valuation?.metodo_id ?? rascT14.metodo_id;
  const multiplo  = valuation?.multiplo  ?? rascT14.multiplo;

  const atualizar = (campo, valor) => {
    setValuation(prev => ({
      wacc_id, g_id, metodo_id, multiplo,
      ...prev,
      [campo]: valor,
    }));
  };

  const HEADER_BLOCO = {
    fontSize: 16, fontWeight: 700, letterSpacing: '0.10em',
    textTransform: 'uppercase', color: G.laranja, marginBottom: 8,
  };
  const HEADER_AUX = {
    fontSize: 12, fontWeight: 400, color: G.txt2,
    textTransform: 'none', letterSpacing: 0, marginLeft: 10,
  };

  // Recalcula projeção T13 para alimentar valuation
  const r13 = {
    receita_inicial: projecoes?.receita_inicial ?? rascT13.receita_inicial,
    crescimento_id:  projecoes?.crescimento_id  ?? rascT13.crescimento_id,
    cpv_id:          projecoes?.cpv_id          ?? rascT13.cpv_id,
    despesas:        projecoes?.despesas        ?? rascT13.despesas,
    capex_id:        projecoes?.capex_id        ?? rascT13.capex_id,
  };
  const anosT13 = calcularProjecao(
    r13.receita_inicial, r13.crescimento_id, r13.cpv_id, r13.despesas, r13.capex_id
  );
  const v = calcularValuation(anosT13, wacc_id, g_id, metodo_id, multiplo);
  const sens = calcularSensibilidade(anosT13, metodo_id, multiplo);
  const fmtMi = (vv) => Math.abs(vv) >= 1000000 ? `R$ ${(vv/1000000).toFixed(2)} mi` : `R$ ${(vv/1000).toFixed(0)} mil`;
  const fmtPct = (vv) => `${(vv*100).toFixed(1)}%`;

  // ═══ Modo Startup · Valuation Inicial (2 métodos: Múltiplos + Scorecard) ═══
  if (modo === 'SU') {
    const base = calcularBaseFinSU(estruturaFin, produtosServicos, empresa, meta);
    const curvaOv = (Array.isArray(projecoes?.crescimentoSU) && projecoes.crescimentoSU.length === 4)
      ? projecoes.crescimentoSU.map(p => (Number(p) || 0) / 100) : undefined;
    const anosSU = calcularProjecaoSU(base, meta, curvaOv);
    // Taxa de desconto da MESMA fonte da aba Valor (WACC real da estrutura de capital) · perpetuidade 3%.
    const waccRealFrac = calcularWACCReal(parametros);
    const waccDefaultPct = waccRealFrac != null ? waccRealFrac * 100 : null;
    const gDefaultPct = 3;
    const waccIsReal = valuation?.val_wacc == null && waccDefaultPct != null;
    const val = calcularValuationSU(base, anosSU, empresa, valuation, waccDefaultPct, gDefaultPct);
    // ASK · captação = maior déficit de caixa acumulado (queima até o breakeven) · editável pelo empresário
    let cumCap = 0, troughCap = 0;
    for (const a of anosSU) { cumCap += (a.fcf || 0); if (cumCap < troughCap) troughCap = cumCap; }
    const captacaoSugerida = troughCap < 0 ? Math.round(-troughCap) : null;
    const captacaoEditada = valuation?.captacaoSU != null;
    const captacaoUsada = captacaoEditada ? valuation.captacaoSU : captacaoSugerida;
    const preMoneyASK = (val && val.central > 0) ? val.central : null;
    const equityPctASK = (captacaoUsada && preMoneyASK) ? +(captacaoUsada / (preMoneyASK + captacaoUsada) * 100).toFixed(1) : null;
    const rodadaSelASK = RODADAS_SU.find(r => r.id === meta?.rodada);
    const resetCaptacao = () => setValuation(prev => { const n = { ...(prev || {}) }; delete n.captacaoSU; return n; });
    const multEditado = valuation?.val_mult != null;
    const setVal = (campo, valor) => setValuation(prev => ({ ...(prev || {}), [campo]: valor }));
    const resetMult = () => setValuation(prev => { const n = { ...(prev || {}) }; delete n.val_mult; return n; });
    const abrirRGAMSU = (termo) => {
      localStorage.setItem('rgam:termoInicial', termo);
      localStorage.setItem('rgam:origem', JSON.stringify({
        tela: 'businessplansu', subTela: 'tarefa-15', label: 'Tarefa 15 · Valuation',
      }));
      window.dispatchEvent(new CustomEvent('rg-navegar', { detail: { tela: 'rgam' } }));
    };

    const HEADER_AUX2 = { fontSize: 12, fontWeight: 400, color: G.txt2, textTransform: 'none', letterSpacing: 0, marginLeft: 8 };
    const caixa = { background: '#FFF', border: `1px solid ${G.borda}`, borderRadius: 0, padding: '14px 16px' };
    const rotCampo = { fontSize: 10, fontWeight: 800, color: G.txt2, letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 6 };

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
        {/* 2 cards */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <div style={{ padding: '12px 16px', background: G.verdeLight, borderLeft: `4px solid ${G.verde}`, borderRadius: 0 }}>
            <div style={{ fontSize: 14, fontWeight: 800, color: G.verde, letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 6 }}>
              O que é o Valuation
            </div>
            <div style={{ fontSize: 12, lineHeight: 1.45, color: G.txt1 }}>
              Uma estimativa do <strong>valor da empresa (pré-money)</strong> por dois caminhos
              consagrados: <strong>múltiplos de mercado</strong> e <strong>fluxo de caixa descontado (DCF)</strong>.
              O resultado é uma <strong>faixa</strong>, não um número exato.
            </div>
          </div>
          <div style={{ padding: '12px 16px', background: G.laranjaLight, borderLeft: `4px solid ${G.laranja}`, borderRadius: 0 }}>
            <div style={{ fontSize: 14, fontWeight: 800, color: G.laranja, letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 6 }}>
              O que você faz aqui
            </div>
            <div style={{ fontSize: 12, lineHeight: 1.45, color: G.txt1 }}>
              A <MarcaAIdvisor /> já pré-preencheu tudo com base no seu setor e na projeção
              da Tarefa 14. <strong>Revise o múltiplo, o ano de referência e a taxa de desconto</strong> —
              a faixa se recalcula sozinha.
            </div>
          </div>
        </div>

        {/* Bloco 1 · Múltiplos comparáveis */}
        <div>
          <div style={HEADER_BLOCO}>
            Bloco 1 · Múltiplos comparáveis
            <span style={HEADER_AUX2}>· valor ≈ múltiplo de receita × receita do ano de referência</span>
          </div>
          <div style={caixa}>
            <div style={{ display: 'grid', gridTemplateColumns: '200px 1fr', gap: 16, alignItems: 'start' }}>
              <div>
                <div style={rotCampo}>Múltiplo de receita</div>
                <CampoNumericoBP60M valor={val.mult} onChange={(v) => setVal('val_mult', v)}
                  sufixo="×" formato="decimal" min={0} max={50} textAlign="right" />
                <div style={{ fontSize: 11, color: G.txt2, marginTop: 6, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                  <span>Sugestão da IA: <strong>{val.multIA}×</strong></span>
                  {multEditado && (
                    <button type="button" onClick={resetMult}
                      style={{ background: 'transparent', color: G.verde, border: `1px solid ${G.verde}`,
                        borderRadius: 0, padding: '2px 8px', fontSize: 10.5, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>
                      <IconeReverter /> Voltar à IA
                    </button>
                  )}
                </div>
              </div>
              <div>
                <div style={rotCampo}>Ano de referência da receita</div>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  {[1,2,3,4,5].map(n => {
                    const ativo = val.anoRef === n;
                    return (
                      <button key={n} type="button" onClick={() => setVal('val_ano_ref', n)}
                        style={{ padding: '7px 14px', fontSize: 12, fontWeight: 700,
                          background: ativo ? G.verde : '#FFF', color: ativo ? '#FFF' : G.txt1,
                          border: `1px solid ${ativo ? G.verde : G.borda}`, borderRadius: 0, cursor: 'pointer', fontFamily: 'inherit' }}>
                        Ano {n}
                      </button>
                    );
                  })}
                </div>
                <div style={{ fontSize: 12, color: G.txt2, marginTop: 8 }}>
                  Receita do Ano {val.anoRef}: <strong style={{ color: G.txt1 }}>{fmtMi(val.receitaRef)}</strong> (vem da Tarefa 14)
                </div>
              </div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginTop: 14, alignItems: 'stretch' }}>
              <div style={{ background: G.verde, color: '#FFF', borderRadius: 0, padding: '14px 18px',
                display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div>
                  <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.10em', textTransform: 'uppercase', opacity: 0.85 }}>
                    Valor pelos múltiplos
                  </div>
                  <div style={{ fontSize: 26, fontWeight: 800, fontVariantNumeric: 'tabular-nums', letterSpacing: '-0.02em' }}>
                    {fmtMi(val.evMultiplos)}
                  </div>
                </div>
                <div style={{ textAlign: 'right', fontSize: 11.5, lineHeight: 1.55, opacity: 0.95 }}>
                  <div>{val.mult}× × receita Ano {val.anoRef}</div>
                  <div><strong>{fmtMi(val.receitaRef)}</strong></div>
                </div>
              </div>
              <div style={{ background: G.verdeLight, borderLeft: `3px solid ${G.verde}`, borderRadius: 0,
                padding: '8px 12px', display: 'flex', flexDirection: 'column' }}>
                <div style={{ fontSize: 10, fontWeight: 800, color: G.verde, letterSpacing: '0.10em', textTransform: 'uppercase', marginBottom: 4 }}>
                  O que são Múltiplos
                </div>
                <div style={{ fontSize: 12, lineHeight: 1.4, color: G.txt1, flex: 1 }}>
                  O mercado precifica empresas parecidas por um múltiplo da receita. Aplicamos esse múltiplo à sua receita para estimar o valor de referência dos comparáveis.
                </div>
                <div style={{ marginTop: 6, textAlign: 'right' }}>
                  <BotaoSaibaMais onClick={() => abrirRGAMSU('multiplos_comparaveis')} />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Bloco 2 · DCF (FCF projetado da T14 + WACC) */}
        <div>
          <div style={HEADER_BLOCO}>
            Bloco 2 · Fluxo de Caixa Descontado (DCF)
            <span style={HEADER_AUX2}>· valor presente do FCF projetado (Tarefa 14) + valor terminal</span>
          </div>
          <div style={caixa}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, maxWidth: 460 }}>
              <div>
                <div style={rotCampo}>WACC (taxa de desconto)</div>
                <CampoNumericoBP60M valor={val.waccPct} onChange={(v) => setVal('val_wacc', v)}
                  sufixo="%" formato="decimal" min={1} max={90} textAlign="right" />
              </div>
              <div>
                <div style={rotCampo}>Crescimento na perpetuidade</div>
                <CampoNumericoBP60M valor={val.gPct} onChange={(v) => setVal('val_g', v)}
                  sufixo="%" formato="decimal" min={0} max={10} textAlign="right" />
              </div>
            </div>
            {/* Card verde do valor + quadro "O que é" (padrão da Valuation) */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginTop: 12, alignItems: 'stretch' }}>
              <div style={{ background: G.verde, color: '#FFF', borderRadius: 0, padding: '14px 18px',
                display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div>
                  <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.10em', textTransform: 'uppercase', opacity: 0.85 }}>
                    Valor pelo DCF
                  </div>
                  <div style={{ fontSize: 26, fontWeight: 800, fontVariantNumeric: 'tabular-nums', letterSpacing: '-0.02em' }}>
                    {fmtMi(val.evDCF)}
                  </div>
                </div>
                <div style={{ textAlign: 'right', fontSize: 11.5, lineHeight: 1.55, opacity: 0.95 }}>
                  <div>FCF descontado: <strong>{fmtMi(val.vpFcf)}</strong> ({fmtPct(val.evDCF ? val.vpFcf / val.evDCF : 0)})</div>
                  <div>Valor Terminal: <strong>{fmtMi(val.vpTerminal)}</strong> ({fmtPct(val.evDCF ? val.vpTerminal / val.evDCF : 0)})</div>
                </div>
              </div>
              <div style={{ background: G.verdeLight, borderLeft: `3px solid ${G.verde}`, borderRadius: 0,
                padding: '8px 12px', display: 'flex', flexDirection: 'column' }}>
                <div style={{ fontSize: 10, fontWeight: 800, color: G.verde, letterSpacing: '0.10em', textTransform: 'uppercase', marginBottom: 4 }}>
                  O que é o DCF
                </div>
                <div style={{ fontSize: 12, lineHeight: 1.4, color: G.txt1, flex: 1 }}>
                  Traz o caixa que a empresa vai gerar nos próximos 5 anos para o valor de hoje, usando o WACC de {val.waccPct.toFixed(1)}% como taxa de desconto. Os fluxos vêm da Tarefa 14.
                </div>
                <div style={{ marginTop: 6, textAlign: 'right' }}>
                  <BotaoSaibaMais onClick={() => abrirRGAMSU('dcf')} />
                </div>
              </div>
            </div>
            {val.evDCF <= 0 && (
              <div style={{ fontSize: 11, color: G.laranja, marginTop: 8, display: 'flex', alignItems: 'flex-start', gap: 6 }}>
                <IconeAlerta /> <span>Com o FCF projetado atual, o DCF não gera valor positivo — a faixa fica ancorada nos múltiplos. É comum quando a empresa ainda queima caixa nos 5 anos.</span>
              </div>
            )}
            {/* Cálculo do DCF · ano a ano · padrão "clique para abrir" */}
            <div style={{ marginTop: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10 }}>
              <span style={{ fontSize: 11.5, fontWeight: 700, color: G.txt2 }}>
                O cálculo do DCF, ano a ano
              </span>
              <button type="button" onClick={() => setDcfTabelaAberta(v => !v)}
                style={{ flexShrink: 0, background: dcfTabelaAberta ? '#FFF' : G.laranjaLight,
                  border: `1px solid ${G.laranja}`, color: G.laranja, padding: '4px 9px',
                  fontSize: 9, fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase',
                  borderRadius: 0, cursor: 'pointer', fontFamily: "'Outfit', sans-serif", whiteSpace: 'nowrap' }}>
                {dcfTabelaAberta ? '▴ Clique para fechar' : '▾ Clique para abrir'}
              </button>
            </div>
            {dcfTabelaAberta && (
              <div style={{ marginTop: 10, background: '#FFF', border: `1px solid ${G.borda}`, borderRadius: 0, padding: '14px 16px' }}>
                <div style={{ fontSize: 12.5, fontWeight: 800, color: G.verde, marginBottom: 10 }}>
                  Como esse número é calculado, passo a passo
                </div>

                {/* 1 · conceito + fórmula */}
                <div style={{ fontSize: 12.5, color: G.txt1, lineHeight: 1.55, marginBottom: 6 }}>
                  <strong>1. O conceito.</strong> O DCF traz cada fluxo de caixa futuro a <strong>valor presente</strong>:
                  um valor recebido no futuro vale menos hoje, pela <strong>taxa de desconto</strong> (o WACC), que reflete
                  custo de oportunidade e risco. Os fluxos (FCF) vêm da projeção da Tarefa 14.
                </div>
                <div style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 11.5, color: G.txt1, background: G.fundo,
                  border: `1px solid ${G.borda}`, borderRadius: 0, padding: '8px 10px', marginBottom: 12 }}>
                  Valor = Σ [ FCF<sub>ano</sub> ÷ (1 + WACC)<sup>ano</sup> ] + Valor Terminal ÷ (1 + WACC)<sup>5</sup>
                </div>

                {/* 2 · fator de desconto */}
                <div style={{ fontSize: 12.5, color: G.txt1, lineHeight: 1.55, marginBottom: 6 }}>
                  <strong>2. O fator de desconto</strong> de cada ano = 1 ÷ (1 + WACC)<sup>ano</sup>. Com WACC de {val.waccPct.toFixed(1)}%:
                </div>
                <div style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 11.5, color: G.txt1, background: G.fundo,
                  border: `1px solid ${G.borda}`, borderRadius: 0, padding: '8px 10px', lineHeight: 1.9, marginBottom: 12 }}>
                  <div>Ano 1 → 1 ÷ (1 + {val.waccPct.toFixed(1)}%)<sup>1</sup> = <strong>{(val.fcfDetalhe[0]?.fator ?? 0).toFixed(4)}</strong></div>
                  <div>Ano 5 → 1 ÷ (1 + {val.waccPct.toFixed(1)}%)<sup>5</sup> = <strong>{(val.fcfDetalhe[4]?.fator ?? 0).toFixed(4)}</strong> <span style={{ color: G.txt2 }}>(desconto maior · anos mais distantes valem menos)</span></div>
                </div>

                {/* 3 · valor presente dos fluxos */}
                <div style={{ fontSize: 12.5, color: G.txt1, lineHeight: 1.55, marginBottom: 6 }}>
                  <strong>3. Valor presente de cada fluxo</strong> (FCF × fator de desconto):
                </div>
                <div style={{ background: '#FFF', border: `1px solid ${G.borda}`, borderRadius: 0, overflow: 'auto', marginBottom: 12 }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: "'Outfit', sans-serif", minWidth: 520 }}>
                    <thead>
                      <tr style={{ background: G.fundo, borderBottom: `1px solid ${G.borda}` }}>
                        {['Ano', 'FCF nominal', 'Fator de desconto', 'FCF a valor presente'].map((h, i) => (
                          <th key={i} style={{ padding: '8px 14px', textAlign: i === 0 ? 'left' : 'right',
                            fontSize: 10, fontWeight: 700, color: G.txt2, letterSpacing: '0.06em', textTransform: 'uppercase' }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {val.fcfDetalhe.map((x, i) => (
                        <tr key={i} style={{ borderTop: i === 0 ? 'none' : `1px solid ${G.borda}` }}>
                          <td style={{ padding: '8px 14px', fontSize: 13, fontWeight: 700, color: G.txt1 }}>{x.ano}</td>
                          <td style={{ padding: '8px 14px', fontSize: 13, color: x.fcf >= 0 ? G.txt1 : G.vermelho, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{fmtMi(x.fcf)}</td>
                          <td style={{ padding: '8px 14px', fontSize: 13, color: G.txt2, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{x.fator.toFixed(4)}</td>
                          <td style={{ padding: '8px 14px', fontSize: 13, color: x.fcfDesc >= 0 ? G.verde : G.vermelho, textAlign: 'right', fontVariantNumeric: 'tabular-nums', fontWeight: 700 }}>{fmtMi(x.fcfDesc)}</td>
                        </tr>
                      ))}
                      <tr style={{ borderTop: `2px solid ${G.borda}`, background: G.verdeLight }}>
                        <td colSpan="3" style={{ padding: '9px 14px', fontSize: 12.5, fontWeight: 800, color: G.txt1 }}>Valor presente dos 5 fluxos</td>
                        <td style={{ padding: '9px 14px', fontSize: 13, color: val.vpFcf >= 0 ? G.verde : G.vermelho, textAlign: 'right', fontVariantNumeric: 'tabular-nums', fontWeight: 800 }}>{fmtMi(val.vpFcf)}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                {/* 4 · valor terminal (Gordon) */}
                <div style={{ fontSize: 12.5, color: G.txt1, lineHeight: 1.55, marginBottom: 6 }}>
                  <strong>4. Valor Terminal.</strong> Após o horizonte de 5 anos, estima-se o valor perpétuo pelo modelo de Gordon
                  (perpetuidade g = {val.gPct.toFixed(0)}%) e traz-se a valor presente:
                </div>
                <div style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 11.5, color: G.txt1, background: G.fundo,
                  border: `1px solid ${G.borda}`, borderRadius: 0, padding: '8px 10px', lineHeight: 1.9, marginBottom: 12 }}>
                  <div style={{ color: G.txt2 }}>= FCF<sub>Ano 5</sub> × (1 + g) ÷ (WACC − g)</div>
                  <div>= {fmtMi(val.fcf5)} × (1 + {val.gPct.toFixed(0)}%) ÷ ({val.waccPct.toFixed(1)}% − {val.gPct.toFixed(0)}%) = <strong>{fmtMi(val.valorTerminal)}</strong></div>
                  <div style={{ color: G.txt2 }}>a valor presente: {fmtMi(val.valorTerminal)} ÷ (1 + {val.waccPct.toFixed(1)}%)<sup>5</sup> = <strong style={{ color: G.txt1 }}>{fmtMi(val.vpTerminal)}</strong></div>
                </div>

                {/* 5 · Enterprise Value */}
                <div style={{ fontSize: 12.5, color: G.txt1, lineHeight: 1.55, background: G.verdeLight, borderRadius: 0, padding: '10px 12px' }}>
                  <strong>5. Valor pelo DCF.</strong> Valor presente dos fluxos ({fmtMi(val.vpFcf)}) + valor presente do Valor Terminal
                  ({fmtMi(val.vpTerminal)}) = <strong style={{ color: G.verde, fontSize: 15 }}>{fmtMi(val.evDCF)}</strong>.
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Bloco 3 · Capital de Risco (VC Method) */}
        <div>
          <div style={HEADER_BLOCO}>
            Bloco 3 · Capital de Risco (VC Method)
            <span style={HEADER_AUX2}>· valor de saída no Ano 5, trazido a hoje pelo retorno do investidor</span>
          </div>
          <div style={caixa}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, maxWidth: 460 }}>
              <div>
                <div style={rotCampo}>Múltiplo de saída (EBITDA)</div>
                <CampoNumericoBP60M valor={val.multSaidaEbitda} onChange={(v) => setVal('val_mult_saida', v)}
                  sufixo="×" formato="decimal" min={0} max={30} textAlign="right" />
              </div>
              <div>
                <div style={rotCampo}>Retorno-alvo do investidor</div>
                <CampoNumericoBP60M valor={val.retornoAlvoPct} onChange={(v) => setVal('val_retorno', v)}
                  sufixo="%" formato="decimal" min={1} max={99} textAlign="right" />
              </div>
            </div>
            {/* Card verde do valor + quadro "O que é" */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginTop: 12, alignItems: 'stretch' }}>
              <div style={{ background: G.verde, color: '#FFF', borderRadius: 0, padding: '14px 18px',
                display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div>
                  <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.10em', textTransform: 'uppercase', opacity: 0.85 }}>
                    Valor pelo Capital de Risco
                  </div>
                  <div style={{ fontSize: 26, fontWeight: 800, fontVariantNumeric: 'tabular-nums', letterSpacing: '-0.02em' }}>
                    {fmtMi(val.evVC)}
                  </div>
                </div>
                <div style={{ textAlign: 'right', fontSize: 11.5, lineHeight: 1.55, opacity: 0.95 }}>
                  <div>Valor de saída (Ano 5): <strong>{fmtMi(val.valorSaida)}</strong></div>
                  <div>Retorno-alvo: <strong>{val.retornoAlvoPct.toFixed(0)}%/ano</strong></div>
                </div>
              </div>
              <div style={{ background: G.verdeLight, borderLeft: `3px solid ${G.verde}`, borderRadius: 0,
                padding: '8px 12px', display: 'flex', flexDirection: 'column' }}>
                <div style={{ fontSize: 10, fontWeight: 800, color: G.verde, letterSpacing: '0.10em', textTransform: 'uppercase', marginBottom: 4 }}>
                  O que é o VC Method
                </div>
                <div style={{ fontSize: 12, lineHeight: 1.4, color: G.txt1, flex: 1 }}>
                  A lógica do investidor de risco: estima o valor de venda da empresa no Ano 5 (múltiplo × EBITDA) e traz para hoje descontando pelo retorno que ele exige.
                </div>
                <div style={{ marginTop: 6, textAlign: 'right' }}>
                  <BotaoSaibaMais onClick={() => abrirRGAMSU('capital_de_risco')} />
                </div>
              </div>
            </div>
            {val.ebitdaAno5 <= 0 && (
              <div style={{ fontSize: 11, color: G.laranja, marginTop: 8, display: 'flex', alignItems: 'flex-start', gap: 6 }}>
                <IconeAlerta /> <span>O EBITDA do Ano 5 ainda não é positivo — sem lucro operacional para multiplicar, este método fica neutro. Reforce a projeção da Tarefa 14.</span>
              </div>
            )}
            {/* Cálculo · clique para abrir */}
            <div style={{ marginTop: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10 }}>
              <span style={{ fontSize: 11.5, fontWeight: 700, color: G.txt2 }}>
                O cálculo do Capital de Risco
              </span>
              <button type="button" onClick={() => setVcContaAberta(v => !v)}
                style={{ flexShrink: 0, background: vcContaAberta ? '#FFF' : G.laranjaLight,
                  border: `1px solid ${G.laranja}`, color: G.laranja, padding: '4px 9px',
                  fontSize: 9, fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase',
                  borderRadius: 0, cursor: 'pointer', fontFamily: "'Outfit', sans-serif", whiteSpace: 'nowrap' }}>
                {vcContaAberta ? '▴ Clique para fechar' : '▾ Clique para abrir'}
              </button>
            </div>
            {vcContaAberta && (
              <div style={{ marginTop: 10, padding: '12px 14px', background: G.fundo, border: `1px solid ${G.borda}`, borderRadius: 0,
                fontFamily: "'IBM Plex Mono', monospace", fontSize: 11.5, color: G.txt1, lineHeight: 1.8 }}>
                <div style={{ color: G.txt2 }}>1. Valor de saída (Ano 5) = múltiplo × EBITDA do Ano 5</div>
                <div>= {val.multSaidaEbitda}× × {fmtMi(val.ebitdaAno5)} = <strong>{fmtMi(val.valorSaida)}</strong></div>
                <div style={{ color: G.txt2, marginTop: 6 }}>2. Trazendo para hoje pelo retorno-alvo de {val.retornoAlvoPct.toFixed(0)}%/ano:</div>
                <div>= {fmtMi(val.valorSaida)} ÷ (1 + {val.retornoAlvoPct.toFixed(0)}%)<sup>5</sup> = <strong style={{ color: G.verde }}>{fmtMi(val.evVC)}</strong></div>
              </div>
            )}
          </div>
        </div>

        {/* Bloco 4 · Quanto vale sua empresa · mesa de negociação */}
        <div>
          <div style={HEADER_BLOCO}>
            Bloco 4 · Quanto vale sua empresa?
            <span style={HEADER_AUX2}>· o número para defender na mesa de negociação</span>
          </div>
          {(() => {
            const pedidoVal = Number(val.evMultiplos) || 0;        // mercado · abertura (teto)
            const pisoVal = Math.max(0, Number(val.evDCF) || 0);   // fundamentos · piso
            const equilibrioVal = Math.max(0, Number(val.evVC) || 0);   // VC Method · equilíbrio
            const temNeg = pedidoVal > 0 && pisoVal > 0 && pedidoVal > pisoVal;
            const margemVal = pedidoVal - pisoVal;
            const margemPct = pisoVal > 0 ? (margemVal / pisoVal) * 100 : 0;
            return (
              <div style={{ background: G.fundo, border: `1px solid ${G.borda}`, borderRadius: 0, padding: '14px 18px' }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: temNeg ? 12 : 0 }}>
                  <div style={{ background: G.laranjaLight, borderLeft: `4px solid ${G.laranja}`, borderRadius: 0, padding: '14px 16px' }}>
                    <div style={{ fontSize: 11, fontWeight: 800, color: '#B45309', letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 6 }}>
                      Valor de Abertura · Pedido
                    </div>
                    <div style={{ fontSize: 30, fontWeight: 900, color: '#B45309', letterSpacing: '-0.02em', lineHeight: 1, marginBottom: 6 }}>
                      {fmtMi(pedidoVal)}
                    </div>
                    <div style={{ fontSize: 12.5, color: G.txt1, lineHeight: 1.4 }}>
                      Base: múltiplos de mercado ({val.mult}× receita). Onde você quer fechar.
                    </div>
                  </div>
                  <div style={{ background: G.verdeLight, borderLeft: `4px solid ${G.verde}`, borderRadius: 0, padding: '14px 16px' }}>
                    <div style={{ fontSize: 11, fontWeight: 800, color: G.verde, letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 6 }}>
                      Piso Defensável · Limite
                    </div>
                    <div style={{ fontSize: 30, fontWeight: 900, color: G.verde, letterSpacing: '-0.02em', lineHeight: 1, marginBottom: 6 }}>
                      {fmtMi(pisoVal)}
                    </div>
                    <div style={{ fontSize: 12.5, color: G.txt1, lineHeight: 1.4 }}>
                      Base: DCF (fundamentos). Abaixo disso, não vende.
                    </div>
                  </div>
                </div>
                {temNeg && (
                  <>
                    <div style={{ marginBottom: 12, padding: '0 4px' }}>
                      <div style={{ position: 'relative', height: 18,
                        background: `linear-gradient(90deg, ${G.verdeLight} 0%, ${G.verdeLight} 33%, #F4A06A 33%, #F4A06A 66%, ${G.laranja} 66%, ${G.laranja} 100%)`,
                        borderRadius: 0 }}>
                        <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
                          fontSize: 16, color: '#FFF', fontWeight: 900, textShadow: '0 1px 2px rgba(0,0,0,0.3)' }}>↔</div>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 4, fontSize: 10.5, fontWeight: 700 }}>
                        <span style={{ color: G.verde }}>Piso · DCF · {fmtMi(pisoVal)}</span>
                        <span style={{ color: G.txt1, fontWeight: 800 }}>Equilíbrio · VC · {fmtMi(equilibrioVal)}</span>
                        <span style={{ color: G.laranja }}>Pedido · múltiplos · {fmtMi(pedidoVal)}</span>
                      </div>
                    </div>
                    <div style={{ background: '#F5F5F1', borderLeft: `3px solid ${G.verde}`, padding: '8px 12px', fontSize: 12.5, lineHeight: 1.5, color: G.txt1 }}>
                      Recomendação <MarcaAIdvisor variant="default" />: abra em <strong>{fmtMi(pedidoVal)}</strong> e ancore no <strong>equilíbrio de {fmtMi(equilibrioVal)}</strong> (o VC Method). Aceite descer apenas até o piso de <strong>{fmtMi(pisoVal)}</strong> · margem total de {fmtMi(margemVal)} (~{margemPct.toFixed(0)}%).
                    </div>
                  </>
                )}
                {!temNeg && (
                  <div style={{ marginTop: 12, background: '#F5F5F1', borderLeft: `3px solid ${G.laranja}`, padding: '8px 12px', fontSize: 12, lineHeight: 1.5, color: G.txt1 }}>
                    Neste cenário os dois métodos ainda não formam uma faixa clara (o DCF não gera valor positivo com o caixa projetado). Use os múltiplos de mercado como referência e reforce o piso conforme o caixa virar.
                  </div>
                )}
              </div>
            );
          })()}

          {/* ASK · captação da rodada · pré-preenchido pela IA e editável */}
          <div style={{ marginTop: 12, background: '#FFF', border: `1px solid ${G.borda}`, borderRadius: 0, padding: '14px 18px' }}>
            <div style={{ fontSize: 11, fontWeight: 800, color: G.txt2, letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 8 }}>
              O ASK · quanto captar {rodadaSelASK ? <>na <strong style={{ color: G.laranja }}>Rodada {rodadaSelASK.titulo}</strong></> : 'nesta rodada'}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
              <CampoNumericoBP60M
                label="Captação pretendida"
                valor={captacaoUsada}
                onChange={(v) => setVal('captacaoSU', v)}
                formato="brl" min={0} largura={170}
              />
              {captacaoEditada && (
                <button type="button" onClick={resetCaptacao}
                  style={{ background: 'none', border: 'none', color: G.laranja, fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: "'Outfit', sans-serif", padding: 0 }}>
                  Voltar à sugestão da IA
                </button>
              )}
            </div>
            <div style={{ marginTop: 10, background: '#F5F5F1', borderLeft: `3px solid ${G.verde}`, padding: '8px 12px', fontSize: 12.5, lineHeight: 1.5, color: G.txt1 }}>
              {captacaoUsada && equityPctASK
                ? <><MarcaAIdvisor variant="default" />: captar <strong>{fmtMi(captacaoUsada)}</strong>{rodadaSelASK && <> na <strong>Rodada {rodadaSelASK.titulo}</strong></>} equivale a ceder aproximadamente <strong>{String(equityPctASK).replace('.', ',')}%</strong> de equity{preMoneyASK ? <> (pré-money de {fmtMi(preMoneyASK)})</> : null}. {captacaoEditada ? 'Valor ajustado por você.' : 'Estimado pela queima de caixa até o breakeven · ajuste se quiser.'}</>
                : <><MarcaAIdvisor variant="default" />: defina a captação para calcular a fatia de equity{!rodadaSelASK && <> · escolha a rodada na Tarefa 1</>}.</>}
            </div>
          </div>
        </div>

        {/* Bloco 5 · Embasamento · defesa do valor */}
        <div>
          <div style={HEADER_BLOCO}>
            Bloco 5 · Embasamento · defesa do valor
            <span style={HEADER_AUX2}>· os pilares que sustentam o número</span>
          </div>
          <div style={{ background: G.fundo, border: `1px solid ${G.borda}`, borderRadius: 0, padding: '14px 18px' }}>
            {(() => {
              const rA1 = Number(anosSU[0]?.receita) || 0;
              const rA5 = Number(anosSU[anosSU.length - 1]?.receita) || 0;
              const cagr = (rA1 > 0 && rA5 > 0) ? (Math.pow(rA5 / rA1, 1 / 4) - 1) * 100 : 0;
              const clamp = (x) => Math.max(0, Math.min(100, x));
              const pilares = [
                { n: 1, label: 'Múltiplo de receita', valor: `${val.mult}× receita`,
                  regua: { min: '1×', max: '10×', pct: clamp((val.mult - 1) / 9 * 100) }, fonte: 'Comparáveis de mercado' },
                { n: 2, label: 'Taxa de desconto (WACC)', valor: `${val.waccPct.toFixed(1)}%`,
                  regua: { min: '8%', max: '22%', pct: clamp((val.waccPct - 8) / 14 * 100) }, fonte: 'Estrutura de capital' },
                { n: 3, label: 'Retorno-alvo do investidor', valor: `${val.retornoAlvoPct.toFixed(0)}%/ano`,
                  regua: { min: '20%', max: '60%', pct: clamp((val.retornoAlvoPct - 20) / 40 * 100) }, fonte: 'Padrão de venture' },
                { n: 4, label: 'Crescimento da receita', valor: `${cagr.toFixed(0)}%/ano`,
                  defesa: 'Ancorado na Meta da Tarefa 1 · curva decrescente, mais realista que taxa constante.', fonte: 'Tarefa 1 · Meta' },
              ];
              return pilares.map((p, i, arr) => (
                <div key={p.n} style={{ display: 'grid', gridTemplateColumns: '1.8fr 2.7fr 1.6fr', gap: 12,
                  padding: '8px 0', borderBottom: i < arr.length - 1 ? `1px solid ${G.borda}` : 'none', alignItems: 'center' }}>
                  <div style={{ fontSize: 13, color: G.txt1 }}>
                    <span style={{ color: G.txt2, fontWeight: 600 }}>{p.n} · </span>
                    {p.label} = <strong style={{ color: G.verde, fontSize: 14 }}>{p.valor}</strong>
                  </div>
                  <div>
                    {p.regua ? (
                      <>
                        <div style={{ position: 'relative', height: 10,
                          background: `linear-gradient(90deg, #B6D8BB 0%, #B6D8BB 30%, ${G.verde} 30%, ${G.verde} 70%, #B6D8BB 70%, #B6D8BB 100%)`, borderRadius: 0 }}>
                          <div style={{ position: 'absolute', left: `calc(${p.regua.pct}% - 2.5px)`, top: -3, width: 5, height: 16,
                            background: G.laranja, borderRadius: 0, boxShadow: '0 0 0 1.5px #FFF' }}/>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 3, fontSize: 10, color: G.txt2, fontWeight: 600 }}>
                          <span>{p.regua.min}</span>
                          <span style={{ color: G.verde, fontWeight: 700 }}>centro do mercado</span>
                          <span>{p.regua.max}</span>
                        </div>
                      </>
                    ) : (
                      <div style={{ fontSize: 12.5, color: G.txt1, lineHeight: 1.45 }}>{p.defesa}</div>
                    )}
                  </div>
                  <div style={{ fontSize: 10.5, color: G.txt2, textAlign: 'right', fontWeight: 600 }}>
                    fonte: {p.fonte}
                  </div>
                </div>
              ));
            })()}
          </div>
        </div>

        {/* Bloco 6 · Antecipando objeções */}
        <div>
          <div style={HEADER_BLOCO}>
            Bloco 6 · Antecipando objeções
            <span style={HEADER_AUX2}>· o que o investidor vai perguntar · resposta pronta</span>
          </div>
          <div style={{ background: G.fundo, border: `1px solid ${G.borda}`, borderRadius: 0, padding: '14px 18px', display: 'flex', flexDirection: 'column', gap: 8 }}>
            {[
              { p: 'E se o crescimento não vier como projetado?',
                r: <>A projeção é uma hipótese — por isso trabalhamos com uma <strong>faixa</strong> (piso pelo DCF · teto pelos múltiplos) e o equilíbrio do VC Method. Se o crescimento decepcionar, o valor tende ao <strong>piso</strong>.</> },
              { p: `Por que um múltiplo de ${val.mult}× a receita?`,
                r: <>É a <strong>referência de mercado</strong> para empresas comparáveis do seu setor — e é editável: ajuste conforme os comparáveis que você conseguir levantar.</> },
              { p: 'Por que o valor pelo DCF ficou baixo?',
                r: <>Nos primeiros anos a empresa <strong>investe e queima caixa</strong>, o que derruba o fluxo descontado. É esperado em startup — por isso o DCF é o piso, e o múltiplo e o VC Method equilibram a faixa.</> },
            ].map((obj, i) => (
              <div key={i} style={{ background: '#FFF', border: `1px solid ${G.borda}`, borderRadius: 0, padding: '10px 14px' }}>
                <div style={{ fontSize: 12.5, fontStyle: 'italic', color: G.txt2, marginBottom: 5 }}>"{obj.p}"</div>
                <div style={{ fontSize: 12.5, color: G.txt1, lineHeight: 1.5 }}>{obj.r}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  const BlocoChipWizard = ({ titulo, sub, campo, valorAtual, opcoes, rgamTermo, rgamTermoId, rgamOQueE }) => {
    const abrirRGAM = () => {
      localStorage.setItem('rgam:termoInicial', rgamTermoId);
      localStorage.setItem('rgam:origem', JSON.stringify({
        tela: 'businessplan',
        subTela: 'tarefa-14',
        label: 'Tarefa 14 · Valuation DCF',
      }));
      window.dispatchEvent(new CustomEvent('rg-navegar', { detail: { tela: 'rgam' } }));
    };
    return (
      <div>
        <div style={HEADER_BLOCO}>
          {titulo}
          <span style={HEADER_AUX}>· {sub}</span>
        </div>
        <div style={{ background: G.fundo, border: `1px solid ${G.borda}`,
          borderRadius: 0, padding: '10px 14px',
          display: rgamOQueE ? 'grid' : 'block',
          gridTemplateColumns: rgamOQueE ? '1fr 1fr' : undefined,
          gap: rgamOQueE ? 14 : undefined,
          alignItems: rgamOQueE ? 'stretch' : undefined }}>
          <div>
            <div style={{ fontSize: 10, fontWeight: 800, color: G.txt2,
              letterSpacing: '0.10em', textTransform: 'uppercase', marginBottom: 8 }}>
              Sugestões da IA · selecione uma classe estratégica
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {opcoes.map(o => {
                const ativo = valorAtual === o.id;
                return (
                  <button key={o.id} type="button"
                    onClick={() => atualizar(campo, o.id)}
                    title={o.desc}
                    style={{
                      padding: '8px 12px', fontSize: 12, fontWeight: 600,
                      background: ativo ? G.verde : '#FFF',
                      color: ativo ? '#FFF' : G.txt1,
                      border: `1px solid ${ativo ? G.verde : G.borda}`,
                      borderRadius: 0, cursor: 'pointer',
                      fontFamily: "'Outfit', sans-serif", textAlign: 'left',
                    }}>
                    {ativo ? '✓ ' : ''}<strong>{o.label}</strong>{o.faixa ? ` · ${o.faixa}` : ''}
                  </button>
                );
              })}
            </div>
            {valorAtual && (
              <div style={{ marginTop: 8, fontSize: 12, color: G.txt2, fontStyle: 'italic',
                lineHeight: 1.4 }}>
                {opcoes.find(o => o.id === valorAtual)?.desc}
              </div>
            )}
          </div>
          {rgamOQueE && (
            <div style={{ background: G.verdeLight, borderLeft: `3px solid ${G.verde}`,
              borderRadius: 0, padding: '8px 12px',
              display: 'flex', flexDirection: 'column' }}>
              <div style={{ fontSize: 10, fontWeight: 800, color: G.verde,
                letterSpacing: '0.10em', textTransform: 'uppercase', marginBottom: 4 }}>
                O que é {rgamTermo}
              </div>
              <div style={{ fontSize: 12, lineHeight: 1.4, color: G.txt1, flex: 1 }}>
                {rgamOQueE}
              </div>
              {rgamTermoId && (
                <div style={{ marginTop: 6, textAlign: 'right' }}>
                  <BotaoSaibaMais onClick={abrirRGAM} />
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <div style={{ padding: '12px 16px', background: G.verdeLight,
          borderLeft: `4px solid ${G.verde}`, borderRadius: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 800, color: G.verde,
            letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 6 }}>
            O que é Valuation DCF
          </div>
          <div style={{ fontSize: 12, lineHeight: 1.45, color: G.txt1 }}>
            DCF (<em>Discounted Cash Flow</em>) calcula o valor da empresa hoje
            trazendo os <strong>fluxos futuros de caixa</strong> a valor presente
            por uma <strong>taxa de retorno exigida</strong> (WACC). Inclui um
            <strong> Valor Terminal</strong> para representar todos os anos
            depois do Ano 5.
          </div>
        </div>
        <div style={{ padding: '12px 16px', background: G.laranjaLight,
          borderLeft: `4px solid ${G.laranja}`, borderRadius: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 800, color: G.laranja,
            letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 6 }}>
            O que você faz aqui
          </div>
          <div style={{ fontSize: 12, lineHeight: 1.45, color: G.txt1 }}>
            <strong>Selecione</strong> as classes estratégicas para WACC, crescimento
            perpétuo e método de Valor Terminal. O <strong>Enterprise Value</strong>,
            a sensibilidade e o comparativo de métodos são calculados
            automaticamente a partir das suas projeções.
          </div>
        </div>
      </div>

      <BlocoChipWizard
        titulo="Bloco 1 · Taxa de retorno exigida (WACC)"
        sub="custo de capital ponderado · base do desconto"
        campo="wacc_id"
        valorAtual={wacc_id}
        opcoes={WACC_INTERVALOS}
        rgamTermo="WACC"
        rgamTermoId="wacc"
        rgamOQueE="Taxa que pondera o custo do dinheiro próprio (equity) e do dinheiro de terceiros (dívida) na proporção em que cada um financia a empresa. Referência mínima de retorno para qualquer decisão de investir."/>

      <BlocoChipWizard
        titulo="Bloco 2 · Crescimento na perpetuidade (g)"
        sub="ritmo de crescimento após o Ano 5 · usado no Valor Terminal"
        campo="g_id"
        valorAtual={g_id}
        opcoes={G_PERPETUIDADE}
        rgamTermo="G · Perpetuidade"
        rgamTermoId="g_perpetuidade"
        rgamOQueE="Taxa anual de crescimento perpétuo aplicada aos fluxos de caixa após o horizonte de projeção. Entra na fórmula do Valor Terminal (modelo de Gordon), para estimar o valor de todos os anos seguintes ao Ano 5."/>

      <BlocoChipWizard
        titulo="Bloco 3 · Método de Valor Terminal"
        sub="como avaliar a empresa depois do Ano 5"
        campo="metodo_id"
        valorAtual={metodo_id}
        opcoes={METODOS_VT}
        rgamTermo="Valor Terminal"
        rgamTermoId="valor_terminal"
        rgamOQueE="Valor presente de todos os fluxos de caixa que a empresa gera depois do horizonte de projeção explícita (geralmente após o Ano 5). Condensa todo o futuro num único valor calculado no Ano 5, que é então trazido a valor presente."/>

      {/* B4 · Múltiplo setorial — visível só se método ≠ gordon */}
      {metodo_id !== 'gordon' && (
        <div>
          <div style={HEADER_BLOCO}>
            Bloco 4 · Múltiplo de saída (EBITDA)
            <span style={HEADER_AUX}>· referência setorial Damodaran/ABVCAP</span>
          </div>
          <div style={{ background: G.fundo, border: `1px solid ${G.borda}`,
            borderRadius: 0, padding: '10px 14px',
            display: 'grid', gridTemplateColumns: '1fr 1fr',
            gap: 14, alignItems: 'stretch' }}>
            <div>
              <div style={{ fontSize: 10, fontWeight: 800, color: G.txt2,
                letterSpacing: '0.10em', textTransform: 'uppercase', marginBottom: 8 }}>
                Sugestão da IA por setor
              </div>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 12,
                flexWrap: 'wrap' }}>
                {[refSetor?.multiplo - 1, refSetor?.multiplo, refSetor?.multiplo + 1, refSetor?.multiplo + 2]
                  .filter(n => n > 0)
                  .map(n => {
                    const ativo = multiplo === n;
                    return (
                      <button key={n} type="button"
                        onClick={() => atualizar('multiplo', n)}
                        style={{
                          padding: '8px 14px', fontSize: 13, fontWeight: 700,
                          background: ativo ? G.verde : '#FFF',
                          color: ativo ? '#FFF' : G.txt1,
                          border: `1px solid ${ativo ? G.verde : G.borda}`,
                          borderRadius: 0, cursor: 'pointer',
                          fontFamily: "'Outfit', sans-serif",
                        }}>
                        {ativo ? '✓ ' : ''}{n}× EBITDA
                      </button>
                    );
                  })}
              </div>
              {refSetor && (
                <div style={{ marginTop: 8, fontSize: 12, color: G.txt2,
                  fontStyle: 'italic', lineHeight: 1.4 }}>
                  Setor: <strong>{refSetor.label}</strong> · faixa típica {refSetor.faixa} · {refSetor.desc}
                </div>
              )}
            </div>
            <div style={{ background: G.verdeLight, borderLeft: `3px solid ${G.verde}`,
              borderRadius: 0, padding: '8px 12px',
              display: 'flex', flexDirection: 'column' }}>
              <div style={{ fontSize: 10, fontWeight: 800, color: G.verde,
                letterSpacing: '0.10em', textTransform: 'uppercase', marginBottom: 4 }}>
                O que é EBITDA
              </div>
              <div style={{ fontSize: 12, lineHeight: 1.4, color: G.txt1, flex: 1 }}>
                Lucro antes de juros, impostos, depreciação e amortização. Isola a geração de caixa operacional, retirando do Lucro Líquido itens que não refletem operação. É a moeda do múltiplo EV/EBITDA — padrão de mercado em M&A.
              </div>
              <div style={{ marginTop: 6, textAlign: 'right' }}>
                <BotaoSaibaMais onClick={() => {
                    localStorage.setItem('rgam:termoInicial', 'ebitda');
                    localStorage.setItem('rgam:origem', JSON.stringify({
                      tela: 'businessplan', subTela: 'tarefa-14',
                      label: 'Tarefa 14 · Valuation DCF',
                    }));
                    window.dispatchEvent(new CustomEvent('rg-navegar', { detail: { tela: 'rgam' } }));
                  }} />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* B5 · Resultado calculado */}
      <div>
        <div style={HEADER_BLOCO}>
          Bloco {metodo_id === 'gordon' ? '4' : '5'} · Valuation final
          <span style={HEADER_AUX}>· Enterprise Value calculado a partir das premissas</span>
        </div>

        {/* Card EV em destaque + quadro O QUE É lateral · grid 2 colunas */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr',
          gap: 14, marginBottom: 12, alignItems: 'stretch' }}>
          <div style={{ background: G.verde, color: '#FFF',
            borderRadius: 0, padding: '14px 18px',
            display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div>
              <div style={{ fontSize: 11, fontWeight: 700,
                letterSpacing: '0.10em', textTransform: 'uppercase', opacity: 0.85 }}>
                Enterprise Value
              </div>
              <div style={{ fontSize: 26, fontWeight: 800,
                fontVariantNumeric: 'tabular-nums', letterSpacing: '-0.02em' }}>
                {fmtMi(v.ev)}
              </div>
            </div>
            <div style={{ textAlign: 'right', fontSize: 11.5, lineHeight: 1.55,
              opacity: 0.95 }}>
              <div>FCF descontado: <strong>{fmtMi(v.fcfDescTotal)}</strong> ({fmtPct(v.pctFCF)})</div>
              <div>Valor Terminal: <strong>{fmtMi(v.vtDescontado)}</strong> ({fmtPct(v.pctVT)})</div>
            </div>
          </div>
          <div style={{ background: G.verdeLight, borderLeft: `3px solid ${G.verde}`,
            borderRadius: 0, padding: '8px 12px',
            display: 'flex', flexDirection: 'column' }}>
            <div style={{ fontSize: 10, fontWeight: 800, color: G.verde,
              letterSpacing: '0.10em', textTransform: 'uppercase', marginBottom: 4 }}>
              O que é Enterprise Value
            </div>
            <div style={{ fontSize: 12, lineHeight: 1.4, color: G.txt1, flex: 1 }}>
              Valor da empresa para todos os provedores de capital (sócios e credores). Numa avaliação DCF, é a soma dos fluxos de caixa descontados ao longo do horizonte de projeção mais o Valor Terminal trazido a valor presente. É o número de referência negociado em M&A.
            </div>
            <div style={{ marginTop: 6, textAlign: 'right' }}>
              <BotaoSaibaMais onClick={() => {
                  localStorage.setItem('rgam:termoInicial', 'enterprise_value');
                  localStorage.setItem('rgam:origem', JSON.stringify({
                    tela: 'businessplan', subTela: 'tarefa-14',
                    label: 'Tarefa 14 · Valuation DCF',
                  }));
                  window.dispatchEvent(new CustomEvent('rg-navegar', { detail: { tela: 'rgam' } }));
                }} />
            </div>
          </div>
        </div>

        {/* Tabela DCF */}
        <div style={{ background: '#FFF', border: `1px solid ${G.borda}`,
          borderRadius: 0, overflow: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse',
            fontFamily: "'Outfit', sans-serif", minWidth: 560 }}>
            <thead>
              <tr style={{ background: G.fundo, borderBottom: `1px solid ${G.borda}` }}>
                {['Período','FCF nominal','Fator desc.','FCF descontado'].map((h, i) => (
                  <th key={i} style={{ padding: '8px 14px',
                    textAlign: i === 0 ? 'left' : 'right',
                    fontSize: 10, fontWeight: 700, color: G.txt2,
                    letterSpacing: '0.06em', textTransform: 'uppercase' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {v.fcfDescontado.map((x, i) => (
                <tr key={i} style={{ borderTop: i === 0 ? 'none' : `1px solid ${G.borda}` }}>
                  <td style={{ padding: '8px 14px', fontSize: 13, fontWeight: 700, color: G.txt1 }}>{x.ano}</td>
                  <td style={{ padding: '8px 14px', fontSize: 13, color: G.txt1,
                    textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{fmtMi(x.fcf)}</td>
                  <td style={{ padding: '8px 14px', fontSize: 13, color: G.txt2,
                    textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{x.fator.toFixed(4)}</td>
                  <td style={{ padding: '8px 14px', fontSize: 13, color: G.verde,
                    textAlign: 'right', fontVariantNumeric: 'tabular-nums', fontWeight: 700 }}>{fmtMi(x.fcfDesc)}</td>
                </tr>
              ))}
              <tr style={{ borderTop: `1px solid ${G.borda}`, background: '#FFFBEB' }}>
                <td style={{ padding: '8px 14px', fontSize: 13, fontWeight: 700, color: G.txt1 }}>Valor Terminal</td>
                <td style={{ padding: '8px 14px', fontSize: 13, color: G.txt1,
                  textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{fmtMi(v.vt)}</td>
                <td style={{ padding: '8px 14px', fontSize: 13, color: G.txt2,
                  textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{v.fatorVT.toFixed(4)}</td>
                <td style={{ padding: '8px 14px', fontSize: 13, color: G.verde,
                  textAlign: 'right', fontVariantNumeric: 'tabular-nums', fontWeight: 700 }}>{fmtMi(v.vtDescontado)}</td>
              </tr>
              <tr style={{ borderTop: `2px solid ${G.verde}`, background: G.verdeLight }}>
                <td style={{ padding: '10px 14px', fontSize: 13, fontWeight: 800, color: G.verde }}>Enterprise Value</td>
                <td colSpan="3" style={{ padding: '10px 14px', fontSize: 15, color: G.verde,
                  textAlign: 'right', fontVariantNumeric: 'tabular-nums', fontWeight: 800 }}>{fmtMi(v.ev)}</td>
              </tr>
            </tbody>
          </table>
        </div>

      </div>

      {/* BLOCO 6 · Quanto vale sua empresa? */}
      <div>
        <div style={HEADER_BLOCO}>
          Bloco {metodo_id === 'gordon' ? '5' : '6'} · Quanto vale sua empresa?
          <span style={HEADER_AUX}>· o número para defender na mesa de negociação</span>
        </div>
        <div style={{ background: G.fundo, border: `1px solid ${G.borda}`,
          borderRadius: 0, padding: '14px 18px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr',
            gap: 14, marginBottom: 12 }}>
            <div style={{ background: G.laranjaLight,
              borderLeft: `4px solid ${G.laranja}`,
              borderRadius: 0, padding: '14px 16px' }}>
              <div style={{ fontSize: 11, fontWeight: 800, color: '#B45309',
                letterSpacing: '0.08em', textTransform: 'uppercase',
                marginBottom: 6 }}>
                Valor de Abertura · Pedido
              </div>
              <div style={{ fontSize: 32, fontWeight: 900, color: '#B45309',
                letterSpacing: '-0.02em', lineHeight: 1, marginBottom: 6 }}>
                {fmtMi((v.evPorMetodo?.multiplo || v.ev * 1.17))}
              </div>
              <div style={{ fontSize: 12.5, color: G.txt1, lineHeight: 1.4 }}>
                Base: múltiplo setorial {multiplo || refSetor?.multiplo || 7}× EBITDA. Onde você quer fechar.
              </div>
            </div>
            <div style={{ background: G.verdeLight,
              borderLeft: `4px solid ${G.verde}`,
              borderRadius: 0, padding: '14px 16px' }}>
              <div style={{ fontSize: 11, fontWeight: 800, color: G.verde,
                letterSpacing: '0.08em', textTransform: 'uppercase',
                marginBottom: 6 }}>
                Piso Defensável · Limite
              </div>
              <div style={{ fontSize: 32, fontWeight: 900, color: G.verde,
                letterSpacing: '-0.02em', lineHeight: 1, marginBottom: 6 }}>
                {fmtMi(v.ev)}
              </div>
              <div style={{ fontSize: 12.5, color: G.txt1, lineHeight: 1.4 }}>
                Base: DCF central. Abaixo disso, não vende.
              </div>
            </div>
          </div>
          {/* Régua da zona de negociação · degradê laranja em 3 trechos */}
          <div style={{ marginBottom: 12, padding: '0 4px' }}>
            <div style={{ position: 'relative', height: 18,
              background: `linear-gradient(90deg, ${G.laranjaLight} 0%, ${G.laranjaLight} 33%, #F4A06A 33%, #F4A06A 66%, ${G.laranja} 66%, ${G.laranja} 100%)`,
              borderRadius: 0 }}>
              {/* Marcador esquerdo · Piso */}
              <div style={{ position: 'absolute', left: -2, top: -4,
                width: 5, height: 26, background: G.laranjaLight,
                border: `2px solid ${G.laranja}`,
                borderRadius: 0, boxShadow: '0 0 0 1px #FFF' }}/>
              {/* Marcador direito · Pedido */}
              <div style={{ position: 'absolute', right: -2, top: -4,
                width: 5, height: 26, background: G.laranja,
                borderRadius: 0, boxShadow: '0 0 0 2px #FFF' }}/>
              {/* Seta dupla · centro */}
              <div style={{ position: 'absolute', top: '50%', left: '50%',
                transform: 'translate(-50%, -50%)', fontSize: 16,
                color: '#FFF', fontWeight: 900,
                textShadow: '0 1px 2px rgba(0,0,0,0.3)' }}>
                ↔
              </div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between',
              marginTop: 4, fontSize: 10.5, fontWeight: 700, color: G.txt2,
              letterSpacing: '0.06em', textTransform: 'uppercase' }}>
              <span style={{ color: G.verde }}>Piso · {fmtMi(v.ev)}</span>
              <span style={{ color: G.txt1, fontWeight: 800 }}>
                margem · {fmtMi(((v.evPorMetodo?.multiplo || v.ev * 1.17)) - v.ev)} (~14%)
              </span>
              <span style={{ color: G.laranja }}>Pedido · {fmtMi((v.evPorMetodo?.multiplo || v.ev * 1.17))}</span>
            </div>
          </div>
          <div style={{ background: '#F5F5F1',
            borderLeft: `3px solid ${G.verde}`,
            padding: '8px 12px', fontSize: 12.5, lineHeight: 1.5, color: G.txt1 }}>
            Recomendação{' '}
            <MarcaAIdvisor variant="default" />
            : abra em <strong>{fmtMi((v.evPorMetodo?.multiplo || v.ev * 1.17))}</strong>. Margem de negociação de{' '}
            <strong>{fmtMi(((v.evPorMetodo?.multiplo || v.ev * 1.17)) - v.ev)}</strong> (~14%) · aceite descer apenas até o piso.
          </div>
        </div>
      </div>

      {/* BLOCO 7 · Embasamento · defesa do valor */}
      <div>
        <div style={HEADER_BLOCO}>
          Bloco {metodo_id === 'gordon' ? '6' : '7'} · Embasamento · defesa do valor
          <span style={HEADER_AUX}>· 4 pilares que sustentam o número</span>
        </div>
        <div style={{ background: G.fundo, border: `1px solid ${G.borda}`,
          borderRadius: 0, padding: '14px 18px' }}>
          {[
            { n: 1, label: 'WACC',                    valor: `${(v.wacc * 100).toFixed(2)}%`,
              regua: { min: 8, max: 22, pct: ((v.wacc * 100) - 8) / 14 * 100, labelMin: '8%', labelMax: '22%' },
              defesa: 'Dentro da faixa típica de PME brasileira em crescimento (10% a 18%).',
              fonte: 'Damodaran' },
            { n: 2, label: 'Crescimento perpétuo (g)', valor: `${(v.g * 100).toFixed(2)}%`,
              regua: { min: 0, max: 6, pct: (v.g * 100) / 6 * 100, labelMin: '0%', labelMax: '6%' },
              defesa: 'Alinhado com a meta de inflação do BCB · postura conservadora.',
              fonte: 'BCB Focus' },
            { n: 3, label: 'Múltiplo setorial',       valor: `${multiplo || refSetor?.multiplo || 7}× EBITDA`,
              defesa: 'Dentro da faixa ABVCAP para o seu setor · levemente acima por margem superior.',
              fonte: 'ABVCAP' },
            { n: 4, label: 'Geração de caixa (FCF)',  valor: `${fmtMi(v.fcfDescontado?.length > 0 ? v.fcfDescontado.reduce((s,x)=>s+x.fcf,0)/v.fcfDescontado.length : 2500000)}/ano`,
              defesa: 'Confirmado pelo seu DRE e DFC dos últimos 12 meses.',
              fonte: 'Histórico da empresa' },
          ].map((p, i, arr) => (
            <div key={p.n} style={{ display: 'grid',
              gridTemplateColumns: '32% 53% 15%', gap: 12,
              padding: '8px 0',
              borderBottom: i < arr.length - 1 ? `1px solid ${G.borda}` : 'none',
              alignItems: 'center' }}>
              <div style={{ fontSize: 13, color: G.txt1 }}>
                <span style={{ color: G.txt2, fontWeight: 600 }}>{p.n} · </span>
                {p.label} ={' '}
                <strong style={{ color: G.verde, fontSize: 14 }}>{p.valor}</strong>
              </div>
              <div>
                {p.regua ? (
                  <>
                    <div style={{ position: 'relative', height: 10,
                      background: `linear-gradient(90deg, #B6D8BB 0%, #B6D8BB 30%, ${G.verde} 30%, ${G.verde} 70%, #B6D8BB 70%, #B6D8BB 100%)`,
                      borderRadius: 0 }}>
                      {/* marker (laranja vivo · constraste forte) */}
                      <div style={{ position: 'absolute',
                        left: `calc(${Math.max(0, Math.min(100, p.regua.pct))}% - 2.5px)`,
                        top: -3, width: 5, height: 16,
                        background: G.laranja, borderRadius: 0,
                        boxShadow: '0 0 0 1.5px #FFF' }}/>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between',
                      marginTop: 3, fontSize: 10, color: G.txt2,
                      fontWeight: 600 }}>
                      <span>{p.regua.labelMin}</span>
                      <span style={{ color: G.verde, fontWeight: 700 }}>centro do mercado</span>
                      <span>{p.regua.labelMax}</span>
                    </div>
                  </>
                ) : (
                  <div style={{ fontSize: 12.5, color: G.txt1, lineHeight: 1.45 }}>
                    {p.defesa}
                  </div>
                )}
              </div>
              <div style={{ fontSize: 10.5, color: G.txt2,
                letterSpacing: '0.08em', textTransform: 'uppercase',
                textAlign: 'right', fontWeight: 600 }}>
                fonte: {p.fonte}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* BLOCO 8 · Antecipando objeções */}
      <div>
        <div style={HEADER_BLOCO}>
          Bloco {metodo_id === 'gordon' ? '7' : '8'} · Antecipando objeções
          <span style={HEADER_AUX}>· perguntas que o comprador, banco ou sócio vai fazer · resposta pronta</span>
        </div>
        <div style={{ background: G.fundo, border: `1px solid ${G.borda}`,
          borderRadius: 0, padding: '14px 18px',
          display: 'flex', flexDirection: 'column', gap: 8 }}>
          {[
            { p: 'E se a Selic subir 2 pontos?',
              r: (<>WACC sobe para 16%. Valuation cai de <strong style={{ color: G.verde }}>{fmtMi(v.ev)}</strong> para <strong style={{ color: G.verde }}>{fmtMi(v.ev * 0.875)}</strong> (−12,5%). Ainda na faixa razoável.</>) },
            { p: 'E se o crescimento perpétuo (g) for apenas 2%?',
              r: (<>Valor Terminal cai. Valuation cai para <strong style={{ color: G.verde }}>{fmtMi(v.ev * 0.92)}</strong> (−8%). Mostra que o número não depende de hipótese agressiva.</>) },
            { p: 'Por que não usar só múltiplo de EBITDA?',
              r: (<>Múltiplo isolado tem viés setorial e depende de comparáveis recentes. Combinar <strong style={{ color: G.verde }}>DCF + múltiplo</strong> (triangulação) traz duas lentes independentes apontando para a mesma região de valor.</>) },
          ].map((obj, i) => (
            <div key={i} style={{ background: '#FFF',
              border: `1px solid ${G.borda}`, borderRadius: 0,
              padding: '10px 14px' }}>
              <div style={{ fontSize: 12.5, fontStyle: 'italic',
                color: G.txt2, marginBottom: 5 }}>
                "{obj.p}"
              </div>
              <div style={{ fontSize: 12.5, color: G.txt1, lineHeight: 1.5 }}>
                {obj.r}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════
// ConteudoTarefa15 · Conclusão · confirma decisões de T1 + síntese RUNGOAL AIDVISOR
// ═══════════════════════════════════════════════════════════════════════
// PitchDeck · template 10 slides Guy Kawasaki populado com dados do BP60M
// =====================================================================
// Pitch Deck — visual editorial · estrutura Guy Kawasaki 10 slides
// =====================================================================
// Utilitário compartilhado · gera PDF a partir de seletor de páginas (section)
// Cada section é renderizada via html2canvas e adicionada como página A4 do jsPDF.
// Páginas Pitch Deck (1280×720 landscape) e A4 (800×1131 portrait) ambos suportados.
const baixarPdfDe = async (seletor, nomeArquivo, orientacao = 'p') => {
  const html2canvas = (await import('html2canvas')).default;
  const { jsPDF } = await import('jspdf');
  // Garante fontes carregadas + tempo de render antes de capturar
  if (document.fonts && document.fonts.ready) {
    try { await document.fonts.ready; } catch (_) { /* ignore */ }
  }
  await new Promise(r => setTimeout(r, 400));
  const paginas = document.querySelectorAll(seletor);
  if (!paginas.length) return;
  const pdf = new jsPDF({ orientation: orientacao, unit: 'pt',
    format: orientacao === 'p' ? [595, 842] : [842, 595] });
  for (let i = 0; i < paginas.length; i++) {
    const canvas = await html2canvas(paginas[i], { scale: 2, useCORS: true,
      backgroundColor: '#F4EDE0', logging: false,
      windowWidth: paginas[i].scrollWidth,
      windowHeight: paginas[i].scrollHeight });
    const imgData = canvas.toDataURL('image/jpeg', 0.92);
    const pageW = pdf.internal.pageSize.getWidth();
    const pageH = pdf.internal.pageSize.getHeight();
    if (i > 0) pdf.addPage();
    pdf.addImage(imgData, 'JPEG', 0, 0, pageW, pageH);
  }
  pdf.save(nomeArquivo);
};

// =====================================================================
// Pitch Deck · Sistema de 3 Temas
// 1) sequoia       — tema original (Fraunces · Outfit · paleta verde/laranja/cream)
// 2) ardosia       — Base A do handoff Claude Design (Newsreader · Schibsted Grotesk · IBM Plex Mono · paleta neutra paper/ink + accent azul-ardósia)
// 3) laranja-verde — Base B do handoff (mesma família tipográfica · accent laranja + accent-2 verde para sinais de crescimento)
// =====================================================================
const DECK_TEMAS = {
  sequoia: {
    id: 'sequoia',
    nome: 'Editorial · Sequoia',
    descricao: 'Padrão narrativo Sequoia memo · Fraunces serif · paleta cream + verde + laranja · marca RUNGOAL AIDVISOR fixa em todos os slides.',
    permiteCarimbo: false,
    slides: 10,
  },
  ardosia: {
    id: 'ardosia',
    nome: 'Ardósia · Neutro',
    descricao: 'Padrão institucional neutro · Newsreader serif · paleta paper/ink · acento azul-ardósia parametrizável.',
    permiteCarimbo: true,
    slides: 11,
    serif: "'Newsreader', Georgia, serif",
    sans: "'Schibsted Grotesk', system-ui, sans-serif",
    mono: "'IBM Plex Mono', monospace",
    ink: '#1D1B17', paper: '#F5F2EA', paper2: '#EBE6DA',
    card: '#FBF9F3', muted: '#6F695D', faint: '#A39C8D', line: '#DCD5C6',
    accentDefault: '#2F4858', accent2Default: '#2F4858',
  },
  'laranja-verde': {
    id: 'laranja-verde',
    nome: 'Laranja & Verde',
    descricao: 'Padrão dual · Newsreader serif · laranja estrutural + verde para sinais de crescimento (ARR · captação).',
    permiteCarimbo: true,
    slides: 11,
    serif: "'Newsreader', Georgia, serif",
    sans: "'Schibsted Grotesk', system-ui, sans-serif",
    mono: "'IBM Plex Mono', monospace",
    ink: '#1D1B17', paper: '#F5F2EA', paper2: '#EBE6DA',
    card: '#FBF9F3', muted: '#6F695D', faint: '#A39C8D', line: '#DCD5C6',
    accentDefault: '#C75D32', accent2Default: '#356B52',
  },
};

const PD = {
  verde: '#1B6B2F',
  verdeNoite: '#0E2B19',
  verdeMusgo: '#3C8B4D',
  verdeClaro: '#E8F2EB',
  laranja: '#F47C3C',
  laranjaFunda: '#E66518',
  laranjaClaro: '#FFF0E6',
  cream: '#F4EDE0',
  creamWarm: '#EDE3D2',
  creamDeep: '#D7CFC0',
  txt1: '#1A1A1A',
  txt2: '#4A5563',
  txt3: '#7C8590',
  branco: '#FAFAF6',
  serif: "'Fraunces', Georgia, serif",
  sans: "'Outfit', system-ui, sans-serif",
  mono: "'JetBrains Mono', monospace",
};

const PoweredBy = ({ inverse }) => (
  <span style={{ fontFamily: PD.sans, fontSize: 10, letterSpacing: '0.22em',
    textTransform: 'uppercase', fontWeight: 600 }}>
    <span style={{ color: inverse ? PD.creamDeep : PD.txt3 }}>Powered by </span>
    <span style={{ color: inverse ? PD.cream : PD.verde, fontWeight: 700 }}>RUNGOAL </span>
    <span style={{ color: PD.laranja, fontWeight: 700 }}>AI</span>
    <span style={{ color: inverse ? PD.cream : PD.verde, fontWeight: 700 }}>DVISOR</span>
  </span>
);

const PageMark = ({ slide, total = 10, inverse, label }) => (
  <span style={{ fontFamily: PD.mono, fontSize: 11, letterSpacing: '0.18em',
    color: inverse ? PD.creamDeep : PD.txt2 }}>
    S.{String(slide).padStart(2,'0')} / S.{total}{label ? ` · ${label}` : ''}
  </span>
);

const SectionLabel = ({ accent, children }) => (
  <div style={{ fontFamily: PD.mono, fontSize: 11, letterSpacing: '0.28em',
    textTransform: 'uppercase', color: accent,
    display: 'flex', alignItems: 'center', gap: 14 }}>
    <span style={{ width: 24, height: 1, background: accent, display: 'inline-block' }} />
    {children}
  </div>
);

const BrandFooter = ({ slide, inverse, label }) => (
  <div style={{ position: 'absolute', bottom: 28, left: 50, right: 50,
    display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
    <PoweredBy inverse={inverse} />
    <PageMark slide={slide} inverse={inverse} label={label} />
  </div>
);

const fmtMoneyMi = (v) => {
  if (v == null || v === 0) return '—';
  if (v >= 1_000_000) return `R$ ${(v/1_000_000).toFixed(v >= 10_000_000 ? 0 : 1)} mi`;
  return `R$ ${(v/1000).toFixed(0)} mil`;
};

const escolherCitacao = (setor) => {
  if (!setor) return null;
  const s = setor.toLowerCase();
  if (s.includes('varejo') || s.includes('comércio') || s.includes('e-commerce') || s.includes('loja'))
    return { texto: 'Há mais oportunidade no varejo do que talento para servi-la.', autor: 'Sam Walton', empresa: 'Walmart' };
  if (s.includes('tech') || s.includes('software') || s.includes('saas') || s.includes('tecnologia'))
    return { texto: 'Sua margem é a oportunidade do próximo concorrente.', autor: 'Jeff Bezos', empresa: 'Amazon' };
  if (s.includes('serviç') || s.includes('consult') || s.includes('b2b'))
    return { texto: 'O resultado de um negócio existe fora dele · no cliente.', autor: 'Peter Drucker', empresa: 'The Practice of Management' };
  if (s.includes('indústr') || s.includes('manufat') || s.includes('família'))
    return { texto: 'A qualidade é construída no chão de fábrica, não na sala da diretoria.', autor: 'Akio Morita', empresa: 'Sony' };
  if (s.includes('saúde') || s.includes('alimen') || s.includes('clínica'))
    return { texto: 'Cuidar de pessoas é o negócio mais resiliente que existe.', autor: 'Howard Schultz', empresa: 'Starbucks' };
  return null;
};

const DEMO_SHOPMAX_BP = {
  empresa: {
    nome: 'ShopMax',
    setor: 'E-commerce · Varejo Multimarcas',
    contato_email: 'ricardo@shopmax.com.br',
    contato_telefone: '+55 11 9 4732 0188',
    endereco: 'Av. Paulista 1230 · São Paulo · SP',
  },
  meta: { tipo: 'ebitda_mult', valor: '5' },
  vmv: {
    missao: { texto: 'Transformar o varejo de moda em operação previsível, com margens corporativas e crescimento de dois dígitos.' },
    visao: { texto: '' }, valores: [],
  },
  produtosServicos: {
    diferenciais: [
      { texto: 'Integração SKU-a-SKU entre 14 lojas físicas e 3 marketplaces. Reposição automática. Quebra zero.' },
      { texto: 'Precificação algorítmica por giro, sazonalidade e marca. EBITDA esperado de 14,8% versus média setorial de 6,2%.' },
      { texto: 'Programa de relacionamento próprio com 230 mil cadastros ativos. Frequência de compra 2,4× a média.' },
    ],
    portfolio: [
      { nome: 'Linha Casual', texto: 'Marcas próprias + 28 marcas parceiras.' },
      { nome: 'Linha Premium', texto: 'Curadoria nacional · 12 marcas selecionadas.' },
      { nome: 'Private Label', texto: 'Marca própria · margem 28%.' },
    ],
  },
  mercadoConcorrencia: {
    tam: 180_000_000_000,
    sam: 22_000_000_000,
    som: 1_800_000_000,
    tendencias: [
      { texto: 'O e-commerce nacional cresceu 218% entre 2019 e 2024, mas a maturidade operacional do varejista médio cresceu menos de 14% no mesmo período. Sobra demanda. Falta operação.' },
      { texto: 'Consolidação acelerada no setor abre espaço para players de médio porte com diferencial operacional.' },
    ],
    players: [
      { nome: 'Renner Mid' }, { nome: 'C&A Local' }, { nome: 'Riachuelo Hub' }, { nome: 'Marisa Lite' },
    ],
  },
  swot: {
    forcas: [{ texto: 'Curva de preço dinâmica proprietária.', marcado: true }],
    fraquezas: [{ texto: 'Marca ainda regional.', marcado: true }],
    oportunidades: [
      { texto: 'Consolidação do varejo médio brasileiro abre janela de M&A.', marcado: true },
      { texto: 'Crescimento do e-commerce de moda em capitais do interior.', marcado: true },
      { texto: 'Programa de fidelidade próprio com base já madura.', marcado: true },
    ],
    ameacas: [
      { texto: 'Entrada de marketplaces internacionais com logística agressiva.', marcado: true },
      { texto: 'Pressão tributária sobre PMEs do varejo.', marcado: true },
      { texto: 'Volatilidade cambial em coleções importadas.', marcado: true },
    ],
  },
  estrategiaModelo: {
    modelo: 'Modelo recorrente em base de clientes própria. 230 mil cadastros ativos respondem por 64% do faturamento mensal.',
  },
  marketingVendas: {
    canais: [
      { nome: 'Adensamento', descricao: 'Consolidação das 14 lojas em SP/RJ. Ampliação do CRM. Foco em LTV e frequência.' },
      { nome: 'Expansão regional', descricao: '7 lojas hub em capitais do Sudeste e Sul. Lançamento do private label.' },
      { nome: 'Digital first', descricao: 'Cresce e-commerce próprio. Marketplace whitelabel para 80 marcas parceiras.' },
      { nome: 'Coroamento', descricao: 'Operação madura · 28 lojas hub · receita R$ 412 mi · prontidão para M&A.' },
    ],
    cac: '38',
    ltv: '1.640',
  },
  estruturaRh: {
    organograma: [
      { nome: 'Ricardo Almeida', cargo: 'Ex-Diretor Renner Mid · MBA Wharton · 18 anos no varejo.', area: 'Sócio Fundador · CEO' },
      { nome: 'Patrícia Yamamoto', cargo: 'Ex-Supply Lead Magalu · Engenharia Poli-USP · 14 anos.', area: 'Sócia · COO' },
      { nome: 'Bruno Vieira', cargo: 'Ex-Tech Lead Nubank · MSc ITA · 16 anos em escala digital.', area: 'Sócio · CTO' },
      { nome: 'Mariana Reis', cargo: 'Ex-FP&A C&A · CFA II · 14 anos em planejamento financeiro.', area: 'Diretora Financeira' },
    ],
  },
  projecoes: {
    anos: [
      { ano: 2026, receita: 108_000_000, ebitda: 11_000_000, fcf: 4_000_000 },
      { ano: 2027, receita: 162_000_000, ebitda: 21_000_000, fcf: 11_000_000 },
      { ano: 2028, receita: 234_000_000, ebitda: 33_000_000, fcf: 22_000_000 },
      { ano: 2029, receita: 322_000_000, ebitda: 47_000_000, fcf: 33_000_000 },
      { ano: 2030, receita: 412_000_000, ebitda: 61_000_000, fcf: 46_000_000 },
    ],
  },
  valuation: { evCalc: 24_000_000, wacc: 14.0, captacao: 14_000_000 },
  equityFlag: 'sim',
};

const GraficoEbitdaPD = ({ anos }) => {
  if (!anos || anos.length < 2 || !anos[anos.length-1]?.ebitda) {
    return (
      <div style={{ fontFamily: PD.sans, fontSize: 12,
        color: PD.creamDeep, fontStyle: 'italic' }}>
        Cadastre projeções para exibir trajetória EBITDA.
      </div>
    );
  }
  const valores = anos.map(a => a?.ebitda || 0);
  const max = Math.max(...valores) * 1.10;
  const W = 380, H = 180, pad = 28;
  const inner = W - pad * 2;
  const barW = (inner / valores.length) * 0.62;
  const gap = (inner / valores.length) * 0.38;
  const mult = (valores[valores.length-1] / valores[0]).toFixed(1).replace('.', ',');
  const ultimoMi = (valores[valores.length-1] / 1_000_000).toFixed(0);
  const primMi = (valores[0] / 1_000_000).toFixed(0);
  return (
    <div>
      <div style={{ fontFamily: PD.mono, fontSize: 10,
        letterSpacing: '0.28em', textTransform: 'uppercase',
        color: PD.creamDeep, fontWeight: 700, marginBottom: 8 }}>
        Trajetória EBITDA
      </div>
      <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`} style={{ display: 'block' }}>
        <defs>
          <linearGradient id="pdEbitdaGrad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={PD.laranja} stopOpacity="1" />
            <stop offset="100%" stopColor={PD.laranjaFunda} stopOpacity="0.7" />
          </linearGradient>
          <linearGradient id="pdEbitdaGradDim" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={PD.cream} stopOpacity="0.55" />
            <stop offset="100%" stopColor={PD.creamDeep} stopOpacity="0.35" />
          </linearGradient>
        </defs>
        <line x1={pad} y1={H-pad} x2={W-pad} y2={H-pad}
          stroke={PD.cream} strokeWidth="1" opacity="0.3" />
        {valores.map((v, i) => {
          const h = (v / max) * (H - pad * 2);
          const x = pad + gap/2 + i * (barW + gap);
          const y = H - pad - h;
          const isUltimo = i === valores.length - 1;
          return (
            <g key={i}>
              <rect x={x} y={y} width={barW} height={h}
                fill={isUltimo ? 'url(#pdEbitdaGrad)' : 'url(#pdEbitdaGradDim)'} />
              <text x={x + barW/2} y={H - pad + 16}
                textAnchor="middle"
                style={{ fontFamily: PD.mono, fontSize: 10,
                  fill: PD.creamDeep, letterSpacing: '0.05em' }}>
                {anos[i]?.ano || `A${i+1}`}
              </text>
              {isUltimo && (
                <text x={x + barW/2} y={y - 8}
                  textAnchor="middle"
                  style={{ fontFamily: PD.serif, fontSize: 18, fontWeight: 700,
                    fill: PD.laranja }}>
                  {ultimoMi}
                </text>
              )}
            </g>
          );
        })}
      </svg>
      <div style={{ marginTop: 6, fontFamily: PD.serif, fontSize: 14,
        fontStyle: 'italic', color: PD.cream, lineHeight: 1.4 }}>
        EBITDA cresce <strong style={{ color: PD.laranja, fontWeight: 700 }}>{mult}×</strong> · R$ {primMi} → R$ {ultimoMi} mi.
      </div>
    </div>
  );
};

const PiramideMercado = ({ tam, sam, som }) => {
  if (!tam && !sam && !som) {
    return (
      <div style={{ fontFamily: PD.sans, fontSize: 13,
        color: PD.creamDeep, fontStyle: 'italic' }}>
        Cadastre TAM/SAM/SOM no bloco de Mercado.
      </div>
    );
  }
  const fmt = (v) => {
    if (!v) return '—';
    if (v >= 1_000_000_000) return `R$ ${(v/1_000_000_000).toFixed(v >= 10_000_000_000 ? 0 : 1).replace('.', ',')} bi`;
    return `R$ ${(v/1_000_000).toFixed(0)} mi`;
  };
  const W = 420, H = 320;
  const niveis = [
    { label: 'TAM', val: tam, sub: 'Mercado total endereçável', cor: PD.cream, txtCor: PD.verdeNoite, w: 1.0 },
    { label: 'SAM', val: sam, sub: 'Mercado disponível e servível', cor: PD.laranja, txtCor: PD.verdeNoite, w: 0.66 },
    { label: 'SOM', val: som, sub: 'Mercado endereçável agora', cor: PD.laranjaFunda, txtCor: PD.cream, w: 0.36 },
  ];
  const hNivel = H / 3 - 6;
  return (
    <div>
      <div style={{ fontFamily: PD.mono, fontSize: 10,
        letterSpacing: '0.3em', textTransform: 'uppercase',
        color: PD.creamDeep, fontWeight: 700, marginBottom: 10 }}>
        Mercado endereçável
      </div>
      <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`} style={{ display: 'block' }}>
        {niveis.map((n, i) => {
          const y = i * (hNivel + 6);
          const wTop = W * (i === 0 ? n.w : niveis[i-1].w);
          const wBot = W * n.w;
          const xTopL = (W - wTop) / 2;
          const xTopR = xTopL + wTop;
          const xBotL = (W - wBot) / 2;
          const xBotR = xBotL + wBot;
          return (
            <g key={i}>
              <polygon
                points={`${xTopL},${y} ${xTopR},${y} ${xBotR},${y+hNivel} ${xBotL},${y+hNivel}`}
                fill={n.cor}
                opacity={i === 0 ? 0.85 : 1}
              />
              <text x={W/2} y={y + hNivel/2 - 4} textAnchor="middle"
                style={{ fontFamily: PD.serif, fontSize: 26, fontWeight: 700,
                  fill: n.txtCor, letterSpacing: '-0.02em' }}>
                {n.label}
              </text>
              <text x={W/2} y={y + hNivel/2 + 22} textAnchor="middle"
                style={{ fontFamily: PD.serif, fontSize: 18, fontWeight: 500,
                  fill: n.txtCor }}>
                {fmt(n.val)}
              </text>
            </g>
          );
        })}
      </svg>
      <div style={{ marginTop: 12, display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16 }}>
        {niveis.map((n, i) => (
          <div key={i} style={{ borderTop: `2px solid ${n.cor}`, paddingTop: 6 }}>
            <div style={{ fontFamily: PD.mono, fontSize: 9,
              letterSpacing: '0.2em', textTransform: 'uppercase',
              color: PD.cream, fontWeight: 700 }}>{n.label}</div>
            <div style={{ marginTop: 3, fontFamily: PD.sans, fontSize: 11,
              color: PD.creamDeep, lineHeight: 1.35 }}>{n.sub}</div>
          </div>
        ))}
      </div>
    </div>
  );
};

// =====================================================================
// SlideArdosia — renderiza qualquer um dos 11 slides do template
// neutro (Base A do handoff Claude Design · também serve para Base B
// via parametrização de accent + accent2).
// Canvas 1920×1080 escalado pelo container pai.
// =====================================================================
const SlideArdosia = ({ slide, tema, corAccent, corAccent2, bp,
  mostraMarca, fraseMeta }) => {
  const t = DECK_TEMAS[tema];
  const { empresa, vmv, produtosServicos, mercadoConcorrencia,
    estrategiaModelo, marketingVendas, estruturaRh, projecoes, valuation,
    equityFlag } = bp;
  const nomeEmp = empresa?.nome || 'Sua Empresa';
  const setorTexto = empresa?.setor || 'Setor';

  // Tokens convertidos para inline (escala para 1920×1080)
  const tk = {
    ink: t.ink, paper: t.paper, paper2: t.paper2, card: t.card,
    muted: t.muted, faint: t.faint, line: t.line,
    serif: t.serif, sans: t.sans, mono: t.mono,
    accent: corAccent, accent2: corAccent2,
    tCover: 118, tTitle: 64, tSub: 38, tBody: 30, tSmall: 24,
    tEyebrow: 24, tStat: 84, tData: 128, padX: 120, padY: 90,
  };

  const Eyebrow = ({ children }) => (
    <div style={{ fontFamily: tk.mono, fontSize: tk.tEyebrow,
      letterSpacing: '0.18em', textTransform: 'uppercase',
      color: tk.accent, marginBottom: 36 }}>{children}</div>
  );
  const Titulo = ({ children, max = 1280 }) => (
    <h2 style={{ fontFamily: tk.serif, fontWeight: 500,
      fontSize: tk.tTitle, lineHeight: 1.05, letterSpacing: '-0.01em',
      margin: 0, maxWidth: max, color: tk.ink }}>{children}</h2>
  );
  const Corpo = ({ children, color, max = 1080 }) => (
    <p style={{ fontFamily: tk.sans, fontSize: tk.tBody, lineHeight: 1.45,
      color: color || tk.muted, margin: 0, maxWidth: max }}>{children}</p>
  );
  const RodapeMarca = () => mostraMarca ? (
    <div style={{ position: 'absolute', bottom: 36, right: tk.padX,
      fontFamily: tk.mono, fontSize: 16, letterSpacing: '0.18em',
      textTransform: 'uppercase', color: tk.faint }}>
      Powered by <span style={{ color: tk.muted, fontWeight: 600 }}>RUNGOAL </span>
      <span style={{ color: '#F47C3C', fontWeight: 600 }}>AI</span>
      <span style={{ color: tk.muted, fontWeight: 600 }}>DVISOR</span>
    </div>
  ) : null;

  const baseSlide = {
    width: 1920, height: 1080, padding: `${tk.padY}px ${tk.padX}px`,
    background: tk.paper, color: tk.ink, fontFamily: tk.sans,
    boxSizing: 'border-box', position: 'relative',
    display: 'flex', flexDirection: 'column',
  };

  // ===== Mapeamento de dados =====
  const anos = (projecoes?.anos || []).slice(0, 5);
  const arrMaxBase = Math.max(...anos.map(a => a.receita || 0), 1);
  const fmtMi = (v) => v ? `${(v / 1_000_000).toFixed(0)}` : '—';
  const fmtMiCurto = (v) => v >= 1_000_000 ? (v / 1_000_000).toFixed(1).replace('.', ',') : (v / 1000).toFixed(0);
  const captacaoVal = valuation?.captacao || (valuation?.evCalc ? valuation.evCalc * 0.6 : null);
  const dif = (produtosServicos?.diferenciais || []).slice(0, 3);
  const canais = (marketingVendas?.canais || []).slice(0, 3);
  const time = (estruturaRh?.organograma || []).slice(0, 3);
  const tend0 = mercadoConcorrencia?.tendencias?.[0]?.texto
    || mercadoConcorrencia?.tendencias?.[0] || '';

  // ===== Slide 1 · Capa =====
  if (slide === 1) {
    return (
      <section data-pdf-pitch-slide style={{ ...baseSlide,
        justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between',
          alignItems: 'center', fontFamily: tk.mono, fontSize: tk.tSmall,
          letterSpacing: '0.18em', textTransform: 'uppercase', color: tk.muted }}>
          <span>Pitch Deck</span>
          <span>2026</span>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 34,
          maxWidth: 1400 }}>
          <div style={{ width: 64, height: 4, background: tk.accent }} />
          <h1 style={{ fontFamily: tk.serif, fontWeight: 500,
            fontSize: tk.tCover, lineHeight: 0.96, letterSpacing: '-0.01em',
            margin: 0, color: tk.ink }}>{nomeEmp}</h1>
          <p style={{ fontFamily: tk.sans, fontWeight: 400, fontSize: tk.tSub,
            lineHeight: 1.25, margin: 0, color: tk.ink, maxWidth: 1080 }}>
            {vmv?.missao?.texto || `${setorTexto} · plano consolidado para os próximos 60 meses.`}
          </p>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between',
          alignItems: 'flex-end', borderTop: `1px solid ${tk.line}`,
          paddingTop: 30, fontFamily: tk.mono, fontSize: tk.tSmall,
          color: tk.muted }}>
          <span style={{ color: tk.ink }}>
            {time[0]?.nome ? `${time[0].nome} · ${time[0].area || time[0].cargo || 'Liderança'}` : 'Liderança · empresa'}
          </span>
          <span>
            {empresa?.contato_email || '—'}
            {empresa?.contato_telefone && ` · ${empresa.contato_telefone}`}
            {empresa?.endereco && ` · ${empresa.endereco.split('·')[1]?.trim() || empresa.endereco}`}
          </span>
        </div>
        {mostraMarca && (
          <div style={{ position: 'absolute', bottom: 18, left: tk.padX,
            fontFamily: tk.mono, fontSize: 14, letterSpacing: '0.18em',
            textTransform: 'uppercase', color: tk.faint }}>
            Powered by <span style={{ color: tk.muted, fontWeight: 600 }}>RUNGOAL </span>
            <span style={{ color: '#F47C3C', fontWeight: 600 }}>AI</span>
            <span style={{ color: tk.muted, fontWeight: 600 }}>DVISOR</span>
          </div>
        )}
      </section>
    );
  }

  // ===== Slide 2 · Problema =====
  if (slide === 2) {
    return (
      <section data-pdf-pitch-slide style={baseSlide}>
        <Eyebrow>01 — Problema</Eyebrow>
        <Titulo>{tend0 || `O mercado de ${setorTexto.toLowerCase()} ainda opera sem o suporte adequado.`}</Titulo>
        <Corpo>O empresário médio enfrenta margens pressionadas, processos manuais e ausência de
          consolidação financeira. O resultado é decisão lenta e oportunidade perdida.</Corpo>
        <div style={{ display: 'flex', gap: 64, marginTop: 'auto', paddingTop: 64 }}>
          {[
            { v: anos[0]?.receita ? `R$ ${fmtMi(anos[0].receita)} mi` : '—', t: 'Receita atual da empresa.' },
            { v: mercadoConcorrencia?.som ? `R$ ${fmtMiCurto(mercadoConcorrencia.som)} ${mercadoConcorrencia.som >= 1_000_000_000 ? 'bi' : 'mi'}` : '—', t: 'Mercado endereçável imediato (SOM).' },
            { v: '14×', t: 'Atraso médio de maturidade operacional em relação ao avanço do mercado.' },
          ].map((s, i) => (
            <React.Fragment key={i}>
              {i > 0 && <div style={{ width: 1, background: tk.line }} />}
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 14 }}>
                <div style={{ fontFamily: tk.serif, fontWeight: 500,
                  fontSize: tk.tStat, lineHeight: 1, color: tk.ink }}>{s.v}</div>
                <div style={{ fontFamily: tk.sans, fontSize: tk.tSmall,
                  lineHeight: 1.4, color: tk.muted }}>{s.t}</div>
              </div>
            </React.Fragment>
          ))}
        </div>
        <RodapeMarca />
      </section>
    );
  }

  // ===== Slide 3 · Proposta de Valor =====
  if (slide === 3) {
    return (
      <section data-pdf-pitch-slide style={baseSlide}>
        <Eyebrow>02 — Proposta de Valor</Eyebrow>
        <Titulo>{vmv?.missao?.texto || `${nomeEmp} entrega operação previsível, margem defendida e crescimento sustentado.`}</Titulo>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)',
          gap: 48, marginTop: 'auto', paddingTop: 80 }}>
          {(dif.length > 0 ? dif : [
            { texto: 'Operação consolidada e processos eficientes.' },
            { texto: 'Curva de margem defendida em ciclos diferentes.' },
            { texto: 'Base de clientes recorrentes com previsibilidade.' },
          ]).map((d, i) => (
            <div key={i} style={{ display: 'flex', flexDirection: 'column',
              gap: 18, borderTop: `3px solid ${tk.accent}`, paddingTop: 28 }}>
              <h3 style={{ fontFamily: tk.sans, fontWeight: 600,
                fontSize: tk.tSub, lineHeight: 1.1, margin: 0, color: tk.ink }}>
                {`Pilar 0${i + 1}`}
              </h3>
              <p style={{ fontFamily: tk.sans, fontSize: tk.tBody,
                lineHeight: 1.4, margin: 0, color: tk.muted }}>{d.texto || d}</p>
            </div>
          ))}
        </div>
        <RodapeMarca />
      </section>
    );
  }

  // ===== Slide 4 · Tecnologia / Como funciona =====
  if (slide === 4) {
    return (
      <section data-pdf-pitch-slide style={baseSlide}>
        <Eyebrow>03 — Como funciona</Eyebrow>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr',
          gap: 80, alignItems: 'center', flex: 1 }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 28, maxWidth: 680 }}>
            <Titulo max={680}>Um fluxo único, do dado bruto ao valor entregue.</Titulo>
            <Corpo>{estrategiaModelo?.modelo ||
              `${nomeEmp} integra captura, processamento e entrega em uma única espinha de dados — resposta em horas, não dias.`}</Corpo>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12,
              fontFamily: tk.mono, fontSize: tk.tSmall, color: tk.accent }}>
              <span>01 / Captura</span>
              <span>02 / Categorização por IA</span>
              <span>03 / Entrega conciliada</span>
            </div>
          </div>
          <div style={{ height: 560, border: `1px solid ${tk.line}`,
            background: tk.card,
            backgroundImage: `repeating-linear-gradient(135deg, ${tk.accent}11 0 12px, transparent 12px 24px)`,
            display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <span style={{ fontFamily: tk.mono, fontSize: tk.tSmall,
              letterSpacing: '0.12em', textTransform: 'uppercase',
              color: tk.faint }}>[ diagrama da arquitetura ]</span>
          </div>
        </div>
        <RodapeMarca />
      </section>
    );
  }

  // ===== Slide 5 · Modelo de Negócio =====
  if (slide === 5) {
    const ano1 = anos[0];
    const ebMargem = ano1?.receita && ano1?.ebitda
      ? ((ano1.ebitda / ano1.receita) * 100).toFixed(1).replace('.', ',') : null;
    return (
      <section data-pdf-pitch-slide style={baseSlide}>
        <Eyebrow>04 — Modelo de Negócio</Eyebrow>
        <Titulo>Cada R$ 1,00 vendido gera margem operacional sustentável.</Titulo>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr',
          gap: 48, marginTop: 56 }}>
          {[
            { label: 'Receita recorrente', valor: 'Base própria',
              sub: 'Clientes recorrentes em portfólio consolidado.' },
            { label: 'Margem operacional', valor: ebMargem ? `${ebMargem}%` : '—',
              sub: 'EBITDA defendido acima da média setorial.' },
          ].map((c, i) => (
            <div key={i} style={{ background: tk.card,
              border: `1px solid ${tk.line}`, padding: 44,
              display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div style={{ fontFamily: tk.mono, fontSize: tk.tSmall,
                letterSpacing: '0.1em', textTransform: 'uppercase',
                color: tk.muted }}>{c.label}</div>
              <div style={{ fontFamily: tk.serif, fontWeight: 500,
                fontSize: tk.tStat, lineHeight: 1, color: tk.ink }}>{c.valor}</div>
              <p style={{ fontFamily: tk.sans, fontSize: tk.tBody,
                lineHeight: 1.4, color: tk.muted, margin: '6px 0 0' }}>{c.sub}</p>
            </div>
          ))}
        </div>
        <div style={{ display: 'flex', gap: 64, marginTop: 'auto',
          paddingTop: 56, borderTop: `1px solid ${tk.line}`,
          fontFamily: tk.mono }}>
          {[
            { v: marketingVendas?.ltv && marketingVendas?.cac
                ? `${(parseFloat(String(marketingVendas.ltv).replace('.','').replace(',','.')) / parseFloat(String(marketingVendas.cac).replace('.','').replace(',','.'))).toFixed(1).replace('.', ',')}×`
                : '—', l: 'LTV / CAC' },
            { v: '18 meses', l: 'payback médio' },
            { v: ebMargem ? `${ebMargem}%` : '—', l: 'margem EBITDA' },
          ].map((m, i) => (
            <div key={i} style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <span style={{ fontSize: tk.tSub, color: tk.ink }}>{m.v}</span>
              <span style={{ fontSize: tk.tSmall, color: tk.muted }}>{m.l}</span>
            </div>
          ))}
        </div>
        <RodapeMarca />
      </section>
    );
  }

  // ===== Slide 6 · Go-to-Market =====
  if (slide === 6) {
    const fases = canais.length >= 3 ? canais : [
      { nome: 'Adensar', descricao: 'Consolidação da base atual.' },
      { nome: 'Expandir', descricao: 'Novos canais e mercados regionais.' },
      { nome: 'Escalar', descricao: 'Operação madura · pronta para captação principal.' },
    ];
    return (
      <section data-pdf-pitch-slide style={baseSlide}>
        <Eyebrow>05 — Go-to-Market</Eyebrow>
        <Titulo>Plano em três horizontes até o 5.º ano.</Titulo>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)',
          gap: 0, marginTop: 'auto', paddingTop: 80 }}>
          {fases.map((f, i) => (
            <div key={i} style={{ display: 'flex', flexDirection: 'column',
              gap: 22, padding: i === 0 ? '28px 48px 0 0' : i === 1 ? '28px 48px' : '28px 0 0 48px',
              borderTop: `${i === 0 ? '1px' : '1px'} solid ${i === 0 ? tk.ink : tk.line}`,
              borderTopWidth: i === 0 ? 1 : 1 }}>
              <div style={{ fontFamily: tk.mono, fontSize: tk.tSmall,
                letterSpacing: '0.1em', color: tk.accent }}>
                {['FASE 01 · Hoje', 'FASE 02 · 12 meses', 'FASE 03 · 24 meses'][i]}
              </div>
              <h3 style={{ fontFamily: tk.sans, fontWeight: 600,
                fontSize: tk.tSub, lineHeight: 1.1, margin: 0,
                color: tk.ink }}>{f.nome}</h3>
              <p style={{ fontFamily: tk.sans, fontSize: tk.tBody,
                lineHeight: 1.4, margin: 0, color: tk.muted }}>{f.descricao}</p>
            </div>
          ))}
        </div>
        <RodapeMarca />
      </section>
    );
  }

  // ===== Slide 7 · Concorrência =====
  if (slide === 7) {
    return (
      <section data-pdf-pitch-slide style={baseSlide}>
        <Eyebrow>06 — Concorrência</Eyebrow>
        <div style={{ display: 'grid', gridTemplateColumns: '1.15fr 1fr',
          gap: 72, alignItems: 'center', flex: 1 }}>
          <div style={{ display: 'flex', flexDirection: 'column',
            gap: 24, maxWidth: 560 }}>
            <Titulo max={560}>Único focado em PME com plataforma completa.</Titulo>
            <Corpo>Gigantes corporativos têm escala mas perdem agilidade. Boutiques têm curadoria
              mas não têm operação. {nomeEmp} cobre o vazio com tecnologia + curadoria + margem
              corporativa numa única operação.</Corpo>
          </div>
          <div style={{ position: 'relative', height: 560,
            borderLeft: `1px solid ${tk.ink}`, borderBottom: `1px solid ${tk.ink}` }}>
            <span style={{ position: 'absolute', left: -8, top: -34,
              fontFamily: tk.mono, fontSize: 20, letterSpacing: '0.08em',
              textTransform: 'uppercase', color: tk.muted }}>Foco em PME ↑</span>
            <span style={{ position: 'absolute', right: 0, bottom: -40,
              fontFamily: tk.mono, fontSize: 20, letterSpacing: '0.08em',
              textTransform: 'uppercase', color: tk.muted }}>Plataforma completa →</span>
            {[
              { x: 8, y: 18, label: 'Gigantes corporativos', cor: tk.faint, size: 14 },
              { x: 62, y: 62, label: 'Marketplaces estrangeiros', cor: tk.faint, size: 14 },
              { x: 10, y: 64, label: 'Boutiques artesanais', cor: tk.faint, size: 14 },
              { x: 70, y: 14, label: nomeEmp, cor: tk.accent, size: 22, dest: true },
            ].map((p, i) => (
              <div key={i} style={{ position: 'absolute',
                left: `${p.x}%`, top: `${p.y}%`,
                display: 'flex', flexDirection: 'column',
                alignItems: 'center', gap: p.dest ? 10 : 8 }}>
                <span style={{ width: p.size, height: p.size, borderRadius: '50%',
                  background: p.cor,
                  boxShadow: p.dest ? `0 0 0 6px ${tk.accent}26` : 'none' }} />
                <span style={{ fontFamily: tk.mono,
                  fontWeight: p.dest ? 600 : 400,
                  fontSize: p.dest ? 24 : 20,
                  color: p.dest ? tk.ink : tk.muted }}>{p.label}</span>
              </div>
            ))}
          </div>
        </div>
        <RodapeMarca />
      </section>
    );
  }

  // ===== Slide 8 · Time =====
  if (slide === 8) {
    return (
      <section data-pdf-pitch-slide style={baseSlide}>
        <Eyebrow>07 — Time</Eyebrow>
        <Titulo>Quem está construindo {nomeEmp}.</Titulo>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)',
          gap: 56, marginTop: 'auto', paddingTop: 72 }}>
          {(time.length > 0 ? time : Array.from({ length: 3 })).map((p, i) => (
            <div key={i} style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
              <div style={{ width: 120, height: 120, borderRadius: '50%',
                border: `1px solid ${tk.line}`, background: tk.card,
                backgroundImage: `repeating-linear-gradient(135deg, ${tk.accent}14 0 8px, transparent 8px 16px)`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontFamily: tk.mono, fontSize: 20, color: tk.faint }}>[foto]</div>
              <div>
                <div style={{ fontFamily: tk.sans, fontWeight: 600,
                  fontSize: tk.tSub, color: tk.ink }}>{p?.nome || `Sócio ${i + 1}`}</div>
                <div style={{ fontFamily: tk.mono, fontSize: tk.tSmall,
                  color: tk.accent }}>{p?.area || 'Posição'}</div>
              </div>
              <p style={{ fontFamily: tk.sans, fontSize: tk.tBody,
                lineHeight: 1.4, color: tk.muted, margin: 0 }}>
                {p?.cargo || 'Bio executiva da posição · cadastrar na Tarefa 10.'}
              </p>
            </div>
          ))}
        </div>
        <div style={{ marginTop: 48, paddingTop: 28,
          borderTop: `1px solid ${tk.line}`,
          fontFamily: tk.mono, fontSize: tk.tSmall, color: tk.muted }}>
          Conselheiros: experiência sênior em varejo, governança e capital.
        </div>
        <RodapeMarca />
      </section>
    );
  }

  // ===== Slide 9 · Projeções =====
  if (slide === 9) {
    return (
      <section data-pdf-pitch-slide style={baseSlide}>
        <div style={{ display: 'flex', justifyContent: 'space-between',
          alignItems: 'baseline', marginBottom: 36 }}>
          <div style={{ fontFamily: tk.mono, fontSize: tk.tEyebrow,
            letterSpacing: '0.18em', textTransform: 'uppercase',
            color: tk.accent }}>08 — Projeções Financeiras</div>
          <div style={{ fontFamily: tk.mono, fontSize: tk.tSmall,
            letterSpacing: '0.1em', textTransform: 'uppercase',
            color: tk.muted }}>5 anos · 60 meses</div>
        </div>
        <Titulo>Receita projetada · {anos[anos.length - 1]?.ano || 'Ano 5'}.</Titulo>
        <div style={{ display: 'grid', gridTemplateColumns: '1.4fr 1fr',
          gap: 80, flex: 1, alignItems: 'end', marginTop: 48 }}>
          <div style={{ display: 'flex', alignItems: 'flex-end',
            gap: 48, height: '100%', paddingBottom: 8 }}>
            {(anos.length > 0 ? anos : [1, 2, 3, 4, 5]).map((a, i) => {
              const v = a?.receita || 0;
              const altura = anos.length > 0
                ? Math.max(18, (v / arrMaxBase) * 380)
                : [18, 56, 150, 300, 380][i];
              const opacidade = [0.5, 0.6, 0.72, 0.86, 1][i];
              return (
                <div key={i} style={{ flex: 1, display: 'flex',
                  flexDirection: 'column', alignItems: 'center', gap: 14 }}>
                  <div style={{ fontFamily: i === (anos.length || 5) - 1 ? tk.serif : tk.mono,
                    fontWeight: i === (anos.length || 5) - 1 ? 500 : 400,
                    fontSize: i === (anos.length || 5) - 1 ? 38 : tk.tSmall,
                    color: tk.ink }}>
                    {v ? fmtMi(v) : '—'}
                  </div>
                  <div style={{ width: '100%', height: altura,
                    background: tk.accent2 || tk.accent,
                    opacity: opacidade }} />
                  <div style={{ fontFamily: tk.mono, fontSize: tk.tSmall,
                    color: tk.muted }}>{a?.ano || `Ano ${i + 1}`}</div>
                </div>
              );
            })}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column',
            gap: 36, paddingBottom: 8 }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <span style={{ fontFamily: tk.mono, fontSize: tk.tSmall,
                letterSpacing: '0.1em', textTransform: 'uppercase',
                color: tk.muted }}>Receita no {anos[anos.length - 1]?.ano || 'Ano 5'}</span>
              <span style={{ fontFamily: tk.serif, fontWeight: 500,
                fontSize: tk.tStat, lineHeight: 1, color: tk.ink }}>
                R$ {fmtMi(anos[anos.length - 1]?.receita)} mi
              </span>
            </div>
            <div style={{ display: 'flex', gap: 48 }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                <span style={{ fontFamily: tk.serif, fontWeight: 500,
                  fontSize: 46, lineHeight: 1, color: tk.ink }}>
                  {valuation?.evCalc ? `R$ ${fmtMi(valuation.evCalc)} mi` : '—'}
                </span>
                <span style={{ fontFamily: tk.mono, fontSize: tk.tSmall,
                  color: tk.muted }}>Enterprise Value · DCF</span>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                <span style={{ fontFamily: tk.serif, fontWeight: 500,
                  fontSize: 46, lineHeight: 1, color: tk.ink }}>
                  {valuation?.wacc ? `${valuation.wacc}%` : '—'}
                </span>
                <span style={{ fontFamily: tk.mono, fontSize: tk.tSmall,
                  color: tk.muted }}>WACC</span>
              </div>
            </div>
            <p style={{ fontFamily: tk.sans, fontSize: tk.tSmall,
              lineHeight: 1.45, color: tk.muted, margin: 0 }}>
              Projeção bottom-up · ancorada em CAC, conversão e churn observados.
            </p>
          </div>
        </div>
        <RodapeMarca />
      </section>
    );
  }

  // ===== Slide 10 · Status & Captação =====
  if (slide === 10) {
    const showCapt = (equityFlag === 'sim' || equityFlag === 'em_breve' || equityFlag === true);
    return (
      <section data-pdf-pitch-slide style={baseSlide}>
        <Eyebrow>09 — Status & Captação</Eyebrow>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr',
          gap: 96, flex: 1 }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 30 }}>
            <Titulo max={680}>Onde estamos.</Titulo>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 22 }}>
              {[
                { quando: 'Hoje', oque: anos[0]?.receita
                    ? `R$ ${fmtMi(anos[0].receita)} mi de receita · operação consolidada · base de clientes recorrente.`
                    : 'Operação consolidada · base de clientes recorrente.' },
                { quando: 'D+30', oque: 'Term sheet executado · investidor-âncora.' },
                { quando: 'D+90', oque: 'Primeira loja hub aberta em capital regional.' },
              ].map((m, i) => (
                <div key={i} style={{ display: 'flex', gap: 20,
                  alignItems: 'baseline' }}>
                  <span style={{ fontFamily: tk.mono, fontSize: tk.tSmall,
                    color: tk.accent, minWidth: 90 }}>{m.quando}</span>
                  <span style={{ fontFamily: tk.sans, fontSize: tk.tBody,
                    lineHeight: 1.35, color: i === 0 ? tk.ink : tk.muted }}>{m.oque}</span>
                </div>
              ))}
            </div>
          </div>
          {showCapt ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 30 }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                <span style={{ fontFamily: tk.mono, fontSize: tk.tSmall,
                  letterSpacing: '0.1em', textTransform: 'uppercase',
                  color: tk.muted }}>Captando</span>
                <span style={{ fontFamily: tk.serif, fontWeight: 500,
                  fontSize: tk.tData, lineHeight: 0.92,
                  color: tk.accent2 || tk.accent }}>
                  R$ {fmtMi(captacaoVal)} mi
                </span>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column',
                gap: 20, marginTop: 8 }}>
                {[
                  { l: 'Expansão de hubs', p: 42 },
                  { l: 'Tecnologia · OS', p: 24 },
                  { l: 'Capital de giro', p: 18 },
                  { l: 'Marketing e CRM', p: 10 },
                  { l: 'Reserva estratégica', p: 6 },
                ].map((b, i) => (
                  <div key={i} style={{ display: 'flex',
                    flexDirection: 'column', gap: 8 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between',
                      fontFamily: tk.mono, fontSize: tk.tSmall, color: tk.ink }}>
                      <span>{b.l}</span><span>{b.p}%</span>
                    </div>
                    <div style={{ height: 14, background: tk.paper2 }}>
                      <div style={{ height: '100%', width: `${b.p}%`,
                        background: tk.accent }} />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 30 }}>
              <Titulo max={680}>Crescimento orgânico.</Titulo>
              <Corpo>{nomeEmp} segue plano de crescimento sem captação no horizonte imediato ·
                janela futura reservada para o horizonte 2.</Corpo>
            </div>
          )}
        </div>
        <RodapeMarca />
      </section>
    );
  }

  // ===== Slide 11 · Encerramento =====
  if (slide === 11) {
    return (
      <section data-pdf-pitch-slide style={{ ...baseSlide,
        background: tk.ink, color: tk.paper,
        justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between',
          alignItems: 'center', fontFamily: tk.mono, fontSize: tk.tSmall,
          letterSpacing: '0.18em', textTransform: 'uppercase',
          color: tk.faint }}>
          <span>{nomeEmp}</span>
          <span>Pitch Deck · 2026</span>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 30 }}>
          <div style={{ width: 64, height: 4, background: tk.accent }} />
          <h2 style={{ fontFamily: tk.serif, fontWeight: 500, fontSize: 96,
            lineHeight: 1, letterSpacing: '-0.01em', margin: 0,
            color: tk.paper }}>Vamos conversar.</h2>
          <p style={{ fontFamily: tk.sans, fontSize: tk.tSub,
            lineHeight: 1.3, margin: 0, color: tk.faint, maxWidth: 900 }}>
            {fraseMeta || `${nomeEmp} está pronta para a próxima etapa do plano de 60 meses.`}
            {' '}O próximo passo é uma conversa.
          </p>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between',
          alignItems: 'flex-end',
          borderTop: '1px solid rgba(245,242,234,0.18)',
          paddingTop: 30, fontFamily: tk.mono, fontSize: tk.tSmall,
          color: tk.faint }}>
          <span style={{ color: tk.paper }}>
            {time[0]?.nome ? `${time[0].nome} · ${time[0].area || 'CEO'}` : 'CEO · empresa'}
          </span>
          <span>
            {empresa?.contato_email || '—'}
            {empresa?.contato_telefone && ` · ${empresa.contato_telefone}`}
          </span>
        </div>
        {mostraMarca && (
          <div style={{ position: 'absolute', bottom: 18, right: tk.padX,
            fontFamily: tk.mono, fontSize: 14, letterSpacing: '0.18em',
            textTransform: 'uppercase', color: 'rgba(245,242,234,0.55)' }}>
            Powered by <span style={{ color: tk.paper, fontWeight: 600 }}>RUNGOAL </span>
            <span style={{ color: '#F47C3C', fontWeight: 600 }}>AI</span>
            <span style={{ color: tk.paper, fontWeight: 600 }}>DVISOR</span>
          </div>
        )}
      </section>
    );
  }

  // fallback
  return <section data-pdf-pitch-slide style={baseSlide}>Slide {slide}</section>;
};

const PitchDeck = ({ bp, onFechar }) => {
  const [slide, setSlide] = useState(1);
  const [modoDemo, setModoDemo] = useState(false);
  const [tema, setTema] = useState('sequoia');
  const [mostraMarca, setMostraMarca] = useState(true);
  const temaDef = DECK_TEMAS[tema];
  const [corAccent, setCorAccent] = useState(temaDef?.accentDefault || '#2F4858');
  const [corAccent2, setCorAccent2] = useState(temaDef?.accent2Default || '#356B52');
  const [painelTema, setPainelTema] = useState(false);
  const totalSlides = temaDef?.slides || 10;

  // Ao trocar de tema · reset slide=1 e cores default do novo tema
  useEffect(() => {
    setSlide(1);
    if (DECK_TEMAS[tema]?.accentDefault) setCorAccent(DECK_TEMAS[tema].accentDefault);
    if (DECK_TEMAS[tema]?.accent2Default) setCorAccent2(DECK_TEMAS[tema].accent2Default);
  }, [tema]);

  const bpAtivo = modoDemo ? DEMO_SHOPMAX_BP : bp;
  const { meta, empresa, vmv, produtosServicos, mercadoConcorrencia, swot,
    estrategiaModelo, marketingVendas, estruturaRh, projecoes, valuation, equityFlag,
    modo, valSU } = bpAtivo;
  const tipoMetaSel = TIPOS_META_BP60M.find(t => t.id === meta?.tipo);
  const fraseMeta = tipoMetaSel ? `${tipoMetaSel.fraseAntes || ''}${meta?.valor || ''}${tipoMetaSel.fraseDepois || ''}` : '';
  const nomeEmp = empresa?.nome || 'Sua Empresa';
  const setorTexto = empresa?.setor || 'Setor';
  // ── Figuras SU da fonte única (valSU) · usadas nos slides 4·5·6·9 e no ASK ──
  const ehSU = modo === 'SU';
  const anosSUpd = ehSU ? (valSU?.anos || []).slice(0, 5) : [];
  const a0SU = anosSUpd[0];
  const aNSU = anosSUpd[anosSUpd.length - 1];
  const margContribPctSU = a0SU?.receita ? Math.round(a0SU.margemContrib / a0SU.receita * 100) : null;
  const custosDirPctSU   = a0SU?.receita ? Math.round(a0SU.custoDir / a0SU.receita * 100) : null;
  const opexPctSU        = a0SU?.receita ? Math.round(a0SU.opex / a0SU.receita * 100) : null;
  const ebitdaPctSU      = a0SU?.receita ? Math.round(a0SU.ebitda / a0SU.receita * 100) : null;
  const recMultSU = (anosSUpd.length >= 2 && a0SU?.receita && aNSU?.receita)
    ? (aNSU.receita / a0SU.receita).toFixed(1).replace('.', ',') : null;
  const cagrRecSU = (anosSUpd.length >= 2 && a0SU?.receita > 0 && aNSU?.receita > 0)
    ? Math.round((Math.pow(aNSU.receita / a0SU.receita, 1 / (anosSUpd.length - 1)) - 1) * 100) : null;
  const fmtMiSU = (v) => v == null ? '—' : (Math.abs(v) >= 1_000_000 ? `R$ ${(v/1_000_000).toFixed(2)} mi` : `R$ ${(v/1000).toFixed(0)} mil`);
  const rodadaSelPD = ehSU ? RODADAS_SU.find(r => r.id === meta?.rodada) : null;

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onFechar();
      if (e.key === 'ArrowRight' && slide < totalSlides) setSlide(slide + 1);
      if (e.key === 'ArrowLeft'  && slide > 1)  setSlide(slide - 1);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [slide, onFechar, totalSlides]);

  // Escala automática: container fixo 1280×720, scale via viewport
  // Canvas: sequoia usa 1280×720 (mantém formato original); ardosia/laranja-verde usam 1920×1080 (handoff)
  const canvasW = tema === 'sequoia' ? 1280 : 1920;
  const canvasH = tema === 'sequoia' ? 720 : 1080;
  const [scale, setScale] = useState(1);
  useEffect(() => {
    const recalcular = () => {
      const w = Math.min(window.innerWidth - 120, canvasW);
      const h = Math.min(window.innerHeight - 180, canvasH);
      const sx = w / canvasW;
      const sy = h / canvasH;
      setScale(Math.min(sx, sy));
    };
    recalcular();
    window.addEventListener('resize', recalcular);
    return () => window.removeEventListener('resize', recalcular);
  }, [canvasW, canvasH]);

  // ============= SHELL UNIFORME =============
  const SlidePadrao = ({ cap, titulo, sub, dark, slide: s, label, children }) => {
    const bg = dark ? PD.verdeNoite : PD.cream;
    const txt = dark ? PD.cream : PD.txt1;
    const txt2c = dark ? PD.creamDeep : PD.txt2;
    const accent = dark ? PD.laranja : PD.verde;
    return (
      <section style={{ position: 'relative', width: 1280, height: 720,
        background: bg, color: txt, fontFamily: PD.sans,
        padding: '70px 80px', overflow: 'hidden', boxSizing: 'border-box' }}>
        <div style={{ fontFamily: PD.mono, fontSize: 11,
          letterSpacing: '0.28em', textTransform: 'uppercase', color: accent,
          display: 'flex', alignItems: 'center', gap: 14 }}>
          <span style={{ width: 24, height: 1, background: accent }} />
          Capítulo {String(cap).padStart(2,'0')} · {label}
        </div>
        <h1 style={{ fontFamily: PD.serif, fontWeight: 500, fontSize: 42,
          lineHeight: 1.18, color: dark ? PD.cream : PD.verdeNoite,
          margin: '20px 0 0 0', maxWidth: 940, letterSpacing: '-0.015em' }}>
          {titulo}
        </h1>
        {sub && (
          <div style={{ marginTop: 14, fontFamily: PD.serif, fontStyle: 'italic',
            fontWeight: 300, fontSize: 20, color: txt2c, lineHeight: 1.45,
            maxWidth: 900 }}>{sub}</div>
        )}
        <div style={{ position: 'absolute', left: 80, right: 80, top: 290, bottom: 100 }}>
          {children}
        </div>
        <BrandFooter slide={s} inverse={dark} label={label} />
      </section>
    );
  };

  // ---------- Slide 1 — CAPA ----------
  const tagline = empresa?.tagline
    || (ehSU ? `Construindo a próxima fase do ${setorTexto.toLowerCase()}.`
      : (setorTexto.toLowerCase().includes('varej') ? 'A operação corporativa do varejo médio.'
        : `Construindo a próxima fase do ${setorTexto.toLowerCase()}.`));
  const Slide1 = () => (
    <section style={{ position: 'relative', width: 1280, height: 720,
      background: PD.cream, color: PD.txt1, fontFamily: PD.sans,
      padding: '70px 80px', overflow: 'hidden', boxSizing: 'border-box' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div style={{ fontFamily: PD.mono, fontSize: 11, letterSpacing: '0.28em',
          textTransform: 'uppercase', color: PD.verde,
          display: 'flex', alignItems: 'center', gap: 14 }}>
          <span style={{ width: 24, height: 1, background: PD.verde }} />
          Pitch Deck · 2026
        </div>
        <div style={{ fontFamily: PD.mono, fontSize: 10, letterSpacing: '0.3em',
          color: PD.txt2, textTransform: 'uppercase' }}>
          Documento Confidencial
        </div>
      </div>

      <div style={{ position: 'absolute', left: 80, top: 240, maxWidth: 940 }}>
        <div style={{ fontFamily: PD.serif, fontWeight: 600, fontSize: 92,
          lineHeight: 0.95, color: PD.verdeNoite, letterSpacing: '-0.025em' }}>
          {nomeEmp}
        </div>
        <div style={{ marginTop: 24, fontFamily: PD.serif, fontStyle: 'italic',
          fontWeight: 300, fontSize: 30, color: PD.txt2,
          maxWidth: 760, lineHeight: 1.4 }}>
          {tagline}
        </div>
        <div style={{ marginTop: 28, fontFamily: PD.mono, fontSize: 11,
          letterSpacing: '0.28em', textTransform: 'uppercase', color: PD.laranja,
          fontWeight: 700 }}>
          {setorTexto}
        </div>
      </div>

      <div style={{ position: 'absolute', right: 80, bottom: 100, textAlign: 'right',
        fontFamily: PD.mono, fontSize: 12, letterSpacing: '0.1em', color: PD.txt2,
        lineHeight: 2 }}>
        {empresa?.contato_email && <>{empresa.contato_email}<br/></>}
        {empresa?.contato_telefone && <>{empresa.contato_telefone}<br/></>}
        {empresa?.endereco && <>{empresa.endereco}</>}
      </div>

      <div style={{ position: 'absolute', bottom: 70, left: 80, right: 80,
        height: 1, background: PD.txt1, opacity: 0.2 }} />
      <BrandFooter slide={1} />
    </section>
  );

  // ---------- Slide 2 — A DOR ----------
  const tendencia0 = mercadoConcorrencia?.tendencias?.[0]?.texto || mercadoConcorrencia?.tendencias?.[0] || '';
  const segmentoEmp = setorTexto.toLowerCase().includes('varej') ? 'varejo médio' : `${setorTexto.toLowerCase()} médio`;
  const Slide2 = () => (
    <SlidePadrao cap={2} dark slide={2} label="A DOR"
      titulo={ehSU
        ? `Há uma dor real em ${setorTexto.toLowerCase()} — nunca antes resolvida.`
        : `O empresário do ${segmentoEmp} vende, mas não dorme.`}
      sub={ehSU
        ? `Mercado grande, solução ruim. ${nomeEmp} existe para resolver isso.`
        : `Estoque parado, margem corroída por concorrentes globais, cliente que volta uma vez por ano. ${nomeEmp} existe para mudar isso.`}>
      <div style={{ display: 'grid', gridTemplateColumns: '1.4fr 1fr', gap: 60,
        height: '100%', alignItems: 'center' }}>
        <div style={{ borderLeft: `2px solid ${PD.laranja}`, paddingLeft: 30 }}>
          <div style={{ fontFamily: PD.mono, fontSize: 11,
            letterSpacing: '0.25em', textTransform: 'uppercase',
            color: PD.creamDeep, marginBottom: 18 }}>
            O sintoma que se repete
          </div>
          <div style={{ fontFamily: PD.serif, fontSize: 21,
            color: PD.cream, lineHeight: 1.55, fontWeight: 400 }}>
            {tendencia0 || (ehSU
              ? `O mercado de ${setorTexto.toLowerCase()} cresce, mas a experiência do cliente ficou para trás. Há espaço para quem resolver de verdade.`
              : 'A maturidade operacional do empresário médio cresceu menos de 14% nos últimos 5 anos, enquanto o mercado cresceu 218%. Sobra demanda. Falta operação.')}
          </div>
        </div>
        <div>
          <PiramideMercado
            tam={mercadoConcorrencia?.tam}
            sam={mercadoConcorrencia?.sam}
            som={mercadoConcorrencia?.som}
          />
        </div>
      </div>
    </SlidePadrao>
  );

  // ---------- Slide 3 — PROPOSTA DE VALOR ----------
  const diferenciais = (produtosServicos?.diferenciais || []).slice(0,3);
  const Slide3 = () => (
    <SlidePadrao cap={3} slide={3} label="PROPOSTA DE VALOR"
      titulo={ehSU ? 'Uma nova resposta para um problema antigo.' : `O ${segmentoEmp} brasileiro foi esquecido.`}
      sub={vmv?.missao?.texto
        ? `"${vmv.missao.texto}"`
        : (ehSU
            ? `Enquanto as soluções atuais não dão conta, ${nomeEmp} entrega de um jeito novo — mais simples, rápido e acessível.`
            : `De um lado, gigantes corporativos. Do outro, pequenas operações artesanais. ${nomeEmp} é a operação que faltava no meio: tecnologia, curadoria e margem.`)}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)',
        gap: 36, alignItems: 'start', height: '100%' }}>
        {(diferenciais.length > 0 ? diferenciais : [
          { texto: 'Diferencial 1 · preencher na Tarefa 3.' },
          { texto: 'Diferencial 2 · preencher na Tarefa 3.' },
          { texto: 'Diferencial 3 · preencher na Tarefa 3.' },
        ]).map((d, i) => (
          <div key={i} style={{ borderTop: `2px solid ${PD.verde}`, paddingTop: 22 }}>
            <div style={{ fontFamily: PD.mono, fontSize: 11,
              letterSpacing: '0.28em', textTransform: 'uppercase',
              color: PD.laranjaFunda, fontWeight: 700 }}>
              {`0${i+1}`}
            </div>
            <div style={{ marginTop: 14, fontFamily: PD.serif, fontSize: 19,
              color: PD.verdeNoite, fontWeight: 500, lineHeight: 1.35 }}>
              {d.texto || d}
            </div>
          </div>
        ))}
      </div>
    </SlidePadrao>
  );

  // ---------- Slide 4 — COMO FUNCIONA ----------
  const Slide4 = () => (
    <SlidePadrao cap={4} slide={4} label="COMO FUNCIONA"
      titulo="Para o cliente, uma única tela."
      sub={estrategiaModelo?.modelo
        ? estrategiaModelo.modelo
        : `Por trás, ${nomeEmp} orquestra a operação em um fluxo único: tudo conversa entre si, nada se perde.`}>
      <div style={{ marginTop: 20, display: 'grid',
        gridTemplateColumns: '1fr 60px 1.5fr 60px 1fr',
        alignItems: 'center' }}>
        <NodoSimples titulo="Cliente" sub="Demanda capturada" />
        <SetaSimples />
        <NodoSimples titulo={`${nomeEmp} OS`} sub="Espinha de dados única · estoque, preço e cliente" destaque />
        <SetaSimples />
        <NodoSimples titulo="Entrega" sub="Valor reconhecido" />
      </div>
      <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0,
        borderTop: `1px solid ${PD.txt1}30`, paddingTop: 22,
        display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 40 }}>
        <KpiSimples label="Resposta de estoque" val="< 24h" />
        <KpiSimples label={ehSU ? 'Margem de contribuição' : 'Margem EBITDA'} val={ehSU ? (margContribPctSU != null ? `${margContribPctSU}%` : '—') : '2,4× setor'} accent />
        <KpiSimples label="Canais integrados" val="Físico · digital · marketplace" mini />
      </div>
    </SlidePadrao>
  );
  const NodoSimples = ({ titulo, sub, destaque }) => (
    <div style={{ aspectRatio: destaque ? '1.7 / 1' : '1.2 / 1',
      background: destaque ? PD.verdeNoite : PD.cream,
      border: `1.5px solid ${PD.verdeNoite}`,
      display: 'flex', flexDirection: 'column', justifyContent: 'center',
      alignItems: 'center', textAlign: 'center', padding: '20px 28px' }}>
      <div style={{ fontFamily: PD.serif, fontWeight: 600,
        fontSize: destaque ? 26 : 22,
        color: destaque ? PD.cream : PD.verdeNoite, lineHeight: 1.15 }}>
        {titulo}
      </div>
      <div style={{ marginTop: 10, fontFamily: PD.sans, fontSize: 13,
        color: destaque ? PD.creamDeep : PD.txt2, lineHeight: 1.4 }}>
        {sub}
      </div>
    </div>
  );
  const SetaSimples = () => (
    <div style={{ display: 'flex', alignItems: 'center',
      justifyContent: 'center' }}>
      <div style={{ position: 'relative', width: 50, height: 1, background: PD.verdeNoite }}>
        <div style={{ position: 'absolute', right: -3, top: -9,
          color: PD.laranja, fontSize: 16 }}>▸</div>
      </div>
    </div>
  );
  const KpiSimples = ({ label, val, accent, mini }) => (
    <div>
      <div style={{ fontFamily: PD.mono, fontSize: 10,
        letterSpacing: '0.25em', textTransform: 'uppercase',
        color: PD.txt2 }}>{label}</div>
      <div style={{ marginTop: 6, fontFamily: PD.serif, fontWeight: 500,
        fontSize: mini ? 16 : 28,
        color: accent ? PD.laranjaFunda : PD.verdeNoite,
        letterSpacing: '-0.015em', lineHeight: 1.2 }}>{val}</div>
    </div>
  );

  // ---------- Slide 5 — MODELO ----------
  const anoAtual = projecoes?.anos?.[0];
  const ebitdaMargem = anoAtual?.receita && anoAtual?.ebitda
    ? ((anoAtual.ebitda / anoAtual.receita) * 100).toFixed(1).replace('.', ',')
    : null;
  const custosDirPct = anoAtual?.receita && anoAtual?.ebitda
    ? ((1 - (anoAtual.ebitda / anoAtual.receita) - 0.25) * 100).toFixed(1).replace('.', ',')
    : null;
  const Slide5 = () => (
    <SlidePadrao cap={5} dark slide={5} label="MODELO"
      titulo={ebitdaMargem
        ? `Cada R$ 1 vendido gera R$ 0,${(ebitdaMargem.replace(',','.') * 1).toFixed(0).padStart(2,'0')} de margem operacional.`
        : 'Modelo recorrente em base própria de clientes.'}
      sub="Receita previsível, margem crescente. Não dependemos de tráfego pago.">
      <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 1fr', gap: 60,
        height: '100%' }}>
        <div>
          <div style={{ fontFamily: PD.mono, fontSize: 11,
            letterSpacing: '0.25em', textTransform: 'uppercase',
            color: PD.creamDeep, borderBottom: '1px solid rgba(255,255,255,0.2)',
            paddingBottom: 12 }}>
            Estrutura unitária · ano-base {anoAtual?.ano || '—'}
          </div>
          <LinhaModelo nome="Receita Bruta" val="100%" />
          <LinhaModelo nome="Custos diretos" val={ehSU ? (custosDirPctSU != null ? `−${custosDirPctSU}%` : '—') : (custosDirPct ? `−${custosDirPct}%` : '—')} />
          {ehSU && <LinhaModelo nome="Margem de contribuição" val={margContribPctSU != null ? `+${margContribPctSU}%` : '—'} />}
          <LinhaModelo nome="Despesas operacionais" val={ehSU ? (opexPctSU != null ? `−${opexPctSU}%` : '—') : '−25,0%'} />
          <LinhaModelo nome="EBITDA" val={ehSU ? (ebitdaPctSU != null ? `${ebitdaPctSU}%` : '—') : (ebitdaMargem ? `${ebitdaMargem}%` : '—')} total />
        </div>
        <div style={{ display: 'flex', flexDirection: 'column',
          justifyContent: 'space-between' }}>
          <div>
            <div style={{ fontFamily: PD.mono, fontSize: 10,
              letterSpacing: '0.3em', textTransform: 'uppercase',
              color: PD.creamDeep }}>
              {ehSU ? 'Margem de contribuição · ano-base' : 'Margem EBITDA · ano-base'}
            </div>
            <div style={{ fontFamily: PD.serif, fontWeight: 500, fontSize: 96,
              lineHeight: 1, color: PD.laranja, letterSpacing: '-0.03em',
              marginTop: 10 }}>
              {ehSU ? (margContribPctSU != null ? margContribPctSU : '—') : (ebitdaMargem || '—')}<span style={{ fontSize: 44 }}>%</span>
            </div>
            <div style={{ marginTop: 12, fontFamily: PD.serif, fontStyle: 'italic',
              fontSize: 19, color: PD.cream, lineHeight: 1.4 }}>
              {ehSU
                ? (margContribPctSU != null ? 'Cada venda cobre os custos diretos e ainda contribui para o crescimento.' : 'Cadastre a estrutura para o cálculo.')
                : (ebitdaMargem ? '2,4× a média do setor.' : 'Cadastre projeções para o cálculo.')}
            </div>
          </div>
          <div style={{ borderTop: '1px solid rgba(255,255,255,0.2)',
            paddingTop: 18 }}>
            <div style={{ fontFamily: PD.mono, fontSize: 10,
              letterSpacing: '0.25em', textTransform: 'uppercase',
              color: PD.creamDeep }}>
              {ehSU ? 'Tração inicial' : 'Base recorrente'}
            </div>
            <div style={{ marginTop: 8, fontFamily: PD.serif, fontSize: 18,
              color: PD.cream, fontWeight: 500, lineHeight: 1.4 }}>
              {ehSU
                ? 'Os primeiros clientes já validam a proposta de valor e sustentam a curva de aquisição.'
                : <>230 mil clientes ativos respondem por <span style={{ color: PD.laranja, fontWeight: 700 }}>64%</span> do faturamento mensal.</>}
            </div>
          </div>
        </div>
      </div>
    </SlidePadrao>
  );
  const LinhaModelo = ({ nome, val, total }) => (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 130px',
      padding: '14px 0', borderBottom: '1px solid rgba(255,255,255,0.08)',
      alignItems: 'baseline' }}>
      <div style={{ fontFamily: PD.serif, fontWeight: total ? 600 : 400,
        fontSize: 17, color: PD.cream }}>{nome}</div>
      <div style={{ fontFamily: PD.mono, fontSize: total ? 19 : 16,
        color: total ? PD.cream : PD.laranja, textAlign: 'right',
        fontWeight: 500 }}>{val}</div>
    </div>
  );

  // ---------- Slide 6 — EXPANSÃO ----------
  const canais = (marketingVendas?.canais || []).slice(0,4);
  const Slide6 = () => (
    <SlidePadrao cap={6} slide={6} label="EXPANSÃO"
      titulo="Plano em quatro horizontes até o 5.º ano."
      sub="Adensar, expandir, escalar e coroar. Crescimento orgânico, com captação no horizonte 2.">
      <div style={{ position: 'relative', marginTop: 10 }}>
        <div style={{ position: 'absolute', top: 14, left: 0, right: 0,
          height: 1, background: PD.verdeNoite }} />
        <div style={{ position: 'absolute', top: 8, right: -3,
          color: PD.laranja, fontSize: 18 }}>▸</div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)',
          gap: 30, position: 'relative' }}>
          {['M0 → M12','M12 → M24','M24 → M40','M40 → M60'].map((periodo, i) => {
            const c = canais[i] || null;
            const fb = [
              { nome: 'Adensar', descricao: 'Consolidação da base atual em SP/RJ.' },
              { nome: 'Expandir', descricao: 'Novos hubs no Sudeste e Sul.' },
              { nome: 'Escalar', descricao: 'Digital first e marketplace whitelabel.' },
              { nome: 'Coroar', descricao: ehSU ? 'Tração consolidada · prontidão para nova rodada ou saída (M&A/IPO).' : 'Operação madura · prontidão para M&A.' },
            ][i];
            const dest = i === 1;
            return (
              <div key={i}>
                <div style={{ width: 14, height: 14, borderRadius: '50%',
                  background: dest ? PD.laranja : PD.cream,
                  border: `2px solid ${dest ? PD.laranja : PD.verdeNoite}`,
                  boxShadow: dest ? `0 0 0 4px ${PD.laranjaClaro}` : 'none',
                  marginBottom: 22, position: 'relative', zIndex: 1 }} />
                <div style={{ fontFamily: PD.mono, fontSize: 10,
                  letterSpacing: '0.28em', textTransform: 'uppercase',
                  color: PD.laranjaFunda, fontWeight: 700 }}>{periodo}</div>
                <div style={{ marginTop: 8, fontFamily: PD.serif, fontWeight: 600,
                  fontSize: 22, color: PD.verdeNoite, letterSpacing: '-0.01em' }}>
                  {c?.nome || fb.nome}
                </div>
                <div style={{ marginTop: 10, fontFamily: PD.sans, fontSize: 13,
                  color: PD.txt2, lineHeight: 1.55, maxWidth: 230 }}>
                  {c?.descricao || fb.descricao}
                </div>
              </div>
            );
          })}
        </div>
      </div>
      <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0,
        borderTop: `1px solid ${PD.txt1}30`, paddingTop: 20,
        display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 40 }}>
        <KpiSimples label="CAC alvo" val={marketingVendas?.cac ? `R$ ${marketingVendas.cac}` : '—'} accent />
        <KpiSimples label="LTV esperado" val={marketingVendas?.ltv ? `R$ ${marketingVendas.ltv}` : '—'} />
        <KpiSimples label="Meta" val={fraseMeta} mini />
      </div>
    </SlidePadrao>
  );

  // ---------- Slide 7 — CONCORRÊNCIA ----------
  const Slide7 = () => (
    <SlidePadrao cap={7} slide={7} label="CONCORRÊNCIA"
      titulo={ehSU ? 'Uma solução simples e diferente de todas.' : 'Três forças no setor. Nenhuma cobre o médio porte com tecnologia.'}
      sub={ehSU
        ? `${nomeEmp} ocupa o espaço que as soluções atuais deixaram aberto.`
        : `${nomeEmp} ocupa o vazio · tecnologia, curadoria e margem corporativa numa única operação.`}>
      <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: 0 }}>
        <thead>
          <tr>
            <th style={{ ...thLight, textAlign: 'left' }}>Player</th>
            <th style={thLight}>Tecnologia</th>
            <th style={thLight}>Curadoria</th>
            <th style={thLight}>Margem</th>
            <th style={thLight}>Cliente recorrente</th>
          </tr>
        </thead>
        <tbody>
          <LinhaConc nome="Gigantes corporativos" cells={['alta','média','média','baixa']} />
          <LinhaConc nome="Boutiques artesanais" cells={['baixa','alta','média','média']} />
          <LinhaConc nome="Marketplaces estrangeiros" cells={['alta','nenhuma','muito baixa','baixa']} />
          <LinhaConc nome={nomeEmp} cells={['alta','alta','alta','alta']} destaque />
        </tbody>
      </table>
    </SlidePadrao>
  );
  const thLight = { fontFamily: PD.mono, fontSize: 10,
    letterSpacing: '0.25em', textTransform: 'uppercase', color: PD.txt2,
    padding: '12px 8px', borderBottom: `1px solid ${PD.verdeNoite}`,
    fontWeight: 600, textAlign: 'center' };
  const LinhaConc = ({ nome, cells, destaque }) => {
    const Cel = ({ v }) => {
      const cor = v === 'alta' ? PD.verde
        : v === 'média' ? PD.laranja
        : PD.txt3;
      return (
        <td style={{ textAlign: 'center', padding: '16px 8px',
          borderBottom: '1px solid rgba(0,0,0,0.06)',
          fontFamily: PD.mono, fontSize: 11, fontWeight: 700,
          color: cor, letterSpacing: '0.1em', textTransform: 'uppercase' }}>
          {v}
        </td>
      );
    };
    return (
      <tr style={{ background: destaque ? PD.verdeClaro : 'transparent' }}>
        <td style={{ padding: '16px 8px',
          borderBottom: '1px solid rgba(0,0,0,0.06)',
          fontFamily: PD.serif, fontSize: 17,
          fontWeight: destaque ? 700 : 500,
          color: destaque ? PD.verdeNoite : PD.txt1 }}>
          {nome}
        </td>
        {cells.map((v, i) => <Cel key={i} v={v} />)}
      </tr>
    );
  };

  // ---------- Slide 8 — TIME ----------
  const time = (estruturaRh?.organograma || []).slice(0,4);
  const conselho = modoDemo
    ? 'Cláudio Galeazzi · Eduardo Mufarej · Luiza Helena Trajano'
    : (estruturaRh?.conselho || 'Cadastrar conselho consultivo na Tarefa 10.');
  const Slide8 = () => (
    <SlidePadrao cap={8} slide={8} label="TIME"
      titulo={time.length > 0 ? 'Quatro sócios · 62 anos somados de experiência.' : 'Time sênior à frente da operação.'}
      sub="Conselho consultivo com líderes do setor.">
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)',
        gap: 26, paddingBottom: 90 }}>
        {(time.length > 0 ? time : Array.from({ length: 4 })).map((p, i) => (
          <div key={i}>
            <div style={{ height: 140, background: PD.creamWarm,
              border: `1px solid ${PD.txt1}30`, position: 'relative',
              overflow: 'hidden' }}>
              <div style={{ position: 'absolute', top: 22, left: '50%',
                transform: 'translateX(-50%)', width: 44, height: 44,
                background: PD.verdeNoite, borderRadius: '50%' }} />
              <div style={{ position: 'absolute', bottom: 0, left: '50%',
                transform: 'translateX(-50%)', width: '60%', height: 56,
                background: PD.verdeNoite,
                clipPath: 'polygon(20% 100%, 50% 0%, 80% 100%)' }} />
            </div>
            <div style={{ marginTop: 12, fontFamily: PD.mono, fontSize: 9,
              letterSpacing: '0.22em', textTransform: 'uppercase',
              color: PD.laranjaFunda, fontWeight: 700 }}>
              {p?.area || 'Posição'}
            </div>
            <div style={{ marginTop: 4, fontFamily: PD.serif, fontWeight: 600,
              fontSize: 17, color: PD.verdeNoite, lineHeight: 1.15 }}>
              {p?.nome || `Sócio ${i+1}`}
            </div>
            <div style={{ marginTop: 6, fontFamily: PD.sans, fontSize: 11,
              color: PD.txt2, lineHeight: 1.45 }}>
              {p?.cargo || 'Cadastrar na Tarefa 10.'}
            </div>
          </div>
        ))}
      </div>
      <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0,
        borderTop: `1px solid ${PD.txt1}30`, paddingTop: 16 }}>
        <div style={{ fontFamily: PD.mono, fontSize: 10,
          letterSpacing: '0.28em', textTransform: 'uppercase',
          color: PD.verde, fontWeight: 700, marginBottom: 8 }}>
          Conselho Consultivo
        </div>
        <div style={{ fontFamily: PD.serif, fontStyle: 'italic',
          fontSize: 17, color: PD.txt1, lineHeight: 1.4 }}>
          {conselho}
        </div>
      </div>
    </SlidePadrao>
  );

  // ---------- Slide 9 — TRAJETÓRIA ----------
  const anosProj = (projecoes?.anos || []).slice(0,5);
  const ebMult = anosProj.length >= 2 && anosProj[0]?.ebitda && anosProj[anosProj.length-1]?.ebitda
    ? (anosProj[anosProj.length-1].ebitda / anosProj[0].ebitda).toFixed(1).replace('.', ',')
    : null;
  const cagrReceita = anosProj.length >= 2 && anosProj[0]?.receita && anosProj[anosProj.length-1]?.receita
    ? (((Math.pow(anosProj[anosProj.length-1].receita / anosProj[0].receita, 1/(anosProj.length-1)) - 1) * 100).toFixed(0))
    : null;
  const cagrEbitda = anosProj.length >= 2 && anosProj[0]?.ebitda && anosProj[anosProj.length-1]?.ebitda
    ? (((Math.pow(anosProj[anosProj.length-1].ebitda / anosProj[0].ebitda, 1/(anosProj.length-1)) - 1) * 100).toFixed(0))
    : null;
  const trajAnos = ehSU ? anosSUpd : anosProj;
  const Slide9 = () => (
    <SlidePadrao cap={9} dark slide={9} label="TRAJETÓRIA"
      titulo={ehSU
        ? (recMultSU ? `Em 5 anos, a receita cresce ${recMultSU}×.` : 'Trajetória financeira de cinco anos.')
        : (ebMult ? `Em 5 anos, o EBITDA cresce ${ebMult}×.` : 'Trajetória financeira de cinco anos.')}
      sub="Receita previsível, margem em expansão, caixa em escala.">
      <div style={{ display: 'grid', gridTemplateColumns: '1.6fr 1fr', gap: 50,
        height: '100%' }}>
        <div>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={thDark}>Indicador</th>
                {(trajAnos.length > 0 ? trajAnos : [1,2,3,4,5]).map((a, i) => (
                  <th key={i} style={thDarkRight}>{a?.ano || `A${i+1}`}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              <LinhaTraj label="Receita" valores={trajAnos.map(a => a.receita)} />
              <LinhaTraj label="EBITDA" valores={trajAnos.map(a => a.ebitda)} dest />
              <LinhaTraj label="Caixa livre" valores={trajAnos.map(a => a.fcf)} />
            </tbody>
          </table>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column',
          justifyContent: 'space-between' }}>
          <div>
            <div style={{ fontFamily: PD.mono, fontSize: 10,
              letterSpacing: '0.3em', textTransform: 'uppercase',
              color: PD.creamDeep }}>
              {ehSU ? 'Valor da empresa · pré-money' : `Enterprise Value · DCF${valuation?.wacc ? ` · WACC ${valuation.wacc}%` : ''}`}
            </div>
            <div style={{ fontFamily: PD.serif, fontWeight: 500, fontSize: 68,
              lineHeight: 1, color: PD.cream, letterSpacing: '-0.03em',
              marginTop: 8 }}>
              <span style={{ fontSize: 30, color: PD.laranja, fontWeight: 400,
                fontStyle: 'italic', marginRight: 8 }}>R$</span>
              {ehSU
                ? (valSU?.central > 0 ? (Math.abs(valSU.central) >= 1e6 ? (valSU.central/1e6).toFixed(1).replace('.', ',') : (valSU.central/1000).toFixed(0)) : '—')
                : (valuation?.evCalc ? (valuation.evCalc / 1_000_000).toFixed(0) : '—')}
              <span style={{ fontFamily: PD.sans, fontSize: 20, fontWeight: 300,
                color: PD.creamDeep, marginLeft: 10 }}>{ehSU ? (valSU?.central >= 1e6 ? 'mi' : 'mil') : 'mi'}</span>
            </div>
            {ehSU && valSU?.faixaMax > 0 && (
              <div style={{ marginTop: 6, fontFamily: PD.serif, fontStyle: 'italic',
                fontSize: 15, color: PD.creamDeep }}>
                faixa {fmtMiSU(valSU.faixaMin)} ↔ {fmtMiSU(valSU.faixaMax)}
              </div>
            )}
            <div style={{ marginTop: 14, display: 'grid',
              gridTemplateColumns: '1fr 1fr', gap: 16 }}>
              <KpiDark label="CAGR Receita" val={ehSU ? (cagrRecSU != null ? `${cagrRecSU}%` : '—') : (cagrReceita ? `${cagrReceita}%` : '—')} />
              <KpiDark label={ehSU ? 'Margem de contribuição' : 'CAGR EBITDA'} val={ehSU ? (margContribPctSU != null ? `${margContribPctSU}%` : '—') : (cagrEbitda ? `${cagrEbitda}%` : '—')} />
            </div>
          </div>
          <div style={{ borderTop: '1px solid rgba(255,255,255,0.2)',
            paddingTop: 14 }}>
            <GraficoEbitdaPD anos={trajAnos} />
          </div>
        </div>
      </div>
    </SlidePadrao>
  );
  const thDark = { fontFamily: PD.mono, fontSize: 10,
    letterSpacing: '0.25em', textTransform: 'uppercase', color: PD.creamDeep,
    padding: '12px 0', borderBottom: '1px solid rgba(255,255,255,0.25)',
    fontWeight: 600, textAlign: 'left' };
  const thDarkRight = { ...thDark, textAlign: 'right' };
  const LinhaTraj = ({ label, valores, dest }) => (
    <tr>
      <td style={{ fontFamily: PD.mono, fontSize: 11,
        letterSpacing: '0.18em', textTransform: 'uppercase',
        color: PD.creamDeep, padding: '15px 0',
        borderBottom: '1px solid rgba(255,255,255,0.08)', fontWeight: 600 }}>
        {label}
      </td>
      {(valores.length > 0 ? valores : [null,null,null,null,null]).map((v, i) => (
        <td key={i} style={{ fontFamily: PD.serif, fontSize: 17,
          color: dest ? PD.laranja : PD.cream,
          padding: '15px 0', textAlign: 'right',
          borderBottom: '1px solid rgba(255,255,255,0.08)',
          fontWeight: dest ? 600 : 400 }}>
          {v ? `R$ ${(v / 1_000_000).toFixed(0)} mi` : '—'}
        </td>
      ))}
    </tr>
  );
  const KpiDark = ({ label, val }) => (
    <div>
      <div style={{ fontFamily: PD.mono, fontSize: 10,
        letterSpacing: '0.25em', textTransform: 'uppercase',
        color: PD.creamDeep }}>{label}</div>
      <div style={{ marginTop: 6, fontFamily: PD.serif, fontWeight: 500,
        fontSize: 28, color: PD.laranja, letterSpacing: '-0.01em' }}>{val}</div>
    </div>
  );

  // ---------- Slide 10 — CAPTAÇÃO ----------
  const captacao = ehSU ? (valSU?.captacao || null) : (valuation?.captacao || (valuation?.evCalc ? valuation.evCalc * 0.6 : null));
  const showCaptacao = (equityFlag === 'sim' || equityFlag === 'em_breve' || equityFlag === true) && (!ehSU || !!captacao);
  const equityPct = ehSU ? (valSU?.equityPct != null ? `${String(valSU.equityPct).replace('.', ',')}%` : '—') : '22%';
  const captMi = captacao ? (captacao / 1_000_000).toFixed(0) : '—';
  const evMi = valuation?.evCalc ? (valuation.evCalc / 1_000_000).toFixed(0) : '—';
  const Slide10 = () => (
    <SlidePadrao cap={10} slide={10} label="CAPTAÇÃO"
      titulo={showCaptacao
        ? (ehSU
            ? `${fmtMiSU(captacao)}${rodadaSelPD ? ` na Rodada ${rodadaSelPD.titulo}` : ''} por ${equityPct} de equity.`
            : `R$ ${captMi} milhões por ${equityPct} de equity.`)
        : 'Status atual e marcos.'}
      sub={showCaptacao
        ? (ehSU
            ? `Valuation pré-money ${fmtMiSU(valSU?.central)}. Equity puro. Destinação em cinco frentes, com marcos de execução nos primeiros 90 dias.`
            : `Valuation pré-money R$ ${evMi} milhões. Equity puro. Destinação em cinco frentes, com marcos de execução nos primeiros 90 dias.`)
        : `${nomeEmp} segue em crescimento orgânico, com captação prevista para o horizonte 2.`}>
      {showCaptacao && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 50 }}>
          <div>
            <div style={{ fontFamily: PD.mono, fontSize: 11,
              letterSpacing: '0.25em', textTransform: 'uppercase',
              color: PD.verde, borderBottom: `1px solid ${PD.verde}`,
              paddingBottom: 12, fontWeight: 700 }}>
              Destinação dos recursos
            </div>
            {[
              { cor: PD.verdeNoite, nome: 'Expansão de hubs', sub: '7 unidades novas em capitais', pct: '42%' },
              { cor: PD.laranja, nome: 'Tecnologia · OS v2', sub: 'IA de precificação · plataforma', pct: '24%' },
              { cor: PD.verdeMusgo, nome: 'Capital de giro', sub: 'Estoque inicial · private label', pct: '18%' },
              { cor: PD.laranjaFunda, nome: 'Marketing e CRM', sub: 'Aquisição e retenção', pct: '10%' },
              { cor: PD.creamDeep, nome: 'Reserva estratégica', sub: 'Contingência operacional', pct: '6%' },
            ].map((d, i) => (
              <div key={i} style={{ display: 'grid',
                gridTemplateColumns: '20px 1fr 50px', alignItems: 'center',
                padding: '12px 0', borderBottom: '1px solid rgba(0,0,0,0.06)',
                gap: 12 }}>
                <div style={{ width: 14, height: 14, background: d.cor }} />
                <div>
                  <div style={{ fontFamily: PD.sans, fontWeight: 600,
                    fontSize: 14, color: PD.txt1 }}>{d.nome}</div>
                  <div style={{ fontFamily: PD.sans, fontSize: 11,
                    color: PD.txt3, marginTop: 2 }}>{d.sub}</div>
                </div>
                <div style={{ fontFamily: PD.mono, fontSize: 14,
                  fontWeight: 700, color: PD.verdeNoite, textAlign: 'right' }}>
                  {d.pct}
                </div>
              </div>
            ))}
          </div>
          <div>
            <div style={{ fontFamily: PD.mono, fontSize: 11,
              letterSpacing: '0.25em', textTransform: 'uppercase',
              color: PD.laranjaFunda, borderBottom: `1px solid ${PD.laranjaFunda}`,
              paddingBottom: 12, fontWeight: 700 }}>
              Próximos 90 dias
            </div>
            {[
              { quando: 'D + 30', oque: 'Term sheet executado · investidor-âncora.' },
              { quando: 'D + 45', oque: 'Início da construção do OS v2.' },
              { quando: 'D + 60', oque: 'Definição dos 2 primeiros novos hubs.' },
              { quando: 'D + 90', oque: 'Primeira loja hub aberta em Belo Horizonte.' },
            ].map((m, i) => (
              <div key={i} style={{ padding: '14px 0',
                borderBottom: '1px solid rgba(0,0,0,0.06)' }}>
                <div style={{ fontFamily: PD.mono, fontSize: 10,
                  letterSpacing: '0.28em', color: PD.laranja,
                  textTransform: 'uppercase', fontWeight: 700 }}>
                  {m.quando}
                </div>
                <div style={{ marginTop: 4, fontFamily: PD.serif,
                  fontSize: 16, color: PD.verdeNoite, fontWeight: 500,
                  lineHeight: 1.3 }}>
                  {m.oque}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </SlidePadrao>
  );

  const slides = { 1: Slide1, 2: Slide2, 3: Slide3, 4: Slide4, 5: Slide5,
    6: Slide6, 7: Slide7, 8: Slide8, 9: Slide9, 10: Slide10 };
  const Atual = slides[slide];
  const ehNeutro = tema !== 'sequoia';

  return (
    <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      background: 'rgba(10,10,8,0.92)', zIndex: 1000,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      flexDirection: 'column', gap: 16 }}>
      {/* Seletor de tema · cards no topo · temas alternativos (SlideArdosia) ocultos até unificar na NOVA 2 */}
      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        {Object.values(DECK_TEMAS).filter(td => td.id === 'sequoia').map(td => (
          <button key={td.id} onClick={() => setTema(td.id)}
            style={{ background: tema === td.id ? '#FFF' : 'rgba(255,255,255,0.12)',
              color: tema === td.id ? '#1A1A1A' : '#FFF',
              border: tema === td.id ? '2px solid #F47C3C' : '1px solid rgba(255,255,255,0.3)',
              padding: '8px 14px', fontSize: 11, fontWeight: 700,
              letterSpacing: '0.06em', textTransform: 'uppercase',
              borderRadius: 0, cursor: 'pointer', fontFamily: PD.sans }}>
            {td.nome}
          </button>
        ))}
        {temaDef?.permiteCarimbo && (
          <label style={{ marginLeft: 16, color: '#FFF', fontSize: 11,
            fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase',
            fontFamily: PD.sans, display: 'flex', alignItems: 'center', gap: 8,
            cursor: 'pointer' }}>
            <input type="checkbox" checked={mostraMarca}
              onChange={e => setMostraMarca(e.target.checked)} />
            Marca RUNGOAL AIDVISOR
          </label>
        )}
        {temaDef?.permiteCarimbo && (
          <button onClick={() => setPainelTema(!painelTema)}
            style={{ marginLeft: 8, background: 'transparent',
              border: '1px solid rgba(255,255,255,0.3)', color: '#FFF',
              padding: '8px 14px', fontSize: 11, fontWeight: 700,
              letterSpacing: '0.06em', textTransform: 'uppercase',
              borderRadius: 0, cursor: 'pointer', fontFamily: PD.sans }}>
            {painelTema ? 'Cores ▴' : 'Cores ▾'}
          </button>
        )}
      </div>

      {/* Painel de cores · só se tema permite */}
      {temaDef?.permiteCarimbo && painelTema && (
        <div style={{ display: 'flex', gap: 24, alignItems: 'center',
          background: 'rgba(255,255,255,0.06)', padding: '8px 16px',
          borderRadius: 0, fontFamily: PD.sans, fontSize: 11,
          color: '#FFF', fontWeight: 700, letterSpacing: '0.06em',
          textTransform: 'uppercase' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            Accent
            <input type="color" value={corAccent}
              onChange={e => setCorAccent(e.target.value)}
              style={{ width: 36, height: 28, border: 'none', borderRadius: 0,
                cursor: 'pointer' }} />
          </label>
          {tema === 'laranja-verde' && (
            <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              Accent 2
              <input type="color" value={corAccent2}
                onChange={e => setCorAccent2(e.target.value)}
                style={{ width: 36, height: 28, border: 'none', borderRadius: 0,
                  cursor: 'pointer' }} />
            </label>
          )}
        </div>
      )}

      <div style={{ width: canvasW * scale, height: canvasH * scale,
        position: 'relative', boxShadow: '0 30px 80px rgba(0,0,0,0.5)' }}>
        <div data-pdf-pitch-container style={{ transform: `scale(${scale})`,
          transformOrigin: 'top left', width: canvasW, height: canvasH }}>
          {ehNeutro ? (
            <SlideArdosia slide={slide} tema={tema}
              corAccent={corAccent} corAccent2={corAccent2}
              bp={bpAtivo} mostraMarca={mostraMarca}
              fraseMeta={fraseMeta} />
          ) : (
            <Atual />
          )}
        </div>
      </div>
      <button onClick={onFechar}
        style={{ position: 'absolute', top: 20, right: 20,
          background: 'transparent', border: '1px solid #FFF',
          color: '#FFF', padding: '6px 12px', fontSize: 11,
          fontWeight: 700, letterSpacing: '0.10em', textTransform: 'uppercase',
          borderRadius: 0, cursor: 'pointer', fontFamily: PD.sans }}>
        Fechar · esc
      </button>
      <button onClick={async () => {
          const html2canvas = (await import('html2canvas')).default;
          const { jsPDF } = await import('jspdf');
          if (document.fonts && document.fonts.ready) {
            try { await document.fonts.ready; } catch (_) { /* ignore */ }
          }
          const pdf = new jsPDF({ orientation: 'l', unit: 'pt', format: [842, 595] });
          const el = document.querySelector('[data-pdf-pitch-container]');
          if (!el) return;
          const slideOriginal = slide;
          const bgPdf = ehNeutro ? (temaDef?.paper || '#F5F2EA') : '#F4EDE0';
          for (let i = 1; i <= totalSlides; i++) {
            setSlide(i);
            await new Promise(r => setTimeout(r, 400));
            const canvas = await html2canvas(el, { scale: 2, useCORS: true,
              backgroundColor: bgPdf, logging: false,
              windowWidth: canvasW, windowHeight: canvasH });
            const imgData = canvas.toDataURL('image/jpeg', 0.92);
            if (i > 1) pdf.addPage();
            pdf.addImage(imgData, 'JPEG', 0, 0, 842, 595);
          }
          pdf.save(`${(empresa?.nome || 'Empresa').replace(/\s+/g,'_')}_Pitch_Deck.pdf`);
          setSlide(slideOriginal);
        }}
        style={{ position: 'absolute', top: 20, right: 130,
          background: PD.verde, border: `1px solid ${PD.verde}`,
          color: '#FFF', padding: '6px 12px', fontSize: 11,
          fontWeight: 700, letterSpacing: '0.10em', textTransform: 'uppercase',
          borderRadius: 0, cursor: 'pointer', fontFamily: PD.sans }}>
        ↓ Baixar PDF
      </button>
      <button onClick={() => setModoDemo(!modoDemo)}
        title="Carrega dados sintéticos de ShopMax para visualização do Pitch Deck"
        style={{ position: 'absolute', top: 20, left: 20,
          background: modoDemo ? PD.laranja : 'transparent',
          border: `1px solid ${modoDemo ? PD.laranja : '#FFF'}`,
          color: '#FFF', padding: '6px 12px', fontSize: 11,
          fontWeight: 700, letterSpacing: '0.10em', textTransform: 'uppercase',
          borderRadius: 0, cursor: 'pointer', fontFamily: PD.sans }}>
        {modoDemo ? 'Modo Demo · ON' : 'Modo Demo · OFF'}
      </button>
      <button onClick={() => slide > 1 && setSlide(slide - 1)}
        disabled={slide === 1}
        style={{ position: 'absolute', left: 30, top: '50%',
          transform: 'translateY(-50%)',
          background: 'rgba(255,255,255,0.15)', border: 'none',
          color: '#FFF', width: 48, height: 48, fontSize: 24,
          borderRadius: '50%', cursor: slide === 1 ? 'not-allowed' : 'pointer',
          opacity: slide === 1 ? 0.3 : 1 }}>
        ←
      </button>
      <button onClick={() => slide < totalSlides && setSlide(slide + 1)}
        disabled={slide === totalSlides}
        style={{ position: 'absolute', right: 30, top: '50%',
          transform: 'translateY(-50%)',
          background: 'rgba(255,255,255,0.15)', border: 'none',
          color: '#FFF', width: 48, height: 48, fontSize: 24,
          borderRadius: '50%', cursor: slide === totalSlides ? 'not-allowed' : 'pointer',
          opacity: slide === totalSlides ? 0.3 : 1 }}>
        →
      </button>
    </div>
  );
};

const ConteudoTarefa15 = ({ meta, equityFlag, descricaoEmpresa, vmv, produtosServicos, mercadoConcorrencia, swot, estrategiaModelo, marketingVendas, estruturaRh, projecoes, valuation, conclusao, setConclusao, empresa, modo = 'EE', estruturaFin, parametros }) => {
  const ehSU = modo === 'SU';
  const HEADER_BLOCO = {
    fontSize: 16, fontWeight: 700, letterSpacing: '0.10em',
    textTransform: 'uppercase', color: G.laranja, marginBottom: 8,
  };
  const HEADER_AUX = {
    fontSize: 12, fontWeight: 400, color: G.txt2,
    textTransform: 'none', letterSpacing: 0, marginLeft: 10,
  };
  const fmtMi = (v) => v >= 1_000_000 ? `R$ ${(v/1_000_000).toFixed(2)} mi` : `R$ ${(v/1000).toFixed(0)} mil`;
  const tipoMetaSel  = (ehSU ? TIPOS_META_BP60M_SU : TIPOS_META_BP60M).find(t => t.id === meta?.tipo);
  const tipoCapSel   = (ehSU ? TIPOS_CAPITAL_T1_SU : TIPOS_CAPITAL_T1).find(t => t.id === meta?.tipoCapital);
  const exitHorSel   = (ehSU ? EXIT_HORIZONTE_T1_SU : EXIT_HORIZONTE_T1).find(t => t.id === meta?.exitHorizonte);
  // Valuation da startup · mesma fonte única da Tarefa 15 (faixa piso↔teto + central).
  const valSU = ehSU ? (() => {
    const base = calcularBaseFinSU(estruturaFin, produtosServicos, empresa, meta);
    const curva = (Array.isArray(projecoes?.crescimentoSU) && projecoes.crescimentoSU.length === 4)
      ? projecoes.crescimentoSU.map(p => (Number(p) || 0) / 100) : undefined;
    const anos = calcularProjecaoSU(base, meta, curva);
    const waccReal = calcularWACCReal(parametros);
    return calcularValuationSU(base, anos, empresa, valuation, waccReal != null ? waccReal * 100 : null, 3);
  })() : null;
  const equityLabel  = {
    sim: 'Sim, considero captação de Equity',
    em_breve: 'Em breve, ainda avaliando',
    nao: 'Não, crescimento via geração própria',
    true: 'Sim, considero captação de Equity',
    false: 'Não, crescimento via geração própria',
  }[ehSU ? 'sim' : equityFlag] || '—';
  const conf = conclusao || { funding: null, exit: null };
  const setConf = (k, v) => setConclusao({ ...conf, [k]: v });

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

      {/* Bloco 1 · Recapitulação (painel B1/B2/B3) */}
      <div>
        <div style={HEADER_BLOCO}>
          Bloco 1 · Recapitulação
          <span style={HEADER_AUX}>· o que você decidiu · conteste se algo mudou</span>
        </div>
      <div style={{ display: 'flex', flexDirection: 'column',
        background: '#FAFAF7', border: `1px solid ${G.borda}`,
        borderRadius: 0, overflow: 'hidden' }}>

        {/* Linha B1 · Recapitulação */}
        <div style={{ padding: '10px 16px', display: 'flex',
          alignItems: 'center', gap: 12,
          borderBottom: `1px solid ${G.borda}` }}>
          <div style={{ fontSize: 10, fontWeight: 800, color: G.txt2,
            letterSpacing: '0.10em', textTransform: 'uppercase',
            minWidth: 120 }}>
            Recapitulação
          </div>
          <div style={{ flex: 1, fontSize: 12.5, color: G.txt1, lineHeight: 1.5 }}>
            <strong>Meta · </strong>{tipoMetaSel?.fraseAntes}<strong style={{ color: G.verde }}>{meta?.valor}</strong>{tipoMetaSel?.fraseDepois}
            {ehSU
              ? (valSU && valSU.faixaMax > 0 && <> · <strong>Valuation: </strong><strong style={{ color: G.verde }}>{fmtMi(valSU.faixaMin)}</strong> piso · <strong style={{ color: G.laranja }}>{fmtMi(valSU.faixaMax)}</strong> pedido</>)
              : (valuation?.evCalc && <> · <strong>Valuation: </strong><strong style={{ color: G.verde }}>{fmtMi(valuation.evCalc)}</strong> piso · <strong style={{ color: G.laranja }}>{fmtMi(valuation.evCalc * 1.17)}</strong> pedido</>)}
          </div>
        </div>

        {/* Linha B2 · Funding */}
        <div style={{ padding: '10px 16px',
          borderBottom: `1px solid ${G.borda}` }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{ fontSize: 10, fontWeight: 800, color: G.txt2,
              letterSpacing: '0.10em', textTransform: 'uppercase',
              minWidth: 120 }}>
              Funding
            </div>
            <div style={{ flex: 1, fontSize: 12.5, color: G.txt1, lineHeight: 1.5 }}>
              {equityLabel}{tipoCapSel && <> · capital preferido <strong>{tipoCapSel.titulo}</strong></>}
            </div>
            <button onClick={() => setConf('funding', conf.funding === 'ajusto' ? null : 'ajusto')}
              style={{ background: 'transparent', border: `1px solid ${G.borda}`,
                color: G.txt2, padding: '4px 10px',
                fontSize: 10, fontWeight: 700, letterSpacing: '0.08em',
                textTransform: 'uppercase', borderRadius: 0, cursor: 'pointer',
                fontFamily: "'Outfit', sans-serif" }}>
              {conf.funding === 'ajusto' ? 'Fechar' : 'Contestar'}
            </button>
          </div>
          {conf.funding === 'ajusto' && (
            <div style={{ marginTop: 10, padding: '10px 12px',
              background: G.laranjaLight, borderLeft: `3px solid ${G.laranja}`,
              borderRadius: 0, fontSize: 12, color: G.txt1, lineHeight: 1.5 }}>
              Para ajustar, volte à <strong>Tarefa 1</strong> e revise a Tese de Equity e o tipo de capital. As demais tarefas se ajustam automaticamente.
            </div>
          )}
        </div>

        {/* Linha B3 · Exit */}
        <div style={{ padding: '10px 16px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{ fontSize: 10, fontWeight: 800, color: G.txt2,
              letterSpacing: '0.10em', textTransform: 'uppercase',
              minWidth: 120 }}>
              Exit
            </div>
            <div style={{ flex: 1, fontSize: 12.5, color: G.txt1, lineHeight: 1.5 }}>
              {exitHorSel?.titulo || 'Saída em 5 anos (meta de Saída via Venda)'}
            </div>
            <button onClick={() => setConf('exit', conf.exit === 'ajusto' ? null : 'ajusto')}
              style={{ background: 'transparent', border: `1px solid ${G.borda}`,
                color: G.txt2, padding: '4px 10px',
                fontSize: 10, fontWeight: 700, letterSpacing: '0.08em',
                textTransform: 'uppercase', borderRadius: 0, cursor: 'pointer',
                fontFamily: "'Outfit', sans-serif" }}>
              {conf.exit === 'ajusto' ? 'Fechar' : 'Contestar'}
            </button>
          </div>
          {conf.exit === 'ajusto' && (
            <div style={{ marginTop: 10, padding: '10px 12px',
              background: G.laranjaLight, borderLeft: `3px solid ${G.laranja}`,
              borderRadius: 0, fontSize: 12, color: G.txt1, lineHeight: 1.5 }}>
              Para ajustar, volte à <strong>Tarefa 1</strong> e revise o horizonte de Exit. O perfil do comprador é refinado na <strong>Tarefa 5</strong>.
            </div>
          )}
        </div>
      </div>
      </div>

      {/* Bloco 2 · Documento Institucional · sempre visível */}
      {true && (
        <div>
          <div style={HEADER_BLOCO}>
            Bloco 2 · Documento Institucional de Conclusão
            <span style={HEADER_AUX}>· anexo ao Relatório Geral · insumo para Teaser, Pitch Deck e Information Memorandum</span>
          </div>

          <div style={{ background: '#FFF', border: `1px solid ${G.borda}`,
            borderRadius: 0, padding: '28px 32px',
            display: 'flex', flexDirection: 'column', gap: 22 }}>

            {/* Cabeçalho do documento */}
            <div style={{ borderBottom: `2px solid ${G.verde}`,
              paddingBottom: 14, marginBottom: 4 }}>
              <div style={{ fontSize: 10.5, fontWeight: 800,
                letterSpacing: '0.16em', textTransform: 'uppercase',
                color: G.verde, marginBottom: 6 }}>
                Conclusão Estratégica · Business Plan 60 meses
              </div>
              <div style={{ fontSize: 22, fontWeight: 900, color: G.txt1,
                letterSpacing: '-0.01em', lineHeight: 1.15 }}>
                {empresa?.nome || 'Empresa'}
              </div>
              <div style={{ fontSize: 12, color: G.txt2, marginTop: 4 }}>
                Emissão: {new Date().toLocaleDateString('pt-BR')} · Documento técnico produzido por{' '}
                <MarcaAIdvisor variant="default" />
              </div>
            </div>

            {/* 1 · Sumário Executivo */}
            <div>
              <div style={{ fontSize: 12, fontWeight: 800,
                letterSpacing: '0.14em', textTransform: 'uppercase',
                color: G.verde, marginBottom: 8 }}>
                1 · Sumário Executivo
              </div>
              <div style={{ fontSize: 13, lineHeight: 1.65, color: G.txt1 }}>
                A {empresa?.nome || 'empresa'} estabelece como meta {tipoMetaSel?.fraseAntes?.toLowerCase()}<strong>{meta?.valor}</strong>{tipoMetaSel?.fraseDepois} O caminho recomendado contempla
                {tipoCapSel ? <> a estruturação de capital via <strong>{tipoCapSel.titulo}</strong>,</> : ' o crescimento sustentado pela geração própria de caixa,'}
                {exitHorSel ? <> com horizonte de saída em <strong>{exitHorSel.titulo}</strong>.</> : ' mantendo a empresa sob controle dos sócios atuais.'}
                {ehSU
                  ? (valSU && valSU.faixaMax > 0 && <> O valor da empresa (pré-money) situa-se entre <strong>{fmtMi(valSU.faixaMin)}</strong> (piso) e <strong>{fmtMi(valSU.faixaMax)}</strong> (pedido), triangulado por múltiplos comparáveis, fluxo de caixa descontado e método do capital de risco.</>)
                  : (valuation?.evCalc && <> O valuation projetado situa-se entre <strong>{fmtMi(valuation.evCalc)}</strong> (piso) e <strong>{fmtMi(valuation.evCalc * 1.17)}</strong> (pedido), com sustentação no modelo DCF e múltiplo setorial.</>)}
              </div>
            </div>

            {/* 2 · Diagnóstico Estratégico */}
            <div>
              <div style={{ fontSize: 12, fontWeight: 800,
                letterSpacing: '0.14em', textTransform: 'uppercase',
                color: G.verde, marginBottom: 8 }}>
                2 · Diagnóstico Estratégico
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr',
                gap: 12 }}>
                <div style={{ background: G.fundo, borderLeft: `3px solid ${G.verde}`,
                  padding: '10px 14px', borderRadius: 0 }}>
                  <div style={{ fontSize: 10, fontWeight: 800, color: G.verde,
                    letterSpacing: '0.10em', textTransform: 'uppercase',
                    marginBottom: 4 }}>
                    Estado atual
                  </div>
                  <div style={{ fontSize: 12, lineHeight: 1.5, color: G.txt1 }}>
                    Setor: <strong>{empresa?.setor || '—'}</strong>. {ehSU ? 'Estágio: startup em fase inicial · construindo tração e caminho para o caixa positivo.' : 'Porte: PME consolidada. Operação geradora de caixa.'}
                  </div>
                </div>
                <div style={{ background: G.fundo, borderLeft: `3px solid ${G.laranja}`,
                  padding: '10px 14px', borderRadius: 0 }}>
                  <div style={{ fontSize: 10, fontWeight: 800, color: G.laranja,
                    letterSpacing: '0.10em', textTransform: 'uppercase',
                    marginBottom: 4 }}>
                    Caminho recomendado
                  </div>
                  <div style={{ fontSize: 12, lineHeight: 1.5, color: G.txt1 }}>
                    {tipoCapSel?.titulo || (ehSU ? 'Bootstrap · geração própria' : 'Crescimento orgânico')}. Fundamentação: {ehSU ? 'comparáveis de mercado, retorno-alvo de venture e a Meta da Tarefa 1' : 'Damodaran, ABVCAP e expectativas BCB'}.
                  </div>
                </div>
                <div style={{ background: G.fundo, borderLeft: `3px solid ${G.txt2}`,
                  padding: '10px 14px', borderRadius: 0 }}>
                  <div style={{ fontSize: 10, fontWeight: 800, color: G.txt2,
                    letterSpacing: '0.10em', textTransform: 'uppercase',
                    marginBottom: 4 }}>
                    Sensibilidade
                  </div>
                  <div style={{ fontSize: 12, lineHeight: 1.5, color: G.txt1 }}>
                    {ehSU ? 'O valor é uma faixa (piso pelo DCF · teto pelos múltiplos · equilíbrio pelo VC Method), refletindo a incerteza natural de uma startup.' : 'Cenários otimista e pessimista calculados em T14. Variação WACC × g cobre 25% do EV.'}
                  </div>
                </div>
              </div>
            </div>

            {/* 3 · Plano de Execução · 60 meses */}
            <div>
              <div style={{ fontSize: 12, fontWeight: 800,
                letterSpacing: '0.14em', textTransform: 'uppercase',
                color: G.verde, marginBottom: 8 }}>
                3 · Plano de Execução · 60 meses
              </div>
              <table style={{ width: '100%', fontSize: 12,
                borderCollapse: 'collapse', fontFamily: "'Outfit', sans-serif" }}>
                <thead>
                  <tr style={{ background: G.fundo,
                    borderBottom: `2px solid ${G.borda}` }}>
                    <th style={{ padding: '6px 10px', textAlign: 'left',
                      fontSize: 10, fontWeight: 800, color: G.txt2,
                      letterSpacing: '0.06em', textTransform: 'uppercase' }}>Ano</th>
                    <th style={{ padding: '6px 10px', textAlign: 'left',
                      fontSize: 10, fontWeight: 800, color: G.txt2,
                      letterSpacing: '0.06em', textTransform: 'uppercase' }}>Marco-chave</th>
                    <th style={{ padding: '6px 10px', textAlign: 'left',
                      fontSize: 10, fontWeight: 800, color: G.txt2,
                      letterSpacing: '0.06em', textTransform: 'uppercase' }}>Premissa crítica</th>
                    <th style={{ padding: '6px 10px', textAlign: 'left',
                      fontSize: 10, fontWeight: 800, color: G.txt2,
                      letterSpacing: '0.06em', textTransform: 'uppercase' }}>Risco · mitigação</th>
                  </tr>
                </thead>
                <tbody>
                  {(ehSU ? [
                    ['Ano 1', 'Product-Market Fit', 'Tração inicial · primeiros clientes fiéis', 'Produto não encaixa · iterar rápido'],
                    ['Ano 2', 'Go-to-market · aquisição', 'CAC abaixo do LTV', 'Canal caro · testar vários canais'],
                    ['Ano 3', 'Rodada de captação', 'Métricas que atraem investidor', 'Runway curto · antecipar a captação'],
                    ['Ano 4', 'Escala · Série A', 'Crescer com eficiência de caixa', 'Diluição de cultura · governança'],
                    ['Ano 5', 'Saída · M&A ou IPO', 'Valuation validado pela tração', 'Janela de mercado · timing'],
                  ] : [
                    ['Ano 1', 'Consolidação operacional', 'Margem EBITDA estável', 'Pressão de CPV · contratos longos'],
                    ['Ano 2', 'Captura de mercado', 'Aquisição de clientes no plano', 'Concorrência · diferencial claro'],
                    ['Ano 3', 'Aceleração com novo CAPEX', 'Conversão de investimento em receita', 'Atraso execução · gestão de portfólio'],
                    ['Ano 4', 'Expansão geográfica · produto', 'Sustentar margem em escala', 'Diluição de cultura · governança'],
                    ['Ano 5', 'Meta atingida · pronto para o evento', 'Valuation validado pelos números', 'Janela de mercado · timing estratégico'],
                  ]).map((row, i, arr) => (
                    <tr key={i} style={{ borderBottom: i < arr.length - 1 ? `1px solid ${G.borda}` : 'none' }}>
                      <td style={{ padding: '6px 10px', fontWeight: 700, color: G.txt1 }}>{row[0]}</td>
                      <td style={{ padding: '6px 10px', color: G.txt1 }}>{row[1]}</td>
                      <td style={{ padding: '6px 10px', color: G.txt2 }}>{row[2]}</td>
                      <td style={{ padding: '6px 10px', color: G.txt2 }}>{row[3]}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* 4 · Indicadores de Performance */}
            <div>
              <div style={{ fontSize: 12, fontWeight: 800,
                letterSpacing: '0.14em', textTransform: 'uppercase',
                color: G.verde, marginBottom: 8 }}>
                4 · Indicadores de Performance · KPIs
              </div>
              <table style={{ width: '100%', fontSize: 12,
                borderCollapse: 'collapse', fontFamily: "'Outfit', sans-serif" }}>
                <thead>
                  <tr style={{ background: G.fundo,
                    borderBottom: `2px solid ${G.borda}` }}>
                    <th style={{ padding: '6px 10px', textAlign: 'left',
                      fontSize: 10, fontWeight: 800, color: G.txt2,
                      letterSpacing: '0.06em', textTransform: 'uppercase' }}>Indicador</th>
                    <th style={{ padding: '6px 10px', textAlign: 'right',
                      fontSize: 10, fontWeight: 800, color: G.txt2,
                      letterSpacing: '0.06em', textTransform: 'uppercase' }}>Alvo</th>
                    <th style={{ padding: '6px 10px', textAlign: 'left',
                      fontSize: 10, fontWeight: 800, color: G.txt2,
                      letterSpacing: '0.06em', textTransform: 'uppercase' }}>Fonte</th>
                  </tr>
                </thead>
                <tbody>
                  {(ehSU ? [
                    ['Receita · crescimento a.a.', 'pela curva da Meta', 'Projeções (Tarefa 14)'],
                    ['MRR · receita recorrente',   'crescente',          'Tarefa 14'],
                    ['CAC / LTV',                  'LTV > 3× CAC',       'Marketing (Tarefa 8)'],
                    ['Churn mensal',               'baixo e decrescente','Analytics'],
                    ['Burn rate · queima de caixa','sob controle',       'Estruturação (Tarefa 13)'],
                    ['Runway · meses de caixa',    'até a próxima rodada','Estruturação (Tarefa 13)'],
                    ['Valuation · pré-money',      valSU && valSU.central > 0 ? fmtMi(valSU.central) : '—', 'Métodos de startup (Tarefa 15)'],
                  ] : [
                    ['Receita líquida (CAGR)',  '13% a.a.', 'Projeções financeiras'],
                    ['Margem EBITDA',           '22%',       'Projeções financeiras'],
                    ['FCF anual',               'R$ 2,5 mi', 'Projeções financeiras'],
                    ['Dívida líquida / EBITDA', '≤ 1,5×',    'Balanço Patrimonial'],
                    ['ROIC',                    '> WACC',    'Indicadores Dashboard'],
                    ['Liquidez corrente',       '≥ 1,5',     'Balanço Patrimonial'],
                    ['Valuation alcançado',     valuation?.evCalc ? fmtMi(valuation.evCalc) : '—', 'Modelo DCF'],
                  ]).map((row, i, arr) => (
                    <tr key={i} style={{ borderBottom: i < arr.length - 1 ? `1px solid ${G.borda}` : 'none' }}>
                      <td style={{ padding: '6px 10px', color: G.txt1 }}>{row[0]}</td>
                      <td style={{ padding: '6px 10px', textAlign: 'right',
                        fontWeight: 800, color: G.verde }}>{row[1]}</td>
                      <td style={{ padding: '6px 10px', color: G.txt2 }}>{row[2]}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* 5 · Pacote de Materiais (menção · abertura na Tarefa 16) */}
            <div>
              <div style={{ fontSize: 12, fontWeight: 800,
                letterSpacing: '0.14em', textTransform: 'uppercase',
                color: G.verde, marginBottom: 8 }}>
                5 · Pacote de Materiais
              </div>
              <div style={{ background: G.fundo, border: `1px solid ${G.borda}`,
                borderLeft: `3px solid ${G.verde}`, padding: '12px 14px',
                fontFamily: "'Outfit', sans-serif" }}>
                <div style={{ fontSize: 12.5, color: G.txt1, lineHeight: 1.55,
                  marginBottom: 8 }}>
                  Quatro documentos serão entregues a partir das decisões consolidadas neste BP60M:
                </div>
                <ul style={{ margin: 0, padding: '0 0 0 20px', fontSize: 12,
                  color: G.txt1, lineHeight: 1.8 }}>
                  <li><strong>Teaser</strong> · aproximação anônima de investidor ou comprador.</li>
                  <li><strong>Pitch Deck</strong> · narrativa executiva em 10 slides com ASK de captação.</li>
                  <li><strong>Information Memorandum</strong> · documento completo · base para Due Diligence.</li>
                  <li><strong>Plano de Ação Interno</strong> · cronograma operacional · responsáveis · KPIs.</li>
                </ul>
                <div style={{ marginTop: 10, fontSize: 11.5, color: G.txt2,
                  fontStyle: 'italic' }}>
                  Acesse, abra e baixe cada documento na <strong style={{ color: G.verde, fontStyle: 'normal' }}>{ehSU ? 'Tarefa 17 · Entrega' : 'Tarefa 16 · Entrega'}</strong>.
                </div>
              </div>
            </div>

            {/* 6 · Assinatura institucional */}
            <div style={{ borderTop: `1px solid ${G.borda}`,
              paddingTop: 12, marginTop: 4,
              display: 'flex', justifyContent: 'space-between',
              alignItems: 'baseline' }}>
              <div style={{ fontSize: 11.5, color: G.txt2, fontStyle: 'italic' }}>
                Documento técnico produzido por <MarcaAIdvisor variant="default" /> · base no Business Plan 60 meses.
              </div>
              <div style={{ fontSize: 11, color: G.txt2 }}>
                {new Date().toLocaleDateString('pt-BR')}
              </div>
            </div>

          </div>
        </div>
      )}

    </div>
  );
};

// =====================================================================
// ConteudoTarefa16 — Entrega
// Sumário Executivo + 4 documentos (abrir + baixar PDF) + Próximos Passos
// =====================================================================
const ConteudoTarefa16 = ({ meta, equityFlag, empresa, vmv, produtosServicos,
  mercadoConcorrencia, swot, estrategiaModelo, marketingVendas, estruturaRh,
  projecoes, valuation, modo = 'EE', estruturaFin, parametros }) => {
  const [docAberto, setDocAberto] = useState(null);
  const ehSU = modo === 'SU';
  // Valuation da startup · mesma fonte única da Tarefa 15 (faixa piso↔teto + central).
  const valSU = ehSU ? (() => {
    const base = calcularBaseFinSU(estruturaFin, produtosServicos, empresa, meta);
    const curva = (Array.isArray(projecoes?.crescimentoSU) && projecoes.crescimentoSU.length === 4)
      ? projecoes.crescimentoSU.map(p => (Number(p) || 0) / 100) : undefined;
    const anosSU = calcularProjecaoSU(base, meta, curva);
    const waccReal = calcularWACCReal(parametros);
    const val = calcularValuationSU(base, anosSU, empresa, valuation, waccReal != null ? waccReal * 100 : null, 3);
    // Captação sugerida = maior déficit de caixa acumulado (a queima até o caixa virar positivo).
    let cum = 0, trough = 0;
    for (const a of anosSU) { cum += (a.fcf || 0); if (cum < trough) trough = cum; }
    const captacao = (valuation?.captacaoSU != null) ? valuation.captacaoSU : (trough < 0 ? Math.round(-trough) : null);
    const preMoney = (val && val.central > 0) ? val.central : null;
    const equityPct = (captacao && preMoney) ? +(captacao / (preMoney + captacao) * 100).toFixed(1) : null;
    return { ...val, anos: anosSU, base, captacao, equityPct };
  })() : null;
  // Regra de perfil: toda startup no BP60M obrigatoriamente capta · equity assumido 'sim' no SU.
  const bpData = { meta, equityFlag: ehSU ? 'sim' : equityFlag, empresa, vmv, produtosServicos,
    mercadoConcorrencia, swot, estrategiaModelo, marketingVendas, estruturaRh,
    projecoes, valuation, modo, valSU };
  const tipoMetaSel = TIPOS_META_BP60M.find(t => t.id === meta?.tipo);
  const fraseMeta = tipoMetaSel ? `${tipoMetaSel.fraseAntes || ''}${meta?.valor || ''}${tipoMetaSel.fraseDepois || ''}` : '';
  const nomeEmp = empresa?.nome || 'Sua Empresa';
  const setorTexto = empresa?.setor || 'Setor';
  const anos = (projecoes?.anos || []).slice(0,5);
  const ev = valuation?.evCalc;
  const capt = valuation?.captacao;
  const ebMult = anos.length >= 2 && anos[0]?.ebitda && anos[anos.length-1]?.ebitda
    ? (anos[anos.length-1].ebitda / anos[0].ebitda).toFixed(1).replace('.', ',')
    : null;
  // Startup · projeção da fonte única (valSU) + múltiplo de receita e formato monetário
  const anosView = (ehSU && valSU?.anos) ? valSU.anos.slice(0, 5) : anos;
  const recMult = anosView.length >= 2 && anosView[0]?.receita && anosView[anosView.length-1]?.receita
    ? (anosView[anosView.length-1].receita / anosView[0].receita).toFixed(1).replace('.', ',')
    : null;
  const fmtMi = (v) => v >= 1_000_000 ? `R$ ${(v/1_000_000).toFixed(2)} mi` : `R$ ${(v/1000).toFixed(0)} mil`;
  const rodadaSel = RODADAS_SU.find(r => r.id === meta?.rodada);
  const materiais = [
    { titulo: 'Teaser', acao: 'teaser',
      desc: 'Documento de aproximação · anônimo · setor, métricas-chave, oportunidade.',
      publico: 'Aproximação com investidor ou comprador.' },
    { titulo: 'Pitch Deck', acao: 'pitch',
      desc: '10 slides Guy Kawasaki · narrativa da jornada + ASK de captação.',
      publico: 'Apresentação executiva a investidor ou banco.' },
    { titulo: 'Information Memorandum', acao: 'im',
      desc: 'Versão completa · todos os capítulos do BP · base para Due Diligence.',
      publico: ehSU ? 'Investidor, fundo de VC ou sócio estratégico interessado em aprofundar.' : 'Investidor ou comprador interessado em aprofundar.' },
    { titulo: 'Plano de Ação Interno', acao: 'plano',
      desc: 'Cronograma operacional · responsáveis · acompanhamento mensal dos KPIs.',
      publico: 'Empresário e equipe interna.' },
  ];
  return (
    <div style={{ background: '#FFF', borderRadius: 0,
      padding: '24px 28px',
      border: `1px solid ${G.borda}`,
      fontFamily: "'Outfit', sans-serif" }}>

      {/* 1 · Sumário Executivo */}
      <div style={{ marginBottom: 22 }}>
        <div style={{ fontSize: 12, fontWeight: 800,
          letterSpacing: '0.14em', textTransform: 'uppercase',
          color: G.verde, marginBottom: 8 }}>
          1 · Sumário Executivo
        </div>
        <div style={{ background: G.fundo, border: `1px solid ${G.borda}`,
          borderLeft: `3px solid ${G.verde}`, padding: '14px 16px',
          fontSize: 13, color: G.txt1, lineHeight: 1.6 }}>
          <strong>{nomeEmp}</strong> · operação brasileira de {setorTexto.toLowerCase()},
          com plano consolidado para 60 meses. A meta declarada é
          <em> {fraseMeta || 'crescimento sustentado'}</em>.
          {anosView[0]?.receita && anosView[anosView.length-1]?.receita && (
            <> As projeções indicam crescimento da receita de
            {' '}{ehSU ? fmtMi(anosView[0].receita) : `R$ ${(anosView[0].receita/1_000_000).toFixed(0)} mi`} em {anosView[0].ano} para
            {' '}{ehSU ? fmtMi(anosView[anosView.length-1].receita) : `R$ ${(anosView[anosView.length-1].receita/1_000_000).toFixed(0)} mi`} em {anosView[anosView.length-1].ano}
            {ehSU
              ? (recMult && <>, com <strong>a receita multiplicando {recMult}×</strong> no período</>)
              : (ebMult && <>, com <strong>EBITDA crescendo {ebMult}×</strong> no período</>)}.</>
          )}
          {ehSU
            ? (valSU && valSU.faixaMax > 0 && (
                <> A avaliação preliminar situa o valor da empresa (pré-money) entre <strong>{fmtMi(valSU.faixaMin)}</strong> (piso) e <strong>{fmtMi(valSU.faixaMax)}</strong> (pedido), triangulado por múltiplos comparáveis, fluxo de caixa descontado e método do capital de risco.</>
              ))
            : (ev && (
                <> A avaliação preliminar pelo método DCF resulta em Enterprise Value
                de <strong>R$ {(ev/1_000_000).toFixed(0)} mi</strong>{valuation?.wacc && ` (WACC ${valuation.wacc}%)`}.</>
              ))}
          {!ehSU && (equityFlag === 'sim' || equityFlag === true) && capt && (
            <> Captação pretendida de <strong>R$ {(capt/1_000_000).toFixed(0)} mi</strong> por
            aproximadamente 22% de equity.</>
          )}
          {ehSU && valSU?.captacao && (
            <> Captação pretendida de <strong>{fmtMi(valSU.captacao)}</strong>
            {rodadaSel && <> na <strong>Rodada {rodadaSel.titulo}</strong></>}
            {valSU.equityPct && <>, equivalente a aproximadamente <strong>{String(valSU.equityPct).replace('.', ',')}%</strong> de equity</>}
            {valSU.central > 0 && <> (pré-money de {fmtMi(valSU.central)})</>}.</>
          )}
        </div>
      </div>

      {/* 2 · Pacote de Materiais */}
      <div style={{ marginBottom: 22 }}>
        <div style={{ fontSize: 12, fontWeight: 800,
          letterSpacing: '0.14em', textTransform: 'uppercase',
          color: G.verde, marginBottom: 8 }}>
          2 · Pacote de Materiais
        </div>
        <div style={{ fontSize: 12.5, color: G.txt2, lineHeight: 1.55,
          marginBottom: 12 }}>
          Os quatro documentos abaixo foram gerados pelo <MarcaAIdvisor variant="default" /> a
          partir de todas as decisões consolidadas no Business Plan. Clique em <strong>Abrir</strong> para
          visualizar em tela cheia ou em <strong>Baixar PDF</strong> para download direto.
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          {materiais.map((m, i) => (
            <div key={i}
              style={{ background: G.fundo,
                border: `1px solid ${G.borda}`, borderRadius: 0,
                padding: '14px 18px',
                fontFamily: "'Outfit', sans-serif" }}>
              <div style={{ fontSize: 14, fontWeight: 800, color: G.txt1,
                marginBottom: 6 }}>
                {m.titulo}
              </div>
              <div style={{ fontSize: 12, color: G.txt2, lineHeight: 1.5,
                marginBottom: 4 }}>
                {m.desc}
              </div>
              <div style={{ fontSize: 11, fontStyle: 'italic',
                color: G.txt2, letterSpacing: '0.04em', marginBottom: 10 }}>
                Público: {m.publico}
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button type="button" onClick={() => setDocAberto(m.acao)}
                  style={{ flex: 1, background: '#FFF',
                    border: `1px solid ${G.verde}`, borderRadius: 0,
                    padding: '6px 12px', fontSize: 11, fontWeight: 700,
                    color: G.verde, letterSpacing: '0.06em',
                    textTransform: 'uppercase', cursor: 'pointer',
                    fontFamily: "'Outfit', sans-serif" }}>
                  Abrir →
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* 3 · Próximos Passos */}
      <div>
        <div style={{ fontSize: 12, fontWeight: 800,
          letterSpacing: '0.14em', textTransform: 'uppercase',
          color: G.verde, marginBottom: 8 }}>
          3 · Próximos Passos
        </div>
        <ol style={{ margin: 0, padding: '0 0 0 22px',
          fontSize: 13, color: G.txt1, lineHeight: 1.8 }}>
          <li><strong>Revisão final:</strong> percorrer os 4 documentos e confirmar dados e narrativa.</li>
          <li><strong>Baixar PDFs:</strong> exportar cada documento via botão <em>Baixar PDF</em> nos modais.</li>
          {(equityFlag === 'sim' || equityFlag === true) ? (
            <li><strong>Aproximação:</strong> enviar Teaser para investidor-âncora · negociar NDA · liberar IM completo.</li>
          ) : (
            <li><strong>Reserva estratégica:</strong> arquivar Pacote como referência para próxima janela de captação.</li>
          )}
          <li><strong>Cadência operacional:</strong> seguir o calendário de revisões do Plano de Ação Interno (mensal · trimestral · anual).</li>
          <li><strong>Atualização anual:</strong> revisitar o BP60M ao fim de cada ano fiscal · refazer documentos com números reais.</li>
        </ol>
      </div>

      {docAberto === 'pitch'  && <PitchDeck bp={bpData} onFechar={() => setDocAberto(null)}/>}
      {docAberto === 'teaser' && <Teaser bp={bpData} onFechar={() => setDocAberto(null)}/>}
      {docAberto === 'im'     && <InformationMemorandum bp={bpData} onFechar={() => setDocAberto(null)}/>}
      {docAberto === 'plano'  && <PlanoAcaoInterno bp={bpData} onFechar={() => setDocAberto(null)}/>}
    </div>
  );
};

// =====================================================================
// Teaser · documento de aproximação anônimo M&A · 1 página retrato
// Padrão: Rothschild · Houlihan · BR Partners · Vinci
// =====================================================================

const codinomeFromSetor = (setor) => {
  if (!setor) return 'Project Alpha';
  const s = setor.toLowerCase();
  if (s.includes('varej') || s.includes('e-commerce') || s.includes('comércio') || s.includes('loja')) return 'Project Vitrine';
  if (s.includes('tech') || s.includes('software') || s.includes('saas')) return 'Project Code';
  if (s.includes('saúde') || s.includes('clínica') || s.includes('hospital')) return 'Project Cura';
  if (s.includes('serviç') || s.includes('consult') || s.includes('b2b')) return 'Project Servir';
  if (s.includes('indústr') || s.includes('manufat')) return 'Project Forja';
  if (s.includes('alimen') || s.includes('restaurante') || s.includes('bar')) return 'Project Sabor';
  if (s.includes('constru')) return 'Project Pilar';
  return 'Project Alpha';
};

const setorAnonimizado = (setor) => {
  if (!setor) return 'Companhia brasileira';
  return `Companhia brasileira de ${setor.toLowerCase().split('·')[0].trim()}`;
};

const GraficoEbitdaTeaser = ({ anos, ehSU = false }) => {
  const mk = ehSU ? 'receita' : 'ebitda';
  const fmtG = (v) => Math.abs(v) >= 1_000_000 ? `${(v/1_000_000).toFixed(1).replace('.', ',')} mi` : `${(v/1000).toFixed(0)} mil`;
  if (!anos || anos.length < 2 || !anos[anos.length-1]?.[mk]) {
    return (
      <div style={{ fontFamily: PD.sans, fontSize: 11,
        color: PD.txt3, fontStyle: 'italic', alignSelf: 'center' }}>
        {ehSU ? 'Cadastre projeções para exibir trajetória de receita.' : 'Cadastre projeções para exibir trajetória EBITDA.'}
      </div>
    );
  }
  const valores = anos.map(a => a?.[mk] || 0);
  const max = Math.max(...valores) * 1.08;
  const W = 280, H = 140, pad = 24;
  const inner = W - pad * 2;
  const barW = (inner / valores.length) * 0.62;
  const gap = (inner / valores.length) * 0.38;
  const mult = (valores[valores.length-1] / valores[0]).toFixed(1).replace('.', ',');
  const ultimoMi = ehSU ? fmtG(valores[valores.length-1]) : (valores[valores.length-1] / 1_000_000).toFixed(0);

  return (
    <div>
      <div style={{ fontFamily: PD.mono, fontSize: 9.5,
        letterSpacing: '0.22em', textTransform: 'uppercase',
        color: PD.txt2, fontWeight: 700, marginBottom: 6 }}>
        {ehSU ? 'Trajetória de receita' : 'Trajetória EBITDA'}
      </div>
      <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`}
        style={{ display: 'block' }}>
        <defs>
          <linearGradient id="ebitdaGrad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={PD.laranja} stopOpacity="0.95" />
            <stop offset="100%" stopColor={PD.laranjaFunda} stopOpacity="0.7" />
          </linearGradient>
          <linearGradient id="ebitdaGradDim" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={PD.verdeMusgo} stopOpacity="0.55" />
            <stop offset="100%" stopColor={PD.verde} stopOpacity="0.45" />
          </linearGradient>
        </defs>
        <line x1={pad} y1={H-pad} x2={W-pad} y2={H-pad}
          stroke={PD.verdeNoite} strokeWidth="1" opacity="0.4" />
        {valores.map((v, i) => {
          const h = (v / max) * (H - pad * 2);
          const x = pad + gap/2 + i * (barW + gap);
          const y = H - pad - h;
          const isUltimo = i === valores.length - 1;
          return (
            <g key={i}>
              <rect x={x} y={y} width={barW} height={h}
                fill={isUltimo ? 'url(#ebitdaGrad)' : 'url(#ebitdaGradDim)'} />
              <text x={x + barW/2} y={H - pad + 14}
                textAnchor="middle"
                style={{ fontFamily: PD.mono, fontSize: 9,
                  fill: PD.txt2, letterSpacing: '0.05em' }}>
                {anos[i]?.ano || `A${i+1}`}
              </text>
              {isUltimo && (
                <text x={x + barW/2} y={y - 6}
                  textAnchor="middle"
                  style={{ fontFamily: PD.serif, fontSize: 14, fontWeight: 700,
                    fill: PD.laranjaFunda }}>
                  {ultimoMi}
                </text>
              )}
            </g>
          );
        })}
      </svg>
      <div style={{ marginTop: 4, fontFamily: PD.serif, fontSize: 13,
        fontStyle: 'italic', color: PD.verdeNoite, lineHeight: 1.4 }}>
        {ehSU ? 'A receita cresce' : 'EBITDA cresce'} <strong style={{ color: PD.laranjaFunda,
          fontWeight: 700 }}>{mult}×</strong> · {ehSU ? fmtG(valores[0]) : `R$ ${(valores[0]/1_000_000).toFixed(0)} mi`} → {ehSU ? fmtG(valores[valores.length-1]) : `R$ ${ultimoMi} mi`}.
      </div>
    </div>
  );
};

const Teaser = ({ bp, onFechar }) => {
  const [modoDemo, setModoDemo] = useState(false);
  const bpAtivo = modoDemo ? DEMO_SHOPMAX_BP : bp;
  const { meta, empresa, produtosServicos, mercadoConcorrencia, projecoes, valuation, equityFlag, vmv, modo, valSU } = bpAtivo;
  const ehSU = modo === 'SU';
  const rodadaSelT = ehSU ? RODADAS_SU.find(r => r.id === meta?.rodada) : null;
  const fmtMiT = (v) => v == null ? '—' : (Math.abs(v) >= 1_000_000 ? `R$ ${(v/1_000_000).toFixed(2)} mi` : `R$ ${(v/1000).toFixed(0)} mil`);

  const [scale, setScale] = useState(1);
  useEffect(() => {
    const recalcular = () => {
      const h = Math.min(window.innerHeight - 80, 1240);
      const w = Math.min(window.innerWidth - 80, 880);
      setScale(Math.min(h / 1240, w / 880));
    };
    recalcular();
    window.addEventListener('resize', recalcular);
    return () => window.removeEventListener('resize', recalcular);
  }, []);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onFechar(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onFechar]);

  const codinome = codinomeFromSetor(empresa?.setor);
  const setorAnon = setorAnonimizado(empresa?.setor);
  const cidade = empresa?.endereco?.split('·')[1]?.trim() || empresa?.endereco?.split(',')[0]?.trim() || 'Brasil';
  const anos = ((ehSU && valSU?.anos) ? valSU.anos : (projecoes?.anos || [])).slice(0, 5);
  const tipoMetaSel = (ehSU ? TIPOS_META_BP60M_SU : TIPOS_META_BP60M).find(t => t.id === meta?.tipo);
  const fraseMeta = tipoMetaSel ? `${tipoMetaSel.fraseAntes || ''}${meta?.valor || ''}${tipoMetaSel.fraseDepois || ''}` : '';

  // Razões da operação derivadas da meta + equityFlag
  let razao = ehSU
    ? `Captação${rodadaSelT ? ` na Rodada ${rodadaSelT.titulo}` : ''} para financiar produto, aquisição de clientes e operação ao longo de 60 meses, até a próxima etapa de crescimento.`
    : 'Captação de capital para acelerar o plano de crescimento e profissionalizar a governança.';
  if (!ehSU && meta?.tipo === 'saida') razao = 'Saída parcial dos sócios fundadores em horizonte de 60 meses, com profissionalização da gestão.';
  else if (!ehSU && meta?.tipo === 'ebitda_mult') razao = `Captação de capital para multiplicar EBITDA em ${meta?.valor || '5'}× ao longo de 60 meses, via expansão de operação e tecnologia.`;
  else if (!ehSU && meta?.tipo === 'receita_mult') razao = `Captação para multiplicar a Receita em ${meta?.valor || '3'}× em 60 meses, via expansão de canais e geografia.`;
  else if (!ehSU && meta?.tipo === 'margem_alvo') razao = `Captação para atingir margem EBITDA-alvo de ${meta?.valor || '20'}% em 60 meses, via reorganização operacional.`;

  const evMi = valuation?.evCalc ? (valuation.evCalc / 1_000_000).toFixed(0) : null;
  const captMi = valuation?.captacao ? (valuation.captacao / 1_000_000).toFixed(0) : null;

  // Highlights derivados do BP
  const highlights = [];
  if (produtosServicos?.diferenciais?.length > 0) {
    produtosServicos.diferenciais.slice(0, 3).forEach(d => highlights.push(d.texto || d));
  }
  if (anos[0]?.receita && anos[anos.length-1]?.receita) {
    const cagr = ((Math.pow(anos[anos.length-1].receita / anos[0].receita, 1/(anos.length-1)) - 1) * 100).toFixed(0);
    highlights.push(`CAGR Receita projetado de ${cagr}% em ${anos.length} anos.`);
  }
  if (mercadoConcorrencia?.som) {
    const somBi = (mercadoConcorrencia.som / 1_000_000_000).toFixed(1).replace('.', ',');
    highlights.push(`Mercado endereçável (SOM) estimado em R$ ${somBi} bilhões.`);
  }
  const highlightsFinal = highlights.slice(0, 5);

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(10,10,8,0.92)',
      zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ width: 880 * scale, height: 1240 * scale, position: 'relative',
        boxShadow: '0 30px 80px rgba(0,0,0,0.5)' }}>
        <div data-pdf-teaser style={{ transform: `scale(${scale})`, transformOrigin: 'top left',
          width: 880, height: 1240 }}>
          <section style={{ width: 880, height: 1240, background: PD.cream,
            color: PD.txt1, fontFamily: PD.sans, padding: '60px 70px',
            boxSizing: 'border-box', position: 'relative', overflow: 'hidden' }}>

            {/* Header */}
            <div style={{ display: 'flex', justifyContent: 'space-between',
              alignItems: 'flex-start', paddingBottom: 20,
              borderBottom: `1px solid ${PD.txt1}30` }}>
              <div>
                <div style={{ fontFamily: PD.mono, fontSize: 10,
                  letterSpacing: '0.3em', textTransform: 'uppercase',
                  color: PD.verde, fontWeight: 700 }}>
                  Teaser · Confidencial
                </div>
                <div style={{ marginTop: 8, fontFamily: PD.serif, fontWeight: 600,
                  fontSize: 38, color: PD.verdeNoite, letterSpacing: '-0.02em',
                  lineHeight: 1.05 }}>
                  {codinome}
                </div>
                <div style={{ marginTop: 6, fontFamily: PD.serif,
                  fontStyle: 'italic', fontSize: 16, color: PD.txt2 }}>
                  {setorAnon} · {cidade}
                </div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontFamily: PD.mono, fontSize: 10,
                  letterSpacing: '0.25em', textTransform: 'uppercase',
                  color: PD.txt3 }}>Documento</div>
                <div style={{ marginTop: 4, fontFamily: PD.mono, fontSize: 11,
                  color: PD.txt2 }}>1 / 1</div>
                <div style={{ marginTop: 10, fontFamily: PD.mono, fontSize: 10,
                  letterSpacing: '0.2em', textTransform: 'uppercase',
                  color: PD.laranja, fontWeight: 600 }}>
                  Junho · 2026
                </div>
              </div>
            </div>

            {/* Resumo */}
            <div style={{ marginTop: 22 }}>
              <SecaoLabel>Resumo</SecaoLabel>
              <p style={{ margin: '8px 0 0 0', fontFamily: PD.serif,
                fontSize: 15, color: PD.txt1, lineHeight: 1.55 }}>
                {vmv?.missao?.texto
                  ? `${vmv.missao.texto}`
                  : (ehSU
                      ? `Startup brasileira em ${setorAnon.toLowerCase()}, com solução própria, tração inicial e plano de crescimento estruturado para os próximos 60 meses.`
                      : `Operação brasileira consolidada em ${setorAnon.toLowerCase()}, com modelo recorrente em base própria de clientes, margem operacional acima da média setorial e plano de crescimento estruturado para os próximos 60 meses.`)}
              </p>
            </div>

            {/* Highlights */}
            <div style={{ marginTop: 18 }}>
              <SecaoLabel>Highlights de Investimento</SecaoLabel>
              <ul style={{ margin: '8px 0 0 0', padding: 0, listStyle: 'none' }}>
                {(highlightsFinal.length > 0 ? highlightsFinal : [
                  'Preencha diferenciais e projeções no BP60M · highlights serão extraídos automaticamente.',
                ]).map((h, i) => (
                  <li key={i} style={{ display: 'grid',
                    gridTemplateColumns: '22px 1fr', gap: 8,
                    padding: '5px 0', fontFamily: PD.sans, fontSize: 13.5,
                    color: PD.txt1, lineHeight: 1.5 }}>
                    <span style={{ fontFamily: PD.mono, fontSize: 11,
                      color: PD.laranjaFunda, fontWeight: 700,
                      letterSpacing: '0.05em' }}>{`0${i+1}`}</span>
                    <span>{h}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Resumo financeiro · tabela + sparkline EBITDA */}
            <div style={{ marginTop: 18 }}>
              <SecaoLabel>Resumo Financeiro</SecaoLabel>
              <div style={{ marginTop: 8, display: 'grid',
                gridTemplateColumns: '1.5fr 1fr', gap: 22, alignItems: 'start' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse',
                  tableLayout: 'fixed' }}>
                  <thead>
                    <tr>
                      <th style={thT}>Indicador (R$ mi)</th>
                      {(anos.length > 0 ? anos : [1,2,3,4,5]).map((a, i) => (
                        <th key={i} style={thTR}>{a?.ano || `A${i+1}`}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    <LinhaT label="Receita líquida" valores={anos.map(a => a.receita)} casas={ehSU ? 1 : 0} />
                    <LinhaT label="EBITDA" valores={anos.map(a => a.ebitda)} dest casas={ehSU ? 1 : 0} />
                    <LinhaT label={ehSU ? 'Margem de contribuição' : 'Margem EBITDA'} valores={anos.map(a => a.receita ? ((ehSU ? (a.margemContrib / a.receita) : (a.ebitda / a.receita)) * 100).toFixed(1).replace('.',',') + '%' : '—')} raw />
                    <LinhaT label="Caixa livre" valores={anos.map(a => a.fcf)} casas={ehSU ? 1 : 0} />
                  </tbody>
                </table>
                <GraficoEbitdaTeaser anos={anos} ehSU={ehSU} />
              </div>
            </div>

            {/* Razão · Oferta · Processo · grid 2 colunas */}
            <div style={{ marginTop: 18, display: 'grid',
              gridTemplateColumns: '1fr 1fr', gap: 28 }}>
              <div>
                <SecaoLabel>Razões da Operação</SecaoLabel>
                {fraseMeta && (
                  <p style={{ margin: '8px 0 0 0', fontFamily: PD.serif, fontStyle: 'italic',
                    fontSize: 13.5, color: PD.verdeNoite, lineHeight: 1.5 }}>
                    Meta: {fraseMeta}
                  </p>
                )}
                <p style={{ margin: '8px 0 0 0', fontFamily: PD.sans,
                  fontSize: 13, color: PD.txt1, lineHeight: 1.55 }}>
                  {razao}
                </p>
              </div>
              <div>
                <SecaoLabel>Estrutura da Oferta</SecaoLabel>
                <div style={{ marginTop: 8, fontFamily: PD.sans, fontSize: 13,
                  color: PD.txt1, lineHeight: 1.7 }}>
                  {(equityFlag === 'sim' || equityFlag === true || (ehSU && equityFlag === 'em_breve')) ? (
                    ehSU ? (
                      <>
                        <div><strong style={{ color: PD.verdeNoite }}>Tipo:</strong> {rodadaSelT ? `Rodada ${rodadaSelT.titulo}` : 'Equity Round'} · Primário</div>
                        <div><strong style={{ color: PD.verdeNoite }}>Captação alvo:</strong> {fmtMiT(valSU?.captacao)}</div>
                        <div><strong style={{ color: PD.verdeNoite }}>Equity ofertado:</strong> {valSU?.equityPct != null ? `~${String(valSU.equityPct).replace('.', ',')}%` : '—'}</div>
                        <div><strong style={{ color: PD.verdeNoite }}>Valuation pré-money:</strong> {fmtMiT(valSU?.central)}</div>
                      </>
                    ) : (
                      <>
                        <div><strong style={{ color: PD.verdeNoite }}>Tipo:</strong> Equity Round · Primário</div>
                        <div><strong style={{ color: PD.verdeNoite }}>Captação alvo:</strong> R$ {captMi || '—'} milhões</div>
                        <div><strong style={{ color: PD.verdeNoite }}>Equity ofertado:</strong> ~22%</div>
                        <div><strong style={{ color: PD.verdeNoite }}>Valuation pré-money:</strong> R$ {evMi || '—'} milhões</div>
                      </>
                    )
                  ) : (
                    <>
                      <div><strong style={{ color: PD.verdeNoite }}>Tipo:</strong> Sem oferta corrente</div>
                      <div><strong style={{ color: PD.verdeNoite }}>Posição:</strong> Operação em crescimento orgânico</div>
                      <div><strong style={{ color: PD.verdeNoite }}>Próxima janela:</strong> {tipoMetaSel?.titulo || 'Em definição'}</div>
                    </>
                  )}
                </div>
              </div>
            </div>

            {/* Processo */}
            <div style={{ marginTop: 18 }}>
              <SecaoLabel>Processo</SecaoLabel>
              <div style={{ marginTop: 8, fontFamily: PD.sans, fontSize: 13,
                color: PD.txt1, lineHeight: 1.65 }}>
                <strong style={{ color: PD.verdeNoite }}>Confidencialidade:</strong> NDA obrigatório para acesso ao Information Memorandum completo. &nbsp;·&nbsp;
                <strong style={{ color: PD.verdeNoite }}>Cronograma indicativo:</strong> {ehSU ? 'primeiras conversas em 30 dias · term sheet em 60 dias · assinatura do contrato em 90 a 120 dias.' : 'primeiras conversas em 30 dias · cartas de intenção em 60 dias · fechamento em 90 a 120 dias.'}
              </div>
            </div>

            {/* Rodapé · contato + brand */}
            <div style={{ position: 'absolute', left: 70, right: 70, bottom: 50,
              borderTop: `1px solid ${PD.txt1}30`, paddingTop: 16,
              display: 'flex', justifyContent: 'space-between',
              alignItems: 'flex-end' }}>
              <div>
                <div style={{ fontFamily: PD.mono, fontSize: 10,
                  letterSpacing: '0.25em', textTransform: 'uppercase',
                  color: PD.txt2, fontWeight: 700 }}>Contato</div>
                <div style={{ marginTop: 6, fontFamily: PD.sans, fontSize: 12,
                  color: PD.txt1, lineHeight: 1.6 }}>
                  {empresa?.contato_email && <div>{empresa.contato_email}</div>}
                  {empresa?.contato_telefone && <div>{empresa.contato_telefone}</div>}
                  {!empresa?.contato_email && !empresa?.contato_telefone && (
                    <div style={{ color: PD.txt3, fontStyle: 'italic' }}>
                      Cadastre contato em Configuração › Cadastro.
                    </div>
                  )}
                </div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <PoweredBy />
                <div style={{ marginTop: 8, fontFamily: PD.mono, fontSize: 9,
                  letterSpacing: '0.2em', textTransform: 'uppercase',
                  color: PD.txt3 }}>
                  Documento confidencial · uso restrito
                </div>
              </div>
            </div>
          </section>
        </div>
      </div>

      <button onClick={onFechar}
        style={{ position: 'absolute', top: 20, right: 20,
          background: 'transparent', border: '1px solid #FFF',
          color: '#FFF', padding: '6px 12px', fontSize: 11,
          fontWeight: 700, letterSpacing: '0.10em', textTransform: 'uppercase',
          borderRadius: 0, cursor: 'pointer', fontFamily: PD.sans }}>
        Fechar · esc
      </button>
      <button onClick={() => baixarPdfDe('[data-pdf-teaser]', `${(empresa?.nome || 'Empresa').replace(/\s+/g,'_')}_Teaser.pdf`, 'p')}
        style={{ position: 'absolute', top: 20, right: 130,
          background: PD.verde, border: `1px solid ${PD.verde}`,
          color: '#FFF', padding: '6px 12px', fontSize: 11,
          fontWeight: 700, letterSpacing: '0.10em', textTransform: 'uppercase',
          borderRadius: 0, cursor: 'pointer', fontFamily: PD.sans }}>
        ↓ Baixar PDF
      </button>
      <button onClick={() => setModoDemo(!modoDemo)}
        title="Carrega dados sintéticos de ShopMax para visualização do Teaser"
        style={{ position: 'absolute', top: 20, left: 20,
          background: modoDemo ? PD.laranja : 'transparent',
          border: `1px solid ${modoDemo ? PD.laranja : '#FFF'}`,
          color: '#FFF', padding: '6px 12px', fontSize: 11,
          fontWeight: 700, letterSpacing: '0.10em', textTransform: 'uppercase',
          borderRadius: 0, cursor: 'pointer', fontFamily: PD.sans }}>
        {modoDemo ? 'Modo Demo · ON' : 'Modo Demo · OFF'}
      </button>
    </div>
  );
};

const SecaoLabel = ({ children }) => (
  <div style={{ fontFamily: PD.mono, fontSize: 10,
    letterSpacing: '0.28em', textTransform: 'uppercase',
    color: PD.verde, fontWeight: 700,
    paddingBottom: 4, borderBottom: `1px solid ${PD.verde}40` }}>
    {children}
  </div>
);
const thT = { fontFamily: PD.mono, fontSize: 9.5,
  letterSpacing: '0.22em', textTransform: 'uppercase', color: PD.txt2,
  padding: '8px 4px', borderBottom: `1px solid ${PD.verdeNoite}`,
  fontWeight: 700, textAlign: 'left' };
const thTR = { ...thT, textAlign: 'right' };
const LinhaT = ({ label, valores, dest, raw, casas = 0 }) => (
  <tr>
    <td style={{ fontFamily: PD.mono, fontSize: 10,
      letterSpacing: '0.15em', textTransform: 'uppercase',
      color: PD.txt2, padding: '10px 4px',
      borderBottom: '1px solid rgba(0,0,0,0.06)', fontWeight: 600 }}>
      {label}
    </td>
    {(valores.length > 0 ? valores : [null,null,null,null,null]).map((v, i) => (
      <td key={i} style={{ fontFamily: PD.serif, fontSize: 12.5,
        color: dest ? PD.laranjaFunda : PD.verdeNoite,
        padding: '10px 4px', textAlign: 'right',
        borderBottom: '1px solid rgba(0,0,0,0.06)',
        fontWeight: dest ? 700 : 500 }}>
        {raw ? (v || '—') : (v ? (v / 1_000_000).toFixed(casas).replace('.', ',') : '—')}
      </td>
    ))}
  </tr>
);

// =====================================================================
// Information Memorandum · documento completo M&A · multi-página A4
// =====================================================================

const IM_PAGE_W = 800;
const IM_PAGE_H = 1131;

const PaginaIM = ({ children, semHeader, capNum, capTitulo, paginaAtual, totalPaginas, documento = 'Information Memorandum' }) => (
  <section data-pdf-im-page style={{ width: IM_PAGE_W, height: IM_PAGE_H,
    minHeight: IM_PAGE_H, flexShrink: 0,
    background: PD.cream,
    color: PD.txt1, fontFamily: PD.sans, padding: '60px 70px',
    boxSizing: 'border-box', position: 'relative', overflow: 'hidden',
    boxShadow: '0 4px 16px rgba(0,0,0,0.18)' }}>
    {!semHeader && (
      <div style={{ position: 'absolute', top: 24, left: 70, right: 70,
        display: 'flex', justifyContent: 'space-between',
        fontFamily: PD.mono, fontSize: 9, letterSpacing: '0.28em',
        textTransform: 'uppercase', color: PD.txt3 }}>
        <span>{documento} · Confidencial</span>
        <span>Capítulo {capNum} · {capTitulo}</span>
      </div>
    )}
    <div style={{ marginTop: semHeader ? 0 : 28, height: 'calc(100% - 80px)' }}>
      {children}
    </div>
    <div style={{ position: 'absolute', bottom: 24, left: 70, right: 70,
      display: 'flex', justifyContent: 'space-between',
      fontFamily: PD.mono, fontSize: 9, letterSpacing: '0.22em',
      textTransform: 'uppercase', color: PD.txt3,
      borderTop: `1px solid ${PD.txt1}20`, paddingTop: 12 }}>
      <PoweredBy />
      <span>{paginaAtual} / {totalPaginas}</span>
    </div>
  </section>
);

const TituloCapIM = ({ numero, titulo, sub }) => (
  <div style={{ marginBottom: 24, paddingBottom: 14,
    borderBottom: `2px solid ${PD.verdeNoite}` }}>
    <div style={{ fontFamily: PD.mono, fontSize: 10,
      letterSpacing: '0.3em', textTransform: 'uppercase',
      color: PD.verde, fontWeight: 700 }}>
      Capítulo {numero}
    </div>
    <h2 style={{ margin: '8px 0 0 0', fontFamily: PD.serif, fontWeight: 600,
      fontSize: 34, color: PD.verdeNoite, letterSpacing: '-0.015em',
      lineHeight: 1.1 }}>
      {titulo}
    </h2>
    {sub && (
      <div style={{ marginTop: 8, fontFamily: PD.serif, fontStyle: 'italic',
        fontWeight: 300, fontSize: 15, color: PD.txt2, lineHeight: 1.4 }}>
        {sub}
      </div>
    )}
  </div>
);

const SubSecaoIM = ({ titulo, children }) => (
  <div style={{ marginTop: 18 }}>
    <div style={{ fontFamily: PD.mono, fontSize: 10,
      letterSpacing: '0.25em', textTransform: 'uppercase',
      color: PD.laranjaFunda, fontWeight: 700, marginBottom: 8 }}>
      {titulo}
    </div>
    <div style={{ fontFamily: PD.sans, fontSize: 13.5,
      color: PD.txt1, lineHeight: 1.6 }}>
      {children}
    </div>
  </div>
);

const MatrizSWOTIM = ({ swot }) => {
  const f = (swot?.forcas || []).filter(x => x.marcado).slice(0,3);
  const fr = (swot?.fraquezas || []).filter(x => x.marcado).slice(0,3);
  const o = (swot?.oportunidades || []).filter(x => x.marcado).slice(0,3);
  const a = (swot?.ameacas || []).filter(x => x.marcado).slice(0,3);
  const Quad = ({ titulo, items, cor, ph }) => (
    <div style={{ background: '#FFF', border: `1px solid ${cor}`, padding: 14,
      borderLeft: `4px solid ${cor}` }}>
      <div style={{ fontFamily: PD.mono, fontSize: 10,
        letterSpacing: '0.25em', textTransform: 'uppercase',
        color: cor, fontWeight: 700, marginBottom: 8 }}>
        {titulo}
      </div>
      {items.length > 0 ? (
        <ul style={{ margin: 0, padding: '0 0 0 14px', fontFamily: PD.sans,
          fontSize: 12, color: PD.txt1, lineHeight: 1.5 }}>
          {items.map((x, i) => <li key={i} style={{ marginBottom: 4 }}>{x.texto || x}</li>)}
        </ul>
      ) : (
        <div style={{ fontFamily: PD.sans, fontSize: 11.5, fontStyle: 'italic',
          color: PD.txt3 }}>{ph}</div>
      )}
    </div>
  );
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr',
      gridTemplateRows: '1fr 1fr', gap: 14, marginTop: 8 }}>
      <Quad titulo="Forças" items={f} cor={PD.verde} ph="Marcar forças no SWOT." />
      <Quad titulo="Fraquezas" items={fr} cor={PD.laranjaFunda} ph="Marcar fraquezas no SWOT." />
      <Quad titulo="Oportunidades" items={o} cor={PD.verdeMusgo} ph="Marcar oportunidades no SWOT." />
      <Quad titulo="Ameaças" items={a} cor="#A8392B" ph="Marcar ameaças no SWOT." />
    </div>
  );
};

const GraficoEbitdaIM = ({ anos, ehSU = false }) => {
  const mk = ehSU ? 'receita' : 'ebitda';
  const fmtG = (v) => Math.abs(v) >= 1_000_000 ? `R$ ${(v/1_000_000).toFixed(1).replace('.', ',')} mi` : `R$ ${(v/1000).toFixed(0)} mil`;
  if (!anos || anos.length < 2 || !anos[anos.length-1]?.[mk]) {
    return (
      <div style={{ fontFamily: PD.sans, fontSize: 12,
        color: PD.txt3, fontStyle: 'italic', textAlign: 'center', padding: 28 }}>
        {ehSU ? 'Cadastre projeções para exibir trajetória de receita.' : 'Cadastre projeções para exibir trajetória EBITDA.'}
      </div>
    );
  }
  const valores = anos.map(a => a?.[mk] || 0);
  const max = Math.max(...valores) * 1.10;
  const W = 600, H = 200, pad = 36;
  const inner = W - pad * 2;
  const barW = (inner / valores.length) * 0.62;
  const gap = (inner / valores.length) * 0.38;
  const ultimoMi = (valores[valores.length-1] / 1_000_000).toFixed(0);
  return (
    <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`} style={{ display: 'block', margin: '0 auto' }}>
      <defs>
        <linearGradient id="imEbitdaGrad" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={PD.laranja} stopOpacity="0.95" />
          <stop offset="100%" stopColor={PD.laranjaFunda} stopOpacity="0.7" />
        </linearGradient>
        <linearGradient id="imEbitdaGradDim" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={PD.verdeMusgo} stopOpacity="0.55" />
          <stop offset="100%" stopColor={PD.verde} stopOpacity="0.40" />
        </linearGradient>
      </defs>
      <line x1={pad} y1={H-pad} x2={W-pad} y2={H-pad}
        stroke={PD.verdeNoite} strokeWidth="1" opacity="0.4" />
      {valores.map((v, i) => {
        const h = (v / max) * (H - pad * 2);
        const x = pad + gap/2 + i * (barW + gap);
        const y = H - pad - h;
        const isUltimo = i === valores.length - 1;
        const valLabel = ehSU ? fmtG(v) : `R$ ${(v / 1_000_000).toFixed(0)} mi`;
        return (
          <g key={i}>
            <rect x={x} y={y} width={barW} height={h}
              fill={isUltimo ? 'url(#imEbitdaGrad)' : 'url(#imEbitdaGradDim)'} />
            <text x={x + barW/2} y={H - pad + 16}
              textAnchor="middle"
              style={{ fontFamily: PD.mono, fontSize: 10,
                fill: PD.txt2, letterSpacing: '0.05em' }}>
              {anos[i]?.ano || `A${i+1}`}
            </text>
            <text x={x + barW/2} y={y - 8}
              textAnchor="middle"
              style={{ fontFamily: PD.serif, fontSize: 13,
                fontWeight: isUltimo ? 700 : 500,
                fill: isUltimo ? PD.laranjaFunda : PD.verdeNoite }}>
              {valLabel}
            </text>
          </g>
        );
      })}
    </svg>
  );
};

const InformationMemorandum = ({ bp, onFechar }) => {
  const [modoDemo, setModoDemo] = useState(false);
  const bpAtivo = modoDemo ? DEMO_SHOPMAX_BP : bp;
  const { meta, empresa, vmv, produtosServicos, mercadoConcorrencia, swot,
    estrategiaModelo, marketingVendas, estruturaRh, projecoes, valuation,
    equityFlag, modo, valSU } = bpAtivo;
  const ehSU = modo === 'SU';
  const tipoMetaSel = (ehSU ? TIPOS_META_BP60M_SU : TIPOS_META_BP60M).find(t => t.id === meta?.tipo);
  const fraseMeta = tipoMetaSel ? `${tipoMetaSel.fraseAntes || ''}${meta?.valor || ''}${tipoMetaSel.fraseDepois || ''}` : '';
  const nomeEmp = empresa?.nome || 'Sua Empresa';
  const setorTexto = empresa?.setor || 'Setor';
  const anos = ((ehSU && valSU?.anos) ? valSU.anos : (projecoes?.anos || [])).slice(0,5);
  const codinome = codinomeFromSetor(empresa?.setor);
  // Helpers Startup (fonte única valSU) para o IM
  const rodadaSelIM = ehSU ? RODADAS_SU.find(r => r.id === meta?.rodada) : null;
  const fmtMiIM = (v) => v == null ? '—' : (Math.abs(v) >= 1_000_000 ? `R$ ${(v/1_000_000).toFixed(2)} mi` : `R$ ${(v/1000).toFixed(0)} mil`);
  const recMultIM = (anos.length >= 2 && anos[0]?.receita && anos[anos.length-1]?.receita) ? (anos[anos.length-1].receita / anos[0].receita).toFixed(1).replace('.', ',') : null;

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onFechar(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onFechar]);

  const TOTAL = 17;
  const dif = (produtosServicos?.diferenciais || []).slice(0,3);
  const tend = (mercadoConcorrencia?.tendencias || []).slice(0,3);
  const players = (mercadoConcorrencia?.players || []).slice(0,4);
  const time = (estruturaRh?.organograma || []).slice(0,4);
  const canais = (marketingVendas?.canais || []).slice(0,4);

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(10,10,8,0.92)',
      zIndex: 1000, display: 'flex', justifyContent: 'center',
      overflowY: 'auto', padding: '40px 0' }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>

        {/* Página 1 · Capa */}
        <PaginaIM semHeader paginaAtual={1} totalPaginas={TOTAL}>
          <div style={{ height: '100%', display: 'flex',
            flexDirection: 'column', justifyContent: 'space-between' }}>
            <div>
              <div style={{ fontFamily: PD.mono, fontSize: 11,
                letterSpacing: '0.32em', textTransform: 'uppercase',
                color: PD.verde, fontWeight: 700 }}>
                Information Memorandum
              </div>
              <div style={{ marginTop: 6, fontFamily: PD.mono, fontSize: 10,
                letterSpacing: '0.28em', color: PD.txt3,
                textTransform: 'uppercase' }}>
                Documento confidencial · uso restrito · NDA obrigatório
              </div>
            </div>
            <div>
              <div style={{ fontFamily: PD.serif, fontStyle: 'italic',
                fontWeight: 300, fontSize: 22, color: PD.txt2 }}>
                {codinome}
              </div>
              <div style={{ marginTop: 16, fontFamily: PD.serif, fontWeight: 700,
                fontSize: 76, color: PD.verdeNoite, letterSpacing: '-0.025em',
                lineHeight: 0.95 }}>
                {nomeEmp}
              </div>
              <div style={{ marginTop: 18, fontFamily: PD.mono, fontSize: 12,
                letterSpacing: '0.28em', textTransform: 'uppercase',
                color: PD.laranja, fontWeight: 700 }}>
                {setorTexto}
              </div>
              <div style={{ marginTop: 60, height: 1, background: PD.txt1,
                opacity: 0.2 }} />
              <div style={{ marginTop: 18, fontFamily: PD.serif, fontStyle: 'italic',
                fontSize: 18, color: PD.txt1, lineHeight: 1.5, maxWidth: 600 }}>
                {vmv?.missao?.texto || (ehSU
                  ? `Documento institucional reunindo os capítulos do Business Plan 60 meses, para uso em rodada de captação e relacionamento com investidores.`
                  : `Documento institucional consolidando os capítulos do Business Plan 60 meses, para uso em processo de captação de capital e relacionamento institucional.`)}
              </div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between',
              alignItems: 'flex-end' }}>
              <div>
                <div style={{ fontFamily: PD.mono, fontSize: 10,
                  letterSpacing: '0.25em', textTransform: 'uppercase',
                  color: PD.txt2, fontWeight: 700 }}>Edição</div>
                <div style={{ marginTop: 4, fontFamily: PD.serif, fontSize: 16,
                  color: PD.verdeNoite }}>Junho · 2026</div>
              </div>
              <div style={{ textAlign: 'right', fontFamily: PD.mono,
                fontSize: 11, color: PD.txt2, lineHeight: 1.8 }}>
                {empresa?.contato_email && <div>{empresa.contato_email}</div>}
                {empresa?.contato_telefone && <div>{empresa.contato_telefone}</div>}
              </div>
            </div>
          </div>
        </PaginaIM>

        {/* Página 2 · Sumário Executivo */}
        <PaginaIM capNum="00" capTitulo="Sumário Executivo" paginaAtual={2} totalPaginas={TOTAL}>
          <TituloCapIM numero="00" titulo="Sumário Executivo"
            sub={ehSU ? "Síntese do plano do Business Plan 60 meses." : "Síntese do plano consolidado do Business Plan 60 meses."} />
          <div style={{ fontFamily: PD.serif, fontSize: 15,
            color: PD.txt1, lineHeight: 1.65 }}>
            <p style={{ margin: '0 0 14px 0' }}>
              {nomeEmp} é uma {ehSU ? 'startup' : 'operação'} brasileira de {setorTexto.toLowerCase()},
              {vmv?.missao?.texto ? ` cuja missão é "${vmv.missao.texto}"` : (ehSU ? ` em estágio inicial, com solução própria e tração em construção` : ` consolidada e com base própria de clientes`)}.
              O plano apresentado neste documento descreve a estratégia para
              os próximos 60 meses, com {fraseMeta ? `meta de ${fraseMeta.toLowerCase()}` : 'meta de crescimento sustentado'}.
            </p>
            {anos[0]?.receita && anos[anos.length-1]?.receita && (
              <p style={{ margin: '0 0 14px 0' }}>
                As projeções financeiras indicam crescimento da receita de
                <strong> {ehSU ? fmtMiIM(anos[0].receita) : `R$ ${(anos[0].receita/1_000_000).toFixed(0)} mi`} </strong>
                em {anos[0].ano} para
                <strong> {ehSU ? fmtMiIM(anos[anos.length-1].receita) : `R$ ${(anos[anos.length-1].receita/1_000_000).toFixed(0)} mi`} </strong>
                em {anos[anos.length-1].ano},
                {ehSU
                  ? (recMultIM && ` com a receita multiplicando ${recMultIM}× no período`)
                  : (anos[anos.length-1]?.ebitda && anos[0]?.ebitda &&
                    ` com EBITDA crescendo ${(anos[anos.length-1].ebitda/anos[0].ebitda).toFixed(1).replace('.', ',')}× no período`)}.
              </p>
            )}
            {ehSU ? (
              valSU && valSU.faixaMax > 0 && (
                <p style={{ margin: '0 0 14px 0' }}>
                  A avaliação preliminar situa o valor da empresa (pré-money) entre
                  <strong> {fmtMiIM(valSU.faixaMin)}</strong> e
                  <strong> {fmtMiIM(valSU.faixaMax)}</strong>, triangulado por múltiplos comparáveis, fluxo de caixa descontado e método do capital de risco.
                  {(equityFlag === 'sim' || equityFlag === true || equityFlag === 'em_breve') && valSU.captacao &&
                    ` A captação pretendida é de ${fmtMiIM(valSU.captacao)}${rodadaSelIM ? ` na Rodada ${rodadaSelIM.titulo}` : ''}${valSU.equityPct != null ? `, equivalente a aproximadamente ${String(valSU.equityPct).replace('.', ',')}% de equity` : ''}.`}
                </p>
              )
            ) : (valuation?.evCalc && (
              <p style={{ margin: '0 0 14px 0' }}>
                A avaliação preliminar pelo método DCF (WACC de {valuation.wacc || '—'}%)
                resulta em Enterprise Value de
                <strong> R$ {(valuation.evCalc/1_000_000).toFixed(0)} mi</strong>.
                {(equityFlag === 'sim' || equityFlag === true) && valuation.captacao &&
                  ` A captação pretendida é de R$ ${(valuation.captacao/1_000_000).toFixed(0)} mi, com aproximadamente 22% de equity ofertado.`}
              </p>
            ))}
          </div>

          <SubSecaoIM titulo="Highlights da Operação">
            <ul style={{ margin: 0, padding: '0 0 0 20px' }}>
              {dif.length > 0 ? dif.map((d, i) => (
                <li key={i} style={{ marginBottom: 6 }}>{d.texto || d}</li>
              )) : (
                <li style={{ fontStyle: 'italic', color: PD.txt3 }}>
                  Preencher diferenciais competitivos no Business Plan.
                </li>
              )}
            </ul>
          </SubSecaoIM>
          <LeituraAIdvisor>
            {ehSU
              ? `Este memorando foi preparado para apresentar ${nomeEmp} a investidores de estágio inicial. Recomendamos concentrar a leitura em três pontos: a margem de contribuição, que revela a saúde da unidade econômica desde cedo; a curva de receita ao longo dos 60 meses; e o uso pretendido dos recursos até o próximo marco de captação. Os números partem das premissas explicitadas ao longo do documento e serão validados em due diligence — o objetivo aqui é dar ao investidor o contexto para formar a própria convicção.`
              : `Este memorando foi preparado para apresentar ${nomeEmp} a investidores e potenciais parceiros. Recomendamos concentrar a leitura em três pontos: a consistência entre estratégia, operação e projeções; a geração de caixa ao longo do plano; e a estrutura da operação proposta. Os números partem das premissas explicitadas ao longo do documento e serão validados em due diligence — o objetivo aqui é dar ao investidor o contexto para formar a própria convicção.`}
          </LeituraAIdvisor>
        </PaginaIM>

        {/* Página 3 · Sumário */}
        <PaginaIM capNum="—" capTitulo="Sumário" paginaAtual={3} totalPaginas={TOTAL}>
          <TituloCapIM numero="—" titulo="Sumário" sub="Navegação dos capítulos deste documento." />
          <ol style={{ margin: 0, padding: 0, listStyle: 'none',
            fontFamily: PD.serif, fontSize: 15, color: PD.txt1, lineHeight: 2 }}>
            {[
              ['01', 'A Empresa', '4'],
              ['02', 'Produtos & Serviços', '5'],
              ['03', 'Mercado & Concorrência', '6'],
              ['04', 'Análise SWOT', '7'],
              ['05', 'Estratégia & Modelo de Negócio', '8'],
              ['06', 'Marketing & Vendas', '9'],
              ['07', 'Operação', '10'],
              ['08', 'Time & Governança', '11'],
              ['09', 'Análise de Riscos', '12'],
              ['10', 'ESG', '13'],
              ['11', 'Projeções Financeiras', '14'],
              ['12', 'Valuation', '15'],
              ['13', 'Meta · Funding & Exit', '16'],
              ['—', 'Anexos · NDA · Disclaimer', '17'],
            ].map(([n, t, p], i) => (
              <li key={i} style={{ display: 'grid',
                gridTemplateColumns: '40px 1fr 30px', borderBottom: '1px dotted rgba(0,0,0,0.12)',
                padding: '4px 0' }}>
                <span style={{ fontFamily: PD.mono, fontSize: 11,
                  color: PD.laranjaFunda, fontWeight: 700, paddingTop: 6 }}>{n}</span>
                <span style={{ fontWeight: 500 }}>{t}</span>
                <span style={{ fontFamily: PD.mono, fontSize: 11,
                  color: PD.txt3, textAlign: 'right', paddingTop: 6 }}>{p}</span>
              </li>
            ))}
          </ol>
        </PaginaIM>

        {/* Página 4 · Cap 1 · A Empresa */}
        <PaginaIM capNum="01" capTitulo="A Empresa" paginaAtual={4} totalPaginas={TOTAL}>
          <TituloCapIM numero="01" titulo="A Empresa"
            sub="Identidade, propósito e marcos da operação." />
          <SubSecaoIM titulo="Identificação">
            <div style={{ display: 'grid', gridTemplateColumns: '160px 1fr',
              rowGap: 8, columnGap: 12 }}>
              <div style={{ color: PD.txt2 }}>Razão social</div>
              <div>{empresa?.razao_social || empresa?.nome || '—'}</div>
              <div style={{ color: PD.txt2 }}>Setor</div>
              <div>{setorTexto}</div>
              <div style={{ color: PD.txt2 }}>Sede</div>
              <div>{empresa?.endereco || '—'}</div>
              <div style={{ color: PD.txt2 }}>Contato</div>
              <div>{empresa?.contato_email || '—'}</div>
            </div>
          </SubSecaoIM>
          {vmv?.missao?.texto && (
            <SubSecaoIM titulo="Missão">
              <p style={{ margin: 0, fontFamily: PD.serif, fontStyle: 'italic',
                fontSize: 15 }}>"{vmv.missao.texto}"</p>
            </SubSecaoIM>
          )}
          {vmv?.visao?.texto && (
            <SubSecaoIM titulo="Visão">
              <p style={{ margin: 0, fontFamily: PD.serif, fontStyle: 'italic',
                fontSize: 15 }}>"{vmv.visao.texto}"</p>
            </SubSecaoIM>
          )}
          <LeituraAIdvisor>
            {ehSU
              ? `Abrimos o memorando pela identidade porque, em estágio inicial, é a tese — o problema atacado e a razão de a empresa existir — que orienta tudo o que vem depois. Sugerimos ao investidor ler os próximos capítulos com essa lente: como produto, mercado e time se encadeiam em torno dessa tese.`
              : `Abrimos o memorando pela identidade porque é ela que dá coerência ao restante do documento. Sugerimos ao investidor ler os próximos capítulos com essa lente: como operação, mercado e resultado se encadeiam em torno do propósito declarado.`}
          </LeituraAIdvisor>
        </PaginaIM>

        {/* Página 5 · Cap 2 · Produtos & Serviços */}
        <PaginaIM capNum="02" capTitulo="Produtos & Serviços" paginaAtual={5} totalPaginas={TOTAL}>
          <TituloCapIM numero="02" titulo="Produtos & Serviços"
            sub="Portfólio e diferenciais competitivos." />
          <SubSecaoIM titulo="Diferenciais">
            {dif.length > 0 ? (
              <ol style={{ margin: 0, padding: '0 0 0 18px' }}>
                {dif.map((d, i) => (
                  <li key={i} style={{ marginBottom: 8, lineHeight: 1.55 }}>
                    {d.texto || d}
                  </li>
                ))}
              </ol>
            ) : (
              <em style={{ color: PD.txt3 }}>Preencher diferenciais no BP60M Tarefa 3.</em>
            )}
          </SubSecaoIM>
          {produtosServicos?.portfolio?.length > 0 && (
            <SubSecaoIM titulo="Portfólio">
              <ul style={{ margin: 0, padding: '0 0 0 18px' }}>
                {produtosServicos.portfolio.slice(0,5).map((p, i) => (
                  <li key={i} style={{ marginBottom: 6 }}>
                    <strong>{p.nome || `Linha ${i+1}`}</strong>{p.texto && ` · ${p.texto}`}
                  </li>
                ))}
              </ul>
            </SubSecaoIM>
          )}
          <LeituraAIdvisor>
            {ehSU
              ? `Este capítulo descreve o que ${nomeEmp} entrega e por que a solução resiste a cópia. Sugerimos ao investidor testar cada diferencial com uma pergunta: ele se sustenta diante de um concorrente com mais capital? Os que passam nesse teste são os que protegem a margem de contribuição — e são exatamente o que a due diligence técnica vai examinar. Ordenamos a lista do diferencial mais defensável ao mais frágil, para que a leitura comece pelo que ancora a tese de produto.`
              : `Este capítulo detalha o portfólio e o que separa ${nomeEmp} da concorrência direta em ${setorTexto.toLowerCase()}. Chamamos a atenção do investidor para a origem da margem acima da média setorial: ela nasce do diferencial de produto, da operação integrada, ou de ambos? A distinção importa porque define quanto da vantagem um comprador estratégico conseguiria replicar. A due diligence comercial costuma abrir esse ponto primeiro, e por isso o antecipamos aqui.`}
          </LeituraAIdvisor>
        </PaginaIM>

        {/* Página 6 · Cap 3 · Mercado */}
        <PaginaIM capNum="03" capTitulo="Mercado & Concorrência" paginaAtual={6} totalPaginas={TOTAL}>
          <TituloCapIM numero="03" titulo="Mercado & Concorrência"
            sub="Dimensão da oportunidade e cenário competitivo." />
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr',
            gap: 24, marginTop: 8 }}>
            <div style={{ background: PD.verdeNoite, padding: 20,
              borderRadius: 0 }}>
              <PiramideMercado
                tam={mercadoConcorrencia?.tam}
                sam={mercadoConcorrencia?.sam}
                som={mercadoConcorrencia?.som}
              />
            </div>
            <div>
              <div style={{ fontFamily: PD.mono, fontSize: 10,
                letterSpacing: '0.25em', textTransform: 'uppercase',
                color: PD.laranjaFunda, fontWeight: 700, marginBottom: 8 }}>
                Tendências
              </div>
              {tend.length > 0 ? (
                <ul style={{ margin: 0, padding: '0 0 0 18px',
                  fontFamily: PD.sans, fontSize: 12.5,
                  color: PD.txt1, lineHeight: 1.55 }}>
                  {tend.map((t, i) => (
                    <li key={i} style={{ marginBottom: 6 }}>{t.texto || t}</li>
                  ))}
                </ul>
              ) : (
                <em style={{ color: PD.txt3, fontSize: 12 }}>Tendências do mercado.</em>
              )}
              <div style={{ fontFamily: PD.mono, fontSize: 10,
                letterSpacing: '0.25em', textTransform: 'uppercase',
                color: PD.laranjaFunda, fontWeight: 700,
                marginTop: 18, marginBottom: 8 }}>
                Principais players
              </div>
              {players.length > 0 ? (
                <ul style={{ margin: 0, padding: '0 0 0 18px',
                  fontFamily: PD.sans, fontSize: 12.5,
                  color: PD.txt1, lineHeight: 1.55 }}>
                  {players.map((p, i) => (
                    <li key={i}>{p.nome || p.texto || p}</li>
                  ))}
                </ul>
              ) : (
                <em style={{ color: PD.txt3, fontSize: 12 }}>Principais concorrentes.</em>
              )}
            </div>
          </div>
          <LeituraAIdvisor>
            {ehSU
              ? `Neste capítulo o que interessa não é o tamanho do TAM, e sim o why-now: por que a janela para ${nomeEmp} abre agora. Recomendamos ao investidor concentrar a leitura na relação SOM/SAM — é ela que traduz o mercado endereçável em receita alcançável nos 60 meses — e no espaço que as soluções atuais deixam em aberto. Os números de mercado partem de fontes públicas que citamos; a due diligence vai querer cruzá-los com dados primários, e é assim que devem ser lidos: como ordem de grandeza, não como precisão.`
              : `Neste capítulo o driver a observar é onde ${nomeEmp} joga: o segmento médio de ${setorTexto.toLowerCase()}, hoje mal servido pelos grandes players. Recomendamos ao investidor ler a relação SOM/SAM como rota de crescimento orgânico — ela não depende de janela regulatória — e checar os players listados para dimensionar a reação competitiva a uma consolidação. As estimativas de mercado vêm de fontes que indicamos e serão aprofundadas em due diligence.`}
          </LeituraAIdvisor>
        </PaginaIM>

        {/* Página 7 · Cap 4 · SWOT */}
        <PaginaIM capNum="04" capTitulo="Análise SWOT" paginaAtual={7} totalPaginas={TOTAL}>
          <TituloCapIM numero="04" titulo="Análise SWOT"
            sub="Forças, fraquezas, oportunidades e ameaças identificadas." />
          <MatrizSWOTIM swot={swot} />
          <LeituraAIdvisor>
            {ehSU
              ? `Construímos esta matriz de dentro para fora, com a equipe, e optamos por não maquiar o quadrante de fraquezas — um SWOT que só mostra forças não ajuda o investidor a decidir. Sugerimos ler as diagonais: onde uma força ${nomeEmp} captura uma oportunidade, e onde uma fraqueza expõe a empresa a uma ameaça. É esse segundo cruzamento que a due diligence vai explorar, e é para ele que o capítulo de Riscos detalha a mitigação.`
              : `Construímos esta matriz com a equipe e mantivemos as fraquezas à vista de propósito — para o investidor de M&A, o valor do SWOT está justamente no que ele revela de exposição. Recomendamos ler os cruzamentos: forças que capturam oportunidades de mercado e, sobretudo, fraquezas que uma ameaça poderia ativar. Esse segundo par é o que a due diligence aprofunda, e o capítulo de Riscos retoma cada ponto com o respectivo plano de mitigação.`}
          </LeituraAIdvisor>
        </PaginaIM>

        {/* Página 8 · Cap 5 · Estratégia */}
        <PaginaIM capNum="05" capTitulo="Estratégia & Modelo de Negócio" paginaAtual={8} totalPaginas={TOTAL}>
          <TituloCapIM numero="05" titulo="Estratégia & Modelo de Negócio"
            sub={ehSU ? "Como a startup transforma a solução em receita." : "Como a empresa transforma operação em receita previsível."} />
          <SubSecaoIM titulo="Modelo de captura de valor">
            <p style={{ margin: 0, lineHeight: 1.6 }}>
              {estrategiaModelo?.modelo || 'Preencher o modelo de negócio na Tarefa 7 do BP60M.'}
            </p>
          </SubSecaoIM>
          {anos[0]?.receita && anos[0]?.ebitda && (
            <SubSecaoIM titulo="Estrutura unitária · ano-base">
              <table style={{ width: '100%', borderCollapse: 'collapse',
                marginTop: 4 }}>
                <tbody>
                  {(ehSU ? [
                    ['Receita Bruta', '100,0%'],
                    ['Custos diretos', `−${(anos[0].custoDir/anos[0].receita*100).toFixed(1).replace('.',',')}%`],
                    ['Margem de contribuição', `${(anos[0].margemContrib/anos[0].receita*100).toFixed(1).replace('.',',')}%`],
                    ['Despesas operacionais', `−${(anos[0].opex/anos[0].receita*100).toFixed(1).replace('.',',')}%`],
                    ['EBITDA', `${(anos[0].ebitda/anos[0].receita*100).toFixed(1).replace('.', ',')}%`],
                  ] : [
                    ['Receita Bruta', '100,0%'],
                    ['Custos diretos', `−${((1 - anos[0].ebitda/anos[0].receita - 0.25) * 100).toFixed(1).replace('.',',')}%`],
                    ['Despesas operacionais', '−25,0%'],
                    ['EBITDA', `${(anos[0].ebitda/anos[0].receita * 100).toFixed(1).replace('.', ',')}%`],
                  ]).map(([n, v], i, arr) => (
                    <tr key={i} style={{ borderBottom: '1px solid rgba(0,0,0,0.06)' }}>
                      <td style={{ padding: '8px 0', fontFamily: PD.serif,
                        fontSize: 13, fontWeight: i === arr.length - 1 ? 700 : 400 }}>{n}</td>
                      <td style={{ padding: '8px 0', textAlign: 'right',
                        fontFamily: PD.mono, fontSize: 13,
                        color: i === arr.length - 1 ? PD.laranjaFunda : PD.verdeNoite,
                        fontWeight: 600 }}>{v}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </SubSecaoIM>
          )}
          <LeituraAIdvisor>
            {ehSU
              ? `Trouxemos a estrutura unitária do ano-base para este capítulo porque é onde a estratégia de ${nomeEmp} vira economia. Recomendamos ao investidor ler a tabela de baixo para cima: a margem de contribuição mostra o que sobra de cada venda depois dos custos diretos, e é esse valor que financia a aquisição de novos clientes. Enquanto essa margem se mantém à medida que a base cresce, a tese se sustenta; se ela comprimir na escala, é o primeiro sinal a acompanhar — e o que a due diligence vai estressar.`
              : `Este capítulo mostra como ${nomeEmp} transforma operação em receita previsível, e a tabela unitária do ano-base é o resumo dessa mecânica. Chamamos a atenção do investidor para a recorrência da base de clientes: é ela que reduz o prêmio de risco do fluxo e ampara o valuation por DCF apresentado adiante. A due diligence vai querer confirmar a taxa de retenção real por trás dessa recorrência — deixamos a premissa explícita para que possa ser verificada.`}
          </LeituraAIdvisor>
        </PaginaIM>

        {/* Página 9 · Cap 6 · Marketing & Vendas */}
        <PaginaIM capNum="06" capTitulo="Marketing & Vendas" paginaAtual={9} totalPaginas={TOTAL}>
          <TituloCapIM numero="06" titulo="Marketing & Vendas"
            sub="Canais, mix e estratégia de aquisição de clientes." />
          <SubSecaoIM titulo="Canais e horizontes de execução">
            {canais.length > 0 ? (
              <ul style={{ margin: 0, padding: '0 0 0 18px' }}>
                {canais.map((c, i) => (
                  <li key={i} style={{ marginBottom: 8 }}>
                    <strong>{c.nome || `Canal ${i+1}`}</strong>
                    {c.descricao && ` · ${c.descricao}`}
                  </li>
                ))}
              </ul>
            ) : (
              <em style={{ color: PD.txt3 }}>Preencher canais na Tarefa 8 do BP60M.</em>
            )}
          </SubSecaoIM>
          <SubSecaoIM titulo="Indicadores-chave">
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr',
              gap: 18, marginTop: 4 }}>
              <KpiIM label="CAC alvo" val={marketingVendas?.cac ? `R$ ${marketingVendas.cac}` : '—'} />
              <KpiIM label="LTV esperado" val={marketingVendas?.ltv ? `R$ ${marketingVendas.ltv}` : '—'} />
              <KpiIM label="LTV/CAC" val={marketingVendas?.cac && marketingVendas?.ltv
                ? (parseFloat(String(marketingVendas.ltv).replace('.','').replace(',','.')) / parseFloat(String(marketingVendas.cac).replace('.','').replace(',','.'))).toFixed(1).replace('.', ',') + '×'
                : '—'} />
            </div>
          </SubSecaoIM>
          <LeituraAIdvisor>
            {ehSU
              ? `O número a observar neste capítulo é a relação LTV/CAC: ela diz se ${nomeEmp} ganha mais com um cliente do que gasta para conquistá-lo. Recomendamos ao investidor olhá-la ao lado da distribuição de canais — um LTV/CAC atraente concentrado num único canal é mais frágil do que parece. Sinalizamos com franqueza que CAC e LTV aqui vêm de coortes ainda jovens; a due diligence vai pedir o histórico por safra, e é assim que os números devem ser lidos nesta fase.`
              : `Neste capítulo o que orienta a leitura é a maturidade dos canais: quais já provaram converter com CAC controlado e quais ainda são hipótese. Recomendamos ao investidor cruzar a relação LTV/CAC com essa maturidade, porque é a combinação das duas que sustenta a curva de receita das projeções. A distribuição por horizontes reduz a dependência de um canal único; a due diligence comercial vai confirmar o CAC efetivo por canal, e deixamos a base de cálculo aberta para isso.`}
          </LeituraAIdvisor>
        </PaginaIM>

        {/* Página 10 · Cap 7 · Operação */}
        <PaginaIM capNum="07" capTitulo="Operação" paginaAtual={10} totalPaginas={TOTAL}>
          <TituloCapIM numero="07" titulo="Operação"
            sub="Estrutura operacional, cultura e tecnologia." />
          <SubSecaoIM titulo="Modelo operacional">
            <p style={{ margin: 0, lineHeight: 1.6 }}>
              {estrategiaModelo?.modelo
                ? `${estrategiaModelo.modelo}`
                : `Preencher Plano Operacional na Tarefa 9 do BP60M.`}
            </p>
          </SubSecaoIM>
          <SubSecaoIM titulo="Tecnologia e processos">
            <p style={{ margin: 0, lineHeight: 1.6, color: PD.txt2 }}>
              {ehSU
                ? `A operação apoia-se em tecnologia própria e processos enxutos, desenhados para escalar sem custo proporcional à medida que a base de clientes cresce.`
                : `A operação é sustentada por sistemas próprios que integram estoque, precificação dinâmica e relacionamento com cliente em uma única espinha de dados, com resposta operacional em menos de 24 horas.`}
            </p>
          </SubSecaoIM>
          <LeituraAIdvisor>
            {ehSU
              ? `Este capítulo responde à pergunta do investidor sobre risco de execução: ${nomeEmp} consegue crescer sem que o custo cresça na mesma proporção? Recomendamos observar se a tecnologia é própria e se os processos estão documentados — arquitetura enxuta que só existe na cabeça dos fundadores é um risco de concentração, não uma vantagem. A due diligence técnica vai pedir essa documentação, e sinalizamos abertamente o que ainda está sendo formalizado.`
              : `Neste capítulo o ponto a examinar é a escalabilidade da operação de ${nomeEmp}: a espinha de dados única que integra estoque, precificação e relacionamento é o ativo que um comprador estratégico costuma precificar bem. Recomendamos ao investidor tratar essa integração como objeto de verificação, não como afirmação — em M&A, o que não está documentado não entra no preço. A due diligence operacional vai auditar processos e sistemas, e preparamos o capítulo para essa leitura.`}
          </LeituraAIdvisor>
        </PaginaIM>

        {/* Página 11 · Cap 8 · Time */}
        <PaginaIM capNum="08" capTitulo="Time & Governança" paginaAtual={11} totalPaginas={TOTAL}>
          <TituloCapIM numero="08" titulo="Time & Governança"
            sub="Quem conduz a empresa e como decisões são tomadas." />
          <SubSecaoIM titulo="Time executivo">
            {time.length > 0 ? (
              <table style={{ width: '100%', borderCollapse: 'collapse',
                marginTop: 4 }}>
                <thead>
                  <tr>
                    <th style={{ ...thT, textAlign: 'left' }}>Cargo</th>
                    <th style={{ ...thT, textAlign: 'left' }}>Nome</th>
                    <th style={{ ...thT, textAlign: 'left' }}>Credenciais</th>
                  </tr>
                </thead>
                <tbody>
                  {time.map((p, i) => (
                    <tr key={i} style={{ borderBottom: '1px solid rgba(0,0,0,0.06)' }}>
                      <td style={{ padding: '10px 8px 10px 0',
                        fontFamily: PD.mono, fontSize: 10,
                        letterSpacing: '0.18em', textTransform: 'uppercase',
                        color: PD.laranjaFunda, fontWeight: 700,
                        verticalAlign: 'top', width: 120 }}>
                        {p.area || '—'}
                      </td>
                      <td style={{ padding: '10px 8px 10px 0',
                        fontFamily: PD.serif, fontSize: 14, fontWeight: 600,
                        color: PD.verdeNoite, verticalAlign: 'top', width: 160 }}>
                        {p.nome || '—'}
                      </td>
                      <td style={{ padding: '10px 0', fontFamily: PD.sans,
                        fontSize: 12.5, color: PD.txt2, lineHeight: 1.45 }}>
                        {p.cargo || '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <em style={{ color: PD.txt3 }}>Cadastrar time na Tarefa 10 do BP60M.</em>
            )}
          </SubSecaoIM>
          <LeituraAIdvisor>
            {ehSU
              ? `Em estágio inicial, o time é o principal fator de decisão do investidor — por isso pedimos atenção especial a este capítulo. O que sugerimos observar não são só os cargos, e sim o encaixe entre a experiência de cada fundador e o problema que ${nomeEmp} ataca, e como as lacunas atuais estão cobertas por mentores e advisors. Somos francos quanto a essas lacunas: mapeá-las é parte do trabalho, e a conversa de referências na due diligence vai justamente aprofundá-las.`
              : `Recomendamos ao investidor ler este capítulo procurando duas coisas: a profundidade de experiência setorial de quem conduz ${nomeEmp} e o grau de dependência da operação em relação ao fundador. Concentração excessiva de decisão em uma pessoa é a fragilidade de governança mais comum em empresas deste porte, e o conselho consultivo existe para endereçá-la. A due diligence vai avaliar planos de sucessão e retenção de pessoas-chave — sinalizamos onde esse trabalho ainda avança.`}
          </LeituraAIdvisor>
        </PaginaIM>

        {/* Página 12 · Cap 9 · Riscos */}
        <PaginaIM capNum="09" capTitulo="Análise de Riscos" paginaAtual={12} totalPaginas={TOTAL}>
          <TituloCapIM numero="09" titulo="Análise de Riscos"
            sub="Riscos identificados e plano de mitigação." />
          <SubSecaoIM titulo="Principais riscos mapeados">
            <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: 4 }}>
              <thead>
                <tr>
                  <th style={{ ...thT, textAlign: 'left' }}>Categoria</th>
                  <th style={{ ...thT, textAlign: 'left' }}>Risco</th>
                  <th style={thT}>Prob.</th>
                  <th style={thT}>Impacto</th>
                </tr>
              </thead>
              <tbody>
                {(swot?.ameacas?.filter(x=>x.marcado) || []).slice(0,4).map((a, i) => (
                  <tr key={i} style={{ borderBottom: '1px solid rgba(0,0,0,0.06)' }}>
                    <td style={{ padding: '10px 0', fontFamily: PD.mono,
                      fontSize: 10, letterSpacing: '0.18em',
                      textTransform: 'uppercase', color: PD.laranjaFunda,
                      fontWeight: 700, width: 130 }}>Estratégico</td>
                    <td style={{ padding: '10px 0', fontFamily: PD.sans,
                      fontSize: 13, color: PD.txt1, lineHeight: 1.45 }}>{a.texto}</td>
                    <td style={{ padding: '10px 0', textAlign: 'center',
                      fontFamily: PD.mono, fontSize: 11,
                      color: PD.verde, fontWeight: 700 }}>Média</td>
                    <td style={{ padding: '10px 0', textAlign: 'center',
                      fontFamily: PD.mono, fontSize: 11,
                      color: PD.laranjaFunda, fontWeight: 700 }}>Alto</td>
                  </tr>
                ))}
                {(!swot?.ameacas || swot.ameacas.filter(x=>x.marcado).length === 0) && (
                  <tr><td colSpan={4} style={{ padding: 20, textAlign: 'center',
                    fontStyle: 'italic', color: PD.txt3 }}>
                    Cadastrar riscos na Tarefa 11 do BP60M.
                  </td></tr>
                )}
              </tbody>
            </table>
          </SubSecaoIM>
          <LeituraAIdvisor>
            {ehSU
              ? `Listamos os riscos sem filtro de propósito: um capítulo de riscos curto demais sinaliza falta de análise, não ausência de exposição. Recomendamos ao investidor concentrar-se nos dois primeiros — os de maior probabilidade e impacto — porque são eles que definem a tese de baixa de ${nomeEmp} e o que perguntamos que a diligência aprofunde. Para cada um, indicamos a mitigação prevista no plano operacional; o objetivo é chegar à primeira reunião com essas perguntas já sobre a mesa.`
              : `Trouxemos os riscos à tona deliberadamente — em M&A, o comprador desconta o que descobre sozinho muito mais do que o que o vendedor apresenta com um plano de resposta. Sugerimos ao investidor tratar os dois riscos de maior impacto como âncoras da análise e verificar, para cada um, se a mitigação descrita é executável com a estrutura atual de ${nomeEmp}. É esse o terreno que a due diligence de riscos vai percorrer, e o antecipamos aqui.`}
          </LeituraAIdvisor>
        </PaginaIM>

        {/* Página 13 · Cap 10 · ESG */}
        <PaginaIM capNum="10" capTitulo="ESG" paginaAtual={13} totalPaginas={TOTAL}>
          <TituloCapIM numero="10" titulo="ESG"
            sub="Pilares ambiental, social e de governança." />
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 14, marginTop: 8 }}>
            {[
              { letra: 'E', cor: PD.verde, nome: 'Ambiental', desc: 'Consumo de energia, gestão de resíduos, emissões.' },
              { letra: 'S', cor: PD.laranja, nome: 'Social', desc: 'Diversidade, segurança, relacionamento com cadeia.' },
              { letra: 'G', cor: PD.verdeNoite, nome: 'Governança', desc: ehSU ? 'LGPD, boas práticas e governança em formação.' : 'Compliance LGPD, controles internos, conselho.' },
            ].map((p, i) => (
              <div key={i} style={{ background: '#FFF', border: `1px solid ${p.cor}40`,
                borderTop: `4px solid ${p.cor}`, padding: 16 }}>
                <div style={{ fontFamily: PD.serif, fontSize: 36, fontWeight: 700,
                  color: p.cor, letterSpacing: '-0.02em', lineHeight: 1 }}>{p.letra}</div>
                <div style={{ marginTop: 6, fontFamily: PD.mono, fontSize: 10,
                  letterSpacing: '0.25em', textTransform: 'uppercase',
                  color: PD.txt2, fontWeight: 700 }}>{p.nome}</div>
                <div style={{ marginTop: 8, fontFamily: PD.sans, fontSize: 12,
                  color: PD.txt1, lineHeight: 1.5 }}>{p.desc}</div>
              </div>
            ))}
          </div>
          <SubSecaoIM titulo="Compromissos públicos">
            <p style={{ margin: 0, lineHeight: 1.6, color: PD.txt2 }}>
              A empresa adota a Lei Geral de Proteção de Dados (LGPD) como prática
              obrigatória e segue evolução em pilares ambientais e sociais
              conforme materialidade do setor. Detalhamento na Tarefa 12 do BP60M.
            </p>
          </SubSecaoIM>
          <LeituraAIdvisor>
            {ehSU
              ? `Tratamos ESG neste capítulo pelo que ele significa em captação, não como discurso: para uma fatia crescente de fundos, conformidade com a LGPD e uma trilha de governança são critério de entrada, não bônus. Recomendamos ao investidor ler a governança (o "G") primeiro — é o pilar que mais pesa em estágio inicial. Somos claros de que ${nomeEmp} está no começo dessa jornada; o próximo passo, evoluir para indicadores mensuráveis, é o que amplia o universo de fundos que podem participar.`
              : `Neste capítulo o pilar que o investidor institucional examina primeiro é a governança e a conformidade com a LGPD — cada vez mais uma exigência de checklist, não um diferencial. Recomendamos observar a materialidade: quais temas ambientais e sociais são de fato relevantes para ${setorTexto.toLowerCase()} e como ${nomeEmp} os endereça. A evolução para indicadores mensuráveis nos três pilares alarga o conjunto de compradores e financiadores habilitados, e a due diligence de ESG vai buscar exatamente esse rastro.`}
          </LeituraAIdvisor>
        </PaginaIM>

        {/* Página 14 · Cap 11 · Projeções */}
        <PaginaIM capNum="11" capTitulo="Projeções Financeiras" paginaAtual={14} totalPaginas={TOTAL}>
          <TituloCapIM numero="11" titulo="Projeções Financeiras"
            sub={ehSU ? "Trajetória de receita, margem e caixa em 5 anos." : "Trajetória de receita, EBITDA e geração de caixa em 5 anos."} />
          <div style={{ marginTop: 6 }}>
            <GraficoEbitdaIM anos={anos} ehSU={ehSU} />
          </div>
          <SubSecaoIM titulo="Quadro consolidado">
            <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed', marginTop: 4 }}>
              <thead>
                <tr>
                  <th style={{ ...thT, textAlign: 'left' }}>R$ mi</th>
                  {(anos.length > 0 ? anos : [1,2,3,4,5]).map((a, i) => (
                    <th key={i} style={thTR}>{a?.ano || `A${i+1}`}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                <LinhaT label="Receita" valores={anos.map(a => a.receita)} casas={ehSU ? 1 : 0} />
                <LinhaT label="EBITDA" valores={anos.map(a => a.ebitda)} dest />
                <LinhaT label={ehSU ? "Margem de contrib." : "Margem"} valores={anos.map(a => a.receita ? ((ehSU ? (a.margemContrib/a.receita) : (a.ebitda/a.receita))*100).toFixed(1).replace('.',',')+'%' : '—')} raw />
                <LinhaT label="Caixa livre" valores={anos.map(a => a.fcf)} />
              </tbody>
            </table>
          </SubSecaoIM>
          <LeituraAIdvisor>
            {ehSU
              ? `Antes de discutir o valuation, este capítulo mostra de onde saem os números. Recomendamos ao investidor não parar na curva de receita e olhar duas linhas: a margem de contribuição, que diz se o crescimento é saudável ou comprado a qualquer custo, e o caixa livre, que revela quanto capital ${nomeEmp} consome até se sustentar sozinha — o vale antes do ponto de equilíbrio. Toda projeção parte das premissas que explicitamos aqui; a due diligence vai testá-las, e é esse o convite deste capítulo.`
              : `Este capítulo abre a caixa-preta das projeções antes do valuation. Chamamos a atenção do investidor para dois movimentos: o CAGR de receita, que ancoramos em comparáveis do setor, e a trajetória da margem — se ela expande, há ganho de escala real; se apenas se mantém, o crescimento é só de volume. A curva de caixa livre é o que dá lastro à conversa de captação. Deixamos cada premissa exposta para que o investidor refaça a conta e a due diligence a valide linha a linha.`}
          </LeituraAIdvisor>
        </PaginaIM>

        {/* Página 15 · Cap 12 · Valuation */}
        <PaginaIM capNum="12" capTitulo="Valuation" paginaAtual={15} totalPaginas={TOTAL}>
          <TituloCapIM numero="12" titulo="Valuation"
            sub={ehSU ? "Faixa de valuation por métodos de startup." : "Avaliação preliminar pelo método DCF."} />
          <SubSecaoIM titulo={ehSU ? "Métodos" : "Premissas"}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr',
              gap: 18, marginTop: 4 }}>
              {ehSU ? (
                <>
                  <KpiIM label="Múltiplos de receita" val={valSU?.evMultiplos ? fmtMiIM(valSU.evMultiplos) : '—'} />
                  <KpiIM label="Fluxo de caixa desc." val={valSU?.evDCF != null ? fmtMiIM(Math.max(0, valSU.evDCF)) : '—'} />
                  <KpiIM label="Capital de risco (VC)" val={valSU?.evVC != null ? fmtMiIM(Math.max(0, valSU.evVC)) : '—'} />
                </>
              ) : (
                <>
                  <KpiIM label="WACC" val={valuation?.wacc ? `${valuation.wacc}%` : '—'} />
                  <KpiIM label="g · Perpetuidade" val={valuation?.g ? `${valuation.g}%` : '3,0%'} />
                  <KpiIM label="Múltiplo setorial" val={valuation?.multiplo ? `${valuation.multiplo}×` : '—'} />
                </>
              )}
            </div>
          </SubSecaoIM>
          <SubSecaoIM titulo="Resultado">
            <div style={{ background: PD.verdeNoite, padding: 28, marginTop: 8 }}>
              <div style={{ fontFamily: PD.mono, fontSize: 10,
                letterSpacing: '0.3em', textTransform: 'uppercase',
                color: PD.creamDeep, fontWeight: 700 }}>
                {ehSU ? 'Valor da empresa · pré-money' : 'Enterprise Value · DCF'}
              </div>
              <div style={{ marginTop: 8, fontFamily: PD.serif, fontWeight: 500,
                fontSize: 72, lineHeight: 1, color: PD.cream,
                letterSpacing: '-0.03em' }}>
                <span style={{ fontSize: 30, color: PD.laranja, fontWeight: 400,
                  fontStyle: 'italic', marginRight: 8 }}>R$</span>
                {ehSU
                  ? (valSU?.central > 0 ? (Math.abs(valSU.central) >= 1e6 ? (valSU.central/1e6).toFixed(1).replace('.', ',') : (valSU.central/1000).toFixed(0)) : '—')
                  : (valuation?.evCalc ? (valuation.evCalc/1_000_000).toFixed(0) : '—')}
                <span style={{ fontFamily: PD.sans, fontSize: 20, fontWeight: 300,
                  color: PD.creamDeep, marginLeft: 10 }}>{ehSU ? (valSU?.central >= 1e6 ? 'milhões' : 'mil') : 'milhões'}</span>
              </div>
              {ehSU && valSU?.faixaMax > 0 && (
                <div style={{ marginTop: 8, fontFamily: PD.serif, fontStyle: 'italic',
                  fontSize: 15, color: PD.creamDeep }}>
                  faixa {fmtMiIM(valSU.faixaMin)} ↔ {fmtMiIM(valSU.faixaMax)}
                </div>
              )}
            </div>
          </SubSecaoIM>
          <LeituraAIdvisor>
            {ehSU
              ? `Apresentamos uma faixa, e não um número único, de propósito: ela resulta da triangulação de três métodos — múltiplos de receita, fluxo de caixa descontado e capital de risco. O piso ancora a conversa em fundamentos; o teto, no potencial de mercado. Explicitamos as premissas de cada método para que o investidor possa refazer a conta e calibrar dentro da faixa conforme a própria leitura de risco.`
              : `O Enterprise Value apoia-se em duas referências independentes — fluxo de caixa descontado e múltiplos setoriais. Explicitamos WACC, crescimento e múltiplo para que o investidor possa refazer a conta e ajustá-la à própria visão de risco; nosso papel foi tornar as premissas auditáveis, não defender um número.`}
          </LeituraAIdvisor>
        </PaginaIM>

        {/* Página 16 · Cap 13 · Meta · Funding & Exit */}
        <PaginaIM capNum="13" capTitulo="Meta · Funding & Exit" paginaAtual={16} totalPaginas={TOTAL}>
          <TituloCapIM numero="13" titulo="Meta · Funding & Exit"
            sub="Objetivo de horizonte e estrutura da operação proposta." />
          <SubSecaoIM titulo="Meta de 5 anos">
            <p style={{ margin: 0, lineHeight: 1.6, fontFamily: PD.serif,
              fontSize: 16, fontStyle: 'italic' }}>
              {fraseMeta || 'Preencher meta na Tarefa 1 do BP60M.'}
            </p>
          </SubSecaoIM>
          {(equityFlag === 'sim' || equityFlag === true || (ehSU && equityFlag === 'em_breve')) && (
            <SubSecaoIM titulo="Estrutura da operação">
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr',
                gap: 18, marginTop: 4 }}>
                {ehSU ? (
                  <>
                    <KpiIM label="Rodada" val={rodadaSelIM ? rodadaSelIM.titulo : 'A definir'} />
                    <KpiIM label="Captação alvo" val={fmtMiIM(valSU?.captacao)} />
                    <KpiIM label="Equity ofertado" val={valSU?.equityPct != null ? `~${String(valSU.equityPct).replace('.', ',')}%` : '—'} />
                    <KpiIM label="Valuation pré-money" val={fmtMiIM(valSU?.central)} />
                  </>
                ) : (
                  <>
                    <KpiIM label="Captação alvo" val={valuation?.captacao ? `R$ ${(valuation.captacao/1_000_000).toFixed(0)} mi` : '—'} />
                    <KpiIM label="Valuation pré-money" val={valuation?.evCalc ? `R$ ${(valuation.evCalc/1_000_000).toFixed(0)} mi` : '—'} />
                    <KpiIM label="Equity ofertado" val="~22%" />
                    <KpiIM label="Tipo" val="Equity puro" />
                  </>
                )}
              </div>
            </SubSecaoIM>
          )}
          <SubSecaoIM titulo="Cenário de saída">
            <p style={{ margin: 0, lineHeight: 1.6, color: PD.txt2 }}>
              {ehSU
                ? 'Saída via aquisição estratégica (M&A) ou IPO, conforme a evolução da tração e o apetite do mercado.'
                : (meta?.tipo === 'saida'
                  ? 'Saída via venda em horizonte de 60 meses, com transição da gestão prevista.'
                  : 'Estratégia de saída a ser detalhada conforme evolução do plano · candidatos a comprador estratégico ou investidor financeiro.')}
            </p>
          </SubSecaoIM>
          <LeituraAIdvisor>
            {ehSU
              ? `Este capítulo fecha a tese com o pedido concreto: quanto ${nomeEmp} capta, por qual fatia e com qual retorno o investidor sai. Recomendamos ler os três juntos — captação, equity e cenário de saída — porque é a coerência entre eles que separa uma proposta estruturada de uma aproximação genérica. Sobre o exit, somos francos: M&A e IPO são horizontes plausíveis, não promessas; o que a rodada financia é chegar ao próximo marco de valor, e é por essa lente que sugerimos avaliá-la.`
              : `Aqui está a estrutura da operação proposta: equity ofertado, valuation pré-money e a janela de saída. Recomendamos ao investidor examinar o encaixe entre a captação pretendida e o uso previsto dos recursos — capta-se para um objetivo específico, não por um valor solto. Sobre a saída, a definição do horizonte e do perfil de comprador provável é o que dá seriedade à conversa; tratamos esses candidatos como hipóteses a validar, e é assim que devem ser lidos nesta fase.`}
          </LeituraAIdvisor>
        </PaginaIM>

        {/* Página 17 · Anexos */}
        <PaginaIM capNum="—" capTitulo="Anexos" paginaAtual={17} totalPaginas={TOTAL}>
          <TituloCapIM numero="—" titulo="Anexos · NDA · Disclaimer"
            sub="Termos de uso, confidencialidade e contato." />
          <SubSecaoIM titulo="Confidencialidade">
            <p style={{ margin: 0, lineHeight: 1.6, color: PD.txt1 }}>
              Este documento é confidencial e destinado exclusivamente ao destinatário
              identificado. Sua reprodução, distribuição ou divulgação a terceiros
              é vedada sem autorização prévia e por escrito da empresa. A continuidade
              do processo depende de NDA assinado.
            </p>
          </SubSecaoIM>
          <SubSecaoIM titulo="Disclaimer">
            <p style={{ margin: 0, lineHeight: 1.6, color: PD.txt2 }}>
              As informações apresentadas refletem a melhor estimativa da empresa
              na data de emissão. Projeções financeiras e indicadores são derivados
              do Business Plan 60 meses e não constituem garantia de resultado.
              Análises adicionais (due diligence) são esperadas em etapas posteriores.
            </p>
          </SubSecaoIM>
          <SubSecaoIM titulo="Contato">
            <div style={{ fontFamily: PD.sans, fontSize: 14, lineHeight: 1.8 }}>
              {empresa?.contato_email && <div><strong>E-mail:</strong> {empresa.contato_email}</div>}
              {empresa?.contato_telefone && <div><strong>Telefone:</strong> {empresa.contato_telefone}</div>}
              {empresa?.endereco && <div><strong>Endereço:</strong> {empresa.endereco}</div>}
            </div>
          </SubSecaoIM>
          <LeituraAIdvisor>
            {ehSU
              ? `Fechamos com os termos porque eles definem as regras da conversa que este memorando abre. O NDA e o disclaimer seguem a prática usual de rodadas de investimento e existem para proteger ${nomeEmp} enquanto as informações sensíveis circulam. Lembramos que tudo aqui reflete a melhor estimativa na data de emissão: os números foram preparados para dar contexto e serão confirmados em due diligence antes de qualquer compromisso vinculante.`
              : `Encerramos com os termos que regem o uso deste material. O NDA e o disclaimer acompanham a prática de processos competitivos de M&A e resguardam ${nomeEmp} durante a troca de informações confidenciais. Registramos com clareza que o conteúdo expressa a melhor estimativa na data de emissão; nada aqui substitui a due diligence, que é a etapa em que cada premissa deste memorando passa a ser verificada.`}
          </LeituraAIdvisor>
        </PaginaIM>

      </div>

      <button onClick={onFechar}
        style={{ position: 'fixed', top: 20, right: 20,
          background: 'transparent', border: '1px solid #FFF',
          color: '#FFF', padding: '6px 12px', fontSize: 11,
          fontWeight: 700, letterSpacing: '0.10em', textTransform: 'uppercase',
          borderRadius: 0, cursor: 'pointer', fontFamily: PD.sans, zIndex: 1001 }}>
        Fechar · esc
      </button>
      <button onClick={() => setModoDemo(!modoDemo)}
        title="Carrega dados sintéticos de ShopMax"
        style={{ position: 'fixed', top: 20, left: 20,
          background: modoDemo ? PD.laranja : 'transparent',
          border: `1px solid ${modoDemo ? PD.laranja : '#FFF'}`,
          color: '#FFF', padding: '6px 12px', fontSize: 11,
          fontWeight: 700, letterSpacing: '0.10em', textTransform: 'uppercase',
          borderRadius: 0, cursor: 'pointer', fontFamily: PD.sans, zIndex: 1001 }}>
        {modoDemo ? 'Modo Demo · ON' : 'Modo Demo · OFF'}
      </button>
      <button onClick={() => baixarPdfDe('[data-pdf-im-page]', `${(empresa?.nome || 'Empresa').replace(/\s+/g,'_')}_IM.pdf`, 'p')}
        style={{ position: 'fixed', top: 20, right: 130,
          background: PD.verde, border: `1px solid ${PD.verde}`,
          color: '#FFF', padding: '6px 12px', fontSize: 11,
          fontWeight: 700, letterSpacing: '0.10em', textTransform: 'uppercase',
          borderRadius: 0, cursor: 'pointer', fontFamily: PD.sans, zIndex: 1001 }}>
        ↓ Baixar PDF
      </button>
    </div>
  );
};

const LeituraAIdvisor = ({ children }) => (
  <div style={{ marginTop: 22, background: '#FFF',
    border: `1px solid ${PD.laranja}40`,
    borderLeft: `4px solid ${PD.laranja}`,
    padding: '14px 18px 14px 18px', position: 'relative',
    overflow: 'hidden' }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: 10,
      marginBottom: 8 }}>
      <span style={{ fontFamily: PD.mono, fontSize: 10,
        letterSpacing: '0.28em', textTransform: 'uppercase',
        fontWeight: 700 }}>
        <span style={{ color: PD.verde }}>Comentário · </span>
        <span style={{ color: PD.verde }}>RUNGOAL </span>
        <span style={{ color: PD.laranja }}>AI</span>
        <span style={{ color: PD.verde }}>DVISOR</span>
      </span>
    </div>
    <p style={{ margin: 0, fontFamily: PD.serif, fontSize: 13.5,
      fontStyle: 'italic', color: PD.txt1, lineHeight: 1.6 }}>
      {children}
    </p>
  </div>
);

const KpiIM = ({ label, val }) => (
  <div style={{ background: '#FFF', border: `1px solid ${PD.txt1}20`,
    borderLeft: `3px solid ${PD.verde}`, padding: 12 }}>
    <div style={{ fontFamily: PD.mono, fontSize: 9.5,
      letterSpacing: '0.25em', textTransform: 'uppercase',
      color: PD.txt2, fontWeight: 700 }}>{label}</div>
    <div style={{ marginTop: 4, fontFamily: PD.serif, fontWeight: 600,
      fontSize: 18, color: PD.verdeNoite }}>{val}</div>
  </div>
);

// =====================================================================
// Plano de Ação Interno · documento operacional · A4 multi-página
// Voz: RUNGOAL AIDVISOR · 3 colunas (frente · ação · KPI) + parecer texto
// =====================================================================

const PlanoAcaoInterno = ({ bp, onFechar }) => {
  const [modoDemo, setModoDemo] = useState(false);
  const bpAtivo = modoDemo ? DEMO_SHOPMAX_BP : bp;
  const { meta, empresa, marketingVendas, estruturaRh, projecoes, valuation,
    swot, equityFlag, modo, valSU } = bpAtivo;
  const ehSU = modo === 'SU';
  const tipoMetaSel = (ehSU ? TIPOS_META_BP60M_SU : TIPOS_META_BP60M).find(t => t.id === meta?.tipo);
  const fraseMeta = tipoMetaSel ? `${tipoMetaSel.fraseAntes || ''}${meta?.valor || ''}${tipoMetaSel.fraseDepois || ''}` : '';
  const nomeEmp = empresa?.nome || 'Sua Empresa';
  const setorTexto = empresa?.setor || 'Setor';
  const anos = ((ehSU && valSU?.anos) ? valSU.anos : (projecoes?.anos || [])).slice(0,5);
  const rodadaSelP = ehSU ? RODADAS_SU.find(r => r.id === meta?.rodada) : null;
  const fmtMiP = (v) => v == null ? '—' : (Math.abs(v) >= 1_000_000 ? `R$ ${(v/1_000_000).toFixed(2)} mi` : `R$ ${(v/1000).toFixed(0)} mil`);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onFechar(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onFechar]);

  const TOTAL = 8;

  // Iniciativas derivadas das tarefas BP60M
  const iniciativas = [
    {
      frente: 'Operação',
      acao: ehSU ? 'Estabelecer processos centrais e tecnologia de suporte que escalem com o crescimento.' : 'Consolidar processos centrais e tecnologia de suporte na base atual.',
      kpi: (ehSU ? 'Margem de contribuição ≥ ' : 'Margem EBITDA ≥ ') + (anos[1]?.receita ? (((ehSU ? (anos[1].margemContrib || 0) : (anos[1].ebitda || 0))/anos[1].receita)*100).toFixed(0) + '%' : (ehSU ? '40%' : '12%')),
    },
    {
      frente: 'Comercial',
      acao: 'Estruturar canais com CAC controlado e LTV crescente.',
      kpi: marketingVendas?.cac ? `CAC ≤ R$ ${marketingVendas.cac}` : 'CAC ≤ R$ 50',
    },
    {
      frente: 'Time',
      acao: ehSU ? 'Completar o time fundador e formar um grupo de mentores e advisors.' : 'Profissionalizar diretoria e fortalecer conselho consultivo.',
      kpi: ehSU ? 'Time fundador completo em 60 dias' : 'Diretoria executiva completa em 90 dias',
    },
    {
      frente: 'Financeiro',
      acao: anos[1]?.receita ? `Atingir receita de ${ehSU ? fmtMiP(anos[1].receita) : `R$ ${(anos[1].receita/1_000_000).toFixed(0)} mi`} no ano 2 do plano.` : 'Atingir meta de receita do ano 2 do plano.',
      kpi: anos[1]?.receita ? `${ehSU ? fmtMiP(anos[1].receita) : `R$ ${(anos[1].receita/1_000_000).toFixed(0)} mi`} de receita` : 'Receita conforme plano',
    },
    {
      frente: 'Captação',
      acao: (equityFlag === 'sim' || equityFlag === true || (ehSU && equityFlag === 'em_breve'))
        ? (ehSU ? `Executar a Rodada ${rodadaSelP ? rodadaSelP.titulo : 'de investimento'} com investidor-âncora em 90 dias.` : 'Executar rodada de captação com investidor-âncora em 90 dias.')
        : 'Reservar próxima janela de captação para o horizonte 2.',
      kpi: ehSU
        ? (valSU?.captacao ? `${fmtMiP(valSU.captacao)} captados` : 'Plano de captação validado')
        : ((equityFlag === 'sim' || equityFlag === true) && valuation?.captacao
          ? `R$ ${(valuation.captacao/1_000_000).toFixed(0)} mi captados`
          : 'Plano de captação validado'),
    },
  ];

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(10,10,8,0.92)',
      zIndex: 1000, display: 'flex', justifyContent: 'center',
      overflowY: 'auto', padding: '40px 0' }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>

        {/* Página 1 · Capa */}
        <PaginaIM semHeader paginaAtual={1} totalPaginas={TOTAL}>
          <div style={{ height: '100%', display: 'flex',
            flexDirection: 'column', justifyContent: 'space-between' }}>
            <div>
              <div style={{ fontFamily: PD.mono, fontSize: 11,
                letterSpacing: '0.32em', textTransform: 'uppercase',
                color: PD.verde, fontWeight: 700 }}>
                Plano de Ação Interno
              </div>
              <div style={{ marginTop: 6, fontFamily: PD.mono, fontSize: 10,
                letterSpacing: '0.28em', color: PD.txt3,
                textTransform: 'uppercase' }}>
                Documento operacional · empresário e equipe
              </div>
            </div>
            <div>
              <div style={{ fontFamily: PD.serif, fontWeight: 700,
                fontSize: 76, color: PD.verdeNoite, letterSpacing: '-0.025em',
                lineHeight: 0.95 }}>
                {nomeEmp}
              </div>
              <div style={{ marginTop: 18, fontFamily: PD.mono, fontSize: 12,
                letterSpacing: '0.28em', textTransform: 'uppercase',
                color: PD.laranja, fontWeight: 700 }}>
                {setorTexto}
              </div>
              <div style={{ marginTop: 60, height: 1, background: PD.txt1,
                opacity: 0.2 }} />
              <div style={{ marginTop: 18, fontFamily: PD.serif, fontStyle: 'italic',
                fontSize: 18, color: PD.txt1, lineHeight: 1.5, maxWidth: 620 }}>
                Plano operacional consolidando todas as iniciativas, indicadores e
                cadência de revisão para os próximos 60 meses, com leitura
                permanente da RUNGOAL AIDVISOR.
              </div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between',
              alignItems: 'flex-end' }}>
              <div>
                <div style={{ fontFamily: PD.mono, fontSize: 10,
                  letterSpacing: '0.25em', textTransform: 'uppercase',
                  color: PD.txt2, fontWeight: 700 }}>Edição</div>
                <div style={{ marginTop: 4, fontFamily: PD.serif, fontSize: 16,
                  color: PD.verdeNoite }}>Junho · 2026</div>
              </div>
              <div style={{ textAlign: 'right', fontFamily: PD.mono,
                fontSize: 11, color: PD.txt2, lineHeight: 1.8 }}>
                {empresa?.contato_email && <div>{empresa.contato_email}</div>}
                {empresa?.contato_telefone && <div>{empresa.contato_telefone}</div>}
              </div>
            </div>
          </div>
        </PaginaIM>

        {/* Página 2 · Visão geral */}
        <PaginaIM capNum="01" capTitulo="Visão Geral" paginaAtual={2} totalPaginas={TOTAL} documento="Plano de Ação">
          <TituloCapIM numero="01" titulo="Visão Geral"
            sub="Meta, horizonte e premissas que ancoram o plano." />
          <SubSecaoIM titulo="Meta de 5 anos">
            <p style={{ margin: 0, fontFamily: PD.serif, fontStyle: 'italic',
              fontSize: 17, lineHeight: 1.55 }}>
              {fraseMeta || 'Preencher meta na Tarefa 1 do BP60M.'}
            </p>
          </SubSecaoIM>
          <SubSecaoIM titulo="Premissas centrais">
            <ul style={{ margin: 0, padding: '0 0 0 18px', lineHeight: 1.7 }}>
              <li>{ehSU ? 'Crescimento acelerado para conquistar mercado e validar o modelo (product-market fit).' : 'Crescimento sustentável da operação atual antes de buscar escala.'}</li>
              <li>{ehSU ? 'Unit economics saudáveis · margem de contribuição controlada enquanto se investe para crescer.' : 'Margem operacional defendida como prioridade · expansão sem perda de eficiência.'}</li>
              <li>{ehSU ? 'Time fundador estruturado e mentores/advisors ativos até o fim do ano 1.' : 'Time-chave estruturado e conselho consultivo ativo até o fim do ano 1.'}</li>
              <li>Cadência mensal de revisão de indicadores e trimestral de plano.</li>
            </ul>
          </SubSecaoIM>
          <SubSecaoIM titulo="Comentário · RUNGOAL AIDVISOR">
            <BlocoParecer>
              {ehSU
                ? `Preparamos este plano ligando a meta declarada na Tarefa 1 à estrutura operacional e ao time fundador — a leitura deste capítulo é conferir se essa linha se mantém de ponta a ponta. O ponto de atenção que pedimos para o gestor não perder de vista são os primeiros 18 meses: é a fase de maior queima de caixa, e acelerar a expansão antes de o modelo se provar é o erro mais caro que ${nomeEmp} pode cometer nesse período. Segure o caixa primeiro; a aceleração vem depois da validação.`
                : `Preparamos este plano amarrando a meta da Tarefa 1 à estrutura operacional e ao time. Recomendamos ao gestor usar esta página como fio condutor de todas as demais. O cuidado central está nos primeiros 18 meses: proteger a margem antes de abrir novas frentes de expansão geográfica e tecnologia. A base recorrente de clientes de ${nomeEmp} dá fôlego para essa disciplina — o risco não é crescer devagar, é crescer erodindo a margem que sustenta a operação.`}
            </BlocoParecer>
          </SubSecaoIM>
        </PaginaIM>

        {/* Página 3 · Roteiro 4 horizontes */}
        <PaginaIM capNum="02" capTitulo="Roteiro Estratégico" paginaAtual={3} totalPaginas={TOTAL} documento="Plano de Ação">
          <TituloCapIM numero="02" titulo="Roteiro Estratégico"
            sub="Quatro horizontes de execução até o 5.º ano." />
          <div style={{ marginTop: 8 }}>
            {(ehSU ? [
              { per: 'M0 → M12', nome: 'Validar', desc: 'Validar o produto e conquistar os primeiros clientes (product-market fit).' },
              { per: 'M12 → M24', nome: 'Tracionar', desc: 'Estruturar o go-to-market e acelerar a aquisição de clientes.' },
              { per: 'M24 → M40', nome: 'Escalar', desc: 'Escalar canais e operação · preparar a próxima rodada.' },
              { per: 'M40 → M60', nome: 'Coroar', desc: 'Tração consolidada · prontidão para Série A ou saída (M&A/IPO).' },
            ] : [
              { per: 'M0 → M12', nome: 'Adensar', desc: 'Consolidar processos centrais e base de clientes existente.' },
              { per: 'M12 → M24', nome: 'Expandir', desc: 'Abrir novos hubs e mercados regionais.' },
              { per: 'M24 → M40', nome: 'Escalar', desc: 'Digital first · marketplace whitelabel · multiplicação da operação.' },
              { per: 'M40 → M60', nome: 'Coroar', desc: 'Operação madura · prontidão para captação principal ou M&A.' },
            ]).map((h, i) => (
              <div key={i} style={{ display: 'grid',
                gridTemplateColumns: '110px 1fr', gap: 18,
                padding: '14px 0', borderBottom: '1px solid rgba(0,0,0,0.08)' }}>
                <div>
                  <div style={{ fontFamily: PD.mono, fontSize: 11,
                    letterSpacing: '0.22em', textTransform: 'uppercase',
                    color: PD.laranjaFunda, fontWeight: 700 }}>{h.per}</div>
                </div>
                <div>
                  <div style={{ fontFamily: PD.serif, fontWeight: 600,
                    fontSize: 20, color: PD.verdeNoite, lineHeight: 1.2 }}>{h.nome}</div>
                  <div style={{ marginTop: 4, fontFamily: PD.sans, fontSize: 13.5,
                    color: PD.txt1, lineHeight: 1.55 }}>{h.desc}</div>
                </div>
              </div>
            ))}
          </div>
        </PaginaIM>

        {/* Página 4 · Iniciativas-chave · 3 colunas + Carimbo + Parecer */}
        <section data-pdf-im-page style={{ width: IM_PAGE_W, height: IM_PAGE_H,
          minHeight: IM_PAGE_H, flexShrink: 0,
          background: PD.cream, color: PD.txt1, fontFamily: PD.sans,
          padding: '60px 70px', boxSizing: 'border-box', position: 'relative',
          overflow: 'hidden', boxShadow: '0 4px 16px rgba(0,0,0,0.18)' }}>
          <CarimboAIdvisor top={24} right={58} tamanho={120} />
          <div style={{ position: 'absolute', top: 24, left: 70, right: 200,
            display: 'flex', justifyContent: 'space-between',
            fontFamily: PD.mono, fontSize: 9, letterSpacing: '0.28em',
            textTransform: 'uppercase', color: PD.txt3 }}>
            <span>Plano de Ação · Confidencial</span>
            <span>Capítulo 03 · Iniciativas-chave</span>
          </div>
          <div style={{ marginTop: 50 }}>
            <TituloCapIM numero="03" titulo="Iniciativas-chave"
              sub="Frentes prioritárias · responsabilidades e indicadores propostos pela RUNGOAL AIDVISOR." />
            <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: 8 }}>
              <thead>
                <tr>
                  <th style={{ ...thT, textAlign: 'left', width: 130 }}>Frente</th>
                  <th style={{ ...thT, textAlign: 'left' }}>Ação proposta</th>
                  <th style={{ ...thT, textAlign: 'left', width: 200 }}>KPI esperado</th>
                </tr>
              </thead>
              <tbody>
                {iniciativas.map((it, i) => (
                  <tr key={i} style={{ borderBottom: '1px solid rgba(0,0,0,0.06)' }}>
                    <td style={{ padding: '14px 8px 14px 0',
                      fontFamily: PD.mono, fontSize: 10,
                      letterSpacing: '0.22em', textTransform: 'uppercase',
                      color: PD.laranjaFunda, fontWeight: 700,
                      verticalAlign: 'top' }}>
                      {it.frente}
                    </td>
                    <td style={{ padding: '14px 8px 14px 0',
                      fontFamily: PD.sans, fontSize: 13, color: PD.txt1,
                      lineHeight: 1.5, verticalAlign: 'top' }}>
                      {it.acao}
                    </td>
                    <td style={{ padding: '14px 0', fontFamily: PD.serif,
                      fontSize: 14, fontWeight: 600,
                      color: PD.verdeNoite, verticalAlign: 'top' }}>
                      {it.kpi}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div style={{ marginTop: 20 }}>
            <SubSecaoIM titulo="Comentário · RUNGOAL AIDVISOR">
              <BlocoParecer>
                Traduzimos aqui a meta de cinco anos em cinco frentes com dono e indicador — este
                é o capítulo que transforma intenção em compromisso. A leitura que sugerimos ao
                gestor é de sequência, não de lista: comece por Operação, porque as outras quatro
                dependem dela; ative Comercial em paralelo; e trate Captação como consequência,
                não como largada — ela só vira prioridade depois do ano 1, com tração já validada.
                Um alerta que fazemos questão de deixar registrado: o time-chave precisa estar
                fechado antes da primeira conversa séria com investidor-âncora, e não durante.
              </BlocoParecer>
            </SubSecaoIM>
          </div>
          <div style={{ position: 'absolute', bottom: 24, left: 70, right: 70,
            display: 'flex', justifyContent: 'space-between',
            fontFamily: PD.mono, fontSize: 9, letterSpacing: '0.22em',
            textTransform: 'uppercase', color: PD.txt3,
            borderTop: `1px solid ${PD.txt1}20`, paddingTop: 12 }}>
            <PoweredBy />
            <span>4 / {TOTAL}</span>
          </div>
        </section>

        {/* Página 5 · Responsabilidades */}
        <PaginaIM capNum="04" capTitulo="Responsabilidades" paginaAtual={5} totalPaginas={TOTAL} documento="Plano de Ação">
          <TituloCapIM numero="04" titulo="Estrutura de Responsabilidades"
            sub="Quem responde por cada área da operação." />
          {(estruturaRh?.organograma || []).length > 0 ? (
            <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: 4 }}>
              <thead>
                <tr>
                  <th style={{ ...thT, textAlign: 'left', width: 160 }}>Área</th>
                  <th style={{ ...thT, textAlign: 'left', width: 200 }}>Responsável</th>
                  <th style={{ ...thT, textAlign: 'left' }}>Reporta a</th>
                </tr>
              </thead>
              <tbody>
                {estruturaRh.organograma.slice(0,8).map((p, i) => (
                  <tr key={i} style={{ borderBottom: '1px solid rgba(0,0,0,0.06)' }}>
                    <td style={{ padding: '12px 0', fontFamily: PD.mono,
                      fontSize: 10, letterSpacing: '0.18em',
                      textTransform: 'uppercase', color: PD.laranjaFunda,
                      fontWeight: 700 }}>{p.area || '—'}</td>
                    <td style={{ padding: '12px 0', fontFamily: PD.serif,
                      fontSize: 14, fontWeight: 600,
                      color: PD.verdeNoite }}>{p.nome || '—'}</td>
                    <td style={{ padding: '12px 0', fontFamily: PD.sans,
                      fontSize: 13, color: PD.txt2 }}>
                      {p.reporta_a || 'CEO'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <em style={{ color: PD.txt3 }}>Cadastrar organograma na Tarefa 10 do BP60M.</em>
          )}
          <SubSecaoIM titulo="Comentário · RUNGOAL AIDVISOR">
            <BlocoParecer>
              {ehSU
                ? 'Montamos esta matriz a partir do desenho atual, e é justamente por isso que pedimos ao gestor uma leitura crítica: o que aqui parece organograma é, na prática, um mapa de risco de concentração. Nossa recomendação é identificar backup para as áreas críticas — Comercial, Operações e Produto — antes que a saída de uma pessoa vire uma crise. O grupo de mentores e advisors entra como apoio de execução, com cadência mensal, para reduzir a dependência das poucas cabeças que hoje seguram tudo.'
                : 'Esta matriz reflete o desenho atual, e a leitura que propomos é enxergá-la como mapa de exposição, não só de cargos. Recomendamos ao gestor garantir backup para as áreas críticas — Comercial, Operações e Financeiro — porque concentração de decisão em poucas pessoas é a fragilidade que um comprador ou financiador identifica primeiro. O conselho consultivo entra como instância independente de governança, com reuniões trimestrais documentadas que criam o rastro que a diligência futura vai procurar.'}
            </BlocoParecer>
          </SubSecaoIM>
        </PaginaIM>

        {/* Página 6 · KPIs */}
        <PaginaIM capNum="05" capTitulo="Indicadores de Acompanhamento" paginaAtual={6} totalPaginas={TOTAL} documento="Plano de Ação">
          <TituloCapIM numero="05" titulo="Indicadores de Acompanhamento"
            sub="O que medir todo mês e como interpretar." />
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr',
            gap: 14, marginTop: 8 }}>
            {[
              { label: 'Receita mensal · YoY', alvo: anos[1]?.receita ? `> ${ehSU ? fmtMiP(anos[1].receita/12) : `R$ ${((anos[1].receita/12)/1_000_000).toFixed(1).replace('.', ',')} mi`}/mês no ano 2` : '—' },
              { label: ehSU ? 'Margem de contribuição mensal' : 'Margem EBITDA mensal', alvo: anos[1]?.receita ? `≥ ${(((ehSU ? (anos[1].margemContrib || 0) : (anos[1].ebitda || 0))/anos[1].receita)*100).toFixed(1).replace('.', ',')}%` : '—' },
              { label: 'CAC efetivo', alvo: marketingVendas?.cac ? `≤ R$ ${marketingVendas.cac}` : '—' },
              { label: 'LTV / CAC', alvo: '≥ 3,0×' },
              { label: ehSU ? 'Runway (meses de caixa)' : 'Caixa em meses de operação', alvo: ehSU ? '≥ 12 meses' : '≥ 6 meses' },
              { label: 'Conversão de proposta', alvo: '≥ 22%' },
            ].map((k, i) => (
              <div key={i} style={{ background: '#FFF',
                border: `1px solid ${PD.txt1}20`,
                borderLeft: `3px solid ${PD.verde}`, padding: 14 }}>
                <div style={{ fontFamily: PD.mono, fontSize: 9.5,
                  letterSpacing: '0.25em', textTransform: 'uppercase',
                  color: PD.txt2, fontWeight: 700 }}>{k.label}</div>
                <div style={{ marginTop: 6, fontFamily: PD.serif,
                  fontWeight: 600, fontSize: 17,
                  color: PD.verdeNoite }}>Alvo · {k.alvo}</div>
              </div>
            ))}
          </div>
          <SubSecaoIM titulo="Comentário · RUNGOAL AIDVISOR">
            <BlocoParecer>
              Selecionamos este painel como o mínimo que o gestor precisa olhar todo mês — poucos
              indicadores acompanhados de perto valem mais do que muitos revisados de vez em quando.
              A leitura que recomendamos: ajuste as metas a cada trimestre conforme os desvios,
              sem refazer o plano inteiro a cada tropeço. E fixe a atenção num deles acima dos
              outros — o caixa em meses de operação. É o indicador de segurança que dá ao gestor o
              tempo de reagir antes que um desvio de receita ou margem vire um problema de solvência.
            </BlocoParecer>
          </SubSecaoIM>
        </PaginaIM>

        {/* Página 7 · Riscos e mitigação */}
        <PaginaIM capNum="06" capTitulo="Riscos e Mitigação" paginaAtual={7} totalPaginas={TOTAL} documento="Plano de Ação">
          <TituloCapIM numero="06" titulo="Riscos e Mitigação"
            sub="O que pode tirar o plano do trilho e como preparar resposta." />
          {(swot?.ameacas?.filter(x=>x.marcado) || []).length > 0 ? (
            <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: 4 }}>
              <thead>
                <tr>
                  <th style={{ ...thT, textAlign: 'left' }}>Risco</th>
                  <th style={{ ...thT, textAlign: 'left', width: 240 }}>Mitigação proposta</th>
                </tr>
              </thead>
              <tbody>
                {swot.ameacas.filter(x=>x.marcado).slice(0,5).map((a, i) => (
                  <tr key={i} style={{ borderBottom: '1px solid rgba(0,0,0,0.06)' }}>
                    <td style={{ padding: '14px 8px 14px 0', fontFamily: PD.sans,
                      fontSize: 13.5, color: PD.txt1, lineHeight: 1.5,
                      verticalAlign: 'top' }}>{a.texto}</td>
                    <td style={{ padding: '14px 0', fontFamily: PD.sans,
                      fontSize: 12.5, color: PD.txt2, lineHeight: 1.5,
                      verticalAlign: 'top', fontStyle: 'italic' }}>
                      Monitorar indicadores trimestralmente · revisar plano caso desvio &gt; 15%.
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <em style={{ color: PD.txt3 }}>Cadastrar ameaças no SWOT (Tarefa 6) e riscos (Tarefa 11) do BP60M.</em>
          )}
          <SubSecaoIM titulo="Comentário · RUNGOAL AIDVISOR">
            <BlocoParecer>
              {ehSU
                ? `Consolidamos aqui os riscos que já apareceram no SWOT e na Tarefa 11, agora com resposta associada. O que pedimos ao gestor não é vigiar todos por igual: escolha dois como riscos-âncora, os de maior impacto sobre a tese, e leve-os toda reunião mensal com founders e mentores, cada um com contingência ativável em 30 dias. O maior risco operacional de ${nomeEmp} não está na tabela — é deixar de revisar a tabela.`
                : `Reunimos aqui os riscos vindos do SWOT e da Tarefa 11, cada um com a mitigação proposta. A recomendação ao gestor é priorizar: eleja dois riscos-âncora, os que de fato podem tirar o plano do trilho, e monitore-os na reunião trimestral de conselho, com plano de contingência pronto para acionar em 30 dias. Registramos com franqueza que o risco mais provável de se concretizar é o de processo — parar de revisar a lista —, e é contra ele que a cadência de governança existe.`}
            </BlocoParecer>
          </SubSecaoIM>
        </PaginaIM>

        {/* Página 8 · Calendário de revisões */}
        <PaginaIM capNum="07" capTitulo="Calendário de Revisões" paginaAtual={8} totalPaginas={TOTAL} documento="Plano de Ação">
          <TituloCapIM numero="07" titulo="Calendário de Revisões"
            sub="Cadência mensal e trimestral · governança do plano." />
          <div style={{ marginTop: 8 }}>
            {[
              { quando: 'Mensal', titulo: 'Painel de indicadores', desc: 'Receita, margem, caixa, CAC, conversão. 1 hora · time executivo.' },
              { quando: 'Mensal', titulo: 'Status das iniciativas-chave', desc: 'Cada frente atualiza o avanço e bloqueios. 1 hora · time executivo + responsáveis.' },
              { quando: 'Trimestral', titulo: 'Revisão de plano', desc: ehSU ? 'Ajuste de metas trimestrais · revisão de riscos · próximos 90 dias. 3 horas · founders + mentores.' : 'Ajuste de metas trimestrais · revisão de riscos · próximos 90 dias. 3 horas · executivo + conselho.' },
              { quando: 'Anual', titulo: 'Revisão estratégica', desc: ehSU ? 'Releitura do BP60M · ajuste de horizontes · regerar pitch e materiais de captação. 1 dia · founders + mentores + investidores.' : 'Releitura do BP60M · ajuste de horizontes · regerar documentos institucionais. 1 dia · executivo + conselho + advisors externos.' },
            ].map((c, i) => (
              <div key={i} style={{ display: 'grid',
                gridTemplateColumns: '110px 1fr', gap: 18,
                padding: '14px 0', borderBottom: '1px solid rgba(0,0,0,0.08)' }}>
                <div>
                  <div style={{ fontFamily: PD.mono, fontSize: 10,
                    letterSpacing: '0.22em', textTransform: 'uppercase',
                    color: PD.laranjaFunda, fontWeight: 700 }}>{c.quando}</div>
                </div>
                <div>
                  <div style={{ fontFamily: PD.serif, fontWeight: 600,
                    fontSize: 16, color: PD.verdeNoite }}>{c.titulo}</div>
                  <div style={{ marginTop: 4, fontFamily: PD.sans, fontSize: 13,
                    color: PD.txt1, lineHeight: 1.55 }}>{c.desc}</div>
                </div>
              </div>
            ))}
          </div>
          <SubSecaoIM titulo="Comentário · RUNGOAL AIDVISOR">
            <BlocoParecer>
              Fechamos o plano por este capítulo de propósito: um plano sem cadência de revisão
              é um documento, não um instrumento de gestão. A leitura que deixamos ao gestor é a
              metáfora das três frequências — a revisão mensal é o motor, a trimestral é o leme, a
              anual é a bússola —, e cada uma tem função distinta. A recomendação prática é uma só:
              bloqueie todas as datas na agenda do ano de uma vez, com quórum mínimo definido, antes
              que a rotina engula o compromisso. A RUNGOAL AIDVISOR acompanha cada revisão propondo a
              leitura dos indicadores e os ajustes de plano.
            </BlocoParecer>
          </SubSecaoIM>
        </PaginaIM>

      </div>

      <button onClick={onFechar}
        style={{ position: 'fixed', top: 20, right: 20,
          background: 'transparent', border: '1px solid #FFF',
          color: '#FFF', padding: '6px 12px', fontSize: 11,
          fontWeight: 700, letterSpacing: '0.10em', textTransform: 'uppercase',
          borderRadius: 0, cursor: 'pointer', fontFamily: PD.sans, zIndex: 1001 }}>
        Fechar · esc
      </button>
      <button onClick={() => setModoDemo(!modoDemo)}
        title="Carrega dados sintéticos de ShopMax"
        style={{ position: 'fixed', top: 20, left: 20,
          background: modoDemo ? PD.laranja : 'transparent',
          border: `1px solid ${modoDemo ? PD.laranja : '#FFF'}`,
          color: '#FFF', padding: '6px 12px', fontSize: 11,
          fontWeight: 700, letterSpacing: '0.10em', textTransform: 'uppercase',
          borderRadius: 0, cursor: 'pointer', fontFamily: PD.sans, zIndex: 1001 }}>
        {modoDemo ? 'Modo Demo · ON' : 'Modo Demo · OFF'}
      </button>
      <button onClick={() => baixarPdfDe('[data-pdf-im-page]', `${(empresa?.nome || 'Empresa').replace(/\s+/g,'_')}_Plano_de_Acao.pdf`, 'p')}
        style={{ position: 'fixed', top: 20, right: 130,
          background: PD.verde, border: `1px solid ${PD.verde}`,
          color: '#FFF', padding: '6px 12px', fontSize: 11,
          fontWeight: 700, letterSpacing: '0.10em', textTransform: 'uppercase',
          borderRadius: 0, cursor: 'pointer', fontFamily: PD.sans, zIndex: 1001 }}>
        ↓ Baixar PDF
      </button>
    </div>
  );
};

const BlocoParecer = ({ children }) => (
  <div style={{ background: '#FFF', border: `1px solid ${PD.verde}40`,
    borderLeft: `4px solid ${PD.verde}`, padding: 14, marginTop: 4 }}>
    <p style={{ margin: 0, fontFamily: PD.serif, fontSize: 13.5,
      color: PD.txt1, lineHeight: 1.6 }}>
      {children}
    </p>
  </div>
);

const compilarTarefa13 = (projecoesState, empresa) => {
  const rascunho = RASCUNHO_TAREFA_13[empresa?.id] || {
    receita_inicial: 0, crescimento_id: 'b', cpv_id: 'b',
    despesas: { pessoal: 'b', estrutura: 'b', marketing: 'b', outras: 'b' },
    capex_id: 'b',
  };
  const receita0   = projecoesState?.receita_inicial ?? rascunho.receita_inicial;
  const crescId    = projecoesState?.crescimento_id  ?? rascunho.crescimento_id;
  const cpvId      = projecoesState?.cpv_id          ?? rascunho.cpv_id;
  const despesasIds= projecoesState?.despesas        ?? rascunho.despesas;
  const capexId    = projecoesState?.capex_id        ?? rascunho.capex_id;
  const anos = calcularProjecao(receita0, crescId, cpvId, despesasIds, capexId);
  const cresc  = CRESCIMENTO_RECEITA_INTERVALOS.find(o => o.id === crescId);
  const cpv    = CPV_INTERVALOS.find(o => o.id === cpvId);
  const capex  = CAPEX_INTERVALOS.find(o => o.id === capexId);
  const fmt = (v) => `R$ ${(v/1000).toFixed(0)} mil`;
  const fmtMi = (v) => v >= 1000000 ? `R$ ${(v/1000000).toFixed(2)} mi` : fmt(v);
  const fmtPct = (v) => `${(v*100).toFixed(1)}%`;
  const buckets = DESPESAS_BUCKETS.map(b => {
    const sel = b.opcoes.find(o => o.id === despesasIds?.[b.id]);
    return `- **${b.label}:** ${sel?.label} (${sel?.faixa})`;
  }).join('\n');
  return `## 13. Projeções 5 anos

### Premissas estratégicas (classes qualitativas)

- **Crescimento de Receita:** ${cresc?.label} (${cresc?.faixa}) — _${cresc?.desc}_
- **Custos diretos (CPV):** ${cpv?.label} (${cpv?.faixa}) — _${cpv?.desc}_
- **CAPEX médio:** ${capex?.label} (${capex?.faixa}) — _${capex?.desc}_

**Composição de despesas operacionais:**

${buckets}

A evolução projetada da Receita, Despesas, EBITDA e FCF ao longo de 5 anos está ilustrada abaixo. O EBITDA aparece como resultado da equação Receita − Custos − Despesas.

<!--GRAFICO:t13_evolucao-->

### DRE / FCF projetado (cenário Base)

| Período | Receita | Custos | Despesas | EBITDA | Margem EBITDA | CAPEX | FCF |
|---------|---------|--------|----------|--------|---------------|-------|-----|
${anos.map(a => `| ${a.ano} | ${fmtMi(a.receita)} | ${fmtMi(a.custos)} | ${fmtMi(a.despesas)} | ${fmtMi(a.ebitda)} | ${fmtPct(a.margemPct)} | ${fmtMi(a.capex)} | ${fmtMi(a.fcf)} |`).join('\n')}

### Cenários

A comparação dos três cenários (Pessimista · Base · Otimista) no Ano 5 está ilustrada abaixo.

<!--GRAFICO:t13_cenarios-->

${CENARIOS_PROJ.map(c => {
  const ano5 = anos[5];
  const fator = 1 + c.ajuste;
  return `- **${c.label}:** Receita Ano 5 ${fmtMi(ano5.receita * fator)} · EBITDA ${fmtMi(ano5.ebitda * fator)} · FCF ${fmtMi(ano5.fcf * fator)}`;
}).join('\n')}`;
};

// ═══════════════════════════════════════════════════════════════════════
// TAREFA 14 — Valuation DCF
// 5 blocos: WACC · g perpetuidade · método de Valor Terminal · múltiplo setorial · resultado calculado
// EV = Σ FCF descontado (anos 1-5) + Valor Terminal descontado
// ═══════════════════════════════════════════════════════════════════════

const WACC_INTERVALOS = [
  { id: 'a', label: 'Conservadora',     faixa: '12-15%', meio: 0.135, desc: 'Empresa madura · baixo risco · baixa alavancagem' },
  { id: 'b', label: 'Padrão',           faixa: '18-22%', meio: 0.20,  desc: 'Patamar típico de PME brasileira saudável' },
  { id: 'c', label: 'Agressiva',        faixa: '25-30%', meio: 0.275, desc: 'Scaleup · setor cíclico · risco moderado a alto' },
  { id: 'd', label: 'Muito agressiva',  faixa: '32%+',   meio: 0.35,  desc: 'Early-stage · forte incerteza · alto risco' },
];

const G_PERPETUIDADE = [
  { id: 'a', label: 'Defensiva',  faixa: '1-2%',     meio: 0.015,  desc: 'Próximo ao crescimento populacional/inflação alvo' },
  { id: 'b', label: 'Padrão',     faixa: '2.5-4%',   meio: 0.0325, desc: 'Inflação alvo + ganho de produtividade · BR típico' },
  { id: 'c', label: 'Otimista',   faixa: '4.5-5.5%', meio: 0.05,   desc: 'Setor em expansão estrutural' },
  { id: 'd', label: 'Agressiva',  faixa: '6%+',      meio: 0.065,  desc: 'Raro · setor disruptivo · alto risco de não sustentar' },
];

const METODOS_VT = [
  { id: 'gordon',   label: 'Perpetuidade Gordon',     desc: 'VT = FCF Ano 5 × (1+g) / (WACC − g) · clássico DCF · sensível a g' },
  { id: 'multiplo', label: 'Múltiplo de saída EBITDA', desc: 'VT = EBITDA Ano 5 × múltiplo setorial (Damodaran/ABVCAP)' },
  { id: 'hibrido',  label: 'Híbrido',                 desc: 'Média entre Gordon e Múltiplo · suaviza o viés de cada método' },
];

// Múltiplos EBITDA por SETOR (Damodaran/ABVCAP — proxy MVP).
//
// Reescrito em 30/07/2026. Antes esta tabela era indexada por **id de empresa
// de teste** (`shopmax`, `sabor_arte`, `clinicavida`, `construmax`), com
// múltiplos próprios que divergiam da tabela do backend — shopmax 7×
// contra E-commerce 5-8×, sabor_arte 8× contra Bar/Restaurante 3-5×. Qualquer
// empresa real caía em `undefined` e a Tarefa 14 do BP60M ficava sem
// referência de múltiplo. Era o vínculo mais direto com as empresas de teste
// que restava no valuation.
//
// Agora indexa por SETOR e espelha `MULTIPLOS_SETOR` de `app/calculator.py`,
// que é a fonte. Antes de mudar um número aqui, mude lá.
const MULTIPLO_POR_SETOR = {
  'Tecnologia/SaaS':        { multiplo: 10, label: 'Tecnologia/SaaS',        faixa: '8-12×', desc: 'Software como serviço · plataformas digitais' },
  'E-commerce':             { multiplo: 6,  label: 'Comércio digital',       faixa: '5-8×',  desc: 'Marketplace · e-commerce · varejo online' },
  'Saude':                  { multiplo: 8,  label: 'Saúde/Clínica',          faixa: '7-10×', desc: 'Serviços médicos e clínicas especializadas' },
  'Construcao Civil':       { multiplo: 5,  label: 'Construção Civil',       faixa: '4-6×',  desc: 'Obras · incorporação · materiais' },
  'Bar/Restaurante':        { multiplo: 4,  label: 'Restaurante/Food',       faixa: '3-5×',  desc: 'Food service · cafeteria · marca regional' },
  'Agronegocio':            { multiplo: 5,  label: 'Agronegócio',            faixa: '4-7×',  desc: 'Produção rural · insumos · processamento' },
  'Educacao':               { multiplo: 6,  label: 'Educação',               faixa: '5-8×',  desc: 'Ensino · cursos · plataformas educacionais' },
  'Logistica/Transporte':   { multiplo: 5,  label: 'Logística/Transporte',   faixa: '4-6×',  desc: 'Frete · armazenagem · última milha' },
  'Industria/Manufatura':   { multiplo: 5,  label: 'Indústria/Manufatura',   faixa: '4-7×',  desc: 'Produção industrial · bens de capital e consumo' },
  'Servicos Profissionais': { multiplo: 6,  label: 'Serviços Profissionais', faixa: '5-8×',  desc: 'Consultoria · engenharia · serviços técnicos' },
  'Varejo Fisico':          { multiplo: 4,  label: 'Varejo Físico',          faixa: '3-5×',  desc: 'Lojas físicas · rede de pontos de venda' },
  'Imobiliario':            { multiplo: 10, label: 'Imobiliário',            faixa: '8-12×', desc: 'Locação · gestão e desenvolvimento de imóveis' },
  'Academia/Fitness':       { multiplo: 5,  label: 'Academia/Fitness',       faixa: '4-6×',  desc: 'Academias · estúdios · bem-estar' },
  'Beleza/Estetica':        { multiplo: 4,  label: 'Beleza/Estética',        faixa: '3-5×',  desc: 'Salões · clínicas de estética · franquias' },
  'geral':                  { multiplo: 6,  label: 'Geral',                  faixa: '5-8×',  desc: 'Faixa de referência quando o setor não está mapeado' },
};

const RASCUNHO_TAREFA_14 = {
  shopmax:     { wacc_id: 'b', g_id: 'b', metodo_id: 'hibrido',  multiplo: 7 },
  sabor_arte:  { wacc_id: 'a', g_id: 'b', metodo_id: 'multiplo', multiplo: 8 },
  clinicavida: { wacc_id: 'a', g_id: 'b', metodo_id: 'hibrido',  multiplo: 10 },
  construmax:  { wacc_id: 'b', g_id: 'a', metodo_id: 'gordon',   multiplo: 6 },
};

// Calcula valuation DCF a partir das premissas + anos da T13
const calcularValuation = (anosT13, waccId, gId, metodoId, multiplo) => {
  const wacc = WACC_INTERVALOS.find(o => o.id === waccId)?.meio || 0.20;
  const g    = G_PERPETUIDADE.find(o => o.id === gId)?.meio    || 0.03;
  const fcfAnos = (anosT13 || []).slice(1); // ignora Ano 0 ("Hoje")
  const fcfDescontado = fcfAnos.map((a, i) => {
    const periodo = i + 1;
    const fator = 1 / Math.pow(1 + wacc, periodo);
    return {
      ano: a.ano, periodo, fcf: a.fcf, fator, fcfDesc: a.fcf * fator,
    };
  });
  const fcfDescTotal = fcfDescontado.reduce((s, x) => s + x.fcfDesc, 0);
  const fcfAno5    = fcfAnos[4]?.fcf    || 0;
  const ebitdaAno5 = fcfAnos[4]?.ebitda || 0;
  const vtGordon   = wacc > g ? (fcfAno5 * (1 + g)) / (wacc - g) : 0;
  const vtMultiplo = ebitdaAno5 * (multiplo || 1);
  const vtHibrido  = (vtGordon + vtMultiplo) / 2;
  const vtPorMetodo = { gordon: vtGordon, multiplo: vtMultiplo, hibrido: vtHibrido };
  const vt = vtPorMetodo[metodoId] ?? vtHibrido;
  const fatorVT = 1 / Math.pow(1 + wacc, 5);
  const vtDescontado = vt * fatorVT;
  const ev = fcfDescTotal + vtDescontado;
  // EVs por método (pra comparativo)
  const evPorMetodo = {
    gordon:   fcfDescTotal + vtGordon * fatorVT,
    multiplo: fcfDescTotal + vtMultiplo * fatorVT,
    hibrido:  fcfDescTotal + vtHibrido * fatorVT,
  };
  return {
    fcfDescontado, fcfDescTotal,
    vt, vtDescontado, vtPorMetodo, evPorMetodo, ev,
    wacc, g, fatorVT,
    pctVT: ev > 0 ? vtDescontado / ev : 0,
    pctFCF: ev > 0 ? fcfDescTotal / ev : 0,
  };
};

// Sensibilidade WACC × g — matriz 4×4 de EV
const calcularSensibilidade = (anosT13, metodoId, multiplo) => {
  const matriz = [];
  WACC_INTERVALOS.forEach(w => {
    const linha = [];
    G_PERPETUIDADE.forEach(gp => {
      const r = calcularValuation(anosT13, w.id, gp.id, metodoId, multiplo);
      linha.push(r.ev);
    });
    matriz.push(linha);
  });
  return matriz;
};

const compilarTarefa14 = (valuationState, projecoesState, empresa) => {
  const rascT13 = RASCUNHO_TAREFA_13[empresa?.id] || {};
  const rascT14 = RASCUNHO_TAREFA_14[empresa?.id] || {
    wacc_id: 'b', g_id: 'b', metodo_id: 'hibrido', multiplo: 7,
  };
  const receita0    = projecoesState?.receita_inicial ?? rascT13.receita_inicial;
  const crescId     = projecoesState?.crescimento_id  ?? rascT13.crescimento_id;
  const cpvId       = projecoesState?.cpv_id          ?? rascT13.cpv_id;
  const despesasIds = projecoesState?.despesas        ?? rascT13.despesas;
  const capexId     = projecoesState?.capex_id        ?? rascT13.capex_id;
  const anos = calcularProjecao(receita0, crescId, cpvId, despesasIds, capexId);
  const waccId   = valuationState?.wacc_id   ?? rascT14.wacc_id;
  const gId      = valuationState?.g_id      ?? rascT14.g_id;
  const metodoId = valuationState?.metodo_id ?? rascT14.metodo_id;
  const multiplo = valuationState?.multiplo  ?? rascT14.multiplo;
  const v = calcularValuation(anos, waccId, gId, metodoId, multiplo);
  const wacc = WACC_INTERVALOS.find(o => o.id === waccId);
  const gp   = G_PERPETUIDADE.find(o => o.id === gId);
  const met  = METODOS_VT.find(o => o.id === metodoId);
  const fmtMi = (vv) => Math.abs(vv) >= 1000000 ? `R$ ${(vv/1000000).toFixed(2)} mi` : `R$ ${(vv/1000).toFixed(0)} mil`;
  const fmtPct = (vv) => `${(vv*100).toFixed(1)}%`;
  return `## 14. Valuation DCF

### Premissas de valuation

- **Taxa de retorno exigida (WACC):** ${wacc?.label} (${wacc?.faixa}) — _${wacc?.desc}_
- **Crescimento na perpetuidade (g):** ${gp?.label} (${gp?.faixa}) — _${gp?.desc}_
- **Método de Valor Terminal:** ${met?.label} — _${met?.desc}_
${metodoId !== 'gordon' ? `- **Múltiplo de saída (EBITDA):** ${multiplo}× — referência setorial Damodaran/ABVCAP` : ''}

### Composição do Enterprise Value

O Enterprise Value (EV) é a soma do FCF descontado dos próximos 5 anos com o Valor Terminal descontado. A barra abaixo ilustra o peso de cada componente.

<!--GRAFICO:t14_composicao-->

- **FCF descontado (Anos 1-5):** ${fmtMi(v.fcfDescTotal)} (${fmtPct(v.pctFCF)} do EV)
- **Valor Terminal descontado:** ${fmtMi(v.vtDescontado)} (${fmtPct(v.pctVT)} do EV)
- **Enterprise Value:** ${fmtMi(v.ev)}

### Cascata DCF

A cascata abaixo mostra o FCF nominal de cada ano e o efeito do desconto pela taxa exigida.

<!--GRAFICO:t14_cascata-->

| Período | FCF nominal | Fator desconto | FCF descontado |
|---------|-------------|----------------|----------------|
${v.fcfDescontado.map(x => `| ${x.ano} | ${fmtMi(x.fcf)} | ${x.fator.toFixed(4)} | ${fmtMi(x.fcfDesc)} |`).join('\n')}
| Valor Terminal | ${fmtMi(v.vt)} | ${v.fatorVT.toFixed(4)} | ${fmtMi(v.vtDescontado)} |
| **Enterprise Value** | | | **${fmtMi(v.ev)}** |

### Sensibilidade WACC × g

A matriz abaixo mostra como o EV muda conforme combinações de WACC e crescimento perpétuo.

<!--GRAFICO:t14_sensibilidade-->

### Comparativo de métodos de Valor Terminal

<!--GRAFICO:t14_comparativo_metodos-->

- **Gordon (perpetuidade):** EV ${fmtMi(v.evPorMetodo.gordon)}
- **Múltiplo de saída (${multiplo}×):** EV ${fmtMi(v.evPorMetodo.multiplo)}
- **Híbrido (média):** EV ${fmtMi(v.evPorMetodo.hibrido)}`;
};

// ═══════════════════════════════════════════════════════════════════════
// TAREFA 11 — Análise de Riscos
// 5 blocos: Mercado · Operacional · Financeiro · Regulatório · Plano de Mitigação
// Estrutura de cada risco: { id_catalogo, titulo, descricao, probabilidade, impacto, mitigacao }
// Probabilidade / Impacto = escala qualitativa Baixa/Média/Alta/Crítica
// ═══════════════════════════════════════════════════════════════════════

const ESCALA_PROB_IMPACTO = [
  { id: 'a', label: 'Baixa',    cor: G.verde },
  { id: 'b', label: 'Média',    cor: '#B45309' },
  { id: 'c', label: 'Alta',     cor: G.laranja },
  { id: 'd', label: 'Crítica',  cor: G.vermelho },
];

// Catálogo de riscos típicos por setor × categoria
// Categorias: 'mercado' · 'operacional' · 'financeiro' · 'regulatorio'
const RISCOS_CATALOGO_POR_SETOR = {
  'E-commerce': {
    mercado: [
      { id: 'concorrencia_preco',    titulo: 'Pressão de preço dos marketplaces',         descricao: 'Magalu/Amazon usam escala e crédito próprio para comprimir margens',     mitigacao: 'Linha private label com margem superior · canal D2C que captura margem direta' },
      { id: 'mudanca_consumo',       titulo: 'Mudança no hábito do consumidor',           descricao: 'Migração entre categorias e preferência por preço afeta mix',           mitigacao: 'Diversificação de linhas · monitoramento de tendências por trimestre' },
      { id: 'sazonalidade_intensa',  titulo: 'Sazonalidade intensa',                      descricao: 'Receita concentrada em datas comerciais comprime caixa em outros meses',mitigacao: 'Linha "always-on" + assinatura/recorrência para amortecer vales' },
    ],
    operacional: [
      { id: 'dep_logistica',         titulo: 'Dependência de poucos transportadores',     descricao: 'Concentração de operadores logísticos eleva risco de SLA',              mitigacao: 'Múltiplos parceiros logísticos · CD próprio reduz dependência' },
      { id: 'cibersegur',            titulo: 'Cibersegurança da plataforma',              descricao: 'Vazamento de dados ou ataque DDoS pode paralisar a operação',           mitigacao: 'Proteção DDoS · pentests semestrais · LGPD compliance · seguro cyber' },
      { id: 'dep_fornec_pl',         titulo: 'Dependência de fornecedores private label', descricao: 'Concentração na produção da linha exclusiva limita escala',             mitigacao: 'Diversificar 2-3 fabricantes parceiros · contratos com cláusulas de capacidade' },
    ],
    financeiro: [
      { id: 'cap_giro_d2c',          titulo: 'Capital de giro para escalar D2C',          descricao: 'Estoque + marketing pré-receita pressiona caixa em fase de expansão',   mitigacao: 'Linha de crédito pré-aprovada · payback de campanhas em <90 dias' },
      { id: 'reconciliacao_adq',     titulo: 'Reconciliação de adquirentes',              descricao: 'Recebíveis com gateway/adquirente expõem a risco de antecipação',       mitigacao: 'Múltiplos adquirentes · monitoramento diário de recebíveis a vencer' },
      { id: 'inadimpl_parcelado',    titulo: 'Inadimplência em parcelado',                descricao: 'Parcelamento sem juros em recessão eleva risco de não-pagamento',       mitigacao: 'Limite de parcelas em períodos críticos · análise de crédito por ticket' },
    ],
    regulatorio: [
      { id: 'lgpd_dados',            titulo: 'Conformidade LGPD',                         descricao: 'Multa ANPD pode atingir 2% do faturamento por incidente',               mitigacao: 'DPO designado · política de privacidade revisada · pentests anuais' },
      { id: 'icms_ecommerce',        titulo: 'ICMS interestadual em e-commerce',          descricao: 'Mudanças de DIFAL impactam preço final regional',                       mitigacao: 'Monitoramento de pauta tributária · simulações trimestrais de preço' },
      { id: 'cdc_devolucao',         titulo: 'CDC · prazo de devolução',                  descricao: '7 dias para devolução pode ser estendido sem custo recuperável',        mitigacao: 'Inspeção rigorosa em retorno · contrato de seguro contra avaria' },
    ],
  },
  'Bar/Restaurante': {
    mercado: [
      { id: 'concorrencia_nova',     titulo: 'Concorrência nova na região',               descricao: 'Casas novas abrem todo mês e disputam o mesmo público gourmet',         mitigacao: 'Diferenciação via experiência única · programa de fidelidade premium' },
      { id: 'mudanca_habito',        titulo: 'Mudança de hábito (delivery vs salão)',     descricao: 'Cliente troca o salão por delivery em alguns segmentos',                mitigacao: 'Linha de delivery premium consistente com a marca' },
      { id: 'turismo_gastr',         titulo: 'Oscilação no turismo gastronômico',         descricao: 'Variação no fluxo turístico afeta receita em datas-chave',              mitigacao: 'Diversificar canais B2C local + corporativo · não depender só de turismo' },
    ],
    operacional: [
      { id: 'pessoa_chave_chef',     titulo: 'Risco de pessoa-chave (chef)',              descricao: 'Marca muito ligada à figura do chef cria fragilidade na sucessão',      mitigacao: 'Equipe sênior treinada · sous-chef com autoridade · processo documentado' },
      { id: 'rotatividade_salao',    titulo: 'Rotatividade no salão',                     descricao: 'Alta rotatividade afeta padrão de atendimento e treinamento',           mitigacao: 'Plano de carreira em salão · gorjetas compartilhadas · bônus de antiguidade' },
      { id: 'forn_sazonal',          titulo: 'Sazonalidade de fornecedores locais',       descricao: 'Ingredientes locais sazonais geram falta em determinadas épocas',       mitigacao: 'Banco de fornecedores reserva · cardápio flexível por sazonalidade' },
    ],
    financeiro: [
      { id: 'aluguel_renovacao',     titulo: 'Reajuste do aluguel na renovação',          descricao: 'Ponto premium pode sofrer reajuste significativo na renovação',         mitigacao: 'Antecipação de negociação · cláusula de reajuste IPCA · plano B de ponto' },
      { id: 'inflacao_insumos',      titulo: 'Inflação de ingredientes',                  descricao: 'Itens importados (vinhos, carnes premium) sobem com câmbio',            mitigacao: 'Repasse parcial via menu · negociação por safra · hedge cambial' },
      { id: 'fluxo_caixa_eventos',   titulo: 'Fluxo de caixa de eventos',                 descricao: 'Eventos privados têm alto ticket mas pagamento concentrado',            mitigacao: 'Sinal contratual de 30-50% · prazo curto de fatura · adiantamento de produção' },
    ],
    regulatorio: [
      { id: 'vigilancia_sanitaria',  titulo: 'Vigilância sanitária',                      descricao: 'Inspeção pode gerar interdição temporária ou multas',                   mitigacao: 'Auditoria preventiva semestral · responsável técnico de cozinha · POPs vigentes' },
      { id: 'alvara_funcionamento',  titulo: 'Alvarás de funcionamento',                  descricao: 'Renovações de alvará podem atrasar e gerar multas',                     mitigacao: 'Contratos com despachante · calendário de renovação no ERP' },
      { id: 'leis_trabalhistas',     titulo: 'Encargos trabalhistas',                     descricao: 'Mudanças em horas extras e gorjetas impactam folha',                    mitigacao: 'Folha sob assessoria jurídica especializada · banco de horas formalizado' },
    ],
  },
  'Saúde': {
    mercado: [
      { id: 'concorrencia_rede',     titulo: 'Concorrência de redes verticalizadas',      descricao: 'Operadoras com rede própria pressionam clínicas independentes',         mitigacao: 'Convênio com operadoras alternativas · canal particular + telemedicina' },
      { id: 'dep_convenios',         titulo: 'Dependência de poucos convênios',           descricao: 'Concentração em 2-3 operadoras eleva risco de descredenciamento',       mitigacao: 'Diversificar convênios · ampliar canal particular para 35%+' },
      { id: 'mudanca_demografica',   titulo: 'Mudança demográfica do público',            descricao: 'Migração de moradores ou perfil etário do bairro pode afetar demanda',  mitigacao: 'Ampliar especialidades (geriatria) · monitorar perfil epidemiológico local' },
    ],
    operacional: [
      { id: 'rotatividade_medica',   titulo: 'Rotatividade de médicos especialistas',     descricao: 'Saída de médico-chave reduz capacidade da especialidade',               mitigacao: 'Contratos com cláusula de não-concorrência · plano de carreira' },
      { id: 'cibersegur_prontuario', titulo: 'Cibersegurança do prontuário',              descricao: 'Vazamento de dado clínico tem multa LGPD agravada',                     mitigacao: 'Backup criptografado · controle de acesso por perfil · auditoria semestral' },
      { id: 'panes_telemed',         titulo: 'Panes na telemedicina',                     descricao: 'Indisponibilidade da plataforma afeta a operação online',               mitigacao: 'Plataforma com SLA contratual · plano B presencial · seguro de operação' },
    ],
    financeiro: [
      { id: 'glosa_convenios',       titulo: 'Glosa de convênios',                        descricao: 'Operadora pode recusar pagamento por divergência de procedimento',      mitigacao: 'Gerente de convênios dedicado · auditoria pré-faturamento · indicadores de glosa' },
      { id: 'prazo_pagamento_conv',  titulo: 'Prazo de pagamento de convênios',           descricao: 'Convênio paga em 60-90 dias · pressiona caixa',                         mitigacao: 'Linha de antecipação de recebíveis · % particular para equilibrar fluxo' },
      { id: 'inadimpl_part',         titulo: 'Inadimplência em particulares',             descricao: 'Pacotes preventivos pagos em parcelas têm risco de não-pagamento',      mitigacao: 'Cobrança no início · cartão recorrente · análise de crédito por pacote' },
    ],
    regulatorio: [
      { id: 'anvisa',                titulo: 'Conformidade ANVISA',                       descricao: 'Inspeção pode gerar interdição ou multas em procedimentos',             mitigacao: 'Responsável técnico · POPs atualizados · auditoria preventiva' },
      { id: 'lgpd_saude',            titulo: 'LGPD em dados de saúde',                    descricao: 'Dados sensíveis têm proteção agravada · multa pesada',                   mitigacao: 'DPO designado · termo de consentimento · pentests semestrais' },
      { id: 'crm_responsavel',       titulo: 'Conselho profissional (CRM/COREN)',         descricao: 'Quebra ética ou irregularidade afeta operação',                         mitigacao: 'Comissão de ética interna · treinamento periódico · seguro de responsabilidade' },
    ],
  },
  'Construção Civil': {
    mercado: [
      { id: 'queda_corp',            titulo: 'Queda no investimento corporativo',         descricao: 'Recessão reduz capex corporativo e atrasa novas obras',                 mitigacao: 'Diversificar B2B + reformas residenciais + loja de materiais' },
      { id: 'concorrencia_grande',   titulo: 'Concorrência de grandes integradoras',      descricao: 'Construtoras maiores podem entrar em obras médias',                     mitigacao: 'Especialização em nicho · prazo curto · relacionamento direto com cliente' },
      { id: 'inadimpl_cliente_corp', titulo: 'Inadimplência em cliente corporativo',      descricao: 'Cliente corporativo pode atrasar pagamento ou cancelar contrato',       mitigacao: 'Garantia contratual · medição mensal · diversificar carteira' },
    ],
    operacional: [
      { id: 'seguranca_canteiro',    titulo: 'Acidentes no canteiro',                     descricao: 'Acidente grave pode gerar interdição e responsabilização criminal',     mitigacao: 'Programa SST robusto · técnicos dedicados · auditoria mensal · seguro de obra' },
      { id: 'qualidade_terceiros',   titulo: 'Qualidade de empreiteiros terceirizados',   descricao: 'Empreiteiros sem padrão podem gerar retrabalho e atraso',               mitigacao: 'Lista de empreiteiros homologados · gestão de pagamento por marco · auditoria de obra' },
      { id: 'forn_materiais',        titulo: 'Concentração em poucos fornecedores',       descricao: 'Falta de cimento/aço pode parar a obra',                                mitigacao: 'Loja própria como reserva · 2-3 fornecedores por insumo crítico · estoque mínimo' },
    ],
    financeiro: [
      { id: 'cap_giro_obra',         titulo: 'Capital de giro entre medições',            descricao: 'Custo da obra antecede a medição/recebimento',                          mitigacao: 'Linha de financiamento de obra · sinal contratual · cronograma de medição mensal' },
      { id: 'inflacao_insumos_c',    titulo: 'Inflação de insumos da construção',         descricao: 'Cimento, aço e mão de obra sobem acima do reajuste contratual',         mitigacao: 'Cláusula de reajuste por INCC · compras antecipadas · contratos com hedge' },
      { id: 'multas_atraso',         titulo: 'Multas contratuais por atraso',             descricao: 'Atraso em prazo gera multa de até 10% do contrato',                     mitigacao: 'Cronograma com folga · monitoramento diário · seguro de garantia de execução' },
    ],
    regulatorio: [
      { id: 'crea_responsabilidade', titulo: 'Responsabilidade técnica CREA',             descricao: 'Engenheiro responsável tem responsabilidade objetiva pela obra',         mitigacao: 'Seguro de responsabilidade civil · ART em todas as obras · revisão por engenheiro sênior' },
      { id: 'licencas_ambientais',   titulo: 'Licenças ambientais',                       descricao: 'Atraso de licença pode parar a obra ou gerar multa',                    mitigacao: 'Despachante ambiental · cronograma de licenças · contingência em prazo' },
      { id: 'leis_trabalhistas_c',   titulo: 'Encargos trabalhistas em canteiro',         descricao: 'Mudanças em horas extras e segurança impactam folha de obra',           mitigacao: 'Folha sob assessoria especializada · auditoria trimestral · banco de horas' },
    ],
  },
  'geral': {
    mercado: [
      { id: 'concorrencia_geral',    titulo: 'Concorrência crescente',                    descricao: 'Novos players podem disputar o mesmo público',                          mitigacao: 'Diferenciação clara · marca forte · monitoramento da concorrência' },
      { id: 'mudanca_consumidor_g',  titulo: 'Mudança no perfil do consumidor',           descricao: 'Gerações novas têm novos hábitos de consumo',                           mitigacao: 'Pesquisa de público periódica · adaptação contínua de produto' },
      { id: 'ciclo_economico',       titulo: 'Ciclo econômico desfavorável',              descricao: 'Recessão afeta consumo e ticket médio',                                 mitigacao: 'Linha de produto com elasticidade · controle de custos fixos · reservas de caixa' },
    ],
    operacional: [
      { id: 'pessoa_chave_g',        titulo: 'Risco de pessoa-chave',                     descricao: 'Saída de líder operacional ou comercial afeta a continuidade',          mitigacao: 'Plano de sucessão · processos documentados · contrato com cláusulas de retenção' },
      { id: 'tecnologia_obs',        titulo: 'Obsolescência tecnológica',                 descricao: 'Sistemas críticos podem se tornar obsoletos ou inseguros',              mitigacao: 'Roadmap de tecnologia · auditoria anual · plano de migração' },
      { id: 'forn_concentrado',      titulo: 'Concentração de fornecedores',              descricao: 'Dependência de poucos fornecedores eleva risco de continuidade',         mitigacao: 'Diversificar fornecedores · contratos com cláusula de continuidade' },
    ],
    financeiro: [
      { id: 'cap_giro_g',            titulo: 'Capital de giro',                           descricao: 'Crescimento acelerado pode pressionar o caixa',                         mitigacao: 'Linha de crédito pré-aprovada · monitoramento de ciclo de caixa · CCC < 60 dias' },
      { id: 'inadimpl_g',            titulo: 'Inadimplência de clientes',                 descricao: 'Recessão ou mudança macro eleva inadimplência',                         mitigacao: 'Política de crédito · análise por cliente · seguro de crédito' },
      { id: 'cambial',               titulo: 'Exposição cambial',                         descricao: 'Insumos ou receitas em dólar geram volatilidade',                       mitigacao: 'Hedge cambial · contratos em moeda local · diversificação de origem de insumos' },
    ],
    regulatorio: [
      { id: 'tributario_g',          titulo: 'Mudanças tributárias',                      descricao: 'Reforma tributária pode alterar carga e regime',                        mitigacao: 'Assessoria tributária · simulação de cenários · monitoramento da pauta' },
      { id: 'lgpd_g',                titulo: 'Conformidade LGPD',                         descricao: 'Multa pesada por incidente de dados',                                   mitigacao: 'DPO · auditoria · política de privacidade · pentests' },
      { id: 'trabalhista_g',         titulo: 'Encargos trabalhistas',                     descricao: 'Mudanças em CLT impactam folha e custo operacional',                   mitigacao: 'Folha sob assessoria · banco de horas · monitoramento da pauta legislativa' },
    ],
  },
};

// Catálogo de riscos SU · lista única (riscos de startup são de maturidade, não
// de setor · fallback 'geral' cobre todos). Selecionado quando modo === 'SU'.
const RISCOS_CATALOGO_POR_SETOR_SU = {
  'geral': {
    mercado: [
      { id: 'concorrente_copia',      titulo: 'Concorrente estabelecido reage e copia',     descricao: 'Incumbente com marca e caixa replica a solução',              mitigacao: 'Velocidade de iteração · moat de dados/comunidade · dominar o nicho antes de escalar' },
      { id: 'tam_superestimado',      titulo: 'Mercado-alvo menor do que o estimado',       descricao: 'TAM superestimado reduz o teto de receita',                   mitigacao: 'Validar bottom-up com dados reais · abrir mercados adjacentes' },
      { id: 'sem_pmf',                titulo: 'Encaixe produto-mercado não confirmado',     descricao: 'Product-market fit ainda não comprovado',                     mitigacao: 'Ciclos curtos de teste · métricas de retenção antes de investir em escala' },
      { id: 'dep_poucos_clientes',    titulo: 'Dependência de poucos clientes/usuários',    descricao: 'Concentração da receita em poucas contas iniciais',           mitigacao: 'Diversificar a base · manter concentração abaixo de 20% por cliente' },
      { id: 'mudanca_comportamento',  titulo: 'Mudança de comportamento antes da escala',   descricao: 'O hábito do consumidor muda antes da massa crítica',          mitigacao: 'Monitorar sinais de uso · pivotar rápido com base em dados' },
    ],
    operacional: [
      { id: 'saida_fundador',         titulo: 'Saída de fundador ou pessoa-chave',          descricao: 'Perda de fundador ou primeiro engenheiro/vendedor',           mitigacao: 'Vesting com cliff · documentação · participação societária para reter' },
      { id: 'provedor_unico',         titulo: 'Dependência de provedor único',              descricao: 'Nuvem, pagamento ou identidade concentrados num fornecedor',  mitigacao: 'Arquitetura portável · contrato com SLA · plano de contingência' },
      { id: 'falha_dados',            titulo: 'Falha técnica ou perda de dados',            descricao: 'Indisponibilidade ou perda de dados paralisa o produto',      mitigacao: 'Backups · observabilidade · testes de recuperação' },
      { id: 'cultura_diluir',         titulo: 'Cultura se perder ao crescer o time',        descricao: 'A cultura se dilui nas primeiras contratações',               mitigacao: 'Livro de cultura · onboarding forte · contratar por valores' },
      { id: 'velocidade_desacelera',  titulo: 'Velocidade de entrega desacelera',           descricao: 'O ritmo cai após as primeiras contratações',                  mitigacao: 'Processos leves · squads autônomos · métricas de entrega' },
    ],
    financeiro: [
      { id: 'esgotamento_caixa',      titulo: 'Esgotamento de caixa antes da rodada',       descricao: 'O runway acaba antes da próxima captação',                    mitigacao: 'Controle de burn · gatilhos de captação com 6 meses de folga' },
      { id: 'down_round',             titulo: 'Rodada seguinte com valuation reduzido',     descricao: 'Down round dilui founders e desmotiva o time',                mitigacao: 'Metas de tração claras · captar em janela favorável' },
      { id: 'captacao_atrasa',        titulo: 'Captação se prolongar e atrasar o plano',    descricao: 'O processo de captação demora e trava o roadmap',             mitigacao: 'Pipeline de investidores antecipado · plano B de caixa próprio' },
      { id: 'cambial',                titulo: 'Exposição cambial',                          descricao: 'Faturamento ou custos em moeda estrangeira geram volatilidade', mitigacao: 'Hedge · contratos em moeda local quando possível' },
      { id: 'cac_ticket',             titulo: 'CAC subir mais rápido que o ticket',         descricao: 'O custo de aquisição cresce acima do ticket médio',           mitigacao: 'Monitorar LTV/CAC · canais de baixo custo (PLG · comunidade)' },
    ],
    regulatorio: [
      { id: 'lgpd_usuarios',          titulo: 'LGPD sobre a base de usuários',              descricao: 'Tratamento de dados pessoais dos usuários',                   mitigacao: 'DPO · política de privacidade · consentimento · segurança' },
      { id: 'reg_ia',                 titulo: 'Regulamentação emergente de IA',             descricao: 'Novas regras de IA podem afetar o produto',                   mitigacao: 'Acompanhar a pauta · IA explicável · governança de modelos' },
      { id: 'reg_setor',              titulo: 'Mudança regulatória do setor',               descricao: 'Setores regulados como saúde, finanças e educação mudam as regras',              mitigacao: 'Assessoria setorial · monitoramento regulatório contínuo' },
      { id: 'tributario_simples',     titulo: 'Tributário · saída do Simples ao crescer',   descricao: 'O crescimento força mudança de regime tributário',            mitigacao: 'Planejamento tributário · simulação de cenários por faixa' },
      { id: 'trabalhista_pj',         titulo: 'Trabalhista · CLT × PJ com prestadores',     descricao: 'Vínculos com prestadores em modelo PJ geram risco',           mitigacao: 'Contratos bem estruturados · assessoria trabalhista' },
    ],
  },
};

const RASCUNHO_TAREFA_11_SU = {
  shopmax: {
    riscos: [
      { id_catalogo: 'sem_pmf',             categoria: 'mercado',     probabilidade: 'c', impacto: 'c' },
      { id_catalogo: 'dep_poucos_clientes', categoria: 'mercado',     probabilidade: 'b', impacto: 'b' },
      { id_catalogo: 'provedor_unico',      categoria: 'operacional', probabilidade: 'b', impacto: 'c' },
      { id_catalogo: 'saida_fundador',      categoria: 'operacional', probabilidade: 'b', impacto: 'd' },
      { id_catalogo: 'esgotamento_caixa',   categoria: 'financeiro',  probabilidade: 'c', impacto: 'd' },
      { id_catalogo: 'cac_ticket',          categoria: 'financeiro',  probabilidade: 'c', impacto: 'c' },
      { id_catalogo: 'lgpd_usuarios',       categoria: 'regulatorio', probabilidade: 'b', impacto: 'c' },
      { id_catalogo: 'tributario_simples',  categoria: 'regulatorio', probabilidade: 'b', impacto: 'b' },
    ],
    mitigacao_extra: '',
  },
  sabor_arte: {
    riscos: [
      { id_catalogo: 'concorrente_copia',     categoria: 'mercado',     probabilidade: 'c', impacto: 'b' },
      { id_catalogo: 'mudanca_comportamento', categoria: 'mercado',     probabilidade: 'b', impacto: 'b' },
      { id_catalogo: 'cultura_diluir',        categoria: 'operacional', probabilidade: 'b', impacto: 'c' },
      { id_catalogo: 'saida_fundador',        categoria: 'operacional', probabilidade: 'b', impacto: 'd' },
      { id_catalogo: 'esgotamento_caixa',     categoria: 'financeiro',  probabilidade: 'c', impacto: 'd' },
      { id_catalogo: 'captacao_atrasa',       categoria: 'financeiro',  probabilidade: 'b', impacto: 'c' },
      { id_catalogo: 'lgpd_usuarios',         categoria: 'regulatorio', probabilidade: 'b', impacto: 'b' },
      { id_catalogo: 'trabalhista_pj',        categoria: 'regulatorio', probabilidade: 'b', impacto: 'c' },
    ],
    mitigacao_extra: '',
  },
  clinicavida: {
    riscos: [
      { id_catalogo: 'sem_pmf',             categoria: 'mercado',     probabilidade: 'b', impacto: 'c' },
      { id_catalogo: 'dep_poucos_clientes', categoria: 'mercado',     probabilidade: 'b', impacto: 'c' },
      { id_catalogo: 'falha_dados',         categoria: 'operacional', probabilidade: 'b', impacto: 'd' },
      { id_catalogo: 'provedor_unico',      categoria: 'operacional', probabilidade: 'b', impacto: 'c' },
      { id_catalogo: 'esgotamento_caixa',   categoria: 'financeiro',  probabilidade: 'c', impacto: 'd' },
      { id_catalogo: 'cac_ticket',          categoria: 'financeiro',  probabilidade: 'b', impacto: 'c' },
      { id_catalogo: 'lgpd_usuarios',       categoria: 'regulatorio', probabilidade: 'c', impacto: 'd' },
      { id_catalogo: 'reg_setor',           categoria: 'regulatorio', probabilidade: 'b', impacto: 'c' },
    ],
    mitigacao_extra: '',
  },
  construmax: {
    riscos: [
      { id_catalogo: 'tam_superestimado',    categoria: 'mercado',     probabilidade: 'b', impacto: 'c' },
      { id_catalogo: 'concorrente_copia',    categoria: 'mercado',     probabilidade: 'b', impacto: 'b' },
      { id_catalogo: 'velocidade_desacelera', categoria: 'operacional', probabilidade: 'b', impacto: 'b' },
      { id_catalogo: 'saida_fundador',       categoria: 'operacional', probabilidade: 'b', impacto: 'c' },
      { id_catalogo: 'esgotamento_caixa',    categoria: 'financeiro',  probabilidade: 'c', impacto: 'c' },
      { id_catalogo: 'down_round',           categoria: 'financeiro',  probabilidade: 'b', impacto: 'c' },
      { id_catalogo: 'tributario_simples',   categoria: 'regulatorio', probabilidade: 'b', impacto: 'b' },
      { id_catalogo: 'reg_setor',            categoria: 'regulatorio', probabilidade: 'b', impacto: 'c' },
    ],
    mitigacao_extra: '',
  },
};

const CATEGORIAS_RISCO = [
  { id: 'mercado',     label: 'Mercado',                cor: G.verde },
  { id: 'operacional', label: 'Operacional',            cor: G.laranja },
  { id: 'financeiro',  label: 'Financeiro',             cor: '#B45309' },
  { id: 'regulatorio', label: 'Regulatório/Compliance', cor: '#7C3AED' },
];

const RASCUNHO_TAREFA_11 = {
  shopmax: {
    riscos: [
      { id_catalogo: 'concorrencia_preco',   categoria: 'mercado',     probabilidade: 'c', impacto: 'c' },
      { id_catalogo: 'mudanca_consumo',      categoria: 'mercado',     probabilidade: 'b', impacto: 'b' },
      { id_catalogo: 'dep_logistica',        categoria: 'operacional', probabilidade: 'b', impacto: 'c' },
      { id_catalogo: 'cibersegur',           categoria: 'operacional', probabilidade: 'b', impacto: 'd' },
      { id_catalogo: 'cap_giro_d2c',         categoria: 'financeiro',  probabilidade: 'c', impacto: 'c' },
      { id_catalogo: 'reconciliacao_adq',    categoria: 'financeiro',  probabilidade: 'b', impacto: 'b' },
      { id_catalogo: 'lgpd_dados',           categoria: 'regulatorio', probabilidade: 'b', impacto: 'd' },
      { id_catalogo: 'icms_ecommerce',       categoria: 'regulatorio', probabilidade: 'b', impacto: 'b' },
    ],
    mitigacao_extra: '',
  },
  sabor_arte: {
    riscos: [
      { id_catalogo: 'concorrencia_nova',     categoria: 'mercado',     probabilidade: 'c', impacto: 'b' },
      { id_catalogo: 'turismo_gastr',         categoria: 'mercado',     probabilidade: 'b', impacto: 'b' },
      { id_catalogo: 'pessoa_chave_chef',     categoria: 'operacional', probabilidade: 'b', impacto: 'd' },
      { id_catalogo: 'rotatividade_salao',    categoria: 'operacional', probabilidade: 'c', impacto: 'b' },
      { id_catalogo: 'aluguel_renovacao',     categoria: 'financeiro',  probabilidade: 'b', impacto: 'c' },
      { id_catalogo: 'inflacao_insumos',      categoria: 'financeiro',  probabilidade: 'c', impacto: 'b' },
      { id_catalogo: 'vigilancia_sanitaria',  categoria: 'regulatorio', probabilidade: 'b', impacto: 'c' },
    ],
    mitigacao_extra: '',
  },
  clinicavida: {
    riscos: [
      { id_catalogo: 'dep_convenios',        categoria: 'mercado',     probabilidade: 'c', impacto: 'd' },
      { id_catalogo: 'concorrencia_rede',    categoria: 'mercado',     probabilidade: 'b', impacto: 'c' },
      { id_catalogo: 'rotatividade_medica',  categoria: 'operacional', probabilidade: 'b', impacto: 'c' },
      { id_catalogo: 'cibersegur_prontuario',categoria: 'operacional', probabilidade: 'b', impacto: 'd' },
      { id_catalogo: 'glosa_convenios',      categoria: 'financeiro',  probabilidade: 'c', impacto: 'b' },
      { id_catalogo: 'prazo_pagamento_conv', categoria: 'financeiro',  probabilidade: 'c', impacto: 'c' },
      { id_catalogo: 'lgpd_saude',           categoria: 'regulatorio', probabilidade: 'b', impacto: 'd' },
      { id_catalogo: 'anvisa',               categoria: 'regulatorio', probabilidade: 'b', impacto: 'c' },
    ],
    mitigacao_extra: '',
  },
  construmax: {
    riscos: [
      { id_catalogo: 'queda_corp',           categoria: 'mercado',     probabilidade: 'b', impacto: 'd' },
      { id_catalogo: 'inadimpl_cliente_corp',categoria: 'mercado',     probabilidade: 'b', impacto: 'c' },
      { id_catalogo: 'seguranca_canteiro',   categoria: 'operacional', probabilidade: 'b', impacto: 'd' },
      { id_catalogo: 'qualidade_terceiros',  categoria: 'operacional', probabilidade: 'c', impacto: 'b' },
      { id_catalogo: 'cap_giro_obra',        categoria: 'financeiro',  probabilidade: 'c', impacto: 'c' },
      { id_catalogo: 'multas_atraso',        categoria: 'financeiro',  probabilidade: 'b', impacto: 'c' },
      { id_catalogo: 'crea_responsabilidade',categoria: 'regulatorio', probabilidade: 'b', impacto: 'd' },
      { id_catalogo: 'licencas_ambientais',  categoria: 'regulatorio', probabilidade: 'b', impacto: 'c' },
    ],
    mitigacao_extra: '',
  },
};

const compilarTarefa11 = (riscosState, empresa) => {
  const rascunho = RASCUNHO_TAREFA_11[empresa?.id] || { riscos: [], mitigacao_extra: '' };
  const riscos = riscosState?.riscos ?? rascunho.riscos;
  const mitigacaoExtra = riscosState?.mitigacao_extra ?? rascunho.mitigacao_extra ?? '';
  const setor = empresa?.setor || 'geral';
  const catalogoRiscos = RISCOS_CATALOGO_POR_SETOR[setor] || RISCOS_CATALOGO_POR_SETOR['geral'];
  const buscar = (catId, categoria) => (catalogoRiscos[categoria] || []).find(c => c.id === catId);
  const linhasPorCategoria = (cat) => {
    const riscosCat = riscos.filter(r => r.categoria === cat);
    if (riscosCat.length === 0) return '_Nenhum risco mapeado nesta categoria._';
    return riscosCat.map(r => {
      const cat = buscar(r.id_catalogo, r.categoria);
      const prob = ESCALA_PROB_IMPACTO.find(o => o.id === r.probabilidade)?.label || '—';
      const imp  = ESCALA_PROB_IMPACTO.find(o => o.id === r.impacto)?.label || '—';
      const titulo = cat?.titulo || r.titulo || '—';
      const desc = cat?.descricao || r.descricao || '';
      const mit = cat?.mitigacao || r.mitigacao || '';
      return `- **${titulo}** · prob. **${prob}** · impacto **${imp}**\n  - _${desc}_\n  - **Mitigação:** ${mit}`;
    }).join('\n');
  };
  return `## 11. Análise de Riscos

### Riscos de Mercado

${linhasPorCategoria('mercado')}

### Riscos Operacionais

${linhasPorCategoria('operacional')}

### Riscos Financeiros

${linhasPorCategoria('financeiro')}

### Riscos Regulatórios / Compliance

${linhasPorCategoria('regulatorio')}

### Plano de Mitigação Consolidado

${riscos.filter(r => r.probabilidade === 'c' || r.probabilidade === 'd' || r.impacto === 'c' || r.impacto === 'd')
  .map(r => {
    const cat = buscar(r.id_catalogo, r.categoria);
    return `- **${cat?.titulo || r.titulo}** — ${cat?.mitigacao || r.mitigacao || ''}`;
  }).join('\n') || '_Riscos críticos consolidados aparecem aqui quando classificados como Alta/Crítica._'}

${mitigacaoExtra ? `\n**Observações adicionais:**\n\n${mitigacaoExtra}` : ''}`;
};

// ═══════════════════════════════════════════════════════════════════════
// TAREFA 12 — ESG & Sustentabilidade
// 4 blocos: Ambiental (E) · Social (S) · Governança (G) · Compromissos Públicos
// Estrutura: { id_catalogo, dimensao, titulo, descricao, acao, maturidade }
// Maturidade = escala qualitativa Inicial/Em desenvolvimento/Consolidado/Líder
// ═══════════════════════════════════════════════════════════════════════

const MATURIDADE_ESG = [
  { id: 'a', label: 'Inicial',           cor: G.vermelho },
  { id: 'b', label: 'Em desenvolvimento',cor: '#B45309'   },
  { id: 'c', label: 'Consolidado',       cor: G.laranja   },
  { id: 'd', label: 'Líder',             cor: G.verde     },
];

const ESG_CATALOGO_POR_SETOR = {
  'E-commerce': {
    ambiental: [
      { id: 'embalagens_eco',    titulo: 'Embalagens recicláveis/recicladas',     descricao: 'Uso de materiais com origem reciclada e destinação correta',         acao: 'Adotar fornecedores de embalagens com selo + programa de retorno de caixas' },
      { id: 'logistica_carbono', titulo: 'Logística de baixo carbono',            descricao: 'Frota com combustível alternativo, rotas otimizadas',                acao: 'Parcerias com transportadoras carbono-neutro · roteirização inteligente' },
      { id: 'cd_eficiencia',     titulo: 'Eficiência energética em CD',           descricao: 'Iluminação LED, sensores de presença, energia renovável',            acao: 'Auditoria energética + migração para energia solar/limpa' },
      { id: 'logistica_reversa', titulo: 'Logística reversa',                     descricao: 'Coleta e reciclagem de embalagens e produtos pós-uso',                acao: 'Programa de devolução incentivado · parcerias com cooperativas de reciclagem' },
    ],
    social: [
      { id: 'diversidade_time',  titulo: 'Diversidade no time',                   descricao: 'Representatividade de gênero, raça e idade na equipe',                acao: 'Metas afirmativas + comitê de diversidade · processo de seleção inclusivo' },
      { id: 'fornec_eticos',     titulo: 'Fornecedores éticos',                   descricao: 'Verificação de condições de trabalho na cadeia',                     acao: 'Código de conduta de fornecedores + auditoria periódica' },
      { id: 'protecao_dados',    titulo: 'Proteção de dados do cliente',          descricao: 'LGPD, segurança de informações pessoais',                            acao: 'DPO designado + auditoria semestral + transparência ativa' },
      { id: 'atendim_acessivel', titulo: 'Atendimento acessível',                 descricao: 'Suporte humano, acessibilidade web (WCAG)',                          acao: 'Auditoria de acessibilidade · suporte por múltiplos canais' },
    ],
    governanca: [
      { id: 'codigo_etica_e',    titulo: 'Código de ética e conduta',             descricao: 'Documento formal de princípios e canal de denúncias',                acao: 'Aprovação no conselho + canal externo independente · treinamento anual' },
      { id: 'compliance_e',      titulo: 'Compliance e prevenção à corrupção',    descricao: 'Programa de prevenção a fraudes e suborno',                          acao: 'Política antifraude + due diligence de parceiros · indicadores de compliance' },
      { id: 'conselho_e',        titulo: 'Conselho consultivo/administrativo',    descricao: 'Órgão de governança independente',                                   acao: 'Formar conselho com pelo menos 3 membros externos · cadência trimestral' },
      { id: 'transparencia_e',   titulo: 'Transparência fiscal',                  descricao: 'Demonstrações claras e auditoria',                                   acao: 'Auditoria externa anual · relatório consolidado público' },
    ],
  },
  'Bar/Restaurante': {
    ambiental: [
      { id: 'desperdicio_alim',  titulo: 'Combate ao desperdício alimentar',      descricao: 'Aproveitamento integral de ingredientes',                            acao: 'Programa de aproveitamento total + parceria com bancos de alimentos' },
      { id: 'ingredientes_loc',  titulo: 'Ingredientes locais',                   descricao: 'Curto raio de abastecimento, sazonalidade',                          acao: 'Cardápio sazonal com produtores num raio de 200 km' },
      { id: 'compostagem',       titulo: 'Compostagem de orgânicos',              descricao: 'Resíduos orgânicos transformados em adubo',                          acao: 'Implementar compostagem + parceria com cooperativa rural' },
      { id: 'descart_oleo',      titulo: 'Descarte correto de óleo',              descricao: 'Coleta e destinação para biodiesel',                                 acao: 'Empresa especializada em coleta + indicadores mensais' },
    ],
    social: [
      { id: 'inclusao_cozinha',  titulo: 'Inclusão na cozinha',                   descricao: 'Treinamento de jovens em situação de vulnerabilidade',               acao: 'Parceria com ONG · 2-3 vagas de aprendiz por ano' },
      { id: 'comunidade_loc',    titulo: 'Relacionamento com comunidade',         descricao: 'Eventos e ações com produtores e moradores',                         acao: 'Jantares colaborativos · mercado de produtores no salão' },
      { id: 'gorjeta_just',      titulo: 'Política de gorjetas justa',            descricao: 'Distribuição transparente entre salão e cozinha',                    acao: 'Sistema de partilha proporcional + comitê de revisão semestral' },
      { id: 'fornec_just',       titulo: 'Comércio justo com produtores',         descricao: 'Pagamento justo aos pequenos produtores',                            acao: 'Contratos diretos · pagamento à vista · preço justo certificado' },
    ],
    governanca: [
      { id: 'crc_responsavel',   titulo: 'Responsabilidade técnica de cozinha',   descricao: 'Nutricionista/chef-responsável formalmente designado',               acao: 'Contratação efetiva + POPs aprovados por profissional habilitado' },
      { id: 'cardapio_alergeno', titulo: 'Transparência de alergênicos',          descricao: 'Sinalização clara no cardápio',                                      acao: 'Treinamento de salão + revisão mensal de alergênicos por item' },
      { id: 'conselho_b',        titulo: 'Conselho consultivo (advisors)',        descricao: 'Conselheiros do setor (chefs/gestores)',                             acao: 'Formar conselho com 3 advisors · encontros trimestrais' },
      { id: 'auditoria_b',       titulo: 'Auditoria contábil externa',            descricao: 'Auditoria independente das demonstrações',                           acao: 'Contratar auditor externo · publicar relatório anual' },
    ],
  },
  'Saúde': {
    ambiental: [
      { id: 'descarte_residuos', titulo: 'Descarte de resíduos infectantes',      descricao: 'Coleta e tratamento conforme RDC ANVISA',                            acao: 'Empresa especializada + auditoria semestral · indicadores de volume' },
      { id: 'energia_clinica',   titulo: 'Eficiência energética',                 descricao: 'Iluminação LED, ar-condicionado eficiente, energia limpa',           acao: 'Migração para energia solar + auditoria de consumo' },
      { id: 'agua_clinica',      titulo: 'Uso racional de água',                  descricao: 'Reuso e captação de chuva',                                          acao: 'Sistema de reuso + sensores de torneira' },
      { id: 'papel_digital',     titulo: 'Digitalização (paperless)',             descricao: 'Prontuário eletrônico + receitas digitais',                          acao: 'Eliminação total de papel até 2027 · auditoria mensal' },
    ],
    social: [
      { id: 'saude_corp_grat',   titulo: 'Saúde para a comunidade',               descricao: 'Atendimento gratuito ou subsidiado para vulneráveis',                acao: 'Mutirões mensais · parceria com ONG' },
      { id: 'mental_equipe',     titulo: 'Saúde mental da equipe',                descricao: 'Prevenção de burnout em médicos e enfermeiros',                      acao: 'Programa de apoio psicológico + ergonomia + jornadas balanceadas' },
      { id: 'acolhimento_pac',   titulo: 'Acolhimento humanizado',                descricao: 'Cuidado centrado no paciente · escuta ativa',                        acao: 'Treinamento periódico de salão + pesquisa de satisfação' },
      { id: 'inclusao_lgbt',     titulo: 'Atendimento inclusivo (LGBTQIA+, idosos, PCD)', descricao: 'Acessibilidade, sigilo e respeito',                            acao: 'Treinamento ético + infra física acessível + sinalização' },
    ],
    governanca: [
      { id: 'crm_etica',         titulo: 'Comitê de ética médica',                descricao: 'Análise de casos eticamente complexos',                              acao: 'Comitê com 3-5 médicos + reuniões mensais · ata pública' },
      { id: 'protocolo_quali',   titulo: 'Protocolos de qualidade clínica',       descricao: 'Padronização baseada em evidência',                                  acao: 'Adoção de protocolos SBC/SBP + auditoria de aderência trimestral' },
      { id: 'lgpd_saude_g',      titulo: 'Conformidade LGPD em saúde',            descricao: 'Proteção de dados sensíveis de saúde',                               acao: 'DPO + auditoria semestral + treinamento anual de equipe' },
      { id: 'ona_g',             titulo: 'Acreditação ONA',                       descricao: 'Selo de qualidade hospitalar',                                       acao: 'Iniciar processo de acreditação · meta 2-3 anos' },
    ],
  },
  'Construção Civil': {
    ambiental: [
      { id: 'residuos_obra',     titulo: 'Gestão de resíduos de obra',            descricao: 'Triagem e destinação correta de RCC',                                acao: 'Plano de gerenciamento + aterro classe IIA · indicadores mensais' },
      { id: 'materiais_recic',   titulo: 'Materiais reciclados/sustentáveis',     descricao: 'Uso de tijolos ecológicos, madeira certificada, aço reciclado',      acao: 'Especificar % mínimo em projeto · fornecedores com selo FSC/CYCLE' },
      { id: 'agua_obra',         titulo: 'Uso racional de água em obra',          descricao: 'Captação de chuva, reuso, redução de desperdício',                   acao: 'Captação na obra + irrigação reciclada · meta 30% reuso' },
      { id: 'energia_solar_c',   titulo: 'Energia solar embarcada nas obras',     descricao: 'Projeto com geração solar integrada desde o início',                 acao: 'Painel solar como item de projeto · meta 100% obras corporativas' },
    ],
    social: [
      { id: 'seguranca_canteiro_s',titulo: 'Segurança do trabalho no canteiro',   descricao: 'Cultura SST · zero acidente',                                        acao: 'DDS diário + SIPAT anual + indicador zero acidente' },
      { id: 'qualificacao_obra', titulo: 'Qualificação de mão de obra',           descricao: 'Treinamento técnico + alfabetização',                                acao: 'Parceria com SENAI · 40h ano por colaborador' },
      { id: 'fornec_dignos',     titulo: 'Empreiteiros com condições dignas',     descricao: 'Auditoria de condições de trabalho na cadeia',                       acao: 'Código de conduta + auditoria trimestral de empreiteiros' },
      { id: 'comunidade_obra',   titulo: 'Relação com vizinhança',                descricao: 'Mitigação de ruído, poeira, trânsito',                                acao: 'Plano de comunicação + indicadores de impacto + canal de queixas' },
    ],
    governanca: [
      { id: 'crea_c',            titulo: 'Responsabilidade técnica CREA',         descricao: 'Engenheiro responsável ativo em todas as obras',                     acao: 'ART em todas as obras + auditoria trimestral' },
      { id: 'compliance_obra',   titulo: 'Compliance em licitações',              descricao: 'Prevenção a fraudes em contratos públicos',                          acao: 'Programa de integridade + due diligence de subcontratados' },
      { id: 'conselho_c',        titulo: 'Conselho consultivo',                   descricao: 'Conselheiros do setor (engenheiros, gestores)',                      acao: 'Formar conselho com 3 advisors + encontros trimestrais' },
      { id: 'auditoria_c',       titulo: 'Auditoria contábil externa',            descricao: 'Auditoria independente das demonstrações',                           acao: 'Contratar auditor externo · publicar relatório anual' },
    ],
  },
  'geral': {
    ambiental: [
      { id: 'energia_g',         titulo: 'Eficiência energética',                 descricao: 'Iluminação LED + energia renovável',                                 acao: 'Auditoria energética · migração progressiva para fontes limpas' },
      { id: 'residuos_g',        titulo: 'Gestão de resíduos',                    descricao: 'Triagem, reciclagem e destinação correta',                           acao: 'Plano de gerenciamento + parceria com cooperativa' },
      { id: 'agua_g',            titulo: 'Uso racional de água',                  descricao: 'Reuso, captação, redução de desperdício',                            acao: 'Sensores + reuso + meta de redução anual' },
      { id: 'carbono_g',         titulo: 'Inventário de carbono',                 descricao: 'Mensuração e plano de redução de emissões',                          acao: 'Inventário GHG protocol + plano de redução por escopo' },
    ],
    social: [
      { id: 'diversidade_g',     titulo: 'Diversidade e inclusão',                descricao: 'Representatividade na equipe e cadeia',                              acao: 'Metas afirmativas + comitê + treinamento contra vieses' },
      { id: 'saude_mental_g',    titulo: 'Saúde mental dos colaboradores',        descricao: 'Programa de apoio + jornada balanceada',                             acao: 'EAP + ergonomia + cultura de pausa · indicadores anuais' },
      { id: 'comunidade_g',      titulo: 'Relação com comunidade',                descricao: 'Investimento social + voluntariado',                                  acao: 'Plano anual de investimento social + horas de voluntariado' },
      { id: 'fornec_eticos_g',   titulo: 'Fornecedores éticos',                   descricao: 'Cadeia auditada · sem trabalho análogo à escravidão',                acao: 'Código de conduta + auditoria periódica + ranking de fornecedores' },
    ],
    governanca: [
      { id: 'etica_g',           titulo: 'Código de ética',                       descricao: 'Documento formal e canal de denúncias',                              acao: 'Aprovação no conselho + canal externo · treinamento anual' },
      { id: 'compliance_g',      titulo: 'Compliance',                            descricao: 'Programa de prevenção a fraudes e corrupção',                        acao: 'Política antifraude + due diligence + indicadores' },
      { id: 'conselho_g',        titulo: 'Conselho consultivo/administrativo',    descricao: 'Órgão de governança independente',                                   acao: 'Formar conselho com membros externos · cadência trimestral' },
      { id: 'transparencia_g',   titulo: 'Transparência fiscal',                  descricao: 'Demonstrações auditadas e públicas',                                 acao: 'Auditoria externa anual · relatório anual público' },
    ],
  },
};

const COMPROMISSOS_PUBLICOS_POR_SETOR = {
  'E-commerce': [
    { id: 'net_zero_2030',     titulo: 'Net-zero em 2030',                       descricao: 'Compensação de 100% das emissões diretas e indiretas até 2030' },
    { id: 'embalagem_2027',    titulo: '100% embalagens recicláveis até 2027',   descricao: 'Substituir embalagens não-recicláveis em 100% do mix' },
    { id: 'b_corp',            titulo: 'Certificação B Corp',                    descricao: 'Conquistar selo B Corp até 2027' },
    { id: 'relatorio_esg_ec',  titulo: 'Relatório ESG anual público',            descricao: 'Publicar relatório de sustentabilidade auditado' },
  ],
  'Bar/Restaurante': [
    { id: 'desperd_zero',      titulo: 'Desperdício zero até 2028',              descricao: 'Reduzir a zero o desperdício alimentar com aproveitamento integral' },
    { id: 'ingred_100_loc',    titulo: '100% ingredientes locais até 2027',      descricao: 'Mover o cardápio para fornecedores num raio máximo de 200 km' },
    { id: 'b_corp_b',          titulo: 'Certificação B Corp',                    descricao: 'Conquistar selo B Corp até 2028' },
    { id: 'energia_renov_b',   titulo: 'Energia 100% renovável até 2026',        descricao: 'Migrar consumo elétrico para fontes renováveis' },
  ],
  'Saúde': [
    { id: 'acreditacao_ona',   titulo: 'Acreditação ONA em 2-3 anos',            descricao: 'Conquistar selo ONA de qualidade hospitalar' },
    { id: 'paperless_2027',    titulo: '100% paperless até 2027',                descricao: 'Eliminar todo uso de papel via prontuário e receita eletrônicos' },
    { id: 'comunidade_anual',  titulo: 'Atendimento à comunidade anual',         descricao: 'Garantir N consultas gratuitas/subsidiadas por ano' },
    { id: 'energia_renov_s',   titulo: 'Energia 100% renovável até 2027',        descricao: 'Migrar consumo elétrico para fontes renováveis' },
  ],
  'Construção Civil': [
    { id: 'leed_corp',         titulo: 'Certificação LEED em obras corporativas',descricao: 'Conquistar LEED em 100% das obras corporativas novas' },
    { id: 'aqua_residencial',  titulo: 'Selo AQUA em residenciais',              descricao: 'Conquistar AQUA em obras residenciais alto padrão' },
    { id: 'zero_acidente_c',   titulo: 'Zero acidente nos canteiros',            descricao: 'Manter zero acidente grave em todas as obras' },
    { id: 'residuos_50_c',     titulo: 'Redução 50% de resíduos até 2028',       descricao: 'Reduzir RCC em 50% via metodologia construtiva' },
  ],
  'geral': [
    { id: 'net_zero_g',        titulo: 'Net-zero em 2030',                       descricao: 'Compensação de 100% das emissões até 2030' },
    { id: 'b_corp_g',          titulo: 'Certificação B Corp',                    descricao: 'Conquistar selo B Corp em horizonte de 3-5 anos' },
    { id: 'relatorio_esg_g',   titulo: 'Relatório ESG anual público',            descricao: 'Publicar relatório de sustentabilidade auditado' },
    { id: 'diversidade_meta',  titulo: 'Metas de diversidade',                   descricao: 'Atingir 40% de mulheres em liderança até 2027' },
  ],
};

const DIMENSOES_ESG = [
  { id: 'ambiental',  label: 'Ambiental (E)',  cor: G.verde   },
  { id: 'social',     label: 'Social (S)',     cor: G.laranja },
  { id: 'governanca', label: 'Governança (G)', cor: '#7C3AED' },
];

// Catálogos ESG SU · lista única (maturidade, não setor). Governança com peso
// maior em startup. Selecionados quando modo === 'SU'.
const ESG_CATALOGO_POR_SETOR_SU = {
  'geral': {
    ambiental: [
      { id: 'carbono_digital',   titulo: 'Pegada de carbono digital',              descricao: 'Servidores em nuvem geram a maior parte da pegada em early-stage', acao: 'Escolher região/nuvem com energia renovável · medir e reduzir consumo' },
      { id: 'nuvem_renovavel',   titulo: 'Energia renovável no provedor de nuvem', descricao: 'Provedor de nuvem com matriz de energia renovável',                acao: 'Selecionar provedor/região com compromisso renovável' },
      { id: 'trabalho_remoto',   titulo: 'Trabalho remoto reduz deslocamento',     descricao: 'Operação remota-first reduz emissões de deslocamento',             acao: 'Política remota · encontros presenciais pontuais' },
      { id: 'produto_substitui', titulo: 'Produto que substitui processo poluente',descricao: 'A solução digital substitui um processo mais poluente',            acao: 'Medir e comunicar a economia gerada ao cliente' },
    ],
    social: [
      { id: 'diversidade_fundador',  titulo: 'Diversidade no time fundador',        descricao: 'Diversidade e inclusão desde o time fundador e as primeiras contratações', acao: 'Metas de diversidade · processo de seleção inclusivo' },
      { id: 'cultura_registrada',    titulo: 'Cultura interna registrada',          descricao: 'Cultura documentada (manifesto · livro de cultura)',              acao: 'Escrever e manter o livro de cultura' },
      { id: 'remuneracao_equitativa',titulo: 'Remuneração equitativa',              descricao: 'Política de remuneração justa e transparente',                     acao: 'Bandas salariais · revisão de equidade periódica' },
      { id: 'acessibilidade_produto',titulo: 'Acessibilidade do produto',           descricao: 'Produto digital acessível (WCAG)',                                 acao: 'Auditoria de acessibilidade · testes com usuários' },
      { id: 'fornecedores_avaliados',titulo: 'Cadeia de fornecedores avaliada',     descricao: 'Fornecedores de tecnologia avaliados eticamente',                  acao: 'Código de conduta de fornecedores · revisão periódica' },
    ],
    governanca: [
      { id: 'acordo_acionistas', titulo: 'Acordo de acionistas registrado',        descricao: 'Acordo entre sócios formalizado',                                  acao: 'Registrar acordo · cláusulas de vesting e saída' },
      { id: 'cap_table',         titulo: 'Cap table clara e atualizada',           descricao: 'Tabela de capitalização organizada',                               acao: 'Manter a cap table atualizada a cada rodada' },
      { id: 'conselho_consultivo', titulo: 'Conselho consultivo formado',          descricao: 'Conselho de advisors ativo',                                       acao: 'Formar conselho · cadência trimestral' },
      { id: 'lgpd_declarada',    titulo: 'Política de LGPD em execução',           descricao: 'LGPD declarada e praticada',                                       acao: 'DPO · política de privacidade · consentimento' },
      { id: 'revisao_externa',   titulo: 'Demonstrações com revisão externa',      descricao: 'Auditoria leve a partir do Pre-Seed · obrigatória na Série A',      acao: 'Contratar revisão contábil externa' },
      { id: 'codigo_etica',      titulo: 'Código de ética interno',                descricao: 'Princípios e canal de denúncias',                                  acao: 'Aprovar código · canal externo independente' },
    ],
  },
};

const COMPROMISSOS_PUBLICOS_POR_SETOR_SU = {
  'geral': [
    { id: 'cap_table_meta',      titulo: 'Cap table auditável até a Série A',       descricao: 'Manter a cap table auditável para a próxima rodada' },
    { id: 'lgpd_meta',           titulo: 'LGPD 100% em execução em 12 meses',       descricao: 'Concluir a implementação da LGPD em até 1 ano' },
    { id: 'diversidade_meta_su', titulo: 'Meta de diversidade no time em 3 anos',   descricao: 'Atingir a meta de diversidade nas contratações em 3 anos' },
    { id: 'nuvem_renovavel_meta',titulo: 'Nuvem 100% renovável em 3-5 anos',        descricao: 'Migrar a infraestrutura para provedor/região renovável' },
    { id: 'auditoria_meta',      titulo: 'Auditoria externa a partir da Série A',   descricao: 'Adotar auditoria externa das demonstrações na Série A' },
  ],
};

const RASCUNHO_TAREFA_12_SU = {
  shopmax: {
    praticas: [
      { id_catalogo: 'carbono_digital',      dimensao: 'ambiental',  maturidade: 'b' },
      { id_catalogo: 'trabalho_remoto',      dimensao: 'ambiental',  maturidade: 'c' },
      { id_catalogo: 'diversidade_fundador', dimensao: 'social',     maturidade: 'b' },
      { id_catalogo: 'cultura_registrada',   dimensao: 'social',     maturidade: 'c' },
      { id_catalogo: 'acordo_acionistas',    dimensao: 'governanca', maturidade: 'c' },
      { id_catalogo: 'cap_table',            dimensao: 'governanca', maturidade: 'c' },
      { id_catalogo: 'lgpd_declarada',       dimensao: 'governanca', maturidade: 'b' },
      { id_catalogo: 'codigo_etica',         dimensao: 'governanca', maturidade: 'b' },
    ],
    compromissos: [
      { id_catalogo: 'cap_table_meta' },
      { id_catalogo: 'lgpd_meta' },
    ],
  },
  sabor_arte: {
    praticas: [
      { id_catalogo: 'trabalho_remoto',       dimensao: 'ambiental',  maturidade: 'c' },
      { id_catalogo: 'produto_substitui',     dimensao: 'ambiental',  maturidade: 'b' },
      { id_catalogo: 'diversidade_fundador',  dimensao: 'social',     maturidade: 'b' },
      { id_catalogo: 'remuneracao_equitativa',dimensao: 'social',     maturidade: 'c' },
      { id_catalogo: 'acordo_acionistas',     dimensao: 'governanca', maturidade: 'c' },
      { id_catalogo: 'cap_table',             dimensao: 'governanca', maturidade: 'b' },
      { id_catalogo: 'conselho_consultivo',   dimensao: 'governanca', maturidade: 'b' },
    ],
    compromissos: [
      { id_catalogo: 'diversidade_meta_su' },
      { id_catalogo: 'lgpd_meta' },
    ],
  },
  clinicavida: {
    praticas: [
      { id_catalogo: 'carbono_digital',      dimensao: 'ambiental',  maturidade: 'b' },
      { id_catalogo: 'nuvem_renovavel',      dimensao: 'ambiental',  maturidade: 'b' },
      { id_catalogo: 'acessibilidade_produto',dimensao: 'social',    maturidade: 'c' },
      { id_catalogo: 'diversidade_fundador', dimensao: 'social',     maturidade: 'b' },
      { id_catalogo: 'lgpd_declarada',       dimensao: 'governanca', maturidade: 'c' },
      { id_catalogo: 'acordo_acionistas',    dimensao: 'governanca', maturidade: 'c' },
      { id_catalogo: 'revisao_externa',      dimensao: 'governanca', maturidade: 'b' },
      { id_catalogo: 'codigo_etica',         dimensao: 'governanca', maturidade: 'b' },
    ],
    compromissos: [
      { id_catalogo: 'lgpd_meta' },
      { id_catalogo: 'auditoria_meta' },
    ],
  },
  construmax: {
    praticas: [
      { id_catalogo: 'trabalho_remoto',      dimensao: 'ambiental',  maturidade: 'c' },
      { id_catalogo: 'carbono_digital',      dimensao: 'ambiental',  maturidade: 'b' },
      { id_catalogo: 'cultura_registrada',   dimensao: 'social',     maturidade: 'b' },
      { id_catalogo: 'fornecedores_avaliados',dimensao: 'social',    maturidade: 'b' },
      { id_catalogo: 'acordo_acionistas',    dimensao: 'governanca', maturidade: 'c' },
      { id_catalogo: 'cap_table',            dimensao: 'governanca', maturidade: 'c' },
      { id_catalogo: 'conselho_consultivo',  dimensao: 'governanca', maturidade: 'b' },
    ],
    compromissos: [
      { id_catalogo: 'cap_table_meta' },
      { id_catalogo: 'auditoria_meta' },
    ],
  },
};

const RASCUNHO_TAREFA_12 = {
  shopmax: {
    praticas: [
      { id_catalogo: 'embalagens_eco',    dimensao: 'ambiental', maturidade: 'b' },
      { id_catalogo: 'logistica_reversa', dimensao: 'ambiental', maturidade: 'a' },
      { id_catalogo: 'diversidade_time',  dimensao: 'social',    maturidade: 'b' },
      { id_catalogo: 'protecao_dados',    dimensao: 'social',    maturidade: 'c' },
      { id_catalogo: 'codigo_etica_e',    dimensao: 'governanca',maturidade: 'b' },
      { id_catalogo: 'transparencia_e',   dimensao: 'governanca',maturidade: 'b' },
    ],
    compromissos: [
      { id_catalogo: 'embalagem_2027' },
      { id_catalogo: 'relatorio_esg_ec' },
    ],
  },
  sabor_arte: {
    praticas: [
      { id_catalogo: 'desperdicio_alim', dimensao: 'ambiental', maturidade: 'c' },
      { id_catalogo: 'ingredientes_loc', dimensao: 'ambiental', maturidade: 'd' },
      { id_catalogo: 'fornec_just',      dimensao: 'social',    maturidade: 'c' },
      { id_catalogo: 'gorjeta_just',     dimensao: 'social',    maturidade: 'c' },
      { id_catalogo: 'cardapio_alergeno',dimensao: 'governanca',maturidade: 'c' },
    ],
    compromissos: [
      { id_catalogo: 'desperd_zero' },
      { id_catalogo: 'ingred_100_loc' },
    ],
  },
  clinicavida: {
    praticas: [
      { id_catalogo: 'descarte_residuos',dimensao: 'ambiental', maturidade: 'c' },
      { id_catalogo: 'papel_digital',    dimensao: 'ambiental', maturidade: 'b' },
      { id_catalogo: 'acolhimento_pac',  dimensao: 'social',    maturidade: 'c' },
      { id_catalogo: 'mental_equipe',    dimensao: 'social',    maturidade: 'b' },
      { id_catalogo: 'lgpd_saude_g',     dimensao: 'governanca',maturidade: 'c' },
      { id_catalogo: 'protocolo_quali',  dimensao: 'governanca',maturidade: 'c' },
    ],
    compromissos: [
      { id_catalogo: 'acreditacao_ona' },
      { id_catalogo: 'paperless_2027' },
    ],
  },
  construmax: {
    praticas: [
      { id_catalogo: 'residuos_obra',         dimensao: 'ambiental', maturidade: 'b' },
      { id_catalogo: 'materiais_recic',       dimensao: 'ambiental', maturidade: 'b' },
      { id_catalogo: 'seguranca_canteiro_s',  dimensao: 'social',    maturidade: 'c' },
      { id_catalogo: 'fornec_dignos',         dimensao: 'social',    maturidade: 'b' },
      { id_catalogo: 'crea_c',                dimensao: 'governanca',maturidade: 'd' },
      { id_catalogo: 'auditoria_c',           dimensao: 'governanca',maturidade: 'b' },
    ],
    compromissos: [
      { id_catalogo: 'leed_corp' },
      { id_catalogo: 'zero_acidente_c' },
    ],
  },
};

const compilarTarefa12 = (esgState, empresa) => {
  const rascunho = RASCUNHO_TAREFA_12[empresa?.id] || { praticas: [], compromissos: [] };
  const praticas = esgState?.praticas ?? rascunho.praticas;
  const compromissos = esgState?.compromissos ?? rascunho.compromissos;
  const setor = empresa?.setor || 'geral';
  const catESG = ESG_CATALOGO_POR_SETOR[setor] || ESG_CATALOGO_POR_SETOR['geral'];
  const catComp = COMPROMISSOS_PUBLICOS_POR_SETOR[setor] || COMPROMISSOS_PUBLICOS_POR_SETOR['geral'];
  const buscarPratica = (catId, dim) => (catESG[dim] || []).find(c => c.id === catId);
  const buscarComp = (catId) => catComp.find(c => c.id === catId);
  const linhasDim = (dim) => {
    const itens = praticas.filter(p => p.dimensao === dim);
    if (!itens.length) return '_Nenhuma prática mapeada nesta dimensão._';
    return itens.map(p => {
      const cat = buscarPratica(p.id_catalogo, p.dimensao);
      const mat = MATURIDADE_ESG.find(o => o.id === p.maturidade)?.label || '—';
      const tit = cat?.titulo || p.titulo || '—';
      const desc = cat?.descricao || p.descricao || '';
      const acao = cat?.acao || p.acao || '';
      return `- **${tit}** · maturidade **${mat}**\n  - _${desc}_\n  - **Ação:** ${acao}`;
    }).join('\n');
  };
  return `## 12. ESG & Sustentabilidade

### Ambiental (E)

${linhasDim('ambiental')}

### Social (S)

${linhasDim('social')}

### Governança (G)

${linhasDim('governanca')}

### Compromissos Públicos

${compromissos.length === 0
    ? '_Nenhum compromisso público formalizado._'
    : compromissos.map(c => {
        const cat = buscarComp(c.id_catalogo);
        const tit = cat?.titulo || c.titulo || '—';
        const desc = cat?.descricao || c.descricao || '';
        return `- **${tit}** — ${desc}`;
      }).join('\n')}`;
};

const compilarTarefa10 = (estruturaRh, empresa) => {
  const rascunho = RASCUNHO_TAREFA_10[empresa?.id] || {
    organograma: '',
    areas_chars: [], areas_extras: [],
    pessoas_chave: [],
    headcount_hoje: '',
    hires_plano: [],
    cultura_chars: [], cultura_extras: [],
    remuneracao: '',
  };
  const obter = (c) => estruturaRh?.[c] ?? rascunho[c];
  const setor = empresa?.setor || 'geral';
  const organogramaNos = obter('organograma_nos') || [];
  const organograma   = obter('organograma') || '_Comentário sobre o organograma ainda não descrito._';
  const areasChars    = obter('areas_chars') || [];
  const areasExtras   = obter('areas_extras') || [];
  const areasLivre    = obter('areas_livre');
  const areasEditou   = obter('areas_editou_livre') || false;
  const pessoas       = obter('pessoas_chave') || [];
  // Tamanho do time hoje é calculado dinamicamente do organograma (read-only)
  const headcount = (() => {
    const total = organogramaNos.reduce((s, n) => s + (n.headcount || 0), 0);
    if (total === 0) return '_Tamanho do time ainda não calculado (preencha o organograma)._';
    const porArea = {};
    organogramaNos.forEach(n => {
      const a = inferirAreaDeCargo(n.cargo);
      porArea[a] = (porArea[a] || 0) + (n.headcount || 0);
    });
    const ordemAreas = ['estrategia', 'comercial', 'marketing', 'operacoes', 'financeiro', 'rh', 'ti'];
    const partes = ordemAreas.filter(a => porArea[a] > 0).map(a => {
      const label = CHIPS_PAPEL_POR_AREA[a]?.label || a;
      return `${label} ${porArea[a]}`;
    });
    return `Hoje: ~${total} pessoas distribuídas em ${partes.join(' · ')}.`;
  })();
  const hires         = obter('hires_plano') || [];
  const cultChars     = obter('cultura_chars') || [];
  const cultExtras    = obter('cultura_extras') || [];
  const cultLivre     = obter('cultura_livre');
  const cultEditou    = obter('cultura_editou_livre') || false;
  const remuneracao   = obter('remuneracao') || '_Política de remuneração ainda não descrita._';

  const fraseAr = (areasEditou && areasLivre)
    ? areasLivre
    : (montarFraseAreas(setor, areasChars, areasExtras) || '_Áreas funcionais ainda não descritas._');
  const fraseCu = (cultEditou && cultLivre)
    ? cultLivre
    : (montarFraseCultura(setor, cultChars, cultExtras) || '_Cultura ainda não descrita._');

  const linhasPessoas = pessoas
    .filter(p => (p.nome || '').trim())
    .sort((a, b) => {
      const o = { a: 0, b: 1, c: 2, d: 3 };
      return (o[b.dependencia] ?? 0) - (o[a.dependencia] ?? 0);
    })
    .map(p => {
      const dep = OPCOES_DEPENDENCIA.find(o => o.id === p.dependencia);
      const alerta = (p.dependencia === 'c' || p.dependencia === 'd') ? ' ⚠' : '';
      const fr = montarFrasePapel(p.area, p.papel_chips) || p.papel || '—';
      return `- **${p.nome}** · _${fr}_ · dependência **${dep?.label || '—'}**${alerta}`;
    })
    .join('\n') || '_Pessoas-chave ainda não descritas._';

  const linhasHires = ANOS_PLANO_HIRES
    .map(ano => {
      const lista = hires.filter(h => h.ano === ano && (h.descricao || '').trim());
      if (lista.length === 0) return null;
      return `**${ano}**\n${lista.map(h => `- ${h.descricao}`).join('\n')}`;
    })
    .filter(Boolean)
    .join('\n\n') || '_Plano de contratações ainda não descrito._';

  // Monta o organograma como lista hierárquica indentada (Markdown não renderiza SVG)
  const funcoesSetor = FUNCOES_POR_SETOR[setor] || FUNCOES_POR_SETOR['geral'];
  const labelFuncao = (id) => funcoesSetor.find(f => f.id === id)?.label || id;
  const renderOrganograma = (nos) => {
    if (!nos || nos.length === 0) return '_Organograma ainda não montado._';
    const raiz = nos.find(n => !n.reporta_a);
    if (!raiz) return '_Estrutura sem cargo raiz._';
    const renderNo = (id, nivel) => {
      const no = nos.find(n => n.id === id);
      if (!no) return '';
      const prefixo = '  '.repeat(nivel) + '- ';
      const hc = (no.headcount > 0) ? ` (${no.headcount} ${no.headcount === 1 ? 'pessoa' : 'pessoas'})` : '';
      const funcs = (no.funcoes || []).length > 0
        ? ` _[${(no.funcoes || []).map(labelFuncao).join(' · ')}]_`
        : '';
      const linhaAtual = `${prefixo}**${no.cargo || '(sem nome)'}**${hc}${funcs}`;
      const filhos = nos.filter(n => n.reporta_a === id);
      const linhasFilhos = filhos.map(f => renderNo(f.id, nivel + 1)).join('\n');
      return linhasFilhos ? `${linhaAtual}\n${linhasFilhos}` : linhaAtual;
    };
    return renderNo(raiz.id, 0);
  };
  // Cobertura das funções essenciais (relata se está tudo ok ou ⚠ descoberta)
  const cobertas = new Set();
  organogramaNos.forEach(n => (n.funcoes || []).forEach(f => cobertas.add(f)));
  const linhasFuncoes = funcoesSetor
    .map(f => `- ${cobertas.has(f.id) ? '✓' : '⚠'} **${f.label}**${cobertas.has(f.id) ? '' : ' _(descoberta)_'}`)
    .join('\n');

  return `## 10. Estrutura Org & RH

### Estrutura do Time

**Funções essenciais do setor**

${linhasFuncoes}

**Organograma**

A estrutura hierárquica da empresa está ilustrada abaixo. ${organogramaNos.length > 0 ? `A organização conta com ${organogramaNos.length} cargos distribuídos em ${Math.max(...organogramaNos.map(n => {
  let nivel = 0;
  let atual = n.reporta_a;
  while (atual) {
    nivel++;
    const pai = organogramaNos.find(x => x.id === atual);
    atual = pai?.reporta_a;
  }
  return nivel + 1;
}))} níveis hierárquicos, totalizando ${organogramaNos.reduce((s, n) => s + (n.headcount || 0), 0)} colaboradores.` : ''}

<!--GRAFICO:t10_organograma-->

**Estrutura hierárquica detalhada**

${renderOrganograma(organogramaNos)}

**Comentário sobre o organograma**

${organograma}

**Áreas funcionais**

${fraseAr}

### Pessoas-Chave

A continuidade operacional da empresa depende dos profissionais listados abaixo. O grau de dependência reflete o impacto na operação em caso de saída — pessoas em patamar **Alta** ou **Crítica** representam risco-chave a ser endereçado no plano de retenção e sucessão.

${linhasPessoas}

### Plano de Pessoas 5 anos

**Tamanho do time hoje**

${headcount}

**Plano de contratações estratégicas**

O plano de contratações estratégicas para os próximos 5 anos sustenta o crescimento previsto no Business Plan. A linha do tempo abaixo consolida as contratações-chave ano a ano.

<!--GRAFICO:t10_timeline_rh-->

**Detalhamento ano a ano**

${linhasHires}

### Cultura e Retenção

${fraseCu}

**Política de remuneração**

${remuneracao}`;
};

// Dispatcher: ao aprovar a Tarefa N, devolve o Markdown da seção
const compilarSecao = (numero, state) => {
  const { meta, equityFlag, descricaoEmpresa, vmv, produtosServicos, mercadoConcorrencia, swot, estrategiaModelo, marketingVendas, planoOperacional, estruturaRh, riscos, esg, projecoes, valuation, empresa } = state;
  if (numero === 1) return compilarTarefa1(meta, equityFlag, empresa);
  if (numero === 2) return compilarTarefa2(descricaoEmpresa, empresa);
  if (numero === 3) return compilarTarefa3(vmv, empresa);
  if (numero === 4) return compilarTarefa4(produtosServicos, empresa);
  if (numero === 5) return compilarTarefa5(mercadoConcorrencia, empresa);
  if (numero === 6) return compilarTarefa6(swot, empresa);
  if (numero === 7) return compilarTarefa7(estrategiaModelo, empresa);
  if (numero === 8) return compilarTarefa8(marketingVendas, empresa);
  if (numero === 9) return compilarTarefa9(planoOperacional, empresa);
  if (numero === 10) return compilarTarefa10(estruturaRh, empresa);
  if (numero === 11) return compilarTarefa11(riscos, empresa);
  if (numero === 12) return compilarTarefa12(esg, empresa);
  if (numero === 13) return compilarTarefa13(projecoes, empresa);
  if (numero === 14) return compilarTarefa14(valuation, projecoes, empresa);
  // Tarefas 15-18: ainda sem compilador específico — placeholder
  return `## ${numero}. ${TAREFAS_BP60M.find(t => t.id === numero)?.titulo || 'Seção'}

_Conteúdo desta seção será gerado quando a Tarefa ${numero} estiver implementada._`;
};

// ═══════════════════════════════════════════════════════════════════════
// ═══════════════════════════════════════════════════════════════════════
// Componentes de gráficos reutilizáveis no BP60M.
// Cada componente recebe os dados e renderiza um SVG autoexplicativo.
// Aparece tanto no botão "Visualizar gráficos e tabelas" das Tarefas
// quanto no documento compilado via marcador <!--GRAFICO:tipo-->.
// ═══════════════════════════════════════════════════════════════════════

// Organograma — usa layoutOrganograma para posicionar os nós e desenha SVG
// Escala automática via viewBox: cabe na largura disponível sem scroll.
const GraficoOrganograma = ({ nos }) => {
  if (!nos || nos.length === 0) {
    return (
      <div style={{ padding: 16, color: G.txt2, fontSize: 12, fontStyle: 'italic',
        textAlign: 'center' }}>
        Organograma ainda não montado.
      </div>
    );
  }
  const layout = layoutOrganograma(nos);
  if (!layout.nodes.length) return null;
  const W_NODE = 150;
  const H_NODE = 52;
  const vbW = layout.width + 20;
  const vbH = layout.height + 8;
  return (
    <div style={{ width: '100%' }}>
      <svg viewBox={`0 0 ${vbW} ${vbH}`}
        preserveAspectRatio="xMidYMid meet"
        style={{ display: 'block', width: '100%', height: 'auto', maxWidth: '100%' }}>
        {layout.edges.map((e, i) => (
          <g key={`e-${i}`} stroke={G.verde} strokeWidth="1.5" fill="none">
            <line x1={e.x1 + 10} y1={e.y1} x2={e.x1 + 10} y2={e.midY}/>
            <line x1={e.x1 + 10} y1={e.midY} x2={e.x2 + 10} y2={e.midY}/>
            <line x1={e.x2 + 10} y1={e.midY} x2={e.x2 + 10} y2={e.y2}/>
          </g>
        ))}
        {layout.nodes.map(n => {
          const ehRaiz = !n.reporta_a;
          return (
            <g key={n.id} transform={`translate(${n.x + 10}, ${n.y})`}>
              <rect width={W_NODE} height={H_NODE} rx={6}
                fill={ehRaiz ? G.verde : '#FFF'}
                stroke={G.verde} strokeWidth={1.5}/>
              <text x={W_NODE / 2} y={20} textAnchor="middle"
                fontFamily="Outfit, sans-serif" fontSize="12" fontWeight="700"
                fill={ehRaiz ? '#FFF' : G.txt1}>
                {(n.cargo || '(sem nome)').substring(0, 22)}
              </text>
              {(n.headcount > 0) && (
                <text x={W_NODE / 2} y={38} textAnchor="middle"
                  fontFamily="Outfit, sans-serif" fontSize="10" fontStyle="italic"
                  fill={ehRaiz ? 'rgba(255,255,255,0.85)' : G.txt2}>
                  {n.headcount} {n.headcount === 1 ? 'pessoa' : 'pessoas'}
                </text>
              )}
            </g>
          );
        })}
      </svg>
    </div>
  );
};

// Canais de Aquisição (T8 Bloco 2) — pizza · adapta o GraficoPizzaMix com label do catálogo
const GraficoCanaisMix = ({ canaisMix }) => {
  const items = (canaisMix || []).map(c => ({
    nome: CANAIS_AQUISICAO.find(x => x.id === c.id)?.label || c.id,
    mix: c.peso,
  }));
  return <GraficoPizzaMix items={items}/>;
};

// Funil Marketing & Vendas (T8 Bloco 3) — 4 trapézios afunilando
// Apresenta o número de elementos marcados por etapa.
const GraficoFunilVendas = ({ funilChars }) => {
  const etapas = [
    { id: 'topo',       label: 'Topo · Descoberta',    largura: 1.0,  cor: G.verde },
    { id: 'meio',       label: 'Meio · Consideração',  largura: 0.80, cor: G.laranja },
    { id: 'fundo',      label: 'Fundo · Decisão',      largura: 0.55, cor: '#B45309' },
    { id: 'fechamento', label: 'Fechamento · Compra',  largura: 0.30, cor: '#7C3AED' },
  ];
  const totalChars = etapas.reduce((s, e) => s + ((funilChars?.[e.id] || []).length), 0);
  if (totalChars === 0) {
    return (
      <div style={{ padding: 16, color: G.txt2, fontSize: 12, fontStyle: 'italic',
        textAlign: 'center' }}>
        Funil ainda não preenchido.
      </div>
    );
  }
  const W = 540, H = 320;
  const altNivel = 70, margemT = 18;
  return (
    <div style={{ width: '100%' }}>
      <svg viewBox={`0 0 ${W} ${H}`}
        preserveAspectRatio="xMidYMid meet"
        style={{ display: 'block', width: '100%', height: 'auto', maxWidth: '100%' }}>
        {etapas.map((e, i) => {
          const n = (funilChars?.[e.id] || []).length;
          const y0 = margemT + i * altNivel;
          const cx = W / 2;
          const wTop = e.largura * (W * 0.7);
          const wBot = (i < etapas.length - 1 ? etapas[i + 1].largura : e.largura * 0.55) * (W * 0.7);
          const x0t = cx - wTop / 2, x1t = cx + wTop / 2;
          const x0b = cx - wBot / 2, x1b = cx + wBot / 2;
          const path = `M ${x0t} ${y0} L ${x1t} ${y0} L ${x1b} ${y0 + altNivel - 6} L ${x0b} ${y0 + altNivel - 6} Z`;
          return (
            <g key={e.id}>
              <path d={path} fill={e.cor} stroke="#FFF" strokeWidth="2"/>
              <text x={cx} y={y0 + altNivel / 2 - 4} textAnchor="middle"
                fontFamily="Outfit, sans-serif" fontSize="13" fontWeight="800" fill="#FFF">
                {e.label}
              </text>
              <text x={cx} y={y0 + altNivel / 2 + 14} textAnchor="middle"
                fontFamily="Outfit, sans-serif" fontSize="11" fontWeight="600"
                fill="rgba(255,255,255,0.92)">
                {n} {n === 1 ? 'elemento marcado' : 'elementos marcados'}
              </text>
            </g>
          );
        })}
      </svg>
    </div>
  );
};

// Matriz SWOT 2x2 (T6) — Forças/Fraquezas/Oportunidades/Ameaças
const GraficoSWOT = ({ forcas, fraquezas, oportunidades, ameacas }) => {
  const itensDe = (lista) => (lista || []).filter(i => i.marcado && i.texto).map(i => i.texto);
  const f = itensDe(forcas), w = itensDe(fraquezas);
  const o = itensDe(oportunidades), t = itensDe(ameacas);
  if (!f.length && !w.length && !o.length && !t.length) {
    return (
      <div style={{ padding: 16, color: G.txt2, fontSize: 12, fontStyle: 'italic',
        textAlign: 'center' }}>
        SWOT ainda não preenchido.
      </div>
    );
  }
  const cor = {
    forcas:        { bg: '#E8F5EC', borda: G.verde,   texto: '#155724' },
    fraquezas:     { bg: '#FEF2F2', borda: G.vermelho, texto: '#8B1818' },
    oportunidades: { bg: '#FFF0E6', borda: G.laranja, texto: '#9A3E10' },
    ameacas:       { bg: '#FFFBEB', borda: '#B45309', texto: '#6B4108' },
  };
  const Quadrante = ({ titulo, eyebrow, itens, c }) => (
    <div style={{
      background: c.bg, border: `2px solid ${c.borda}`, borderRadius: 0,
      padding: '12px 14px', minHeight: 110,
    }}>
      <div style={{ fontSize: 10, fontWeight: 700, color: c.borda,
        letterSpacing: '0.10em', textTransform: 'uppercase', marginBottom: 2 }}>
        {eyebrow}
      </div>
      <div style={{ fontSize: 14, fontWeight: 800, color: c.borda,
        textTransform: 'uppercase', marginBottom: 8 }}>
        {titulo}
      </div>
      {itens.length === 0
        ? <div style={{ fontSize: 11, color: G.txt2, fontStyle: 'italic' }}>
            Nenhum item marcado.
          </div>
        : <ul style={{ margin: 0, paddingLeft: 18, fontSize: 12,
            color: c.texto, lineHeight: 1.45 }}>
            {itens.map((txt, i) => <li key={i} style={{ marginBottom: 3 }}>{txt}</li>)}
          </ul>}
    </div>
  );
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
      <Quadrante eyebrow="Interno · positivo" titulo="Forças" itens={f} c={cor.forcas}/>
      <Quadrante eyebrow="Interno · negativo" titulo="Fraquezas" itens={w} c={cor.fraquezas}/>
      <Quadrante eyebrow="Externo · positivo" titulo="Oportunidades" itens={o} c={cor.oportunidades}/>
      <Quadrante eyebrow="Externo · negativo" titulo="Ameaças" itens={t} c={cor.ameacas}/>
    </div>
  );
};

// TAM/SAM/SOM (T5) — tabela elegante estilo relatório M&A
// 3 linhas (TAM, SAM, SOM) · sigla colorida · faixa de valor · descrição.
const GraficoTAMSAMSOM = ({ tamId, samId, somId }) => {
  const tam = INTERVALOS_TAM.find(o => o.id === tamId);
  const sam = INTERVALOS_SAM.find(o => o.id === samId);
  const som = INTERVALOS_SOM.find(o => o.id === somId);
  if (!tam && !sam && !som) {
    return (
      <div style={{ padding: 16, color: G.txt2, fontSize: 12, fontStyle: 'italic',
        textAlign: 'center' }}>
        Mercado endereçável ainda não dimensionado.
      </div>
    );
  }
  const fmtMi = (v) => v >= 1000 ? `R$ ${(v/1000).toFixed(v/1000 < 10 ? 1 : 0)} bi`
                                  : `R$ ${v.toFixed(0)} mi`;
  const niveis = [
    { sigla: 'TAM', nome: 'Mercado Total',         valor: tam, cor: G.verde },
    { sigla: 'SAM', nome: 'Mercado Endereçável',   valor: sam, cor: G.laranja },
    { sigla: 'SOM', nome: 'Mercado Conquistável',  valor: som, cor: '#B45309' },
  ];
  return (
    <div style={{ width: '100%', background: '#FFF',
      border: `1px solid ${G.borda}`, borderRadius: 0, overflow: 'hidden',
      fontFamily: "'Outfit', sans-serif" }}>
      {/* Cabeçalho */}
      <div style={{
        display: 'grid', gridTemplateColumns: '120px 1fr 1.2fr 1.6fr',
        gap: 14, padding: '10px 18px',
        background: G.fundo, borderBottom: `1px solid ${G.borda}`,
        fontSize: 10, fontWeight: 800, color: G.txt2,
        letterSpacing: '0.10em', textTransform: 'uppercase',
      }}>
        <div>Nível</div>
        <div>Estimativa central</div>
        <div/>
        <div>Faixa &amp; Caracterização</div>
      </div>
      {/* Linhas */}
      {(() => {
        const maxValor = Math.max(...niveis.map(x => x.valor?.meio || 0), 1);
        return niveis.map((n, i) => {
          const propLinear = (n.valor?.meio || 0) / maxValor;
          // Mínimo 8% pra todos ficarem visíveis · escala log suavizada
          const prop = n.valor
            ? Math.max(0.08, propLinear === 1 ? 1 : 0.08 + 0.92 * Math.log10(1 + 9 * propLinear))
            : 0;
          return (
        <div key={i} style={{
          display: 'grid', gridTemplateColumns: '120px 1fr 1.2fr 1.6fr',
          gap: 14, padding: '16px 18px',
          alignItems: 'start',
          borderTop: i === 0 ? 'none' : `1px solid ${G.borda}`,
        }}>
          {/* Coluna 1 · Sigla + nome */}
          <div style={{ gridColumn: '1 / 2' }}>
            <div style={{ fontSize: 22, fontWeight: 900, color: n.cor,
              lineHeight: 1, marginBottom: 4 }}>
              {n.sigla}
            </div>
            <div style={{ fontSize: 10, fontWeight: 700, color: G.txt2,
              letterSpacing: '0.06em', textTransform: 'uppercase' }}>
              {n.nome}
            </div>
          </div>
          {/* Coluna 2 · Estimativa central */}
          <div style={{ gridColumn: '2 / 3', fontSize: 18, fontWeight: 800, color: G.txt1 }}>
            {n.valor ? `~${fmtMi(n.valor.meio)}` : '—'}
          </div>
          {/* Coluna 3 · vazia · liberada para a barra atravessar */}
          <div style={{ gridColumn: '3 / 4' }}/>
          {/* Coluna 4 · Faixa em cima + Caracterização embaixo · alinhamento original à esquerda */}
          <div style={{ gridColumn: '4 / 5' }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: G.txt1, marginBottom: 4 }}>
              {n.valor ? n.valor.label : '— não dimensionado —'}
            </div>
            <div style={{ fontSize: 12, color: G.txt2, fontStyle: 'italic',
              lineHeight: 1.4 }}>
              {n.valor ? n.valor.desc : ''}
            </div>
          </div>
          {/* Barra proporcional · atravessa colunas 1-3 · espessura discreta */}
          <div style={{ gridColumn: '1 / 4', marginTop: 8 }}>
            <div style={{ width: '100%', height: 4, background: G.borda, borderRadius: 0 }}>
              <div style={{ width: `${Math.round(prop * 100)}%`, height: '100%',
                background: n.cor, borderRadius: 0 }}/>
            </div>
          </div>
        </div>
          );
        });
      })()}
    </div>
  );
};

// Donut de Mix de Receita (T4) — cores como moldura · legenda em tabela padrão T5
// Paleta com matizes bem afastados pra evitar confusão visual no donut translúcido.
const PALETA_PIZZA = [
  '#1B6B2F', // verde brand (escuro)
  '#F47C3C', // laranja brand
  '#2563EB', // azul royal (vívido · longe do verde)
  '#6B21A8', // roxo profundo (longe do rosa/laranja)
  '#EAB308', // amarelo gold vivo
  '#0F766E', // teal escuro
  '#BE123C', // carmim profundo
  '#78350F', // marrom
  '#334155', // grafite/slate
  '#A3E635', // lima brilhante
];
const GraficoPizzaMix = ({ items, legendaItem }) => {
  const lista = (items || []).filter(i => Number(i.mix) > 0);
  if (lista.length === 0) {
    return (
      <div style={{ padding: 16, color: G.txt2, fontSize: 12, fontStyle: 'italic',
        textAlign: 'center' }}>
        Mix ainda não informado.
      </div>
    );
  }
  const total = lista.reduce((s, i) => s + Number(i.mix), 0);
  // SVG só com o donut (sem legenda interna)
  const TAM = 260, cx = TAM / 2, cy = TAM / 2, r = 110, rInner = 72;
  let acum = 0;
  const fatias = lista.map((it, idx) => {
    const pct = Number(it.mix) / total;
    const a0 = acum * 2 * Math.PI - Math.PI / 2;
    acum += pct;
    const a1 = acum * 2 * Math.PI - Math.PI / 2;
    const largeArc = pct > 0.5 ? 1 : 0;
    const x0 = cx + r * Math.cos(a0), y0 = cy + r * Math.sin(a0);
    const x1 = cx + r * Math.cos(a1), y1 = cy + r * Math.sin(a1);
    const x0i = cx + rInner * Math.cos(a0), y0i = cy + rInner * Math.sin(a0);
    const x1i = cx + rInner * Math.cos(a1), y1i = cy + rInner * Math.sin(a1);
    const path = `M ${x0} ${y0} A ${r} ${r} 0 ${largeArc} 1 ${x1} ${y1} L ${x1i} ${y1i} A ${rInner} ${rInner} 0 ${largeArc} 0 ${x0i} ${y0i} Z`;
    return { path, cor: PALETA_PIZZA[idx % PALETA_PIZZA.length], item: it, pct };
  });
  return (
    <div style={{ width: '100%', background: '#FFF',
      border: `1px solid ${G.borda}`, borderRadius: 0, overflow: 'hidden',
      fontFamily: "'Outfit', sans-serif" }}>
      <div style={{
        display: 'grid', gridTemplateColumns: `${TAM + 20}px 1fr`, gap: 24,
        padding: '18px', alignItems: 'center',
      }}>
        {/* Esquerda · donut */}
        <div>
          <svg viewBox={`0 0 ${TAM} ${TAM}`}
            preserveAspectRatio="xMidYMid meet"
            style={{ display: 'block', width: '100%', height: 'auto', maxWidth: TAM }}>
            {fatias.map((f, i) => (
              <path key={i} d={f.path}
                fill={f.cor} fillOpacity="0.35"
                stroke={f.cor} strokeWidth="3"/>
            ))}
            <text x={cx} y={cy - 6} textAnchor="middle"
              fontFamily="Outfit, sans-serif" fontSize="10" fontWeight="800"
              fill={G.txt2} letterSpacing="0.10em">
              RECEITA TOTAL
            </text>
            <text x={cx} y={cy + 16} textAnchor="middle"
              fontFamily="Outfit, sans-serif" fontSize="22" fontWeight="900"
              fill={G.txt1}>
              {total.toFixed(0)}%
            </text>
          </svg>
        </div>
        {/* Direita · legenda em tabela padrão T5 */}
        <div>
          {/* Cabeçalho da legenda */}
          <div style={{
            display: 'grid', gridTemplateColumns: '24px 1fr 70px',
            gap: 10, padding: '6px 0',
            borderBottom: `1px solid ${G.borda}`,
            fontSize: 10, fontWeight: 800, color: G.txt2,
            letterSpacing: '0.10em', textTransform: 'uppercase',
            marginBottom: 4,
          }}>
            <div/>
            <div>Produto / Serviço</div>
            <div style={{ textAlign: 'right' }}>% Receita</div>
          </div>
          {fatias.map((f, i) => (
            <div key={i} style={{
              display: 'grid', gridTemplateColumns: '24px 1fr 70px',
              gap: 10, padding: '10px 0', alignItems: 'center',
              borderTop: i === 0 ? 'none' : `1px solid ${G.borda}`,
            }}>
              {/* Moldura colorida (mesmo estilo do donut) */}
              <div style={{
                width: 18, height: 18, borderRadius: 0,
                background: f.cor + '5A', // ~35% alpha (hex AA)
                border: `2px solid ${f.cor}`, boxSizing: 'border-box',
              }}/>
              {/* Nome */}
              <div style={{ fontSize: 13, fontWeight: 700, color: G.txt1 }}>
                {(legendaItem ? legendaItem(f.item) : f.item.nome)}
              </div>
              {/* % */}
              <div style={{ fontSize: 14, fontWeight: 800, color: f.cor,
                textAlign: 'right' }}>
                {Number(f.item.mix).toFixed(0)}%
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// Economia Unitária do Cliente (T7 Bloco 3) — CAC · LTV · LTV/CAC
// Calcula a partir das 5 respostas qualitativas (valor médio · `meio` de cada opção).
const GraficoUnitEconomics = ({ respostas }) => {
  if (!respostas || Object.keys(respostas).length < 5) {
    return (
      <div style={{ padding: 16, color: G.txt2, fontSize: 12, fontStyle: 'italic',
        textAlign: 'center' }}>
        Responda as 5 perguntas para ver a economia unitária.
      </div>
    );
  }
  const meio = (perguntaId) => {
    const p = PERGUNTAS_UNIT_ECON.find(q => q.id === perguntaId);
    const o = p?.opcoes.find(x => x.id === respostas[perguntaId]);
    return o?.meio || 0;
  };
  const novos    = meio('novos_clientes_mes');
  const gasto    = meio('gasto_marketing_mes');
  const ticket   = meio('ticket_medio');
  const vida     = meio('vida_util_meses');
  const margem   = meio('margem_media');
  if (!novos || !gasto || !ticket || !vida || !margem) {
    return (
      <div style={{ padding: 16, color: G.txt2, fontSize: 12, fontStyle: 'italic',
        textAlign: 'center' }}>
        Faltam respostas para calcular.
      </div>
    );
  }
  const CAC = gasto / novos;
  const LTV = ticket * vida * (margem / 100);
  const ratio = LTV / CAC;
  // Caracterização do LTV/CAC
  let faixa, faixaCor, faixaDesc;
  if (ratio < 1)       { faixa = 'Insustentável'; faixaCor = G.vermelho;
    faixaDesc = 'Custo de aquisição supera o valor gerado · revisar canais e ticket.'; }
  else if (ratio < 3)  { faixa = 'Atenção';       faixaCor = '#B45309';
    faixaDesc = 'Margem operacional baixa · explorar redução de CAC ou aumento de LTV.'; }
  else if (ratio < 5)  { faixa = 'Saudável';      faixaCor = G.verde;
    faixaDesc = 'Patamar de mercado · sustenta crescimento orgânico.'; }
  else                 { faixa = 'Excelente';     faixaCor = G.verde;
    faixaDesc = 'Forte alavancagem · possibilita acelerar investimento em aquisição.'; }
  const fmtR$ = (v) => v >= 1000 ? `R$ ${(v/1000).toFixed(v/1000 < 10 ? 1 : 0)} mil`
                                  : `R$ ${v.toFixed(0)}`;
  const linhas = [
    { sigla: 'CAC', nome: 'Custo de Aquisição',
      valor: fmtR$(CAC), prop: 1.0, cor: G.laranja,
      faixaTxt: 'Custo por novo cliente',
      desc: `${fmtR$(gasto)} / mês ÷ ${novos.toFixed(0)} novos clientes` },
    { sigla: 'LTV', nome: 'Valor por Cliente',
      valor: fmtR$(LTV), prop: Math.min(1, LTV / (CAC * 6) || 0.05), cor: G.verde,
      faixaTxt: 'Receita marginal ao longo da vida do cliente',
      desc: `${fmtR$(ticket)} × ${vida.toFixed(0)} meses × ${margem.toFixed(0)}% margem` },
    { sigla: 'LTV/CAC', nome: 'Saúde da Economia',
      valor: `${ratio.toFixed(1)}×`, prop: Math.min(1, ratio / 6),
      cor: faixaCor,
      faixaTxt: `${faixa} · ${ratio.toFixed(1)}× retorno sobre cada R$ investido em aquisição`,
      desc: faixaDesc },
  ];
  return (
    <div style={{ width: '100%', background: '#FFF',
      border: `1px solid ${G.borda}`, borderRadius: 0, overflow: 'hidden',
      fontFamily: "'Outfit', sans-serif" }}>
      <div style={{
        display: 'grid', gridTemplateColumns: '160px 1fr 1.2fr 1.6fr',
        gap: 14, padding: '10px 18px',
        background: G.fundo, borderBottom: `1px solid ${G.borda}`,
        fontSize: 10, fontWeight: 800, color: G.txt2,
        letterSpacing: '0.10em', textTransform: 'uppercase',
      }}>
        <div>Indicador</div>
        <div>Valor</div>
        <div/>
        <div>Caracterização</div>
      </div>
      {linhas.map((l, i) => (
        <div key={i} style={{
          display: 'grid', gridTemplateColumns: '160px 1fr 1.2fr 1.6fr',
          gap: 14, padding: '14px 18px',
          alignItems: 'start',
          borderTop: i === 0 ? 'none' : `1px solid ${G.borda}`,
        }}>
          <div style={{ gridColumn: '1 / 2' }}>
            <div style={{ fontSize: 20, fontWeight: 900, color: l.cor,
              lineHeight: 1, marginBottom: 4 }}>
              {l.sigla}
            </div>
            <div style={{ fontSize: 10, fontWeight: 700, color: G.txt2,
              letterSpacing: '0.06em', textTransform: 'uppercase' }}>
              {l.nome}
            </div>
          </div>
          <div style={{ gridColumn: '2 / 3', fontSize: 18, fontWeight: 800, color: G.txt1 }}>
            {l.valor}
          </div>
          <div style={{ gridColumn: '3 / 4' }}/>
          <div style={{ gridColumn: '4 / 5' }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: G.txt1, marginBottom: 4 }}>
              {l.faixaTxt}
            </div>
            <div style={{ fontSize: 12, color: G.txt2, fontStyle: 'italic',
              lineHeight: 1.4 }}>
              {l.desc}
            </div>
          </div>
          <div style={{ gridColumn: '1 / 4', marginTop: 8 }}>
            <div style={{ width: '100%', height: 4, background: G.borda, borderRadius: 0 }}>
              <div style={{ width: `${Math.round(l.prop * 100)}%`, height: '100%',
                background: l.cor, borderRadius: 0 }}/>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════
// GRÁFICOS T14 — Valuation DCF
// ═══════════════════════════════════════════════════════════════════════

// Composição do Enterprise Value — barra horizontal segmentada
const GraficoComposicaoEV = ({ valuation }) => {
  if (!valuation || valuation.ev <= 0) {
    return (
      <div style={{ padding: 16, color: G.txt2, fontSize: 12,
        fontStyle: 'italic', textAlign: 'center' }}>
        Valuation indisponível com as premissas atuais.
      </div>
    );
  }
  const { fcfDescTotal, vtDescontado, ev, pctFCF, pctVT } = valuation;
  const corFCF = G.laranja, corVT = '#7C3AED';
  const fmt = (v) => v >= 1000000 ? `R$ ${(v/1000000).toFixed(2)} mi` : `R$ ${(v/1000).toFixed(0)} mil`;
  return (
    <div style={{ width: '100%', background: '#FFF',
      border: `1px solid ${G.borda}`, borderRadius: 0,
      padding: '20px 24px', fontFamily: "'Outfit', sans-serif" }}>
      <div style={{ display: 'flex', justifyContent: 'space-between',
        alignItems: 'baseline', marginBottom: 16 }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: G.txt2,
          letterSpacing: '0.08em', textTransform: 'uppercase' }}>
          Enterprise Value
        </div>
        <div style={{ fontSize: 22, fontWeight: 800, color: G.verde,
          fontVariantNumeric: 'tabular-nums', letterSpacing: '-0.02em' }}>
          {fmt(ev)}
        </div>
      </div>
      <div style={{ display: 'flex', height: 36, borderRadius: 0,
        overflow: 'hidden', border: `1px solid ${G.borda}` }}>
        <div style={{ width: `${pctFCF * 100}%`, background: corFCF,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          color: '#FFF', fontSize: 12, fontWeight: 700 }}>
          {pctFCF >= 0.15 && `${(pctFCF*100).toFixed(0)}%`}
        </div>
        <div style={{ width: `${pctVT * 100}%`, background: corVT,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          color: '#FFF', fontSize: 12, fontWeight: 700 }}>
          {pctVT >= 0.15 && `${(pctVT*100).toFixed(0)}%`}
        </div>
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between',
        gap: 16, marginTop: 14 }}>
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8,
            marginBottom: 4 }}>
            <span style={{ width: 12, height: 12, background: corFCF,
              borderRadius: 0 }}/>
            <span style={{ fontSize: 11, fontWeight: 700, color: G.txt2,
              letterSpacing: '0.06em', textTransform: 'uppercase' }}>
              FCF descontado (Anos 1-5)
            </span>
          </div>
          <div style={{ fontSize: 16, fontWeight: 800, color: G.txt1,
            fontVariantNumeric: 'tabular-nums' }}>
            {fmt(fcfDescTotal)}
          </div>
        </div>
        <div style={{ flex: 1, textAlign: 'right' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8,
            marginBottom: 4, justifyContent: 'flex-end' }}>
            <span style={{ fontSize: 11, fontWeight: 700, color: G.txt2,
              letterSpacing: '0.06em', textTransform: 'uppercase' }}>
              Valor Terminal descontado
            </span>
            <span style={{ width: 12, height: 12, background: corVT,
              borderRadius: 0 }}/>
          </div>
          <div style={{ fontSize: 16, fontWeight: 800, color: G.txt1,
            fontVariantNumeric: 'tabular-nums' }}>
            {fmt(vtDescontado)}
          </div>
        </div>
      </div>
    </div>
  );
};

// Cascata DCF — barras horizontais por período: FCF nominal vs descontado
const GraficoCascataDCF = ({ valuation }) => {
  if (!valuation || !valuation.fcfDescontado?.length) return null;
  const itens = [
    ...valuation.fcfDescontado.map(x => ({
      label: x.ano, nominal: x.fcf, descontado: x.fcfDesc,
    })),
    { label: 'Valor Terminal', nominal: valuation.vt, descontado: valuation.vtDescontado, isVT: true },
  ];
  const max = Math.max(...itens.map(i => Math.max(Math.abs(i.nominal), Math.abs(i.descontado))));
  const fmt = (v) => Math.abs(v) >= 1000000 ? `R$ ${(v/1000000).toFixed(2)} mi` : `R$ ${(v/1000).toFixed(0)} mil`;
  return (
    <div style={{ width: '100%', background: '#FFF',
      border: `1px solid ${G.borda}`, borderRadius: 0,
      fontFamily: "'Outfit', sans-serif" }}>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr style={{ borderBottom: `1px solid ${G.borda}` }}>
            <th style={{ padding: '10px 14px', textAlign: 'left',
              fontSize: 10, fontWeight: 700, color: G.txt2,
              letterSpacing: '0.08em', textTransform: 'uppercase', width: 130 }}>
              Período
            </th>
            <th style={{ padding: '10px 14px', textAlign: 'left',
              fontSize: 10, fontWeight: 700, color: G.txt2,
              letterSpacing: '0.08em', textTransform: 'uppercase' }}>
              FCF nominal
            </th>
            <th style={{ padding: '10px 14px', textAlign: 'left',
              fontSize: 10, fontWeight: 700, color: G.txt2,
              letterSpacing: '0.08em', textTransform: 'uppercase' }}>
              FCF descontado
            </th>
          </tr>
        </thead>
        <tbody>
          {itens.map((it, i) => {
            const pctN = max > 0 ? Math.abs(it.nominal) / max * 100 : 0;
            const pctD = max > 0 ? Math.abs(it.descontado) / max * 100 : 0;
            return (
              <tr key={i} style={{
                borderTop: i === 0 ? 'none' : `1px solid ${G.borda}`,
                background: it.isVT ? '#FFFBEB' : 'transparent',
              }}>
                <td style={{ padding: '12px 14px', fontSize: 13,
                  fontWeight: it.isVT ? 800 : 700, color: G.txt1 }}>
                  {it.label}
                </td>
                <td style={{ padding: '12px 14px', verticalAlign: 'middle' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <div style={{ flex: 1, height: 6, background: G.fundo,
                      borderRadius: 0, overflow: 'hidden' }}>
                      <div style={{ height: '100%', width: `${pctN}%`,
                        background: G.txt2, opacity: it.nominal < 0 ? 0.45 : 0.7,
                        borderRadius: 0 }}/>
                    </div>
                    <div style={{ fontSize: 12, color: it.nominal < 0 ? G.vermelho : G.txt1,
                      fontVariantNumeric: 'tabular-nums', minWidth: 110,
                      textAlign: 'right', fontWeight: 600 }}>
                      {fmt(it.nominal)}
                    </div>
                  </div>
                </td>
                <td style={{ padding: '12px 14px', verticalAlign: 'middle' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <div style={{ flex: 1, height: 6, background: G.fundo,
                      borderRadius: 0, overflow: 'hidden' }}>
                      <div style={{ height: '100%', width: `${pctD}%`,
                        background: it.isVT ? '#7C3AED' : G.verde,
                        opacity: it.descontado < 0 ? 0.45 : 1,
                        borderRadius: 0 }}/>
                    </div>
                    <div style={{ fontSize: 12, color: it.descontado < 0 ? G.vermelho : G.txt1,
                      fontVariantNumeric: 'tabular-nums', minWidth: 110,
                      textAlign: 'right', fontWeight: 700 }}>
                      {fmt(it.descontado)}
                    </div>
                  </div>
                </td>
              </tr>
            );
          })}
          <tr style={{ borderTop: `2px solid ${G.verde}`, background: G.verdeLight }}>
            <td style={{ padding: '12px 14px', fontSize: 13, fontWeight: 800, color: G.verde }}>
              Enterprise Value
            </td>
            <td/>
            <td style={{ padding: '12px 14px', fontSize: 15, color: G.verde,
              textAlign: 'right', fontWeight: 800, fontVariantNumeric: 'tabular-nums' }}>
              {fmt(valuation.ev)}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
};

// Sensibilidade WACC × g — mini-heatmap 4×4
const GraficoSensibilidadeWACCg = ({ matriz }) => {
  if (!matriz?.length) return null;
  const valores = matriz.flat();
  const min = Math.min(...valores);
  const max = Math.max(...valores);
  const range = max - min || 1;
  const cor = (v) => {
    const pct = (v - min) / range;
    // gradiente lilás → laranja → verde
    if (pct < 0.5) {
      const p = pct / 0.5;
      const r = Math.round(124 + (244 - 124) * p);
      const gr = Math.round(58 + (124 - 58) * p);
      const b = Math.round(237 + (60 - 237) * p);
      return `rgb(${r},${gr},${b})`;
    }
    const p = (pct - 0.5) / 0.5;
    const r = Math.round(244 + (27 - 244) * p);
    const gr = Math.round(124 + (107 - 124) * p);
    const b = Math.round(60 + (47 - 60) * p);
    return `rgb(${r},${gr},${b})`;
  };
  const fmt = (v) => v >= 1000000 ? `${(v/1000000).toFixed(1)}mi` : `${(v/1000).toFixed(0)}k`;
  return (
    <div style={{ width: '100%', background: '#FFF',
      border: `1px solid ${G.borda}`, borderRadius: 0,
      padding: '18px 18px', fontFamily: "'Outfit', sans-serif" }}>
      <div style={{ display: 'grid',
        gridTemplateColumns: '80px repeat(4, 1fr)', gap: 4,
        alignItems: 'center' }}>
        <div/>
        {G_PERPETUIDADE.map(gp => (
          <div key={gp.id} style={{ fontSize: 10, fontWeight: 700,
            color: G.txt2, letterSpacing: '0.06em', textTransform: 'uppercase',
            textAlign: 'center' }}>
            g · {gp.label}
          </div>
        ))}
        {WACC_INTERVALOS.map((w, i) => (
          <React.Fragment key={w.id}>
            <div style={{ fontSize: 10, fontWeight: 700, color: G.txt2,
              letterSpacing: '0.06em', textTransform: 'uppercase',
              textAlign: 'right', paddingRight: 8 }}>
              WACC<br/>{w.label}
            </div>
            {matriz[i].map((v, j) => (
              <div key={j} style={{
                background: cor(v), color: '#FFF',
                padding: '12px 4px', borderRadius: 0,
                fontSize: 12, fontWeight: 700, textAlign: 'center',
                fontVariantNumeric: 'tabular-nums',
              }}>
                R$ {fmt(v)}
              </div>
            ))}
          </React.Fragment>
        ))}
      </div>
      <div style={{ marginTop: 12, fontSize: 11, color: G.txt2,
        fontStyle: 'italic', lineHeight: 1.45 }}>
        Cada célula = Enterprise Value sob aquela combinação WACC × g · cores
        de lilás (menor) a verde (maior).
      </div>
    </div>
  );
};

// Comparativo de métodos de Valor Terminal — tabela com barras horizontais
const GraficoComparativoMetodos = ({ valuation, multiplo }) => {
  if (!valuation?.evPorMetodo) return null;
  const linhas = [
    { id: 'gordon',   label: 'Perpetuidade Gordon',  cor: '#7C3AED', vt: valuation.vtPorMetodo.gordon,   ev: valuation.evPorMetodo.gordon },
    { id: 'multiplo', label: `Múltiplo ${multiplo}× EBITDA`, cor: G.laranja, vt: valuation.vtPorMetodo.multiplo, ev: valuation.evPorMetodo.multiplo },
    { id: 'hibrido',  label: 'Híbrido (média)',       cor: G.verde,   vt: valuation.vtPorMetodo.hibrido,   ev: valuation.evPorMetodo.hibrido },
  ];
  const maxVT = Math.max(...linhas.map(l => Math.abs(l.vt)));
  const maxEV = Math.max(...linhas.map(l => Math.abs(l.ev)));
  const fmt = (v) => Math.abs(v) >= 1000000 ? `R$ ${(v/1000000).toFixed(2)} mi` : `R$ ${(v/1000).toFixed(0)} mil`;
  return (
    <div style={{ width: '100%', background: '#FFF',
      border: `1px solid ${G.borda}`, borderRadius: 0,
      fontFamily: "'Outfit', sans-serif" }}>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr style={{ borderBottom: `1px solid ${G.borda}` }}>
            <th style={{ padding: '12px 16px', textAlign: 'left',
              fontSize: 10, fontWeight: 700, color: G.txt2,
              letterSpacing: '0.08em', textTransform: 'uppercase' }}>
              Método
            </th>
            <th style={{ padding: '12px 16px', textAlign: 'left',
              fontSize: 10, fontWeight: 700, color: G.txt2,
              letterSpacing: '0.08em', textTransform: 'uppercase', width: '35%' }}>
              Valor Terminal
            </th>
            <th style={{ padding: '12px 16px', textAlign: 'left',
              fontSize: 10, fontWeight: 700, color: G.txt2,
              letterSpacing: '0.08em', textTransform: 'uppercase', width: '35%' }}>
              Enterprise Value
            </th>
          </tr>
        </thead>
        <tbody>
          {linhas.map((l, i) => {
            const pctVT = maxVT > 0 ? Math.abs(l.vt) / maxVT * 100 : 0;
            const pctEV = maxEV > 0 ? Math.abs(l.ev) / maxEV * 100 : 0;
            return (
              <tr key={i} style={{ borderTop: i === 0 ? 'none' : `1px solid ${G.borda}` }}>
                <td style={{ padding: '14px 16px 14px 14px',
                  borderLeft: `4px solid ${l.cor}`, verticalAlign: 'middle' }}>
                  <div style={{ fontSize: 13, fontWeight: 800, color: l.cor,
                    letterSpacing: '0.04em' }}>
                    {l.label}
                  </div>
                </td>
                <td style={{ padding: '14px 16px', verticalAlign: 'middle' }}>
                  <div style={{ fontSize: 15, fontWeight: 800, color: G.txt1,
                    fontVariantNumeric: 'tabular-nums', marginBottom: 6 }}>
                    {fmt(l.vt)}
                  </div>
                  <div style={{ height: 5, background: G.fundo,
                    borderRadius: 0, overflow: 'hidden' }}>
                    <div style={{ height: '100%', width: `${pctVT}%`,
                      background: l.cor, borderRadius: 0 }}/>
                  </div>
                </td>
                <td style={{ padding: '14px 16px', verticalAlign: 'middle' }}>
                  <div style={{ fontSize: 15, fontWeight: 800, color: G.txt1,
                    fontVariantNumeric: 'tabular-nums', marginBottom: 6 }}>
                    {fmt(l.ev)}
                  </div>
                  <div style={{ height: 5, background: G.fundo,
                    borderRadius: 0, overflow: 'hidden' }}>
                    <div style={{ height: '100%', width: `${pctEV}%`,
                      background: l.cor, borderRadius: 0 }}/>
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

// Evolução 5 anos (T13) — linhas contínuas: Receita · EBITDA · FCF por ano
const GraficoProjecao5Anos = ({ anos }) => {
  if (!anos || anos.length === 0) {
    return (
      <div style={{ padding: 16, color: G.txt2, fontSize: 12, fontStyle: 'italic',
        textAlign: 'center' }}>
        Defina as premissas para gerar a projeção.
      </div>
    );
  }
  const W = 720, H = 340;
  const margemL = 60, margemR = 90, margemT = 24, margemB = 56;
  const areaW = W - margemL - margemR;
  const areaH = H - margemT - margemB;
  const max = Math.max(...anos.map(a => Math.max(
    a.receita,
    (a.custos || 0) + (a.despesas || 0),
    a.ebitda,
    Math.max(0, a.fcf)
  ))) * 1.1;
  const min = Math.min(0, ...anos.map(a => a.fcf)) * 1.1;
  const range = max - min || 1;
  const passo = areaW / Math.max(1, anos.length - 1);
  const cores = { receita: G.verde, despesas: '#2563EB', ebitda: G.laranja, fcf: '#7C3AED' };
  const fmtY = (v) => v >= 1000000 ? `${(v/1000000).toFixed(1)}mi` : v >= 1000 ? `${(v/1000).toFixed(0)}k` : v.toFixed(0);
  const yPara = (v) => margemT + areaH * (1 - (v - min) / range);
  const xPara = (i) => margemL + passo * i;
  const series = [
    { key: 'receita',  label: 'Receita',  cor: cores.receita,  pontos: anos.map(a => a.receita) },
    { key: 'despesas', label: 'Despesas', cor: cores.despesas, pontos: anos.map(a => (a.custos || 0) + (a.despesas || 0)) },
    { key: 'ebitda',   label: 'EBITDA',   cor: cores.ebitda,   pontos: anos.map(a => a.ebitda) },
    { key: 'fcf',      label: 'FCF',      cor: cores.fcf,      pontos: anos.map(a => a.fcf) },
  ];
  return (
    <div style={{ width: '100%' }}>
      <svg viewBox={`0 0 ${W} ${H}`}
        preserveAspectRatio="xMidYMid meet"
        style={{ display: 'block', width: '100%', height: 'auto', maxWidth: '100%' }}>
        {/* Grade horizontal */}
        {[0, 0.25, 0.5, 0.75, 1].map((p, i) => {
          const y = margemT + areaH * (1 - p);
          const val = min + range * p;
          return (
            <g key={i}>
              <line x1={margemL} y1={y} x2={W - margemR} y2={y}
                stroke={G.borda} strokeWidth="0.5"/>
              <text x={margemL - 6} y={y + 4} textAnchor="end"
                fontFamily="Outfit, sans-serif" fontSize="10" fill={G.txt2}>
                R$ {fmtY(val)}
              </text>
            </g>
          );
        })}
        {/* Eixo X linha base (zero) */}
        <line x1={margemL} y1={yPara(0)} x2={W - margemR} y2={yPara(0)}
          stroke={G.txt2} strokeWidth="1"/>
        {/* Rótulos do eixo X (períodos) */}
        {anos.map((a, i) => (
          <text key={i} x={xPara(i)} y={margemT + areaH + 18} textAnchor="middle"
            fontFamily="Outfit, sans-serif" fontSize="11" fontWeight="700"
            fill={G.txt2}>
            {a.ano}
          </text>
        ))}
        {/* Linhas */}
        {series.map((s, si) => {
          const path = s.pontos
            .map((v, i) => `${i === 0 ? 'M' : 'L'} ${xPara(i)} ${yPara(v)}`)
            .join(' ');
          const ultimo = s.pontos[s.pontos.length - 1];
          const xUlt = xPara(s.pontos.length - 1);
          const yUlt = yPara(ultimo);
          return (
            <g key={si}>
              <path d={path} fill="none" stroke={s.cor} strokeWidth="2.5"
                strokeLinecap="round" strokeLinejoin="round"/>
              {s.pontos.map((v, i) => (
                <circle key={i} cx={xPara(i)} cy={yPara(v)} r={4}
                  fill="#FFF" stroke={s.cor} strokeWidth="2"/>
              ))}
              {/* Rótulo no ponto final */}
              <text x={xUlt + 10} y={yUlt + 4}
                fontFamily="Outfit, sans-serif" fontSize="11" fontWeight="700"
                fill={s.cor}>
                R$ {fmtY(ultimo)}
              </text>
            </g>
          );
        })}
        {/* Legenda */}
        <g transform={`translate(${margemL}, ${H - 18})`}>
          {series.map((l, i) => (
            <g key={i} transform={`translate(${i * 95}, 0)`}>
              <line x1={0} y1={-2} x2={18} y2={-2}
                stroke={l.cor} strokeWidth="2.5" strokeLinecap="round"/>
              <circle cx={9} cy={-2} r={4} fill="#FFF" stroke={l.cor} strokeWidth="2"/>
              <text x={26} y={2} fontFamily="Outfit, sans-serif"
                fontSize="11" fontWeight="600" fill={G.txt1}>
                {l.label}
              </text>
            </g>
          ))}
        </g>
      </svg>
    </div>
  );
};

// Comparativo de cenários T13 — tabela elegante 3 linhas × 3 colunas
// com barras horizontais proporcionais (padrão T5 TAM/SAM/SOM)
const GraficoCenarios5 = ({ anoFinal }) => {
  if (!anoFinal) return null;
  const cenarios = CENARIOS_PROJ.map(c => ({
    label: c.label, cor: c.cor, ajuste: c.ajuste,
    receita: anoFinal.receita * (1 + c.ajuste),
    ebitda:  anoFinal.ebitda  * (1 + c.ajuste),
    fcf:     anoFinal.fcf     * (1 + c.ajuste),
  }));
  const colunas = [
    { key: 'receita', label: 'Receita' },
    { key: 'ebitda',  label: 'EBITDA'  },
    { key: 'fcf',     label: 'FCF'     },
  ];
  const maxPorCol = {};
  colunas.forEach(c => {
    maxPorCol[c.key] = Math.max(...cenarios.map(s => Math.abs(s[c.key])));
  });
  const fmtMi = (v) => {
    const abs = Math.abs(v);
    if (abs >= 1000000) return `R$ ${(v/1000000).toFixed(2)} mi`;
    return `R$ ${(v/1000).toFixed(0)} mil`;
  };
  return (
    <div style={{ width: '100%', background: '#FFF',
      border: `1px solid ${G.borda}`, borderRadius: 0,
      fontFamily: "'Outfit', sans-serif" }}>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr style={{ borderBottom: `1px solid ${G.borda}` }}>
            <th style={{ padding: '12px 16px', textAlign: 'left',
              fontSize: 10, fontWeight: 700, color: G.txt2,
              letterSpacing: '0.08em', textTransform: 'uppercase' }}>
              Cenário
            </th>
            {colunas.map(c => (
              <th key={c.key} style={{ padding: '12px 16px', textAlign: 'left',
                fontSize: 10, fontWeight: 700, color: G.txt2,
                letterSpacing: '0.08em', textTransform: 'uppercase',
                width: '28%' }}>
                {c.label} · Ano 5
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {cenarios.map((s, i) => (
            <tr key={i} style={{ borderTop: i === 0 ? 'none' : `1px solid ${G.borda}` }}>
              <td style={{ padding: '16px 16px 16px 14px',
                borderLeft: `4px solid ${s.cor}`, verticalAlign: 'middle' }}>
                <div style={{ fontSize: 13, fontWeight: 800, color: s.cor,
                  letterSpacing: '0.06em', textTransform: 'uppercase' }}>
                  {s.label}
                </div>
                {s.ajuste !== 0 && (
                  <div style={{ fontSize: 11, color: G.txt2, marginTop: 2 }}>
                    {s.ajuste > 0 ? '+' : ''}{Math.round(s.ajuste*100)}% vs Base
                  </div>
                )}
              </td>
              {colunas.map(c => {
                const v = s[c.key];
                const max = maxPorCol[c.key] || 1;
                const pct = max > 0 ? (Math.abs(v) / max) * 100 : 0;
                const negativo = v < 0;
                return (
                  <td key={c.key} style={{ padding: '16px 16px',
                    verticalAlign: 'middle' }}>
                    <div style={{ fontSize: 16, fontWeight: 800,
                      color: negativo ? G.vermelho : G.txt1,
                      fontVariantNumeric: 'tabular-nums',
                      letterSpacing: '-0.01em', marginBottom: 8 }}>
                      {fmtMi(v)}
                    </div>
                    <div style={{ height: 5, background: G.fundo,
                      borderRadius: 0, overflow: 'hidden' }}>
                      <div style={{
                        height: '100%', width: `${pct}%`,
                        background: s.cor, opacity: negativo ? 0.45 : 1,
                        borderRadius: 0,
                      }}/>
                    </div>
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// Trajetória da meta — tabela elegante com 6 linhas (Hoje + Ano 1-5) (T1)
// Padrão alinhado com a T5: rótulo · valor · barra proporcional atravessando · descrição.
const GraficoTrajetoriaMeta = ({ tipoId, valor }) => {
  const v = Number(valor) || 0;
  if (!v) {
    return (
      <div style={{ padding: 16, color: G.txt2, fontSize: 12, fontStyle: 'italic',
        textAlign: 'center' }}>
        Meta ainda não informada.
      </div>
    );
  }
  // Define a série Hoje → Ano 5 conforme o tipo da meta
  let serie, sufixo, baseHoje, unidade;
  if (tipoId === 'ebitda_mult') {
    const fator = Math.pow(v, 1 / 5);
    serie = [0, 1, 2, 3, 4, 5].map(a => 100 * Math.pow(fator, a));
    sufixo = ''; baseHoje = 100; unidade = 'EBITDA (base 100)';
  } else if (tipoId === 'receita_mult') {
    const fator = Math.pow(v, 1 / 5);
    serie = [0, 1, 2, 3, 4, 5].map(a => 100 * Math.pow(fator, a));
    sufixo = ''; baseHoje = 100; unidade = 'Receita (base 100)';
  } else if (tipoId === 'margem_alvo') {
    const inicial = 10;
    serie = [0, 1, 2, 3, 4, 5].map(a => inicial + (v - inicial) * (a / 5));
    sufixo = '%'; baseHoje = inicial; unidade = 'Margem EBITDA recorrente';
  } else {
    const fator = Math.pow(v, 1 / 5);
    serie = [0, 1, 2, 3, 4, 5].map(a => Math.pow(fator, a));
    sufixo = '× hoje'; baseHoje = 1; unidade = 'Valor da empresa (múltiplo do valor atual)';
  }
  const rotulos = ['Hoje', 'Ano 1', 'Ano 2', 'Ano 3', 'Ano 4', 'Ano 5'];
  const max = serie[serie.length - 1];
  return (
    <div style={{ width: '100%', background: '#FFF',
      border: `1px solid ${G.borda}`, borderRadius: 0, overflow: 'hidden',
      fontFamily: "'Outfit', sans-serif" }}>
      {/* Cabeçalho */}
      <div style={{
        display: 'grid', gridTemplateColumns: '120px 1fr 1.2fr 1.6fr',
        gap: 14, padding: '10px 18px',
        background: G.fundo, borderBottom: `1px solid ${G.borda}`,
        fontSize: 10, fontWeight: 800, color: G.txt2,
        letterSpacing: '0.10em', textTransform: 'uppercase',
      }}>
        <div>Período</div>
        <div>Valor projetado</div>
        <div/>
        <div>Posição &amp; Marco</div>
      </div>
      {/* Linhas */}
      {serie.map((val, i) => {
        const prop = val / max;
        const ehHoje = i === 0;
        const ehAno5 = i === serie.length - 1;
        const cor = ehAno5 ? G.verde : G.laranja;
        const fmtVal = `${val.toFixed(val < 10 ? 1 : 0)}${sufixo === '%' ? '%' : sufixo === '× hoje' ? '×' : ''}`;
        const variacao = ehHoje
          ? 'Ponto de partida'
          : `+${(((val - baseHoje) / baseHoje) * 100).toFixed(0)}% sobre Hoje`;
        const marco = ehHoje ? 'Base de cálculo'
                    : ehAno5 ? 'Meta-alvo do Business Plan'
                    : `${i} ${i === 1 ? 'ano' : 'anos'} no plano`;
        return (
          <div key={i} style={{
            display: 'grid', gridTemplateColumns: '120px 1fr 1.2fr 1.6fr',
            gap: 14, padding: '14px 18px',
            alignItems: 'start',
            borderTop: i === 0 ? 'none' : `1px solid ${G.borda}`,
          }}>
            {/* Coluna 1 · Rótulo do período + unidade */}
            <div style={{ gridColumn: '1 / 2' }}>
              <div style={{ fontSize: 18, fontWeight: 900, color: cor,
                lineHeight: 1, marginBottom: 4 }}>
                {rotulos[i]}
              </div>
              <div style={{ fontSize: 10, fontWeight: 700, color: G.txt2,
                letterSpacing: '0.06em', textTransform: 'uppercase' }}>
                {ehHoje ? 'Início' : ehAno5 ? 'Fim do plano' : `t = ${i}`}
              </div>
            </div>
            {/* Coluna 2 · Valor projetado */}
            <div style={{ gridColumn: '2 / 3', fontSize: 18, fontWeight: 800, color: G.txt1 }}>
              {fmtVal}
            </div>
            {/* Coluna 3 · vazia · liberada para a barra atravessar */}
            <div style={{ gridColumn: '3 / 4' }}/>
            {/* Coluna 4 · Variação em cima + Marco embaixo */}
            <div style={{ gridColumn: '4 / 5' }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: G.txt1, marginBottom: 4 }}>
                {variacao}
              </div>
              <div style={{ fontSize: 12, color: G.txt2, fontStyle: 'italic',
                lineHeight: 1.4 }}>
                {marco}
              </div>
            </div>
            {/* Barra proporcional · atravessa colunas 1-3 · espessura discreta */}
            <div style={{ gridColumn: '1 / 4', marginTop: 8 }}>
              <div style={{ width: '100%', height: 4, background: G.borda, borderRadius: 0 }}>
                <div style={{ width: `${Math.round(prop * 100)}%`, height: '100%',
                  background: cor, borderRadius: 0 }}/>
              </div>
            </div>
          </div>
        );
      })}
      {/* Rodapé · unidade da série */}
      <div style={{ padding: '8px 18px', borderTop: `1px solid ${G.borda}`,
        background: G.fundo, fontSize: 10, color: G.txt2, fontStyle: 'italic',
        textAlign: 'center', letterSpacing: '0.04em' }}>
        Série: {unidade}
      </div>
    </div>
  );
};

// Timeline horizontal de contratações 5 anos (Ano 1 → Ano 5)
const GraficoTimelineRH = ({ hires }) => {
  const anos = ['Ano 1', 'Ano 2', 'Ano 3', 'Ano 4', 'Ano 5'];
  const porAno = anos.map(a => ({
    ano: a,
    lista: (hires || []).filter(h => h.ano === a && (h.descricao || '').trim()),
  }));
  if (porAno.every(c => c.lista.length === 0)) {
    return (
      <div style={{ padding: 16, color: G.txt2, fontSize: 12, fontStyle: 'italic',
        textAlign: 'center' }}>
        Plano de contratações ainda não descrito.
      </div>
    );
  }
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 8 }}>
      {porAno.map((c, idx) => (
        <div key={c.ano} style={{
          background: '#FFF', border: `1px solid ${G.borda}`, borderRadius: 0,
          display: 'flex', flexDirection: 'column', overflow: 'hidden',
        }}>
          <div style={{
            padding: '6px 10px', background: G.verde, color: '#FFF',
            fontSize: 11, fontWeight: 800, letterSpacing: '0.06em',
            textTransform: 'uppercase', textAlign: 'center',
            position: 'relative',
          }}>
            {c.ano}
            {idx < 4 && (
              <span style={{
                position: 'absolute', right: -6, top: '50%',
                transform: 'translateY(-50%)',
                color: G.verde, fontSize: 14, fontWeight: 700,
                background: '#FFF', padding: '0 1px', zIndex: 2,
              }}>→</span>
            )}
          </div>
          <div style={{ padding: '8px 10px', fontSize: 11, color: G.txt1, lineHeight: 1.4,
            display: 'flex', flexDirection: 'column', gap: 6, minHeight: 60 }}>
            {c.lista.length === 0
              ? <span style={{ color: G.txt2, fontStyle: 'italic' }}>—</span>
              : c.lista.map((h, i) => <span key={i}>• {h.descricao}</span>)}
          </div>
        </div>
      ))}
    </div>
  );
};

// Títulos padrão das "Ilustrações" (gráficos numerados) no documento compilado
const TITULO_EXHIBIT_POR_TIPO = {
  t1_trajetoria:    'Trajetória da meta · Hoje → Ano 5',
  t13_evolucao:     'Evolução 5 anos · Receita · EBITDA · FCF',
  t13_cenarios:     'Comparativo de cenários · Ano 5',
  t14_composicao:           'Composição do Enterprise Value',
  t14_cascata:              'Cascata DCF · FCF nominal vs descontado',
  t14_sensibilidade:        'Sensibilidade WACC × g · matriz de Enterprise Value',
  t14_comparativo_metodos:  'Comparativo de métodos de Valor Terminal',
  t4_mix_receita:   'Mix de Receita por produto/serviço',
  t5_tam_sam_som:   'Mercado endereçável · TAM · SAM · SOM',
  t6_swot:          'Matriz SWOT · forças · fraquezas · oportunidades · ameaças',
  t7_unit_econ:     'Economia Unitária do Cliente · CAC · LTV · LTV/CAC',
  t8_canais:        'Canais de aquisição · participação no mix',
  t8_funil:         'Funil Marketing & Vendas · conversão por etapa',
  t10_organograma:  'Organograma · estrutura hierárquica',
  t10_timeline_rh:  'Plano de Pessoas · contratações estratégicas Ano 1-5',
};

// <BotaoVisualizarGraficos /> — botão verde "▾ visualizar gráficos e tabelas"
// que acompanha CADA bloco de Tarefa BP60M com elementos visualizáveis.
// Default fechado.
const BotaoVisualizarGraficos = ({ aberto, onToggle, children, rotulo }) => (
  <div style={{ marginTop: 10 }}>
    <button type="button" onClick={onToggle}
      style={{
        padding: '8px 14px', fontSize: 11, fontWeight: 700,
        background: G.verde, color: '#FFF',
        border: `1px solid ${G.verde}`, borderRadius: 0,
        cursor: 'pointer', fontFamily: "'Outfit', sans-serif",
        letterSpacing: '0.08em', textTransform: 'uppercase',
        display: 'inline-flex', alignItems: 'center', gap: 8,
      }}>
      <span>{aberto ? '▴' : '▾'}</span>
      <span>{rotulo || 'Visualizar gráficos e tabelas'}</span>
    </button>
    {aberto && (
      <div style={{ marginTop: 10, padding: '14px',
        background: G.fundo, border: `1px solid ${G.verde}`, borderRadius: 0,
        display: 'flex', flexDirection: 'column', gap: 16 }}>
        {children}
      </div>
    )}
  </div>
);

// <MarkdownPreview /> — renderiza Markdown simples (## · ### · **bold** ·
// listas · parágrafos · _itálico_) sem dependência externa. Suficiente
// pro preview do BP60M compilado.
// ═══════════════════════════════════════════════════════════════════════
const MarkdownPreview = ({ texto, graficos }) => {
  if (!texto) return null;
  const linhas = texto.split('\n');
  const elementos = [];
  let i = 0;
  let chave = 0;
  // Contador de exhibits para numeração tipo "Exhibit N · ..."
  const exhibitsRef = { n: 0 };

  // helper para processar inline **bold** e _italic_
  const inline = (str) => {
    const partes = [];
    let resto = str;
    let kk = 0;
    while (resto.length > 0) {
      const mBold = resto.match(/\*\*(.+?)\*\*/);
      const mItal = resto.match(/_(.+?)_/);
      let m = null, kind = null;
      if (mBold && (!mItal || mBold.index <= mItal.index)) { m = mBold; kind = 'bold'; }
      else if (mItal) { m = mItal; kind = 'ital'; }
      if (!m) { partes.push(resto); break; }
      if (m.index > 0) partes.push(resto.slice(0, m.index));
      partes.push(kind === 'bold'
        ? <strong key={`k${kk++}`}>{m[1]}</strong>
        : <em key={`k${kk++}`}>{m[1]}</em>);
      resto = resto.slice(m.index + m[0].length);
    }
    return partes;
  };

  while (i < linhas.length) {
    const l = linhas[i];
    // Marcador de gráfico: <!--GRAFICO:tipo--> ou <!--GRAFICO:tipo|legenda-->
    const mGrafico = l.match(/^<!--GRAFICO:([a-zA-Z0-9_]+)(?:\|(.+?))?-->$/);
    if (mGrafico) {
      const tipo = mGrafico[1];
      const legendaCustom = mGrafico[2] || null;
      const componente = (graficos && graficos[tipo]) || null;
      exhibitsRef.n += 1;
      const numExhibit = exhibitsRef.n;
      const tituloDefault = TITULO_EXHIBIT_POR_TIPO[tipo] || tipo;
      elementos.push(
        <figure key={chave++} style={{
          margin: '18px 0', padding: '14px',
          background: '#FFF', border: `1px solid ${G.borda}`, borderRadius: 0,
        }}>
          <figcaption style={{
            fontSize: 10, fontWeight: 800, color: G.txt2,
            letterSpacing: '0.10em', textTransform: 'uppercase', marginBottom: 10,
          }}>
            Ilustração {numExhibit} · {legendaCustom || tituloDefault}
          </figcaption>
          {componente || (
            <div style={{ padding: 16, background: G.fundo, color: G.txt2,
              fontSize: 12, fontStyle: 'italic', textAlign: 'center' }}>
              (Gráfico "{tipo}" — dados ainda não disponíveis)
            </div>
          )}
          <div style={{ marginTop: 8, fontSize: 10, color: G.txt2, fontStyle: 'italic',
            letterSpacing: '0.02em' }}>
            Fonte: dados da empresa · análise <MarcaAIdvisor variant="default" />
          </div>
        </figure>
      );
      i++;
      continue;
    }
    if (l.startsWith('## ')) {
      elementos.push(<h2 key={chave++} style={{ fontSize: 22, fontWeight: 800,
        color: G.txt1, margin: '20px 0 10px', borderBottom: `2px solid ${G.verde}`,
        paddingBottom: 6 }}>{inline(l.slice(3))}</h2>);
      i++;
    } else if (l.startsWith('### ')) {
      elementos.push(<h3 key={chave++} style={{ fontSize: 15, fontWeight: 700,
        color: G.verde, margin: '14px 0 6px', letterSpacing: '0.04em',
        textTransform: 'uppercase' }}>{inline(l.slice(4))}</h3>);
      i++;
    } else if (l.startsWith('- ')) {
      const items = [];
      while (i < linhas.length && linhas[i].startsWith('- ')) {
        items.push(<li key={chave++} style={{ marginBottom: 4 }}>{inline(linhas[i].slice(2))}</li>);
        i++;
      }
      elementos.push(<ul key={chave++} style={{ margin: '6px 0 10px 20px',
        fontSize: 14, color: G.txt1, lineHeight: 1.55 }}>{items}</ul>);
    } else if (l.trim() === '') {
      i++;
    } else {
      elementos.push(<p key={chave++} style={{ margin: '0 0 10px',
        fontSize: 14, color: G.txt1, lineHeight: 1.55 }}>{inline(l)}</p>);
      i++;
    }
  }
  return <div>{elementos}</div>;
};

// ═══════════════════════════════════════════════════════════════════════
// <ConteudoTarefa2 /> — Descrição da Empresa (2ª tarefa do BP60M).
// Tutorial + chips Cadastro read-only + 3 blocos editáveis (História ·
// Equipe-chave · Instalações Adicionais). Carrega rascunho heurístico
// hardcoded por empresa, salva edições em localStorage.
// ═══════════════════════════════════════════════════════════════════════
const ConteudoTarefa2 = ({ descricaoEmpresa, setDescricaoEmpresa, empresa, modo = 'EE' }) => {
  const rascunho = RASCUNHO_TAREFA_2[empresa?.id] || { historia: [], equipe: [], instalacoes: [] };

  // Estado efetivo: usa o salvo do usuário se houver, senão o rascunho heurístico
  const historia      = descricaoEmpresa?.historia      ?? rascunho.historia;
  const equipe        = descricaoEmpresa?.equipe        ?? rascunho.equipe;
  const instalacoes   = descricaoEmpresa?.instalacoes   ?? rascunho.instalacoes;

  const atualizar = (campo, valor) => {
    setDescricaoEmpresa(prev => ({
      historia, equipe, instalacoes,   // garante presença das chaves
      ...prev,
      [campo]: valor,
    }));
  };

  // ── Helpers de edição de listas ────────────────────────────────────
  const editarHistoria = (i, k, v) => {
    const nova = historia.map((m, idx) => idx === i ? { ...m, [k]: v } : m);
    atualizar('historia', nova);
  };
  const removerHistoria = (i) => atualizar('historia', historia.filter((_, idx) => idx !== i));
  const adicionarHistoria = () => atualizar('historia', [...historia, { id_catalogo: null, ano: '', marco: '' }]);

  const editarEquipe = (i, k, v) => {
    const nova = equipe.map((m, idx) => idx === i ? { ...m, [k]: v } : m);
    atualizar('equipe', nova);
  };
  const removerEquipe = (i) => atualizar('equipe', equipe.filter((_, idx) => idx !== i));
  const adicionarEquipe = () => atualizar('equipe', [...equipe, { id_catalogo: null, nome: '', funcao: '', bio: '' }]);

  const editarInst = (i, k, v) => {
    const nova = instalacoes.map((m, idx) => idx === i ? { ...m, [k]: v } : m);
    atualizar('instalacoes', nova);
  };
  const removerInst = (i) => atualizar('instalacoes', instalacoes.filter((_, idx) => idx !== i));
  const adicionarInst = () => atualizar('instalacoes', [...instalacoes, { id_catalogo: null, tipo: '', local: '' }]);

  // Catálogo da T2 vem da pesquisa da IA POR EMPRESA (cadastro + site + redes), não por setor
  // Hoje (Fase 1) usa o rascunho hardcoded · na Fase 3 vai vir de pesquisa real
  const catalogoHist  = rascunho.historia     || [];
  const catalogoEq    = rascunho.equipe       || [];
  const catalogoInst  = rascunho.instalacoes  || [];
  const chipHistMarcado = (catId) => historia.some(h => h.id_catalogo === catId);
  const chipEqMarcado   = (catId) => equipe.some(e => e.id_catalogo === catId);
  const chipInstMarcado = (catId) => instalacoes.some(i => i.id_catalogo === catId);
  const toggleChipHist = (catItem) => {
    if (chipHistMarcado(catItem.id_catalogo)) atualizar('historia', historia.filter(h => h.id_catalogo !== catItem.id_catalogo));
    else atualizar('historia', [...historia, catItem]);
  };
  const toggleChipEq = (catItem) => {
    if (chipEqMarcado(catItem.id_catalogo)) atualizar('equipe', equipe.filter(e => e.id_catalogo !== catItem.id_catalogo));
    else atualizar('equipe', [...equipe, catItem]);
  };
  const toggleChipInst = (catItem) => {
    if (chipInstMarcado(catItem.id_catalogo)) atualizar('instalacoes', instalacoes.filter(i => i.id_catalogo !== catItem.id_catalogo));
    else atualizar('instalacoes', [...instalacoes, catItem]);
  };

  // ── Estilos compartilhados ─────────────────────────────────────────
  const inputBase = {
    width: '100%', padding: '8px 12px', fontSize: 13,
    border: `1px solid ${G.borda}`, borderRadius: 0,
    fontFamily: "'Outfit', sans-serif", color: G.txt1,
    background: '#FFF', boxSizing: 'border-box',
  };
  const btnAdd = {
    background: '#FFF', border: `1px dashed ${G.verde}`,
    color: G.verde, padding: '8px 14px',
    fontSize: 11, fontWeight: 700, letterSpacing: '0.06em',
    textTransform: 'uppercase', borderRadius: 0, cursor: 'pointer',
    fontFamily: "'Outfit', sans-serif", marginTop: 8,
  };
  const btnRemover = {
    background: 'transparent', border: 'none', color: G.txt2,
    fontSize: 16, cursor: 'pointer', padding: '2px 6px',
    fontFamily: "'Outfit', sans-serif",
  };
  const HEADER_BLOCO = {
    fontSize: 16, fontWeight: 700, letterSpacing: '0.10em',
    textTransform: 'uppercase', color: G.laranja, marginBottom: 10,
  };
  const HEADER_AUX = {
    fontSize: 12, fontWeight: 400, color: G.txt2,
    textTransform: 'none', letterSpacing: 0, marginLeft: 10,
  };

  // ── Snapshot do Cadastro (chips read-only) ─────────────────────────
  // Em modo Startup, adiciona 2 chips relevantes para investidor de SU
  // (Estágio e Rodadas anteriores). Conteúdo aprovado no Apêndice G · T2.
  const chipsBase = [
    { label: 'CNPJ',       valor: empresa?.cnpj || '—' },
    { label: 'Setor',      valor: empresa?.setor || '—' },
    { label: 'Sede',       valor: empresa?.endereco_cidade || empresa?.endereco || 'São Paulo · SP' },
    { label: 'Capital',    valor: empresa?.capital_social ? `R$ ${Number(empresa.capital_social).toLocaleString('pt-BR')}` : '—' },
    { label: 'Regime',     valor: empresa?.regime || '—' },
  ];
  const chipsSU = modo === 'SU' ? [
    { label: 'Estágio',           valor: empresa?.estagio_su || 'A definir' },
    { label: 'Rodadas anteriores', valor: empresa?.rodadas_anteriores || 'Nenhuma' },
  ] : [];
  const chips = [...chipsBase, ...chipsSU];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
      {/* 2 quadros lado a lado: Definição + Instruções */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <div style={{ padding: '12px 16px', background: G.verdeLight,
          borderLeft: `4px solid ${G.verde}`, borderRadius: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 800, color: G.verde,
            letterSpacing: '0.06em', textTransform: 'uppercase',
            marginBottom: 6 }}>
            O que é Descrição da Empresa
          </div>
          <div style={{ fontSize: 12, lineHeight: 1.45, color: G.txt1 }}>
            Abre o Business Plan apresentando o negócio para investidor, comprador
            ou banco. Cobre <strong>identidade</strong>, <strong>história</strong>,
            {' '}<strong>equipe</strong> e <strong>estrutura física</strong>.
          </div>
        </div>
        <div style={{ padding: '12px 16px', background: G.laranjaLight,
          borderLeft: `4px solid ${G.laranja}`, borderRadius: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 800, color: G.laranja,
            letterSpacing: '0.06em', textTransform: 'uppercase',
            marginBottom: 6 }}>
            O que você faz aqui
          </div>
          <div style={{ fontSize: 12, lineHeight: 1.45, color: G.txt1 }}>
            O <MarcaAIdvisor /> sugeriu um rascunho a partir do Cadastro e da
            Presença Digital. <strong>Revise os 3 blocos</strong> (História · Equipe ·
            Instalações), edite o que precisar, adicione próprios e clique Aprovar
            para incluir no BP.
          </div>
        </div>
      </div>

      {/* ═══ BLOCO 1: Snapshot do Cadastro (chips read-only) ══════════ */}
      <div>
        <div style={HEADER_BLOCO}>
          Bloco 1 · Identidade
          <span style={HEADER_AUX}>· vem do Cadastro · não edita aqui</span>
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
          {chips.map(c => (
            <div key={c.label} style={{
              padding: '6px 12px', background: '#F5F5F1',
              border: `1px solid ${G.borda}`, borderRadius: 0,
              fontSize: 12, color: G.txt1, lineHeight: 1.3,
            }}>
              <span style={{ fontWeight: 700, color: G.txt2, marginRight: 6,
                fontSize: 10, letterSpacing: '0.08em', textTransform: 'uppercase' }}>
                {c.label}
              </span>
              {c.valor}
            </div>
          ))}
        </div>
      </div>

      {/* ═══ BLOCO 2: História & Marcos ═══════════════════════════════ */}
      <div>
        <div style={HEADER_BLOCO}>
          Bloco 2 · História &amp; Marcos
          <span style={HEADER_AUX}>· <MarcaAIdvisor /> pesquisou no seu cadastro, site e redes · marque os marcos que se confirmam</span>
        </div>
        {catalogoHist.length > 0 && (
          <div style={{ background: G.fundo, border: `1px solid ${G.borda}`,
            borderRadius: 0, padding: '10px 14px', marginBottom: 10 }}>
            <div style={{ fontSize: 10, fontWeight: 800, color: G.txt2,
              letterSpacing: '0.10em', textTransform: 'uppercase', marginBottom: 8 }}>
              Encontrado pela IA · marque os que se confirmam
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {catalogoHist.map(c => {
                const ativo = chipHistMarcado(c.id_catalogo);
                return (
                  <button key={c.id_catalogo} type="button"
                    onClick={() => toggleChipHist(c)}
                    title={c.marco}
                    style={{
                      padding: '5px 11px', fontSize: 12, fontWeight: 600,
                      background: ativo ? G.verde : '#FFF',
                      color: ativo ? '#FFF' : G.txt1,
                      border: `1px solid ${ativo ? G.verde : G.borda}`,
                      borderRadius: 999, cursor: 'pointer',
                      fontFamily: "'Outfit', sans-serif",
                    }}>
                    {ativo ? '✓ ' : ''}{c.ano} · {(c.marco || '').substring(0, 50)}
                  </button>
                );
              })}
            </div>
          </div>
        )}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {historia.map((m, i) => {
            const doCatalogo = !!m.id_catalogo;
            return (
              <div key={i} style={{ display: 'grid',
                gridTemplateColumns: '80px 1fr auto', gap: 10, alignItems: 'start' }}>
                {doCatalogo ? (
                  <>
                    <div style={{ padding: '7px 10px', fontSize: 13, fontWeight: 700,
                      color: G.txt1, textAlign: 'center' }}>
                      {m.ano}
                    </div>
                    <div style={{ padding: '7px 10px', fontSize: 12, color: G.txt2,
                      fontStyle: 'italic', lineHeight: 1.4 }}>
                      {m.marco}
                    </div>
                  </>
                ) : (
                  <>
                    <input value={m.ano} onChange={e => editarHistoria(i, 'ano', e.target.value)}
                      placeholder="2024" style={{ ...inputBase, textAlign: 'center' }}/>
                    <input value={m.marco} onChange={e => editarHistoria(i, 'marco', e.target.value)}
                      placeholder="Evento importante · resultado obtido"
                      style={inputBase}/>
                  </>
                )}
                <button onClick={() => removerHistoria(i)} title="Remover" style={btnRemover}><IconeLixeira /></button>
              </div>
            );
          })}
        </div>
        <button onClick={adicionarHistoria} style={btnAdd}>+ Adicionar marco próprio</button>
      </div>

      {/* ═══ BLOCO 3: Equipe-chave ════════════════════════════════════ */}
      <div>
        <div style={HEADER_BLOCO}>
          Bloco 3 · Equipe-chave{modo === 'SU' ? ' · investidor avalia primeiro a equipe, depois a tese' : ''}
          <span style={HEADER_AUX}>· <MarcaAIdvisor /> pesquisou no cadastro e LinkedIn · marque as pessoas-chave que se confirmam</span>
        </div>
        {catalogoEq.length > 0 && (
          <div style={{ background: G.fundo, border: `1px solid ${G.borda}`,
            borderRadius: 0, padding: '10px 14px', marginBottom: 10 }}>
            <div style={{ fontSize: 10, fontWeight: 800, color: G.txt2,
              letterSpacing: '0.10em', textTransform: 'uppercase', marginBottom: 8 }}>
              Encontrado pela IA · marque os que se confirmam
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {catalogoEq.map(c => {
                const ativo = chipEqMarcado(c.id_catalogo);
                return (
                  <button key={c.id_catalogo} type="button"
                    onClick={() => toggleChipEq(c)}
                    title={c.bio}
                    style={{
                      padding: '5px 11px', fontSize: 12, fontWeight: 600,
                      background: ativo ? G.verde : '#FFF',
                      color: ativo ? '#FFF' : G.txt1,
                      border: `1px solid ${ativo ? G.verde : G.borda}`,
                      borderRadius: 999, cursor: 'pointer',
                      fontFamily: "'Outfit', sans-serif",
                    }}>
                    {ativo ? '✓ ' : ''}{c.nome} · {c.funcao}
                  </button>
                );
              })}
            </div>
          </div>
        )}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {equipe.map((p, i) => {
            const doCatalogo = !!p.id_catalogo;
            return (
              <div key={i} style={{
                padding: '12px 14px', background: '#FFF',
                border: `1px solid ${G.borda}`, borderRadius: 0,
                display: 'grid', gridTemplateColumns: '1fr 1fr auto', gap: 10 }}>
                {doCatalogo ? (
                  <>
                    <div style={{ padding: '7px 10px', fontSize: 13, fontWeight: 700,
                      color: G.txt1 }}>
                      {p.nome}
                    </div>
                    <div style={{ padding: '7px 10px', fontSize: 13, fontWeight: 600,
                      color: G.txt2 }}>
                      {p.funcao}
                    </div>
                    <button onClick={() => removerEquipe(i)} title="Remover" style={btnRemover}><IconeLixeira /></button>
                    <div style={{ padding: '7px 10px', fontSize: 12, color: G.txt2,
                      fontStyle: 'italic', lineHeight: 1.45, gridColumn: '1 / -1' }}>
                      {p.bio}
                    </div>
                  </>
                ) : (
                  <>
                    <input value={p.nome} onChange={e => editarEquipe(i, 'nome', e.target.value)}
                      placeholder="Nome próprio" style={inputBase}/>
                    <input value={p.funcao} onChange={e => editarEquipe(i, 'funcao', e.target.value)}
                      placeholder="Função / cargo" style={inputBase}/>
                    <button onClick={() => removerEquipe(i)} title="Remover" style={btnRemover}><IconeLixeira /></button>
                    <textarea value={p.bio || ''} onChange={e => editarEquipe(i, 'bio', e.target.value)}
                      placeholder="Bio curta (opcional) · formação, experiência, responsabilidade"
                      rows={2} style={{ ...inputBase, gridColumn: '1 / -1', resize: 'vertical',
                        fontFamily: "'Outfit', sans-serif", lineHeight: 1.45 }}/>
                  </>
                )}
              </div>
            );
          })}
        </div>
        <button onClick={adicionarEquipe} style={btnAdd}>+ Adicionar pessoa própria</button>
      </div>

      {/* ═══ BLOCO 4: Instalações ══════════════════════════ */}
      <div>
        <div style={HEADER_BLOCO}>
          Bloco 4 · {modo === 'SU' ? 'Infraestrutura' : 'Instalações'}
          <span style={HEADER_AUX}>· <MarcaAIdvisor /> pesquisou no site e Google Maps · marque as que se confirmam</span>
        </div>
        {catalogoInst.length > 0 && (
          <div style={{ background: G.fundo, border: `1px solid ${G.borda}`,
            borderRadius: 0, padding: '10px 14px', marginBottom: 10 }}>
            <div style={{ fontSize: 10, fontWeight: 800, color: G.txt2,
              letterSpacing: '0.10em', textTransform: 'uppercase', marginBottom: 8 }}>
              Encontrado pela IA · marque os que se confirmam
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {catalogoInst.map(c => {
                const ativo = chipInstMarcado(c.id_catalogo);
                return (
                  <button key={c.id_catalogo} type="button"
                    onClick={() => toggleChipInst(c)}
                    title={c.local}
                    style={{
                      padding: '5px 11px', fontSize: 12, fontWeight: 600,
                      background: ativo ? G.verde : '#FFF',
                      color: ativo ? '#FFF' : G.txt1,
                      border: `1px solid ${ativo ? G.verde : G.borda}`,
                      borderRadius: 999, cursor: 'pointer',
                      fontFamily: "'Outfit', sans-serif",
                    }}>
                    {ativo ? '✓ ' : ''}{c.tipo}
                  </button>
                );
              })}
            </div>
          </div>
        )}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {instalacoes.map((u, i) => {
            const doCatalogo = !!u.id_catalogo;
            return (
              <div key={i} style={{ display: 'grid',
                gridTemplateColumns: '200px 1fr auto', gap: 10, alignItems: 'start' }}>
                {doCatalogo ? (
                  <>
                    <div style={{ padding: '7px 10px', fontSize: 13, fontWeight: 700,
                      color: G.txt1 }}>
                      {u.tipo}
                    </div>
                    <div style={{ padding: '7px 10px', fontSize: 12, color: G.txt2,
                      fontStyle: 'italic', lineHeight: 1.4 }}>
                      {u.local}
                    </div>
                  </>
                ) : (
                  <>
                    <input value={u.tipo} onChange={e => editarInst(i, 'tipo', e.target.value)}
                      placeholder="Tipo · sede, filial, CD" style={inputBase}/>
                    <input value={u.local} onChange={e => editarInst(i, 'local', e.target.value)}
                      placeholder="Cidade · UF · área · detalhe"
                      style={inputBase}/>
                  </>
                )}
                <button onClick={() => removerInst(i)} title="Remover" style={btnRemover}><IconeLixeira /></button>
              </div>
            );
          })}
        </div>
        <button onClick={adicionarInst} style={btnAdd}>+ Adicionar instalação própria</button>
      </div>
    </div>
  );
};


// ═══════════════════════════════════════════════════════════════════════
// <ConteudoTarefa1 /> — Meta & Equity (1ª tarefa do BP60M).
// Define a meta de transformação (4 tipos) + valor + horizonte fixo de 60m,
// e o flag de Equity (define o tom dos Materiais de Captação · Tarefa 17).
// Pensado em 11/06/2026.
// ═══════════════════════════════════════════════════════════════════════
// Tipos de capital — preferência preliminar do empresário (T1 · Bloco 2 quando equity='sim'|'em_breve')
const TIPOS_CAPITAL_T1 = [
  { id: 'equity',      titulo: 'Equity dilutivo',  descricao: 'Aporte de novo sócio · dilui controle, sem dívida.' },
  { id: 'divida',      titulo: 'Dívida bancária',  descricao: 'Empréstimo convencional · sem diluir, com juros.' },
  { id: 'bndes',       titulo: 'BNDES',            descricao: 'Linha de fomento · juro subsidiado, prazo longo.' },
  { id: 'conversivel', titulo: 'Conversível',      descricao: 'Dívida que vira equity em evento futuro.' },
  { id: 'misto',       titulo: 'Misto',            descricao: 'Combina dívida e equity em tranches.' },
  { id: 'naosei',      titulo: 'Ainda não sei',    descricao: 'RUNGOAL AIDVISOR sugere o caminho mais coerente.' },
];

// Catálogo de tipos de capital para a modalidade Startup · vocabulário próprio
// (Anjo · Pre-Seed · Semente · Série A) substitui "Equity dilutivo" amplo.
// Conversível enriquecido para incluir notas como SAFE. BNDES e Dívida bancária
// saem (raros em early-stage). Adiciona Dívida Venture, Financiamento coletivo
// de equity e Sem capital externo (bootstrap).
const TIPOS_CAPITAL_T1_SU = [
  { id: 'equity_su',          titulo: 'Equity (Anjo · Pre-Seed · Semente · Série A ou superior)',
    descricao: 'Aporte de fundo, anjo ou aceleradora em troca de participação societária.' },
  { id: 'conversivel_su',     titulo: 'Conversível (SAFE · Nota Conversível)',
    descricao: 'Aporte que vira participação societária num evento futuro · evita discussão de valor no início.' },
  { id: 'venture_debt',       titulo: 'Dívida Venture',
    descricao: 'Dívida específica para Startup, sem garantias reais · contrata após equity.' },
  { id: 'crowdfunding',       titulo: 'Financiamento coletivo de equity',
    descricao: 'Captação via plataforma regulada (CVM 88) · muitos pequenos investidores.' },
  { id: 'bootstrap',          titulo: 'Sem capital externo (bootstrap)',
    descricao: 'Crescimento financiado pelo próprio caixa · sem diluir, sem dívida.' },
  { id: 'misto_su',           titulo: 'Misto',
    descricao: 'Combina dois ou mais instrumentos em tranches diferentes.' },
  { id: 'naosei_su',          titulo: 'Ainda não sei',
    descricao: 'RUNGOAL AIDVISOR sugere o caminho mais coerente com o estágio.' },
];

// Horizonte de Exit — preferência preliminar (T1 · Bloco 3 quando meta ≠ 'saida')
const EXIT_HORIZONTE_T1 = [
  { id: '5',         titulo: 'Sim, em 5 anos',    descricao: 'Alinhado ao horizonte do Business Plan.' },
  { id: '7_10',      titulo: 'Sim, em 7 a 10 anos', descricao: 'Janela mais larga · prepara estrutura aos poucos.' },
  { id: 'talvez',    titulo: 'Talvez',            descricao: 'Mantém a opção aberta · decide conforme oportunidade.' },
  { id: 'nao',       titulo: 'Não, quero continuar dono', descricao: 'Empresa familiar · crescimento orgânico.' },
];

// Horizonte de Exit para a modalidade Startup · vias típicas (Aquisição · IPO)
// substituem as opções genéricas. "Bootstrap" preserva a opção de não-exit.
const EXIT_HORIZONTE_T1_SU = [
  { id: '5_ma',         titulo: 'Sim, em 5 anos via Aquisição',
    descricao: 'Acelera relacionamento com compradores estratégicos desde já.' },
  { id: '7_10_ipo',     titulo: 'Sim, em 7 a 10 anos via IPO',
    descricao: 'Janela mais larga · prepara governança e auditoria desde já.' },
  { id: 'talvez_tracao', titulo: 'Talvez · dependendo da tração',
    descricao: 'Mantém a opção aberta · decisão guiada pelos números.' },
  { id: 'nao_bootstrap', titulo: 'Não, quero continuar dono (bootstrap)',
    descricao: 'Manter o controle integral · crescimento orgânico.' },
];

// Rodada de captação (Startup) · escolha do empresário na T1 · nomeia o ASK nos Materiais (Tarefa 17)
const RODADAS_SU = [
  { id: 'pre_seed', titulo: 'Pré-Seed', faixa: 'até ~R$ 2 mi',
    descricao: 'Capital inicial para validar o produto e as primeiras vendas.' },
  { id: 'seed',     titulo: 'Seed',     faixa: '~R$ 2 a 15 mi',
    descricao: 'Capital para encontrar o product-market fit e estruturar a operação.' },
  { id: 'serie_a',  titulo: 'Série A',  faixa: '~R$ 15 a 60 mi',
    descricao: 'Capital para escalar um modelo já validado.' },
  { id: 'serie_b',  titulo: 'Série B',  faixa: '~R$ 60 mi+',
    descricao: 'Capital para acelerar a expansão e ganhar mercado.' },
];

const TIPOS_META_BP60M = [
  {
    id: 'ebitda_mult',
    titulo: 'Multiplicar EBITDA',
    descricao: 'Empresa lucrativa que quer escalar resultado.',
    sufixo: '×', placeholder: 5, min: 2, max: 20,
    fraseAntes: 'Em 5 anos, multiplicar o EBITDA em ',
    fraseDepois: '×.',
  },
  {
    id: 'receita_mult',
    titulo: 'Multiplicar Receita',
    descricao: 'Foco em ganhar escala de top-line.',
    sufixo: '×', placeholder: 3, min: 2, max: 20,
    fraseAntes: 'Em 5 anos, multiplicar a Receita em ',
    fraseDepois: '×.',
  },
  {
    id: 'margem_alvo',
    titulo: 'Atingir Margem-Alvo',
    descricao: 'Foco em eficiência, não em volume.',
    sufixo: '%', placeholder: 20, min: 5, max: 60,
    fraseAntes: 'Em 5 anos, atingir ',
    fraseDepois: '% de Margem EBITDA recorrente.',
  },
  {
    id: 'saida',
    titulo: 'Saída via Venda',
    descricao: 'Vender o negócio em 5 anos (valuation-alvo).',
    sufixo: 'R$ mi', placeholder: 10, min: 1, max: 999,
    fraseAntes: 'Em 5 anos, vender a empresa por R$ ',
    fraseDepois: ' milhões.',
  },
];

// ═══════════════════════════════════════════════════════════════════════
// TIPOS_META_BP60M_SU · 4 tipos de meta apropriados para Startup, onde
// não existe base histórica para multiplicar. Substitui TIPOS_META_BP60M
// quando ConteudoTarefa1 recebe prop modo='SU'.
// Fonte canônica: Apêndice G · varredura T1 aprovada em 26/06/2026.
// ═══════════════════════════════════════════════════════════════════════
const TIPOS_META_BP60M_SU = [
  {
    id: 'tracao_alvo',
    titulo: 'Atingir Tração-Alvo',
    descricao: 'Meta em métrica de uso (clientes, usuários, MRR ou GMV).',
    sufixo: 'unidades', placeholder: 1000, min: 1, max: 9999999,
    fraseAntes: 'Em 5 anos, atingir ',
    fraseDepois: ' (unidades) de tração.',
  },
  {
    id: 'receita_alvo',
    titulo: 'Atingir Receita-Alvo',
    descricao: 'Meta em receita anual recorrente (R$ milhões).',
    sufixo: 'R$ mi', placeholder: 10, min: 1, max: 999,
    fraseAntes: 'Em 5 anos, atingir R$ ',
    fraseDepois: ' milhões de Receita Anual.',
  },
  {
    id: 'valuation_alvo',
    titulo: 'Atingir Valor de Empresa Alvo',
    descricao: 'Chegar a uma rodada com valor de empresa (valuation) declarado.',
    sufixo: 'R$ mi', placeholder: 50, min: 1, max: 9999,
    fraseAntes: 'Em 5 anos, chegar a uma rodada com Valor de Empresa de R$ ',
    fraseDepois: ' milhões.',
  },
  {
    id: 'saida_ipo_ma',
    titulo: 'Saída via IPO ou Aquisição',
    descricao: 'Sair via abertura de capital ou aquisição estratégica.',
    sufixo: 'R$ mi', placeholder: 100, min: 1, max: 99999,
    fraseAntes: 'Em 5 anos, sair via IPO ou Aquisição por R$ ',
    fraseDepois: ' milhões.',
  },
];

const ConteudoTarefa1 = ({ meta, setMeta, equityFlag, setEquityFlag, empresa, modo = 'EE' }) => {
  // Seleção dos catálogos pelo modo (EE = Empresa Estabelecida · SU = Startup).
  // Fonte das listas SU: Apêndice G · varredura T1 aprovada em 26/06/2026.
  const TIPOS_META    = modo === 'SU' ? TIPOS_META_BP60M_SU : TIPOS_META_BP60M;
  const TIPOS_CAPITAL = modo === 'SU' ? TIPOS_CAPITAL_T1_SU : TIPOS_CAPITAL_T1;
  const EXIT_HORIZ    = modo === 'SU' ? EXIT_HORIZONTE_T1_SU : EXIT_HORIZONTE_T1;
  // Rótulo do Bloco 1 muda conforme modalidade.
  const ROT_BLOCO_1_TITULO = modo === 'SU' ? 'Meta de Construção' : 'Meta de Transformação';
  const ROT_BLOCO_1_AUX    = modo === 'SU'
    ? '· aspiração construída do zero · orienta as 16 demais tarefas'
    : '· orienta todas as outras 17 tarefas';
  // Identificador do tipo "saída via venda/aquisição" muda entre EE e SU.
  const ID_TIPO_SAIDA      = modo === 'SU' ? 'saida_ipo_ma' : 'saida';
  // Navegação para o Manual (RGAM) a partir da T1 · usada no bloco de rodada (Startup).
  const abrirRGAMT1 = (termo) => {
    localStorage.setItem('rgam:termoInicial', termo);
    localStorage.setItem('rgam:origem', JSON.stringify({
      tela: 'businessplansu', subTela: 'tarefa-1', label: 'Tarefa 1 · Meta',
    }));
    window.dispatchEvent(new CustomEvent('rg-navegar', { detail: { tela: 'rgam' } }));
  };

  const tipoSel = TIPOS_META.find(t => t.id === meta.tipo) || TIPOS_META[0];
  const [graficosT1Aberto, setGraficosT1Aberto] = useState(false);
  const [tipoMetaAberto,   setTipoMetaAberto]   = useState(false);

  const trocarTipo = (id) => {
    const novoTipo = TIPOS_META.find(t => t.id === id);
    // Quando troca de tipo, reseta valor pro placeholder do novo tipo
    setMeta({ tipo: id, valor: novoTipo.placeholder });
  };

  const fmtNum = v => Number(v || 0).toLocaleString('pt-BR');

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {/* 2 quadros lado a lado: Definição + Instruções */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <div style={{ padding: '12px 16px', background: G.verdeLight,
          borderLeft: `4px solid ${G.verde}`, borderRadius: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 800, color: G.verde,
            letterSpacing: '0.06em', textTransform: 'uppercase',
            marginBottom: 6 }}>
            O que é Meta & Equity
          </div>
          <div style={{ fontSize: 12, lineHeight: 1.45, color: G.txt1 }}>
            Define o <strong>norte estratégico</strong> para os próximos 5 anos:
            tipo de meta (EBITDA · Receita · Margem · Saída) + valor + tese de
            Equity (abertura ou não a investidor externo). Orienta as outras 17
            tarefas do Business Plan.
          </div>
        </div>
        <div style={{ padding: '12px 16px', background: G.laranjaLight,
          borderLeft: `4px solid ${G.laranja}`, borderRadius: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 800, color: G.laranja,
            letterSpacing: '0.06em', textTransform: 'uppercase',
            marginBottom: 6 }}>
            O que você faz aqui
          </div>
          <div style={{ fontSize: 12, lineHeight: 1.45, color: G.txt1 }}>
            <strong>Escolha um dos 4 tipos de meta</strong> · digite o valor ·
            marque se considera <strong>captação de Equity</strong> (em metas
            não-Saída) · clique Aprovar para enviar ao BP.
          </div>
        </div>
      </div>

      {/* ═══ BLOCO 1: Meta de Transformação (EE) ou Meta de Construção (SU) ═══ */}
      <div>
        <div style={{ fontSize: 16, fontWeight: 700,
          letterSpacing: '0.10em', textTransform: 'uppercase',
          color: G.laranja, marginBottom: 8 }}>
          Bloco 1 · {ROT_BLOCO_1_TITULO}
          <span style={{ fontSize: 12, fontWeight: 400, color: G.txt2,
            textTransform: 'none', letterSpacing: 0, marginLeft: 10 }}>
            {ROT_BLOCO_1_AUX}
          </span>
        </div>

        {/* Accordion único · Tipo de Meta (padrão canônico) */}
        <div style={{
          background: '#FFF',
          border: `1px solid ${tipoMetaAberto ? G.verde : G.borda}`,
          borderRadius: 0, overflow: 'hidden',
        }}>
          <div style={{
            background: tipoMetaAberto ? G.verdeLight : 'transparent',
            padding: '8px 14px',
            display: 'flex', alignItems: 'center', gap: 12,
          }}>
            <div style={{
              flexShrink: 0, width: 22, height: 22, borderRadius: '50%',
              background: meta.tipo ? G.verde : G.borda,
              color: '#FFF', fontSize: 11, fontWeight: 800,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              {meta.tipo ? '✓' : '★'}
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: G.txt1,
                lineHeight: 1.3 }}>
                Qual é o tipo da sua meta de transformação?
              </div>
              {meta.tipo && !tipoMetaAberto && (
                <div style={{ fontSize: 12, color: G.verde, fontWeight: 600,
                  marginTop: 2 }}>
                  {tipoSel.titulo} — {tipoSel.descricao}
                </div>
              )}
            </div>
            <button onClick={() => setTipoMetaAberto(v => !v)}
              style={{
                flexShrink: 0,
                background: tipoMetaAberto ? '#FFF' : G.laranjaLight,
                border: `1px solid ${G.laranja}`,
                color: G.laranja,
                padding: '4px 9px', fontSize: 9, fontWeight: 800,
                letterSpacing: '0.08em', textTransform: 'uppercase',
                borderRadius: 0, cursor: 'pointer',
                fontFamily: "'Outfit', sans-serif",
                whiteSpace: 'nowrap',
              }}>
              {tipoMetaAberto ? '▴ Clique para fechar' : '▾ Clique para abrir'}
            </button>
          </div>
          {tipoMetaAberto && (
            <div style={{ padding: '4px 14px 12px',
              display: 'flex', flexDirection: 'column', gap: 6 }}>
              {TIPOS_META.map(t => {
                const ativo = meta.tipo === t.id;
                return (
                  <label key={t.id}
                    style={{ display: 'flex', alignItems: 'flex-start', gap: 10,
                      padding: '8px 12px', cursor: 'pointer',
                      background: ativo ? G.verdeLight : '#FFF',
                      border: ativo ? `2px solid ${G.verde}` : `1px solid ${G.borda}`,
                      borderRadius: 0,
                      fontFamily: "'Outfit', sans-serif",
                    }}>
                    <input type="radio"
                      name="tipo_meta"
                      checked={ativo}
                      onChange={() => trocarTipo(t.id)}
                      style={{
                        appearance: 'none', WebkitAppearance: 'none',
                        width: 14, height: 14, marginTop: 3, flexShrink: 0,
                        border: `2px solid ${ativo ? G.verde : G.borda}`,
                        borderRadius: 0,
                        background: ativo ? G.verde : '#FFF',
                        cursor: 'pointer',
                      }}/>
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 800,
                        color: ativo ? G.verde : G.txt1, marginBottom: 2 }}>
                        {t.titulo}
                      </div>
                      <div style={{ fontSize: 11, color: G.txt2, lineHeight: 1.4 }}>
                        {t.descricao}
                      </div>
                    </div>
                  </label>
                );
              })}
            </div>
          )}
        </div>

        {/* Card do valor da meta */}
        <div style={{ marginTop: 10, padding: '12px 16px',
          background: '#FFF', border: `1px solid ${G.borda}`,
          borderRadius: 0, display: 'flex', alignItems: 'center', gap: 16 }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 10, fontWeight: 700,
              letterSpacing: '0.10em', textTransform: 'uppercase',
              color: G.txt2, marginBottom: 4 }}>
              Valor da meta
            </div>
            <div style={{ fontSize: 13, color: G.txt2, lineHeight: 1.4 }}>
              {tipoSel.fraseAntes}{fmtNum(meta.valor || 0)}{tipoSel.fraseDepois}
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{ width: 160 }}>
              <CampoNumericoBP60M
                valor={meta.valor || ''}
                onChange={(v) => setMeta({ ...meta, valor: v ?? 0 })}
                label={`Valor da meta · ${tipoSel.titulo}`}
                permitirDecimal={tipoSel.sufixo === '%' || tipoSel.sufixo === 'R$ mi'}
                min={tipoSel.min} max={tipoSel.max}
                sufixo={tipoSel.sufixo}
                formato={tipoSel.sufixo === '×' ? 'decimal' : (tipoSel.sufixo === '%' ? 'decimal' : (tipoSel.sufixo === 'R$ mi' ? 'brl' : 'int'))}
              />
            </div>
          </div>
        </div>

        {/* Botão de visualização da trajetória da meta */}
        <BotaoVisualizarGraficos
          aberto={graficosT1Aberto}
          onToggle={() => setGraficosT1Aberto(v => !v)}
          rotulo="Visualizar trajetória da meta">
          <div>
            <div style={{ fontSize: 10, fontWeight: 800, color: G.txt2,
              letterSpacing: '0.10em', textTransform: 'uppercase', marginBottom: 8 }}>
              Ilustração · {TITULO_EXHIBIT_POR_TIPO['t1_trajetoria']}
            </div>
            <GraficoTrajetoriaMeta tipoId={meta.tipo} valor={meta.valor}/>
          </div>
        </BotaoVisualizarGraficos>
      </div>

      {/* ═══ BLOCO 2: Tese de Equity / Tom dos Materiais ══════════════ */}
      {meta.tipo !== ID_TIPO_SAIDA ? (
        <div>
          <div style={{ fontSize: 16, fontWeight: 700,
            letterSpacing: '0.10em', textTransform: 'uppercase',
            color: G.laranja, marginBottom: 8 }}>
            {modo === 'SU' ? 'Bloco 2 · Captação' : 'Bloco 2 · Tese de Equity'}
            <span style={{ fontSize: 12, fontWeight: 400, color: G.txt2,
              textTransform: 'none', letterSpacing: 0, marginLeft: 10 }}>
              {modo === 'SU' ? '· como você pretende financiar o crescimento?' : '· caso precise de capital externo para cumprir a meta, qual seria o caminho?'}
            </span>
          </div>

          {/* 3 cards · Sim · Em breve · Não · ocultos no SU (startup sempre capta) */}
          {modo !== 'SU' && (
          <div style={{ display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)', gap: 10 }}>
            {[
              { id: 'sim',      titulo: 'Sim, considero captação de Equity', cor: G.laranja, corBg: G.laranjaLight, desc: 'Materiais direcionados para investidor · BP estima valor a captar.' },
              { id: 'em_breve', titulo: 'Em breve, ainda avaliando',         cor: '#B45309', corBg: '#FFFBEB',     desc: 'Mantém a opção aberta · revisita após ver as projeções (T13).' },
              { id: 'nao',      titulo: 'Não, crescimento via geração própria', cor: G.verde, corBg: G.verdeLight, desc: 'Materiais em tom institucional (bancos, posicionamento, futuro).' },
            ].map(opt => {
              const ativo = equityFlag === opt.id || (opt.id === 'sim' && equityFlag === true) || (opt.id === 'nao' && equityFlag === false);
              return (
                <button key={opt.id} onClick={() => setEquityFlag(opt.id)}
                  style={{
                    textAlign: 'left', cursor: 'pointer',
                    background: ativo ? opt.corBg : '#FFF',
                    border: ativo ? `2px solid ${opt.cor}` : `1px solid ${G.borda}`,
                    borderRadius: 0, padding: '11px 14px',
                    fontFamily: "'Outfit', sans-serif", transition: 'all 0.15s',
                  }}>
                  <div style={{ fontSize: 13.5, fontWeight: 800,
                    color: ativo ? opt.cor : G.txt1, marginBottom: 2 }}>
                    {opt.titulo}
                  </div>
                  <div style={{ fontSize: 12, color: G.txt2, lineHeight: 1.4 }}>
                    {opt.desc}
                  </div>
                </button>
              );
            })}
          </div>
          )}

          {/* Sub-bloco · Tipo de capital · equity 'sim'/'em_breve' ou sempre no SU */}
          {(equityFlag === 'sim' || equityFlag === true || equityFlag === 'em_breve' || modo === 'SU') && (
            <div style={{ marginTop: 12, padding: '12px 16px',
              background: '#FFF', border: `1px solid ${G.borda}`,
              borderRadius: 0 }}>
              <div style={{ fontSize: 11, fontWeight: 800, color: G.txt2,
                letterSpacing: '0.10em', textTransform: 'uppercase',
                marginBottom: 8 }}>
                Que tipo de capital combina mais com você?
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6,
                marginBottom: 10 }}>
                {TIPOS_CAPITAL.map(t => {
                  const ativo = meta.tipoCapital === t.id;
                  return (
                    <button key={t.id} type="button"
                      onClick={() => setMeta({ ...meta, tipoCapital: t.id })}
                      title={t.descricao}
                      style={{
                        padding: '8px 12px', fontSize: 12, fontWeight: 600,
                        background: ativo ? G.laranja : '#FFF',
                        color: ativo ? '#FFF' : G.txt1,
                        border: `1px solid ${ativo ? G.laranja : G.borda}`,
                        borderRadius: 0, cursor: 'pointer',
                        fontFamily: "'Outfit', sans-serif", textAlign: 'left',
                      }}>
                      {ativo ? '✓ ' : ''}<strong>{t.titulo}</strong>
                    </button>
                  );
                })}
              </div>
              {meta.tipoCapital && (
                <div style={{ fontSize: 12, color: G.txt2, fontStyle: 'italic',
                  lineHeight: 1.4 }}>
                  {TIPOS_CAPITAL.find(t => t.id === meta.tipoCapital)?.descricao}
                </div>
              )}
              {/* Frase do RUNGOAL AIDVISOR · intervalo heurístico preliminar */}
              <div style={{ marginTop: 10, padding: '8px 12px',
                background: '#F5F5F1', borderLeft: `3px solid ${G.verde}`,
                fontSize: 12.5, lineHeight: 1.5, color: G.txt1 }}>
                <MarcaAIdvisor variant="default" />{' '}
                · estimo necessidade preliminar entre <strong>R$ 1 mi</strong> e <strong>R$ 5 mi</strong> com base no porte e setor. Vamos confirmar e refinar na Tarefa 13 (Projeções 5 anos).
              </div>
            </div>
          )}

          {/* Sub-bloco · Rodada de captação · só no modo Startup quando há intenção de captar */}
          {modo === 'SU' && (
            <div style={{ marginTop: 12, padding: '12px 16px',
              background: '#FFF', border: `1px solid ${G.borda}`,
              borderRadius: 0 }}>
              <div style={{ fontSize: 11, fontWeight: 800, color: G.txt2,
                letterSpacing: '0.10em', textTransform: 'uppercase',
                marginBottom: 8 }}>
                Qual rodada de captação você busca?
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6,
                marginBottom: 10 }}>
                {RODADAS_SU.map(r => {
                  const ativo = meta.rodada === r.id;
                  return (
                    <button key={r.id} type="button"
                      onClick={() => setMeta({ ...meta, rodada: r.id })}
                      title={r.descricao}
                      style={{
                        padding: '8px 12px', fontSize: 12, fontWeight: 600,
                        background: ativo ? G.laranja : '#FFF',
                        color: ativo ? '#FFF' : G.txt1,
                        border: `1px solid ${ativo ? G.laranja : G.borda}`,
                        borderRadius: 0, cursor: 'pointer',
                        fontFamily: "'Outfit', sans-serif", textAlign: 'left',
                      }}>
                      {ativo ? '✓ ' : ''}<strong>{r.titulo}</strong>
                    </button>
                  );
                })}
              </div>
              {meta.rodada && (
                <div style={{ fontSize: 12, color: G.txt2, fontStyle: 'italic',
                  lineHeight: 1.4 }}>
                  {RODADAS_SU.find(r => r.id === meta.rodada)?.descricao}
                  {' · '}<strong>Faixa típica:</strong> {RODADAS_SU.find(r => r.id === meta.rodada)?.faixa}
                </div>
              )}
              <div style={{ marginTop: 10 }}>
                <BotaoSaibaMais onClick={() => abrirRGAMT1('rodada_' + (meta.rodada || 'pre_seed'))} />
              </div>
            </div>
          )}
        </div>
      ) : (
        // Saída via Venda — Tese fica determinada (comprador)
        <div>
          <div style={{ fontSize: 16, fontWeight: 700,
            letterSpacing: '0.10em', textTransform: 'uppercase',
            color: G.laranja, marginBottom: 8 }}>
            Bloco 2 · Tom dos Materiais de Captação
          </div>
          <div style={{ padding: '11px 14px',
            background: G.laranjaLight, borderLeft: `4px solid ${G.laranja}`,
            borderRadius: 0 }}>
            <div style={{ fontSize: 13, color: G.txt1, lineHeight: 1.45 }}>
              <strong style={{ color: G.laranja }}>Saída via Venda</strong> direciona
              os Materiais de Captação (Tarefa 17) para <strong>candidatos a comprador</strong>.
            </div>
          </div>
        </div>
      )}

      {/* ═══ BLOCO 3: Horizonte de Exit · só se meta ≠ 'saida' ══════════ */}
      {meta.tipo !== ID_TIPO_SAIDA && (
        <div>
          <div style={{ fontSize: 16, fontWeight: 700,
            letterSpacing: '0.10em', textTransform: 'uppercase',
            color: G.laranja, marginBottom: 8 }}>
            Bloco 3 · Horizonte de Exit
            <span style={{ fontSize: 12, fontWeight: 400, color: G.txt2,
              textTransform: 'none', letterSpacing: 0, marginLeft: 10 }}>
              · tem horizonte de vender a empresa um dia?
            </span>
          </div>
          <div style={{ display: 'grid',
            gridTemplateColumns: 'repeat(2, 1fr)', gap: 10 }}>
            {EXIT_HORIZ.map(opt => {
              const ativo = meta.exitHorizonte === opt.id;
              return (
                <button key={opt.id}
                  onClick={() => setMeta({ ...meta, exitHorizonte: opt.id })}
                  style={{
                    textAlign: 'left', cursor: 'pointer',
                    background: ativo ? G.verdeLight : '#FFF',
                    border: ativo ? `2px solid ${G.verde}` : `1px solid ${G.borda}`,
                    borderRadius: 0, padding: '11px 14px',
                    fontFamily: "'Outfit', sans-serif", transition: 'all 0.15s',
                  }}>
                  <div style={{ fontSize: 13.5, fontWeight: 800,
                    color: ativo ? G.verde : G.txt1, marginBottom: 2 }}>
                    {opt.titulo}
                  </div>
                  <div style={{ fontSize: 12, color: G.txt2, lineHeight: 1.4 }}>
                    {opt.descricao}
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}

    </div>
  );
};


// ═══════════════════════════════════════════════════════════════════════
// RGAM — RUNGOAL AIDVISOR Manual do Empresário
// Glossário curado de termos financeiros e de M&A · Fase 1 estático
// Páginas individuais por termo · landing com grid 5×4 modelo BP60M
// Disponibilidade: Fase 1 todos os usuários · Fase 3 chat AiPro premium
// ═══════════════════════════════════════════════════════════════════════

// Telas com altura fixa em 100vh que gerenciam scroll internamente.
// Wrapper externo desliga overflowY pra essas telas — evita 2 scrollbars.
// RGAM fica de fora porque tem sub-telas (Landing scroll-zero · Página do termo scroll normal);
// a LandingRGAM trava o wrapper sozinha via useEffect.
// `valor` SAIU da lista em 30/07: com o COMENTÁRIO DA ASSESSORIA no rodapé, o
// conteúdo deixou de caber em 100vh e os blocos passaram a se sobrepor — a
// faixa de Modelos de Avaliação cobria os rótulos de Geração de Valor.
// Altura travada só serve enquanto o conteúdo é fixo; assim que a tela ganha
// bloco novo, ela precisa rolar (memória `telas-scroll-zero`).
const TELAS_SCROLL_ZERO = ['dashboard'];

// Estrutura aprovada 17/06 · 6 capítulos · 63 termos · planilha rgam-manual-estrutura-2026-06-17.csv
// Cores alinhadas com Hero laranja: alternância verde/laranja/verde/laranja/verde escuro/laranja
const RGAM_CAPITULOS = [
  { numero: 1, nome: 'Gestão',              cor: G.verde,    corHover: G.verdeLight,
    items: [
      { id: 'dfc',                       nome: 'DFC' },
      { id: 'capital_giro',              nome: 'Capital de Giro' },
      { id: 'capex',                     nome: 'CAPEX' },
      { id: 'nopat',                     nome: 'NOPAT' },
      { id: 'fcf',                       nome: 'FCF' },
      { id: 'endividamento',             nome: 'Endividamento' },
      { id: 'liquidez_corrente',         nome: 'Liquidez Corrente' },
      { id: 'roic',                      nome: 'ROIC' },
    ]},
  { numero: 2, nome: 'DRE',                 cor: G.laranja,  corHover: '#FFF0E6',
    items: [
      { id: 'receita',                   nome: 'Receita' },
      { id: 'cpv',                       nome: 'Custo (CPV)' },
      { id: 'lucro_bruto',               nome: 'Lucro Bruto' },
      { id: 'margem_bruta',              nome: 'Margem Bruta' },
      { id: 'despesa_operacional',       nome: 'Despesa Operacional' },
      { id: 'lucro_operacional',         nome: 'Lucro Operacional' },
      { id: 'ebit',                      nome: 'EBIT' },
      { id: 'margem_operacional',        nome: 'Margem Operacional' },
      { id: 'ebitda',                    nome: 'EBITDA' },
      { id: 'margem_ebitda',             nome: 'Margem EBITDA' },
      { id: 'despesa_financeira',        nome: 'Despesa Financeira' },
      { id: 'lair',                      nome: 'LAIR' },
      { id: 'tributos',                  nome: 'IR · CSLL · Tributos' },
      { id: 'lucro_liquido',             nome: 'Lucro Líquido' },
      { id: 'margem_liquida',            nome: 'Margem Líquida' },
    ]},
  { numero: 3, nome: 'Custos de Capital',   cor: G.verde,    corHover: G.verdeLight,
    items: [
      { id: 'capital_proprio',           nome: 'Capital próprio (aporte de sócios)' },
      { id: 'emprestimo_bancario',       nome: 'Linhas de Crédito Curto e Longo Prazo' },
      { id: 'bndes',                     nome: 'BNDES (linhas de fomento)' },
      { id: 'antecipacao_factoring',     nome: 'Antecipação de recebíveis · Factoring' },
      { id: 'ke',                        nome: 'Ke' },
      { id: 'kd',                        nome: 'Kd' },
      { id: 'capm',                      nome: 'CAPM' },
      { id: 'wacc',                      nome: 'WACC' },
    ]},
  { numero: 4, nome: 'Balanço Patrimonial', cor: G.laranja,  corHover: '#FFF0E6',
    items: [
      { id: 'ativo',                     nome: 'Ativo' },
      { id: 'ativo_circulante',          nome: 'Ativo Circulante' },
      { id: 'ativo_imobilizado',         nome: 'Ativo Imobilizado' },
      { id: 'passivo',                   nome: 'Passivo' },
      { id: 'passivo_circulante',        nome: 'Passivo Circulante' },
      { id: 'passivo_nao_circulante',    nome: 'Passivo Não Circulante' },
      { id: 'patrimonio_liquido',        nome: 'Patrimônio Líquido' },
      { id: 'capital_social',            nome: 'Capital Social' },
    ]},
  { numero: 5, nome: 'M&A',                 cor: '#134721',  corHover: '#DCEBE0',
    items: [
      { id: 'dcf',                          nome: 'DCF' },
      { id: 'g_perpetuidade',               nome: 'G · Perpetuidade' },
      { id: 'valor_terminal',               nome: 'Valor Terminal' },
      { id: 'enterprise_value',             nome: 'Enterprise Value (EV)' },
      { id: 'multiplo_ev',                  nome: 'Múltiplo EV/EBITDA' },
      { id: 'multiplos_comparaveis',        nome: 'Múltiplos Comparáveis' },
      { id: 'capital_de_risco',             nome: 'Capital de Risco (VC Method)' },
      { id: 'agio',                         nome: 'Ágio · Goodwill' },
      { id: 'teaser',                       nome: 'Teaser' },
      { id: 'nda',                          nome: 'NDA' },
      { id: 'sinergia',                     nome: 'Sinergia' },
      { id: 'due_diligence',                nome: 'Due Diligence' },
      { id: 'earn_out',                     nome: 'Earn-out' },
      { id: 'lock_up_vesting',              nome: 'Lock-up · Vesting' },
      { id: 'lbo',                          nome: 'LBO' },
      { id: 'estruturas_acordo',            nome: 'Estruturas Societárias · Acordo de Acionistas' },
      { id: 'shotgun_put_call_dividend',    nome: 'Shotgun · Put · Call Option · Dividend Recap · Exit Strategies' },
      { id: 'tir',                          nome: 'TIR' },
    ]},
  { numero: 6, nome: 'Startups',            cor: G.laranja,  corHover: '#FFF0E6',
    items: [
      { id: 'swot',                      nome: 'SWOT' },
      { id: 'tam',                       nome: 'TAM' },
      { id: 'sam',                       nome: 'SAM' },
      { id: 'som',                       nome: 'SOM' },
      { id: 'cac',                       nome: 'CAC' },
      { id: 'ltv',                       nome: 'LTV' },
      { id: 'pitch_deck',                nome: 'Pitch Deck' },
      { id: 'rodada_pre_seed',           nome: 'Rodada Pré-Seed' },
      { id: 'rodada_seed',               nome: 'Rodada Seed' },
      { id: 'rodada_serie_a',            nome: 'Rodada Série A' },
      { id: 'rodada_serie_b',            nome: 'Rodada Série B' },
    ]},
];

// Lookup linear pros componentes PaginaTermoRGAM/TelaRGAM (retrocompat)
const RGAM_TERMOS = RGAM_CAPITULOS.flatMap(c => c.items.map(i => ({ ...i, capitulo: c.numero })));

// ── Landing do RGAM v2 · 6 capítulos com scroll por coluna ─────────────
const LandingRGAM = ({ onAbrirTermo }) => {
  const [hoveredItem, setHoveredItem] = useState(null);
  // Trava scroll do wrapper externo enquanto a Landing está montada · scroll fica só nas colunas.
  // Quando a Página do Termo monta, este efeito desmonta e o scroll volta.
  useEffect(() => {
    const wrapper = Array.from(document.querySelectorAll('div')).find(el => {
      const cs = getComputedStyle(el);
      return cs.backgroundColor === 'rgb(168, 210, 176)' && cs.flexGrow === '1';
    });
    if (!wrapper) return;
    const anterior = wrapper.style.overflowY;
    wrapper.style.overflowY = 'hidden';
    return () => { wrapper.style.overflowY = anterior; };
  }, []);
  return (
    <div style={{ maxWidth: 1300, margin: '0 auto', padding: '12px 28px',
      height: 'calc(100vh - 60px)', display: 'flex', flexDirection: 'column',
      gap: 22, overflow: 'hidden', boxSizing: 'border-box' }}>

      {/* ═══════════ HERO ═══════════ */}
      <section style={{ background: G.laranja, color: '#FFF',
        borderRadius: 0, padding: '26px 44px', position: 'relative',
        overflow: 'hidden', flexShrink: 0 }}>
        <div style={{ position: 'absolute', right: 24, top: '50%',
          transform: 'translateY(-50%)', zIndex: 0,
          fontFamily: "'Outfit', sans-serif", fontSize: 240, fontWeight: 900,
          lineHeight: 1, color: 'rgba(255,255,255,0.15)',
          pointerEvents: 'none', userSelect: 'none', letterSpacing: '-0.04em' }}>
          A·Z
        </div>
        <div style={{ fontSize: 12, fontWeight: 700,
          color: 'rgba(255,255,255,0.85)', letterSpacing: '0.20em',
          textTransform: 'uppercase', marginBottom: 14, position: 'relative' }}>
          Manual do Empresário · glossário curado
        </div>
        <h1 style={{ fontSize: 36, fontWeight: 900, lineHeight: 1.1,
          letterSpacing: '-0.02em', margin: 0, maxWidth: 1100,
          whiteSpace: 'nowrap', position: 'relative',
          fontFamily: "'Outfit', sans-serif" }}>
          O vocabulário do <span style={{ color: G.verde }}>empreendedor sênior</span> ao seu alcance.
        </h1>
        <p style={{ fontSize: 16, lineHeight: 1.55, fontWeight: 500,
          color: 'rgba(255,255,255,0.95)', maxWidth: 900,
          marginTop: 12, marginBottom: 0, position: 'relative' }}>
          O{' '}
          <span style={{ display: 'inline-block', background: '#FFF',
            padding: '2px 9px', borderRadius: 0, verticalAlign: 'baseline',
            lineHeight: 1.1 }}>
            <MarcaAIdvisor variant="default" />
          </span>
          {' '}traduz cada termo com definição clara, fórmula, exemplo em reais
          e curiosidade histórica · fontes de alta credibilidade.
        </p>
      </section>

      {/* ═══════════ TABELA DE 6 CAPÍTULOS · scroll individual por coluna ═══════════ */}
      <section style={{ display: 'grid',
        gridTemplateColumns: 'repeat(6, 1fr)', gap: 10,
        flex: 1, minHeight: 0, marginBottom: 70 }}>
        {RGAM_CAPITULOS.map(cap => (
          <div key={cap.numero} style={{ display: 'flex', flexDirection: 'column',
            background: '#FFF', borderRadius: 0, overflow: 'hidden',
            boxShadow: '0 1px 0 rgba(0,0,0,0.04)', minHeight: 0 }}>

            {/* Header com altura fixa e marca d'água numérica */}
            <div style={{ position: 'relative', height: 62,
              padding: '8px 58px 8px 14px', background: cap.cor,
              color: '#FFF', flexShrink: 0, overflow: 'hidden',
              display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
              <span style={{ fontSize: 12, fontWeight: 800,
                textTransform: 'uppercase', letterSpacing: '0.04em',
                lineHeight: 1.15, position: 'relative', zIndex: 1,
                fontFamily: "'Outfit', sans-serif" }}>
                {cap.nome}
              </span>
              <span style={{ fontSize: 9, fontWeight: 600,
                letterSpacing: '0.10em', textTransform: 'uppercase',
                opacity: 0.85, marginTop: 4, position: 'relative', zIndex: 1 }}>
                {cap.items.length} termos
              </span>
              {/* Marca d'água numérica */}
              <span style={{ position: 'absolute', right: 6, top: '50%',
                transform: 'translateY(-50%)', fontSize: 58, fontWeight: 900,
                fontFamily: "'Outfit', sans-serif",
                color: 'rgba(255,255,255,0.20)', letterSpacing: '-0.04em',
                lineHeight: 1, pointerEvents: 'none', userSelect: 'none' }}>
                {cap.numero}
              </span>
            </div>

            {/* Lista de itens com scroll individual */}
            <div style={{ flex: 1, minHeight: 0, overflowY: 'auto',
              scrollbarWidth: 'thin', scrollbarColor: 'rgba(0,0,0,0.18) transparent' }}>
              {cap.items.map(item => {
                const ativo = hoveredItem === `${cap.numero}-${item.id}`;
                return (
                  <div key={item.id}
                    onClick={() => onAbrirTermo && onAbrirTermo(item.id)}
                    onMouseEnter={() => setHoveredItem(`${cap.numero}-${item.id}`)}
                    onMouseLeave={() => setHoveredItem(null)}
                    style={{ padding: '10px 12px', fontSize: 12,
                      fontWeight: 600, color: G.txt1,
                      borderLeft: `3px solid ${cap.cor}`,
                      borderBottom: '1px solid #F0EEEA',
                      cursor: 'pointer', background: ativo ? cap.corHover : '#FFF',
                      lineHeight: 1.35, transition: 'background 0.12s ease',
                      fontFamily: "'Outfit', sans-serif" }}>
                    {item.nome}
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </section>
    </div>
  );
};

// ── Donut elegante de composição (2 segmentos) — usado nas páginas RGAM ──
const DonutComposicao = ({ segmentos, valorCentro, rotuloCentro }) => {
  const tamanho = 160;
  const raio = 65;
  const espessura = 22;
  const centro = tamanho / 2;
  const circ = 2 * Math.PI * raio;
  const total = segmentos.reduce((s, x) => s + x.valor, 0) || 1;
  let offset = 0;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 24,
      flexWrap: 'wrap', justifyContent: 'center' }}>
      <svg width={tamanho} height={tamanho} viewBox={`0 0 ${tamanho} ${tamanho}`}>
        <g transform={`rotate(-90 ${centro} ${centro})`}>
          {segmentos.map((seg, i) => {
            const pct = seg.valor / total;
            const dash = circ * pct;
            const gap = circ - dash;
            const dashOffset = -offset;
            offset += dash;
            return (
              <circle key={i} cx={centro} cy={centro} r={raio}
                fill="none" stroke={seg.cor} strokeWidth={espessura}
                strokeDasharray={`${dash} ${gap}`}
                strokeDashoffset={dashOffset}/>
            );
          })}
        </g>
        <text x={centro} y={centro - 4} textAnchor="middle"
          fontFamily="Outfit, sans-serif" fontSize="24" fontWeight="800"
          fill={G.txt1} style={{ fontVariantNumeric: 'tabular-nums',
            letterSpacing: '-0.02em' }}>
          {valorCentro}
        </text>
        <text x={centro} y={centro + 14} textAnchor="middle"
          fontFamily="Outfit, sans-serif" fontSize="9" fontWeight="700"
          fill={G.txt2} style={{ letterSpacing: '0.10em',
            textTransform: 'uppercase' }}>
          {rotuloCentro}
        </text>
      </svg>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {segmentos.map((seg, i) => {
          const pct = (seg.valor / total * 100).toFixed(0);
          return (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{ width: 12, height: 12, background: seg.cor,
                borderRadius: 0 }}/>
              <div style={{ fontSize: 12, color: G.txt2, fontWeight: 600,
                letterSpacing: '0.06em', textTransform: 'uppercase' }}>
                {seg.label}
              </div>
              <div style={{ fontSize: 14, color: G.txt1, fontWeight: 800,
                fontVariantNumeric: 'tabular-nums' }}>
                {pct}%
              </div>
              <div style={{ fontSize: 11, color: G.txt2 }}>
                {seg.detalhe}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ── Régua qualitativa (faixas coloridas com marcador) — RGAM ────────────
const ReguaQualitativa = ({ faixas, valorMarcador, rotuloMarcador }) => {
  // faixas: [{ label, min, max, cor }] ordenadas por min crescente
  const minTotal = Math.min(...faixas.map(f => f.min));
  const maxTotal = Math.max(...faixas.map(f => f.max));
  const range = maxTotal - minTotal || 1;
  const posicaoMarcador = ((valorMarcador - minTotal) / range) * 100;
  return (
    <div style={{ width: '100%' }}>
      {/* Barra com faixas */}
      <div style={{ position: 'relative', height: 14, display: 'flex',
        borderRadius: 0, overflow: 'hidden', border: `1px solid ${G.borda}` }}>
        {faixas.map((f, i) => {
          const w = ((f.max - f.min) / range) * 100;
          return (
            <div key={i} style={{ width: `${w}%`, background: f.cor }}/>
          );
        })}
        {/* Marcador */}
        <div style={{ position: 'absolute',
          left: `calc(${Math.max(0, Math.min(100, posicaoMarcador))}% - 6px)`,
          top: -3, width: 12, height: 20,
          background: '#FFF', border: `2px solid ${G.txt1}`,
          borderRadius: 0, pointerEvents: 'none' }}/>
      </div>
      {/* Rótulos das faixas */}
      <div style={{ display: 'flex', marginTop: 6 }}>
        {faixas.map((f, i) => {
          const w = ((f.max - f.min) / range) * 100;
          return (
            <div key={i} style={{ width: `${w}%`,
              fontSize: 10, fontWeight: 700,
              color: f.cor, letterSpacing: '0.06em',
              textTransform: 'uppercase', textAlign: 'center',
              padding: '0 4px' }}>
              {f.label}
              <div style={{ fontSize: 9, color: G.txt2, fontWeight: 600,
                marginTop: 2, letterSpacing: 0, textTransform: 'none' }}>
                {f.faixa}
              </div>
            </div>
          );
        })}
      </div>
      {/* Legenda do marcador */}
      <div style={{ marginTop: 10, fontSize: 11, color: G.txt2,
        fontStyle: 'italic', textAlign: 'center' }}>
        ↑ Marcador: {rotuloMarcador}
      </div>
    </div>
  );
};

// ── Estilos compartilhados das páginas de termo ──────────────────────────
const RGAM_TIPO = {
  h2: { fontSize: 18, fontWeight: 800, color: G.laranja,
    letterSpacing: '0.06em', textTransform: 'uppercase',
    marginTop: 24, marginBottom: 10,
    fontFamily: "'Outfit', sans-serif" },
  p: { fontSize: 14, lineHeight: 1.6, color: G.txt1,
    marginBottom: 10 },
  formula: { fontFamily: 'monospace', fontSize: 14,
    background: G.fundo, border: `1px solid ${G.borda}`,
    borderRadius: 0, padding: '12px 16px',
    color: G.txt1, lineHeight: 1.7, whiteSpace: 'pre-wrap',
    overflow: 'auto' },
  variaveis: { fontSize: 12, lineHeight: 1.7, color: G.txt2,
    marginTop: 8 },
};

// ── Página WACC ─────────────────────────────────────────────────────────
const PaginaWACC = () => {
  // Exemplo canônico — Equity 60%, Dívida 40%, Ke 18%, Kd 12%, IR 34%
  const pE = 0.60, pD = 0.40, Ke = 0.18, Kd = 0.12, IR = 0.34;
  const KdLiq = Kd * (1 - IR);
  const wacc = pE * Ke + pD * KdLiq;
  const pct = (v) => `${(v*100).toFixed(2).replace('.', ',')}%`;
  // Trava scroll do wrapper externo enquanto a página estiver montada e força topo
  useEffect(() => {
    const wrapper = Array.from(document.querySelectorAll('div')).find(el => {
      const cs = getComputedStyle(el);
      return cs.backgroundColor === 'rgb(168, 210, 176)' && cs.flexGrow === '1';
    });
    if (!wrapper) return;
    const anterior = wrapper.style.overflowY;
    wrapper.style.overflowY = 'hidden';
    wrapper.scrollTop = 0;
    return () => { wrapper.style.overflowY = anterior; };
  }, []);
  const cardConteudo = {
    border: `1px solid ${G.borda}`, borderRadius: 0,
    padding: '5px 9px', background: '#FFF',
  };
  const cardHeader = {
    fontSize: 11.5, fontWeight: 800, letterSpacing: '0.08em',
    textTransform: 'uppercase', color: G.txt1, marginBottom: 4,
    display: 'flex', alignItems: 'baseline', gap: 6,
    fontFamily: "'Outfit', sans-serif",
  };
  const cardNumero = { fontSize: 13, fontWeight: 900, color: G.laranja };
  return (
    <div style={{ background: '#FFF', borderRadius: 0,
      boxShadow: '0 4px 18px rgba(0,0,0,0.08)',
      padding: '8px 18px 10px', position: 'relative',
      height: '100%', width: '100%', overflow: 'hidden', boxSizing: 'border-box' }}>
      <CarimboAIdvisor top={-24} right={25} tamanho={120}/>

      {/* HEADER do termo */}
      <div style={{ position: 'relative', paddingBottom: 4,
        borderBottom: `1px solid ${G.borda}`, marginBottom: 6 }}>
        <span style={{ fontSize: 24, fontWeight: 900,
          letterSpacing: '-0.01em', color: G.txt1, lineHeight: 1.05,
          fontFamily: "'Outfit', sans-serif", display: 'inline-block',
          position: 'relative' }}>
          WACC
        </span>
        <span style={{ fontSize: 11, fontWeight: 700,
          letterSpacing: '0.14em', textTransform: 'uppercase',
          color: G.verde, marginLeft: 14 }}>
          Termo 9 · Custos de Capital
        </span>
        <div style={{ fontSize: 13, color: G.txt2, marginTop: 2,
          lineHeight: 1.35, maxWidth: 900 }}>
          Custo Médio Ponderado de Capital — taxa de referência que mistura
          o custo do equity e da dívida e serve de piso para qualquer
          investimento.
        </div>
      </div>

      {/* O QUE É · POR QUE IMPORTA */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr',
        gap: 7, marginBottom: 7 }}>
        <div style={{ padding: '6px 10px', borderRadius: 0,
          background: G.verdeLight, borderLeft: `4px solid ${G.verde}` }}>
          <div style={{ fontSize: 12, fontWeight: 800,
            letterSpacing: '0.08em', textTransform: 'uppercase',
            color: G.verde, marginBottom: 2 }}>
            O que é
          </div>
          <div style={{ fontSize: 12.5, lineHeight: 1.35, color: G.txt1 }}>
            Taxa que pondera o custo do dinheiro próprio (equity) e do
            dinheiro de terceiros (dívida) na proporção em que cada um
            financia a empresa. Referência mínima de retorno para qualquer
            decisão de investir.
          </div>
        </div>
        <div style={{ padding: '6px 10px', borderRadius: 0,
          background: G.laranjaLight, borderLeft: `4px solid ${G.laranja}` }}>
          <div style={{ fontSize: 12, fontWeight: 800,
            letterSpacing: '0.08em', textTransform: 'uppercase',
            color: '#B45309', marginBottom: 2 }}>
            Por que importa
          </div>
          <div style={{ fontSize: 12.5, lineHeight: 1.35, color: G.txt1 }}>
            Projeto que rende menos que o WACC destrói valor da empresa.
            Termômetro para negociar dívida, atrair sócio e decidir
            investimento — quem não bate o WACC consome o capital de quem
            aportou.
          </div>
        </div>
      </div>

      {/* GRID 2×3 cards */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr',
        gap: 8 }}>

        {/* Card 1 · Fórmula */}
        <div style={cardConteudo}>
          <div style={cardHeader}>
            <span style={cardNumero}>1</span>Como é calculado
          </div>
          <div style={{ background: '#F5F5F1', padding: '8px 10px',
            borderRadius: 0, fontFamily: 'Courier New, monospace',
            fontSize: 14, fontWeight: 700, color: G.txt1,
            textAlign: 'center', marginBottom: 6 }}>
            WACC = (E/V × Ke) + (D/V × Kd × (1 − IR))
          </div>
          <div style={{ fontSize: 12, lineHeight: 1.45, color: G.txt2 }}>
            <strong style={{ color: G.txt1 }}>E</strong> equity ·
            <strong style={{ color: G.txt1 }}> D</strong> dívida ·
            <strong style={{ color: G.txt1 }}> V</strong> capital total (E+D) ·
            <strong style={{ color: G.txt1 }}> Ke</strong> custo do equity ·
            <strong style={{ color: G.txt1 }}> Kd</strong> custo da dívida ·
            <strong style={{ color: G.txt1 }}> IR</strong> alíquota de IR
          </div>
        </div>

        {/* Card 2 · Exemplo numérico */}
        <div style={cardConteudo}>
          <div style={cardHeader}>
            <span style={cardNumero}>2</span>Exemplo numérico · PME brasileira
          </div>
          <table style={{ width: '100%', fontSize: 13,
            borderCollapse: 'collapse' }}>
            <tbody>
              {[
                ['Equity (E/V)',         '60%'],
                ['Dívida (D/V)',         '40%'],
                ['Custo do equity (Ke)', '18,0%'],
                ['Custo da dívida (Kd)', '12,0%'],
                ['Alíquota IR',          '34%'],
              ].map(([k, v], i) => (
                <tr key={i}>
                  <td style={{ padding: '2px 0' }}>{k}</td>
                  <td style={{ padding: '2px 0', textAlign: 'right',
                    fontWeight: 700 }}>{v}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div style={{ marginTop: 6, padding: '5px 8px',
            background: G.verdeLight, borderLeft: `3px solid ${G.verde}`,
            fontSize: 13, fontWeight: 700, color: G.verde }}>
            WACC = 0,60 × 18% + 0,40 × 12% × (1−0,34) = <b>{pct(wacc)}</b>
          </div>
        </div>

        {/* Card 3 · Donut + Cascata */}
        <div style={cardConteudo}>
          <div style={cardHeader}>
            <span style={cardNumero}>3</span>Composição do capital
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <svg width="80" height="80" viewBox="0 0 42 42">
              <circle cx="21" cy="21" r="15.915" fill="#FFF"
                stroke={G.verde} strokeWidth="8"
                strokeDasharray="60 40" strokeDashoffset="25"/>
              <circle cx="21" cy="21" r="15.915" fill="transparent"
                stroke={G.laranja} strokeWidth="8"
                strokeDasharray="40 60" strokeDashoffset="-35"/>
            </svg>
            <div style={{ fontSize: 13, lineHeight: 1.55 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{ width: 10, height: 10, borderRadius: '50%',
                  background: G.verde, display: 'inline-block' }}/>
                <b>Equity</b> · 60%
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{ width: 10, height: 10, borderRadius: '50%',
                  background: G.laranja, display: 'inline-block' }}/>
                <b>Dívida</b> · 40%
              </div>
              <div style={{ marginTop: 4, fontSize: 11.5,
                color: G.txt2 }}>
                Proporção típica de PME brasileira em fase de crescimento.
              </div>
            </div>
          </div>
          <div style={{ marginTop: 8, paddingTop: 8,
            borderTop: `1px dashed ${G.borda}`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            gap: 6, flexWrap: 'wrap' }}>
            <span style={{ padding: '4px 8px', borderRadius: 0,
              fontSize: 12.5, fontWeight: 700, lineHeight: 1.1,
              background: G.verdeLight, color: G.verde,
              border: '1px solid #C7E0CD' }}>
              60,00% × Ke 18,00%
            </span>
            <span style={{ fontSize: 12, color: G.txt2, fontWeight: 600 }}>+</span>
            <span style={{ padding: '4px 8px', borderRadius: 0,
              fontSize: 12.5, fontWeight: 700, lineHeight: 1.1,
              background: G.laranjaLight, color: '#B45309',
              border: '1px solid #F4C9A8' }}>
              40,00% × Kd 7,92%
            </span>
            <span style={{ fontSize: 12, color: G.txt2, fontWeight: 600 }}>=</span>
            <span style={{ padding: '4px 10px', borderRadius: 0,
              background: G.txt1, color: '#FFF', fontSize: 16,
              fontWeight: 900, letterSpacing: '0.01em' }}>
              WACC {pct(wacc)}
            </span>
          </div>
          <div style={{ marginTop: 6, fontSize: 11.5, lineHeight: 1.4,
            color: G.txt2, textAlign: 'center' }}>
            Equity custa Ke · dívida custa Kd já líquida de IR · soma
            ponderada = WACC.
          </div>
        </div>

        {/* Card 4 · Régua */}
        <div style={cardConteudo}>
          <div style={cardHeader}>
            <span style={cardNumero}>4</span>Faixa de interpretação · Brasil
          </div>
          <div style={{ display: 'flex', height: 14, borderRadius: 0,
            overflow: 'hidden', position: 'relative' }}>
            {[
              { label: 'Bom',      cor: G.verde },
              { label: 'Médio',    cor: '#EAB308' },
              { label: 'Atenção',  cor: G.laranja },
              { label: 'Alto',     cor: G.vermelho },
            ].map((f, i) => (
              <div key={i} style={{ flex: '0 0 25%', background: f.cor,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 9, fontWeight: 700, color: '#FFF' }}>
                {f.label}
              </div>
            ))}
            <div style={{ position: 'absolute', top: -4, left: '36%',
              width: 2, height: 22, background: G.txt1 }}>
              <span style={{ position: 'absolute', top: -13, left: '50%',
                transform: 'translateX(-50%)', fontSize: 9, fontWeight: 800,
                color: G.txt1, whiteSpace: 'nowrap' }}>
                {pct(wacc)}
              </span>
            </div>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between',
            fontSize: 9, color: G.txt2, marginTop: 4, fontWeight: 600 }}>
            <span>0%</span><span>12%</span><span>18%</span><span>24%</span><span>30%+</span>
          </div>
          <div style={{ fontSize: 10.5, color: G.txt2, marginTop: 6,
            lineHeight: 1.4 }}>
            Exemplo acima ({pct(wacc)}) cai na faixa <b>médio</b> — compatível
            com PME bem estruturada operando no Brasil.
          </div>
        </div>

        {/* Card 5 · Curiosidade */}
        <div style={cardConteudo}>
          <div style={cardHeader}>
            <span style={cardNumero}>5</span>Curiosidade
          </div>
          <div style={{ background: '#F5F5F1',
            borderLeft: '3px solid #C4A574',
            padding: '8px 10px', fontSize: 13, lineHeight: 1.45,
            color: G.txt1, fontStyle: 'italic' }}>
            <b style={{ fontStyle: 'normal' }}>Modigliani &amp; Miller (1958)</b> mostraram
            que, num mundo sem dívida e sem impostos, o valor da empresa
            não muda com a estrutura de capital. Quando se acrescenta IR
            à conta, a dívida cria <b style={{ fontStyle: 'normal' }}>escudo
            fiscal</b> — e aí, sim, faz diferença. Modigliani levou o Nobel
            em 1985 e Miller em 1990.
          </div>
        </div>

        {/* Card 6 · Fontes */}
        <div style={cardConteudo}>
          <div style={cardHeader}>
            <span style={cardNumero}>6</span>Fontes
          </div>
          <ul style={{ fontSize: 12, lineHeight: 1.5, color: G.txt2,
            paddingLeft: 14, margin: 0 }}>
            <li><b style={{ color: G.txt1 }}>Damodaran</b> — NYU Stern, base de prêmios de risco e betas por setor</li>
            <li><b style={{ color: G.txt1 }}>Modigliani &amp; Miller (1958)</b> — The Cost of Capital, AER</li>
            <li><b style={{ color: G.txt1 }}>Brealey &amp; Myers</b> — Princípios de Finanças Corporativas</li>
            <li><b style={{ color: G.txt1 }}>BCB</b> — Histórico de Selic e taxa livre de risco</li>
          </ul>
        </div>

      </div>
    </div>
  );
};

// ── Página DRE ──────────────────────────────────────────────────────────
const PaginaDRE = ({ onAbrirAba, onAbrirTermo }) => {
  return (
    <div>
      {/* HERO */}
      <div style={{ background: '#134721', color: '#FFF',
        borderRadius: 0, padding: '24px 32px', position: 'relative',
        overflow: 'hidden', marginBottom: 18 }}>
        <div style={{ position: 'absolute', right: -10, bottom: -50,
          fontSize: 180, fontWeight: 900, lineHeight: 0.85,
          color: 'rgba(255,255,255,0.05)',
          pointerEvents: 'none', userSelect: 'none',
          letterSpacing: '-0.04em',
          fontFamily: "'Outfit', sans-serif" }}>
          DRE
        </div>
        <div style={{ fontSize: 10, fontWeight: 700,
          color: 'rgba(255,255,255,0.65)', letterSpacing: '0.20em',
          textTransform: 'uppercase', marginBottom: 6 }}>
          Termo 1 · Fundamentos Contábeis
        </div>
        <h1 style={{ fontSize: 32, fontWeight: 900, lineHeight: 1.1,
          letterSpacing: '-0.02em', marginBottom: 8,
          fontFamily: "'Outfit', sans-serif" }}>
          DRE · Receita · Custo · Despesas · Lucros · LAIR
        </h1>
        <div style={{ fontSize: 16, fontWeight: 600,
          color: 'rgba(255,255,255,0.85)', marginBottom: 10,
          fontStyle: 'italic' }}>
          Income Statement · Profit &amp; Loss Statement (P&amp;L)
        </div>
        <div style={{ fontSize: 14, lineHeight: 1.55,
          color: 'rgba(255,255,255,0.92)', maxWidth: 720 }}>
          O filme do desempenho da empresa em um período — mostra, em cascata,
          como cada real de receita é consumido até virar lucro.
        </div>
      </div>

      {/* CORPO */}
      <div style={{ background: '#FFF', borderRadius: 0,
        padding: '24px 32px' }}>

        {/* 1 · O que é */}
        <h2 style={RGAM_TIPO.h2}>1 · O que é</h2>
        <p style={RGAM_TIPO.p}>
          A DRE — <strong>Demonstração do Resultado do Exercício</strong> —
          é o relatório contábil que conta como sua empresa transformou
          vendas em lucro (ou prejuízo) ao longo de um período (mês, trimestre
          ou ano). Diferente do Balanço Patrimonial, que é uma "fotografia"
          em uma data específica, a DRE é o <strong>"filme" do período</strong>:
          começa na linha do que entrou (Receita) e desce, subtraindo custos,
          despesas, juros e impostos, até chegar no Lucro Líquido lá embaixo.
        </p>
        <p style={RGAM_TIPO.p}>
          Cada linha responde a uma pergunta econômica diferente: quanto a
          operação produziu antes das estruturas de financiamento e tributação
          interferirem, quanto o banco levou em juros, quanto o governo levou
          em imposto, e quanto efetivamente sobrou para o dono.
        </p>

        {/* 2 · Por que isso te importa */}
        <h2 style={RGAM_TIPO.h2}>2 · Por que isso te importa</h2>
        <p style={RGAM_TIPO.p}>
          A DRE é o documento que um comprador, banco ou investidor pede
          <strong> primeiro</strong>. Toda análise de valuation, todo múltiplo,
          todo cálculo de WACC, todo earn-out começa lendo a sua DRE. Se ela
          estiver bagunçada, mal classificada ou inconsistente com o Balanço,
          qualquer negociação séria trava na hora.
        </p>
        <p style={RGAM_TIPO.p}>
          Mais que isso: a DRE é o instrumento gerencial que diz
          <strong> onde sua empresa está vazando dinheiro</strong>.
          Margem bruta caindo? Problema em compras ou em precificação. EBIT
          despencando com receita estável? Despesa operacional explodindo.
          LAIR ok mas Lucro Líquido fraco? Estrutura tributária ineficiente
          ou dívida cara. Cada degrau da cascata isola uma causa diferente.
        </p>

        {/* 3 · Como se Apresenta · DRE replicando design da TelaDRE do app */}
        <h2 style={RGAM_TIPO.h2}>3 · Como se Apresenta</h2>
        {(() => {
          const Destaque = ({ label, valor, pct, cor, bold = true, tamanho = 14, separador = false }) => (
            <>
              {separador && <div style={{ borderTop: `2px solid ${G.borda}`, margin: '4px 0' }}/>}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 150px 130px',
                padding: '10px 20px', borderRadius: 0,
                background: cor || 'transparent', alignItems: 'center' }}>
                <span style={{ fontSize: tamanho, fontWeight: bold ? 800 : 400, color: G.txt1 }}>
                  {label}
                </span>
                <span style={{ fontSize: tamanho, fontWeight: bold ? 800 : 600,
                  color: valor >= 0 ? G.txt1 : G.vermelho, textAlign: 'right',
                  fontVariantNumeric: 'tabular-nums' }}>
                  {valor.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                </span>
                <span style={{ fontSize: 12, color: G.txt2, textAlign: 'right' }}>
                  {pct != null ? `${pct.toFixed(1)}%` : ''}
                </span>
              </div>
            </>
          );
          const Deducao = ({ label, valor, pct, sobreBruta = false }) => (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 150px 130px',
              padding: '8px 20px 8px 36px', alignItems: 'center' }}>
              <span style={{ fontSize: 13, color: G.txt2 }}>
                {label}
                {sobreBruta && (
                  <span style={{ fontSize: 11.5, color: G.txt2, opacity: 0.75 }}>
                    {' '}· sobre a receita bruta
                  </span>
                )}
              </span>
              <span style={{ fontSize: 13, color: G.vermelho, textAlign: 'right',
                fontVariantNumeric: 'tabular-nums' }}>
                ({Math.abs(valor).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })})
              </span>
              <span style={{ fontSize: 12, color: G.txt2, textAlign: 'right' }}>
                {pct.toFixed(1)}%
              </span>
            </div>
          );
          return (
            <div style={{ background: G.card, borderRadius: 0,
              overflow: 'hidden', marginBottom: 16 }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 150px 130px',
                padding: '12px 20px', background: G.verde }}>
                {['Linha', 'Valor', '% da Receita Líquida'].map(h => (
                  <span key={h} style={{ fontSize: 11, fontWeight: 700,
                    color: 'rgba(255,255,255,0.7)', textTransform: 'uppercase',
                    textAlign: h !== 'Linha' ? 'right' : 'left' }}>{h}</span>
                ))}
              </div>
              {/* Percentuais sobre a RECEITA LÍQUIDA (R$ 1.260.400), a mesma base
                  da DRE real — este exemplo ficara na base antiga e divergia da
                  tela de verdade: Lucro Bruto 37,0% aqui, 40,2% lá (laudo 2).
                  O material que ENSINA tem de usar a mesma convenção da tela:
                  bruta sem percentual (está acima da base) e o tributo avisando
                  que mede sobre ela. */}
              <Destaque label="Receita Bruta" valor={1370000} pct={null} tamanho={15}/>
              <Deducao label="(-) DAS — Simples Nacional" valor={109600} pct={8.0} sobreBruta/>
              <Destaque label="= Receita Líquida" valor={1260400} pct={100.0} separador/>
              <Deducao label="(-) CPV — Custo do Produto/Serviço Vendido" valor={753450} pct={59.8}/>
              <Destaque label="= Lucro Bruto" valor={506950} pct={40.2}
                cor={G.verdeLight} separador/>
              <Deducao label="(-) Despesas Comerciais" valor={205900} pct={16.3}/>
              <Deducao label="(-) Despesas Administrativas" valor={216000} pct={17.1}/>
              <Destaque label="= EBIT (Resultado Operacional)" valor={85050} pct={6.7}
                cor={G.verdeLight} separador/>
              <Destaque label="(+/-) Resultado Financeiro" valor={600} pct={0.0} bold={false}/>
              <Destaque label="= LAIR (Lucro Antes do IR)" valor={85650} pct={6.8} separador/>
              <Destaque label="= Lucro Líquido" valor={85650} pct={6.8}
                cor={G.verdeLight} tamanho={16} separador/>
            </div>
          );
        })()}

      </div>
    </div>
  );
};

// ── Template universal de página de termo · usado pros 62 termos sem componente próprio
// Conteúdo via prop `conteudo` (objeto de RGAM_TERMOS_CONTEUDO). Quando o campo não é
// preenchido, mostra "(preencher)" — esqueleto pronto pra ir completando termo a termo.
const PaginaTermoTemplate = ({ conteudo }) => {
  useEffect(() => {
    const wrapper = Array.from(document.querySelectorAll('div')).find(el => {
      const cs = getComputedStyle(el);
      return cs.backgroundColor === 'rgb(168, 210, 176)' && cs.flexGrow === '1';
    });
    if (!wrapper) return;
    const anterior = wrapper.style.overflowY;
    wrapper.style.overflowY = 'hidden';
    wrapper.scrollTop = 0;
    return () => { wrapper.style.overflowY = anterior; };
  }, []);
  const cor = conteudo.corCapitulo || G.verde;
  const corHover = conteudo.corCapituloHover || G.verdeLight;
  const placeholder = <em style={{ color: G.txt2 }}>(preencher)</em>;
  const cardConteudo = {
    border: `1px solid ${G.borda}`, borderRadius: 0,
    padding: '5px 9px', background: '#FFF',
  };
  const cardHeader = {
    fontSize: 11.5, fontWeight: 800, letterSpacing: '0.08em',
    textTransform: 'uppercase', color: G.txt1, marginBottom: 4,
    display: 'flex', alignItems: 'baseline', gap: 6,
    fontFamily: "'Outfit', sans-serif",
  };
  const cardNumero = { fontSize: 13, fontWeight: 900, color: G.laranja };
  const titulosCards = [
    'Como é calculado',
    'Exemplo numérico',
    'Visualização',
    'Faixa de interpretação',
    'Curiosidade',
    'Fontes',
  ];
  const cards = conteudo.cards || [];
  return (
    <div style={{ background: '#FFF', borderRadius: 0,
      boxShadow: '0 4px 18px rgba(0,0,0,0.08)',
      padding: '8px 18px 10px', position: 'relative',
      height: '100%', width: '100%', overflow: 'hidden', boxSizing: 'border-box' }}>
      <CarimboAIdvisor top={-24} right={25} tamanho={120}/>

      {/* HEADER */}
      <div style={{ position: 'relative', paddingBottom: 4,
        borderBottom: `1px solid ${G.borda}`, marginBottom: 6 }}>
        <span style={{ fontSize: 24, fontWeight: 900,
          letterSpacing: '-0.01em', color: G.txt1, lineHeight: 1.05,
          fontFamily: "'Outfit', sans-serif", display: 'inline-block' }}>
          {conteudo.nome}
        </span>
        <span style={{ fontSize: 11, fontWeight: 700,
          letterSpacing: '0.14em', textTransform: 'uppercase',
          color: cor, marginLeft: 14 }}>
          Termo {conteudo.numero} · {conteudo.capituloNome}
        </span>
        <div style={{ fontSize: 13, color: G.txt2, marginTop: 2,
          lineHeight: 1.35, maxWidth: 900 }}>
          {conteudo.descricao || placeholder}
        </div>
      </div>

      {/* O QUE É · POR QUE IMPORTA */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr',
        gap: 7, marginBottom: 7 }}>
        <div style={{ padding: '6px 10px', borderRadius: 0,
          background: corHover, borderLeft: `4px solid ${cor}` }}>
          <div style={{ fontSize: 12, fontWeight: 800,
            letterSpacing: '0.08em', textTransform: 'uppercase',
            color: cor, marginBottom: 2 }}>
            O que é
          </div>
          <div style={{ fontSize: 12.5, lineHeight: 1.35, color: G.txt1 }}>
            {conteudo.oQueE || placeholder}
          </div>
        </div>
        <div style={{ padding: '6px 10px', borderRadius: 0,
          background: G.laranjaLight, borderLeft: `4px solid ${G.laranja}` }}>
          <div style={{ fontSize: 12, fontWeight: 800,
            letterSpacing: '0.08em', textTransform: 'uppercase',
            color: '#B45309', marginBottom: 2 }}>
            Por que importa
          </div>
          <div style={{ fontSize: 12.5, lineHeight: 1.35, color: G.txt1 }}>
            {conteudo.porQueImporta || placeholder}
          </div>
        </div>
      </div>

      {/* 6 cards · grid 2×3 */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
        {[0,1,2,3,4,5].map(i => {
          const card = cards[i] || {};
          return (
            <div key={i} style={cardConteudo}>
              <div style={cardHeader}>
                <span style={cardNumero}>{i+1}</span>{card.titulo || titulosCards[i]}
              </div>
              <div>{card.conteudo || placeholder}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// Conteúdo real dos termos preenchidos · merge sobre o esqueleto gerado abaixo.
// Adicionar uma entrada aqui = termo passa a ter conteúdo rico no template.
const RGAM_CONTEUDOS_PREENCHIDOS = {
  g_perpetuidade: {
    descricao: 'Ritmo de crescimento que a empresa mantém depois do horizonte de projeção · usado no Valor Terminal para capturar todo o valor após o Ano 5.',
    oQueE: (<>Taxa anual de crescimento perpétuo aplicada aos fluxos de caixa após o horizonte de projeção. Entra na fórmula do Valor Terminal (modelo de Gordon, que calcula o valor de um ativo que produz fluxos crescendo a uma taxa constante para sempre), para estimar o valor de todos os anos seguintes ao Ano 5.</>),
    porQueImporta: (<>Mais de 50% do Enterprise Value via DCF tipicamente vem do Valor Terminal — e o <b>g</b> é a alavanca mais sensível dele. Um ponto adicional em <b>g</b> pode mover o valuation em 15 a 25%.</>),
    cards: [
      {
        titulo: 'Como é calculado',
        conteudo: (
          <>
            <div style={{ background: '#F5F5F1', padding: '8px 10px',
              borderRadius: 0, fontFamily: 'Courier New, monospace',
              fontSize: 14, fontWeight: 700, color: G.txt1,
              textAlign: 'center', marginBottom: 6 }}>
              VT = FCF₆ / (WACC − g)
            </div>
            <div style={{ fontSize: 12, lineHeight: 1.45, color: G.txt2 }}>
              <strong style={{ color: G.txt1 }}>FCF₆</strong> fluxo de caixa do primeiro ano após o horizonte ·
              <strong style={{ color: G.txt1 }}> WACC</strong> custo médio ponderado de capital ·
              <strong style={{ color: G.txt1 }}> g</strong> taxa de crescimento perpétuo
            </div>
          </>
        ),
      },
      {
        titulo: 'Exemplo numérico · PME brasileira',
        conteudo: (
          <>
            <table style={{ width: '100%', fontSize: 13, borderCollapse: 'collapse' }}>
              <tbody>
                {[
                  ['FCF Ano 6', 'R$ 3,00 mi'],
                  ['WACC',      '14,00%'],
                  ['g',         '3,00%'],
                ].map(([k, v], i) => (
                  <tr key={i}>
                    <td style={{ padding: '2px 0' }}>{k}</td>
                    <td style={{ padding: '2px 0', textAlign: 'right', fontWeight: 700 }}>{v}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div style={{ marginTop: 6, padding: '5px 8px',
              background: G.verdeLight, borderLeft: `3px solid ${G.verde}`,
              fontSize: 12, lineHeight: 1.45, color: G.verde, fontWeight: 600 }}>
              VT (Ano 5) = 3,00 / (0,14 − 0,03) = <b>R$ 27,27 mi</b> · Valor presente = 27,27 / (1,14)⁵ = <b>R$ 14,16 mi</b>
            </div>
          </>
        ),
      },
      {
        titulo: 'Sensibilidade do Valor Terminal a g',
        conteudo: (
          <>
            <table style={{ width: '100%', fontSize: 12, borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: `1px solid ${G.borda}` }}>
                  <th style={{ padding: '3px 0', textAlign: 'left',  fontWeight: 700 }}>g</th>
                  <th style={{ padding: '3px 0', textAlign: 'right', fontWeight: 700 }}>VT no Ano 5</th>
                  <th style={{ padding: '3px 0', textAlign: 'right', fontWeight: 700 }}>Δ vs g 3%</th>
                </tr>
              </thead>
              <tbody>
                {[
                  ['2,00%', 'R$ 25,00 mi', '−9%'],
                  ['3,00%', 'R$ 27,27 mi', 'referência'],
                  ['4,00%', 'R$ 30,00 mi', '+10%'],
                  ['5,00%', 'R$ 33,33 mi', '+22%'],
                ].map((row, i) => (
                  <tr key={i}>
                    {row.map((cell, j) => (
                      <td key={j} style={{ padding: '2px 0', textAlign: j === 0 ? 'left' : 'right' }}>{cell}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
            <div style={{ fontSize: 11, fontStyle: 'italic', color: G.txt2,
              marginTop: 4, lineHeight: 1.35 }}>
              Premissas fixas: WACC 14%, FCF₆ R$ 3,00 mi. Cada ponto adicional em g desloca o valuation entre 9 e 12% na faixa típica.
            </div>
          </>
        ),
      },
      {
        titulo: 'Faixa de interpretação · Brasil',
        conteudo: (
          <>
            <div style={{ display: 'flex', height: 14, borderRadius: 0,
              overflow: 'hidden', position: 'relative' }}>
              {[
                { label: 'Defensivo', cor: G.verde },
                { label: 'Padrão',    cor: '#EAB308' },
                { label: 'Otimista',  cor: G.laranja },
                { label: 'Agressivo', cor: G.vermelho },
              ].map((f, i) => (
                <div key={i} style={{ flex: '0 0 25%', background: f.cor,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 9, fontWeight: 700, color: '#FFF' }}>
                  {f.label}
                </div>
              ))}
              <div style={{ position: 'absolute', top: -4, left: '37%',
                width: 2, height: 22, background: G.txt1 }}>
                <span style={{ position: 'absolute', top: -13, left: '50%',
                  transform: 'translateX(-50%)', fontSize: 9, fontWeight: 800,
                  color: G.txt1, whiteSpace: 'nowrap' }}>3,00%</span>
              </div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between',
              fontSize: 9, color: G.txt2, marginTop: 4, fontWeight: 600 }}>
              <span>0%</span><span>2%</span><span>4%</span><span>6%</span><span>8%+</span>
            </div>
            <div style={{ fontSize: 10.5, color: G.txt2, marginTop: 6, lineHeight: 1.4 }}>
              Brasil padrão fica entre 2,5 e 4% (inflação alvo de 3% mais ganho real modesto).
            </div>
          </>
        ),
      },
      {
        titulo: 'Curiosidade',
        conteudo: (
          <div style={{ background: '#F5F5F1', borderLeft: '3px solid #C4A574',
            padding: '8px 10px', fontSize: 13, lineHeight: 1.45,
            color: G.txt1, fontStyle: 'italic' }}>
            O modelo de crescimento de Gordon (Gordon Growth Model) foi formalizado por <b style={{ fontStyle: 'normal' }}>Myron Gordon em 1959</b> — ele mostrou que o valor de um ativo com crescimento perpétuo constante converge para uma fração simples. Regra de Damodaran: <b style={{ fontStyle: 'normal' }}>g não pode ser maior que a taxa livre de risco do país</b>, porque uma empresa não pode crescer para sempre mais rápido que a economia que a contém.
          </div>
        ),
      },
      {
        titulo: 'Fontes',
        conteudo: (
          <ul style={{ fontSize: 12, lineHeight: 1.5, color: G.txt2,
            paddingLeft: 14, margin: 0 }}>
            <li><b style={{ color: G.txt1 }}>Gordon, M. J. (1959)</b> — "Dividends, Earnings, and Stock Prices"</li>
            <li><b style={{ color: G.txt1 }}>Damodaran</b> — NYU Stern, Investment Valuation</li>
            <li><b style={{ color: G.txt1 }}>BCB Focus</b> — expectativas de IPCA e PIB</li>
            <li><b style={{ color: G.txt1 }}>ABVCAP</b> — práticas de valuation em M&A no Brasil</li>
          </ul>
        ),
      },
    ],
  },

  valor_terminal: {
    descricao: 'Valor que condensa todos os fluxos perpétuos após o horizonte de projeção · representa tipicamente 50 a 70% do Enterprise Value num DCF.',
    oQueE: (<>Valor presente de todos os fluxos de caixa que a empresa gera depois do horizonte de projeção explícita (geralmente após o Ano 5). Como projetar fluxo a fluxo indefinidamente é impraticável, condensa-se todo o futuro num único valor calculado no Ano 5, que é então trazido a valor presente.</>),
    porQueImporta: (<>Tipicamente representa <b>50 a 70% do Enterprise Value</b> num DCF de empresa em crescimento — quem decide a metodologia do VT decide o valuation. Uma pequena mudança no método pode alterar o resultado em mais de 30%.</>),
    cards: [
      {
        titulo: 'Como é calculado · 3 métodos consagrados',
        conteudo: (
          <>
            <div style={{ marginBottom: 4 }}>
              <div style={{ fontSize: 10.5, fontWeight: 800, color: G.txt2,
                letterSpacing: '0.06em', textTransform: 'uppercase' }}>
                Método 1 · Gordon
              </div>
              <div style={{ background: '#F5F5F1', padding: '4px 8px',
                borderRadius: 0, fontFamily: 'Courier New, monospace',
                fontSize: 12, fontWeight: 700, color: G.txt1, marginTop: 2 }}>
                VT = FCF₆ / (WACC − g)
              </div>
            </div>
            <div style={{ marginBottom: 4 }}>
              <div style={{ fontSize: 10.5, fontWeight: 800, color: G.txt2,
                letterSpacing: '0.06em', textTransform: 'uppercase' }}>
                Método 2 · Múltiplo de saída
              </div>
              <div style={{ background: '#F5F5F1', padding: '4px 8px',
                borderRadius: 0, fontFamily: 'Courier New, monospace',
                fontSize: 12, fontWeight: 700, color: G.txt1, marginTop: 2 }}>
                VT = EBITDA₅ × múltiplo setorial
              </div>
            </div>
            <div>
              <div style={{ fontSize: 10.5, fontWeight: 800, color: G.txt2,
                letterSpacing: '0.06em', textTransform: 'uppercase' }}>
                Método 3 · Valor de liquidação
              </div>
              <div style={{ background: '#F5F5F1', padding: '4px 8px',
                borderRadius: 0, fontFamily: 'Courier New, monospace',
                fontSize: 12, fontWeight: 700, color: G.txt1, marginTop: 2 }}>
                VT = valor de venda dos ativos − dívidas
              </div>
            </div>
          </>
        ),
      },
      {
        titulo: 'Exemplo numérico · comparativo dos 3 métodos',
        conteudo: (
          <>
            <div style={{ fontSize: 11, color: G.txt2, marginBottom: 4,
              lineHeight: 1.35 }}>
              FCF₆ R$ 3,00 mi · WACC 14,00% · g 3,00% · EBITDA₅ R$ 4,00 mi · múltiplo 7×
            </div>
            <table style={{ width: '100%', fontSize: 12, borderCollapse: 'collapse' }}>
              <tbody>
                {[
                  ['Gordon',     '3,00 / (0,14 − 0,03)',         'R$ 27,27 mi'],
                  ['Múltiplo',   '4,00 × 7',                     'R$ 28,00 mi'],
                  ['Liquidação', 'venda ativos − dívidas',       'R$ 18,00 mi'],
                ].map((row, i) => (
                  <tr key={i} style={{ borderTop: i === 0 ? 'none' : `1px solid ${G.borda}` }}>
                    <td style={{ padding: '3px 0', fontWeight: 700 }}>{row[0]}</td>
                    <td style={{ padding: '3px 0', fontSize: 11, color: G.txt2,
                      fontFamily: 'Courier New, monospace' }}>{row[1]}</td>
                    <td style={{ padding: '3px 0', textAlign: 'right',
                      fontWeight: 800, color: G.verde }}>{row[2]}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div style={{ marginTop: 5, padding: '4px 8px',
              background: '#FFFBEB', borderLeft: '3px solid #D97706',
              fontSize: 11, lineHeight: 1.35, color: '#92400E' }}>
              Spread entre os métodos: <b>35% (Múltiplo vs Liquidação)</b> — quanto maior o spread, maior a incerteza sobre o valuation.
            </div>
          </>
        ),
      },
      {
        titulo: 'Comparativo visual dos 3 métodos',
        conteudo: (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
            {[
              { label: 'Gordon',     valor: 27.27, cor: G.verde,   max: 30 },
              { label: 'Múltiplo',   valor: 28.00, cor: G.laranja, max: 30 },
              { label: 'Liquidação', valor: 18.00, cor: '#6B7280', max: 30 },
            ].map((b, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: G.txt1,
                  width: 70, flexShrink: 0 }}>
                  {b.label}
                </div>
                <div style={{ flex: 1, background: G.borda, borderRadius: 0,
                  height: 16, position: 'relative', overflow: 'hidden' }}>
                  <div style={{ width: `${(b.valor / b.max) * 100}%`,
                    height: '100%', background: b.cor }}/>
                </div>
                <div style={{ fontSize: 11, fontWeight: 800, color: G.txt1,
                  width: 76, textAlign: 'right', flexShrink: 0 }}>
                  R$ {b.valor.toFixed(2).replace('.', ',')} mi
                </div>
              </div>
            ))}
            <div style={{ fontSize: 10, color: G.txt2, marginTop: 2,
              fontStyle: 'italic' }}>
              Eixo: 0 a R$ 30 mi · 3 métodos comparados nas mesmas premissas.
            </div>
          </div>
        ),
      },
      {
        titulo: 'Quando usar cada método',
        conteudo: (
          <ul style={{ fontSize: 12, lineHeight: 1.55, color: G.txt1,
            paddingLeft: 0, margin: 0, listStyle: 'none' }}>
            {[
              ['Gordon',     'empresa estabilizada com crescimento previsível'],
              ['Múltiplo',   'comparáveis setoriais disponíveis · saída via M&A provável'],
              ['Liquidação', 'empresa em fim de vida ou sem perpetuidade plausível'],
            ].map(([titulo, desc], i) => (
              <li key={i} style={{ marginBottom: 4, paddingLeft: 14, position: 'relative' }}>
                <span style={{ position: 'absolute', left: 0, top: 0,
                  color: '#134721', fontWeight: 900 }}>·</span>
                <b>{titulo}</b> · {desc}
              </li>
            ))}
          </ul>
        ),
      },
      {
        titulo: 'Curiosidade',
        conteudo: (
          <div style={{ background: '#F5F5F1', borderLeft: '3px solid #C4A574',
            padding: '8px 10px', fontSize: 13, lineHeight: 1.45,
            color: G.txt1, fontStyle: 'italic' }}>
            <b style={{ fontStyle: 'normal' }}>Damodaran recomenda sempre calcular pelo menos dois métodos</b> e analisar o spread como medida de incerteza. Múltiplos de saída no Brasil seguem padrão <b style={{ fontStyle: 'normal' }}>ABVCAP por setor</b>: varejo 4 a 6× · SaaS 8 a 12× · indústria 5 a 8×.
          </div>
        ),
      },
      {
        titulo: 'Fontes',
        conteudo: (
          <ul style={{ fontSize: 12, lineHeight: 1.5, color: G.txt2,
            paddingLeft: 14, margin: 0 }}>
            <li><b style={{ color: G.txt1 }}>Damodaran</b> — NYU Stern, Investment Valuation</li>
            <li><b style={{ color: G.txt1 }}>ABVCAP</b> — Múltiplos setoriais Brasil</li>
            <li><b style={{ color: G.txt1 }}>Brealey & Myers</b> — Princípios de Finanças Corporativas</li>
            <li><b style={{ color: G.txt1 }}>Mauboussin (Morgan Stanley)</b> — pesquisa sobre Valor Terminal em DCF</li>
          </ul>
        ),
      },
    ],
  },

  ebitda: {
    descricao: 'Métrica que isola a geração de caixa operacional retirando do Lucro Líquido juros, impostos e depreciação/amortização · moeda do múltiplo EV/EBITDA, padrão M&A para comparar empresas.',
    oQueE: (<>Earnings Before Interest, Taxes, Depreciation and Amortization · Lucro antes de juros, impostos, depreciação e amortização. Retira do Lucro Líquido três grupos de itens que não refletem operação (juros, impostos sobre o lucro e depreciação/amortização) para deixar apenas a geração de caixa operacional pura, comparável entre empresas de estruturas financeiras e regimes diferentes.</>),
    porQueImporta: (<>É a moeda do múltiplo (<b>EV/EBITDA</b>), padrão de mercado em M&A. Permite comparar empresas alavancadas e sem dívida, Simples e Lucro Real, CAPEX antigo e novo, numa base operacional comum. No DCF entra como base do múltiplo de saída do Valor Terminal.</>),
    cards: [
      {
        titulo: 'Como é calculado',
        conteudo: (
          <>
            <div style={{ marginBottom: 4 }}>
              <div style={{ fontSize: 10.5, fontWeight: 800, color: G.txt2,
                letterSpacing: '0.06em', textTransform: 'uppercase' }}>
                Forma 1 · a partir do Lucro Líquido
              </div>
              <div style={{ background: '#F5F5F1', padding: '4px 8px',
                borderRadius: 0, fontFamily: 'Courier New, monospace',
                fontSize: 11.5, fontWeight: 700, color: G.txt1, marginTop: 2 }}>
                EBITDA = LL + Juros + Impostos + Depreciação + Amortização
              </div>
            </div>
            <div style={{ marginBottom: 5 }}>
              <div style={{ fontSize: 10.5, fontWeight: 800, color: G.txt2,
                letterSpacing: '0.06em', textTransform: 'uppercase' }}>
                Forma 2 · a partir do EBIT
              </div>
              <div style={{ background: '#F5F5F1', padding: '4px 8px',
                borderRadius: 0, fontFamily: 'Courier New, monospace',
                fontSize: 11.5, fontWeight: 700, color: G.txt1, marginTop: 2 }}>
                EBITDA = EBIT + Depreciação + Amortização
              </div>
            </div>
            <div style={{ fontSize: 11, lineHeight: 1.4, color: G.txt2 }}>
              <strong style={{ color: G.txt1 }}>LL</strong> lucro líquido ·
              <strong style={{ color: G.txt1 }}> EBIT</strong> lucro operacional ·
              <strong style={{ color: G.txt1 }}> D&A</strong> depreciação e amortização (não envolvem saída de caixa, apenas ajuste contábil)
            </div>
          </>
        ),
      },
      {
        titulo: 'Exemplo numérico · cascata até EBITDA',
        conteudo: (
          <>
            <table style={{ width: '100%', fontSize: 12, borderCollapse: 'collapse' }}>
              <tbody>
                {[
                  ['Receita líquida',                'R$ 10,00 mi', false],
                  ['(−) Custo (CPV)',                'R$ 5,50 mi',  false],
                  ['= Lucro Bruto',                  'R$ 4,50 mi',  true],
                  ['(−) Despesas operacionais',      'R$ 2,80 mi',  false],
                  ['= EBIT (Lucro Operacional)',     'R$ 1,70 mi',  true],
                  ['(+) Depreciação e amortização',  'R$ 0,50 mi',  false],
                  ['= EBITDA',                       'R$ 2,20 mi',  true],
                ].map(([k, v, destaque], i) => (
                  <tr key={i} style={{ background: destaque ? G.verdeLight : 'transparent' }}>
                    <td style={{ padding: '1px 4px', fontSize: 11.5, fontWeight: destaque ? 700 : 400 }}>{k}</td>
                    <td style={{ padding: '1px 4px', fontSize: 11.5, textAlign: 'right',
                      fontWeight: destaque ? 800 : 600,
                      color: destaque ? G.verde : G.txt1 }}>{v}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div style={{ marginTop: 5, padding: '4px 8px',
              background: G.verdeLight, borderLeft: `3px solid ${G.verde}`,
              fontSize: 12, color: G.verde, fontWeight: 600 }}>
              Margem EBITDA = 2,20 / 10,00 = <b>22,00%</b>
            </div>
          </>
        ),
      },
      {
        titulo: 'Cascata DRE até EBITDA',
        conteudo: (
          <>
            <div style={{ display: 'flex', alignItems: 'flex-end',
              gap: 6, height: 62, marginBottom: 2 }}>
              {[
                { label: 'Receita',  valor: 10.0, cor: G.laranja, eixo: 10 },
                { label: 'L. Bruto', valor: 4.5,  cor: G.laranjaLight, borda: G.laranja, eixo: 10 },
                { label: 'EBIT',     valor: 1.7,  cor: G.laranjaLight, borda: G.laranja, eixo: 10 },
                { label: 'EBITDA',   valor: 2.2,  cor: '#134721', eixo: 10 },
              ].map((b, i) => (
                <div key={i} style={{ flex: 1, display: 'flex',
                  flexDirection: 'column', alignItems: 'center', height: '100%' }}>
                  <div style={{ flex: 1, width: '100%', display: 'flex',
                    flexDirection: 'column', justifyContent: 'flex-end' }}>
                    <div style={{ background: b.cor,
                      border: b.borda ? `1px solid ${b.borda}` : 'none',
                      height: `${(b.valor / b.eixo) * 100}%`,
                      borderRadius: 0,
                      display: 'flex', alignItems: 'flex-start',
                      justifyContent: 'center', paddingTop: 2 }}>
                      <span style={{ fontSize: 9, fontWeight: 800,
                        color: i === 3 ? '#FFF' : G.txt1 }}>
                        {b.valor.toFixed(1)}
                      </span>
                    </div>
                  </div>
                  <div style={{ fontSize: 9, fontWeight: 700, color: G.txt1,
                    marginTop: 3, textAlign: 'center' }}>
                    {b.label}
                  </div>
                </div>
              ))}
            </div>
            <div style={{ fontSize: 10, color: G.txt2, fontStyle: 'italic',
              lineHeight: 1.35 }}>
              Eixo: 0 a R$ 10 mi. Receita desce após CPV e despesas até EBIT, sobe novamente quando se acrescenta D&A para chegar ao EBITDA.
            </div>
          </>
        ),
      },
      {
        titulo: 'Faixa de Margem EBITDA · setor',
        conteudo: (
          <>
            <ul style={{ fontSize: 12, lineHeight: 1.55, color: G.txt1,
              paddingLeft: 0, margin: 0, listStyle: 'none' }}>
              {[
                ['Varejo',                  '5 a 12%'],
                ['Indústria',               '10 a 18%'],
                ['Serviços profissionais',  '15 a 25%'],
                ['SaaS / tecnologia',       '25 a 40%'],
              ].map(([setor, faixa], i) => (
                <li key={i} style={{ marginBottom: 3, paddingLeft: 14,
                  position: 'relative' }}>
                  <span style={{ position: 'absolute', left: 0, top: 0,
                    color: '#134721', fontWeight: 900 }}>·</span>
                  <b>{setor}</b> · {faixa}
                </li>
              ))}
            </ul>
            <div style={{ fontSize: 10.5, color: G.txt2, marginTop: 6,
              lineHeight: 1.4, fontStyle: 'italic' }}>
              Margem EBITDA do exemplo (22,00%) é compatível com empresa de serviços profissionais ou indústria de margem alta.
            </div>
          </>
        ),
      },
      {
        titulo: 'Curiosidade',
        conteudo: (
          <div style={{ background: '#F5F5F1', borderLeft: '3px solid #C4A574',
            padding: '8px 10px', fontSize: 13, lineHeight: 1.45,
            color: G.txt1, fontStyle: 'italic' }}>
            <b style={{ fontStyle: 'normal' }}>Warren Buffett</b> tem uma crítica clássica: "EBITDA é como ignorar que o capital se desgasta" — porque retira a depreciação, que representa investimento real em ativos. <b style={{ fontStyle: 'normal' }}>Damodaran</b> responde: use EBITDA para comparar empresas, e Lucro Líquido para julgar resultado. Ambos têm razão — cada métrica responde uma pergunta diferente.
          </div>
        ),
      },
      {
        titulo: 'Fontes',
        conteudo: (
          <ul style={{ fontSize: 12, lineHeight: 1.5, color: G.txt2,
            paddingLeft: 14, margin: 0 }}>
            <li><b style={{ color: G.txt1 }}>Damodaran</b> — NYU Stern, Investment Valuation</li>
            <li><b style={{ color: G.txt1 }}>ABVCAP</b> — múltiplos EV/EBITDA por setor (Brasil)</li>
            <li><b style={{ color: G.txt1 }}>IBRI</b> — Instituto Brasileiro de Relações com Investidores</li>
            <li><b style={{ color: G.txt1 }}>Brealey & Myers</b> — Princípios de Finanças Corporativas</li>
          </ul>
        ),
      },
    ],
  },

  enterprise_value: {
    descricao: 'Valor da empresa para todos os provedores de capital (sócios e credores) · resultado central do DCF e métrica de referência em M&A.',
    oQueE: (<>Valor da empresa hoje considerando todos os fluxos de caixa futuros, somando o que ela vale para os sócios (Equity) e para os credores (Dívida líquida). Numa avaliação DCF, é a soma dos fluxos de caixa descontados ao longo do horizonte de projeção (Anos 1 a 5) mais o Valor Terminal trazido a valor presente.</>),
    porQueImporta: (<>Valor de referência usado em transações M&A — quando alguém diz que "comprou a empresa por R$ X milhões", está falando de <b>Enterprise Value</b> (ou um derivado dele). Independe da estrutura de capital: empresas com mesma operação e estruturas financeiras diferentes têm o mesmo EV.</>),
    cards: [
      {
        titulo: 'Como é calculado',
        conteudo: (
          <>
            <div style={{ marginBottom: 4 }}>
              <div style={{ fontSize: 10.5, fontWeight: 800, color: G.txt2,
                letterSpacing: '0.06em', textTransform: 'uppercase' }}>
                Via DCF
              </div>
              <div style={{ background: '#F5F5F1', padding: '4px 8px',
                borderRadius: 0, fontFamily: 'Courier New, monospace',
                fontSize: 11.5, fontWeight: 700, color: G.txt1, marginTop: 2 }}>
                EV = Σ FCFₜ / (1+WACC)ᵗ + VT / (1+WACC)ⁿ
              </div>
            </div>
            <div style={{ marginBottom: 5 }}>
              <div style={{ fontSize: 10.5, fontWeight: 800, color: G.txt2,
                letterSpacing: '0.06em', textTransform: 'uppercase' }}>
                Via Balanço
              </div>
              <div style={{ background: '#F5F5F1', padding: '4px 8px',
                borderRadius: 0, fontFamily: 'Courier New, monospace',
                fontSize: 11.5, fontWeight: 700, color: G.txt1, marginTop: 2 }}>
                EV = Equity + Dívida líquida   (Dívida total − Caixa)
              </div>
            </div>
            <div style={{ fontSize: 11, lineHeight: 1.4, color: G.txt2 }}>
              <strong style={{ color: G.txt1 }}>FCFₜ</strong> fluxo de caixa do ano t ·
              <strong style={{ color: G.txt1 }}> VT</strong> Valor Terminal ·
              <strong style={{ color: G.txt1 }}> WACC</strong> custo médio ponderado de capital ·
              <strong style={{ color: G.txt1 }}> n</strong> último ano do horizonte
            </div>
          </>
        ),
      },
      {
        titulo: 'Exemplo numérico',
        conteudo: (
          <>
            <table style={{ width: '100%', fontSize: 11.5, borderCollapse: 'collapse' }}>
              <tbody>
                {[
                  ['FCF Ano 1 descontado', 'R$ 1,75 mi', false],
                  ['FCF Ano 2 descontado', 'R$ 1,72 mi', false],
                  ['FCF Ano 3 descontado', 'R$ 1,98 mi', false],
                  ['FCF Ano 4 descontado', 'R$ 2,17 mi', false],
                  ['FCF Ano 5 descontado', 'R$ 2,22 mi', false],
                  ['(=) Σ FCFs descontados', 'R$ 9,84 mi', true],
                  ['(+) VT descontado',     'R$ 14,16 mi', true],
                  ['= Enterprise Value',    'R$ 24,00 mi', true],
                ].map(([k, v, destaque], i) => (
                  <tr key={i} style={{ background: destaque ? G.verdeLight : 'transparent' }}>
                    <td style={{ padding: '1px 4px', fontWeight: destaque ? 700 : 400 }}>{k}</td>
                    <td style={{ padding: '1px 4px', textAlign: 'right',
                      fontWeight: destaque ? 800 : 600,
                      color: destaque ? G.verde : G.txt1 }}>{v}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div style={{ marginTop: 4, padding: '4px 8px',
              background: G.verdeLight, borderLeft: `3px solid ${G.verde}`,
              fontSize: 11.5, color: G.verde, fontWeight: 600 }}>
              VT representa <b>59%</b> do EV — sensibilidade alta a g e WACC.
            </div>
          </>
        ),
      },
      {
        titulo: 'Composição do EV',
        conteudo: (
          <>
            <div style={{ display: 'flex', height: 22, borderRadius: 0,
              overflow: 'hidden', marginBottom: 5 }}>
              <div style={{ flex: '0 0 41%', background: G.verde,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 10.5, fontWeight: 800, color: '#FFF' }}>
                FCFs · R$ 9,84 mi
              </div>
              <div style={{ flex: '0 0 59%', background: G.laranja,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 10.5, fontWeight: 800, color: '#FFF' }}>
                VT · R$ 14,16 mi
              </div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between',
              fontSize: 11, fontWeight: 700, color: G.txt1, marginBottom: 4 }}>
              <span>41%</span><span>Enterprise Value · <b>R$ 24,00 mi</b></span><span>59%</span>
            </div>
            <div style={{ fontSize: 11, color: G.txt2, fontStyle: 'italic',
              lineHeight: 1.4 }}>
              Tipicamente 50 a 70% do EV num DCF de empresa em crescimento vem do Valor Terminal.
            </div>
          </>
        ),
      },
      {
        titulo: 'EV vs Equity Value',
        conteudo: (
          <>
            <div style={{ display: 'flex', alignItems: 'center', gap: 5,
              marginBottom: 5 }}>
              <div style={{ flex: 1, background: '#134721', color: '#FFF',
                padding: '6px 8px', borderRadius: 0, textAlign: 'center' }}>
                <div style={{ fontSize: 9, fontWeight: 700, opacity: 0.85,
                  letterSpacing: '0.06em', textTransform: 'uppercase' }}>EV</div>
                <div style={{ fontSize: 13, fontWeight: 800 }}>R$ 24,00 mi</div>
              </div>
              <span style={{ fontSize: 14, fontWeight: 900, color: G.txt2 }}>−</span>
              <div style={{ flex: 1, background: '#FEE2E2', color: G.vermelho,
                padding: '6px 8px', borderRadius: 0, textAlign: 'center',
                border: '1px solid #FCA5A5' }}>
                <div style={{ fontSize: 9, fontWeight: 700,
                  letterSpacing: '0.06em', textTransform: 'uppercase' }}>Dívida líquida</div>
                <div style={{ fontSize: 13, fontWeight: 800 }}>R$ 4,00 mi</div>
              </div>
              <span style={{ fontSize: 14, fontWeight: 900, color: G.txt2 }}>=</span>
              <div style={{ flex: 1, background: G.verde, color: '#FFF',
                padding: '6px 8px', borderRadius: 0, textAlign: 'center' }}>
                <div style={{ fontSize: 9, fontWeight: 700, opacity: 0.85,
                  letterSpacing: '0.06em', textTransform: 'uppercase' }}>Equity Value</div>
                <div style={{ fontSize: 13, fontWeight: 800 }}>R$ 20,00 mi</div>
              </div>
            </div>
            <div style={{ fontSize: 11, color: G.txt2, lineHeight: 1.4 }}>
              <b style={{ color: G.txt1 }}>EV</b> é o valor da operação. <b style={{ color: G.txt1 }}>Equity Value</b> é o que sobra para os sócios depois de pagar a dívida. Em M&A se negocia EV; o sócio recebe Equity Value.
            </div>
          </>
        ),
      },
      {
        titulo: 'Curiosidade',
        conteudo: (
          <div style={{ background: '#F5F5F1', borderLeft: '3px solid #C4A574',
            padding: '8px 10px', fontSize: 13, lineHeight: 1.45,
            color: G.txt1, fontStyle: 'italic' }}>
            Por que o mercado usa EV em vez de só Equity Value? <b style={{ fontStyle: 'normal' }}>Damodaran</b> responde: porque EV é <b style={{ fontStyle: 'normal' }}>agnóstico à estrutura de capital</b>. Duas empresas com o mesmo EBITDA — uma cheia de dívida, outra sem — devem ter o mesmo EV. O Equity Value varia conforme a dívida. Múltiplos comparativos (EV/EBITDA, EV/Receita) só fazem sentido em EV.
          </div>
        ),
      },
      {
        titulo: 'Fontes',
        conteudo: (
          <ul style={{ fontSize: 12, lineHeight: 1.5, color: G.txt2,
            paddingLeft: 14, margin: 0 }}>
            <li><b style={{ color: G.txt1 }}>Damodaran</b> — NYU Stern, Investment Valuation</li>
            <li><b style={{ color: G.txt1 }}>Koller, Goedhart, Wessels (McKinsey)</b> — Valuation: Measuring and Managing the Value of Companies</li>
            <li><b style={{ color: G.txt1 }}>ABVCAP</b> — práticas de valuation em M&A no Brasil</li>
            <li><b style={{ color: G.txt1 }}>Brealey & Myers</b> — Princípios de Finanças Corporativas</li>
          </ul>
        ),
      },
    ],
  },
};

// Mapa id → conteúdo do termo · gera esqueleto pros 62 termos + mescla conteúdos preenchidos
const RGAM_TERMOS_CONTEUDO = (() => {
  const map = {};
  let n = 0;
  RGAM_CAPITULOS.forEach(cap => {
    cap.items.forEach(item => {
      n++;
      map[item.id] = {
        id: item.id,
        nome: item.nome,
        capituloNome: cap.nome,
        numero: n,
        corCapitulo: cap.cor,
        corCapituloHover: cap.corHover,
        ...(RGAM_CONTEUDOS_PREENCHIDOS[item.id] || {}),
      };
    });
  });
  return map;
})();

// ── Página individual de termo · dispatcher por id ──────────────────────
const PaginaTermoRGAM = ({ termo, onAnterior, onProximo, onVoltarLanding, temAnterior, temProximo, onAbrirAba, onAbrirTermo }) => {
  const t = RGAM_TERMOS.find(x => x.id === termo);
  // Origem do link "Saiba mais" — só aparece se usuário entrou via link contextual
  const [origem, setOrigem] = useState(null);
  useEffect(() => {
    const o = localStorage.getItem('rgam:origem');
    if (o) {
      try { setOrigem(JSON.parse(o)); } catch (e) { setOrigem(null); }
    }
  }, []);
  const voltarPraOrigem = () => {
    if (!origem) return;
    if ((origem.tela === 'businessplan' || origem.tela === 'businessplansu') && origem.subTela) {
      localStorage.setItem('bp60m:subTelaBridge', origem.subTela);
    }
    localStorage.removeItem('rgam:origem');
    window.dispatchEvent(new CustomEvent('rg-navegar', { detail: { tela: origem.tela } }));
  };
  return (
    <div style={{ display: 'flex', flexDirection: 'column',
      height: 'calc(100vh - 103px)' }}>
      <div style={{ maxWidth: 1300, margin: '0 auto',
        padding: '6px 20px 4px', width: '100%', boxSizing: 'border-box',
        flex: 1, minHeight: 0, display: 'flex' }}>
        {termo === 'wacc' ? (
          <PaginaWACC/>
        ) : termo === 'dre' ? (
          <PaginaDRE onAbrirAba={onAbrirAba} onAbrirTermo={onAbrirTermo}/>
        ) : RGAM_TERMOS_CONTEUDO[termo] ? (
          <PaginaTermoTemplate conteudo={RGAM_TERMOS_CONTEUDO[termo]}/>
        ) : (
          <div style={{ background: '#FFF', borderRadius: 0,
            padding: '32px 36px', textAlign: 'center', color: G.txt2,
            fontSize: 13, fontStyle: 'italic' }}>
            <div style={{ fontSize: 22, fontWeight: 900, color: G.txt1,
              marginBottom: 8, fontStyle: 'normal',
              fontFamily: "'Outfit', sans-serif" }}>
              {t?.nome || termo}
            </div>
            Termo sem entrada em RGAM_TERMOS_CONTEUDO.
          </div>
        )}
      </div>

      {/* RODAPÉ FIXO · padrão BP60M (Anterior laranja sólido · Voltar à Landing-page outline · Avançar verde) */}
      <div style={{
        position: 'relative', zIndex: 10, flexShrink: 0,
        background: G.card,
        borderTop: `1px solid ${G.borda}`,
        padding: '10px 28px',
        display: 'flex', justifyContent: 'space-between',
        alignItems: 'center', gap: 14,
      }}>
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={onAnterior}
            disabled={!temAnterior}
            style={{
              ...BP60M_TOKENS.botoes.base,
              ...BP60M_TOKENS.botoes.primario,
              background: G.laranja, color: '#FFF',
              border: `2px solid ${G.laranja}`,
              ...(!temAnterior ? { cursor: 'not-allowed', opacity: 0.45 } : {}),
            }}>
            ← Anterior
          </button>
          <button onClick={onVoltarLanding}
            style={{
              ...BP60M_TOKENS.botoes.base,
              ...BP60M_TOKENS.botoes.editar,
              border: `2px solid ${G.laranja}`, color: G.laranja,
            }}>
            ↩ Voltar à Landing-page
          </button>
          {origem && (
            <button onClick={voltarPraOrigem}
              style={{
                ...BP60M_TOKENS.botoes.base,
                ...BP60M_TOKENS.botoes.primario,
                background: '#8B5CF6', color: '#FFF',
                border: '2px solid #8B5CF6',
                boxShadow: '0 0 0 2px rgba(139,92,246,0.25)',
              }}>
              ↩ Voltar para {origem.label}
            </button>
          )}
        </div>
        <button onClick={onProximo}
          disabled={!temProximo}
          style={{
            ...BP60M_TOKENS.botoes.base,
            ...BP60M_TOKENS.botoes.primario,
            ...(!temProximo ? { cursor: 'not-allowed', opacity: 0.45 } : {}),
          }}>
          Avançar →
        </button>
      </div>
    </div>
  );
};

// ── Wrapper que orquestra Landing ↔ Página de termo ──────────────────────
const TelaRGAM = ({ onAbrirAba }) => {
  const [termoAtivo, setTermoAtivo] = useState(null);
  // Se chegou aqui via link "Saiba mais" do BP60M, abre direto o termo
  useEffect(() => {
    const t = localStorage.getItem('rgam:termoInicial');
    if (t) {
      setTermoAtivo(t);
      localStorage.removeItem('rgam:termoInicial');
    }
  }, []);
  if (termoAtivo) {
    const idx = RGAM_TERMOS.findIndex(t => t.id === termoAtivo);
    const anterior = idx > 0 ? RGAM_TERMOS[idx - 1].id : null;
    const proximo  = idx < RGAM_TERMOS.length - 1 ? RGAM_TERMOS[idx + 1].id : null;
    return (
      <PaginaTermoRGAM
        termo={termoAtivo}
        onAnterior={anterior ? () => setTermoAtivo(anterior) : undefined}
        onProximo={proximo  ? () => setTermoAtivo(proximo)  : undefined}
        onVoltarLanding={() => setTermoAtivo(null)}
        temAnterior={!!anterior}
        temProximo={!!proximo}
        onAbrirAba={onAbrirAba}
        onAbrirTermo={setTermoAtivo}/>
    );
  }
  return <LandingRGAM onAbrirTermo={setTermoAtivo}/>;
};

// ═══════════════════════════════════════════════════════════════════════
// ConteudoTarefa13EstruturacaoFinanceiraSU · Tarefa nova exclusiva do
// BP60M Startup · constrói receitas, custos, impostos, CAPEX, capital de
// giro e runway do zero como premissas que alimentam as Projeções (T14 SU).
// Esqueleto inicial com 7 blocos accordion · conteúdo de cada bloco é
// implementado em iterações seguintes.
// ═══════════════════════════════════════════════════════════════════════
const BLOCOS_ESTRUTURACAO_SU = [
  { num: 1, titulo: 'Receitas Projetadas',
    sub: 'Modelo de receita · ticket médio · volume · curva de crescimento · sazonalidade.' },
  { num: 2, titulo: 'Custos Diretos (Variáveis com Receita)',
    sub: 'CMV/CSV · insumos · frete · taxas · comissões · saída em % sobre Receita Líquida.' },
  { num: 3, titulo: 'Despesas Indiretas Fixas (OPEX)',
    sub: 'Infraestrutura · pessoal administrativo · serviços terceirizados · outros fixos.' },
  { num: 4, titulo: 'Impostos · vinculados ao Regime Tributário',
    sub: 'Regime (Simples · Presumido · Real) · sobre Receita · sobre Lucro · sobre Folha.' },
  { num: 5, titulo: 'Investimentos Iniciais (CAPEX)',
    sub: 'Imobilizado · intangível · pré-operacionais · cronograma mês a mês.' },
  { num: 6, titulo: 'Capital de Giro Inicial',
    sub: 'Necessidade de Capital de Giro · caixa mínimo operacional · reserva de contingência.' },
  { num: 7, titulo: 'Runway',
    sub: 'Burn rate mensal · capital inicial · runway em meses · marcos de captação.' },
];

// Catálogos do Bloco A · Receitas Projetadas (SU). Modelo de receita em
// escolha única · curva de crescimento em faixa (chip-wizard). Vocabulário
// em português (memória linguagem-formal-rungoal · reduzir anglicismos).
const MODELO_RECEITA_SU = [
  { id: 'assinatura',   label: 'Assinatura recorrente', desc: 'Cliente paga periodicamente (mensal/anual) pelo acesso contínuo.' },
  { id: 'transacional', label: 'Transacional (comissão)', desc: 'Ganho por transação intermediada · percentual sobre o volume (marketplace).' },
  { id: 'servico',      label: 'Serviço',               desc: 'Cobrança por projeto, hora ou pacote de serviço entregue.' },
  { id: 'produto',      label: 'Venda de produto',      desc: 'Venda unitária de produto físico ou digital.' },
  { id: 'licenciamento',label: 'Licenciamento',         desc: 'Licença de uso de tecnologia ou marca · contrato por período.' },
];

// Curva de crescimento mensal · faixas com ponto médio para o cálculo.
const CRESCIMENTO_RECEITA_SU = [
  { id: 'ate5',    label: 'Até 5% ao mês',        mid: 0.03 },
  { id: '5a10',    label: '5% a 10% ao mês',      mid: 0.075 },
  { id: '10a20',   label: '10% a 20% ao mês',     mid: 0.15 },
  { id: '20a40',   label: '20% a 40% ao mês',     mid: 0.30 },
  { id: 'acima40', label: 'Acima de 40% ao mês',  mid: 0.45 },
];

// Estimativa inicial da RUNGOAL AIDVISOR (heurística · stand-in da IA real
// até a Fase 3 · memória ia-e-o-consultor). Todo campo nasce pré-preenchido
// com estas referências · o cliente ajusta se quiser. Conjunto 'geral' por
// enquanto · refinamento por setor entra com os catálogos.
// Estruturação Financeira do BP60M — ESVAZIADA em 29/07/2026.
// Eram ~40 campos inventados (ticket 150, volume 100, aluguel 4.000, CAPEX
// 15.000, prazos 30/15/20) idênticos para toda empresa. Foi a origem dos
// R$ 20.300/mês de despesa fixa que apareceram na Mored.
// Fabio, 28/07: "BP60M e Plano anual nascem zerados em empresa nova."
//
// Seguro esvaziar: os 20 consumidores usam `Number(...) || 0`, então campo
// ausente vira zero, nunca NaN. E `blocoACompleto` passa a dizer a verdade —
// antes dava o bloco por preenchido com dado que o consultor nunca informou.
// A constante permanece para não quebrar as referências; o que sai é o DADO.
const IA_REF_ESTRUTURA_SU = {};

// Bloco 3 · Despesas Indiretas Fixas (OPEX) · linhas em R$/mês agrupadas
// em 4 categorias. RGAI pré-estima · cliente confirma/ajusta.
const OPEX_FIXO_SU = [
  { grupo: 'Infraestrutura', linhas: [
    { id: 'opex_aluguel',    label: 'Aluguel' },
    { id: 'opex_condominio', label: 'Condomínio' },
    { id: 'opex_utilidades', label: 'Utilidades (água · energia · internet)' },
    { id: 'opex_manutencao', label: 'Manutenção e limpeza' },
    { id: 'opex_seguros',    label: 'Seguros' },
  ] },
  { grupo: 'Pessoal administrativo', linhas: [
    { id: 'opex_pessoal_adm', label: 'Salários + encargos da equipe não-produtiva' },
  ] },
  { grupo: 'Serviços terceirizados', linhas: [
    { id: 'opex_contabilidade', label: 'Contabilidade' },
    { id: 'opex_juridico',      label: 'Jurídico' },
    { id: 'opex_ti_software',   label: 'TI / Software' },
    { id: 'opex_marketing_fixo',label: 'Marketing fixo' },
    { id: 'opex_consultoria',   label: 'Consultoria' },
  ] },
  { grupo: 'Outros fixos', linhas: [
    { id: 'opex_escritorio',   label: 'Material de escritório' },
    { id: 'opex_tarifas_banco',label: 'Tarifas bancárias' },
    { id: 'opex_esg',          label: 'ESG' },
    { id: 'opex_pd',           label: 'P&D' },
  ] },
];

// Bloco 5 · CAPEX (Investimentos Iniciais) · linhas em R$ (gasto único) agrupadas
// em 3 categorias. RGAI pré-estima · empresário/consultor confirma/ajusta.
const CAPEX_INICIAL_SU = [
  { grupo: 'Imobilizado', linhas: [
    { id: 'capex_equipamentos', label: 'Equipamentos' },
    { id: 'capex_moveis',       label: 'Móveis e instalações' },
    { id: 'capex_obras',        label: 'Reforma / obras' },
  ] },
  { grupo: 'Intangível', linhas: [
    { id: 'capex_software',         label: 'Software e licenças' },
    { id: 'capex_marca',           label: 'Marca / registro (INPI)' },
    { id: 'capex_desenvolvimento', label: 'Desenvolvimento de produto' },
  ] },
  { grupo: 'Pré-operacionais', linhas: [
    { id: 'capex_constituicao',   label: 'Constituição e legal' },
    { id: 'capex_consultorias',   label: 'Consultorias iniciais' },
    { id: 'capex_mkt_lancamento', label: 'Marketing de lançamento' },
  ] },
];

// Bloco 4 · Impostos · alíquotas efetivas de referência por regime (RGAI · heurística).
// Três bases: sobre Receita · sobre Lucro · sobre Folha. Empresário/consultor ajusta.
const IMPOSTOS_REF_SU = {
  'Simples':   { imp_aliq_receita: 8.0,  imp_aliq_lucro: 0.0,  imp_aliq_folha: 0.0 },
  'Presumido': { imp_aliq_receita: 11.3, imp_aliq_lucro: 0.0,  imp_aliq_folha: 20.0 },
  'Real':      { imp_aliq_receita: 9.25, imp_aliq_lucro: 34.0, imp_aliq_folha: 20.0 },
};
const REGIMES_SU = ['Simples', 'Presumido', 'Real'];
const normalizarRegimeSU = (r) => {
  const s = (r || '').toLowerCase();
  if (s.includes('presum')) return 'Presumido';
  if (s.includes('real')) return 'Real';
  return 'Simples';
};

// ═══════════════════════════════════════════════════════════════════════
// Motor financeiro compartilhado · Startup (fonte única T13 SU → T14 Projeções)
// calcularBaseFinSU: extrai da Estruturação Financeira (T13 SU) TODOS os números
// -base do Ano 1 (receita · custo direto · OPEX · impostos · CAPEX · giro).
// A T13 SU e as Projeções (T14) chamam esta mesma função — os dois quadros nunca
// divergem. calcularProjecaoSU projeta 5 anos a partir dessa base.
// ═══════════════════════════════════════════════════════════════════════
const calcularBaseFinSU = (estruturaFin, produtosServicos, empresa, meta) => {
  const obterIA = (campo) => (estruturaFin?.[campo] ?? IA_REF_ESTRUTURA_SU[campo]);

  // ── Receita Ano 1 · ticket × volume, crescendo mês a mês no ponto médio ──
  const recA_ticket         = obterIA('recA_ticket');
  const recA_volume         = obterIA('recA_volume');
  const recA_crescimento_id = obterIA('recA_crescimento_id');
  const cresc = CRESCIMENTO_RECEITA_SU.find(c => c.id === recA_crescimento_id);
  const gMensal = cresc ? cresc.mid : 0;
  const receitaAno1 = (() => {
    const t = Number(recA_ticket) || 0;
    const v0 = Number(recA_volume) || 0;
    if (!t || !v0) return 0;
    let total = 0;
    for (let m = 0; m < 12; m++) total += t * v0 * Math.pow(1 + gMensal, m);
    return Math.round(total);
  })();

  // ── Custo direto · média ponderada pelo mix (vem da Formação de Preço · T4) ──
  const rascunhoT4 = RASCUNHO_TAREFA_4[empresa?.id] || {};
  const produtos = produtosServicos?.portfolio ?? (rascunhoT4.portfolio || []);
  const setor = empresa?.setor;
  let custoDirPctPond = 0;
  produtos.forEach(p => {
    const preco = precoVendaSU(p, setor);
    const custo = custoDiretoUnitSU(p, setor);
    const mix = Number(p.mix) || 0;
    if (preco > 0) custoDirPctPond += (mix / 100) * (custo / preco) * 100;
  });
  const margemContribPct = 100 - custoDirPctPond;
  const custoDirRS = receitaAno1 * (custoDirPctPond / 100);
  const margemContribRS = receitaAno1 - custoDirRS;

  // ── OPEX fixo · R$/mês → ano ──
  const opexTotalMes = OPEX_FIXO_SU.reduce((s, g) =>
    s + g.linhas.reduce((sg, l) => sg + (Number(obterIA(l.id)) || 0), 0), 0);
  const opexTotalAno = opexTotalMes * 12;

  // ── Impostos · por regime tributário ──
  const regime = estruturaFin?.imp_regime ?? normalizarRegimeSU(empresa?.regime);
  const refImp = IMPOSTOS_REF_SU[regime] || IMPOSTOS_REF_SU['Simples'];
  const obterAliq = (campo) => (estruturaFin?.[campo] ?? refImp[campo]);
  const aliqRec = Number(obterAliq('imp_aliq_receita')) || 0;
  const aliqLuc = Number(obterAliq('imp_aliq_lucro')) || 0;
  const aliqFol = Number(obterAliq('imp_aliq_folha')) || 0;
  const folhaAno = (Number(obterIA('opex_pessoal_adm')) || 0) * 12;
  const lucroBase = margemContribRS - opexTotalAno;   // lucro operacional antes de impostos
  const impRec = receitaAno1 * (aliqRec / 100);
  const impLuc = Math.max(0, lucroBase) * (aliqLuc / 100);
  const impFol = folhaAno * (aliqFol / 100);
  const impTotal = impRec + impLuc + impFol;

  // ── CAPEX inicial (gasto único) ──
  const capexTotal = CAPEX_INICIAL_SU.reduce((s, g) =>
    s + g.linhas.reduce((sg, l) => sg + (Number(obterIA(l.id)) || 0), 0), 0);

  // ── Capital de giro inicial · NCG + caixa mínimo + reserva ──
  const cgDiasReceber = Number(obterIA('cg_dias_receber')) || 0;
  const cgDiasEstoque = Number(obterIA('cg_dias_estoque')) || 0;
  const cgDiasPagar   = Number(obterIA('cg_dias_pagar')) || 0;
  const cgMesesCaixa  = Number(obterIA('cg_meses_caixa')) || 0;
  const cgReservaPct  = Number(obterIA('cg_reserva_pct')) || 0;
  const cgNCG = (cgDiasReceber * receitaAno1 + (cgDiasEstoque - cgDiasPagar) * custoDirRS) / 360;
  const cgNCGnec = Math.max(0, cgNCG);
  const custoFixoMensal = opexTotalMes + (impTotal / 12);
  const cgCaixaMinimo = cgMesesCaixa * custoFixoMensal;
  const cgSubtotal = cgNCGnec + cgCaixaMinimo;
  const cgReserva = cgSubtotal * (cgReservaPct / 100);
  const cgTotal = cgSubtotal + cgReserva;

  // ── Vínculo com a Meta (T1) · CAGR necessário para o alvo de receita em 5 anos ──
  const isReceitaAlvo = meta?.tipo === 'receita_alvo';
  const gAnualEscolhido = Math.pow(1 + gMensal, 12) - 1;
  const metaReceitaAno5 = isReceitaAlvo ? (Number(meta?.valor) || 0) * 1e6 : null;
  const cagrNecessario = (metaReceitaAno5 && receitaAno1 > 0)
    ? Math.pow(metaReceitaAno5 / receitaAno1, 1 / 4) - 1 : null;

  return {
    receitaAno1, custoDirPctPond, margemContribPct, custoDirRS, margemContribRS,
    opexTotalMes, opexTotalAno,
    regime, aliqRec, aliqLuc, aliqFol, folhaAno,
    lucroBase, impRec, impLuc, impFol, impTotal,
    capexTotal,
    cgNCG, cgNCGnec, custoFixoMensal, cgCaixaMinimo, cgSubtotal, cgReserva, cgTotal,
    gMensal, gAnualEscolhido, cagrNecessario, metaReceitaAno5, isReceitaAlvo,
  };
};

// Perfis de crescimento de receita por tese de meta (Startup · heurística RGAI ·
// substituída pela IA real na Fase 3). Vetor de crescimento ANO-A-ANO [g2,g3,g4,g5]
// (frações) · sempre DECRESCENTE (tapering) para não explodir a projeção.
const PERFIS_CRESCIMENTO_SU = {
  ambicioso: [1.00, 0.70, 0.50, 0.35],   // valuation-alvo · saída via IPO/aquisição
  tracao:    [0.80, 0.55, 0.40, 0.30],   // tração-alvo (uso · MRR/GMV)
  padrao:    [0.60, 0.45, 0.35, 0.25],   // genérico
};
const perfilCrescimentoTeseSU = (metaTipo) => {
  if (metaTipo === 'valuation_alvo' || metaTipo === 'saida_ipo_ma') return 'ambicioso';
  if (metaTipo === 'tracao_alvo') return 'tracao';
  return 'padrao';
};
// Curva sugerida pela RUNGOAL AIDVISOR para a tese. Meta de receita → CAGR constante
// até o alvo (bounded). Demais teses → perfil decrescente do catálogo acima.
const curvaCrescimentoSU = (base, meta) => {
  const b = base || {};
  if (meta?.tipo === 'receita_alvo' && b.cagrNecessario != null && Number.isFinite(b.cagrNecessario)) {
    const c = Math.max(0, b.cagrNecessario);
    return [c, c, c, c];
  }
  return PERFIS_CRESCIMENTO_SU[perfilCrescimentoTeseSU(meta?.tipo)] || PERFIS_CRESCIMENTO_SU.padrao;
};

// Projeta 5 anos a partir da base (Ano 1). Receita cresce pela CURVA por tese (ou
// pelo override editado pelo usuário) · OPEX cresce a ~metade do ritmo de cada ano
// (alavancagem operacional) · custo direto mantém % constante da receita · CAPEX só
// no Ano 1 · giro escala com a receita (só o incremento a partir do Ano 2).
const calcularProjecaoSU = (base, meta, curvaOverride) => {
  const b = base || {};
  const receita0 = Number(b.receitaAno1) || 0;
  const curva = (Array.isArray(curvaOverride) && curvaOverride.length === 4)
    ? curvaOverride.map(x => (Number.isFinite(Number(x)) ? Number(x) : 0))
    : curvaCrescimentoSU(b, meta);
  const custoPct = (Number(b.custoDirPctPond) || 0) / 100;  // % constante da receita
  const aliqRec = (Number(b.aliqRec) || 0) / 100;
  const aliqLuc = (Number(b.aliqLuc) || 0) / 100;
  const aliqFol = (Number(b.aliqFol) || 0) / 100;
  const capex0  = Number(b.capexTotal) || 0;
  const cg0     = Number(b.cgTotal) || 0;

  const anos = [];
  let receita  = receita0;
  let opexAno  = Number(b.opexTotalAno) || 0;
  let folhaAno = Number(b.folhaAno) || 0;
  let cgAnt    = 0;
  for (let i = 1; i <= 5; i++) {
    if (i > 1) {
      const g = Number(curva[i - 2]) || 0;   // crescimento do ano (fração)
      const gOpex = g / 2;                    // alavancagem operacional
      receita  = receita  * (1 + g);
      opexAno  = opexAno  * (1 + gOpex);
      folhaAno = folhaAno * (1 + gOpex);
    }
    const custoDir      = receita * custoPct;
    const margemContrib = receita - custoDir;
    const ebitda        = margemContrib - opexAno;           // lucro operacional (proxy EBITDA)
    const margemPct     = receita > 0 ? ebitda / receita : 0;
    const impRec        = receita * aliqRec;
    const impLuc        = Math.max(0, ebitda) * aliqLuc;
    const impFol        = folhaAno * aliqFol;
    const impostos      = impRec + impLuc + impFol;
    const lucroLiquido  = ebitda - impostos;
    const capex         = i === 1 ? capex0 : 0;              // CAPEX só no Ano 1
    const cgAcum        = receita0 > 0 ? cg0 * (receita / receita0) : (i === 1 ? cg0 : 0);
    const deltaCG       = i === 1 ? cg0 : Math.max(0, cgAcum - cgAnt);
    cgAnt = cgAcum;
    const fcf           = ebitda - impostos - deltaCG - capex;
    anos.push({
      ano: `Ano ${i}`,
      receita, custoDir, margemContrib, opex: opexAno,
      ebitda, margemPct, impostos, lucroLiquido,
      capex, deltaCG, fcf,
    });
  }
  return anos;
};

// ═══════════════════════════════════════════════════════════════════════
// Valuation Inicial de Startup (T15 SU) · 2 métodos: Múltiplos + Scorecard
// Heurística RGAI (substituída pela IA real na Fase 3). Startup usa múltiplo de
// RECEITA (EBITDA costuma ser negativo no início).
// ═══════════════════════════════════════════════════════════════════════
const multiploReceitaSetorSU = (setor) => {
  const s = (setor || '').toLowerCase();
  if (/(software|saas|tecnolog|tech|app|plataforma|digital)/.test(s)) return 6;
  if (/(sa[úu]de|cl[íi]nica|health|bio|farmac)/.test(s)) return 3.5;
  if (/(servi[çc]o|consultor|educa)/.test(s)) return 2.5;
  if (/(com[ée]rcio|varejo|e-?commerce|loja|restaurante|aliment)/.test(s)) return 2;
  if (/(ind[úu]stria|constru|manufatur)/.test(s)) return 1.8;
  return 3;   // geral
};

// DCF · valor presente do FCF projetado (T14) + valor terminal (Gordon). WACC alto (startup).
const WACC_PADRAO_SU = 0.25;    // taxa de desconto de referência para startup (fração · editável)
const G_PERP_PADRAO_SU = 0.04;  // crescimento na perpetuidade (fração · editável)
const MULT_SAIDA_EBITDA_SU = 6; // múltiplo de saída (EBITDA) no VC Method · faixa startup 5–10× (editável)
const RETORNO_ALVO_SU = 0.40;   // retorno-alvo do investidor de risco · fração/ano (editável)

// WACC real da empresa · mesma fórmula do backend (calculator.py) e da aba Valor:
// (E/T)·Ke + (D/T)·Kd·(1−IR). Retorna fração, ou null se faltar estrutura de capital.
const calcularWACCReal = (parametros) => {
  const p = parametros || {};
  const ke = Number(p.wacc_ke), kd = Number(p.wacc_kd);
  const e = Number(p.wacc_e), d = Number(p.wacc_d);
  const ir = Number.isFinite(Number(p.ir)) ? Number(p.ir) : 0.15;
  const total = (Number.isFinite(e) ? e : 0) + (Number.isFinite(d) ? d : 0);
  if (!total || !Number.isFinite(ke) || !Number.isFinite(kd)) return null;
  return (e / total) * ke + (d / total) * kd * (1 - ir);
};

// Calcula o valuation SU pelos 2 métodos + síntese (faixa + valor central).
// waccDefaultPct/gDefaultPct vêm da MESMA fonte da aba Valor (WACC real + perpetuidade).
const calcularValuationSU = (base, anosSU, empresa, valuation, waccDefaultPct, gDefaultPct) => {
  const anos = Array.isArray(anosSU) ? anosSU : [];

  // ── Método 1 · Múltiplos comparáveis (receita de um ano de referência) ──
  const multIA = multiploReceitaSetorSU(empresa?.setor);
  const mult = Number(valuation?.val_mult ?? multIA) || 0;
  const anoRef = Math.min(5, Math.max(1, Number(valuation?.val_ano_ref ?? 1) || 1));
  const receitaRef = anos[anoRef - 1] ? (Number(anos[anoRef - 1].receita) || 0) : 0;
  const evMultiplos = mult * receitaRef;

  // ── Método 2 · DCF (FCF projetado da T14 + WACC + valor terminal de Gordon) ──
  // Defaults vêm da mesma fonte da aba Valor (WACC real da estrutura de capital · perpetuidade).
  const waccPadraoPct = (Number.isFinite(Number(waccDefaultPct)) && Number(waccDefaultPct) > 0) ? Number(waccDefaultPct) : WACC_PADRAO_SU * 100;
  const gPadraoPct = Number.isFinite(Number(gDefaultPct)) ? Number(gDefaultPct) : G_PERP_PADRAO_SU * 100;
  const waccPct = (valuation?.val_wacc != null && Number.isFinite(Number(valuation.val_wacc)) && Number(valuation.val_wacc) > 0)
    ? Number(valuation.val_wacc) : waccPadraoPct;
  const gPct = (valuation?.val_g != null && Number.isFinite(Number(valuation.val_g)))
    ? Number(valuation.val_g) : gPadraoPct;
  const wacc = waccPct / 100;
  const gPerp = gPct / 100;
  // Decomposição ano a ano (para a tabela didática): FCF · fator de desconto · FCF descontado.
  const fcfDetalhe = anos.map((a, i) => {
    const fcf = Number(a.fcf) || 0;
    const fator = 1 / Math.pow(1 + wacc, i + 1);
    return { ano: a.ano, fcf, fator, fcfDesc: fcf * fator };
  });
  const vpFcf = fcfDetalhe.reduce((s, x) => s + x.fcfDesc, 0);
  const fcf5 = anos.length ? (Number(anos[anos.length - 1].fcf) || 0) : 0;
  // Valor terminal só faz sentido com WACC > g e FCF final positivo (senão, 0).
  const valorTerminal = (wacc > gPerp && fcf5 > 0) ? (fcf5 * (1 + gPerp)) / (wacc - gPerp) : 0;
  const fatorTerminal = 1 / Math.pow(1 + wacc, anos.length || 5);
  const vpTerminal = valorTerminal * fatorTerminal;
  const evDCF = vpFcf + vpTerminal;

  // ── Método 3 · Capital de Risco (VC Method) · saída via múltiplo de EBITDA, descontada ──
  const ebitdaAno5 = anos.length ? (Number(anos[anos.length - 1].ebitda) || 0) : 0;
  const multSaidaEbitda = (valuation?.val_mult_saida != null && Number.isFinite(Number(valuation.val_mult_saida)) && Number(valuation.val_mult_saida) > 0)
    ? Number(valuation.val_mult_saida) : MULT_SAIDA_EBITDA_SU;
  const retornoAlvoPct = (valuation?.val_retorno != null && Number.isFinite(Number(valuation.val_retorno)) && Number(valuation.val_retorno) > 0)
    ? Number(valuation.val_retorno) : RETORNO_ALVO_SU * 100;
  const retornoAlvo = retornoAlvoPct / 100;
  const valorSaida = ebitdaAno5 > 0 ? multSaidaEbitda * ebitdaAno5 : 0;   // valor de venda no Ano 5
  const fatorVC = 1 / Math.pow(1 + retornoAlvo, anos.length || 5);
  const evVC = valorSaida * fatorVC;

  // ── Síntese · faixa (menor–maior) + valor central (média dos métodos válidos) ──
  const valores = [evMultiplos, evDCF, evVC].filter(x => Number.isFinite(x) && x > 0);
  const faixaMin = valores.length ? Math.min(...valores) : 0;
  const faixaMax = valores.length ? Math.max(...valores) : 0;
  const central = valores.length ? valores.reduce((s, x) => s + x, 0) / valores.length : 0;

  return {
    mult, multIA, anoRef, receitaRef, evMultiplos,
    waccPct, gPct, fcfDetalhe, vpFcf, fcf5, valorTerminal, fatorTerminal, vpTerminal, evDCF,
    ebitdaAno5, multSaidaEbitda, retornoAlvoPct, valorSaida, fatorVC, evVC,
    faixaMin, faixaMax, central,
  };
};

const ConteudoTarefa13EstruturacaoFinanceiraSU = ({ empresa, estruturaFin, setEstruturaFin, produtosServicos, meta }) => {
  const HEADER_BLOCO = {
    fontSize: 16, fontWeight: 700, letterSpacing: '0.10em',
    textTransform: 'uppercase', color: G.laranja, marginBottom: 8,
  };
  const HEADER_AUX = {
    fontSize: 12, fontWeight: 400, color: G.txt2,
    textTransform: 'none', letterSpacing: 0, marginLeft: 10,
  };
  const ROT_CAMPO = {
    fontSize: 10.5, fontWeight: 800, color: G.txt2,
    letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 6,
  };

  // Estado · forma funcional (memória setstate-funcional-atualizar)
  const obter = (campo, padrao) => (estruturaFin?.[campo] ?? padrao);
  const atualizar = (campo, valor) =>
    setEstruturaFin(prev => ({ ...(prev || {}), [campo]: valor }));
  // Valor exibido = o que o cliente salvou OU a estimativa inicial da
  // RUNGOAL AIDVISOR (memória ia-e-o-consultor · campo nunca nasce vazio).
  const obterIA = (campo) => (estruturaFin?.[campo] ?? IA_REF_ESTRUTURA_SU[campo]);

  // Fonte única · a T13 SU e a T14 (Projeções) leem os mesmos números-base.
  const _b = calcularBaseFinSU(estruturaFin, produtosServicos, empresa, meta);

  // Bloco A · campos (nascem com a estimativa da RUNGOAL AIDVISOR)
  const recA_modelo_id     = obterIA('recA_modelo_id');
  const recA_ticket        = obterIA('recA_ticket');
  const recA_volume        = obterIA('recA_volume');
  const recA_crescimento_id= obterIA('recA_crescimento_id');
  const recA_sazonalidade  = obter('recA_sazonalidade', false);

  // Receita Ano 1 estimada · volume cresce mês a mês no ponto médio da faixa.
  const fmtBRL = (n) => (Number(n) || 0).toLocaleString('pt-BR',
    { style: 'currency', currency: 'BRL',
      minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const receitaAno1 = _b.receitaAno1;
  const blocoACompleto = !!recA_modelo_id && !!recA_ticket && !!recA_volume && !!recA_crescimento_id;

  // ── Conteúdo real do Bloco A ──────────────────────────────────────────
  // Coerência com a Meta (Tarefa 1) — vínculo T1 → T13 (painel comparativo · P1=A)
  const metaDefSU = TIPOS_META_BP60M_SU.find(t => t.id === meta?.tipo);
  const isReceitaAlvoSU = meta?.tipo === 'receita_alvo';
  const gAnualEscolhidoSU = _b.gAnualEscolhido;
  const cagrNecessarioSU = _b.cagrNecessario;
  const metaAlinhadaSU = cagrNecessarioSU != null ? gAnualEscolhidoSU >= cagrNecessarioSU : null;
  const fmtPctAnoSU = (x) => (x * 100).toLocaleString('pt-BR', { maximumFractionDigits: 0 }) + '%/ano';
  const corCoerSU = metaAlinhadaSU == null ? G.txt2 : (metaAlinhadaSU ? G.verde : G.laranja);
  const bgCoerSU = metaAlinhadaSU == null ? G.fundo : (metaAlinhadaSU ? G.verdeLight : G.laranjaLight);

  const conteudoBlocoA = (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div style={{ padding: '10px 14px', background: G.verdeLight,
        borderLeft: `3px solid ${G.verde}`, borderRadius: 0,
        fontSize: 12, lineHeight: 1.45, color: G.txt1 }}>
        A <MarcaAIdvisor /> já preencheu uma <strong>estimativa inicial</strong> de
        cada campo (sem histórico para puxar · usa referências do setor e do modelo
        de negócio). Clique em qualquer campo para ajustar ao seu caso.
      </div>

      {/* Modelo de receita · escolha única (chips) */}
      <div>
        <div style={ROT_CAMPO}>Modelo de receita</div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          {MODELO_RECEITA_SU.map(m => {
            const sel = recA_modelo_id === m.id;
            return (
              <button key={m.id} type="button"
                onClick={() => atualizar('recA_modelo_id', m.id)}
                title={m.desc}
                style={{ padding: '7px 12px', fontSize: 12, fontWeight: 600,
                  background: sel ? G.verde : '#FFF',
                  color: sel ? '#FFF' : G.txt1,
                  border: `1px solid ${sel ? G.verde : G.borda}`,
                  borderRadius: 999, cursor: 'pointer',
                  fontFamily: "'Outfit', sans-serif" }}>
                {sel ? '✓ ' : ''}{m.label}
              </button>
            );
          })}
        </div>
        {recA_modelo_id && (
          <div style={{ fontSize: 11.5, color: G.txt2, fontStyle: 'italic',
            marginTop: 6, lineHeight: 1.4 }}>
            {MODELO_RECEITA_SU.find(m => m.id === recA_modelo_id)?.desc}
          </div>
        )}
      </div>

      {/* Ticket médio + Volume mensal inicial */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <div>
          <div style={ROT_CAMPO}>Ticket médio</div>
          <CampoNumericoBP60M
            valor={recA_ticket}
            onChange={(v) => atualizar('recA_ticket', v)}
            label="Ticket médio (R$ por cliente/transação)"
            permitirDecimal={true}
            min={0}
            formato="brl"
          />
        </div>
        <div>
          <div style={ROT_CAMPO}>Volume mensal inicial</div>
          <CampoNumericoBP60M
            valor={recA_volume}
            onChange={(v) => atualizar('recA_volume', v)}
            label="Volume mensal inicial (clientes/pedidos por mês)"
            permitirDecimal={false}
            min={0}
            sufixo="/mês"
            formato="int"
          />
        </div>
      </div>

      {/* Curva de crescimento · faixa (chips) */}
      <div>
        <div style={ROT_CAMPO}>Curva de crescimento</div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          {CRESCIMENTO_RECEITA_SU.map(c => {
            const sel = recA_crescimento_id === c.id;
            return (
              <button key={c.id} type="button"
                onClick={() => atualizar('recA_crescimento_id', c.id)}
                style={{ padding: '7px 12px', fontSize: 12, fontWeight: 600,
                  background: sel ? G.verde : '#FFF',
                  color: sel ? '#FFF' : G.txt1,
                  border: `1px solid ${sel ? G.verde : G.borda}`,
                  borderRadius: 999, cursor: 'pointer',
                  fontFamily: "'Outfit', sans-serif" }}>
                {sel ? '✓ ' : ''}{c.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Sazonalidade · liga/desliga */}
      <div>
        <div style={ROT_CAMPO}>Sazonalidade</div>
        <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8,
          cursor: 'pointer', fontSize: 12.5, color: G.txt1,
          fontFamily: "'Outfit', sans-serif" }}>
          <input type="checkbox" checked={!!recA_sazonalidade}
            onChange={e => atualizar('recA_sazonalidade', e.target.checked)} />
          O faturamento varia por época do ano (a IA distribui ao detalhar os meses)
        </label>
      </div>

      {/* Receita Ano 1 estimada · calculada read-only */}
      <div style={{ background: '#FFF', border: `1px solid ${G.borda}`,
        borderLeft: `3px solid ${G.verde}`, borderRadius: 0,
        padding: '12px 14px', display: 'flex', alignItems: 'baseline',
        justifyContent: 'space-between', gap: 14 }}>
        <div style={{ fontSize: 10, fontWeight: 800, color: G.txt2,
          letterSpacing: '0.10em', textTransform: 'uppercase' }}>
          Receita estimada · Ano 1
        </div>
        <div style={{ fontSize: 22, fontWeight: 900, color: G.verde,
          fontFamily: "'IBM Plex Mono', monospace" }}>
          {blocoACompleto ? fmtBRL(receitaAno1) : '—'}
        </div>
      </div>
      {!blocoACompleto && (
        <div style={{ fontSize: 11.5, color: G.txt2, fontStyle: 'italic' }}>
          Preencha modelo · ticket · volume · curva de crescimento para a
          <MarcaAIdvisor /> estimar a receita do primeiro ano.
        </div>
      )}

      {/* Coerência com a Meta (Tarefa 1) — vínculo T1 → T13 */}
      {metaDefSU && (
        <div style={{ background: bgCoerSU, border: `1px solid ${corCoerSU}`,
          borderLeft: `3px solid ${corCoerSU}`, borderRadius: 0, padding: '10px 14px',
          fontSize: 12, lineHeight: 1.5, color: G.txt1 }}>
          <div style={{ fontSize: 10, fontWeight: 800, color: corCoerSU,
            letterSpacing: '0.10em', textTransform: 'uppercase', marginBottom: 4 }}>
            Coerência com a Meta (Tarefa 1)
          </div>
          {isReceitaAlvoSU ? (
            (blocoACompleto && receitaAno1 > 0 && cagrNecessarioSU != null) ? (
              <div>
                Meta (T1): <strong>Atingir Receita-Alvo · R$ {Number(meta?.valor) || 0} mi</strong> no ano 5.
                Partindo de <strong>{fmtBRL(receitaAno1)}</strong> no ano 1, o crescimento precisa
                ser ~<strong>{fmtPctAnoSU(cagrNecessarioSU)}</strong>. Seu ritmo escolhido equivale
                a ~<strong>{fmtPctAnoSU(gAnualEscolhidoSU)}</strong>.{' '}
                <span style={{ fontWeight: 800, color: corCoerSU }}>
                  {metaAlinhadaSU ? '✓ alinhado' : <><IconeAlerta /> abaixo da meta</>}
                </span>
              </div>
            ) : (
              <div>
                Meta (T1): <strong>Atingir Receita-Alvo · R$ {Number(meta?.valor) || 0} mi</strong> no
                ano 5. Preencha ticket · volume · crescimento para comparar com a projeção.
              </div>
            )
          ) : (
            <div>
              Meta (T1): <strong>{metaDefSU.titulo}</strong>. A checagem de coerência de receita
              se aplica à meta "Atingir Receita-Alvo".
            </div>
          )}
        </div>
      )}
    </div>
  );

  // ── Bloco 2 · Custos Diretos · CONSOLIDAÇÃO (leitura) vinda da T4 ──────
  // A Formação de Preço (Tarefa 4) montou custo direto e preço por produto.
  // Aqui só consolidamos · custo direto % da receita = média ponderada pelo
  // mix · margem de contribuição = consequência. Nada se digita neste bloco.
  const fmtBRL2 = (n) => (Number(n) || 0).toLocaleString('pt-BR',
    { style: 'currency', currency: 'BRL',
      minimumFractionDigits: 2, maximumFractionDigits: 2 });
  // Mesma fonte efetiva da T4 · estado salvo OU rascunho sugerido pela RGAI.
  const rascunhoT4 = RASCUNHO_TAREFA_4[empresa?.id] || {};
  const produtosT13 = produtosServicos?.portfolio ?? (rascunhoT4.portfolio || []);
  const setorT13 = empresa?.setor;
  // % do custo direto sobre a receita, ponderado pelo mix de cada produto.
  let somaMixT13 = 0;
  produtosT13.forEach(p => {
    const preco = precoVendaSU(p, setorT13);
    const mix = Number(p.mix) || 0;
    if (preco > 0) somaMixT13 += mix;
  });
  const temProdutos = produtosT13.length > 0 && somaMixT13 > 0;
  const custoDirPctPond = _b.custoDirPctPond;
  const margemContribPct = _b.margemContribPct;
  const custoDirRS = _b.custoDirRS;
  const margemContribRS = _b.margemContribRS;

  const conteudoBloco2 = (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div style={{ padding: '10px 14px', background: G.verdeLight,
        borderLeft: `3px solid ${G.verde}`, borderRadius: 0,
        fontSize: 12, lineHeight: 1.45, color: G.txt1 }}>
        Os custos diretos vêm da <strong>Formação de Preço</strong> (Tarefa 4) ·
        a <MarcaAIdvisor /> consolida aqui o custo direto e a margem de contribuição
        do conjunto, ponderados pelo mix de receita. Para ajustar, edite os custos
        de cada produto na Tarefa 4.
      </div>

      {!temProdutos ? (
        <div style={{ fontSize: 12, color: G.txt2, fontStyle: 'italic',
          padding: '12px 14px', background: G.fundo, border: `1px solid ${G.borda}`,
          borderRadius: 0 }}>
          Defina os produtos/serviços e o mix de receita na Tarefa 4 (Produtos &
          Serviços) · o custo direto consolidado aparece aqui automaticamente.
        </div>
      ) : (
        <>
          {/* Tabela por produto (leitura) */}
          <div style={{ background: '#FFF', border: `1px solid ${G.borda}`,
            borderRadius: 0, overflow: 'hidden' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 90px 100px 100px 70px',
              gap: 8, padding: '8px 12px', background: G.fundo,
              fontSize: 10, fontWeight: 800, color: G.txt2,
              letterSpacing: '0.06em', textTransform: 'uppercase' }}>
              <div>Produto/Serviço</div>
              <div style={{ textAlign: 'right' }}>Mix</div>
              <div style={{ textAlign: 'right' }}>Custo</div>
              <div style={{ textAlign: 'right' }}>Preço</div>
              <div style={{ textAlign: 'right' }}>Contrib.</div>
            </div>
            {produtosT13.map((p, i) => {
              const preco = precoVendaSU(p, setorT13);
              const custo = custoDiretoUnitSU(p, setorT13);
              const contribPct = preco > 0 ? ((preco - custo) / preco) * 100 : 0;
              return (
                <div key={i} style={{ display: 'grid',
                  gridTemplateColumns: '1fr 90px 100px 100px 70px', gap: 8,
                  padding: '8px 12px', fontSize: 12, color: G.txt1,
                  borderTop: `1px solid ${G.borda}`,
                  fontFamily: "'IBM Plex Mono', monospace" }}>
                  <div style={{ fontFamily: "'Outfit', sans-serif" }}>
                    {p.nome || 'Sem nome'}
                  </div>
                  <div style={{ textAlign: 'right' }}>{(Number(p.mix) || 0)}%</div>
                  <div style={{ textAlign: 'right' }}>{fmtBRL2(custo)}</div>
                  <div style={{ textAlign: 'right' }}>{fmtBRL2(preco)}</div>
                  <div style={{ textAlign: 'right', color: contribPct >= 0 ? G.verde : G.vermelho,
                    fontWeight: 700 }}>{contribPct.toLocaleString('pt-BR', { maximumFractionDigits: 0 })}%</div>
                </div>
              );
            })}
          </div>

          {/* Consolidado · Custo Direto + Margem de Contribuição (% e R$) */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div style={{ background: '#FFF', border: `1px solid ${G.borda}`,
              borderLeft: `3px solid ${G.laranja}`, borderRadius: 0,
              padding: '12px 14px' }}>
              <div style={{ fontSize: 10, fontWeight: 800, color: G.txt2,
                letterSpacing: '0.10em', textTransform: 'uppercase', marginBottom: 4 }}>
                Custo Direto consolidado
              </div>
              <div style={{ fontSize: 20, fontWeight: 900, color: G.laranja,
                fontFamily: "'IBM Plex Mono', monospace" }}>
                {custoDirPctPond.toLocaleString('pt-BR', { maximumFractionDigits: 0 })}%
                <span style={{ fontSize: 12, fontWeight: 700, color: G.txt2, marginLeft: 8 }}>
                  {fmtBRL2(custoDirRS)}/ano
                </span>
              </div>
            </div>
            <div style={{ background: '#FFF',
              border: `1px solid ${margemContribPct >= 0 ? G.borda : G.vermelho}`,
              borderLeft: `3px solid ${margemContribPct >= 0 ? G.verde : G.vermelho}`,
              borderRadius: 0, padding: '12px 14px' }}>
              <div style={{ fontSize: 10, fontWeight: 800, color: G.txt2,
                letterSpacing: '0.10em', textTransform: 'uppercase', marginBottom: 4 }}>
                Margem de Contribuição
              </div>
              <div style={{ fontSize: 20, fontWeight: 900,
                color: margemContribPct >= 0 ? G.verde : G.vermelho,
                fontFamily: "'IBM Plex Mono', monospace" }}>
                {margemContribPct.toLocaleString('pt-BR', { maximumFractionDigits: 0 })}%
                <span style={{ fontSize: 12, fontWeight: 700, color: G.txt2, marginLeft: 8 }}>
                  {fmtBRL2(margemContribRS)}/ano
                </span>
              </div>
            </div>
          </div>
          <div style={{ fontSize: 11, color: G.txt2, fontStyle: 'italic' }}>
            Antes de impostos e despesas variáveis · o equilíbrio com os custos
            fixos é fechado nos blocos seguintes.
          </div>
        </>
      )}
    </div>
  );

  // ── Bloco 3 · Despesas Indiretas Fixas (OPEX) · R$/mês ──────────────────
  const opexTotalMes = _b.opexTotalMes;
  const opexTotalAno = _b.opexTotalAno;

  const conteudoBloco3 = (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div style={{ padding: '10px 14px', background: G.verdeLight,
        borderLeft: `3px solid ${G.verde}`, borderRadius: 0,
        fontSize: 12, lineHeight: 1.45, color: G.txt1 }}>
        A <MarcaAIdvisor /> já estimou os custos fixos mensais (os que não variam
        com a venda). Confirme ou ajuste cada linha em R$/mês.
      </div>

      {OPEX_FIXO_SU.map(g => {
        const fmt = (n) => (Number(n) || 0).toLocaleString('pt-BR',
          { style: 'currency', currency: 'BRL', minimumFractionDigits: 2, maximumFractionDigits: 2 });
        const subtotal = g.linhas.reduce((s, l) => s + (Number(obterIA(l.id)) || 0), 0);
        return (
          <div key={g.grupo}>
            <div style={{ display: 'flex', justifyContent: 'space-between',
              alignItems: 'baseline', marginBottom: 6 }}>
              <div style={{ fontSize: 10.5, fontWeight: 800, color: G.txt2,
                letterSpacing: '0.08em', textTransform: 'uppercase' }}>
                {g.grupo}
              </div>
              <div style={{ fontSize: 11.5, color: G.txt2,
                fontFamily: "'IBM Plex Mono', monospace" }}>
                {fmt(subtotal)}/mês
              </div>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {g.linhas.map(l => (
                <div key={l.id} style={{ display: 'grid',
                  gridTemplateColumns: '1fr 150px', gap: 12, alignItems: 'center' }}>
                  <div style={{ fontSize: 12.5, color: G.txt1, lineHeight: 1.35 }}>
                    {l.label}
                  </div>
                  <CampoNumericoBP60M
                    valor={obterIA(l.id)}
                    onChange={(v) => atualizar(l.id, v)}
                    label={l.label}
                    permitirDecimal={true}
                    min={0}
                    formato="brl"
                  />
                </div>
              ))}
            </div>
          </div>
        );
      })}

      {/* Total · mensal e anual */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <div style={{ background: '#FFF', border: `1px solid ${G.borda}`,
          borderLeft: `3px solid ${G.laranja}`, borderRadius: 0,
          padding: '12px 14px', display: 'flex', alignItems: 'baseline',
          justifyContent: 'space-between', gap: 10 }}>
          <div style={{ fontSize: 10, fontWeight: 800, color: G.txt2,
            letterSpacing: '0.10em', textTransform: 'uppercase' }}>
            Despesa Fixa Mensal
          </div>
          <div style={{ fontSize: 20, fontWeight: 900, color: G.laranja,
            fontFamily: "'IBM Plex Mono', monospace" }}>
            {opexTotalMes.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </div>
        </div>
        <div style={{ background: '#FFF', border: `1px solid ${G.borda}`,
          borderLeft: `3px solid ${G.verde}`, borderRadius: 0,
          padding: '12px 14px', display: 'flex', alignItems: 'baseline',
          justifyContent: 'space-between', gap: 10 }}>
          <div style={{ fontSize: 10, fontWeight: 800, color: G.txt2,
            letterSpacing: '0.10em', textTransform: 'uppercase' }}>
            Despesa Fixa Anual
          </div>
          <div style={{ fontSize: 20, fontWeight: 900, color: G.verde,
            fontFamily: "'IBM Plex Mono', monospace" }}>
            {opexTotalAno.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL',
              minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </div>
        </div>
      </div>
    </div>
  );

  // ── Bloco 4 · Impostos · vinculados ao Regime Tributário ──────────────
  const regimeSU = estruturaFin?.imp_regime ?? normalizarRegimeSU(empresa?.regime);
  const refImp = IMPOSTOS_REF_SU[regimeSU] || IMPOSTOS_REF_SU['Simples'];
  const obterAliq = (campo) => (estruturaFin?.[campo] ?? refImp[campo]);
  const folhaAnoSU = _b.folhaAno;
  const lucroBaseSU = _b.lucroBase;
  const impRecSU = _b.impRec;
  const impLucSU = _b.impLuc;
  const impFolSU = _b.impFol;
  const impTotalSU = _b.impTotal;
  const impPctReceitaSU = receitaAno1 > 0 ? (impTotalSU / receitaAno1) * 100 : 0;
  const fmtI = (n) => (Number(n) || 0).toLocaleString('pt-BR',
    { style: 'currency', currency: 'BRL', minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const COL_IMP = '1.3fr 130px 1fr 1fr';
  const linhaImposto = (rot, base, aliqCampo, imposto, obsBase) => (
    <div style={{ display: 'grid', gridTemplateColumns: COL_IMP, gap: 12, alignItems: 'center' }}>
      <div style={{ fontSize: 12.5, color: G.txt1, lineHeight: 1.3 }}>
        {rot}
        <span style={{ fontSize: 10.5, color: G.txt2, display: 'block' }}>{obsBase}</span>
      </div>
      <CampoNumericoBP60M
        valor={obterAliq(aliqCampo)}
        onChange={(v) => atualizar(aliqCampo, v)}
        label={rot + ' · alíquota (%)'}
        permitirDecimal={true} min={0} max={100} sufixo="%" formato="decimal"
      />
      <div style={{ fontSize: 11.5, color: G.txt2, textAlign: 'right',
        fontFamily: "'IBM Plex Mono', monospace" }}>base {fmtI(base)}</div>
      <div style={{ fontSize: 13, fontWeight: 800, color: G.laranja, textAlign: 'right',
        fontFamily: "'IBM Plex Mono', monospace" }}>{fmtI(imposto)}/ano</div>
    </div>
  );

  const conteudoBloco4 = (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div style={{ padding: '10px 14px', background: G.verdeLight,
        borderLeft: `3px solid ${G.verde}`, borderRadius: 0,
        fontSize: 12, lineHeight: 1.45, color: G.txt1 }}>
        A <MarcaAIdvisor /> estima os impostos pelo <strong>Regime Tributário</strong> da
        empresa. As alíquotas nascem pré-preenchidas por regime · confirme ou ajuste.
        Os valores são consequência (aplicados sobre Receita, Lucro e Folha).
      </div>

      {/* Regime tributário */}
      <div>
        <div style={ROT_CAMPO}>Regime tributário</div>
        <div style={{ display: 'flex', gap: 6 }}>
          {REGIMES_SU.map(rg => {
            const sel = regimeSU === rg;
            return (
              <button key={rg} type="button" onClick={() => atualizar('imp_regime', rg)}
                style={{ padding: '7px 14px', fontSize: 12, fontWeight: 700,
                  background: sel ? G.verde : '#FFF', color: sel ? '#FFF' : G.txt1,
                  border: `1px solid ${sel ? G.verde : G.borda}`, borderRadius: 999,
                  cursor: 'pointer', fontFamily: "'Outfit', sans-serif" }}>
                {sel ? '✓ ' : ''}{rg}
              </button>
            );
          })}
        </div>
        <div style={{ fontSize: 11, color: G.txt2, fontStyle: 'italic', marginTop: 6 }}>
          Ao trocar o regime, as alíquotas voltam à estimativa daquele regime (a menos que você já tenha ajustado alguma).
        </div>
      </div>

      {/* Cabeçalho das colunas */}
      <div style={{ display: 'grid', gridTemplateColumns: COL_IMP, gap: 12, padding: '0 2px' }}>
        <div style={ROT_CAMPO}>Base</div>
        <div style={ROT_CAMPO}>Alíquota</div>
        <div style={{ ...ROT_CAMPO, textAlign: 'right' }}>Base anual</div>
        <div style={{ ...ROT_CAMPO, textAlign: 'right' }}>Imposto</div>
      </div>
      {linhaImposto('Sobre a Receita', receitaAno1, 'imp_aliq_receita', impRecSU, 'Receita estimada do Ano 1 (Bloco 1)')}
      {linhaImposto('Sobre o Lucro', Math.max(0, lucroBaseSU), 'imp_aliq_lucro', impLucSU, 'Margem de contribuição − custos fixos (Blocos 2 e 3)')}
      {linhaImposto('Sobre a Folha', folhaAnoSU, 'imp_aliq_folha', impFolSU, 'Folha administrativa anual (Bloco 3)')}

      {/* Totais */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <div style={{ background: '#FFF', border: `1px solid ${G.borda}`,
          borderLeft: `3px solid ${G.laranja}`, borderRadius: 0,
          padding: '12px 14px', display: 'flex', alignItems: 'baseline',
          justifyContent: 'space-between', gap: 10 }}>
          <div style={{ fontSize: 10, fontWeight: 800, color: G.txt2,
            letterSpacing: '0.10em', textTransform: 'uppercase' }}>
            Impostos estimados/ano
          </div>
          <div style={{ fontSize: 20, fontWeight: 900, color: G.laranja,
            fontFamily: "'IBM Plex Mono', monospace" }}>
            {fmtI(impTotalSU)}
          </div>
        </div>
        <div style={{ background: '#FFF', border: `1px solid ${G.borda}`,
          borderLeft: `3px solid ${G.verde}`, borderRadius: 0,
          padding: '12px 14px', display: 'flex', alignItems: 'baseline',
          justifyContent: 'space-between', gap: 10 }}>
          <div style={{ fontSize: 10, fontWeight: 800, color: G.txt2,
            letterSpacing: '0.10em', textTransform: 'uppercase' }}>
            Carga sobre a Receita
          </div>
          <div style={{ fontSize: 20, fontWeight: 900, color: G.verde,
            fontFamily: "'IBM Plex Mono', monospace" }}>
            {impPctReceitaSU.toLocaleString('pt-BR', { maximumFractionDigits: 1 })}%
          </div>
        </div>
      </div>
      <div style={{ fontSize: 11, color: G.txt2, fontStyle: 'italic' }}>
        Estimativa · as alíquotas efetivas variam por anexo/atividade · o consultor ajusta ao caso.
        Impostos específicos sobre a venda (comissões · taxas) entram no bloco de Despesas Variáveis.
      </div>
    </div>
  );

  // ── Bloco 5 · CAPEX (Investimentos Iniciais) · R$ único ───────────────
  const capexTotalSU = _b.capexTotal;

  const conteudoBloco5 = (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div style={{ padding: '10px 14px', background: G.verdeLight,
        borderLeft: `3px solid ${G.verde}`, borderRadius: 0,
        fontSize: 12, lineHeight: 1.45, color: G.txt1 }}>
        A <MarcaAIdvisor /> estimou os <strong>investimentos iniciais (CAPEX)</strong> para a
        operação começar — gasto único, não recorrente. Confirme ou ajuste cada linha em R$.
      </div>

      {CAPEX_INICIAL_SU.map(g => {
        const fmt = (n) => (Number(n) || 0).toLocaleString('pt-BR',
          { style: 'currency', currency: 'BRL', minimumFractionDigits: 2, maximumFractionDigits: 2 });
        const subtotal = g.linhas.reduce((s, l) => s + (Number(obterIA(l.id)) || 0), 0);
        return (
          <div key={g.grupo}>
            <div style={{ display: 'flex', justifyContent: 'space-between',
              alignItems: 'baseline', marginBottom: 6 }}>
              <div style={{ fontSize: 10.5, fontWeight: 800, color: G.txt2,
                letterSpacing: '0.08em', textTransform: 'uppercase' }}>
                {g.grupo}
              </div>
              <div style={{ fontSize: 11.5, color: G.txt2,
                fontFamily: "'IBM Plex Mono', monospace" }}>
                {fmt(subtotal)}
              </div>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {g.linhas.map(l => (
                <div key={l.id} style={{ display: 'grid',
                  gridTemplateColumns: '1fr 150px', gap: 12, alignItems: 'center' }}>
                  <div style={{ fontSize: 12.5, color: G.txt1, lineHeight: 1.35 }}>
                    {l.label}
                  </div>
                  <CampoNumericoBP60M
                    valor={obterIA(l.id)}
                    onChange={(v) => atualizar(l.id, v)}
                    label={l.label}
                    permitirDecimal={true}
                    min={0}
                    formato="brl"
                  />
                </div>
              ))}
            </div>
          </div>
        );
      })}

      {/* Total do CAPEX inicial */}
      <div style={{ background: '#FFF', border: `1px solid ${G.borda}`,
        borderLeft: `3px solid ${G.laranja}`, borderRadius: 0,
        padding: '12px 14px', display: 'flex', alignItems: 'baseline',
        justifyContent: 'space-between', gap: 10 }}>
        <div style={{ fontSize: 10, fontWeight: 800, color: G.txt2,
          letterSpacing: '0.10em', textTransform: 'uppercase' }}>
          CAPEX Inicial Total
        </div>
        <div style={{ fontSize: 20, fontWeight: 900, color: G.laranja,
          fontFamily: "'IBM Plex Mono', monospace" }}>
          {capexTotalSU.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', minimumFractionDigits: 2, maximumFractionDigits: 2 })}
        </div>
      </div>
      <div style={{ fontSize: 11, color: G.txt2, fontStyle: 'italic' }}>
        Gasto único para começar · alimenta o Runway (Bloco 7). CAPEX recorrente
        (manutenção/crescimento) é tratado nas Projeções.
      </div>
    </div>
  );

  // ── Bloco 6 · Capital de Giro Inicial ─────────────────────────────────
  const cgNCG = _b.cgNCG;
  const custoFixoMensalSU = _b.custoFixoMensal;
  const cgCaixaMinimo = _b.cgCaixaMinimo;
  const cgReserva = _b.cgReserva;
  const cgTotal = _b.cgTotal;
  const fmtCG = (n) => (Number(n) || 0).toLocaleString('pt-BR',
    { style: 'currency', currency: 'BRL', minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const rowCG = (rot, campo, sufixo, formato) => (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 150px', gap: 12, alignItems: 'center' }}>
      <div style={{ fontSize: 12.5, color: G.txt1, lineHeight: 1.35 }}>{rot}</div>
      <CampoNumericoBP60M valor={obterIA(campo)} onChange={(v) => atualizar(campo, v)}
        label={rot} permitirDecimal={formato !== 'int'} min={0} sufixo={sufixo} formato={formato} />
    </div>
  );
  const resultCG = (rot, valor, extra) => (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline',
      padding: '6px 0', marginTop: 4, borderTop: `1px dashed ${G.borda}` }}>
      <div style={{ fontSize: 11.5, color: G.txt2 }}>
        {rot}{extra ? <span style={{ fontStyle: 'italic' }}> · {extra}</span> : null}
      </div>
      <div style={{ fontSize: 13, fontWeight: 800, color: G.txt1,
        fontFamily: "'IBM Plex Mono', monospace" }}>{valor}</div>
    </div>
  );

  const conteudoBloco6 = (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div style={{ padding: '10px 14px', background: G.verdeLight,
        borderLeft: `3px solid ${G.verde}`, borderRadius: 0,
        fontSize: 12, lineHeight: 1.45, color: G.txt1 }}>
        A <MarcaAIdvisor /> estimou o <strong>capital de giro inicial</strong> — o caixa para
        girar a operação até ela se sustentar. Confirme ou ajuste os prazos, os meses de caixa
        e a reserva.
      </div>

      {/* Necessidade de Capital de Giro (ciclo) */}
      <div>
        <div style={ROT_CAMPO}>Necessidade de Capital de Giro (ciclo operacional)</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {rowCG('Prazo médio de recebimento', 'cg_dias_receber', 'dias', 'int')}
          {rowCG('Prazo médio de estoque', 'cg_dias_estoque', 'dias', 'int')}
          {rowCG('Prazo médio de pagamento', 'cg_dias_pagar', 'dias', 'int')}
        </div>
        {resultCG('Necessidade de Capital de Giro', fmtCG(cgNCG),
          cgNCG < 0 ? 'ciclo favorável · sem necessidade' : null)}
      </div>

      {/* Caixa Mínimo */}
      <div>
        <div style={ROT_CAMPO}>Caixa Mínimo</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {rowCG('Meses de custo fixo mantidos em caixa', 'cg_meses_caixa', 'meses', 'int')}
        </div>
        {resultCG('Caixa Mínimo', fmtCG(cgCaixaMinimo),
          `custo fixo mensal ${fmtCG(custoFixoMensalSU)} (OPEX + impostos)`)}
      </div>

      {/* Reserva de Contingência */}
      <div>
        <div style={ROT_CAMPO}>Reserva de Contingência</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {rowCG('Percentual sobre o subtotal', 'cg_reserva_pct', '%', 'decimal')}
        </div>
        {resultCG('Reserva', fmtCG(cgReserva), null)}
      </div>

      {/* Total */}
      <div style={{ background: '#FFF', border: `1px solid ${G.borda}`,
        borderLeft: `3px solid ${G.laranja}`, borderRadius: 0,
        padding: '12px 14px', display: 'flex', alignItems: 'baseline',
        justifyContent: 'space-between', gap: 10 }}>
        <div style={{ fontSize: 10, fontWeight: 800, color: G.txt2,
          letterSpacing: '0.10em', textTransform: 'uppercase' }}>
          Capital de Giro Inicial
        </div>
        <div style={{ fontSize: 20, fontWeight: 900, color: G.laranja,
          fontFamily: "'IBM Plex Mono', monospace" }}>
          {fmtCG(cgTotal)}
        </div>
      </div>
      <div style={{ fontSize: 11, color: G.txt2, fontStyle: 'italic' }}>
        Necessidade do ciclo + caixa mínimo + reserva · alimenta o Runway (Bloco 7).
      </div>
    </div>
  );

  // ── Bloco 7 · Runway ──────────────────────────────────────────────────
  const necessidadeInicialSU = capexTotalSU + cgTotal;
  const custoMensalTotalSU = opexTotalMes + (custoDirRS / 12) + (impTotalSU / 12);
  const receitaMensalSU = receitaAno1 / 12;
  const burnMensalSU = Math.max(0, custoMensalTotalSU - receitaMensalSU);
  const capitalInicialDefaultSU = Math.round(necessidadeInicialSU + 12 * burnMensalSU);
  const capitalInicialSU = estruturaFin?.rw_capital_inicial ?? capitalInicialDefaultSU;
  const sobraOperacaoSU = capitalInicialSU - necessidadeInicialSU;
  const operacaoSePagaSU = burnMensalSU <= 0;
  const capitalInsuficienteSU = sobraOperacaoSU < 0;
  const runwayMesesSU = (operacaoSePagaSU || capitalInsuficienteSU) ? null : sobraOperacaoSU / burnMensalSU;
  const instrCapitalSU = TIPOS_CAPITAL_T1_SU.find(t => t.id === meta?.tipoCapital)?.titulo || null;
  let corRunwaySU = G.txt2, seloRunwaySU = '';
  if (capitalInsuficienteSU) { corRunwaySU = G.vermelho; seloRunwaySU = 'capital não cobre a necessidade inicial'; }
  else if (operacaoSePagaSU) { corRunwaySU = G.verde; seloRunwaySU = '✓ operação se paga (sem queima de caixa)'; }
  else if (runwayMesesSU >= 12) { corRunwaySU = G.verde; seloRunwaySU = '✓ runway confortável (≥ 12 meses)'; }
  else { corRunwaySU = G.laranja; seloRunwaySU = 'runway curto (< 12 meses)'; }
  const runwayTxtSU = operacaoSePagaSU ? '∞'
    : (capitalInsuficienteSU ? '0 meses'
    : runwayMesesSU.toLocaleString('pt-BR', { maximumFractionDigits: 1 }) + ' meses');

  const conteudoBloco7 = (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div style={{ padding: '10px 14px', background: G.verdeLight,
        borderLeft: `3px solid ${G.verde}`, borderRadius: 0,
        fontSize: 12, lineHeight: 1.45, color: G.txt1 }}>
        A <MarcaAIdvisor /> estimou o <strong>capital inicial</strong> para cobrir a necessidade
        inicial e ~12 meses de operação. Ajuste o valor · o runway se recalcula.
      </div>

      {/* Instrumento de capital (vem da Tarefa 1) */}
      <div style={{ fontSize: 12, color: G.txt1 }}>
        Instrumento de capital (Tarefa 1):{' '}
        <strong>{instrCapitalSU || 'não definido na Tarefa 1'}</strong>
      </div>

      {/* Capital inicial disponível */}
      <div>
        <div style={ROT_CAMPO}>Capital inicial disponível</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 150px', gap: 12, alignItems: 'center' }}>
          <div style={{ fontSize: 12.5, color: G.txt1, lineHeight: 1.35 }}>
            Quanto a startup tem para começar (aporte ou captação)
          </div>
          <CampoNumericoBP60M
            valor={capitalInicialSU}
            onChange={(v) => atualizar('rw_capital_inicial', v)}
            label="Capital inicial disponível (R$)"
            permitirDecimal={true} min={0} formato="brl"
          />
        </div>
      </div>

      {/* Composição do runway (leitura) */}
      <div>
        {resultCG('Necessidade inicial (CAPEX + Capital de Giro)', fmtCG(necessidadeInicialSU),
          `CAPEX ${fmtCG(capexTotalSU)} + Giro ${fmtCG(cgTotal)}`)}
        {resultCG('Sobra para operação', fmtCG(sobraOperacaoSU),
          capitalInsuficienteSU ? 'capital abaixo da necessidade inicial' : null)}
        {resultCG('Queima mensal (burn)', operacaoSePagaSU ? '—' : fmtCG(burnMensalSU),
          operacaoSePagaSU ? 'a receita já cobre os custos' : 'custos mensais − receita mensal')}
      </div>

      {/* Runway */}
      <div style={{ background: '#FFF', border: `1px solid ${corRunwaySU}`,
        borderLeft: `3px solid ${corRunwaySU}`, borderRadius: 0, padding: '12px 14px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 10 }}>
          <div style={{ fontSize: 10, fontWeight: 800, color: G.txt2,
            letterSpacing: '0.10em', textTransform: 'uppercase' }}>
            Runway
          </div>
          <div style={{ fontSize: 22, fontWeight: 900, color: corRunwaySU,
            fontFamily: "'IBM Plex Mono', monospace" }}>
            {runwayTxtSU}
          </div>
        </div>
        <div style={{ fontSize: 11.5, fontWeight: 700, color: corRunwaySU, marginTop: 4 }}>
          {(corRunwaySU === G.vermelho || corRunwaySU === G.laranja) && seloRunwaySU
            ? <><IconeAlerta /> {seloRunwaySU}</>
            : seloRunwaySU}
        </div>
        {(!operacaoSePagaSU && !capitalInsuficienteSU && runwayMesesSU != null) && (
          <div style={{ fontSize: 11, color: G.txt2, fontStyle: 'italic', marginTop: 2 }}>
            Próxima captação sugerida até o mês {Math.floor(runwayMesesSU)}.
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
      <div style={{ padding: '10px 14px', background: G.fundo,
        border: `1px solid ${G.borda}`, borderRadius: 0,
        fontSize: 12.5, lineHeight: 1.5, color: G.txt1 }}>
        Na modalidade <strong>Startup</strong>, a estrutura financeira não vem
        de extratos · ela é construída com você como premissa do futuro.
        Os blocos alimentam as Projeções e o Valuation de Startup.
      </div>
      {BLOCOS_ESTRUTURACAO_SU.map(b => (
        <div key={b.num}>
          <div style={HEADER_BLOCO}>
            Bloco {b.num} · {b.titulo}
            <span style={HEADER_AUX}>· {b.sub}</span>
          </div>
          {b.num === 1 ? conteudoBlocoA : b.num === 2 ? conteudoBloco2 : b.num === 3 ? conteudoBloco3 : b.num === 4 ? conteudoBloco4 : b.num === 5 ? conteudoBloco5 : b.num === 6 ? conteudoBloco6 : b.num === 7 ? conteudoBloco7 : (
            <div style={{ fontSize: 12.5, color: G.txt2,
              fontStyle: 'italic', lineHeight: 1.5,
              padding: '10px 14px', background: G.fundo,
              border: `1px solid ${G.borda}`, borderRadius: 0 }}>
              Conteúdo do Bloco {b.num} será implementado na próxima
              iteração desta tarefa.
            </div>
          )}
        </div>
      ))}
    </div>
  );
};


// ═══════════════════════════════════════════════════════════════════════
// <TelaComposicoes /> — funcionalidade standalone (menu DADOS DE ENTRADA,
// logo abaixo de Configurações). Reúne 3 blocos que hoje também vivem no
// BP60M (mantidos lá · a decisão de "puxar daqui" fica para depois):
//   1) Formação de Preço de Venda              (espelho do Bloco 2 da T4)
//   2) Custos Diretos (Variáveis com Receita)  (espelho do Bloco 2 da T13)
//   3) Despesas Indiretas Fixas · OPEX         (espelho do Bloco 3 da T13)
// Estado PRÓPRIO e INDEPENDENTE (localStorage `composicoes:<empresaId>`),
// semeado 1× pela sugestão da RUNGOAL AIDVISOR (rascunho da T4). Serve EE + SU.
// Perfis: RG Pro + RG Consultor (o gate exclui apenas um tier gratuito futuro;
// hoje só existem pro/aipro + admin, então aparece para todos os perfis atuais).
// Reaproveita helpers de arquivo (fonte única): tipoProdutoSU · insumoValorSU ·
// custoDiretoUnitSU · precoVendaSU · INSUMOS_PRECO_SU · OPEX_FIXO_SU · IA_REF_ESTRUTURA_SU.
// ═══════════════════════════════════════════════════════════════════════
// ═══════════════════════════════════════════════════════════════════════
// <TelaPlanilhaInsumos /> — o cadastro que ALIMENTA a Formação de Preço.
// Vive no banco (tabela `insumos`), não no localStorage: a RUNGOAL AIDVISOR
// precisa escrever a atualização de preço, e trocar de navegador não pode
// apagar o trabalho do consultor.
//
// Dois códigos de propósito: o da RunGoal é automático e imutável (é por ele
// que a composição acha o insumo); o do cliente é livre, para ele manter a
// codificação que já usa sem que uma troca de rótulo quebre referência.
// ═══════════════════════════════════════════════════════════════════════
const UNIDADES_INSUMO = ['kg', 'g', 'L', 'mL', 'un', 'h', 'm', 'm²', 'm³', 'cx', 'pct'];

// ═══════════════════════════════════════════════════════════════════════
// <TelaFormacaoPreco /> — a composição de custo de cada produto.
//
// O consultor insere um insumo da Planilha, informa o TAMANHO do produto, e a
// ferramenta faz tamanho × índice × preço unitário. Bolo de 3 kg com farinha a
// 0,5 kg/kg e R$ 4,00/kg → R$ 6,00 de farinha.
//
// O cálculo vem do backend (`produtos.py`), não é refeito aqui: é conta de
// dinheiro, e duas versões da mesma regra divergem cedo ou tarde.
// ═══════════════════════════════════════════════════════════════════════
// Régua da tabela "Quanto é Preciso Vender". Global porque a mesma tabela é
// desenhada em DUAS telas — Formação de Preço e Composições — e uma régua por
// tela abriria duas versões da mesma coisa.
const GRID_CVL = 'minmax(160px,1fr) 60px 118px 118px 104px 92px 92px';

// Régua da tabela "Custos Diretos consolidado".
// +1 coluna em 14/09: Margem Final, ao lado da de contribuição.
const GRID_CONSOL = 'minmax(180px,1fr) 80px 110px 110px 110px 90px 90px';

// Lente de rateio (10/09): compara os dois cenários de fatia da despesa
// indireta sem gravar nada — o cadastro no banco fica onde está, e só a conta
// desta resposta muda. Mesmo desenho dos botões de cenário do Resumo Mensal.
const SeletorRateio = ({ valor, onChange }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 12,
    flexWrap: 'wrap', marginBottom: 12 }}>
    <span style={{ fontSize: 10.5, fontWeight: 700, color: G.txt2,
      textTransform: 'uppercase', letterSpacing: '0.07em' }}>
      Rateio da fatia
    </span>
    <div style={{ display: 'flex', gap: 8 }}>
      {[['ponderada', 'Ponderado'], ['igual', 'Por igual']].map(([v, rot]) => {
        const on = valor === v;
        return (
          <button key={v} onClick={() => onChange(v)}
            style={{ width: 116, padding: '9px 0', border: 'none',
              borderRadius: 0, cursor: 'pointer', fontFamily: 'inherit',
              fontSize: 12.5, fontWeight: 700, letterSpacing: '0.01em',
              background: on ? G.laranja : G.laranjaLight,
              color: on ? '#FFF' : G.laranja,
              transition: 'background .15s, color .15s' }}>
            {rot}
          </button>
        );
      })}
    </div>
    <span style={{ fontSize: 11.5, color: G.txt2, lineHeight: 1.45 }}>
      {valor === 'igual'
        ? 'Cada produto assume a mesma fatia da despesa indireta.'
        : 'Cada produto assume a fatia proporcional à contribuição que gerou.'}
      {' '}Só muda a análise — o cadastro não é alterado.
    </span>
  </div>
);

// O que a empresa ENTREGOU no período, ao lado do que a simulação diz que ela
// precisaria vender. Tudo o mais nesta tela é hipótese; este bloco é medição —
// sai da quantidade que o consultor (ou a carga contábil) informou por produto.
//
// `fmtBRL` global não serve aqui: ele aplica Math.abs, e prejuízo apareceria
// como lucro. Dinheiro com sinal se formata com o sinal.
const dinheiroComSinal = (v) => {
  const n = Number(v) || 0;
  return `${n < 0 ? '−' : ''}R$ ${Math.abs(n).toLocaleString('pt-BR',
    { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

// Mora AO LADO das células de simulação (Fabio, 09/09): mesma altura, mesmo
// rótulo, mesma caixa de leitura — o contraste entre desejado e realizado só
// funciona se os dois estiverem na mesma linha do olho.
const BlocoMargemRealizada = ({ realizado, margemDesejada }) => {
  const alvo = Number(margemDesejada) || 0;
  const rot = { fontSize: 10, fontWeight: 800, color: G.txt2,
    letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 4 };
  const caixa = (cor, fundo) => ({ height: 36, display: 'flex', alignItems: 'center',
    justifyContent: 'flex-end', padding: '0 12px', border: `1px solid ${cor}`,
    borderRadius: 0, background: fundo, fontSize: 14, fontWeight: 700, color: cor,
    fontFamily: "'IBM Plex Mono', monospace" });

  // Sem quantidade informada não existe realizado. Mostrar 0% seria a tela
  // afirmar que a empresa não ganha nada; a célula fica com o traço e diz o
  // que falta.
  if (!realizado) {
    return (
      <div>
        <div style={rot}>Margem operacional realizada</div>
        <div style={caixa(G.txt2, G.fundo)}>—</div>
        <div style={{ fontSize: 10.5, color: G.txt2, marginTop: 4 }}>
          informe a quantidade vendida por produto
        </div>
      </div>
    );
  }

  const m = Number(realizado.margem) || 0;
  // Três estados, e o do meio existe porque "cobriu a conta mas não a meta" não
  // é fracasso: é informação diferente de "não cobriu a conta".
  const [cor, fundo, recado] = !realizado.cobre_o_fixo
    ? [G.vermelho, G.vermelhoL, 'não cobre a despesa indireta']
    : m >= alvo
      ? [G.verde, G.verdeLight, 'acima da desejada · sobra ' +
         dinheiroComSinal(realizado.resultado_mensal)]
      : ['#B45309', '#FFFBEB', 'abaixo da desejada · sobra ' +
         dinheiroComSinal(realizado.resultado_mensal)];
  const parcial = realizado.produtos_com_quantidade < realizado.produtos_total;
  return (
    <div>
      <div style={rot}>Margem operacional realizada</div>
      <div style={caixa(cor, fundo)}>
        {(m * 100).toLocaleString('pt-BR',
          { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%
      </div>
      <div style={{ fontSize: 10.5, color: G.txt2, marginTop: 4 }}>
        {recado}
        {parcial && (
          <div style={{ color: '#B45309', fontWeight: 700, marginTop: 2 }}>
            só {realizado.produtos_com_quantidade} de {realizado.produtos_total}{' '}
            produtos com quantidade
          </div>
        )}
      </div>
    </div>
  );
};

// Quantas linhas uma tabela longa mostra antes de pedir licença para crescer.
// Cinco porque é o mesmo corte dos rankings do Dashboard — o usuário vê o topo
// nos dois lugares e reconhece a mesma leitura.
const LIMITE_TABELA = 5;

// Criado em 14/09: as três tabelas da Formação de Preço abriam com os 75
// produtos da MORED, e a página virava três telas de rolagem antes do primeiro
// número consolidado. Um componente só, e não três botões copiados, porque o
// rótulo tem de contar quantos faltam — e isso é onde cópias divergem.
const BotaoMostrarMais = ({ total, limite, aberto, onAlternar, rotulo = 'itens' }) => {
  // Nada a esconder: o botão não aparece. Uma empresa com 4 produtos nunca vê
  // um "Mostrar mais" que não mostra nada ([[base-zerada-nada-nativo]]).
  if (total <= limite) return null;
  return (
    <button onClick={onAlternar}
      style={{ marginTop: 10, padding: '8px 14px', fontSize: 12, fontWeight: 700,
        background: '#FFF', color: G.laranja, border: `1px solid ${G.laranja}`,
        borderRadius: 0, cursor: 'pointer', fontFamily: "'Outfit', sans-serif" }}>
      {aberto
        ? `Mostrar menos · voltar a ${limite}`
        : `Mostrar mais · ${total - limite} ${rotulo} ocultos de ${total}`}
    </button>
  );
};

const TelaFormacaoPreco = ({ empresa, periodoInicio, periodoFim }) => {
  const [produtos, setProdutos] = useState([]);
  const [insumos, setInsumos] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState(null);
  const [aberto, setAberto] = useState(null);      // id do produto expandido
  // Uma tabela longa por vez (14/09). Estados separados: abrir a lista de
  // produtos não tem por que abrir a análise de breakeven.
  const [verTodosProdutos, setVerTodosProdutos] = useState(false);
  const [verTodosConsol, setVerTodosConsol] = useState(false);
  // Por qual coluna a tabela de consolidado sai ordenada (14/09). Sempre do
  // MAIOR para o menor — é ranking, e ranking não se lê de baixo para cima.
  const [ordemConsol, setOrdemConsol] = useState('mix');
  // Ordem da tabela de breakeven. 'nenhuma' = ordem de cadastro, que é como ela
  // sempre saiu; o consultor escolhe a leitura e desliga clicando de novo.
  const [ordemCVL, setOrdemCVL] = useState('nenhuma');

  // A margem final vive AQUI, numa função só, e não em duas contas com o mesmo
  // nome: a tabela ORDENA e EXIBE pelo mesmo número. Foi exatamente esse o erro
  // de 30/07 neste arquivo — duas telas, dois valores, um nome.
  // Contribuição do mês (já sem imposto) menos a fatia do fixo que o produto
  // carrega, sobre a receita dele.
  const margemFinalPct = (p) => {
    const qtd = Number(p.quantidade_mes) || 0;
    const receita = (Number(p.preco_venda) || 0) * qtd;
    if (!(receita > 0) || !(despesaIndireta > 0) || p.margem_contribuicao == null) return null;
    const cota = (Number(despesaIndireta) || 0) * (Number(p.fatia_fixo) || 0) / 100;
    return ((Number(p.margem_contribuicao) * qtd) - cota) / receita * 100;
  };

  // O que o empresário PLANEJA vender daquele item. Vinha de `produtos[i]`,
  // casando as duas listas pela POSIÇÃO — o que só funciona enquanto ninguém
  // reordena nenhuma delas. Com a tabela ordenável (14/09) isso passaria a
  // exibir o plano de um produto na linha de outro, silenciosamente.
  const planejadoDe = (it) => {
    const p = produtos.find(x => x.id === it.id)
           || produtos.find(x => x.nome === it.nome);
    return Number(p?.quantidade_mes) || 0;
  };
  // Positivo = vendeu mais do que precisa; negativo = falta.
  const gapDe = (it) => it.quantidade === null ? null : planejadoDe(it) - it.quantidade;

  const ordenarCVL = (lista) => {
    if (ordemCVL === 'nenhuma') return lista;
    const valor = { fatia: (it) => Number(it.fatia) || 0,
                    precisa: (it) => it.quantidade,
                    gap: gapDe }[ordemCVL];
    return [...lista].sort((a, b) => {
      const va = valor(a), vb = valor(b);
      if (va == null && vb == null) return 0;
      if (va == null) return 1;
      if (vb == null) return -1;
      return vb - va;
    });
  };

  // Cópia antes de ordenar: `sort` muta, e `produtos` é estado do React.
  // Produto sem base para a margem vai para o FIM, nunca para o topo — `null`
  // não é "zero", é "não dá para saber" ([[nao-se-chuta-o-que-nao-se-estima]]).
  const ordenarConsol = (lista) => {
    const valor = ordemConsol === 'margem_final'
      ? margemFinalPct
      : (p) => (Number(p.mix) || 0);
    return [...lista].sort((a, b) => {
      const va = valor(a), vb = valor(b);
      if (va == null && vb == null) return 0;
      if (va == null) return 1;
      if (vb == null) return -1;
      return vb - va;
    });
  };
  const [verTodosCVL, setVerTodosCVL] = useState(false);
  const [novo, setNovo] = useState(null);          // rascunho de produto novo
  const [escolhendo, setEscolhendo] = useState(null); // { produtoId, tipo }
  // Despesa indireta medida (aba Despesas Indiretas) e resultado da Análise
  // Custo-Volume-Lucro. Os dois blocos abaixo da composição dependem deles.
  const [despesaIndireta, setDespesaIndireta] = useState(null);
  const [cvl, setCvl] = useState(null);
  const [margemDesejadaPct, setMargemDesejadaPct] = useState(15);
  // Lente de rateio — 'ponderada' abre igual ao que a tabela já mostrava, e
  // 'igual' é o cenário de comparação. Nada disto grava no cadastro.
  const [metodoFatia, setMetodoFatia] = useState('ponderada');
  // Cenários do produto novo: qual produto está sendo avaliado como "o novo".
  const [produtoNovo, setProdutoNovo] = useState(null);
  const [cenarios, setCenarios] = useState(null);
  const [removendoExemplo, setRemovendoExemplo] = useState(false);

  const carregar = React.useCallback(async () => {
    try {
      const [rp, ri] = await Promise.all([
        fetch(`${API}/produtos/${empresa.id}`),
        fetch(`${API}/insumos/${empresa.id}`),
      ]);
      if (!rp.ok || !ri.ok) throw new Error('Não foi possível carregar.');
      const [dp, di] = await Promise.all([rp.json(), ri.json()]);
      setProdutos(dp.produtos || []);
      setInsumos((di.insumos || []).filter(i => i.ativo));
      setErro(null);
    } catch (e) { setErro(e.message); }
    finally { setCarregando(false); }
  }, [empresa.id]);

  useEffect(() => { carregar(); }, [carregar]);

  // Despesa indireta do período — a MESMA da aba Despesas Indiretas, para as
  // duas telas nunca divergirem.
  const dataInicio = periodoInicio?.inicio || null;
  const dataFim = periodoFim?.fim || null;
  useEffect(() => {
    let vivo = true;
    (async () => {
      try {
        const url = new URL(`${API}/indicadores/custos-fixos/${empresa.id}`);
        if (dataInicio && dataFim) {
          url.searchParams.set('data_inicio', dataInicio);
          url.searchParams.set('data_fim', dataFim);
        }
        const r = await fetch(url);
        if (!r.ok) { if (vivo) setDespesaIndireta(null); return; }
        const d = await r.json();
        if (vivo) setDespesaIndireta(d.opex?.total_mensal ?? null);
      } catch (_) { if (vivo) setDespesaIndireta(null); }
    })();
    return () => { vivo = false; };
  }, [empresa.id, dataInicio, dataFim]);

  // Análise Custo-Volume-Lucro — cálculo no backend, nunca refeito aqui.
  const chaveCVL = JSON.stringify([
    produtos.map(p => ({
      id: p.id, nome: p.nome, preco: Number(p.preco_venda) || 0,
      custo_direto: Number(p.custo_direto) || 0,
      fatia: (Number(p.fatia_fixo) || 0) / 100,
      quantidade_informada: Number(p.quantidade_mes) || 0,
    })),
    despesaIndireta, margemDesejadaPct, empresa?.setor, metodoFatia,
  ]);
  useEffect(() => {
    const [comps, fixo, margem, setorEmp, metodo] = JSON.parse(chaveCVL);
    if (!comps.length || !(fixo > 0)) { setCvl(null); return; }
    let vivo = true;
    const t = setTimeout(async () => {
      try {
        const r = await fetch(`${API}/indicadores/custo-volume-lucro/${empresa.id}`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ composicoes: comps, despesa_fixa_mensal: fixo,
            margem_desejada: (Number(margem) || 0) / 100, setor: setorEmp,
            metodo_fatia: metodo }),
        });
        if (!r.ok) { if (vivo) setCvl(null); return; }
        const d = await r.json();
        if (vivo) setCvl(d);
      } catch (_) { if (vivo) setCvl(null); }
    }, 400);
    return () => { vivo = false; clearTimeout(t); };
  }, [chaveCVL, empresa.id]);

  // Cenários — só faz sentido com um produto marcado como novo e ao menos um
  // anterior para aliviar.
  useEffect(() => {
    const [comps, fixo, margem, setorEmp] = JSON.parse(chaveCVL);
    if (!produtoNovo || comps.length < 2 || !(fixo > 0)) { setCenarios(null); return; }
    let vivo = true;
    const t = setTimeout(async () => {
      try {
        const r = await fetch(`${API}/indicadores/cenarios-produto-novo/${empresa.id}`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ composicoes: comps, id_novo: produtoNovo,
            despesa_fixa_mensal: fixo,
            margem_desejada: (Number(margem) || 0) / 100, setor: setorEmp }),
        });
        if (!r.ok) { if (vivo) setCenarios(null); return; }
        const d = await r.json();
        if (vivo) setCenarios(d);
      } catch (_) { if (vivo) setCenarios(null); }
    }, 400);
    return () => { vivo = false; clearTimeout(t); };
  }, [chaveCVL, empresa.id, produtoNovo]);

  const chamar = async (url, opcoes, msgErro) => {
    try {
      const r = await fetch(url, opcoes);
      const d = await r.json().catch(() => ({}));
      if (!r.ok) { setErro(d.detail || msgErro); return false; }
      setErro(null); await carregar(); return true;
    } catch (e) { setErro(msgErro); return false; }
  };

  const salvarNovo = async () => {
    if (!novo?.nome?.trim()) { setErro('Informe o nome do produto.'); return; }
    const ok = await chamar(`${API}/produtos/${empresa.id}`,
      { method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(novo) }, 'Não foi possível criar o produto.');
    if (ok) setNovo(null);
  };

  const editarProduto = (id, campo, valor) => {
    setProdutos(prev => prev.map(p => p.id === id ? { ...p, [campo]: valor } : p));
    chamar(`${API}/produtos/${empresa.id}/${id}`,
      { method: 'PATCH', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ [campo]: valor }) }, 'Não foi possível salvar.');
  };

  const addInsumo = (produtoId, insumoId) => {
    setEscolhendo(null);
    chamar(`${API}/produtos/${empresa.id}/${produtoId}/insumos`,
      { method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ insumo_id: insumoId }) }, 'Não foi possível inserir o insumo.');
  };

  const editarIndice = (produtoId, itemId, valor) =>
    chamar(`${API}/produtos/${empresa.id}/${produtoId}/insumos/${itemId}`,
      { method: 'PATCH', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ indice_consumo: valor }) }, 'Não foi possível salvar o índice.');

  const removerItem = (produtoId, itemId) =>
    chamar(`${API}/produtos/${empresa.id}/${produtoId}/insumos/${itemId}`,
      { method: 'DELETE' }, 'Não foi possível remover o insumo.');

  const removerProduto = (p) => {
    if (!window.confirm(`Remover "${p.nome}" da formação de preço?`)) return;
    chamar(`${API}/produtos/${empresa.id}/${p.id}`, { method: 'DELETE' },
      'Não foi possível remover o produto.');
  };

  // Mesmo endpoint da Planilha de Insumos: o exemplo é um conjunto só
  // (7 insumos + produto + composição) e sai inteiro, de qualquer uma das
  // duas telas. Removê-lo pela metade deixaria produto sem insumo.
  const removerExemplo = async () => {
    if (!window.confirm('Remover o exemplo da fábrica de bolos?\n\n'
      + 'Saem o produto de demonstração e os 7 insumos que o compõem, aqui e '
      + 'na Planilha de Insumos. A remoção é definitiva.')) return;
    setRemovendoExemplo(true);
    try {
      const r = await fetch(`${API}/insumos/${empresa.id}/exemplo`, { method: 'DELETE' });
      if (!r.ok) throw new Error('Não foi possível remover o exemplo.');
      await carregar();
      setErro(null);
    } catch (e) { setErro(e.message); }
    finally { setRemovendoExemplo(false); }
  };

  const fmt = (n) => (Number(n) || 0).toLocaleString('pt-BR',
    { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const card = { background: '#FFF', border: `1px solid ${G.borda}`,
    borderRadius: 0, padding: '16px 18px', marginBottom: 16 };
  const rotulo = { fontSize: 10, fontWeight: 800, color: G.txt2,
    letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 4 };
  const leitura = (cor, fundo) => ({ height: 36, display: 'flex', alignItems: 'center',
    justifyContent: 'flex-end', padding: '0 12px', border: `1px solid ${cor}`,
    borderRadius: 0, background: fundo, fontSize: 14, fontWeight: 700, color: cor,
    fontFamily: "'IBM Plex Mono', monospace" });

  const GRID_ITEM = '86px minmax(150px,1fr) 130px 116px 96px 108px 34px';

  return (
    <div style={{ padding: '24px 28px', position: 'relative', overflow: 'hidden' }}>
      <CarimboAIdvisor />
      <div style={{ marginBottom: 20 }}>
        <div style={{ position: 'relative', display: 'inline-block' }}>
          <h2 style={{ margin: 0, fontSize: 20, fontWeight: 800, color: G.txt1,
            position: 'relative', zIndex: 1 }}>
            Formação de Preço de Venda
          </h2>
          <MarcaDaguaTitulo>
            <polygon points="12 2 2 7 12 12 22 7 12 2"/>
            <polyline points="2 17 12 22 22 17"/>
            <polyline points="2 12 12 17 22 12"/>
          </MarcaDaguaTitulo>
        </div>
        <p style={{ ...TIPO.subtitulo, margin: '4px 0 0' }}>
          composição de custo e preço de cada produto
        </p>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 18 }}>
        <div style={{ padding: '12px 16px', background: G.verdeLight,
          borderLeft: `4px solid ${G.verde}`, borderRadius: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 800, color: G.verde,
            letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 6 }}>
            O que é a Formação de Preço
          </div>
          <div style={{ fontSize: 12, lineHeight: 1.45, color: G.txt1 }}>
            A composição de custo de cada produto. Você insere o insumo e informa o
            <strong> tamanho do produto</strong>; a conta é <strong>tamanho × índice ×
            preço unitário</strong>. Bolo de 3 kg com farinha a 0,5 kg/kg e R$ 4,00/kg
            consome R$ 6,00 de farinha.
          </div>
        </div>
        <div style={{ padding: '12px 16px', background: G.laranjaLight,
          borderLeft: `4px solid ${G.laranja}`, borderRadius: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 800, color: G.laranja,
            letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 6 }}>
            O que você faz aqui
          </div>
          <div style={{ fontSize: 12, lineHeight: 1.45, color: G.txt1 }}>
            Cadastre o produto com nome, tipo e tamanho, e monte a composição pelos botões
            de <strong>Matéria-prima</strong> e <strong>Mão de obra</strong>. A{' '}
            <MarcaAIdvisor /> calcula o custo direto e a Margem de Contribuição.
          </div>
        </div>
      </div>

      <FaixaExemplo quantos={produtos.filter(p => p.exemplo).length}
        onRemover={removerExemplo} removendo={removendoExemplo}/>

      {erro && (
        <div style={{ fontSize: 12.5, lineHeight: 1.45, color: G.txt1, marginBottom: 14,
          padding: '10px 14px', background: '#FEF2F2',
          borderLeft: `3px solid ${G.vermelho}`, borderRadius: 0 }}>{erro}</div>
      )}

      {/* Sem insumo cadastrado não há o que compor — a planilha vem antes. */}
      {!carregando && insumos.length === 0 && (
        <div style={{ fontSize: 12.5, lineHeight: 1.45, color: G.txt1, marginBottom: 14,
          padding: '12px 16px', background: G.laranjaLight,
          borderLeft: `3px solid ${G.laranja}`, borderRadius: 0 }}>
          A Planilha de Insumos está vazia. Cadastre os insumos primeiro — é deles que
          sai o custo de cada composição.
        </div>
      )}

      <div style={card}>
        {carregando ? (
          <div style={{ fontSize: 12.5, color: G.txt2, fontStyle: 'italic' }}>
            Carregando os produtos…
          </div>
        ) : (
          <>
            {produtos.length === 0 && !novo && (
              <div style={{ fontSize: 12, color: G.txt2, fontStyle: 'italic',
                padding: '12px 14px', background: G.fundo, borderRadius: 0, marginBottom: 12 }}>
                A <MarcaAIdvisor /> ainda não tem produtos desta empresa · adicione abaixo
                para montar a composição de custos e preço.
              </div>
            )}

            {/* Contagem no mesmo formato da Planilha de Insumos — a tela irmã
                já dizia "N insumos" e esta não dizia nada. */}
            {produtos.length > 0 && (
              <div style={{ fontSize: 11.5, color: G.txt2, marginBottom: 8 }}>
                {produtos.length} {produtos.length === 1 ? 'produto' : 'produtos'}
              </div>
            )}

            {produtos.slice(0, verTodosProdutos ? undefined : LIMITE_TABELA).map((p, n) => {
              const expandido = aberto === p.id;
              const mp = p.composicao.filter(l => l.tipo === 'materia_prima');
              const mo = p.composicao.filter(l => l.tipo === 'mao_de_obra');
              return (
                <div key={p.id} style={{ border: `1px solid ${expandido ? G.laranja : G.borda}`,
                  borderRadius: 0, marginBottom: 10, overflow: 'hidden' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12,
                    padding: '10px 14px', background: expandido ? G.laranjaLight : '#FFF' }}>
                    <div style={{ fontSize: 12, fontWeight: 700, color: G.laranja,
                      fontFamily: "'IBM Plex Mono', monospace", display: 'flex',
                      alignItems: 'center', gap: 6 }}>
                      <span style={{ color: G.txt2, fontWeight: 600 }}>{n + 1}.</span>
                      {p.codigo}
                      {p.exemplo && <SeloExemplo/>}
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 13.5, fontWeight: 700, color: G.txt1 }}>
                        {p.nome}
                        {p.codigo_empresa && (
                          <span style={{ marginLeft: 8, fontSize: 11, color: G.txt2,
                            fontFamily: "'IBM Plex Mono', monospace" }}>· {p.codigo_empresa}</span>
                        )}
                      </div>
                      <div style={{ fontSize: 11.5, color: G.txt2, marginTop: 2 }}>
                        {p.tipo === 'servico' ? 'Serviço' : 'Mercadoria'} ·{' '}
                        {Number(p.tamanho).toLocaleString('pt-BR', { maximumFractionDigits: 4 })}
                        {' '}{p.unidade_tamanho || ''} · custo R$ {fmt(p.custo_direto)} ·
                        preço <strong>R$ {fmt(p.preco_venda)}</strong> ·{' '}
                        {/* Imposto na linha-resumo (30/07): sem ele, a
                            contribuição parecia sair só do custo. */}
                        <span style={{ color: G.laranja }}>
                          imposto R$ {fmt(p.imposto_venda)}
                        </span> ·{' '}
                        contribuição <strong>R$ {fmt(p.margem_contribuicao)}</strong>
                        {p.margem_contribuicao_pct != null &&
                          ` (${p.margem_contribuicao_pct.toLocaleString('pt-BR',
                            { maximumFractionDigits: 0 })}%)`}
                      </div>
                    </div>
                    <button onClick={() => setAberto(expandido ? null : p.id)}
                      style={{ background: expandido ? '#FFF' : G.laranjaLight,
                        border: `1px solid ${G.laranja}`, color: G.laranja,
                        padding: '5px 12px', minWidth: 170, textAlign: 'center',
                        fontSize: 11, fontWeight: 700, letterSpacing: '0.06em',
                        textTransform: 'uppercase', borderRadius: 0, cursor: 'pointer',
                        fontFamily: "'Outfit', sans-serif", whiteSpace: 'nowrap' }}>
                      {expandido ? '▴ Clique para fechar' : '▾ Clique para abrir'}
                    </button>
                    <BotaoRemover onClick={() => removerProduto(p)}
                      titulo={`Remover o produto ${p.nome}`}/>
                  </div>

                  {expandido && (
                    <div style={{ padding: '14px', borderTop: `1px solid ${G.borda}`,
                      display: 'flex', flexDirection: 'column', gap: 14 }}>
                      {/* Identificação e tamanho */}
                      <div style={{ display: 'grid',
                        gridTemplateColumns: '1fr 140px 150px 150px 160px', gap: 12 }}>
                        <div>
                          <div style={rotulo}>Nome do produto</div>
                          <input defaultValue={p.nome}
                            onBlur={e => e.target.value !== p.nome && editarProduto(p.id, 'nome', e.target.value)}
                            style={{ fontSize: 12.5, padding: '8px 10px', height: 36,
                              border: `1px solid ${G.borda}`, borderRadius: 0,
                              width: '100%', boxSizing: 'border-box' }}/>
                        </div>
                        <div>
                          <div style={rotulo}>Cód. da empresa</div>
                          <input defaultValue={p.codigo_empresa || ''} placeholder="—"
                            onBlur={e => e.target.value !== (p.codigo_empresa || '') &&
                              editarProduto(p.id, 'codigo_empresa', e.target.value)}
                            style={{ fontSize: 12.5, padding: '8px 10px', height: 36,
                              border: `1px solid ${G.borda}`, borderRadius: 0, width: '100%',
                              boxSizing: 'border-box', fontFamily: "'IBM Plex Mono', monospace" }}/>
                        </div>
                        <div>
                          <div style={rotulo}>Tipo</div>
                          <select value={p.tipo}
                            onChange={e => editarProduto(p.id, 'tipo', e.target.value)}
                            style={{ fontSize: 12.5, padding: '8px 6px', height: 36,
                              border: `1px solid ${G.borda}`, borderRadius: 0,
                              width: '100%', boxSizing: 'border-box' }}>
                            <option value="mercadoria">Mercadoria</option>
                            <option value="servico">Serviço</option>
                          </select>
                        </div>
                        <div>
                          {/* O TAMANHO multiplica o índice de cada insumo. Não é o
                              volume de vendas — esse vive na análise de volume. */}
                          <div style={rotulo}>Tamanho do produto</div>
                          <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                            <CampoNumericoBP60M
                              valor={Number(p.tamanho) || 0}
                              onChange={(v) => editarProduto(p.id, 'tamanho', v)}
                              label={`Tamanho do produto · ${p.nome}`}
                              permitirDecimal={true} min={0} formato="decimal"
                            />
                            <select value={p.unidade_tamanho || ''}
                              onChange={e => editarProduto(p.id, 'unidade_tamanho', e.target.value)}
                              style={{ fontSize: 11.5, padding: '8px 2px', height: 36,
                                border: `1px solid ${G.borda}`, borderRadius: 0 }}>
                              <option value="">—</option>
                              {UNIDADES_INSUMO.map(u => <option key={u} value={u}>{u}</option>)}
                            </select>
                          </div>
                        </div>
                        <div>
                          <div style={rotulo}>Preço de venda · mercado</div>
                          <CampoNumericoBP60M
                            valor={Number(p.preco_venda) || 0}
                            onChange={(v) => editarProduto(p.id, 'preco_venda', v)}
                            label={`Preço de venda · ${p.nome}`}
                            permitirDecimal={true} min={0} formato="brl"
                          />
                        </div>
                      </div>

                      {/* Dados de venda — alimentam o consolidado e a análise de
                          volume. O TAMANHO acima forma o custo unitário; a
                          QUANTIDADE aqui é quantas unidades se vende por mês. */}
                      <div style={{ display: 'grid',
                        gridTemplateColumns: 'repeat(3, 1fr)', gap: 12 }}>
                        <div>
                          <div style={rotulo}>Mix · participação na receita</div>
                          <CampoNumericoBP60M
                            valor={Number(p.mix) || 0}
                            onChange={(v) => editarProduto(p.id, 'mix', v)}
                            label={`Mix de receita · ${p.nome}`}
                            permitirDecimal={true} min={0} max={100}
                            sufixo="%" formato="decimal"
                          />
                        </div>
                        <div>
                          <div style={rotulo}>Quantidade vendida · por mês</div>
                          <CampoNumericoBP60M
                            valor={Number(p.quantidade_mes) || 0}
                            onChange={(v) => editarProduto(p.id, 'quantidade_mes', v)}
                            label={`Quantidade vendida por mês · ${p.nome}`}
                            permitirDecimal={false} min={0} sufixo="un." formato="int"
                          />
                        </div>
                        <div>
                          <div style={rotulo}>Fatia da despesa indireta</div>
                          <CampoNumericoBP60M
                            valor={Number(p.fatia_fixo) || 0}
                            onChange={(v) => editarProduto(p.id, 'fatia_fixo', v)}
                            label={`Fatia da despesa indireta · ${p.nome}`}
                            permitirDecimal={true} min={0} max={100}
                            sufixo="%" formato="decimal"
                          />
                        </div>
                      </div>

                      {/* Composição — os dois botões separados */}
                      {[['materia_prima', 'Matéria-prima', G.verde, G.verdeLight, mp],
                        ['mao_de_obra', 'Mão de obra', G.laranja, G.laranjaLight, mo]].map(
                        ([tipo, titulo, cor, fundo, linhas]) => (
                        <div key={tipo}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 10,
                            marginBottom: 8 }}>
                            <div style={{ fontSize: 12, fontWeight: 900, color: cor,
                              letterSpacing: '0.06em', textTransform: 'uppercase',
                              background: fundo, border: `1px solid ${cor}`,
                              borderRadius: 0, padding: '5px 12px' }}>{titulo}</div>
                            <button onClick={() => setEscolhendo(
                                escolhendo?.produtoId === p.id && escolhendo?.tipo === tipo
                                  ? null : { produtoId: p.id, tipo })}
                              disabled={insumos.filter(i => i.tipo === tipo).length === 0}
                              style={{ marginLeft: 'auto', padding: '6px 14px', fontSize: 11.5,
                                fontWeight: 700, background: '#FFF', color: cor,
                                border: `1px dashed ${cor}`, borderRadius: 0,
                                cursor: insumos.filter(i => i.tipo === tipo).length ? 'pointer' : 'not-allowed',
                                opacity: insumos.filter(i => i.tipo === tipo).length ? 1 : 0.45,
                                fontFamily: "'Outfit', sans-serif" }}>
                              + Inserir {titulo.toLowerCase()}
                            </button>
                          </div>

                          {/* Escolha do insumo, vinda da Planilha */}
                          {escolhendo?.produtoId === p.id && escolhendo?.tipo === tipo && (
                            <div style={{ marginBottom: 10, padding: '10px 12px',
                              background: fundo, border: `1px solid ${cor}`, borderRadius: 0 }}>
                              <div style={{ fontSize: 11, fontWeight: 800, color: cor,
                                marginBottom: 8, letterSpacing: '0.06em',
                                textTransform: 'uppercase' }}>
                                Escolha o insumo da Planilha
                              </div>
                              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                                {insumos.filter(i => i.tipo === tipo).map(i => {
                                  const jaTem = p.composicao.some(l => l.insumo_id === i.id);
                                  return (
                                    <button key={i.id} disabled={jaTem}
                                      onClick={() => addInsumo(p.id, i.id)}
                                      title={jaTem ? 'Já está na composição' :
                                        `${i.indice_consumo} ${i.unidade}/${i.unidade_produto || '?'} · R$ ${fmt(i.preco_unitario)}/${i.unidade}`}
                                      style={{ padding: '6px 12px', fontSize: 11.5,
                                        fontWeight: 600, background: jaTem ? G.fundo : '#FFF',
                                        color: jaTem ? G.txt2 : G.txt1,
                                        border: `1px solid ${jaTem ? G.borda : cor}`,
                                        borderRadius: 0, cursor: jaTem ? 'not-allowed' : 'pointer',
                                        fontFamily: "'Outfit', sans-serif" }}>
                                      {i.nome}
                                      <span style={{ marginLeft: 6, fontSize: 10, color: G.txt2 }}>
                                        {i.codigo}
                                      </span>
                                    </button>
                                  );
                                })}
                              </div>
                            </div>
                          )}

                          {linhas.length === 0 ? (
                            <div style={{ fontSize: 11.5, color: G.txt2, fontStyle: 'italic',
                              padding: '8px 12px', background: G.fundo, borderRadius: 0 }}>
                              Nenhum insumo de {titulo.toLowerCase()} nesta composição.
                            </div>
                          ) : (
                            <>
                              <div style={{ display: 'grid', gridTemplateColumns: GRID_ITEM,
                                gap: 10, padding: '0 0 5px', borderBottom: `1px solid ${G.borda}` }}>
                                {[['Código','left'],['Insumo','left'],['Índice de consumo','right'],
                                  ['Consumo','right'],['Preço unit.','right'],['Custo','right'],['','center']]
                                  .map(([h, al], i) => (
                                  <div key={i} style={{ fontSize: 10, fontWeight: 800,
                                    color: G.txt2, letterSpacing: '0.08em',
                                    textTransform: 'uppercase', textAlign: al }}>{h}</div>
                                ))}
                              </div>
                              {/* `k`, não `n`: `n` já é o número do produto no
                                  laço de fora, e reusar o nome esconderia um do
                                  outro. */}
                              {linhas.map((l, k) => (
                                <div key={l.id} style={{ display: 'grid',
                                  gridTemplateColumns: GRID_ITEM, gap: 10, alignItems: 'center',
                                  padding: '6px 0', borderBottom: `1px solid ${G.borda}` }}>
                                  <div style={{ fontSize: 11.5, color: cor,
                                    fontFamily: "'IBM Plex Mono', monospace" }}>
                                    <span style={{ color: G.txt2, fontWeight: 600 }}>{k + 1}.</span>{' '}
                                    {l.codigo}
                                  </div>
                                  <div style={{ fontSize: 12.5, color: G.txt1 }}>
                                    {l.nome}
                                    {l.insumo_inativo && (
                                      <span style={{ marginLeft: 6, fontSize: 10, fontWeight: 700,
                                        color: G.vermelho, textTransform: 'uppercase' }}>
                                        · removido da planilha</span>
                                    )}
                                  </div>
                                  {/* Índice EDITÁVEL aqui: a farinha é 0,5 kg/kg no bolo
                                      e pode ser 0,7 kg/kg no pão. */}
                                  <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                                    <CampoNumericoBP60M
                                      valor={Number(l.indice_consumo) || 0}
                                      onChange={(v) => editarIndice(p.id, l.id, v)}
                                      label={`Índice · ${l.nome} em ${p.nome}`}
                                      permitirDecimal={true} min={0} formato="decimal"
                                    />
                                    <span style={{ fontSize: 10, color: G.txt2, whiteSpace: 'nowrap' }}>
                                      {l.unidade}/{l.unidade_produto || '?'}
                                    </span>
                                  </div>
                                  <div style={{ fontSize: 12, textAlign: 'right', color: G.txt2,
                                    fontFamily: "'IBM Plex Mono', monospace" }}>
                                    {Number(l.consumo).toLocaleString('pt-BR',
                                      { minimumFractionDigits: 2, maximumFractionDigits: 3 })} {l.unidade}
                                  </div>
                                  <div style={{ fontSize: 12, textAlign: 'right', color: G.txt2,
                                    fontFamily: "'IBM Plex Mono', monospace" }}>
                                    {fmt(l.preco_unitario)}
                                  </div>
                                  <div style={{ fontSize: 13, fontWeight: 900, textAlign: 'right',
                                    color: cor, fontFamily: "'IBM Plex Mono', monospace" }}>
                                    {fmt(l.custo)}
                                  </div>
                                  <BotaoRemover onClick={() => removerItem(p.id, l.id)}
                                    titulo={`Remover ${l.nome} da composição`}/>
                                </div>
                              ))}
                              <div style={{ display: 'flex', justifyContent: 'flex-end',
                                gap: 10, marginTop: 6, fontSize: 12, color: cor,
                                fontWeight: 800 }}>
                                Subtotal {titulo.toLowerCase()}:
                                <span style={{ fontFamily: "'IBM Plex Mono', monospace" }}>
                                  R$ {fmt(tipo === 'materia_prima'
                                    ? p.custo_materia_prima : p.custo_mao_de_obra)}
                                </span>
                              </div>
                            </>
                          )}
                        </div>
                      ))}

                      {/* ── Fechamento do produto · DEMONSTRATIVO ──────────
                          Reescrito em 30/07 a pedido de Fabio: *"Destaque o
                          imposto no demonstrativo."* Antes eram três caixas
                          (custo · preço · margem) e o imposto não aparecia em
                          lugar nenhum — a margem de contribuição saía sem ele,
                          inflada em 48% no bolo. Agora é uma cascata do preço
                          até a margem, com cada dedução na própria linha. */}
                      {(() => {
                        const pctTxt = (v) => v == null ? '—'
                          : `${v.toLocaleString('pt-BR', { minimumFractionDigits: 2,
                              maximumFractionDigits: 2 })}%`;
                        const linha = (rot, valor, pct, opts = {}) => (
                          <div style={{ display: 'grid',
                            gridTemplateColumns: 'minmax(180px, 1fr) 90px 120px',
                            gap: 10, alignItems: 'center', padding: '7px 12px',
                            background: opts.fundo || 'transparent',
                            borderTop: opts.traco ? `1px solid ${G.borda}` : 'none',
                            borderLeft: opts.barra ? `3px solid ${opts.cor}` : 'none' }}>
                            <span style={{ fontSize: opts.forte ? 12.5 : 12,
                              fontWeight: opts.forte ? 900 : 600,
                              color: opts.cor || G.txt1,
                              letterSpacing: opts.forte ? '0.04em' : 0 }}>{rot}</span>
                            <span style={{ fontSize: 11, textAlign: 'right',
                              color: opts.cor || G.txt2,
                              fontFamily: "'IBM Plex Mono', monospace" }}>{pctTxt(pct)}</span>
                            <span style={{ fontSize: opts.forte ? 14 : 12.5,
                              fontWeight: opts.forte ? 900 : 700, textAlign: 'right',
                              color: opts.cor || G.txt1,
                              fontFamily: "'IBM Plex Mono', monospace" }}>
                              R$ {fmt(valor)}
                            </span>
                          </div>
                        );
                        const mcOk = p.margem_contribuicao >= 0;
                        return (
                          <div style={{ borderTop: `2px solid ${G.borda}`, paddingTop: 10 }}>
                            {linha('Preço de venda', p.preco_venda, 100)}
                            {linha('(−) Custo direto', p.custo_direto,
                              p.preco_venda > 0 ? p.custo_direto / p.preco_venda * 100 : null,
                              { cor: G.txt2 })}
                            {linha('= Margem Bruta', p.margem_bruta, p.margem_bruta_pct,
                              { traco: true })}
                            {/* A linha que faltava. Laranja e com barra à
                                esquerda porque é a dedução que o consultor
                                esquece — e a que muda o preço de tabela. */}
                            {linha(
                              `(−) Imposto sobre a venda${p.aliquota_venda
                                ? `  ·  ${(p.aliquota_venda * 100).toLocaleString('pt-BR',
                                    { minimumFractionDigits: 2, maximumFractionDigits: 2 })}% medido na empresa`
                                : ''}`,
                              p.imposto_venda, p.imposto_venda_pct,
                              { cor: G.laranja, fundo: G.laranjaLight, barra: true })}
                            {linha('= MARGEM DE CONTRIBUIÇÃO', p.margem_contribuicao,
                              p.margem_contribuicao_pct,
                              { forte: true, traco: true,
                                cor: mcOk ? G.verde : G.vermelho,
                                fundo: mcOk ? G.verdeLight : '#FEF2F2',
                                barra: true })}
                          </div>
                        );
                      })()}
                      <div style={{ fontSize: 11, color: G.txt2, fontStyle: 'italic',
                        marginTop: 6 }}>
                        A Margem de Contribuição é o que sobra de cada unidade para pagar a
                        despesa indireta — já descontados o custo direto e o imposto sobre a
                        venda. É ela que o Rateio de Despesas Fixas usa.
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
            <BotaoMostrarMais total={produtos.length} limite={LIMITE_TABELA}
              aberto={verTodosProdutos} rotulo="produtos"
              onAlternar={() => setVerTodosProdutos(v => !v)}/>

            {/* Novo produto */}
            {novo ? (
              <div style={{ marginTop: 10, padding: '12px 14px', background: G.laranjaLight,
                border: `1px solid ${G.laranja}`, borderRadius: 0 }}>
                <div style={{ fontSize: 11, fontWeight: 800, color: G.laranja, marginBottom: 8,
                  letterSpacing: '0.06em', textTransform: 'uppercase' }}>
                  Novo produto · o código é emitido pela ferramenta
                </div>
                <div style={{ display: 'grid',
                  gridTemplateColumns: '1fr 130px 140px 150px 150px', gap: 10, alignItems: 'end' }}>
                  <div>
                    <div style={rotulo}>Nome do produto</div>
                    <input value={novo.nome} autoFocus
                      onChange={e => setNovo({ ...novo, nome: e.target.value })}
                      style={{ fontSize: 12.5, padding: '8px 10px', height: 36,
                        border: `1px solid ${G.borda}`, borderRadius: 0, width: '100%',
                        boxSizing: 'border-box' }}/>
                  </div>
                  <div>
                    <div style={rotulo}>Cód. da empresa</div>
                    <input value={novo.codigo_empresa}
                      onChange={e => setNovo({ ...novo, codigo_empresa: e.target.value })}
                      style={{ fontSize: 12.5, padding: '8px 10px', height: 36,
                        border: `1px solid ${G.borda}`, borderRadius: 0, width: '100%',
                        boxSizing: 'border-box', fontFamily: "'IBM Plex Mono', monospace" }}/>
                  </div>
                  <div>
                    <div style={rotulo}>Tipo</div>
                    <select value={novo.tipo}
                      onChange={e => setNovo({ ...novo, tipo: e.target.value })}
                      style={{ fontSize: 12.5, padding: '8px 6px', height: 36,
                        border: `1px solid ${G.borda}`, borderRadius: 0, width: '100%',
                        boxSizing: 'border-box' }}>
                      <option value="mercadoria">Mercadoria</option>
                      <option value="servico">Serviço</option>
                    </select>
                  </div>
                  <div>
                    <div style={rotulo}>Tamanho</div>
                    <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                      <CampoNumericoBP60M
                        valor={Number(novo.tamanho) || 0}
                        onChange={(v) => setNovo({ ...novo, tamanho: v })}
                        label="Tamanho do novo produto"
                        permitirDecimal={true} min={0} formato="decimal"
                      />
                      <select value={novo.unidade_tamanho}
                        onChange={e => setNovo({ ...novo, unidade_tamanho: e.target.value })}
                        style={{ fontSize: 11.5, padding: '8px 2px', height: 36,
                          border: `1px solid ${G.borda}`, borderRadius: 0 }}>
                        <option value="">—</option>
                        {UNIDADES_INSUMO.map(u => <option key={u} value={u}>{u}</option>)}
                      </select>
                    </div>
                  </div>
                  <div>
                    <div style={rotulo}>Preço de venda</div>
                    <CampoNumericoBP60M
                      valor={Number(novo.preco_venda) || 0}
                      onChange={(v) => setNovo({ ...novo, preco_venda: v })}
                      label="Preço de venda do novo produto"
                      permitirDecimal={true} min={0} formato="brl"
                    />
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                  <button onClick={salvarNovo}
                    style={{ padding: '8px 18px', fontSize: 12, fontWeight: 700,
                      background: G.laranja, color: '#FFF', border: 'none', borderRadius: 0,
                      cursor: 'pointer', fontFamily: "'Outfit', sans-serif" }}>Salvar produto</button>
                  <button onClick={() => { setNovo(null); setErro(null); }}
                    style={{ padding: '8px 18px', fontSize: 12, fontWeight: 700,
                      background: '#FFF', color: G.txt2, border: `1px solid ${G.borda}`,
                      borderRadius: 0, cursor: 'pointer',
                      fontFamily: "'Outfit', sans-serif" }}>Cancelar</button>
                </div>
              </div>
            ) : (
              <button onClick={() => setNovo({ nome: '', codigo_empresa: '', tipo: 'mercadoria',
                tamanho: 1, unidade_tamanho: '', preco_venda: 0 })}
                style={{ marginTop: 10, padding: '8px 14px', fontSize: 12, fontWeight: 700,
                  background: '#FFF', color: G.laranja, border: `1px dashed ${G.laranja}`,
                  borderRadius: 0, cursor: 'pointer', fontFamily: "'Outfit', sans-serif" }}>
                + Adicionar produto/serviço
              </button>
            )}
          </>
        )}
      </div>

      {/* ═══ Custos Diretos consolidado · ponderado pelo mix ═══ */}
      {produtos.length > 0 && (() => {
        const somaMix = produtos.reduce((s, p) => s + (Number(p.mix) || 0), 0);
        // Sem mix não há como ponderar: dizer isso é melhor que exibir uma
        // média simples fazendo passar por ponderada.
        const ponderar = (campo) => somaMix > 0
          ? produtos.reduce((s, p) => {
              const preco = Number(p.preco_venda) || 0;
              if (!(preco > 0)) return s;
              return s + ((Number(p.mix) || 0) / somaMix) * (Number(p[campo]) || 0) / preco * 100;
            }, 0)
          : null;
        const pond = ponderar('custo_direto');
        const pondImposto = ponderar('imposto_venda');
        // A margem consolidada era `100 - pond` — a conta pulava o imposto e
        // batia 65% no bolo, contra os 44% reais (30/07). Agora sai da mesma
        // cascata do demonstrativo: preço − custo − imposto.
        const pondMC = pond === null ? null : 100 - pond - (pondImposto || 0);
        return (
          <div style={card}>
            <div style={{ fontSize: 16, fontWeight: 700, letterSpacing: '0.10em',
              textTransform: 'uppercase', color: G.laranja, marginBottom: 8 }}>
              Custos Diretos (Variáveis com Receita)
              <span style={{ fontSize: 12, fontWeight: 400, letterSpacing: 0,
                textTransform: 'none', color: G.txt2 }}>
                {' '}· consolidado das composições · ponderado pelo mix
                {' '}· <span style={{ color: G.laranja, fontWeight: 700 }}>clique em
                ↕ para ordenar</span></span>
            </div>
            <div style={{ padding: '10px 14px', background: G.verdeLight,
              borderLeft: `3px solid ${G.verde}`, borderRadius: 0, marginBottom: 12,
              fontSize: 12, lineHeight: 1.45, color: G.txt1 }}>
              A <MarcaAIdvisor /> consolida o custo direto e a margem de contribuição do
              conjunto, ponderados pelo mix de receita. Para ajustar, edite a composição
              de cada produto acima.
            </div>
            {/* Coluna de IMPOSTO acrescentada em 30/07: sem ela a tabela ia de
                custo direto a contribuição sem mostrar o que sai no meio. */}
            {/* "Mix" virou "% Receita" (Fabio, 09/09): o rótulo antigo não
                dizia o que o número era. */}
            {/* Ordenação por clique (14/09) em % Receita e Marg. Final. Só
                nessas duas: são as leituras que o Dashboard rankeia, e a tabela
                passa a responder à mesma pergunta com a lista inteira. */}
            <div style={{ display: 'grid', gridTemplateColumns: GRID_CONSOL,
              gap: 10, padding: '0 0 6px', borderBottom: `2px solid ${G.borda}` }}>
              {[['Produto/serviço','left',null],['% Receita','right','mix'],
                ['Custo','right',null],
                ['Imposto','right',null],['Preço','right',null],['Contrib.','right',null],
                ['Marg. Final','right','margem_final']].map(([h, al, campo], i) => {
                const ativo = campo && ordemConsol === campo;
                return (
                  <div key={i}
                    onClick={campo ? () => setOrdemConsol(campo) : undefined}
                    title={campo ? `Ordenar por ${h}, do maior para o menor` : ''}
                    style={{ fontSize: 10, fontWeight: 800,
                      color: ativo ? '#FFF' : (h === 'Imposto' ? G.laranja : G.txt2),
                      letterSpacing: '0.08em', textTransform: 'uppercase', textAlign: al,
                      cursor: campo ? 'pointer' : 'default',
                      // A coluna ORDENÁVEL tem moldura tracejada; a ATIVA é
                      // laranja cheia. Antes eram `·` e `↓` num cinza de 10px —
                      // o `·` não dizia "clicável" para ninguém.
                      background: ativo ? G.laranja : 'transparent',
                      border: campo
                        ? `1px ${ativo ? 'solid' : 'dashed'} ${ativo ? G.laranja : G.borda}`
                        : '1px solid transparent',
                      borderRadius: 0,
                      padding: campo ? '3px 6px' : '3px 0',
                      marginBottom: 2 }}>
                    {h}{campo ? (ativo ? ' ↓' : ' ↕') : ''}
                  </div>
                );
              })}
            </div>
            {ordenarConsol(produtos).slice(0, verTodosConsol ? undefined : LIMITE_TABELA).map((p, n) => (
              <div key={p.id} style={{ display: 'grid',
                gridTemplateColumns: GRID_CONSOL,
                gap: 10, alignItems: 'center', padding: '7px 0',
                borderBottom: `1px solid ${G.borda}` }}>
                <div style={{ fontSize: 12.5, color: G.txt1 }}>
                  <span style={{ color: G.txt2, fontWeight: 600 }}>{n + 1}.</span> {p.nome}
                </div>
                <div style={{ fontSize: 12, textAlign: 'right', color: G.txt2,
                  fontFamily: "'IBM Plex Mono', monospace" }}>
                  {(Number(p.mix) || 0).toLocaleString('pt-BR', { maximumFractionDigits: 1 })}%
                </div>
                <div style={{ fontSize: 12.5, textAlign: 'right', color: G.txt2,
                  fontFamily: "'IBM Plex Mono', monospace" }}>R$ {fmt(p.custo_direto)}</div>
                <div style={{ fontSize: 12.5, textAlign: 'right', color: G.laranja,
                  fontFamily: "'IBM Plex Mono', monospace" }}>R$ {fmt(p.imposto_venda)}</div>
                <div style={{ fontSize: 12.5, textAlign: 'right', color: G.txt1,
                  fontFamily: "'IBM Plex Mono', monospace" }}>R$ {fmt(p.preco_venda)}</div>
                <div style={{ fontSize: 12.5, fontWeight: 900, textAlign: 'right',
                  color: p.margem_contribuicao >= 0 ? G.verde : G.vermelho,
                  fontFamily: "'IBM Plex Mono', monospace" }}>
                  {p.margem_contribuicao_pct != null
                    ? `${p.margem_contribuicao_pct.toLocaleString('pt-BR',
                        { maximumFractionDigits: 0 })}%` : '—'}
                </div>
                {/* MARGEM FINAL (14/09) · contribuição MENOS a fatia do fixo que
                    este produto carrega, sobre a receita dele. É a coluna do
                    Dashboard, com uma diferença: aqui o imposto já saiu, porque
                    `margem_contribuicao` é depois dele. Sem quantidade ou sem
                    despesa fixa medida a conta não existe, e a célula diz "—"
                    em vez de fingir um número ([[nao-se-chuta-o-que-nao-se-estima]]). */}
                {(() => {
                  const pct = margemFinalPct(p);
                  return (
                    <div style={{ fontSize: 12.5, fontWeight: 900, textAlign: 'right',
                      color: pct == null ? G.txt2 : (pct >= 0 ? G.verde : G.vermelho),
                      fontFamily: "'IBM Plex Mono', monospace" }}>
                      {pct == null ? '—'
                        : `${pct.toLocaleString('pt-BR', { maximumFractionDigits: 0 })}%`}
                    </div>
                  );
                })()}
              </div>
            ))}
            {/* O consolidado abaixo é do CONJUNTO, não das 5 linhas visíveis —
                ponderado pelo mix dos 75. Esconder linha não muda total. */}
            <BotaoMostrarMais total={produtos.length} limite={LIMITE_TABELA}
              aberto={verTodosConsol} rotulo="produtos"
              onAlternar={() => setVerTodosConsol(v => !v)}/>
            <div style={{ display: 'flex', gap: 14, marginTop: 12 }}>
              {[['Custo direto consolidado', pond, G.laranja, G.laranjaLight],
                ['Imposto sobre a venda', pondImposto, '#B45309', '#FFFBEB'],
                ['Margem de contribuição', pondMC, G.verde, G.verdeLight]]
                .map(([rot, val, cor, fundo]) => (
                <div key={rot} style={{ flex: 1, background: fundo,
                  borderLeft: `4px solid ${cor}`, borderRadius: 0, padding: '10px 14px' }}>
                  <div style={{ fontSize: 10, fontWeight: 800, color: G.txt2,
                    letterSpacing: '0.08em', textTransform: 'uppercase' }}>{rot}</div>
                  <div style={{ fontSize: 20, fontWeight: 900, color: cor, marginTop: 2 }}>
                    {val === null ? '—'
                      : `${val.toLocaleString('pt-BR', { maximumFractionDigits: 0 })}%`}
                  </div>
                </div>
              ))}
            </div>
            {somaMix === 0 && (
              <div style={{ fontSize: 11.5, color: G.txt2, marginTop: 8 }}>
                Informe o mix de cada produto para a ferramenta ponderar o consolidado.
              </div>
            )}
            {somaMix > 0 && Math.abs(somaMix - 100) > 0.5 && (
              <div style={{ fontSize: 12, lineHeight: 1.45, color: G.txt1, marginTop: 10,
                padding: '10px 14px', background: G.laranjaLight,
                borderLeft: `3px solid ${G.laranja}`, borderRadius: 0 }}>
                O mix soma <strong>{somaMix.toLocaleString('pt-BR',
                  { maximumFractionDigits: 1 })}%</strong> — o consolidado foi ponderado
                sobre esse total, não sobre 100%.
              </div>
            )}
          </div>
        );
      })()}

      {/* ═══ Quanto é Preciso Vender · Análise Custo-Volume-Lucro ═══ */}
      <div style={card}>
        <div style={{ fontSize: 16, fontWeight: 700, letterSpacing: '0.10em',
          textTransform: 'uppercase', color: G.laranja, marginBottom: 8 }}>
          Quanto é Preciso Vender
          <span style={{ fontSize: 12, fontWeight: 400, letterSpacing: 0,
            textTransform: 'none', color: G.txt2 }}>
            {' '}· análise custo-volume-lucro · calculada pela{' '}
            <MarcaAIdvisor variant="default" /></span>
        </div>
        <div style={{ padding: '10px 14px', background: G.verdeLight,
          borderLeft: `3px solid ${G.verde}`, borderRadius: 0, marginBottom: 12,
          fontSize: 12, lineHeight: 1.45, color: G.txt1 }}>
          Dada a despesa indireta, o custo de cada item e os impostos, a{' '}
          <MarcaAIdvisor /> calcula <strong>quanto cada produto precisa vender</strong> para
          cobrir sua fatia do fixo e ainda entregar a margem operacional que você definir.
        </div>

        {/* Margem operacional (digitada) · Imposto sobre o lucro · Margem
            líquida — três células do MESMO tamanho, lado a lado (forma ditada
            pelo Fabio, 30/08). As duas últimas são leitura: a alíquota vem do
            backend (medida no balancete quando há; senão a do regime — no
            Simples é zero, o imposto já está no DAS) e a líquida é a
            operacional depois desse imposto. */}
        <div style={{ display: 'grid', gridTemplateColumns: '220px 200px 200px 220px 1fr',
          gap: 14, alignItems: 'start', marginBottom: 14 }}>
          <div>
            {/* Era "Margem líquida desejada" e não era líquida (30/07): a conta
                para no EBIT — depois do custo direto, do imposto sobre a venda
                e da despesa indireta, mas ANTES do IRPJ/CSLL e do resultado
                financeiro. Isso é margem OPERACIONAL. Fabio optou por manter o
                campo na língua do consultor e MOSTRAR a líquida ao lado. */}
            <div style={rotulo}>Margem operacional desejada</div>
            <CampoNumericoBP60M
              valor={margemDesejadaPct}
              onChange={(v) => setMargemDesejadaPct(v)}
              label="Margem operacional desejada (% da receita)"
              permitirDecimal={true} min={0} max={99} sufixo="%" formato="decimal"
            />
          </div>
          {(() => {
            const aliq = (cvl && typeof cvl.aliquota_ir === 'number') ? cvl.aliquota_ir : null;
            const op = Number(margemDesejadaPct) || 0;
            const fmt1 = (v) => v.toFixed(1).replace('.', ',');
            return (
              <>
                <div>
                  <div style={rotulo}>Imposto sobre o lucro</div>
                  <div style={leitura(G.txt2, G.fundo)}>
                    {aliq === null ? '—' : `${fmt1(aliq * 100)}%`}
                  </div>
                  <div style={{ fontSize: 10.5, color: G.txt2, marginTop: 4 }}>
                    {aliq === null ? 'calculado pela ferramenta'
                      : aliq === 0 ? 'já incluído no DAS do Simples'
                        : cvl.aliquota_ir_origem === 'medida'
                          ? 'medido na contabilidade da empresa'
                          : 'pelo regime tributário'}
                  </div>
                </div>
                <div>
                  <div style={rotulo}>Margem líquida resultante</div>
                  <div style={leitura(G.verde, G.verdeLight)}>
                    {aliq === null ? '—' : `${fmt1(op * (1 - aliq))}%`}
                  </div>
                  <div style={{ fontSize: 10.5, color: G.txt2, marginTop: 4 }}>
                    após o imposto sobre o lucro · antes do resultado financeiro
                  </div>
                </div>
              </>
            );
          })()}
          {/* O realizado FECHA a linha: as três células à esquerda são hipótese
              (o que se quer), esta é medição (o que saiu). */}
          <BlocoMargemRealizada realizado={cvl?.realizado}
            margemDesejada={cvl?.margem_desejada} />
          <div style={{ fontSize: 11.5, color: G.txt2, lineHeight: 1.45 }}>
            Uma só para a empresa. O que modula produto a produto é a fatia da
            despesa indireta, informada na composição de cada um.
            <div style={{ marginTop: 6, color: G.txt2 }}>
              É o que sobra <strong>do negócio funcionando</strong> — já
              descontados o custo direto, o imposto sobre a venda e a despesa
              indireta. Ainda não passou pelo IRPJ/CSLL nem pelos juros.
            </div>
          </div>
        </div>

        {!cvl ? (
          <div style={{ fontSize: 12, color: G.txt2, fontStyle: 'italic',
            padding: '12px 14px', background: G.fundo, borderRadius: 0 }}>
            {!(despesaIndireta > 0)
              ? <>Sem despesa indireta apurada não há o que ratear · confira a aba Despesas Indiretas.</>
              : <>Cadastre ao menos um produto com preço e custo para a <MarcaAIdvisor /> calcular.</>}
          </div>
        ) : (
          <>
            <SeletorRateio valor={metodoFatia} onChange={setMetodoFatia} />
            {/* GRID_CVL: uma constante para cabeçalho e linha — as duas colunas
                novas (09/09) entrariam torto se cada um tivesse a sua régua. */}
            <div style={{ display: 'grid', gridTemplateColumns: GRID_CVL,
              gap: 10, marginBottom: 6 }}>
              {[['Produto/serviço', null], ['Fatia', 'fatia'],
                ['Quantidade para Breakeven', null],
                ['Quantidade a + para Margem', null],
                ['Precisa vender', 'precisa'], ['Você planeja', null],
                ['Diferença', 'gap']]
                .map(([h, campo], i) => {
                const ativo = campo && ordemCVL === campo;
                return (
                  <div key={h}
                    onClick={campo ? () => setOrdemCVL(ativo ? 'nenhuma' : campo) : undefined}
                    title={campo ? `Ordenar por ${h}, do maior para o menor` : ''}
                    style={{ fontSize: 10, fontWeight: 800,
                      color: ativo ? '#FFF' : G.txt2,
                      letterSpacing: '0.08em', textTransform: 'uppercase',
                      textAlign: i === 0 ? 'left' : 'right',
                      cursor: campo ? 'pointer' : 'default',
                      background: ativo ? G.laranja : 'transparent',
                      border: campo
                        ? `1px ${ativo ? 'solid' : 'dashed'} ${ativo ? G.laranja : G.borda}`
                        : '1px solid transparent',
                      borderRadius: 0, padding: campo ? '3px 6px' : '3px 0' }}>
                    {h}{campo ? (ativo ? ' ↓' : ' ↕') : ''}
                  </div>
                );
              })}
            </div>
            {ordenarCVL(cvl.itens || []).slice(0, verTodosCVL ? undefined : LIMITE_TABELA).map((it, i) => {
              // Casado pelo ID do item, nunca pela posição na lista.
              const planeja = planejadoDe(it);
              // Diferença na leitura do empresário (Fabio, 09/09): o que ele
              // vendeu MENOS o que precisaria vender. Positivo = passou da
              // conta; negativo = falta. Estava invertido — vender 5 a mais
              // aparecia como −5.
              const gap = gapDe(it);
              return (
                <div key={it.id || i} style={{ display: 'grid',
                  gridTemplateColumns: GRID_CVL,
                  gap: 10, alignItems: 'center', padding: '7px 0',
                  borderTop: `1px solid ${G.borda}` }}>
                  <div style={{ fontSize: 12.5, color: G.txt1 }}>
                    <span style={{ color: G.txt2, fontWeight: 600 }}>{i + 1}.</span> {it.nome}
                  </div>
                  <div style={{ fontSize: 12.5, textAlign: 'right', color: G.txt2,
                    fontFamily: "'IBM Plex Mono', monospace" }}>
                    {(it.fatia * 100).toLocaleString('pt-BR',
                      { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%
                  </div>
                  {/* Paga a despesa indireta. Existe mesmo quando o LUCRO é
                      inalcançável — e é o que o empresário ainda consegue. */}
                  <div style={{ fontSize: 12.5, fontWeight: 700, textAlign: 'right',
                    color: G.txt1, fontFamily: "'IBM Plex Mono', monospace" }}>
                    {it.quantidade_breakeven == null ? '—'
                      : it.quantidade_breakeven.toLocaleString('pt-BR', { maximumFractionDigits: 0 })}
                  </div>
                  {/* O que se vende SÓ porque se pediu lucro — o pedaço negociável. */}
                  {/* Mesmo tom da coluna anterior: o laranja da marca, num
                      número, era lido como alerta (Fabio, 09/09). */}
                  <div style={{ fontSize: 12.5, fontWeight: 700, textAlign: 'right',
                    color: G.txt1, fontFamily: "'IBM Plex Mono', monospace" }}>
                    {it.quantidade_pelo_lucro == null ? '—'
                      : `+${it.quantidade_pelo_lucro.toLocaleString('pt-BR', { maximumFractionDigits: 0 })}`}
                  </div>
                  <div style={{ fontSize: 13, fontWeight: 900, textAlign: 'right',
                    color: it.inviavel ? G.vermelho : G.verde,
                    fontFamily: "'IBM Plex Mono', monospace" }}>
                    {it.inviavel ? 'não fecha'
                      : it.quantidade.toLocaleString('pt-BR', { maximumFractionDigits: 0 })}
                  </div>
                  <div style={{ fontSize: 12.5, textAlign: 'right', color: G.txt2,
                    fontFamily: "'IBM Plex Mono', monospace" }}>
                    {planeja.toLocaleString('pt-BR', { maximumFractionDigits: 0 })}
                  </div>
                  <div style={{ fontSize: 12.5, fontWeight: 700, textAlign: 'right',
                    color: gap === null ? G.txt2 : (gap < 0 ? G.vermelho : G.verde),
                    fontFamily: "'IBM Plex Mono', monospace" }}>
                    {gap === null ? '—'
                      : `${gap > 0 ? '+' : ''}${gap.toLocaleString('pt-BR', { maximumFractionDigits: 0 })}`}
                  </div>
                </div>
              );
            })}
            {/* Os três quadros abaixo somam o conjunto inteiro, não o recorte. */}
            <BotaoMostrarMais total={(cvl.itens || []).length} limite={LIMITE_TABELA}
              aberto={verTodosCVL} rotulo="itens"
              onAlternar={() => setVerTodosCVL(v => !v)}/>
            <div style={{ display: 'flex', gap: 14, marginTop: 12, flexWrap: 'wrap' }}>
              {[['Receita necessária por mês', `R$ ${fmt(cvl.receita_mensal)}`, G.verde, G.verdeLight],
                ['Despesa indireta a cobrir', `R$ ${fmt(cvl.despesa_fixa_mensal)}`, G.laranja, G.laranjaLight],
                ['Imposto sobre a venda',
                 `${(cvl.aliquota_sobre_venda * 100).toLocaleString('pt-BR',
                   { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%`,
                 G.txt1, G.fundo]].map(([rot, val, cor, fundo]) => (
                <div key={rot} style={{ flex: '1 1 200px', background: fundo,
                  border: `1px solid ${cor}`, borderRadius: 0, padding: '10px 14px' }}>
                  <div style={{ fontSize: 10, fontWeight: 800, color: cor,
                    letterSpacing: '0.08em', textTransform: 'uppercase' }}>{rot}</div>
                  <div style={{ fontSize: 16, fontWeight: 900, color: cor, marginTop: 4,
                    fontFamily: "'IBM Plex Mono', monospace" }}>{val}</div>
                </div>
              ))}
            </div>
            <div style={{ fontSize: 11, color: G.txt2, marginTop: 8 }}>
              Regime <strong>{cvl.regime}</strong>
              {cvl.regime === 'Simples Nacional'
                ? <> · anexo {cvl.anexo} · alíquota resolvida em {cvl.iteracoes} passo
                    {cvl.iteracoes === 1 ? '' : 's'}</>
                : <> · alíquota medida nos lançamentos da empresa</>}
            </div>
            {(cvl.avisos || []).map((a, i) => (
              <div key={i} style={{ fontSize: 12, lineHeight: 1.45, color: G.txt1,
                padding: '10px 14px', background: G.laranjaLight, marginTop: 8,
                borderLeft: `3px solid ${G.laranja}`, borderRadius: 0 }}>{a}</div>
            ))}
            {(cvl.erros || []).map((e, i) => (
              <div key={i} style={{ fontSize: 12, lineHeight: 1.45, color: G.txt1,
                padding: '10px 14px', background: '#FEF2F2', marginTop: 8,
                borderLeft: `3px solid ${G.vermelho}`, borderRadius: 0 }}>{e}</div>
            ))}
          </>
        )}
      </div>

      {/* ═══ Cenários do produto novo ═══
          Quando o fixo já está coberto, a contribuição do produto novo deixa de
          ser obrigação e vira decisão comercial. */}
      {produtos.length >= 2 && despesaIndireta > 0 && (
        <div style={card}>
          <div style={{ fontSize: 16, fontWeight: 700, letterSpacing: '0.10em',
            textTransform: 'uppercase', color: G.laranja, marginBottom: 8 }}>
            Entrou Produto Novo · o que fazer com o alívio
            <span style={{ fontSize: 12, fontWeight: 400, letterSpacing: 0,
              textTransform: 'none', color: G.txt2 }}>
              {' '}· quatro caminhos, calculados pela <MarcaAIdvisor variant="default" /></span>
          </div>
          <div style={{ padding: '10px 14px', background: G.verdeLight,
            borderLeft: `3px solid ${G.verde}`, borderRadius: 0, marginBottom: 12,
            fontSize: 12, lineHeight: 1.45, color: G.txt1 }}>
            Com a despesa indireta já coberta pelos produtos existentes, a contribuição
            do novo vira <strong>escolha</strong>. Marque qual produto está entrando e
            compare os caminhos — a <MarcaAIdvisor /> recomenda aliviar todos.
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 10,
            flexWrap: 'wrap', marginBottom: 14 }}>
            <div style={{ ...rotulo, marginBottom: 0 }}>Produto que está entrando:</div>
            {produtos.map((p, n) => (
              <button key={p.id}
                onClick={() => setProdutoNovo(produtoNovo === p.id ? null : p.id)}
                style={{ padding: '6px 14px', fontSize: 11.5, fontWeight: 700,
                  background: produtoNovo === p.id ? G.laranja : '#FFF',
                  color: produtoNovo === p.id ? '#FFF' : G.txt1,
                  border: `1px solid ${produtoNovo === p.id ? G.laranja : G.borda}`,
                  borderRadius: 0, cursor: 'pointer',
                  fontFamily: "'Outfit', sans-serif" }}>
                {n + 1}. {p.nome}
              </button>
            ))}
          </div>

          {!produtoNovo ? (
            <div style={{ fontSize: 12, color: G.txt2, fontStyle: 'italic',
              padding: '12px 14px', background: G.fundo, borderRadius: 0 }}>
              Marque acima qual produto está entrando para a <MarcaAIdvisor /> montar
              os cenários.
            </div>
          ) : !cenarios ? (
            <div style={{ fontSize: 12, color: G.txt2, fontStyle: 'italic',
              padding: '12px 14px', background: G.fundo, borderRadius: 0 }}>
              Calculando os cenários…
            </div>
          ) : (cenarios.erros || []).length > 0 ? (
            cenarios.erros.map((e, i) => (
              <div key={i} style={{ fontSize: 12, lineHeight: 1.45, color: G.txt1,
                padding: '10px 14px', background: '#FEF2F2',
                borderLeft: `3px solid ${G.vermelho}`, borderRadius: 0 }}>{e}</div>
            ))
          ) : (
            <div style={{ display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(270px, 1fr))', gap: 12 }}>
              {(cenarios.cenarios || []).map(c => (
                <div key={c.chave} style={{
                  border: `${c.padrao ? 2 : 1}px solid ${c.padrao ? G.verde : G.borda}`,
                  borderRadius: 0, padding: '14px 16px',
                  background: c.padrao ? G.verdeLight : '#FFF' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8,
                    marginBottom: 6 }}>
                    <div style={{ fontSize: 13, fontWeight: 900,
                      color: c.padrao ? G.verde : G.txt1 }}>{c.nome}</div>
                    {c.padrao && (
                      <span style={{ fontSize: 9, fontWeight: 800, color: '#FFF',
                        background: G.verde, borderRadius: 0, padding: '2px 8px',
                        letterSpacing: '0.06em', textTransform: 'uppercase' }}>
                        Recomendado</span>
                    )}
                  </div>
                  <div style={{ fontSize: 11.5, lineHeight: 1.45, color: G.txt2,
                    marginBottom: 10 }}>{c.descricao}</div>

                  {(c.resultado?.itens || []).map((i, n) => {
                    const al = c.alivio_mensal?.[i.id];
                    const ehNovo = i.id === cenarios.id_novo;
                    return (
                      <div key={i.id} style={{ display: 'grid',
                        gridTemplateColumns: '1fr 52px 74px', gap: 6,
                        alignItems: 'center', padding: '4px 0',
                        borderTop: `1px solid ${G.borda}` }}>
                        <div style={{ fontSize: 11.5,
                          color: ehNovo ? G.laranja : G.txt1,
                          fontWeight: ehNovo ? 700 : 400 }}>
                          <span style={{ color: G.txt2, fontWeight: 600 }}>{n + 1}.</span>{' '}
                          {i.nome}{ehNovo ? ' ·  novo' : ''}
                        </div>
                        <div style={{ fontSize: 11, textAlign: 'right', color: G.txt2,
                          fontFamily: "'IBM Plex Mono', monospace" }}>
                          {(i.fatia * 100).toLocaleString('pt-BR',
                            { maximumFractionDigits: 0 })}%
                        </div>
                        <div style={{ fontSize: 11.5, textAlign: 'right', fontWeight: 700,
                          color: i.inviavel ? G.vermelho : G.txt1,
                          fontFamily: "'IBM Plex Mono', monospace" }}>
                          {i.inviavel ? 'não fecha'
                            : `${i.quantidade.toLocaleString('pt-BR',
                                { maximumFractionDigits: 0 })} un`}
                        </div>
                      </div>
                    );
                  })}

                  <div style={{ borderTop: `2px solid ${G.borda}`, marginTop: 8,
                    paddingTop: 8, display: 'flex', flexDirection: 'column', gap: 3 }}>
                    {[['Alívio total', `R$ ${fmt(c.alivio_total)}`,
                       c.alivio_total > 0 ? G.verde : G.txt2],
                      ['Excedente', `R$ ${fmt(c.excedente)}`,
                       c.excedente > 0 ? G.verde : G.txt2],
                      ['Receita necessária', `R$ ${fmt(c.resultado?.receita_mensal)}`, G.txt1]]
                      .map(([rot, val, cor]) => (
                      <div key={rot} style={{ display: 'flex',
                        justifyContent: 'space-between', fontSize: 11.5 }}>
                        <span style={{ color: G.txt2 }}>{rot}</span>
                        <span style={{ fontWeight: 800, color: cor,
                          fontFamily: "'IBM Plex Mono', monospace" }}>{val}</span>
                      </div>
                    ))}
                  </div>

                  {c.alvo_transferencia && (
                    <div style={{ fontSize: 10.5, color: G.txt2, marginTop: 8,
                      lineHeight: 1.4 }}>
                      Alívio dirigido ao produto com a menor margem de contribuição —
                      é onde ele rende mais.
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};


const TelaPlanilhaInsumos = ({ empresa }) => {
  const [insumos, setInsumos] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState(null);
  const [novo, setNovo] = useState(null);   // rascunho do insumo em criação
  const [removendoExemplo, setRemovendoExemplo] = useState(false);

  const carregar = React.useCallback(async () => {
    setCarregando(true);
    try {
      const r = await fetch(`${API}/insumos/${empresa.id}`);
      if (!r.ok) throw new Error('Não foi possível carregar a planilha.');
      const d = await r.json();
      setInsumos(d.insumos || []);
      setErro(null);
    } catch (e) {
      setErro(e.message);
    } finally {
      setCarregando(false);
    }
  }, [empresa.id]);

  useEffect(() => { carregar(); }, [carregar]);

  // Apaga o exemplo didático inteiro — insumos, produto e a composição dele.
  // Confirma antes: é remoção definitiva, ao contrário do insumo comum, que
  // apenas fica inativo.
  const removerExemplo = async () => {
    if (!window.confirm('Remover o exemplo da fábrica de bolos?\n\n'
      + 'Saem os 7 insumos e o produto de demonstração. A remoção é '
      + 'definitiva — a planilha fica pronta para o cadastro real.')) return;
    setRemovendoExemplo(true);
    try {
      const r = await fetch(`${API}/insumos/${empresa.id}/exemplo`, { method: 'DELETE' });
      if (!r.ok) throw new Error('Não foi possível remover o exemplo.');
      await carregar();
      setErro(null);
    } catch (e) { setErro(e.message); }
    finally { setRemovendoExemplo(false); }
  };

  const salvarNovo = async () => {
    if (!novo?.nome?.trim()) { setErro('Informe o nome do insumo.'); return; }
    if (!novo?.unidade) { setErro('Informe a unidade — sem ela o índice e o preço não se interpretam.'); return; }
    try {
      const r = await fetch(`${API}/insumos/${empresa.id}`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(novo),
      });
      const d = await r.json();
      if (!r.ok) { setErro(d.detail || 'Não foi possível criar o insumo.'); return; }
      setNovo(null); setErro(null); carregar();
    } catch (e) { setErro('Falha ao salvar o insumo.'); }
  };

  const editar = async (id, campo, valor) => {
    setInsumos(prev => prev.map(i => i.id === id ? { ...i, [campo]: valor } : i));
    try {
      const r = await fetch(`${API}/insumos/${empresa.id}/${id}`, {
        method: 'PATCH', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ [campo]: valor }),
      });
      if (!r.ok) {
        const d = await r.json();
        setErro(d.detail || 'Não foi possível salvar a alteração.');
        carregar();                       // desfaz o otimismo se o banco recusou
      } else { setErro(null); }
    } catch (e) { setErro('Falha ao salvar.'); carregar(); }
  };

  const desativar = async (id, nome) => {
    if (!window.confirm(`Remover "${nome}" da planilha?\n\nEle deixa de aparecer para novas composições. As composições que já o usam continuam com o custo calculado.`)) return;
    try {
      await fetch(`${API}/insumos/${empresa.id}/${id}`, { method: 'DELETE' });
      carregar();
    } catch (e) { setErro('Falha ao remover.'); }
  };

  // Mesmo card das demais telas de dados de entrada.
  const card = { background: '#FFF', border: `1px solid ${G.borda}`,
    borderRadius: 0, padding: '16px 18px', marginBottom: 16 };

  const ativos = insumos.filter(i => i.ativo);
  const GRID = '92px 92px minmax(160px, 1fr) 78px 132px 128px 44px';

  const Cabecalho = () => (
    <div style={{ display: 'grid', gridTemplateColumns: GRID, gap: 10,
      padding: '0 0 6px', borderBottom: `2px solid ${G.borda}` }}>
      {[['Código', 'left'], ['Cód. empresa', 'left'], ['Insumo', 'left'],
        ['Unid.', 'center'], ['Índice de consumo', 'right'],
        ['Preço unitário', 'right'], ['', 'center']].map(([h, al], i) => (
        <div key={i} style={{ fontSize: 10, fontWeight: 800, color: G.txt2,
          letterSpacing: '0.08em', textTransform: 'uppercase', textAlign: al }}>{h}</div>
      ))}
    </div>
  );

  const Secao = ({ tipo, titulo, cor, fundo }) => {
    const lista = ativos.filter(i => i.tipo === tipo);
    return (
      <div style={{ marginBottom: 18 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
          <div style={{ fontSize: 12, fontWeight: 900, color: cor, letterSpacing: '0.06em',
            textTransform: 'uppercase', background: fundo, border: `1px solid ${cor}`,
            borderRadius: 0, padding: '5px 12px' }}>{titulo}</div>
          <div style={{ fontSize: 11.5, color: G.txt2 }}>
            {lista.length} {lista.length === 1 ? 'insumo' : 'insumos'}
          </div>
          <button onClick={() => setNovo({ tipo, nome: '', unidade: '', unidade_produto: '',
            indice_consumo: '', preco_unitario: '', codigo_empresa: '' })}
            style={{ marginLeft: 'auto', padding: '6px 14px', fontSize: 11.5, fontWeight: 700,
              background: '#FFF', color: cor, border: `1px dashed ${cor}`, borderRadius: 0,
              cursor: 'pointer', fontFamily: "'Outfit', sans-serif" }}>
            + Adicionar {titulo.toLowerCase()}
          </button>
        </div>
        {lista.length === 0 && !(novo?.tipo === tipo) && (
          <div style={{ fontSize: 12, color: G.txt2, fontStyle: 'italic',
            padding: '10px 14px', background: G.fundo, borderRadius: 0 }}>
            Nenhum insumo de {titulo.toLowerCase()} cadastrado.
          </div>
        )}
        {lista.length > 0 && <Cabecalho />}
        {lista.map((i, n) => (
          <div key={i.id} style={{ display: 'grid', gridTemplateColumns: GRID, gap: 10,
            alignItems: 'center', padding: '7px 0', borderBottom: `1px solid ${G.borda}` }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: cor,
              fontFamily: "'IBM Plex Mono', monospace", display: 'flex',
              alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
              {/* Número de ordem: com dezenas de linhas, o código sozinho não
                  diz em que ponto da lista se está nem quantas já passaram. */}
              <span style={{ color: G.txt2, fontWeight: 600 }}>{n + 1}.</span>
              {i.codigo}
              {i.exemplo && <SeloExemplo/>}
            </div>
            <input value={i.codigo_empresa || ''} placeholder="—"
              onChange={e => setInsumos(prev => prev.map(x => x.id === i.id
                ? { ...x, codigo_empresa: e.target.value } : x))}
              onBlur={e => editar(i.id, 'codigo_empresa', e.target.value)}
              style={{ fontSize: 12, padding: '5px 7px', border: `1px solid ${G.borda}`,
                borderRadius: 0, width: '100%', boxSizing: 'border-box',
                fontFamily: "'IBM Plex Mono', monospace" }}/>
            <input value={i.nome || ''}
              onChange={e => setInsumos(prev => prev.map(x => x.id === i.id
                ? { ...x, nome: e.target.value } : x))}
              onBlur={e => editar(i.id, 'nome', e.target.value)}
              style={{ fontSize: 12.5, padding: '5px 8px', border: `1px solid ${G.borda}`,
                borderRadius: 0, width: '100%', boxSizing: 'border-box' }}/>
            <select value={i.unidade || ''} onChange={e => editar(i.id, 'unidade', e.target.value)}
              style={{ fontSize: 12, padding: '5px 4px', border: `1px solid ${G.borda}`,
                borderRadius: 0, width: '100%', boxSizing: 'border-box', textAlign: 'center' }}>
              {UNIDADES_INSUMO.map(u => <option key={u} value={u}>{u}</option>)}
            </select>
            {/* Índice e preço usam o CampoNumericoBP60M — padrão obrigatório do
                app para entrada numérica (teclado próprio). Input de texto cru
                reformatava o valor a cada tecla e corrompia o número. */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <CampoNumericoBP60M
                valor={Number(i.indice_consumo) || 0}
                onChange={(v) => editar(i.id, 'indice_consumo', v)}
                label={`Índice de consumo · ${i.nome}`}
                permitirDecimal={true} min={0} formato="decimal"
              />
              <span style={{ fontSize: 10.5, color: G.txt2, whiteSpace: 'nowrap' }}>
                {i.unidade}/{i.unidade_produto || '?'}
              </span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <CampoNumericoBP60M
                valor={Number(i.preco_unitario) || 0}
                onChange={(v) => editar(i.id, 'preco_unitario', v)}
                label={`Preço unitário · ${i.nome}`}
                permitirDecimal={true} min={0} formato="brl"
              />
              <span style={{ fontSize: 10.5, color: G.txt2 }}>/{i.unidade}</span>
            </div>
            <BotaoRemover onClick={() => desativar(i.id, i.nome)}
              titulo={`Remover ${i.nome} da planilha`}/>
          </div>
        ))}
        {novo?.tipo === tipo && (
          <div style={{ marginTop: 10, padding: '12px 14px', background: fundo,
            border: `1px solid ${cor}`, borderRadius: 0 }}>
            <div style={{ fontSize: 11, fontWeight: 800, color: cor, marginBottom: 8,
              letterSpacing: '0.06em', textTransform: 'uppercase' }}>
              Novo insumo · o código é emitido pela ferramenta
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 120px 90px 130px 130px',
              gap: 10, alignItems: 'end' }}>
              {[['nome', 'Nome do insumo', 'text'],
                ['codigo_empresa', 'Cód. da empresa', 'text']].map(([campo, rot]) => (
                <div key={campo}>
                  <div style={{ fontSize: 10, fontWeight: 700, color: G.txt2,
                    textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 3 }}>{rot}</div>
                  <input value={novo[campo] || ''} autoFocus={campo === 'nome'}
                    onChange={e => setNovo({ ...novo, [campo]: e.target.value })}
                    style={{ fontSize: 12.5, padding: '7px 8px', border: `1px solid ${G.borda}`,
                      borderRadius: 0, width: '100%', boxSizing: 'border-box' }}/>
                </div>
              ))}
              <div>
                <div style={{ fontSize: 10, fontWeight: 700, color: G.txt2,
                  textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 3 }}>Unidade</div>
                <select value={novo.unidade || ''}
                  onChange={e => setNovo({ ...novo, unidade: e.target.value })}
                  style={{ fontSize: 12.5, padding: '7px 4px', border: `1px solid ${G.borda}`,
                    borderRadius: 0, width: '100%', boxSizing: 'border-box' }}>
                  <option value="">—</option>
                  {UNIDADES_INSUMO.map(u => <option key={u} value={u}>{u}</option>)}
                </select>
              </div>
              <div>
                <div style={{ fontSize: 10, fontWeight: 700, color: G.txt2,
                  textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 3 }}>
                  Índice · por unidade do produto</div>
                <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                  <CampoNumericoBP60M
                    valor={Number(novo.indice_consumo) || 0}
                    onChange={(v) => setNovo({ ...novo, indice_consumo: v })}
                    label="Índice de consumo do novo insumo"
                    permitirDecimal={true} min={0} formato="decimal"
                  />
                  <select value={novo.unidade_produto || ''}
                    onChange={e => setNovo({ ...novo, unidade_produto: e.target.value })}
                    style={{ fontSize: 11.5, padding: '7px 2px', border: `1px solid ${G.borda}`,
                      borderRadius: 0, boxSizing: 'border-box' }}>
                    <option value="">/?</option>
                    {UNIDADES_INSUMO.map(u => <option key={u} value={u}>/{u}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <div style={{ fontSize: 10, fontWeight: 700, color: G.txt2,
                  textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 3 }}>Preço unitário</div>
                <CampoNumericoBP60M
                  valor={Number(novo.preco_unitario) || 0}
                  onChange={(v) => setNovo({ ...novo, preco_unitario: v })}
                  label="Preço unitário do novo insumo"
                  permitirDecimal={true} min={0} formato="brl"
                />
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
              <button onClick={salvarNovo}
                style={{ padding: '8px 18px', fontSize: 12, fontWeight: 700, background: cor,
                  color: '#FFF', border: 'none', borderRadius: 0, cursor: 'pointer',
                  fontFamily: "'Outfit', sans-serif" }}>Salvar insumo</button>
              <button onClick={() => { setNovo(null); setErro(null); }}
                style={{ padding: '8px 18px', fontSize: 12, fontWeight: 700, background: '#FFF',
                  color: G.txt2, border: `1px solid ${G.borda}`, borderRadius: 0,
                  cursor: 'pointer', fontFamily: "'Outfit', sans-serif" }}>Cancelar</button>
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div style={{ padding: '24px 28px', position: 'relative', overflow: 'hidden' }}>
      <CarimboAIdvisor />
      <div style={{ marginBottom: 20 }}>
        <div style={{ position: 'relative', display: 'inline-block' }}>
          <h2 style={{ margin: 0, fontSize: 20, fontWeight: 800, color: G.txt1,
            position: 'relative', zIndex: 1 }}>
            Planilha de Insumos
          </h2>
          <MarcaDaguaTitulo>
            <path d="M3 7h18M3 12h18M3 17h18"/><circle cx="7" cy="7" r="1"/>
            <circle cx="7" cy="12" r="1"/><circle cx="7" cy="17" r="1"/>
          </MarcaDaguaTitulo>
        </div>
        <p style={{ ...TIPO.subtitulo, margin: '4px 0 0' }}>
          cadastro que alimenta a formação de preço
        </p>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 18 }}>
        <div style={{ padding: '12px 16px', background: G.verdeLight,
          borderLeft: `4px solid ${G.verde}`, borderRadius: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 800, color: G.verde,
            letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 6 }}>
            O que é a Planilha de Insumos
          </div>
          <div style={{ fontSize: 12, lineHeight: 1.45, color: G.txt1 }}>
            O cadastro de tudo que entra nos seus produtos. Cada insumo guarda o
            <strong> índice de consumo</strong> e o <strong>preço unitário</strong> —
            meio quilo de farinha por quilo de bolo, a R$ 4,00 o quilo. Cadastre uma vez;
            todos os produtos passam a usar.
          </div>
        </div>
        <div style={{ padding: '12px 16px', background: G.laranjaLight,
          borderLeft: `4px solid ${G.laranja}`, borderRadius: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 800, color: G.laranja,
            letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 6 }}>
            O que você faz aqui
          </div>
          <div style={{ fontSize: 12, lineHeight: 1.45, color: G.txt1 }}>
            Cadastre os insumos de matéria-prima e de mão de obra, com unidade,
            índice e preço. O código é emitido pela ferramenta; se a empresa já tem
            o dela, informe ao lado. A <MarcaAIdvisor /> mantém os preços atualizados.
          </div>
        </div>
      </div>

      <FaixaExemplo quantos={ativos.filter(i => i.exemplo).length}
        onRemover={removerExemplo} removendo={removendoExemplo}/>

      {erro && (
        <div style={{ fontSize: 12.5, lineHeight: 1.45, color: G.txt1, marginBottom: 14,
          padding: '10px 14px', background: '#FEF2F2',
          borderLeft: `3px solid ${G.vermelho}`, borderRadius: 0 }}>{erro}</div>
      )}

      <div style={card}>
        {carregando ? (
          <div style={{ fontSize: 12.5, color: G.txt2, fontStyle: 'italic' }}>
            Carregando a planilha…
          </div>
        ) : (
          <>
            <Secao tipo="materia_prima" titulo="Matéria-prima" cor={G.verde} fundo={G.verdeLight}/>
            <Secao tipo="mao_de_obra"  titulo="Mão de obra"   cor={G.laranja} fundo={G.laranjaLight}/>
            {ativos.length > 0 && (
              <div style={{ fontSize: 11.5, color: G.txt2, borderTop: `1px solid ${G.borda}`,
                paddingTop: 10 }}>
                {ativos.length} {ativos.length === 1 ? 'insumo ativo' : 'insumos ativos'} ·
                o índice é o consumo padrão e continua editável em cada composição,
                porque o mesmo insumo rende diferente em produtos diferentes.
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};


// ═══════════════════════════════════════════════════════════════════════
// <TelaCustosDiretos /> — landing page com DUAS páginas, no modelo da LP de
// Dados de Entrada (hero verde-escuro + cards + rodapé de navegação).
//
// A ordem é a de trabalho, não a de menção: a Planilha de Insumos vem primeiro
// porque ALIMENTA a formação de preço — sem insumo cadastrado não há o que
// compor. Por isso o "Começar" leva a ela.
// ═══════════════════════════════════════════════════════════════════════
const TelaCustosDiretos = ({ empresa, periodoInicio, periodoFim, indicadores }) => {
  const [sub, setSub] = useState('landing');
  const VERDE_DEEP = '#134721';
  const ORDEM = ['insumos', 'preco'];

  if (sub !== 'landing') {
    const idx = ORDEM.indexOf(sub);
    const anterior = idx > 0 ? ORDEM[idx - 1] : null;
    const proximo  = idx < ORDEM.length - 1 ? ORDEM[idx + 1] : null;
    // Tudo lê do banco: produtos, composição, consolidado e a análise de volume.
    const conteudo = sub === 'insumos'
      ? <TelaPlanilhaInsumos empresa={empresa} />
      : <TelaFormacaoPreco empresa={empresa}
          periodoInicio={periodoInicio} periodoFim={periodoFim} />;
    return (
      <div style={{ height: 'calc(100vh - 103px)', display: 'flex', flexDirection: 'column' }}>
        <div style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>{conteudo}</div>
        <RodapeNavFonte
          onAnterior={anterior ? () => setSub(anterior) : undefined}
          onVoltar={() => setSub('landing')}
          onProximo={proximo ? () => setSub(proximo) : undefined}
          temAnterior={!!anterior} temProximo={!!proximo}
          rotuloVoltar="Custos Diretos"
        />
      </div>
    );
  }

  const ctaPrimario = {
    background: G.laranja, color: '#FFF', padding: '14px 32px',
    fontSize: 13, fontWeight: 800, letterSpacing: '0.10em',
    textTransform: 'uppercase', borderRadius: 0, border: 'none',
    cursor: 'pointer', fontFamily: 'inherit',
    boxShadow: '0 4px 16px rgba(244,124,60,0.40)',
    display: 'inline-flex', alignItems: 'center', gap: 8,
  };

  const paginas = [
    { id: 'insumos', eyebrow: 'Etapa 1', titulo: 'Planilha de Insumos', cor: G.verde,
      desc: 'O cadastro do que entra nos seus produtos — matéria-prima e mão de obra, cada um com índice de consumo e preço unitário. Cadastre uma vez; todos os produtos usam.',
      icone: (<><path d="M3 7h18M3 12h18M3 17h18"/><circle cx="7" cy="7" r="1"/><circle cx="7" cy="12" r="1"/><circle cx="7" cy="17" r="1"/></>) },
    { id: 'preco', eyebrow: 'Etapa 2', titulo: 'Formação de Preço de Venda', cor: G.laranja,
      desc: 'A composição de cada produto: insere o insumo, informa o tamanho, e a ferramenta calcula o custo. Daí saem a Margem de Contribuição e o Rateio de Despesas Fixas.',
      icone: (<><polygon points="12 2 2 7 12 12 22 7 12 2"/><polyline points="2 17 12 22 22 17"/><polyline points="2 12 12 17 22 12"/></>) },
  ];

  return (
    <div style={{ maxWidth: 1100, margin: '0 auto', padding: '20px 28px 24px', position: 'relative' }}>
      <section style={{ background: VERDE_DEEP, color: '#FFF', borderRadius: 0,
        padding: '28px 36px', position: 'relative', overflow: 'hidden', marginBottom: 14 }}>
        <svg width="280" height="280" viewBox="0 0 24 24" fill="none" stroke="#FFF"
          strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"
          style={{ position: 'absolute', right: -24, bottom: -70, opacity: 0.05,
            pointerEvents: 'none', userSelect: 'none' }}>
          <polygon points="12 2 2 7 12 12 22 7 12 2"/>
          <polyline points="2 17 12 22 22 17"/><polyline points="2 12 12 17 22 12"/>
        </svg>
        <div style={{ fontSize: 10, fontWeight: 700, color: 'rgba(255,255,255,0.65)',
          letterSpacing: '0.20em', textTransform: 'uppercase', marginBottom: 10 }}>
          Custos Diretos · o que nasce com a venda
        </div>
        <h1 style={{ fontSize: 32, fontWeight: 900, lineHeight: 1.1, letterSpacing: '-0.02em',
          marginBottom: 12, maxWidth: 720, fontFamily: "'Outfit', sans-serif" }}>
          Preço se calcula, não se <span style={{ color: G.laranja }}>adivinha</span>.
        </h1>
        <p style={{ fontSize: 14, lineHeight: 1.5, color: 'rgba(255,255,255,0.92)',
          maxWidth: 720, margin: 0 }}>
          Cadastre os insumos com índice de consumo e preço, monte a composição de cada
          produto, e a <span style={{ display: 'inline-block', background: '#FFF',
            padding: '2px 9px', borderRadius: 0, verticalAlign: 'baseline',
            lineHeight: 1.1 }}><MarcaAIdvisor variant="default" /></span> devolve o custo
          direto, a Margem de Contribuição e quanto cada produto precisa vender para pagar
          a despesa indireta.
        </p>
      </section>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 16, marginBottom: 14 }}>
        {paginas.map(p => (
          <CardFonteEntrada key={p.id} fonte={p} onClick={() => setSub(p.id)} />
        ))}
      </div>

      <section style={{ display: 'flex', justifyContent: 'space-between',
        alignItems: 'center', flexWrap: 'wrap', gap: 14, padding: '4px 6px' }}>
        <div style={{ fontSize: 12, color: G.txt2, lineHeight: 1.5, maxWidth: 560 }}>
          A planilha vem antes: sem insumo cadastrado não há o que compor.
        </div>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginLeft: 'auto' }}>
          <button onClick={() => setSub('insumos')} style={ctaPrimario}>Começar →</button>
        </div>
      </section>
    </div>
  );
};


// `secao` divide a tela em DUAS ABAS do menu — 'indiretas' e 'diretos'. O
// componente continua único de propósito: o Rateio de Despesas Fixas, que vai
// viver em Custos Diretos, precisa ler a despesa indireta total. Separar em dois
// componentes obrigaria a duplicar a leitura do razão e o estado das composições.
const TelaComposicoes = ({ empresa, periodoInicio, periodoFim, indicadores,
                           secao = 'indiretas' }) => {
  const ehIndiretas = secao === 'indiretas';
  // Hooks SEMPRE antes de qualquer return condicional (regra do React).
  const [opexContabil, setOpexContabil] = useState(null);
  // Mesma lente de rateio da tela irmã (10/09) — análise, nunca gravação.
  const [metodoFatia, setMetodoFatia] = useState('ponderada');
  // Grupos recolhidos do quadro de Despesas Indiretas Fixas. Vazio = todos
  // abertos, que é como a tela nasce.
  const [gruposFechados, setGruposFechados] = useState([]);
  const alternarGrupo = (g) => setGruposFechados(prev =>
    prev.includes(g) ? prev.filter(x => x !== g) : [...prev, g]);
  // O período vem do seletor único do cabeçalho (estado de AppPrivado), o mesmo
  // que Dashboard, DRE e Resumo Mensal usam — mudar ali muda a ferramenta toda.
  const dataInicio = periodoInicio?.inicio || null;
  const dataFim    = periodoFim?.fim || null;
  useEffect(() => {
    let vivo = true;
    (async () => {
      try {
        const url = new URL(`${API}/indicadores/custos-fixos/${empresa.id}`);
        if (dataInicio && dataFim) {
          url.searchParams.set('data_inicio', dataInicio);
          url.searchParams.set('data_fim', dataFim);
        }
        const r = await fetch(url);
        if (!r.ok) { if (vivo) setOpexContabil(null); return; }
        const d = await r.json();
        if (vivo) {
          setOpexContabil(d.opex?.grupos?.length ? d.opex : null);
          // A MESMA lista que alimenta o card do Dashboard. `opex.grupos` traz
          // agregados ("Pessoal administrativo", 60,5%) e a classificação traz
          // a CONTA ("Salários", 15,8%) — dois números com o mesmo nome em duas
          // telas era o risco aqui ([[a-tela-nao-pode-mentir-sobre-os-dados]]).
          setFixoContas(Array.isArray(d.classificacao?.fixo) ? d.classificacao.fixo : []);
        }
      } catch (_) { if (vivo) { setOpexContabil(null); setFixoContas([]); } }
    })();
    return () => { vivo = false; };
  }, [empresa.id, dataInicio, dataFim]);



  const setor = empresa?.setor;
  const [comp, setComp] = useState(null);
  // Ranking das maiores despesas (14/09): abre com 5 e cresce sob demanda.
  const [verTodasMaiores, setVerTodasMaiores] = useState(false);
  const [fixoContas, setFixoContas] = useState([]);
  const [precoAberto, setPrecoAberto] = useState(null);
  // Resultado da Análise Custo-Volume-Lucro, vindo do backend.
  const [cvl, setCvl] = useState(null);

  // Carrega estado próprio por empresa · na 1ª vez semeia com a sugestão da IA (T4).
  useEffect(() => {
    if (!empresa?.id) return;
    try {
      const raw = localStorage.getItem(`composicoes:${empresa.id}`);
      if (raw) { setComp(JSON.parse(raw)); return; }
    } catch (e) { /* json inválido · re-semeia abaixo */ }
    const seed = (RASCUNHO_TAREFA_4[empresa.id]?.portfolio || []).map(p => ({ ...p }));
    setComp({ portfolio: seed, opex: {} });
  }, [empresa?.id]);

  // Persiste a cada mudança (estado independente do BP60M).
  useEffect(() => {
    if (!empresa?.id || !comp) return;
    try { localStorage.setItem(`composicoes:${empresa.id}`, JSON.stringify(comp)); } catch (e) { /* quota */ }
  }, [empresa?.id, comp]);

  const HEADER_BLOCO = { fontSize: 16, fontWeight: 700, letterSpacing: '0.10em',
    textTransform: 'uppercase', color: G.laranja, marginBottom: 8 };
  const HEADER_AUX = { fontSize: 12, fontWeight: 400, color: G.txt2,
    textTransform: 'none', letterSpacing: 0, marginLeft: 10 };
  const card = { background: '#FFF', border: `1px solid ${G.borda}`,
    borderRadius: 0, padding: '16px 18px', marginBottom: 16 };
  const btnAdd = { marginTop: 10, padding: '8px 14px', fontSize: 12, fontWeight: 700,
    background: '#FFF', color: G.laranja, border: `1px dashed ${G.laranja}`,
    borderRadius: 0, cursor: 'pointer', fontFamily: "'Outfit', sans-serif" };
  const fmt = (n) => (Number(n) || 0).toLocaleString('pt-BR',
    { style: 'currency', currency: 'BRL',
      minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const fmt2 = (n) => (Number(n) || 0).toLocaleString('pt-BR',
    { style: 'currency', currency: 'BRL', maximumFractionDigits: 2 });
  // Célula de valor no padrão do app (CampoNumericoBP60M): o "R$" é o prefixo
  // da caixa, então o número vai SEM símbolo — senão lê-se "R$ R$ 16.681,67".
  const fmtNum = (n) => (Number(n) || 0).toLocaleString('pt-BR',
    { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  // Participação de uma parte no todo. Uma casa decimal: com zero casas, as
  // rubricas pequenas (Material de escritório, 0,5%) apareceriam todas como
  // "0%". Base zero ou inválida devolve "—", nunca 0% nem NaN — a resposta
  // honesta é "não dá para calcular", não "não representa nada".
  const pctDe = (parte, total) => {
    const t = Number(total);
    if (!Number.isFinite(t) || t === 0) return '—';
    const p = (Number(parte) || 0) / t * 100;
    if (!Number.isFinite(p)) return '—';
    return `${p.toLocaleString('pt-BR',
      { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%`;
  };

  // O `return null` de carregamento fica LÁ EMBAIXO, depois de todos os hooks:
  // um `useEffect` depois de return condicional quebra a regra dos hooks do
  // React. Por isso as derivações abaixo usam `comp?.`.
  const portfolio = comp?.portfolio || [];

  // Helpers locais delegando aos helpers de arquivo (fonte única com T4/T13).
  const tipoDoProduto = (p) => tipoProdutoSU(p, setor);
  const insumoValor   = (p, insId) => insumoValorSU(p, insId, setor);
  const custoUnit     = (p) => custoDiretoUnitSU(p, setor);
  const precoVenda    = (p) => precoVendaSU(p, setor);
  const margemContrib = (p) => precoVenda(p) - custoUnit(p);
  const editarComposicao = (idx, campo, valor) =>
    setComp(prev => ({ ...prev, portfolio: prev.portfolio.map((p, i) =>
      i === idx ? { ...p, [campo]: valor } : p) }));
  const editarInsumoComposicao = (idx, insId, valor) =>
    setComp(prev => ({ ...prev, portfolio: prev.portfolio.map((p, i) =>
      i === idx ? { ...p, insumos: { ...(p.insumos || {}), [insId]: valor } } : p) }));
  const adicionarProduto = () =>
    setComp(prev => ({ ...prev, portfolio: [...(prev.portfolio || []),
      { id_catalogo: null, nome: '', descricao: '', mix: 0 }] }));

  // ── Quantidade mensal por produto ────────────────────────────────────────
  // A Análise Custo-Volume-Lucro precisa do VOLUME: é ele que a interpolação
  // procura e é sobre ele que o rateio da despesa fixa se divide. A tela tinha
  // preço, custo e mix, mas não quantidade.
  // Estimativa da RGAI: a receita do período, trazida ao mês, repartida pelo
  // mix e dividida pelo preço. O consultor confirma ou ajusta — é dado que só
  // a empresa tem (nem extrato nem balancete informam unidades vendidas).
  const mesesDoPeriodo = (() => {
    if (!periodoInicio?.inicio || !periodoFim?.fim) return 12;
    const [a1, m1] = periodoInicio.inicio.slice(0, 7).split('-').map(Number);
    const [a2, m2] = periodoFim.fim.slice(0, 7).split('-').map(Number);
    const n = (a2 - a1) * 12 + (m2 - m1) + 1;
    return Number.isFinite(n) && n > 0 ? n : 12;   // período invertido não zera a conta
  })();
  // Líquida de devoluções: mercadoria devolvida não foi vendida, e esta receita
  // vira ESTIMATIVA DE QUANTIDADE logo abaixo. Antes de 01/08/2026 a dedução já
  // vinha embutida em `receita_bruta`; agora vem em linha própria.
  const receitaMensal = Math.max(0,
    (Number(indicadores?.receita_bruta) || 0) - (Number(indicadores?.deducoes_receita) || 0)
  ) / mesesDoPeriodo;
  const quantidadeEstimada = (p) => {
    const preco = precoVenda(p), mix = Number(p.mix) || 0;
    if (!(preco > 0) || !(mix > 0) || !(receitaMensal > 0)) return 0;
    return (receitaMensal * (mix / 100)) / preco;
  };
  // Valor digitado prevalece sobre a estimativa; `null`/vazio volta a estimar.
  const quantidadeProduto = (p) =>
    (p.quantidade === null || p.quantidade === undefined || p.quantidade === '')
      ? quantidadeEstimada(p)
      : Math.max(0, Number(p.quantidade) || 0);

  // ── Rateio de Despesas Fixas ─────────────────────────────────────────────
  // Cada produto assume uma FATIA da despesa indireta. Enquanto ninguém editou,
  // a fatia espelha o mix; sem mix, é igual para todos (4 produtos → 25% cada,
  // entra o quinto → 20% cada). A partir da primeira edição, as fatias que o
  // consultor fixou são PRESERVADAS e só o restante se redistribui entre as
  // livres — o trabalho dele não se perde quando entra produto novo.
  const fatiaEditada = (p) =>
    (p.fatiaFixo === null || p.fatiaFixo === undefined || p.fatiaFixo === '')
      ? null : Math.max(0, Number(p.fatiaFixo) || 0) / 100;
  const fatias = (() => {
    const n = portfolio.length;
    if (!n) return [];
    const somaMix = portfolio.reduce((s, p) => s + (Number(p.mix) || 0), 0);
    const base = portfolio.map(p => somaMix > 0 ? (Number(p.mix) || 0) / somaMix : 1 / n);
    const fixadas = portfolio.map(fatiaEditada);
    const somaFixadas = fixadas.reduce((s, v) => s + (v ?? 0), 0);
    const restante = Math.max(0, 1 - somaFixadas);
    const baseLivres = portfolio.reduce(
      (s, p, i) => s + (fixadas[i] === null ? base[i] : 0), 0);
    return portfolio.map((p, i) => {
      if (fixadas[i] !== null) return fixadas[i];
      if (baseLivres <= 0) return 0;             // tudo já foi fixado
      return restante * (base[i] / baseLivres);
    });
  })();
  const somaFatias = fatias.reduce((s, v) => s + v, 0);
  // Fatias fixadas acima de 100% deixam parte do fixo sem dono — e o resultado
  // sairia otimista em silêncio. A tela precisa dizer.
  const fatiasEstouradas = portfolio.reduce(
    (s, p) => s + (fatiaEditada(p) ?? 0), 0) > 1.0001;

  // Seção 2 · consolidação (% ponderado pelo mix). Sem receita nesta tela,
  // mostramos só percentuais (o R$/ano depende da Receita, que vive na T13).
  let custoDirPctPond = 0, somaMix = 0;
  portfolio.forEach(p => {
    const preco = precoVenda(p), custo = custoUnit(p), mix = Number(p.mix) || 0;
    if (preco > 0) { custoDirPctPond += (mix / 100) * (custo / preco) * 100; somaMix += mix; }
  });
  const temProdutos = portfolio.length > 0 && somaMix > 0;
  const margemContribPct = 100 - custoDirPctPond;

  // Seção 3 · OPEX. Com balancete, as rubricas vêm MEDIDAS do razão; sem
  // balancete, seguem com a estimativa da IA e editáveis (26/07).
  // SEM default embarcado (28/07): o campo nasce VAZIO. A tabela de referência
  // dava R$ 20.300/mês de despesa fixa para qualquer empresa — número redondo
  // com cara de medição, que apareceu na Mored e não tinha nada a ver com ela.
  // Sem balancete o consultor digita; com balancete vem medido. Não há terceiro
  // estado inventado. Princípio `base-zerada-nada-nativo`.
  const obterOpex = (id) => (comp?.opex?.[id] ?? '');
  const atualizarOpex = (id, v) =>
    setComp(prev => ({ ...prev, opex: { ...(prev.opex || {}), [id]: v } }));

  // A LISTAGEM DA FERRAMENTA É O PADRÃO: com balancete, o backend devolve as
  // mesmas rubricas preenchidas + as que só existem no plano de contas da
  // empresa. Sem balancete, cai na lista fixa de sempre.
  const gruposOpex = opexContabil
    ? opexContabil.grupos.map(g => ({
        grupo: g.grupo,
        linhas: g.linhas.map(l => ({ ...l, valorContabil: l.valor_mensal })),
      }))
    : OPEX_FIXO_SU;
  const valorLinhaOpex = (l) =>
    opexContabil ? (l.valorContabil || 0) : (Number(obterOpex(l.id)) || 0);
  // Total do PERÍODO apurado: o que saiu do caixa nos meses lidos. Sem
  // balancete não há série, então cai no mensal × 12 (o que já era feito).
  const periodoLinhaOpex = (l) =>
    opexContabil ? (l.valor_periodo || 0) : (Number(obterOpex(l.id)) || 0) * 12;
  const mesesApurados = opexContabil?.meses || 12;
  // Um único mês selecionado é caso comum (o consultor olha o mês fechado):
  // "1 meses" apareceria em três lugares da tela.
  const rotuloMeses = `${mesesApurados} ${mesesApurados === 1 ? 'mês' : 'meses'}`;
  const opexTotalMes = opexContabil
    ? opexContabil.total_mensal
    : OPEX_FIXO_SU.reduce((s, g) =>
        s + g.linhas.reduce((sg, l) => sg + (Number(obterOpex(l.id)) || 0), 0), 0);
  const opexTotalPeriodo = opexContabil
    ? opexContabil.total_periodo : opexTotalMes * 12;

  // Rateio em reais — depende de `opexTotalMes`, por isso vem depois dele.
  const rateioMensal = (i) => (fatias[i] || 0) * opexTotalMes;
  const rateioUnitario = (i, p) => {
    const q = quantidadeProduto(p);
    if (!(q > 0)) return null;          // sem volume não há rateio por unidade
    return rateioMensal(i) / q;
  };

  // ── Quanto é preciso vender ──────────────────────────────────────────────
  // O cálculo vive no backend (`custo_volume_lucro.py`): é conta de dinheiro
  // com tabela do Simples e busca iterativa, e duplicá-la aqui abriria duas
  // versões da mesma regra. A tela só envia o que o consultor montou.
  const margemDesejadaPct = Number(comp?.margemDesejada ?? 15);
  const composicoesParaCVL = portfolio.map((p, i) => ({
    id: p.id_catalogo || `i${i}`,
    nome: p.nome || 'Produto/serviço sem nome',
    preco: precoVenda(p),
    custo_direto: custoUnit(p),
    fatia: fatias[i] || 0,
    quantidade_informada: quantidadeProduto(p),
  }));
  const chaveCVL = JSON.stringify([composicoesParaCVL, opexTotalMes, margemDesejadaPct,
    setor, metodoFatia]);
  useEffect(() => {
    if (ehIndiretas) return;            // só a aba Custos Diretos usa
    const [comps, fixo, margem, setorEmp, metodo] = JSON.parse(chaveCVL);
    if (!comps.length || !(fixo > 0)) { setCvl(null); return; }
    let vivo = true;
    const t = setTimeout(async () => {
      try {
        const r = await fetch(`${API}/indicadores/custo-volume-lucro/${empresa.id}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            composicoes: comps, despesa_fixa_mensal: fixo,
            margem_desejada: (Number(margem) || 0) / 100, setor: setorEmp,
            metodo_fatia: metodo,
          }),
        });
        if (!r.ok) { if (vivo) setCvl(null); return; }
        const d = await r.json();
        if (vivo) setCvl(d);
      } catch (_) { if (vivo) setCvl(null); }
    }, 400);                            // espera o consultor parar de digitar
    return () => { vivo = false; clearTimeout(t); };
  }, [chaveCVL, empresa.id, ehIndiretas]);

  // Carregando: agora sim, depois de TODOS os hooks.
  if (!comp) return null;

  return (
    <div style={{ padding: '24px 28px', position: 'relative', overflow: 'hidden' }}>
      <CarimboAIdvisor />
      {/* Cabeçalho */}
      <div style={{ marginBottom: 20 }}>
        <div style={{ position: 'relative', display: 'inline-block' }}>
          <h2 style={{ margin: 0, fontSize: 20, fontWeight: 800, color: G.txt1,
            position: 'relative', zIndex: 1 }}>
            {ehIndiretas ? 'Despesas Indiretas' : 'Custos Diretos'}
          </h2>
          <MarcaDaguaTitulo>
            <polygon points="12 2 2 7 12 12 22 7 12 2"/>
            <polyline points="2 17 12 22 22 17"/>
            <polyline points="2 12 12 17 22 12"/>
          </MarcaDaguaTitulo>
        </div>
        <p style={{ ...TIPO.subtitulo, margin: '4px 0 0' }}>
          {ehIndiretas
            ? (opexContabil
                ? 'despesas administrativas · o que o contador lançou fora do custo e da venda'
                : 'despesas fixas · as que a empresa paga mesmo sem vender')
            : 'formação de preço · composição de custos · rateio de despesas fixas'}
        </p>
      </div>

      {/* Par de quadros de introdução, no padrão das tarefas do BP60M
          (Definição em verde · Instruções em laranja, lado a lado). */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12,
        marginBottom: 18 }}>
        <div style={{ padding: '12px 16px', background: G.verdeLight,
          borderLeft: `4px solid ${G.verde}`, borderRadius: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 800, color: G.verde,
            letterSpacing: '0.06em', textTransform: 'uppercase',
            marginBottom: 6 }}>
            {ehIndiretas
              ? (opexContabil ? 'O que é Despesa Administrativa'
                              : 'O que é Despesa Indireta Fixa')
              : 'O que é Custo Direto e Margem de Contribuição'}
          </div>
          <div style={{ fontSize: 12, lineHeight: 1.45, color: G.txt1 }}>
            {ehIndiretas
              ? (opexContabil
                ? <>O que o contador lançou <strong>fora do custo do produto e fora da
                  venda</strong> — aluguel, equipe administrativa, contabilidade. É o
                  grupo "Despesas Administrativas" do plano de contas dele, e é o
                  valor que a <strong>Margem de Contribuição</strong> precisa cobrir
                  antes de existir lucro. Parte dessas contas se repete todo mês;
                  parte não.</>
                : <>O que a empresa paga todo mês <strong>mesmo sem vender nada</strong> —
                  aluguel, equipe administrativa, contabilidade. Não varia com o volume
                  de vendas, e é o valor que a <strong>Margem de Contribuição</strong> precisa
                  cobrir antes de existir lucro.</>)
              : <><strong>Custo Direto</strong> é o que nasce com a venda — a mercadoria,
                o insumo, a mão de obra do serviço. A <strong>Margem de Contribuição</strong> é
                o que sobra de cada venda (Preço − Custo Direto − Impostos) para cobrir as
                Despesas Indiretas e formar o lucro.</>}
          </div>
        </div>
        <div style={{ padding: '12px 16px', background: G.laranjaLight,
          borderLeft: `4px solid ${G.laranja}`, borderRadius: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 800, color: G.laranja,
            letterSpacing: '0.06em', textTransform: 'uppercase',
            marginBottom: 6 }}>
            O que você faz aqui
          </div>
          <div style={{ fontSize: 12, lineHeight: 1.45, color: G.txt1 }}>
            {ehIndiretas
              ? <>A <MarcaAIdvisor /> mede estas despesas nos lançamentos do razão e
                classifica cada conta. Confira os valores e o período apurado. Sem
                balancete e razão, informe cada linha em R$/mês.</>
              : <>Monte a composição de cada produto ou serviço, com insumos, preço de
                venda e participação no mix. A <MarcaAIdvisor /> calcula a Margem de
                Contribuição e o Rateio de Despesas Fixas de cada composição.</>}
          </div>
        </div>
      </div>

      {ehIndiretas && (
      /* ═══ ABA · Despesas Indiretas ═══ */
      <div style={card}>
        {/* Com fonte contábil o nome é o do plano de contas — "Despesas
            Administrativas", nível 2, igual no ContMatic e no Domínio. Sem
            razão processado não há plano, o valor é estimativa, e "Despesas
            Indiretas Fixas" continua descrevendo o que a tela mostra
            (decisão do Fabio, 21/09). */}
        <div style={HEADER_BLOCO}>
          {opexContabil
            ? (opexContabil.rotulo || 'Despesas Administrativas')
            : 'Despesas Indiretas Fixas'}
          <span style={HEADER_AUX}>
            {opexContabil
              ? <>· {opexContabil.codigos ? `${opexContabil.codigos} · ` : ''}média por mês
                  {' '}· medidas no razão pela <MarcaAIdvisor variant="default" /></>
              : <>· despesas fixas mensais · estimadas pela <MarcaAIdvisor variant="default" /></>}
          </span>
        </div>
        <div style={{ padding: '10px 14px', background: G.verdeLight,
          borderLeft: `3px solid ${G.verde}`, borderRadius: 0, marginBottom: 10,
          fontSize: 12, lineHeight: 1.45, color: G.txt1 }}>
          {opexContabil
            ? (opexContabil.origem === 'plano_do_cliente'
              ? <>Os grupos abaixo são os do <strong>plano de contas do seu contador</strong>,
                do jeito que ele os escreveu. A <MarcaAIdvisor /> mediu cada conta nos
                lançamentos do razão — {rotuloMeses}{' '}
                {opexContabil.meses === 1 ? 'apurado' : 'apurados'}. Não é preciso digitar
                nem classificar: quando o escritório trocar de sistema, a tela se refaz
                sozinha.{opexContabil.mudou_de_plano
                  ? <> <strong style={{ color: G.laranja }}>O plano de contas mudou dentro
                    deste período</strong> — por isso aparecem dois nomes para grupos
                    parecidos. Cada um é o nome que o documento daquele mês trazia.</>
                  : null}</>
              : <>A <MarcaAIdvisor /> mediu as despesas administrativas nos lançamentos do razão —
                {' '}{rotuloMeses} {opexContabil.meses === 1 ? 'apurado' : 'apurados'}. Não é preciso digitar: os valores vêm da
                contabilidade. As linhas marcadas <strong style={{ color: G.laranja }}>· do balancete</strong>{' '}
                não existiam na listagem padrão e foram acrescentadas.</>)
            : <>A <MarcaAIdvisor /> já estimou as despesas fixas mensais (as que não variam
              com a venda). Confirme ou ajuste cada linha em R$/mês.</>}
        </div>
        {/* ═══ AS MAIORES DESPESAS · ranking que ATRAVESSA os grupos ═══
            14/09. A tabela abaixo é por grupo contábil, e nela a maior despesa
            da empresa pode estar escondida no terceiro grupo: na MORED,
            "Assistência Médica/Odontológica" é 20,4% do fixo e não aparecia em
            primeiro lugar em lugar nenhum da aba. Esta leitura só existia no
            card do Dashboard — e card que é a única fonte de uma informação
            deixa de ser resumo e vira a tela.
            Sem chamada nova: ordena o que `gruposOpex` já trouxe. */}
        {(() => {
          // Com a árvore do contador, este ranking sai DELA — e não da
          // classificação do razão. Eram duas verdades na mesma tela: em cima
          // "SERVIÇOS PRESTADOS PESSOA JURIDICA R$ 34.197,23" (razão) e logo
          // abaixo R$ 33.917,23 (balancete), com denominadores diferentes
          // (auditoria de 21/09). A coluna "Como o motor leu" passa a mostrar o
          // GRUPO do plano, que é a resposta certa para "de onde veio isto".
          const daArvore = opexContabil?.origem === 'plano_do_cliente';
          const todas = daArvore
            ? gruposOpex.flatMap(g => g.linhas.map(l => ({
                nome: l.label || '—', grupo: g.grupo,
                valor: Number(l.valor_mensal) || 0,
                meses: l.meses_com_movimento, mesesTotal: l.meses_periodo,
                menor: l.menor_mes, maior: l.maior_mes })))
                .filter(x => x.valor > 0)
                .sort((a, b) => b.valor - a.valor)
            : fixoContas
                .map(c => ({ nome: c.conta || '—', grupo: c.motivo || '',
                             valor: Number(c.media_mensal) || 0 }))
                .filter(x => x.valor > 0)
                .sort((a, b) => b.valor - a.valor);
          // O denominador é o MESMO total que a tela exibe embaixo — senão os
          // percentuais não somam 100% do número que o consultor está vendo.
          const totalFixo = daArvore
            ? (Number(opexContabil.total_mensal) || 0)
            : fixoContas.reduce((t, c) => t + (Number(c.media_mensal) || 0), 0);
          // Base zerada não mostra quadro vazio ([[base-zerada-nada-nativo]]).
          if (!todas.length) return null;
          const visiveis = todas.slice(0, verTodasMaiores ? undefined : LIMITE_TABELA);
          const GRID_MAIORES = '22px minmax(200px,1fr) minmax(140px,0.6fr) 130px 80px';
          return (
            <div style={{ marginBottom: 18 }}>
              <div style={{ fontSize: 13, fontWeight: 800, color: G.txt1,
                marginBottom: 2 }}>As maiores despesas</div>
              <div style={{ fontSize: 11.5, color: G.txt2, marginBottom: 10 }}>
                do maior para o menor · conta a conta, atravessando os grupos
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: GRID_MAIORES,
                gap: '0 10px', paddingBottom: 5,
                borderBottom: `2px solid ${G.borda}` }}>
                {[['', 'left'], ['Despesa', 'left'],
                  [opexContabil ? 'Como o motor leu' : 'Por que é fixa', 'left'],
                  ['Média mensal', 'right'],
                  [opexContabil ? '% do Total' : '% do Fixo', 'right']].map(([h, al], i) => (
                  <div key={i} style={{ fontSize: 10, fontWeight: 800, color: G.txt2,
                    letterSpacing: '0.08em', textTransform: 'uppercase',
                    textAlign: al }}>{h}</div>
                ))}
              </div>
              {visiveis.map((d, i) => (
                <div key={`${d.grupo}-${d.nome}`} style={{ display: 'grid',
                  gridTemplateColumns: GRID_MAIORES, gap: '0 10px',
                  alignItems: 'baseline', padding: '7px 0',
                  borderBottom: `1px solid ${G.borda}` }}>
                  <span style={{ fontSize: 11, fontWeight: 700, color: '#9AA5A0' }}>{i + 1}</span>
                  {/* Duas medidas, as duas por CONTAGEM — nada de julgamento:
                      em quantos meses do período a conta teve movimento, e
                      quanto ela variou entre o menor e o maior mês. É o que
                      separa o aluguel (12/12, R$ 14,0 mil a R$ 17,0 mil) da
                      conta pulverizada (12/12 também, mas R$ 11,6 mil a
                      R$ 64,7 mil). Sem isso as duas liam-se igual, e uma conta
                      com 47 fornecedores encabeçava a lista de despesa
                      administrativa como se fosse compromisso previsível. */}
                  <span style={{ minWidth: 0 }}>
                    <span style={{ display: 'block', fontSize: 12.5, color: G.txt1,
                      overflow: 'hidden', textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap' }} title={d.nome}>{d.nome}</span>
                    {d.meses > 0 && (
                      <span style={{ display: 'block', fontSize: 10.5, marginTop: 1,
                        color: (d.maior && d.menor && d.maior >= d.menor * 2)
                          ? G.laranja : G.txt2 }}>
                        {d.meses === d.mesesTotal ? 'todo mês' : `${d.meses} de ${d.mesesTotal} meses`}
                        {d.menor != null && d.maior != null && (d.maior > d.menor
                          ? ` · ${fmt2(d.menor)} a ${fmt2(d.maior)}`
                          : ' · sempre o mesmo valor')}
                      </span>
                    )}
                  </span>
                  <span style={{ fontSize: 11.5, color: G.txt2, overflow: 'hidden',
                    textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={d.grupo}>{d.grupo}</span>
                  {/* `fmt2` já traz o "R$" (style: 'currency'), e o prefixo
                      literal fazia a tela ler "R$ R$ 34.197,23" — exatamente o
                      que o comentário de `fmt2` avisa. */}
                  <span style={{ fontSize: 12.5, fontWeight: 700, textAlign: 'right',
                    fontFamily: "'IBM Plex Mono', monospace" }}>{fmt2(d.valor)}</span>
                  <span style={{ fontSize: 12.5, fontWeight: 700, textAlign: 'right',
                    color: G.laranja, fontVariantNumeric: 'tabular-nums' }}>
                    {totalFixo > 0
                      ? `${(d.valor / totalFixo * 100).toLocaleString('pt-BR',
                          { maximumFractionDigits: 1 })}%` : '—'}
                  </span>
                </div>
              ))}
              <BotaoMostrarMais total={todas.length} limite={LIMITE_TABELA}
                aberto={verTodasMaiores} rotulo="despesas"
                onAlternar={() => setVerTodasMaiores(v => !v)}/>
            </div>
          );
        })()}

        {/* Cinco colunas têm largura mínima. Em janela estreita o quadro rola
            dentro de si, em vez de cortar a última coluna ou espremer o botão
            do grupo para fora da moldura — a página nunca rola de lado. */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14,
          overflowX: 'auto' }}>
          {/* Cabeçalho das colunas em CÉLULA própria — antes era um texto de
              10px solto, que sumia ao lado dos valores (26/07). */}
          <div style={{ display: 'grid', gridTemplateColumns: 'minmax(300px, 1fr) 190px 210px 92px 92px',
            gap: 18, alignItems: 'stretch', marginBottom: 4 }}>
            <div/>
            {[['Média mensal', G.laranja, G.laranjaLight],
              [`Total do período${opexContabil ? ` · ${rotuloMeses}` : ''}`,
               G.verde, G.verdeLight]].map(([rot, cor, fundo]) => (
              <div key={rot} style={{ background: fundo,
                border: `1px solid ${cor}`, borderRadius: 0,
                padding: '8px 12px', textAlign: 'center' }}>
                <span style={{ fontSize: 12, fontWeight: 900, color: cor,
                  letterSpacing: '0.06em', textTransform: 'uppercase' }}>
                  {rot}
                </span>
              </div>
            ))}
            {/* Participação: leitura derivada dos valores, não dinheiro — fica
                em cinza para não competir com as duas colunas de moeda. */}
            {['% do grupo', '% do total'].map(rot => (
              <div key={rot} style={{ background: G.fundo,
                border: `1px solid ${G.borda}`, borderRadius: 0,
                padding: '8px 6px', textAlign: 'center' }}>
                <span style={{ fontSize: 11, fontWeight: 900, color: G.txt2,
                  letterSpacing: '0.04em', textTransform: 'uppercase' }}>
                  {rot}
                </span>
              </div>
            ))}
          </div>
          {gruposOpex.map(g => {
            const subtotal = g.linhas.reduce((s, l) => s + valorLinhaOpex(l), 0);
            const subtotalPeriodo = g.linhas.reduce((s, l) => s + periodoLinhaOpex(l), 0);
            return (
              <div key={g.grupo}>
                {/* Título do grupo E o subtotal na MESMA linha, alinhados às
                    colunas de valor — a soma do grupo vem ANTES dos itens.
                    A moldura vai do nome até encostar nas células de valor. */}
                <div style={{ display: 'grid',
                  gridTemplateColumns: 'minmax(300px, 1fr) 190px 210px 92px 92px', gap: 18,
                  alignItems: 'stretch', marginBottom: 8 }}>
                  {(() => {
                    const aberto = !gruposFechados.includes(g.grupo);
                    return (
                      <div onClick={() => alternarGrupo(g.grupo)}
                        style={{ display: 'flex', alignItems: 'center',
                          justifyContent: 'space-between', gap: 12,
                          background: '#FFF',
                          border: `1px solid ${aberto ? G.verde : G.borda}`,
                          borderRadius: 0, padding: '10px 14px', cursor: 'pointer' }}>
                        <span style={{ fontSize: 13, fontWeight: 800, color: G.txt1,
                          letterSpacing: '0.06em', textTransform: 'uppercase' }}>
                          {g.grupo}
                        </span>
                        <button type="button"
                          onClick={(e) => { e.stopPropagation(); alternarGrupo(g.grupo); }}
                          style={{ flexShrink: 0,
                            background: aberto ? '#FFF' : G.laranjaLight,
                            border: `1px solid ${G.laranja}`, color: G.laranja,
                            padding: '5px 12px', minWidth: 170, textAlign: 'center',
                            fontSize: 11, fontWeight: 700, letterSpacing: '0.06em',
                            textTransform: 'uppercase', borderRadius: 0, cursor: 'pointer',
                            fontFamily: "'Outfit', sans-serif", whiteSpace: 'nowrap' }}>
                          {aberto ? '▴ Clique para fechar' : '▾ Clique para abrir'}
                        </button>
                      </div>
                    );
                  })()}
                  {[subtotal, subtotalPeriodo].map((v, i) => (
                    <div key={i} style={{ padding: '7px 12px',
                      background: G.laranjaLight,
                      border: `1px solid ${G.laranja}`, borderRadius: 0,
                      display: 'flex', justifyContent: 'space-between',
                      alignItems: 'center', gap: 8 }}>
                      <span style={{ fontSize: 10.5, color: G.laranja }}>R$</span>
                      <span style={{ fontSize: 13, fontWeight: 900, color: G.laranja,
                        fontFamily: "'IBM Plex Mono', monospace" }}>
                        {fmtNum(v)}
                      </span>
                    </div>
                  ))}
                  {/* "% do grupo" não se aplica à linha do próprio grupo — seria
                      sempre 100% e não informaria nada. Fica vazia. */}
                  <div/>
                  <div style={{ padding: '7px 6px', background: G.laranjaLight,
                    border: `1px solid ${G.laranja}`, borderRadius: 0,
                    display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                    <span style={{ fontSize: 12.5, fontWeight: 900, color: G.laranja,
                      fontFamily: "'IBM Plex Mono', monospace" }}>
                      {pctDe(subtotal, opexTotalMes)}
                    </span>
                  </div>
                </div>
                <div style={{ display: gruposFechados.includes(g.grupo) ? 'none' : 'flex',
                  flexDirection: 'column', gap: 6 }}>
                  {g.linhas.map(l => (
                    <div key={l.id} style={{ display: 'grid',
                      gridTemplateColumns: 'minmax(300px, 1fr) 190px 210px 92px 92px', gap: 18, alignItems: 'center' }}>
                      <div style={{ fontSize: 12.5, color: G.txt1, lineHeight: 1.35 }}>
                        {l.label}
                        {l.novo && (
                          <span style={{ marginLeft: 6, fontSize: 10, fontWeight: 700,
                            color: G.laranja, textTransform: 'uppercase',
                            letterSpacing: '0.06em' }}>· do balancete</span>
                        )}
                        {l.contas?.length > 1 && (
                          <span style={{ marginLeft: 6, fontSize: 10.5, color: G.txt2 }}
                            title={l.contas.map(c => `${c.conta}: ${fmt(c.valor)}`).join('\n')}>
                            ({l.contas.length} contas)
                          </span>
                        )}
                      </div>
                      {opexContabil ? (
                        <div style={{ padding: '8px 12px', background: G.verdeLight,
                          border: `1px solid ${G.verde}`, borderRadius: 0,
                          display: 'flex', justifyContent: 'space-between',
                          alignItems: 'center', gap: 8 }}>
                          <span style={{ fontSize: 11, color: G.txt2 }}>R$</span>
                          <span style={{ fontSize: 13, fontWeight: 700, color: G.txt1,
                            fontFamily: "'IBM Plex Mono', monospace" }}>
                            {fmtNum(valorLinhaOpex(l))}
                          </span>
                        </div>
                      ) : (
                        <CampoNumericoBP60M
                          valor={obterOpex(l.id)}
                          onChange={(v) => atualizarOpex(l.id, v)}
                          label={l.label}
                          permitirDecimal={true}
                          min={0}
                          formato="brl"
                        />
                      )}
                      {/* Total do período: leitura, nunca digitado — é a soma
                          dos meses apurados. */}
                      <div style={{ padding: '8px 12px', background: G.fundo,
                        border: `1px solid ${G.borda}`, borderRadius: 0,
                        display: 'flex', justifyContent: 'space-between',
                        alignItems: 'center', gap: 8 }}>
                        <span style={{ fontSize: 11, color: G.txt2 }}>R$</span>
                        <span style={{ fontSize: 13, fontWeight: 700, color: G.txt2,
                          fontFamily: "'IBM Plex Mono', monospace" }}>
                          {fmtNum(periodoLinhaOpex(l))}
                        </span>
                      </div>
                      {/* Participação da rubrica: no grupo dela e na despesa
                          total. A razão é a mesma na média e no período (os
                          meses se cancelam), então um percentual por linha. */}
                      {[pctDe(valorLinhaOpex(l), subtotal),
                        pctDe(valorLinhaOpex(l), opexTotalMes)].map((p, i) => (
                        <div key={i} style={{ padding: '8px 6px', background: G.fundo,
                          border: `1px solid ${G.borda}`, borderRadius: 0,
                          display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                          <span style={{ fontSize: 12.5, fontWeight: 700, color: G.txt2,
                            fontFamily: "'IBM Plex Mono', monospace" }}>
                            {p}
                          </span>
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
          {/* Totalizadores nas MESMAS colunas dos valores: o total mensal cai
              exatamente sob a coluna de média mensal, e o do período sob a dele.
              Antes eram dois cards de meia largura, descolados das colunas. */}
          <div style={{ display: 'grid', gridTemplateColumns: 'minmax(300px, 1fr) 190px 210px 92px 92px',
            gap: 18, alignItems: 'stretch', marginTop: 6,
            borderTop: `2px solid ${G.borda}`, paddingTop: 12 }}>
            <div style={{ fontSize: 13, fontWeight: 900, color: G.txt1,
              letterSpacing: '0.06em', textTransform: 'uppercase',
              textAlign: 'right', alignSelf: 'center' }}>
              {opexContabil ? 'Despesa Administrativa Total' : 'Despesa Fixa Total'}
            </div>
            {/* Cada total com o rótulo DELE: o rótulo único falava de 24 meses
                colado no valor da média mensal e confundia os dois (26/07). */}
            {[['Média mensal', opexTotalMes, G.laranja, G.laranjaLight],
              [`Período${opexContabil ? ` · ${rotuloMeses}` : ''}`,
               opexTotalPeriodo, G.verde, G.verdeLight]].map(([rot, val, cor, fundo]) => (
              <div key={rot} style={{ background: fundo,
                border: `2px solid ${cor}`, borderRadius: 0, padding: '10px 12px' }}>
                <div style={{ fontSize: 10, fontWeight: 800, color: cor,
                  letterSpacing: '0.08em', textTransform: 'uppercase',
                  textAlign: 'center', marginBottom: 4 }}>{rot}</div>
                <div style={{ display: 'flex', justifyContent: 'space-between',
                  alignItems: 'baseline', gap: 8 }}>
                  <span style={{ fontSize: 10.5, color: cor }}>R$</span>
                  <span style={{ fontSize: 16, fontWeight: 900, color: cor,
                    fontFamily: "'IBM Plex Mono', monospace" }}>
                    {fmtNum(val)}
                  </span>
                </div>
              </div>
            ))}
            {/* Fechamento da coluna: a soma das participações dos grupos tem de
                dar 100%. Calculado, não escrito à mão — se algum grupo ficar de
                fora da soma, aparece aqui. */}
            <div/>
            <div style={{ background: G.fundo, border: `2px solid ${G.borda}`,
              borderRadius: 0, padding: '10px 6px', display: 'flex',
              justifyContent: 'center', alignItems: 'center' }}>
              <span style={{ fontSize: 13, fontWeight: 900, color: G.txt1,
                fontFamily: "'IBM Plex Mono', monospace" }}>
                {pctDe(gruposOpex.reduce((s, g) =>
                  s + g.linhas.reduce((sg, l) => sg + valorLinhaOpex(l), 0), 0),
                  opexTotalMes)}
              </span>
            </div>
          </div>

        </div>
      </div>
      )}

      {!ehIndiretas && (<>
      {/* ═══ ABA · Custos Diretos · composições ═══ */}
      <div style={card}>
        <div style={HEADER_BLOCO}>
          Formação de Preço de Venda
          <span style={HEADER_AUX}>· composição de custos e preço de cada produto/serviço</span>
        </div>
        <div style={{ padding: '10px 14px', background: G.verdeLight,
          borderLeft: `3px solid ${G.verde}`, borderRadius: 0, marginBottom: 10,
          fontSize: 12, lineHeight: 1.45, color: G.txt1 }}>
          A <MarcaAIdvisor /> conhece seus produtos e já sugeriu, para cada um, a{' '}
          <strong>composição de insumos</strong> e o <strong>Preço de Venda de mercado</strong>.
          Confirme ou ajuste. A <strong>Margem de Contribuição</strong> é o resultado
          (Preço − Custo Direto) · impostos e despesas variáveis entram depois.
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {portfolio.map((item, idx) => {
            const aberto = precoAberto === idx;
            const tipo = tipoDoProduto(item);
            const cu = custoUnit(item);
            const pv = precoVenda(item);
            const mc = margemContrib(item);
            const mcPct = pv > 0 ? (mc / pv) * 100 : 0;
            const insumos = INSUMOS_PRECO_SU[tipo];
            return (
              <div key={idx} style={{ background: '#FFF',
                border: `1px solid ${aberto ? G.verde : G.borda}`, borderRadius: 0 }}>
                <div onClick={() => setPrecoAberto(aberto ? null : idx)}
                  style={{ padding: '10px 14px', cursor: 'pointer',
                    display: 'flex', alignItems: 'center', gap: 12 }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 800, color: G.txt1 }}>
                      {item.nome || 'Produto/serviço sem nome'}
                    </div>
                    <div style={{ fontSize: 11.5, color: G.txt2, marginTop: 2 }}>
                      {tipo === 'servico' ? 'Serviço' : 'Mercadoria'}
                      {' · '}custo {fmt2(cu)} · preço <strong>{fmt2(pv)}</strong>
                      {' · '}contribuição {fmt2(mc)}
                    </div>
                  </div>
                  <button type="button"
                    onClick={(e) => { e.stopPropagation(); setPrecoAberto(aberto ? null : idx); }}
                    style={{ flexShrink: 0,
                      background: aberto ? '#FFF' : G.laranjaLight,
                      border: `1px solid ${G.laranja}`, color: G.laranja,
                      padding: '5px 12px', minWidth: 170, textAlign: 'center',
                      fontSize: 11, fontWeight: 700, letterSpacing: '0.06em',
                      textTransform: 'uppercase', borderRadius: 0, cursor: 'pointer',
                      fontFamily: "'Outfit', sans-serif", whiteSpace: 'nowrap' }}>
                    {aberto ? '▴ Clique para fechar' : '▾ Clique para abrir'}
                  </button>
                </div>
                {aberto && (
                  <div style={{ padding: '12px 14px 14px',
                    borderTop: `1px dashed ${G.borda}`,
                    display: 'flex', flexDirection: 'column', gap: 12 }}>
                    <div>
                      <div style={{ fontSize: 10.5, fontWeight: 800, color: G.txt2,
                        letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 4 }}>
                        Tipo <span style={{ fontWeight: 500, textTransform: 'none',
                          letterSpacing: 0, color: G.txt2 }}>· sugerido pela RUNGOAL AIDVISOR</span>
                      </div>
                      <div style={{ display: 'flex', gap: 6, maxWidth: 320 }}>
                        {[{ id: 'mercadoria', label: 'Mercadoria' }, { id: 'servico', label: 'Serviço' }].map(t => {
                          const sel = tipo === t.id;
                          return (
                            <button key={t.id} type="button"
                              onClick={() => editarComposicao(idx, 'tipo', t.id)}
                              style={{ flex: 1, padding: '8px 10px', fontSize: 12, fontWeight: 700,
                                background: sel ? G.verde : '#FFF', color: sel ? '#FFF' : G.txt1,
                                border: `1px solid ${sel ? G.verde : G.borda}`, borderRadius: 0,
                                cursor: 'pointer', fontFamily: "'Outfit', sans-serif" }}>
                              {sel ? '✓ ' : ''}{t.label}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                    <div>
                      <div style={{ fontSize: 10.5, fontWeight: 800, color: G.txt2,
                        letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 6 }}>
                        Insumos · custo direto unitário (R$)
                        <span style={{ fontWeight: 500, textTransform: 'none',
                          letterSpacing: 0, color: G.txt2 }}> · estimado pela RUNGOAL AIDVISOR · ajuste</span>
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                        {insumos.map(it => (
                          <div key={it.id} style={{ display: 'grid',
                            gridTemplateColumns: '1fr 150px', gap: 12, alignItems: 'center' }}>
                            <div style={{ fontSize: 12.5, color: G.txt1, lineHeight: 1.35 }}>
                              {it.label}
                            </div>
                            <CampoNumericoBP60M
                              valor={insumoValor(item, it.id)}
                              onChange={(v) => editarInsumoComposicao(idx, it.id, v)}
                              label={it.label}
                              permitirDecimal={true}
                              min={0}
                              formato="brl"
                            />
                          </div>
                        ))}
                      </div>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 12 }}>
                      <div>
                        <div style={{ fontSize: 10.5, fontWeight: 800, color: G.txt2,
                          letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 4 }}>
                          Preço de Venda <span style={{ fontWeight: 500, textTransform: 'none',
                            letterSpacing: 0, color: G.txt2 }}>· mercado</span>
                        </div>
                        <CampoNumericoBP60M
                          valor={pv}
                          onChange={(v) => editarComposicao(idx, 'precoVenda', v)}
                          label="Preço de Venda competitivo de mercado (R$)"
                          permitirDecimal={true}
                          min={0}
                          formato="brl"
                        />
                      </div>
                      <div>
                        <div style={{ fontSize: 10.5, fontWeight: 800, color: G.txt2,
                          letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 4 }}>
                          Custo Direto Unitário
                        </div>
                        <div style={{ height: 36, display: 'flex', alignItems: 'center',
                          justifyContent: 'flex-end', padding: '0 12px',
                          border: `1px solid ${G.borda}`, borderRadius: 0, background: G.fundo,
                          fontSize: 14, fontWeight: 700, color: G.txt1,
                          fontFamily: "'IBM Plex Mono', monospace" }}>
                          {fmt2(cu)}
                        </div>
                      </div>
                      <div>
                        <div style={{ fontSize: 10.5, fontWeight: 800, color: G.txt2,
                          letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 4 }}>
                          Margem de Contribuição
                        </div>
                        <div style={{ height: 36, display: 'flex', alignItems: 'center',
                          justifyContent: 'flex-end', gap: 8, padding: '0 12px',
                          border: `1px solid ${mc >= 0 ? G.verde : G.vermelho}`, borderRadius: 0,
                          background: mc >= 0 ? G.verdeLight : '#FEF2F2',
                          fontSize: 14, fontWeight: 900, color: mc >= 0 ? G.verde : G.vermelho,
                          fontFamily: "'IBM Plex Mono', monospace" }}>
                          <span>{fmt2(mc)}</span>
                          <span style={{ fontSize: 11, fontWeight: 700 }}>
                            {mcPct.toLocaleString('pt-BR', { maximumFractionDigits: 0 })}%
                          </span>
                        </div>
                      </div>
                      <div>
                        <div style={{ fontSize: 10.5, fontWeight: 800, color: G.txt2,
                          letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 4 }}>
                          Quantidade <span style={{ fontWeight: 500, textTransform: 'none',
                            letterSpacing: 0, color: G.txt2 }}>· por mês</span>
                        </div>
                        <CampoNumericoBP60M
                          valor={quantidadeProduto(item)}
                          onChange={(v) => editarComposicao(idx, 'quantidade', v)}
                          label="Quantidade vendida por mês"
                          permitirDecimal={false}
                          min={0}
                          sufixo="un."
                          formato="int"
                        />
                        <div style={{ fontSize: 10.5, color: G.txt2, marginTop: 4 }}>
                          {item.quantidade === null || item.quantidade === undefined || item.quantidade === ''
                            ? (quantidadeEstimada(item) > 0
                                ? <>estimada pela <MarcaAIdvisor variant="default" /> · confirme</>
                                : <>informe o volume · sem ele não há cálculo de volume</>)
                            : 'informada pelo consultor'}
                        </div>
                      </div>
                    </div>
                    {/* Rateio de Despesas Fixas — a novidade da composição.
                        Fica em bloco próprio, com moldura, porque é o item que
                        liga esta tela às Despesas Indiretas. */}
                    {(() => {
                      const fatia = fatias[idx] || 0;
                      const rMes = rateioMensal(idx);
                      const rUn = rateioUnitario(idx, item);
                      const sobra = rUn === null ? null : mc - rUn;
                      return (
                        <div style={{ border: `1px solid ${G.laranja}`, borderRadius: 0,
                          background: G.laranjaLight, padding: '12px 14px' }}>
                          <div style={{ fontSize: 12, fontWeight: 900, color: G.laranja,
                            letterSpacing: '0.06em', textTransform: 'uppercase',
                            marginBottom: 10 }}>
                            Rateio de Despesas Fixas
                            <span style={{ fontWeight: 500, textTransform: 'none',
                              letterSpacing: 0, color: G.txt2, fontSize: 11 }}>
                              {' '}· quanto desta composição paga a despesa indireta
                            </span>
                          </div>
                          <div style={{ display: 'grid',
                            gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 12 }}>
                            <div>
                              <div style={{ fontSize: 10.5, fontWeight: 800, color: G.txt2,
                                letterSpacing: '0.08em', textTransform: 'uppercase',
                                marginBottom: 4 }}>Fatia assumida</div>
                              <CampoNumericoBP60M
                                valor={Number((fatia * 100).toFixed(2))}
                                onChange={(v) => editarComposicao(idx, 'fatiaFixo', v)}
                                label="Fatia da despesa indireta assumida por este item (%)"
                                permitirDecimal={true} min={0} max={100}
                                sufixo="%" formato="decimal"
                              />
                              <div style={{ fontSize: 10.5, color: G.txt2, marginTop: 4 }}>
                                {item.fatiaFixo === null || item.fatiaFixo === undefined || item.fatiaFixo === ''
                                  ? 'proporcional ao mix · ajuste se quiser'
                                  : 'fixada pelo consultor'}
                              </div>
                            </div>
                            <div>
                              <div style={{ fontSize: 10.5, fontWeight: 800, color: G.txt2,
                                letterSpacing: '0.08em', textTransform: 'uppercase',
                                marginBottom: 4 }}>Assume por mês</div>
                              <div style={{ height: 36, display: 'flex', alignItems: 'center',
                                justifyContent: 'flex-end', padding: '0 12px',
                                border: `1px solid ${G.borda}`, borderRadius: 0, background: '#FFF',
                                fontSize: 14, fontWeight: 700, color: G.txt1,
                                fontFamily: "'IBM Plex Mono', monospace" }}>
                                {fmt2(rMes)}
                              </div>
                            </div>
                            <div>
                              <div style={{ fontSize: 10.5, fontWeight: 800, color: G.txt2,
                                letterSpacing: '0.08em', textTransform: 'uppercase',
                                marginBottom: 4 }}>Rateio por unidade</div>
                              <div style={{ height: 36, display: 'flex', alignItems: 'center',
                                justifyContent: 'flex-end', padding: '0 12px',
                                border: `1px solid ${G.laranja}`, borderRadius: 0, background: '#FFF',
                                fontSize: 14, fontWeight: 900, color: G.laranja,
                                fontFamily: "'IBM Plex Mono', monospace" }}>
                                {rUn === null ? '—' : fmt2(rUn)}
                              </div>
                            </div>
                            <div>
                              <div style={{ fontSize: 10.5, fontWeight: 800, color: G.txt2,
                                letterSpacing: '0.08em', textTransform: 'uppercase',
                                marginBottom: 4 }}>Sobra por unidade</div>
                              <div style={{ height: 36, display: 'flex', alignItems: 'center',
                                justifyContent: 'flex-end', padding: '0 12px',
                                border: `1px solid ${sobra !== null && sobra < 0 ? G.vermelho : G.verde}`,
                                borderRadius: 0,
                                background: sobra !== null && sobra < 0 ? '#FEF2F2' : G.verdeLight,
                                fontSize: 14, fontWeight: 900,
                                color: sobra !== null && sobra < 0 ? G.vermelho : G.verde,
                                fontFamily: "'IBM Plex Mono', monospace" }}>
                                {sobra === null ? '—' : fmt2(sobra)}
                              </div>
                            </div>
                          </div>
                          <div style={{ fontSize: 11, color: G.txt2, marginTop: 8, lineHeight: 1.45 }}>
                            {rUn === null
                              ? <>Informe a quantidade vendida por mês para a <MarcaAIdvisor variant="default" /> calcular o rateio por unidade.</>
                              : sobra < 0
                                ? <>A margem de contribuição não cobre o rateio: cada unidade vendida deixa <strong style={{ color: G.vermelho }}>{fmt2(Math.abs(sobra))}</strong> a descoberto, que os outros itens precisam pagar.</>
                                : fatia === 0
                                  ? <>Este item não assume despesa indireta — quem paga a parte dele são os demais. A sobra inteira vira resultado.</>
                                  : <>Depois de pagar sua fatia da despesa indireta, cada unidade deixa <strong style={{ color: G.verde }}>{fmt2(sobra)}</strong> para impostos sobre a venda e lucro.</>}
                          </div>
                        </div>
                      );
                    })()}
                    <div style={{ fontSize: 11, color: G.txt2, fontStyle: 'italic' }}>
                      Margem de contribuição antes de impostos e despesas variáveis.
                    </div>
                  </div>
                )}
              </div>
            );
          })}
          {portfolio.length === 0 && (
            <div style={{ fontSize: 12, color: G.txt2, fontStyle: 'italic',
              padding: '12px 14px', background: G.fundo, borderRadius: 0 }}>
              A <MarcaAIdvisor /> ainda não tem produtos/serviços desta empresa ·
              adicione abaixo para montar a composição de custos e preço.
            </div>
          )}
          {/* Sem este aviso, parte da despesa indireta ficaria sem dono e o
              resultado sairia otimista em silêncio. */}
          {fatiasEstouradas && (
            <div style={{ fontSize: 12, lineHeight: 1.45, color: G.txt1,
              padding: '10px 14px', background: '#FEF2F2',
              borderLeft: `3px solid ${G.vermelho}`, borderRadius: 0 }}>
              As fatias fixadas somam mais de 100% da despesa indireta. Os itens
              que ainda não foram fixados ficaram com <strong>zero</strong> —
              reduza alguma fatia para que a soma feche em 100%.
            </div>
          )}
          {!fatiasEstouradas && portfolio.length > 0 && somaFatias < 0.9999 && (
            <div style={{ fontSize: 12, lineHeight: 1.45, color: G.txt1,
              padding: '10px 14px', background: G.laranjaLight,
              borderLeft: `3px solid ${G.laranja}`, borderRadius: 0 }}>
              As fatias somam <strong>{(somaFatias * 100).toLocaleString('pt-BR',
                { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%</strong> —
              o restante da despesa indireta está sem nenhum item assumindo.
            </div>
          )}
        </div>
        <button onClick={adicionarProduto} style={btnAdd}>+ Adicionar produto/serviço</button>
      </div>

      {/* ═══ ABA · Custos Diretos · consolidado ═══ */}
      <div style={card}>
        <div style={HEADER_BLOCO}>
          Custos Diretos (Variáveis com Receita)
          <span style={HEADER_AUX}>· consolidado a partir da Formação de Preço · ponderado pelo mix</span>
        </div>
        <div style={{ padding: '10px 14px', background: G.verdeLight,
          borderLeft: `3px solid ${G.verde}`, borderRadius: 0, marginBottom: 10,
          fontSize: 12, lineHeight: 1.45, color: G.txt1 }}>
          A <MarcaAIdvisor /> consolida o custo direto e a margem de contribuição do
          conjunto, ponderados pelo mix de receita. Para ajustar, edite os custos de
          cada produto na Formação de Preço acima.
        </div>
        {!temProdutos ? (
          <div style={{ fontSize: 12, color: G.txt2, fontStyle: 'italic',
            padding: '12px 14px', background: G.fundo, border: `1px solid ${G.borda}`,
            borderRadius: 0 }}>
            Defina os produtos/serviços e o mix de receita na Formação de Preço ·
            o custo direto consolidado aparece aqui automaticamente.
          </div>
        ) : (
          <>
            <div style={{ background: '#FFF', border: `1px solid ${G.borda}`,
              borderRadius: 0, overflow: 'hidden' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 90px 100px 100px 70px',
                gap: 8, padding: '8px 12px', background: G.fundo,
                fontSize: 10, fontWeight: 800, color: G.txt2,
                letterSpacing: '0.06em', textTransform: 'uppercase' }}>
                <div>Produto/Serviço</div>
                <div style={{ textAlign: 'right' }}>Mix</div>
                <div style={{ textAlign: 'right' }}>Custo</div>
                <div style={{ textAlign: 'right' }}>Preço</div>
                <div style={{ textAlign: 'right' }}>Contrib.</div>
              </div>
              {portfolio.map((p, i) => {
                const preco = precoVenda(p);
                const custo = custoUnit(p);
                const contribPct = preco > 0 ? ((preco - custo) / preco) * 100 : 0;
                return (
                  <div key={i} style={{ display: 'grid',
                    gridTemplateColumns: '1fr 90px 100px 100px 70px', gap: 8,
                    padding: '8px 12px', fontSize: 12, color: G.txt1,
                    borderTop: `1px solid ${G.borda}`,
                    fontFamily: "'IBM Plex Mono', monospace" }}>
                    <div style={{ fontFamily: "'Outfit', sans-serif" }}>
                      {p.nome || 'Sem nome'}
                    </div>
                    <div style={{ textAlign: 'right' }}>{(Number(p.mix) || 0)}%</div>
                    <div style={{ textAlign: 'right' }}>{fmt(custo)}</div>
                    <div style={{ textAlign: 'right' }}>{fmt(preco)}</div>
                    <div style={{ textAlign: 'right', color: contribPct >= 0 ? G.verde : G.vermelho,
                      fontWeight: 700 }}>{contribPct.toLocaleString('pt-BR', { maximumFractionDigits: 0 })}%</div>
                  </div>
                );
              })}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginTop: 12 }}>
              <div style={{ background: '#FFF', border: `1px solid ${G.borda}`,
                borderLeft: `3px solid ${G.laranja}`, borderRadius: 0, padding: '12px 14px' }}>
                <div style={{ fontSize: 10, fontWeight: 800, color: G.txt2,
                  letterSpacing: '0.10em', textTransform: 'uppercase', marginBottom: 4 }}>
                  Custo Direto consolidado
                </div>
                <div style={{ fontSize: 20, fontWeight: 900, color: G.laranja,
                  fontFamily: "'IBM Plex Mono', monospace" }}>
                  {custoDirPctPond.toLocaleString('pt-BR', { maximumFractionDigits: 0 })}%
                </div>
              </div>
              <div style={{ background: '#FFF',
                border: `1px solid ${margemContribPct >= 0 ? G.borda : G.vermelho}`,
                borderLeft: `3px solid ${margemContribPct >= 0 ? G.verde : G.vermelho}`,
                borderRadius: 0, padding: '12px 14px' }}>
                <div style={{ fontSize: 10, fontWeight: 800, color: G.txt2,
                  letterSpacing: '0.10em', textTransform: 'uppercase', marginBottom: 4 }}>
                  Margem de Contribuição
                </div>
                <div style={{ fontSize: 20, fontWeight: 900,
                  color: margemContribPct >= 0 ? G.verde : G.vermelho,
                  fontFamily: "'IBM Plex Mono', monospace" }}>
                  {margemContribPct.toLocaleString('pt-BR', { maximumFractionDigits: 0 })}%
                </div>
              </div>
            </div>
            <div style={{ fontSize: 11, color: G.txt2, fontStyle: 'italic', marginTop: 8 }}>
              Percentuais sobre o preço · antes de impostos e despesas variáveis. Os
              valores em R$ dependem da Receita projetada (Estruturação Financeira).
            </div>
          </>
        )}
      </div>

      {/* ═══ ABA · Custos Diretos · quanto é preciso vender ═══ */}
      <div style={card}>
        <div style={HEADER_BLOCO}>
          Quanto é Preciso Vender
          <span style={HEADER_AUX}>· análise custo-volume-lucro · calculada pela{' '}
            <MarcaAIdvisor variant="default" /></span>
        </div>
        <div style={{ padding: '10px 14px', background: G.verdeLight,
          borderLeft: `3px solid ${G.verde}`, borderRadius: 0, marginBottom: 12,
          fontSize: 12, lineHeight: 1.45, color: G.txt1 }}>
          Dada a despesa indireta, o custo de cada item e os impostos, a{' '}
          <MarcaAIdvisor /> calcula <strong>quanto cada produto precisa vender</strong> para
          cobrir sua fatia do fixo e ainda entregar a margem operacional que você definir.
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '220px 220px 1fr',
          gap: 14, alignItems: 'start', marginBottom: 14 }}>
          <div>
            <div style={{ fontSize: 10.5, fontWeight: 800, color: G.txt2,
              letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 4 }}>
              Margem operacional desejada
            </div>
            <CampoNumericoBP60M
              valor={margemDesejadaPct}
              onChange={(v) => setComp(prev => ({ ...prev, margemDesejada: v }))}
              label="Margem operacional desejada (% da receita)"
              permitirDecimal={true} min={0} max={99}
              sufixo="%" formato="decimal"
            />
          </div>
          {/* O desejado e o realizado lado a lado — mesma decisão da tela irmã. */}
          <BlocoMargemRealizada realizado={cvl?.realizado}
            margemDesejada={cvl?.margem_desejada} />
          <div style={{ fontSize: 11.5, color: G.txt2, lineHeight: 1.45 }}>
            Uma só para a empresa. O que modula produto a produto é a fatia da
            despesa indireta, no bloco de cada composição.
          </div>
        </div>

        {!cvl ? (
          <div style={{ fontSize: 12, color: G.txt2, fontStyle: 'italic',
            padding: '12px 14px', background: G.fundo, borderRadius: 0 }}>
            {opexTotalMes > 0
              ? <>Monte ao menos uma composição com preço e custo para a <MarcaAIdvisor /> calcular.</>
              : <>Sem despesa indireta apurada não há o que ratear · confira a aba Despesas Indiretas.</>}
          </div>
        ) : (
          <>
            <SeletorRateio valor={metodoFatia} onChange={setMetodoFatia} />
            <div style={{ display: 'grid', gridTemplateColumns: GRID_CVL,
              gap: 12, marginBottom: 6 }}>
              {['Produto/serviço', 'Fatia', 'Quantidade para Breakeven',
                'Quantidade a + para Margem',
                'Precisa vender', 'Você planeja', 'Diferença']
                .map((h, i) => (
                <div key={h} style={{ fontSize: 10, fontWeight: 800, color: G.txt2,
                  letterSpacing: '0.08em', textTransform: 'uppercase',
                  textAlign: i === 0 ? 'left' : 'right' }}>{h}</div>
              ))}
            </div>
            {(cvl.itens || []).map((it, i) => {
              const planeja = quantidadeProduto(portfolio[i] || {});
              // Diferença na leitura do empresário (Fabio, 09/09): o que ele
              // vendeu MENOS o que precisaria vender. Positivo = passou da
              // conta; negativo = falta. Estava invertido — vender 5 a mais
              // aparecia como −5.
              const gap = it.quantidade === null ? null : planeja - it.quantidade;
              return (
                <div key={it.id || i} style={{ display: 'grid',
                  gridTemplateColumns: GRID_CVL,
                  gap: 12, alignItems: 'center', padding: '7px 0',
                  borderTop: `1px solid ${G.borda}` }}>
                  <div style={{ fontSize: 12.5, color: G.txt1 }}>
                    <span style={{ color: G.txt2, fontWeight: 600 }}>{i + 1}.</span> {it.nome}
                  </div>
                  <div style={{ fontSize: 12.5, textAlign: 'right', color: G.txt2,
                    fontFamily: "'IBM Plex Mono', monospace" }}>
                    {(it.fatia * 100).toLocaleString('pt-BR',
                      { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%
                  </div>
                  <div style={{ fontSize: 12.5, fontWeight: 700, textAlign: 'right',
                    color: G.txt1, fontFamily: "'IBM Plex Mono', monospace" }}>
                    {it.quantidade_breakeven == null ? '—'
                      : it.quantidade_breakeven.toLocaleString('pt-BR', { maximumFractionDigits: 0 })}
                  </div>
                  {/* Mesmo tom da coluna anterior: o laranja da marca, num
                      número, era lido como alerta (Fabio, 09/09). */}
                  <div style={{ fontSize: 12.5, fontWeight: 700, textAlign: 'right',
                    color: G.txt1, fontFamily: "'IBM Plex Mono', monospace" }}>
                    {it.quantidade_pelo_lucro == null ? '—'
                      : `+${it.quantidade_pelo_lucro.toLocaleString('pt-BR', { maximumFractionDigits: 0 })}`}
                  </div>
                  <div style={{ fontSize: 13, fontWeight: 900, textAlign: 'right',
                    color: it.inviavel ? G.vermelho : G.verde,
                    fontFamily: "'IBM Plex Mono', monospace" }}>
                    {it.inviavel ? 'não fecha'
                      : it.quantidade.toLocaleString('pt-BR', { maximumFractionDigits: 0 })}
                  </div>
                  <div style={{ fontSize: 12.5, textAlign: 'right', color: G.txt2,
                    fontFamily: "'IBM Plex Mono', monospace" }}>
                    {planeja.toLocaleString('pt-BR', { maximumFractionDigits: 0 })}
                  </div>
                  <div style={{ fontSize: 12.5, fontWeight: 700, textAlign: 'right',
                    color: gap === null ? G.txt2 : (gap < 0 ? G.vermelho : G.verde),
                    fontFamily: "'IBM Plex Mono', monospace" }}>
                    {gap === null ? '—'
                      : `${gap > 0 ? '+' : ''}${gap.toLocaleString('pt-BR', { maximumFractionDigits: 0 })}`}
                  </div>
                </div>
              );
            })}
            <div style={{ display: 'flex', gap: 14, marginTop: 12, flexWrap: 'wrap' }}>
              {[['Receita necessária por mês', fmt2(cvl.receita_mensal), G.verde, G.verdeLight],
                ['Despesa indireta a cobrir', fmt2(cvl.despesa_fixa_mensal), G.laranja, G.laranjaLight],
                ['Imposto sobre a venda',
                 `${(cvl.aliquota_sobre_venda * 100).toLocaleString('pt-BR',
                   { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%`,
                 G.txt1, G.fundo]].map(([rot, val, cor, fundo]) => (
                <div key={rot} style={{ flex: '1 1 200px', background: fundo,
                  border: `1px solid ${cor}`, borderRadius: 0, padding: '10px 14px' }}>
                  <div style={{ fontSize: 10, fontWeight: 800, color: cor,
                    letterSpacing: '0.08em', textTransform: 'uppercase' }}>{rot}</div>
                  <div style={{ fontSize: 16, fontWeight: 900, color: cor, marginTop: 4,
                    fontFamily: "'IBM Plex Mono', monospace" }}>{val}</div>
                </div>
              ))}
            </div>
            <div style={{ fontSize: 11, color: G.txt2, marginTop: 8 }}>
              Regime <strong>{cvl.regime}</strong>
              {cvl.regime === 'Simples Nacional' && <> · anexo {cvl.anexo} · alíquota
                resolvida em {cvl.iteracoes} passo{cvl.iteracoes === 1 ? '' : 's'}</>}
              {cvl.regime !== 'Simples Nacional' && <> · alíquota medida nos lançamentos da empresa</>}
            </div>
            {/* Avisos do motor: teto do Simples, sublimite de ICMS/ISS, fatias
                que não fecham, itens inviáveis. Nunca silenciar. */}
            {(cvl.avisos || []).map((a, i) => (
              <div key={i} style={{ fontSize: 12, lineHeight: 1.45, color: G.txt1,
                padding: '10px 14px', background: G.laranjaLight, marginTop: 8,
                borderLeft: `3px solid ${G.laranja}`, borderRadius: 0 }}>{a}</div>
            ))}
            {(cvl.erros || []).map((e, i) => (
              <div key={i} style={{ fontSize: 12, lineHeight: 1.45, color: G.txt1,
                padding: '10px 14px', background: '#FEF2F2', marginTop: 8,
                borderLeft: `3px solid ${G.vermelho}`, borderRadius: 0 }}>{e}</div>
            ))}
          </>
        )}
      </div>
      </>)}
    </div>
  );
};


const TelaBP60M = ({ empresa, onAbrirConfig, modo = 'EE', parametros }) => {
  // subTela: 'landing' | 'preview-template' | 'tarefa-1' .. 'tarefa-20'
  // Init consome bridge do RGAM (só roda 1x, mesmo com StrictMode)
  const [subTela, setSubTela] = useState(() => {
    const bridge = localStorage.getItem('bp60m:subTelaBridge');
    if (bridge) {
      localStorage.removeItem('bp60m:subTelaBridge');
      return bridge;
    }
    return 'landing';
  });
  // Ref pra detectar troca de empresa · não resetar no primeiro mount nem em StrictMode
  const empresaAnteriorRef = useRef(empresa?.id);
  // Progresso, flag de Equity e Meta — persistidos em localStorage por empresa
  const [tarefasFeitas, setTarefasFeitas] = useState([]);
  const [equityFlag, setEquityFlag] = useState(false);
  // Meta = { tipo: 'ebitda_mult'|'receita_mult'|'margem_alvo'|'saida', valor: number }
  const [meta, setMeta] = useState({ tipo: 'ebitda_mult', valor: 5 });
  // Descrição da Empresa (Tarefa 2) = { historia: [], equipe: [], instalacoes: [] }
  // null = ainda não editou (usa rascunho heurístico padrão)
  const [descricaoEmpresa, setDescricaoEmpresa] = useState(null);
  // Visão · Missão · Valores (Tarefa 3) = { visao:{texto, escolhas}, missao:{...}, valores:{...} }
  const [vmv, setVmv] = useState(null);
  // Produtos & Serviços (Tarefa 4) = { portfolio, diferenciais, pipeline, investimentoPD, parcerias }
  const [produtosServicos, setProdutosServicos] = useState(null);
  // Mercado & Concorrência (Tarefa 5) = { tam_*, sam_*, som_*, tendencias, players, moats }
  const [mercadoConcorrencia, setMercadoConcorrencia] = useState(null);
  // SWOT (Tarefa 6) = { forcas, fraquezas, oportunidades, ameacas } cada um lista de {texto, marcado, fonte}
  const [swot, setSwot] = useState(null);
  // Estratégia & Modelo de Negócio (Tarefa 7) = { estrategias, proposta_valor, modelo_receita, segmentos, cac, ltv, margem_unitaria }
  const [estrategiaModelo, setEstrategiaModelo] = useState(null);
  // Marketing & Vendas (Tarefa 8) = { posicionamento, tom_comunicacao, canais_mix, funil_observacoes, funil_gargalo, invest_mkt_respostas }
  const [marketingVendas, setMarketingVendas] = useState(null);
  // Plano Operacional (Tarefa 9) = { atividades_chars, atividades_extras, fornecedores, sistemas_chars, sistemas_extras, instalacoes, expansao, capacidade_resposta }
  const [planoOperacional, setPlanoOperacional] = useState(null);
  // Estrutura Org & RH (Tarefa 10) = { organograma, areas_chars, areas_extras, pessoas_chave, headcount_hoje, hires_plano, cultura_chars, cultura_extras, remuneracao }
  const [estruturaRh, setEstruturaRh] = useState(null);
  const [riscos, setRiscos] = useState(null);
  const [esg, setEsg] = useState(null);
  const [projecoes, setProjecoes] = useState(null);
  const [valuation, setValuation] = useState(null);
  const [conclusao, setConclusao] = useState(null);
  // Estruturação Financeira Inicial (Tarefa 13 · exclusiva Startup) = { recA_*, ... }
  const [estruturaFinSU, setEstruturaFinSU] = useState(null);
  // Documento BP compilado por seção: { secao_1: "md...", secao_2: "md..." }
  // Cada aprovação de tarefa compila o markdown e salva aqui.
  const [documentoBP, setDocumentoBP] = useState({});
  // Modal de preview do BP compilado
  const [preverBP, setPreverBP] = useState(false);

  // localStorage key por empresa
  const LS_KEY = `rungoal:bp60m:${empresa?.id || 'default'}`;

  // Carrega progresso ao montar / trocar empresa (reseta subTela para landing)
  useEffect(() => {
    try {
      const raw = localStorage.getItem(LS_KEY);
      if (raw) {
        const saved = JSON.parse(raw);
        setTarefasFeitas(saved.tarefasFeitas || []);
        setEquityFlag(!!saved.equityFlag);
        if (saved.meta) setMeta(saved.meta);
        else setMeta({ tipo: 'ebitda_mult', valor: 5 });
        setDescricaoEmpresa(saved.descricaoEmpresa || null);
        setVmv(saved.vmv || null);
        setProdutosServicos(saved.produtosServicos || null);
        setMercadoConcorrencia(saved.mercadoConcorrencia || null);
        setSwot(saved.swot || null);
        setEstrategiaModelo(saved.estrategiaModelo || null);
        setMarketingVendas(saved.marketingVendas || null);
        setPlanoOperacional(saved.planoOperacional || null);
        setEstruturaRh(saved.estruturaRh || null);
        setRiscos(saved.riscos || null);
        setEsg(saved.esg || null);
        setProjecoes(saved.projecoes || null);
        setValuation(saved.valuation || null);
        setConclusao(saved.conclusao || null);
        setEstruturaFinSU(saved.estruturaFinSU || null);
        setDocumentoBP(saved.documentoBP || {});
      } else {
        setTarefasFeitas([]);
        setEquityFlag(false);
        setMeta({ tipo: 'ebitda_mult', valor: 5 });
        setDescricaoEmpresa(null);
        setVmv(null);
        setProdutosServicos(null);
        setMercadoConcorrencia(null);
        setSwot(null);
        setEstrategiaModelo(null);
        setMarketingVendas(null);
        setPlanoOperacional(null);
        setEstruturaRh(null);
        setRiscos(null);
        setEsg(null);
        setProjecoes(null);
        setValuation(null);
        setConclusao(null);
        setEstruturaFinSU(null);
        setDocumentoBP({});
      }
    } catch (e) { /* localStorage inacessível — ignora */ }
    // Reset subTela só quando a empresa MUDA (não no mount inicial nem em StrictMode duplicado)
    if (empresaAnteriorRef.current !== empresa?.id) {
      setSubTela('landing');
      empresaAnteriorRef.current = empresa?.id;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [empresa?.id]);

  // Salva progresso a cada mudança
  useEffect(() => {
    try {
      localStorage.setItem(LS_KEY, JSON.stringify({ tarefasFeitas, equityFlag, meta, descricaoEmpresa, vmv, produtosServicos, mercadoConcorrencia, swot, estrategiaModelo, marketingVendas, planoOperacional, estruturaRh, riscos, esg, projecoes, valuation, conclusao, estruturaFinSU, documentoBP }));
    } catch (e) { /* ignora */ }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tarefasFeitas, equityFlag, meta, descricaoEmpresa, vmv, produtosServicos, mercadoConcorrencia, swot, estrategiaModelo, marketingVendas, planoOperacional, estruturaRh, riscos, esg, projecoes, valuation, conclusao, estruturaFinSU, documentoBP]);

  // ── Navegação universal ──────────────────────────────────────────────
  // Todas as 18 tarefas SEMPRE ativas. Tarefa 17 (Materiais de Captação)
  // produz Teaser + Pitch Deck/Apresentação — deliverable padrão do BP.
  // A Tese de Equity (equityFlag) e o tipo de Meta direcionam o TOM dos
  // materiais (comprador · investidor · institucional), não ligam/desligam.
  const proximaTarefa = (numAtual) => {
    const prox = numAtual + 1;
    if (prox > 18) return null;
    return prox;
  };
  const tarefaAnterior = (numAtual) => numAtual - 1;

  const irParaTarefa = (n) => {
    if (n < 1 || n > 20) return;
    setSubTela(`tarefa-${n}`);
  };
  const aprovarEAvancar = (numAtual) => {
    // Validação T4: soma do mix de portfólio deve ser exatamente 100%
    if (numAtual === 4) {
      const portfolioAtual = produtosServicos?.portfolio ?? RASCUNHO_TAREFA_4[empresa?.id]?.portfolio ?? [];
      const soma = portfolioAtual.reduce((s, p) => s + (Number(p.mix) || 0), 0);
      if (soma !== 100) {
        alert(`Não dá para aprovar enquanto a soma do mix do portfólio não fechar 100%.\n\nSoma atual: ${soma}%\nFaltam/excedem: ${100 - soma}%\n\nAjuste os percentuais e tente novamente.`);
        return;
      }
    }
    // Validação T8: soma do mix de canais de aquisição deve ser exatamente 100%
    if (numAtual === 8) {
      const mixAtual = marketingVendas?.canais_mix ?? RASCUNHO_TAREFA_8[empresa?.id]?.canais_mix ?? [];
      const soma = mixAtual.reduce((s, c) => s + (Number(c.peso) || 0), 0);
      if (soma !== 100) {
        alert(`Não dá para aprovar enquanto a soma do mix de canais não fechar 100%.\n\nSoma atual: ${soma}%\nFaltam/excedem: ${100 - soma}%\n\nAjuste os percentuais e tente novamente.`);
        return;
      }
    }
    // Validação T10: todas as funções essenciais do setor devem estar cobertas por pelo menos 1 cargo
    if (numAtual === 10) {
      const setorEmp = empresa?.setor || 'geral';
      const funcoesSetor = FUNCOES_POR_SETOR[setorEmp] || FUNCOES_POR_SETOR['geral'];
      const nos = estruturaRh?.organograma_nos ?? RASCUNHO_TAREFA_10[empresa?.id]?.organograma_nos ?? [];
      const cobertas = new Set();
      nos.forEach(n => (n.funcoes || []).forEach(f => cobertas.add(f)));
      const descobertas = funcoesSetor.filter(f => !cobertas.has(f.id));
      if (descobertas.length > 0) {
        alert(`Não dá para aprovar enquanto houver função essencial sem cargo cobrindo.\n\nFunções descobertas:\n- ${descobertas.map(f => f.label).join('\n- ')}\n\nMarque a função em pelo menos 1 cargo (1 cargo pode cobrir várias funções).`);
        return;
      }
    }
    setTarefasFeitas(prev => prev.includes(numAtual) ? prev : [...prev, numAtual]);
    // Compila a seção e adiciona ao documento BP
    const md = compilarSecao(numAtual, { meta, equityFlag: (modo === 'SU' ? 'sim' : equityFlag), descricaoEmpresa, vmv, produtosServicos, mercadoConcorrencia, swot, estrategiaModelo, marketingVendas, planoOperacional, estruturaRh, riscos, esg, projecoes, valuation, empresa });
    setDocumentoBP(prev => ({ ...prev, [`secao_${numAtual}`]: md }));
    const prox = proximaTarefa(numAtual);
    if (prox) irParaTarefa(prox); else setSubTela('landing');   // depois da 20, volta landing
  };
  const avancar = (numAtual) => {
    const prox = proximaTarefa(numAtual);
    if (prox) irParaTarefa(prox);
  };
  const voltar = (numAtual) => {
    const prev = tarefaAnterior(numAtual);
    if (prev < 1) { setSubTela('landing'); return; }
    irParaTarefa(prev);
  };

  // Landing-page (boas-vindas premium · porta de entrada do BP60M)
  if (subTela === 'landing') {
    return (
      <div>
        <LandingBP60M
          onComecar={() => {
            // Se tem progresso, vai para próxima tarefa não-feita; senão Tarefa 1
            const proxNaoFeita = TAREFAS_BP60M.find(t => !tarefasFeitas.includes(t.id));
            irParaTarefa(proxNaoFeita ? proxNaoFeita.id : 1);
          }}
          onContinuar={() => {
            const proxNaoFeita = TAREFAS_BP60M.find(t => !tarefasFeitas.includes(t.id));
            irParaTarefa(proxNaoFeita ? proxNaoFeita.id : 20);
          }}
          onVerExemplo={() => alert('Ver exemplo ShopMax — em construção')}
          temProgresso={tarefasFeitas.length > 0}
          tarefasFeitas={tarefasFeitas}
          onIrParaTarefa={(n) => irParaTarefa(n)}
          modo={modo}
        />
      </div>
    );
  }

  // ═══════════ Renderização universal das 20 tarefas ═══════════
  // Extrai número da subTela ('tarefa-7' → 7) e monta via TarefaBP60M shell.
  // Conteúdo central é PLACEHOLDER — cada tarefa terá seu conteúdo específico
  // codado depois (round seguinte: uma tarefa por vez).
  const match = /^tarefa-(\d+)$/.exec(subTela);
  if (match) {
    const numero = parseInt(match[1], 10);
    // No modo SU, T13 é a nova "Estruturação Financeira Inicial" e os slots
    // 14..17 correspondem aos antigos 13..16 (Projeções · Valuation · Conclusão · Entrega).
    const tarefasArr = modo === 'SU' ? TAREFAS_BP60M_SU : TAREFAS_BP60M;
    const totalTarefas = tarefasArr.length;
    const tarefa = tarefasArr.find(t => t.id === numero);
    if (!tarefa) { setSubTela('landing'); return null; }
    // Slot equivalente no array do EE (para reuso dos componentes existentes
    // enquanto cada tarefa do SU não recebe personalização própria).
    const numeroEE = modo === 'SU' && numero >= 14 ? numero - 1 : numero;

    // Status visual: aprovado / em-edicao / nao-iniciado
    const status = tarefasFeitas.includes(numero) ? 'aprovado' : 'em-edicao';

    const proxNum = proximaTarefa(numero);
    const ehUltima = (numero === totalTarefas);

    const secoesPreenchidas = Object.keys(documentoBP).length;

    const usarRodapeFixo = true;   // aplicado a todas as 18 tarefas BP60M (exceto landing)
    return (
      <div style={{
        padding: usarRodapeFixo ? '20px 28px 0' : '20px 28px',
        // min-height força o wrapper a ocupar a altura útil da viewport (descontando
        // a faixa superior de 60px com nome da empresa) · faz o rodapé sticky bottom
        // grudar na base mesmo quando o conteúdo é curto. display:flex column +
        // flex:1 no espaço intermediário empurra o rodapé pra base.
        minHeight: usarRodapeFixo ? 'calc(100vh - 60px)' : undefined,
        display: usarRodapeFixo ? 'flex' : undefined,
        flexDirection: usarRodapeFixo ? 'column' : undefined,
        boxSizing: 'border-box',
      }}>
        <TarefaBP60M
          ocultarBotoes={usarRodapeFixo}
          numero={numero}
          total={totalTarefas}
          eyebrow={`Tarefa ${numero} de ${totalTarefas} · ${tarefa.grupoLabel}`}
          titulo={tarefa.titulo}
          subtitulo={tarefa.subtitulo}
          iconeMarca={ICONES_BP60M[tarefa.id]}
          status={status}
          tarefasFeitas={tarefasFeitas}
          podeVoltar={true}
          podeAvancar={!ehUltima && !!proxNum}
          onVoltar={() => voltar(numero)}
          onAvancar={() => avancar(numero)}
          onAprovar={() => aprovarEAvancar(numero)}
          previewCount={secoesPreenchidas}
          onPreview={() => setPreverBP(true)}
          onSalvarRascunho={() => alert(`Rascunho salvo (Tarefa ${numero})`)}
          onEditar={() => alert(`Modo edição da Tarefa ${numero} — em construção`)}
          onRevisar={() => alert(`Revisar Tarefa ${numero} — em construção`)}
        >
          {modo === 'SU' && numero === 13 ? (
            <ConteudoTarefa13EstruturacaoFinanceiraSU
              empresa={empresa}
              estruturaFin={estruturaFinSU}
              setEstruturaFin={setEstruturaFinSU}
              produtosServicos={produtosServicos}
              meta={meta}
            />
          ) : numero === 1 ? (
            <ConteudoTarefa1
              meta={meta} setMeta={setMeta}
              equityFlag={equityFlag} setEquityFlag={setEquityFlag}
              empresa={empresa}
              modo={modo}
            />
          ) : numero === 2 ? (
            <ConteudoTarefa2
              descricaoEmpresa={descricaoEmpresa}
              setDescricaoEmpresa={setDescricaoEmpresa}
              empresa={empresa}
              modo={modo}
            />
          ) : numero === 3 ? (
            <ConteudoTarefa3
              vmv={vmv}
              setVmv={setVmv}
              empresa={empresa}
              modo={modo}
            />
          ) : numero === 4 ? (
            <ConteudoTarefa4
              produtosServicos={produtosServicos}
              setProdutosServicos={setProdutosServicos}
              empresa={empresa}
              modo={modo}
            />
          ) : numero === 5 ? (
            <ConteudoTarefa5
              mercadoConcorrencia={mercadoConcorrencia}
              setMercadoConcorrencia={setMercadoConcorrencia}
              empresa={empresa}
              modo={modo}
            />
          ) : numero === 6 ? (
            <ConteudoTarefa6
              swot={swot}
              setSwot={setSwot}
              empresa={empresa}
              modo={modo}
            />
          ) : numero === 7 ? (
            <ConteudoTarefa7
              estrategiaModelo={estrategiaModelo}
              setEstrategiaModelo={setEstrategiaModelo}
              empresa={empresa}
              modo={modo}
            />
          ) : numero === 8 ? (
            <ConteudoTarefa8
              marketingVendas={marketingVendas}
              setMarketingVendas={setMarketingVendas}
              empresa={empresa}
              modo={modo}
            />
          ) : numero === 9 ? (
            <ConteudoTarefa9
              planoOperacional={planoOperacional}
              setPlanoOperacional={setPlanoOperacional}
              empresa={empresa}
              modo={modo}
            />
          ) : numero === 10 ? (
            <ConteudoTarefa10
              estruturaRh={estruturaRh}
              setEstruturaRh={setEstruturaRh}
              empresa={empresa}
              modo={modo}
            />
          ) : numero === 11 ? (
            <ConteudoTarefa11
              riscos={riscos}
              setRiscos={setRiscos}
              empresa={empresa}
              modo={modo}
            />
          ) : numero === 12 ? (
            <ConteudoTarefa12
              esg={esg}
              setEsg={setEsg}
              empresa={empresa}
              modo={modo}
            />
          ) : numeroEE === 13 ? (
            <ConteudoTarefa13
              projecoes={projecoes}
              setProjecoes={setProjecoes}
              empresa={empresa}
              modo={modo}
              estruturaFin={estruturaFinSU}
              produtosServicos={produtosServicos}
              meta={meta}
            />
          ) : numeroEE === 14 ? (
            <ConteudoTarefa14
              valuation={valuation}
              setValuation={setValuation}
              projecoes={projecoes}
              empresa={empresa}
              modo={modo}
              estruturaFin={estruturaFinSU}
              produtosServicos={produtosServicos}
              meta={meta}
              parametros={parametros}
            />
          ) : numeroEE === 15 ? (
            <ConteudoTarefa15
              meta={meta}
              equityFlag={equityFlag}
              descricaoEmpresa={descricaoEmpresa}
              vmv={vmv}
              produtosServicos={produtosServicos}
              mercadoConcorrencia={mercadoConcorrencia}
              swot={swot}
              estrategiaModelo={estrategiaModelo}
              marketingVendas={marketingVendas}
              estruturaRh={estruturaRh}
              projecoes={projecoes}
              valuation={valuation}
              conclusao={conclusao}
              setConclusao={setConclusao}
              empresa={empresa}
              modo={modo}
              estruturaFin={estruturaFinSU}
              parametros={parametros}
            />
          ) : numeroEE === 16 ? (
            <ConteudoTarefa16
              meta={meta}
              equityFlag={equityFlag}
              empresa={empresa}
              vmv={vmv}
              produtosServicos={produtosServicos}
              mercadoConcorrencia={mercadoConcorrencia}
              swot={swot}
              estrategiaModelo={estrategiaModelo}
              marketingVendas={marketingVendas}
              estruturaRh={estruturaRh}
              projecoes={projecoes}
              valuation={valuation}
              modo={modo}
              estruturaFin={estruturaFinSU}
              parametros={parametros}
            />
          ) : (
            <div style={{
              background: '#F5F5F1', border: '2px dashed #E5E7EB',
              borderRadius: 0, padding: '40px 24px', textAlign: 'center',
              color: G.txt2, fontSize: 13, fontStyle: 'italic',
            }}>
              <div style={{
                fontSize: 15, fontWeight: 700, color: G.txt1,
                marginBottom: 6, fontStyle: 'normal',
              }}>
                Conteúdo da Tarefa {numero} — em construção
              </div>
              Esqueleto da jornada pronto · conteúdo específico de cada tarefa
              será adicionado no próximo round (uma tarefa por vez).
              <div style={{ marginTop: 14, fontSize: 11, color: G.txt2,
                fontStyle: 'normal' }}>
                <strong>Grupo:</strong> {tarefa.grupoLabel}
              </div>
            </div>
          )}
        </TarefaBP60M>

        {/* RODAPÉ FIXO (teste UX em SWOT) — botões saem do card e ficam sempre visíveis */}
        {usarRodapeFixo ? (
          <div style={{
            position: 'sticky', bottom: 0, zIndex: 10,
            background: G.card,
            borderTop: `1px solid ${G.borda}`,
            padding: '14px 28px',
            margin: '14px -28px 0',
            marginTop: 'auto',   // empurra rodapé pra base quando conteúdo é curto
            display: 'flex', justifyContent: 'space-between',
            alignItems: 'center', gap: 14,
          }}>
            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={() => voltar(numero)}
                style={{ ...BP60M_TOKENS.botoes.base, ...BP60M_TOKENS.botoes.primario,
                  background: G.laranja, color: '#FFF', border: `2px solid ${G.laranja}` }}>
                ← Anterior
              </button>
              <button onClick={() => setSubTela('landing')}
                style={{ ...BP60M_TOKENS.botoes.base, ...BP60M_TOKENS.botoes.editar,
                  border: `2px solid ${G.laranja}`, color: G.laranja }}>
                ↩ Voltar à Landing-page
              </button>
            </div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <button onClick={() => alert(`Rascunho salvo (Tarefa ${numero})`)}
                style={{ ...BP60M_TOKENS.botoes.base, ...BP60M_TOKENS.botoes.rascunho }}>
                Salvar Rascunho
              </button>
              <button onClick={() => alert(`Modo edição da Tarefa ${numero} — em construção`)}
                style={{ ...BP60M_TOKENS.botoes.base, ...BP60M_TOKENS.botoes.editar }}>
                Editar
              </button>
              <button onClick={() => alert(`Revisar Tarefa ${numero} — em construção`)}
                style={{ ...BP60M_TOKENS.botoes.base, ...BP60M_TOKENS.botoes.revisar }}>
                Revisar
              </button>
              <button onClick={() => aprovarEAvancar(numero)}
                style={{ ...BP60M_TOKENS.botoes.base, ...BP60M_TOKENS.botoes.aprovar }}>
                ✓ Aprovar
              </button>
            </div>
            <button onClick={() => avancar(numero)}
              disabled={ehUltima || !proxNum}
              style={{ ...BP60M_TOKENS.botoes.base, ...BP60M_TOKENS.botoes.primario,
                ...(ehUltima || !proxNum ? { cursor: 'not-allowed', opacity: 0.45 } : {}) }}>
              Avançar →
            </button>
          </div>
        ) : (
          /* Atalho dev (somente quando NÃO está usando rodapé fixo): link textual no final */
          <div style={{ marginTop: 14, textAlign: 'center' }}>
            <button onClick={() => setSubTela('landing')}
              style={{ background: 'transparent', color: G.txt2,
                border: 'none', fontSize: 11, cursor: 'pointer',
                letterSpacing: '0.06em', textTransform: 'uppercase' }}>
              ⬅ Voltar à Landing-page
            </button>
          </div>
        )}

        {/* ═══ Modal: Preview do Business Plan compilado ═══════════════ */}
        {preverBP && (
          <div onClick={() => setPreverBP(false)}
            style={{
              position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
              background: 'rgba(0,0,0,0.5)', zIndex: 100,
              display: 'flex', justifyContent: 'center', alignItems: 'center',
              padding: 24,
            }}>
            <div onClick={e => e.stopPropagation()}
              style={{
                background: '#FFF', maxWidth: 820, width: '100%',
                maxHeight: '90vh', overflow: 'auto',
                borderRadius: 0, padding: '28px 36px',
                boxShadow: '0 10px 40px rgba(0,0,0,0.25)',
                position: 'relative',
              }}>
              {/* Header do modal */}
              <div style={{ display: 'flex', justifyContent: 'space-between',
                alignItems: 'flex-start', marginBottom: 18, gap: 14,
                borderBottom: `1px solid ${G.borda}`, paddingBottom: 14 }}>
                <div>
                  <div style={{ fontSize: 11, fontWeight: 700,
                    letterSpacing: '0.10em', textTransform: 'uppercase',
                    color: G.verde, marginBottom: 4 }}>
                    Business Plan · {empresa?.nome || 'empresa'}
                  </div>
                  <div style={{ fontSize: 20, fontWeight: 800, color: G.txt1 }}>
                    Preview do documento compilado
                  </div>
                  <div style={{ ...TIPO.subtitulo, margin: '4px 0 0' }}>
                    {Object.keys(documentoBP).length} de 18 seções aprovadas
                    · cada aprovação adiciona uma seção · Tarefa 18 entrega o PDF final
                  </div>
                </div>
                <button onClick={() => setPreverBP(false)}
                  style={{ background: 'transparent', border: 'none',
                    fontSize: 24, cursor: 'pointer', color: G.txt2,
                    padding: 0, lineHeight: 1 }} title="Fechar" aria-label="Fechar"><IconeFechar /></button>
              </div>
              {/* Conteúdo: seções compiladas em ordem */}
              {Object.keys(documentoBP).length === 0 ? (
                <div style={{ padding: '40px 20px', textAlign: 'center',
                  color: G.txt2, fontSize: 14, fontStyle: 'italic' }}>
                  Nenhuma seção aprovada ainda. Aprove uma Tarefa para começar
                  a montar o Business Plan.
                </div>
              ) : (
                (() => {
                  // Monta dicionário de gráficos disponíveis com os dados atuais
                  const rascT10 = RASCUNHO_TAREFA_10[empresa?.id] || {};
                  const orgNos  = (estruturaRh?.organograma_nos) ?? rascT10.organograma_nos ?? [];
                  const hires   = (estruturaRh?.hires_plano)     ?? rascT10.hires_plano     ?? [];
                  const rascT4 = RASCUNHO_TAREFA_4[empresa?.id] || {};
                  const portfolio = (produtosServicos?.portfolio) ?? rascT4.portfolio ?? [];
                  const rascT5 = RASCUNHO_TAREFA_5[empresa?.id] || {};
                  const tamId = (mercadoConcorrencia?.tam_intervalo) ?? rascT5.tam_intervalo;
                  const samId = (mercadoConcorrencia?.sam_intervalo) ?? rascT5.sam_intervalo;
                  const somId = (mercadoConcorrencia?.som_intervalo) ?? rascT5.som_intervalo;
                  const rascT6 = RASCUNHO_TAREFA_6[empresa?.id] || { forcas: [], fraquezas: [], oportunidades: [], ameacas: [] };
                  const candidatosOuMarcados = (quad) => swot?.[quad]
                    ?? rascT6[quad].map(t => ({ texto: t, marcado: true, fonte: 'ia' }));
                  const rascT8 = RASCUNHO_TAREFA_8[empresa?.id] || {};
                  const canaisMix = (marketingVendas?.canais_mix) ?? rascT8.canais_mix ?? [];
                  const funilChars = (marketingVendas?.funil_chars) ?? rascT8.funil_chars ?? {};
                  const graficosBP = {
                    t1_trajetoria:   <GraficoTrajetoriaMeta tipoId={meta?.tipo} valor={meta?.valor}/>,
                    t4_mix_receita:  <GraficoPizzaMix items={portfolio}/>,
                    t5_tam_sam_som:  <GraficoTAMSAMSOM tamId={tamId} samId={samId} somId={somId}/>,
                    t6_swot:         <GraficoSWOT
                                      forcas={candidatosOuMarcados('forcas')}
                                      fraquezas={candidatosOuMarcados('fraquezas')}
                                      oportunidades={candidatosOuMarcados('oportunidades')}
                                      ameacas={candidatosOuMarcados('ameacas')}/>,
                    t7_unit_econ:    <GraficoUnitEconomics
                                      respostas={(estrategiaModelo?.unit_econ_respostas)
                                        ?? (RASCUNHO_TAREFA_7[empresa?.id]?.unit_econ_respostas) ?? {}}/>,
                    t8_canais:       <GraficoCanaisMix canaisMix={canaisMix}/>,
                    t8_funil:        <GraficoFunilVendas funilChars={funilChars}/>,
                    t10_organograma: <GraficoOrganograma nos={orgNos}/>,
                    t10_timeline_rh: <GraficoTimelineRH hires={hires}/>,
                    t13_evolucao:    (() => {
                      const r = RASCUNHO_TAREFA_13[empresa?.id] || {};
                      const a = calcularProjecao(
                        projecoes?.receita_inicial ?? r.receita_inicial,
                        projecoes?.crescimento_id  ?? r.crescimento_id,
                        projecoes?.cpv_id          ?? r.cpv_id,
                        projecoes?.despesas        ?? r.despesas,
                        projecoes?.capex_id        ?? r.capex_id,
                      );
                      return <GraficoProjecao5Anos anos={a}/>;
                    })(),
                    t13_cenarios:    (() => {
                      const r = RASCUNHO_TAREFA_13[empresa?.id] || {};
                      const a = calcularProjecao(
                        projecoes?.receita_inicial ?? r.receita_inicial,
                        projecoes?.crescimento_id  ?? r.crescimento_id,
                        projecoes?.cpv_id          ?? r.cpv_id,
                        projecoes?.despesas        ?? r.despesas,
                        projecoes?.capex_id        ?? r.capex_id,
                      );
                      return <GraficoCenarios5 anoFinal={a[5]}/>;
                    })(),
                    ...(() => {
                      const r13 = RASCUNHO_TAREFA_13[empresa?.id] || {};
                      const r14 = RASCUNHO_TAREFA_14[empresa?.id] || {};
                      const anos14 = calcularProjecao(
                        projecoes?.receita_inicial ?? r13.receita_inicial,
                        projecoes?.crescimento_id  ?? r13.crescimento_id,
                        projecoes?.cpv_id          ?? r13.cpv_id,
                        projecoes?.despesas        ?? r13.despesas,
                        projecoes?.capex_id        ?? r13.capex_id,
                      );
                      const waccId   = valuation?.wacc_id   ?? r14.wacc_id;
                      const gId      = valuation?.g_id      ?? r14.g_id;
                      const metodoId = valuation?.metodo_id ?? r14.metodo_id;
                      const mult     = valuation?.multiplo  ?? r14.multiplo;
                      const vCalc = calcularValuation(anos14, waccId, gId, metodoId, mult);
                      const sens  = calcularSensibilidade(anos14, metodoId, mult);
                      return {
                        t14_composicao:          <GraficoComposicaoEV valuation={vCalc}/>,
                        t14_cascata:             <GraficoCascataDCF valuation={vCalc}/>,
                        t14_sensibilidade:       <GraficoSensibilidadeWACCg matriz={sens}/>,
                        t14_comparativo_metodos: <GraficoComparativoMetodos valuation={vCalc} multiplo={mult}/>,
                      };
                    })(),
                  };
                  return Array.from({ length: 18 }, (_, i) => i + 1)
                    .filter(n => documentoBP[`secao_${n}`])
                    .map(n => (
                      <MarkdownPreview key={n} texto={documentoBP[`secao_${n}`]} graficos={graficosBP}/>
                    ));
                })()
              )}
              {/* Rodapé com nota */}
              <div style={{ marginTop: 24, paddingTop: 14,
                borderTop: `1px solid ${G.borda}`, fontSize: 11,
                color: G.txt2, fontStyle: 'italic', lineHeight: 1.5 }}>
                Versão compilada heurística · IA real (Auditoria Fase 3) vai
                enriquecer a narrativa e gerar PDF/PPTX/XLSX na Tarefa 18.
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  return null;
};

// ═══════════════════════════════════════════════════════════════════════
// <TelaBP60MSU /> — wrapper da modalidade Startup do Business Plan 60M.
// Renderiza o mesmo TelaBP60M com prop `modo='SU'` · ponto de partida
// idêntico ao da Empresa Estabelecida. Customizações tarefa por tarefa
// vão sendo feitas conforme decisão tomada em 26/06/2026 (Opção 1 ·
// duas modalidades distintas no menu lateral).
// ═══════════════════════════════════════════════════════════════════════
const TelaBP60MSU = ({ empresa, onAbrirConfig, parametros }) => (
  <TelaBP60M empresa={empresa} parametros={parametros} onAbrirConfig={onAbrirConfig} modo="SU" />
);

// Tela Alertas
// ═══════════════════════════════════════════════════════════════════════
// Reforma Tributária · Cenários de Transição (31/08). A tela é uma LENTE:
// tudo vem calculado do backend (/reforma), que por sua vez lê a tabela de
// parâmetros legais com fonte linha a linha. Nada aqui grava estado.
// Mockup aprovado: docs/mockups/MOCKUP_ABA_REFORMA_TRIBUTARIA v1 2026-08-31.html
// ═══════════════════════════════════════════════════════════════════════
const TelaReformaTributaria = ({ empresa }) => {
  const [dados, setDados] = useState(null);
  const [erro, setErro] = useState(null);
  const [carregando, setCarregando] = useState(true);
  // Nasce VAZIO de propósito: número de sócios não tem base de estimativa, e o
  // que não se sabe sem condições de estimar não se chuta (regra do Fabio,
  // 31/08). Sem ele o imposto mostra "—" e pede o dado.
  const [nSocios, setNSocios] = useState('');

  useEffect(() => {
    if (!empresa?.id) return;
    setDados(null); setErro(null); setCarregando(true);
    let vivo = true;
    (async () => {
      try {
        const q = nSocios ? `?n_socios=${nSocios}` : '';
        const r = await fetch(`${API}/reforma/${empresa.id}${q}`);
        const j = await r.json();
        if (!r.ok) throw new Error(j.detail || 'Falha ao montar os cenários.');
        if (vivo) setDados(j);
      } catch (e) { if (vivo) setErro(e.message); }
      if (vivo) setCarregando(false);
    })();
    return () => { vivo = false; };
  }, [empresa?.id, nSocios]);

  const pct = (v) => (v * 100).toFixed(2).replace('.', ',') + '%';
  const pts = (v) => (v > 0 ? '+' : '−') + Math.abs(v * 100).toFixed(2).replace('.', ',') + ' ponto' + (Math.abs(v * 100) >= 1.995 ? 's' : '');
  const dinheiro = (v) => 'R$ ' + Number(v || 0).toLocaleString('pt-BR',
    { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const rotulo = { fontSize: 10, fontWeight: 800, color: G.txt2,
    letterSpacing: '0.08em', textTransform: 'uppercase' };
  const cartao = { border: `1px solid ${G.borda}`, borderRadius: 0, padding: '12px 14px', background: '#FFF' };
  // Grade de 3 faixas: cabeçalho 44 (ano no topo, alinhado ao título do
  // gráfico ao lado; selo ESTIMATIVA centrado abaixo do ano) · miolo 1fr com a
  // alíquota no CENTRO do card nos dois eixos · apoio 44. Faixas iguais nos 5
  // cards = régua reta (Fabio, 31/08).
  const cartaoKPI = { ...cartao, display: 'grid',
    gridTemplateRows: '44px 1fr 60px', justifyItems: 'center', textAlign: 'center' };
  // Série do gráfico de variação: Hoje + cenários, na carga LÍQUIDA — a mesma
  // dos cards ao lado (a carga de decisão, não a nominal destacada).
  const serieCargas = dados?.tem_analise
    ? [{ rot: 'Hoje', val: dados.cenarios?.[0]?.aliquota_atual_liquida || 0 },
       ...(dados.cenarios || []).map((c) => ({ rot: String(c.ano),
         val: c.aliquota_cenario_liquida }))]
    : [];
  // Escala apertada na faixa dos dados: eixo a partir do zero achatava a
  // linha; o desnível de subida e descida entre os anos tem de ser sensível
  // (Fabio, 31/08). Os valores reais estão escritos sobre cada ponto.
  const valsCarga = serieCargas.map((s) => s.val);
  const maxCarga = Math.max(...valsCarga, 0.0001);
  const minCarga = Math.min(...valsCarga, maxCarga);
  const faixaCarga = (maxCarga - minCarga) || maxCarga * 0.1;
  const pisoCarga = Math.max(0, minCarga - faixaCarga * 0.3);
  const tetoCarga = maxCarga + faixaCarga * 0.45;

  const LINHA_TEMPO = [
    { ano: '2026', oq: 'Fase de teste: CBS 0,9% + IBS 0,1%, dispensáveis cumprindo as obrigações. Começa a regra dos dividendos.', ativo: true },
    { ano: '2027', oq: 'CBS plena entra; PIS/Cofins acabam; IPI zerado fora da Zona Franca.' },
    { ano: '2029', oq: 'ICMS e ISS começam a encolher: 90% neste ano, caindo 10 pontos por ano.' },
    { ano: '2032', oq: 'Último ano do ICMS/ISS, a 60% da alíquota original.' },
    { ano: '2033', oq: 'Sistema cheio: só CBS + IBS.' },
  ];

  return (
    <div style={{ padding: '24px 28px', position: 'relative', overflow: 'hidden' }}>
      <CarimboAIdvisor />
      <div style={{ marginBottom: 16 }}>
        <div style={{ position: 'relative', display: 'inline-block' }}>
          <h2 style={{ fontSize: 20, fontWeight: 800, letterSpacing: '-0.01em', margin: 0 }}>
            Reforma Tributária
          </h2>
          <MarcaDaguaTitulo>
            <path d="M21 12a9 9 0 1 1-9-9c2.52 0 4.93 1 6.74 2.74L21 8"/>
            <path d="M21 3v5h-5"/>
            <line x1="15" y1="9" x2="9" y2="15"/>
            <circle cx="9.5" cy="9.5" r="1.5"/>
            <circle cx="14.5" cy="14.5" r="1.5"/>
          </MarcaDaguaTitulo>
        </div>
        <p style={{ fontSize: 12.5, color: G.txt2, margin: '4px 0 0' }}>
          o que muda para a sua empresa, no seu regime
          {dados?.regime ? ` (${dados.regime})` : ''}
        </p>
      </div>

      {/* Quadros de abertura no padrão das demais abas (pedido do Fabio,
          31/08); a frase-síntese da reforma virou o corpo do "O que é". O
          marginTop abre passagem para o carimbo do RUNGOAL AIDVISOR: esta tela tem
          faixa de largura total logo sob o subtítulo, e sem o respiro o selo
          encavalava no texto. */}
      <div style={{ marginTop: 30 }}>
        <QuadrosIntro
          titulo="a Reforma Tributária"
          oQueE={<>Cinco tributos sobre o consumo (PIS, Cofins, IPI, ICMS e ISS)
            serão substituídos, aos poucos, por dois — a <strong>CBS</strong>{' '}
            (federal) e o <strong>IBS</strong> (estados e municípios) —, com uma
            regra nova que importa muito: <strong>quase tudo o que a empresa
            compra gera crédito</strong> para abater do imposto a pagar.</>}
          oQueFaz={<>Acompanhe a linha do tempo, o impacto <strong>medido nos
            seus números</strong> e a regra dos R$ 50 mil da distribuição de
            lucros. Nas abas DRE e Resumo Mensal, o seletor{' '}
            <strong>Regime</strong> mostra os demonstrativos sob cada ano do
            cenário.</>}
        />
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12, marginBottom: 16 }}>
        <div style={cartao}>
          <div style={{ ...rotulo, marginBottom: 6 }}>1 · O que acaba</div>
          <div style={{ fontSize: 12.5, lineHeight: 1.5 }}>PIS e Cofins deixam de existir em 2027; o IPI é zerado (fora da Zona Franca); ICMS e ISS encolhem ano a ano até acabarem em 2033.</div>
        </div>
        <div style={cartao}>
          <div style={{ ...rotulo, marginBottom: 6 }}>2 · O que entra</div>
          <div style={{ fontSize: 12.5, lineHeight: 1.5 }}>CBS a partir de 2027 e IBS assumindo o lugar do ICMS/ISS entre 2029 e 2032. Alíquota somada de referência: <strong>26,5%</strong> (estimativa do Ministério da Fazenda) — estimativas mais recentes apontam até 27,9%. O número definitivo é fixado por resolução do Senado até 15/12/2026.</div>
        </div>
        <div style={cartao}>
          <div style={{ ...rotulo, marginBottom: 6 }}>3 · O que muda para o sócio</div>
          <div style={{ fontSize: 12.5, lineHeight: 1.5 }}>Desde 2026, distribuição acima de R$ 50.000,00 por mês, por sócio, paga 10% de imposto na fonte — sobre o valor inteiro do mês.</div>
        </div>
      </div>

      {/* Linha do tempo — células no fundo padrão dos cards (pedido do Fabio,
          31/08); o filete superior colorido continua marcando o ano ativo. */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 12, marginBottom: 20 }}>
        {LINHA_TEMPO.map((m) => (
          <div key={m.ano} style={{ ...cartao, padding: '10px 12px',
            borderTop: `3px solid ${m.ativo ? G.laranja : G.verde}` }}>
            <div style={{ fontFamily: "'IBM Plex Mono', monospace", fontWeight: 600,
              fontSize: 13, color: m.ativo ? G.laranja : G.verde }}>{m.ano}</div>
            <div style={{ fontSize: 11, color: G.txt2, lineHeight: 1.4, marginTop: 3 }}>{m.oq}</div>
          </div>
        ))}
      </div>

      {carregando && (
        <div style={{ fontSize: 12.5, color: G.txt2, fontStyle: 'italic' }}>
          Montando os cenários da sua empresa…
        </div>
      )}
      {erro && (
        <div style={{ padding: '10px 14px', background: G.vermelhoL,
          borderLeft: `3px solid ${G.vermelho}`, borderRadius: 0, fontSize: 12.5 }}>
          <strong>Cenários indisponíveis.</strong> {erro}
        </div>
      )}

      {/* Simples Nacional: a escolha é outra — permanecer × aderir */}
      {dados?.eh_simples && dados?.simples && (
        <>
          <div style={{ fontSize: 13, fontWeight: 800, letterSpacing: '0.06em',
            textTransform: 'uppercase', color: G.laranja, margin: '4px 0 10px' }}>
            A sua empresa é do Simples — a decisão é outra
          </div>
          <div style={{ ...cartao, fontSize: 12.5, lineHeight: 1.55 }}>
            {dados.simples.mensagem}
            <div style={{ marginTop: 8, fontWeight: 700, color: G.verde }}>
              {dados.simples.prazo_opcao}
            </div>
          </div>
        </>
      )}

      {/* Impacto medido — só com balancete anual e fora do Simples */}
      {dados?.tem_analise && (
        <>
          <div style={{ fontSize: 13, fontWeight: 800, letterSpacing: '0.06em',
            textTransform: 'uppercase', color: G.laranja, margin: '4px 0 10px' }}>
            Impacto na carga sobre as vendas — medido nos seus números
          </div>
          {/* Cards à esquerda, gráfico à direita (ordem definida pelo Fabio,
              31/08). Cards homogêneos: rótulo curto · valor · uma linha de
              apoio — a explicação da carga cheia desceu para as premissas. */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12,
            alignItems: 'stretch' }}>
          {/* minmax(0,1fr): trava as 5 colunas na MESMA largura — com 1fr puro
              o conteúdo mínimo (selo, texto) alargava umas colunas. */}
          <div style={{ display: 'grid',
            gridTemplateColumns: `repeat(${1 + (dados.cenarios?.length || 0)}, minmax(0, 1fr))`,
            gap: 12 }}>
            <div style={cartaoKPI}>
              <div style={{ ...rotulo, lineHeight: 1, color: G.txt1 }}>Hoje</div>
              <div style={{ display: 'flex', alignItems: 'center',
                fontFamily: "'IBM Plex Mono', monospace", fontWeight: 600,
                fontSize: 17, color: G.laranja }}>
                {pct(dados.cenarios?.[0]?.aliquota_atual_liquida || 0)}
              </div>
              <div style={{ fontSize: 10.5, color: G.txt2, lineHeight: 1.35 }}>
                líquida de créditos · balancete {dados.ano_base}
              </div>
            </div>
            {(dados.cenarios || []).map((c) => {
              const melhora = c.delta_liquido < 0;
              const cor = melhora ? G.verde : G.vermelho;
              return (
                <div key={c.ano} style={cartaoKPI}>
                  <div>
                    <div style={{ ...rotulo, lineHeight: 1, color: G.txt1 }}>{c.ano}</div>
                    {c.tem_estimativa && (
                      <div style={{ marginTop: 2 }}>
                        <span style={{ fontSize: 9, background: '#FFFBEB',
                          color: '#B45309', border: '1px solid #B45309', borderRadius: 0,
                          padding: '1px 5px' }}>ESTIMATIVA</span>
                      </div>
                    )}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center',
                    fontFamily: "'IBM Plex Mono', monospace", fontWeight: 600,
                    fontSize: 17, color: G.laranja }}>
                    {pct(c.aliquota_cenario_liquida)}
                  </div>
                  <div style={{ fontSize: 10.5, color: cor, lineHeight: 1.35 }}>
                    {pts(c.delta_liquido)}{melhora ? ' · melhora' : ''}
                  </div>
                </div>
              );
            })}
          </div>
          {/* Gráfico em linha no padrão do painel TENDÊNCIA do Dashboard
              (Recharts · AreaChart monotone com gradiente), valores anuais
              visíveis sobre cada ponto. */}
          <div style={{ ...cartao, display: 'flex', flexDirection: 'column' }}>
            <div style={rotulo}>Variação da carga líquida sobre as vendas</div>
            <div style={{ flex: 1, minHeight: 138, marginTop: 4 }}>
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={serieCargas}
                  margin={{ top: 24, right: 26, bottom: 0, left: 26 }}>
                  <defs>
                    <linearGradient id="gCargaRT" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor={G.verde} stopOpacity={0.18}/>
                      <stop offset="100%" stopColor={G.verde} stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <XAxis dataKey="rot" tick={{ fontSize: 11, fill: '#7F8C8D' }}
                    axisLine={{ stroke: '#E5E7EB' }} tickLine={false}
                    interval={0} padding={{ left: 6, right: 6 }}/>
                  <YAxis hide domain={[pisoCarga, tetoCarga]}/>
                  <Area type="monotone" dataKey="val" stroke={G.verde}
                    strokeWidth={2.5} fill="url(#gCargaRT)"
                    dot={{ r: 4, fill: G.verde, strokeWidth: 0 }}
                    isAnimationActive={false}
                    label={(p) => (
                      <text key={`rt-${p.index}`} x={p.x} y={p.y - 11}
                        textAnchor="middle" fontSize={12.5} fontWeight={700}
                        fontFamily="'IBM Plex Mono', monospace" fill={G.laranja}>
                        {pct(p.value)}
                      </text>
                    )}/>
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>
          </div>
          {/* Seção com título laranja FORA do card, como as demais da tela
              (Fabio, 31/08): são as hipóteses por trás do cálculo, com o
              mesmo destaque do resto. */}
          <div style={{ fontSize: 13, fontWeight: 800, letterSpacing: '0.06em',
            textTransform: 'uppercase', color: G.laranja, margin: '18px 0 10px' }}>
            Como esta conta foi feita — leia antes de decidir
          </div>
          <div style={{ ...cartao, padding: '12px 16px', fontSize: 11.5,
            color: G.txt1, lineHeight: 1.55 }}>
            <ol style={{ marginLeft: 16, marginTop: 0, marginBottom: 0 }}>
              <li>A carga cheia destacada hoje é {pct(dados.cenarios?.[0]?.aliquota_atual_bruta || 0)}; os créditos medidos a abatem até a carga líquida de {pct(dados.cenarios?.[0]?.aliquota_atual_liquida || 0)} do card Hoje.</li>
              <li>Créditos atuais de ICMS ({dinheiro(dados.decomposicao?.creditos_icms)}) e IPI ({dinheiro(dados.decomposicao?.creditos_ipi)}) medidos no seu balancete de {dados.ano_base}.</li>
              <li>Crédito futuro de CBS/IBS estimado só sobre o CMV ({pct(dados.decomposicao?.cmv_fracao || 0)} da receita) — energia, fretes e serviços creditáveis ficaram de fora, então o número novo tende a ser MENOR que o mostrado.</li>
              <li>Alíquota de referência de 26,5% (17,7% de IBS + 8,8% de CBS) é a estimativa do Ministério da Fazenda. Referências mais recentes são maiores: o Comitê Gestor do IBS adotou 18,7% para o IBS em projeções (Resolução CGIBS nº 14, de 29/07/2026) e estimou 27,91% para o IVA total. O número definitivo será fixado por resolução do Senado até 15/12/2026, com base em cálculo homologado pelo TCU — <strong>o cenário tende a piorar, não a melhorar</strong>.</li>
              <li>2026 não muda o caixa: as alíquotas de teste são dispensáveis cumprindo as obrigações acessórias.</li>
              <li>IRPJ, CSLL e encargos de folha não são alterados por esta reforma; por isso o cenário não os recalcula.</li>
            </ol>
          </div>
        </>
      )}
      {dados && !dados.tem_analise && !dados.eh_simples && (
        <div style={{ ...cartao, fontSize: 12.5, lineHeight: 1.5 }}>
          A análise medida nos números da empresa fica disponível quando houver{' '}
          <strong>balancete anual</strong> processado na Fonte Contábil — é dele
          que saem a carga atual e os créditos.
        </div>
      )}

      {/* Capital de giro na transição — tratado AQUI, à parte das medidas de
          compensação do card da DRE (decisão do Fabio, 31/08); veio para antes
          da distribuição de lucros e ganhou o fundo padrão dos cards (31/08). */}
      <div style={{ fontSize: 13, fontWeight: 800, letterSpacing: '0.06em',
        textTransform: 'uppercase', color: G.laranja, margin: '18px 0 10px' }}>
        Capital de giro na transição
      </div>
      <div style={{ ...cartao, padding: '12px 16px', fontSize: 12.5, lineHeight: 1.55 }}>
        No sistema novo, parte do imposto será recolhida <strong>no momento do
        pagamento</strong> (o chamado split payment): o valor do tributo é
        separado na liquidação, em vez de passar pelo caixa da empresa até a
        data de recolher. O efeito não é de resultado — é de <strong>fluxo</strong>:
        o dinheiro do imposto deixa de "dormir" no caixa entre a venda e o
        vencimento da guia. Quem hoje usa esse intervalo como fôlego deve
        renegociar prazos com fornecedores <strong>antes</strong> de 2027, quando
        a CBS plena chega. O desenho operacional ainda depende de regulamentação
        — este quadro será atualizado junto com a tabela de parâmetros.
      </div>

      {/* Dividendos — todos os regimes */}
      {dados?.dividendos && !dados.dividendos.erro && (
        <>
          <div style={{ fontSize: 13, fontWeight: 800, letterSpacing: '0.06em',
            textTransform: 'uppercase', color: G.laranja, margin: '18px 0 10px' }}>
            Distribuição de lucros — a regra dos R$ 50 mil por mês
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '220px 220px 220px', gap: 14 }}>
            {(dados.dividendos.meses || []).slice(-1).map((m) => {
              // Sem o número de sócios não há "isento" nem "tributável": a
              // classificação depende da divisão. Só o FATO medido aparece.
              const neutro = dados.dividendos.aguardando_socios;
              const cor = neutro ? G.txt2 : (m.acima_do_limite ? G.vermelho : G.verde);
              const fundo = neutro ? G.fundo : (m.acima_do_limite ? G.vermelhoL : G.verdeLight);
              const bordaC = neutro ? G.borda : cor;
              return (
                <div key={m.mes}>
                  <div style={{ ...rotulo, marginBottom: 4 }}>Distribuído em {m.mes}</div>
                  <div style={{ height: 36, display: 'flex', alignItems: 'center',
                    justifyContent: 'flex-end', padding: '0 12px', borderRadius: 0,
                    fontFamily: "'IBM Plex Mono', monospace", fontWeight: 600, fontSize: 13.5,
                    border: `1px solid ${bordaC}`, background: fundo, color: cor }}>
                    {dinheiro(m.distribuido)}
                  </div>
                  <div style={{ fontSize: 10.5, color: G.txt2, marginTop: 4 }}>
                    {neutro ? 'medido no balancete — última distribuição do acervo'
                      : m.acima_do_limite ? 'acima do limite: paga 10% sobre o TOTAL do mês'
                        : 'dentro do limite mensal — isento'}
                  </div>
                </div>
              );
            })}
            <div>
              <div style={{ ...rotulo, marginBottom: 4 }}>Imposto no cenário</div>
              {dados.dividendos.aguardando_socios ? (
                <>
                  <div style={{ height: 36, display: 'flex', alignItems: 'center',
                    justifyContent: 'flex-end', padding: '0 12px', borderRadius: 0,
                    fontFamily: "'IBM Plex Mono', monospace", fontWeight: 600,
                    fontSize: 13.5, border: `1px solid ${G.borda}`,
                    background: G.fundo, color: G.txt2 }}>—</div>
                  <div style={{ fontSize: 10.5, color: G.txt2, marginTop: 4 }}>
                    informe o número de sócios para calcular
                  </div>
                </>
              ) : (
                <>
                  <div style={{ height: 36, display: 'flex', alignItems: 'center',
                    justifyContent: 'flex-end', padding: '0 12px', borderRadius: 0,
                    fontFamily: "'IBM Plex Mono', monospace", fontWeight: 600, fontSize: 13.5,
                    border: `1px solid ${dados.dividendos.imposto_cenario > 0 ? G.vermelho : G.verde}`,
                    background: dados.dividendos.imposto_cenario > 0 ? G.vermelhoL : G.verdeLight,
                    color: dados.dividendos.imposto_cenario > 0 ? G.vermelho : G.verde }}>
                    {dinheiro(dados.dividendos.imposto_cenario)}
                  </div>
                  <div style={{ fontSize: 10.5, color: G.txt2, marginTop: 4 }}>
                    {(dados.dividendos.meses || []).length} mês(es) com distribuição no acervo
                  </div>
                </>
              )}
            </div>
            <div>
              <div style={{ ...rotulo, marginBottom: 4 }}>Número de sócios</div>
              <input inputMode="numeric" value={nSocios} placeholder="informe"
                onChange={(e) => {
                  const so = (e.target.value || '').replace(/\D/g, '');
                  if (so === '') { setNSocios(''); return; }
                  setNSocios(String(Math.min(100, Math.max(1, parseInt(so, 10)))));
                }}
                aria-label="Número de sócios que recebem a distribuição"
                style={{ height: 36, width: '100%', padding: '0 12px', borderRadius: 0,
                  border: `1px solid ${nSocios ? G.borda : G.laranja}`,
                  fontFamily: 'inherit', fontSize: 13.5 }} />
              <div style={{ fontSize: 10.5, color: G.txt2, marginTop: 4 }}>
                {dados.dividendos.premissa
                  || 'sem base para estimar — o cálculo espera este dado'}
              </div>
            </div>
          </div>
          <div style={{ padding: '12px 16px', background: G.laranjaLight,
            borderLeft: `3px solid ${G.laranja}`, borderRadius: 0, marginTop: 14,
            fontSize: 12.5, lineHeight: 1.55 }}>
            <strong>O conselho do efeito degrau:</strong> o imposto incide sobre o
            valor inteiro do mês, não sobre o que passa do limite — R$ 50.000,00
            não pagam nada; R$ 50.001,00 pagam R$ 5.000,10. Programar o calendário
            de distribuição, ou repartir entre mais sócios, muda o imposto.
          </div>
        </>
      )}

      <div style={{ fontSize: 10.5, color: G.txt2, borderTop: `1px solid ${G.borda}`,
        marginTop: 20, paddingTop: 10, lineHeight: 1.6 }}>
        <strong>Fontes:</strong> EC 132/2023 (ADCT arts. 125–129 e 134) · LC 214/2025 ·
        LC 227/2026 (saldos credores de ICMS) · Lei 15.270/2025 (art. 6º-A) ·
        parâmetros conferidos em 31/08/2026, com fonte legal linha a linha.
        Esta análise apoia a conversa com o seu contador — não o substitui.
      </div>
    </div>
  );
};

const TelaAlertas = ({ empresa, parametros, periodoInicio, periodoFim }) => {
  const [alertas, setAlertas]   = useState([]);
  const [loading, setLoading]   = useState(false);
  const [erro, setErro]         = useState(null);

  // O seletor de período é ÚNICO e comanda a ferramenta inteira
  // ([[seletor-periodo-unico]]). Esta tela era a exceção — e o preço foi alto.
  const dataInicio = periodoInicio?.inicio || null;
  const dataFim    = periodoFim?.fim || null;

  useEffect(() => {
    // Trava contra resposta obsoleta. O período chega de uma requisição
    // própria, então o efeito dispara ANTES dele existir: medi 6 chamadas, as
    // 4 primeiras sem data. A resposta de uma delas chegava depois e
    // sobrescrevia a boa — a tela voltava a mostrar o alerta do recorte errado.
    // É o mesmo padrão que a blindagem resolve para EMPRESA; aqui é por PERÍODO,
    // e a blindagem não alcança (a URL é da mesma empresa).
    let vivo = true;
    const buscar = async () => {
      setLoading(true); setErro(null);
      try {
        const p = parametros || {};
        const url = new URL(`${API}/indicadores/alertas/${empresa.id}`);
        // PERÍODO DO SELETOR (31/07). Antes a tela não mandava data nenhuma e
        // o backend caía no default "últimos 12 meses até hoje" — que na MORED
        // resolvia para set/2025 a ago/2026, quatro meses de dado real. O
        // cabeçalho dizia Jan/2024 → Dez/2025 e os alertas analisavam outra
        // coisa: ROIC de 14,8% contra WACC anual de 15,0%, e a tela acusava
        // **"Empresa destrói valor"** numa empresa que cria (ROIC real 38,6%).
        // Comparar retorno de 4 meses com custo de capital anual não tem
        // sentido — e era o alerta crítico que o consultor levaria ao cliente.
        if (dataInicio && dataFim) {
          url.searchParams.set('data_inicio', dataInicio);
          url.searchParams.set('data_fim',    dataFim);
        }
        // ?? p/ preservar 0 (Simples tem ir=0)
        url.searchParams.set('saldo_cc',           p.saldo_cc           ?? 0);
        url.searchParams.set('saldo_cdb',          p.saldo_cdb          ?? 0);
        url.searchParams.set('ir',                 p.ir                 ?? 0.15);
        url.searchParams.set('wacc_ke',            p.wacc_ke            ?? 0.28);
        url.searchParams.set('wacc_kd',            p.wacc_kd            ?? 0.14);
        url.searchParams.set('wacc_e',             p.wacc_e             ?? 500000);
        url.searchParams.set('wacc_d',             p.wacc_d             ?? 200000);
        url.searchParams.set('depreciacao_mensal', p.depreciacao_mensal ?? 0);
        const res  = await fetch(url);
        const data = await res.json();
        if (!vivo) return;
        if (!res.ok) throw new Error(data.detail || 'Erro');
        setAlertas(data);
      } catch(e) { if (vivo) setErro('Erro ao carregar alertas.'); }
      if (vivo) setLoading(false);
    };
    buscar();
    return () => { vivo = false; };
  }, [empresa, parametros, dataInicio, dataFim]);

  const CORES = {
    critico:  { bg: '#FEF2F2', borda: '#DC2626', txt: '#DC2626', badge: '#FEE2E2' },
    alerta:   { bg: '#FFF7ED', borda: '#F47C3C', txt: '#F47C3C', badge: '#FFEDD5' },
    atencao:  { bg: '#FFFBEB', borda: '#B45309', txt: '#B45309', badge: '#FEF3C7' },
    positivo: { bg: '#F0FDF4', borda: '#1B6B2F', txt: '#1B6B2F', badge: '#DCFCE7' },
  };

  // 3 severidades (consolidado): critico · atencao · positivo. 'alerta' unificado com 'critico'.
  const criticos  = alertas.filter(a => a.severidade === 'critico');
  const atencoes  = alertas.filter(a => a.severidade === 'atencao');
  const positivos = alertas.filter(a => a.severidade === 'positivo');

  const CardAlerta = ({ alerta }) => {
    const cor = CORES[alerta.severidade] || CORES.atencao;
    return (
      <div style={{ background: cor.bg, borderRadius: 0,
        padding: 0, overflow: 'hidden' }}>
        <div style={{ height: 4, background: cor.borda }}/>
        <div style={{ padding: '18px 20px', display: 'flex',
          alignItems: 'flex-start', gap: 12 }}>
          <span style={{ fontSize: 22 }}>{alerta.icone}</span>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 14, fontWeight: 800, color: cor.txt, marginBottom: 4 }}>
              {alerta.titulo}
            </div>
            <div style={{ fontSize: 13, color: G.txt1, marginBottom: 8, lineHeight: 1.5 }}>
              {alerta.descricao}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ fontSize: 11, fontWeight: 700, color: cor.txt }}>
                Acao sugerida:
              </span>
              <span style={{ fontSize: 12, color: G.txt2 }}>{alerta.acao}</span>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const Secao = ({ titulo, lista, cor }) => {
    if (lista.length === 0) return null;
    return (
      <div style={{ marginBottom: 24 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
          <h3 style={{ margin: 0, fontSize: 14, fontWeight: 800, color: G.txt1 }}>{titulo}</h3>
          <span style={{ background: cor, color: '#fff', borderRadius: 0,
            padding: '2px 10px', fontSize: 11, fontWeight: 700 }}>{lista.length}</span>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {lista.map((a, i) => <CardAlerta key={i} alerta={a}/>)}
        </div>
      </div>
    );
  };

  return (
    <div style={{ padding: '24px 28px', position: 'relative', overflow: 'hidden' }}>
      <CarimboAIdvisor />
      <div style={{ marginBottom: 24 }}>
        <div style={{ position: 'relative', display: 'inline-block' }}>
          <h2 style={{ margin: 0, fontSize: 20, fontWeight: 800, color: G.txt1,
            position: 'relative', zIndex: 1 }}>
            Alertas
          </h2>
          {/* Marca d'água — ícone do menu (alertas · sino), 5× (20→100),
              right -10 do final do título, bottom -30, rotação 30° horário */}
          <MarcaDaguaTitulo>
              <path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9"/>
            <path d="M10.3 21a1.94 1.94 0 0 0 3.4 0"/>
            </MarcaDaguaTitulo>
        </div>
        <p style={{ ...TIPO.subtitulo, margin: '4px 0 0' }}>
          diagnostico automatico baseado nos indicadores
        </p>
      </div>

      <QuadrosIntro
        titulo="a tela de Alertas"
        oQueE={<>O <strong>diagnóstico automático</strong> dos indicadores. Cruza
          os números da empresa com as faixas do setor e aponta o que exige
          atenção.</>}
        oQueFaz={<>Percorra os alertas na ordem em que aparecem — os mais graves
          vêm primeiro. Cada um nomeia o <strong>indicador de origem</strong>:
          confira-o no Dashboard antes de levar a conclusão ao cliente.</>}
      />

      {erro && (
        <div style={{ background: G.vermelhoL, color: G.vermelho,
          padding: 14, borderRadius: 0, marginBottom: 16 }}>{erro}</div>
      )}

      {loading && (
        <div style={{ textAlign: 'center', padding: 60, color: G.txt2 }}>
          Analisando indicadores...
        </div>
      )}

      {!loading && alertas.length === 0 && !erro && (
        <div style={{ textAlign: 'center', padding: 60 }}>
          <p style={{ color: G.txt2 }}>Nenhum alerta encontrado. Empresa em boa situacao!</p>
        </div>
      )}

      {!loading && alertas.length > 0 && (
        <>
          {/* Resumo */}
          <div style={{ display: 'flex', gap: 12, marginBottom: 28 }}>
            {[
              { label: 'Criticos',  val: criticos.length,  cor: '#DC2626', bg: '#FEF2F2' },
              { label: 'Atencao',   val: atencoes.length,  cor: '#B45309', bg: '#FFFBEB' },
              { label: 'Positivos', val: positivos.length, cor: '#1B6B2F', bg: '#F0FDF4' },
            ].map(c => (
              <div key={c.label} style={{ background: c.bg, borderRadius: 0,
                padding: '12px 20px', flex: 1, textAlign: 'center' }}>
                <div style={{ fontSize: 11, color: c.cor, fontWeight: 700,
                  textTransform: 'uppercase' }}>{c.label}</div>
                <div style={{ fontSize: 28, fontWeight: 800, color: c.cor }}>{c.val}</div>
              </div>
            ))}
          </div>

          <Secao titulo="Situacoes Criticas" lista={criticos} cor="#DC2626"/>
          <Secao titulo="Pontos de Atencao" lista={atencoes} cor="#B45309"/>
          <Secao titulo="Destaques Positivos" lista={positivos} cor="#1B6B2F"/>

          {/* Mesmo período da lista acima. Sem isto, comentário e lista
              respondiam por recortes diferentes — foi assim que a divergência
              apareceu (31/07): a lista dizia 1 crítico, o comentário dizia 0. */}
          <ComentarioAssessoria empresa={empresa} tela="alertas"
            periodoInicio={periodoInicio} periodoFim={periodoFim}/>
        </>
      )}
    </div>
  );
};

// Tela Valor
// Hub unico de geracao e avaliacao de valor da empresa (renomeado de "Valor Terminal"
// quando o painel "Valor" do Dashboard foi unificado nesta aba — 01/06/2026).
// Setor e g vivem na aba Configuracoes (config societaria, nao op por sessao).
// Periodo vem do seletor global (mesmo do DRE) — antes era hardcoded 2025-01..12,
// o que descasava Receita/EBITDA daqui vs DRE quando o periodo era rolante.
const TelaValor = ({ empresa, parametros, indicadores, periodoInicio, periodoFim }) => {
  const [resultado, setResultado] = useState(null);
  const [loading, setLoading]     = useState(false);
  const [erro, setErro]           = useState(null);
  const [tipoMultiplo] = useState('medio');   // setter removido (dead code) · valor fixo por enquanto

  // Leituras das Configuracoes (read-only nesta aba):
  const setor = empresa.setor || 'geral';
  const g     = String(Math.round(((parametros?.perpetuidade_g) ?? 0.03) * 100));

  const getMultiplo = (tipo, res) => {
    if (!res) return null;
    if (tipo === 'minimo') return res.multiplo_min;
    if (tipo === 'maximo') return res.multiplo_max;
    return res.multiplo_usado;
  };

  const calcular = async () => {
    if (!periodoInicio || !periodoFim) return;
    setLoading(true); setErro(null);
    try {
      const p = parametros || {};
      const url = new URL(`${API}/indicadores/valor-terminal/${empresa.id}`);
      url.searchParams.set('g', parseFloat(g)/100);
      url.searchParams.set('setor', setor);
      url.searchParams.set('data_inicio', periodoInicio.inicio);
      url.searchParams.set('data_fim',    periodoFim.fim);
      if (resultado) {
        const m = getMultiplo(tipoMultiplo, resultado);
        if (m) url.searchParams.set('multiplo', m);
      }
      // ?? p/ preservar 0 (Simples tem ir=0)
      url.searchParams.set('saldo_cc',           p.saldo_cc           ?? 0);
      url.searchParams.set('saldo_cdb',          p.saldo_cdb          ?? 0);
      url.searchParams.set('ir',                 p.ir                 ?? 0.15);
      url.searchParams.set('wacc_ke',            p.wacc_ke            ?? 0.28);
      url.searchParams.set('wacc_kd',            p.wacc_kd            ?? 0.14);
      url.searchParams.set('wacc_e',             p.wacc_e             ?? 500000);
      url.searchParams.set('wacc_d',             p.wacc_d             ?? 200000);
      url.searchParams.set('depreciacao_mensal', p.depreciacao_mensal ?? 0);
      const res  = await fetch(url, { method: 'POST' });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Erro');
      setResultado(data);
      // multiplo gerenciado por tipoMultiplo
    } catch(e) { setErro(e.message); }
    setLoading(false);
  };

  // Recalcula quando empresa, parametros (g/wacc) ou periodo global mudam.
  useEffect(() => { calcular(); }, [empresa, parametros?.perpetuidade_g, empresa?.setor,
    periodoInicio?.inicio, periodoFim?.fim]);

  // ── Cenário da Reforma (Etapa 5 · 31/08): lente sobre os MODELOS DE
  // AVALIAÇÃO — rota dedicada /reforma/valor, mesmos parâmetros do cálculo
  // oficial + ano. 'Hoje' nem passa por aqui; contaminação zero.
  const [anoCenarioV, setAnoCenarioV] = useState(null);
  const [cenValor, setCenValor] = useState(null);
  const [erroCenV, setErroCenV] = useState(null);
  const [carregandoCenV, setCarregandoCenV] = useState(false);
  useEffect(() => {   // troca de empresa ou de período desliga a lente
    setAnoCenarioV(null); setCenValor(null); setErroCenV(null);
  }, [empresa, periodoInicio, periodoFim]);
  useEffect(() => {
    if (!anoCenarioV || !periodoInicio || !periodoFim) { setCenValor(null); return; }
    let vivo = true;
    (async () => {
      setCarregandoCenV(true); setErroCenV(null);
      try {
        const p = parametros || {};
        const url = new URL(`${API}/reforma/valor/${empresa.id}`);
        url.searchParams.set('ano', anoCenarioV);
        url.searchParams.set('g', parseFloat(g) / 100);
        url.searchParams.set('setor', setor);
        url.searchParams.set('data_inicio', periodoInicio.inicio);
        url.searchParams.set('data_fim', periodoFim.fim);
        url.searchParams.set('saldo_cc', p.saldo_cc ?? 0);
        url.searchParams.set('saldo_cdb', p.saldo_cdb ?? 0);
        url.searchParams.set('ir', p.ir ?? 0.15);
        url.searchParams.set('wacc_ke', p.wacc_ke ?? 0.28);
        url.searchParams.set('wacc_kd', p.wacc_kd ?? 0.14);
        url.searchParams.set('wacc_e', p.wacc_e ?? 500000);
        url.searchParams.set('wacc_d', p.wacc_d ?? 200000);
        url.searchParams.set('depreciacao_mensal', p.depreciacao_mensal ?? 0);
        const res = await fetch(url, { method: 'POST' });
        const data = await res.json();
        if (!res.ok) throw new Error(data.detail || 'Cenário indisponível.');
        if (vivo) setCenValor(data);
      } catch (e) { if (vivo) { setErroCenV(e.message); setCenValor(null); } }
      if (vivo) setCarregandoCenV(false);
    })();
    return () => { vivo = false; };
  }, [anoCenarioV, empresa, periodoInicio, periodoFim, parametros, g, setor]);
  const emCenarioV = anoCenarioV != null && cenValor?.ano === anoCenarioV;
  // Os cards dos modelos leem daqui: em cenário, a MESMA forma de resposta
  // (ebitda · vt_gordon · multiplos) vinda da rota dedicada.
  const modelo = emCenarioV ? cenValor.valor_cenario : resultado;

  const fmtM = v => v == null ? 'N/D' :
    'R$ ' + (v/1000000).toFixed(2).replace('.', ',') + ' mi';

  // `minHeight` no lugar de `height` + `overflow: hidden` (30/07): a altura
  // travada cortava tudo o que passasse de 100vh, e com o Comentário da
  // Assessoria no rodapé os blocos passaram a se sobrepor — Modelos de
  // Avaliação cobria os rótulos de Geração de Valor. Agora a tela cresce com
  // o conteúdo e rola.
  return (
    <div style={{ padding: '24px 28px',
      minHeight: 'calc(100vh - 158px)',
      position: 'relative',
      display: 'flex', flexDirection: 'column', gap: 14 }}>
      <CarimboAIdvisor />
      <div style={{ flexShrink: 0 }}>
        <div style={{ position: 'relative', display: 'inline-block' }}>
          <h2 style={{ margin: 0, fontSize: 20, fontWeight: 800, color: G.txt1,
            position: 'relative', zIndex: 1 }}>
            Valor
          </h2>
          {/* Marca d'água — ícone do menu (valor · círculos), 5× (20→100),
              right -10 do final do título, bottom -30, rotação 30° horário */}
          <MarcaDaguaTitulo>
              <circle cx="8" cy="8" r="6"/>
            <path d="M18.09 10.37A6 6 0 1 1 10.34 18"/>
            <line x1="7" y1="6" x2="7" y2="8"/>
            <line x1="9" y1="10" x2="9" y2="10.01"/>
            <line x1="15" y1="14" x2="15" y2="14.01"/>
            <line x1="17" y1="18" x2="17" y2="18.01"/>
            </MarcaDaguaTitulo>
        </div>
        <p style={{ ...TIPO.subtitulo, margin: '4px 0 0' }}>
          estimativa do valor da empresa
        </p>
      </div>

      <QuadrosIntro
        titulo="a tela Valor"
        oQueE={<>A estimativa de <strong>quanto a empresa vale hoje</strong>, a
          partir do resultado que ela gera e do custo do capital que a
          financia.</>}
        oQueFaz={<>Compare os métodos e observe a <strong>faixa</strong>, não o
          número exato. O valor depende das premissas — mude-as nas
          Configurações Complementares e veja o efeito.</>}
      />

      {erro && (
        <div style={{ background: G.vermelhoL, color: G.vermelho,
          padding: 12, fontSize: 12, flexShrink: 0 }}>{erro}</div>
      )}

      {resultado && (
        <>
          {/* GERAÇÃO DE VALOR — 4 quadros com descricao + valor + interpretacao */}
          <div style={{ fontSize: 11, fontWeight: 700, color: G.txt2,
            textTransform: 'uppercase', letterSpacing: '0.10em',
            flexShrink: 0 }}>Geração de Valor</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)',
            gap: 14, flex: 1, minHeight: 0 }}>
            {(() => {
              const ind = indicadores || {};
              const corPorInterp = (interp) => {
                if (!interp) return { cor: G.txt2, bg: G.fundo };
                const i = interp.toLowerCase();
                if (i.includes('excelente') || i.includes('boa') || i.includes('saudável') || i.includes('positivo'))
                  return { cor: G.verde, bg: G.verdeLight };
                if (i.includes('negativ') || i.includes('crítico') || i.includes('fraca'))
                  return { cor: G.vermelho, bg: G.vermelhoL };
                if (i.includes('atenção') || i.includes('média') || i.includes('regular') || i.includes('normal'))
                  return { cor: '#B45309', bg: '#FFFBEB' };
                return { cor: G.txt2, bg: G.fundo };
              };
              const cards = [
                {
                  label: 'EBITDA',
                  descr: 'Lucro operacional antes de juros, impostos, depreciação e amortização. Mede a geração de caixa do negócio.',
                  valor: 'R$ ' + (ind.ebitda || 0).toLocaleString('pt-BR', {minimumFractionDigits:2, maximumFractionDigits:2}),
                  sub:   'Margem ' + ((ind.margem_ebitda || 0)*100).toFixed(1).replace('.', ',') + '%',
                  interp: ind.interpretacao_ebitda,
                },
                {
                  label: 'Fluxo de Caixa Livre',
                  descr: 'Caixa que sobra após operação, impostos e investimentos (CAPEX). Disponível para sócios e credores.',
                  valor: (ind.fcf >= 0 ? '+' : '') + 'R$ ' + (ind.fcf || 0).toLocaleString('pt-BR', {minimumFractionDigits:2, maximumFractionDigits:2}),
                  sub:   'Conversão ' + ((ind.conversao_caixa || 0)*100).toFixed(0) + '% do EBITDA',
                  interp: ind.interpretacao_fcf,
                },
                {
                  label: 'WACC',
                  descr: 'Custo médio ponderado do capital — mistura de capital próprio (Ke) e dívida (Kd). Patamar a ser superado pelo ROIC.',
                  valor: ((ind.wacc || 0)*100).toFixed(1).replace('.', ',') + '%',
                  sub:   'Custo de capital combinado',
                  interp: null,
                },
                {
                  label: 'ROIC',
                  descr: 'Retorno sobre o capital investido. Compara o que a empresa gera com o custo do capital (WACC).',
                  valor: ((ind.roic || 0)*100).toFixed(1).replace('.', ',') + '%',
                  sub:   'vs WACC ' + ((ind.wacc || 0)*100).toFixed(1).replace('.', ',') + '%',
                  interp: ind.interpretacao_roic,
                },
              ];
              return cards.map(c => {
                const co = corPorInterp(c.interp);
                return (
                  <div key={c.label} style={{ background: G.card,
                    padding: '16px 18px',
                    display: 'flex', flexDirection: 'column', gap: 6 }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: G.txt2,
                      textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                      {c.label}
                    </div>
                    <div style={{ fontSize: 11.5, color: G.txt2, lineHeight: 1.4 }}>
                      {c.descr}
                    </div>
                    <div style={{ fontSize: 22, fontWeight: 700, color: co.cor,
                      letterSpacing: '-0.01em', marginTop: 4 }}>
                      {c.valor}
                    </div>
                    <div style={{ fontSize: 11, color: G.txt2 }}>
                      {c.sub}
                    </div>
                    {c.interp && (
                      <div style={{ marginTop: 'auto', paddingTop: 8 }}>
                        <span style={{ background: co.bg, color: co.cor,
                          fontSize: 11, fontWeight: 700, padding: '4px 10px',
                          borderRadius: 0, display: 'inline-block' }}>
                          {c.interp}
                        </span>
                      </div>
                    )}
                  </div>
                );
              });
            })()}
          </div>

          {/* Titulo da secao "Modelos de Avaliacao" — cobre tanto a linha de
              parametros (Setor/g) quanto os 3 cards abaixo. */}
          <div style={{ fontSize: 11, fontWeight: 700, color: G.txt2,
            textTransform: 'uppercase', letterSpacing: '0.10em',
            flexShrink: 0 }}>Modelos de Avaliação</div>

          {/* Toggle do cenário da reforma — padrão das abas DRE e Resumo.
              A lente age SÓ nos modelos (o valor segue o EBITDA); a Geração
              de Valor acima continua sendo o medido de hoje. */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 12,
            flexShrink: 0, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 11, fontWeight: 800, color: G.txt2,
              letterSpacing: '0.08em', textTransform: 'uppercase' }}>
              Regime
            </span>
            <div style={{ display: 'flex', gap: 8 }}>
              {[[null, 'Hoje'], [2027, '2027'], [2030, '2030'], [2033, '2033']]
                .map(([ano, rot]) => {
                  const on = anoCenarioV === ano;
                  return (
                    <button key={rot} onClick={() => setAnoCenarioV(ano)}
                      style={{ width: 96, padding: '8px 0', border: 'none',
                        borderRadius: 0, cursor: 'pointer', fontFamily: 'inherit',
                        fontSize: 12.5, fontWeight: 700,
                        background: on ? G.laranja : G.laranjaLight,
                        color: on ? '#FFF' : G.laranja,
                        transition: 'background .15s, color .15s' }}>
                      {rot}
                    </button>
                  );
                })}
            </div>
            {carregandoCenV && (
              <span style={{ fontSize: 11.5, color: G.txt2, fontStyle: 'italic' }}>
                calculando o cenário…
              </span>
            )}
            {emCenarioV && (
              <span style={{ display: 'inline-block', padding: '4px 10px',
                background: '#FFFBEB', border: '1px solid #B45309', borderRadius: 0,
                fontSize: 11, fontWeight: 700, color: '#B45309',
                letterSpacing: '0.06em', textTransform: 'uppercase' }}>
                · cenário da reforma · {anoCenarioV}
                {cenValor?.tem_estimativa ? ' · estimativa' : ''}
              </span>
            )}
          </div>
          {erroCenV && (
            <div style={{ padding: '8px 14px', background: '#FFFBEB',
              borderLeft: '3px solid #B45309', borderRadius: 0, fontSize: 12,
              color: '#B45309', flexShrink: 0 }}>
              <strong>Cenário indisponível.</strong> {erroCenV}
            </div>
          )}

          {/* Parametros — read-only desta tela. Edicao em Configuracoes.
              Posicionado antes dos Modelos de Avaliacao pois afetam apenas
              esses calculos (Gordon/Multiplos), nao a Geracao de Valor. */}
          <div style={{ background: G.card, padding: '12px 18px',
            display: 'flex', alignItems: 'center',
            gap: 22, flexWrap: 'wrap', flexShrink: 0 }}>
            <div>
              <div style={{ fontSize: 10, fontWeight: 700, color: G.txt2,
                textTransform: 'uppercase', letterSpacing: '0.08em' }}>Setor</div>
              <div style={{ fontSize: 14, fontWeight: 700, color: G.txt1, marginTop: 2 }}>
                {setor}
              </div>
            </div>
            <div style={{ width: 1, height: 28, background: G.borda }}/>
            <div>
              <div style={{ fontSize: 10, fontWeight: 700, color: G.txt2,
                textTransform: 'uppercase', letterSpacing: '0.08em' }}>Taxa Crescimento (g)</div>
              <div style={{ fontSize: 14, fontWeight: 700, color: G.verde, marginTop: 2 }}>
                {g}%
              </div>
            </div>
            <div style={{ flex: 1 }}/>
            <div style={{ fontSize: 11, color: G.txt2, fontStyle: 'italic' }}>
              Editar em Configurações
            </div>
            <button onClick={calcular} disabled={loading}
              style={{ padding: '8px 22px', borderRadius: 0,
                border: 'none', background: G.laranja, color: '#fff',
                fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
              {loading ? 'Calculando...' : 'Recalcular'}
            </button>
          </div>

          {/* 3 cards (Gordon, Múltiplo Mín, Múltiplo Máx) — sem bordas coloridas */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr',
            gap: 14, flex: 1, minHeight: 0 }}>
            <div style={{ background: G.card, borderRadius: 0,
              padding: '18px 22px', display: 'flex', flexDirection: 'column' }}>
              <div style={{ fontSize: 11, fontWeight: 800, color: G.txt2,
                textTransform: 'uppercase', marginBottom: 6 }}>
                Gordon — Perpetuidade {g}%
              </div>
              <div style={{ fontSize: 26, fontWeight: 800, color: G.verde, marginBottom: 6 }}>
                {fmtM(modelo.vt_gordon)}
              </div>
              <div style={{ fontSize: 11, color: G.txt2, marginBottom: 8 }}>
                EBITDA × (1+{g}%) / (WACC {(modelo.wacc*100).toFixed(1)}% − {g}%)
              </div>
              <div style={{ background: G.verdeLight, borderRadius: 0,
                padding: '6px 10px', fontSize: 11, color: G.verde, fontWeight: 600,
                marginTop: 'auto', alignSelf: 'flex-start' }}>
                Crescimento perpétuo de {g}% ao ano
              </div>
            </div>

            <div style={{ background: G.card, borderRadius: 0,
              padding: '18px 22px', display: 'flex', flexDirection: 'column' }}>
              <div style={{ fontSize: 11, fontWeight: 800, color: G.txt2,
                textTransform: 'uppercase', marginBottom: 6 }}>
                Múltiplo Mínimo — {modelo.multiplo_min}×
              </div>
              <div style={{ fontSize: 26, fontWeight: 800, color: '#B45309', marginBottom: 6 }}>
                {fmtM(modelo.ebitda * modelo.multiplo_min)}
              </div>
              <div style={{ fontSize: 11, color: G.txt2, marginBottom: 8 }}>
                EBITDA × {modelo.multiplo_min}×
              </div>
              <div style={{ background: '#FFFBEB', borderRadius: 0,
                padding: '6px 10px', fontSize: 11, color: '#B45309', fontWeight: 600,
                marginTop: 'auto', alignSelf: 'flex-start' }}>
                Piso da faixa para {setor}
              </div>
            </div>

            <div style={{ background: G.card, borderRadius: 0,
              padding: '18px 22px', display: 'flex', flexDirection: 'column' }}>
              <div style={{ fontSize: 11, fontWeight: 800, color: G.txt2,
                textTransform: 'uppercase', marginBottom: 6 }}>
                Múltiplo Máximo — {modelo.multiplo_max}×
              </div>
              <div style={{ fontSize: 26, fontWeight: 800, color: G.laranja, marginBottom: 6 }}>
                {fmtM(modelo.ebitda * modelo.multiplo_max)}
              </div>
              <div style={{ fontSize: 11, color: G.txt2, marginBottom: 8 }}>
                EBITDA × {modelo.multiplo_max}×
              </div>
              <div style={{ background: G.laranjaLight, borderRadius: 0,
                padding: '6px 10px', fontSize: 11, color: G.laranja, fontWeight: 600,
                marginTop: 'auto', alignSelf: 'flex-start' }}>
                Teto da faixa para {setor}
              </div>
            </div>
          </div>

          {/* Interpretacao + Nota — compactado em uma linha */}
          <div style={{ background: G.verdeLight, padding: '12px 18px',
            display: 'flex', alignItems: 'flex-start', gap: 12, flexShrink: 0 }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 13, color: G.txt1, lineHeight: 1.5 }}>
                Valor estimado da {empresa.nome}
                {emCenarioV ? ` no cenário da reforma de ${anoCenarioV}` : ''}: entre{' '}
                <strong>{fmtM(modelo.ebitda * modelo.multiplo_min)}</strong> e{' '}
                <strong>{fmtM(modelo.ebitda * modelo.multiplo_max)}</strong>,
                com Gordon apontando para <strong>{fmtM(modelo.vt_gordon)}</strong>.
              </div>
              <div style={{ fontSize: 11, color: G.txt2, lineHeight: 1.45, marginTop: 4 }}>
                <strong>Nota:</strong> múltiplos Damodaran/ABVCAP — PMEs brasileiras.
                Estimativas — consulte assessor financeiro para avaliação formal.
              </div>
            </div>
          </div>
          {/* Card do RGAI do cenário — determinístico, montado dos números no
              backend; mesmo padrão do card da DRE em cenário. */}
          {emCenarioV && cenValor?.comentario && (
            <div style={{ background: '#FFF', border: `1px solid ${G.borda}`,
              borderRadius: 0, padding: '12px 14px', flexShrink: 0 }}>
              <div style={{ marginBottom: 6 }}>
                <MarcaAIdvisor variant="default" />
              </div>
              <div style={{ fontSize: 12.5, fontWeight: 800, color: G.txt1,
                marginBottom: 6 }}>
                {cenValor.comentario.titulo}
              </div>
              {(cenValor.comentario.paragrafos || []).map((p, i) => (
                <p key={i} style={{ fontSize: 11.5, lineHeight: 1.5,
                  color: G.txt1, margin: '0 0 6px' }}>{p}</p>
              ))}
              {cenValor.comentario.tem_estimativa && (
                <div style={{ fontSize: 10.5, color: '#B45309', marginTop: 2 }}>
                  Estimativa — a alíquota de referência ainda será fixada por
                  resolução do Senado.
                </div>
              )}
            </div>
          )}
          <ComentarioAssessoria empresa={empresa} tela="valor"
            periodoInicio={periodoInicio} periodoFim={periodoFim}/>
        </>
      )}
    </div>
  );
};

// Modal Detalhamento
const ModalDetalhamento = ({ transacao, onConfirmar, onFechar }) => {
  const valorTotal = Math.abs(transacao.valor);
  const [itens, setItens] = React.useState([
    { descricao: '', categoria: 'Despesa Operacional', valor: '' }
  ]);

  const CATS = [
    'Despesa Operacional', 'Despesa Comercial', 'Despesa Administrativa',
    'Despesa Financeira', 'CAPEX', 'Retirada de Socio (PF)',
  ];

  const totalAlocado = itens.reduce((s, i) => s + (parseFloat(i.valor) || 0), 0);
  const restante = Math.round((valorTotal - totalAlocado) * 100) / 100;

  const addItem = () => setItens(prev => [...prev, { descricao: '', categoria: 'Despesa Operacional', valor: '' }]);
  const updateItem = (idx, campo, val) => setItens(prev => prev.map((it, i) => i === idx ? {...it, [campo]: val} : it));
  const removeItem = (idx) => { if (itens.length === 1) return; setItens(prev => prev.filter((_, i) => i !== idx)); };

  const handleConfirmar = () => {
    const novos = itens.filter(it => parseFloat(it.valor) > 0).map(it => ({
      descricao: it.descricao || transacao.descricao,
      categoria: it.categoria,
      categoria_conta: it.categoria,
      valor: -parseFloat(it.valor),
      data: transacao.data,
      tipo: 'SAIDA',
    }));
    if (restante > 0.01) {
      novos.push({
        descricao: 'Restante — ' + transacao.descricao,
        categoria: 'Despesa Operacional',
        categoria_conta: 'Nao Classificado',
        valor: -restante,
        data: transacao.data,
        tipo: 'SAIDA',
      });
    }
    onConfirmar(transacao, novos);
  };

  return (
    <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      background: 'rgba(0,0,0,0.5)', zIndex: 1000,
      display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ background: G.card, borderRadius: 0, padding: 28,
        width: 580, maxHeight: '85vh', overflowY: 'auto',
        }}>
        <div style={{ display: 'flex', justifyContent: 'space-between',
          alignItems: 'flex-start', marginBottom: 16 }}>
          <div>
            <h3 style={{ margin: 0, fontSize: 16, fontWeight: 800, color: G.txt1 }}>
              Detalhar Lancamento
            </h3>
            <p style={{ margin: '4px 0 0', fontSize: 12, color: G.txt2 }}>
              {transacao.descricao} - {transacao.data}
            </p>
          </div>
          <button onClick={onFechar} style={{ background: 'none', border: 'none',
            fontSize: 20, cursor: 'pointer', color: G.txt2 }}>x</button>
        </div>

        <div style={{ background: G.fundo, borderRadius: 0, padding: '12px 16px', marginBottom: 16 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8 }}>
            {[
              { label: 'Total', val: valorTotal, cor: G.txt1 },
              { label: 'Alocado', val: totalAlocado, cor: G.verde },
              { label: 'Restante', val: restante, cor: restante > 0 ? G.laranja : G.verde },
            ].map(c => (
              <div key={c.label} style={{ textAlign: 'center' }}>
                <div style={{ fontSize: 10, color: G.txt2, fontWeight: 700, textTransform: 'uppercase' }}>{c.label}</div>
                <div style={{ fontSize: 16, fontWeight: 800, color: c.cor }}>
                  R$ {c.val.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </div>
              </div>
            ))}
          </div>
          <div style={{ height: 8, background: G.borda, borderRadius: 0, overflow: 'hidden', marginTop: 10 }}>
            <div style={{ height: '100%', borderRadius: 0,
              background: restante <= 0 ? G.verde : G.laranja,
              width: `${Math.min(100, (totalAlocado/valorTotal)*100)}%`,
              transition: 'width 0.3s' }}/>
          </div>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {itens.map((it, i) => (
            <div key={i} style={{ background: G.fundo, borderRadius: 0, padding: 14, position: 'relative' }}>
              {itens.length > 1 && (
                <button onClick={() => removeItem(i)}
                  style={{ position: 'absolute', top: 8, right: 8,
                    background: 'none', border: 'none', cursor: 'pointer', color: G.vermelho, fontSize: 14 }}>x</button>
              )}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                <div>
                  <label style={{ fontSize: 10, fontWeight: 700, color: G.txt2,
                    textTransform: 'uppercase', display: 'block', marginBottom: 4 }}>Descricao</label>
                  <input value={it.descricao}
                    onChange={e => updateItem(i, 'descricao', e.target.value)}
                    placeholder="Ex: Fornecedor NEXO"
                    style={{ width: '100%', padding: '8px 10px', borderRadius: 0,
                      fontSize: 13, color: G.txt1, boxSizing: 'border-box' }}/>
                </div>
                <div>
                  <label style={{ fontSize: 10, fontWeight: 700, color: G.txt2,
                    textTransform: 'uppercase', display: 'block', marginBottom: 4 }}>Valor (R$)</label>
                  <input value={it.valor}
                    onChange={e => updateItem(i, 'valor', e.target.value)}
                    placeholder="0.00" type="text" inputMode="decimal"
                    style={{ width: '100%', padding: '8px 10px', borderRadius: 0,
                      fontSize: 13, color: G.txt1, boxSizing: 'border-box' }}/>
                </div>
              </div>
              <div style={{ marginTop: 10 }}>
                <label style={{ fontSize: 10, fontWeight: 700, color: G.txt2,
                  textTransform: 'uppercase', display: 'block', marginBottom: 4 }}>Categoria</label>
                <select value={it.categoria}
                  onChange={e => updateItem(i, 'categoria', e.target.value)}
                  style={{ width: '100%', padding: '8px 10px', borderRadius: 0,
                    fontSize: 13, color: G.txt1, background: '#fff' }}>
                  {CATS.map(c => <option key={c}>{c}</option>)}
                </select>
              </div>
            </div>
          ))}
        </div>

        <button onClick={addItem}
          style={{ width: '100%', marginTop: 10, padding: '10px', borderRadius: 0,
            border: `2px dashed ${G.borda}`, background: 'transparent',
            cursor: 'pointer', fontSize: 13, color: G.txt2, fontWeight: 600 }}>
          + Adicionar lancamento
        </button>

        <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
          <button onClick={onFechar}
            style={{ flex: 1, padding: '12px', borderRadius: 0,
              background: 'transparent',
              cursor: 'pointer', fontSize: 13, color: G.txt2, fontWeight: 600 }}>
            Cancelar
          </button>
          <button onClick={handleConfirmar} disabled={totalAlocado <= 0}
            style={{ flex: 2, padding: '12px', borderRadius: 0, border: 'none',
              cursor: totalAlocado > 0 ? 'pointer' : 'default', fontSize: 13, fontWeight: 700,
              background: totalAlocado > 0 ? G.verde : G.borda,
              color: totalAlocado > 0 ? '#fff' : G.txt2 }}>
            {restante <= 0.01 ? 'Confirmar Detalhamento'
              : 'Confirmar (restante sera classificado automaticamente)'}
          </button>
        </div>
      </div>
    </div>
  );
};

const ModalItens = ({ empresa, modo, lump, onFechar, onConcluido }) => {
  const [linhas, setLinhas]       = useState([{ descricao: '', valor: '', categoria: '' }]);
  const [loading, setLoading]     = useState(false);
  const [resultado, setResultado] = useState(null);
  const [erro, setErro]           = useState(null);
  const lumpValor = lump ? Math.abs(lump.valor || 0) : 0;
  const total = linhas.reduce((s, l) => s + (parseFloat(l.valor) || 0), 0);
  const diff  = lumpValor ? +(lumpValor - total).toFixed(2) : 0;

  const setLinha = (i, c, v) => setLinhas(p => p.map((l, j) => j === i ? { ...l, [c]: v } : l));
  const addLinha = () => setLinhas(p => [...p, { descricao: '', valor: '', categoria: '' }]);
  const rmLinha  = (i) => setLinhas(p => p.filter((_, j) => j !== i));

  const enviar = async () => {
    const itens = linhas.filter(l => l.descricao.trim() && l.valor)
      .map(l => ({ descricao: l.descricao.trim(), valor: -Math.abs(parseFloat(l.valor)), categoria: l.categoria || undefined }));
    if (!itens.length) { setErro('Adicione ao menos um item com descrição e valor.'); return; }
    setLoading(true); setErro(null); setResultado(null);
    try {
      const url = modo === 'itemizar'
        ? `${API}/extrato/itemizar/${empresa.id}` : `${API}/extrato/despesa-manual/${empresa.id}`;
      const body = modo === 'itemizar'
        ? { pagamento_id: lump.id, valor_pago: lumpValor, itens } : { itens };
      const res = await fetch(url, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Erro');
      setResultado(data);
      if (onConcluido) onConcluido();
    } catch (e) { setErro(e.message); }
    setLoading(false);
  };

  return (
    <div onClick={onFechar} style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.45)',
      display:'flex', alignItems:'center', justifyContent:'center', zIndex:200 }}>
      <div onClick={e=>e.stopPropagation()} style={{ background:'#fff', borderRadius:12,
        padding:'26px 30px', width:560, maxWidth:'92vw', maxHeight:'88vh', overflowY:'auto',
        fontFamily:"'Outfit', sans-serif" }}>
        <h3 style={{ margin:'0 0 4px', fontSize:18, fontWeight:800, color:G.txt1 }}>
          {modo === 'itemizar' ? 'Itemizar despesa' : 'Registrar despesa da empresa paga pelo sócio ou por terceiro'}
        </h3>
        <p style={{ ...TIPO.subtitulo, margin: '0 0 16px' }}>
          {modo === 'itemizar'
            ? `${lump?.descricao} · R$ ${lumpValor.toLocaleString('pt-BR',{minimumFractionDigits:2,maximumFractionDigits:2})} — descreva no que foi gasto até compor esse valor.`
            : 'Descreva a despesa (fornecedor + valor). A ferramenta classifica sozinha.'}
        </p>
        {linhas.map((l, i) => (
          <div key={i} style={{ display:'flex', gap:8, marginBottom:8, alignItems:'center' }}>
            <input value={l.descricao} placeholder="Fornecedor / descrição"
              onChange={e=>setLinha(i,'descricao',e.target.value)}
              style={{ flex:1, padding:'8px 10px', borderRadius:8, border:`1px solid ${G.borda}`, fontSize:13 }} />
            <input value={l.valor} placeholder="Valor" type="text" inputMode="decimal"
              onChange={e=>setLinha(i,'valor',e.target.value)}
              style={{ width:100, padding:'8px 10px', borderRadius:8, border:`1px solid ${G.borda}`, fontSize:13 }} />
            <select value={l.categoria} onChange={e=>setLinha(i,'categoria',e.target.value)}
              title="Escolha a categoria, ou deixe em 'auto' que a ferramenta classifica pela descrição (aprendizado)"
              style={{ width:150, padding:'8px 6px', borderRadius:8, border:`1px solid ${G.borda}`, fontSize:12 }}>
              <option value="">auto (aprende)</option>
              {CATEGORIAS_TIPO.map(c=><option key={c} value={c}>{c}</option>)}
            </select>
            {linhas.length > 1 && <BotaoRemover onClick={()=>rmLinha(i)} titulo="Remover item" />}
          </div>
        ))}
        <button onClick={addLinha} style={{ border:'none', background:'none', color:G.verde, fontWeight:700, cursor:'pointer', fontSize:13, padding:0, marginBottom:12 }}>+ adicionar item</button>

        {modo === 'itemizar' && (
          <div style={{ fontSize:13, color: Math.abs(diff)<0.01 ? G.verde : G.txt2, marginBottom:12 }}>
            Total itens: R$ {total.toLocaleString('pt-BR',{minimumFractionDigits:2,maximumFractionDigits:2})} · {Math.abs(diff)<0.01 ? 'compõe o valor' : `faltam R$ ${diff.toLocaleString('pt-BR',{minimumFractionDigits:2,maximumFractionDigits:2})}`}
          </div>
        )}
        {erro && <div style={{ background:G.vermelhoL, color:G.vermelho, padding:'10px 12px', borderRadius:8, fontSize:13, marginBottom:12 }}>{erro}</div>}
        {resultado && (
          <div style={{ background:'#E8F2EB', border:`1px solid ${G.verde}`, borderRadius:8, padding:'12px 14px', fontSize:13, marginBottom:12 }}>
            <strong>{resultado.itens.length}</strong> despesa(s) classificada(s) e lançada(s).
            {resultado.itens.map((c,k)=>(<div key={k} style={{ color:G.txt2, marginTop:4 }}>• {c.descricao} → {c.categoria} / {c.categoria_conta}</div>))}
            {modo==='itemizar' && resultado.alerta && <div style={{ color:'#B45309', marginTop:6 }}>{resultado.alerta}</div>}
            {modo==='manual' && resultado.aportes_gerados > 0 && (
              <div style={{ marginTop:10, paddingTop:10, borderTop:`1px dashed ${G.verde}`, color:G.txt1 }}>
                ✓ <strong>Aporte societário de R$ {resultado.total_aportes.toLocaleString('pt-BR',{minimumFractionDigits:2,maximumFractionDigits:2})}</strong> registrado automaticamente para fechar o caixa contábil. Se a empresa reembolsar o sócio depois, o reembolso aparece no extrato e zera o saldo.
              </div>
            )}
          </div>
        )}
        <div style={{ display:'flex', gap:10, justifyContent:'flex-end' }}>
          <button onClick={onFechar} style={{ padding:'9px 16px', borderRadius:8, border:`1px solid ${G.borda}`, background:'#fff', cursor:'pointer', fontSize:13 }}>Fechar</button>
          {(() => {
            const naoConcilia = modo === 'itemizar' && Math.abs(diff) >= 0.01;
            const desativado = loading || !!resultado || naoConcilia;
            const titulo = naoConcilia ? `Faltam R$ ${diff.toLocaleString('pt-BR',{minimumFractionDigits:2,maximumFractionDigits:2})} para compor o valor` : '';
            return (
              <button onClick={enviar} disabled={desativado} title={titulo} style={{ padding:'9px 16px', borderRadius:8, border:'none', fontSize:13, fontWeight:700, background: desativado ? G.borda : G.laranja, color:'#fff', cursor: desativado ? 'not-allowed' : 'pointer' }}>
                {loading ? 'Processando...' : resultado ? (modo==='itemizar' ? 'Itemizado ✓' : 'Despesa cadastrada ✓') : (modo==='itemizar' ? 'Itemizar' : 'Cadastrar despesa')}
              </button>
            );
          })()}
        </div>
      </div>
    </div>
  );
};

// Item dentro do popup de ajustes — adapta a acao ao tipo do lancamento.
const ItemAjuste = ({ t, onFatura, onItemizar }) => {
  const conta = t.categoria_conta;
  const valor = `R$ ${Math.abs(t.valor).toLocaleString('pt-BR',{minimumFractionDigits:2,maximumFractionDigits:2})}`;
  const base  = { border:`1px solid ${G.borda}`, borderRadius:8, padding:'12px 14px', marginBottom:8 };
  const btn   = { marginTop:8, padding:'6px 12px', borderRadius:6, border:'none', background:G.laranja, color:'#fff', fontWeight:700, cursor:'pointer', fontSize:12 };
  const cab = (
    <div style={{ display:'flex', justifyContent:'space-between' }}>
      <div><div style={{ fontSize:13, fontWeight:600, color:G.txt1 }}>{t.descricao}</div>
        <div style={{ fontSize:12, color:G.txt2 }}>{t.data} · {conta}</div></div>
      <span style={{ fontSize:13, fontWeight:700, color:G.vermelho }}>{valor}</span>
    </div>
  );
  if (conta === 'Cartão de Crédito')
    return <div style={base}>{cab}<button onClick={onFatura} style={btn}>Anexar fatura</button></div>;
  // Saque / Reembolso (Pix a PF) -> itemizar por lista manual
  return <div style={base}>{cab}<button onClick={onItemizar} style={btn}>Itemizar (no que foi gasto)</button></div>;
};

// Popup orientado a alerta, acionado de dentro do Extrato. Percorre os itens
// ajustaveis e provoca a despesa-do-bolso (PF->PJ) ate zerar.
const ModalAjustes = ({ empresa, transacoes, onFechar, onAtualizar }) => {
  const CONTAS = ['Cartão de Crédito', 'Saque', 'Reembolso'];
  const [sub, setSub] = useState(null); // { tipo:'fatura'|'itens'|'manual', lump }
  const pendentes = transacoes.filter(t => CONTAS.includes(t.categoria_conta));

  return (
    <div onClick={onFechar} style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.5)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:150 }}>
      <div onClick={e=>e.stopPropagation()} style={{ background:'#fff', borderRadius:12, padding:'26px 30px', width:660, maxWidth:'94vw', maxHeight:'88vh', overflowY:'auto', fontFamily:"'Outfit', sans-serif" }}>
        <h3 style={{ margin:'0 0 4px', fontSize:18, fontWeight:800, color:G.txt1 }}>Ajustar despesas não contabilizadas</h3>
        <p style={{ ...TIPO.subtitulo, margin: '0 0 16px' }}>
          {pendentes.length > 0
            ? `${pendentes.length} item(ns) precisam de ajuste. Resolva cada um para a despesa entrar na conta certa.`
            : 'Nenhum lançamento pendente de ajuste.'}
        </p>

        {pendentes.map((t,i)=>(
          <ItemAjuste key={t.id||i} t={t}
            onFatura={()=>setSub({ tipo:'fatura', lump:t })}
            onItemizar={()=>setSub({ tipo:'itens', lump:t })} />
        ))}

        <div style={{ background:'#FFF7ED', border:'1px solid #F9C99B', borderRadius:10, padding:'14px 16px', marginTop:16 }}>
          <div style={{ fontWeight:700, color:'#B45309', fontSize:14, marginBottom:4 }}>Pagou alguma despesa desta PJ através da PF ou não registrada neste extrato?</div>
          <div style={{ fontSize:13, color:G.txt2, marginBottom:10 }}>A ferramenta não tem como enxergar isso. Se aconteceu, registre para não ficar de fora.</div>
          <button onClick={()=>setSub({ tipo:'manual' })} style={{ padding:'8px 14px', borderRadius:8, border:'none', background:G.laranja, color:'#fff', fontWeight:700, cursor:'pointer', fontSize:13 }}>Registrar Despesa</button>
        </div>

        <div style={{ display:'flex', justifyContent:'flex-end', marginTop:18 }}>
          <button onClick={onFechar} style={{ padding:'9px 18px', borderRadius:8, border:`1px solid ${G.borda}`, background:'#fff', cursor:'pointer', fontSize:13 }}>Fechar</button>
        </div>

        {sub?.tipo === 'fatura' && <ModalFatura empresa={empresa} transacao={sub.lump} onFechar={()=>setSub(null)} onConcluido={onAtualizar} />}
        {(sub?.tipo === 'itens' || sub?.tipo === 'manual') &&
          <ModalItens empresa={empresa} modo={sub.tipo==='itens'?'itemizar':'manual'} lump={sub.lump} onFechar={()=>setSub(null)} onConcluido={onAtualizar} />}
      </div>
    </div>
  );
};

const ModalFatura = ({ empresa, transacao, onFechar, onConcluido }) => {
  const [arquivo, setArquivo]     = useState(null);
  const [loading, setLoading]     = useState(false);
  const [resultado, setResultado] = useState(null);
  const [erro, setErro]           = useState(null);
  const valorPago = Math.abs(transacao.valor || 0);

  const enviar = async () => {
    if (!arquivo) return;
    setLoading(true); setErro(null); setResultado(null);
    try {
      const fd = new FormData();
      fd.append('file', arquivo);
      const url = `${API}/extrato/fatura/${empresa.id}?pagamento_id=${transacao.id}&valor_pago=${valorPago}`;
      const res = await fetch(url, { method: 'POST', body: fd });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Erro ao processar fatura');
      setResultado(data);
      if (onConcluido) onConcluido();
    } catch (e) { setErro(e.message); }
    setLoading(false);
  };

  return (
    <div onClick={onFechar} style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.45)',
      display:'flex', alignItems:'center', justifyContent:'center', zIndex:200 }}>
      <div onClick={e=>e.stopPropagation()} style={{ background:'#fff', borderRadius:12,
        padding:'28px 32px', width:480, maxWidth:'90vw', fontFamily:"'Outfit', sans-serif" }}>
        <h3 style={{ margin:'0 0 6px', fontSize:18, fontWeight:800, color:G.txt1 }}>Anexar fatura do cartão</h3>
        <p style={{ ...TIPO.subtitulo, margin: '0 0 18px' }}>
          Pagamento: {transacao.descricao} · R$ {valorPago.toLocaleString('pt-BR',{minimumFractionDigits:2,maximumFractionDigits:2})}
        </p>
        <input type="file" accept=".csv" onChange={e=>setArquivo(e.target.files[0])} style={{ fontSize:13, marginBottom:16 }} />
        {erro && <div style={{ background:G.vermelhoL, color:G.vermelho, padding:'10px 12px', borderRadius:8, fontSize:13, marginBottom:12 }}>{erro}</div>}
        {resultado && (
          <div style={{ background: resultado.conciliado ? '#E8F2EB' : '#FFFBEB',
            border:`1px solid ${resultado.conciliado ? G.verde : '#B45309'}`, borderRadius:8,
            padding:'12px 14px', fontSize:13, marginBottom:12, color:G.txt1 }}>
            <div><strong>{resultado.itens_inseridos}</strong> itens lançados · Total da fatura R$ {resultado.total_itens.toLocaleString('pt-BR',{minimumFractionDigits:2,maximumFractionDigits:2})}</div>
            <div style={{ marginTop:6, fontWeight:700, color: resultado.conciliado ? G.verde : '#B45309' }}>
              {resultado.conciliado ? 'Conciliado com o pagamento' : resultado.alerta}
            </div>
          </div>
        )}
        <div style={{ display:'flex', gap:10, justifyContent:'flex-end', marginTop:8 }}>
          <button onClick={onFechar} style={{ padding:'9px 16px', borderRadius:8, border:`1px solid ${G.borda}`, background:'#fff', cursor:'pointer', fontSize:13 }}>Fechar</button>
          <button onClick={enviar} disabled={!arquivo||loading||!!resultado} style={{ padding:'9px 16px', borderRadius:8, border:'none', fontSize:13, fontWeight:700,
            background:(!arquivo||loading||resultado) ? G.borda : G.laranja, color:'#fff', cursor:(!arquivo||loading||resultado)?'not-allowed':'pointer' }}>
            {loading ? 'Processando...' : resultado ? 'Fatura enviada ✓' : 'Enviar fatura'}
          </button>
        </div>
      </div>
    </div>
  );
};

const TelaExtrato = ({ empresa, periodoInicio, periodoFim }) => {
  const [transacoes, setTransacoes] = useState([]);
  const [loading, setLoading]       = useState(false);
  const [erro, setErro]             = useState(null);
  const [salvando, setSalvando]     = useState(false);
  const [sucesso, setSucesso]       = useState(false);
  const [modalTransacao, setModalTransacao] = useState(null);
  const [modalFatura, setModalFatura] = useState(null);
  const [recarga, setRecarga] = useState(0);
  const [mostrarAjustes, setMostrarAjustes] = useState(false);

  // ── Importar Extrato — área expansível embutida (era a antiga TelaUpload)
  // Banner sempre visível no topo · clica e expande inline o uploader.
  // Backend detecta automaticamente se é CC, Aplicação ou Cartão.
  const [mostrarImportar, setMostrarImportar] = useState(false);
  const [arquivoImport, setArquivoImport] = useState(null);
  const [loadingImport, setLoadingImport] = useState(false);
  const [erroImport, setErroImport] = useState(null);
  const [resultadoImport, setResultadoImport] = useState(null);

  const ROTULO_TIPO_IMPORT = {
    corrente: 'Conta Corrente',
    aplicacao: 'Aplicação Financeira',
    cartao: 'Fatura de Cartão',
  };

  const handleImport = async () => {
    if (!arquivoImport) return;
    setLoadingImport(true); setErroImport(null); setResultadoImport(null);
    try {
      const form = new FormData();
      form.append('file', arquivoImport);
      const res = await fetch(`${API}/extrato/upload/${empresa.id}`,
        { method: 'POST', body: form });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Erro no upload');
      setResultadoImport(data);
      setRecarga(r => r + 1);   // recarrega tabela de lançamentos automaticamente
    } catch(e) { setErroImport(e.message); }
    setLoadingImport(false);
  };

  const resetImport = () => {
    setArquivoImport(null);
    setResultadoImport(null);
    setErroImport(null);
  };

  const fecharImport = () => {
    resetImport();
    setMostrarImportar(false);
  };

  const handleConfirmarDetalhamento = (transacaoOriginal, novosItens) => {
    setTransacoes(prev => {
      const sem = prev.filter(t => t !== transacaoOriginal);
      return [...sem, ...novosItens];
    });
    setModalTransacao(null);
  };

  const CATEGORIAS_TIPO = [
    'Receita Operacional', 'Receita Financeira',
    'Despesa Operacional', 'Despesa Comercial', 'Despesa Administrativa',
    'Despesa Financeira', 'Tributos', 'CAPEX', 'Transferencia',
    'Retirada de Socio', 'Aporte de Socio',
  ];

  useEffect(() => {
    const buscar = async () => {
      setLoading(true); setErro(null);
      try {
        const res  = await fetch(`${API}/extrato/transacoes/${empresa.id}`);
        const data = await res.json();
        if (!res.ok) throw new Error(data.detail || "Erro");
        setTransacoes(data);
      } catch(e) { setErro("Erro ao carregar transacoes."); }
      setLoading(false);
    };
    buscar();
  }, [empresa, recarga]);

  // Só os lançamentos REALMENTE alterados são enviados ao salvar. Antes ia a
  // lista inteira — com 18 mil lançamentos (empresa com fonte contábil) o
  // servidor fazia ~36 mil gravações e a tela travava (24/07).
  const [idsAlterados, setIdsAlterados] = useState(() => new Set());

  const handleCorrigir = (idx, campo, valor) => {
    setTransacoes(prev => prev.map((t, i) =>
      i === idx ? { ...t, [campo]: valor } : t
    ));
    const alvo = transacoes[idx];
    if (alvo?.id) setIdsAlterados(prev => new Set(prev).add(alvo.id));
  };

  const handleSalvarCorrecoes = async () => {
    if (bloquearSeAdmin()) return;
    const alterados = transacoes.filter(t => t.id && idsAlterados.has(t.id));
    if (!alterados.length) { setSucesso(true); setTimeout(() => setSucesso(false), 2000); return; }
    setSalvando(true); setSucesso(false);
    try {
      const res = await fetch(`${API}/extrato/corrigir/${empresa.id}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(alterados)
      });
      if (!res.ok) throw new Error("Erro ao salvar");
      setIdsAlterados(new Set());
      setSucesso(true);
      setTimeout(() => setSucesso(false), 2000);
    } catch(e) { setErro(e.message); }
    setSalvando(false);
  };

  // Filtra pelo período selecionado na topbar (de/até). Se não houver período
  // disponível, mostra tudo.
  const transacoesFiltradas = (periodoInicio && periodoFim)
    ? transacoes.filter(t => t.data && t.data >= periodoInicio.inicio && t.data <= periodoFim.fim)
    : transacoes;

  const naoClass = transacoesFiltradas.filter(
    t => t.categoria_conta === "Nao Classificado" || t.categoria_conta === "Nao Classificado"
  ).length;
  const totalPF = transacoesFiltradas.filter(t => t.tipo === "PF").length;
  const totalEspecial = transacoesFiltradas.filter(t => t.categoria === "Lançamento Especial").length;
  const CONTAS_AJUSTE = ['Cartão de Crédito', 'Saque', 'Reembolso'];
  const pendentesAjuste = transacoes.filter(t => CONTAS_AJUSTE.includes(t.categoria_conta)).length;

  return (
    <div style={{ padding: "24px 28px", position: 'relative', overflow: 'hidden' }}>
      <CarimboAIdvisor />
      <div style={{ display: "flex", justifyContent: "space-between",
        alignItems: "flex-start", marginBottom: 24 }}>
        <div>
          <div style={{ position: 'relative', display: 'inline-block' }}>
            <h2 style={{ margin: 0, fontSize: 20, fontWeight: 800, color: G.txt1,
              position: 'relative', zIndex: 1 }}>
              Extrato
            </h2>
            {/* Marca d'água — ícone do menu (extrato · linhas), 5× (20→100),
                right -10 do final do título, bottom -30, rotação 30° horário */}
            <MarcaDaguaTitulo>
              <line x1="8" y1="6" x2="21" y2="6"/>
              <line x1="8" y1="12" x2="21" y2="12"/>
              <line x1="8" y1="18" x2="21" y2="18"/>
              <line x1="3" y1="6" x2="3.01" y2="6"/>
              <line x1="3" y1="12" x2="3.01" y2="12"/>
              <line x1="3" y1="18" x2="3.01" y2="18"/>
            </MarcaDaguaTitulo>
          </div>
          <p style={{ margin: "4px 0 0", fontSize: 13, color: G.txt2 }}>
            visualize e corrija classificacoes
          </p>
        </div>
        {/* Botão "Salvar Correções" migrou pro Banner verde abaixo (Visualizar e Editar Categoria) */}
      </div>

      {/* ═══════════ BANNER IMPORTAR · fundo verde ═══════════
          Título grande "Importar" em branco · botão direita verde com borda branca.
          Clica → expande linha compacta com seletor + nome + Enviar.
          Backend detecta automaticamente CC/Aplicação/Cartão. */}
      <div style={{ marginBottom: 14, background: G.verde,
        borderRadius: 0, overflow: 'hidden' }}>
        <div style={{ display: 'flex', alignItems: 'center',
          justifyContent: 'space-between', padding: '14px 20px', gap: 16 }}>
          <div>
            <div style={{ fontFamily: "'Outfit', sans-serif",
              fontSize: 17, fontWeight: 800, color: '#FFF',
              letterSpacing: '-0.005em' }}>
              Importar
            </div>
            <div style={{ fontSize: 12, color: '#FFF', opacity: 0.92, marginTop: 3 }}>
              Conta corrente · Aplicação financeira · Fatura de cartão —
              a ferramenta identifica o tipo automaticamente
            </div>
          </div>
          <button onClick={() => mostrarImportar ? fecharImport() : setMostrarImportar(true)}
            style={{ background: G.verde, color: '#FFF',
              border: '2px solid #FFF', borderRadius: 0,
              padding: '7px 18px', fontSize: 11, fontWeight: 700,
              letterSpacing: '0.08em', textTransform: 'uppercase',
              cursor: 'pointer', whiteSpace: 'nowrap',
              transition: 'all 0.15s ease' }}>
            {mostrarImportar ? 'Fechar ×' : 'Importar'}
          </button>
        </div>

        {/* Área expansível — 1 linha compacta, alinhada à esquerda */}
        {mostrarImportar && (
          <div style={{ padding: '10px 20px 14px',
            borderTop: '1px solid rgba(255,255,255,0.30)',
            background: 'rgba(255,255,255,0.10)' }}>
            {erroImport && (
              <div style={{ background: G.vermelhoL, color: G.vermelho,
                padding: '6px 12px', marginBottom: 8, fontSize: 12,
                borderRadius: 0, display: 'inline-block' }}>
                {erroImport}
              </div>
            )}

            {!resultadoImport && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 12,
                fontSize: 12, color: '#FFF' }}>
                <input type="file" accept=".csv"
                  onChange={e => setArquivoImport(e.target.files[0])}
                  style={{ fontSize: 12, color: '#FFF' }}/>
                {arquivoImport && (
                  <span style={{ fontWeight: 600 }}>
                    ✓ {arquivoImport.name}
                  </span>
                )}
                <button onClick={handleImport}
                  disabled={!arquivoImport || loadingImport}
                  style={{ background: G.verde, color: '#FFF',
                    border: '2px solid #FFF', borderRadius: 0,
                    padding: '6px 18px', fontSize: 11, fontWeight: 700,
                    letterSpacing: '0.08em', textTransform: 'uppercase',
                    cursor: arquivoImport ? 'pointer' : 'default',
                    opacity: arquivoImport ? 1 : 0.55,
                    marginLeft: 'auto', transition: 'all 0.15s ease' }}>
                  {loadingImport ? 'Processando…' : 'Enviar'}
                </button>
              </div>
            )}

            {resultadoImport && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 14,
                fontSize: 12, color: '#FFF', flexWrap: 'wrap' }}>
                <span style={{ fontWeight: 800 }}>✓ Arquivo processado</span>
                <span>
                  <span style={{ opacity: 0.85 }}>Tipo:</span>{' '}
                  <strong>{ROTULO_TIPO_IMPORT[resultadoImport.tipo_detectado] || resultadoImport.tipo_detectado}</strong>
                </span>
                <span>
                  <span style={{ opacity: 0.85 }}>Inseridas:</span>{' '}
                  <strong>{resultadoImport.transacoes_inseridas}</strong>
                </span>
                {resultadoImport.casamentos_fatura_pgto > 0 && (
                  <span>
                    <span style={{ opacity: 0.85 }}>Faturas vinculadas:</span>{' '}
                    <strong>{resultadoImport.casamentos_fatura_pgto}</strong>
                  </span>
                )}
                {/* Alerta de gap temporal entre uploads de CC */}
                {resultadoImport.gap_temporal && (
                  <span style={{ background: 'rgba(0,0,0,0.20)', padding: '3px 10px',
                    borderRadius: 0, border: '1px dashed rgba(255,255,255,0.5)' }}>
                    <IconeAlerta /> <strong>Lacuna de {resultadoImport.gap_temporal.dias_lacuna} dias</strong>{' '}
                    entre o extrato anterior (até {new Date(resultadoImport.gap_temporal.ultimo_extrato_ate).toLocaleDateString('pt-BR')})
                    {' '}e o novo (a partir de {new Date(resultadoImport.gap_temporal.novo_extrato_inicia).toLocaleDateString('pt-BR')}).
                    {' '}Pode ter perdido movimentação.
                  </span>
                )}
                <button onClick={resetImport}
                  style={{ background: '#FFF', color: G.laranja,
                    border: 'none', borderRadius: 0,
                    padding: '5px 14px', fontSize: 10, fontWeight: 700,
                    letterSpacing: '0.06em', textTransform: 'uppercase',
                    cursor: 'pointer', marginLeft: 'auto' }}>
                  Enviar outro
                </button>
                <button onClick={fecharImport}
                  style={{ background: 'transparent', color: '#FFF',
                    border: '1px solid #FFF', borderRadius: 0,
                    padding: '5px 14px', fontSize: 10, fontWeight: 700,
                    letterSpacing: '0.06em', textTransform: 'uppercase',
                    cursor: 'pointer' }}>
                  Fechar
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* ═══════════ 4 mini-cards (esquerda) + botão Registrar Despesa (direita) ═══════════ */}
      <div style={{ display:"flex", gap:12, marginBottom:16, alignItems:"center" }}>
        {[
          { label: "Total", val: transacoesFiltradas.length, cor: G.verde, bg: G.verdeLight },
          { label: "Nao Classificadas", val: naoClass,
            cor: naoClass > 0 ? G.laranja : G.verde,
            bg: naoClass > 0 ? G.laranjaLight : G.verdeLight },
          { label: "Possivel PF", val: totalPF, cor: "#7C3AED", bg: "#F5F3FF" },
          { label: "Lançamento Especial", val: totalEspecial, cor: "#B45309", bg: "#FFFBEB" },
        ].map(c => (
          <div key={c.label} style={{ background: c.bg, borderRadius: 0,
            padding: "10px 16px" }}>
            <div style={{ fontSize: 10, color: c.cor, fontWeight: 700,
              textTransform: "uppercase" }}>{c.label}</div>
            <div style={{ fontSize: 20, fontWeight: 800, color: c.cor }}>{c.val}</div>
          </div>
        ))}
        <button onClick={() => setMostrarAjustes(true)}
          style={{ marginLeft: "auto", background: G.laranja, color: "#FFF",
            border: "none", borderRadius: 0, padding: "12px 22px",
            fontSize: 13, fontWeight: 700, cursor: "pointer", whiteSpace: "nowrap" }}>
          Se Pagou Despesa da PJ na PF - Registre Aqui
        </button>
      </div>

      {/* ═══════════ BANNER VISUALIZAR E EDITAR CATEGORIA · fundo laranja ═══════════
          Mesma diagramação do banner Importar (título grande branco + frase abaixo)
          Contém o botão Salvar Correções migrado do header. */}
      <div style={{ marginBottom: 14, background: G.laranja,
        borderRadius: 0, overflow: 'hidden' }}>
        <div style={{ display: 'flex', alignItems: 'center',
          justifyContent: 'space-between', padding: '14px 20px', gap: 16 }}>
          <div>
            <div style={{ fontFamily: "'Outfit', sans-serif",
              fontSize: 17, fontWeight: 800, color: '#FFF',
              letterSpacing: '-0.005em' }}>
              Visualizar e Editar Categoria
            </div>
            <div style={{ fontSize: 12, color: '#FFF', opacity: 0.92, marginTop: 3 }}>
              Use o dropdown da coluna Categoria para editar · clique em Salvar Correções ao fim
            </div>
          </div>
          <button onClick={handleSalvarCorrecoes} disabled={salvando}
            style={{ background: G.laranja, color: '#FFF',
              border: '2px solid #FFF', borderRadius: 0,
              padding: '7px 18px', fontSize: 11, fontWeight: 700,
              letterSpacing: '0.08em', textTransform: 'uppercase',
              cursor: salvando ? 'wait' : 'pointer', whiteSpace: 'nowrap',
              opacity: salvando ? 0.7 : 1,
              transition: 'all 0.15s ease' }}>
            {salvando ? 'Salvando…' : 'Salvar Correções'}
          </button>
        </div>
      </div>

      {sucesso && (
        <div style={{ background: G.verdeLight, color: G.verde,
          padding: 12, borderRadius: 0, marginBottom: 16,
          fontSize: 13, fontWeight: 700 }}>
          Correcoes salvas com sucesso!
        </div>
      )}
      {erro && (
        <div style={{ background: G.vermelhoL, color: G.vermelho,
          padding: 12, borderRadius: 0, marginBottom: 16, fontSize: 13 }}>
          {erro}
        </div>
      )}

      {loading && (
        <div style={{ textAlign: "center", padding: 40, color: G.txt2 }}>
          Carregando...
        </div>
      )}

      {!loading && transacoesFiltradas.length > 0 && (
        <div style={{ background: G.card, borderRadius: 0,
          boxShadow: "0 2px 12px rgba(0,0,0,0.06)", overflow: "hidden" }}>
          <div style={{ display: "grid",
            gridTemplateColumns: "90px 1fr 200px 110px",
            padding: "10px 16px", background: G.fundo,
            borderBottom: `1px solid ${G.borda}` }}>
            {["Data", "Descricao", "Categoria", "Valor"].map(h => (
              <span key={h} style={{ fontSize: 10, fontWeight: 700,
                color: G.txt2, textTransform: "uppercase" }}>{h}</span>
            ))}
          </div>
          <div style={{ maxHeight: 520, overflowY: "auto" }}>
            {transacoesFiltradas.map((t, i) => {
              const nc = t.categoria_conta === "Nao Classificado";
              const pf = t.tipo === "PF";
              return (
                <div key={i} style={{
                  display: "grid",
                  gridTemplateColumns: "90px 1fr 200px 110px",
                  padding: "10px 16px", alignItems: "center",
                  borderBottom: `1px solid ${G.borda}`,
                  background: t.categoria === "Lançamento Especial" ? "#FFFBEB" : pf ? "#F5F3FF" : nc ? G.laranjaLight
                    : (i%2===0 ? "#fff" : G.fundo),
                }}>
                  <span style={{ fontSize: 12, color: G.txt2 }}>{t.data}</span>
                  <span style={{ fontSize: 12, color: G.txt1, paddingRight: 8,
                    overflow: "hidden", textOverflow: "ellipsis",
                    whiteSpace: "nowrap" }}>{t.descricao}</span>
                  <select value={t.categoria || ""}
                    onChange={e => handleCorrigir(i, "categoria", e.target.value)}
                    style={{ fontSize: 11, padding: "4px 6px", borderRadius: 0,
                      border: `1px solid ${pf ? "#7C3AED" : nc ? G.laranja : G.borda}`,
                      background: "#fff", color: G.txt1 }}>
                    {CATEGORIAS_TIPO.map(c => <option key={c}>{c}</option>)}
                  </select>
                  <div style={{ display: 'flex', flexDirection: 'column',
                    alignItems: 'flex-end', gap: 4 }}>
                    <span style={{ fontSize: 12, fontWeight: 700,
                      color: t.valor >= 0 ? G.verde : G.vermelho }}>
                      {t.valor >= 0 ? "+" : ""}{t.valor != null
                        ? "R$ " + Math.abs(t.valor).toLocaleString("pt-BR",
                          { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : "N/D"}
                    </span>
                    {t.categoria === "Lançamento Especial" && (
                      <button onClick={() => setModalTransacao(t)}
                        style={{ fontSize: 10, fontWeight: 700, padding: '2px 8px',
                          borderRadius: 0, border: 'none', cursor: 'pointer',
                          background: '#B45309', color: '#fff' }}>
                        Detalhar
                      </button>
                    )}
                    {t.categoria_conta === "Cartão de Crédito" && (
                      <button onClick={() => setModalFatura(t)}
                        style={{ fontSize: 10, fontWeight: 700, padding: '2px 8px',
                          borderRadius: 0, border: 'none', cursor: 'pointer',
                          background: G.laranja, color: '#fff' }}>
                        Anexar fatura
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {!loading && transacoesFiltradas.length === 0 && !erro && (
        <div style={{ textAlign: "center", padding: 60, color: G.txt2 }}>
          <p>Nenhuma transacao encontrada para este periodo.</p>
        </div>
      )}

      {modalTransacao && (
        <ModalDetalhamento
          transacao={modalTransacao}
          onConfirmar={handleConfirmarDetalhamento}
          onFechar={() => setModalTransacao(null)}
        />
      )}

      {modalFatura && (
        <ModalFatura
          empresa={empresa}
          transacao={modalFatura}
          onFechar={() => setModalFatura(null)}
          onConcluido={() => setRecarga(r => r + 1)}
        />
      )}

      {mostrarAjustes && (
        <ModalAjustes
          empresa={empresa}
          transacoes={transacoes}
          onFechar={() => setMostrarAjustes(false)}
          onAtualizar={() => setRecarga(r => r + 1)}
        />
      )}
    </div>
  );
};

// ── App Principal ─────────────────────────────────────────────────

// Setores da sidebar, na ORDEM em que aparecem. Esta lista manda: grupo fora
// dela não é desenhado, por mais que exista no NAV — e sem erro nenhum.
const SETORES_MENU = ['DASHBOARD', 'CONSULTORIA', 'DADOS DE ENTRADA', 'ROTINA CONTÁBIL',
                      'VISUALIZAÇÃO', 'PLANEJAMENTO', 'M&A'];

// APOSENTADO EM 27/09/2026 — o recorte por SETOR e papel, de 10 e 11/09
// (memória `niveis-hierarquicos-de-acesso`), foi substituído pelo recorte por
// ABA e MODALIDADE, logo abaixo. As listas eram:
//     admin    → todos os setores
//     contador → DADOS DE ENTRADA · CONTABILIDADE
//     empresa  → DASHBOARD · VISUALIZAÇÃO · PLANEJAMENTO
// Ficam registradas aqui, e não em código vivo, porque as listas de modalidade
// as contradiziam: a do Consultor atravessa cinco setores e o papel `contador`
// enxergava dois. Dois donos da mesma decisão fazem aba sumir em silêncio.
//
// O PAPEL não foi aposentado — só deixou de desenhar o menu. Ele continua
// decidindo quem edita e qual empresa se alcança, no backend, que é onde a
// segurança tem de estar.

// ─────────────────────────────────────────────────────────────────────────
// ABAS POR MODALIDADE · o que foi CONTRATADO (Fabio, 26 e 27/09/2026)
// ─────────────────────────────────────────────────────────────────────────
//
// São DOIS eixos, e eles não se substituem:
//   PAPEL      (admin · contador · empresa) — quem a pessoa é na hierarquia.
//   MODALIDADE (controller · consultor · consultor_ma · contabil) — o que a
//              empresa ou o consultor contratou.
//
// POR QUE A LISTA É POR ABA E NÃO POR SETOR, como o recorte de papel acima:
// as listas ditadas pelo Fabio atravessam os grupos. O Manual do Empresário
// vive no grupo M&A e só o Controller o enxerga; os dois Business Plan moram no
// MESMO grupo e são exclusivos do Consultor M&A. Recorte por setor não expressa
// isso — tentar forçá-lo exigiria remontar os grupos, que é outra obra.
//
// A ORDEM aqui é a que o Fabio ditou, e ela bate com a ordem do NAV. Quem
// desenha a barra lateral continua sendo o NAV: esta lista só diz o que passa.
const ABAS_POR_MODALIDADE = {
  // Do próprio empresário. UMA empresa, até três acessos (titular + 2).
  // Era "Empresário" até 27/09.
  controller: [
    'dashboard',
    'cadastro', 'extrato', 'configuracoes',
    'despesas_custos', 'resumo', 'alertas', 'dre', 'balanco', 'valor', 'reforma',
    'plano', 'comparativo',
    'rgam',                    // o Manual do Empresário é SÓ desta modalidade
  ],
  // Do consultor. Várias empresas sob o mesmo guarda-chuva.
  // Acrescenta Consultoria; NÃO tem o Manual nem a Rotina Contábil.
  // Era "Controller" até 27/09 — cuidado ao ler documento antigo.
  consultor: [
    'dashboard',
    'consultoria',
    'cadastro', 'extrato', 'configuracoes',
    'despesas_custos', 'resumo', 'alertas', 'dre', 'balanco', 'valor', 'reforma',
    'plano', 'comparativo',
  ],
  // O Consultor mais os dois Business Plan de 60 meses. Era "M&A" até 27/09.
  consultor_ma: [
    'dashboard',
    'consultoria',
    'cadastro', 'extrato', 'configuracoes',
    'despesas_custos', 'resumo', 'alertas', 'dre', 'balanco', 'valor', 'reforma',
    'plano', 'comparativo',
    'businessplan', 'businessplansu',
  ],
  // Do escritório de contabilidade. Automatiza a rotina e elimina retrabalho.
  // Quatro abas: nada de leitura econômica, que não é o trabalho dele aqui.
  contabil: [
    'cadastro', 'extrato', 'configuracoes',
    'razao_simulado', 'balancete_simulado', 'composicao_custo',
  ],
};

// Modalidade desconhecida ou ausente não vê ABA NENHUMA — fail-closed, igual ao
// papel. Quem se cadastrou e ainda não tem credencial cai na vitrine, e a
// vitrine não é o NAV: é outra tela.
//
// O ADMIN é a exceção e enxerga tudo, com ou sem modalidade: ele não contrata o
// produto, ele o administra. Devolve `null` — não uma lista — porque o `NAV` só
// existe dentro do componente, mais abaixo. Quem filtra entende `null` como
// "todas", e é o ÚNICO valor que abre tudo: `[]` continua fechando.
const ABAS_VISIVEIS = (role, modalidade) =>
  role === 'admin' ? null : (ABAS_POR_MODALIDADE[modalidade] || []);

// Icones SVG (lucide-style) para o menu lateral
const IconeMenu = ({ id }) => {
  const props = {
    width: 20, height: 20, viewBox: "0 0 24 24",
    fill: "none", stroke: "currentColor",
    strokeWidth: 1.8, strokeLinecap: "round", strokeLinejoin: "round",
  };
  switch (id) {
    case 'dashboard':
      return (<svg {...props}><rect x="3" y="3" width="7" height="9" rx="1.5"/><rect x="14" y="3" width="7" height="5" rx="1.5"/><rect x="14" y="12" width="7" height="9" rx="1.5"/><rect x="3" y="16" width="7" height="5" rx="1.5"/></svg>);
    case 'resumo':
      return (<svg {...props}><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>);
    case 'comparativo':
      return (<svg {...props}><polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/></svg>);
    case 'alertas':
      return (<svg {...props}><path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9"/><path d="M10.3 21a1.94 1.94 0 0 0 3.4 0"/></svg>);
    case 'dre':
      return (<svg {...props}><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="8" y1="13" x2="16" y2="13"/><line x1="8" y1="17" x2="16" y2="17"/><line x1="8" y1="9" x2="10" y2="9"/></svg>);
    case 'balanco':
      // Balança · símbolo de equilíbrio Ativo = Passivo + PL
      return (<svg {...props}><line x1="12" y1="3" x2="12" y2="21"/><line x1="3" y1="7" x2="21" y2="7"/><circle cx="6" cy="7" r="1"/><circle cx="18" cy="7" r="1"/><path d="M3 12l3-5 3 5a3 3 0 11-6 0z"/><path d="M15 12l3-5 3 5a3 3 0 11-6 0z"/></svg>);
    case 'valor':
      return (<svg {...props}><circle cx="8" cy="8" r="6"/><path d="M18.09 10.37A6 6 0 1 1 10.34 18"/><line x1="7" y1="6" x2="7" y2="8"/><line x1="9" y1="10" x2="9" y2="10.01"/><line x1="15" y1="14" x2="15" y2="14.01"/><line x1="17" y1="18" x2="17" y2="18.01"/></svg>);
    case 'configuracoes':
      return (<svg {...props}><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"/></svg>);
    case 'despesas_custos':
      // Duas barras de peso desigual · o fixo que não se mexe ao lado do que
      // sobe com o volume. Os ícones antigos das duas telas (prédio · camadas)
      // desceram para os cards da landing, em 11/09.
      return (<svg {...props}><rect x="3" y="4" width="7" height="16" rx="1"/><path d="M14 20V9l4-4 3 3v12"/><line x1="14" y1="14" x2="21" y2="14"/></svg>);
    case 'extrato':
      return (<svg {...props}><line x1="8" y1="6" x2="21" y2="6"/><line x1="8" y1="12" x2="21" y2="12"/><line x1="8" y1="18" x2="21" y2="18"/><line x1="3" y1="6" x2="3.01" y2="6"/><line x1="3" y1="12" x2="3.01" y2="12"/><line x1="3" y1="18" x2="3.01" y2="18"/></svg>);
    case 'cadastro':
      return (<svg {...props}><rect x="4" y="2" width="16" height="20" rx="2"/><line x1="9" y1="6" x2="11" y2="6"/><line x1="13" y1="6" x2="15" y2="6"/><line x1="9" y1="10" x2="11" y2="10"/><line x1="13" y1="10" x2="15" y2="10"/><line x1="9" y1="14" x2="11" y2="14"/><line x1="13" y1="14" x2="15" y2="14"/><path d="M10 22v-4h4v4"/></svg>);
    case 'plano':
      return (<svg {...props}><circle cx="12" cy="12" r="10"/><circle cx="12" cy="12" r="6"/><circle cx="12" cy="12" r="2"/></svg>);
    case 'businessplan':
      // Tendência crescente apontando para alvo no topo direito (cresce e mira na meta)
      return (<svg {...props}><polyline points="3 17 9 11 13 15 21 7"/><polyline points="14 7 21 7 21 14"/><circle cx="21" cy="7" r="2.5"/></svg>);
    case 'businessplansu':
      // Broto · 2 folhas saindo de uma haste · crescimento orgânico (Startup)
      return (<svg {...props}><path d="M12 22V12"/><path d="M12 12c0-4 2-7 7-8-0.5 5-3 8-7 8z"/><path d="M12 14c0-3-2-5-6-5.5 0.5 4 2.5 5.5 6 5.5z"/></svg>);
    case 'rgam':
      // Livro aberto (manual)
      return (<svg {...props}><path d="M2 4h7a3 3 0 0 1 3 3v13"/><path d="M22 4h-7a3 3 0 0 0-3 3v13"/><path d="M2 4v16h7"/><path d="M22 4v16h-7"/></svg>);
    case 'reforma':
      // Alíquota em transição · % dentro da seta circular (trocou o edifício
      // institucional, que nada dizia de tributo — Fabio, 31/08)
      return (<svg {...props}><path d="M21 12a9 9 0 1 1-9-9c2.52 0 4.93 1 6.74 2.74L21 8"/><path d="M21 3v5h-5"/><line x1="15" y1="9" x2="9" y2="15"/><circle cx="9.5" cy="9.5" r="1.5"/><circle cx="14.5" cy="14.5" r="1.5"/></svg>);
    case 'razao_simulado':
      // Duas folhas sobrepostas · o razão sai sempre acompanhado do balancete.
      // Distinto do `dre`, que é uma folha só, e do `rgam`, que é livro aberto.
      return (<svg {...props}><path d="M15 3H10a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2V8z"/><polyline points="15 3 15 8 20 8"/><path d="M4 8v11a2 2 0 0 0 2 2h9"/></svg>);
    case 'balancete_simulado':
      // Balança de dois pratos · o balancete é a conta que tem de fechar dos
      // dois lados: Ativo de um, Passivo e Patrimônio Líquido do outro.
      return (<svg {...props}><line x1="12" y1="3" x2="12" y2="21"/><line x1="5" y1="21" x2="19" y2="21"/><line x1="4" y1="7" x2="20" y2="7"/><path d="M4 7l-2 6a3 3 0 0 0 6 0z"/><path d="M20 7l-2 6a3 3 0 0 0 6 0z" transform="translate(-2)"/></svg>);
    case 'composicao_custo':
      // Camadas empilhadas · o custo é feito de partes que se somam.
      return (<svg {...props}><polygon points="12 2 2 7 12 12 22 7 12 2"/><polyline points="2 17 12 22 22 17"/><polyline points="2 12 12 17 22 12"/></svg>);
    case 'consultoria':
      // Maleta executiva · funil de captação consultor ↔ cliente
      return (<svg {...props}><rect x="3" y="7" width="18" height="13" rx="2"/><path d="M8 7V5a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/><line x1="3" y1="13" x2="21" y2="13"/><line x1="11" y1="11" x2="13" y2="11"/></svg>);
    default:
      return null;
  }
};

// Card do campeão do Dashboard (19/09/2026) · o MAIOR de cada modalidade.
// UM componente para os três: o desenho é o mesmo e duas cópias divergiriam
// no primeiro ajuste.
//
// Substituiu o painel de Top 5 de 14/09. O motivo é de leitura, não de espaço:
// num painel de controle o que vale é o campeão, e as cinco linhas gastavam a
// altura toda para deixar o número em 12,5px. Aqui ele vai a 29px, e a altura
// que sobra volta para a Tendência.
//
// `linhas` continua sendo a lista inteira que o backend manda — o card usa a
// primeira. Manter a lista preserva a distinção que o painel já fazia: `null`
// é "ainda não respondeu" e `[]` é "respondeu e não há nada". Sem ela o card
// diria "cadastre" durante a chamada ([[verificacao-que-nao-verifica]]).
const CardCampeao = ({ titulo, colunas, linhas, cor, vazio, rodapeEsq, rodapeDir,
                       destino, onIr }) => {
  const carregando = linhas === null;
  const l = carregando ? null : linhas[0];
  return (
    <div style={{ background: '#FFF', padding: '15px 18px', display: 'flex',
      flexDirection: 'column', cursor: destino ? 'pointer' : 'default' }}
      onClick={destino ? () => onIr(destino) : undefined}
      title={destino ? 'Abrir a aba de origem' : ''}>
      <div style={{ fontSize: 11, color: '#4A5563', letterSpacing: '0.10em',
        fontWeight: 600, textDecoration: 'underline', textDecorationColor: '#F47C3C',
        textDecorationThickness: 2, textUnderlineOffset: 3, marginBottom: 12 }}>
        {titulo} →
      </div>

      {carregando ? (
        <div style={{ fontSize: 11, color: '#7F8C8D', padding: '26px 0',
          textAlign: 'center' }}>Carregando…</div>
      ) : !l ? (
        <div style={{ fontSize: 11.5, color: '#7F8C8D', padding: '22px 0',
          textAlign: 'center', lineHeight: 1.5 }}>{vazio}</div>
      ) : (
        <>
          {/* 57px = duas linhas de nome mais a linha da posição. A altura é
              RESERVADA mesmo quando o nome ocupa uma linha só ("Aluguel") e
              mesmo nos cards que não têm posição: sem isso o número de cada
              card nasce numa altura diferente e os quatro deixam de se ler na
              horizontal. */}
          <div style={{ minHeight: 57 }}>
            {/* Duas linhas, sem reticências no meio da palavra. Era o gargalo
                do painel anterior: o nome dividia a linha com os números e
                sobravam 101px, que cortam "AP 12-14 X 4 S…". */}
            <div style={{ fontSize: 16, fontWeight: 600, color: '#1A1A1A',
              lineHeight: 1.22, letterSpacing: '-0.01em', display: '-webkit-box',
              WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}
              title={l.nome}>{l.nome}</div>
            {/* `pos` (14/09, mantido): o card de rentabilidade não numera — o
                número é a posição do produto no ranking de VENDAS. Virou texto
                porque um "38" solto ao lado do nome não diz do que é. */}
            {l.pos != null && (
              <div style={{ fontSize: 10.5, color: '#9AA5A0', fontWeight: 600,
                marginTop: 4 }}>{l.pos}º em vendas</div>
            )}
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr',
            gap: '0 14px', marginTop: 12 }}>
            {/* As duas métricas, os dois rótulos e o par de cores são os do
                painel anterior: a 1ª em preto, a 2ª em verde (vermelho se
                alerta). A cor da modalidade vive na barra, não no número. */}
            <div>
              <div style={{ fontSize: 9.5, fontWeight: 700, letterSpacing: '0.07em',
                textTransform: 'uppercase', color: '#9AA5A0', whiteSpace: 'nowrap',
                marginBottom: 2 }}>{colunas[0]}</div>
              <div style={{ fontSize: 29, fontWeight: 700, letterSpacing: '-0.025em',
                lineHeight: 1, fontVariantNumeric: 'tabular-nums',
                color: '#1A1A1A' }}>{fmtPct(l.a)}</div>
            </div>
            {colunas.length > 1 && (
              <div>
                <div style={{ fontSize: 9.5, fontWeight: 700, letterSpacing: '0.07em',
                  textTransform: 'uppercase', color: '#9AA5A0', whiteSpace: 'nowrap',
                  marginBottom: 2 }}>{colunas[1]}</div>
                <div style={{ fontSize: 29, fontWeight: 700, letterSpacing: '-0.025em',
                  lineHeight: 1, fontVariantNumeric: 'tabular-nums',
                  color: l.alerta ? '#DC2626' : '#1B6B2F' }}>{fmtPct(l.b)}</div>
              </div>
            )}
          </div>

          {/* A barra mede o CRITÉRIO DO CARD, que nem sempre é a 1ª coluna: no
              card de vendas a 1ª é a margem, mas o campeão é o que mais vende.
              Sem `barra`, cai na 1ª coluna. */}
          <div style={{ height: 3, background: '#EFEFE9', margin: '13px 0 0' }}>
            <div style={{ height: 3, background: cor,
              width: `${Math.min(100, Math.max(0, ((l.barra ?? l.a) || 0) * 100))}%` }}/>
          </div>

          {/* `flexWrap`: em tela estreita os dois textos quebram em vez de
              espremer o card. */}
          <div style={{ display: 'flex', justifyContent: 'space-between',
            flexWrap: 'wrap', gap: '0 8px',
            fontSize: 10.5, color: '#7F8C8D', marginTop: 'auto', paddingTop: 9,
            borderTop: '1px solid #E4E4DE' }}>
            <span>{rodapeEsq}</span><span>{rodapeDir}</span>
          </div>
        </>
      )}
    </div>
  );
};

// `horizontal` (14/09): os três níveis lado a lado em vez de empilhados. O
// card saiu do Tier 1 — onde tinha altura de sobra — para o lado do Valor
// Empresa, numa faixa baixa. Um prop, não um componente novo: duas cópias
// divergiriam no primeiro ajuste de alerta.
function AlertasMiniInline({ empresa, parametros, periodoInicio, periodoFim, onVerTodos,
                             horizontal = false }) {
  // `null` = ainda não respondeu. Distinto de `[]`, que é "respondeu e não há
  // alerta". Enquanto o estado inicial era `[]`, o card exibia 0 · 0 · 0 e
  // "0 ativos" durante os 2 a 4 segundos da chamada — com a mesma cara de
  // resposta pronta. O consultor lia "esta empresa não tem alerta", e era
  // falso ([[verificacao-que-nao-verifica]]).
  const [alertas, setAlertas] = React.useState(null);
  const [erro, setErro] = React.useState(false);

  // Mesmo recorte da aba Alertas: o seletor de período é único e comanda a
  // ferramenta inteira ([[seletor-periodo-unico]]). Sem isto o card lia todo o
  // histórico enquanto a aba lia o trimestre escolhido — dois números sob o
  // mesmo nome, em duas telas que o consultor compara lado a lado.
  const dataInicio = periodoInicio?.inicio || null;
  const dataFim    = periodoFim?.fim || null;

  React.useEffect(() => {
    if (!empresa || !parametros) return;
    let vivo = true;
    // Volta a "carregando" ao trocar de período: manter o número antigo sob o
    // rótulo novo seria a mesma mentira, só que mais convincente.
    setAlertas(null);
    const carregar = async () => {
      try {
        const url = new URL(`${API}/indicadores/alertas/${empresa.id}`);
        if (dataInicio && dataFim) {
          url.searchParams.set('data_inicio', dataInicio);
          url.searchParams.set('data_fim',    dataFim);
        }
        // ?? p/ preservar 0 (Simples tem ir=0)
        url.searchParams.set('saldo_cc',          parametros.saldo_cc          ?? 0);
        url.searchParams.set('saldo_cdb',         parametros.saldo_cdb         ?? 0);
        url.searchParams.set('ir',                parametros.ir                ?? 0.15);
        url.searchParams.set('depreciacao_mensal',parametros.depreciacao_mensal?? 0);
        url.searchParams.set('wacc_ke',           parametros.wacc_ke           ?? 0.28);
        url.searchParams.set('wacc_kd',           parametros.wacc_kd           ?? 0.14);
        url.searchParams.set('wacc_e',            parametros.wacc_e            ?? 500000);
        url.searchParams.set('wacc_d',            parametros.wacc_d            ?? 200000);
        const res = await fetch(url);
        if (!res.ok) throw new Error('erro');
        const data = await res.json();
        if (!vivo) return;
        setAlertas(data);
        setErro(false);
      } catch(e) { if (vivo) setErro(true); }
    };
    carregar();
    return () => { vivo = false; };
  }, [empresa, parametros, dataInicio, dataFim]);

  // Sem empresa ou sem parâmetros não há o que consultar — o card mostra "—",
  // nunca "Carregando…", que ficaria eterno.
  const semConsulta = !empresa || !parametros;
  const carregando  = !semConsulta && alertas === null && !erro;
  const lista       = alertas || [];

  // Inclui 'critico' (era bug: criticos sumiam do card do Dashboard, batendo divergente
  // com a aba Alertas detalhada — empresas com alertas criticos pareciam ter menos).
  // 3 severidades (consolidado): critico · atencao · positivo. Card mostra os 3 contadores.
  const criticos  = lista.filter(a => a.severidade === 'critico').length;
  const atencoes  = lista.filter(a => a.severidade === 'atencao').length;
  const positivos = lista.filter(a => a.severidade === 'positivo').length;

  return (
    // `justifyContent: center` (14/09): o card ficou mais estreito que os
    // vizinhos e o conteúdo é curto — encostado no topo, sobrava um vazio
    // embaixo que desalinhava a linha inteira.
    <div style={{ background: '#FFF', padding: '14px 20px',
      display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between',
        alignItems: 'center', marginBottom: 10 }}>
        <div onClick={onVerTodos} title="Ver todos os alertas"
          style={{ fontSize: 12, fontWeight: 600,
            letterSpacing: '0.14em', color: '#F47C3C',
            textTransform: 'uppercase', cursor: 'pointer',
            textDecoration: 'underline', textDecorationColor: 'rgba(244,124,60,0.35)',
            textUnderlineOffset: 3 }}>
          ALERTAS →
        </div>
        <span onClick={onVerTodos} style={{ fontSize: 11, color: '#7F8C8D',
          cursor: 'pointer' }}>
          {carregando ? 'Carregando…' : semConsulta ? '—' : `${lista.length} ativos`}
        </span>
      </div>

      <div style={{ display: horizontal ? 'grid' : 'flex',
        gridTemplateColumns: horizontal ? 'repeat(3, 1fr)' : undefined,
        flexDirection: horizontal ? undefined : 'column',
        gap: 6, marginBottom: horizontal ? 0 : 10 }}>
        {[
          { label: 'CRITICO',  val: criticos, cor: '#DC2626', bg: '#FEF2F2' },
          { label: 'ATENCAO',  val: atencoes, cor: '#F59E0B', bg: '#FFFBEB' },
          { label: 'POSITIVO', val: positivos, cor: '#1B6B2F', bg: '#EBF5EE' },
        ].map(item => (
          <div key={item.label} style={{ padding: '9px 14px', background: item.bg,
            display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ fontSize: 11, color: '#7F8C8D',
              letterSpacing: '0.08em', fontWeight: 600 }}>{item.label}</div>
            <div style={{ fontSize: 22, fontWeight: 700,
              color: (carregando || semConsulta) ? '#C7CDD2' : item.cor,
              lineHeight: 1 }}>
              {(carregando || semConsulta) ? '—' : item.val}
            </div>
          </div>
        ))}
      </div>

      {erro && (
        <div style={{ color: '#C03030', fontSize: 10 }}>
          Erro ao carregar alertas
        </div>
      )}
    </div>
  );
}

function TelaCriarEmpresa({ perfilEmail, onCriada }) {
  const SETORES = [
    'Comercio',
    'Servicos',
    'Industria',
    'Tecnologia',
    'Saude',
    'Educacao',
    'Alimentacao',
    'Construcao',
    'Agronegocio',
    'Outro',
  ];
  const [nome, setNome] = React.useState('');
  const [setor, setSetor] = React.useState('');
  const [salvando, setSalvando] = React.useState(false);
  const [erro, setErro] = React.useState(null);
  const [focado, setFocado] = React.useState(null);

  const handleCriar = async () => {
    setErro(null);
    if (!nome.trim()) { setErro('Informe o nome da empresa'); return; }
    if (!setor) { setErro('Selecione o setor'); return; }
    setSalvando(true);
    try {
      const res = await fetch(`${API}/empresas/criar`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ nome: nome.trim(), setor }),
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Erro ao criar empresa');
      }
      const data = await res.json();
      onCriada(data);
    } catch(e) {
      setErro(e.message);
      setSalvando(false);
    }
  };

  const fonte = "'Outfit', -apple-system, BlinkMacSystemFont, sans-serif";

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      fontFamily: fonte,
      background: '#FFF',
      color: '#1A1A1A',
    }}>
      {/* Painel verde esquerdo */}
      <div style={{
        flex: '0 0 44%',
        background: '#1B6B2F',
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        padding: '56px 56px',
        color: '#FFF',
        overflow: 'hidden',
      }}>
        <div>
          <img
            src="/logos/RUNGOAL_LOGO_SF_V2.png"
            alt="RunGoal"
            style={{ height: 42, width: 'auto', display: 'block' }}
          />
        </div>
        <div style={{ position: 'relative', zIndex: 1 }}>
          <div style={{
            fontSize: 13, fontWeight: 600, letterSpacing: '0.18em',
            opacity: 0.7, textTransform: 'uppercase', marginBottom: 16,
          }}>
            COMECE SUA JORNADA
          </div>
          <h2 style={{
            fontSize: 36, fontWeight: 700, lineHeight: 1.15,
            margin: 0, letterSpacing: '-0.02em',
          }}>
            Voce esta a um passo<br/>de transformar sua<br/>gestao financeira.
          </h2>
          <p style={{
            fontSize: 15, lineHeight: 1.6, marginTop: 24,
            opacity: 0.85, fontWeight: 400, maxWidth: 360,
          }}>
            Cadastre sua empresa para acessar indicadores
            financeiros que vao guiar suas decisoes com seguranca.
          </p>
        </div>
        <div style={{ fontSize: 12, opacity: 0.5, fontWeight: 400 }}>
          rungoal © 2025
        </div>
      </div>

      {/* Painel direito - formulario */}
      <div style={{
        flex: 1, display: 'flex',
        alignItems: 'center', justifyContent: 'center', padding: 40,
      }}>
        <div style={{ width: '100%', maxWidth: 400 }}>
          <div style={{
            fontSize: 11, fontWeight: 600, letterSpacing: '0.16em',
            color: '#F47C3C', textTransform: 'uppercase', marginBottom: 8,
          }}>
            BEM-VINDO
          </div>
          <h1 style={{
            fontSize: 28, fontWeight: 700, color: '#1A1A1A',
            margin: 0, letterSpacing: '-0.02em', marginBottom: 8,
          }}>
            Cadastre sua empresa
          </h1>
          <p style={{
            fontSize: 14, color: '#6B7280', marginTop: 0, marginBottom: 28,
            lineHeight: 1.5,
          }}>
            Apenas o essencial para comecar. Voce completa
            o cadastro depois em Configuração › Cadastro.
          </p>

          <div style={{ marginBottom: 18 }}>
            <label style={{
              display: 'block', fontSize: 13, fontWeight: 500,
              color: focado === 'nome' ? '#1B6B2F' : '#4A5563',
              marginBottom: 8, transition: 'color 0.2s',
            }}>
              Nome da empresa
            </label>
            <input
              type="text"
              value={nome}
              onChange={e => setNome(e.target.value)}
              onFocus={() => setFocado('nome')}
              onBlur={() => setFocado(null)}
              placeholder="Ex: Padaria do Joao"
              style={{
                width: '100%', padding: '14px 16px',
                fontSize: 15, fontFamily: fonte, fontWeight: 400,
                color: '#1A1A1A', background: '#FFF',
                border: `1.5px solid ${focado === 'nome' ? '#1B6B2F' : '#D5DAE2'}`,
                borderRadius: 0, boxSizing: 'border-box',
                outline: 'none', transition: 'border-color 0.2s, box-shadow 0.2s',
                boxShadow: focado === 'nome' ? '0 0 0 4px #E8F2EB' : 'none',
              }}/>
          </div>

          <div style={{ marginBottom: 24 }}>
            <label style={{
              display: 'block', fontSize: 13, fontWeight: 500,
              color: focado === 'setor' ? '#1B6B2F' : '#4A5563',
              marginBottom: 8, transition: 'color 0.2s',
            }}>
              Setor
            </label>
            <select
              value={setor}
              onChange={e => setSetor(e.target.value)}
              onFocus={() => setFocado('setor')}
              onBlur={() => setFocado(null)}
              style={{
                width: '100%', padding: '14px 16px',
                fontSize: 15, fontFamily: fonte, fontWeight: 400,
                color: setor ? '#1A1A1A' : '#8A95A4', background: '#FFF',
                border: `1.5px solid ${focado === 'setor' ? '#1B6B2F' : '#D5DAE2'}`,
                borderRadius: 0, boxSizing: 'border-box',
                outline: 'none', transition: 'border-color 0.2s, box-shadow 0.2s',
                boxShadow: focado === 'setor' ? '0 0 0 4px #E8F2EB' : 'none',
                cursor: 'pointer',
              }}>
              <option value="">Selecione um setor</option>
              {SETORES.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>

          {erro && (
            <div style={{
              padding: 12, background: '#FDEAEA',
              color: '#D14848', fontSize: 13, marginBottom: 16,
              fontWeight: 500, borderRadius: 0,
            }}>
              {erro}
            </div>
          )}

          <button
            onClick={handleCriar}
            disabled={salvando}
            style={{
              width: '100%', padding: 15,
              fontSize: 15, fontWeight: 600, fontFamily: fonte,
              color: '#FFF', background: '#1B6B2F',
              border: 'none', borderRadius: 0,
              cursor: salvando ? 'not-allowed' : 'pointer',
              opacity: salvando ? 0.6 : 1,
              transition: 'opacity 0.15s, background 0.15s',
              letterSpacing: '0.01em',
            }}>
            {salvando ? 'Criando empresa...' : 'Criar empresa e comecar'}
          </button>

          <div style={{
            marginTop: 20, fontSize: 12, color: '#8A95A4',
            textAlign: 'center',
          }}>
            Logado como: <span style={{ color: '#4A5563', fontWeight: 500 }}>{perfilEmail}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// DADOS DE ENTRADA — hub (LP · 3 fontes) + templates novos
// Substitui o que a aba "Extrato" renderiza. id de menu segue 'extrato'.
// ═══════════════════════════════════════════════════════════════════════

// Template · Documentos Contábeis (porta única: Balancete mensal/anual + Razão · a IA identifica tipo e período)
// Frontend agora (interface + upload) · leitura/ingestão pela IA é etapa dedicada (Etapa 3 · Fonte Contábil).
const TelaDocumentosContabeis = ({ empresa }) => {
  const [mostrarImportar, setMostrarImportar] = useState(false);
  const [enviados, setEnviados] = useState([]);
  const [carregandoAcervo, setCarregandoAcervo] = useState(true);
  // Mês digitado por documento pendente, fora da lista: o acervo é recarregado
  // do servidor e não pode levar embora o que o usuário acabou de escolher.
  const [mesPorDoc, setMesPorDoc] = useState({});
  const inputRef = useRef(null);
  // Pré-voo (19/09/2026): o relatório de conferência e o resultado do
  // processamento. `null` é "ainda não conferiu" — distinto de um relatório
  // vazio, que seria "conferiu e não há nada a fazer".
  const [conferencia, setConferencia] = useState(null);
  const [conferindo, setConferindo] = useState(false);
  const [processando, setProcessando] = useState(false);
  const [resultado, setResultado] = useState(null);
  const [erroAcao, setErroAcao] = useState(null);
  // Conta renomeada na troca de sistema: a escolha do usuário por linha, e
  // qual está salvando. A escolha vive fora do relatório porque o relatório é
  // recarregado a cada conferência e levaria a seleção embora.
  const [escolhaRenom, setEscolhaRenom] = useState({});
  const [salvandoRenom, setSalvandoRenom] = useState(null);
  // A lista de contas renomeadas nasce FECHADA: ela é o detalhe de uma
  // premissa já adotada, não uma fila de perguntas a responder.
  const [verRenom, setVerRenom] = useState(false);

  // Carrega o acervo já enviado (o servidor é a fonte da verdade — a lista
  // persiste ao sair e voltar da tela). Em `useCallback` porque o
  // processamento muda o status dos documentos e precisa reler a lista: sem
  // isto a tela continuaria dizendo "Identificado" depois de processar.
  const carregarAcervo = React.useCallback(async () => {
    if (!empresa?.id) { setCarregandoAcervo(false); return; }
    try {
      const r = await fetch(`${API}/documentos-contabeis/${empresa.id}`);
      if (r.ok) {
        const docs = await r.json();
        setEnviados(docs.map(d => ({
          id: d.id, nome: d.nome, status: d.status, tipo: d.tipo,
          periodo_inicio: d.periodo_inicio, periodo_fim: d.periodo_fim,
        })));
      }
    } catch (e) { /* acervo indisponível: a tela segue permitindo enviar */ }
    setCarregandoAcervo(false);
  }, [empresa?.id]);

  useEffect(() => { carregarAcervo(); }, [carregarAcervo]);

  // Trocar de empresa zera o relatório: o pré-voo é de UM acervo, e deixá-lo
  // na tela faria o consultor ler o diagnóstico de um cliente sob o nome de
  // outro ([[blindagem-contaminacao-empresas]]).
  useEffect(() => {
    setConferencia(null); setResultado(null); setErroAcao(null);
  }, [empresa?.id]);

  const conferir = async () => {
    setConferindo(true); setErroAcao(null); setResultado(null);
    try {
      const r = await fetch(`${API}/documentos-contabeis/conferir/${empresa.id}`);
      if (!r.ok) throw new Error('falha');
      setConferencia(await r.json());
    } catch (e) {
      setErroAcao('Não foi possível conferir os documentos agora. Tente de novo.');
    }
    setConferindo(false);
  };

  const confirmarRenom = async (velho, novo, descartar) => {
    setSalvandoRenom(velho); setErroAcao(null);
    try {
      const r = await fetch(
        `${API}/documentos-contabeis/contas-renomeadas/${empresa.id}`,
        { method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ nome_antigo: velho, nome_novo: novo,
                                 descartar: !!descartar }) });
      if (!r.ok) throw new Error('falha');
      // Reconfere: a conta decidida sai da lista, e as médias das outras podem
      // mudar — unir duas contas muda a série de quem restou.
      const c = await fetch(`${API}/documentos-contabeis/conferir/${empresa.id}`);
      if (c.ok) setConferencia(await c.json());
    } catch (e) {
      setErroAcao('Não foi possível registrar a decisão sobre esta conta.');
    }
    setSalvandoRenom(null);
  };

  // O processamento GRAVA. Reconfere no servidor antes de chamar — o relatório
  // na tela pode ter minutos e alguém pode ter enviado outro arquivo nesse
  // meio-tempo. É a mesma conferência, não uma versão de tela.
  const processar = async () => {
    setProcessando(true); setErroAcao(null);
    try {
      const c = await fetch(`${API}/documentos-contabeis/conferir/${empresa.id}`);
      if (!c.ok) throw new Error('falha');
      const rel = await c.json();
      setConferencia(rel);
      if (!rel.pode_processar) {
        setErroAcao('Há documento que precisa de correção antes de gravar. '
                    + 'Veja a lista abaixo — o resto do acervo não foi tocado.');
        setProcessando(false);
        return;
      }
      const r = await fetch(`${API}/documentos-contabeis/processar/${empresa.id}`,
        { method: 'POST' });
      if (!r.ok) throw new Error('falha');
      setResultado(await r.json());
      await carregarAcervo();
      // Reconfere depois de gravar: os documentos passaram a 'processado' e o
      // relatório antigo diria que ainda há o que fazer.
      const d = await fetch(`${API}/documentos-contabeis/conferir/${empresa.id}`);
      if (d.ok) setConferencia(await d.json());
    } catch (e) {
      // NÃO dizer "nada foi gravado": o servidor grava documento a documento,
      // e se o quinto falhar os quatro anteriores já estão no banco. Afirmar o
      // contrário sobre dado financeiro é pior do que não afirmar nada.
      setErroAcao('A gravação foi interrompida. Cada documento é gravado por '
                  + 'inteiro ou não é gravado, e cada mês substitui o anterior '
                  + '— mas parte do lote pode já ter entrado. Confira de novo '
                  + 'para ver o que ficou pendente e processe outra vez: '
                  + 'reprocessar substitui, nunca soma.');
    }
    setProcessando(false);
  };

  const aoSelecionar = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setMostrarImportar(false);
    if (inputRef.current) inputRef.current.value = '';
    // Só dispara a gravação se ALGUM arquivo virou documento novo. Reenviar um
    // que já está no acervo devolve 'duplicado' e não cria pendência — e sem
    // esta guarda o `processar` cairia no ramo "releia tudo", minutos de
    // trabalho para nada.
    let entrouAlgum = false;
    for (const f of files) {
      const marca = { nome: f.name, tamanho: f.size, status: 'enviando' };
      setEnviados(prev => [...prev, marca]);
      try {
        const form = new FormData();
        form.append('file', f);
        const res = await fetch(`${API}/documentos-contabeis/upload/${empresa.id}`,
          { method: 'POST', body: form });
        const data = await res.json();
        if (!res.ok) throw new Error(data.detail || 'Falha ao enviar.');
        if (data.status && data.status !== 'duplicado') entrouAlgum = true;
        setEnviados(prev => prev.map(d => d === marca
          ? { ...d, id: data.documento_id, status: data.status, tipo: data.tipo,
              motivo: data.motivo,
              periodo_inicio: data.periodo_inicio, periodo_fim: data.periodo_fim } : d));
      } catch (err) {
        setEnviados(prev => prev.map(d => d === marca
          ? { ...d, status: 'erro', erro: err.message } : d));
      }
    }
    // O app flui (22/09): subiu, a ferramenta confere e grava sozinha. Até
    // aqui o upload não virava número em tela nenhuma até alguém clicar
    // Conferir e depois Processar — dois cliques que não decidiam nada, porque
    // a conferência é aritmética e a gravação é idempotente. O que exige
    // pessoa continua exigindo: documento com impeditivo fica de fora, com o
    // motivo na tela, e o painel segue disponível para reprocessar o acervo.
    if (entrouAlgum) await processar();
  };

  // Balancete cujo arquivo não trouxe o mês de referência: o campo já vem
  // preenchido com a sugestão tirada do nome do arquivo, mas quem confirma é
  // quem envia — nome de arquivo pode estar errado sem contradizer nada, e o
  // saldo iria para o mês errado, calado.
  const confirmarMes = async (doc, inicio, fim) => {
    // Sem período não há o que gravar — e calar aqui deixaria o usuário
    // clicando sem entender por que nada acontece.
    const ok = m => /^20\d{2}-(0[1-9]|1[0-2])$/.test(m || '');
    if (!ok(inicio) || !ok(fim)) {
      setEnviados(prev => prev.map(d => d.id === doc.id
        ? { ...d, erroMes: 'Informe o período de referência (ex.: 01/2025 a 12/2025).' } : d));
      return;
    }
    if (fim < inicio) {
      setEnviados(prev => prev.map(d => d.id === doc.id
        ? { ...d, erroMes: 'O mês final é anterior ao inicial.' } : d));
      return;
    }
    setEnviados(prev => prev.map(d => d.id === doc.id ? { ...d, salvandoMes: true } : d));
    try {
      const r = await fetch(`${API}/documentos-contabeis/${doc.id}/periodo`,
        { method: 'PATCH', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ inicio, fim }) });
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || 'Falha ao gravar o mês.');
      setEnviados(prev => prev.map(d => d.id === doc.id
        ? { ...d, status: data.status, tipo: data.tipo, salvandoMes: false, erroMes: null,
            periodo_inicio: data.periodo_inicio, periodo_fim: data.periodo_fim } : d));
    } catch (err) {
      setEnviados(prev => prev.map(d => d.id === doc.id
        ? { ...d, salvandoMes: false, erroMes: err.message } : d));
    }
  };

  return (
    <div style={{ padding: '24px 28px', position: 'relative', overflow: 'hidden' }}>
      <CarimboAIdvisor />
      <div style={{ marginBottom: 20 }}>
        <div style={{ position: 'relative', display: 'inline-block' }}>
          <h2 style={{ margin: 0, fontSize: 20, fontWeight: 800, color: G.txt1,
            position: 'relative', zIndex: 1 }}>
            Documentos Contábeis
          </h2>
          <MarcaDaguaTitulo>
            <rect x="4" y="2" width="16" height="20" rx="2"/>
            <line x1="8" y1="7" x2="16" y2="7"/>
            <line x1="8" y1="11" x2="16" y2="11"/>
            <line x1="8" y1="15" x2="13" y2="15"/>
          </MarcaDaguaTitulo>
        </div>
        {/* Frase em linha própria: sem o nome da empresa à frente ela termina
            antes do CarimboAIdvisor, que fica absoluto no canto direito. */}
        <p style={{ margin: '2px 0 0', fontSize: 13, color: G.txt2 }}>
          Faça o Upload de Balancete (mensal ou anual) e Razão Analítico. A <MarcaAIdvisor variant="default" /> os identifica e monta DRE e Balanço.
        </p>
      </div>

      {/* Banner Importar · espelha o padrão do Extrato */}
      <div style={{ marginBottom: 14, background: G.verde, borderRadius: 0, overflow: 'hidden' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '14px 20px', gap: 16 }}>
          <div>
            <div style={{ fontSize: 17, fontWeight: 800, color: '#FFF', letterSpacing: '-0.005em' }}>
              Importar documentos
            </div>
            <div style={{ fontSize: 12, color: '#FFF', opacity: 0.92, marginTop: 3 }}>
              PDF, CSV e Excel exportados do sistema contábil (não digitalizados).
            </div>
          </div>
          <button onClick={() => (mostrarImportar ? setMostrarImportar(false) : setMostrarImportar(true))}
            style={{ background: G.verde, color: '#FFF', border: '2px solid #FFF', borderRadius: 0,
              padding: '7px 18px', fontSize: 11, fontWeight: 700, letterSpacing: '0.08em',
              textTransform: 'uppercase', cursor: 'pointer', whiteSpace: 'nowrap' }}>
            {mostrarImportar ? 'Fechar ×' : 'Importar'}
          </button>
        </div>
        {mostrarImportar && (
          <div style={{ padding: '12px 20px 16px', borderTop: '1px solid rgba(255,255,255,0.30)',
            background: 'rgba(255,255,255,0.10)', display: 'flex', alignItems: 'center', gap: 12 }}>
            <input ref={inputRef} type="file" multiple accept=".pdf,.csv,.xlsx,.xls"
              onChange={aoSelecionar}
              style={{ fontSize: 12, color: '#FFF' }} />
          </div>
        )}
      </div>

      {carregandoAcervo && (
        <div style={{ fontSize: 12.5, color: G.txt2, fontStyle: 'italic', marginBottom: 14 }}>
          Carregando documentos já enviados…
        </div>
      )}

      {/* ══════════ PRÉ-VOO (19/09/2026) ══════════
          O passo que faltava entre "Identificado ✓" e virar número. Até aqui o
          endpoint de processar existia no servidor e NENHUMA tela o chamava: o
          usuário subia o documento, lia "Identificado ✓" e nada acontecia, sem
          uma linha dizendo que faltava um passo. Os documentos de 2024 da Mored
          estavam "Processado" porque um script os processou, nunca a tela.

          Conferir primeiro, processar depois: as travas já existiam, mas só
          rodavam DENTRO da gravação — o usuário descobria o problema quando os
          documentos bons já estavam no banco. */}
      {/* A condição olha para o ACERVO, nunca para o que está pendente. Já foi
          remendada uma vez (19/09: `|| resultado`, porque o painel sumia logo
          depois de processar) e o remendo não pegou a causa: em 22/09 o Fabio
          abriu a tela com os 44 documentos da Mored todos em 'processado' e não
          havia botão Conferir nenhum. Conferir é LEITURA — não grava nada — e o
          `_lote()` do backend trata 'identificado' e 'processado' como o mesmo
          universo. Enquanto houver documento no acervo, o painel fica de pé. */}
      {enviados.length > 0 && (
        <div style={{ marginBottom: 16, background: '#FFF',
          border: `1px solid ${G.borda}`, borderRadius: 0, padding: '14px 18px' }}>

          <div style={{ display: 'flex', alignItems: 'center',
            justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 13, fontWeight: 800, color: G.txt1 }}>
                Conferir e processar
              </div>
              <div style={{ fontSize: 12, color: G.txt2, marginTop: 2 }}>
                {conferencia
                  ? `${conferencia.total.prontos} de ${conferencia.total.documentos} documentos prontos`
                  : 'A ferramenta lê cada documento e aponta o que precisa de correção — '
                    + 'sem gravar nada.'}
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <button onClick={conferir} disabled={conferindo || processando}
                style={{ background: '#FFF', color: G.verde, border: `2px solid ${G.verde}`,
                  borderRadius: 0, padding: '8px 18px', fontSize: 11, fontWeight: 800,
                  letterSpacing: '0.08em', textTransform: 'uppercase',
                  fontFamily: 'inherit',
                  cursor: (conferindo || processando) ? 'default' : 'pointer',
                  opacity: (conferindo || processando) ? 0.6 : 1 }}>
                {conferindo ? 'Conferindo…' : (conferencia ? 'Conferir de novo' : 'Conferir')}
              </button>
              {/* O botão de gravar só aparece DEPOIS da conferência. Antes dela
                  não há como saber se o acervo está bom, e um botão sempre
                  disponível convida a pular o passo que existe para proteger. */}
              {conferencia && (
                <button onClick={processar}
                  disabled={!conferencia.pode_processar || processando || conferindo}
                  title={conferencia.pode_processar
                    ? 'Grava os saldos e os lançamentos. Reprocessa o acervo inteiro, '
                      + 'não só os pendentes — pode levar alguns minutos.'
                    : 'Corrija os documentos impedidos antes de processar'}
                  style={{ background: conferencia.pode_processar ? G.laranja : G.borda,
                    color: conferencia.pode_processar ? '#FFF' : G.txt2, border: 'none',
                    borderRadius: 0, padding: '8px 22px', fontSize: 11, fontWeight: 800,
                    letterSpacing: '0.08em', textTransform: 'uppercase',
                    fontFamily: 'inherit',
                    cursor: (conferencia.pode_processar && !processando) ? 'pointer' : 'default' }}>
                  {processando ? 'Processando…' : 'Processar →'}
                </button>
              )}
            </div>
          </div>

          {erroAcao && (
            <div style={{ marginTop: 12, padding: '10px 12px', background: G.vermelhoL,
              border: `1px solid ${G.vermelho}`, borderRadius: 0,
              fontSize: 12.5, color: G.vermelho, lineHeight: 1.45 }}>
              {erroAcao}
            </div>
          )}

          {/* IMPEDITIVO · o documento não é gravado, e o motivo é o mesmo que o
              processamento daria. Vermelho porque exige ação. */}
          {conferencia?.impeditivos?.length > 0 && (
            <div style={{ marginTop: 12 }}>
              <div style={{ fontSize: 11, fontWeight: 800, color: G.vermelho,
                letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 6 }}>
                {conferencia.impeditivos.length} documento(s) precisam de correção
              </div>
              {conferencia.impeditivos.map((im, i) => (
                <div key={i} style={{ padding: '9px 12px', background: G.vermelhoL,
                  border: `1px solid ${G.vermelho}`, borderRadius: 0, marginBottom: 5 }}>
                  <div style={{ fontSize: 12.5, fontWeight: 700, color: G.txt1 }}>
                    {im.arquivo}{im.mes ? ` · ${im.mes}` : ''}
                  </div>
                  <div style={{ fontSize: 12, color: G.vermelho, marginTop: 2,
                    lineHeight: 1.45 }}>{im.motivo}</div>
                </div>
              ))}
            </div>
          )}

          {/* AVISO · grava assim mesmo, e o usuário precisa saber. Âmbar, não
              vermelho: divergência entre os documentos do escritório é achado,
              não erro nosso — nós comparamos e mostramos, não escolhemos lado. */}
          {conferencia?.avisos?.length > 0 && (
            <div style={{ marginTop: 12 }}>
              <div style={{ fontSize: 11, fontWeight: 800, color: '#B45309',
                letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 6 }}>
                {conferencia.avisos.length} ponto(s) de atenção
              </div>
              {conferencia.avisos.map((av, i) => (
                <div key={i} style={{ padding: '9px 12px', background: '#FFFBEB',
                  border: '1px solid #FDE68A', borderRadius: 0, marginBottom: 5 }}>
                  <div style={{ fontSize: 12.5, fontWeight: 700, color: G.txt1 }}>
                    {av.titulo}{av.arquivo ? ` · ${av.arquivo}` : ''}
                  </div>
                  <div style={{ fontSize: 12, color: '#B45309', marginTop: 2,
                    lineHeight: 1.45 }}>{av.detalhe}</div>
                  {/* O cruzamento razão × balancete vem com as contas. É o
                      achado que nenhum dos dois documentos dá sozinho, e é por
                      conta que o contador procura. */}
                  {av.contas?.length > 0 && (
                    <div style={{ marginTop: 8, display: 'grid',
                      gridTemplateColumns: 'minmax(0,1fr) repeat(2, 96px)',
                      gap: '3px 10px', fontSize: 11.5 }}>
                      <span style={{ fontWeight: 700, color: G.txt2 }}>
                        Conta <span style={{ fontWeight: 400 }}>
                          ({av.lado || 'movimento'})</span>
                      </span>
                      <span style={{ fontWeight: 700, color: G.txt2, textAlign: 'right' }}>Razão</span>
                      <span style={{ fontWeight: 700, color: G.txt2, textAlign: 'right' }}>Balancete</span>
                      {av.contas.slice(0, 8).map((c, j) => {
                        const dif = Math.abs(c.diferenca_debito || 0)
                          >= Math.abs(c.diferenca_credito || 0);
                        return (
                          <React.Fragment key={j}>
                            <span style={{ color: G.txt1, overflow: 'hidden',
                              textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {c.nome || c.codigo}
                            </span>
                            <span style={{ textAlign: 'right',
                              fontVariantNumeric: 'tabular-nums' }}>
                              {fmtBRL(dif ? c.razao_debito : c.razao_credito)}
                            </span>
                            <span style={{ textAlign: 'right',
                              fontVariantNumeric: 'tabular-nums' }}>
                              {c.tipo === 'ausente' ? '—'
                                : fmtBRL(dif ? c.balancete_debito : c.balancete_credito)}
                            </span>
                          </React.Fragment>
                        );
                      })}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* ══════════ CONTAS RENOMEADAS (19/09/2026) ══════════
              Quando o escritório troca de sistema contábil, o plano de contas
              vem todo renomeado. A conta PATRIMONIAL a ferramenta une sozinha —
              o saldo de abertura do mês novo é o de fechamento do anterior, ao
              centavo. A de RESULTADO zera no encerramento do exercício e não
              tem esse número: aqui ela pergunta.

              Não é detalhe de arrumação. A classificação fixo × variável mede a
              correlação de cada conta com a receita ao longo da série INTEIRA;
              com a mesma conta partida em dois nomes, a correlação se desfaz
              nos dois e conta variável atravessa para o lado fixo. */}
          {conferencia?.contas_renomeadas?.length > 0 && (
            <div style={{ marginTop: 12 }}>
              <div style={{ fontSize: 11, fontWeight: 800, color: G.verde,
                letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 6 }}>
                O plano de contas mudou{conferencia.plano_mudou_em
                  ? ` em ${rotuloMesCurto(conferencia.plano_mudou_em)}` : ''}
              </div>
              <div style={{ fontSize: 12, color: G.txt2, marginBottom: 8,
                lineHeight: 1.45 }}>
                <strong style={{ color: G.txt1 }}>Premissa adotada:</strong> onde o
                saldo provou que a conta é a mesma, a ferramenta uniu sozinha. As
                outras {conferencia.contas_renomeadas.length} ficam
                {' '}<strong style={{ color: G.txt1 }}>separadas</strong> na série —
                nome parecido não é prova, e unir por semelhança já trocou conta
                por conta antes. Quem conhece a empresa pode unir uma a uma.
              </div>
              <button onClick={() => setVerRenom(v => !v)}
                style={{ background: 'transparent', color: G.verde,
                  border: `1px solid ${G.verde}`, borderRadius: 0,
                  padding: '6px 14px', fontSize: 11, fontWeight: 700,
                  letterSpacing: '0.06em', textTransform: 'uppercase',
                  fontFamily: 'inherit', cursor: 'pointer', marginBottom: 8 }}>
                {verRenom ? 'Ocultar as contas separadas'
                  : `Ver as ${conferencia.contas_renomeadas.length} e unir, se alguma for a mesma`}
              </button>
              {verRenom && conferencia.contas_renomeadas.slice(0, 12).map((r, i) => {
                const esc = escolhaRenom[r.nome_velho]
                  ?? r.candidatos[0]?.nome_novo ?? '';
                return (
                  <div key={i} style={{ padding: '10px 12px', background: '#FFF',
                    border: `1px solid ${G.borda}`, borderRadius: 0, marginBottom: 5 }}>
                    <div style={{ fontSize: 12.5, color: G.txt1 }}>
                      <strong>{r.nome_velho}</strong>
                      <span style={{ color: G.txt2 }}>
                        {' '}· {fmtBRL(r.media_velho)}/mês até {r.ultimo_mes_velho}
                      </span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8,
                      marginTop: 8, flexWrap: 'wrap' }}>
                      <span style={{ fontSize: 12, color: G.txt2 }}>virou</span>
                      {/* Um <select> e não uma lista de botões: são até nove
                          candidatos por conta, e nove botões por linha em doze
                          linhas é uma tela que ninguém lê. */}
                      <select value={esc}
                        onChange={e => setEscolhaRenom(m =>
                          ({ ...m, [r.nome_velho]: e.target.value }))}
                        style={{ fontFamily: 'inherit', fontSize: 12.5,
                          padding: '5px 8px', border: `1px solid ${G.borda}`,
                          borderRadius: 0, color: G.txt1, maxWidth: 380 }}>
                        {r.candidatos.map((c, j) => (
                          <option key={j} value={c.nome_novo}>
                            {c.nome_novo} — {fmtBRL(c.media_novo)}/mês desde {c.primeiro_mes_novo}
                          </option>
                        ))}
                      </select>
                      <button disabled={salvandoRenom === r.nome_velho}
                        onClick={() => confirmarRenom(r.nome_velho, esc, false)}
                        style={{ background: G.verde, color: '#FFF', border: 'none',
                          borderRadius: 0, padding: '6px 14px', fontSize: 11,
                          fontWeight: 700, letterSpacing: '0.06em',
                          textTransform: 'uppercase', fontFamily: 'inherit',
                          cursor: 'pointer' }}>
                        {salvandoRenom === r.nome_velho ? 'Salvando…' : 'É a mesma'}
                      </button>
                      {/* Descartar é tão necessário quanto unir: sem ele a mesma
                          sugestão volta a cada processamento e a pessoa aprende
                          a ignorar a lista toda. */}
                      <button disabled={salvandoRenom === r.nome_velho}
                        onClick={() => confirmarRenom(r.nome_velho, null, true)}
                        style={{ background: 'transparent', color: G.txt2,
                          border: `1px solid ${G.borda}`, borderRadius: 0,
                          padding: '6px 14px', fontSize: 11, fontWeight: 700,
                          letterSpacing: '0.06em', textTransform: 'uppercase',
                          fontFamily: 'inherit', cursor: 'pointer' }}>
                        Não é
                      </button>
                    </div>
                  </div>
                );
              })}
              {verRenom && conferencia.contas_renomeadas.length > 12 && (
                <div style={{ fontSize: 11.5, color: G.txt2, fontStyle: 'italic' }}>
                  Mostrando as 12 de maior valor. Confira de novo depois de
                  decidir estas para ver as seguintes.
                </div>
              )}
            </div>
          )}

          {/* Conferiu, passou e não há nada a apontar. Dizer isso é parte do
              trabalho: silêncio depois de conferir se lê como "não rodou". */}
          {conferencia && !conferencia.impeditivos.length
            && !conferencia.avisos.length && !resultado && (
            <div style={{ marginTop: 12, padding: '10px 12px', background: G.verdeLight,
              border: `1px solid ${G.verde}`, borderRadius: 0, fontSize: 12.5,
              color: G.verde, fontWeight: 700 }}>
              Tudo certo — {conferencia.total.prontos} documento(s) prontos para processar.
            </div>
          )}

          {resultado && (
            <div style={{ marginTop: 12, padding: '10px 12px', background: G.verdeLight,
              border: `1px solid ${G.verde}`, borderRadius: 0, fontSize: 12.5,
              color: G.txt1, lineHeight: 1.5 }}>
              <strong style={{ color: G.verde }}>Processado.</strong>{' '}
              {resultado.contas_gravadas} saldo(s) de conta e{' '}
              {resultado.lancamentos_gravados} lançamento(s) gravados
              {resultado.falhas > 0 && (
                <span style={{ color: G.vermelho }}>
                  {' '}· {resultado.falhas} documento(s) recusados no servidor
                </span>
              )}.
            </div>
          )}
        </div>
      )}

      {/* Documentos enviados · acervo da empresa (vem do servidor) */}
      {enviados.length > 0 && (
        <div style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 11, fontWeight: 800, color: G.txt2, letterSpacing: '0.08em',
            textTransform: 'uppercase', marginBottom: 8 }}>
            Documentos recebidos ({enviados.length})
          </div>
          {enviados.map((d, i) => {
            const S = {
              enviando:     { txt: 'Enviando…',      cor: G.txt2 },
              identificado: { txt: 'Identificado ✓', cor: G.verde },
              // 'processado' = já foi LIDO e virou número na ferramenta. Faltava
              // aqui, então os 26 balancetes da Mored — que já tinham gerado 4.006
              // saldos — apareciam como "Aguardando identificação" (25/07).
              processado:   { txt: 'Processado ✓',   cor: G.verde },
              // O motivo vem do leitor e é escrito para o usuário, não para
              // quem mantém o código: "Arquivo Corrompido. Fazer Upload de Nova
              // Versão" diz o que houve E o que fazer. Sem ele, "Não
              // identificado" em laranja fazia o arquivo danificado parecer
              // limitação da ferramenta.
              recebido:     d.motivo
                ? { txt: d.motivo, cor: G.vermelho }
                : { txt: 'Não identificado', cor: G.laranja },
              duplicado:    { txt: 'Já enviado',    cor: G.txt2 },
              erro:         { txt: d.erro || 'Erro', cor: G.vermelho },
            }[d.status] || { txt: 'Aguardando identificação', cor: G.laranja };
            const ROTULO_TIPO = {
              balancete_mensal: 'Balancete mensal',
              balancete_anual:  'Balancete anual',
              razao:            'Razão',
              notas_entrada:    'Notas de entrada',
              notas_saida:      'Notas de saída',
              inventario:       'Inventário',
            };
            const fmtPeriodo = (ini, fim) => {
              if (!ini) return '';
              const [ai, mi] = ini.split('-');
              const [af, mf] = (fim || ini).split('-');
              const MES = ['', 'jan', 'fev', 'mar', 'abr', 'mai', 'jun',
                'jul', 'ago', 'set', 'out', 'nov', 'dez'];
              return (ai === af && mi === mf)
                ? `${MES[+mi]}/${ai}`
                : `${MES[+mi]}/${ai} a ${MES[+mf]}/${af}`;
            };
            const desc = d.tipo
              ? `${ROTULO_TIPO[d.tipo] || d.tipo} · ${fmtPeriodo(d.periodo_inicio, d.periodo_fim)}`
              : null;
            // O balancete só entra no processamento com mês de referência — é
            // ele a chave do saldo. Quando o arquivo não o traz, a linha pede a
            // confirmação aqui mesmo, sem tirar o usuário da tela.
            const pedeMes = d.id && d.status === 'recebido'
              && (d.tipo || '').startsWith('balancete');
            return (
              <div key={i} style={{ padding: '10px 14px', background: '#FFF',
                border: `1px solid ${pedeMes ? G.laranja : G.borda}`,
                borderRadius: 0, marginBottom: 6 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between',
                  alignItems: 'center', gap: 12 }}>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: 13, color: G.txt1 }}>{d.nome}</div>
                    {desc && (
                      <div style={{ fontSize: 12, color: G.verde, fontWeight: 700, marginTop: 2 }}>
                        {desc}
                      </div>
                    )}
                  </div>
                  <span style={{ fontSize: 11, fontWeight: 700,
                    color: pedeMes ? G.laranja : S.cor, whiteSpace: 'nowrap',
                    textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                    {pedeMes ? 'Informe o período' : S.txt}
                  </span>
                </div>
                {pedeMes && (
                  <div style={{ marginTop: 10, paddingTop: 10,
                    borderTop: `1px solid ${G.laranjaLight}`, display: 'flex',
                    alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                    <span style={{ fontSize: 12, color: G.txt2 }}>
                      O arquivo não traz o período de referência.
                    </span>
                    {/* Campos CONTROLADOS: guardar o valor mutando o objeto da
                        lista fazia a tela mostrar um e enviar outro assim que o
                        acervo recarregasse.
                        São DOIS — é do período que sai mensal × anual. Só o mês
                        final faria um balancete anual ser gravado como mensal,
                        apagando o mês verdadeiro. */}
                    <input type="month" aria-label="Mês inicial"
                      value={mesPorDoc[`${d.id}:ini`] ?? (d.periodo_inicio || '').slice(0, 7)}
                      onChange={e => setMesPorDoc(m => ({ ...m, [`${d.id}:ini`]: e.target.value }))}
                      style={{ fontFamily: 'inherit', fontSize: 12.5, padding: '5px 8px',
                        border: `1px solid ${G.borda}`, borderRadius: 0, color: G.txt1 }} />
                    <span style={{ fontSize: 12, color: G.txt2 }}>a</span>
                    <input type="month" aria-label="Mês final"
                      value={mesPorDoc[`${d.id}:fim`] ?? (d.periodo_fim || '').slice(0, 7)}
                      onChange={e => setMesPorDoc(m => ({ ...m, [`${d.id}:fim`]: e.target.value }))}
                      style={{ fontFamily: 'inherit', fontSize: 12.5, padding: '5px 8px',
                        border: `1px solid ${G.borda}`, borderRadius: 0, color: G.txt1 }} />
                    <button disabled={!!d.salvandoMes}
                      onClick={() => confirmarMes(d,
                        mesPorDoc[`${d.id}:ini`] ?? (d.periodo_inicio || '').slice(0, 7),
                        mesPorDoc[`${d.id}:fim`] ?? (d.periodo_fim || '').slice(0, 7))}
                      style={{ background: G.laranja, color: '#FFF', border: 'none',
                        borderRadius: 0, padding: '6px 16px', fontSize: 11, fontWeight: 700,
                        letterSpacing: '0.08em', textTransform: 'uppercase',
                        cursor: d.salvandoMes ? 'default' : 'pointer', opacity: d.salvandoMes ? 0.6 : 1 }}>
                      {d.salvandoMes ? 'Salvando…' : 'Confirmar'}
                    </button>
                    {d.erroMes && (
                      <span style={{ fontSize: 12, color: G.vermelho }}>{d.erroMes}</span>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

    </div>
  );
};

// =====================================================================
// Despesas e Custos (11/09/2026) · une Despesas Indiretas e Custos Diretos
// numa aba só, no setor VISUALIZAÇÃO. LP no padrão de Dados de Entrada —
// hero + cards + rodapé + modal. As duas telas descem INTACTAS para
// sub-páginas: nenhuma linha do conteúdo delas mudou.
// =====================================================================
const TelaDespesasCustos = ({ empresa, indicadores, periodoInicio, periodoFim }) => {
  const [sub, setSub] = useState('landing');
  const [saibaMais, setSaibaMais] = useState(false);
  const VERDE_DEEP = '#134721';
  const ORDEM = ['indiretas', 'diretos'];

  const blocos = [
    { id: 'indiretas', eyebrow: 'Bloco 1', titulo: 'Despesas Indiretas', cor: G.verde,
      desc: 'O que a empresa paga mesmo sem vender — estrutura, equipe, aluguel. Vem medida da contabilidade e não muda com o volume.',
      icone: (<><rect x="4" y="3" width="16" height="18" rx="1.5"/><line x1="8" y1="7" x2="8" y2="7.01"/><line x1="12" y1="7" x2="12" y2="7.01"/><line x1="16" y1="7" x2="16" y2="7.01"/><line x1="8" y1="11" x2="8" y2="11.01"/><line x1="12" y1="11" x2="12" y2="11.01"/><line x1="16" y1="11" x2="16" y2="11.01"/><path d="M10 21v-5h4v5"/></>) },
    { id: 'diretos', eyebrow: 'Bloco 2', titulo: 'Custos Diretos', cor: G.laranja,
      desc: 'O que cada produto ou serviço consome para existir. Sobe e desce com o volume, e é dele que sai a margem de contribuição.',
      icone: (<><polygon points="12 2 2 7 12 12 22 7 12 2"/><polyline points="2 17 12 22 22 17"/><polyline points="2 12 12 17 22 12"/></>) },
  ];

  // Sub-páginas · as telas originais, sem alteração, com o rodapé de navegação
  if (sub !== 'landing') {
    const idx = ORDEM.indexOf(sub);
    const anterior = idx > 0 ? ORDEM[idx - 1] : null;
    const proximo = idx < ORDEM.length - 1 ? ORDEM[idx + 1] : null;
    const conteudo = sub === 'indiretas'
      ? <TelaComposicoes key={empresa.id} empresa={empresa} secao="indiretas"
          periodoInicio={periodoInicio} periodoFim={periodoFim}/>
      : <TelaCustosDiretos key={empresa.id} empresa={empresa} indicadores={indicadores}
          periodoInicio={periodoInicio} periodoFim={periodoFim}/>;
    return (
      <div style={{ height: 'calc(100vh - 103px)', display: 'flex', flexDirection: 'column' }}>
        <div style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
          {conteudo}
        </div>
        <RodapeNavFonte
          onAnterior={anterior ? () => setSub(anterior) : undefined}
          onVoltar={() => setSub('landing')}
          onProximo={proximo ? () => setSub(proximo) : undefined}
          temAnterior={!!anterior}
          temProximo={!!proximo}
          rotuloVoltar="Despesas e Custos"
        />
      </div>
    );
  }

  const ctaPrimario = {
    background: G.laranja, color: '#FFF', padding: '14px 32px',
    fontSize: 13, fontWeight: 800, letterSpacing: '0.10em',
    textTransform: 'uppercase', borderRadius: 0, border: 'none',
    cursor: 'pointer', fontFamily: 'inherit',
    boxShadow: '0 4px 16px rgba(244,124,60,0.40)',
    display: 'inline-flex', alignItems: 'center', gap: 8,
  };

  return (
    <div style={{ maxWidth: 1100, margin: '0 auto', padding: '20px 28px 24px', position: 'relative' }}>
      {/* ═══════════ HERO EXPLICATIVO ═══════════ */}
      <section style={{ background: VERDE_DEEP, color: '#FFF', borderRadius: 0,
        padding: '28px 36px', position: 'relative', overflow: 'hidden', marginBottom: 14 }}>
        {/* Marca d'água grande temática · a balança entre o que é fixo e o que varia */}
        <svg width="280" height="280" viewBox="0 0 24 24" fill="none" stroke="#FFF"
          strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"
          style={{ position: 'absolute', right: -24, bottom: -70, opacity: 0.05,
            pointerEvents: 'none', userSelect: 'none' }}>
          <rect x="3" y="4" width="7" height="16" rx="1"/>
          <path d="M14 20V9l4-4 3 3v12"/>
          <line x1="14" y1="14" x2="21" y2="14"/>
        </svg>
        <div style={{ fontSize: 10, fontWeight: 700, color: 'rgba(255,255,255,0.65)',
          letterSpacing: '0.20em', textTransform: 'uppercase', marginBottom: 10 }}>
          Despesas e Custos · o que pesa antes do lucro
        </div>
        <h1 style={{ fontSize: 32, fontWeight: 900, lineHeight: 1.1, letterSpacing: '-0.02em',
          marginBottom: 12, maxWidth: 720, fontFamily: "'Outfit', sans-serif" }}>
          Nem tudo que sai <span style={{ color: G.laranja }}>sai do mesmo jeito</span>.
        </h1>
        <p style={{ fontSize: 14, lineHeight: 1.5, color: 'rgba(255,255,255,0.92)',
          maxWidth: 720, margin: 0 }}>
          A despesa indireta existe com ou sem venda; o custo direto só nasce quando
          o produto nasce. Separar os dois é o que revela a margem de contribuição de
          cada item, o ponto de equilíbrio e quanto é preciso vender. A <span style={{ display: 'inline-block', background: '#FFF', padding: '2px 9px', borderRadius: 0, verticalAlign: 'baseline', lineHeight: 1.1 }}><MarcaAIdvisor variant="default" /></span> usa
          essa separação em toda a análise.
        </p>
      </section>

      {/* ═══════════ 2 BLOCOS ═══════════ */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 16, marginBottom: 14 }}>
        {blocos.map(b => (
          <CardFonteEntrada key={b.id} fonte={b} onClick={() => setSub(b.id)} />
        ))}
      </div>

      {/* ═══════════ RODAPÉ (padrão BP60M) ═══════════ */}
      <section style={{ display: 'flex', justifyContent: 'space-between',
        alignItems: 'center', flexWrap: 'wrap', gap: 14, padding: '4px 6px' }}>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginLeft: 'auto' }}>
          <BotaoSaibaMais onClick={() => setSaibaMais(true)} contorno tamanho="lg" seta={false} />
          <button onClick={() => setSub('indiretas')} style={ctaPrimario}>Começar →</button>
        </div>
      </section>

      {/* ═══════════ MODAL · SAIBA MAIS ═══════════ */}
      {saibaMais && (
        <div onClick={() => setSaibaMais(false)}
          style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            background: 'rgba(0,0,0,0.5)', zIndex: 100, display: 'flex',
            justifyContent: 'center', alignItems: 'center', padding: 24 }}>
          <div onClick={ev => ev.stopPropagation()}
            style={{ background: '#FFF', maxWidth: 720, width: '100%', maxHeight: '90vh',
              overflow: 'auto', borderRadius: 0, padding: '28px 36px',
              boxShadow: '0 10px 40px rgba(0,0,0,0.25)', position: 'relative' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
              marginBottom: 18, gap: 14, borderBottom: `1px solid ${G.borda}`, paddingBottom: 14 }}>
              <div>
                <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.10em',
                  textTransform: 'uppercase', color: G.verde, marginBottom: 4 }}>
                  Despesas e Custos · saiba mais
                </div>
                <div style={{ fontSize: 20, fontWeight: 800, color: G.txt1 }}>
                  Por que a separação muda a decisão
                </div>
              </div>
              <button onClick={() => setSaibaMais(false)}
                style={{ background: 'transparent', border: 'none', fontSize: 24,
                  cursor: 'pointer', color: G.txt2, padding: 0, lineHeight: 1 }} title="Fechar" aria-label="Fechar"><IconeFechar /></button>
            </div>

            <p style={{ fontSize: 13.5, color: G.txt1, lineHeight: 1.6, margin: '0 0 16px' }}>
              O preço de um item menos o custo direto dele, menos o imposto sobre
              aquela venda, é a <strong>margem de contribuição</strong> — o que sobra
              para pagar a estrutura. Só depois de a estrutura estar paga é que
              começa o lucro.
            </p>
            <p style={{ fontSize: 13.5, color: G.txt1, lineHeight: 1.6, margin: '0 0 16px' }}>
              Daí a ordem das duas telas: a primeira mede <strong>quanto a estrutura
              custa</strong>; a segunda, <strong>quanto cada item contribui</strong> para
              pagá-la. Juntas, respondem quanto é preciso vender — por item, não no
              agregado.
            </p>

            <div style={{ background: G.verdeLight, border: `1px solid ${G.verde}40`,
              borderRadius: 0, padding: '14px 18px' }}>
              <p style={{ margin: 0, fontSize: 12.5, color: G.txt1, lineHeight: 1.6 }}>
                A fatia da despesa fixa que cabe a cada item pode ser rateada por igual
                ou <strong>ponderada pela contribuição</strong>. A segunda é mais justa:
                ratear por igual dá a item pequeno uma conta que ele só paga vendendo
                muito. O seletor está na tela de Custos Diretos.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// =====================================================================
// Rotina Contábil (11/09/2026) · o serviço do escritório contábil dentro do
// app. LP no MESMO padrão de Dados de Entrada e do Hub Configuração —
// hero + cards + rodapé + modal —, reusando CardFonteEntrada, RodapeNavFonte
// e BotaoSaibaMais. Não é porta de upload: os documentos entram pela Fonte
// Contábil e a IA separa o que serve à rotina. Aqui só aparecem os DOIS
// entregáveis do mês. Visível a Admin/Consultor e Contador; o empresário não.
// =====================================================================
// Célula e cabeçalho da planilha na tela, no desenho das tabelas do app
// (Custos Diretos · Planejamento): rótulo fino em caixa alta, texto que QUEBRA
// em vez de empurrar a largura — era o `nowrap` que criava a barra lateral.

const TelaRotinaContabil = ({ empresa, aba, onIr }) => {
  // A Rotina Contábil virou GRUPO de abas (02/10/2026). Quem diz o que mostrar
  // é o menu, não um estado interno: `aba` é o id da aba do NAV. A landing
  // interna, com os cartões de entrega, deixou de existir — o grupo da barra
  // lateral faz esse papel, e dois índices para a mesma coisa divergem.
  const SUB_DA_ABA = { razao_simulado: 'razao', balancete_simulado: 'balancete',
                       composicao_custo: 'composicao' };
  const ABA_DO_SUB = { razao: 'razao_simulado', balancete: 'balancete_simulado',
                       composicao: 'composicao_custo' };
  const sub = SUB_DA_ABA[aba] || 'razao';
  const setSub = id => onIr && onIr(ABA_DO_SUB[id] || 'razao_simulado');
  const [saibaMais, setSaibaMais] = useState(false);
  // `null` = ainda não respondeu, distinto de `[]` = respondeu e não há nada.
  // Sem essa distinção a tela diria "nenhum arquivo" durante a chamada, com a
  // mesma cara de resposta pronta ([[verificacao-que-nao-verifica]]).
  const [arquivos, setArquivos] = useState(null);
  const VERDE_DEEP = '#134721';
  const ORDEM = ['razao', 'balancete', 'composicao'];
  // O razão e o balancete são UMA entrega de DOIS arquivos: saem do mesmo
  // botão, da mesma apuração, e o contador confere os dois juntos. Listá-los
  // em cartões separados sugeriria que um pode existir sem o outro.
  const TAREFA = { razao: ['razao_simulado'],
                   balancete: ['balancete_simulado'],
                   composicao: ['composicao_custo'] };
  // COM O QUE cada versão foi feita, e o que ela não alcança. A modalidade
  // Contábil entrega duas vezes por mês (decisão de 30/09/2026): a primeira no
  // primeiro dia do mês seguinte, só com os extratos; a segunda quando os
  // relatórios de notas chegam. Duas versões do mesmo mês sem esta linha
  // deixariam o contador sem saber o que muda de uma para a outra.
  const ESCOPO = {
    extratos: {
      rotulo: 'só extratos',
      falta: 'Feito só com os extratos bancários. Não inclui o que não passou '
           + 'pelo banco: notas de entrada e de saída, folha de pagamento e '
           + 'apropriações.',
    },
    extratos_notas: {
      rotulo: 'extratos e notas',
      falta: 'Feito com os extratos, as notas de entrada e de saída e o '
           + 'inventário. Não inclui a folha de pagamento nem as notas de '
           + 'serviço e de frete.',
    },
  };

  useEffect(() => {
    if (!empresa) return;
    let vivo = true;
    fetch(`${API}/entregaveis/${empresa.id}`)
      .then(r => r.ok ? r.json() : [])
      .then(d => { if (vivo) setArquivos(Array.isArray(d) ? d : []); })
      .catch(() => { if (vivo) setArquivos([]); });
    // Resposta de empresa anterior não pode vencer a atual e pintar a tela com
    // arquivo de outro cliente ([[resposta-obsoleta-zera-a-tela]]).
    return () => { vivo = false; };
  }, [empresa]);

  const doTipo = t => (arquivos || []).filter(a => (TAREFA[t] || []).includes(a.tarefa));

  // ── Visualizador da planilha ──────────────────────────────────────
  // `vendo` guarda o entregável aberto; `folha` o conteúdo já carregado.
  const [vendo, setVendo] = useState(null);
  const [folha, setFolha] = useState(null);
  const [carregando, setCarregando] = useState(false);

  const carregarFolha = async (entregavel, aba = '', apartir = 0) => {
    setCarregando(true);
    try {
      const r = await fetch(`${API}/entregaveis/conteudo/${entregavel.id}`
        + `?aba=${encodeURIComponent(aba)}&linha=${apartir}&limite=500`);
      if (!r.ok) throw new Error('Não foi possível abrir esta planilha.');
      const d = await r.json();
      // Ao paginar, ACUMULA; ao trocar de aba ou abrir, recomeça.
      setFolha(prev => (apartir > 0 && prev && prev.aba === d.aba)
        ? { ...d, linhas: [...prev.linhas, ...d.linhas] }
        : d);
    } catch (err) {
      alert(err.message || 'Erro ao abrir a planilha.');
      setFolha(null);
    }
    setCarregando(false);
  };

  const abrirPlanilha = (a) => { setVendo(a); setFolha(null); carregarFolha(a); };

  // Sair da sub-página fecha o visualizador. Sem isto, abrir o Razão e navegar
  // para a Composição pelo rodapé deixaria a planilha do Razão aberta sob o
  // título da Composição — a tela afirmando o que não é.
  useEffect(() => { setVendo(null); setFolha(null); }, [sub]);

  // ── Gerar o razão do mês, só com os extratos ──────────────────────
  // A entrega do dia 1: os extratos já estão no aplicativo e o razão sai antes
  // de a contabilidade fechar. O mês vem do usuário, nunca do relógio: quem
  // gera em 1º de outubro quer setembro, e em qualquer outro dia pode querer
  // o mês que ficou para trás.
  const mesPassado = (() => {
    const h = new Date();
    const d = new Date(h.getFullYear(), h.getMonth() - 1, 1);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
  })();
  const [competencia, setCompetencia] = useState(mesPassado);
  const [gerando, setGerando] = useState(false);
  const [aviso, setAviso] = useState(null);

  const gerarRazao = async () => {
    setGerando(true); setAviso(null);
    try {
      const r = await fetch(`${API}/razao-simulado/${empresa.id}`
        + `?competencia=${encodeURIComponent(competencia)}`, { method: 'POST' });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.detail || 'Não foi possível gerar o razão.');
      // Um botão, dois arquivos: o balancete nasce dos MESMOS lançamentos do
      // razão, na mesma geração. O aviso conta os dois, e diz quanto ficou
      // pendente de classificação — é o que o contador precisa olhar primeiro.
      const b = d.balancete;
      const pendente = b && b.a_classificar
        ? ` A classificar: ${fmtBRL(Math.abs(b.a_classificar))}.` : '';
      setAviso({ tipo: 'ok', txt: `Razão e balancete ${d.versao} gerados: `
        + `${d.fatos} lançamentos do extrato, ${d.linhas} linhas no razão`
        + (b ? ` e ${b.contas} contas no balancete.` : '.') + pendente });
      // Relê a lista para a versão nova aparecer sem recarregar a página.
      const lista = await fetch(`${API}/entregaveis/${empresa.id}`);
      if (lista.ok) setArquivos(await lista.json());
    } catch (err) {
      setAviso({ tipo: 'erro', txt: err.message });
    }
    setGerando(false);
  };

  const [baixando, setBaixando] = useState('');
  const baixar = async (a) => {
    setBaixando(a.id);
    try {
      const r = await fetch(`${API}/entregaveis/arquivo/${a.id}`);
      if (!r.ok) throw new Error('Não foi possível baixar o arquivo.');
      const url = URL.createObjectURL(await r.blob());
      const el = document.createElement('a');
      el.href = url;
      el.download = a.nome_arquivo;
      document.body.appendChild(el); el.click(); document.body.removeChild(el);
      URL.revokeObjectURL(url);
    } catch (err) {
      alert(err.message || 'Erro ao baixar o arquivo.');
    }
    setBaixando('');
  };

  const entregas = [
    { id: 'razao', eyebrow: 'Entrega 1', titulo: 'Razão Simulado', cor: G.verde,
      desc: 'O razão do mês montado a partir do extrato, antes de a contabilidade fechar. Sai em duas versões: a primeira no primeiro dia do mês seguinte, só com os extratos; a segunda quando os relatórios de notas chegam. O contador baixa, confere e devolve o oficial.',
      icone: (<><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="8" y1="13" x2="16" y2="13"/><line x1="8" y1="17" x2="13" y2="17"/></>) },
    { id: 'balancete', eyebrow: 'Entrega 2', titulo: 'Balancete Simulado', cor: G.verde,
      desc: 'O resumo por conta do mesmo mês: com o que cada conta abriu, quanto andou e com o que fecha. Sai JUNTO do razão, da mesma apuração e no mesmo botão — um não existe sem o outro. A trava é contábil: Ativo mais A Classificar igual a Passivo mais Patrimônio Líquido mais Resultado, ao centavo. Não fechou, nada é gravado.',
      icone: (<><line x1="12" y1="3" x2="12" y2="21"/><line x1="5" y1="21" x2="19" y2="21"/><line x1="4" y1="7" x2="20" y2="7"/><path d="M4 7l-2 6a3 3 0 0 0 6 0z"/></>) },
    { id: 'composicao', eyebrow: 'Entrega 3', titulo: 'Composição de Custos', cor: G.laranja,
      desc: 'O custo médio por item e o CMV do mês, a partir das notas de entrada e saída e do inventário valorizado.',
      icone: (<><polygon points="12 2 2 7 12 12 22 7 12 2"/><polyline points="2 17 12 22 22 17"/><polyline points="2 12 12 17 22 12"/></>) },
  ];

  // Sub-páginas · conteúdo + rodapé de navegação (padrão Dados de Entrada)
  if (sub !== 'landing') {
    const e = entregas.find(x => x.id === sub);
    const idx = ORDEM.indexOf(sub);
    const anterior = idx > 0 ? ORDEM[idx - 1] : null;
    const proximo = idx < ORDEM.length - 1 ? ORDEM[idx + 1] : null;
    return (
      <div style={{ height: 'calc(100vh - 103px)', display: 'flex', flexDirection: 'column' }}>
        <div style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
          <div style={{ maxWidth: 1100, margin: '0 auto', padding: '20px 28px 24px' }}>
            <div style={{ fontSize: 11, fontWeight: 800, color: e.cor, letterSpacing: '0.10em',
              textTransform: 'uppercase', marginBottom: 6 }}>{e.eyebrow}</div>
            <h2 style={{ margin: '0 0 6px', fontSize: 22, fontWeight: 800, color: G.txt1 }}>
              {e.titulo}
            </h2>
            <p style={{ margin: '0 0 20px', fontSize: 13.5, color: G.txt2, lineHeight: 1.55,
              maxWidth: 720 }}>{e.desc}</p>
            {sub === 'razao' && (
              <div style={{ background: '#FFF', border: `1px solid ${G.borda}`,
                borderTop: `3px solid ${e.cor}`, borderRadius: 0,
                padding: '16px 22px', marginBottom: 14, display: 'flex',
                alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
                <div>
                  <div style={{ fontSize: 11, fontWeight: 700, color: G.txt2,
                    letterSpacing: '0.06em', textTransform: 'uppercase' }}>
                    Mês a processar
                  </div>
                  <input type="text" inputMode="numeric" value={competencia}
                    onChange={ev => setCompetencia(ev.target.value.slice(0, 7))}
                    placeholder="AAAA-MM"
                    style={{ marginTop: 6, width: 110, padding: '7px 10px',
                      fontSize: 14, fontFamily: 'inherit', color: G.txt1,
                      border: `1px solid ${G.borda}`, borderRadius: 0 }} />
                </div>
                <button onClick={gerarRazao} disabled={gerando}
                  style={{ background: gerando ? G.txt2 : e.cor, color: '#FFF',
                    padding: '11px 22px', fontSize: 12, fontWeight: 800,
                    letterSpacing: '0.06em', textTransform: 'uppercase',
                    borderRadius: 0, border: 'none', fontFamily: 'inherit',
                    cursor: gerando ? 'default' : 'pointer', alignSelf: 'flex-end' }}>
                  {gerando ? 'Gerando…' : 'Gerar razão e balancete'}
                </button>
                <p style={{ margin: 0, fontSize: 11.5, color: G.txt2,
                  lineHeight: 1.5, maxWidth: 360 }}>
                  Usa o balancete do mês anterior e os extratos bancários já
                  enviados. Não usa notas nem folha de pagamento. Saem dois
                  arquivos, o razão e o balancete do mês.
                </p>
                {aviso && (
                  <div style={{ flexBasis: '100%', fontSize: 12.5, lineHeight: 1.5,
                    color: aviso.tipo === 'ok' ? G.verde : G.vermelho,
                    fontWeight: 700 }}>
                    {aviso.txt}
                  </div>
                )}
              </div>
            )}
            {sub === 'balancete' && (
              <p style={{ margin: '0 0 16px', fontSize: 12.5, color: G.txt2,
                lineHeight: 1.55, maxWidth: 560, background: '#FFF',
                border: `1px solid ${G.borda}`, borderLeft: `3px solid ${G.verde}`,
                borderRadius: 0, padding: '12px 16px' }}>
                O balancete é gerado junto do razão, na aba <b>Razão Simulado</b>.
                Aqui ficam os arquivos prontos, para baixar e conferir.
              </p>
            )}
            {arquivos === null ? (
              <div style={{ background: '#FFF', border: `1px solid ${G.borda}`,
                borderTop: `3px solid ${e.cor}`, borderRadius: 0, padding: '32px 26px',
                textAlign: 'center', fontSize: 13, color: G.txt2 }}>
                Carregando…
              </div>
            ) : doTipo(sub).length === 0 ? (
              <div style={{ background: '#FFF', border: `1px solid ${G.borda}`,
                borderTop: `3px solid ${e.cor}`, borderRadius: 0, padding: '32px 26px',
                textAlign: 'center' }}>
                <div style={{ fontSize: 14, fontWeight: 800, color: G.txt1, marginBottom: 6 }}>
                  Nenhum arquivo ainda
                </div>
                <p style={{ margin: 0, fontSize: 12.5, color: G.txt2, lineHeight: 1.6 }}>
                  A planilha aparece aqui assim que o mês for processado.
                </p>
              </div>
            ) : (
              doTipo(sub).map(a => (
                <div key={a.id} style={{ background: '#FFF', border: `1px solid ${G.borda}`,
                  borderTop: `3px solid ${e.cor}`, borderRadius: 0, padding: '18px 22px',
                  marginBottom: 12, display: 'flex', alignItems: 'center',
                  justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}>
                  <div>
                    <div style={{ fontSize: 15, fontWeight: 800, color: G.txt1 }}>
                      {a.nome_arquivo}
                    </div>
                    <div style={{ marginTop: 5, fontSize: 11.5, color: G.txt2,
                      display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
                      <span>competência {a.competencia}</span>
                      {a.versao && <span>versão {a.versao}</span>}
                      {/* `null` é "esta tarefa não mede confiança" — nunca 0%.
                          Exibir 0,0% aqui seria a tela mentindo sobre o dado. */}
                      {a.confiabilidade_alta !== null && a.confiabilidade_alta !== undefined && (
                        <span style={{ fontWeight: 800, color: G.verde }}>
                          {(Number(a.confiabilidade_alta) * 100).toLocaleString('pt-BR',
                            { minimumFractionDigits: 1, maximumFractionDigits: 1 })}% em confiança Alta
                        </span>
                      )}
                      {ESCOPO[a.escopo] && (
                        <span style={{ fontWeight: 800, color: G.txt1 }}>
                          {ESCOPO[a.escopo].rotulo}
                        </span>
                      )}
                    </div>
                    {/* Entregável antigo não tem escopo gravado, e a linha
                        simplesmente não aparece: melhor faltar do que afirmar
                        um escopo que ninguém registrou. */}
                    {ESCOPO[a.escopo] && (
                      <p style={{ margin: '8px 0 0', fontSize: 11.5, color: G.txt2,
                        lineHeight: 1.5, maxWidth: 560 }}>
                        {ESCOPO[a.escopo].falta}
                      </p>
                    )}
                  </div>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <button onClick={() => abrirPlanilha(a)}
                      style={{ background: '#FFF', color: e.cor, padding: '10px 18px',
                        fontSize: 12, fontWeight: 800, letterSpacing: '0.06em',
                        textTransform: 'uppercase', borderRadius: 0,
                        border: `1px solid ${e.cor}`, cursor: 'pointer',
                        whiteSpace: 'nowrap', fontFamily: 'inherit' }}>
                      Visualizar
                    </button>
                    {/* Botão, não <a href>: o token vai num interceptor de
                        `window.fetch` (App.js:95), e uma navegação de link não
                        passa por ele — o download voltaria 401. Mesmo padrão de
                        blob do PDF da Consultoria. */}
                    <button onClick={() => baixar(a)} disabled={baixando === a.id}
                      style={{ background: baixando === a.id ? G.txt2 : e.cor, color: '#FFF',
                        padding: '10px 20px', fontSize: 12, fontWeight: 800,
                        letterSpacing: '0.06em', textTransform: 'uppercase', borderRadius: 0,
                        border: 'none', cursor: baixando === a.id ? 'default' : 'pointer',
                        whiteSpace: 'nowrap', fontFamily: 'inherit' }}>
                      {baixando === a.id ? 'Baixando…' : 'Baixar Excel'}
                    </button>
                  </div>
                </div>
              ))
            )}

          </div>

          {/* ═══ VISUALIZADOR ═══
              Não é a planilha renderizada como planilha: é a TABELA DO APP, no
              mesmo desenho de Custos Diretos — cabeçalho uppercase fino, linhas
              numeradas, valor à direita, divisória de seção. Sem coluna de
              número de linha do Excel e sem rolagem própria: a página já rola,
              e o texto longo quebra em vez de empurrar a largura. */}
          {vendo && (
            <div style={{ padding: '0 28px 24px' }}>
              <div style={{ background: '#FFF', border: `1px solid ${G.borda}`,
                borderRadius: 0, overflow: 'hidden' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10,
                  padding: '14px 18px', borderBottom: `1px solid ${G.borda}`,
                  flexWrap: 'wrap' }}>
                  <div style={{ fontSize: 13, fontWeight: 800, color: G.laranja,
                    letterSpacing: '0.06em', textTransform: 'uppercase' }}>
                    {vendo.nome_arquivo.replace(/\.xlsx$/, '')}
                  </div>
                  {folha && folha.abas.length > 1 && folha.abas.map(ab => (
                    <button key={ab} onClick={() => carregarFolha(vendo, ab)}
                      style={{ fontSize: 11, fontWeight: 700, padding: '5px 12px',
                        borderRadius: 0, cursor: 'pointer', fontFamily: 'inherit',
                        border: `1px solid ${folha.aba === ab ? G.verde : G.borda}`,
                        background: folha.aba === ab ? G.verdeLight : '#FFF',
                        color: folha.aba === ab ? G.verde : G.txt2 }}>
                      {ab}
                    </button>
                  ))}
                  <div style={{ marginLeft: 'auto', display: 'flex', gap: 12,
                    alignItems: 'center' }}>
                    {folha && (
                      <span style={{ fontSize: 11, color: G.txt2 }}>
                        {folha.linhas.filter(l => l.tipo === 'dado').length} de {folha.total_linhas} linhas
                      </span>
                    )}
                    <button onClick={() => { setVendo(null); setFolha(null); }}
                      style={{ background: 'transparent', border: 'none', fontSize: 20,
                        cursor: 'pointer', color: G.txt2, padding: 0, lineHeight: 1 }}
                      title="Fechar" aria-label="Fechar"><IconeFechar /></button>
                  </div>
                </div>

                <table style={{ width: '100%', borderCollapse: 'collapse',
                  fontVariantNumeric: 'tabular-nums' }}>
                  <tbody>
                    {(folha ? folha.linhas : []).map((l, li) => {
                      if (l.tipo === 'vazia') return null;
                      const cols = l.c.length;

                      // O cabeçalho fica ONDE está na planilha. Tirá-lo do lugar
                      // e fixar um só no topo foi o erro: o razão traz um por
                      // bloco de conta, e a aba Confiabilidade tem várias
                      // tabelas, cada uma com o seu.
                      if (l.tipo === 'cabecalho') {
                        return (
                          <tr key={li}>
                            {l.c.map((cel, ci) => (
                              <th key={ci} style={{ ...thPlan,
                                // Destaque: fundo, filete verde abrindo a tabela
                                // e texto escuro. Caixa alta vem do thPlan e se
                                // aplica igual nas duas abas — a 2ª tabela da
                                // Confiabilidade ficava minúscula e sem fundo
                                // porque nem chegava a ser classificada aqui.
                                background: '#FAFAF6', color: G.txt1, fontWeight: 800,
                                borderTop: `2px solid ${G.verde}`,
                                borderBottom: `1px solid ${G.borda}`,
                                textAlign: ci >= 5 ? 'right' : 'left' }}>
                                {cel.v ?? ''}
                              </th>
                            ))}
                          </tr>
                        );
                      }

                      if (l.tipo === 'secao') {
                        const texto = l.c.map(c => c.v).filter(Boolean).join(' · ');
                        return (
                          <tr key={li}>
                            <td colSpan={cols} style={{ padding: '12px 16px',
                              borderTop: `1px solid ${G.borda}`, fontSize: 12.5,
                              fontWeight: 800, color: G.txt1, lineHeight: 1.5 }}>
                              {texto}
                            </td>
                          </tr>
                        );
                      }

                      return (
                        <tr key={li} style={{ borderBottom: '1px solid #F0F0EC' }}>
                          {l.c.map((cel, ci) => (
                            <td key={ci} style={{ ...tdPlan,
                              whiteSpace: (folha.larguras || [])[ci] > COLUNA_LARGA
                                ? 'normal' : 'nowrap',
                              wordBreak: (folha.larguras || [])[ci] > COLUNA_LARGA
                                ? 'break-word' : 'normal',
                              textAlign: typeof cel.v === 'number' ? 'right'
                                : (ci >= 5 ? 'right' : 'left'),
                              color: cel.formula ? G.laranja : G.txt1,
                              fontWeight: ['Alta', 'Média', 'Baixa'].includes(cel.v) ? 700 : 400 }}>
                              {cel.formula ? 'fórmula ·' : (
                                typeof cel.v === 'number'
                                  ? cel.v.toLocaleString('pt-BR',
                                      { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                                  : (cel.v ?? '')
                              )}
                            </td>
                          ))}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>

                {folha && folha.tem_mais && (
                  <div style={{ padding: '14px', textAlign: 'center',
                    borderTop: `1px solid ${G.borda}` }}>
                    <button disabled={carregando}
                      onClick={() => carregarFolha(vendo, folha.aba, folha.linhas.length)}
                      style={{ background: G.verdeLight, color: G.verde, border: `1px solid ${G.verde}`,
                        padding: '8px 22px', fontSize: 12, fontWeight: 800, borderRadius: 0,
                        cursor: carregando ? 'default' : 'pointer', fontFamily: 'inherit' }}>
                      {carregando ? 'Carregando…' : 'Carregar mais 500 linhas'}
                    </button>
                  </div>
                )}
                {!folha && carregando && (
                  <div style={{ padding: 26, textAlign: 'center', fontSize: 13,
                    color: G.txt2 }}>Abrindo a planilha…</div>
                )}
              </div>
            </div>
          )}
        </div>
        <RodapeNavFonte
          onAnterior={anterior ? () => setSub(anterior) : undefined}
          onVoltar={undefined}
          onProximo={proximo ? () => setSub(proximo) : undefined}
          temAnterior={!!anterior}
          temProximo={!!proximo}
          rotuloVoltar="Rotina Contábil"
        />
      </div>
    );
  }

  const ctaPrimario = {
    background: G.laranja, color: '#FFF', padding: '14px 32px',
    fontSize: 13, fontWeight: 800, letterSpacing: '0.10em',
    textTransform: 'uppercase', borderRadius: 0, border: 'none',
    cursor: 'pointer', fontFamily: 'inherit',
    boxShadow: '0 4px 16px rgba(244,124,60,0.40)',
    display: 'inline-flex', alignItems: 'center', gap: 8,
  };

  const CICLO = [
    ['1', 'A Rotina Contábil produz o Razão Simulado do mês, a partir dos extratos e das notas.'],
    ['2', 'O contador baixa o Excel, valida no processo dele e devolve o Razão Oficial e o Balancete Oficial pela Fonte Contábil, com a formatação do escritório.'],
    ['3', 'A partir do documento oficial, o RunGoal monta DRE, Balancete, Movimento do mês e as demais abas.'],
    ['4', 'O empresário visualiza a entrega contábil com os indicadores, o Dashboard e o parecer da IA.'],
  ];

  return (
    <div style={{ maxWidth: 1100, margin: '0 auto', padding: '20px 28px 24px', position: 'relative' }}>
      {/* ═══════════ HERO EXPLICATIVO ═══════════ */}
      <section style={{ background: VERDE_DEEP, color: '#FFF', borderRadius: 0,
        padding: '28px 36px', position: 'relative', overflow: 'hidden', marginBottom: 14 }}>
        {/* Marca d'água grande temática · duas folhas = as duas planilhas do mês */}
        <svg width="280" height="280" viewBox="0 0 24 24" fill="none" stroke="#FFF"
          strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"
          style={{ position: 'absolute', right: -24, bottom: -70, opacity: 0.05,
            pointerEvents: 'none', userSelect: 'none' }}>
          <path d="M15 3H10a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2V8z"/>
          <polyline points="15 3 15 8 20 8"/>
          <path d="M4 8v11a2 2 0 0 0 2 2h9"/>
        </svg>
        <div style={{ fontSize: 10, fontWeight: 700, color: 'rgba(255,255,255,0.65)',
          letterSpacing: '0.20em', textTransform: 'uppercase', marginBottom: 10 }}>
          Rotina Contábil · o mês do escritório, pré-montado
        </div>
        <h1 style={{ fontSize: 32, fontWeight: 900, lineHeight: 1.1, letterSpacing: '-0.02em',
          marginBottom: 12, maxWidth: 720, fontFamily: "'Outfit', sans-serif" }}>
          Entra documento, <span style={{ color: G.laranja }}>sai planilha</span>.
        </h1>
        <p style={{ fontSize: 14, lineHeight: 1.5, color: 'rgba(255,255,255,0.92)',
          maxWidth: 720, margin: 0 }}>
          Os documentos chegam pela Fonte Contábil e a <span style={{ display: 'inline-block', background: '#FFF', padding: '2px 9px', borderRadius: 0, verticalAlign: 'baseline', lineHeight: 1.1 }}><MarcaAIdvisor variant="default" /></span> separa
          o que serve à rotina do mês. Saem duas planilhas — o Razão Simulado e a
          Composição de Custos — para o contador conferir, baixar e devolver o
          documento oficial. É alívio de digitação, não substituição do contador.
        </p>
      </section>

      {/* ═══════════ 2 ENTREGAS ═══════════ */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 16, marginBottom: 14 }}>
        {entregas.map(e => {
          // O selo diz o ESTADO REAL da entrega, não uma promessa: "Breve"
          // enquanto não há arquivo, e a contagem quando há.
          const n = doTipo(e.id).length;
          const selo = arquivos === null ? undefined
            : n === 0 ? 'Breve'
            : n === 1 ? '1 arquivo' : `${n} arquivos`;
          return (
            <CardFonteEntrada key={e.id} fonte={{ ...e, selo }}
              onClick={() => setSub(e.id)} />
          );
        })}
      </div>

      {/* ═══════════ RODAPÉ (padrão BP60M) ═══════════ */}
      <section style={{ display: 'flex', justifyContent: 'space-between',
        alignItems: 'center', flexWrap: 'wrap', gap: 14, padding: '4px 6px' }}>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginLeft: 'auto' }}>
          <BotaoSaibaMais onClick={() => setSaibaMais(true)} contorno tamanho="lg" seta={false} />
          <button onClick={() => setSub('razao')} style={ctaPrimario}>Começar →</button>
        </div>
      </section>

      {/* ═══════════ MODAL · SAIBA MAIS ═══════════ */}
      {saibaMais && (
        <div onClick={() => setSaibaMais(false)}
          style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            background: 'rgba(0,0,0,0.5)', zIndex: 100, display: 'flex',
            justifyContent: 'center', alignItems: 'center', padding: 24 }}>
          <div onClick={ev => ev.stopPropagation()}
            style={{ background: '#FFF', maxWidth: 720, width: '100%', maxHeight: '90vh',
              overflow: 'auto', borderRadius: 0, padding: '28px 36px',
              boxShadow: '0 10px 40px rgba(0,0,0,0.25)', position: 'relative' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
              marginBottom: 18, gap: 14, borderBottom: `1px solid ${G.borda}`, paddingBottom: 14 }}>
              <div>
                <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.10em',
                  textTransform: 'uppercase', color: G.verde, marginBottom: 4 }}>
                  Rotina Contábil · saiba mais
                </div>
                <div style={{ fontSize: 20, fontWeight: 800, color: G.txt1 }}>
                  O ciclo do mês, em quatro passos
                </div>
              </div>
              <button onClick={() => setSaibaMais(false)}
                style={{ background: 'transparent', border: 'none', fontSize: 24,
                  cursor: 'pointer', color: G.txt2, padding: 0, lineHeight: 1 }} title="Fechar" aria-label="Fechar"><IconeFechar /></button>
            </div>

            <p style={{ fontSize: 13.5, color: G.txt1, lineHeight: 1.6, margin: '0 0 18px' }}>
              O contador <strong>não edita nada aqui dentro</strong>. Ele lê, baixa o Excel
              e devolve o documento oficial pela Fonte Contábil — com a formatação do
              escritório dele. O que muda é só o tempo de digitação.
            </p>

            {CICLO.map(([n, texto]) => (
              <div key={n} style={{ display: 'flex', gap: 14, marginBottom: 14 }}>
                <div style={{ minWidth: 28, height: 28, borderRadius: 0, background: G.verdeLight,
                  color: G.verde, fontSize: 13, fontWeight: 800, display: 'flex',
                  alignItems: 'center', justifyContent: 'center' }}>{n}</div>
                <p style={{ margin: 0, fontSize: 13, color: G.txt1, lineHeight: 1.6 }}>{texto}</p>
              </div>
            ))}

            <div style={{ background: G.verdeLight, border: `1px solid ${G.verde}40`,
              borderRadius: 0, padding: '14px 18px', marginTop: 18 }}>
              <p style={{ margin: 0, fontSize: 12.5, color: G.txt1, lineHeight: 1.6 }}>
                O Razão Oficial que volta no passo 2 é o <strong>gabarito</strong> do Razão
                Simulado do passo 1. Mês a mês, dá para medir o acerto real da
                pré-classificação, lançamento a lançamento.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Template · Open Finance (habilitado · aviso "em desenvolvimento" + benefícios/segurança)
const TelaOpenFinance = ({ empresa }) => (
  <div style={{ padding: '24px 28px', position: 'relative', overflow: 'hidden' }}>
    <CarimboAIdvisor />
    <div style={{ marginBottom: 20 }}>
      <div style={{ position: 'relative', display: 'inline-block' }}>
        <h2 style={{ margin: 0, fontSize: 20, fontWeight: 800, color: G.txt1,
          position: 'relative', zIndex: 1 }}>
          Open Finance
          <span style={{ marginLeft: 12, fontSize: 10, fontWeight: 800, color: G.laranja,
            background: G.laranjaLight, border: `1px solid ${G.laranja}`, borderRadius: 0,
            padding: '3px 10px', letterSpacing: '0.08em', textTransform: 'uppercase',
            verticalAlign: 'middle' }}>Em desenvolvimento</span>
        </h2>
        <MarcaDaguaTitulo>
          <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
          <path d="M9 12l2 2 4-4"/>
        </MarcaDaguaTitulo>
      </div>
      <p style={{ margin: '4px 0 0', fontSize: 13, color: G.txt2 }}>
        a entrada automática de dados, com o seu consentimento.
      </p>
    </div>

    <div style={{ background: G.verdeLight, border: `1px solid ${G.verde}40`, borderRadius: 0,
      padding: '22px 26px', marginBottom: 16 }}>
      <div style={{ fontSize: 17, fontWeight: 800, color: G.verde, marginBottom: 8 }}>
        Em breve: seus dados, sem upload manual.
      </div>
      <p style={{ margin: 0, fontSize: 13.5, color: G.txt1, lineHeight: 1.6 }}>
        O Open Finance conecta suas contas pelo padrão regulado do Banco Central e traz os extratos
        automaticamente — sempre atualizados, sem trabalho de importar arquivo. É a mesma informação
        que hoje chega ao RunGoal por upload (como os extratos de conta-corrente), agora com entrada
        automática e contínua.
      </p>
    </div>

    <div style={{ background: '#FFF', border: `1px solid ${G.borda}`, borderLeft: `4px solid ${G.verde}`,
      borderRadius: 0, padding: '16px 20px', marginBottom: 16 }}>
      <div style={{ fontSize: 12, fontWeight: 800, color: G.verde, letterSpacing: '0.08em',
        textTransform: 'uppercase', marginBottom: 8 }}>Segurança em primeiro lugar</div>
      <ul style={{ margin: 0, padding: '0 0 0 18px', fontSize: 13, color: G.txt1, lineHeight: 1.7 }}>
        <li>Conexão pelo padrão oficial do <strong>Banco Central</strong> — sem compartilhar senhas do seu banco.</li>
        <li>A <MarcaAIdvisor variant="default" /> acessa <strong>somente os dados que você autorizar</strong>, e nada além disso.</li>
        <li>Você concede e <strong>revoga o consentimento</strong> quando quiser.</li>
      </ul>
    </div>

    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12 }}>
      {[
        ['Dados sempre atualizados', 'Os lançamentos entram sozinhos, sem depender de exportar arquivo.'],
        ['Menos trabalho', 'Fim do upload manual mês a mês — a conciliação fica mais rápida.'],
        ['Base para a IA', 'Histórico contínuo alimenta o diagnóstico e o Business Plan com mais precisão.'],
      ].map(([t, d], i) => (
        <div key={i} style={{ background: '#FFF', border: `1px solid ${G.borda}`,
          borderRadius: 0, padding: '14px 16px' }}>
          <div style={{ fontSize: 14, fontWeight: 800, color: G.txt1, marginBottom: 4 }}>{t}</div>
          <div style={{ fontSize: 12.5, color: G.txt2, lineHeight: 1.45 }}>{d}</div>
        </div>
      ))}
    </div>
  </div>
);


// Hub "Dados de Entrada" · LP no padrão BP60M (hero + 3 fontes + rodapé)
const TelaDadosEntrada = ({ empresa, periodoInicio, periodoFim }) => {
  const [sub, setSub] = useState('landing');
  const [saibaMais, setSaibaMais] = useState(false);
  const VERDE_DEEP = '#134721';
  const ORDEM_FONTES = ['extrato', 'documentos', 'openfinance'];

  // Páginas de fonte · conteúdo + rodapé de navegação (padrão Tarefas BP60M)
  if (sub !== 'landing') {
    const idx = ORDEM_FONTES.indexOf(sub);
    const anterior = idx > 0 ? ORDEM_FONTES[idx - 1] : null;
    const proximo  = idx < ORDEM_FONTES.length - 1 ? ORDEM_FONTES[idx + 1] : null;
    const conteudo =
      sub === 'extrato'
        ? <TelaExtrato empresa={empresa} periodoInicio={periodoInicio} periodoFim={periodoFim} />
        : sub === 'documentos'
          ? <TelaDocumentosContabeis empresa={empresa} />
          : <TelaOpenFinance empresa={empresa} />;
    return (
      <div style={{ height: 'calc(100vh - 103px)', display: 'flex', flexDirection: 'column' }}>
        <div style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
          {conteudo}
        </div>
        <RodapeNavFonte
          onAnterior={anterior ? () => setSub(anterior) : undefined}
          onVoltar={() => setSub('landing')}
          onProximo={proximo ? () => setSub(proximo) : undefined}
          temAnterior={!!anterior}
          temProximo={!!proximo}
        />
      </div>
    );
  }

  const ctaPrimario = {
    background: G.laranja, color: '#FFF', padding: '14px 32px',
    fontSize: 13, fontWeight: 800, letterSpacing: '0.10em',
    textTransform: 'uppercase', borderRadius: 0, border: 'none',
    cursor: 'pointer', fontFamily: 'inherit',
    boxShadow: '0 4px 16px rgba(244,124,60,0.40)',
    display: 'inline-flex', alignItems: 'center', gap: 8,
  };

  const fontes = [
    { id: 'extrato', eyebrow: 'Fonte 1', titulo: 'Extrato', cor: G.laranja,
      desc: 'Upload de extrato bancário — a ferramenta identifica o tipo (conta, aplicação, cartão) e classifica os lançamentos.',
      icone: (<><line x1="8" y1="6" x2="21" y2="6"/><line x1="8" y1="12" x2="21" y2="12"/><line x1="8" y1="18" x2="21" y2="18"/><line x1="3" y1="6" x2="3.01" y2="6"/><line x1="3" y1="12" x2="3.01" y2="12"/><line x1="3" y1="18" x2="3.01" y2="18"/></>) },
    { id: 'documentos', eyebrow: 'Fonte 2', titulo: 'Documentos Contábeis', cor: G.verde,
      desc: 'Balancete e Razão — a IA identifica o documento e reconstrói resultado, margem e patrimônio, com mais profundidade que o extrato.',
      icone: (<><rect x="4" y="2" width="16" height="20" rx="2"/><line x1="8" y1="7" x2="16" y2="7"/><line x1="8" y1="11" x2="16" y2="11"/><line x1="8" y1="15" x2="13" y2="15"/></>) },
    { id: 'openfinance', eyebrow: 'Fonte 3', titulo: 'Open Finance', cor: G.txt2, selo: 'Em desenvolvimento',
      desc: 'Entrada automática dos dados pelo padrão do Banco Central — com o seu consentimento e segurança.',
      icone: (<><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/><path d="M9 12l2 2 4-4"/></>) },
  ];

  return (
    <div style={{ maxWidth: 1100, margin: '0 auto', padding: '20px 28px 24px', position: 'relative' }}>
      {/* ═══════════ HERO EXPLICATIVO ═══════════ */}
      <section style={{ background: VERDE_DEEP, color: '#FFF', borderRadius: 0,
        padding: '28px 36px', position: 'relative', overflow: 'hidden', marginBottom: 14 }}>
        {/* Marca d'água grande temática (dados) */}
        <svg width="280" height="280" viewBox="0 0 24 24" fill="none" stroke="#FFF"
          strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"
          style={{ position: 'absolute', right: -24, bottom: -70, opacity: 0.05,
            pointerEvents: 'none', userSelect: 'none' }}>
          <line x1="8" y1="6" x2="21" y2="6"/><line x1="8" y1="12" x2="21" y2="12"/>
          <line x1="8" y1="18" x2="21" y2="18"/><line x1="3" y1="6" x2="3.01" y2="6"/>
          <line x1="3" y1="12" x2="3.01" y2="12"/><line x1="3" y1="18" x2="3.01" y2="18"/>
        </svg>
        <div style={{ fontSize: 10, fontWeight: 700, color: 'rgba(255,255,255,0.65)',
          letterSpacing: '0.20em', textTransform: 'uppercase', marginBottom: 10 }}>
          Dados de Entrada · a base de toda análise
        </div>
        <h1 style={{ fontSize: 32, fontWeight: 900, lineHeight: 1.1, letterSpacing: '-0.02em',
          marginBottom: 12, maxWidth: 720, fontFamily: "'Outfit', sans-serif" }}>
          Bons números começam por <span style={{ color: G.laranja }}>bons dados</span>.
        </h1>
        <p style={{ fontSize: 14, lineHeight: 1.5, color: 'rgba(255,255,255,0.92)',
          maxWidth: 720, margin: 0 }}>
          Extrato bancário, documentos contábeis ou Open Finance — escolha por onde
          alimentar a análise. A <span style={{ display: 'inline-block', background: '#FFF', padding: '2px 9px', borderRadius: 0, verticalAlign: 'baseline', lineHeight: 1.1 }}><MarcaAIdvisor variant="default" /></span> combina todas as
          fontes disponíveis para montar o diagnóstico e o Business Plan. Quanto mais
          fontes, mais precisa a leitura.
        </p>
      </section>

      {/* ═══════════ 3 FONTES ═══════════ */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16, marginBottom: 14 }}>
        {fontes.map(f => (
          <CardFonteEntrada key={f.id} fonte={f} onClick={() => setSub(f.id)} />
        ))}
      </div>

      {/* ═══════════ RODAPÉ (padrão BP60M) ═══════════ */}
      <section style={{ display: 'flex', justifyContent: 'space-between',
        alignItems: 'center', flexWrap: 'wrap', gap: 14, padding: '4px 6px' }}>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginLeft: 'auto' }}>
          <BotaoSaibaMais onClick={() => setSaibaMais(true)} contorno tamanho="lg" seta={false} />
          <button onClick={() => setSub('extrato')} style={ctaPrimario}>Começar →</button>
        </div>
      </section>

      {/* ═══════════ MODAL · SAIBA MAIS ═══════════ */}
      {saibaMais && (
        <div onClick={() => setSaibaMais(false)}
          style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            background: 'rgba(0,0,0,0.5)', zIndex: 100, display: 'flex',
            justifyContent: 'center', alignItems: 'center', padding: 24 }}>
          <div onClick={e => e.stopPropagation()}
            style={{ background: '#FFF', maxWidth: 720, width: '100%', maxHeight: '90vh',
              overflow: 'auto', borderRadius: 0, padding: '28px 36px',
              boxShadow: '0 10px 40px rgba(0,0,0,0.25)', position: 'relative' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
              marginBottom: 18, gap: 14, borderBottom: `1px solid ${G.borda}`, paddingBottom: 14 }}>
              <div>
                <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.10em',
                  textTransform: 'uppercase', color: G.verde, marginBottom: 4 }}>
                  Dados de Entrada · saiba mais
                </div>
                <div style={{ fontSize: 20, fontWeight: 800, color: G.txt1 }}>
                  As três fontes e como elas se somam
                </div>
              </div>
              <button onClick={() => setSaibaMais(false)}
                style={{ background: 'transparent', border: 'none', fontSize: 24,
                  cursor: 'pointer', color: G.txt2, padding: 0, lineHeight: 1 }} title="Fechar" aria-label="Fechar"><IconeFechar /></button>
            </div>

            <p style={{ fontSize: 13.5, color: G.txt1, lineHeight: 1.6, margin: '0 0 18px' }}>
              A <MarcaAIdvisor variant="default" /> trabalha com todas as fontes que você
              conseguir fornecer — elas não competem, se somam. Cada uma cobre uma parte da história.
            </p>

            {[
              ['1. Extrato', 'O dia a dia do caixa. Mostra entradas e saídas reais, classifica os lançamentos e revela o padrão de movimentação.'],
              ['2. Documentos Contábeis', 'A profundidade histórica. Do balancete e do razão a IA reconstrói resultado, margem e patrimônio ano a ano — o que o extrato sozinho não mostra.'],
              ['3. Open Finance', 'A entrada automática (em desenvolvimento). Os mesmos extratos, sem upload manual, sempre atualizados.'],
            ].map(([t, d], i) => (
              <div key={i} style={{ marginBottom: 12, background: G.fundo, borderRadius: 0,
                padding: '14px 16px' }}>
                <div style={{ fontSize: 14, fontWeight: 800, color: G.txt1, marginBottom: 3 }}>{t}</div>
                <div style={{ fontSize: 12.5, color: G.txt2, lineHeight: 1.5 }}>{d}</div>
              </div>
            ))}

            <div style={{ marginTop: 8, background: '#FFF', border: `1px solid ${G.borda}`,
              borderLeft: `4px solid ${G.verde}`, borderRadius: 0, padding: '12px 16px',
              fontSize: 12.5, color: G.txt1, lineHeight: 1.55 }}>
              <strong style={{ color: G.verde }}>Segurança e privacidade.</strong> A ferramenta
              acessa somente os dados que você fornecer ou autorizar. No Open Finance, a conexão
              segue o padrão regulado do Banco Central, sem compartilhar senhas do banco, e o
              consentimento pode ser revogado quando você quiser (LGPD).
            </div>
          </div>
        </div>
      )}
    </div>
  );
};


// =====================================================================
// Hub "Configuração" · LP no MESMO padrão de Dados de Entrada
// (hero + 3 cards + rodapé + modal). Cards: 1. Cadastro · 2. Configurações
// Financeiras · 3. Configurações Balanço Patrimonial (AiPro). Reusa
// CardFonteEntrada, RodapeNavFonte e BotaoSaibaMais.
// =====================================================================
const TelaConfigHub = ({ empresa, onSalvo }) => {
  const [sub, setSub] = useState('landing');
  const [saibaMais, setSaibaMais] = useState(false);
  const VERDE_DEEP = '#134721';
  // Destravado em 25/07: o Balanço entra no fluxo para toda empresa. A trava de
  const ORDEM = ['financeiras', 'balanco'];

  // Páginas de funcionalidade · conteúdo + rodapé de navegação (padrão Dados de Entrada)
  if (sub !== 'landing') {
    const idx = ORDEM.indexOf(sub);
    const anterior = idx > 0 ? ORDEM[idx - 1] : null;
    const proximo  = idx >= 0 && idx < ORDEM.length - 1 ? ORDEM[idx + 1] : null;
    const conteudo =
      sub === 'financeiras'
        ? <TelaConfiguracoes empresa={empresa} onSalvo={onSalvo} subTelaInicial="principal" />
        : <TelaConfiguracoes empresa={empresa} onSalvo={onSalvo} subTelaInicial="adicionais" />;
    return (
      <div style={{ height: 'calc(100vh - 103px)', display: 'flex', flexDirection: 'column' }}>
        <div style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
          {conteudo}
        </div>
        <RodapeNavFonte
          onAnterior={anterior ? () => setSub(anterior) : undefined}
          onVoltar={() => setSub('landing')}
          onProximo={proximo ? () => setSub(proximo) : undefined}
          temAnterior={!!anterior}
          temProximo={!!proximo}
          rotuloVoltar="Configurações Complementares"
        />
      </div>
    );
  }

  const ctaPrimario = {
    background: G.laranja, color: '#FFF', padding: '14px 32px',
    fontSize: 13, fontWeight: 800, letterSpacing: '0.10em',
    textTransform: 'uppercase', borderRadius: 0, border: 'none',
    cursor: 'pointer', fontFamily: 'inherit',
    boxShadow: '0 4px 16px rgba(244,124,60,0.40)',
    display: 'inline-flex', alignItems: 'center', gap: 8,
  };

  const cards = [
    { id: 'financeiras', eyebrow: 'Página 1', titulo: 'Configurações Financeiras', cor: G.verde,
      desc: 'Saldos, WACC, impostos e perpetuidade — os parâmetros que alimentam indicadores, DRE e Valor.',
      icone: (<><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></>) },
    { id: 'balanco', eyebrow: 'Página 2', titulo: 'Configurações Balanço Patrimonial', cor: G.txt2,
      desc: 'Patrimônio operacional, capital social, objeto social e dívidas — dados estruturais do Balanço Patrimonial.',
      icone: (<><line x1="12" y1="3" x2="12" y2="21"/><line x1="3" y1="7" x2="21" y2="7"/><circle cx="6" cy="7" r="1"/><circle cx="18" cy="7" r="1"/><path d="M3 12l3-5 3 5a3 3 0 11-6 0z"/><path d="M15 12l3-5 3 5a3 3 0 11-6 0z"/></>) },
  ];

  const abrirCard = (id) => setSub(id);

  return (
    <div style={{ maxWidth: 1100, margin: '0 auto', padding: '20px 28px 24px', position: 'relative' }}>
      {/* ═══════════ HERO EXPLICATIVO ═══════════ */}
      <section style={{ background: VERDE_DEEP, color: '#FFF', borderRadius: 0,
        padding: '28px 36px', position: 'relative', overflow: 'hidden', marginBottom: 14 }}>
        {/* Marca d'água temática (engrenagem) */}
        <svg width="280" height="280" viewBox="0 0 24 24" fill="none" stroke="#FFF"
          strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"
          style={{ position: 'absolute', right: -24, bottom: -70, opacity: 0.05,
            pointerEvents: 'none', userSelect: 'none' }}>
          <circle cx="12" cy="12" r="3"/>
          <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"/>
        </svg>
        <div style={{ fontSize: 10, fontWeight: 700, color: 'rgba(255,255,255,0.65)',
          letterSpacing: '0.20em', textTransform: 'uppercase', marginBottom: 10 }}>
          Configurações Complementares · a base de cada empresa
        </div>
        <h1 style={{ fontSize: 32, fontWeight: 900, lineHeight: 1.1, letterSpacing: '-0.02em',
          marginBottom: 12, maxWidth: 720, fontFamily: "'Outfit', sans-serif" }}>
          Toda análise começa com a empresa <span style={{ color: G.laranja }}>bem configurada</span>.
        </h1>
        <p style={{ fontSize: 14, lineHeight: 1.5, color: 'rgba(255,255,255,0.92)',
          maxWidth: 720, margin: 0 }}>
          Parâmetros financeiros e dados do Balanço Patrimonial — configure cada bloco e a{' '}
          <span style={{ display: 'inline-block', background: '#FFF', padding: '2px 9px', borderRadius: 0, verticalAlign: 'baseline', lineHeight: 1.1 }}><MarcaAIdvisor variant="default" /></span>{' '}
          usa tudo no diagnóstico e no Business Plan.
        </p>
      </section>

      {/* ═══════════ 3 CARDS ═══════════ */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16, marginBottom: 14 }}>
        {cards.map(c => (
          <CardFonteEntrada key={c.id} fonte={c} onClick={() => abrirCard(c.id)} />
        ))}
      </div>

      {/* ═══════════ RODAPÉ (padrão BP60M) ═══════════ */}
      <section style={{ display: 'flex', justifyContent: 'space-between',
        alignItems: 'center', flexWrap: 'wrap', gap: 14, padding: '4px 6px' }}>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginLeft: 'auto' }}>
          <BotaoSaibaMais onClick={() => setSaibaMais(true)} contorno tamanho="lg" seta={false} />
          <button onClick={() => setSub('financeiras')} style={ctaPrimario}>Começar →</button>
        </div>
      </section>

      {/* ═══════════ MODAL SAIBA MAIS ═══════════ */}
      {saibaMais && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(10,10,8,0.7)', zIndex: 9999,
          display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}
          onClick={() => setSaibaMais(false)}>
          <div style={{ background: '#FFF', borderRadius: 0, padding: '26px 30px', maxWidth: 560,
            width: '100%', border: `1px solid ${G.borda}`, maxHeight: '85vh', overflowY: 'auto' }}
            onClick={e => e.stopPropagation()}>
            <div style={{ fontSize: 11, fontWeight: 800, color: G.verde, letterSpacing: '0.10em',
              textTransform: 'uppercase', marginBottom: 8 }}>Configurações Complementares</div>
            <h2 style={{ fontSize: 22, fontWeight: 800, color: G.txt1, margin: '0 0 14px' }}>
              Dois blocos de configuração
            </h2>
            {[
              ['1. Configurações Financeiras', 'Os parâmetros de cálculo: saldos, WACC, impostos e perpetuidade.'],
              ['2. Configurações Balanço Patrimonial', 'Os dados estruturais do patrimônio: estoque, imobilizado, capital social e dívidas.'],
            ].map(([t, d], i) => (
              <div key={i} style={{ marginBottom: 12, background: G.fundo, borderRadius: 0, padding: '14px 16px' }}>
                <div style={{ fontSize: 14, fontWeight: 800, color: G.txt1, marginBottom: 3 }}>{t}</div>
                <div style={{ fontSize: 12.5, color: G.txt2, lineHeight: 1.5 }}>{d}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// Tela PÚBLICA de aceite da Proposta · o cliente abre /rungoal-proposta/<token>
// (sem login) · vê a proposta montada e escolhe Concordar ou Recusar.
// Seção 6 da Proposta · Confidencialidade e Proteção de Dados (unificação Proposta+NDA, 24/07).
// O aceite da Proposta sela também esta confidencialidade — retorna as linhas do preâmbulo + cláusulas.
const secaoConfidencialidadeLinhas = (d) => {
  const empresa = d.razao_social || d.nome_empresa || 'a empresa CLIENTE';
  const cnpj = d.cnpj || '—', rep = d.representante || '—';
  return [
    'PROTEÇÃO DOS SEUS DADOS',
    'A RUNGOAL AIDVISOR foi construída para tratar as informações da sua empresa com rigor de segurança. Comprometemo-nos a:',
    '1. registrar toda consulta aos seus dados numa trilha de auditoria (quem acessou, o quê, quando e de onde);',
    '2. isolar logicamente as informações por empresa — só você e o seu consultor acessam os seus dados;',
    '3. proteger os dados com criptografia em trânsito e em repouso;',
    '4. guardar credenciais e senhas segundo os padrões de segurança da indústria;',
    '5. tratar dados pessoais apenas para a finalidade da consultoria, conforme a LGPD (Lei nº 13.709/2018);',
    '6. permitir que você conceda e revogue o consentimento a qualquer momento.',
    'Estes compromissos vigoram durante toda a prestação do serviço.',
    '',
    `As partes — NXS Consultoria (CONSULTORA) e ${empresa}, inscrita no CNPJ nº ${cnpj}, representada por ${rep} (CLIENTE) — ajustam as obrigações de confidencialidade a seguir, que integram esta Proposta:`,
    '6.1 Objeto. São confidenciais todos os dados financeiros, contábeis, tributários, estratégicos, operacionais, cadastrais e pessoais que uma parte compartilhe com a outra em razão da consultoria, em qualquer forma ou meio, inclusive os já trocados desde o primeiro contato.',
    '6.2 Sigilo. A parte receptora obriga-se a manter as informações em sigilo, usá-las só para a finalidade da consultoria, restringir o acesso a quem precise conhecê-las e não divulgá-las a terceiros sem autorização escrita.',
    '6.3 Exceções. Não são confidenciais as informações que se tornem públicas sem violação deste Termo, já fossem comprovadamente conhecidas, ou cuja revelação seja exigida por lei ou autoridade competente (com aviso prévio quando possível).',
    '6.4 LGPD. O tratamento de dados pessoais observará a Lei nº 13.709/2018, limitado à finalidade da consultoria.',
    '6.5 Vigência. As obrigações vigoram durante a consultoria e por 5 (cinco) anos após o seu término.',
    '6.6 Reciprocidade. As obrigações aplicam-se igualmente a ambas as partes.',
    '6.7 Responsabilidade. A parte que descumprir responderá pelas perdas e danos a que der causa.',
    '6.8 Foro. Fica eleito o foro da comarca de São Paulo/SP para dirimir controvérsias.',
    'O aceite eletrônico desta Proposta, com identificação do signatário e registro de data e hora, formaliza também a concordância do CLIENTE com estas obrigações de confidencialidade.',
  ];
};

const TelaPropostaPublica = ({ token }) => {
  const [carregando, setCarregando] = useState(true);
  const [erroCarga, setErroCarga] = useState('');
  const [prop, setProp] = useState(null);
  const [aba, setAba] = useState(null);              // null | 'concordar' | 'recusar'
  const [nome, setNome] = useState('');
  const [motivo, setMotivo] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [erroAcao, setErroAcao] = useState('');
  const [resultado, setResultado] = useState(null);  // 'aceita' | 'recusada'

  useEffect(() => {
    document.title = 'Rungoal Proposta';
    (async () => {
      try {
        const r = await fetch(`${API}/rungoal-proposta/${token}`);
        if (!r.ok) { const j = await r.json().catch(() => ({})); throw new Error(j.detail || 'Proposta não encontrada ou link expirado.'); }
        const j = await r.json();
        setProp(j);
        if (j.status === 'aceita') setResultado('aceita');
        if (j.status === 'recusada') setResultado('recusada');
      } catch (e) { setErroCarga(e.message || 'Falha ao carregar a proposta.'); }
      setCarregando(false);
    })();
  }, [token]);

  const concordar = async () => {
    setErroAcao('');
    if (!nome.trim()) { setErroAcao('Informe o seu nome.'); return; }
    setEnviando(true);
    try {
      const r = await fetch(`${API}/rungoal-proposta/${token}/aceitar`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ aceito_por: nome.trim() }),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j.detail || 'Falha ao registrar o aceite.');
      setResultado('aceita');
    } catch (e) { setErroAcao(e.message); }
    setEnviando(false);
  };
  const recusar = async () => {
    setErroAcao('');
    if (motivo.trim().length < 20) { setErroAcao('Descreva o motivo com ao menos 20 caracteres.'); return; }
    setEnviando(true);
    try {
      const r = await fetch(`${API}/rungoal-proposta/${token}/recusar`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ motivo: motivo.trim() }),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j.detail || 'Falha ao registrar a recusa.');
      setResultado('recusada');
    } catch (e) { setErroAcao(e.message); }
    setEnviando(false);
  };

  const palco = { minHeight: '100vh', background: '#EAEEE8', padding: '40px 20px 64px', fontFamily: "'Outfit', sans-serif" };
  const folha = { maxWidth: 760, margin: '0 auto', background: '#FDFCF8', border: `1px solid ${G.borda}`, borderTop: `3px solid ${G.verde}`, borderRadius: 0, padding: '46px 52px 40px', color: G.txt1, boxShadow: '0 24px 60px -28px rgba(19,71,33,.28)' };

  if (carregando) return <div style={palco}><div style={{ ...folha, textAlign: 'center', color: G.txt2 }}>Carregando proposta…</div></div>;
  if (erroCarga) return <div style={palco}><div style={{ ...folha, textAlign: 'center' }}><div style={{ fontSize: 18, fontWeight: 800, color: G.vermelho, marginBottom: 8 }}>Link indisponível</div><div style={{ color: G.txt2 }}>{erroCarga}</div></div></div>;

  const p = prop;
  const escopoSel = new Set(p.proposta_escopo || []);
  const catalogo = p.tipo_consultoria === 'ma' ? CATALOGO_ESCOPO_MA : CATALOGO_ESCOPO_GESTAO;
  const catsComSel = catalogo.map(cat => ({ cat, itens: cat.itens.filter(i => escopoSel.has(i.id)) })).filter(g => g.itens.length > 0);
  const objs = (p.proposta_objetivos || []).map(o => String(o).trim()).filter(Boolean);
  const valor = Number(p.proposta_valor_total) || 0;
  const nparc = Number(p.proposta_num_parcelas) || 0;
  const parcela = (valor > 0 && nparc >= 1) ? valor / nparc : 0;
  const fmtBRL = (v) => (Number(v) || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
  const secTit = { fontFamily: "'Outfit', sans-serif", fontSize: 13, textTransform: 'uppercase', letterSpacing: '0.10em', fontWeight: 800, color: G.verde, margin: '26px 0 0', paddingBottom: 8, borderBottom: `1px solid ${G.borda}` };
  const item = { display: 'flex', gap: 10, padding: '3px 0', fontSize: 14.5 };
  const numSpan = { color: G.verde, fontWeight: 700 };
  const contato = [p.proposta_assina_telefone, p.proposta_assina_email].filter(Boolean).join(' · ');

  return (
    <div style={palco} lang="pt-BR">
      <div style={{ maxWidth: 760, margin: '0 auto', position: 'relative' }}>
        <CarimboAIdvisor top={-28} right={-10} tamanho={120} />
        <div style={folha}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', borderBottom: `1px solid ${G.borda}`, paddingBottom: 14 }}>
          <div>
            <div style={{ fontWeight: 800, fontSize: 16 }}>{p.proposta_assina_nome || 'NXS Consultoria'}</div>
            <div style={{ fontSize: 11.5, color: G.txt2 }}>{p.proposta_assina_titulo || ''}</div>
          </div>
          <div style={{ textAlign: 'right', fontSize: 11.5, color: G.txt2 }}>{contato}</div>
        </div>
        <div style={{ fontSize: 13.5, marginTop: 16 }}>
          <div>À <b>{p.nome_empresa || '—'}</b></div>
          {p.destinatario_nome && <div>A/C. {p.destinatario_nome}{p.destinatario_cargo ? ` — ${p.destinatario_cargo}` : ''}</div>}
          <div style={{ marginTop: 4 }}>Ref.: <span style={{ fontFamily: 'monospace', color: '#B8531F', background: '#FFF1E7', border: '1px solid #F6C9AC', borderRadius: 0, padding: '2px 8px' }}>{p.proposta_numero || '—'}</span></div>
        </div>
        {p.destinatario_nome && <p style={{ marginTop: 20, fontSize: 15 }}>Prezado(a) {p.destinatario_nome},</p>}
        <p style={{ marginTop: 12, fontSize: 15, textAlign: 'justify' }}>Conforme nossos entendimentos, formalizamos a seguir proposta de Consultoria {p.tipo_consultoria === 'ma' ? 'em Fusão e Aquisição' : 'Empresarial'} a ser desenvolvida para {p.nome_empresa || 'a empresa'}.</p>
        {p.proposta_introducao && <p style={{ marginTop: 10, fontSize: 15, textAlign: 'justify' }}>{p.proposta_introducao}</p>}

        <h3 style={secTit}>1. A Empresa</h3>
        <p style={{ fontSize: 14.5, textAlign: 'justify' }}>{p.proposta_a_empresa || '—'}</p>

        <h3 style={secTit}>2. Objetivos</h3>
        {objs.length ? objs.map((o, i) => <div key={i} style={item}><span style={numSpan}>2.{i + 1}</span><span style={{ textAlign: 'justify' }}>{o}</span></div>) : <span style={{ color: G.txt2, fontSize: 14 }}>—</span>}

        <h3 style={secTit}>3. Escopo</h3>
        {catsComSel.length ? catsComSel.map((g, ci) => (
          <div key={g.cat.id} style={{ marginTop: 12 }}>
            <div style={{ fontWeight: 700, fontSize: 14 }}><span style={numSpan}>3.{ci + 1}</span> {g.cat.nome}</div>
            <div style={{ marginLeft: 24 }}>
              {g.itens.map((it, ii) => <div key={it.id} style={{ ...item, fontSize: 14 }}><span style={numSpan}>3.{ci + 1}.{ii + 1}</span><span style={{ textAlign: 'justify' }}><b>{it.titulo}</b>{it.desc ? <span style={{ color: G.txt2 }}> — {it.desc}</span> : null}</span></div>)}
            </div>
          </div>
        )) : <span style={{ color: G.txt2, fontSize: 14 }}>—</span>}

        <h3 style={secTit}>4. Condições Comerciais</h3>
        <div style={item}><span style={numSpan}>4.1</span><span><b>Valor total:</b> {fmtBRL(valor)}.</span></div>
        <div style={item}><span style={numSpan}>4.2</span><span><b>Parcelamento:</b> {nparc >= 1 && parcela > 0 ? `${nparc} parcelas mensais de ${fmtBRL(parcela)} — a 1ª parcela constitui o sinal.` : '—'}</span></div>
        <div style={item}><span style={numSpan}>4.3</span><span><b>Vencimentos:</b> a 1ª parcela (sinal) no aceite desta proposta; as demais no mesmo dia dos meses subsequentes.</span></div>
        {Number(p.proposta_success_fee_pct) > 0 && <div style={item}><span style={numSpan}>4.4</span><span><b>Success-fee:</b> {p.proposta_success_fee_pct}% sobre o valor da transação.</span></div>}

        {p.condicoes_gerais && (<>
          <h3 style={secTit}>5. Condições Gerais</h3>
          {String(p.condicoes_gerais).split('\n').map(l => l.trim()).filter(Boolean).map((l, i) => <div key={i} style={item}><span style={numSpan}>5.{i + 1}</span><span style={{ textAlign: 'justify' }}>{l}</span></div>)}
        </>)}

        <h3 style={secTit}>6. Confidencialidade e Proteção de Dados</h3>
        {secaoConfidencialidadeLinhas(p).map((l, i) => l === ''
          ? <div key={i} style={{ height: 6 }} />
          : <p key={i} style={{ fontSize: 13.5, textAlign: 'justify', margin: '3px 0',
              color: (l === 'PROTEÇÃO DOS SEUS DADOS' || l.startsWith('As partes —')) ? G.txt1 : G.txt2,
              fontWeight: l === 'PROTEÇÃO DOS SEUS DADOS' ? 700 : 400 }}>{l}</p>)}

        <p style={{ marginTop: 24, fontSize: 14.5, textAlign: 'justify' }}>Agradecemos a oportunidade de apresentar esta proposta e permanecemos à disposição para os esclarecimentos que se fizerem necessários.</p>
        <p style={{ marginTop: 12, fontSize: 14.5 }}>Atenciosamente,</p>
        <div style={{ marginTop: 26 }}>
          <div style={{ fontWeight: 800 }}>{p.proposta_assina_nome || '—'}</div>
          <div style={{ fontSize: 12.5, color: G.txt2 }}>{p.proposta_assina_titulo || ''}</div>
          <div style={{ fontSize: 12.5, color: G.txt2 }}>{contato}</div>
        </div>
      </div>

      <div style={{ maxWidth: 760, margin: '18px auto 0' }}>
        {resultado === 'aceita' ? (
          <div style={{ background: G.verdeLight, border: `1px solid ${G.verde}`, borderRadius: 0, padding: '18px 20px', textAlign: 'center' }}>
            <div style={{ fontSize: 16, fontWeight: 800, color: G.verde }}>Proposta aceita · {p.proposta_numero}</div>
            <div style={{ fontSize: 13, color: G.txt1, marginTop: 6 }}>Obrigado. Em breve entraremos em contato para os próximos passos.</div>
          </div>
        ) : resultado === 'recusada' ? (
          <div style={{ background: '#FFF7ED', border: '1px solid #F9C99B', borderRadius: 0, padding: '18px 20px', textAlign: 'center' }}>
            <div style={{ fontSize: 16, fontWeight: 800, color: '#B45309' }}>Proposta recusada</div>
            <div style={{ fontSize: 13, color: G.txt1, marginTop: 6 }}>Registramos a sua resposta. Obrigado pelo retorno.</div>
          </div>
        ) : (<>
          {!aba && (
            <div style={{ display: 'flex', gap: 12, justifyContent: 'center' }}>
              <button onClick={() => { setAba('concordar'); setErroAcao(''); }} style={{ background: G.verde, color: '#FFF', border: 'none', borderRadius: 0, padding: '13px 30px', fontSize: 13, fontWeight: 800, letterSpacing: '0.06em', textTransform: 'uppercase', cursor: 'pointer', fontFamily: 'inherit' }}>Concordar</button>
              <button onClick={() => { setAba('recusar'); setErroAcao(''); }} style={{ background: '#FFF', color: G.txt2, border: `1px solid ${G.borda}`, borderRadius: 0, padding: '13px 26px', fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>Recusar</button>
            </div>
          )}
          {aba === 'concordar' && (
            <div style={{ background: '#FFF', border: `1px solid ${G.verde}`, borderRadius: 0, padding: '18px 20px' }}>
              <div style={{ fontSize: 14, fontWeight: 800, color: G.txt1, marginBottom: 10 }}>Concordo com a {p.proposta_numero}</div>
              <label style={{ fontSize: 11, fontWeight: 700, color: G.txt2, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Seu nome</label>
              <input value={nome} onChange={e => setNome(e.target.value)} placeholder="Nome de quem concorda" style={{ width: '100%', boxSizing: 'border-box', border: `1px solid ${G.borda}`, borderRadius: 0, padding: '10px 12px', fontSize: 14, marginTop: 5, fontFamily: 'inherit', outline: 'none' }} />
              {erroAcao && <div style={{ color: G.vermelho, fontSize: 12.5, marginTop: 8 }}>{erroAcao}</div>}
              <div style={{ display: 'flex', gap: 10, marginTop: 12, alignItems: 'center' }}>
                <button onClick={concordar} disabled={enviando} style={{ background: G.verde, color: '#FFF', border: 'none', borderRadius: 0, padding: '11px 24px', fontSize: 12.5, fontWeight: 800, cursor: 'pointer', fontFamily: 'inherit' }}>{enviando ? 'Registrando…' : 'Confirmar aceite'}</button>
                <button onClick={() => setAba(null)} style={{ background: 'none', border: 'none', color: G.txt2, cursor: 'pointer', fontSize: 12.5, fontFamily: 'inherit' }}>Voltar</button>
              </div>
            </div>
          )}
          {aba === 'recusar' && (
            <div style={{ background: '#FFF', border: `1px solid ${G.borda}`, borderRadius: 0, padding: '18px 20px' }}>
              <div style={{ fontSize: 14, fontWeight: 800, color: G.txt1, marginBottom: 10 }}>Recusar a proposta</div>
              <label style={{ fontSize: 11, fontWeight: 700, color: G.txt2, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Motivo (mínimo 20 caracteres)</label>
              <textarea value={motivo} onChange={e => setMotivo(e.target.value)} placeholder="Conte-nos o motivo da recusa." style={{ width: '100%', boxSizing: 'border-box', border: `1px solid ${G.borda}`, borderRadius: 0, padding: '10px 12px', fontSize: 14, marginTop: 5, minHeight: 80, resize: 'vertical', fontFamily: 'inherit', outline: 'none' }} />
              {erroAcao && <div style={{ color: G.vermelho, fontSize: 12.5, marginTop: 8 }}>{erroAcao}</div>}
              <div style={{ display: 'flex', gap: 10, marginTop: 12, alignItems: 'center' }}>
                <button onClick={recusar} disabled={enviando} style={{ background: '#B45309', color: '#FFF', border: 'none', borderRadius: 0, padding: '11px 24px', fontSize: 12.5, fontWeight: 800, cursor: 'pointer', fontFamily: 'inherit' }}>{enviando ? 'Registrando…' : 'Confirmar recusa'}</button>
                <button onClick={() => setAba(null)} style={{ background: 'none', border: 'none', color: G.txt2, cursor: 'pointer', fontSize: 12.5, fontFamily: 'inherit' }}>Voltar</button>
              </div>
            </div>
          )}
        </>)}
        <div style={{ textAlign: 'center', marginTop: 18, fontSize: 11, color: G.txt2 }}>
          Preparado com <span style={{ display: 'inline-block', background: '#FFF', padding: '2px 9px', borderRadius: 0, verticalAlign: 'baseline', lineHeight: 1.1 }}><MarcaAIdvisor variant="default" /></span>
        </div>
        </div>
      </div>
    </div>
  );
};

// =====================================================================
// TelaPreCadastroPublico — Módulo Consultoria
// Renderiza o formulário público (sem login) do Pré-Cadastro inicial
// que o prospect preenche pelo link `/rungoal-pre-cadastro/<token>`.
// 7 campos mínimos · CNPJ vira referência de unicidade.
// =====================================================================
const TelaPreCadastroPublico = ({ token }) => {
  // Ramos de atividade · catálogo curado para PME brasileiro · evita texto livre
  // (memória `publico-alvo-bp60m` · "proibido pedir para empresário escrever").
  const RAMOS = [
    'Agronegócio', 'Alimentação · Padaria · Confeitaria',
    'Bar · Restaurante · Lanchonete', 'Beleza · Estética · Salão',
    'Cafeteria · Sorveteria', 'Comércio · Atacado',
    'Comunicação · Marketing · Publicidade',
    'Construção Civil', 'Cooperativa', 'Cosméticos · Perfumaria',
    'Educação · Cursos', 'E-commerce', 'Eventos · Buffet',
    'Farmácia · Drogaria', 'Floricultura',
    'Hotelaria · Pousada · Turismo',
    'Imobiliário', 'Indústria · Manufatura', 'Indústria Têxtil · Confecção',
    'Jurídico · Advocacia', 'Limpeza · Conservação',
    'Logística · Transporte', 'Livraria · Papelaria',
    'Oficina Mecânica · Auto Peças', 'Pet Shop · Veterinária',
    'Posto de Combustível', 'Recursos Humanos · Recrutamento',
    'Saúde · Clínica · Hospital', 'Segurança',
    'Serviços Contábeis · Consultoria', 'Serviços Profissionais',
    'Tecnologia · Software · SaaS', 'Telecomunicações',
    'Transporte de Passageiros', 'Varejo Físico', 'Vestuário · Calçados',
    'Outro · descrever no campo Breve Descritivo',
  ];
  const REGIMES = ['Simples Nacional', 'Lucro Presumido', 'Lucro Real'];
  const [form, setForm] = useState({
    nome_empresa: '', cnpj: '', ramo_atividade: '', representante: '',
    email: '', celular: '', breve_descritivo: '',
    regime: '', site: '', instagram: '', linkedin: '',
  });
  const [carregando, setCarregando] = useState(true);
  const [salvando, setSalvando]     = useState(false);
  const [erroCarga, setErroCarga]   = useState(null);
  const [erroSalvar, setErroSalvar] = useState(null);
  const [sucesso, setSucesso]       = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`${API}/rungoal-pre-cadastro/${token}`);
        if (!r.ok) {
          const j = await r.json().catch(() => ({}));
          throw new Error(j.detail || 'Link inválido ou expirado.');
        }
        const data = await r.json();
        setForm(f => ({
          ...f,
          ...Object.fromEntries(Object.entries(data).filter(
            ([k, v]) => v !== null && k !== 'status'
          )),
        }));
        document.title = data.nome_empresa
          ? `Rungoal Pré-Cadastro · ${data.nome_empresa}`
          : 'Rungoal Pré-Cadastro';
      } catch (e) {
        setErroCarga(e.message || 'Falha ao carregar o Pré-Cadastro.');
      } finally {
        setCarregando(false);
      }
    })();
  }, [token]);

  // Presença social: máscara leve para facilitar (Instagram sempre com @).
  const mascaraInstagram = (v) => {
    const s = String(v).replace(/^@+/, '').trim();
    return s ? '@' + s : '';
  };
  const onChange = (campo, valor) => {
    if (campo === 'instagram') valor = mascaraInstagram(valor);
    setForm(f => ({ ...f, [campo]: valor }));
  };

  const submeter = async () => {
    if (!form.regime) { setErroSalvar('Selecione o regime tributário (obrigatório).'); return; }
    setSalvando(true);
    setErroSalvar(null);
    try {
      const r = await fetch(`${API}/rungoal-pre-cadastro/${token}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      if (!r.ok) {
        const j = await r.json().catch(() => ({}));
        throw new Error(j.detail || 'Falha ao enviar o Pré-Cadastro.');
      }
      setSucesso(true);
    } catch (e) {
      setErroSalvar(e.message || 'Falha ao enviar o Pré-Cadastro.');
    } finally {
      setSalvando(false);
    }
  };

  const estiloPagina = {
    minHeight: '100vh', background: G.fundo, padding: '36px 24px',
    fontFamily: "'Outfit', sans-serif", color: G.txt1,
  };
  const card = {
    maxWidth: 760, margin: '0 auto', background: '#FFF',
    border: `1px solid ${G.borda}`, borderRadius: 0,
    padding: '28px 32px',
  };
  const inputStyle = {
    width: '100%', padding: '10px 12px', fontSize: 14,
    border: `1px solid ${G.borda}`, borderRadius: 0,
    fontFamily: "'Outfit', sans-serif", color: G.txt1,
    boxSizing: 'border-box',
  };
  const textareaStyle = { ...inputStyle, minHeight: 100, resize: 'vertical' };
  const labelStyle = {
    fontSize: 12, fontWeight: 600, color: G.txt2,
    display: 'block', marginBottom: 4,
  };

  if (carregando) {
    return (
      <div style={estiloPagina}>
        <div style={card}>
          <div style={{ fontSize: 14, color: G.txt2, fontStyle: 'italic' }}>
            Carregando…
          </div>
        </div>
      </div>
    );
  }

  if (erroCarga) {
    return (
      <div style={estiloPagina}>
        <div style={card}>
          <div style={{ fontSize: 11, fontWeight: 800, color: G.laranja,
            letterSpacing: '0.10em', textTransform: 'uppercase',
            marginBottom: 6 }}>Link inválido</div>
          <div style={{ fontSize: 16, color: G.txt1, lineHeight: 1.5 }}>
            {erroCarga}
          </div>
          <div style={{ marginTop: 12, fontSize: 13, color: G.txt2,
            lineHeight: 1.5 }}>
            Verifique com o consultor que enviou o link.
          </div>
        </div>
      </div>
    );
  }

  if (sucesso) {
    return (
      <div style={estiloPagina}>
        <div style={card}>
          <div style={{ fontSize: 11, fontWeight: 800, color: G.verde,
            letterSpacing: '0.10em', textTransform: 'uppercase',
            marginBottom: 6 }}>Pré-Cadastro enviado</div>
          <h1 style={{ fontSize: 28, fontWeight: 800, color: G.txt1,
            margin: '0 0 12px 0', letterSpacing: '-0.01em' }}>
            Obrigado pelo preenchimento.
          </h1>
          <p style={{ fontSize: 15, color: G.txt2, lineHeight: 1.55, margin: 0 }}>
            Seus dados foram recebidos pela <MarcaAIdvisor variant="default" />.
            O consultor responsável já consegue dar continuidade ao processo ·
            você receberá em breve a proposta de consultoria.
          </p>
          <div style={{ marginTop: 18, fontSize: 13, color: G.txt2,
            fontStyle: 'italic' }}>
            Você pode fechar esta janela.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={estiloPagina}>
      <div style={{ ...card, position: 'relative' }}>
        <CarimboAIdvisor top={-22} right={24} tamanho={120} />
        <div style={{ borderBottom: `1px solid ${G.borda}`, paddingBottom: 14,
          marginBottom: 18, fontSize: 11, fontWeight: 700,
          letterSpacing: '0.18em', textTransform: 'uppercase',
          color: G.txt2 }}>
          Powered by <MarcaAIdvisor variant="default" />
        </div>

        <div style={{ fontSize: 11, fontWeight: 800, color: G.laranja,
          letterSpacing: '0.10em', textTransform: 'uppercase',
          marginBottom: 8 }}>Pré-Cadastro de Consultoria</div>
        <h1 style={{ fontSize: 26, fontWeight: 800, color: G.txt1,
          margin: '0 0 6px 0', letterSpacing: '-0.01em' }}>
          Conte-nos um pouco sobre sua empresa.
        </h1>
        <p style={{ fontSize: 13.5, color: G.txt2, lineHeight: 1.55, margin: 0 }}>
          Essas informações iniciais ajudam o consultor a desenhar a proposta
          de consultoria mais adequada.
        </p>

        <div style={{ marginTop: 22, display: 'grid',
          gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <div>
            <label style={labelStyle}>Nome da Empresa</label>
            <input style={inputStyle} value={form.nome_empresa}
              onChange={e => onChange('nome_empresa', e.target.value)} />
          </div>
          <div>
            <label style={labelStyle}>CNPJ</label>
            <input style={inputStyle} value={form.cnpj}
              onChange={e => onChange('cnpj', e.target.value)}
              placeholder="00.000.000/0000-00" />
          </div>
          <div>
            <label style={labelStyle}>Ramo de Atividade</label>
            <select style={inputStyle} value={form.ramo_atividade}
              onChange={e => onChange('ramo_atividade', e.target.value)}>
              <option value="">Selecione…</option>
              {RAMOS.map(r => <option key={r} value={r}>{r}</option>)}
            </select>
          </div>
          <div>
            <label style={labelStyle}>Regime Tributário *</label>
            <select style={inputStyle} value={form.regime}
              onChange={e => onChange('regime', e.target.value)}>
              <option value="">Selecione…</option>
              {REGIMES.map(r => <option key={r} value={r}>{r}</option>)}
            </select>
          </div>
          <div>
            <label style={labelStyle}>Representante</label>
            <input style={inputStyle} value={form.representante}
              onChange={e => onChange('representante', e.target.value)} />
          </div>
          <div>
            <label style={labelStyle}>E-mail</label>
            <input style={inputStyle} value={form.email}
              onChange={e => onChange('email', e.target.value)} type="email" />
          </div>
          <div>
            <label style={labelStyle}>Celular</label>
            <input style={inputStyle} value={form.celular}
              onChange={e => onChange('celular', e.target.value)} />
          </div>
        </div>

        <div style={{ marginTop: 18, fontSize: 12, fontWeight: 800, color: G.verde,
          letterSpacing: '0.08em', textTransform: 'uppercase' }}>
          Presença Social <span style={{ fontWeight: 600, color: G.txt2,
            textTransform: 'none', letterSpacing: 0 }}>(opcional)</span>
        </div>
        <div style={{ marginTop: 10, display: 'grid',
          gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
          <div>
            <label style={labelStyle}>Site</label>
            <input style={inputStyle} value={form.site}
              onChange={e => onChange('site', e.target.value)}
              placeholder="https://" />
          </div>
          <div>
            <label style={labelStyle}>Instagram</label>
            <input style={inputStyle} value={form.instagram}
              onChange={e => onChange('instagram', e.target.value)}
              placeholder="@" />
          </div>
          <div>
            <label style={labelStyle}>LinkedIn</label>
            <input style={inputStyle} value={form.linkedin}
              onChange={e => onChange('linkedin', e.target.value)}
              placeholder="linkedin.com/company/…" />
          </div>
        </div>
        <div style={{ marginTop: 12 }}>
          <label style={labelStyle}>Breve Descritivo da Empresa</label>
          <textarea style={textareaStyle} value={form.breve_descritivo}
            onChange={e => onChange('breve_descritivo', e.target.value)}
            placeholder="Descreva em poucas linhas o que a empresa faz, há quanto tempo opera, principais produtos ou serviços." />
        </div>

        {erroSalvar && (
          <div style={{ background: '#FFF0E6', border: `1px solid ${G.laranja}`,
            color: G.laranja, padding: '8px 12px', borderRadius: 0,
            fontSize: 13, marginTop: 16 }}>{erroSalvar}</div>
        )}

        <div style={{ marginTop: 22, display: 'flex',
          justifyContent: 'flex-end' }}>
          <button onClick={submeter} disabled={salvando}
            style={{ background: G.verde, color: '#FFF', border: 'none',
              padding: '10px 22px', fontSize: 13, fontWeight: 700,
              letterSpacing: '0.08em', textTransform: 'uppercase',
              borderRadius: 0, cursor: salvando ? 'wait' : 'pointer',
              fontFamily: "'Outfit', sans-serif",
              opacity: salvando ? 0.6 : 1 }}>
            {salvando ? 'Enviando…' : 'Enviar Pré-Cadastro'}
          </button>
        </div>

        <div style={{ marginTop: 28, paddingTop: 14,
          borderTop: `1px solid ${G.borda}`,
          fontSize: 10.5, color: G.txt2,
          letterSpacing: '0.05em', textTransform: 'uppercase',
          textAlign: 'right' }}>
          Powered by <MarcaAIdvisor variant="default" />
        </div>
      </div>
    </div>
  );
};

// =====================================================================
// TelaConsultoria — aba admin do Módulo Consultoria
// Landing Page com hub (cards Em Prospecção · Em Consultoria) · botão
// "+ Captar nova consultoria" · listas internas com tabs.
// Estados do funil:
//   Em Prospecção:    pre_cadastro_pendente · pre_cadastro_recebido ·
//                     escopo_em_definicao · proposta_emitida · recusada
//   Em Consultoria:   aceita · cadastro_completo_pendente · cadastro_completo_recebido ·
//                     nda_pendente · nda_assinado · convertida
// =====================================================================
// =====================================================================
// Catálogos de escopo da Proposta de Consultoria (fonte: documentos NXS).
// Modalidade Gestão (Consultoria Empresarial) e M&A (Fusão e Aquisição).
// Seleção híbrida: caixa da categoria marca/desmarca todos os itens dela.
// =====================================================================
const CATALOGO_ESCOPO_GESTAO = [
  { id: 'estrategica', nome: 'Consultoria Estratégica', foco: 'Direcionamento de longo prazo e tomada de decisão de alto nível.', itens: [
    { id: 'plan_estrategico', titulo: 'Planejamento Estratégico', desc: 'Missão, visão, valores, metas e plano de ação de longo prazo.' },
    { id: 'swot', titulo: 'Análise SWOT', desc: 'Identificação de forças, fraquezas, oportunidades e ameaças do negócio.' },
    { id: 'posicionamento', titulo: 'Posicionamento de Mercado', desc: 'Proposta de valor e diferenciação competitiva.' },
    { id: 'modelagem', titulo: 'Modelagem de Negócio', desc: 'Estruturação ou revisão do modelo de geração de receita.' },
    { id: 'expansao', titulo: 'Planejamento de Expansão', desc: 'Estruturação de crescimento, novas unidades, regiões ou mercados.' },
  ]},
  { id: 'financeira', nome: 'Consultoria Financeira', foco: 'Sustentabilidade financeira e maximização de resultados.', itens: [
    { id: 'fluxo_caixa', titulo: 'Gestão de Fluxo de Caixa', desc: 'Controle e projeção de entradas e saídas financeiras.' },
    { id: 'orcamentario', titulo: 'Planejamento Orçamentário (Budget)', desc: 'Elaboração e acompanhamento de orçamento empresarial.' },
    { id: 'custos_margens', titulo: 'Análise de Custos e Margens', desc: 'Identificação de custos, formação de preços e rentabilidade.' },
    { id: 'valuation', titulo: 'Valuation (Avaliação de Empresas)', desc: 'Determinação do valor econômico do negócio.' },
    { id: 'reestruturacao', titulo: 'Reestruturação Financeira', desc: 'Ajustes em dívidas, capital e estrutura financeira.' },
    { id: 'kpis', titulo: 'Indicadores de Performance (KPIs)', desc: 'Definição e monitoramento de métricas financeiras.' },
  ]},
  { id: 'administrativa', nome: 'Consultoria Administrativa/Operacional', foco: 'Eficiência operacional e organização interna.', itens: [
    { id: 'processos', titulo: 'Mapeamento e Otimização de Processos', desc: 'Identificação de gargalos e melhoria de fluxos operacionais.' },
    { id: 'estrutura_org', titulo: 'Estrutura Organizacional', desc: 'Validação de cargos, responsabilidades e hierarquia.' },
    { id: 'procedimentos', titulo: 'Implantação de Procedimentos', desc: 'Padronização de rotinas operacionais.' },
    { id: 'indic_operacionais', titulo: 'Gestão de Indicadores Operacionais', desc: 'Monitoramento de produtividade e eficiência.' },
    { id: 'projetos', titulo: 'Gestão de Projetos', desc: 'Planejamento, execução e controle de iniciativas internas.' },
  ]},
  { id: 'comercial', nome: 'Consultoria de Mercado e Comercial', foco: 'Crescimento de receita e posicionamento competitivo.', itens: [
    { id: 'pesquisa_mercado', titulo: 'Pesquisa de Mercado', desc: 'Análise de demanda, concorrência e comportamento do cliente.' },
    { id: 'estrategia_comercial', titulo: 'Estratégia Comercial', desc: 'Estruturação de canais de vendas, metas e abordagem comercial.' },
    { id: 'pricing', titulo: 'Precificação (Pricing)', desc: 'Definição de preços com base em valor, custo e mercado.' },
    { id: 'segmentacao', titulo: 'Segmentação de Clientes', desc: 'Identificação de público-alvo e nichos estratégicos.' },
    { id: 'marketing', titulo: 'Planejamento de Marketing', desc: 'Definição de ações para aquisição e retenção de clientes.' },
  ]},
  { id: 'governanca', nome: 'Consultoria em Governança e Compliance', foco: 'Segurança, transparência e conformidade.', itens: [
    { id: 'gov_corporativa', titulo: 'Implantação de Governança Corporativa', desc: 'Definição de regras, papéis e processos decisórios.' },
    { id: 'riscos', titulo: 'Gestão de Riscos', desc: 'Identificação e mitigação de riscos operacionais e financeiros.' },
    { id: 'compliance', titulo: 'Compliance e Controles Internos', desc: 'Adequação a normas legais e regulatórias.' },
    { id: 'auditoria', titulo: 'Auditoria Interna', desc: 'Avaliação de processos e controles.' },
  ]},
  { id: 'logistica', nome: 'Consultoria em Logística e Supply Chain', foco: 'Eficiência na cadeia de suprimentos.', itens: [
    { id: 'estoques', titulo: 'Gestão de Estoques', desc: 'Controle de níveis, giro e armazenagem.' },
    { id: 'plan_logistico', titulo: 'Planejamento Logístico', desc: 'Otimização de transporte e distribuição.' },
  ]},
];

const CATALOGO_ESCOPO_MA = [
  { id: 'antes_mercado', nome: 'Antes de ir a Mercado', itens: [
    { id: 'diagnostico', titulo: 'Diagnóstico e preparação', desc: 'Conhecimento da estrutura societária e desejo dos sócios · análise de documentação contábil dos últimos 3 a 5 anos · checklist técnico, jurídico e estratégico · análise de governança.' },
    { id: 'avaliacao_prelim', titulo: 'Avaliação Preliminar', desc: 'Fluxo de caixa livre · CAPEX · capital de giro · endividamento · base tributária · cálculo do valor terminal – valuation preliminar.' },
    { id: 'bp_valuation', titulo: 'Elaboração de Business Plan e Valuation', desc: 'Resumo executivo · descrição da empresa · análise de mercado · estratégia de marketing · oferta de produto/serviço · modelo de negócio · plano operacional · análise de competitividade · projeções financeiras · valuation revisado.' },
    { id: 'modalidade_comerc', titulo: 'Definição da Modalidade de Comercialização', desc: 'Aquisição de participação · aquisição de ativos · reorganização societária.' },
    { id: 'estrutura_pagto', titulo: 'Definição da Estrutura de Pagamento', desc: 'Cash in · cash out · incorporação de ações · earn out · alavancada.' },
    { id: 'comprador_alvo', titulo: 'Definição de Comprador Alvo', desc: 'Investidor · fundos de investimento · empresa do setor.' },
    { id: 'teaser', titulo: 'Elaboração de Teaser de Apresentação', desc: 'Identificação do setor e região · evolução e projeção de receita · evolução e projeção de EBITDA · evolução e projeção de crescimento · destaques estratégicos.' },
    { id: 'nda_minuta', titulo: 'Elaboração de Minuta de Termo de Confidencialidade (NDA)', desc: '' },
    { id: 'memorando', titulo: 'Elaboração de Memorando de Informações e Data Room', desc: 'Nas esferas: societário · financeiro · tributário · trabalhista · contratos · operacional · ambiental.' },
    { id: 'prop_nao_vinc', titulo: 'Elaboração de Minuta de Proposta Não Vinculante', desc: 'Percentual de participação · preço · estrutura de pagamento · earn-out · condições precedentes.' },
  ]},
  { id: 'indo_mercado', nome: 'Indo a Mercado', successFee: true, itens: [
    { id: 'busca_ativa', titulo: 'Busca ativa de sócio/investidor', desc: 'Conforme perfil definido.' },
    { id: 'acomp_nao_vinc', titulo: 'Acompanhamento e análise de propostas não vinculantes', desc: '' },
  ]},
  { id: 'fechamento', nome: 'Fechamento', itens: [
    { id: 'due_diligence', titulo: 'Acompanhamento de Auditoria (Due Diligence)', desc: 'Nas esferas: financeira · jurídica · tributária · trabalhista · operacional · ambiental.' },
    { id: 'prop_vinculante', titulo: 'Consolidação da Proposta Vinculante', desc: 'Preço final · ajustes · estrutura de pagamento · garantias.' },
    { id: 'spa', titulo: 'Acompanhamento de Elaboração de Contrato de Compra e Venda de Participação Societária (SPA)', desc: 'Condições comerciais acordadas · relação entre sócios · regras de governança · direitos políticos · direitos econômicos · regras de saída · resolução de conflitos · indenização · não concorrência.' },
    { id: 'closing', titulo: 'Acompanhamento do Fechamento (Closing)', desc: 'Assinatura do contrato · transferência de ações/quotas · pagamento · alteração societária · registro nos órgãos competentes.' },
  ]},
];

// Modalidades da proposta (nunca juntas). codigo entra no número Prop NNN/Cliente/<codigo>/Ano.
const MODALIDADES_CONSULTORIA = [
  { id: 'gestao', label: 'Consultoria Empresarial', codigo: 'Gestão', refTexto: 'Empresarial', catalogo: CATALOGO_ESCOPO_GESTAO },
  { id: 'ma', label: 'Fusão e Aquisição (M&A)', codigo: 'M&A', refTexto: 'em Fusão e Aquisição', catalogo: CATALOGO_ESCOPO_MA },
];

const ESTADOS_PROSPECCAO = new Set([
  'pre_cadastro_pendente', 'pre_cadastro_recebido',
  'escopo_em_definicao', 'proposta_emitida', 'recusada',
]);
const LABEL_STATUS = {
  pre_cadastro_pendente:        'Pré-Cadastro pendente',
  pre_cadastro_recebido:        'Pré-Cadastro recebido',
  escopo_em_definicao:          'Escopo em definição',
  proposta_emitida:             'Proposta emitida',
  recusada:                     'Recusada',
  aceita:                       'Proposta aceita',
  cadastro_completo_pendente:   'Cadastro completo pendente',
  cadastro_completo_recebido:   'Cadastro completo recebido',
  nda_pendente:                 'NDA pendente',
  nda_assinado:                 'NDA assinado',
  convertida:                   'Convertida em Empresa',
  cancelada:                    'Captação cancelada',
};

const TelaConsultoria = ({ onPedidoAberto }) => {
  const [consultorias, setConsultorias] = useState([]);
  const [carregando, setCarregando]     = useState(true);
  const [tab, setTab]                   = useState('hub');
  const [captando, setCaptando]         = useState(false);
  const [linkGerado, setLinkGerado]     = useState(null);
  const [linkCopiado, setLinkCopiado]   = useState(false);
  const [aberta, setAberta]             = useState(null); // consultoria aberta para detalhe

  const carregar = async () => {
    try {
      const r = await fetch(`${API}/consultorias`);
      if (r.ok) setConsultorias(await r.json());
    } catch (e) { /* silencioso · UI mostra vazio */ }
    setCarregando(false);
  };

  useEffect(() => { carregar(); }, []);

  const captarNova = async () => {
    setCaptando(true);
    try {
      const r = await fetch(`${API}/consultorias/captar`, { method: 'POST' });
      if (!r.ok) {
        const j = await r.json().catch(() => ({}));
        throw new Error(j.detail || 'Falha ao captar.');
      }
      const d = await r.json();
      const url = `${window.location.origin}${d.link_publico}`;
      setLinkGerado(url);
      await carregar();
    } catch (e) {
      alert(e.message || 'Falha ao captar consultoria.');
    } finally {
      setCaptando(false);
    }
  };

  // Abrir um pedido que chegou pela landing apaga um do número da aba. Só os
  // que vieram de fora e ainda não foram abertos: captação do próprio consultor
  // nunca entrou na conta.
  const marcarVisto = async (c) => {
    if (!c?.origem || c.visto_em) return;
    try {
      const r = await fetch(`${API}/consultorias/${c.id}/visto`, { method: 'PATCH' });
      if (!r.ok) return;
      setConsultorias(lista => lista.map(x =>
        x.id === c.id ? { ...x, visto_em: new Date().toISOString() } : x));
      if (onPedidoAberto) onPedidoAberto();
    } catch (e) { /* silencioso: o pedido continua na lista de qualquer jeito */ }
  };

  const copiar = async () => {
    if (!linkGerado) return;
    try {
      await navigator.clipboard.writeText(linkGerado);
      setLinkCopiado(true);
      setTimeout(() => setLinkCopiado(false), 2000);
    } catch (_) { /* ignore */ }
  };

  const prospeccao  = consultorias.filter(c => ESTADOS_PROSPECCAO.has(c.status));
  const emConsultoria = consultorias.filter(c => !ESTADOS_PROSPECCAO.has(c.status));

  const cabec = {
    fontSize: 20, fontWeight: 800, color: G.txt1,
    letterSpacing: '-0.01em', margin: 0,
  };
  const cardHub = {
    background: '#FFF', border: `1px solid ${G.borda}`, borderRadius: 0,
    padding: '24px 28px', cursor: 'pointer',
    transition: 'all 0.15s',
  };

  return (
    <div style={{ padding: '24px 28px', position: 'relative', overflow: 'hidden' }}>
      <CarimboAIdvisor right={300} />
      <div style={{ display: 'flex', justifyContent: 'space-between',
        alignItems: 'flex-start', marginBottom: 22 }}>
        <div>
          <div style={{ position: 'relative', display: 'inline-block' }}>
            <h2 style={{ ...cabec, position: 'relative', zIndex: 1 }}>Consultoria</h2>
            <MarcaDaguaTitulo>
              <rect x="3" y="7" width="18" height="13" rx="2"/>
              <path d="M8 7V5a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
              <line x1="3" y1="13" x2="21" y2="13"/>
              <line x1="11" y1="11" x2="13" y2="11"/>
            </MarcaDaguaTitulo>
          </div>
          <p style={{ ...TIPO.subtitulo, margin: '4px 0 0' }}>
            Funil de captação · proposta · onboarding · operado por <MarcaAIdvisor variant="default" />.
          </p>
        </div>
        <button onClick={captarNova} disabled={captando}
          style={{ background: G.laranja, color: '#FFF', border: 'none',
            borderRadius: 0, padding: '10px 18px', fontSize: 12, fontWeight: 700,
            letterSpacing: '0.06em', textTransform: 'uppercase',
            cursor: captando ? 'wait' : 'pointer',
            fontFamily: "'Outfit', sans-serif",
            opacity: captando ? 0.6 : 1 }}>
          {captando ? 'Captando…' : '+ Captar nova consultoria'}
        </button>
      </div>

      <QuadrosIntro
        titulo="a Consultoria"
        oQueE={<>O <strong>funil de pré-venda</strong> do consultor: da captação
          ao onboarding. Cada consultoria percorre Pré-Cadastro · Escopo ·
          Proposta · Aceite · Cadastro · NDA, e só então vira empresa
          operacional.</>}
        oQueFaz={<>Capte uma nova consultoria para gerar o <strong>link público
          </strong> que o cliente preenche sem login. Acompanhe o funil pelas
          abas e promova a cliente quando os três selos estiverem verdes.</>}
      />

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 18,
        borderBottom: `1px solid ${G.borda}` }}>
        {[
          { id: 'hub',          label: 'Visão Geral' },
          { id: 'prospeccao',   label: `Em Prospecção · ${prospeccao.length}` },
          { id: 'consultoria',  label: `Em Consultoria · ${emConsultoria.length}` },
        ].map(t => (
          <button key={t.id} onClick={() => setTab(t.id)}
            style={{ background: 'transparent',
              border: 'none', borderBottom: tab === t.id ? `2px solid ${G.laranja}` : '2px solid transparent',
              color: tab === t.id ? G.txt1 : G.txt2,
              padding: '10px 14px', fontSize: 12, fontWeight: 700,
              letterSpacing: '0.06em', textTransform: 'uppercase',
              cursor: 'pointer', fontFamily: "'Outfit', sans-serif",
              marginBottom: -1 }}>
            {t.label}
          </button>
        ))}
      </div>

      {carregando && (
        <div style={{ color: G.txt2, fontStyle: 'italic', padding: 20 }}>
          Carregando consultorias…
        </div>
      )}

      {!carregando && tab === 'hub' && (
        <>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          <div style={cardHub} onClick={() => setTab('prospeccao')}>
            <div style={{ fontSize: 11, fontWeight: 800, color: G.laranja,
              letterSpacing: '0.10em', textTransform: 'uppercase',
              marginBottom: 8 }}>Em Prospecção</div>
            <div style={{ fontSize: 48, fontWeight: 800, color: G.txt1,
              letterSpacing: '-0.025em', lineHeight: 1 }}>
              {prospeccao.length}
            </div>
            <div style={{ marginTop: 10, fontSize: 13, color: G.txt2 }}>
              Captação · escopo em definição · proposta enviada · recusas.
            </div>
          </div>
          <div style={cardHub} onClick={() => setTab('consultoria')}>
            <div style={{ fontSize: 11, fontWeight: 800, color: G.verde,
              letterSpacing: '0.10em', textTransform: 'uppercase',
              marginBottom: 8 }}>Em Consultoria</div>
            <div style={{ fontSize: 48, fontWeight: 800, color: G.txt1,
              letterSpacing: '-0.025em', lineHeight: 1 }}>
              {emConsultoria.length}
            </div>
            <div style={{ marginTop: 10, fontSize: 13, color: G.txt2 }}>
              Proposta aceita · cadastro completo · NDA · convertidas em Empresa.
            </div>
          </div>
        </div>
        {(() => {
          const ACAO = {
            pre_cadastro_recebido: 'Montar a proposta',
            escopo_em_definicao: 'Montar a proposta',
            aceita: 'Enviar o NDA',
            cadastro_completo_recebido: 'Enviar o NDA',
            nda_assinado: 'Promover para Empresa',
          };
          const PROSP = ['pre_cadastro_recebido', 'escopo_em_definicao'];
          const pend = [...prospeccao, ...emConsultoria].filter(x => ACAO[x.status]);
          if (!pend.length) return null;
          return (
            <div style={{ marginTop: 16, background: '#FFF', border: `1px solid ${G.borda}`,
              borderRadius: 0, padding: '16px 18px' }}>
              <div style={{ fontSize: 11, fontWeight: 800, color: G.laranja,
                letterSpacing: '0.10em', textTransform: 'uppercase', marginBottom: 4 }}>
                Precisam de ação agora · {pend.length}
              </div>
              {pend.map(x => (
                <div key={x.id} onClick={() => setTab(PROSP.includes(x.status) ? 'prospeccao' : 'consultoria')}
                  style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    padding: '11px 0 10px', borderTop: `1px solid ${G.borda}`, cursor: 'pointer' }}>
                  <span style={{ fontSize: 13.5, fontWeight: 700, color: G.txt1 }}>
                    {x.nome_empresa || 'Empresa sem nome ainda'}
                  </span>
                  <span style={{ fontSize: 12.5, fontWeight: 700, color: G.verde }}>
                    {ACAO[x.status]} →
                  </span>
                </div>
              ))}
            </div>
          );
        })()}
        </>
      )}

      {!carregando && tab !== 'hub' && (
        <ListaConsultorias
          itens={tab === 'prospeccao' ? prospeccao : emConsultoria}
          vazio={tab === 'prospeccao'
            ? 'Nenhuma consultoria em prospecção. Use o botão "+ Captar nova consultoria" para começar.'
            : 'Nenhuma consultoria em andamento ainda.'}
          abertaId={aberta?.id || null}
          onToggle={c => { setAberta(a => (a?.id === c.id ? null : c)); marcarVisto(c); }}
          onRecarregar={async () => { await carregar(); setAberta(null); }}
        />
      )}

      {/* Modal · link gerado após Captar */}
      {linkGerado && (
        <div style={{ position: 'fixed', inset: 0,
          background: 'rgba(10,10,8,0.7)', zIndex: 9999,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          padding: 24 }} onClick={() => setLinkGerado(null)}>
          <div style={{ background: '#FFF', borderRadius: 0,
            padding: '24px 28px', maxWidth: 560, width: '100%',
            border: `1px solid ${G.borda}` }}
            onClick={e => e.stopPropagation()}>
            <div style={{ fontSize: 11, fontWeight: 800, color: G.verde,
              letterSpacing: '0.10em', textTransform: 'uppercase',
              marginBottom: 6 }}>Consultoria captada · link gerado</div>
            <h3 style={{ margin: '0 0 10px 0', fontSize: 18,
              color: G.txt1, letterSpacing: '-0.01em' }}>
              Envie ao prospect
            </h3>
            <p style={{ fontSize: 13, color: G.txt2, lineHeight: 1.55,
              margin: '0 0 14px 0' }}>
              Link público de Pré-Cadastro. O prospect preenche 7 campos
              simples · dados caem no funil para você montar o Escopo.
            </p>
            <div style={{ background: G.fundo, border: `1px solid ${G.borda}`,
              borderRadius: 0, padding: '10px 12px', fontFamily: 'monospace',
              fontSize: 12, color: G.txt1, wordBreak: 'break-all',
              marginBottom: 12 }}>{linkGerado}</div>
            <div style={{ display: 'flex', gap: 10,
              justifyContent: 'flex-end' }}>
              <button onClick={() => setLinkGerado(null)}
                style={{ background: '#FFF', color: G.txt2,
                  border: `1px solid ${G.borda}`,
                  borderRadius: 0, padding: '8px 16px', fontSize: 12,
                  fontWeight: 700, cursor: 'pointer',
                  letterSpacing: '0.06em', textTransform: 'uppercase',
                  fontFamily: "'Outfit', sans-serif" }}>
                Fechar
              </button>
              <button onClick={copiar}
                style={{ background: linkCopiado ? G.verde : G.laranja,
                  color: '#FFF', border: 'none',
                  borderRadius: 0, padding: '8px 16px', fontSize: 12,
                  fontWeight: 700, cursor: 'pointer',
                  letterSpacing: '0.06em', textTransform: 'uppercase',
                  fontFamily: "'Outfit', sans-serif" }}>
                {linkCopiado ? '✓ Copiado' : 'Copiar link'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const ListaConsultorias = ({ itens, vazio, abertaId, onToggle, onRecarregar }) => {
  if (!itens.length) return (
    <div style={{ background: G.fundo, border: `1px dashed ${G.borda}`,
      borderRadius: 0, padding: 24, textAlign: 'center',
      fontSize: 13, color: G.txt2, fontStyle: 'italic' }}>{vazio}</div>
  );
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {itens.map(c => {
        const aberta = abertaId === c.id;
        return (
          <div key={c.id} style={{ display: 'flex', flexDirection: 'column' }}>
            {/* Faixa do item · clicável · vira toggle do detalhe */}
            <div onClick={() => onToggle && onToggle(c)}
              style={{ background: '#FFF',
                border: `1px solid ${aberta ? G.laranja : G.borda}`,
                borderRadius: aberta ? '4px 4px 0 0' : 4,
                borderBottom: aberta ? `1px dashed ${G.borda}` : `1px solid ${G.borda}`,
                padding: '14px 18px', display: 'grid',
                gridTemplateColumns: '1fr 200px 160px 150px',
                gap: 16, alignItems: 'center',
                cursor: onToggle ? 'pointer' : 'default',
                transition: 'border-color 0.15s' }}>
              <div>
                <div style={{ fontSize: 14, fontWeight: 700, color: G.txt1 }}>
                  {c.nome_empresa || 'Empresa sem nome ainda'}
                </div>
                <div style={{ marginTop: 2, fontSize: 12, color: G.txt2 }}>
                  {c.cnpj || 'CNPJ não informado'} · {c.ramo_atividade || 'ramo a definir'}
                </div>
              </div>
              <div style={{ fontSize: 11, fontWeight: 700, color: G.laranja,
                letterSpacing: '0.06em', textTransform: 'uppercase' }}>
                {LABEL_STATUS[c.status] || c.status}
              </div>
              <div style={{ fontSize: 11, color: G.txt2, textAlign: 'right' }}>
                {c.representante || '—'}
                <br/>{c.email || ''}
              </div>
              <div style={{ fontSize: 12, fontWeight: 700, color: G.laranja,
                textAlign: 'right', whiteSpace: 'nowrap' }}>
                {aberta ? '▴ Clique para fechar' : '▾ Clique para abrir'}
              </div>
            </div>
            {/* Detalhe inline · expande sob a faixa · sem flutuação */}
            {aberta && (
              <div style={{ background: '#FFF',
                border: `1px solid ${G.laranja}`, borderTop: 'none',
                borderRadius: 0,
                padding: '16px 18px' }}>
                <DetalheConsultoria consultoria={c}
                  onRecarregar={onRecarregar} />
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

// Texto-base das Condições Gerais da Proposta · cada frase é um item.
// Editável pelo consultor a cada proposta · template inicial sugerido.
const CONDICOES_PADRAO = [
  'Pagamento por boleto com aceite.',
  'O cliente declara, ao assinar esta proposta, concordar formalmente com a emissão dos boletos referentes às parcelas acordadas.',
  'Prazo de execução conforme indicado acima.',
  'Vigência: proposta válida por 30 dias a contar da emissão.',
];

const ITENS_ESCOPO = [
  { id: 'escopo_organizacao_historico',
    letra: 'A',
    titulo: 'Organização e Classificação de Histórico Financeiro' },
  { id: 'escopo_montagem_demonstrativos',
    letra: 'B',
    titulo: 'Montagem de DRE, Balanço Patrimonial e índices de desempenho' },
  { id: 'escopo_diagnostico_gestao',
    letra: 'C',
    titulo: 'Diagnóstico de Gestão' },
  { id: 'escopo_valuation_basico',
    letra: 'D',
    titulo: 'Valuation Básico' },
  { id: 'escopo_bp60m',
    letra: 'E',
    titulo: 'Business Plan 60 Meses incluindo Teaser, Pitch Deck, Memorando Informativo e Plano de Ação' },
  { id: 'escopo_startup_bp60m',
    letra: 'F',
    titulo: 'Startup Business Plan 60 meses incluindo Teaser, Pitch Deck, Memorando Informativo e Plano de Ação' },
];

// =====================================================================
// <BuilderProposta /> — monta a Proposta Comercial (página única · overlay).
// Porta de modalidade → seções numeradas → escopo híbrido → Condições
// Comerciais (sinal = 1ª parcela) → pré-visualização. Estado local;
// persistência/emissão no backend entram em etapa à parte.
// =====================================================================
const BuilderProposta = ({ consultoria, onFechar, onRecarregar }) => {
  const c = consultoria || {};
  const num = (s) => { const n = Number(String(s).replace(',', '.')); return isNaN(n) ? 0 : n; };
  const fmtBRL = (v) => (Number(v) || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
  const MESES = ['janeiro','fevereiro','março','abril','maio','junho','julho','agosto','setembro','outubro','novembro','dezembro'];
  const maskTel = (v) => {
    const d = String(v || '').replace(/\D/g, '').slice(0, 11);
    if (d.length === 0) return '';
    if (d.length <= 2) return `(${d}`;
    if (d.length <= 6) return `(${d.slice(0, 2)}) ${d.slice(2)}`;
    if (d.length <= 10) return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`;
    return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
  };
  const emailValido = (e) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(e || '').trim());

  // Rascunhos heurísticos (Fase 1) · o campo nasce pré-preenchido · consultor edita · IA real na Fase 3
  const _cli = c.nome_empresa || 'a empresa';
  const introHeur = `Esta proposta de consultoria tem por objetivo estruturar a gestão de ${_cli} e estabelecer as ações necessárias para sustentar o seu próximo ciclo de crescimento.`;
  const aEmpresaHeur = c.ramo_atividade
    ? `${_cli} atua no ramo de ${c.ramo_atividade} e busca profissionalizar a sua gestão para crescer com segurança.`
    : `${_cli} busca profissionalizar a sua gestão para crescer com segurança.`;
  const objsHeur = ['Organizar e dar visibilidade aos números da operação.', 'Definir as prioridades do próximo ciclo de crescimento.'];

  const [modalidade, setModalidade] = useState(c.tipo_consultoria || null);
  const [preview, setPreview] = useState(false);
  const [destNome, setDestNome] = useState(c.destinatario_nome || c.representante || '');
  const [destCargo, setDestCargo] = useState(c.destinatario_cargo || '');
  const [intro, setIntro] = useState(c.proposta_introducao || introHeur);
  const [aEmpresa, setAEmpresa] = useState(c.proposta_a_empresa || aEmpresaHeur);
  const [objetivos, setObjetivos] = useState((c.proposta_objetivos && c.proposta_objetivos.length) ? c.proposta_objetivos : objsHeur);
  const [escopoSel, setEscopoSel] = useState(() => new Set(c.proposta_escopo || []));
  const [valorTotal, setValorTotal] = useState(c.proposta_valor_total != null ? String(c.proposta_valor_total) : '');
  const [numParcelas, setNumParcelas] = useState(c.proposta_num_parcelas != null ? String(c.proposta_num_parcelas) : '');
  const [successFee, setSuccessFee] = useState(c.proposta_success_fee_pct != null ? String(c.proposta_success_fee_pct) : '');
  const [assinaNome, setAssinaNome] = useState(c.proposta_assina_nome || '');
  const [assinaTitulo, setAssinaTitulo] = useState(c.proposta_assina_titulo || 'Consultor Sênior');
  const [assinaTelefone, setAssinaTelefone] = useState(maskTel(c.proposta_assina_telefone || ''));
  const [assinaEmail, setAssinaEmail] = useState(c.proposta_assina_email || '');
  const [condicoesGerais, setCondicoesGerais] = useState(c.condicoes_gerais || CONDICOES_PADRAO.join('\n'));
  const [emitindo, setEmitindo] = useState(false);
  const [erroEmit, setErroEmit] = useState('');
  const [emitResultado, setEmitResultado] = useState(
    c.proposta_numero ? { proposta_numero: c.proposta_numero, proposta_token: c.proposta_token } : null);

  const mod = MODALIDADES_CONSULTORIA.find(m => m.id === modalidade) || null;
  const catalogo = mod ? mod.catalogo : [];
  const cliente = c.nome_empresa || 'Cliente';
  const hoje = new Date();
  const dataExtenso = `São Paulo, ${hoje.getDate()} de ${MESES[hoje.getMonth()]} de ${hoje.getFullYear()}.`;
  const numeroProp = mod ? `Prop 001/${cliente}/${mod.codigo}/${hoje.getFullYear()}` : '';
  const mostraSuccessFee = modalidade === 'ma'
    && catalogo.some(cat => cat.successFee && cat.itens.some(i => escopoSel.has(i.id)));
  const valorParcela = (num(valorTotal) > 0 && Number(numParcelas) >= 1)
    ? num(valorTotal) / Number(numParcelas) : 0;

  const catTodos = (cat) => cat.itens.every(i => escopoSel.has(i.id));
  const catAlgum = (cat) => cat.itens.some(i => escopoSel.has(i.id));
  const toggleItem = (id) => setEscopoSel(s => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleCat = (cat) => setEscopoSel(s => {
    const n = new Set(s); const todos = cat.itens.every(i => n.has(i.id));
    cat.itens.forEach(i => todos ? n.delete(i.id) : n.add(i.id));
    return n;
  });
  const objsValidos = objetivos.map(o => o.trim()).filter(Boolean);
  const catsComSel = catalogo
    .map(cat => ({ cat, itens: cat.itens.filter(i => escopoSel.has(i.id)) }))
    .filter(g => g.itens.length > 0);

  // ── Snapshot-texto canônico da Proposta (documento legal · com rótulos do escopo) ──
  // Espelha o renderPreview em texto. Vai para `proposta_documento_snapshot`, que o
  // backend congela e usa como corpo do PDF com valor legal.
  const montarTextoProposta = () => {
    const L = [];
    L.push(assinaNome || 'NXS Consultoria');
    L.push(`Consultoria de ${mod ? mod.codigo : ''}`);
    L.push([assinaTitulo, [assinaTelefone, assinaEmail].filter(Boolean).join(' · ')].filter(Boolean).join(' · '));
    L.push('', dataExtenso, '');
    L.push(`À ${cliente}`);
    L.push(`A/C. ${destNome || '—'}${destCargo ? ` — ${destCargo}` : ''}`);
    L.push(`Ref.: ${numeroProp}`, '');
    L.push(`Prezado(a) ${destNome || '…'},`, '');
    L.push(`Conforme nossos entendimentos, formalizo a seguir proposta de Consultoria ${mod ? mod.refTexto : ''} a ser desenvolvida para a ${cliente}.`);
    if (intro) { L.push('', intro); }
    L.push('', '1. A EMPRESA', aEmpresa || '—', '');
    L.push('2. OBJETIVOS');
    objsValidos.forEach((o, i) => L.push(`2.${i + 1} ${o}`));
    if (!objsValidos.length) L.push('—');
    L.push('', '3. ESCOPO');
    if (catsComSel.length) {
      catsComSel.forEach((g, ci) => {
        L.push(`3.${ci + 1} ${g.cat.nome}`);
        g.itens.forEach((it, ii) => L.push(`3.${ci + 1}.${ii + 1} ${it.titulo}${it.desc ? ` — ${it.desc}` : ''}`));
      });
    } else L.push('Nenhum item selecionado.');
    L.push('', '4. CONDIÇÕES COMERCIAIS');
    L.push(`4.1 Valor total: ${num(valorTotal) > 0 ? fmtBRL(num(valorTotal)) : '—'}.`);
    L.push(`4.2 Parcelamento: ${Number(numParcelas) >= 1 && valorParcela > 0 ? `${numParcelas} parcelas mensais de ${fmtBRL(valorParcela)} — a 1ª parcela constitui o sinal.` : '—'}`);
    L.push('4.3 Vencimentos: a 1ª parcela (sinal) no aceite desta proposta; as demais no mesmo dia dos meses subsequentes.');
    if (mostraSuccessFee) L.push(`4.4 Success-fee: ${num(successFee) > 0 ? `${successFee}% sobre o valor da transação.` : '—'}`);
    L.push('', '5. CONDIÇÕES GERAIS');
    condicoesGerais.split('\n').map(l => l.trim()).filter(Boolean).forEach((linha, i) => L.push(`5.${i + 1} ${linha}`));
    L.push('', '6. CONFIDENCIALIDADE E PROTEÇÃO DE DADOS');
    secaoConfidencialidadeLinhas(consultoria).forEach(l => L.push(l));
    L.push('', 'Agradecemos a oportunidade de apresentar esta proposta e permanecemos à disposição para os esclarecimentos que se fizerem necessários.');
    L.push('Atenciosamente,', '');
    L.push(assinaNome || '—', assinaTitulo || '', [assinaTelefone, assinaEmail].filter(Boolean).join(' · '));
    return L.join('\n');
  };

  // ── Salvar rascunho + Emitir (backend) ──────────────────────────
  const montarPayload = () => ({
    tipo_consultoria: modalidade,
    destinatario_nome: destNome, destinatario_cargo: destCargo,
    proposta_documento_snapshot: montarTextoProposta(),
    proposta_introducao: intro, proposta_a_empresa: aEmpresa,
    proposta_objetivos: objsValidos,
    proposta_escopo: [...escopoSel],
    proposta_valor_total: num(valorTotal),
    proposta_num_parcelas: Number(numParcelas) || 0,
    proposta_success_fee_pct: mostraSuccessFee ? num(successFee) : null,
    proposta_assina_nome: assinaNome, proposta_assina_titulo: assinaTitulo,
    proposta_assina_telefone: assinaTelefone, proposta_assina_email: assinaEmail,
    condicoes_gerais: condicoesGerais,
  });
  const salvarRascunho = async () => {
    const r = await fetch(`${API}/consultorias/${c.id}/proposta-completa`, {
      method: 'PATCH', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(montarPayload()),
    });
    if (!r.ok) { const j = await r.json().catch(() => ({})); throw new Error(j.detail || 'Falha ao salvar.'); }
  };
  const [salvando, setSalvando] = useState(false);
  const [salvouOk, setSalvouOk] = useState(false);
  const [linkCopiado, setLinkCopiado] = useState(false);
  const aoSalvar = async () => {
    setErroEmit(''); setSalvando(true); setSalvouOk(false);
    try { await salvarRascunho(); setSalvouOk(true); }
    catch (e) { setErroEmit(e.message || 'Erro ao salvar.'); }
    setSalvando(false);
  };
  const emitir = async () => {
    setErroEmit('');
    if (!modalidade) { setErroEmit('Escolha a modalidade.'); return; }
    if (num(valorTotal) <= 0 || Number(numParcelas) < 1) { setErroEmit('Preencha o valor total e o número de parcelas.'); return; }
    if (escopoSel.size === 0) { setErroEmit('Selecione ao menos um item de escopo.'); return; }
    if (assinaEmail && !emailValido(assinaEmail)) { setErroEmit('E-mail do signatário inválido.'); return; }
    if (!window.confirm('Confirmo que houve acordo verbal com o cliente. Emitir a proposta e gerar o link de aceite?')) return;
    setEmitindo(true);
    try {
      await salvarRascunho();
      const r = await fetch(`${API}/consultorias/${c.id}/proposta-completa/emitir`, { method: 'POST' });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j.detail || 'Falha ao emitir.');
      setEmitResultado(j);
    } catch (e) { setErroEmit(e.message || 'Erro ao emitir.'); }
    setEmitindo(false);
  };

  const wrap = { position: 'fixed', inset: 0, zIndex: 200, background: G.fundo, overflowY: 'auto', fontFamily: "'Outfit', sans-serif" };
  const topbar = { position: 'sticky', top: 0, zIndex: 2, background: '#FFF', borderBottom: `1px solid ${G.borda}`, padding: '12px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16 };
  const body = { maxWidth: 900, margin: '0 auto', padding: '22px 28px 70px' };
  const card = { background: '#FFF', border: `1px solid ${G.borda}`, borderRadius: 0, padding: '18px 20px', marginBottom: 16 };
  const secTit = { fontSize: 12.5, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.10em', color: G.verde, borderBottom: `1px solid ${G.borda}`, paddingBottom: 8, marginBottom: 14 };
  const lbl = { display: 'block', fontSize: 11, fontWeight: 700, color: G.txt2, textTransform: 'uppercase', letterSpacing: '0.06em', margin: '0 0 5px' };
  const inp = { width: '100%', boxSizing: 'border-box', border: `1px solid ${G.borda}`, borderRadius: 0, padding: '9px 11px', fontSize: 14, color: G.txt1, fontFamily: 'inherit', outline: 'none', background: '#FFF' };
  const ta = { ...inp, minHeight: 70, resize: 'vertical', lineHeight: 1.5 };
  const btnPrim = { background: G.verde, color: '#FFF', border: 'none', borderRadius: 0, padding: '9px 18px', fontSize: 12, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', cursor: 'pointer', fontFamily: 'inherit' };
  const btnGhost = { background: '#FFF', color: G.txt2, border: `1px solid ${G.borda}`, borderRadius: 0, padding: '9px 16px', fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' };

  // ---- PRÉ-VISUALIZAÇÃO (documento montado) ----
  const renderPreview = () => (
    <div style={{ maxWidth: 760, margin: '0 auto', background: '#FDFCF8', border: `1px solid ${G.borda}`,
      borderTop: `3px solid ${G.verde}`, borderRadius: 0, padding: '46px 52px 40px', color: G.txt1,
      lineHeight: 1.6 }} lang="pt-BR">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
        borderBottom: `1px solid ${G.borda}`, paddingBottom: 14 }}>
        <div>
          <div style={{ fontWeight: 800, fontSize: 16 }}>{assinaNome || 'NXS Consultoria'}</div>
          <div style={{ fontSize: 11.5, color: G.txt2 }}>Consultoria de {mod ? mod.codigo : ''}</div>
        </div>
        <div style={{ textAlign: 'right', fontSize: 11.5, color: G.txt2 }}>
          {assinaTitulo}<br/>{[assinaTelefone, assinaEmail].filter(Boolean).join(' · ')}
        </div>
      </div>
      <div style={{ textAlign: 'right', fontSize: 12.5, color: G.txt2, marginTop: 16 }}>{dataExtenso}</div>
      <div style={{ fontSize: 13.5, marginTop: 12 }}>
        <div>À <b>{cliente}</b></div>
        <div>A/C. {destNome || '—'}{destCargo ? ` — ${destCargo}` : ''}</div>
        <div style={{ marginTop: 4 }}>Ref.: <span style={{ fontFamily: 'monospace', color: '#B8531F', background: '#FFF1E7', border: '1px solid #F6C9AC', borderRadius: 0, padding: '2px 8px' }}>{numeroProp}</span></div>
      </div>
      <p style={{ marginTop: 20, fontSize: 15 }}>Prezado(a) {destNome || '…'},</p>
      <p style={{ marginTop: 12, fontSize: 15, textAlign: 'justify' }}>Conforme nossos entendimentos, formalizo a seguir proposta de Consultoria {mod ? mod.refTexto : ''} a ser desenvolvida para a {cliente}.</p>
      {intro && <p style={{ marginTop: 10, fontSize: 15, textAlign: 'justify' }}>{intro}</p>}

      <h3 style={{ ...secTit, marginTop: 26 }}>1. A Empresa</h3>
      <p style={{ fontSize: 14.5, textAlign: 'justify' }}>{aEmpresa || <span style={{ color: G.txt2 }}>—</span>}</p>

      <h3 style={{ ...secTit, marginTop: 22 }}>2. Objetivos</h3>
      {objsValidos.length ? objsValidos.map((o, i) => (
        <div key={i} style={{ display: 'flex', gap: 10, padding: '3px 0', fontSize: 14.5 }}>
          <span style={{ color: G.verde, fontWeight: 700 }}>2.{i + 1}</span><span style={{ textAlign: 'justify' }}>{o}</span>
        </div>
      )) : <span style={{ color: G.txt2, fontSize: 14 }}>—</span>}

      <h3 style={{ ...secTit, marginTop: 22 }}>3. Escopo</h3>
      {catsComSel.length ? catsComSel.map((g, ci) => (
        <div key={g.cat.id} style={{ marginTop: 12 }}>
          <div style={{ fontWeight: 700, fontSize: 14 }}><span style={{ color: G.verde }}>3.{ci + 1}</span> {g.cat.nome}</div>
          <div style={{ marginLeft: 24 }}>
            {g.itens.map((it, ii) => (
              <div key={it.id} style={{ display: 'flex', gap: 10, padding: '3px 0', fontSize: 14 }}>
                <span style={{ color: G.verde, fontWeight: 600 }}>3.{ci + 1}.{ii + 1}</span>
                <span style={{ textAlign: 'justify' }}><b>{it.titulo}</b>{it.desc ? <span style={{ color: G.txt2 }}> — {it.desc}</span> : null}</span>
              </div>
            ))}
          </div>
        </div>
      )) : <span style={{ color: G.txt2, fontSize: 14 }}>Nenhum item selecionado.</span>}

      <h3 style={{ ...secTit, marginTop: 22 }}>4. Condições Comerciais</h3>
      <div style={{ display: 'flex', gap: 10, padding: '3px 0', fontSize: 14.5 }}>
        <span style={{ color: G.verde, fontWeight: 700 }}>4.1</span>
        <span><b>Valor total:</b> {num(valorTotal) > 0 ? fmtBRL(num(valorTotal)) : '—'}.</span>
      </div>
      <div style={{ display: 'flex', gap: 10, padding: '3px 0', fontSize: 14.5 }}>
        <span style={{ color: G.verde, fontWeight: 700 }}>4.2</span>
        <span><b>Parcelamento:</b> {Number(numParcelas) >= 1 && valorParcela > 0
          ? `${numParcelas} parcelas mensais de ${fmtBRL(valorParcela)} — a 1ª parcela constitui o sinal.` : '—'}</span>
      </div>
      <div style={{ display: 'flex', gap: 10, padding: '3px 0', fontSize: 14.5 }}>
        <span style={{ color: G.verde, fontWeight: 700 }}>4.3</span>
        <span><b>Vencimentos:</b> a 1ª parcela (sinal) no aceite desta proposta; as demais no mesmo dia dos meses subsequentes.</span>
      </div>
      {mostraSuccessFee && (
        <div style={{ display: 'flex', gap: 10, padding: '3px 0', fontSize: 14.5 }}>
          <span style={{ color: G.verde, fontWeight: 700 }}>4.4</span>
          <span><b>Success-fee:</b> {num(successFee) > 0 ? `${successFee}% sobre o valor da transação.` : '—'}</span>
        </div>
      )}

      <h3 style={{ ...secTit, marginTop: 22 }}>5. Condições Gerais</h3>
      {condicoesGerais.split('\n').map(l => l.trim()).filter(Boolean).map((linha, i) => (
        <div key={i} style={{ display: 'flex', gap: 10, padding: '3px 0', fontSize: 14.5 }}>
          <span style={{ color: G.verde, fontWeight: 700 }}>5.{i + 1}</span><span style={{ textAlign: 'justify' }}>{linha}</span>
        </div>
      ))}

      <h3 style={{ ...secTit, marginTop: 22 }}>6. Confidencialidade e Proteção de Dados</h3>
      {secaoConfidencialidadeLinhas(consultoria).map((l, i) => l === ''
        ? <div key={i} style={{ height: 6 }} />
        : <p key={i} style={{ fontSize: 13.5, textAlign: 'justify', margin: '3px 0',
            color: (l === 'PROTEÇÃO DOS SEUS DADOS' || l.startsWith('As partes —')) ? G.txt1 : G.txt2,
            fontWeight: l === 'PROTEÇÃO DOS SEUS DADOS' ? 700 : 400 }}>{l}</p>)}

      <p style={{ marginTop: 24, fontSize: 14.5, textAlign: 'justify' }}>Agradecemos a oportunidade de apresentar esta proposta e permanecemos à disposição para os esclarecimentos que se fizerem necessários.</p>
      <p style={{ marginTop: 12, fontSize: 14.5 }}>Atenciosamente,</p>
      <div style={{ marginTop: 26 }}>
        <div style={{ fontWeight: 800 }}>{assinaNome || '—'}</div>
        <div style={{ fontSize: 12.5, color: G.txt2 }}>{assinaTitulo}</div>
        <div style={{ fontSize: 12.5, color: G.txt2 }}>{[assinaTelefone, assinaEmail].filter(Boolean).join(' · ')}</div>
      </div>
    </div>
  );

  return (
    <div style={wrap}>
      <div style={topbar}>
        <div>
          <div style={{ fontSize: 15, fontWeight: 800, color: G.txt1 }}>Elaborar Proposta</div>
          <div style={{ fontSize: 12, color: G.txt2 }}>{cliente}{mod ? ` · ${mod.label}` : ''}</div>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          {modalidade && (
            <button style={btnGhost} onClick={() => setPreview(p => !p)}>
              {preview ? '← Editar' : 'Pré-visualizar →'}
            </button>
          )}
          <button style={btnGhost} onClick={async () => { if ((salvouOk || emitResultado) && onRecarregar) { await onRecarregar(); } else { onFechar(); } }}>Fechar ×</button>
        </div>
      </div>

      <div style={body}>
        {preview ? renderPreview() : (
          <>
            {/* Porta · Modalidade */}
            <div style={card}>
              <div style={secTit}>Modalidade da proposta</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                {MODALIDADES_CONSULTORIA.map(m => {
                  const ativo = modalidade === m.id;
                  return (
                    <button key={m.id} onClick={() => setModalidade(m.id)}
                      style={{ textAlign: 'left', cursor: 'pointer', fontFamily: 'inherit',
                        background: ativo ? G.verdeLight : '#FFF',
                        border: `2px solid ${ativo ? G.verde : G.borda}`, borderRadius: 0, padding: '16px 18px' }}>
                      <div style={{ fontSize: 15, fontWeight: 800, color: G.txt1 }}>{m.label}</div>
                      <div style={{ fontSize: 12, color: G.txt2, marginTop: 4 }}>Código: {m.codigo}</div>
                    </button>
                  );
                })}
              </div>
              {!modalidade && <div style={{ fontSize: 12.5, color: G.txt2, marginTop: 12 }}>Escolha a modalidade para liberar o restante da proposta. Nunca as duas juntas.</div>}
            </div>

            {modalidade && (<>
              {/* Cabeçalho */}
              <div style={card}>
                <div style={secTit}>Cabeçalho</div>
                <div style={{ fontSize: 12.5, color: G.txt2, marginBottom: 12 }}>
                  {dataExtenso} · À <b>{cliente}</b> · Ref.: <b>{numeroProp}</b>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  <div><label style={lbl}>Destinatário (A/C.)</label><input style={inp} value={destNome} onChange={e => setDestNome(e.target.value)} placeholder="Nome" /></div>
                  <div><label style={lbl}>Cargo</label><input style={inp} value={destCargo} onChange={e => setDestCargo(e.target.value)} placeholder="Ex.: Diretor" /></div>
                </div>
              </div>

              {/* Introdução */}
              <div style={card}>
                <div style={secTit}>Introdução</div>
                <textarea style={ta} value={intro} onChange={e => setIntro(e.target.value)} placeholder="Parágrafo do objetivo da proposta · edite à vontade." />
              </div>

              {/* 1. A Empresa */}
              <div style={card}>
                <div style={secTit}>1. A Empresa</div>
                <textarea style={ta} value={aEmpresa} onChange={e => setAEmpresa(e.target.value)} placeholder="Nome · ramo de atividade · tempo de atuação." />
              </div>

              {/* 2. Objetivos */}
              <div style={card}>
                <div style={secTit}>2. Objetivos</div>
                {objetivos.map((o, i) => (
                  <div key={i} style={{ display: 'flex', gap: 8, marginBottom: 8, alignItems: 'center' }}>
                    <span style={{ fontSize: 13, fontWeight: 700, color: G.verde, minWidth: 30 }}>2.{i + 1}</span>
                    <input style={inp} value={o} onChange={e => setObjetivos(arr => arr.map((x, j) => j === i ? e.target.value : x))} placeholder="Problema a resolver" />
                    {objetivos.length > 1 && <BotaoRemover onClick={() => setObjetivos(arr => arr.filter((_, j) => j !== i))} titulo="Remover objetivo" />}
                  </div>
                ))}
                <button style={btnGhost} onClick={() => setObjetivos(arr => [...arr, ''])}>+ Objetivo</button>
              </div>

              {/* 3. Escopo */}
              <div style={card}>
                <div style={secTit}>3. Escopo <span style={{ color: G.txt2, fontWeight: 600, textTransform: 'none', letterSpacing: 0 }}>· {escopoSel.size} itens</span></div>
                {catalogo.map((cat, ci) => (
                  <div key={cat.id} style={{ marginBottom: 14 }}>
                    <label style={{ display: 'flex', gap: 10, alignItems: 'center', cursor: 'pointer', fontWeight: 700, fontSize: 14, color: G.txt1 }}>
                      <input type="checkbox" checked={catTodos(cat)}
                        ref={el => { if (el) el.indeterminate = !catTodos(cat) && catAlgum(cat); }}
                        onChange={() => toggleCat(cat)} />
                      <span><span style={{ color: G.verde }}>{ci + 1}.</span> {cat.nome}</span>
                    </label>
                    <div style={{ marginLeft: 26, marginTop: 4 }}>
                      {cat.itens.map(it => (
                        <label key={it.id} style={{ display: 'flex', gap: 10, alignItems: 'flex-start', cursor: 'pointer', padding: '3px 0', fontSize: 13.5 }}>
                          <input type="checkbox" checked={escopoSel.has(it.id)} onChange={() => toggleItem(it.id)} style={{ marginTop: 3 }} />
                          <span><b>{it.titulo}</b>{it.desc ? <span style={{ color: G.txt2 }}> — {it.desc}</span> : null}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                ))}
              </div>

              {/* 4. Condições Comerciais */}
              <div style={card}>
                <div style={secTit}>4. Condições Comerciais</div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  <div><label style={lbl}>Valor total (R$)</label>
                    <CampoNumericoBP60M valor={valorTotal} onChange={setValorTotal} formato="brl" /></div>
                  <div><label style={lbl}>Nº de parcelas mensais</label>
                    <CampoNumericoBP60M valor={numParcelas} onChange={setNumParcelas} formato="int" permitirDecimal={false} /></div>
                </div>
                <div style={{ fontSize: 13, color: G.txt1, marginTop: 10 }}>
                  Valor da parcela: <b>{valorParcela > 0 ? fmtBRL(valorParcela) : '—'}</b> <span style={{ color: G.txt2 }}>(a 1ª parcela é o sinal, paga no aceite)</span>
                </div>
                {mostraSuccessFee && (
                  <div style={{ marginTop: 12, maxWidth: 260 }}>
                    <label style={lbl}>Success-fee (%) · Indo a Mercado</label>
                    <CampoNumericoBP60M valor={successFee} onChange={setSuccessFee} formato="decimal" sufixo="%" max={100} />
                  </div>
                )}
              </div>

              {/* 5. Condições Gerais */}
              <div style={card}>
                <div style={secTit}>5. Condições Gerais</div>
                <textarea style={{ ...ta, minHeight: 110 }} value={condicoesGerais}
                  onChange={e => setCondicoesGerais(e.target.value)}
                  placeholder="Uma condição por linha." />
                <div style={{ fontSize: 11.5, color: G.txt2, marginTop: 6 }}>Uma condição por linha · texto-padrão sugerido, edite à vontade.</div>
              </div>

              {/* Signatário */}
              <div style={card}>
                <div style={secTit}>Signatário</div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  <div><label style={lbl}>Nome</label><input style={inp} value={assinaNome} onChange={e => setAssinaNome(e.target.value)} /></div>
                  <div><label style={lbl}>Título</label><input style={inp} value={assinaTitulo} onChange={e => setAssinaTitulo(e.target.value)} /></div>
                  <div><label style={lbl}>Telefone</label><input type="tel" inputMode="numeric" style={inp} value={assinaTelefone} onChange={e => setAssinaTelefone(maskTel(e.target.value))} placeholder="(11) 99999-9999" /></div>
                  <div><label style={lbl}>E-mail</label><input type="email" inputMode="email" style={{ ...inp, borderColor: (assinaEmail && !emailValido(assinaEmail)) ? G.vermelho : G.borda }} value={assinaEmail} onChange={e => setAssinaEmail(e.target.value)} placeholder="nome@empresa.com" /></div>
                </div>
              </div>

              {erroEmit && (
                <div style={{ background: G.vermelhoL, color: G.vermelho, border: `1px solid ${G.vermelho}`,
                  borderRadius: 0, padding: '10px 14px', fontSize: 13, marginTop: 8 }}>{erroEmit}</div>
              )}
              {salvouOk && !emitResultado && (
                <div style={{ background: G.verdeLight, color: G.verde, borderRadius: 0,
                  padding: '10px 14px', fontSize: 13, marginTop: 8 }}>Rascunho salvo.</div>
              )}
              {emitResultado && (
                <div style={{ background: G.verdeLight, border: `1px solid ${G.verde}`, borderRadius: 0,
                  padding: '14px 16px', marginTop: 8 }}>
                  <div style={{ fontSize: 13, fontWeight: 800, color: G.verde }}>Proposta emitida · {emitResultado.proposta_numero}</div>
                  <div style={{ fontSize: 12.5, color: G.txt1, marginTop: 6 }}>
                    Link para o cliente concordar:
                    <div style={{ fontFamily: 'monospace', fontSize: 12, color: G.txt2, marginTop: 4, wordBreak: 'break-all' }}>
                      {window.location.origin}/rungoal-proposta/{emitResultado.proposta_token}
                    </div>
                  </div>
                  <button
                    onClick={async () => {
                      const link = `${window.location.origin}/rungoal-proposta/${emitResultado.proposta_token}`;
                      try { await navigator.clipboard.writeText(link); }
                      catch { const t = document.createElement('textarea'); t.value = link; document.body.appendChild(t); t.select(); document.execCommand('copy'); document.body.removeChild(t); }
                      setLinkCopiado(true); setTimeout(() => setLinkCopiado(false), 2500);
                    }}
                    style={{ marginTop: 12, background: linkCopiado ? G.verde : '#FFF', color: linkCopiado ? '#FFF' : G.verde,
                      border: `1px solid ${G.verde}`, borderRadius: 0, padding: '8px 16px', fontSize: 12, fontWeight: 700,
                      letterSpacing: '0.06em', textTransform: 'uppercase', cursor: 'pointer', fontFamily: 'inherit' }}>
                    {linkCopiado ? 'Link copiado ✓' : 'Copiar link'}
                  </button>
                </div>
              )}
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 12, alignItems: 'center' }}>
                <button style={btnGhost} onClick={() => setPreview(true)}>Pré-visualizar →</button>
                <button style={btnGhost} onClick={aoSalvar} disabled={salvando}>{salvando ? 'Salvando…' : 'Salvar rascunho'}</button>
                <button style={btnPrim} onClick={emitir} disabled={emitindo}>{emitindo ? 'Emitindo…' : 'Emitir para aceite'}</button>
              </div>
            </>)}
          </>
        )}
      </div>
    </div>
  );
};

const DetalheConsultoria = ({ consultoria, onFechar, onRecarregar }) => {
  const c = consultoria;
  const [builderAberto, setBuilderAberto] = useState(false);

  // Aceite da proposta libera o NDA (o Cadastro Completo foi eliminado 24/07).
  const aceiteFeito = ['aceita', 'nda_pendente', 'nda_assinado', 'convertida']
    .includes(c.status);

  // ---- Promoção para Empresa · disponível após o aceite (confidencialidade embutida) ----
  const [promovendo, setPromovendo] = useState(false);
  const [erroPromover, setErroPromover] = useState('');
  const [promResultado, setPromResultado] = useState(null);
  const jaConvertida = c.status === 'convertida' || !!c.empresa_id;

  const promover = async () => {
    if (!window.confirm('Promover esta consultoria a Empresa? Os dados do cadastro serão copiados para o banco operacional.')) return;
    setErroPromover(''); setPromovendo(true);
    try {
      const r = await fetch(`${API}/consultorias/${c.id}/promover`, { method: 'POST' });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j.detail || 'Falha ao promover.');
      setPromResultado(j);
    } catch (e) {
      setErroPromover(e.message || 'Erro ao promover.');
    }
    setPromovendo(false);
  };

  // ---- Baixar PDF (via com valor legal) da Proposta aceita / NDA assinado --
  const [baixandoPdf, setBaixandoPdf] = useState('');
  const baixarPdf = async (tipo) => {   // tipo = 'proposta' | 'nda'
    setBaixandoPdf(tipo);
    try {
      const r = await fetch(`${API}/consultorias/${c.id}/${tipo}/pdf`);
      if (!r.ok) { const j = await r.json().catch(() => ({})); throw new Error(j.detail || 'Falha ao gerar o PDF.'); }
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${tipo}_${(c.nome_empresa || 'documento').replace(/\s+/g, '_')}.pdf`;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) { alert(e.message || 'Erro ao baixar o PDF.'); }
    setBaixandoPdf('');
  };

  // Estilo padrão dos inputs da ficha de Proposta · local ao componente
  // para evitar qualquer ambiguidade de escopo do linter.
  const inputProposta = (editavel) => ({
    width: '100%', height: 36, boxSizing: 'border-box',
    border: `1px solid ${G.borda}`, borderRadius: 0,
    padding: '0 10px', fontSize: 13, color: G.txt1,
    fontFamily: "'Outfit', sans-serif",
    background: editavel ? '#FFF' : G.fundo,
    outline: 'none',
  });
  const [cancelando, setCancelando] = useState(false);
  const podeCancelar = c.status === 'pre_cadastro_pendente'
                    || c.status === 'pre_cadastro_recebido';

  // Escopo · state local inicializado pelo registro atual
  const [escopo, setEscopo] = useState({
    escopo_organizacao_historico:   !!c.escopo_organizacao_historico,
    escopo_montagem_demonstrativos: !!c.escopo_montagem_demonstrativos,
    escopo_diagnostico_gestao:      !!c.escopo_diagnostico_gestao,
    escopo_valuation_basico:        !!c.escopo_valuation_basico,
    escopo_bp60m:                   !!c.escopo_bp60m,
    escopo_startup_bp60m:           !!c.escopo_startup_bp60m,
    escopo_outros:                  c.escopo_outros || '',
  });
  const [salvandoEscopo, setSalvandoEscopo] = useState(false);
  const escopoEditavel = c.status === 'pre_cadastro_pendente'
                      || c.status === 'pre_cadastro_recebido'
                      || c.status === 'escopo_em_definicao';
  const escopoTemAlgum = ITENS_ESCOPO.some(i => escopo[i.id])
                     || (escopo.escopo_outros || '').trim().length > 0;

  const toggleEscopo = (id) => {
    if (!escopoEditavel || salvandoEscopo) return;
    setEscopo(e => ({ ...e, [id]: !e[id] }));
  };

  const salvarEscopo = async () => {
    setSalvandoEscopo(true);
    try {
      const r = await fetch(`${API}/consultorias/${c.id}/escopo`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(escopo),
      });
      if (!r.ok) {
        const j = await r.json().catch(() => ({}));
        throw new Error(j.detail || 'Falha ao salvar escopo.');
      }
      if (onRecarregar) await onRecarregar();
    } catch (e) {
      alert(e.message || 'Erro ao salvar escopo.');
      setSalvandoEscopo(false);
    }
  };

  // ---- Proposta · state e ações --------------------------------
  const [proposta, setProposta] = useState({
    prazo_execucao_dias:    c.prazo_execucao_dias   || '',
    preco_total:            c.preco_total           || '',
    numero_parcelas:        c.numero_parcelas       || '',
    data_primeira_parcela:  c.data_primeira_parcela || '',
    condicoes_gerais:       c.condicoes_gerais      || CONDICOES_PADRAO.join('\n'),
  });
  const [salvandoProposta, setSalvandoProposta] = useState(false);
  const [emitindoProposta, setEmitindoProposta] = useState(false);
  const propostaEditavel = c.status === 'pre_cadastro_recebido'
                        || c.status === 'escopo_em_definicao'
                        || c.status === 'proposta_emitida';
  const propostaEmitida = !!c.proposta_emitida_em;
  const podeEmitir = c.status === 'pre_cadastro_recebido'
                  || c.status === 'escopo_em_definicao';

  const numPreco   = Number(String(proposta.preco_total).replace(',', '.')) || 0;
  const numParc    = Number(proposta.numero_parcelas) || 0;
  const valorParcela = numParc > 0 ? numPreco / numParc : 0;
  const propostaCompleta = numPreco > 0 && numParc > 0
                        && Number(proposta.prazo_execucao_dias) > 0
                        && !!proposta.data_primeira_parcela;

  const _payloadProposta = () => ({
    prazo_execucao_dias:   Number(proposta.prazo_execucao_dias) || null,
    preco_total:           numPreco || null,
    numero_parcelas:       numParc || null,
    data_primeira_parcela: proposta.data_primeira_parcela || null,
    condicoes_gerais:      proposta.condicoes_gerais || null,
  });

  const salvarProposta = async () => {
    setSalvandoProposta(true);
    try {
      const r = await fetch(`${API}/consultorias/${c.id}/proposta`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(_payloadProposta()),
      });
      if (!r.ok) {
        const j = await r.json().catch(() => ({}));
        throw new Error(j.detail || 'Falha ao salvar Proposta.');
      }
      if (onRecarregar) await onRecarregar();
      else setSalvandoProposta(false);
    } catch (e) {
      alert(e.message || 'Erro ao salvar Proposta.');
      setSalvandoProposta(false);
    }
  };

  const emitirProposta = async () => {
    const ok = window.confirm(
      'Emitir Proposta?\n\n' +
      'Será gerado o link público de aceite/recusa para o cliente. ' +
      'Os dados ainda podem ser ajustados antes do aceite.'
    );
    if (!ok) return;
    setEmitindoProposta(true);
    try {
      // garante que o último rascunho está salvo antes de emitir
      await fetch(`${API}/consultorias/${c.id}/proposta`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(_payloadProposta()),
      });
      const r = await fetch(`${API}/consultorias/${c.id}/proposta/emitir`, {
        method: 'POST',
      });
      if (!r.ok) {
        const j = await r.json().catch(() => ({}));
        throw new Error(j.detail || 'Falha ao emitir.');
      }
      if (onRecarregar) await onRecarregar();
    } catch (e) {
      alert(e.message || 'Erro ao emitir Proposta.');
      setEmitindoProposta(false);
    }
  };

  // ---- Teclado numérico virtual · controla quais inputs numéricos da
  // Proposta estão ativos · sobe na coluna direita do modal ---------------
  // campoTeclado: { campo, label, permitirDecimal } | null
  const [campoTeclado, setCampoTeclado] = useState(null);
  const [valorTeclado, setValorTeclado] = useState('');

  const abrirTeclado = (campo, label, permitirDecimal) => {
    if (!propostaEditavel) return;
    const atual = proposta[campo];
    setValorTeclado(atual !== undefined && atual !== null ? String(atual) : '');
    setCampoTeclado({ campo, label, permitirDecimal });
  };
  const tecladoDigito = (d) => {
    setValorTeclado(v => {
      if (d === '.' && v.includes('.')) return v;
      return (v || '') + d;
    });
  };
  const tecladoApagar = () => setValorTeclado(v => v.slice(0, -1));
  const tecladoLimpar = () => setValorTeclado('');
  const tecladoConfirma = () => {
    if (campoTeclado) {
      setProposta(s => ({ ...s, [campoTeclado.campo]: valorTeclado }));
    }
    setCampoTeclado(null);
    setValorTeclado('');
  };

  // Display clicável para campos numéricos · imita ValorDisplay mas integrado
  // ao state do teclado virtual local · destaca campo ativo em verde.
  const CampoNumerico = ({ label, campo, formato, permitirDecimal = true, prefixo, sufixo }) => {
    const ativo = campoTeclado?.campo === campo;
    const mostrarValor = ativo ? valorTeclado : proposta[campo];
    let txt = '—';
    if (mostrarValor !== '' && mostrarValor !== null && mostrarValor !== undefined) {
      const num = Number(String(mostrarValor).replace(',', '.'));
      if (!isNaN(num)) {
        if (formato === 'brl') {
          txt = num.toLocaleString('pt-BR',
            { minimumFractionDigits: 2, maximumFractionDigits: 2 });
        } else if (formato === 'int') {
          txt = num.toLocaleString('pt-BR', { maximumFractionDigits: 0 });
        } else {
          txt = String(mostrarValor);
        }
      } else {
        txt = String(mostrarValor);
      }
    }
    return (
      <div>
        <div style={{ fontSize: 10.5, fontWeight: 800, color: G.txt2,
          letterSpacing: '0.08em', textTransform: 'uppercase',
          marginBottom: 4 }}>{label}</div>
        <div onClick={() => abrirTeclado(campo, label, permitirDecimal)}
          style={{ display: 'flex', alignItems: 'stretch',
            border: `1px solid ${ativo ? G.verde : G.borda}`,
            borderRadius: 0, overflow: 'hidden',
            cursor: propostaEditavel ? 'pointer' : 'default',
            background: ativo ? G.verdeLight : (propostaEditavel ? '#FFF' : G.fundo),
            height: 36, transition: 'border-color 0.12s' }}>
          {prefixo && (
            <span style={{ padding: '0 10px', display: 'flex',
              alignItems: 'center', fontSize: 11, color: G.txt2,
              background: G.fundo, borderRight: `1px solid ${G.borda}` }}>
              {prefixo}
            </span>
          )}
          <span style={{ flex: 1, padding: '0 12px', display: 'flex',
            alignItems: 'center', justifyContent: 'flex-end',
            fontSize: 14, fontWeight: 700, color: G.txt1,
            fontFamily: "'IBM Plex Mono', monospace" }}>
            {txt}
          </span>
          {sufixo && (
            <span style={{ padding: '0 10px', display: 'flex',
              alignItems: 'center', fontSize: 11, color: G.txt2,
              background: G.fundo, borderLeft: `1px solid ${G.borda}` }}>
              {sufixo}
            </span>
          )}
        </div>
      </div>
    );
  };

  const cancelarCaptacao = async () => {
    const ok = window.confirm(
      'Cancelar esta captação?\n\n' +
      'A consultoria sai das listas ativas (fica arquivada). ' +
      'Use quando o cliente não devolver o Pré-Cadastro ou a captação não evoluir.'
    );
    if (!ok) return;
    setCancelando(true);
    try {
      const r = await fetch(`${API}/consultorias/${c.id}/cancelar`, { method: 'POST' });
      if (!r.ok) {
        const j = await r.json().catch(() => ({}));
        throw new Error(j.detail || 'Falha ao cancelar.');
      }
      if (onRecarregar) await onRecarregar();
      else onFechar();
    } catch (e) {
      alert(e.message || 'Erro ao cancelar captação.');
      setCancelando(false);
    }
  };

  // -------- Estilos compartilhados dos 3 cards · padrão BP60M ------------
  // Cards brancos · cantos vivos (radius 4) · sem boxShadow · borda fina.
  const cardBase = {
    background: '#FFF', border: `1px solid ${G.borda}`,
    borderRadius: 0, padding: '14px 18px',
  };
  // HEADER_BLOCO canônico · laranja 16px 700 uppercase 0.10em (design-system-bp60m).
  const HEADER_BLOCO = {
    fontSize: 16, fontWeight: 700, letterSpacing: '0.10em',
    textTransform: 'uppercase', color: G.laranja, marginBottom: 10,
  };
  const HEADER_AUX = {
    fontSize: 12, fontWeight: 400, color: G.txt2,
    textTransform: 'none', letterSpacing: 0, marginLeft: 10,
  };
  const rotuloCampo = {
    fontSize: 10.5, fontWeight: 800, color: G.txt2,
    letterSpacing: '0.08em', textTransform: 'uppercase',
    marginBottom: 2,
  };

  return (
    <div>
      {builderAberto && <BuilderProposta consultoria={c} onFechar={() => setBuilderAberto(false)} onRecarregar={onRecarregar} />}
      {/* Conteúdo inline · 1 coluna · numpad é flutuante sob demanda */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>

            {/* CARD · Pré-Cadastro · etapa de entrada (não é selo) */}
            <div style={cardBase}>
              <div style={HEADER_BLOCO}>
                Pré-Cadastro
                <span style={HEADER_AUX}>· identificação inicial do prospect</span>
              </div>
              <div style={{ display: 'grid',
                gridTemplateColumns: '140px 1fr 140px 1fr',
                rowGap: 6, columnGap: 12, fontSize: 12.5 }}>
                <div style={{ color: G.txt2 }}>Nome</div>
                <div>{c.nome_empresa || '—'}</div>
                <div style={{ color: G.txt2 }}>CNPJ</div>
                <div>{c.cnpj || '—'}</div>
                <div style={{ color: G.txt2 }}>Ramo</div>
                <div>{c.ramo_atividade || '—'}</div>
                <div style={{ color: G.txt2 }}>Representante</div>
                <div>{c.representante || '—'}</div>
                <div style={{ color: G.txt2 }}>E-mail</div>
                <div>{c.email || '—'}</div>
                <div style={{ color: G.txt2 }}>Celular</div>
                <div>{c.celular || '—'}</div>
                <div style={{ color: G.txt2, gridColumn: '1' }}>Descritivo</div>
                <div style={{ gridColumn: '2 / 5', lineHeight: 1.45 }}>
                  {c.breve_descritivo || '—'}
                </div>
              </div>
              <div style={{ fontSize: 11, color: G.txt2, fontStyle: 'italic',
                marginTop: 8, paddingTop: 8, borderTop: `1px dashed ${G.borda}` }}>
                {c.pre_cadastro_recebido_em
                  ? `Pré-Cadastro recebido em ${new Date(c.pre_cadastro_recebido_em).toLocaleString('pt-BR')}`
                  : 'Aguardando preenchimento pelo cliente.'}
              </div>
            </div>

            {/* CARD · 1º selo · Proposta · comercial e aceite */}
            <div style={cardBase}>
              <div style={HEADER_BLOCO}>
                1º selo · Proposta
                <span style={HEADER_AUX}>· proposta comercial e aceite</span>
              </div>
              {!c.proposta_aceita_em && (
                <>
                  <div style={{ fontSize: 12.5, color: G.txt2, lineHeight: 1.5, marginBottom: 12 }}>
                    {c.status === 'proposta_emitida'
                      ? 'Proposta emitida · aguardando o aceite do cliente.'
                      : c.status === 'recusada'
                        ? 'Proposta recusada pelo cliente. Ajuste e reenvie se desejar.'
                        : 'Monte a proposta comercial e emita para o cliente aceitar.'}
                  </div>
                  <button onClick={() => setBuilderAberto(true)}
                    style={{ background: G.verde, color: '#FFF', border: 'none', borderRadius: 0,
                      padding: '9px 18px', fontSize: 12, fontWeight: 700, letterSpacing: '0.06em',
                      textTransform: 'uppercase', cursor: 'pointer', fontFamily: "'Outfit', sans-serif" }}>
                    {c.status === 'proposta_emitida' ? 'Revisar Proposta →' : 'Elaborar Proposta →'}
                  </button>
                </>
              )}
              <div style={{ fontSize: 11,
                color: c.proposta_aceita_em ? G.verde : G.txt2,
                fontWeight: c.proposta_aceita_em ? 700 : 400,
                fontStyle: c.proposta_aceita_em ? 'normal' : 'italic',
                marginTop: 12, paddingTop: 8, borderTop: `1px dashed ${G.borda}` }}>
                {c.proposta_aceita_em
                  ? `✓ Proposta aceita em ${new Date(c.proposta_aceita_em).toLocaleString('pt-BR')}`
                  : c.status === 'recusada'
                    ? 'Proposta recusada pelo cliente.'
                    : 'Aguardando aceite do cliente.'}
              </div>
              {c.proposta_aceita_em && (
                <button onClick={() => baixarPdf('proposta')} disabled={baixandoPdf === 'proposta'}
                  style={{ marginTop: 10, background: '#FFF', color: G.verde, border: `1px solid ${G.verde}`,
                    borderRadius: 0, padding: '8px 16px', fontSize: 12, fontWeight: 700, letterSpacing: '0.06em',
                    textTransform: 'uppercase', cursor: 'pointer', fontFamily: "'Outfit', sans-serif" }}>
                  {baixandoPdf === 'proposta' ? 'Gerando PDF…' : 'Baixar PDF da Proposta (via legal)'}
                </button>
              )}
            </div>

            {/* Promoção para Empresa · disponível após o aceite (confidencialidade embutida) */}
            {aceiteFeito && (
              <div style={cardBase}>
                <div style={HEADER_BLOCO}>
                  Promoção para Empresa
                  <span style={HEADER_AUX}>· 2 selos verdes · entra no banco operacional</span>
                </div>

                {(jaConvertida || promResultado) ? (
                  <div style={{ background: G.verdeLight, border: `1px solid ${G.verde}`,
                    borderRadius: 0, padding: '14px 16px', fontSize: 13, color: G.verde, fontWeight: 700 }}>
                    ✓ Consultoria convertida em Empresa. Já disponível no seletor de empresas.
                  </div>
                ) : (
                  <>
                    <div style={{ fontSize: 12.5, color: G.txt2, lineHeight: 1.5, marginBottom: 12 }}>
                      Proposta aceita · NDA assinado. A consultoria está pronta para virar Empresa operacional.
                    </div>
                    <button onClick={promover} disabled={promovendo}
                      style={{ background: G.verde, color: '#FFF', border: 'none', borderRadius: 0,
                        padding: '10px 20px', fontSize: 12.5, fontWeight: 800, letterSpacing: '0.06em',
                        textTransform: 'uppercase', cursor: promovendo ? 'wait' : 'pointer',
                        fontFamily: "'Outfit', sans-serif", opacity: promovendo ? 0.6 : 1 }}>
                      {promovendo ? 'Promovendo…' : 'Promover para Empresa →'}
                    </button>
                  </>
                )}

                {erroPromover && (
                  <div style={{ background: G.vermelhoL, color: G.vermelho,
                    border: `1px solid ${G.vermelho}`, borderRadius: 0, padding: '10px 14px',
                    fontSize: 13, marginTop: 10 }}>{erroPromover}</div>
                )}
              </div>
            )}

      </div>

      {/* Teclado numérico flutuante · só visível quando algum campo está ativo · OK fecha */}
      {campoTeclado && (
        <div onClick={tecladoConfirma}
          style={{ position: 'fixed', inset: 0,
            background: 'rgba(0,0,0,0.40)', zIndex: 9999,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: 20 }}>
          <div onClick={e => e.stopPropagation()}
            style={{ maxWidth: 320, width: '100%' }}>
            <TecladoNumerico
              valor={valorTeclado}
              label={campoTeclado.label}
              onDigito={tecladoDigito}
              onApagar={tecladoApagar}
              onLimpar={tecladoLimpar}
              onFechar={tecladoConfirma}
              permitirDecimal={campoTeclado.permitirDecimal}
            />
          </div>
        </div>
      )}

        {/* Rodapé · cancelar + assinatura */}
        <div style={{ display: 'flex', justifyContent: 'space-between',
          alignItems: 'center', marginTop: 12, paddingTop: 10,
          borderTop: `1px solid ${G.borda}` }}>
          {podeCancelar ? (
            <button onClick={cancelarCaptacao} disabled={cancelando}
              style={{ background: 'transparent',
                border: `1px solid ${G.borda}`,
                borderRadius: 0, padding: '6px 12px', fontSize: 11,
                fontWeight: 700, color: G.txt2,
                cursor: cancelando ? 'wait' : 'pointer',
                letterSpacing: '0.06em', textTransform: 'uppercase',
                fontFamily: "'Outfit', sans-serif",
                opacity: cancelando ? 0.6 : 1 }}>
              {cancelando ? 'Cancelando…' : 'Cancelar captação'}
            </button>
          ) : <span />}
          <div style={{ fontSize: 10.5, color: G.txt2, fontStyle: 'italic' }}>
            Powered by <MarcaAIdvisor variant="default" />
          </div>
        </div>
    </div>
  );
};

// Wrapper de roteamento · separa as telas públicas (por token) do app autenticado.
// Não pode ter hooks · o early return abaixo dispararia rules-of-hooks
// se estivesse dentro do componente que carrega useState/useEffect.
export default function App() {
  const matchPreCadastro = typeof window !== 'undefined'
    && window.location.pathname.match(/^\/rungoal-pre-cadastro\/([\w-]+)$/);
  if (matchPreCadastro) {
    return <TelaPreCadastroPublico token={matchPreCadastro[1]} />;
  }
  const matchProposta = typeof window !== 'undefined'
    && window.location.pathname.match(/^\/rungoal-proposta\/([\w-]+)$/);
  if (matchProposta) {
    return <TelaPropostaPublica token={matchProposta[1]} />;
  }
  return <AppPrivado />;
}

function AppPrivado() {
  // A empresa escolhida pelo consultor PERSISTE entre recarregamentos — antes
  // voltava sempre à primeira da lista (24/07). O empresário continua preso à
  // própria empresa (sincronizada do perfil logo abaixo).
  const [empresa, setEmpresa] = useState(() => {
    try {
      const salva = JSON.parse(localStorage.getItem('rungoal:empresaSelecionada') || 'null');
      if (salva && salva.id) return salva;
    } catch (e) { /* preferência inválida: cai no padrão */ }
    return EMPRESAS[0];
  });

  // Guarda a escolha a cada troca de empresa.
  useEffect(() => {
    if (empresa?.id) {
      try {
        // Guarda o `tier` junto (30/07): sem ele a empresa restaurada de um F5
        // perdia o pacote e caía no gate do Pro até a lista do banco chegar.
        localStorage.setItem('rungoal:empresaSelecionada', JSON.stringify({
          id: empresa.id, nome: empresa.nome, setor: empresa.setor,
          tier: empresa.tier,
        }));
      } catch (e) { /* quota */ }
    }
  }, [empresa?.id, empresa?.nome, empresa?.setor, empresa?.tier]);
  // periodos vem do range real dos dados (1o mes alimentado -> mes vigente)
  // PAINEL DE DEMONSTRAÇÃO (01/10/2026) · só para administrador. Os botões da
  // faixa do topo OCULTAM as abas que não pertencem à modalidade escolhida,
  // usando a MESMA lista que decide o menu de cada cliente — `ABAS_POR_MODALIDADE`.
  // Nada muda no banco, nas permissões ou no que cada tela faz: o Fabio demonstra
  // as quatro modalidades com um login só. `null` é o ADM, que vê tudo.
  const [demoModalidade, setDemoModalidade] = useState(null);
  const DEMOS = [
    { id: null, rotulo: 'ADM' },
    { id: 'controller', rotulo: 'Controller' },
    { id: 'consultor', rotulo: 'Consultor' },
    { id: 'consultor_ma', rotulo: 'Consultor M&A' },
    { id: 'contabil', rotulo: 'Contábil' },
  ];
  // PEDIDOS NOVOS DA LANDING (02/10/2026) · o número laranja na aba Consultoria.
  // Quem pede contato pelo formulário da landing page entra no funil sozinho, e
  // ninguém ficaria sabendo até alguém abrir a aba. Este é o aviso, e por
  // decisão do Fabio ele mora AQUI DENTRO: nada de e-mail, serviço de terceiro
  // nem chave na publicação para contar o que o próprio aplicativo sabe.
  const [pedidosNovos, setPedidosNovos] = useState(0);
  const contarPedidosNovos = React.useCallback(async () => {
    try {
      const r = await fetch(`${API}/consultorias/pedidos-novos`);
      if (r.ok) { const d = await r.json(); setPedidosNovos(d.novos || 0); }
    } catch (e) { /* silencioso: selo é aviso, não pode derrubar a tela */ }
  }, []);

  const [periodos, setPeriodos]         = useState([]);
  const [periodoInicio, setPeriodoInicio] = useState(null);
  const [periodoFim, setPeriodoFim]       = useState(null);
  // Segundos até a volta automática à janela padrão. `null` = já está nela e
  // nenhum relógio corre. A análise pontual é livre; o que não pode é a tela
  // ficar num recorte esquecido e o próximo número ser lido fora de contexto.
  const [voltaEm, setVoltaEm]           = useState(null);
  // Painel ativo do Dashboard hierarquico (Master/Dia+Mês/Equity/Crédito/Valor)
  // [REMOVIDO 01/06/2026] painelDashboard/painelData — seletor multi-painel
  // do Dashboard foi descontinuado. Dashboard unico, sem hierarquia.
  // Equity e Credito (conceitos do antigo seletor) registrados em
  // jornada-em-curso.md para futura aba lateral.
  const [, setPlanoVigente]                   = useState(null);   // value never read · só setado
  // Benchmarks setoriais (faixas de despesa por categoria, com fonte)
  const [benchmarks, setBenchmarks]           = useState(null);
  // Top 5 produtos e Top 5 despesas fixas do Tier 4 (14/09). `null` enquanto
  // não respondeu — o painel distingue isso de "não há nada a mostrar".
  const [rankings, setRankings]               = useState(null);
  // Balanço do card do Tier 1 (14/09). `null` = ainda não respondeu, distinto
  // de empresa sem balancete carregado — o card diz uma coisa ou outra.
  const [balanco, setBalanco]                 = useState(null);
  // Estado do Tier 2: 'geral' (Receita · Despesas · Tendência) ou 'top5'
  // (Top 5 Produtos · Top 5 Despesas Fixas). Abre em 'geral' — o agregado é a
  // leitura de rotina; o detalhe é a pergunta seguinte, não a primeira.
  const [vistaTier2, setVistaTier2]           = useState('geral');
  const [indicadores, setIndicadores]   = useState(null);
  const [resumoMensal, setResumoMensal] = useState([]);
  const [parametros, setParametros]     = useState(null);
  const [loading, setLoading]           = useState(false);
  const [erro, setErro]                 = useState(null);
  const [tela, setTela]                 = useState('dashboard');
  const [usuario, setUsuario]           = useState(null);
  const [perfil, setPerfil]             = useState(null);
  const [sidebarRetraido, setSidebarRetraido] = useState(false);
  // Modo Consultoria · empresas não-seed carregadas do banco (admin-listar) no boot.
  // Novos clientes entram pelo funil de Consultoria (Pré-Cadastro), não mais por aqui.
  const [empresasExtras, setEmpresasExtras] = useState([]);

  // ─────────────────────────────────────────────────────────────────────
  // Blindagem anti-contaminação · 2 de 3 camadas ficam aqui (30/07/2026)
  // ─────────────────────────────────────────────────────────────────────
  // 1ª camada (transporte): registra quem está em foco ANTES de qualquer
  // effect deste ciclo rodar. Toda resposta da API que pertencer a outra
  // empresa é descartada no interceptor do topo do arquivo.
  focarEmpresa(empresa?.id, [...EMPRESAS, ...empresasExtras].map(e => e.id));

  // 3ª camada (estado): o Dashboard mora aqui no AppPrivado, que nunca
  // desmonta — o `key={empresa.id}` das telas não o alcança. Zerar durante o
  // render (padrão oficial do React para estado derivado de prop) descarta os
  // números da empresa anterior ANTES de qualquer pixel ir para a tela. Um
  // useEffect não serviria: rodaria depois do commit, deixando um quadro com
  // o nome de uma empresa e os números de outra.
  const [idEmFoco, setIdEmFoco] = useState(empresa?.id);
  if (empresa?.id !== idEmFoco) {
    setIdEmFoco(empresa?.id);
    setIndicadores(null);
    setResumoMensal([]);
    setParametros(null);
    setBenchmarks(null);
    setPlanoVigente(null);
    setPeriodos([]);
    setPeriodoInicio(null);
    setPeriodoFim(null);
    setErro(null);
    setLoading(true);   // já está buscando os dados da nova empresa
  }

  // 2ª camada (selo): o backend carimba `empresa_id` na resposta dos
  // indicadores. Confere aqui; o Dashboard recusa exibir se divergir.
  // Resposta sem carimbo (endpoint ainda não atualizado) não trava a tela —
  // a 1ª camada, no transporte, já é a defesa efetiva.
  const seloConfere = !indicadores?.empresa_id
    || String(indicadores.empresa_id) === String(empresa?.id);

  // Escuta evento global de navegação · usado pelo link "Saiba mais" do RGAM
  // e pelo botão "Voltar pra X" do PaginaTermoRGAM
  useEffect(() => {
    const handler = (e) => { if (e.detail?.tela) setTela(e.detail.tela); };
    window.addEventListener('rg-navegar', handler);
    return () => window.removeEventListener('rg-navegar', handler);
  }, []);

  // Sidebar só retrai/expande no modo manual (decisão de Fabio, 27/07): o
  // auto-retrair de 20s e o expandir por hover foram removidos — quem comanda
  // são os botões de retrair/expandir.
  const [authCarregando, setAuthCarregando] = useState(true);
  // Recovery: detectado no hash da URL (#type=recovery) ou via evento PASSWORD_RECOVERY
  const [recuperandoSenha, setRecuperandoSenha] = useState(
    typeof window !== 'undefined' && window.location.hash.includes('type=recovery')
  );


  // Detecta sessão ao carregar e escuta mudanças de auth
  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      guardarToken(session?.access_token);   // alimenta o token em memória
      setUsuario(session?.user || null);
      setAuthCarregando(false);
    });
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      // Único ponto que atualiza o token usado pelo interceptor. Cobre login,
      // logout e TOKEN_REFRESHED — sem isto o token expirado ficaria em
      // memória e toda chamada passaria a devolver 401 (30/07).
      guardarToken(session?.access_token);
      // Quando o usuario clica no link de recuperacao do email, o Supabase JS
      // emite PASSWORD_RECOVERY e cria uma sessao especial. Forcamos a tela de
      // redefinir senha mesmo se ja houver usuario logado.
      if (event === 'PASSWORD_RECOVERY') {
        setRecuperandoSenha(true);
      } else if (event === 'USER_UPDATED') {
        // Senha redefinida com sucesso — sai do modo recuperacao
        setRecuperandoSenha(false);
      }
      setUsuario(session?.user || null);
    });
    return () => subscription.unsubscribe();
  }, []);

  // Busca o perfil do usuario logado (role, empresa_id, etc)
  useEffect(() => {
    if (!usuario) { setPerfil(null); window.__ehAdmin = false; return; }
    const buscarPerfil = async () => {
      try {
        const res = await fetch(`${API}/auth/me`);
        if (!res.ok) throw new Error('Falha ao buscar perfil');
        const data = await res.json();
        setPerfil(data);
        // Flag global usada por bloqueios de edicao em componentes filhos
        window.__ehAdmin = data.role === 'admin';
        
        // Sincroniza empresa do estado com a empresa do perfil
        // (empresario so pode ver sua propria empresa)
        if (data.role === 'empresa' && data.empresa_id) {
          const empRes = await fetch(`${API}/empresas/${data.empresa_id}`);
          if (empRes.ok) {
            const emp = await empRes.json();
            setEmpresa({ id: emp.id, nome: emp.nome, setor: emp.setor });
          }
        }

        // O CONTADOR entra na empresa que ele atende, não na primeira de uma
        // lista fixa. Sem isto a tela abria na ShopMax para qualquer usuário, e
        // na modalidade Contábil não há seletor para corrigir — o contador via
        // no topo o nome de uma empresa que não é dele (medido em 30/09/2026).
        // Com mais de uma empresa vinculada, mantém-se a escolhida: quem atende
        // várias troca pelo seletor, e a preferência dele não se atropela.
        if (data.role === 'contador') {
          const r = await fetch(`${API}/empresas/minhas`);
          if (r.ok) {
            const minhas = await r.json();
            setEmpresasExtras(minhas.map(e => ({ id: e.id, nome: e.nome,
              setor: e.setor || 'geral', tier: e.tier || 'pro' })));
            setEmpresa(atual => {
              const valida = minhas.some(e => e.id === atual?.id);
              if (valida) return atual;
              const e = minhas[0];
              return e ? { id: e.id, nome: e.nome, setor: e.setor || 'geral',
                           tier: e.tier || 'pro' } : atual;
            });
          }
        }
      } catch(e) {
        console.error('Erro ao buscar perfil:', e);
      }
    };
    buscarPerfil();
  }, [usuario]);

  // Ao trocar de empresa · zera todos os states de dados antes que qualquer
  // useEffect dependente rode com dados da empresa anterior. Evita aparecer
  // dados da ShopMax quando admin seleciona uma empresa nova/vazia.
  useEffect(() => {
    setIndicadores(null);
    setResumoMensal([]);
    setParametros(null);
    setErro(null);
  }, [empresa.id]);

  // Modo Consultoria · admin carrega lista completa de empresas do banco no boot.
  // Adiciona ao seletor as empresas que NÃO são seeds (clientes promovidos pelo
  // funil de Consultoria, ou criados em sessões anteriores). Persiste entre reloads.
  useEffect(() => {
    if (perfil?.role !== 'admin') return;
    (async () => {
      try {
        const r = await fetch(`${API}/empresas/admin-listar`);
        if (!r.ok) return;
        const todas = await r.json();
        const idsSeeds = new Set(EMPRESAS.map(e => e.id));
        const extras = (todas || [])
          .filter(e => !idsSeeds.has(e.id))
          .map(e => ({ id: e.id, nome: e.nome,
            setor: e.setor || 'geral',
            tier: e.tier || 'pro' }));
        setEmpresasExtras(extras);
        // Se a empresa restaurada do último acesso não existe mais, volta ao
        // padrão — evita a tela ficar presa numa empresa apagada.
        const idsValidos = new Set([...EMPRESAS, ...extras].map(e => e.id));
        setEmpresa(atual => (atual?.id && !idsValidos.has(atual.id)) ? EMPRESAS[0] : atual);
      } catch (e) { /* silencioso · seletor mantém só seeds */ }
    })();
  }, [perfil]);

  // Busca parâmetros da empresa
  useEffect(() => {
    if (!usuario) return;   // só busca com sessão pronta (evita 401 no 1º login)
    const buscar = async () => {
      try {
        const res = await fetch(`${API}/parametros/${empresa.id}`);
        if (!res.ok) {
          // Empresa sem parametros cadastrados (caso de empresa nova)
          setParametros(null);
          setLoading(false);   // conclusão: não há parâmetros · solta a espera
          return;
        }
        const data = await res.json();
        // Verifica se o retorno tem campos validos de parametros
        if (data && typeof data === 'object' && 'saldo_cc' in data) {
          // Carimba de QUAL empresa vieram: sem isso, ao trocar de empresa os
          // parâmetros da anterior ficavam na memória e os indicadores da nova
          // eram calculados com eles — número errado, sem aviso (24/07).
          setParametros({ ...data, __empresa_id: empresa.id });
        } else {
          setParametros(null);
          setLoading(false);
        }
      } catch(e) {
        console.error(e);
        setParametros(null);
        setLoading(false);
      }
    };
    buscar();
  }, [empresa, usuario]);

  // Carrega plano vigente (define se Painel "Criação de Valor" do Dashboard habilita)
  useEffect(() => {
    if (!empresa || !usuario) { setPlanoVigente(null); return; }
    fetch(`${API}/planos/${empresa.id}/vigente`)
      .then(r => r.ok ? r.json() : null)
      .then(d => setPlanoVigente(d))
      .catch(() => setPlanoVigente(null));
  }, [empresa, usuario]);

  // Carrega benchmarks setoriais (fallback 'geral' se setor desconhecido)
  useEffect(() => {
    if (!empresa || !usuario) { setBenchmarks(null); return; }
    const setor = empresa.setor || 'geral';
    fetch(`${API}/benchmarks/${encodeURIComponent(setor)}`)
      .then(r => r.ok ? r.json() : null)
      .then(d => setBenchmarks(d))
      .catch(() => setBenchmarks(null));
  }, [empresa, usuario]);

  // Rankings do Tier 4. Segue o período do seletor: mudar De/Até muda o
  // ranking, senão a tela mostraria despesa média de um período e receita de
  // outro sob o mesmo rótulo.
  useEffect(() => {
    if (!empresa || !usuario || !periodoInicio || !periodoFim) { setRankings(null); return; }
    let vivo = true;
    fetch(`${API}/indicadores/rankings/${empresa.id}`
      + `?data_inicio=${periodoInicio.inicio}&data_fim=${periodoFim.fim}`)
      .then(r => r.ok ? r.json() : null)
      // Resposta de uma empresa anterior não pode vencer a atual e pintar o
      // painel com produto de outro cliente ([[resposta-obsoleta-zera-a-tela]]).
      .then(d => { if (vivo) setRankings(d); })
      .catch(() => { if (vivo) setRankings(null); });
    return () => { vivo = false; };
  }, [empresa, usuario, periodoInicio, periodoFim]);

  useEffect(() => {
    if (!empresa || !usuario) { setBalanco(null); return; }
    let vivo = true;
    fetch(`${API}/empresas/${empresa.id}/balanco`
      + (periodoFim?.fim ? `?ate=${periodoFim.fim}` : ''))
      .then(r => r.ok ? r.json() : null)
      // Resposta de empresa anterior não pode vencer a atual e mostrar o
      // patrimônio de outro cliente ([[resposta-obsoleta-zera-a-tela]]).
      .then(d => { if (vivo) setBalanco(d); })
      .catch(() => { if (vivo) setBalanco(null); });
    return () => { vivo = false; };
  }, [empresa, usuario, periodoFim]);

  // [REMOVIDO 01/06/2026] effect que carregava /indicadores/painel/{id}/{painel}
  // para o seletor multi-painel do Dashboard (Equity/Credito/Dia+Mes).
  // Endpoint backend mantido — alimentará futura aba lateral de Equity/Credito.

  // Carrega o range real de datas da empresa e gera lista de periodos disponiveis
  useEffect(() => {
    if (!empresa || !usuario) return;
    fetch(`${API}/extrato/periodo-disponivel/${empresa.id}`)
      .then(r => r.json())
      .then(({ data_inicio, data_fim }) => {
        const p = gerarPeriodos(data_inicio, data_fim);
        setPeriodos(p);
        if (p.length > 0) {
          // Abre na JANELA PADRÃO — 12 meses completos —, não em todo o
          // histórico. Antes de 11/09 abria em p[0]..p[último], e a MORED
          // entrava com 24 meses somados numa coluna só.
          const j = janelaPadrao(p);
          setPeriodoInicio(j.inicio);
          setPeriodoFim(j.fim);
          setVoltaEm(null);
        } else {
          setPeriodoInicio(null);
          setPeriodoFim(null);
          setLoading(false);   // empresa sem lançamento · não há o que calcular
        }
      })
      .catch(() => { setPeriodos([]); setPeriodoInicio(null); setPeriodoFim(null);
                     setLoading(false); });
  }, [empresa, usuario]);

  // Volta automática à janela padrão, 60s após a última alteração do seletor
  // (regra de Fabio · 11/09/2026). Cada nova alteração REINICIA a contagem —
  // quem está mexendo não é interrompido no meio. O aviso ao lado do seletor
  // mostra o tempo correndo: a tela nunca muda sozinha sem ter avisado antes.
  const SEGUNDOS_VOLTA = 60;
  useEffect(() => {
    if (voltaEm === null) return;
    if (voltaEm <= 0) {
      const j = janelaPadrao(periodos);
      if (j) { setPeriodoInicio(j.inicio); setPeriodoFim(j.fim); }
      setVoltaEm(null);
      return;
    }
    const t = setTimeout(() => setVoltaEm(v => (v === null ? null : v - 1)), 1000);
    return () => clearTimeout(t);
  }, [voltaEm, periodos]);

  // Toda alteração manual do De/Até passa por aqui — é o que arma o relógio.
  const escolherPeriodo = (qual, label) => {
    const p = periodos.find(x => x.label === label);
    if (!p) return;
    if (qual === 'inicio') setPeriodoInicio(p); else setPeriodoFim(p);
    setVoltaEm(SEGUNDOS_VOLTA);
  };

  // Busca indicadores usando parâmetros salvos
  useEffect(() => {
    // As saídas antecipadas precisam soltar o `loading`: a troca de empresa
    // agora o liga (3ª camada da blindagem), e sem isso o Dashboard ficaria
    // preso em "Carregando..." nas telas que não calculam indicadores.
    if (tela === 'configuracoes' || tela === 'cadastro') { setLoading(false); return; }
    // Sem parâmetros ou sem período AINDA: a busca deles está em voo, então o
    // `loading` fica ligado de propósito — soltá-lo aqui faria a tela piscar
    // "empresa em preparação" para uma empresa que tem dados. Quem solta é o
    // effect de parâmetros, ao concluir que a empresa realmente não os tem.
    if (!parametros || !periodoInicio || !periodoFim) return;
    // Trava anti-contaminação: só calcula com os parâmetros DESTA empresa.
    if (parametros.__empresa_id && parametros.__empresa_id !== empresa.id) return;
    const carregar = async () => {
      setLoading(true); setErro(null);
      // Limpa estado da empresa anterior antes de carregar nova · evita que
      // o dashboard mostre dados da empresa antiga enquanto a nova carrega
      // (ou enquanto está vazia · caso típico de cliente recém-criado no
      // modo Consultoria que ainda não preencheu a ficha).
      setIndicadores(null);
      setResumoMensal([]);
      // Empresa sem parâmetros (nova · sem upload de extrato ainda) · não
      // chama indicadores. UI mostra tela "empresa em preparação".
      if (!parametros) {
        setLoading(false);
        return;
      }
      try {
        // ?? (nullish) ao inves de || (falsy) — Simples tem ir=0; com || cairia em 0.15
        const params = {
          data_inicio:        periodoInicio.inicio,
          data_fim:           periodoFim.fim,
          saldo_cc:           parametros.saldo_cc ?? 0,
          saldo_cdb:          parametros.saldo_cdb ?? 0,
          ir:                 parametros.ir ?? 0.15,
          depreciacao_mensal: parametros.depreciacao_mensal ?? 0,
          wacc_ke:            parametros.wacc_ke ?? 0.28,
          wacc_kd:            parametros.wacc_kd ?? 0.14,
          wacc_e:             parametros.wacc_e ?? 500000,
          wacc_d:             parametros.wacc_d ?? 200000,
        };
        const [r1, r2] = await Promise.all([
          fetch(`${API}/indicadores/calcular/${empresa.id}`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(params),
          }),
          fetch(`${API}/indicadores/resumo-mensal/${empresa.id}`),
        ]);
        // 404 = empresa sem dados (típico de cliente novo no modo Consultoria).
        // Não é erro de API · só não há indicadores para calcular ainda.
        if (r1.status === 404 || r2.status === 404) {
          setIndicadores(null);
          setResumoMensal([]);
          setLoading(false);
          return;
        }
        const ind = await r1.json();
        const res = await r2.json();
        if (!r1.ok) throw new Error(ind.detail || 'Erro');
        setIndicadores(ind);
        // Mantemos `mes` no formato "YYYY-MM" (para filtrar por período no Dashboard)
        // e adicionamos `mesLabel` curto ("mar/25") para os eixos dos gráficos.
        // Antes era só o número do mês — com mais de um ano, o eixo ia de 01 a 12
        // e recomeçava, sem dizer de que ano (24/07).
        const _MES_AB = ['jan','fev','mar','abr','mai','jun','jul','ago','set','out','nov','dez'];
        setResumoMensal(res.map(m => ({
          ...m,
          mesLabel: (_MES_AB[parseInt(String(m.mes).slice(5, 7), 10) - 1] || String(m.mes).slice(5, 7))
                    + '/' + String(m.mes).slice(2, 4),
          receita: m.receita_operacional - (m.deducoes_receita || 0) + m.receita_financeira,
          despesa: m.despesa_operacional + (m.despesa_comercial || 0) + m.despesa_administrativa +
                   m.despesa_financeira  + m.tributos,
        })));
      } catch(e) { setErro('Erro ao conectar com a API.'); }
      setLoading(false);
    };
    carregar();
  }, [empresa, periodoInicio, periodoFim, parametros, tela]);

  // Relê o número a cada troca de aba. Sem relógio e sem espera: a consulta é
  // uma contagem, e quem acabou de abrir um pedido vê o número cair na hora.
  useEffect(() => {
    if (perfil?.role === 'admin') contarPedidosNovos();
  }, [perfil?.role, tela, contarPedidosNovos]);

  const NAV = [
    // Ordem da jornada: Dashboard (panorama) → Dados de Entrada (alimenta) →
    // Visualização (lê) → Planejamento (cria ciclo input/output).
    // Decisão 06/06: 4º bloco PLANEJAMENTO próprio para BP 12M e BP 60M (natureza
    // híbrida — input ciclo edição+validação, output após IA processar).
    { id: 'dashboard',     label: 'Dashboard',        grupo: 'DASHBOARD' },
    { id: 'cadastro',      label: 'Cadastro',                     grupo: 'DADOS DE ENTRADA' },
    { id: 'extrato',       label: 'Dados de Entrada',             grupo: 'DADOS DE ENTRADA' },
    { id: 'configuracoes', label: 'Configurações Complementares', grupo: 'DADOS DE ENTRADA' },
    // Separadas em 28/07 (a despesa indireta vem medida da contabilidade; o custo
    // direto é construído pelo consultor), REUNIDAS em 11/09 sob "Despesas e
    // Custos" e movidas de DADOS DE ENTRADA para VISUALIZAÇÃO. O conteúdo das
    // duas telas não mudou — virou sub-página da landing.
    { id: 'despesas_custos', label: 'Despesas e Custos', grupo: 'VISUALIZAÇÃO' },
    { id: 'resumo',        label: 'Resumo Mensal',    grupo: 'VISUALIZAÇÃO' },
    { id: 'alertas',       label: 'Alertas',          grupo: 'VISUALIZAÇÃO' },
    { id: 'dre',           label: 'DRE',              grupo: 'VISUALIZAÇÃO' },
    { id: 'balanco',       label: 'Balanço Patrimonial', grupo: 'VISUALIZAÇÃO' },
    { id: 'valor',         label: 'Valor',            grupo: 'VISUALIZAÇÃO' },
      // Cenários de Transição da reforma (31/08): didático + impacto medido.
      // FECHA o grupo, depois de Valor (Fabio, 26/09/2026). Antes abria, na frente
      // do Resumo Mensal — a leitura tributária vem depois de a econômica fechar.
    { id: 'reforma',       label: 'Reforma Tributária', grupo: 'VISUALIZAÇÃO' },
    { id: 'plano',         label: 'Planejamento 12M', grupo: 'PLANEJAMENTO' },
    { id: 'comparativo',   label: 'Previsto x Real',  grupo: 'PLANEJAMENTO' },
    // Bloco M&A criado em 27/07: os dois Business Plan de 60 meses são material
    // de fusão e aquisição / captação, natureza distinta do planejamento 12M.
    { id: 'businessplan',  label: 'Business Plan 60M',                        grupo: 'M&A' },
    { id: 'businessplansu',label: 'Business Plan 60M · Startup',              grupo: 'M&A' },
    { id: 'rgam',          label: 'Manual do Empresário', grupo: 'M&A' },
    // Rotina Contábil (11/09): o serviço do escritório contábil, no setor
    // CONTABILIDADE. Quem vê o quê é decidido por SETOR, em SETORES_POR_PAPEL —
    // o item não carrega mais portão próprio.
    // A Rotina Contábil deixou de ser UMA aba com tudo dentro e virou o GRUPO
    // (02/10/2026, decisão do Fabio). Cada entregável é uma aba, como em todo
    // o resto do menu. A tela é a mesma: ela recebe qual entregável mostrar.
    { id: 'razao_simulado',     label: 'Razão Simulado',       grupo: 'ROTINA CONTÁBIL' },
    { id: 'balancete_simulado', label: 'Balancete Simulado',   grupo: 'ROTINA CONTÁBIL' },
    { id: 'composicao_custo',   label: 'Composição de Custos', grupo: 'ROTINA CONTÁBIL' },
    // Funil pré-venda do consultor (memória `modulo-consultoria`). O setor
    // CONSULTORIA só consta na lista do admin, e é isso que o esconde.
    { id: 'consultoria', label: 'Consultoria', grupo: 'CONSULTORIA' },
  ];

    // A tela inicial é 'dashboard', mas nem todo mundo o enxerga — a modalidade
    // Contábil, por exemplo, abre em Cadastro. Sem este ajuste a pessoa cairia
    // numa tela que o menu dela não tem como reabrir nem abandonar.
    //
    // QUEM MANDA NO MENU É A MODALIDADE (27/09/2026), não mais o papel.
    //
    // O recorte por SETOR (`SETORES_POR_PAPEL`) é de 10/09, quando modalidade não
    // existia. Os dois juntos se CONTRADIZEM: o papel `contador` enxerga dois
    // setores, e a lista do Consultor atravessa cinco — o Consultor veria três
    // abas das catorze contratadas, e as onze sumiriam em silêncio, que é o pior
    // modo de uma aba desaparecer.
    //
    // As listas de `ABAS_POR_MODALIDADE` são COMPLETAS e explícitas: dizem aba por
    // aba o que cada modalidade contratou. Não precisam de um segundo filtro por
    // cima, e ter dois donos da mesma decisão é como a Rotina Contábil ficou
    // invisível em 11/09.
    //
    // O papel NÃO foi aposentado: ele continua decidindo QUEM EDITA e QUAL EMPRESA
    // se alcança, e essa checagem mora no backend (`verificar_acesso_empresa`), que
    // é onde a segurança tem de estar. Aqui se decide só o desenho do menu.
    //
    // Sem modalidade, aba nenhuma — é o VISITANTE, e o lugar dele é a vitrine.
    // Em demonstração, o administrador passa a ver o menu da modalidade
    // escolhida. Fora dela, nada muda para ninguém.
    // `ehAdmin` só é declarado mais abaixo: usá-lo aqui quebrava o aplicativo
    // inteiro com tela em branco ("Cannot access before initialization").
    const abasContratadas = (perfil?.role === 'admin' && demoModalidade)
      ? (ABAS_POR_MODALIDADE[demoModalidade] || [])
      : ABAS_VISIVEIS(perfil?.role, perfil?.modalidade);
    const abasVisiveis = NAV.filter(i =>
      abasContratadas === null || abasContratadas.includes(i.id));
  useEffect(() => {
    if (!perfil || abasVisiveis.length === 0) return;
    if (!abasVisiveis.some(i => i.id === tela)) setTela(abasVisiveis[0].id);
  }, [perfil, tela, abasVisiveis]);

  // Guarda — enquanto carrega sessao
  if (authCarregando) {
    return (
      <div style={{
        minHeight: '100vh', display: 'flex', alignItems: 'center',
        justifyContent: 'center', background: G.fundo,
        color: G.txt2, fontSize: '14px'
      }}>
        Carregando...
      </div>
    );
  }

  // Se nao esta logado OU esta em modo recuperacao de senha, mostra tela de Auth
  if (!usuario || recuperandoSenha) {
    return <Auth recoveryMode={recuperandoSenha} onRecoveryModeChange={setRecuperandoSenha} />;
  }

  // Logout
  const handleLogout = async () => {
    await supabase.auth.signOut();
    setTela('dashboard');
  };
  // Recarrega os parâmetros financeiros após salvar Cadastro ou Configurações Complementares.
  const recarregarParametros = () => {
    setParametros(null);
    fetch(`${API}/parametros/${empresa.id}`)
      .then(async r => {
        if (!r.ok) { setParametros(null); return; }
        const data = await r.json();
        setParametros(data && typeof data === 'object' && 'saldo_cc' in data ? data : null);
      });
  };
  const ehAdmin = perfil?.role === 'admin';
  const ehEmpresarioSemEmpresa = perfil && perfil.role === 'empresa' && !perfil.empresa_id;

  // Filtra resumoMensal pelo período selecionado no seletor superior do Dashboard.
  // resumoMensal.mes está em "YYYY-MM"; periodoInicio.inicio em "YYYY-MM-DD".
  const resumoNoPeriodo = (periodoInicio && periodoFim)
    ? resumoMensal.filter(m => m.mes >= periodoInicio.inicio.slice(0,7) && m.mes <= periodoFim.fim.slice(0,7))
    : resumoMensal;

  // A linha "Despesa fixa" sai da MESMA definição do break-even: tudo que não é
  // CPV nem tributo sobre venda — comercial + administrativa + financeira, as
  // três já vêm por mês no resumo. Até 22/09 ela vinha da régua estatística do
  // razão (`/indicadores/custos-fixos`), que responde outra pergunta ("esta
  // conta acompanha a receita?") e dava R$ 63.575,69/mês contra os
  // R$ 121.390,23/mês sobre os quais o break-even é calculado — 2,0×, com o
  // mesmo nome, no mesmo Dashboard. A régua continua existindo, na aba Despesas
  // e Custos, onde ela classifica conta a conta ([[rotulo-tem-que-descrever-o-numero]]).
  const tendenciaDados = resumoNoPeriodo.map(m => {
    const fixo = (m.despesa_comercial || 0) + (m.despesa_administrativa || 0)
      + (m.despesa_financeira || 0);
    return { ...m, custo_fixo: fixo > 0 ? fixo : null };
  });
  const temCustoFixo = tendenciaDados.some(m => m.custo_fixo != null);

  // Bloqueia render do dashboard enquanto perfil nao chegou
  // Evita flash de dados de outra empresa
  if (usuario && !perfil) {
    return (
      <div style={{
        minHeight: '100vh', background: '#FAFAF7',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontFamily: "'Outfit', sans-serif",
        color: '#6B7280', fontSize: 14,
      }}>
        Carregando...
      </div>
    );
  }

  // Empresario sem empresa vinculada: mostra tela de criacao
  if (ehEmpresarioSemEmpresa) {
    return <TelaCriarEmpresa
      perfilEmail={perfil.email}
      onCriada={async (data) => {
        // Recarrega o perfil para pegar o novo empresa_id
        const res = await fetch(`${API}/auth/me`);
        const perfilAtualizado = await res.json();
        setPerfil(perfilAtualizado);
        window.__ehAdmin = perfilAtualizado.role === 'admin';
        // Atualiza empresa no estado
        const empresaRes = await fetch(`${API}/empresas/${data.empresa_id}`);
        const empresaData = await empresaRes.json();
        setEmpresa({
          id: data.empresa_id,
          nome: empresaData.nome,
          setor: empresaData.setor,
        });
      }}
    />;
  }


  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100vh', overflow: 'hidden',
      fontFamily: "'Outfit', sans-serif", background: G.fundo }}>
      {/* Banner Admin - somente leitura */}
      {ehAdmin && (
        <div style={{
          background: 'linear-gradient(90deg, #F47C3C 0%, #E06A2A 100%)',
          color: '#FFF',
          padding: '3px 20px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 8,
          fontSize: 11,
          fontWeight: 600,
          letterSpacing: '0.01em',
          zIndex: 100,
        }}>
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor"
            strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
            <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
          </svg>
          <span>
            {demoModalidade
              // Em demonstração o cliente está olhando a tela: o e-mail do
              // administrador sai daqui.
              ? `Demonstração · ${(DEMOS.find(d => d.id === demoModalidade) || {}).rotulo}`
              : `${ADMIN_SOMENTE_LEITURA ? 'Modo Administrador (somente leitura)' : 'Modo Administrador — edição liberada (DEV)'} — ${perfil?.email}`}
          </span>
          {DEMOS.map(d => (
            <button key={d.id || 'adm'} type="button"
              onClick={() => setDemoModalidade(d.id)}
              style={{
                background: demoModalidade === d.id ? '#FFF' : 'rgba(255,255,255,0.18)',
                color: demoModalidade === d.id ? G.laranja : '#FFF',
                border: 'none', borderRadius: 0, padding: '3px 10px',
                fontSize: 10.5, fontWeight: 700, letterSpacing: '0.04em',
                cursor: 'pointer', fontFamily: 'inherit',
              }}>
              {d.rotulo}
            </button>
          ))}
          {/* O selo só aparece em DEMONSTRAÇÃO. Enquanto o botão ADM está
              marcado à esquerda, escrever "ADMIN" aqui à direita dizia a mesma
              coisa duas vezes — e a faixa inteira já diz que o modo é de
              administrador. Corrigido em 08/10/2026; estava anotado desde
              02/10. */}
          {demoModalidade && (
            <span style={{
              background: 'rgba(255,255,255,0.20)',
              padding: '2px 10px',
              borderRadius: 0,
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: '0.05em',
            }}>
              DEMONSTRAÇÃO
            </span>
          )}
        </div>
      )}
    <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>

      {/* Sidebar — retrai e expande só pelos botões (modo manual, 27/07) */}
      <div
        style={{
        width: sidebarRetraido ? 72 : 232,
        background: G.verde,
        display: 'flex',
        flexDirection: 'column',
        transition: 'width 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        position: 'relative',
        zIndex: 20,
      }}>
        {/* Header: Logo + botao retrair
            Logo expandido: ocupa toda a largura do container (sem padding lateral).
            Logo retraido: ícone quadrado centralizado. */}
        <div style={{
          padding: sidebarRetraido ? '12px 12px' : '8px 0',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          borderBottom: '1px solid rgba(255,255,255,0.10)',
          position: 'relative',
        }}>
          <img
            src={sidebarRetraido ? '/logos/RUNGOAL_ICON_VERDE.jpg' : '/logos/RUNGOAL_LOGO_LARANJA_SLIM.jpg'}
            alt="RunGoal"
            style={{
              width: sidebarRetraido ? 48 : '100%',
              height: sidebarRetraido ? 48 : 'auto',
              maxHeight: sidebarRetraido ? 48 : 90,
              display: 'block',
              objectFit: 'contain',
            }}
          />
          {!sidebarRetraido && (
            <button onClick={() => setSidebarRetraido(true)} aria-label="Retrair menu"
              style={{
                position: 'absolute',
                right: 10, top: '50%', transform: 'translateY(-50%)',
                background: 'rgba(255,255,255,0.10)', border: 'none',
                cursor: 'pointer', padding: 6, borderRadius: 0,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: 'rgba(255,255,255,0.85)',
                transition: 'background 0.2s',
              }}
              onMouseOver={e => e.currentTarget.style.background = 'rgba(255,255,255,0.20)'}
              onMouseOut={e => e.currentTarget.style.background = 'rgba(255,255,255,0.10)'}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M15 18l-6-6 6-6"/>
              </svg>
            </button>
          )}
        </div>

        {/* Botao flutuante para expandir (so aparece quando retraido) */}
        {sidebarRetraido && (
          <button onClick={() => setSidebarRetraido(false)} aria-label="Expandir menu"
            style={{
              position: 'absolute', top: 76, right: -12, zIndex: 30,
              width: 24, height: 24, borderRadius: '50%',
              background: G.laranja, border: '2px solid #FFF',
              cursor: 'pointer', display: 'flex',
              alignItems: 'center', justifyContent: 'center',
              color: '#FFF', }}>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
              <path d="M9 18l6-6-6-6"/>
            </svg>
          </button>
        )}

        {/* Menu — calibrado para caber sem scroll (13 itens em 6 grupos · inclui
            Consultoria do admin). O overflow era `hidden` e, ao entrar o 6º grupo
            (M&A, 27/07), o último item ficou invisível — item que some é pior que
            rolagem, então a válvula agora é `auto`. */}
        <div className="sidebar-nav" style={{ flex: 1, padding: '6px 8px', overflowY: 'auto', minHeight: 0 }}>
            {/* `SETORES_MENU` MANDA na ordem e na existência dos grupos: grupo fora
                dela não aparece, por mais que exista no NAV, e sem erro nenhum.
                CONTABILIDADE entrou em 11/09 logo abaixo de DADOS DE ENTRADA.

                QUEM ENXERGA O QUÊ é `abasVisiveis` (27/09), montado a partir da
                MODALIDADE contratada. Filtra-se `abasVisiveis`, NUNCA o `NAV`
                inteiro: filtrar o NAV aqui desenharia todas as abas do grupo,
                contratadas ou não, e o recorte de modalidade não teria efeito
                nenhum na tela — foi assim que estava até esta correção.

                Grupo sem nenhuma aba contratada não desenha nem o cabeçalho. */}
            {SETORES_MENU.map((grupo, gi) => {
              const itensGrupo = abasVisiveis.filter(item => item.grupo === grupo);
            if (itensGrupo.length === 0) return null;   // não desenha cabeçalho de grupo vazio (ex.: CONSULTORIA p/ não-admin)
            return (
            <div key={grupo} style={{ marginBottom: 1 }}>
              {!sidebarRetraido && (
                <div style={{
                  fontSize: 9.5, color: 'rgba(255,255,255,0.55)',
                  textTransform: 'uppercase', letterSpacing: '0.15em',
                  fontWeight: 700, padding: '1px 12px 1px',
                  marginTop: gi === 0 ? 0 : 2,
                }}>
                  {grupo}
                </div>
              )}
              {sidebarRetraido && gi > 0 && (
                <div style={{
                  height: 1, background: 'rgba(255,255,255,0.10)',
                  margin: '10px 14px',
                }}/>
              )}
              {itensGrupo.map(item => {
                const ativo = tela === item.id;
                return (
                  <button key={item.id}
                    onClick={() => setTela(item.id)}
                    title={sidebarRetraido ? item.label : ''}
                    style={{
                      display: 'flex', alignItems: 'center',
                      gap: sidebarRetraido ? 0 : 11,
                      justifyContent: sidebarRetraido ? 'center' : 'flex-start',
                      width: '100%',
                      padding: sidebarRetraido ? '7px 0' : '4px 12px',
                      borderRadius: 0, border: 'none', cursor: 'pointer',
                      textAlign: 'left',
                      background: ativo ? G.laranja : 'transparent',
                      color: '#FFF',
                      fontWeight: ativo ? 600 : 500,
                      fontSize: 13,
                      fontFamily: "'Outfit', sans-serif",
                      letterSpacing: '0.005em',
                      marginBottom: 0,
                      transition: 'background 0.15s, transform 0.1s',
                      boxShadow: ativo ? '0 1px 6px rgba(244, 124, 60, 0.4)' : 'none',
                    }}
                    onMouseOver={e => { if (!ativo) e.currentTarget.style.background = 'rgba(255,255,255,0.08)'; }}
                    onMouseOut={e => { if (!ativo) e.currentTarget.style.background = 'transparent'; }}
                  >
                    <IconeMenu id={item.id} />
                    {!sidebarRetraido && (
                      <span style={{ flex: 1, display: 'flex',
                        alignItems: 'center', justifyContent: 'space-between' }}>
                        <span>{item.label}</span>
                        {/* Pedidos que chegaram sozinhos pela landing page e
                            ninguém abriu ainda. Canto vivo, como tudo. */}
                        {item.id === 'consultoria' && pedidosNovos > 0 && (
                          <span style={{
                            background: G.laranja, color: '#FFF',
                            fontSize: 10.5, fontWeight: 800, lineHeight: 1,
                            padding: '3px 7px', borderRadius: 0,
                            minWidth: 18, textAlign: 'center',
                            fontVariantNumeric: 'tabular-nums',
                          }}>{pedidosNovos}</span>
                        )}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
            );
          })}
        </div>

        {/* Footer: empresa + logout */}
        <div style={{
          padding: sidebarRetraido ? '12px 8px' : '12px',
          borderTop: '1px solid rgba(255,255,255,0.10)',
        }}>
          {!sidebarRetraido && ehAdmin && !demoModalidade && (
            <>
              <div style={{
                fontSize: 10.5, color: 'rgba(255,255,255,0.55)',
                textTransform: 'uppercase', letterSpacing: '0.15em',
                fontWeight: 700, padding: '2px 4px 6px',
              }}>EMPRESA</div>
              <select value={empresa.id}
                onChange={e => {
                  const todas = [...EMPRESAS, ...empresasExtras];
                  const s = todas.find(emp => emp.id === e.target.value);
                  if (s) { setEmpresa(s); setTela('dashboard'); }
                }}
                style={{
                  width: '100%', padding: '10px 12px', borderRadius: 0,
                  border: 'none', background: 'rgba(255,255,255,0.10)',
                  color: '#FFF', fontSize: 13, fontWeight: 600,
                  fontFamily: "'Outfit', sans-serif",
                  cursor: 'pointer', outline: 'none',
                  appearance: 'none',
                  backgroundImage: 'url("data:image/svg+xml;charset=US-ASCII,%3Csvg xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22 width%3D%2216%22 height%3D%2216%22 viewBox%3D%220 0 24 24%22 fill%3D%22none%22 stroke%3D%22white%22 stroke-width%3D%222%22 stroke-linecap%3D%22round%22 stroke-linejoin%3D%22round%22%3E%3Cpolyline points%3D%226 9 12 15 18 9%22%3E%3C%2Fpolyline%3E%3C%2Fsvg%3E")',
                  backgroundRepeat: 'no-repeat',
                  backgroundPosition: 'right 10px center',
                  paddingRight: '32px',
                }}>
                {/* Empresa restaurada do último acesso que ainda não está em
                    nenhuma das duas listas (30/07): a lista de clientes chega
                    do banco depois do primeiro render, e sem esta opção o
                    <select> não achava o `value` e exibia a PRIMEIRA da lista.
                    Resultado: cabeçalho dizia MORED e o seletor dizia ShopMax
                    — a mesma leitura errada que a blindagem existe para evitar. */}
                {empresa?.id && ![...EMPRESAS, ...empresasExtras].some(e => e.id === empresa.id) && (
                  <option value={empresa.id} style={{ background: G.verde, color: '#FFF' }}>
                    {empresa.nome}
                  </option>
                )}
                {/* Separador espelha o de "clientes ativos" logo abaixo: o
                    consultor precisa ver, no próprio seletor, o que é dado
                    sintético e o que é cliente com documentação real. */}
                <option disabled style={{ background: G.verde, color: 'rgba(255,255,255,0.4)' }}>
                  — demonstração —
                </option>
                {EMPRESAS.map(e => (
                  <option key={e.id} value={e.id} style={{ background: G.verde, color: '#FFF' }}>
                    {e.nome}
                  </option>
                ))}
                {empresasExtras.length > 0 && (
                  <option disabled style={{ background: G.verde, color: 'rgba(255,255,255,0.4)' }}>
                    — clientes ativos —
                  </option>
                )}
                {empresasExtras.map(e => (
                  <option key={e.id} value={e.id} style={{ background: G.verde, color: '#FFF' }}>
                    {e.nome}
                  </option>
                ))}
              </select>
              <div style={{
                fontSize: 11, color: 'rgba(255,255,255,0.55)',
                padding: '4px 4px 0', display: 'flex', alignItems: 'center',
                gap: 6, flexWrap: 'wrap',
              }}>
                {empresa.setor}
                {ehEmpresaDemo(empresa.id) && <SeloDemonstracao compacto/>}
              </div>
            </>
          )}
          <button onClick={handleLogout} title={sidebarRetraido ? 'Sair' : ''}
            style={{
              marginTop: sidebarRetraido ? 0 : 8,
              width: '100%',
              padding: sidebarRetraido ? '10px 0' : '10px 12px',
              borderRadius: 0, border: '1px solid rgba(255,255,255,0.18)',
              background: 'transparent', color: 'rgba(255,255,255,0.85)',
              cursor: 'pointer', fontSize: 13, fontWeight: 500,
              fontFamily: "'Outfit', sans-serif",
              display: 'flex', alignItems: 'center',
              justifyContent: sidebarRetraido ? 'center' : 'flex-start',
              gap: sidebarRetraido ? 0 : 10,
              transition: 'background 0.15s, border-color 0.15s',
            }}
            onMouseOver={e => {
              e.currentTarget.style.background = 'rgba(255,255,255,0.08)';
              e.currentTarget.style.borderColor = 'rgba(255,255,255,0.32)';
            }}
            onMouseOut={e => {
              e.currentTarget.style.background = 'transparent';
              e.currentTarget.style.borderColor = 'rgba(255,255,255,0.18)';
            }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
              <polyline points="16 17 21 12 16 7"/>
              <line x1="21" y1="12" x2="9" y2="12"/>
            </svg>
            {!sidebarRetraido && <span>Sair</span>}
          </button>
        </div>
      </div>

            {/* Main */}
      <div style={{ flex: 1,
        // 25/09 · `hidden` ESCONDIA em vez de caber. O Dashboard é
        // scroll-zero por desenho e cabe na janela cheia; mas o card do
        // campeão fica mais ALTO quando a janela estreita (o nome do produto
        // quebra em mais linhas), e aí o que não cabia sumia — em 1054px
        // faltavam 84px, em 1440px faltavam 51. Número escondido é pior que
        // barra de rolagem: `auto` mantém a tela sem rolagem quando cabe e
        // devolve o acesso ao conteúdo quando não cabe.
        overflowY: 'auto',
        overflowX: 'hidden', background: '#A8D2B0' }}>
        <div style={{ padding: '8px 28px', background: G.card,
          borderBottom: `1px solid ${G.borda}`,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          position: 'sticky', top: 0, zIndex: 10 }}>
          <div>
            <h1 style={{ margin: 0, fontSize: 20, fontWeight: 800, color: G.txt1 }}>
              {empresa.nome}
            </h1>
            <p style={{ margin: 0, fontSize: 12, color: G.txt2 }}>{empresa.setor}</p>
          </div>
          {(tela === 'dashboard' || tela === 'dre' || tela === 'resumo' || tela === 'alertas' || tela === 'valor' || tela === 'extrato' || tela === 'despesas_custos') && periodos.length > 0 && periodoInicio && periodoFim && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontSize: 12, color: G.txt2, fontWeight: 600 }}>De</span>
              <select value={periodoInicio.label}
                onChange={e => escolherPeriodo('inicio', e.target.value)}
                style={{ padding: '8px 12px', borderRadius: 0,
                  fontSize: 13,
                  fontWeight: 600, color: G.verde, background: G.verdeLight,
                  cursor: 'pointer' }}>
                {periodos.map(p => (
                  <option key={p.label}>{p.label}</option>
                ))}
              </select>
              <span style={{ fontSize: 12, color: G.txt2, fontWeight: 600 }}>Até</span>
              <select value={periodoFim.label}
                onChange={e => escolherPeriodo('fim', e.target.value)}
                style={{ padding: '8px 12px', borderRadius: 0,
                  fontSize: 13,
                  fontWeight: 600, color: G.verde, background: G.verdeLight,
                  cursor: 'pointer' }}>
                {periodos.map(p => (
                  <option key={p.label}>{p.label}</option>
                ))}
              </select>
              {/* Aviso da volta automática. Só aparece fora da janela padrão —
                  e some sozinho quando ela é restaurada. */}
              {voltaEm !== null && (
                <span title="Análise pontual. O período volta sozinho aos últimos 12 meses completos."
                  style={{ fontSize: 11, fontWeight: 700, color: G.laranja,
                    background: G.laranjaLight, border: `1px solid ${G.laranja}`,
                    borderRadius: 0, padding: '4px 10px', whiteSpace: 'nowrap' }}>
                  volta ao padrão em {voltaEm}s
                </span>
              )}
            </div>
          )}
        </div>

        {/* `key={empresa.id}` em TODA tela que recebe empresa (universalizado
            em 30/07/2026, a partir do padrão que já existia na TelaCadastro):
            ao trocar de empresa o React desmonta e remonta, e todo estado
            interno da tela anterior morre junto. Sem isso, o componente
            continua vivo exibindo os dados da empresa antiga até o effect
            recarregar — segunda via da contaminação, invisível ao usuário.
            TelaRGAM e TelaConsultoria ficam de fora: não recebem empresa. */}
        {tela === 'alertas' ? (
          <TelaAlertas key={empresa.id} empresa={empresa} parametros={parametros}
            periodoInicio={periodoInicio} periodoFim={periodoFim}/>
        ) : tela === 'plano' ? (
          <TelaPlano key={empresa.id} empresa={empresa} onValidado={p => setPlanoVigente(p)}
            onAbrirConfig={() => setTela('configuracoes')}/>
        ) : tela === 'businessplan' ? (
          <TelaBP60M key={empresa.id} empresa={empresa} parametros={parametros}
            onAbrirConfig={() => setTela('configuracoes')}/>
        ) : tela === 'businessplansu' ? (
          <TelaBP60MSU key={empresa.id} empresa={empresa} parametros={parametros}
            onAbrirConfig={() => setTela('configuracoes')}/>
        ) : tela === 'rgam' ? (
          <TelaRGAM onAbrirAba={setTela}/>
        ) : tela === 'balanco' ? (
          <TelaBalancoPatrimonial key={empresa.id} empresa={empresa}
            periodoFim={periodoFim}/>
        ) : tela === 'comparativo' ? (
          <TelaComparativo key={empresa.id} empresa={empresa} parametros={parametros}/>
        ) : ['razao_simulado', 'balancete_simulado', 'composicao_custo'].includes(tela) ? (
          <TelaRotinaContabil key={empresa.id + tela} empresa={empresa}
            aba={tela} onIr={setTela}/>
        ) : tela === 'consultoria' ? (
          <TelaConsultoria onPedidoAberto={contarPedidosNovos} />
        ) : tela === 'valor' ? (
          <TelaValor key={empresa.id} empresa={empresa} parametros={parametros}
            indicadores={indicadores}
            periodoInicio={periodoInicio} periodoFim={periodoFim}/>
        ) : tela === 'reforma' ? (
          <TelaReformaTributaria key={empresa.id} empresa={empresa}/>
        ) : tela === 'despesas_custos' ? (
          <TelaDespesasCustos key={empresa.id} empresa={empresa} indicadores={indicadores}
            periodoInicio={periodoInicio} periodoFim={periodoFim}/>
        ) : tela === 'extrato' ? (
          <TelaDadosEntrada key={empresa.id} empresa={empresa}
            periodoInicio={periodoInicio} periodoFim={periodoFim}/>
        ) : tela === 'resumo' ? (
          <TelaResumoMensal key={empresa.id} empresa={empresa}
            periodoInicio={periodoInicio} periodoFim={periodoFim}/>
        ) : tela === 'dre' ? (
          // `periodoInicio`/`periodoFim` são ZERADOS na troca de empresa (3ª
          // camada da blindagem anti-contaminação) e ficam nulos até o período
          // da nova empresa chegar. Ler `.label` direto quebrava a tela inteira
          // nessa janela: "Cannot read properties of null" no AppPrivado, com a
          // DRE em branco (30/07). Enquanto não há período, a tela espera.
          (periodoInicio && periodoFim) ? (
            <TelaDRE key={empresa.id} indicadores={indicadores}
              periodo={{ label: `${periodoInicio.label} → ${periodoFim.label}`,
                         inicio: periodoInicio.inicio, fim: periodoFim.fim }}
              empresa={empresa}/>
          ) : (
            <div style={{ padding: '24px 28px', color: G.txt2, fontSize: 13 }}>
              Carregando o período…
            </div>
          )
        ) : tela === 'cadastro' ? (
          <TelaCadastro key={empresa.id} empresa={empresa} onSalvo={recarregarParametros}/>
        ) : tela === 'configuracoes' ? (
          <TelaConfigHub key={empresa.id} empresa={empresa} onSalvo={recarregarParametros}/>
        ) : (
          /* 25/09 · a altura era `calc(100vh - 158px)` com `overflow:hidden`.
             O 158 era cravado e não batia com a tela real: com a faixa de
             administrador no topo faltavam 165px, e o que não cabia SUMIA —
             a fileira dos campeões cortada ao meio. Agora o bloco ocupa o
             espaço que o pai lhe der (`flex:1` + `minHeight:0`) e rola se o
             conteúdo pedir. Cabe na janela cheia como sempre coube; em janela
             estreita, onde o nome do produto quebra em mais linhas, o número
             fica acessível em vez de escondido. */
          <div style={{ padding: '7px 20px', background: '#A8D2B0',
            flex: 1, minHeight: 0,
            display: 'flex', flexDirection: 'column', gap: 9,
            fontFamily: "'Outfit', sans-serif", overflowY: 'auto' }}>
            {/* Dashboard unico — seletor de paineis (Master/Dia+Mes/Equity/Credito)
                removido em 01/06/2026. Equity e Credito ficam para futura aba lateral. */}
            {loading && (
              <div style={{ textAlign: 'center', padding: 40, color: '#4A5563' }}>
                Carregando...
              </div>
            )}
            {parametros === null && !ehAdmin && !loading && (
              <div style={{ background: '#FFF', padding: '40px 48px',
                maxWidth: 720, margin: '20px auto', width: '100%' }}>
                <div style={{ fontSize: 12, fontWeight: 600, letterSpacing: '0.16em',
                  color: '#F47C3C', textTransform: 'uppercase', marginBottom: 12 }}>
                  PROXIMOS PASSOS
                </div>
                <h2 style={{ fontSize: 26, fontWeight: 700, color: '#1A1A1A',
                  margin: 0, letterSpacing: '-0.02em', marginBottom: 10 }}>
                  Bem-vindo, {empresa?.nome}!
                </h2>
                <p style={{ fontSize: 14, color: '#6B7280', lineHeight: 1.6,
                  marginTop: 0, marginBottom: 28 }}>
                  Para começar a ver seus indicadores financeiros, você precisa
                  cadastrar algumas informações iniciais. Siga os passos abaixo:
                </p>

                <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                  {[
                    { num: 1, titulo: 'Configurar parâmetros financeiros',
                      desc: 'Saldo em conta, CDB e custo de capital.',
                      botao: 'Ir para Configurações', tela: 'configuracoes' },
                    { num: 2, titulo: 'Importar movimentações bancárias',
                      desc: 'Faça upload do extrato em CSV ou OFX.',
                      botao: 'Ir para Extrato', tela: 'extrato' },
                    { num: 3, titulo: 'Pronto!',
                      desc: 'Seus indicadores estarão calculados e disponíveis no Dashboard.',
                      botao: null, tela: null },
                  ].map(p => (
                    <div key={p.num} style={{ display: 'flex', alignItems: 'flex-start',
                      gap: 16, padding: '18px 20px', background: '#FAFAF7',
                      borderLeft: '3px solid #1B6B2F' }}>
                      <div style={{ width: 32, height: 32, background: '#1B6B2F',
                        color: '#FFF', display: 'flex', alignItems: 'center',
                        justifyContent: 'center', fontWeight: 700, fontSize: 15,
                        borderRadius: '50%', flexShrink: 0 }}>
                        {p.num}
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 15, fontWeight: 600, color: '#1A1A1A',
                          marginBottom: 4 }}>{p.titulo}</div>
                        <div style={{ fontSize: 13, color: '#6B7280',
                          marginBottom: p.botao ? 10 : 0 }}>{p.desc}</div>
                        {p.botao && (
                          <button onClick={() => setTela(p.tela)}
                            style={{ padding: '8px 16px', background: '#1B6B2F',
                              color: '#FFF', border: 'none', borderRadius: 0,
                              fontSize: 13, fontWeight: 600, cursor: 'pointer',
                              fontFamily: "'Outfit', sans-serif" }}>
                            {p.botao} →
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {erro && ehAdmin && (
              <div style={{ background: '#FFE5E5', color: '#C03030',
                padding: 16, fontWeight: 600 }}>{erro}</div>
            )}
            {/* 2ª camada da blindagem · SELO DE IDENTIDADE (30/07/2026).
                O backend carimba `empresa_id` na resposta dos indicadores.
                Se o carimbo não bate com a empresa selecionada, a tela recusa
                exibir os números e diz de quem eles são — em vez de mostrar um
                valor plausível sob o nome errado, que foi o que aconteceu com
                os R$ 680.000 da Construmax apresentados como MORED. */}
            {indicadores && !loading && !seloConfere && (
              <div style={{ background: '#FFE5E5', border: '2px solid #C03030',
                padding: '20px 24px', maxWidth: 720, margin: '20px auto',
                width: '100%' }}>
                <div style={{ fontSize: 13, fontWeight: 800, color: '#C03030',
                  letterSpacing: '0.08em', textTransform: 'uppercase',
                  marginBottom: 8 }}>
                  Indicadores não exibidos
                </div>
                <div style={{ fontSize: 14, lineHeight: 1.5, color: '#1A1A1A' }}>
                  Os números recebidos pertencem a <strong>outra empresa</strong>
                  {' '}(<code>{String(indicadores.empresa_id)}</code>) e não a{' '}
                  <strong>{empresa?.nome}</strong>. Nada é exibido para não
                  induzir a uma leitura errada. Troque de empresa e volte, ou
                  recarregue a página.
                </div>
              </div>
            )}
            {indicadores && !loading && seloConfere && (
              <div style={{ display: 'contents' }}>
                {/* ============ TIER 1 - CAIXA + DRE + BALANÇO + ALERTAS ============ */}
                <div style={{ display: 'grid',
                  // 14/09: Caixa e DRE seguem alinhados com Receita e Despesas
                  // do Tier 2 (1.1fr cada). O 1.6fr que lá é a Tendência aqui se
                  // divide entre Balanço e Alertas, que somam o mesmo 1.6 — a
                  // borda direita dos dois tiers continua batendo.
                  //
                  // 14/09: `1.1fr` aqui NÃO é `1.1fr` no Tier 2. Este grid tem
                  // 4 colunas (3 gaps = 48 px) e o de baixo tem 3 (2 gaps =
                  // 32 px), então a mesma fração distribui espaços diferentes —
                  // 4,6 px a menos no Caixa. As duas primeiras colunas passam a
                  // usar a fórmula DO TIER 2 (`100% - 32px`), e Balanço e
                  // Alertas dividem o que sobra.
                  // Ordem: Alertas · Caixa · DRE · Balanço (14/09). As larguras
                  // não mudaram — só trocaram de lugar na declaração, junto com
                  // os cards no JSX.
                  gridTemplateColumns:
                    '0.55fr '
                    + 'calc((100% - 32px) * 1.1 / 3.8) '
                    + 'calc((100% - 32px) * 1.1 / 3.8) '
                    + '1.05fr',
                  gap: 12, flexShrink: 0 }}>


                  {/* CARD ALERTAS RESUMIDOS · vertical, depois do Balanço */}
                  <AlertasMiniInline empresa={empresa} parametros={parametros}
                    periodoInicio={periodoInicio} periodoFim={periodoFim}
                    onVerTodos={() => setTela('alertas')}/>
                  {/* CARD SOBREVIVENCIA */}
                  <div style={{ background: '#FFF', padding: '10px 20px',
                    display: 'flex', flexDirection: 'column' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between',
                      alignItems: 'center', marginBottom: 12 }}>
                      <div style={{ fontSize: 12, fontWeight: 600,
                        letterSpacing: '0.14em', color: '#F47C3C',
                        textTransform: 'uppercase' }}>CAIXA</div>
                      {indicadores.runway_meses != null && indicadores.runway_meses < 3 && (
                        <span style={{ background: '#FFF0E6', color: '#C45826',
                          fontSize: 10, fontWeight: 600, padding: '3px 8px',
                          letterSpacing: '0.05em' }}>ATENCAO</span>
                      )}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'baseline', gap: 6,
                      marginBottom: 2 }}>
                      {/* Sem runway = a operação NÃO queima caixa (se paga). Mostra
                          "∞", não "--": "--" parece falta de dado (laudo 2 · 25/07). */}
                      <div style={{ fontSize: 36, fontWeight: 700,
                        letterSpacing: '-0.02em', lineHeight: 1, color: '#1A1A1A' }}>
                        {indicadores.runway_meses != null
                          ? indicadores.runway_meses.toFixed(1)
                          : (indicadores.interpretacao_runway || '').startsWith('N/D') ? '--' : '∞'}
                      </div>
                      <div style={{ fontSize: 14, color: '#4A5563', fontWeight: 500 }}>
                        meses de caixa
                      </div>
                    </div>
                    <div style={{ fontSize: 11, color: '#7F8C8D', marginBottom: 6 }}>
                      {indicadores.interpretacao_runway}
                    </div>
                    {indicadores.runway_meses != null && indicadores.fcf < 0 && (() => {
                      // Caixa só vira negativo se a operação está consumindo caixa (FCF<0).
                      // Quando FCF>=0 a empresa gera, não faz sentido projetar "zerar".
                      const d = new Date();
                      d.setMonth(d.getMonth() + Math.round(indicadores.runway_meses));
                      const MESES = ['Jan','Fev','Mar','Abr','Mai','Jun','Jul','Ago','Set','Out','Nov','Dez'];
                      return (
                        <div style={{ fontSize: 11, color: '#C45826', fontWeight: 600, marginBottom: 12 }}>
                          Caixa estimado zerar em <strong>{MESES[d.getMonth()]}/{d.getFullYear()}</strong>
                        </div>
                      );
                    })()}
                    {indicadores.fcf >= 0 && (
                      <div style={{ fontSize: 11, color: '#1B6B2F', fontWeight: 600, marginBottom: 12 }}>
                        ✓ Operação gera caixa — projeção sem ponto de zerar
                      </div>
                    )}
                    {/* barra de progresso removida — informação já está em meses/data */}
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr',
                      gap: 12, paddingTop: 10,
                      borderTop: '0.5px solid #F0F0EC' }}>
                      <div>
                        <div style={{ fontSize: 9, color: '#7F8C8D',
                          letterSpacing: '0.08em', fontWeight: 600 }}>SALDO TOTAL</div>
                        <div style={{ fontSize: 15, fontWeight: 600,
                          color: '#1A1A1A', marginTop: 2 }}>
                          {fmtBRL((parametros?.saldo_cc || 0) + (parametros?.saldo_cdb || 0))}
                        </div>
                        <div style={{ fontSize: 10, color: '#7F8C8D' }}>CC + CDB</div>
                      </div>
                      <div>
                        <div style={{ fontSize: 9, color: '#7F8C8D',
                          letterSpacing: '0.08em', fontWeight: 600 }}>BREAK-EVEN</div>
                        <div style={{ fontSize: 15, fontWeight: 600,
                          color: '#1A1A1A', marginTop: 2 }}>
                          {fmtBRL(indicadores.breakeven)}
                        </div>
                        {/* Cor e texto na MESMA base do break-even (a líquida) —
                            antes a cor usava a bruta e podia dizer "Abaixo do BE"
                            em verde (laudo 2 · 25/07). */}
                        <div style={{ fontSize: 10,
                          color: (indicadores.receita_liquida ?? indicadores.receita_bruta) >= (indicadores.breakeven || 0) ? '#1B6B2F' : '#C03030' }}>
                          {(indicadores.receita_liquida ?? indicadores.receita_bruta) >= (indicadores.breakeven || 0) ? 'Acima do BE' : 'Abaixo do BE'}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* CARD DRE — 4 margens empilhadas (espelha rodape da aba DRE) */}
                  <div onClick={() => setTela('dre')} title="Ver DRE completa"
                    style={{ background: '#FFF', padding: '10px 18px',
                      display: 'flex', flexDirection: 'column', cursor: 'pointer' }}>
                    <div style={{ fontSize: 12, fontWeight: 600,
                      letterSpacing: '0.14em', color: '#1B6B2F',
                      textTransform: 'uppercase', marginBottom: 10,
                      textDecoration: 'underline',
                      textDecorationColor: 'rgba(27,107,47,0.35)', textUnderlineOffset: 3 }}>
                      DRE →
                    </div>
                    {(() => {
                      // Faixas por margem (alinhadas com critério do produto):
                      //   ≥15% = Ótimo (verde), 5-15% = Regular (amarelo), <5% = Ruim (vermelho)
                      // Escala da barra: 0% a 30%. Margens >30% saem na ponta direita;
                      // margens <0% (prejuízo) saem na ponta esquerda com cor crítica.
                      const corPor = (v) => v >= 0.15 ? '#1B6B2F' : v >= 0.05 ? '#F59E0B' : '#DC2626';
                      const linhas = [
                        { label: 'Margem Bruta',   v: indicadores.margem_bruta   || 0, valor: indicadores.lucro_bruto   || 0 },
                        { label: 'Margem EBITDA',  v: indicadores.margem_ebitda  || 0, valor: indicadores.ebitda        || 0 },
                        { label: 'Margem EBIT',    v: indicadores.margem_ebit    || 0, valor: indicadores.ebit          || 0 },
                        { label: 'Margem Líquida', v: indicadores.margem_liquida || 0, valor: indicadores.lucro_liquido || 0 },
                      ];
                      // Barra semáforo padronizada (DRE/Receita/Despesa):
                      // bar height 10px · border 2px · separator 1.5px · marker 4×16
                      // Zonas DRE: 0-5% vermelho (16.66% bar) · 5-15% âmbar (33.34%) · 15-30% verde (50%)
                      return (
                        <div style={{ display: 'flex', flexDirection: 'column',
                          flex: 1, justifyContent: 'space-around', gap: 4 }}>
                          {linhas.map(l => {
                            const cor = corPor(l.v);
                            const pct = l.v * 100;
                            const pos = Math.max(0, Math.min(100, (pct / 30) * 100));
                            return (
                              <div key={l.label} style={{ paddingBottom: 4 }}>
                                <div style={{ display: 'flex',
                                  justifyContent: 'space-between', alignItems: 'baseline',
                                  marginBottom: 4, gap: 8 }}>
                                  <span style={{ fontSize: 11.5, color: '#4A5563', fontWeight: 500 }}>
                                    {l.label}
                                  </span>
                                  <span style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
                                    <span style={{ fontSize: 12, color: '#4A5563', fontWeight: 500 }}>
                                      {fmtBRL(l.valor)}
                                    </span>
                                    <span style={{ fontSize: 16, fontWeight: 700, color: cor,
                                      letterSpacing: '-0.01em', minWidth: 48, textAlign: 'right' }}>
                                      {fmtPct(l.v)}
                                    </span>
                                  </span>
                                </div>
                                {/* DRE — barra em tons de azul (claro=ruim, médio=regular, escuro=ótimo).
                                    Cor diferente de Receita (verde) e Despesa (laranja). */}
                                <div style={{ position: 'relative', height: 6,
                                  borderRadius: 0,
                                  background: 'linear-gradient(90deg, ' +
                                    'rgba(30,91,170,0.18) 0%, rgba(30,91,170,0.18) 16.66%, ' +
                                    'rgba(30,91,170,0.50) 16.66%, rgba(30,91,170,0.50) 50%, ' +
                                    'rgba(30,91,170,1.0) 50%, rgba(30,91,170,1.0) 100%)' }}>
                                  <div style={{ position: 'absolute', left: `${pos}%`,
                                    top: -2, width: 3, height: 10,
                                    background: cor, borderRadius: 0,
                                    transform: 'translateX(-1.5px)',
                                    boxShadow: '0 0 0 1.5px #FFF', zIndex: 2 }}/>
                                </div>
                              </div>
                            );
                          })}
                          <div style={{ display: 'flex',
                            justifyContent: 'center', gap: 16, fontSize: 11,
                            marginTop: 'auto', paddingTop: 8,
                            letterSpacing: '0.04em', fontWeight: 600 }}>
                            <span style={{ color: '#DC2626' }}>RUIM</span>
                            <span style={{ color: '#F59E0B' }}>REGULAR</span>
                            <span style={{ color: '#1B6B2F' }}>ÓTIMO</span>
                          </div>
                        </div>
                      );
                    })()}
                  </div>

                  {/* CARD BALANÇO PATRIMONIAL — Ativo | Passivo + PL (14/09)
                      Nos moldes do card DRE: linhas principais e totais, sem
                      detalhe de conta. Duas colunas porque é assim que um
                      balanço se lê — as duas somam o MESMO total, e vê-las
                      lado a lado é a conferência visual da partida dobrada. */}
                  <div onClick={() => setTela('balanco')} title="Ver Balanço Patrimonial"
                    style={{ background: '#FFF', padding: '13px 20px',
                    display: 'flex', flexDirection: 'column', cursor: 'pointer' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between',
                      alignItems: 'baseline', marginBottom: 12 }}>
                      <div style={{ fontSize: 11, color: '#4A5563',
                        letterSpacing: '0.10em', fontWeight: 600,
                        textDecoration: 'underline', textDecorationColor: '#F47C3C',
                        textUnderlineOffset: 3 }}>BALANÇO PATRIMONIAL →</div>
                      {balanco?.data_referencia && (
                        <div style={{ fontSize: 10, color: '#7F8C8D' }}>
                          {String(balanco.data_referencia).split('-').reverse().join('/')}
                        </div>
                      )}
                    </div>

                    {/* `null` = ainda não respondeu, distinto de empresa sem
                        balanço — que é o `!balanco.total_passivo_pl` abaixo. */}
                    {balanco === null ? (
                      <div style={{ fontSize: 11, color: '#7F8C8D', padding: '22px 0',
                        textAlign: 'center' }}>Carregando…</div>
                    ) : !balanco.total_passivo_pl ? (
                      <div style={{ fontSize: 11.5, color: '#7F8C8D', padding: '18px 0',
                        textAlign: 'center', lineHeight: 1.5 }}>
                        Sem balancete carregado.<br/>Envie pela Fonte Contábil.
                      </div>
                    ) : (
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr',
                        gap: 18, flex: 1 }}>
                        {[
                          { titulo: 'ATIVO', cor: '#1B6B2F', linhas: [
                              ['Circulante', balanco.ativo?.circulante?.subtotal],
                              ['Não Circulante', balanco.ativo?.nao_circulante?.subtotal],
                            ], total: balanco.ativo?.total },
                          { titulo: 'PASSIVO + PL', cor: '#F47C3C', linhas: [
                              ['Circulante', balanco.passivo?.circulante?.subtotal],
                              ['Não Circulante', balanco.passivo?.nao_circulante?.subtotal],
                              ['Patrimônio Líquido', balanco.patrimonio_liquido?.total],
                            ], total: balanco.total_passivo_pl },
                        ].map(col => (
                          <div key={col.titulo} style={{ display: 'flex',
                            flexDirection: 'column' }}>
                            <div style={{ fontSize: 9.5, fontWeight: 700,
                              letterSpacing: '0.08em', color: col.cor,
                              marginBottom: 7 }}>{col.titulo}</div>
                            {/* 14/09: rótulo EM CIMA do valor. Lado a lado, os
                                valores de 7 dígitos disputavam a largura com o
                                rótulo e quebravam. As duas colunas — Ativo e
                                Passivo + PL — continuam separadas. */}
                            {col.linhas.map(([rot, val]) => (
                              <div key={rot} style={{ marginBottom: 8 }}>
                                <div style={{ fontSize: 11, color: '#4A5563',
                                  lineHeight: 1.25 }}>{rot}</div>
                                <div style={{ fontSize: 12.5, color: '#1A1A1A',
                                  fontWeight: 600, fontVariantNumeric: 'tabular-nums',
                                  lineHeight: 1.3 }}>
                                  {fmtBRL(val)}
                                </div>
                              </div>
                            ))}
                            <div style={{ marginTop: 'auto', paddingTop: 7,
                              borderTop: '1px solid #E4E4DE' }}>
                              <div style={{ fontSize: 10, fontWeight: 700,
                                letterSpacing: '0.06em', color: '#4A5563' }}>TOTAL</div>
                              <div style={{ fontSize: 14, fontWeight: 700,
                                color: col.cor, fontVariantNumeric: 'tabular-nums',
                                lineHeight: 1.3 }}>
                                {fmtBRL(col.total)}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {/* ============ TIER 2 - RECEITA + DESPESA + TENDENCIA ============ */}
                <div style={{ display: 'grid',
                  gridTemplateColumns: '1.1fr 1.1fr 1.6fr', gap: 12,
                  flex: '1 1 auto', minHeight: 150 }}>

                  {/* RECEITA */}
                  <div onClick={() => setTela('comparativo')} title="Ver Previsto x Real"
                    style={{ background: '#FFF', padding: '13px 20px',
                    display: 'flex', flexDirection: 'column', cursor: 'pointer' }}>
                    {(() => {
                      // Mesma cascata da DRE (01/08/2026). O card mostrava a
                      // receita JÁ LÍQUIDA de devoluções sob o rótulo
                      // "Operacional": R$ 17.214.525,39 aqui contra
                      // R$ 17.610.388,27 na DRE, mesma empresa, mesmo período.
                      // Com a dedução em linha própria, o total não muda e o
                      // número de cima passa a ser o da contabilidade.
                      const totRecOp  = resumoNoPeriodo.reduce((s,m) => s + (m.receita_operacional || 0), 0);
                      const totDeduc  = resumoNoPeriodo.reduce((s,m) => s + (m.deducoes_receita   || 0), 0);
                      const totRecFin = resumoNoPeriodo.reduce((s,m) => s + (m.receita_financeira  || 0), 0);
                      const total = totRecOp - totDeduc + totRecFin;
                      const headerLine = (
                        <div style={{ display: 'flex', justifyContent: 'space-between',
                          alignItems: 'baseline', marginBottom: 10 }}>
                          <div style={{ fontSize: 12, fontWeight: 600,
                            letterSpacing: '0.14em', color: '#1B6B2F',
                            textTransform: 'uppercase',
                            textDecoration: 'underline',
                            textDecorationColor: 'rgba(27,107,47,0.35)',
                            textUnderlineOffset: 3 }}>RECEITA →</div>
                          <div style={{ fontSize: 15, fontWeight: 600, color: '#1A1A1A',
                            letterSpacing: '-0.01em' }}>{fmtBRL(total)}</div>
                        </div>
                      );
                      if (total === 0) return (<>{headerLine}<div style={{
                        fontSize: 11, color: '#7F8C8D', padding: '20px 0',
                        textAlign: 'center' }}>Sem receitas</div></>);
                      // Participação da operação: sobre a receita LÍQUIDA de
                      // devoluções, senão passaria de 100% com a bruta em cima.
                      const pctOp  = total > 0 ? ((totRecOp - totDeduc) / total) : 0;
                      const pctFin = total > 0 ? (totRecFin / total) : 0;
                      // Receita: quanto MAIOR a % Operacional, melhor (saúde = vive da operação).
                      // ≥85% verde · 60-85% amarelo · <60% vermelho (vive de receita financeira).
                      const cor = pctOp >= 0.85 ? '#1B6B2F' : pctOp >= 0.60 ? '#F59E0B' : '#DC2626';
                      const pos = pctOp * 100;
                      return (
                        <>
                          {headerLine}
                          <div style={{ fontSize: 11, color: '#7F8C8D',
                            marginBottom: 14 }}>
                            Margem bruta {fmtPct(indicadores.margem_bruta)}
                          </div>

                          <div style={{ display: 'flex', justifyContent: 'space-between',
                            alignItems: 'baseline', marginBottom: 4, gap: 8 }}>
                            <span style={{ fontSize: 12, color: '#4A5563', fontWeight: 500 }}>
                              Operacional
                            </span>
                            <span style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
                              <span style={{ fontSize: 12, color: '#4A5563', fontWeight: 500 }}>
                                {fmtBRL(totRecOp)}
                              </span>
                              <span style={{ fontSize: 14, fontWeight: 700, color: cor,
                                minWidth: 56, textAlign: 'right' }}>
                                {fmtPct(pctOp)}
                              </span>
                            </span>
                          </div>

                          {/* Devoluções e abatimentos — só aparece quando existem
                              ([[base-zerada-nada-nativo]]). */}
                          {totDeduc > 0 && (
                            <div style={{ display: 'flex', justifyContent: 'space-between',
                              alignItems: 'baseline', marginBottom: 4, gap: 8 }}>
                              <span style={{ fontSize: 12, color: '#7F8C8D' }}>
                                (-) Devoluções
                              </span>
                              <span style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
                                <span style={{ fontSize: 12, color: '#C03030' }}>
                                  {fmtBRL(totDeduc)}
                                </span>
                                <span style={{ fontSize: 14, fontWeight: 700, color: '#7F8C8D',
                                  minWidth: 56, textAlign: 'right' }}>
                                  {fmtPct(total > 0 ? totDeduc / total : 0)}
                                </span>
                              </span>
                            </div>
                          )}
                          <div style={{ display: 'flex', justifyContent: 'space-between',
                            alignItems: 'baseline', marginBottom: 8, gap: 8 }}>
                            <span style={{ fontSize: 12, color: '#4A5563', fontWeight: 500 }}>
                              Financeira
                            </span>
                            <span style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
                              <span style={{ fontSize: 12, color: '#4A5563', fontWeight: 500 }}>
                                {fmtBRL(totRecFin)}
                              </span>
                              <span style={{ fontSize: 14, fontWeight: 700, color: '#7F8C8D',
                                minWidth: 56, textAlign: 'right' }}>
                                {fmtPct(pctFin)}
                              </span>
                            </span>
                          </div>
                          {/* Receita — barra em tons de verde (claro=ruim, médio=regular, escuro=ótimo) */}
                          <div style={{ position: 'relative', height: 6,
                            borderRadius: 0,
                            background: 'linear-gradient(90deg, ' +
                              'rgba(27,107,47,0.18) 0%, rgba(27,107,47,0.18) 60%, ' +
                              'rgba(27,107,47,0.50) 60%, rgba(27,107,47,0.50) 85%, ' +
                              'rgba(27,107,47,1.0) 85%, rgba(27,107,47,1.0) 100%)' }}>
                            <div style={{ position: 'absolute', left: `${pos}%`,
                              top: -2, width: 3, height: 10,
                              background: cor, borderRadius: 0,
                              transform: 'translateX(-1.5px)',
                              boxShadow: '0 0 0 1.5px #FFF', zIndex: 2 }}/>
                          </div>
                          <div style={{ display: 'flex', justifyContent: 'center', gap: 16,
                            fontSize: 11, marginTop: 'auto', paddingTop: 8,
                            letterSpacing: '0.04em', fontWeight: 600 }}>
                            <span style={{ color: '#DC2626' }}>RUIM</span>
                            <span style={{ color: '#F59E0B' }}>REGULAR</span>
                            <span style={{ color: '#1B6B2F' }}>ÓTIMO</span>
                          </div>
                        </>
                      );
                    })()}
                  </div>

                  {/* DESPESAS */}
                  <div onClick={() => setTela('comparativo')} title="Ver Previsto x Real"
                    style={{ background: '#FFF', padding: '13px 20px',
                    display: 'flex', flexDirection: 'column', cursor: 'pointer' }}>
                    {(() => {
                      const dOp     = resumoNoPeriodo.reduce((s,m) => s + (m.despesa_operacional    || 0), 0);
                      const dComerc = resumoNoPeriodo.reduce((s,m) => s + (m.despesa_comercial      || 0), 0);
                      const dAdm    = resumoNoPeriodo.reduce((s,m) => s + (m.despesa_administrativa || 0), 0);
                      const dFin    = resumoNoPeriodo.reduce((s,m) => s + (m.despesa_financeira     || 0), 0);
                      const dTrib   = resumoNoPeriodo.reduce((s,m) => s + (m.tributos               || 0), 0);
                      const total   = dOp + dComerc + dAdm + dFin + dTrib;
                      const headerLine = (
                        <div style={{ display: 'flex', justifyContent: 'space-between',
                          alignItems: 'baseline', marginBottom: 10 }}>
                          <div style={{ fontSize: 12, fontWeight: 600,
                            letterSpacing: '0.14em', color: '#F47C3C',
                            textTransform: 'uppercase',
                            textDecoration: 'underline',
                            textDecorationColor: 'rgba(244,124,60,0.35)',
                            textUnderlineOffset: 3 }}>DESPESAS →</div>
                          <div style={{ fontSize: 15, fontWeight: 600, color: '#1A1A1A',
                            letterSpacing: '-0.01em' }}>{fmtBRL(total)}</div>
                        </div>
                      );
                      if (total === 0) return (<>{headerLine}<div style={{
                        fontSize: 11, color: '#7F8C8D', padding: '20px 0',
                        textAlign: 'center' }}>Sem despesas</div></>);
                      // Benchmarks setoriais (do banco). Fallback: faixas gerais.
                      const B = benchmarks?.benchmarks || {};
                      const _b = (cat, dA, dAlto) => ({
                        atencao: B[cat]?.atencao ?? dA, alto: B[cat]?.alto ?? dAlto
                      });
                      const cats = [
                        { label: 'Operacional',    val: dOp,     ...(_b('Operacional',     0.55, 0.70)) },
                        { label: 'Comercial',      val: dComerc, ...(_b('Comercial',       0.15, 0.25)) },
                        { label: 'Administrativa', val: dAdm,    ...(_b('Administrativa',  0.25, 0.40)) },
                        { label: 'Financeira',     val: dFin,    ...(_b('Financeira',      0.05, 0.10)) },
                        { label: 'Tributos',       val: dTrib,   ...(_b('Tributos',        0.15, 0.25)) },
                      ];
                      return (
                        <>
                          {headerLine}
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                            {cats.map(c => {
                              const pct = c.val / total;
                              const cor = pct >= c.alto ? '#DC2626' : pct >= c.atencao ? '#F59E0B' : '#1B6B2F';
                              // Escala visual fixa 0-100% (acomoda CPV de Construção até ~82%).
                              // Cores INVERTIDAS: verde à esquerda (despesa baixa = bom), vermelho à direita.
                              const ESCALA = 1.0;
                              const aP = Math.min(100, (c.atencao / ESCALA) * 100);
                              const hP = Math.min(100, (c.alto / ESCALA) * 100);
                              const pos = Math.min(100, (pct / ESCALA) * 100);
                              return (
                                <div key={c.label}>
                                  <div style={{ display: 'flex', justifyContent: 'space-between',
                                    alignItems: 'baseline', marginBottom: 4, gap: 8 }}>
                                    <span style={{ fontSize: 11, color: '#4A5563',
                                      fontWeight: 500 }}>{c.label}</span>
                                    <span style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
                                      <span style={{ fontSize: 11, color: '#4A5563', fontWeight: 500 }}>
                                        {fmtBRL(c.val)}
                                      </span>
                                      <span style={{ fontSize: 13, fontWeight: 700,
                                        color: cor, minWidth: 52, textAlign: 'right' }}>
                                        {fmtPct(pct)}
                                      </span>
                                    </span>
                                  </div>
                                  {/* Despesa — barra em tons de laranja, invertido: escuro à esquerda = ótimo */}
                                  <div style={{ position: 'relative', height: 6,
                                    borderRadius: 0,
                                    background: `linear-gradient(90deg, ` +
                                      `rgba(244,124,60,1.0) 0%, rgba(244,124,60,1.0) ${aP}%, ` +
                                      `rgba(244,124,60,0.50) ${aP}%, rgba(244,124,60,0.50) ${hP}%, ` +
                                      `rgba(244,124,60,0.18) ${hP}%, rgba(244,124,60,0.18) 100%)` }}>
                                    <div style={{ position: 'absolute', left: `${pos}%`,
                                      top: -2, width: 3, height: 10,
                                      background: cor, borderRadius: 0,
                                      transform: 'translateX(-1.5px)',
                                      boxShadow: '0 0 0 1.5px #FFF', zIndex: 2 }}/>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                          {benchmarks && (
                            <div title={benchmarks.fonte || ''}
                              style={{ fontSize: 9, color: '#7F8C8D', marginTop: 6,
                                fontStyle: 'italic' }}>
                              Faixas do setor: <strong>{benchmarks.setor}</strong> ⓘ
                            </div>
                          )}
                          <div style={{ display: 'flex', justifyContent: 'center', gap: 16,
                            fontSize: 11, marginTop: 'auto', paddingTop: 8,
                            letterSpacing: '0.04em', fontWeight: 600 }}>
                            <span style={{ color: '#1B6B2F' }}>ÓTIMO</span>
                            <span style={{ color: '#F59E0B' }}>REGULAR</span>
                            <span style={{ color: '#DC2626' }}>RUIM</span>
                          </div>
                        </>
                      );
                    })()}
                  </div>

                  {/* TENDENCIA 12M */}
                  <div onClick={() => setTela('comparativo')} title="Ver Previsto x Real"
                    style={{ background: '#FFF', padding: '13px 20px',
                    display: 'flex', flexDirection: 'column', cursor: 'pointer' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between',
                      alignItems: 'center', marginBottom: 12 }}>
                      <div style={{ fontSize: 12, fontWeight: 600,
                        letterSpacing: '0.14em', color: '#1B6B2F',
                        textTransform: 'uppercase',
                        textDecoration: 'underline',
                        textDecorationColor: 'rgba(27,107,47,0.35)',
                        textUnderlineOffset: 3 }}>
                        {/* O rótulo segue o seletor de período: 24 meses = 24M.
                            Dizia "12M" fixo, o que era falso na maioria das
                            seleções (26/07). */}
                        TENDÊNCIA {resumoNoPeriodo.length}M →
                      </div>
                      <div style={{ display: 'flex', gap: 14, fontSize: 13, color: '#4A5563', fontWeight: 500 }}>
                        <span><span style={{ color: '#1B6B2F', fontSize: 16 }}>●</span> Receita</span>
                        <span><span style={{ color: '#F47C3C', fontSize: 16 }}>●</span> Despesa</span>
                        {temCustoFixo && (
                          <span><span style={{ color: '#7F8C8D', fontSize: 16 }}>●</span> Despesa fixa</span>
                        )}
                      </div>
                    </div>
                    {resumoNoPeriodo.length > 0 && (
                      <div style={{ flex: 1, minHeight: 0 }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <AreaChart data={tendenciaDados}
                          margin={{ top: 6, right: 8, bottom: 4, left: 0 }}>
                          <defs>
                            <linearGradient id="gT1" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="0%" stopColor="#1B6B2F" stopOpacity={0.22}/>
                              <stop offset="100%" stopColor="#1B6B2F" stopOpacity={0}/>
                            </linearGradient>
                            <linearGradient id="gT2" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="0%" stopColor="#F47C3C" stopOpacity={0.14}/>
                              <stop offset="100%" stopColor="#F47C3C" stopOpacity={0}/>
                            </linearGradient>
                          </defs>
                          <XAxis dataKey="mesLabel" tick={{ fontSize: 11, fill: '#7F8C8D' }}
                            axisLine={{ stroke: '#E5E7EB' }} tickLine={false}/>
                          <YAxis tick={{ fontSize: 10, fill: '#7F8C8D' }}
                            axisLine={false} tickLine={false}
                            tickFormatter={v => v >= 1000 ? `${(v/1000).toFixed(0)}k` : v}/>
                          <Area type="monotone" dataKey="receita"
                            stroke="#1B6B2F" strokeWidth={2.5} fill="url(#gT1)" dot={false}/>
                          <Area type="monotone" dataKey="despesa"
                            stroke="#F47C3C" strokeWidth={2.5} fill="url(#gT2)" dot={false}/>
                          {/* Despesa fixa: linha contínua, como as outras duas. Sem
                              preenchimento — as áreas de receita e despesa são
                              volume; esta é referência e não pode competir. */}
                          {temCustoFixo && (
                            <Area type="monotone" dataKey="custo_fixo"
                              stroke="#7F8C8D" strokeWidth={2.5}
                              fill="none" dot={false} connectNulls/>
                          )}
                        </AreaChart>
                      </ResponsiveContainer>
                      </div>
                    )}
                  </div>
                </div>

                {/* ============ TIER 3 - CAMPEÕES + VALOR ============
                    19/09, nesta ordem: Maior Despesa Fixa · Maior em Vendas ·
                    Mais Rentável · Valor Empresa. O Valor VOLTOU para cá: ele
                    tinha saído em 14/09 porque, com quatro cards de cinco
                    linhas, o nome do produto ficava com 101px. Mostrando só o
                    campeão o nome ocupa a largura inteira do card, em duas
                    linhas, e a restrição acabou.
                    Este tier é o elástico do Dashboard (`flex: 1`): absorve a
                    sobra vertical que ficava embaixo. */}
                <div style={{ display: 'grid',
                  // Quatro colunas IGUAIS (19/09). As larguras negociadas em
                  // 14/09 — 331px para a despesa, 165px para o Valor — eram do
                  // desenho em tabela, onde cada card precisava de uma largura
                  // diferente para o nome e o rodapé não quebrarem. Com o nome
                  // em duas linhas e o número embaixo, os quatro cabem no mesmo
                  // molde: em 1400px são 338px cada, e nenhum dos três nomes da
                  // MORED corta.
                  gridTemplateColumns: 'repeat(4, 1fr)',
                  gap: 12,
                  flex: '1 1 auto', minHeight: 76 }}>
                  {/* O campeão de cada modalidade. Sempre o MAIOR — a ordem vem
                      do backend, aqui não se reordena. Duas métricas de
                      propósito: quem mais vende nem sempre é quem mais sustenta
                      — na MORED o 3º maior em receita (5,9%) contribui 4,6%.
                      Os números vêm prontos de `/indicadores/rankings`: o
                      Dashboard exibe, não calcula. */}
                  <CardCampeao
                    /* "MAIOR CONTA DE DESPESA", não "maior despesa" (21/09):
                       o que está aqui é uma CONTA do plano, que reúne dezenas
                       de beneficiários — na Mored, 45 numa conta só. Chamar de
                       despesa promete um item que o documento não tem
                       ([[rotulo-tem-que-descrever-o-numero]]).
                       O rodapé mostra QUEM recebeu, que é o que permite agir, e
                       diz quanto do valor tem nome no histórico. Folha não tem
                       fornecedor: numa conta de salário a cobertura é 0% e o
                       rodapé cai para o acumulado ([[expor-nao-interpretar]]). */
                    /* O rótulo descreve o que o número É: o backend ranqueia
                       só as contas que aparecem em TODOS os meses e variam
                       menos de 2× — o maior COMPROMISSO, não o maior volume. */
                    titulo="MAIOR DESPESA FIXA" destino="despesas_custos" onIr={setTela}
                    /* "% do Total" quando o ranking vem da árvore do plano (o
                       denominador é a despesa administrativa inteira, a mesma
                       da aba); "% do Fixo" quando não há balancete e o ranking
                       cai na classificação do razão. Rótulo tem de descrever o
                       denominador ([[rotulo-tem-que-descrever-o-numero]]). */
                    colunas={[rankings?.despesas_da_arvore ? '% do Total' : '% do Fixo',
                      '% da Rec.']} cor="#F47C3C"
                    vazio="Sem despesa classificada no período."
                    linhas={rankings === null ? null : (rankings.despesas || []).map(x => ({
                      nome: x.nome, a: x.pct_fixo, b: x.pct_faturamento, alerta: false,
                    }))}
                    /* Nomear alguém é dizer "é aqui que o dinheiro vai". O
                       backend só autoriza (`nomear`) quando esse alguém supera
                       a SEGUNDA conta do bloco — senão o card promoveria o
                       maior de um balaio acima de uma conta inteira maior. Na
                       Mored o Joselito perde para Assistência Médica nos dois
                       períodos ([[verificar-nos-dois-lados-da-virada]]). Sem
                       autorização o fato continua na tela, sem o nome. */
                    rodapeEsq={rankings?.despesas_maior_beneficiario?.nome
                      ? (rankings.despesas_maior_beneficiario.nomear === false
                        ? <>Pulverizada: o maior recebe{' '}
                          <b style={{ color: '#1A1A1A' }}>
                            {fmtPct(rankings.despesas_maior_beneficiario.pct)}</b> da conta</>
                        : <>Maior: <b style={{ color: '#1A1A1A' }}>{rankings.despesas_maior_beneficiario.nome}</b>
                          {' · '}{fmtPct(rankings.despesas_maior_beneficiario.pct)} da conta</>)
                      : (rankings?.despesas_pct_acumulado != null
                        ? <>Os Top 5 somam <b style={{ color: '#1A1A1A' }}>{fmtPct(rankings.despesas_pct_acumulado)}</b>
                          {rankings.despesas_da_arvore ? ' do total' : ' do fixo'}</>
                        : null)}
                    rodapeDir={rankings?.despesas_maior_beneficiario?.cobertura_valor != null
                      ? `${rankings.despesas_maior_beneficiario.distintos} beneficiários · ${fmtPct(rankings.despesas_maior_beneficiario.cobertura_valor)} com nome`
                      : (rankings
                        ? `${rankings.despesas_total} contas · ${fmtBRL(rankings.despesas_fixo_mensal)}/mês` : '')}
                  />
                  <CardCampeao
                    titulo="MAIOR EM VENDAS" destino="despesas_custos" onIr={setTela}
                    colunas={['Margem', '% Vendas']} cor="#1B6B2F"
                    vazio="Cadastre os Custos Diretos para ver o ranking."
                    linhas={rankings === null ? null : (rankings.produtos || []).map(x => ({
                      // Mesmas colunas do card ao lado, para que as duas
                      // leituras da carteira se comparem lado a lado. O que
                      // separa os dois cards é o CRITÉRIO: aqui, venda.
                      nome: x.nome, a: x.margem, b: x.pct_receita,
                      barra: x.pct_receita, alerta: false,
                    }))}
                    rodapeEsq={rankings?.produtos_pct_acumulado != null
                      ? <>Os Top 5 somam <b style={{ color: '#1A1A1A' }}>{fmtPct(rankings.produtos_pct_acumulado)}</b> da receita</> : null}
                    rodapeDir={rankings ? `${rankings.produtos_total} produtos` : ''}
                  />
                  {/* A MESMA carteira lida por outro eixo (14/09): o card ao
                      lado ordena por quanto o produto VENDE; este, por quanto
                      ele RENDE em cada real vendido. Sem ele, um produto muito
                      rentável de venda pequena nunca aparece no Dashboard — e é
                      justamente esse o achado que o consultor procura.
                      Verde nos dois porque a cor diz PRODUTO; laranja é
                      despesa. */}
                  <CardCampeao
                    titulo="MAIS RENTÁVEL" destino="despesas_custos" onIr={setTela}
                    colunas={['Margem', '% Vendas']} cor="#1B6B2F"
                    vazio="Cadastre os Custos Diretos para ver o ranking."
                    linhas={rankings === null ? null : (rankings.produtos_rentaveis || []).map(x => ({
                      nome: x.nome, a: x.margem, b: x.pct_receita, alerta: false,
                      pos: x.posicao_vendas,
                    }))}
                    rodapeEsq={rankings?.produtos_margem_mediana != null
                      ? <>Mediana da carteira <b style={{ color: '#1A1A1A' }}>{fmtPct(rankings.produtos_margem_mediana)}</b></> : null}
                    rodapeDir={rankings ? `${rankings.produtos_com_venda} com venda` : ''}
                  />
                  {/* Valor → Valor */}
                  <div onClick={() => setTela('valor')} title="Ver Valor"
                    style={{ background: '#FFF0E6', padding: '7px 16px',
                    display: 'flex', flexDirection: 'column',
                    justifyContent: 'center', alignItems: 'center',
                    textAlign: 'center', cursor: 'pointer' }}>
                    <div style={{ fontSize: 11, color: '#C45826',
                      letterSpacing: '0.10em', fontWeight: 600,
                      textDecoration: 'underline',
                      textDecorationColor: 'rgba(196,88,38,0.35)',
                      textUnderlineOffset: 3,
                      whiteSpace: 'nowrap' }}>VALOR EMPRESA →</div>
                    {(() => {
                      // O Dashboard é painel de SINALIZADORES: exibe, não
                      // calcula. Os dois números vêm do backend
                      // (`/indicadores/calcular`), que os mede com o setor REAL
                      // da empresa e o EBITDA ANUALIZADO — a mesma base da tela
                      // Valor.
                      //
                      // Até 30/07 esta função fazia `ebitda * 5` e `ebitda * 8`
                      // aqui mesmo: os múltiplos do setor "geral" copiados à mão
                      // da tabela do backend, aplicados ao EBITDA do PERÍODO.
                      // Na MORED (Indústria/Manufatura, 4× a 7×) dava
                      // R$ 20,6M—R$ 32,9M contra os R$ 8,2M—R$ 14,4M da tela
                      // Valor. Dois números para a mesma empresa, 2,5× de
                      // diferença, e o consultor sem saber qual levar ao cliente.
                      const minV = indicadores.valor_empresa_min || 0;
                      const maxV = indicadores.valor_empresa_max || 0;
                      if (maxV <= 0) return (
                        <>
                          <div style={{ fontSize: 19, fontWeight: 700,
                            color: '#7F8C8D', marginTop: 6 }}>—</div>
                          <div style={{ fontSize: 11, color: '#7F8C8D', marginTop: 3 }}>
                            {(indicadores.ebitda || 0) <= 0
                              ? 'EBITDA negativo'
                              : 'Sem faixa para o setor'}
                          </div>
                        </>
                      );
                      const fmt = (v) => {
                        if (v >= 1000000) return 'R$ ' + (v/1000000).toFixed(1).replace('.', ',') + 'M';
                        if (v >= 1000)    return 'R$ ' + (v/1000).toFixed(0) + 'k';
                        return 'R$ ' + v.toFixed(0);
                      };
                      const mMin = indicadores.multiplo_min;
                      const mMax = indicadores.multiplo_max;
                      // Gordon (19/09): a segunda leitura do mesmo valor,
                      // empilhada sob a faixa. Vem calculada do backend, pela
                      // MESMA função da aba Valor. `null` quando a fórmula não
                      // tem significado — EBITDA não positivo, ou WACC ≤ g, em
                      // que o denominador zera ou inverte o sinal. Nesse caso a
                      // pilha mostra só a faixa, sem linha vazia.
                      const gordon = indicadores.valor_gordon;
                      const gPct   = indicadores.perpetuidade_g;
                      const wacc   = indicadores.wacc;
                      const LINHA_VALOR = { fontSize: 22, fontWeight: 700,
                        color: '#F47C3C', letterSpacing: '-0.02em',
                        whiteSpace: 'nowrap', lineHeight: 1.15 };
                      const LINHA_NOTA = { fontSize: 11, color: '#7F8C8D',
                        whiteSpace: 'nowrap' };
                      // #C45826 é a cor que o próprio card já usa no título.
                      // Os cards irmãos rotulam em #9AA5A0, que sobre este
                      // fundo pêssego fica sujo — e inventar um hex novo para
                      // o mesmo papel é criar paleta paralela.
                      const ROTULO = { fontSize: 9.5, fontWeight: 700,
                        letterSpacing: '0.07em', textTransform: 'uppercase',
                        color: '#C45826', whiteSpace: 'nowrap' };
                      return (
                        <>
                          <div style={{ ...ROTULO, marginTop: 6 }}>Múltiplo do setor</div>
                          {/* Medido em 19/09: a faixa da MORED ocupa 195px e o
                              card tem 195px úteis em tela de 1024px — cabe por
                              zero, e uma empresa de porte maior
                              ("R$ 128,4M a R$ 224,7M") estoura em qualquer
                              largura. O Dashboard é `scroll-zero`
                              (overflow: hidden), então o que não cabe some, não
                              rola. Sem `nowrap` no conjunto a linha quebra
                              quando precisa; o `nowrap` fica em cada valor, que
                              nunca pode partir no meio ("R$ 15," / "8M"). */}
                          <div style={{ ...LINHA_VALOR, whiteSpace: 'normal',
                            marginTop: 2 }}>
                            <span style={{ whiteSpace: 'nowrap' }}>{fmt(minV)}</span>{' '}
                            <span style={{ fontSize: 12, fontWeight: 600,
                              color: '#7F8C8D' }}>a</span>{' '}
                            <span style={{ whiteSpace: 'nowrap' }}>{fmt(maxV)}</span>
                          </div>
                          {mMin && mMax ? (
                            <div style={{ ...LINHA_NOTA, marginTop: 3 }}>
                              EBITDA anual × {mMin}—{mMax}
                            </div>
                          ) : (
                            <div style={{ ...LINHA_NOTA, marginTop: 3 }}>Faixa estimada</div>
                          )}
                          {gordon > 0 && (
                            <>
                              <div style={{ width: 46, height: 1,
                                background: 'rgba(196,88,38,0.30)', margin: '11px 0' }}/>
                              <div style={ROTULO}>Perpetuidade Gordon</div>
                              <div style={{ ...LINHA_VALOR, marginTop: 2 }}>{fmt(gordon)}</div>
                              <div style={{ ...LINHA_NOTA, marginTop: 3 }}>
                                g {fmtPct(gPct)} · WACC {fmtPct(wacc)}
                              </div>
                            </>
                          )}
                        </>
                      );
                    })()}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>

    </div>
  );
}